import React, { useState, useMemo, useRef, useEffect, useCallback, Fragment } from 'react';
import { BarChart, Bar, LineChart, Line, ComposedChart, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine, ReferenceArea, LabelList, Rectangle } from 'recharts';
import { Bell, BellOff, MoreHorizontal, UserCog, Award, Target, Trophy, Settings, Wrench, Upload, X, Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, FileUp, Check, AlertCircle, ClipboardList, Copy, TrendingUp, DollarSign, FileText, Activity, BookOpen, Play, File, Plus, Tag, Folder, Edit2, Trash2, ExternalLink, Receipt, UtensilsCrossed, LogOut, Users, Shield, Eye, UserPlus, UserMinus, User, KeyRound, ScrollText, Share2, Compass, Coins, Wallet, RefreshCw, WifiOff, Info, Hash, LifeBuoy, MessageSquare, Mail, Paperclip, Send, ArrowUpCircle, Lightbulb, Gauge, Zap, Flame, Sun, Moon, Menu, CalendarDays, Star, Smartphone, Tablet, RotateCcw, SlidersHorizontal, Home, Briefcase, Factory, Plug, Package, UserCheck, UserX, Wand2, BarChart3, Phone, Building2, MapPin, CreditCard, TrendingDown, Camera, FileSpreadsheet, FileArchive, Bold, Italic, Underline, Strikethrough, Eraser, Baseline, Highlighter, List, ListOrdered, Indent, Outdent, AlignLeft, AlignCenter, AlignRight, Link2, Link2Off, Minus, Undo2, Redo2, Code2, Pencil, StickyNote, Layers, Inbox, ThumbsUp, ThumbsDown } from 'lucide-react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { getUsers, register, updateUserRole, updateOwnEtApiKey, deleteUser, resendWelcome, setUserActive, impersonateUser, stopImpersonate, changePassword, exportData, deleteAccount, getAuditLogs } from './auth/api';
import LoginPage from './auth/LoginPage';
import MfaVerifyPage from './auth/MfaVerifyPage';
import ResetPasswordPage from './auth/ResetPasswordPage';
import PrivacyPolicyPage from './auth/PrivacyPolicyPage';
import { scopeFilter } from './auth/scope';
import { evaluateBrandAccess, getBrandFromHostname, etLogoToegestaan, isOnrenderHost, MAIN_BRAND } from './brandConfig';
import { leesMerkCache, schrijfMerkCache, leesGebruikerBranding, schrijfGebruikerBranding, onthoudTabMerk } from './brandingCache';
import { brandHopTarget, hopToBrandDomain } from './auth/handoff';
import { StageDescContext, useStageDesc } from './oppStage';
import { computePortfolioKPIs, buildMonthlySeries, detectProductSwitchesAndRenewals, aggregateByMonth, closedContractDurations, binDurations, cohortRetentionMatrix, multiConnectionLookup, monthsBetween, parseDate, DEPARTURE_DROP, DEPARTURE_EOC, DEPARTURE_LOSS, DEPARTURE_ILC, LOST_STATUSES, PORTFOLIO_STATUS_LABEL, vertrekStand, STAND_NAAR_STATUS, DEPARTURE_KIND_LABELS } from './portfolio';
import {
  SERVOLT_DEFAULT_TARIEVEN, normaliseerTarieven, bouwEanRijen, feePerCategorie, vorigeMaand, maandVan, orderStartDatum,
  EVIDENT_DEFAULT_TARIEVEN, normaliseerEvident, evidentEanRijen, evidentPerVerkoper, EVIDENT_GROEP_LABEL,
  evidentActieveOrders, evidentProjectie, evidentProjectiePerVerkoper, evidentGemiddeldTarief,
} from './customFee';
import { exportBestandsnaam, exportScopeStaart } from './exportNaam';
import { versieLabel } from './versie';
import { voegVoorGroet, slotPositie } from './mailGroet';
import { ontbrekendNieuwTicket, ontbrekendMail, ontbrekendBericht, somOp } from './helpdeskVerplicht';
import { zetProspect, bronLabel, verbergBronLabel, isProspectRol } from './prospect';
import { startDemoWeergave, stopDemoWeergave, demoWeergaveActief, maskeerCel, maskeerTekst, isIdentificatieKolom } from './demoWeergave';
import { bepaalLeads, LEADS_BRONNEN } from './leads';
import { mergeBranding } from './brandingMerge';
import { scopeOptions, EMPTY_STRUCTURE, defaultScopeSelection, favorietScope } from './salesStructure';
import { enrichCommissionTeams, mapApiCommissionItem } from './partnerCommission';
import { resellerFeeUitzondering, resellerFeeUitzonderingViaVerkoper } from './resellerFee';
import { makeZip } from './zip';
import ReactDOM from 'react-dom';
import { MODULE_DOCS } from './moduleDocs';
import { RC_ORDER, RC_JAREN, RC_CASES, RC_CASE_IDS, rcCase, rcLock, calcAll, validateField, rcKopieerCase, jGrens, RC_TYPES, rcType, rcTypeVoorRol, rcMagTypeWisselen, rcFabriek, rcStandaard, rcStaat, rcPersoonlijkVan, rcFavorietVan, rcAantalVelden, rcSetVerschil, rcConfigVoorOpslag, rcConfigRijen, rcRijWaarde, rcZetRijWaarde, RC_PARAM_INFO, RC_SEGMENT_NAAM } from './revenueCalculator';
import { MENUKAART, brpCostVoor } from './menukaart';
import { PAY_STATUS_PARTIAL, migrateSavedFilters } from './filterMigrations';
import { fetchPaginated } from './apiPaged';
import SchermMarkeerVenster from './SchermMarkeerVenster';
import { openPortalPage } from './displayMode';
import { maandenInPeriode, maandVanDatum } from './billshockPeriode';
import { perAfrekeningsmaand, cumulatief, maandLabel as bsMaandLabel } from './billshockMaanden';
import { readSavedFilters, writeSavedFilters } from './filterStorage';
import { speel as gamiSpeel, ontgrendelAudio, SOUNDS as GAMI_SOUNDS } from './gamification/sounds';
import { bundel as gamiBundel, plafond as gamiPlafond, sindsLaatsteBezoek, perDag as gamiPerDag, effectiefVolume, leesGeluid, leesKanalen, vandaagIso, isGepauzeerd } from './gamification/presentatie';
import { ACTIVE_PAGE_KEY, leesSessieWaarde, schrijfSessieWaarde, actieveSubpaginas } from './sessieOpslag';
import { groepeerEanVolumes } from './eanVolume';
import { staafGrafiekSvg, donutSvg, legendeHtml, meetGrafiekSvg } from './reportCharts';
import { sorteerBillShock, BS_SORT_DEFAULT, bouwTopGroepen, groepenNaarRijen, aggregeerPerKlant as bsAggregeerPerKlant, klantTotalen as bsKlantTotalen, klantSleutel as bsKlantSleutel, leesBedrag as bsLeesBedrag, filterMinimumKlantBedrag as bsFilterMinimumKlantBedrag, filterMinimumKlantDeviation as bsFilterMinimumKlantDeviation } from './billshockSort';
import { OPMAAK_KLEUREN, OPMAAK_MARKERINGEN, OPMAAK_GROOTTES, OPMAAK_LETTERTYPES, OPMAAK_BLOKKEN, verwijderOpmaak, fontNaarSpan, blokVanWaarde, grootteVan, lettertypeVan } from './opmaak';
import { afbeeldingenUitEvent, verkleinAfbeelding, afbeeldingHtml, inlineBytesIn, MAX_PER_BEELD as PLAK_MAX_PER_BEELD } from './plakAfbeelding';
import {
  unitInfo, toKwh, fmtQty, dedupeNewest, readingRows as mtrReadingRows, localDayWindowUtc,
  aggregateBuckets, energyDatasets, splitByCategory, mtrFmtNum,
  ENERGY_METHODS, CONSUMPTION_METHODS, PRODUCTION_METHODS,
  quarterDetail, verkoopprijs, heeftInjectiePrijs, beDayKey as mtrBeDagKey, beTimeKey as mtrBeTijdKey,
  siteFlows, combineSites, topBuckets, pctDelta, bucketKey,
} from './metering';
import {
  prijsMogelijk, prijsUitleg, prijsBron, basisprijs, indexEurMwh, indexMeta, meterKlasse,
  maandVanBucket, INDEX_CODES,
} from './indexPrijs';
import { gasSerieMeta, gasRijen, gasVergelijkingen, gasDekking } from './gasPrijzen';
// Platformregel datumfilters: "Van" telt mee, "Tot" niet — zie src/datumFilter.js.
import { laatsteDag, totNa, dagenInVenster, maandNa } from './datumFilter';
import { laatsteNotering, bouwMaandprijzen, bouwProfiel, historischeMaandprijzen, maandprijzenKolommen, maandprijzenBestandsnaam, AANTAL_MAANDEN, SOORT_LABEL, sorteerContracten, forwardLadder, LADDER_STANDAARD, LADDER_MAAND_OPTIES, LADDER_KWARTAAL_OPTIES } from './forwardMaandprijzen';
import { actueelRapport, gasRapport, liveRapport, profielRapport, forwardRapport, maandNaamKort } from './marktRapport';
import { werkdagenTussen, werkdagBucket, WERKDAG_BUCKETS } from './werkdagen';
import { hdTijdMs, hdFmtBrussel, hdUurBrussel, hdVoegWijzigingenIn } from './helpdeskDatum';
import { InfoTip, RevenueDocs, RevenueDocsButton, RevenueMenukaartDocs, RevenueMenukaartButton, RevenueTechDocs, RevenueTechDocsButton, REVENUE_TABLE_DOCS, sbImbalanceRatesForLabel, revenueDocsStandaard, toontRevenueDocs } from './revenueDocs';
import { AI_DELEN, AI_PERIODES, AI_VARIANTEN, AI_UITKOMST, AI_MAX_INSTRUCTIE, AI_VOORSTEL_KOLOMMEN, aiGetal, aiPct, aiDuur, aiTokens, aiVerschil, aiVerschilPunten, aiPeriodeLabel, aiPeriodeTitel, aiDatumTijd, aiDatum, aiVariantNaam, aiKanaalNaam, aiQuery, aiGroepKolommen, aiHoofdletter, aiBronSoort } from './helpdeskAi';
import { SUBPAGE_CATALOG, SUBPAGE_FULL_ACCESS_ROLES, subPageKey, subPageAllowedForLabel, subPageAllowedForRole, defaultSubPagesForRole, subPageSetForUser, filterSubPages } from './subPages';

// Tab-menu van een module met per gebruiker instelbare subpagina's.
// Geeft de zichtbare tabs terug plus `mag(id)` voor de bijhorende inhoudsblokken.
// `alleTabs` moet stabiel zijn (module-level const of eigen useMemo), anders
// herberekent dit elke render.
//
// admin/ET-dossierbeheerder krijgen `null` uit subPageSetForUser en zien dus
// altijd alles, ook subpagina's die later worden toegevoegd.
function useSubPages(moduleId, currentUser, alleTabs) {
  return useMemo(() => {
    const tabs = filterSubPages(moduleId, alleTabs, subPageSetForUser(currentUser));
    const ids = new Set(tabs.map((t) => t.id));
    return { tabs, mag: (id) => ids.has(id), leeg: tabs.length === 0 };
  }, [moduleId, currentUser, alleTabs]);
}

// Melding wanneer een gebruiker de module wél ziet, maar geen enkele subpagina
// toegekend kreeg — anders zou de pagina zonder uitleg leeg blijven.
const GeenSubpaginas = ({ module }) => (
  <div className="rounded-2xl border border-slate-700/50 bg-slate-800/60 p-6 text-center">
    <p className="text-slate-300 text-sm font-semibold">Geen subpagina's toegekend</p>
    <p className="text-slate-500 text-xs mt-1">Voor {module} is er voor jouw account geen enkele subpagina zichtbaar gemaakt. Vraag een beheerder om er minstens één toe te kennen.</p>
  </div>
);

const parseNumber = (val) => { if (!val || val === '' || val === '#N/B') return 0; return parseFloat(String(val).replace(/[€\s]/g, '').replace(/\./g, '').replace(',', '.')) || 0; };
const parseInvoiceNumber = (val) => { if (!val || val === '' || val === '#N/B') return 0; return parseFloat(String(val).replace(/[€\s]/g, '')) || 0; };
// Legacy-signatuur (array van platte objecten + filename zonder extensie).
// Delegeert naar de centrale exportRowsToCsv zodat ELKE tabel-export exact
// hetzelfde Excel-NL-formaat krijgt (BOM, ';'-separator, komma-decimalen).
// Kolommen worden afgeleid uit de keys van de eerste rij.
const exportToCSV = (data, filename) => {
  if (!data || !data.length) return;
  const columns = Object.keys(data[0]).map(k => ({ header: k, get: r => r[k] }));
  exportRowsToCsv(`${filename}.csv`, columns, data);
};
// Non-breaking space tussen € en bedrag zodat het euroteken nooit op een andere
// regel komt dan het getal (afbreken op de spatie). De duizend/decimaal-scheiders
// in nl-BE zijn '.'/',' (geen spaties), dus dit raakt enkel de spatie na €.
const formatCurrency = (v) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(v).replace(/\s/g, ' ');
const formatNumber = (v) => new Intl.NumberFormat('nl-BE').format(v);
const formatNumberRounded = (v) => new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 0 }).format(Math.round(v || 0));
// Afgeronde euro-weergave (geen centen, nl-BE-groepering, "€ "-prefix) — één bron
// i.p.v. losse `€{Math.round(x).toLocaleString('nl-BE')}`-expressies verspreid.
const formatEuroRounded = (v) => `€ ${formatNumberRounded(v)}`;
// Statuskleuren van de Revenue-samenvatting. ÉÉN bron voor de vier blokken (icoon,
// linkerrand, achtergrond totaalrij) én voor de reeksen in de grafiek "Vergelijking
// per categorie", zodat blok en balk altijd dezelfde kleur dragen.
const STATUS_ACCENT = { goed: '#22c55e', wanbetaler: '#ef4444', deels: '#f59e0b', openstaand: '#84cc16' };

// ── Grafiekpalet van het platform ────────────────────────────────────────────
// ÉÉN bron voor alle categorische reeksen, zodat hetzelfde type overal dezelfde
// kleur draagt: B2C is op Sales en op Revenue dezelfde blauw, een contract-
// verlenging is op Sales en op Portfolio Insights dezelfde groen.
//
// De acht tinten zijn dark-gekalibreerde stappen: ze vallen binnen de
// lichtheidsband van ZOWEL het lichte (#ffffff) als het donkere (#1e293b)
// kaartoppervlak, zodat één hex in beide thema's werkt en geen enkele grafiek
// het thema hoeft te kennen. Gevalideerd met de validator van de dataviz-skill
// op de vier reekscombinaties die echt naast elkaar voorkomen — segmentstapel,
// meterstapel (8 reeksen), bewegingen en de Revenue-bolletjes — in beide
// thema's: lichtheidsband, chromafloor, kleurenblindheid-scheiding (protan/
// deutan/tritan), normaalzicht-drempel en contrast, allemaal PASS.
//
// Twee dingen die je NIET zomaar mag wijzigen:
//  1. De VOLGORDE waarin reeksen gestapeld worden. De scheiding is per aangrenzend
//     paar gemeten; rood naast aqua of geel naast oranje zakt onder de drempel.
//     Zie de stapelvolgorde bij COLORS_METER hieronder.
//  2. Een tint vervangen zonder de validator opnieuw te draaien.
const CHART_HUES = {
  blauw: '#3987e5', oranje: '#d95926', aqua: '#199e70', geel: '#c98500',
  magenta: '#d55181', groen: '#0f9b0f', violet: '#9085e9', rood: '#e66767',
  // Goud draagt alleen B2B. Het staat bewust náást `oranje` i.p.v. in de plaats
  // ervan: oranje blijft SMR3 in de meterweergave, en twee bijna gelijke warme
  // tinten in één grafiek is precies wat we hier aan het oplossen zijn. Goud en
  // B2B komen nergens samen met `geel` in beeld — gecontroleerd op de vier
  // reekscombinaties.
  goud: '#BC8B1E',
};
// Klantsegment — identiek op Sales, Revenue en de taartgrafieken.
// `total` is de referentielijn, geen categorie — vandaar neutrale inkt via de
// bestaande themavariabele i.p.v. een negende tint.
const COLORS_SEGMENT = { B2C: CHART_HUES.blauw, B2B: CHART_HUES.goud, Custom: CHART_HUES.violet, Injectie: CHART_HUES.aqua, total: 'var(--chart-neutral)' };
// Portfoliobewegingen — identiek op Sales en Portfolio Insights. Sales voegt
// product- en klantenwissel samen tot één reeks; die erft de magenta van de
// productwissel (de dominante soort).
const COLORS_BEWEGING = {
  productwissel: CHART_HUES.magenta, klantenwissel: CHART_HUES.geel,
  verlenging: CHART_HUES.groen, wissels: CHART_HUES.magenta,
};
// Kleurcodering van de segmentbolletjes in de Revenue-samenvatting: per TYPE
// klant, niet per rij. Zo heeft B2C overal dezelfde kleur, ongeacht elektriciteit
// of gas, en springt injectie er als aparte categorie uit.
const SUMMARY_DOT_COLORS = { B2C: COLORS_SEGMENT.B2C, B2B: COLORS_SEGMENT.B2B, Custom: COLORS_SEGMENT.Custom, Injectie: COLORS_SEGMENT.Injectie };
const summaryDotColor = (label) => {
  const l = String(label || '');
  if (/injection/i.test(l)) return SUMMARY_DOT_COLORS.Injectie;
  if (/^B2C/i.test(l)) return SUMMARY_DOT_COLORS.B2C;
  if (/^B2B/i.test(l)) return SUMMARY_DOT_COLORS.B2B;
  return SUMMARY_DOT_COLORS.Custom;
};
// kWh → MWh voor weergave (1 MWh = 1000 kWh). Normaal zonder decimalen, maar een
// volume dat wél bestaat en tóch op 0 MWh zou afronden krijgt één decimaal — anders
// zou een kleine post als "0" worden gelezen terwijl er wel degelijk volume is.
const formatMwhFromKwh = (kwh) => {
  const raw = kwh || 0;
  const mwh = raw / 1000;
  const decimals = (Math.round(mwh) === 0 && raw !== 0) ? 1 : 0;
  return new Intl.NumberFormat('nl-BE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(mwh);
};
// Gedeelde Recharts-tooltip-stijl — één bron van waarheid i.p.v. ~4 licht
// afwijkende inline-varianten. Donker-thema-waarden; in het lichte thema wint de
// `.recharts-default-tooltip`-override (met !important) in het brand-color-blok.
const CHART_TOOLTIP = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10, color: '#e2e8f0', boxShadow: '0 8px 28px -10px rgba(0,0,0,.45)' };
// ── Grafiekafwerking per module (klasse `grafiek-modern`) ─────────────────────
// Een module die deze klasse op haar buitenste element zet, krijgt de strakkere
// grafiekstijl: alleen horizontale rasterlijnen, geen aslijn of streepjes op de
// Y-as, een compacte tooltip met kleurbolletjes en een zachte aanwijsbalk. Dat
// zit allemaal in het centrale themablok (zoek op `grafiek-modern`), zodat het
// per thema klopt en geen enkele grafiek het thema hoeft te kennen. Wat CSS niet
// kan — vorm van het legenda-icoon, balkbreedte, afronding — staat hieronder.
// Kleuren en stapelvolgorde blijven wat ze zijn: zie CHART_HUES hierboven.
const CHART_LEGENDA = { iconType: 'circle', iconSize: 8, wrapperStyle: { fontSize: 12, paddingTop: 10 } };
const CHART_AS_X = { tickLine: false, tickMargin: 6 };
const CHART_AS_Y = { axisLine: false, tickLine: false, tickMargin: 6 };
const CHART_STAPEL_STRAAL = 5;
// Afronding van een gestapelde balk. Recharts rondt af per REEKS: staat de
// afronding op de laatste reeks en is die in een maand leeg, dan eindigt de
// stapel daar vierkant. Deze vorm kijkt per balk of er nog iets bovenop ligt.
// `sleutels` = de dataKeys in stapelvolgorde, `i` = de plaats van deze reeks.
const stapelVorm = (sleutels, i) => (props) => {
  const rij = props.payload || {};
  const bovenste = !sleutels.slice(i + 1).some(k => Number(rij[k]) > 0);
  return <Rectangle {...props} radius={bovenste ? [CHART_STAPEL_STRAAL, CHART_STAPEL_STRAAL, 0, 0] : 0} />;
};
// Lijngrafieken: het legenda-icoon is een streepje dat het lijntype volgt, zodat
// vol (gemeten) en gestreept (forecast) ook in de legenda te onderscheiden zijn.
const CHART_LEGENDA_LIJN = { iconType: 'plainline', iconSize: 18, wrapperStyle: { fontSize: 12, paddingTop: 10 } };
const CHART_LIJN_PUNT = { r: 2.5, strokeWidth: 1.5, fill: 'var(--chart-surface, #ffffff)' };
const CHART_LIJN_PUNT_ACTIEF = { r: 5, strokeWidth: 2, stroke: 'var(--chart-surface, #ffffff)' };
// Y-as-tick voor de Revenue "Vergelijking per categorie"-grafiek. De EL/GAS-
// kleuring zit nu in de sectiekop, dus het label zelf is neutraal; het bolletje
// vóór het label volgt dezelfde segmenttype-codering als de samenvattingsblokken
// (B2C/B2B/Custom/Injectie), zodat beide weergaven dezelfde taal spreken.
const CompCatTick = ({ x, y, payload }) => {
  const label = (payload && payload.value) || '';
  return <>
    <circle cx={x - 122} cy={y} r={3.5} fill={summaryDotColor(label)} />
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} className="comp-axis-tick">{label}</text>
  </>;
};
const getDaysOverdue = (dueDate) => { if (!dueDate) return 0; const today = new Date(); today.setHours(0, 0, 0, 0); const due = new Date(dueDate); due.setHours(0, 0, 0, 0); return Math.floor((today - due) / (1000 * 60 * 60 * 24)); };
const getAgingCategory = (daysOverdue) => { if (daysOverdue < 0) return 'nietVervallen'; if (daysOverdue <= 30) return 'd1_30'; if (daysOverdue <= 60) return 'd31_60'; if (daysOverdue <= 90) return 'd61_90'; if (daysOverdue <= 120) return 'd91_120'; if (daysOverdue <= 150) return 'd121_150'; if (daysOverdue <= 180) return 'd151_180'; if (daysOverdue <= 365) return 'd181_365'; if (daysOverdue <= 730) return 'd1_2jaar'; if (daysOverdue <= 1825) return 'd2_5jaar'; return 'ouder5jaar'; };
// ── Per-energielabel wanbetaler-config ───────────────────────────────────────
// Twee instellingen, allebei per energielabel te overschrijven in Instellingen →
// Revenue (bron: /api/label-config). Labels zonder rij vallen op de defaults:
//   - drempel wanbetaler      : BAD_DEBT_OVERDUE_DAYS_DEFAULT (40 dagen)
//   - oninbaarheidsperiode    : BAD_DEBT_RECOVERY_MONTHS_DEFAULT (9999 = volledig)
//
// De drempel is bewust een VAST aantal dagen. De vorige regel was "meer dan één
// kalendermaand", en die duurde 28 tot 31 dagen afhankelijk van de maand — met
// bovendien een sprong op maandeinden (31 maart − 1 maand rolde in JavaScript
// door naar 3 maart, waardoor klanten een dag of drie te vroeg wanbetaler werden
// en op 1 april weer terugsprongen).
//
// De config heeft de vorm { "<energielabel>": { overdueDays, recoveryMonths } }.
// Lees hem UITSLUITEND via de accessors hieronder: die vullen de defaults in, zodat
// een label zonder rij overal hetzelfde gedrag krijgt.
const BAD_DEBT_OVERDUE_DAYS_DEFAULT = 40;
const BAD_DEBT_RECOVERY_MONTHS_DEFAULT = 9999;

// ── Vervallen facturen in de verkoperstabellen (Sales) ───────────────────────
// De kolom "Vervallen" bij Top/Bottom/alle verkopers telt een factuur mee zodra
// ze minstens zoveel dagen over haar vervaldatum staat. Bewust LOSGEKOPPELD van
// de wanbetaler-drempel hierboven: die zegt "deze klant betaalt niet meer" (per
// energielabel instelbaar, standaard 40 dagen), terwijl deze kolom veel vroeger
// wil signaleren dat er iets blijft liggen. Eén vast getal voor alle labels, want
// het is een opvolgingssignaal voor de verkoper, geen afrekening.
const VERKOPER_VERVALLEN_DAGEN = 7;

// Belgisch standaard-BTW-tarief. Alle vergoedingsbedragen in het platform zijn
// EXCLUSIEF BTW; waar een bedrag inclusief getoond wordt, is dat een afgeleide
// weergave en nooit iets wat we opslaan.
const BTW_TARIEF = 0.21;
const metBtw = (bedragExcl) => (Number(bedragExcl) || 0) * (1 + BTW_TARIEF);

// API-vorm ({recovery_months, overdue_days}) → interne vorm. Tolerant voor de
// oude vorm (label → getal = maanden), zodat een gecachte respons niet stukloopt.
const normalizeLabelConfig = (raw) => {
  const out = {};
  for (const [label, v] of Object.entries(raw || {})) {
    if (v && typeof v === 'object') {
      out[label] = {
        recoveryMonths: Number.isFinite(Number(v.recovery_months)) ? Number(v.recovery_months) : BAD_DEBT_RECOVERY_MONTHS_DEFAULT,
        overdueDays: Number.isFinite(Number(v.overdue_days)) ? Number(v.overdue_days) : BAD_DEBT_OVERDUE_DAYS_DEFAULT,
      };
    } else {
      out[label] = { recoveryMonths: Number(v), overdueDays: BAD_DEBT_OVERDUE_DAYS_DEFAULT };
    }
  }
  return out;
};
const labelOverdueDays = (labelConfig, label) => {
  const v = Number(labelConfig?.[label]?.overdueDays);
  return Number.isFinite(v) && v > 0 ? v : BAD_DEBT_OVERDUE_DAYS_DEFAULT;
};
const labelRecoveryMonths = (labelConfig, label) => {
  const v = Number(labelConfig?.[label]?.recoveryMonths);
  return Number.isFinite(v) ? v : BAD_DEBT_RECOVERY_MONTHS_DEFAULT;
};
// De enige overdue-test in de app. `days` komt uit labelOverdueDays(...) op het
// energielabel van de factuur; zonder config valt hij op de default terug.
const isBadDebtOverdue = (vervaldatum, days = BAD_DEBT_OVERDUE_DAYS_DEFAULT) =>
  !!vervaldatum && getDaysOverdue(vervaldatum) >= days;
// Per-factuur variant: leest zelf het energielabel (relatieBedrijf) van de rij.
const isRecordBadDebtOverdue = (rec, labelConfig) =>
  isBadDebtOverdue(rec?.vervaldatum ?? rec?.dueDate, labelOverdueDays(labelConfig, rec?.relatieBedrijf));


// ─── Exporteren: ÉÉN knop, altijd op dezelfde plek ───────────────────────────
// Elke tabel en elke grafiek exporteert met hetzelfde compacte icoonknopje, en dat
// staat DIRECT NAAST DE TITEL van die tabel/grafiek. Daarvoor stond het overal
// anders: eens "Export Excel" met tekst in de titelrij, dan weer "Exporteer"
// onderaan de tabel, elders alleen een icoon in de filterbalk of in de
// paginakop — dezelfde handeling die je op elke pagina opnieuw moest zoeken.
//
// Het icoon zegt wat voor bestand je krijgt: een rekenblad (Excel/CSV), een
// document (PDF-rapport) of een archief (zip met meerdere bestanden). De volledige
// omschrijving zit in de tooltip én in aria-label — een icoon alleen is voor een
// schermlezer niets.
//
// Gebruik: <ExportKnop onExport={…} label="Exporteer de facturen naar Excel" count={rijen.length} />
// Zet hem in dezelfde flex-rij als de titel: <div className="flex items-center gap-2 min-w-0"><h3 …>Titel</h3><ExportKnop … /></div>
// 'maand' = een rekenblad per kalendermaand (Forward → Maandprijzen): zelfde CSV,
// maar het kalender-icoon onderscheidt hem van de gewone Excel-knop ernaast.
const EXPORT_ICONEN = { excel: FileSpreadsheet, pdf: FileText, zip: FileArchive, mail: Mail, maand: CalendarDays };
// Een PDF-rapport ziet er platformbreed anders uit dan een Excel-/CSV-export
// (22-10-2026): rode tint en het woordje "PDF" naast het documenticoon, zodat de
// twee knoppen naast elkaar nooit verwisseld worden. Rose-500 leest op het lichte
// én het donkere thema.
const ExportKnop = ({ onExport, label = 'Exporteer naar Excel', soort = 'excel', count = null, disabled = false, busy = false, className = '', innerRef = null }) => {
  const Icon = EXPORT_ICONEN[soort] || FileSpreadsheet;
  const titel = count == null ? label : `${label} (${formatNumberRounded(count)})`;
  const pdf = soort === 'pdf';
  return (
    <button
      ref={innerRef} type="button" onClick={onExport} disabled={disabled || busy} title={titel} aria-label={titel}
      className={`inline-flex items-center justify-center h-7 shrink-0 rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${pdf
        ? 'px-1.5 gap-0.5 border-rose-500/40 bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 hover:text-rose-400'
        : 'w-7 border-slate-600/60 bg-slate-700/50 text-slate-300 hover:bg-slate-600/60 hover:text-lime-300'} ${className}`}
    >
      {busy ? <Spinner size={13} /> : <Icon size={14} />}
      {pdf && <span className="text-[8px] font-bold leading-none tracking-wide">PDF</span>}
    </button>
  );
};
// De oude naam blijft bestaan voor de plekken die hem in de PAGINAKOP gebruiken
// (Orders, Bill Shock…): daar hoort dezelfde knop, alleen naast de paginatitel
// i.p.v. naast een tabeltitel.
const ExportButton = ({ onExport, label = 'Exporteer naar Excel', soort = 'excel', count = null, disabled = false }) =>
  <ExportKnop onExport={onExport} label={label} soort={soort} count={count} disabled={disabled} />;

// ─── Openstaand bedrag van een factuur ───────────────────────────────────────
// De facturatie-API levert het nog verschuldigde bedrag nu zelf mee
// (`amount_residual` → veld `openstaand` op het record). Dat is de bron van
// waarheid: ook een deelbetaling krijgt zo een exact bedrag i.p.v. het vroegere
// '?'. Het bedrag is INCL. btw — geverifieerd op de live API: bij een onbetaalde
// factuur is het exact gelijk aan het totaal, nooit aan het bedrag excl. btw.
//
// De afleiding uit de betaalstatus blijft als vangnet staan voor records waarvoor
// nog geen restbedrag is opgehaald (de snelle keyset-feed draagt het veld niet;
// de job "Invoices — openstaande bedragen" vult het aan):
//   Betaald / Omgekeerd → er staat niets meer open (0)
//   Niet betaald        → het volledige factuurbedrag staat open
//   Deels betaald / in_payment → ONBEKEND (null) → het scherm toont een '?'.
//     Bewust geen schatting: een verzonnen getal zou als harde vordering gelezen
//     worden.
// Werkt op beide factuurvormen in het portaal: Invoices gebruikt
// statusBetaling/totaal, Aging paymentStatus/total.
const invOutstanding = (f) => {
  const api = f?.openstaand;
  if (api != null && api !== '' && Number.isFinite(Number(api))) return Number(api);
  const s = String(f?.statusBetaling ?? f?.paymentStatus ?? '').toLowerCase();
  if (s === 'betaald' || s === 'omgekeerd') return 0;
  if (s === 'niet betaald') return Number(f?.totaal ?? f?.total) || 0;
  return null;
};

// Dezelfde vraag, maar voor OPTELLINGEN (aging-buckets, totalen per verkoper …).
// Een som mag niet stilzwijgend een factuur overslaan omdat haar restbedrag nog
// niet opgehaald is: dan valt er een vordering uit het beeld. Onbekend telt hier
// dus voor het volledige factuurbedrag — de bovengrens, precies zoals vóór het
// restbedrag bestond. Voor één cel in een tabel blijft `invOutstanding` de juiste
// keuze: daar is '?' eerlijker dan een bovengrens die als exact bedrag leest.
const invOutstandingOrTotal = (f) => invOutstanding(f) ?? (Number(f?.totaal ?? f?.total) || 0);

// ─── Uniforme tabel-helpers ──────────────────────────────────────────────────
// Standaardpagina-grootte voor ALLE tabellen in het portaal. Wijzig hier om
// het portaal-breed aan te passen. Tabellen die deze constante / hook
// gebruiken zijn automatisch consistent met deze regel.
const TABLE_PAGE_SIZE = 15;

// Excel-compatibele CSV-export. `columns` is een array van { header, get }
// waar `get(row)` de waarde uit een rij haalt. Numerieke waarden krijgen
// komma-decimal, BOM + ';' separator voor Excel-NL.
function exportRowsToCsv(filename, columns, rows, collect) {
  const txt = v => v == null ? '' : String(v).replace(/[\r\n;]/g, ' ');
  const num = v => v == null || Number.isNaN(Number(v)) ? '' : String(Number(v)).replace('.', ',');
  // Forceer Excel om de waarde als TEKST te behandelen (bv. een 18-cijferige GSRN
  // niet afronden naar 15 cijfers + nullen): schrijf ="waarde" als CSV-gequote
  // formule. Gebruik dit via { header, get, text: true } op de kolomdefinitie.
  const asText = v => {
    const s = (v == null ? '' : String(v)).replace(/[\r\n]/g, ' ').replace(/"/g, '""');
    return s === '' ? '' : `"=""${s}"""`;
  };
  // Identificatie-kolommen (GSRN/EAN = 18 cijfers) en élke lange puur-numerieke
  // string moeten als TEKST naar Excel, anders rondt Excel af op 15 significante
  // cijfers (de laatste cijfers worden nullen). Detectie gebeurt platform-breed
  // op twee manieren zodat geen enkele tabel-export dit nog hoeft te zetten:
  //  (1) kolomtitel die "gsrn" of "ean" bevat (vangt ook lege cellen consistent);
  //  (2) de waarde zelf is een string van ≥16 cijfers (≥16 = boven Excels
  //      15-cijfer-precisiegrens, dus precies de waarden die anders corrupt gaan).
  const isIdHeader = h => /gsrn|(^|[^a-z])ean([^a-z]|$)/i.test(String(h || ''));
  const isLongDigits = v => typeof v === 'string' && /^\d{16,}$/.test(v);
  // Demo-account (ook prospect): klantnummers en factuurnummers vallen uit élke
  // export weg, EAN's en het bronlabel gaan gemaskeerd mee — zoals op het scherm
  // (src/demoWeergave.js). Hier centraal, zodat geen enkele tabel-export het kan
  // vergeten.
  const demo = demoWeergaveActief();
  if (demo) columns = columns.filter(c => !isIdentificatieKolom(c.header));
  const lines = [columns.map(c => c.header).join(';')];
  for (const r of rows || []) {
    lines.push(columns.map(c => {
      const v = demo ? maskeerCel(c.get(r)) : c.get(r);
      if (c.text || isIdHeader(c.header) || isLongDigits(v)) return asText(v);
      if (typeof v === 'number') return num(v);
      return txt(v);
    }).join(';'));
  }
  // UTF-8 mét BOM en CRLF: zo herkent Excel de codering bij dubbelklikken.
  //
  // GEEN 'sep=;'-regel meer vooraan. Die dwong Excel om op ';' te splitsen
  // ongeacht de regionale lijstscheider, maar zodra die regel er staat NEGEERT
  // Excel de UTF-8-BOM en leest het bestand als ANSI (bevestigd door Microsoft:
  // learn.microsoft.com/answers/questions/4807400). Elk teken buiten ASCII kwam
  // daardoor verminkt binnen — "Loss — andere leverancier" werd "Loss â€" andere
  // leverancier", en hetzelfde gold voor é, ë en · in namen en redenen. Zonder
  // die regel splitst Excel op de lijstscheider van de regio, en die is ';' in
  // de Belgische en Nederlandse instellingen die onze gebruikers hebben.
  const csv = '﻿' + lines.join('\r\n');
  // `collect` meegeven → niet downloaden maar de tekst afgeven. Zo kan "Export All"
  // dezelfde kolomdefinities gebruiken en alles in ÉÉN zip stoppen.
  if (collect) return collect(filename, csv);
  return downloadTekst(filename, csv);
}

// Eén tekstbestand downloaden. Losse helper omdat elke export hem nodig heeft en
// de blob-URL pas ná de klik vrijgegeven mag worden.
function downloadTekst(filename, tekst) {
  const blob = new Blob([tekst], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(filename, blob);
}

// Handgebouwde CSV afleveren: downloaden, of — als "Export All" een verzamelaar
// meegeeft — enkel de tekst afgeven zodat alles samen in één zip kan.
const leverCsv = (filename, csv, collect) => (collect ? collect(filename, csv) : downloadTekst(filename, csv));

// Voorvoegsel van elke exportnaam: "YYYYMMDD_UUMM_" in Brusselse tijd. Vooraan,
// zodat een map met exports van verschillende momenten chronologisch sorteert.
// hourCycle 'h23' → 00–23 (zonder dat zou middernacht als 24 kunnen uitkomen).
const EXPORT_STAMP_FMT = new Intl.DateTimeFormat('nl-BE', {
  timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const exportTimestamp = () => {
  const d = {};
  for (const p of EXPORT_STAMP_FMT.formatToParts(new Date())) d[p.type] = p.value;
  return `${d.year}${d.month}${d.day}_${d.hour}${d.minute}_`;
};

function downloadBlob(filename, blob) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// Forceer Excel om een waarde als TEKST te behandelen (bv. een 18-cijferige GSRN
// niet afronden naar 15 cijfers + nullen): ="waarde" als CSV-gequote formule.
// Voor de handmatig opgebouwde CSV-exports (detail/self-bills).
const csvTextCell = (v) => { const s = (v == null ? '' : String(v)).replace(/[\r\n]/g, ' ').replace(/"/g, '""'); return s === '' ? '' : `"=""${s}"""`; };

// Pagination-hook voor tabellen. Reset naar pagina 1 zodra de items-referentie
// wisselt (typisch na filterwijziging). Clamp page als items kleiner worden.
function useTablePagination(items, pageSize = TABLE_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [items]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const start = (page - 1) * pageSize;
  const pageItems = (items || []).slice(start, start + pageSize);
  return { page, setPage, totalPages, total, pageItems, start, pageSize };
}

// Uniforme footer voor alle tabellen: telling links, optioneel een export-knop,
// rechts de paginatieknoppen. Gebruik `<TableFooter pagination={pag} onExport={...} />`
const TableFooter = ({ pagination, onExport }) => {
  const { page, setPage, totalPages, total, start, pageSize } = pagination;
  if (total === 0 && !onExport) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 border-t border-slate-700/50 text-xs text-slate-400">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
        {total > 0 && (
          <span>Toon <span className="text-slate-200">{start + 1}–{Math.min(start + pageSize, total)}</span> van <span className="text-slate-200">{total}</span></span>
        )}
        {onExport && <ExportKnop onExport={onExport} disabled={total === 0} count={total} label="Exporteer alle gefilterde records naar Excel" />}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1.5">
          <button disabled={page === 1} onClick={() => setPage(1)} title="Eerste pagina" className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">«</button>
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">‹ Vorige</button>
          <span className="px-2">Pagina <span className="text-slate-200">{page}</span> / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">Volgende ›</button>
          <button disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Laatste pagina" className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">»</button>
        </div>
      )}
    </div>
  );
};
// Segment-toggle. `flex-wrap` + `max-w-full`: een toggle met lange labels (bv.
// "Est. Offtake (MWh)") is breder dan een telefoonscherm en rekte voorheen de
// pagina op. Op mobiel iets compactere padding maar wél een 44px touch-target;
// vanaf sm exact de oude maatvoering (desktop ongewijzigd).
// Compacter op mobiel: een toggle met zes lange labels (Sales) wrapte daar naar
// vier regels en duwde de grafiek van het scherm. 36px blijft een werkbaar
// aanraakdoel; vanaf `sm` is alles ongewijzigd.
const Toggle = ({ options, value, onChange }) => <div className="flex flex-wrap max-w-full bg-slate-800 rounded-lg p-0.5 sm:p-1">{options.map(o => <button key={o.value} onClick={() => onChange(o.value)} className={`px-2 sm:px-4 py-1 sm:py-2 min-h-[36px] sm:min-h-0 text-xs sm:text-sm rounded-md transition-all ${value === o.value ? 'bg-lime-500 text-slate-900 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}>{o.label}</button>)}</div>;
// ── Breedte van filters ───────────────────────────────────────────────────────
// Filters stonden op elke pagina op een eigen breedte (gemeten op Orders: 100,
// 140, 160, 170 en 178 px naast elkaar; Verkoper was 160 op Invoices en 170 op
// Orders). Dat oogt rommelig zonder dat het iets oplevert. Er zijn nu nog TWEE
// maten, en de keuze hangt af van wat er IN het filter staat:
//
//   FILTER_SM  korte, opsombare waarden — Market (EL/NG), Segment, Status,
//              Meter Method, CP Status, Payment Status. Langste echte waarde
//              gemeten: "Wanbetaler" = 60 px tekst.
//   FILTER_MD  namen en langere waarden — energielabel, verkoopteam, verkoper,
//              ratecard, productgroep, workflow. Langste echte waarde gemeten:
//              "Prijspunten Energie België" = 138 px tekst; 184 px laat die met
//              padding en pijltje volledig zien.
//
// Een filter mag SMALLER zijn dan zijn langste waarde: het uitklappaneel groeit
// mee met zijn inhoud (zie DropdownPanel) en de knop draagt een title met de
// volledige waarde. Wat je kiest blijft dus altijd leesbaar.
const FILTER_SM = 'w-[132px]';
const FILTER_MD = 'w-[184px]';
// Zoeken is het enige veld dat breder mag: er staan e-mailadressen in. Het langste
// adres in de data is 36 tekens = 201 px; met icoon en padding past dat in 280 px.
// Het stond op `flex-1` en werd daardoor 656 px op een breed scherm — een zoekveld
// dat vier keer zo breed is als de rest trekt de balk scheef. De zoekvelden dragen
// `w-full sm:w-[280px] shrink-0`: op een telefoon volle breedte (anders kan de rij
// niet wrappen en loopt de pagina 271 px over — gemeten), vanaf sm de vaste maat.

const SearchableSelect = ({ label, value, onChange, options, placeholder = "Alle", size = 'md' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const knopRef = useRef(null);
  // Rol-afgedwongen scope: bij precies één toegestane waarde geen "Alle" — forceer die waarde.
  const single = options.length === 1;
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const displayValue = value === 'all' ? placeholder : value;
  useEffect(() => { if (single && value !== options[0]) onChange(options[0]); }, [single, options, value]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      // Het paneel staat in een portal op document.body en zit dus NIET in `ref`;
      // zonder die extra check sluit een klik erin het paneel meteen weer.
      if (e.target.closest && e.target.closest("[data-dropdown-panel]")) return;
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return <div className={`relative shrink-0 ${size === 'sm' ? FILTER_SM : FILTER_MD}`} ref={ref}>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <button ref={knopRef} title={displayValue} onClick={() => { setIsOpen(!isOpen); setSearch(''); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs text-left flex items-center justify-between ${value !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
      <span className="truncate">{displayValue}</span><ChevronDown size={12} className="shrink-0" />
    </button>
    {isOpen && <DropdownPanel anchorRef={knopRef}>
      <div className="p-1.5 border-b border-slate-600 bg-slate-800"><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoeken..." autoFocus className="w-full px-2 py-1 bg-slate-700 border border-slate-500 rounded text-xs text-slate-200 focus:outline-none focus:border-lime-500" /></div>
      <div className="max-h-44 overflow-y-auto bg-slate-800">
        {!single && <div onClick={() => { onChange('all'); setIsOpen(false); }} className={`px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-700 ${value === 'all' ? 'text-lime-400' : 'text-slate-300'}`}>{placeholder}</div>}
        {filtered.map(opt => <div key={opt} onClick={() => { onChange(opt); setIsOpen(false); }} className={`px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-700 whitespace-nowrap ${value === opt ? 'text-lime-400' : 'text-slate-200'}`}>{opt}</div>)}
        {filtered.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Geen resultaten</div>}
      </div>
    </DropdownPanel>}
  </div>;
};
// Scope-bewuste "Alle"-optie voor de cascade-filters (energielabel/team/verkoper).
// Verdwijnt zodra de rol-rechten maar één waarde toelaten: de gescopete optie-
// array (uit scopeOptions, al backend-gescoped) heeft dan precies één element, dus
// "Alle" is zinloos/misleidend. Bij 0 (laden/leeg) of >1 opties blijft "Alle" staan.
const AllOption = ({ options, children = 'Alle' }) =>
  (options && options.length === 1) ? null : <option value="all">{children}</option>;

// Mobiele filter-collapse: op smartphone staan de filters standaard ingeklapt achter
// een "Filters"-knop (minder eindeloos scrollen, kaarten meteen zichtbaar); op
// desktop (md+) staan ze gewoon inline zoals altijd. Puur presentationeel — dezelfde
// filter-velden en dezelfde state; geen wijziging aan filtering/logica/rechten.
const CollapsibleFilters = ({ active = false, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} className="md:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-slate-200 text-sm font-medium">
        <span className="flex items-center gap-2"><SlidersHorizontal size={15} />Filters{active && <span className="w-2 h-2 rounded-full bg-lime-400" title="Actieve filters" />}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`${open ? 'block mt-3' : 'hidden'} md:block md:mt-0`}>{children}</div>
    </>
  );
};

// Skeleton-placeholders: rustige, pulserende vlakken tijdens het laden i.p.v. een
// kale "Laden…"-tekst. Puur visueel — geen logica/data. `Skeleton` is de primitieve
// balk; `ListSkeleton` toont een aantal kaart-rijen (werkt op mobiel én desktop).
const Skeleton = ({ className = '' }) => <div className={`animate-pulse rounded bg-slate-700/60 ${className}`} />;
const ListSkeleton = ({ rows = 6 }) => (
  <div className="space-y-2" aria-busy="true" aria-label="Laden">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="rounded-xl border border-slate-700/50 bg-slate-800 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-5 w-16 shrink-0" />
        </div>
      </div>
    ))}
  </div>
);

// Favoriete filter-combinaties per pagina. De pagina levert een `filters`-snapshot
// (ZONDER de energielabel-filter — die is altijd gebruiker-afhankelijk) en een
// `onApply(filters)` die de filters terugzet. Eigen + gedeelde favorieten worden
// getoond; alleen een admin kan "delen met iedereen" (met waarschuwing). Andere
// rollen maken uitsluitend persoonlijke favorieten. Server: /api/filter-favorites.


// `teksten` laat een pagina die geen filters maar iets anders bewaart (de Revenue
// Calculator: een configuratie) haar eigen woorden gebruiken.
const FAVORIET_TEKSTEN = { titel: 'Favoriete filtercombinaties', opslaan: 'Huidige filters opslaan…', gedeeld: 'Het energielabel blijft per gebruiker bepaald.' };
const FilterFavorites = ({ pageKey, filters, onApply, teksten = FAVORIET_TEKSTEN }) => {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState('');
  const [shareAll, setShareAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const knopRef = useRef(null);

  const api = async (path, opts = {}) => {
    const r = await fetch(`/api/filter-favorites${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    return r.status === 204 ? null : r.json();
  };

  const load = async () => {
    try { const d = await api(`?page=${encodeURIComponent(pageKey)}`); setItems(d.favorites || []); setError(null); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pageKey, token]);

  useEffect(() => {
    if (!open) return undefined;
    // Het paneel rendert in een portal en zit dus buiten `ref`; zonder de
    // extra test sluit een klik IN het paneel het meteen weer.
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-dropdown-panel]")) { setOpen(false); setShowSave(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const save = async () => {
    const nm = name.trim();
    if (!nm) { setError('Geef een naam op'); return; }
    const shared = isAdmin && shareAll;
    if (shared && !window.confirm('Deze favoriet wordt zichtbaar én toepasbaar voor ÁLLE gebruikers van het systeem. Doorgaan?')) return;
    setSaving(true); setError(null);
    try {
      await api('', { method: 'POST', body: JSON.stringify({ page: pageKey, name: nm, filters: filters || {}, shared }) });
      setName(''); setShareAll(false); setShowSave(false);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Favoriet verwijderen?')) return;
    try { await api(`/${id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(e.message); }
  };

  // Een favoriet kan van vóór een rename dateren (ook een gedeelde van een ander),
  // dus vertalen we verouderde filterwaarden vóór het toepassen.
  const apply = (f) => { onApply(migrateSavedFilters(f.filters || {})); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      {/* Enkel het sterretje — geen label, zodat de knop tussen de filters past.
          Gevuld = er zijn favorieten, contour = nog geen. Het aantal staat in de
          tooltip i.p.v. naast het icoon. */}
      <button ref={knopRef} onClick={() => { setOpen(o => !o); setShowSave(false); }}
        title={items.length ? `${teksten.titel} (${items.length})` : teksten.titel}
        aria-label={teksten.titel}
        className="flex items-center justify-center w-8 h-8 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded text-slate-200">
        <Star size={15} className="text-amber-400" fill={items.length ? 'currentColor' : 'none'} />
      </button>
      {/* Paneel links uitgelijnd: de knop staat nu vooraan in de filterbalk, dus
          een rechts-verankerd paneel zou links buiten beeld vallen. */}
      {open && (
        <DropdownPanel anchorRef={knopRef} width={288} className="bg-slate-800 border border-slate-600 rounded-lg shadow-2xl p-2">
          <div className="max-h-56 overflow-y-auto">
            {items.length === 0 && <div className="px-2 py-3 text-xs text-slate-500 text-center">Nog geen favorieten</div>}
            {items.map(f => (
              <div key={f.id} className="group flex items-center gap-1 rounded hover:bg-slate-700/60">
                <button onClick={() => apply(f)} className="flex-1 min-w-0 text-left px-2 py-1.5 text-xs text-slate-200 flex items-center gap-1.5">
                  <span className="truncate">{f.name}</span>
                  {f.is_shared && <span title="Gedeeld met iedereen" className="shrink-0 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-indigo-500/20 text-indigo-300"><Users size={9} />Gedeeld</span>}
                </button>
                {(f.owned || isAdmin) && <button onClick={() => remove(f.id)} title="Verwijderen" className="shrink-0 p-1 mr-1 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100"><X size={12} /></button>}
              </div>
            ))}
          </div>
          <div className="border-t border-slate-700 mt-1 pt-2">
            {!showSave ? (
              <button onClick={() => { setShowSave(true); setName(''); setShareAll(false); setError(null); }}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-lime-400 hover:bg-slate-700/60 rounded">
                <Plus size={12} />{teksten.opslaan}
              </button>
            ) : (
              <div className="px-1 space-y-2">
                <input autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') save(); }}
                  placeholder="Naam van favoriet" className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200 focus:outline-none focus:border-lime-500" />
                {isAdmin && (
                  <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
                    <input type="checkbox" checked={shareAll} onChange={e => setShareAll(e.target.checked)} className="rounded border-slate-500 text-indigo-500 focus:ring-indigo-500 bg-slate-600 w-3.5 h-3.5" />
                    Delen met iedereen
                  </label>
                )}
                {isAdmin && shareAll && (
                  <div className="flex items-start gap-1.5 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5">
                    <AlertCircle size={13} className="shrink-0 mt-0.5" />
                    <span>Deze favoriet wordt zichtbaar én toepasbaar voor <strong>alle</strong> gebruikers. {teksten.gedeeld}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={save} disabled={saving || !name.trim()} className="flex-1 px-2 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-600 disabled:text-slate-400 text-slate-900 font-semibold rounded text-xs">{saving ? 'Opslaan…' : 'Opslaan'}</button>
                  <button onClick={() => { setShowSave(false); setShareAll(false); setError(null); }} className="px-2 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs">Annuleren</button>
                </div>
              </div>
            )}
            {error && <div className="mt-1 px-1 text-[11px] text-red-300">{error}</div>}
          </div>
        </DropdownPanel>
      )}
    </div>
  );
};

// ─── Uitklappaneel dat NOOIT achter iets anders verdwijnt ────────────────────
// Een `position:absolute`-paneel met een hoge z-index is niet genoeg. Dat z-index
// rangschikt alleen BINNEN de dichtstbijzijnde stapelcontext, en onze kaarten maken
// er zelf een: `Card` draagt `backdrop-blur-sm`, en backdrop-filter creëert een
// stapelcontext. Een paneel dat buiten die kaart uitsteekt, kan dus nooit boven een
// volgende kaart komen — hoe hoog z-[100] ook staat. Een `overflow:hidden` ergens in
// de keten kapt het bovendien af.
//
// Daarom rendert het paneel in een PORTAL op document.body, met `position:fixed` op
// de coördinaten van de knop. Buiten elke stapelcontext en elke overflow om.
// Gebruik dit voor ELK nieuw uitklappaneel (filter, combobox, actiemenu).
const DROPDOWN_Z = 9000;   // onder modals/toasts, boven alle pagina-inhoud
// Standaard-omhulsel. Panelen met eigen padding of hoogte geven een eigen
// className mee; de positionering (portal + fixed) blijft dan identiek.
const DROPDOWN_SHELL = "bg-slate-800 border border-slate-600 rounded shadow-2xl max-h-72 overflow-hidden";

// `width` is optioneel: zonder waarde neemt het paneel de breedte van de knop,
// gemeten op het moment van openen. Dat is betrouwbaarder dan de breedte tijdens
// het renderen uit een ref lezen — die is bij de eerste opening nog leeg of nul.
// Het paneel is standaard MINSTENS zo breed als de knop, maar mag breder worden
// dan zijn knop wanneer de inhoud dat vraagt. Dat is de tegenhanger van de twee
// vaste filterbreedtes: een filter mag smal zijn zolang je bij het openklappen
// nog steeds de volledige waarde leest. Zonder dit zou "Prijspunten Energie
// België" in een 132px-filter als "Prijspunten Ene…" blijven staan, óók in de
// lijst — en dan kun je niet kiezen wat je niet kunt lezen.
//
// De breedte wordt in twee passen bepaald: eerst positioneren op de knopbreedte,
// daarna de werkelijk gerenderde inhoud opmeten (scrollWidth) en het paneel zo
// nodig verbreden. Vooraf tekst opmeten met canvas zou de padding, het vinkje en
// de schuifbalk niet meetellen.
const DropdownPanel = ({ anchorRef, children, width, minWidth = 160, maxWidth = 420, className = DROPDOWN_SHELL }) => {
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);
  const [inhoudBreedte, setInhoudBreedte] = useState(0);
  React.useLayoutEffect(() => {
    const meet = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gewenst = width || Math.max(r.width, minWidth, inhoudBreedte);
      const breedte = Math.min(gewenst, maxWidth, window.innerWidth - 16);
      // Rechts uitlijnen als het paneel anders buiten beeld valt, en omhoog
      // klappen als er onderaan te weinig plaats is.
      const links = Math.max(8, Math.min(r.left, window.innerWidth - breedte - 8));
      const ruimteOnder = window.innerHeight - r.bottom;
      const omhoog = ruimteOnder < 240 && r.top > ruimteOnder;
      setPos({ left: links, top: omhoog ? undefined : r.bottom + 4, bottom: omhoog ? window.innerHeight - r.top + 4 : undefined, width: breedte });
    };
    meet();
    window.addEventListener('resize', meet);
    window.addEventListener('scroll', meet, true);   // ook bij scrollen in een container
    return () => { window.removeEventListener('resize', meet); window.removeEventListener('scroll', meet, true); };
  }, [anchorRef, width, minWidth, maxWidth, inhoudBreedte]);
  // Tweede pas: wat heeft de inhoud écht nodig? Alleen verbreden, nooit versmallen
  // — anders zou het paneel bij het typen in het zoekveld heen en weer springen.
  React.useLayoutEffect(() => {
    if (width || !panelRef.current) return;
    const nodig = panelRef.current.scrollWidth;
    if (nodig > (pos?.width || 0) + 1) setInhoudBreedte((b) => Math.max(b, nodig));
  });
  if (!pos) return null;
  return ReactDOM.createPortal(
    <div ref={panelRef} data-dropdown-panel style={{ position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, zIndex: DROPDOWN_Z }}
      className={className}>
      {children}
    </div>, document.body);
};

const SearchableMultiSelect = ({ label, values, onChange, options, placeholder = "Alle", size = 'md' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const knopRef = useRef(null);
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const displayValue = values.length === 0 ? placeholder : `${values.length} gesel.`;
  const toggle = (opt) => { onChange(values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt]); };

  React.useEffect(() => {
    // Het paneel staat in een portal en dus BUITEN `ref` — daarom sluit de
    // buitenklik-test ook op het paneel zelf (data-dropdown-panel).
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.('[data-dropdown-panel]')) setIsOpen(false);
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('keydown', handleEsc); };
  }, []);

  return <div className={`relative shrink-0 ${size === 'sm' ? FILTER_SM : FILTER_MD}`} ref={ref}>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <button ref={knopRef} title={values.length ? values.join(', ') : placeholder} onClick={() => { setIsOpen(!isOpen); setSearch(''); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs text-left flex items-center justify-between ${values.length > 0 ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
      <span className="truncate">{displayValue}</span><ChevronDown size={12} className="shrink-0" />
    </button>
    {isOpen && <DropdownPanel anchorRef={knopRef}>
      <div>
        <div className="p-1.5 border-b border-slate-600 bg-slate-800"><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoeken..." autoFocus className="w-full px-2 py-1 bg-slate-700 border border-slate-500 rounded text-xs text-slate-200 focus:outline-none focus:border-lime-500" /></div>
        <div className="p-1.5 border-b border-slate-600 flex justify-between bg-slate-800"><button onClick={() => onChange([])} className="text-xs text-slate-400 hover:text-lime-400">Alles wissen</button><button onClick={() => onChange([...options])} className="text-xs text-slate-400 hover:text-lime-400">Alles selecteren</button></div>
        <div className="max-h-48 overflow-y-auto bg-slate-800">
          {filtered.map(opt => <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-700 cursor-pointer"><input type="checkbox" checked={values.includes(opt)} onChange={() => toggle(opt)} className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-3 h-3" /><span className="text-xs text-slate-200 whitespace-nowrap">{opt}</span></label>)}
          {filtered.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Geen resultaten</div>}
        </div>
      </div>
    </DropdownPanel>}
  </div>;
};
const Card = ({ children, className = '', style, id }) => <div id={id} style={style} className={`app-card bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 ${className}`}>{children}</div>;

// ── Vangnet voor render-fouten ───────────────────────────────────────────────
// Zonder foutgrens haalt React bij ÉÉN uitzondering tijdens het renderen de hele
// boom weg: een wit scherm, op pc én telefoon, zonder enige melding. Dat gebeurde
// op Metering (een tooltip die bleef staan met een label van de vorige resolutie).
// Deze grens zit om de ACTIEVE PAGINA, zodat de zijbalk en de rest van de app
// blijven werken en de gebruiker kan herstellen zonder te herladen. `resetKey` =
// de actieve pagina: naar een andere module gaan wist de fout vanzelf.
class PaginaFoutgrens extends React.Component {
  constructor(props) { super(props); this.state = { fout: null }; }
  static getDerivedStateFromError(fout) { return { fout }; }
  componentDidCatch(fout, info) { console.error('Paginafout:', fout, info?.componentStack); }
  componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.fout) this.setState({ fout: null }); }
  render() {
    if (!this.state.fout) return this.props.children;
    const boodschap = String(this.state.fout?.message || this.state.fout || 'onbekende fout');
    return (
      <Card className="max-w-2xl mx-auto mt-10">
        <div className="flex items-start gap-3">
          <AlertCircle size={22} className="shrink-0 w-6 text-amber-400 mt-0.5" />
          <div className="min-w-0 space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-100">Er ging iets mis op deze pagina</h3>
              <p className="text-sm text-slate-400 mt-1">De pagina kon niet getekend worden. Je gegevens zijn niet verloren: probeer opnieuw of herlaad de pagina. Blijft het gebeuren, meld het dan via het lampje naast de paginatitel.</p>
            </div>
            <pre className="text-xs text-slate-500 bg-slate-900/60 border border-slate-700/50 rounded-lg px-3 py-2 overflow-x-auto whitespace-pre-wrap break-words">{boodschap}</pre>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => this.setState({ fout: null })} className="px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-xl text-sm">Opnieuw proberen</button>
              <button onClick={() => window.location.reload()} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl text-sm">Pagina herladen</button>
            </div>
          </div>
        </div>
      </Card>
    );
  }
}
// ── Documentatie bij een paginatitel ────────────────────────────────────────
// De brede knoppen "Functionele beschrijving" / "Technische beschrijving" stonden
// als aparte rij bóven de pagina en kostten altijd verticale ruimte, ook als
// niemand ze opende. Ze zijn vervangen door kleine icoonknopjes direct rechts van
// de paginatitel; de inhoud opent in een overlay. Gesloten kost dat 0px hoogte.
//
// De actieve module komt uit deze context (gezet in renderPage), zodat PageHeader
// de juiste documentatie zelf vindt en geen enkele pagina aangepast hoeft te
// worden. Pagina's met eigen documentatie (Revenue) geven `docs` mee aan PageHeader.
const PageDocsContext = React.createContext(null);

// Klein rond icoonknopje naast een titel. `tone='tech'` markeert de technische
// beschrijving (beheerders) visueel apart van de functionele.
const DocsIconButton = ({ icon: Icon, label, onClick, tone = 'neutral' }) => (
  <button
    type="button" onClick={onClick} title={label} aria-label={label}
    className={`inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-full border transition-colors ${tone === 'tech'
      ? 'bg-blue-500/10 border-blue-500/30 text-blue-300 hover:bg-blue-500/20 hover:text-blue-100'
      : tone === 'feedback'
      ? 'bg-lime-500/10 border-lime-500/30 text-lime-300 hover:bg-lime-500/20 hover:text-lime-100'
      : 'bg-slate-700/50 border-slate-600/60 text-slate-300 hover:bg-slate-600/60 hover:text-slate-100'}`}
  >
    <Icon size={13} />
  </button>
);

// Overlay met de documentatie. Bewust een overlay i.p.v. een uitklap onder de
// titel: de pagina verschuift niet en lange documentatie (Revenue) blijft goed
// leesbaar op volle breedte. Sluiten met Esc, de achtergrond of het kruisje.
const DocsModal = ({ open, onClose, title, subtitle, padded = true, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-3 sm:p-6">
      <button className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" aria-label="Sluiten" onClick={onClose} />
      <div className="relative w-full max-w-4xl my-2 flex flex-col max-h-[88vh] bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-5 py-3.5 border-b border-slate-700/70 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-100 uppercase tracking-wide">{title}</h3>
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="shrink-0 text-slate-400 hover:text-slate-100 transition-colors"><X size={18} /></button>
        </div>
        <div className={`overflow-y-auto ${padded ? 'px-5 py-4' : ''}`}>{children}</div>
      </div>
    </div>
  );
};

// De icoontjes voor modules met documentatie in moduleDocs.js. Rolregels zijn
// ongewijzigd: functioneel voor iedereen (merknaam gestript voor partners),
// technisch enkel voor admin.
const PageDocsIcons = () => {
  const ctx = React.useContext(PageDocsContext);
  const { user } = useAuth();
  const [openF, setOpenF] = useState(false);
  const [openT, setOpenT] = useState(false);
  const doc = ctx?.moduleId ? MODULE_DOCS[ctx.moduleId] : null;
  const hasF = !!(doc && ctx.showFunctional !== false && doc.functional);
  const hasT = !!(doc && user?.role === 'admin' && doc.technical);
  if (!hasF && !hasT) return null;
  const showBrand = seesEtBrand(user?.role);
  // Een module kan een eigen beschrijving voor partnerrollen hebben
  // (`functionalPartner`): de Helpdesk legt in de interne tekst sjablonen,
  // standaardteksten en het AI-voorstel uit, en dat is kennis die een partner niet
  // krijgt. Bestaat ze, dan leest elke rol die het merk niet mag zien díe tekst.
  const bron = hasF && (!showBrand && doc.functionalPartner ? doc.functionalPartner : doc.functional);
  const functional = hasF && (showBrand ? bron : {
    ...bron,
    summary: hideEtBrand(bron.summary),
    // `paragraphs` loopt óók door hideEtBrand — élke tekst die de merknaam kán
    // bevatten moet afgeschermd worden, ongeacht in welk veld ze staat.
    paragraphs: Array.isArray(bron.paragraphs) ? bron.paragraphs.map(hideEtBrand) : bron.paragraphs,
    points: Array.isArray(bron.points) ? bron.points.map(hideEtBrand) : bron.points,
    // `sections` (titel + punten, na de opsomming) volgt dezelfde regel.
    sections: Array.isArray(bron.sections)
      ? bron.sections.map(s => ({ ...s, title: hideEtBrand(s.title), intro: s.intro ? hideEtBrand(s.intro) : s.intro, points: (s.points || []).map(hideEtBrand) }))
      : bron.sections,
  });
  return <>
    {hasF && <DocsIconButton icon={BookOpen} label="Functionele beschrijving" onClick={() => setOpenF(true)} />}
    {hasT && <DocsIconButton icon={Wrench} tone="tech" label="Technische beschrijving" onClick={() => setOpenT(true)} />}
    {hasF && <DocsModal open={openF} onClose={() => setOpenF(false)} title="Functionele beschrijving"><ModuleDocsSection data={functional} /></DocsModal>}
    {hasT && <DocsModal open={openT} onClose={() => setOpenT(false)} title="Technische beschrijving" subtitle="Alleen zichtbaar voor beheerders"><ModuleDocsSection data={doc.technical} /></DocsModal>}
  </>;
};

// De hoofdtitel houdt exact `text-2xl font-bold text-slate-100` (de merk-gradient
// in het thema-blok hangt daaraan — zie CLAUDE.md).
// De actieknoppen/filters rechts stonden in een `flex` ZONDER flex-wrap: pagina's
// met meerdere selects (Sales, Revenue) maakten de header daardoor breder dan een
// telefoonscherm. Nu wrappen ze; `min-w-0` laat beide blokken krimpen i.p.v. de
// pagina op te rekken. Op desktop is er ruimte genoeg, dus daar verandert niets.
// De titelrij (titel + docs-iconen, en op sommige pagina's ook actieknoppen in de
// `docs`-slot) MOET kunnen afbreken. Zonder `flex-wrap` blijven titel en knoppen op
// één regel staan en duwen ze de pagina breder dan het scherm: op Revenue liep
// "Partnervergoeding" + Data ophalen + Vernieuwen op een telefoon 115px buiten beeld.
// De actieknoppen rechts in de titelrij mogen niet ONDER de vaste accountbalk
// rechtsboven vallen. Die balk is `position:fixed` en zweeft dus over de pagina;
// op Portfolio Insights lagen "6m / 12m / Alles" eronder, op Revenue "Export PDF
// Report" en "Export All", op Orders "Export Excel", enzovoort.
//
// De balk is niet even breed voor iedereen (naamlengte, rol, de impersonatie-pil,
// de preview-knop voor admin), dus meten we hem en zetten we de uitkomst in de
// CSS-variabele `--accountbar-w` (zie useAccountBarBreedte in de app-root). Hier
// reserveren we die breedte rechts van de HELE titelrij — ook links van de titel
// staan op sommige pagina's knoppen (Revenue: "Data ophalen"). Onder `md` is de
// variabele 0: daar staat de balk boven de mobiele topbalk, niet boven de titelrij.
const PageHeader = ({ title, subtitle, children, docs }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 mb-6" style={{ paddingRight: "var(--accountbar-w, 0px)" }}>
    <div className="min-w-0">
      {/* Ook deze binnenrij moet kunnen afbreken: op Revenue staat naast de titel
          nog "Data ophalen", en op een smal scherm schoof die anders voorbij de
          gereserveerde ruimte en tóch onder de accountbalk. */}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <h2 className="text-2xl font-bold text-slate-100">{title}</h2>
        {docs || <PageDocsIcons />}
        {/* Feedback/probleem melden — staat naast élke titel, met de pagina en de
            technische context al ingevuld (zie FeedbackKnop). */}
        <FeedbackKnop />
      </div>
      {subtitle && <p className="text-slate-400 text-sm mt-1">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 max-w-full">
      {children}
    </div>
  </div>
);
// Meet de vaste accountbalk rechtsboven en publiceert zijn breedte als de CSS-
// variabele `--accountbar-w`. PageHeader reserveert die ruimte rechts van zijn
// actieknoppen, zodat de balk nooit over een knop valt.
//
// Meten in plaats van een vast getal, want de balk groeit mee met de naam en de
// rol van de gebruiker, met de impersonatie-pil en met de preview-knop die alleen
// admin ziet. Een ResizeObserver vangt al die gevallen én een taalwissel op.
//
// Onder `md` is de waarde 0: daar zweeft de balk boven de mobiele topbalk met het
// logo, niet boven de titelrij, en zou reserveren alleen ruimte verspillen.
const useAccountBarBreedte = (ref) => {
  useEffect(() => {
    const el = ref.current;
    const zet = () => {
      const breed = window.matchMedia('(min-width: 768px)').matches;
      // 16px = de `right-4` van de balk, 12px = ademruimte tussen knop en balk.
      const px = breed && el ? Math.round(el.getBoundingClientRect().width) + 28 : 0;
      document.documentElement.style.setProperty('--accountbar-w', `${px}px`);
    };
    zet();
    const ro = el && 'ResizeObserver' in window ? new ResizeObserver(zet) : null;
    if (ro && el) ro.observe(el);
    window.addEventListener('resize', zet);
    return () => { window.removeEventListener('resize', zet); if (ro) ro.disconnect(); };
  }, [ref]);
};

const EmptyState = ({ message, onSettings }) => <div className="flex flex-col items-center justify-center py-16"><div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mb-4"><FileUp size={32} className="text-slate-500" /></div><p className="text-slate-400 mb-4">{message}</p>{onSettings && <button onClick={onSettings} className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-medium rounded-lg"><Settings size={18} />Instellingen</button>}{!onSettings && <p className="text-slate-500 text-sm">Neem contact op met een admin als dit zo blijft</p>}</div>;
// Laadstaat voor een pagina die nog op zijn eerste data wacht. Bewust ALLEEN een
// spinner + één regel: geen uitleg en geen Instellingen-knop, want er is (nog)
// niets mis — die knop suggereerde ten onrechte dat de gebruiker iets moest doen.
const PageLoading = ({ message = 'Gegevens worden geladen…' }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-4">
    <Spinner size={32} />
    <p className="text-slate-400 text-sm">{message}</p>
  </div>
);
// Gedeelde spinner — gebruikt de merk-accentkleur (var(--brand-primary), indigo
// als fallback voor admin/zonder branding) i.p.v. hardgecodeerd ET-groen. `size`
// in px. Vervangt de losse `border-lime-500`-spinners platformbreed.
const Spinner = ({ size = 24, className = '' }) => (
  <span role="status" aria-label="Laden"
    className={`inline-block box-border rounded-full animate-spin ${className}`}
    style={{ width: size, height: size, borderWidth: 2, borderStyle: 'solid', borderColor: 'var(--brand-primary, #6366F1)', borderTopColor: 'transparent' }} />
);
// Uniforme lege staat voor tabellen/secties: rustig icoon + één regel, i.p.v. een
// kaal grijs "Geen data". `icon` default = Search (past bij leeggefilterde lijsten;
// geef Folder mee voor "nog geen data"). Houdt tabellen visueel heel bij nul rijen.
// Workflow-weergave. De ET-API levert sinds 2026-07-30 zowel de flow ("Stop
// Contract") als de stap ("Stopcontract Step Leaving") in `workflow.current` /
// `workflow.last`. De stap zegt hetzelfde als de flow, maar preciezer — de flow er
// nog eens bij tonen is dus dubbele, grovere informatie. Daarom: ALTIJD de stap,
// en enkel als die (nog) leeg is valt de weergave terug op de flow.
// Eén bron voor tonen én filteren, zodat de filterlijst exact de getoonde
// varianten bevat.
const workflowLabel = (flow, step) => step || flow || '';
const WorkflowCell = ({ flow, step }) => {
  const label = workflowLabel(flow, step);
  if (!label) return <span className="text-slate-600">—</span>;
  return <div className="min-w-0 text-slate-300 truncate" title={label}>{label}</div>;
};

const TableEmpty = ({ message = 'Geen resultaten', icon: Icon = Search, className = '' }) => (
  <div className={`flex flex-col items-center justify-center gap-2 py-10 text-center ${className}`}>
    <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/60 flex items-center justify-center"><Icon size={18} className="text-slate-500" /></div>
    <p className="text-sm text-slate-500">{message}</p>
  </div>
);
// ── Toast-systeem ─────────────────────────────────────────────────────────────
// Module-level event-bus: elke code (ook helpers buiten React) kan toast.success/
// .error/.info(...) aanroepen; één <ToastHost/> in de app-root toont ze. Geen
// context-wrapping nodig — sluit aan op de window-event-patronen elders in de app.
let __toastSeq = 0;
const pushToast = (message, opts = {}) => {
  try {
    window.dispatchEvent(new CustomEvent('app-toast', { detail: { id: ++__toastSeq, message: String(message ?? ''), title: opts.title ? String(opts.title) : '', icon: opts.icon || null, type: opts.type || 'info', duration: opts.duration ?? 4500 } }));
  } catch { /* ignore */ }
};
const toast = {
  success: (m, o) => pushToast(m, { ...o, type: 'success' }),
  error: (m, o) => pushToast(m, { ...o, type: 'error' }),
  info: (m, o) => pushToast(m, { ...o, type: 'info' }),
  // Gamification: positieve sales-melding met titel + eigen icoon. `prestatie` =
  // niveau 2 (iets nadrukkelijker rand). Kleur = merk-accent (lime wordt per label omgezet).
  sale: (m, o) => pushToast(m, { duration: 5000, ...o, type: 'sale' }),
  prestatie: (m, o) => pushToast(m, { duration: 7000, ...o, type: 'prestatie' }),
};
const TOAST_STYLE = {
  success: { Icon: Check, border: 'border-emerald-500/40', chip: 'bg-emerald-500/15 border-emerald-500/30', dot: 'text-emerald-400' },
  error: { Icon: AlertCircle, border: 'border-rose-500/40', chip: 'bg-rose-500/15 border-rose-500/30', dot: 'text-rose-400' },
  info: { Icon: Info, border: 'border-indigo-500/40', chip: 'bg-indigo-500/15 border-indigo-500/30', dot: 'text-indigo-400' },
  sale: { Icon: TrendingUp, border: 'border-lime-500/30', chip: 'bg-lime-500/10 border-lime-500/30', dot: 'text-lime-400' },
  prestatie: { Icon: Award, border: 'border-lime-500/60', chip: 'bg-lime-500/15 border-lime-500/40', dot: 'text-lime-400' },
};
function ToastHost() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const onToast = (e) => {
      const t = e.detail; if (!t) return;
      setItems((list) => [...list, t]);
      if (t.duration > 0) setTimeout(() => setItems((list) => list.filter((x) => x.id !== t.id)), t.duration);
    };
    window.addEventListener('app-toast', onToast);
    return () => window.removeEventListener('app-toast', onToast);
  }, []);
  const dismiss = (id) => setItems((list) => list.filter((x) => x.id !== id));
  if (!items.length) return null;
  return (
    <div className="fixed z-[60] top-3 right-3 left-3 sm:left-auto sm:w-96 flex flex-col gap-2" style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}>
      {items.map((t) => {
        const s = TOAST_STYLE[t.type] || TOAST_STYLE.info; const Icon = t.icon || s.Icon;
        return (
          <div key={t.id} role="status" className={`flex items-center gap-3 rounded-2xl bg-slate-900/95 backdrop-blur-xl border ${s.border} shadow-2xl p-3`}>
            <div className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${s.chip}`}><Icon size={18} className={s.dot} /></div>
            <div className="min-w-0 flex-1 text-sm font-medium text-slate-100 break-words">{t.title ? <><div className="font-semibold">{t.title}</div>{t.message && <div className="text-xs font-normal text-slate-400 mt-0.5">{t.message}</div>}</> : t.message}</div>
            <button onClick={() => dismiss(t.id)} aria-label="Sluiten" className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X size={16} /></button>
          </div>
        );
      })}
    </div>
  );
}
// KPI-/kengetal-tegel — uniforme opbouw (accent-stip · label · kengetal · sub).
// Huisstijl: neutrale tegel-achtergrond; enkel het kengetal krijgt een accentkleur
// (`accent`), `dot` is de stip-kleur (matcht bij voorkeur de grafiekserie). Erft de
// kaart-hover via `app-card`. Vervangt de handgebouwde KPI-tegels platformbreed.
// `min(<minW>px, 100%)` i.p.v. een harde px-minimum: op een smal scherm krimpt de
// tegel mee i.p.v. de rij (en dus de pagina) breder te maken.
// Compacter op mobiel (kleinere padding, kleiner kengetal, strakkere subtekst) zodat
// een rij tegels daar niet het halve scherm inneemt. Vanaf `sm` ongewijzigd; `minW`
// zakt op mobiel naar 140px zodat er twee tegels naast elkaar passen.
// Kengetal — een groot cijfer dat ALTIJD volledig zichtbaar blijft.
//
// Een bedrag als "€ 154.429" in een tegel van 140 px werd met `truncate`
// "€ 154...." — precies het cijfer waar de tegel om draait, weggeknipt. Daarom:
// nooit afkappen, maar het lettertype verkleinen tot de tekst past. Zet dit
// OM de waarde heen in elke kengetal-tegel; de klasse met de lettergrootte
// (text-2xl e.d.) blijft op het omhullende element staan, want de verkleining
// is een percentage van de geërfde grootte. Zo werkt het ook samen met de
// `!important`-regels van het lichte thema (die zetten de grootte op de
// omhullende div, en dit element schaalt daar weer relatief op).
//
// Meten gebeurt op 100 % (anders zou een eerder verkleind cijfer nooit meer
// groeien), daarna wordt de schaal gezet. Een ResizeObserver op het element
// zelf vangt elke oorzaak van minder ruimte op: een smaller venster, in- of
// uitzoomen in de browser, een zijbalk die open- of dichtklapt, een tegel die
// door een filter een andere breedte krijgt. Een nieuwe waarde meet opnieuw.
// Het element is een blok met min-width 0, zodat het in een flex-rij ook echt
// mag krimpen — anders rekt de tekst zijn ouder op en is er niets te meten.
const KENGETAL_MIN_SCHAAL = 0.3;
const Kengetal = ({ children, className = '', style, title }) => {
  const ref = useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const meet = () => {
      el.style.fontSize = '';
      const beschikbaar = el.clientWidth;
      const nodig = el.scrollWidth;
      if (beschikbaar > 0 && nodig > beschikbaar) {
        const schaal = Math.max(KENGETAL_MIN_SCHAAL, beschikbaar / nodig);
        // Iets onder de exacte verhouding: subpixel-afronding zou anders net
        // één pixel te veel geven en de laatste cijfer alsnog laten wegvallen.
        el.style.fontSize = `${Math.floor(schaal * 98)}%`;
      }
    };
    meet();
    // Meteen in de callback meten, niet via requestAnimationFrame: in een
    // tabblad dat niet zichtbaar is (of een venster achter een ander venster)
    // draait rAF niet, en dan bleef een cijfer op zijn oude grootte staan tot
    // de gebruiker terugkwam. Een ResizeObserver-callback komt ná de layout;
    // de stijlwijziging hier levert hooguit één extra ronde op die niets meer
    // verandert.
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    // Webfonts laden na de eerste meting: dan wordt de tekst breder.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(meet).catch(() => {});
    return () => ro.disconnect();
  }, [children]);
  return (
    <span ref={ref} className={`kengetal block min-w-0 whitespace-nowrap ${className}`} style={style} title={title}>
      {children}
    </span>
  );
};

const StatTile = ({ label, value, accent, dot, sub, minW = 160, className = '' }) => (
  <div className={`stat-tile app-card flex-1 bg-slate-800/50 border border-slate-700/50 rounded-xl p-2.5 sm:p-4 ${className}`}
    style={{ '--stat-mw': `min(${minW}px, 100%)`, minWidth: 'var(--stat-mw)' }}>
    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-2">
      {dot && <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full shrink-0" style={{ backgroundColor: dot }} />}
      <span className="text-slate-400 text-xs sm:text-sm truncate">{label}</span>
    </div>
    <div className="text-lg sm:text-2xl font-bold text-slate-100" style={accent ? { color: accent } : undefined}><Kengetal>{value}</Kengetal></div>
    {sub != null && <div className="mt-1 sm:mt-2 space-y-0 sm:space-y-0.5 text-[10px] sm:text-xs text-slate-500 leading-tight">{sub}</div>}
  </div>
);
// Haal primaire/secundaire/accent-kleur uit een logo (data-URL) via canvas —
// geen dependency. We quantizen pixels (4 bits/kanaal), negeren transparante en
// (bijna) wit/zwarte pixels, en kiezen op frequentie + verzadiging. Resolved
// altijd (met lime-fallback) zodat de UI nooit blokkeert.
function extractLogoColors(dataUrl) {
  const FALLBACK = { primary: '#84cc16', secondary: '#65a30d', accent: '#a3e635' };
  const toHex = (r, g, b) => '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
  const hsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    const l = (max + min) / 2;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    let h = 0;
    if (d) {
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h = (h * 60 + 360) % 360;
    }
    return { h, s, l };
  };
  const hueDist = (a, b) => { const d = Math.abs(a - b); return Math.min(d, 360 - d); };
  return new Promise((resolve) => {
    if (!dataUrl) return resolve(FALLBACK);
    const img = new Image();
    img.onload = () => {
      try {
        const N = 64;
        const c = document.createElement('canvas');
        c.width = N; c.height = N;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, N, N);
        const { data } = ctx.getImageData(0, 0, N, N);
        const buckets = new Map();
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 128) continue; // transparant
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const { l } = hsl(r, g, b);
          if (l > 0.93 || l < 0.07) continue; // bijna wit/zwart
          const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
          let e = buckets.get(key);
          if (!e) { e = { r: 0, g: 0, b: 0, count: 0 }; buckets.set(key, e); }
          e.r += r; e.g += g; e.b += b; e.count++;
        }
        if (buckets.size === 0) return resolve(FALLBACK);
        const list = [...buckets.values()].map(e => {
          const r = e.r / e.count, g = e.g / e.count, b = e.b / e.count;
          return { r, g, b, count: e.count, ...hsl(r, g, b) };
        });
        const byFreq = [...list].sort((a, b) => b.count - a.count);
        const primary = byFreq[0];
        const secondary = byFreq.find(o => o !== primary && hueDist(o.h, primary.h) > 40) || byFreq[1] || primary;
        const accent = [...list].sort((a, b) => b.s - a.s)[0] || primary;
        resolve({
          primary: toHex(primary.r, primary.g, primary.b),
          secondary: toHex(secondary.r, secondary.g, secondary.b),
          accent: toHex(accent.r, accent.g, accent.b),
        });
      } catch { resolve(FALLBACK); }
    };
    img.onerror = () => resolve(FALLBACK);
    img.src = dataUrl;
  });
}

// ─── Kleur-contrast helpers (WCAG) ───────────────────────────────────────────
// Gebruikt voor brand-kleur-leesbaarheid: een per-energielabel gekozen kleur
// wordt zowel als knop-achtergrond (donkere/lichte tekst erop) als als
// tekstkleur (op wit licht-thema / donker admin-thema) gebruikt. Deze helpers
// kiezen een leesbare voorgrond en stellen een te licht/donker gekozen kleur
// automatisch bij tot een minimaal contrast.
function _hexToRgb(hex) {
  const m = (hex || '').trim().replace('#', '');
  const s = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
  if (s.length !== 6 || /[^0-9a-fA-F]/.test(s)) return null;
  const n = parseInt(s, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function _rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}
function _relLuminance({ r, g, b }) {
  const lin = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
// WCAG-contrastverhouding tussen twee hex-kleuren (1 = identiek, 21 = max).
function contrastRatio(hex1, hex2) {
  const a = _hexToRgb(hex1), b = _hexToRgb(hex2);
  if (!a || !b) return 1;
  const L1 = _relLuminance(a), L2 = _relLuminance(b);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}
// Meng twee hex-kleuren; t = 0 geeft `a`, t = 1 geeft `b`.
function mixHex(a, b, t) {
  const x = _hexToRgb(a), y = _hexToRgb(b);
  if (!x || !y) return a;
  return _rgbToHex(x.r + (y.r - x.r) * t, x.g + (y.g - x.g) * t, x.b + (y.b - x.b) * t);
}
// Oppervlakken afgeleid van een ingestelde achtergrondkleur. Zonder dit blijven
// kaarten, sidebar en filters op hun hardgecodeerde wit/slate staan en zweeft er
// een witte interface boven een gekleurde achtergrond.
//
// Eén regel voor beide thema's: een oppervlak ligt altijd een stap RICHTING WIT
// t.o.v. de pagina — dat klopt in licht (witte kaart op gebroken-witte pagina) én
// in donker (slate-800-kaart op slate-950-pagina). Alleen de stapgrootte verschilt,
// want op een donkere achtergrond is een kleine stap al zichtbaar en op een lichte
// niet. Vandaar de splitsing op luminantie.
function surfacesFromBackground(bg) {
  const rgb = _hexToRgb(bg);
  if (!rgb) return null;
  const licht = _relLuminance(rgb) > 0.4;
  const stap = (t) => mixHex(bg, '#ffffff', t);
  return {
    pagina: bg,
    kaart: licht ? stap(0.70) : stap(0.07),
    invoer: licht ? stap(0.30) : stap(0.13),
    subtiel: licht ? stap(0.50) : stap(0.04),
    rand: licht ? stap(0.12) : stap(0.20),
    hover: licht ? stap(0.45) : stap(0.11),
  };
}
// Kies zwart (#0f172a) of wit als best leesbare voorgrond op een achtergrondkleur.
function readableTextColor(hex) {
  return contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#0f172a') ? '#ffffff' : '#0f172a';
}
// Stel een kleur bij (verdonkeren op lichte achtergrond, verlichten op donkere)
// tot hij minstens `target` contrast haalt met `background`. Geeft de originele
// kleur terug als die al voldoet (of ongeldig is).
function ensureContrast(hex, background, target = 4.5) {
  const rgb = _hexToRgb(hex);
  const bg = _hexToRgb(background);
  if (!rgb || !bg) return hex;
  if (contrastRatio(hex, background) >= target) return hex;
  const darken = _relLuminance(bg) > 0.5; // lichte achtergrond → kleur verdonkeren
  let { r, g, b } = rgb;
  for (let i = 0; i < 30; i++) {
    if (darken) { r *= 0.9; g *= 0.9; b *= 0.9; }
    else { r += (255 - r) * 0.1; g += (255 - g) * 0.1; b += (255 - b) * 0.1; }
    const cur = _rgbToHex(r, g, b);
    if (contrastRatio(cur, background) >= target) return cur;
  }
  return _rgbToHex(r, g, b);
}
function _rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (d) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  return { h, s, l };
}
function _hslToHex(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return _rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}
// Donkere varianten van dezelfde kleur: behoudt tint + verzadiging, verlaagt de
// helderheid in stappen → een reeks "donkerder, dezelfde kleur"-alternatieven.
// Bv. #fef3bc → progressief donkerdere gelen. Ontdubbeld; ongeldige invoer = [].
function darkerVariants(hex, factors = [0.72, 0.45, 0.3]) {
  const rgb = _hexToRgb(hex);
  if (!rgb) return [];
  const { h, s, l } = _rgbToHsl(rgb);
  const seen = new Set([hex.toLowerCase()]);
  const out = [];
  for (const f of factors) {
    const v = _hslToHex(h, s, Math.max(0, l * f));
    if (!seen.has(v.toLowerCase())) { seen.add(v.toLowerCase()); out.push(v); }
  }
  return out;
}

// === Admin-instellingen, opgesplitst in twee pagina's ======================
// 1) BrandingSettingsPage → de huisstijl per energielabel én per verkoopteam.
// 2) GeneralSettingsPage → label-onafhankelijke, algemene instellingen
//    (externe API-keys, customer-tag-zichtbaarheid, lokale partner-branding).
// Beide zijn admin-only (gerouteerd via setActivePage, niet langer een modal).

// --- Branding: per energielabel en per verkoopteam --------------------------
// Merknaam "Energy Together" is enkel zichtbaar voor admin + ET Dossierbeheerder.
// Voor partners/verkopers wordt ze niet getoond: inline teksten kiezen een
// neutrale variant, doc-teksten worden geneutraliseerd naar "Support".
// ── Customer Tags ────────────────────────────────────────────────────────────
// De klant-tags uit Odoo/ET komen op de order mee als één komma-gescheiden string
// ("BE- SOC TARIEF, MAIL"). De configuratie (Revenue → Admin → Customer Tags) staat
// per UNIEKE tag, dus overal waar we tags tonen of filteren werken we met de losse
// tags. Van een order met drie tags kunnen er twee zichtbaar zijn en één niet.
const splitCustomerTags = (s) => String(s || '').split(',').map(t => t.trim()).filter(Boolean);
// De tags van één order die deze gebruiker mag zien, in de volgorde van de order.
// `zichtbaar` is null zolang de config nog niet geladen is → dan tonen we niets,
// want een tag per ongeluk tonen is erger dan hem even niet tonen.
const zichtbareTags = (partnerTags, zichtbaar) => (zichtbaar ? splitCustomerTags(partnerTags).filter(t => zichtbaar.has(t)) : []);
// Voor tabelcellen: de zichtbare tags als tekst, of '' als er niets te tonen valt.
const zichtbareTagsTekst = (partnerTags, zichtbaar) => zichtbareTags(partnerTags, zichtbaar).join(', ');

const seesEtBrand = (role) => role === 'admin' || role === 'et_dossier';
// Vaste "Energy Together"-merkbranding (energy_label-sleutel). Standaard-huisstijl
// voor admin + ET Dossierbeheerder — de enige rollen die het ET-merk mogen zien.
const ET_BRAND_LABEL = 'Energy Together';
// Letterstijl-opties voor branding (CSS font-family stacks). Bewust system/web-safe
// stacks zodat er geen externe fonts geladen hoeven te worden. '' = platform-default.
const BRAND_FONT_OPTIONS = [
  { value: '', label: 'Standaard (platform)' },
  { value: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif", label: 'Inter / System' },
  { value: "'Segoe UI', system-ui, Roboto, sans-serif", label: 'Segoe UI' },
  { value: "Georgia, 'Times New Roman', serif", label: 'Georgia (serif)' },
  { value: "'Trebuchet MS', 'Segoe UI', sans-serif", label: 'Trebuchet' },
  { value: "'Courier New', ui-monospace, monospace", label: 'Courier (mono)' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial / Helvetica' },
];
// Vervang de merknaam + directe/indirecte varianten door een neutrale term. Vangt
// "Energy Together (B.V.)", "EnergyTogether", "ET API" en "ET(-)Dossierbeheerder".
const hideEtBrand = (text) => (typeof text === 'string'
  // Prospect: ook het bronlabel (Power2You) verdwijnt uit elke tekst (src/prospect.js).
  ? verbergBronLabel(text
      .replace(/Energy\s*Together(?:\s+B\.?\s*V\.?)?/gi, 'Support')
      .replace(/\bET[-\s]?Dossierbeheerder/gi, 'Support-beheerder')
      .replace(/\bET API\b/gi, 'de API'))
  : text);

// --- Opportunity-stage weergave met hover-tooltip --------------------------
// Toont de stage-waarde; staat er een (admin-configureerbare) beschrijving bij,
// dan verschijnt die als tooltip wanneer de muis erover gaat. De lookup gebeurt
// via StageDescContext (genormaliseerd op de basis-stage, suffix-onafhankelijk).
function oppStageBadgeClass(v) {
  // Zelfde precedentie als voorheen: contracted → switch → ready → neutraal.
  const s = (v || '').toLowerCase();
  if (s.includes('contracted')) return 'bg-lime-900/50 text-lime-300';
  if (s.includes('switch')) return 'bg-blue-900/50 text-blue-300';
  if (s.includes('ready')) return 'bg-lime-900/50 text-lime-300';
  return 'bg-slate-700 text-slate-300';
}
function OppStageText({ value }) {
  const stageDesc = useStageDesc();
  if (!value) return <>—</>;
  const d = stageDesc(value);
  return <span title={d || undefined} className={d ? 'cursor-help underline decoration-dotted decoration-slate-500 underline-offset-2' : undefined}>{value}</span>;
}
function OppStageBadge({ value }) {
  const stageDesc = useStageDesc();
  const d = stageDesc(value);
  return <span title={d || undefined} className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${oppStageBadgeClass(value)}${d ? ' cursor-help' : ''}`}>{value}</span>;
}

// Cohortmatrix: startmaand vanaf wanneer een energielabel meetelt. Alles vóór die
// maand valt uit de matrix — bedoeld voor labels waarvan de oudere historiek
// overgenomen data is die de retentiecijfers vertekent. Alleen deze grafiek; de
// overige Portfolio-cijfers blijven op de volledige historiek rekenen.
const COHORT_MIN_MAAND_PER_LABEL = { 'Evident Energie': '2025-01' };
// 'YYYY-MM' → 'januari 2025' (nl-BE, conform de datumconventie van het platform).
const formatMaand = (ym) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return String(ym || '');
  return new Intl.DateTimeFormat('nl-BE', { month: 'long', year: 'numeric' })
    .format(new Date(Number(m[1]), Number(m[2]) - 1, 1));
};

// ── Branding per thema ───────────────────────────────────────────────────────
// De basisvelden (logo_data_url, primary_color, …) zijn de LICHTE variant; de
// *_dark-velden zijn een optionele override voor het donkere thema. Is er maar
// één ingevuld, dan gebruikt het andere thema die ook — in beide richtingen, dus
// wie enkel een donker logo instelt krijgt dat ook in het lichte thema.
// Gebruik deze functie op ELKE plek die branding toont; lees nooit rechtstreeks
// `branding.primary_color`, want dan mist het donkere thema zijn override.
const THEMED_BRAND_FIELDS = ['logo_data_url', 'favicon_data_url', 'primary_color', 'secondary_color', 'accent_color'];
// Achtergrond zoals het portaal die zonder branding toont.
const DEFAULT_BACKGROUND = { light: '#f8fafc', dark: '#020617' };
// Maximale lengte van een logo-/favicon-data-URL. Moet gelijk blijven aan
// MAX_LOGO_BYTES in backend/src/routes/branding.js — de frontend meet hier de
// data-URL (niet het bronbestand), zodat de melding klopt met wat de server doet.
const MAX_BRAND_DATA_URL = 200 * 1024;
const brandingForTheme = (branding, theme) => {
  if (!branding) return branding;
  const uit = { ...branding };
  for (const veld of THEMED_BRAND_FIELDS) {
    const licht = branding[veld] || '';
    const donker = branding[`${veld}_dark`] || '';
    uit[veld] = theme === 'dark' ? (donker || licht) : (licht || donker);
  }
  // De ACHTERGROND doet bewust NIET mee aan die kruislingse terugval. Een donkere
  // achtergrond die overwaait naar het lichte thema levert een donkere pagina met
  // lichte tekst op — precies wat je niet wil. Niets ingesteld voor dit thema =
  // de standaardachtergrond van het portaal, niet die van het andere thema.
  uit.background_color = (theme === 'dark' ? branding.background_color_dark : branding.background_color) || '';
  return uit;
};

// Branding-instellingen (admin). Twee niveaus in één scherm, met exact hetzelfde
// formulier: de huisstijl van een ENERGIELABEL en die van een VERKOOPTEAM binnen
// zo'n label. Wat een team leeg laat, blijft dat van het label — de samenvoeging
// zelf staat in src/brandingMerge.js (en gespiegeld in de backend, die ze voor
// teamleader/verkoper al toepast op /api/branding).
const BrandingSettingsPage = ({ token, allBranding, allTeamBranding = [], availableLabels, prospectTeams = [], prospectLabels = [], salesStructure = EMPTY_STRUCTURE, onBrandingChanged }) => {
  const labelLogoRef = useRef(null);
  const labelFaviconRef = useRef(null);
  // 'label' = branding per energielabel, 'team' = per verkoopteam.
  const [brandScope, setBrandScope] = useState('label');
  const isTeamScope = brandScope === 'team';

  // Logo, favicon, de drie merkkleuren én de achtergrond bestaan in twee
  // varianten. De basisvelden zijn de LICHTE, de *_dark de donkere; leeg laten
  // betekent "volg de andere" (zie brandingForTheme). De donkere kleuren starten
  // daarom leeg — een voorgevulde waarde zou de terugval onmogelijk maken.
  const blankForm = {
    energy_label: '', sales_team: '', display_name: '', mail_from: '', font_family: '',
    mail_support_email: '', mail_support_phone: '', mail_website: '', mail_footer: '', mail_iban: '',
    logo_data_url: '', favicon_data_url: '',
    primary_color: '#84cc16', secondary_color: '#65a30d', accent_color: '#a3e635',
    background_color: '',
    logo_data_url_dark: '', favicon_data_url_dark: '',
    primary_color_dark: '', secondary_color_dark: '', accent_color_dark: '',
    background_color_dark: '',
  };
  const [labelForm, setLabelForm] = useState(blankForm);
  const [labelStatus, setLabelStatus] = useState({ saving: false, error: '', success: '' });
  const [extracting, setExtracting] = useState(false);
  // Welke variant je op dit moment bewerkt. Puur een weergaveschakelaar: de
  // waarden van het andere thema blijven gewoon in het formulier staan.
  const [brandTheme, setBrandTheme] = useState('light');
  const isDarkBrand = brandTheme === 'dark';
  const veld = (basis) => (isDarkBrand ? `${basis}_dark` : basis);
  const zetVeld = (basis, waarde) => setLabelForm(p => ({ ...p, [veld(basis)]: waarde }));
  const leesVeld = (basis) => labelForm[veld(basis)] || '';
  // De kleur die dit thema uiteindelijk toont, inclusief terugval op het andere.
  const effectieveKleur = (basis) => leesVeld(basis) || labelForm[isDarkBrand ? basis : `${basis}_dark`] || '';
  // Wat een verkoopteam ERFT van zijn energielabel. Zonder dit zou het kleurvakje
  // bij een leeg veld het portaalgroen tonen — een kleur die nergens vandaan komt
  // en die dit team ook niet krijgt.
  const geerfdeKleur = (basis) => {
    if (!isTeamScope) return '';
    const label = (allBranding || []).find(b => b.energy_label === labelForm.energy_label);
    return brandingForTheme(label, brandTheme)?.[basis] || '';
  };
  const BRAND_SURFACE = { light: '#ffffff', dark: '#1e293b' };

  // Teams van het gekozen energielabel — dezelfde centrale cascade als elke
  // filterbalk, dus nooit een team dat in de data niet bestaat.
  // Prospect-resellers (rol prospect, smaak reseller) staan als "verkoopteam"
  // onder hun bronlabel: zo krijgen ze hier hun huisstijl zoals elk team.
  const teamOpties = useMemo(() => {
    if (!labelForm.energy_label) return [];
    const basis = scopeOptions(salesStructure, labelForm.energy_label, 'all').teams || [];
    const extra = (prospectTeams || []).filter(p => p.energy_label === labelForm.energy_label).map(p => p.sales_team).filter(t => t && !basis.includes(t));
    return extra.length ? [...basis, ...extra] : basis;
  }, [salesStructure, labelForm.energy_label, prospectTeams]);
  const teamBrandingVoor = (label, team) => (allTeamBranding || []).find(b => b.energy_label === label && b.sales_team === team);
  // Bewerken kan pas als de sleutel volledig is: een label, en in team-modus ook
  // een verkoopteam.
  const scopeGekozen = isTeamScope ? !!(labelForm.energy_label && labelForm.sales_team) : !!labelForm.energy_label;

  // Vul het formulier met een bestaande rij (of leeg). `bron` is de branding van
  // het gekozen niveau; in team-modus tonen we NIET de labelwaarden als voorzet —
  // leeg betekent daar bewust "volg het energielabel".
  const vulForm = ({ label, team, bron }) => {
    const existing = bron;
    // Een nog niet gebrand ENERGIELABEL start op het portaalgroen; een nog niet
    // gebrand VERKOOPTEAM start leeg, want daar betekent leeg "neem de kleuren
    // van het energielabel over" — een voorgevulde kleur zou die stilzwijgend
    // vervangen zodra je opslaat.
    const kleurDefault = (waarde, portaal) => existing?.[waarde] || (team ? '' : portaal);
    setLabelForm({
      energy_label: label,
      sales_team: team || '',
      display_name: existing?.display_name || '',
      logo_data_url: existing?.logo_data_url || '',
      primary_color: kleurDefault('primary_color', '#84cc16'),
      secondary_color: kleurDefault('secondary_color', '#65a30d'),
      accent_color: kleurDefault('accent_color', '#a3e635'),
      favicon_data_url: existing?.favicon_data_url || '',
      background_color: existing?.background_color || '',
      logo_data_url_dark: existing?.logo_data_url_dark || '',
      favicon_data_url_dark: existing?.favicon_data_url_dark || '',
      primary_color_dark: existing?.primary_color_dark || '',
      secondary_color_dark: existing?.secondary_color_dark || '',
      accent_color_dark: existing?.accent_color_dark || '',
      background_color_dark: existing?.background_color_dark || '',
      mail_from: existing?.mail_from || '',
      font_family: existing?.font_family || '',
      mail_support_email: existing?.mail_support_email || '',
      mail_support_phone: existing?.mail_support_phone || '',
      mail_website: existing?.mail_website || '',
      mail_footer: existing?.mail_footer || '',
      mail_iban: existing?.mail_iban || '',
    });
    setBrandTheme('light');
    setLabelStatus({ saving: false, error: '', success: '' });
  };

  // De server begrenst de DATA-URL op MAX_BRAND_DATA_URL, niet het bronbestand.
  // Base64 maakt een bestand ~1,37× groter, dus op bestandsgrootte controleren
  // liet bestanden door die de server daarna alsnog weigerde. Meet daarom de
  // data-URL zelf, met exact dezelfde grens als de backend.
  const teGroot = (dataUrl) => dataUrl.length > MAX_BRAND_DATA_URL;

  const handleLabelLogo = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = async ev => {
      const dataUrl = ev.target.result;
      if (teGroot(dataUrl)) {
        setLabelStatus({ saving: false, error: `Logo te groot: ${Math.round(dataUrl.length / 1024)} KB na codering, maximaal ${Math.round(MAX_BRAND_DATA_URL / 1024)} KB. Verklein de afbeelding (of sla 'm op als geoptimaliseerde PNG/SVG).`, success: '' });
        return;
      }
      zetVeld('logo_data_url', dataUrl);
      // Kleuren automatisch uit het logo afleiden (blijven bewerkbaar) — voor de
      // variant die je nu bewerkt, zodat een donker logo geen lichte kleuren
      // overschrijft.
      setExtracting(true);
      try {
        const c = await extractLogoColors(dataUrl);
        setLabelForm(p => ({
          ...p,
          [veld('primary_color')]: c.primary,
          [veld('secondary_color')]: c.secondary,
          [veld('accent_color')]: c.accent,
        }));
      } finally { setExtracting(false); }
    };
    r.readAsDataURL(f);
  };

  // Favicon-upload (los van het logo). Zelfde grens als logo.
  const handleFavicon = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = ev => {
      const dataUrl = ev.target.result;
      if (teGroot(dataUrl)) {
        setLabelStatus({ saving: false, error: `Favicon te groot: ${Math.round(dataUrl.length / 1024)} KB na codering, maximaal ${Math.round(MAX_BRAND_DATA_URL / 1024)} KB. Een favicon hoort klein te zijn (16–64 px).`, success: '' });
        return;
      }
      zetVeld('favicon_data_url', dataUrl);
    };
    r.readAsDataURL(f);
  };

  // Eén endpoint-pad voor beide niveaus. Het team-pad heeft twee segmenten, dus
  // de backend-route /:label vangt het niet af.
  const brandingUrl = () => (isTeamScope
    ? `/api/branding/teams/${encodeURIComponent(labelForm.energy_label)}/${encodeURIComponent(labelForm.sales_team)}`
    : `/api/branding/${encodeURIComponent(labelForm.energy_label)}`);
  const scopeNaam = () => (isTeamScope ? `${labelForm.sales_team} (${labelForm.energy_label})` : labelForm.energy_label);

  const saveLabelBranding = async () => {
    if (!scopeGekozen) { setLabelStatus({ saving: false, error: isTeamScope ? 'Kies eerst een energielabel én een verkoopteam' : 'Kies eerst een energielabel', success: '' }); return; }
    setLabelStatus({ saving: true, error: '', success: '' });
    try {
      const r = await fetch(brandingUrl(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          display_name: labelForm.display_name || null,
          logo_data_url: labelForm.logo_data_url || null,
          primary_color: labelForm.primary_color || null,
          secondary_color: labelForm.secondary_color || null,
          accent_color: labelForm.accent_color || null,
          favicon_data_url: labelForm.favicon_data_url || null,
          background_color: labelForm.background_color || null,
          logo_data_url_dark: labelForm.logo_data_url_dark || null,
          favicon_data_url_dark: labelForm.favicon_data_url_dark || null,
          primary_color_dark: labelForm.primary_color_dark || null,
          secondary_color_dark: labelForm.secondary_color_dark || null,
          accent_color_dark: labelForm.accent_color_dark || null,
          background_color_dark: labelForm.background_color_dark || null,
          // Het verzendadres blijft een zaak van het energielabel: één
          // geverifieerd Resend-domein per label, niet per team.
          ...(isTeamScope ? {} : { mail_from: labelForm.mail_from || null }),
          font_family: labelForm.font_family || null,
          mail_support_email: labelForm.mail_support_email || null,
          mail_support_phone: labelForm.mail_support_phone || null,
          mail_website: labelForm.mail_website || null,
          mail_footer: labelForm.mail_footer || null,
          mail_iban: labelForm.mail_iban || null,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setLabelStatus({ saving: false, error: '', success: 'Opgeslagen' });
      onBrandingChanged?.();
    } catch (err) { setLabelStatus({ saving: false, error: err.message, success: '' }); }
  };

  const resetLabelBranding = async () => {
    if (!scopeGekozen) return;
    if (!window.confirm(`Branding voor "${scopeNaam()}" wissen?${isTeamScope ? ' Het team valt dan terug op de branding van het energielabel.' : ''}`)) return;
    setLabelStatus({ saving: true, error: '', success: '' });
    try {
      const r = await fetch(brandingUrl(), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setLabelForm(p => ({ ...blankForm, energy_label: p.energy_label, sales_team: p.sales_team, ...(isTeamScope ? { primary_color: '', secondary_color: '', accent_color: '' } : {}) }));
      setLabelStatus({ saving: false, error: '', success: 'Branding gewist' });
      onBrandingChanged?.();
    } catch (err) { setLabelStatus({ saving: false, error: err.message, success: '' }); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Branding" subtitle="Huisstijl per energielabel en per verkoopteam. De wanbetaler-instellingen staan op Revenue → Admin." />
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h4 className="text-sm font-semibold text-lime-400 uppercase">Branding per {isTeamScope ? 'Verkoopteam' : 'Energielabel'}</h4>
          <Toggle
            options={[{ value: 'label', label: 'Energielabels' }, { value: 'team', label: 'Verkoopteams' }]}
            value={brandScope}
            onChange={(v) => { setBrandScope(v); setLabelForm(blankForm); setLabelStatus({ saving: false, error: '', success: '' }); }}
          />
        </div>
        {isTeamScope
          ? <p className="text-xs text-slate-400 mb-4">Teamleaders en verkopers van dit verkoopteam zien deze huisstijl zodra ze inloggen — ook in hun exports (o.a. het partnervergoeding-rapport). <strong>Alles wat je hier leeg laat, blijft de branding van het energielabel.</strong></p>
          : <p className="text-xs text-slate-400 mb-4">Master/teamleader/reseller-accounts gekoppeld aan een energielabel zien automatisch het hier ingestelde logo en de primary-kleur. <strong>Energy Together</strong> (bovenaan) is de standaard-huisstijl voor admin en ET Dossierbeheerder.</p>}
        <div className="space-y-4 max-w-2xl">
          <div>
            <label className="block text-sm text-slate-300 mb-2">Energielabel</label>
            <select value={labelForm.energy_label}
              onChange={e => vulForm({ label: e.target.value, team: '', bron: isTeamScope ? null : (allBranding || []).find(b => b.energy_label === e.target.value) })}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-slate-100 focus:border-lime-500 focus:outline-none">
              <option value="">— kies een energielabel —</option>
              {[ET_BRAND_LABEL, ...(availableLabels || []).filter(l => l !== ET_BRAND_LABEL)].map(l => <option key={l} value={l}>{l}{(prospectLabels || []).includes(l) ? ' · prospect' : ''}{!isTeamScope && (allBranding || []).some(b => b.energy_label === l) ? ' (ingesteld)' : ''}</option>)}
            </select>
          </div>

          {isTeamScope && labelForm.energy_label && (
            <div>
              <label className="block text-sm text-slate-300 mb-2">Verkoopteam</label>
              <select value={labelForm.sales_team}
                onChange={e => vulForm({ label: labelForm.energy_label, team: e.target.value, bron: teamBrandingVoor(labelForm.energy_label, e.target.value) })}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-slate-100 focus:border-lime-500 focus:outline-none">
                <option value="">— kies een verkoopteam —</option>
                {teamOpties.map(t => <option key={t} value={t}>{t}{(prospectTeams || []).some(p => p.energy_label === labelForm.energy_label && p.sales_team === t) ? ' · prospect' : ''}{teamBrandingVoor(labelForm.energy_label, t) ? ' (ingesteld)' : ''}</option>)}
              </select>
              {teamOpties.length === 0 && <p className="text-xs text-amber-400 mt-1.5">Voor dit energielabel zijn (nog) geen verkoopteams bekend in de sales-structuur.</p>}
            </div>
          )}

          {scopeGekozen && <>
            <div>
              <label className="block text-sm text-slate-300 mb-2">Display-naam (sidebar / login-titel)</label>
              <input type="text" value={labelForm.display_name} onChange={e => setLabelForm(p => ({ ...p, display_name: e.target.value }))} placeholder={isTeamScope ? `leeg = ${labelForm.energy_label}` : labelForm.energy_label} className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-slate-100 placeholder-slate-500 focus:border-lime-500 focus:outline-none" />
            </div>

            {/* Het verzendadres hoort bij het energielabel: er is één geverifieerd
                Resend-domein per label. Een team erft dat gewoon mee. */}
            {!isTeamScope && <div>
              <label className="block text-sm text-slate-300 mb-2">Afzender e-mail (verzendadres voor dit label)</label>
              <input type="email" value={labelForm.mail_from} onChange={e => setLabelForm(p => ({ ...p, mail_from: e.target.value }))} placeholder="bv. relations@partners.evidentenergie.be" className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-slate-100 placeholder-slate-500 focus:border-lime-500 focus:outline-none font-mono text-sm" />
              <p className="text-xs text-slate-500 mt-1.5">Uitgaande mail voor klanten van dit energielabel vertrekt vanaf dit adres. <strong>Leeg laten</strong> = de algemene afzender (main). Werkt pas zodra het verzenddomein in de mailprovider (Resend) is geverifieerd met de juiste DNS-records; tot dan valt dit label automatisch terug op de main.</p>
            </div>}

            {/* Contactblok onderaan elke e-mail (welkomstmail, wachtwoord-reset,
                helpdesk-mails): support-mail, telefoon, website en een vrije
                voettekst. Bij een team: leeg = volg het energielabel. */}
            <div className="rounded-xl border border-slate-700/60 p-4 space-y-3">
              <label className="block text-sm text-slate-300 flex items-center gap-1.5">
                Contact in e-mails
                <InfoTip title="Contact in e-mails" text={'Deze gegevens staan onderaan elke e-mail die het portaal namens dit merk verstuurt, onder de ondertekening. Ze zijn ook als variabelen beschikbaar in mailsjablonen: {{supportEmail}}, {{supportPhone}}, {{website}} en {{iban}} (het rekeningnummer, voor standaardteksten over voorschotten, waarborgen en herinneringen).' + (isTeamScope ? '\n\nLeeg laten bij een team = de gegevens van het energielabel gebruiken.' : '')} />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input type="email" value={labelForm.mail_support_email} onChange={e => setLabelForm(p => ({ ...p, mail_support_email: e.target.value }))} placeholder="support@merk.be" className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100" />
                <input value={labelForm.mail_support_phone} onChange={e => setLabelForm(p => ({ ...p, mail_support_phone: e.target.value }))} placeholder="+32 3 000 00 00" className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100" />
                <input value={labelForm.mail_website} onChange={e => setLabelForm(p => ({ ...p, mail_website: e.target.value }))} placeholder="https://www.merk.be" className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100" />
              </div>
              {/* Rekeningnummer van het merk: {{iban}} in standaardteksten (voorschot,
                  waarborg, herinnering). Wordt bij het opslaan genormaliseerd. */}
              <label className="block sm:w-1/2"><span className="text-xs text-slate-400">Rekeningnummer (IBAN) — variabele {'{{iban}}'}</span>
                <input value={labelForm.mail_iban} onChange={e => setLabelForm(p => ({ ...p, mail_iban: e.target.value }))} placeholder={isTeamScope ? 'Leeg = rekeningnummer van het energielabel' : 'BE68 5390 0754 7034'} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 font-mono" /></label>
              <textarea value={labelForm.mail_footer} onChange={e => setLabelForm(p => ({ ...p, mail_footer: e.target.value }))} rows={2} placeholder="Voettekst (optioneel): bv. maatschappelijke zetel, BTW-nummer, privacyverwijzing" className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100" />
            </div>

            {/* Vanaf hier is alles thema-afhankelijk: logo, favicon, de drie
                merkkleuren en de achtergrond. De schakelaar wisselt WELKE variant
                je bewerkt; de waarden van het andere thema blijven staan. */}
            <div className="rounded-xl border border-slate-700/60 p-4 space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="block text-sm text-slate-300 flex items-center gap-1.5">
                  Weergave per thema
                  <InfoTip title="Weergave per thema" text={'Logo, favicon, merkkleuren en achtergrond kun je apart instellen voor het lichte en het donkere thema.\n\nVul je er maar één in, dan gebruikt het andere thema die ook — in beide richtingen. Je hoeft dus niets te dupliceren zolang één set volstaat; zet alleen een variant waar het licht/donker-verschil echt uitmaakt (bv. een donker logo dat wegvalt op een donkere achtergrond).\n\nDe velden hieronder tonen telkens de variant die je met deze schakelaar kiest.'} />
                </label>
                <Toggle options={[{ value: 'light', label: 'Licht thema' }, { value: 'dark', label: 'Donker thema' }]} value={brandTheme} onChange={setBrandTheme} />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2">Logo <span className="text-xs text-slate-500">— {isDarkBrand ? 'donker thema' : 'licht thema'}</span></label>
                <div className="border-2 border-dashed border-slate-600 rounded-xl p-6 text-center cursor-pointer hover:border-lime-500/50"
                  style={{ backgroundColor: isDarkBrand ? '#1e293b' : '#ffffff' }}
                  onClick={() => labelLogoRef.current?.click()}>
                  {leesVeld('logo_data_url')
                    ? <div className="space-y-3"><img src={leesVeld('logo_data_url')} alt="Logo" className="max-h-16 mx-auto" /><button onClick={e => { e.stopPropagation(); zetVeld('logo_data_url', ''); }} className="px-3 py-1 text-xs bg-red-900/50 text-red-300 rounded-lg hover:bg-red-900">Logo wissen</button></div>
                    : <div><Upload className="mx-auto text-slate-500" size={28} /><p className="text-sm text-slate-400 mt-2">Upload logo — max {Math.round(MAX_BRAND_DATA_URL / 1024)} KB na codering (~{Math.round(MAX_BRAND_DATA_URL / 1024 * 0.75)} KB bestand)</p>
                        {labelForm[isDarkBrand ? 'logo_data_url' : 'logo_data_url_dark'] && <p className="text-xs text-slate-500 mt-1">Leeg = gebruikt het logo van het {isDarkBrand ? 'lichte' : 'donkere'} thema</p>}
                      </div>}
                </div>
                <input ref={labelLogoRef} type="file" accept=".png,.jpg,.jpeg,.svg" onChange={handleLabelLogo} className="hidden" />
              </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Favicon (browsertab-icoon) <span className="text-xs text-slate-500">— {isDarkBrand ? 'donker thema' : 'licht thema'}</span></label>
              <div className="border-2 border-dashed border-slate-600 rounded-xl p-4 text-center cursor-pointer hover:border-lime-500/50"
                style={{ backgroundColor: isDarkBrand ? '#1e293b' : '#ffffff' }}
                onClick={() => labelFaviconRef.current?.click()}>
                {leesVeld('favicon_data_url')
                  ? <div className="flex items-center justify-center gap-3"><img src={leesVeld('favicon_data_url')} alt="Favicon" className="h-8 w-8 object-contain" /><button onClick={e => { e.stopPropagation(); zetVeld('favicon_data_url', ''); }} className="px-3 py-1 text-xs bg-red-900/50 text-red-300 rounded-lg hover:bg-red-900">Favicon wissen</button></div>
                  : <div><Upload className="mx-auto text-slate-500" size={22} /><p className="text-xs text-slate-400 mt-1">Upload favicon (.png/.ico, ~16–64px)</p>
                      {labelForm[isDarkBrand ? 'favicon_data_url' : 'favicon_data_url_dark'] && <p className="text-xs text-slate-500 mt-1">Leeg = gebruikt de favicon van het {isDarkBrand ? 'lichte' : 'donkere'} thema</p>}
                    </div>}
              </div>
              <input ref={labelFaviconRef} type="file" accept=".png,.ico,.jpg,.jpeg,.svg" onChange={handleFavicon} className="hidden" />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Achtergrondkleur <span className="text-xs text-slate-500">— {isDarkBrand ? 'donker thema' : 'licht thema'}</span></label>
              <div className="flex gap-2 items-center">
                <input type="color" value={leesVeld('background_color') || DEFAULT_BACKGROUND[brandTheme]}
                  onChange={e => zetVeld('background_color', e.target.value)}
                  className="h-10 w-12 rounded cursor-pointer bg-slate-700 border border-slate-600" />
                <input type="text" value={leesVeld('background_color')} placeholder={DEFAULT_BACKGROUND[brandTheme]}
                  onChange={e => zetVeld('background_color', e.target.value)}
                  className="flex-1 min-w-0 px-2 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-100 text-xs font-mono placeholder-slate-500" />
                {leesVeld('background_color') && <button type="button" onClick={() => zetVeld('background_color', '')}
                  className="px-3 py-2 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg whitespace-nowrap">Standaard</button>}
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                Leeg = de standaardachtergrond van het portaal ({DEFAULT_BACKGROUND[brandTheme]} met verloop). Een eigen kleur maakt de achtergrond egaal.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm text-slate-300">
                  Kleuren <span className="text-xs text-slate-500">— {isDarkBrand ? 'donker thema' : 'licht thema'}</span>
                  {leesVeld('logo_data_url') && <span className="text-xs text-slate-500"> · auto uit logo, bewerkbaar</span>}
                </label>
                {extracting && <span className="text-xs text-lime-400 flex items-center gap-1"><div className="w-3 h-3 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />kleuren afleiden…</span>}
              </div>
              {isDarkBrand && <p className="text-xs text-slate-500 mb-2">Leeg laten = deze kleur volgt het lichte thema.</p>}
              {isTeamScope && <p className="text-xs text-slate-500 mb-2">Alle drie leeg laten = dit team gebruikt de kleuren van het energielabel. Upload je een teamlogo, dan worden ze automatisch afgeleid.</p>}
              <div className="grid grid-cols-3 gap-3">
                {[['primary_color', 'Primary'], ['secondary_color', 'Secundair'], ['accent_color', 'Accent']].map(([basis, lbl]) => {
                  const key = veld(basis);
                  // Contrast tegen het oppervlak van HET GEKOZEN thema: wit in
                  // licht, de kaartkleur in donker. Een merkkleur die op wit
                  // voldoet kan op donker alsnog wegvallen, en omgekeerd.
                  const surface = BRAND_SURFACE[brandTheme];
                  const kleur = effectieveKleur(basis);
                  const heeftKleur = !!kleur;
                  const ratio = heeftKleur ? contrastRatio(kleur, surface) : 0;
                  const ok = ratio >= 4.5;
                  const warn = ratio >= 3 && ratio < 4.5;
                  const variants = !heeftKleur || ok ? [] : darkerVariants(kleur);
                  return <div key={key}>
                  <label className="block text-xs text-slate-400 mb-1.5">{lbl}</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={kleur || geerfdeKleur(basis) || '#84cc16'} onChange={e => setLabelForm(p => ({ ...p, [key]: e.target.value }))} className="h-10 w-12 rounded cursor-pointer bg-slate-700 border border-slate-600" />
                    <input type="text" value={labelForm[key] || ''} placeholder={isDarkBrand ? (labelForm[basis] || (isTeamScope ? 'volgt label' : 'volgt licht')) : (isTeamScope ? 'volgt label' : '')} onChange={e => setLabelForm(p => ({ ...p, [key]: e.target.value }))} className="flex-1 min-w-0 px-2 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-100 text-xs font-mono placeholder-slate-500" />
                  </div>
                  <div className="mt-1.5 text-[11px]">
                    {heeftKleur && <span className={ok ? 'text-lime-400' : warn ? 'text-amber-400' : 'text-red-400'} title={`Contrast van deze kleur als tekst op de achtergrond van het ${isDarkBrand ? 'donkere' : 'lichte'} thema (WCAG AA = 4,5:1).`}>contrast op {isDarkBrand ? 'donker' : 'wit'} {ratio.toFixed(1)}:1</span>}
                    {heeftKleur && !ok && variants.length > 0 && <div className="flex items-center flex-wrap gap-1.5 mt-1">
                      <span className="text-slate-400">Donkerder:</span>
                      {variants.map(v => {
                        const vr = contrastRatio(v, surface);
                        return <button key={v} type="button" onClick={() => setLabelForm(p => ({ ...p, [key]: v }))}
                          title={`${v} — contrast ${vr.toFixed(1)}:1${vr >= 4.5 ? ' (voldoet)' : ''} · klik om toe te passen`}
                          className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded border border-slate-600 hover:border-lime-500 bg-slate-800 transition-colors">
                          <span className="inline-block w-3.5 h-3.5 rounded-sm border border-slate-500" style={{ backgroundColor: v }} />
                          <span className={`font-mono ${vr >= 4.5 ? 'text-lime-400' : 'text-slate-400'}`}>{vr.toFixed(1)}</span>
                        </button>;
                      })}
                    </div>}
                  </div>
                </div>; })}
              </div>
            </div>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Letterstijl</label>
              <select value={labelForm.font_family} onChange={e => setLabelForm(p => ({ ...p, font_family: e.target.value }))} className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-slate-100 focus:border-lime-500 focus:outline-none">
                {BRAND_FONT_OPTIONS.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}
              </select>
              <p className="mt-2 px-3 py-2 bg-slate-900/40 border border-slate-700/50 rounded-lg text-slate-200" style={{ fontFamily: labelForm.font_family || undefined }}>Voorbeeld: The quick brown fox · 0123456789</p>
            </div>

            {labelStatus.error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{labelStatus.error}</div>}
            {labelStatus.success && <div className="bg-lime-500/10 border border-lime-500/30 text-lime-300 px-3 py-2 rounded text-sm">{labelStatus.success}</div>}

            <div className="flex gap-2 justify-end">
              <button onClick={resetLabelBranding} disabled={labelStatus.saving} className="px-4 py-2.5 bg-red-900/40 hover:bg-red-900/60 text-red-300 rounded-xl text-sm disabled:opacity-50">Wissen</button>
              <button onClick={saveLabelBranding} disabled={labelStatus.saving} className="px-6 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-xl text-sm disabled:opacity-50">{labelStatus.saving ? 'Bezig…' : 'Opslaan'}</button>
            </div>
          </>}
        </div>
      </Card>

    </div>
  );
};

// --- Mailsjablonen (admin) -------------------------------------------------
// Eén sleutel (actie-id) mag meerdere sjablonen hebben: globaal, per energielabel,
// per verkoopteam en per taal — de server kiest bij het verzenden het meest
// specifieke (team > label > globaal). De variabelen komen uit de catalogus van de
// server (/api/mail-templates/vars): één bron voor de knoppen, de validatie, de
// preview en de testmail. De preview is de mail zoals ze aankomt: gerenderd met
// voorbeeldwaarden en merk-gewrapt (logo, kleuren, contactblok) van het gekozen
// label/team, in een sandboxed iframe zodat de mail-CSS het portaal niet raakt.
const MT_TALEN = [{ id: 'nl', label: 'NL' }, { id: 'fr', label: 'FR' }, { id: 'en', label: 'EN' }];
const MT_SNIPPET_MODULE = 'helpdesk-bericht';
const MT_INTERN_MODULE = 'helpdesk-intern';
const mtSleutel = (k) => String(k || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
// Labelgrens van een sjabloon: één label (energy_label, met eventueel een team)
// of een lijst (energy_labels, sinds 15-09-2026); niets = alle energielabels.
const mtLabels = (t) => (t.energy_label ? [t.energy_label] : Array.isArray(t.energy_labels) ? t.energy_labels : []);
const mtScope = (t) => { const l = mtLabels(t); return t.sales_team ? `${t.energy_label} · ${t.sales_team}` : l.length ? l.join(' · ') : 'Alle labels'; };
const mtDatum = (d) => { if (!d) return ''; try { return new Date(d).toLocaleDateString('nl-BE'); } catch { return ''; } };
const mtBytes = (n) => (n >= 1024 * 1024 ? `${(n / 1048576).toLocaleString('nl-BE', { maximumFractionDigits: 1 })} MB` : `${Math.round(n / 1024)} kB`);
const mtLeeg = () => ({ key: '', name: '', subject: '', html_body: '', from_address: '', from_name: '', reply_to: '', modules: [], energy_label: '', energy_labels: [], sales_team: '', language: 'nl', active: true, restricted: false, category: '', attachments: [], isNew: true });

// ─── Opmaakbalk van de tekst-editors (08-10-2026) ─────────────────────────
// Eén balk voor elke contentEditable-editor in het platform: het antwoordblok
// en Nieuw ticket in de Helpdesk, de kennisbank en de mailsjablonen. Dezelfde
// opties als bij het verwerken van een ticket in het bronsysteem, aangevuld:
// blokstijl (Normaal/Kop/Citaat/Code), vet/cursief/onderstreept/doorgehaald,
// opmaak verwijderen, tekst- en markeerkleur, lettertype en -grootte, lijsten,
// in-/uitspringen, uitlijning, link (en link weg), horizontale lijn, ongedaan
// maken/opnieuw. De knoppen tonen de toestand op de cursor (vet staat "aan").
//
// Werkt op document.execCommand (in elke browser aanwezig, geen bibliotheek).
// Twee valkuilen die hier opgelost zijn:
//   • Een knop mag de selectie in de editor niet afpakken: onMouseDown wordt
//     onderdrukt. Een keuzelijst (<select>) kán dat niet — daarom bewaart de
//     balk bij elke selectiewijziging de range en zet ze die vóór elke opdracht
//     terug (`herstelSelectie`).
//   • Lettergrootte/-type geeft de browser als <font size="7"|face>; dat wordt
//     meteen een <span style="…"> met de gekozen px/css (src/opmaak.js).
// `editorRef` wijst naar het contentEditable; `onChange` krijgt de nieuwe
// innerHTML na elke bewerking; `rechts` is een optionele tekst rechts in de balk.
const OPMAAK_KNOP = 'p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-700/60';
const OPMAAK_KNOP_AAN = 'p-1.5 rounded bg-lime-500/20 text-lime-300';
const OPMAAK_KEUZE = 'h-7 rounded bg-slate-800/60 border border-slate-600/50 text-[11px] text-slate-300 px-1 focus:outline-none focus:border-lime-500/60';
// Een knop neemt de selectie in de editor niet over (geen focus op mousedown).
const opmaakStil = (e) => e.preventDefault();
const OpmaakKnop = ({ aan, titel, onClick, children, extra = '' }) => (
  <button type="button" onMouseDown={opmaakStil} onClick={onClick} title={titel} className={`${aan ? OPMAAK_KNOP_AAN : OPMAAK_KNOP} ${extra}`}>{children}</button>
);
const OpmaakScheiding = () => <span className="w-px h-4 bg-slate-600/60 mx-1" />;
const OpmaakBalk = ({ editorRef, onChange, rechts = null }) => {
  const [staat, setStaat] = useState({});
  const [paneel, setPaneel] = useState(null);   // 'kleur' | 'markeer' | null
  const kleurRef = useRef(null);
  const markeerRef = useRef(null);
  const balkRef = useRef(null);
  const bewaard = useRef(null);                 // laatste selectie IN de editor
  const selectieBinnen = useCallback(() => {
    const el = editorRef.current; const sel = window.getSelection();
    return !!(el && sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer));
  }, [editorRef]);
  // De toestand op de cursor, voor de "aan"-weergave van de knoppen.
  const peil = useCallback(() => {
    if (!selectieBinnen()) { setStaat((s) => (Object.keys(s).length ? {} : s)); return; }
    const sel = window.getSelection();
    bewaard.current = sel.getRangeAt(0).cloneRange();
    const q = (c) => { try { return !!document.queryCommandState(c); } catch { return false; } };
    const v = (c) => { try { return document.queryCommandValue(c); } catch { return ''; } };
    const node = sel.getRangeAt(0).startContainer;
    const ouder = node.nodeType === 3 ? node.parentElement : node;
    setStaat({
      bold: q('bold'), italic: q('italic'), underline: q('underline'), strike: q('strikeThrough'),
      ul: q('insertUnorderedList'), ol: q('insertOrderedList'),
      links: q('justifyLeft'), midden: q('justifyCenter'), rechts: q('justifyRight'),
      blok: blokVanWaarde(v('formatBlock')), grootte: grootteVan(node, editorRef.current), lettertype: lettertypeVan(node, editorRef.current),
      link: !!(ouder && ouder.closest && ouder.closest('a') && editorRef.current.contains(ouder.closest('a'))),
    });
  }, [editorRef, selectieBinnen]);
  useEffect(() => {
    document.addEventListener('selectionchange', peil);
    return () => document.removeEventListener('selectionchange', peil);
  }, [peil]);
  // Buitenklik en Escape sluiten het kleurpaneel (dat in een portal staat).
  useEffect(() => {
    if (!paneel) return undefined;
    const klik = (e) => { if (balkRef.current && balkRef.current.contains(e.target)) return; if (e.target.closest && e.target.closest('[data-dropdown-panel]')) return; setPaneel(null); };
    const toets = (e) => { if (e.key === 'Escape') setPaneel(null); };
    document.addEventListener('mousedown', klik); document.addEventListener('keydown', toets);
    return () => { document.removeEventListener('mousedown', klik); document.removeEventListener('keydown', toets); };
  }, [paneel]);
  // Focus terug in de editor, met de selectie van daarnet (na een keuzelijst).
  const herstelSelectie = () => {
    const el = editorRef.current; if (!el) return null;
    el.focus();
    if (!selectieBinnen() && bewaard.current && el.contains(bewaard.current.commonAncestorContainer)) {
      const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(bewaard.current);
    }
    return el;
  };
  const meld = () => { if (editorRef.current) onChange(editorRef.current.innerHTML); peil(); };
  // css=true: de browser schrijft <span style="…"> (kleur, uitlijning);
  // css=false: <font>/<b>-tags, die daarna omgezet worden waar nodig.
  const cmd = (c, arg = null, css = true) => {
    if (!herstelSelectie()) return;
    try { document.execCommand('styleWithCSS', false, css); } catch { /* oude browser */ }
    document.execCommand(c, false, arg);
    meld();
  };
  const zetGrootte = (px) => { if (!herstelSelectie()) return; document.execCommand('styleWithCSS', false, false); document.execCommand('fontSize', false, '7'); fontNaarSpan(editorRef.current, { px: px || null }); meld(); };
  const zetLettertype = (naam) => {
    if (!herstelSelectie()) return;
    const t = OPMAAK_LETTERTYPES.find((x) => x.naam === naam) || null;
    document.execCommand('styleWithCSS', false, false); document.execCommand('fontName', false, t ? t.naam : 'standaard');
    fontNaarSpan(editorRef.current, { lettertype: t ? t.css : null }); meld();
  };
  const zetKleur = (soort, kleur) => { setPaneel(null); cmd(soort === 'kleur' ? 'foreColor' : 'hiliteColor', kleur || (soort === 'kleur' ? 'inherit' : 'transparent')); };
  // Opmaak verwijderen: op de selectie, of zonder selectie op de hele tekst —
  // de knop bestaat om alles weer in hetzelfde lettertype te zetten.
  const opmaakWeg = () => {
    const el = herstelSelectie(); if (!el) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed) { document.execCommand('removeFormat'); fontNaarSpan(el); }
    else verwijderOpmaak(el);
    meld();
  };
  const link = () => {
    const el = herstelSelectie(); if (!el) return;
    const sel = window.getSelection();
    const url = window.prompt('Adres van de link (https://…)'); if (!url) return;
    if (sel && sel.rangeCount > 0 && sel.getRangeAt(0).collapsed) document.execCommand('insertHTML', false, `<a href="${hdEscape(url)}">${hdEscape(url)}</a>&nbsp;`);
    else document.execCommand('createLink', false, url);
    meld();
  };
  const Knop = OpmaakKnop;
  const Scheiding = OpmaakScheiding;
  const palet = (soort, kleuren) => (
    <DropdownPanel anchorRef={soort === 'kleur' ? kleurRef : markeerRef} minWidth={200} maxWidth={240} className={`${DROPDOWN_SHELL} p-2`}>
      <div className="flex flex-wrap gap-1.5">
        {kleuren.map((k) => <button key={k} type="button" onMouseDown={opmaakStil} onClick={() => zetKleur(soort, k)} title={k} className="w-6 h-6 rounded border border-slate-500/60 hover:scale-110 transition-transform" style={{ background: k }} />)}
      </div>
      <button type="button" onMouseDown={opmaakStil} onClick={() => zetKleur(soort, null)} className="mt-2 w-full text-left text-[11px] text-slate-300 hover:text-slate-100 px-1 py-0.5 rounded hover:bg-slate-700/60">Standaard (geen kleur)</button>
    </DropdownPanel>
  );
  return (
    <div ref={balkRef} className="flex items-center gap-0.5 px-1.5 py-1 border-b border-slate-600/40 flex-wrap">
      <select value={staat.blok || 'p'} onChange={(e) => cmd('formatBlock', `<${e.target.value}>`, false)} title="Blokstijl" className={OPMAAK_KEUZE}>
        {OPMAAK_BLOKKEN.map((b) => <option key={b.tag} value={b.tag}>{b.label}</option>)}
      </select>
      <select value={staat.lettertype || ''} onChange={(e) => zetLettertype(e.target.value)} title="Lettertype" className={`${OPMAAK_KEUZE} max-w-[96px]`}>
        <option value="">Lettertype</option>
        {OPMAAK_LETTERTYPES.map((t) => <option key={t.naam} value={t.naam}>{t.naam}</option>)}
      </select>
      <select value={staat.grootte || ''} onChange={(e) => zetGrootte(Number(e.target.value) || null)} title="Lettergrootte" className={OPMAAK_KEUZE}>
        <option value="">Grootte</option>
        {OPMAAK_GROOTTES.map((g) => <option key={g} value={g}>{g}</option>)}
      </select>
      <Scheiding />
      <Knop aan={staat.bold} titel="Vet (Ctrl+B)" onClick={() => cmd('bold')}><Bold size={14} /></Knop>
      <Knop aan={staat.italic} titel="Cursief (Ctrl+I)" onClick={() => cmd('italic')}><Italic size={14} /></Knop>
      <Knop aan={staat.underline} titel="Onderstreept (Ctrl+U)" onClick={() => cmd('underline')}><Underline size={14} /></Knop>
      <Knop aan={staat.strike} titel="Doorgehaald" onClick={() => cmd('strikeThrough')}><Strikethrough size={14} /></Knop>
      <Knop titel="Opmaak verwijderen — van de selectie, of zonder selectie van de hele tekst (alles in hetzelfde lettertype)" onClick={opmaakWeg}><Eraser size={14} /></Knop>
      <Scheiding />
      <span ref={kleurRef}><Knop aan={paneel === 'kleur'} titel="Tekstkleur" onClick={() => setPaneel((p) => (p === 'kleur' ? null : 'kleur'))}><Baseline size={14} /></Knop></span>
      <span ref={markeerRef}><Knop aan={paneel === 'markeer'} titel="Markeerkleur" onClick={() => setPaneel((p) => (p === 'markeer' ? null : 'markeer'))}><Highlighter size={14} /></Knop></span>
      {paneel === 'kleur' && palet('kleur', OPMAAK_KLEUREN)}
      {paneel === 'markeer' && palet('markeer', OPMAAK_MARKERINGEN)}
      <Scheiding />
      <Knop aan={staat.ul} titel="Opsomming" onClick={() => cmd('insertUnorderedList', null, false)}><List size={14} /></Knop>
      <Knop aan={staat.ol} titel="Genummerde lijst" onClick={() => cmd('insertOrderedList', null, false)}><ListOrdered size={14} /></Knop>
      <Knop titel="Inspringen" onClick={() => cmd('indent', null, false)}><Indent size={14} /></Knop>
      <Knop titel="Uitspringen" onClick={() => cmd('outdent', null, false)}><Outdent size={14} /></Knop>
      <Scheiding />
      <Knop aan={staat.links && !staat.midden && !staat.rechts} titel="Links uitlijnen" onClick={() => cmd('justifyLeft')}><AlignLeft size={14} /></Knop>
      <Knop aan={staat.midden} titel="Centreren" onClick={() => cmd('justifyCenter')}><AlignCenter size={14} /></Knop>
      <Knop aan={staat.rechts} titel="Rechts uitlijnen" onClick={() => cmd('justifyRight')}><AlignRight size={14} /></Knop>
      <Scheiding />
      <Knop aan={staat.link} titel="Link" onClick={link}><Link2 size={14} /></Knop>
      {staat.link && <Knop titel="Link verwijderen" onClick={() => cmd('unlink')}><Link2Off size={14} /></Knop>}
      <Knop titel="Horizontale lijn" onClick={() => cmd('insertHorizontalRule', null, false)}><Minus size={14} /></Knop>
      <Scheiding />
      <Knop titel="Ongedaan maken (Ctrl+Z)" onClick={() => cmd('undo')}><Undo2 size={14} /></Knop>
      <Knop titel="Opnieuw (Ctrl+Y)" onClick={() => cmd('redo')}><Redo2 size={14} /></Knop>
      {rechts && <span className="ml-auto text-[11px] text-slate-500 pr-1 flex items-center gap-1">{rechts}</span>}
    </div>
  );
};

// ─── Visuele sjablooneditor (Mailsjablonen, 11-09-2026) ───────────────────
// De sjabloontekst bewerken zoals ze in de mail verschijnt — zonder HTML te
// kennen. De HTML wordt eruit afgeleid. Variabelen ({{klantnaam}}, {{#mfa}} …)
// staan als GEMARKEERDE VELDEN in de tekst: één blokje, niet los te bewerken
// (contenteditable=false), met de omschrijving als tooltip. Backspace haalt het
// hele veld weg. Bij het opslaan worden de blokjes weer gewone {{…}}-tekst.
//
// Werkwijze: html_body (met {{…}}) → chips → contentEditable; elke wijziging
// → innerHTML → chips terug naar {{…}} → html_body. De tekst wordt alleen
// vanuit `html` gezet als `versie` verandert (sjabloon geladen, gewisseld
// vanuit de HTML-weergave), anders springt de cursor bij elke toets.
const MT_VAR_RE = /\{\{\s*([#/]?[A-Za-z0-9_.]+)\s*\}\}/g;
const mtChipHtml = (naam, label) => {
  const sectie = naam.startsWith('#') ? 'begin van een voorwaardelijk blok' : naam.startsWith('/') ? 'einde van het blok' : '';
  const tip = hdEscape(label ? `${label}${sectie ? ` — ${sectie}` : ''}` : (sectie || 'Variabele: wordt bij het verzenden ingevuld'));
  return `<span class="mt-var" data-var="${hdEscape(naam)}" contenteditable="false" title="${tip}" style="display:inline-block;padding:0 6px;margin:0 1px;border-radius:6px;background:#ecfccb;color:#3f6212;border:1px solid #bef264;font-family:ui-monospace,Menlo,monospace;font-size:.85em;line-height:1.6;white-space:nowrap;user-select:all;">{{${hdEscape(naam)}}}</span>`;
};
// {{…}} in de opgeslagen HTML → chips voor de editor. Alleen buiten tags
// vervangen (een {{url}} in een href blijft tekst; die wordt bij het verzenden
// gewoon ingevuld en is in de weergave niet zichtbaar).
const mtNaarChips = (html, labels) => String(html || '').split(/(<[^>]+>)/g).map((deel) => (
  deel.startsWith('<') ? deel : deel.replace(MT_VAR_RE, (_, naam) => mtChipHtml(naam, labels.get(naam.replace(/^[#/]/, ''))))
)).join('');
// Chips → {{…}}: op het data-var-attribuut, ongeacht wat de browser met de
// binnenkant deed.
const mtVanChips = (html) => String(html || '')
  .replace(/<span[^>]*class="mt-var"[^>]*data-var="([^"]+)"[^>]*>[\s\S]*?<\/span>/g, (_, naam) => `{{${naam.replace(/&amp;/g, '&')}}}`)
  .replace(/<span[^>]*data-var="([^"]+)"[^>]*class="mt-var"[^>]*>[\s\S]*?<\/span>/g, (_, naam) => `{{${naam}}}`);
const MtVisueleEditor = ({ html, versie, onChange, vars = [], editorRef }) => {
  const ref = useRef(null);
  const labels = useMemo(() => new Map((vars || []).map((v) => [v.name, v.label || v.name])), [vars]);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = mtNaarChips(html, labels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versie]);
  const meld = () => onChange(mtVanChips(ref.current.innerHTML));
  // Variabele invoegen op de cursor als chip. `insertHTML` zet ze op de plek
  // van de selectie; staat de cursor niet in de editor, dan achteraan.
  const voegVar = (naam) => {
    const el = ref.current; if (!el) return;
    const sel = window.getSelection();
    const binnen = sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer);
    if (!binnen) { el.focus(); const r = document.createRange(); r.selectNodeContents(el); r.collapse(false); sel.removeAllRanges(); sel.addRange(r); }
    const stuk = naam.startsWith('#')
      ? `${mtChipHtml(naam, labels.get(naam.slice(1)))}&nbsp;…&nbsp;${mtChipHtml(`/${naam.slice(1)}`, labels.get(naam.slice(1)))}`
      : mtChipHtml(naam, labels.get(naam));
    document.execCommand('insertHTML', false, `${stuk}&nbsp;`);
    meld();
  };
  if (editorRef) editorRef.current = { voegVar };
  return (
    <div className="border border-slate-600/50 rounded-lg overflow-hidden bg-slate-700/50">
      <OpmaakBalk editorRef={ref} onChange={() => meld()} rechts={<><span className="inline-block w-3 h-3 rounded-sm bg-lime-200 border border-lime-400" />gemarkeerd = variabele, wordt bij het verzenden ingevuld</>} />
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={meld} onBlur={meld}
        className="hd-html hd-mail-canvas min-h-[320px] max-h-[65vh] overflow-y-auto px-6 py-5 text-sm focus:outline-none" />
    </div>
  );
};

const MailTemplatesPage = ({ token, availableLabels = [], salesStructure = EMPTY_STRUCTURE }) => {
  const hdr = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const mt = useMemo(() => ({
    get: async (p) => { const r = await fetch(`/api/mail-templates${p}`, { headers: hdr }); const b = await r.json().catch(() => null); if (!r.ok) throw Object.assign(new Error((b && b.error) || `HTTP ${r.status}`), { body: b }); return b; },
    send: async (p, method, body) => { const r = await fetch(`/api/mail-templates${p}`, { method, headers: { ...hdr, 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) }); const b = await r.json().catch(() => null); if (!r.ok) throw Object.assign(new Error((b && b.error) || `HTTP ${r.status}`), { body: b }); return b; },
  }), [hdr]);

  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('inhoud'); // inhoud | afzender | versies
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewBreed, setPreviewBreed] = useState('desktop'); // desktop | mobiel
  const [preview, setPreview] = useState(null); // { subject, html, text, sender, validatie }
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sender, setSender] = useState(null);
  const [versions, setVersions] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testLabel, setTestLabel] = useState('');
  const [testTeam, setTestTeam] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  // Filters + sortering van de lijst.
  const [q, setQ] = useState('');
  const [fModule, setFModule] = useState('all');
  const [fLabel, setFLabel] = useState('all');
  const [fTaal, setFTaal] = useState('all');
  const [fStatus, setFStatus] = useState('all'); // all | active | concept | system
  const [sortKey, setSortKey] = useState('naam'); // naam | gewijzigd | gebruik
  const bodyRef = useRef(null);
  // Inhoud bewerken als tekst (visueel, standaard) of als HTML. De visuele
  // editor zet zijn tekst alleen opnieuw bij een nieuwe `bodyVersie` (ander
  // sjabloon, wissel vanuit HTML, versie teruggezet).
  const [bodyMode, setBodyMode] = useState('tekst');
  const [bodyVersie, setBodyVersie] = useState(0);
  const visueelRef = useRef(null);
  useEffect(() => { setBodyVersie((v) => v + 1); }, [draft && draft.id, bodyMode]);   // eslint-disable-line react-hooks/exhaustive-deps
  const fileRef = useRef(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try { const d = await mt.get(''); setTemplates(d.templates || []); setError(''); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); mt.get('/vars').then(setCatalog).catch(() => {}); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const moduleOpties = useMemo(() => [...MODULE_CATALOG, { id: MT_SNIPPET_MODULE, label: 'Helpdesk — snelle antwoorden (naar de klant)' }, { id: MT_INTERN_MODULE, label: 'Helpdesk — snelle antwoorden (intern)' }], []);
  const moduleLabel = (id) => (moduleOpties.find((m) => m.id === id) || { label: id }).label;
  const teamsVoor = (label) => (label ? scopeOptions(salesStructure, label, 'all').teams : []);
  const categorieen = useMemo(() => [...new Set(templates.map((t) => t.category).filter(Boolean))].sort(), [templates]);

  // Variabelen voor het geopende sjabloon: actie + modules + algemeen (catalogus).
  const vars = useMemo(() => {
    if (!draft || !catalog) return [];
    const uit = [];
    const zet = (v) => { if (!uit.some((x) => x.name === v.name)) uit.push(v); };
    const actie = catalog.acties[mtSleutel(draft.key)];
    if (actie) actie.vars.forEach(zet);
    for (const m of (draft.modules || [])) ((catalog.modules[m] || {}).vars || []).forEach(zet);
    (catalog.algemeen || []).forEach(zet);
    return uit;
  }, [draft, catalog]);
  const verplicht = (catalog && draft && catalog.acties[mtSleutel(draft.key)] && catalog.acties[mtSleutel(draft.key)].required) || [];

  const gefilterd = useMemo(() => {
    const term = q.trim().toLowerCase();
    const lijst = templates.filter((t) => {
      if (fModule === 'system' && (t.modules || []).length) return false;
      if (fModule !== 'all' && fModule !== 'system' && !(t.modules || []).includes(fModule)) return false;
      if (fLabel === 'global' && mtLabels(t).length) return false;
      if (fLabel !== 'all' && fLabel !== 'global' && !mtLabels(t).includes(fLabel)) return false;
      if (fTaal !== 'all' && (t.language || 'nl') !== fTaal) return false;
      if (fStatus === 'active' && !t.active) return false;
      if (fStatus === 'concept' && t.active) return false;
      if (fStatus === 'beperkt' && !t.restricted) return false;
      if (fStatus === 'system' && (t.modules || []).length) return false;
      if (term && !`${t.key} ${t.name} ${t.subject} ${t.category || ''} ${mtScope(t)}`.toLowerCase().includes(term)) return false;
      return true;
    });
    const cmp = { naam: (a, b) => a.name.localeCompare(b.name, 'nl-BE'), gewijzigd: (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0), gebruik: (a, b) => (b.usage_count || 0) - (a.usage_count || 0) }[sortKey];
    return [...lijst].sort(cmp);
  }, [templates, q, fModule, fLabel, fTaal, fStatus, sortKey]);
  const hasFilters = q || fModule !== 'all' || fLabel !== 'all' || fTaal !== 'all' || fStatus !== 'all';

  const editTemplate = (t) => { setSelId(t.id); setDraft({ ...t, energy_label: t.energy_label || '', energy_labels: mtLabels(t), sales_team: t.sales_team || '', category: t.category || '', from_address: t.from_address || '', from_name: t.from_name || '', reply_to: t.reply_to || '', restricted: !!t.restricted, attachments: t.attachments || [], isNew: false }); setTab('inhoud'); setVersions(null); setError(''); setTestLabel(t.energy_label || ''); setTestTeam(t.sales_team || ''); };
  const newTemplate = () => { setSelId(null); setDraft(mtLeeg()); setTab('inhoud'); setVersions(null); setError(''); };
  // Kopie: alles blijft staan, ook de sleutel — een kopie is meestal dezelfde
  // actie voor een ander label, team of taal. Botst de scope, dan zegt de server dat.
  const copyTemplate = () => { if (!draft) return; setSelId(null); setDraft({ ...draft, id: undefined, name: `${draft.name} (kopie)`, isNew: true, attachments: [] }); setVersions(null); toast.info('Kopie gemaakt — kies een ander label, team of taal en sla op.'); };

  const bodyVoorServer = () => ({
    key: mtSleutel(draft.key), name: draft.name, subject: draft.subject, html_body: draft.html_body,
    from_address: draft.from_address || '', from_name: draft.from_name || '', reply_to: draft.reply_to || '',
    modules: draft.modules || [], energy_label: draft.energy_label || '', energy_labels: draft.energy_labels || [], sales_team: draft.sales_team || '',
    language: draft.language || 'nl', active: !!draft.active, restricted: !!draft.restricted, category: draft.category || '',
    attachments: (draft.attachments || []).map((a) => (a.content_base64 ? { filename: a.filename, content_type: a.content_type, content_base64: a.content_base64 } : { filename: a.filename })),
  });

  const save = async () => {
    if (!draft) return;
    const key = mtSleutel(draft.key);
    if (!key || !draft.name.trim() || !draft.subject.trim()) { setError('Sleutel, naam en onderwerp zijn verplicht.'); return; }
    setBusy(true); setError('');
    try {
      const d = draft.isNew ? await mt.send('', 'POST', bodyVoorServer()) : await mt.send(`/${draft.id}`, 'PUT', bodyVoorServer());
      const w = (d.validatie && d.validatie.waarschuwingen) || [];
      toast.success('Sjabloon opgeslagen.');
      if (w.length) toast.info(`Let op: ${w[0]}${w.length > 1 ? ` (+${w.length - 1})` : ''}`);
      await load();
      editTemplate(d.template);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!draft || draft.isNew) return;
    if (!window.confirm(`Sjabloon "${draft.name}" verwijderen?${['welcome', 'password_reset'].includes(draft.key) && !draft.energy_label ? ' Het valt dan terug op de ingebouwde tekst.' : ''}`)) return;
    setBusy(true); setError('');
    try { await mt.send(`/${draft.id}`, 'DELETE'); toast.success('Sjabloon verwijderd.'); setDraft(null); setSelId(null); load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!draft || draft.isNew) { setError('Sla het sjabloon eerst op, dan kun je een testmail sturen.'); return; }
    if (!testTo.trim()) { setError('Vul een ontvangeradres in voor de test.'); return; }
    setTestBusy(true); setError('');
    try {
      const d = await mt.send(`/${draft.id}/test`, 'POST', { to: testTo.trim(), energy_label: testLabel || null, sales_team: testTeam || null, ...bodyVoorServer() });
      toast.success(`Testmail verstuurd naar ${d.to}${testLabel ? ` (branding ${testTeam ? `${testLabel} · ${testTeam}` : testLabel})` : ''}.`);
    } catch (e) { setError(`${e.message}${e.body && e.body.detail ? ` — ${e.body.detail}` : ''}`); } finally { setTestBusy(false); }
  };

  // Preview + afzender: live bij het typen, met een korte pauze. De server rendert
  // met voorbeeldwaarden en de branding van het label/team van het sjabloon —
  // of van de testkeuze, zodat je een globaal sjabloon per merk kunt bekijken.
  const previewLabel = draft ? (draft.energy_label || testLabel || '') : '';
  const previewTeam = draft ? (draft.sales_team || (draft.energy_label ? '' : testTeam) || '') : '';
  useEffect(() => {
    if (!draft || !previewOpen) return undefined;
    const h = setTimeout(() => {
      setPreviewBusy(true);
      mt.send('/preview', 'POST', { key: mtSleutel(draft.key) || 'sjabloon', modules: draft.modules || [], energy_label: previewLabel, sales_team: previewTeam, subject: draft.subject, html_body: draft.html_body })
        .then(setPreview).catch(() => setPreview(null)).finally(() => setPreviewBusy(false));
    }, 500);
    return () => clearTimeout(h);
  }, [draft && draft.key, draft && draft.subject, draft && draft.html_body, draft && (draft.modules || []).join(','), previewLabel, previewTeam, previewOpen, mt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!draft) return undefined;
    const h = setTimeout(() => {
      const qs = new URLSearchParams({ label: previewLabel, from: draft.from_address || '', from_name: draft.from_name || '', reply_to: draft.reply_to || '' });
      mt.get(`/sender?${qs}`).then(setSender).catch(() => setSender(null));
    }, 400);
    return () => clearTimeout(h);
  }, [draft && draft.from_address, draft && draft.from_name, draft && draft.reply_to, previewLabel, mt]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVersions = async () => { if (!draft || draft.isNew) return; try { const d = await mt.get(`/${draft.id}/versions`); setVersions(d.versions || []); } catch (e) { setError(e.message); } };
  useEffect(() => { if (tab === 'versies' && versions === null) loadVersions(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (vid) => {
    if (!window.confirm('Deze versie terugzetten? De huidige inhoud wordt eerst zelf als versie bewaard.')) return;
    setBusy(true);
    try { const d = await mt.send(`/${draft.id}/versions/${vid}/restore`, 'POST'); toast.success('Versie teruggezet.'); await load(); editTemplate(d.template); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Variabele op de cursorpositie invoegen (of aan het einde als er geen focus is).
  const insertVar = (naam) => {
    if (bodyMode === 'tekst' && visueelRef.current) { visueelRef.current.voegVar(naam); return; }
    const tekst = naam.startsWith('#') ? `{{${naam}}}…{{/${naam.slice(1)}}}` : `{{${naam}}}`;
    const el = bodyRef.current;
    setDraft((p) => {
      const s = p.html_body || '';
      const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : s.length;
      const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : s.length;
      const uit = `${s.slice(0, start)}${tekst}${s.slice(end)}`;
      setTimeout(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = start + tekst.length; } }, 0);
      return { ...p, html_body: uit };
    });
  };
  const addAttachment = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error(`"${file.name}" is te groot (max. 3 MB per bestand).`); return; }
    const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });
    setDraft((p) => ({ ...p, attachments: [...(p.attachments || []).filter((a) => a.filename !== file.name), { filename: file.name, content_type: file.type || 'application/octet-stream', size: file.size, content_base64: b64 }] }));
  };

  const inputCls = 'w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500';
  const selCls = 'w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200';
  const tabCls = (id) => `px-3 py-1.5 rounded-lg text-sm ${tab === id ? 'bg-lime-500/10 text-lime-300 border border-lime-500/30' : 'text-slate-400 hover:text-slate-200 border border-transparent'}`;
  const warnings = (preview && preview.validatie && preview.validatie.waarschuwingen) || [];
  const fouten = (preview && preview.validatie && preview.validatie.fouten) || [];

  const senderRegel = sender && (
    <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 border ${sender.verified ? 'border-lime-500/30 bg-lime-500/5 text-slate-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'}`}>
      {sender.verified ? <Check size={14} className="shrink-0 mt-0.5 text-lime-400" /> : <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-400" />}
      <span>Vertrekt vanaf <strong className="font-mono">{sender.fromName} &lt;{sender.from}&gt;</strong>{sender.replyTo ? <> · antwoorden naar <span className="font-mono">{sender.replyTo}</span></> : null}{sender.verified ? ' — geverifieerd verzenddomein.' : previewLabel ? ` — ${previewLabel} heeft nog geen geverifieerd verzenddomein (Mail Domeinen); de mail vertrekt vanaf het systeemadres.` : ' — kies een energielabel om de merk-afzender te zien.'}</span>
    </div>
  );

  const previewPaneel = draft && previewOpen && (
    <div className="space-y-2 min-w-0">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-slate-400 flex items-center gap-2"><Eye size={14} className="text-lime-400" />Preview{previewBusy && <Spinner size={12} />}</div>
        <div className="flex items-center gap-1">
          {!draft.energy_label && <select value={testLabel} onChange={(e) => { setTestLabel(e.target.value); setTestTeam(''); }} className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" title="Bekijk met de branding van dit energielabel"><option value="">Neutraal (geen label)</option>{availableLabels.map((l) => <option key={l} value={l}>{l}</option>)}</select>}
          {!draft.energy_label && testLabel && teamsVoor(testLabel).length > 0 && <select value={testTeam} onChange={(e) => setTestTeam(e.target.value)} className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" title="Branding van een verkoopteam"><option value="">Label</option>{teamsVoor(testLabel).map((t) => <option key={t} value={t}>{t}</option>)}</select>}
          <button onClick={() => setPreviewBreed('desktop')} title="Desktop (600 px)" className={`p-1.5 rounded ${previewBreed === 'desktop' ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}><Tablet size={14} /></button>
          <button onClick={() => setPreviewBreed('mobiel')} title="Telefoon (360 px)" className={`p-1.5 rounded ${previewBreed === 'mobiel' ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}><Smartphone size={14} /></button>
        </div>
      </div>
      {preview && <div className="text-xs text-slate-300 px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg truncate"><span className="text-slate-500">Onderwerp: </span>{preview.subject || <span className="text-slate-500">(leeg)</span>}</div>}
      <div className="border border-slate-700 rounded-lg overflow-hidden bg-[#f1f5f9] flex justify-center">
        {preview
          ? <iframe title="Mailpreview" sandbox="" srcDoc={preview.html} style={{ width: previewBreed === 'mobiel' ? 360 : '100%', maxWidth: '100%', height: 640, border: 0, background: '#f1f5f9' }} />
          : <div className="py-16 text-slate-500 text-sm">{previewBusy ? 'Preview maken…' : 'Nog geen preview.'}</div>}
      </div>
      {senderRegel}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Mailsjablonen" subtitle="Onderwerp en tekst per e-mail, met velden die bij het verzenden worden ingevuld — per energielabel, verkoopteam en taal.">
        <button onClick={newTemplate} className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm"><Plus size={16} />Nieuw sjabloon</button>
      </PageHeader>
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl text-sm">{error}</div>}
      <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-4 items-start">
        {/* ── Lijst met zoeken, filters en sortering ── */}
        <Card className="!p-4 space-y-3 min-w-0">
          <div className="relative"><Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam, sleutel, onderwerp…" className="w-full pl-7 pr-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" /></div>
          <div className="grid grid-cols-2 gap-2">
            <select value={fModule} onChange={(e) => setFModule(e.target.value)} className={selCls}><option value="all">Alle modules</option><option value="system">Systeemsjablonen</option>{moduleOpties.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>
            <select value={fLabel} onChange={(e) => setFLabel(e.target.value)} className={selCls}><option value="all">Alle labels</option><option value="global">Enkel globaal</option>{availableLabels.map((l) => <option key={l} value={l}>{l}</option>)}</select>
            <select value={fTaal} onChange={(e) => setFTaal(e.target.value)} className={selCls}><option value="all">Alle talen</option>{MT_TALEN.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={selCls}><option value="all">Actief + concept</option><option value="active">Enkel actief</option><option value="concept">Enkel concept</option><option value="beperkt">Enkel beperkt (intern)</option></select>
          </div>
          <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
            <span>{gefilterd.length} van {templates.length}{hasFilters && <button onClick={() => { setQ(''); setFModule('all'); setFLabel('all'); setFTaal('all'); setFStatus('all'); }} className="ml-2 text-red-300 hover:underline">wissen</button>}</span>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200"><option value="naam">Op naam</option><option value="gewijzigd">Laatst gewijzigd</option><option value="gebruik">Meest gebruikt</option></select>
          </div>
          {loading ? <div className="flex justify-center py-6"><Spinner size={24} /></div> :
          <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
            {gefilterd.map((t) => (
              <button key={t.id} onClick={() => editTemplate(t)} className={`w-full text-left px-3 py-2 rounded-lg border ${selId === t.id ? 'bg-lime-500/10 text-lime-300 border-lime-500/30' : 'hover:bg-slate-800 text-slate-300 border-transparent'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!t.active && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px]">concept</span>}
                    {t.restricted && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px]" title="Enkel kiesbaar voor de toegestane logins">beperkt</span>}
                    <span className="px-1.5 py-0.5 rounded bg-slate-700/60 border border-slate-600/50 text-[10px] text-slate-300 uppercase">{t.language || 'nl'}</span>
                  </div>
                </div>
                <div className="text-xs text-slate-500 font-mono truncate">{t.key} · <span className="font-sans">{mtScope(t)}</span></div>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {t.category && <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 text-[10px]">{t.category}</span>}
                  {(t.modules || []).length > 0
                    ? (t.modules || []).map((m) => <span key={m} className="px-1.5 py-0.5 rounded bg-slate-700/60 border border-slate-600/50 text-[10px] text-slate-300">{moduleLabel(m)}</span>)
                    : <span className="text-[10px] text-slate-600">systeemsjabloon</span>}
                  {(t.usage_count > 0) && <span className="ml-auto text-[10px] text-slate-500" title={t.last_used_at ? `Laatst gebruikt ${mtDatum(t.last_used_at)}` : ''}>{t.usage_count}× gebruikt</span>}
                </div>
              </button>
            ))}
            {gefilterd.length === 0 && <p className="text-sm text-slate-500 px-1 py-4">{templates.length ? 'Geen sjablonen voor deze filters.' : 'Nog geen sjablonen.'}</p>}
          </div>}
        </Card>

        {/* ── Editor ── */}
        <Card className="min-w-0">
          {!draft ? <div className="py-12 text-center text-slate-500 text-sm">Kies links een sjabloon of maak een nieuw sjabloon aan.</div> : <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1 flex-wrap">
                <button onClick={() => setTab('inhoud')} className={tabCls('inhoud')}>Inhoud</button>
                <button onClick={() => setTab('afzender')} className={tabCls('afzender')}>Afzender & zichtbaarheid</button>
                {!draft.isNew && <button onClick={() => setTab('versies')} className={tabCls('versies')}>Versies{draft.version_count ? ` (${draft.version_count})` : ''}</button>}
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer" title="Een concept staat in geen enkele keuzelijst en wordt niet gebruikt bij het verzenden"><input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft((p) => ({ ...p, active: e.target.checked }))} className="w-3.5 h-3.5 rounded accent-lime-500" />{draft.active ? 'Actief' : 'Concept'}</label>
                {/* Beperkt: alleen kiesbaar voor de logins in Instellingen → "Beperkte
                    mailsjablonen — toegestane logins". Voor iedereen anders bestaat
                    het sjabloon in geen enkele keuzelijst. */}
                <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer" title="Enkel kiesbaar voor de logins in Instellingen → Beperkte mailsjablonen; voor iedereen anders onzichtbaar in de keuzelijsten"><input type="checkbox" checked={!!draft.restricted} onChange={(e) => setDraft((p) => ({ ...p, restricted: e.target.checked }))} className="w-3.5 h-3.5 rounded accent-amber-500" />Beperkt (intern)</label>
                <button onClick={() => setPreviewOpen((s) => !s)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs"><Eye size={13} />{previewOpen ? 'Verberg preview' : 'Toon preview'}</button>
              </div>
            </div>

            {tab === 'inhoud' && (
              <div className={`grid gap-4 ${previewOpen ? '2xl:grid-cols-2' : ''}`}>
                <div className="space-y-3 min-w-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block"><span className="text-xs text-slate-400">Sleutel (actie-id)</span>
                      <input value={draft.key} onChange={(e) => setDraft((p) => ({ ...p, key: e.target.value }))} placeholder="bv. helpdesk_status" list="mt-sleutels" className={`${inputCls} font-mono`} />
                      <datalist id="mt-sleutels">{[...new Set(templates.map((t) => t.key))].map((k) => <option key={k} value={k} />)}</datalist></label>
                    <label className="block"><span className="text-xs text-slate-400">Naam</span>
                      <input value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} placeholder="bv. Stand van zaken" className={inputCls} /></label>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    <label className="block col-span-2 sm:col-span-3"><span className="text-xs text-slate-400">Categorie</span>
                      <input value={draft.category} onChange={(e) => setDraft((p) => ({ ...p, category: e.target.value }))} placeholder="bv. Facturatie" list="mt-categorieen" className={inputCls} />
                      <datalist id="mt-categorieen">{categorieen.map((c) => <option key={c} value={c} />)}</datalist></label>
                    <label className="block"><span className="text-xs text-slate-400">Taal</span>
                      <select value={draft.language || 'nl'} onChange={(e) => setDraft((p) => ({ ...p, language: e.target.value }))} className={inputCls}>{MT_TALEN.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></label>
                  </div>
                  {/* Energielabels: vink er één of meer aan; niets = alle labels. Precies
                      één label kan bovendien tot een verkoopteam beperkt worden. */}
                  <div>
                    <span className="text-xs text-slate-400">Energielabels <span className="text-slate-500">(niets aangevinkt = alle labels)</span></span>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-1">
                      {availableLabels.map((l) => {
                        const aan = (draft.energy_labels || []).includes(l);
                        return (
                          <label key={l} className="flex items-center gap-1.5 text-sm text-slate-200 cursor-pointer">
                            <input type="checkbox" checked={aan} onChange={(e) => setDraft((p) => {
                              const lijst = e.target.checked ? [...(p.energy_labels || []).filter((x) => x !== l), l] : (p.energy_labels || []).filter((x) => x !== l);
                              const geordend = availableLabels.filter((x) => lijst.includes(x));
                              return { ...p, energy_labels: geordend, energy_label: geordend.length === 1 ? geordend[0] : '', sales_team: geordend.length === 1 ? p.sales_team : '' };
                            })} className="w-3.5 h-3.5 rounded accent-lime-500" />{l}
                          </label>
                        );
                      })}
                      {availableLabels.length === 0 && <span className="text-xs text-slate-500">Geen energielabels bekend.</span>}
                    </div>
                  </div>
                  {draft.energy_label && teamsVoor(draft.energy_label).length > 0 && (
                    <label className="block sm:w-1/2"><span className="text-xs text-slate-400">Verkoopteam (optioneel — enkel voor dit team)</span>
                      <select value={draft.sales_team} onChange={(e) => setDraft((p) => ({ ...p, sales_team: e.target.value }))} className={inputCls}><option value="">Heel het label</option>{teamsVoor(draft.energy_label).map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
                  )}
                  <label className="block"><span className="text-xs text-slate-400">Onderwerp</span>
                    <input value={draft.subject} onChange={(e) => setDraft((p) => ({ ...p, subject: e.target.value }))} className={inputCls} /></label>
                  <div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">Inhoud</span>
                        {/* Tekst = bewerken zoals in de mail (HTML wordt afgeleid); HTML = de bron. */}
                        <span className="inline-flex rounded-md border border-slate-600/60 overflow-hidden text-[11px]">
                          <button type="button" onClick={() => setBodyMode('tekst')} title="Bewerken als tekst, zoals in de mail; de HTML wordt eruit afgeleid" className={`px-2 py-0.5 ${bodyMode === 'tekst' ? 'bg-lime-500 text-slate-900 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}>Tekst</button>
                          <button type="button" onClick={() => setBodyMode('html')} title="De HTML rechtstreeks bewerken" className={`px-2 py-0.5 ${bodyMode === 'html' ? 'bg-lime-500 text-slate-900 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}>HTML</button>
                        </span>
                      </span>
                      <span className="text-[11px] text-slate-500">{(draft.html_body || '').length.toLocaleString('nl-BE')} tekens</span>
                    </div>
                    {bodyMode === 'tekst' && <MtVisueleEditor html={draft.html_body} versie={bodyVersie} vars={vars} editorRef={visueelRef} onChange={(h) => setDraft((p) => ({ ...p, html_body: h }))} />}
                    {bodyMode === 'html' && <textarea ref={bodyRef} value={draft.html_body} onChange={(e) => setDraft((p) => ({ ...p, html_body: e.target.value }))} rows={previewOpen ? 22 : 16} spellCheck={false} className={`${inputCls} font-mono text-xs leading-relaxed`} />}
                  </div>
                  {(fouten.length > 0 || warnings.length > 0) && (
                    <div className="space-y-1">
                      {fouten.map((f) => <div key={f} className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2.5 py-1.5"><AlertCircle size={13} className="shrink-0 mt-0.5" />{f}</div>)}
                      {warnings.map((w) => <div key={w} className="flex items-start gap-2 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-2.5 py-1.5"><AlertCircle size={13} className="shrink-0 mt-0.5" />{w}</div>)}
                    </div>
                  )}
                  {vars.length > 0 && (
                    <div>
                      <div className="text-xs text-slate-400 mb-1">Variabelen — klik om in te voegen op de cursor{verplicht.length ? <span className="text-slate-500"> · verplicht: {verplicht.map((v) => `{{${v}}}`).join(', ')}</span> : null}</div>
                      <div className="flex flex-wrap gap-1">
                        {vars.map((v) => <button key={v.name} type="button" onClick={() => insertVar(v.name)} title={`${v.label}${v.sample != null && typeof v.sample !== 'boolean' ? ` — bv. ${v.sample}` : ''}`} className={`px-1.5 py-0.5 rounded text-[11px] font-mono border ${verplicht.includes(v.name) ? 'bg-lime-500/10 border-lime-500/40 text-lime-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-lime-500/40 hover:text-lime-300'}`}>{v.name.startsWith('#') ? `{{${v.name}}}…` : `{{${v.name}}}`}</button>)}
                      </div>
                      {mtSleutel(draft.key) === 'welcome' && <p className="text-[11px] text-slate-500 mt-1">De MFA-QR-code zit als inline-afbeelding via <code className="px-1 bg-slate-800 rounded text-lime-300">cid:qrcode</code>.</p>}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center justify-between gap-2"><span className="text-xs text-slate-400 flex items-center gap-1"><Paperclip size={12} />Bijlagen (gaan met elke mail van dit sjabloon mee)</span><button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="text-xs text-lime-400 hover:underline">+ bestand</button><input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) addAttachment(f); e.target.value = ''; }} /></div>
                    {(draft.attachments || []).length === 0 ? <p className="text-[11px] text-slate-600 mt-1">Geen bijlagen.</p> : (
                      <div className="mt-1 space-y-1">{(draft.attachments || []).map((a) => (
                        <div key={a.filename} className="flex items-center gap-2 text-xs text-slate-300 bg-slate-800/60 border border-slate-700/50 rounded px-2 py-1"><File size={12} className="text-slate-500 shrink-0 w-3" /><span className="truncate flex-1">{a.filename}</span><span className="text-slate-500 shrink-0">{a.size ? mtBytes(a.size) : ''}</span><button type="button" onClick={() => setDraft((p) => ({ ...p, attachments: (p.attachments || []).filter((x) => x.filename !== a.filename) }))} className="text-slate-500 hover:text-red-300" title="Verwijderen"><X size={12} /></button></div>
                      ))}</div>
                    )}
                  </div>
                </div>
                {previewPaneel}
              </div>
            )}

            {tab === 'afzender' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg">
                  <label className="block"><span className="text-xs text-slate-400">Afzender (From-adres)</span>
                    <input value={draft.from_address} onChange={(e) => setDraft((p) => ({ ...p, from_address: e.target.value }))} placeholder="leeg = label-standaard" className={`${inputCls} font-mono`} /></label>
                  <label className="block"><span className="text-xs text-slate-400">Afzendernaam</span>
                    <input value={draft.from_name} onChange={(e) => setDraft((p) => ({ ...p, from_name: e.target.value }))} placeholder="bv. Power2You" className={inputCls} /></label>
                  <label className="block"><span className="text-xs text-slate-400">Reply-To</span>
                    <input value={draft.reply_to} onChange={(e) => setDraft((p) => ({ ...p, reply_to: e.target.value }))} placeholder="optioneel" className={`${inputCls} font-mono`} /></label>
                  <div className="sm:col-span-3 space-y-2">
                    {senderRegel}
                    <p className="text-xs text-slate-500">Het From-adres wordt bij verzenden gevalideerd tegen een <strong>geverifieerd</strong> partnerdomein (Mail Domeinen). Een niet-geverifieerd of vreemd domein wordt genegeerd en valt terug op de standaard-afzender van het energielabel.</p>
                  </div>
                </div>
                <div className="p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg">
                  <span className="text-xs text-slate-400">Kiesbaar in deze modules</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 mt-2">
                    {moduleOpties.map((m) => {
                      const aan = (draft.modules || []).includes(m.id);
                      return (
                        <label key={m.id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors ${aan ? 'border-lime-500/40 bg-lime-500/10 text-lime-300' : 'border-slate-600/50 bg-slate-700/30 text-slate-300 hover:border-lime-500/30'}`}>
                          <input type="checkbox" checked={aan} className="w-3.5 h-3.5 rounded accent-lime-500" onChange={() => setDraft((p) => {
                            const cur = new Set(p.modules || []);
                            if (cur.has(m.id)) cur.delete(m.id);
                            else {
                              cur.add(m.id);
                              // Een snel antwoord naar de klant is standaard ook intern
                              // bruikbaar (afspraak 17-09-2026): het vinkje gaat mee aan,
                              // en kan daarna los weer uit.
                              if (m.id === MT_SNIPPET_MODULE) cur.add(MT_INTERN_MODULE);
                            }
                            return { ...p, modules: [...cur] };
                          })} />
                          <span className="truncate">{m.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {(draft.modules || []).length === 0
                      ? <><strong className="text-slate-400">Niets aangevinkt = systeemsjabloon.</strong> Het hoort bij zijn eigen actie (uitnodiging, wachtwoord-reset) en is in geen enkele module te kiezen — zo duikt de welkomstmail nooit op in de Helpdesk.</>
                      : <>Gebruikers kunnen dit sjabloon kiezen in: <strong className="text-slate-300">{(draft.modules || []).map(moduleLabel).join(', ')}</strong>.{((draft.modules || []).includes(MT_SNIPPET_MODULE) || (draft.modules || []).includes(MT_INTERN_MODULE)) && <> Snelle antwoorden worden als <strong>platte tekst</strong> in het berichtveld van een ticket gezet.</>}</>}
                  </p>
                </div>
                <div className="p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg text-xs text-slate-400">
                  <strong className="text-slate-300">Geldigheid:</strong> {draft.sales_team ? <>enkel voor verkoopteam <strong className="text-slate-200">{draft.sales_team}</strong> van {draft.energy_label}</> : draft.energy_label ? <>enkel voor energielabel <strong className="text-slate-200">{draft.energy_label}</strong></> : (draft.energy_labels || []).length > 1 ? <>enkel voor de energielabels <strong className="text-slate-200">{draft.energy_labels.join(', ')}</strong></> : 'alle energielabels'} · taal <strong className="text-slate-200 uppercase">{draft.language || 'nl'}</strong>. Bij het verzenden wint het meest specifieke sjabloon met dezelfde sleutel (team &gt; label &gt; globaal).
                </div>
              </div>
            )}

            {tab === 'versies' && (
              <div className="space-y-2">
                {versions === null ? <div className="flex justify-center py-6"><Spinner size={20} /></div> : versions.length === 0 ? <p className="text-sm text-slate-500">Nog geen eerdere versies — elke keer dat je opslaat, wordt de vorige inhoud hier bewaard (max. 30).</p> : (
                  <table className="w-full text-sm resp-card">
                    <thead className="text-xs text-slate-400"><tr><th className="text-left px-2 py-1.5">Bewaard op</th><th className="text-left px-2 py-1.5">Door</th><th className="text-left px-2 py-1.5">Naam / onderwerp</th><th className="text-right px-2 py-1.5">HTML</th><th className="px-2 py-1.5"></th></tr></thead>
                    <tbody>{versions.map((v) => (
                      <tr key={v.id} className="border-t border-slate-700/40">
                        <td className="px-2 py-1.5 text-slate-300 whitespace-nowrap">{v.saved_at ? new Date(v.saved_at).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                        <td className="px-2 py-1.5 text-slate-400">{v.saved_by_name || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-300 min-w-0"><div className="truncate">{v.name}</div><div className="text-xs text-slate-500 truncate">{v.subject}</div></td>
                        <td className="px-2 py-1.5 text-right text-xs text-slate-500 whitespace-nowrap">{Number(v.html_length || 0).toLocaleString('nl-BE')} tekens</td>
                        <td className="px-2 py-1.5 text-right"><button onClick={() => restore(v.id)} disabled={busy} className="flex items-center gap-1 px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs ml-auto"><RotateCcw size={12} />Terugzetten</button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            )}

            <div className="flex gap-2 flex-wrap items-center pt-1 border-t border-slate-700/50">
              <button onClick={save} disabled={busy} className="px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg disabled:opacity-40 text-sm">{busy ? 'Bezig…' : draft.isNew ? 'Aanmaken' : 'Opslaan'}</button>
              {!draft.isNew && <button onClick={copyTemplate} title="Maak een bewerkbare kopie (bv. voor een ander label of een andere taal)" className="flex items-center gap-1.5 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm"><Copy size={14} />Kopiëren</button>}
              {!draft.isNew && <button onClick={del} disabled={busy} className="px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-300 rounded-lg text-sm disabled:opacity-40 ml-auto">Verwijderen</button>}
            </div>
            {!draft.isNew && (
              <div className="p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-slate-300 flex items-center gap-1.5"><Send size={13} />Testmail versturen</p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <select value={testLabel} onChange={(e) => { setTestLabel(e.target.value); setTestTeam(''); }} disabled={!!draft.energy_label} className={inputCls} title="Bepaalt logo, kleuren, contactblok en afzender van de testmail"><option value="">Systeem-standaard (geen label)</option>{availableLabels.map((l) => <option key={l} value={l}>{l}</option>)}</select>
                  <select value={testTeam} onChange={(e) => setTestTeam(e.target.value)} disabled={!!draft.sales_team || !testLabel} className={inputCls} title="Branding van een verkoopteam"><option value="">Heel het label</option>{teamsVoor(testLabel).map((t) => <option key={t} value={t}>{t}</option>)}</select>
                  <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="ontvanger@adres.be" className={`${inputCls} font-mono`} />
                  <button onClick={sendTest} disabled={testBusy} className="px-4 py-2 mt-1 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg text-sm font-medium disabled:opacity-40 whitespace-nowrap">{testBusy ? 'Versturen…' : 'Verstuur test'}</button>
                </div>
                <p className="text-[11px] text-slate-500">Gebruikt de huidige editor-inhoud + de branding en afzender van het gekozen energielabel/verkoopteam. Voorbeeldwaarden worden automatisch ingevuld.</p>
              </div>
            )}
          </div>}
        </Card>
      </div>
    </div>
  );
};

// --- Partner mail-domeinen (Resend) -----------------------------------------
// Admin koppelt per energielabel een eigen verzenddomein, laat het in Resend
// aanmaken, toont de DNS-records en checkt de verificatiestatus. Pas een
// 'verified' domein mag als afzender gebruikt worden (zie resolveSender backend).
const MailDomainsPage = ({ token }) => {
  const [domains, setDomains] = useState([]);
  const [resendConfigured, setResendConfigured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ allowed_from: '', default_from_name: '', default_reply_to: '' });
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    if (!token) return;
    setLoading(true);
    fetch('/api/partner-mail-domains', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(d => { setDomains(d.domains || []); setResendConfigured(d.resendConfigured !== false); setError(''); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Synchroniseer de gemapte labels met de geverifieerde Resend-domeinen, zodat
  // de portaal-tabel klopt zonder shell-toegang (status/DNS/resend_domain_id).
  const syncFromResend = async () => {
    setSyncing(true); setError(''); setSuccess('');
    try {
      const r = await fetch('/api/partner-mail-domains/sync', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json(); if (!r.ok) throw new Error(`${d.error || `HTTP ${r.status}`}${d.detail ? ` — ${d.detail}` : ''}`);
      const rows = d.summary || [];
      const changed = rows.filter(s => s.action === 'created' || s.action === 'updated');
      const skipped = rows.filter(s => s.action === 'skipped' || s.action === 'error');
      setSuccess(`Sync klaar: ${changed.length} domein(en) bijgewerkt${skipped.length ? `, ${skipped.length} overgeslagen` : ''}.`);
      load();
    } catch (e) { setError(e.message); } finally { setSyncing(false); }
  };

  // Afzenderconfig per domein bewerken. Het eerste From-adres is de default-
  // afzender (klantenservice@<domein> indien leeg gelaten). PUT raakt alleen de
  // afzender-velden, niet domein/label/verificatie.
  const startEdit = (d) => {
    setEditId(d.id); setError(''); setSuccess('');
    setEditForm({
      allowed_from: (d.allowed_from_addresses || []).join(', '),
      default_from_name: d.default_from_name || '',
      default_reply_to: d.default_reply_to || '',
    });
  };
  const saveEdit = async (d) => {
    setBusyId(d.id); setError(''); setSuccess('');
    try {
      const allowed = editForm.allowed_from.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
      const r = await fetch(`/api/partner-mail-domains/${d.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ allowed_from_addresses: allowed, default_from_name: editForm.default_from_name, default_reply_to: editForm.default_reply_to }) });
      const j = await r.json(); if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setSuccess(`Afzender van ${d.domain} opgeslagen.`); setEditId(null); load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const inputCls = 'w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500';
  const statusBadge = (s) => {
    const map = { verified: 'bg-lime-500/15 text-lime-300 border-lime-500/40', pending: 'bg-amber-500/15 text-amber-300 border-amber-500/40', failed: 'bg-red-500/15 text-red-300 border-red-500/40' };
    const label = { verified: 'Geverifieerd', pending: 'In afwachting', failed: 'Mislukt' };
    return <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${map[s] || map.pending}`}>{label[s] || s}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <PageHeader title="Mail Domeinen" subtitle="Per energielabel hoort een geverifieerd Resend-verzenddomein. Klik op 'Sync vanuit Resend' om status en DNS-records op te halen. App-mails verzenden alleen vanaf een geverifieerd partnerdomein." />
        <div className="shrink-0 flex flex-col items-stretch gap-2">
          <button onClick={syncFromResend} disabled={syncing || !resendConfigured} title="Synchroniseer de bekende labels met de geverifieerde Resend-domeinen" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg text-sm font-medium disabled:opacity-40 flex items-center justify-center gap-2">
            <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />{syncing ? 'Synchroniseren…' : 'Sync vanuit Resend'}
          </button>
          <a href="https://resend.com/" target="_blank" rel="noopener noreferrer" title="Open het Resend-dashboard in een nieuw tabblad" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            <ExternalLink size={15} />Resend.com
          </a>
        </div>
      </div>
      {!resendConfigured && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-2.5 rounded-xl text-sm">Resend API-key is nog niet ingesteld (Instellingen → Mailserver). Synchroniseren werkt pas daarna.</div>}
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl text-sm">{error}</div>}
      {success && <div className="bg-lime-500/10 border border-lime-500/30 text-lime-400 px-4 py-2.5 rounded-xl text-sm flex items-center gap-2"><Check size={15} />{success}</div>}

      {loading ? <div className="flex justify-center py-8"><Spinner size={28} /></div> :
      domains.length === 0 ? <Card><p className="text-sm text-slate-500 py-4 text-center">Nog geen domeinen. Klik op "Sync vanuit Resend" om de geverifieerde domeinen op te halen.</p></Card> :
      <div className="space-y-4">
        {domains.map(d => <Card key={d.id}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2"><span className="text-base font-semibold text-slate-100 font-mono">{d.domain}</span>{statusBadge(d.status)}</div>
              <div className="text-xs text-slate-500 mt-0.5">Energielabel: <strong className="text-slate-300">{d.energy_label}</strong>{d.allowed_from_addresses?.length ? ` · From: ${d.allowed_from_addresses.join(', ')}` : ''}{d.default_reply_to ? ` · Reply-To: ${d.default_reply_to}` : ''}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => (editId === d.id ? setEditId(null) : startEdit(d))} disabled={busyId === d.id} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg text-xs font-medium disabled:opacity-40">{editId === d.id ? 'Sluiten' : 'Afzender bewerken'}</button>
            </div>
          </div>
          {editId === d.id && <div className="mt-3 pt-3 border-t border-slate-700/40 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <label className="block"><span className="text-xs text-slate-400">Afzenderadres</span>
              <input value={editForm.allowed_from} onChange={e => setEditForm(p => ({ ...p, allowed_from: e.target.value }))} placeholder={`klantenservice@${d.domain}`} className={`${inputCls} font-mono`} />
              <span className="text-[11px] text-slate-500">Eerste adres = standaard afzender. Leeg → <span className="font-mono">klantenservice@{d.domain}</span>. Meerdere toegestane adressen komma-gescheiden.</span></label>
            <label className="block"><span className="text-xs text-slate-400">Afzendernaam</span>
              <input value={editForm.default_from_name} onChange={e => setEditForm(p => ({ ...p, default_from_name: e.target.value }))} placeholder={d.energy_label} className={inputCls} /></label>
            <label className="block"><span className="text-xs text-slate-400">Reply-To (optioneel)</span>
              <input value={editForm.default_reply_to} onChange={e => setEditForm(p => ({ ...p, default_reply_to: e.target.value }))} placeholder="optioneel" className={`${inputCls} font-mono`} /></label>
            <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
              <button onClick={() => saveEdit(d)} disabled={busyId === d.id} className="px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg disabled:opacity-40 text-sm">{busyId === d.id ? 'Opslaan…' : 'Afzender opslaan'}</button>
            </div>
          </div>}
          {Array.isArray(d.dns_records) && d.dns_records.length > 0 && <div className="mt-3 overflow-x-auto">
            <p className="text-xs text-slate-400 mb-1.5">Zet deze DNS-records bij de domeinprovider van de partner:</p>
            <table className="w-full text-xs">
              <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Naam/Host</th><th className="py-1 pr-3">Waarde</th><th className="py-1 pr-3">Status</th></tr></thead>
              <tbody>{d.dns_records.map((rec, i) => <tr key={i} className="border-t border-slate-700/40 align-top">
                <td className="py-1 pr-3 font-mono text-slate-300">{rec.type}{rec.priority != null ? ` (prio ${rec.priority})` : ''}</td>
                <td className="py-1 pr-3 font-mono text-slate-300 break-all">{rec.name}</td>
                <td className="py-1 pr-3 font-mono text-slate-400 break-all max-w-[28rem]">{rec.value}</td>
                <td className="py-1 pr-3">{rec.status ? statusBadge(dnsRecordStatus(rec.status)) : '—'}</td>
              </tr>)}</tbody>
            </table>
          </div>}
        </Card>)}
      </div>}
    </div>
  );
};
// Resend record-status → ons badge-schema.
function dnsRecordStatus(s) { const v = String(s || '').toLowerCase(); return v === 'verified' ? 'verified' : v === 'failed' ? 'failed' : 'pending'; }

// --- Opportunity-stage beschrijvingen (admin) -------------------------------
// Beheer de tooltip-beschrijving per stage. Toont de stages die echt in de data
// voorkomen (bovenaan) + eventueel overige opgeslagen defaults. Per stage een
// bewerkbare beschrijving die als tooltip in elke "Opp. Stage"-kolom verschijnt.
// onSaved: laat de tooltips elders in de app (StageDescContext) meteen meebewegen.
// ─── Helpdesk tags (beheer, admin) ───────────────────────────────────────────
// Odoo kent honderden helpdesk-tags; de meeste horen niet in de keuzelijst van
// een nieuw ticket. Hier zet een beheerder per tag of hij kiesbaar is. Uit =
// niet meer in de keuzelijst bij het aanmaken; bestaande tickets tonen hun tags
// altijd. Server: GET/PUT /api/helpdesk/tag-config, filter in GET /api/helpdesk/tags.
// Eenmalige actie (11-09-2026): alle bestaande tickets op "Actie vereist van:
// Energy Together" zetten. Nieuwe tickets krijgen dat al bij het aanmaken. De
// waarde leeft in het portaal zelf (helpdesk_ticket_arb), niet in het
// bronsysteem: één klik, één query, meteen klaar.
const HdArbBulkKaart = ({ token }) => {
  const [stand, setStand] = useState(null);
  const [ookGesloten, setOokGesloten] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const H = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    fetch('/api/helpdesk/admin/arb-energy-together', { headers: H }).then((r) => (r.ok ? r.json() : null)).then((b) => { if (b) setStand(b); }).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    setFout(null); setBezig(true);
    try {
      const r = await fetch('/api/helpdesk/admin/arb-energy-together', { method: 'POST', headers: { ...H, 'Content-Type': 'application/json' }, body: JSON.stringify({ ook_gesloten: ookGesloten ? '1' : '0' }) });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
      setStand(b);
      toast.success(`${formatNumberRounded(b.laatste ? b.laatste.gezet : 0)} tickets op "Actie vereist van: Energy Together" gezet`);
    } catch (e) { setFout(e.message); } finally { setBezig(false); }
  };
  const tijd = (iso) => (iso ? new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) : '');
  const laatste = stand && stand.laatste;
  return (
    <Card className="!p-4">
      <h3 className="text-sm font-semibold text-slate-200">Actie vereist van: Energy Together — eenmalig voor alle tickets</h3>
      <p className="text-xs text-slate-400 mt-1">
        Nieuwe tickets staan bij het aanmaken al op "Actie vereist van: Energy Together". Met deze knop zet je ook alle bestaande tickets die daar nog niet op staan in één keer om. De waarde wordt in het portaal bewaard en gaat niet naar het bronsysteem; wijzigt het bronsysteem ze later zelf, dan telt die nieuwe waarde.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={start} disabled={bezig}
          className="px-3 py-1.5 rounded-lg bg-lime-500 hover:bg-lime-400 text-slate-900 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
          {bezig ? 'Bezig…' : 'Alle tickets omzetten'}
        </button>
        <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={ookGesloten} disabled={bezig} onChange={(e) => setOokGesloten(e.target.checked)} className="accent-lime-500" />
          ook gesloten tickets (standaard enkel open)
        </label>
        {laatste && (
          <span className="text-xs text-slate-400 tabular-nums">
            Laatste keer: <span className="text-lime-300">{formatNumberRounded(laatste.gezet)} tickets gezet</span>{laatste.ookGesloten ? ' (incl. gesloten)' : ''} · {tijd(laatste.klaar)}{laatste.door ? ` · ${laatste.door}` : ''}
          </span>
        )}
      </div>
      {fout && <p className="mt-2 text-xs text-red-300">{fout}</p>}
    </Card>
  );
};

// Het kleurenpalet van Odoo-tags (kleurindex 0–11), zoals Odoo 17 ze tekent.
// 0 = geen kleur. Dezelfde index gaat naar PATCH /helpdesk/tags/{id} {color}.
const ODOO_TAG_KLEUREN = [
  { i: 0, naam: 'Geen kleur', hex: null }, { i: 1, naam: 'Rood', hex: '#F06050' }, { i: 2, naam: 'Oranje', hex: '#F4A460' }, { i: 3, naam: 'Geel', hex: '#F7CD1F' },
  { i: 4, naam: 'Lichtblauw', hex: '#6CC1ED' }, { i: 5, naam: 'Donkerpaars', hex: '#814968' }, { i: 6, naam: 'Zalm', hex: '#EB7E7F' }, { i: 7, naam: 'Blauwgroen', hex: '#2C8397' },
  { i: 8, naam: 'Donkerblauw', hex: '#475577' }, { i: 9, naam: 'Framboos', hex: '#D6145F' }, { i: 10, naam: 'Groen', hex: '#30C381' }, { i: 11, naam: 'Paars', hex: '#9365B8' },
];
const odooTagHex = (i) => (ODOO_TAG_KLEUREN.find((k) => k.i === Number(i)) || ODOO_TAG_KLEUREN[0]).hex;

const HelpdeskTagsPage = ({ token }) => {
  const [tags, setTags] = useState([]);
  // Hernoemen: welk id staat open, en de tekst in het veld.
  const [bewerkId, setBewerkId] = useState(null);
  const [bewerkNaam, setBewerkNaam] = useState('');
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [zoek, setZoek] = useState('');
  const [filter, setFilter] = useState('alle'); // 'alle' | 'aan' | 'uit'
  const [bezig, setBezig] = useState(false);
  const H = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLaden(true); setFout(null);
    try {
      const r = await fetch('/api/helpdesk/tag-config', { headers: H });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
      setTags((b.tags || []).slice().sort((a, c) => a.name.localeCompare(c.name, 'nl')));
    } catch (e) { setFout(e.message); }
    finally { setLaden(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const bewaar = async (lijst) => {
    setBezig(true); setFout(null);
    // Meteen tonen, daarna bevestigen; bij een fout de lijst opnieuw laden.
    setTags((cur) => cur.map((t) => { const w = lijst.find((x) => x.id === t.id); return w ? { ...t, enabled: w.enabled } : t; }));
    try {
      const r = await fetch('/api/helpdesk/tag-config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...H }, body: JSON.stringify({ tags: lijst }) });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
      toast.success(lijst.length === 1 ? `Tag "${lijst[0].name}" ${lijst[0].enabled ? 'kiesbaar' : 'verborgen'}.` : `${lijst.length} tags bijgewerkt.`);
    } catch (e) { setFout(e.message); load(); }
    finally { setBezig(false); }
  };

  // Naam of kleur wijzigen in het bronsysteem (PATCH /api/helpdesk/tags/:id).
  // Tags zijn globaal: een nieuwe naam is meteen overal zichtbaar, ook in Odoo.
  const wijzigTag = async (t, patch) => {
    setBezig(true); setFout(null);
    try {
      const r = await fetch(`/api/helpdesk/tags/${t.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...H }, body: JSON.stringify(patch) });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
      setTags((cur) => cur.map((x) => (x.id === t.id ? { ...x, name: b && b.name ? b.name : (patch.name || x.name), color: b && Number.isInteger(b.color) ? b.color : (patch.color !== undefined ? patch.color : x.color) } : x)).sort((a, c) => a.name.localeCompare(c.name, 'nl')));
      toast.success(patch.name !== undefined ? `Tag hernoemd naar "${patch.name}".` : `Kleur van "${t.name}" gewijzigd.`);
      setBewerkId(null);
    } catch (e) { setFout(e.message); }
    finally { setBezig(false); }
  };
  const startBewerken = (t) => { setBewerkId(t.id); setBewerkNaam(t.name); };
  const bewaarNaam = (t) => { const n = bewerkNaam.trim(); if (!n || n === t.name) { setBewerkId(null); return; } wijzigTag(t, { name: n }); };

  const term = zoek.trim().toLowerCase();
  const zichtbaar = tags.filter((t) => (filter === 'alle' || (filter === 'aan') === t.enabled) && (!term || t.name.toLowerCase().includes(term)));
  const aantalAan = tags.filter((t) => t.enabled).length;

  return (
    <div className="space-y-6">
      <PageHeader title="Helpdesk tags" subtitle="Welke tags kiesbaar zijn bij het aanmaken van een ticket, en hun naam en kleur in het bronsysteem. Bestaande tickets tonen hun tags altijd." />
      {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{fout}</div>}
      <Card className="!p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative"><Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" /><input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek een tag…" className="pl-7 pr-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-sm text-slate-200 w-[280px]" /></div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200">
            <option value="alle">Alle tags</option><option value="aan">Kiesbaar</option><option value="uit">Verborgen</option>
          </select>
          <span className="text-xs text-slate-400">{aantalAan} van {tags.length} kiesbaar{term || filter !== 'alle' ? ` · ${zichtbaar.length} getoond` : ''}</span>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" disabled={bezig || !zichtbaar.length} onClick={() => bewaar(zichtbaar.filter((t) => !t.enabled).map((t) => ({ id: t.id, name: t.name, enabled: true })))} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg">Getoonde aan</button>
            <button type="button" disabled={bezig || !zichtbaar.length} onClick={() => bewaar(zichtbaar.filter((t) => t.enabled).map((t) => ({ id: t.id, name: t.name, enabled: false })))} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg">Getoonde uit</button>
          </div>
        </div>
        {laden ? <ListSkeleton rows={10} /> : zichtbaar.length === 0 ? <EmptyState message={tags.length ? 'Geen tags voor deze zoekopdracht.' : 'Geen tags gevonden in het bronsysteem.'} /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
            <thead className="bg-slate-800 text-slate-400 text-xs"><tr>
              <th className="px-3 py-2 font-medium text-left w-24">Kiesbaar</th>
              <th className="px-3 py-2 font-medium text-left">Tag</th>
              <th className="px-3 py-2 font-medium text-left w-44">Kleur</th>
              <th className="px-3 py-2 font-medium text-right whitespace-nowrap">Tickets met deze tag</th>
            </tr></thead>
            <tbody>
              {zichtbaar.map((t) => (
                <tr key={t.id} className={`border-t border-slate-700/40 ${t.enabled ? '' : 'opacity-60'}`}>
                  <td className="px-3 py-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={t.enabled} disabled={bezig} onChange={(e) => bewaar([{ id: t.id, name: t.name, enabled: e.target.checked }])} className="w-4 h-4 accent-lime-500" />
                      <span className={`text-xs ${t.enabled ? 'text-lime-300' : 'text-slate-500'}`}>{t.enabled ? 'aan' : 'uit'}</span>
                    </label>
                  </td>
                  <td className="px-3 py-2 text-slate-200">
                    {bewerkId === t.id ? (
                      <span className="inline-flex items-center gap-1.5">
                        <input autoFocus value={bewerkNaam} onChange={(e) => setBewerkNaam(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') bewaarNaam(t); if (e.key === 'Escape') setBewerkId(null); }} maxLength={80} disabled={bezig}
                          className="px-2 py-1 bg-slate-700 border border-slate-500 rounded text-sm text-slate-100 w-[260px] focus:outline-none focus:border-lime-500" />
                        <button type="button" disabled={bezig} onClick={() => bewaarNaam(t)} className="p-1 rounded text-lime-300 hover:bg-slate-700" title="Bewaren (Enter)"><Check size={14} /></button>
                        <button type="button" disabled={bezig} onClick={() => setBewerkId(null)} className="p-1 rounded text-slate-400 hover:bg-slate-700" title="Annuleren (Esc)"><X size={14} /></button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-slate-600/60" style={{ backgroundColor: odooTagHex(t.color) || 'transparent' }} />
                        {t.name}<span className="text-slate-600 text-xs">#{t.id}</span>
                        <button type="button" disabled={bezig} onClick={() => startBewerken(t)} className="p-1 rounded text-slate-500 hover:text-slate-200 hover:bg-slate-700" title="Tag hernoemen (geldt overal, ook in het bronsysteem)"><Pencil size={13} /></button>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select value={Number.isInteger(t.color) ? t.color : 0} disabled={bezig} onChange={(e) => wijzigTag(t, { color: Number(e.target.value) })} className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200 w-[132px]" title="Kleur van de tag in het bronsysteem" style={odooTagHex(t.color) ? { borderLeft: `4px solid ${odooTagHex(t.color)}` } : undefined}>
                      {ODOO_TAG_KLEUREN.map((k) => <option key={k.i} value={k.i}>{k.naam}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{t.tickets ? t.tickets.toLocaleString('nl-BE') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
      <HdArbBulkKaart token={token} />
    </div>
  );
};

const OppStagesPage = ({ token, onSaved }) => {
  const [descriptions, setDescriptions] = useState({});
  const [existing, setExisting] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const load = () => {
    if (!token) return;
    setLoading(true);
    fetch('/api/opportunity-stages', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(d => { setDescriptions(d.descriptions || {}); setExisting(d.existingStages || []); setError(''); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Rijen = unie van stages-in-data en opgeslagen beschrijvingen. Data-stages eerst.
  const rows = useMemo(() => {
    const map = new Map();
    for (const s of existing) map.set(s.key, { key: s.key, label: s.label, count: s.count, inData: true, description: (descriptions[s.key] && descriptions[s.key].description) || '' });
    for (const key of Object.keys(descriptions)) {
      if (map.has(key)) continue;
      map.set(key, { key, label: descriptions[key].label || key, count: 0, inData: false, description: descriptions[key].description || '' });
    }
    return [...map.values()].sort((a, b) => (b.inData - a.inData) || a.label.localeCompare(b.label));
  }, [existing, descriptions]);

  const save = async (row) => {
    setBusyKey(row.key); setError(''); setSavedKey('');
    try {
      const description = drafts[row.key] !== undefined ? drafts[row.key] : row.description;
      const r = await fetch(`/api/opportunity-stages/${encodeURIComponent(row.key)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ label: row.label, description }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setSavedKey(row.key);
      setDescriptions(prev => ({ ...prev, [row.key]: { label: row.label, description } }));
      if (onSaved) onSaved(row.key, { label: row.label, description });
    } catch (e) { setError(e.message); } finally { setBusyKey(''); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Opp. Stage beschrijvingen" subtitle="Beschrijving per opportunity-stage. Deze verschijnt als tooltip wanneer een gebruiker met de muis over een stage in de tabellen gaat. Varianten (cu./st.) delen dezelfde beschrijving." />
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl text-sm">{error}</div>}
      {loading ? <div className="flex justify-center py-10"><Spinner size={28} /></div> :
      <Card className="p-0 overflow-hidden">
        {/* resp-card: op mobiel kaarten. De Card heeft overflow-hidden, dus zonder
            dit werden de rechterkolommen simpelweg afgeknipt. */}
        <table className="w-full resp-card">
          <thead><tr className="border-b border-slate-700/50">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Stage</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Beschrijving (tooltip)</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>{rows.map(row => {
            const val = drafts[row.key] !== undefined ? drafts[row.key] : row.description;
            const dirty = drafts[row.key] !== undefined && drafts[row.key] !== row.description;
            return <tr key={row.key} className="border-b border-slate-700/30 align-top">
              <td className="px-4 py-3 text-sm text-slate-200 whitespace-nowrap">
                <div className="font-medium">{row.label}</div>
                <div className="text-xs text-slate-500">{row.inData ? `${row.count} order(s)` : 'niet in huidige data'}</div>
              </td>
              <td className="px-4 py-3">
                <textarea value={val} onChange={e => setDrafts(p => ({ ...p, [row.key]: e.target.value }))} rows={2} placeholder="Geen beschrijving — voeg er een toe" className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500" />
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => save(row)} disabled={busyKey === row.key || !dirty} className="px-3 py-1.5 bg-lime-500 hover:bg-lime-400 text-slate-900 text-xs font-semibold rounded-lg disabled:opacity-40">{busyKey === row.key ? '…' : savedKey === row.key && !dirty ? 'Opgeslagen' : 'Opslaan'}</button>
              </td>
            </tr>;
          })}
          {rows.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-500">Geen stages gevonden.</td></tr>}
          </tbody>
        </table>
      </Card>}
    </div>
  );
};

// --- Customer Tags: admin-configuratie (kaart op Revenue → Admin) -----------
// De klant-tags uit Odoo/ET, geconfigureerd per UNIEKE tag; geldt voor alle
// energielabels. Een order draagt zijn tags als komma-gescheiden string, maar de
// config kent enkel losse tags — van drie tags op één order kunnen er dus twee
// zichtbaar zijn en één niet.
//
// Drie onafhankelijke vlaggen:
//   · Visible for Energy Together — admin + ET-dossierbeheerder, standaard AAN
//   · Visible for partners        — master/teamleader/verkoper, standaard UIT
//   · Fee Allowed                 — standaard AAN; staat één tag van de klant op
//     No, dan krijgt die klant geen vergoeding en verschijnt hij in "Geblokkeerd
//     door Customer Tag" hieronder.
// Zichtbaarheid staat per publiek apart zodat een tag die de vergoeding blokkeert
// nooit onzichtbaar is voor wie moet uitleggen waarom een klant niets krijgt.
const JANEE_OPTIES = [{ id: 'all', label: 'Alle' }, { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }];
// Eén filterknopgroep per kolom. `waarde` is 'all' | 'yes' | 'no'.
const JaNeeFilter = ({ label, waarde, onChange }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-xs text-slate-400">{label}</span>
    <div className="inline-flex rounded-lg border border-slate-600/50 overflow-hidden">
      {JANEE_OPTIES.map(o => (
        <button key={o.id} type="button" onClick={() => onChange(o.id)}
          className={`px-2 py-1 text-xs transition-colors ${waarde === o.id ? 'bg-lime-500 text-slate-900 font-semibold' : 'bg-slate-700/40 text-slate-300 hover:bg-slate-700'}`}>
          {o.label}
        </button>
      ))}
    </div>
  </div>
);
// true → 'yes', false → 'no'; 'all' matcht altijd.
const matchtJaNee = (filter, waarde) => filter === 'all' || (filter === 'yes') === (waarde !== false);

const CustomerTagsConfigCard = ({ token, partnerTagAll = [], onPartnerTagsChanged }) => {
  const [partnerTagError, setPartnerTagError] = useState('');
  const [partnerTagSearch, setPartnerTagSearch] = useState('');
  const [filterEt, setFilterEt] = useState('all');
  const [filterPartners, setFilterPartners] = useState('all');
  const [filterFee, setFilterFee] = useState('all');

  // patch = { visible?, visible_et?, fee_allowed? } — enkel meegegeven velden wijzigen.
  const togglePartnerTag = async (tag, patch) => {
    setPartnerTagError('');
    try {
      const r = await fetch(`/api/partner-tags/${encodeURIComponent(tag)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      onPartnerTagsChanged?.();
    } catch (err) { setPartnerTagError(err.message); }
  };

  const zoek = partnerTagSearch.trim().toLowerCase();
  const zichtbareRijen = partnerTagAll.filter(t =>
    t.tag.toLowerCase().includes(zoek)
    && matchtJaNee(filterEt, t.visible_et)
    && matchtJaNee(filterPartners, t.visible)
    && matchtJaNee(filterFee, t.fee_allowed));
  const filtersActief = zoek !== '' || filterEt !== 'all' || filterPartners !== 'all' || filterFee !== 'all';

  return (
    <Card className="!p-4">
      <h3 className="text-base font-semibold text-slate-100 mb-1">CUSTOMER TAGS</h3>
      <p className="text-xs text-slate-400 mb-3">
        Configuratie per unieke customer tag, voor alle energielabels samen.{' '}
        <strong>Visible for Energy Together</strong>: tonen aan admin en ET-dossierbeheerder (standaard aan).{' '}
        <strong>Visible for partners</strong>: tonen aan master, teamleader en verkoper (standaard verborgen).{' '}
        Beide gelden voor Customer 360° en de Orders-tabel, en werken per losse tag — van drie tags op één order kunnen er dus twee getoond worden.{' '}
        <strong>Fee Allowed</strong>: staat één tag van de klant op No, dan krijgt die klant geen partnervergoeding. Hij telt dan niet mee bij Goede klanten of Wanbetalers en verschijnt in “Geblokkeerd door Customer Tag” hieronder.
      </p>
      {partnerTagError && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm mb-3">{partnerTagError}</div>}
      {partnerTagAll.length === 0 ? (
        <p className="text-sm text-slate-500">Nog geen customer tags gevonden — orders worden geladen.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
            <input type="text" value={partnerTagSearch} onChange={e => setPartnerTagSearch(e.target.value)} placeholder="Zoek tag…" className="w-full sm:w-56 px-3 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500" />
            <JaNeeFilter label="Visible for Energy Together" waarde={filterEt} onChange={setFilterEt} />
            <JaNeeFilter label="Visible for partners" waarde={filterPartners} onChange={setFilterPartners} />
            <JaNeeFilter label="Fee Allowed" waarde={filterFee} onChange={setFilterFee} />
            {filtersActief && (
              <button type="button" onClick={() => { setPartnerTagSearch(''); setFilterEt('all'); setFilterPartners('all'); setFilterFee('all'); }}
                className="px-2 py-1 text-xs text-slate-300 bg-slate-700/40 hover:bg-slate-700 rounded-lg">Wis filters</button>
            )}
          </div>
          <div className="border border-slate-700/50 rounded-xl overflow-hidden max-h-72 overflow-y-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-sm resp-card">
                <thead className="bg-slate-700/40 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs text-slate-300 font-medium">Customer Tag</th>
                    <th className="text-center px-4 py-2 text-xs text-slate-300 font-medium">Visible for Energy&nbsp;Together</th>
                    <th className="text-center px-4 py-2 text-xs text-slate-300 font-medium">Visible for partners</th>
                    <th className="text-center px-4 py-2 text-xs text-slate-300 font-medium">Fee Allowed</th>
                  </tr>
                </thead>
                <tbody>
                  {zichtbareRijen.map(t => (
                    <tr key={t.tag} className={`border-t border-slate-700/40 ${t.fee_allowed === false ? 'bg-red-900/10' : ''}`}>
                      {/* data-label: op mobiel wordt deze tabel een gestapelde kaart
                          (resp-card) en haalt de CSS het label uit dit attribuut —
                          zonder zou je drie naamloze vinkjes onder elkaar zien. */}
                      <td className="px-4 py-2 text-slate-200" data-label="Customer Tag">{t.tag}</td>
                      <td className="px-4 py-2 text-center" data-label="Visible for Energy Together">
                        <input type="checkbox" checked={t.visible_et !== false} onChange={e => togglePartnerTag(t.tag, { visible_et: e.target.checked })} className="w-4 h-4 accent-lime-500 cursor-pointer" />
                      </td>
                      <td className="px-4 py-2 text-center" data-label="Visible for partners">
                        <input type="checkbox" checked={t.visible} onChange={e => togglePartnerTag(t.tag, { visible: e.target.checked })} className="w-4 h-4 accent-lime-500 cursor-pointer" />
                      </td>
                      <td className="px-4 py-2 text-center" data-label="Fee Allowed">
                        <input type="checkbox" checked={t.fee_allowed !== false} onChange={e => togglePartnerTag(t.tag, { fee_allowed: e.target.checked })} className="w-4 h-4 accent-lime-500 cursor-pointer" />
                      </td>
                    </tr>
                  ))}
                  {zichtbareRijen.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6"><TableEmpty message="Geen customer tags binnen deze filters." /></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            {zichtbareRijen.length} van {partnerTagAll.length} tags getoond ·{' '}
            {partnerTagAll.filter(t => t.visible_et !== false).length} zichtbaar voor Energy Together ·{' '}
            {partnerTagAll.filter(t => t.visible).length} zichtbaar voor partners ·{' '}
            {partnerTagAll.filter(t => t.fee_allowed === false).length} tag(s) zonder fee (Fee Allowed = No).
          </p>
        </>
      )}
    </Card>
  );
};

// --- Jobs-module: admin-beheer van geplande data-imports --------------------
// Toont per databron de full/delta/refresh-jobs met hun cron-schema, aan/uit,
// laatste run + historiek, en een "Nu uitvoeren"-knop. Backend: routes/jobs.js +
// jobs/scheduler.js. Enkel admin (rol-gated in renderPage + Beheer-menu).
const JOB_CRON_PRESETS = [
  { label: 'Elke 5 minuten', cron: '*/5 * * * *' },
  { label: 'Elke 15 minuten', cron: '*/15 * * * *' },
  { label: 'Elk uur', cron: '0 * * * *' },
  { label: 'Elke 6 uur', cron: '0 */6 * * *' },
  { label: '2× per dag (06:00, 14:00)', cron: '0 6,14 * * *' },
  { label: 'Nachtelijk 02:00', cron: '0 2 * * *' },
  { label: 'Nachtelijk 03:00', cron: '0 3 * * *' },
  { label: 'Nachtelijk 04:00', cron: '0 4 * * *' },
  { label: 'Wekelijks — zaterdag 04:00', cron: '0 4 * * 6' },
  { label: 'Wekelijks — zondag 04:00', cron: '0 4 * * 0' },
];
const JOB_MODE_BADGE = {
  full: { label: 'Full', cls: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' },
  delta: { label: 'Delta', cls: 'bg-teal-500/15 text-teal-300 border-teal-500/30' },
  refresh: { label: 'Refresh', cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
};
const jobFmtDateTime = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };
const jobFmtDuration = (ms) => { if (ms == null) return ''; if (ms < 1000) return `${ms} ms`; const s = ms / 1000; if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`; return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`; };
// Exact tijdstip van een wijziging: DD/MM/YYYY UU:MM:SS in BRUSSELSE tijd. Niet
// de tijdzone van de browser — de databank en de jobs rekenen in Brussel, dus een
// gebruiker die van elders kijkt hoort hetzelfde te zien als zijn collega hier.
const fmtMomentBE = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('nl-BE', {
      timeZone: 'Europe/Brussels',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    });
  } catch { return '—'; }
};

const JobStatusBadge = ({ run, running }) => {
  if (running || run?.status === 'running') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />Bezig…</span>;
  if (!run) return <span className="text-[11px] text-slate-500">nog niet gedraaid</span>;
  if (run.status === 'success') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><Check size={11} />Gelukt</span>;
  if (run.status === 'error') return <span title={run.message || ''} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-500/15 text-red-300 border border-red-500/30"><AlertCircle size={11} />Fout</span>;
  return <span className="text-[11px] text-slate-400">{run.status}</span>;
};

// Doelen van de job "Revenue — partnervergoeding mailen (zip)": per rij een
// energielabel, of een (energielabel, verkoopteam). De job bouwt per doel een zip
// met precies die filtering en mailt hem naar de ontvangers die voor dat doel
// ingesteld zijn (Revenue → Admin → Ontvanger Mail Partnervergoeding). Een doel
// zonder ontvanger staat hier meteen als waarschuwing: de job slaat het over.
const JobFeeMailDoelen = ({ token, job, onSaved }) => {
  const [structuur, setStructuur] = useState(null);
  const [ontvangers, setOntvangers] = useState([]);
  const [label, setLabel] = useState('');
  const [team, setTeam] = useState('');
  const [bezig, setBezig] = useState(false);
  const doelen = (job.params && Array.isArray(job.params.targets)) ? job.params.targets : [];
  const H = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let weg = false;
    fetch('/api/reference/sales-structure', { headers: H }).then((r) => r.json()).then((d) => { if (!weg) setStructuur(d); }).catch(() => {});
    fetch('/api/revenue/fee-mail/recipients', { headers: H }).then((r) => r.json()).then((d) => { if (!weg) setOntvangers(d.recipients || []); }).catch(() => {});
    return () => { weg = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Belvus en de Odoo-entiteit horen niet in Revenue (zelfde regel als de filter).
  const labels = useMemo(() => (structuur?.energy_labels || []).filter((l) => !/^energy together/i.test(l) && l.toLowerCase() !== 'belvus energie'), [structuur]);
  const teams = label ? (structuur?.teams_by_label?.[label] || []) : [];
  // Teamrij van dit doel, of de labelrij van hetzelfde label (die wint de job ook).
  const heeftOntvanger = (d) => ontvangers.some((o) => o.energy_label === d.energy_label && (!o.sales_team || o.sales_team === d.sales_team));

  const bewaar = async (targets) => {
    setBezig(true);
    try {
      const r = await fetch(`/api/jobs/${encodeURIComponent(job.key)}/params`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...H }, body: JSON.stringify({ params: { targets } }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      onSaved?.(d);
      toast.success('Doelen bijgewerkt');
    } catch (e) { toast.error(e.message); }
    finally { setBezig(false); }
  };
  const voegToe = () => {
    if (!label) return;
    const nieuw = { energy_label: label, sales_team: team || null };
    if (doelen.some((d) => d.energy_label === nieuw.energy_label && (d.sales_team || null) === nieuw.sales_team)) { toast.info('Dat doel staat er al.'); return; }
    bewaar([...doelen, nieuw]);
    setTeam('');
  };
  const verwijder = (i) => bewaar(doelen.filter((_, j) => j !== i));

  return (
    <div className="mt-2 border border-slate-700/60 rounded-lg p-2.5 bg-slate-800/40 max-w-xl">
      <div className="text-[11px] font-semibold text-slate-300 uppercase tracking-wide mb-1.5">Doelen (energielabel of verkoopteam)</div>
      {doelen.length === 0 ? <div className="text-xs text-slate-500 mb-2">Nog geen doelen — de job verstuurt niets.</div> : (
        <ul className="space-y-1 mb-2">
          {doelen.map((d, i) => {
            const ok = heeftOntvanger(d);
            return (
              <li key={`${d.energy_label}|${d.sales_team || ''}`} className="flex items-center gap-2 text-xs">
                <span className="text-slate-200">{d.energy_label}</span>
                <span className="text-slate-400">{d.sales_team ? `· ${d.sales_team}` : '· alle verkoopteams'}</span>
                {!ok && <span className="px-1.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px]" title="Stel de ontvanger in onder Revenue → Admin → Ontvanger Mail Partnervergoeding; anders slaat de job dit doel over.">geen ontvanger</span>}
                <button type="button" onClick={() => verwijder(i)} disabled={bezig} title="Doel verwijderen" className="ml-auto text-slate-500 hover:text-red-400"><X size={13} /></button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <select value={label} onChange={(e) => { setLabel(e.target.value); setTeam(''); }} className="px-2 py-1 bg-slate-700/50 border border-slate-600/50 rounded text-xs text-slate-200">
          <option value="">Energielabel…</option>
          {labels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={team} onChange={(e) => setTeam(e.target.value)} disabled={!label} className="px-2 py-1 bg-slate-700/50 border border-slate-600/50 rounded text-xs text-slate-200 disabled:opacity-50">
          <option value="">Alle verkoopteams</option>
          {teams.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="button" onClick={voegToe} disabled={!label || bezig} className="inline-flex items-center gap-1 px-2 py-1 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-slate-900 text-xs font-semibold rounded"><Plus size={12} />Doel toevoegen</button>
      </div>
      <div className="text-[11px] text-slate-500 mt-1.5">Per doel één zip met enkel de data van dat energielabel of verkoopteam, naar de ontvanger die daarvoor ingesteld is. Nooit naar een ander adres.</div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Notifications (17-09-2026)
//
// Subtiele, positieve feedback bij commercieel resultaat: een korte toast en
// sound bij een nieuwe sale, een mijlpaalkaart bij iets uitzonderlijks, en de tab
// Sales → Prestaties met doel, records en reeks. De server detecteert
// (backend/src/util/salesEvents.js); hier wordt alleen getoond.
//
// Regels die hier hard in de code zitten:
//  - Wat er bij het LADEN al stond, wordt nooit als toast of geluid afgespeeld —
//    alleen wat binnenkomt terwijl de app open staat. Dus geen geluid bij page
//    load, refresh of login.
//  - Alleen positieve gebeurtenissen; er bestaat geen negatief event.
//  - De beheerder bepaalt wat bestaat (/api/gamification/mij), de gebruiker wat hij
//    ervan merkt (user_ui_prefs `gami.geluid` en `gami.kanalen` — server-side
//    bewaard, dus één keer instellen geldt op elk toestel).
// ═══════════════════════════════════════════════════════════════════════════
const GAMI_ICOON = {
  'sale.new': TrendingUp, 'customer.new': UserPlus, 'connection.new': Plug,
  'contract.biggest_month': Award, 'customer.biggest_ever': Trophy, 'customers.milestone': Trophy,
  'volume.milestone': Zap, 'sales.record_month': Trophy, 'volume.record_month': Zap,
  'sales.growth': TrendingUp, 'sales.streak': Flame, 'personal.record': Award,
  'goal.reached': Target, 'fee.threshold': Coins, 'month.closed': CalendarDays,
};
const GAMI_POLL_MS = 60000;
const gamiHeaders = (token) => ({ Authorization: `Bearer ${token}` });
// De voorkeur zoals ze NU geldt. useUiPref spiegelt elke wijziging meteen naar
// localStorage; hier lezen houdt de runtime in de pas met de Account-pagina zonder
// dat de twee hooks elkaar moeten kennen.
const gamiPref = (sleutel) => { try { const v = localStorage.getItem(`uiPref:${sleutel}`); return v == null ? null : JSON.parse(v); } catch (_) { return null; } };
const gamiTijd = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' }); };
const gamiDagLabel = (dag) => {
  if (!dag) return '';
  if (dag === vandaagIso()) return 'Vandaag';
  const gisteren = new Date(); gisteren.setDate(gisteren.getDate() - 1);
  if (dag === vandaagIso(gisteren)) return 'Gisteren';
  return new Date(`${dag}T12:00:00`).toLocaleDateString('nl-BE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Browsermelding voor een tabblad op de achtergrond. Via de service worker waar die
// er is (verplicht op Android), anders de gewone Notification.
async function gamiBrowserMelding({ titel, tekst }) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
    const icoon = (document.querySelector('link[rel="apple-touch-icon"]') || document.querySelector('link[rel~="icon"]') || {}).href;
    const opties = { body: tekst, icon: icoon, badge: icoon, tag: 'gami-hoogtepunt', renotify: false, silent: true };
    const reg = navigator.serviceWorker && await navigator.serviceWorker.getRegistration();
    if (reg && reg.showNotification) { await reg.showNotification(titel, opties); return true; }
    const n = new Notification(titel, opties);
    n.onclick = () => { try { window.focus(); n.close(); } catch (_) { /* niets */ } };
    return true;
  } catch (_) { return false; }
}

// Eén runtime per ingelogde sessie (gemount in de app-root).
function useGamification(token, user) {
  const [mij, setMij] = useState(null);
  const [items, setItems] = useState([]);
  const [lastSeenId, setLastSeenId] = useState(0);
  const [volgende, setVolgende] = useState(null);
  const [kaart, setKaart] = useState(null);
  const basis = useRef(0);
  const basisKlaar = useRef(false);   // pas NA een geslaagde eerste lading mag iets "nieuw" heten
  const mijRef = useRef(null);
  const toastTijden = useRef([]);
  const userSleutel = user ? `${user.id}|${user.role}|${user.energyLabel || ''}|${user.salesTeam || ''}|${user.sellerName || ''}` : '';

  // Geluid mag pas na een eerste klik of toets (autoplay-beleid).
  useEffect(() => {
    const aan = () => ontgrendelAudio();
    window.addEventListener('pointerdown', aan, { once: true });
    window.addEventListener('keydown', aan, { once: true });
    return () => { window.removeEventListener('pointerdown', aan); window.removeEventListener('keydown', aan); };
  }, []);

  const presenteer = useCallback((events, { proef = false } = {}) => {
    const m = mijRef.current;
    if (!m || !m.actief) return;
    const kanalen = leesKanalen(gamiPref('gami.kanalen'));
    const b = gamiBundel(events, { kanalen: proef ? { ...kanalen, pauzeTot: null, categorieen: {}, toasts: true } : kanalen });
    if (document.visibilityState === 'visible') {
      for (const t of b.toasts) {
        const p = gamiPlafond(toastTijden.current, (m.plafonds && m.plafonds.toastsPerUur) || 6, 3600000);
        toastTijden.current = p.tijden;
        if (!p.mag && !proef) break;   // boven het plafond: staat wel in Hoogtepunten
        const eerste = events.find((e) => t.ids.includes(e.id));
        (t.tier >= 2 ? toast.prestatie : toast.sale)(t.tekst, { title: t.titel, icon: t.ids.length === 1 && eerste ? GAMI_ICOON[eerste.key] : undefined });
      }
      if (b.kaart) {
        setKaart({ ...b.kaart, confetti: m.confetti, n: Date.now() });
        if (kanalen.trillen && navigator.vibrate) { try { navigator.vibrate(60); } catch (_) { /* niets */ } }
      }
      if (b.sound) gamiSpeel(b.sound, effectiefVolume(m, gamiPref('gami.geluid')), { voorbeeld: proef });
    } else if (kanalen.browser && m.browserBeschikbaar && b.browser.length) {
      let tijden = [];
      try { tijden = JSON.parse(localStorage.getItem('gami.browserTijden') || '[]'); } catch (_) { tijden = []; }
      const p = gamiPlafond(tijden, (m.plafonds && m.plafonds.browserPerDag) || 5, 86400000);
      if (p.mag) {
        try { localStorage.setItem('gami.browserTijden', JSON.stringify(p.tijden)); } catch (_) { /* niets */ }
        gamiBrowserMelding(b.browser[0]);
      }
    }
  }, []);

  // Start: instellingen + wat er al staat (STIL — dit is "sinds je laatste bezoek").
  useEffect(() => {
    setMij(null); setItems([]); setKaart(null); basis.current = 0; basisKlaar.current = false; mijRef.current = null;
    if (!token || !userSleutel) return undefined;
    let weg = false;
    (async () => {
      try {
        const m = await fetch('/api/gamification/mij', { headers: gamiHeaders(token) }).then((r) => (r.ok ? r.json() : { actief: false }));
        if (weg) return;
        mijRef.current = m; setMij(m);
        if (!m.actief) return;
        setLastSeenId(m.lastSeenId || 0);
        const f = await fetch('/api/gamification/feed', { headers: gamiHeaders(token) }).then((r) => (r.ok ? r.json() : null));
        if (weg || !f) return;
        setItems(f.items || []); setVolgende(f.next_cursor || null);
        basis.current = f.maxId || 0;
        basisKlaar.current = true;
      } catch (_) { /* best-effort: zonder feed werkt de rest van het portaal gewoon */ }
    })();
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userSleutel]);

  // Poll: alleen wat NA het laden binnenkomt, wordt live getoond.
  useEffect(() => {
    if (!token || !mij || !mij.actief) return undefined;
    let bezig = false;
    const haal = async () => {
      if (bezig || !navigator.onLine) return;
      bezig = true;
      try {
        // Mislukte de eerste lading, dan is dit de stille basis — anders zou alles
        // van de laatste 60 dagen ineens als "nieuw" afgaan.
        if (!basisKlaar.current) {
          const eerste = await fetch('/api/gamification/feed', { headers: gamiHeaders(token) }).then((r) => (r.ok ? r.json() : null));
          if (eerste) { setItems(eerste.items || []); setVolgende(eerste.next_cursor || null); basis.current = eerste.maxId || 0; basisKlaar.current = true; }
          return;
        }
        const f = await fetch(`/api/gamification/feed?after=${basis.current}`, { headers: gamiHeaders(token) }).then((r) => (r.ok ? r.json() : null));
        if (f && f.items && f.items.length) {
          basis.current = Math.max(basis.current, f.maxId || 0);
          setItems((oud) => [...f.items, ...oud.filter((o) => !f.items.some((n) => n.id === o.id))]);
          presenteer(f.items);
        }
      } catch (_) { /* volgende ronde */ } finally { bezig = false; }
    };
    const t = setInterval(haal, GAMI_POLL_MS);
    const opZicht = () => { if (document.visibilityState === 'visible') haal(); };
    document.addEventListener('visibilitychange', opZicht);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', opZicht); };
  }, [token, mij, presenteer]);

  // Proefmelding vanaf de adminpagina.
  useEffect(() => {
    const op = (e) => { if (e.detail) presenteer([e.detail], { proef: true }); };
    window.addEventListener('gami-proef', op);
    return () => window.removeEventListener('gami-proef', op);
  }, [presenteer]);

  const markeerGelezen = useCallback(() => {
    const max = items.reduce((m, e) => Math.max(m, e.id), 0);
    if (!token || max <= lastSeenId) return;
    setLastSeenId(max);
    fetch('/api/gamification/seen', { method: 'POST', headers: { ...gamiHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify({ lastId: max }) }).catch(() => {});
  }, [items, lastSeenId, token]);

  const laadMeer = useCallback(async () => {
    if (!token || !volgende) return;
    const f = await fetch(`/api/gamification/feed?cursor=${volgende}`, { headers: gamiHeaders(token) }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!f) return;
    setItems((oud) => [...oud, ...(f.items || []).filter((n) => !oud.some((o) => o.id === n.id))]);
    setVolgende(f.next_cursor || null);
  }, [token, volgende]);

  const ongelezen = useMemo(() => items.filter((e) => e.id > lastSeenId).length, [items, lastSeenId]);
  return { actief: Boolean(mij && mij.actief), mij, items, ongelezen, lastSeenId, kaart, sluitKaart: () => setKaart(null), markeerGelezen, laadMeer, heeftMeer: Boolean(volgende) };
}

// Paneel "Hoogtepunten": alles van de laatste 60 dagen, per dag. Ook het vangnet
// tegen meldingsmoeheid — wat niet als toast verscheen, staat hier.
function HoogtepuntenPaneel({ gami, onSluit, toonContext }) {
  const samenvatting = useMemo(() => sindsLaatsteBezoek(gami.items, gami.lastSeenId), []); // eslint-disable-line react-hooks/exhaustive-deps
  const grens = useRef(gami.lastSeenId);
  useEffect(() => {
    const t = setTimeout(() => gami.markeerGelezen(), 1200);
    const esc = (e) => { if (e.key === 'Escape') onSluit(); };
    window.addEventListener('keydown', esc);
    return () => { clearTimeout(t); window.removeEventListener('keydown', esc); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const groepen = useMemo(() => gamiPerDag(gami.items), [gami.items]);
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9600]" role="dialog" aria-label="Hoogtepunten">
      <button className="absolute inset-0 bg-slate-950/40 backdrop-blur-[2px]" aria-label="Sluiten" onClick={onSluit} />
      <aside className="absolute right-0 top-0 bottom-0 w-full sm:w-[26rem] bg-slate-900 border-l border-slate-700/60 shadow-2xl flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-700/60 shrink-0">
          <div className="min-w-0"><h3 className="text-lg font-semibold text-slate-100">Hoogtepunten</h3><p className="text-xs text-slate-500">Nieuwe sales, prestaties en mijlpalen van de laatste 60 dagen</p></div>
          <button onClick={onSluit} aria-label="Sluiten" className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" style={{ paddingBottom: 'calc(var(--tabbar-h, 0px) + 1rem)' }}>
          {samenvatting && <div className="rounded-xl border border-lime-500/30 bg-lime-500/10 px-3 py-2 text-sm text-slate-200"><span className="font-semibold">Sinds je laatste bezoek:</span> {samenvatting.tekst}.</div>}
          {!gami.items.length && <TableEmpty message="Nog geen hoogtepunten. Nieuwe sales en mijlpalen verschijnen hier." icon={Bell} />}
          {groepen.map((g) => (
            <div key={g.dag || 'x'}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">{gamiDagLabel(g.dag)}</div>
              <ul className="space-y-1.5">
                {g.items.map((e) => {
                  const Icoon = GAMI_ICOON[e.key] || Award;
                  const nieuw = e.id > grens.current;
                  const context = toonContext ? [e.company, e.salespersonTeam, e.salesperson].filter(Boolean).join(' · ') : (e.niveau === 'order' ? [e.salespersonTeam, e.salesperson].filter(Boolean).join(' · ') : '');
                  return (
                    <li key={e.id} className={`flex items-start gap-3 rounded-xl border px-3 py-2 ${nieuw ? 'border-lime-500/30 bg-lime-500/5' : 'border-slate-700/50 bg-slate-800/40'}`}>
                      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${e.tier >= 3 ? 'bg-lime-500/15 border-lime-500/40' : 'bg-slate-800 border-slate-700/60'}`}><Icoon size={16} className={e.tier >= 2 ? 'text-lime-400' : 'text-slate-400'} /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2"><span className="text-sm font-semibold text-slate-100">{e.titel}</span><span className="text-[11px] text-slate-500 shrink-0 tabular-nums">{gamiTijd(e.at)}</span></div>
                        {e.tekst && <div className="text-xs text-slate-400 break-words">{e.tekst}</div>}
                        {context && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{hideEtBrand(context)}</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {gami.heeftMeer && <button onClick={gami.laadMeer} className="w-full py-2 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800">Meer laden</button>}
        </div>
      </aside>
    </div>, document.body);
}

// Mijlpaalkaart (niveau 3): één kaart, één korte ring-animatie, sluit vanzelf.
// Confetti alleen als de beheerder het aanzette, binnen de kaart, 1,5 s.
function MijlpaalKaart({ kaart, onSluit, onBekijk }) {
  useEffect(() => {
    if (!kaart) return undefined;
    const t = setTimeout(onSluit, 9000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kaart && kaart.n]);
  const deeltjes = useMemo(() => Array.from({ length: 22 }, (_, i) => ({ links: 6 + ((i * 41) % 88), vertraging: (i % 7) * 0.05, draai: (i * 53) % 360, tint: i % 3 })), []);
  if (!kaart) return null;
  const Icoon = GAMI_ICOON[kaart.key] || Trophy;
  return ReactDOM.createPortal(
    <div key={kaart.n} role="status" className="gami-kaart fixed z-[9550] right-3 left-3 sm:left-auto sm:right-6 sm:w-[23rem]" style={{ bottom: 'calc(var(--tabbar-h, 0px) + 1rem + env(safe-area-inset-bottom, 0px))' }}>
      <style>{`
        @keyframes gamiIn { from { opacity: 0; transform: translateY(12px) scale(.98); } to { opacity: 1; transform: none; } }
        @keyframes gamiRing { from { stroke-dashoffset: 151; } to { stroke-dashoffset: 0; } }
        @keyframes gamiVal { 0% { opacity: 0; transform: translateY(-8px) rotate(0deg); } 12% { opacity: 1; } 100% { opacity: 0; transform: translateY(120px) rotate(260deg); } }
        .gami-kaart { animation: gamiIn .35s ease-out both; }
        .gami-ring { stroke-dasharray: 151; animation: gamiRing 1.2s ease-out both; }
        .gami-deeltje { position: absolute; top: 0; width: 6px; height: 9px; border-radius: 2px; opacity: 0; animation: gamiVal 1.5s ease-in both; }
        @media (prefers-reduced-motion: reduce) { .gami-kaart, .gami-ring { animation: none; } .gami-ring { stroke-dashoffset: 0; } .gami-deeltje { display: none; } }
      `}</style>
      <div className="relative overflow-hidden rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-lime-500/40 shadow-2xl p-4">
        {kaart.confetti && <div className="absolute inset-0 pointer-events-none" aria-hidden="true">{deeltjes.map((d, i) => <span key={i} className="gami-deeltje" style={{ left: `${d.links}%`, animationDelay: `${d.vertraging}s`, background: d.tint === 0 ? 'var(--brand-primary, #6366F1)' : d.tint === 1 ? '#14B8A6' : '#F59E0B', transform: `rotate(${d.draai}deg)` }} />)}</div>}
        <div className="relative flex items-start gap-3">
          <div className="relative w-14 h-14 shrink-0">
            <svg viewBox="0 0 56 56" className="absolute inset-0 -rotate-90" aria-hidden="true"><circle cx="28" cy="28" r="24" fill="none" stroke="currentColor" strokeWidth="3" className="text-slate-700/60" /><circle cx="28" cy="28" r="24" fill="none" strokeWidth="3" strokeLinecap="round" className="gami-ring" style={{ stroke: 'var(--brand-primary, #6366F1)' }} /></svg>
            <div className="absolute inset-0 flex items-center justify-center"><Icoon size={22} className="text-lime-400" /></div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-lime-400">Mijlpaal</div>
            <div className="text-base font-semibold text-slate-100 leading-snug">{kaart.titel}</div>
            {kaart.tekst && <div className="text-sm text-slate-300 mt-0.5 break-words">{kaart.tekst}</div>}
            {onBekijk && <button onClick={() => { onBekijk(); onSluit(); }} className="mt-2 text-sm font-semibold text-lime-400 hover:underline">Bekijk prestaties</button>}
          </div>
          <button onClick={onSluit} aria-label="Sluiten" className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X size={16} /></button>
        </div>
      </div>
    </div>, document.body);
}

// Aan/uit-schakelaar voor de instellingen.
const GamiSwitch = ({ checked, onChange, disabled, label }) => (
  <button type="button" role="switch" aria-checked={!!checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-lime-500' : 'bg-slate-600'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);
const GamiRij = ({ titel, uitleg, children }) => (
  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 py-3 border-b border-slate-700/40 last:border-b-0">
    <div className="min-w-0 flex-1 basis-52"><div className="text-sm font-medium text-slate-200">{titel}</div>{uitleg && <div className="text-xs text-slate-500 mt-0.5">{uitleg}</div>}</div>
    <div className="flex flex-wrap items-center justify-end gap-2 max-w-full">{children}</div>
  </div>
);
const GamiBalk = ({ waarde, max, markering }) => {
  const pct = max > 0 ? Math.min(100, Math.max(0, (waarde / max) * 100)) : 0;
  const mPct = max > 0 && markering != null ? Math.min(100, Math.max(0, (markering / max) * 100)) : null;
  return (
    <div className="relative h-2.5 rounded-full bg-slate-700/60 overflow-visible">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: 'var(--brand-primary, #6366F1)' }} />
      {mPct != null && <span title="Vorige maand, volledige maand" className="absolute -top-1 w-0.5 h-4.5 bg-slate-400" style={{ left: `${mPct}%`, height: '1.125rem' }} />}
    </div>
  );
};
const gamiVolume = (kwh) => { const v = Number(kwh) || 0; return v >= 1e6 ? `${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 }).format(v / 1e6)} GWh` : `${formatNumberRounded(v / 1000)} MWh`; };
const GAMI_MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const gamiMaand = (ym) => { const [j, m] = String(ym || '').split('-').map(Number); return j && m ? `${GAMI_MAANDEN[m - 1]} ${j}` : ''; };
const GAMI_METRICS = [{ id: 'orders', label: 'Sales', eenheid: 'sales' }, { id: 'mwh', label: 'Gecontracteerd volume', eenheid: 'MWh' }, { id: 'klanten', label: 'Nieuwe klanten', eenheid: 'klanten' }];

// Sales → Prestaties: maanddoel, eigen records, reeks en mijlpalen. Vergelijkt
// uitsluitend met de EIGEN eerdere prestaties — geen ranglijst tussen collega's.
function PrestatiesTab({ token, currentUser }) {
  const zietAlles = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  const [label, setLabel] = useState('');
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  const [concept, setConcept] = useState({});
  const laad = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`/api/gamification/overzicht${label ? `?company=${encodeURIComponent(label)}` : ''}`, { headers: gamiHeaders(token) });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setData(d); setFout('');
      setConcept(Object.fromEntries((d.doelen || []).map((x) => [x.metric, String(x.doel)])));
    } catch (_) { setFout('De prestaties konden niet geladen worden.'); }
  }, [token, label]);
  useEffect(() => { laad(); }, [laad]);

  const bewaarDoel = async (metric) => {
    const ruw = String(concept[metric] ?? '').replace(',', '.').trim();
    const doel = ruw === '' ? null : Number(ruw);
    if (doel !== null && !(doel > 0)) { toast.error('Vul een getal groter dan nul in, of laat het veld leeg om het doel te wissen.'); return; }
    try {
      const r = await fetch(`/api/gamification/doel${data?.company && zietAlles ? `?company=${encodeURIComponent(data.company)}` : ''}`, { method: 'PUT', headers: { ...gamiHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify({ metric, doel }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Bewaren mislukt');
      toast.success(doel === null ? 'Doel gewist' : 'Doel bewaard');
      laad();
    } catch (e) { toast.error(e.message); }
  };

  if (fout) return <Card><TableEmpty message={fout} icon={AlertCircle} /></Card>;
  if (!data) return <Card><div className="flex justify-center py-10"><Spinner size={28} /></div></Card>;
  if (!data.beschikbaar) return <Card><TableEmpty message="Prestaties zijn niet beschikbaar voor dit account." icon={Award} /></Card>;
  const o = data.overzicht;
  const niveauTekst = data.niveau === 'seller' ? 'jouw eigen sales' : data.niveau === 'team' ? 'je verkoopteam' : 'het energielabel';
  const doelVan = (metric) => (data.doelen || []).find((d) => d.metric === metric);
  const waardeVan = (metric) => (!o ? 0 : metric === 'mwh' ? o.dezeMaand.kwh / 1000 : metric === 'klanten' ? o.dezeMaand.nieuweKlanten : o.dezeMaand.n);
  const vorigeVan = (metric) => (!o ? null : metric === 'mwh' ? o.vorigeMaand.kwh / 1000 : metric === 'orders' ? o.vorigeMaand.n : null);
  const magDoelZetten = data.doelenBeschikbaar && currentUser?.role !== 'et_dossier' && !currentUser?.demoMode;
  const pctVanRecord = (nu, record) => (record > 0 ? `${formatNumberRounded((nu / record) * 100)} % van je record deze maand` : '');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-400">Cijfers van {niveauTekst}{data.company ? <> — <span className="text-slate-200 font-medium">{hideEtBrand([data.company, data.salespersonTeam, data.salesperson].filter(Boolean).join(' · '))}</span></> : null}. Een verlenging van een lopend contract telt niet als sale.</p>
        {zietAlles && data.labels?.length > 0 && <select value={data.company || ''} onChange={(e) => setLabel(e.target.value)} className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200" style={{ width: 184 }} aria-label="Energielabel">{data.labels.map((l) => <option key={l} value={l}>{hideEtBrand(l)}</option>)}</select>}
      </div>
      {!o ? <Card><TableEmpty message="Nog geen sales op dit niveau." icon={Folder} /></Card> : <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile label={`Sales in ${gamiMaand(o.maand).split(' ')[0]}`} value={formatNumberRounded(o.dezeMaand.n)} dot={CHART_HUES.blauw} sub={`vorige maand: ${formatNumberRounded(o.vorigeMaand.n)}`} minW={140} />
          <StatTile label="Gecontracteerd volume" value={gamiVolume(o.dezeMaand.kwh)} dot={CHART_HUES.aqua} sub={`vorige maand: ${gamiVolume(o.vorigeMaand.kwh)}`} minW={140} />
          <StatTile label="Nieuwe klanten" value={formatNumberRounded(o.dezeMaand.nieuweKlanten)} dot={CHART_HUES.violet} sub="deze maand" minW={140} />
          <StatTile label="Weken op rij sales" value={formatNumberRounded(o.reeks)} dot={CHART_HUES.geel} sub={`langste reeks: ${formatNumberRounded(o.langsteReeks)} weken`} minW={140} />
        </div>

        {data.doelenBeschikbaar && <Card>
          <h3 className="text-lg font-semibold text-slate-100 mb-1 flex items-center gap-2"><Target size={18} className="text-lime-400" />Maanddoel</h3>
          <p className="text-xs text-slate-500 mb-4">Een doel geldt elke maand opnieuw tot je het wijzigt. Het streepje op de balk toont het eindresultaat van vorige maand.</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {GAMI_METRICS.map((m) => {
              const d = doelVan(m.id); const w = waardeVan(m.id);
              return (
                <div key={m.id} className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2 mb-1.5"><span className="text-sm font-medium text-slate-200">{m.label}</span><span className="text-sm tabular-nums text-slate-300">{new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 1 }).format(w)}{d ? <span className="text-slate-500"> / {new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 1 }).format(d.doel)}</span> : null} <span className="text-slate-500 text-xs">{m.eenheid}</span></span></div>
                  {d ? <><GamiBalk waarde={w} max={d.doel} markering={vorigeVan(m.id)} /><div className="text-xs text-slate-500 mt-1.5">{w >= d.doel ? 'Doel gehaald' : `${formatNumberRounded((w / d.doel) * 100)} % — nog ${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 1 }).format(d.doel - w)} ${m.eenheid}`}</div></> : <div className="text-xs text-slate-500">Nog geen doel ingesteld.</div>}
                  {magDoelZetten && <div className="flex items-center gap-2 mt-2"><input inputMode="decimal" value={concept[m.id] ?? ''} onChange={(e) => setConcept((c) => ({ ...c, [m.id]: e.target.value }))} placeholder="Doel per maand" aria-label={`Maanddoel ${m.label}`} className="w-32 px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-lime-500" /><button onClick={() => bewaarDoel(m.id)} className="px-3 py-1.5 rounded-lg border border-slate-600 text-sm text-slate-200 hover:bg-slate-700">Bewaren</button></div>}
                </div>
              );
            })}
          </div>
        </Card>}

        <Card>
          <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2"><Trophy size={18} className="text-lime-400" />Eigen records</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatTile label="Beste maand (sales)" value={o.besteMaandN ? formatNumberRounded(o.besteMaandN.waarde) : '—'} sub={o.besteMaandN ? `${gamiMaand(o.besteMaandN.maand)} · ${pctVanRecord(o.dezeMaand.n, o.besteMaandN.waarde)}` : ''} minW={140} />
            <StatTile label="Beste maand (volume)" value={o.besteMaandKwh ? gamiVolume(o.besteMaandKwh.waarde) : '—'} sub={o.besteMaandKwh ? `${gamiMaand(o.besteMaandKwh.maand)} · ${pctVanRecord(o.dezeMaand.kwh, o.besteMaandKwh.waarde)}` : ''} minW={140} />
            <StatTile label="Beste week" value={formatNumberRounded(o.besteWeek)} sub={`deze week: ${formatNumberRounded(o.dezeWeek)} sales`} minW={140} />
            <StatTile label="Grootste contract" value={o.grootsteContract ? `${gamiVolume(o.grootsteContract.kwh)}` : '—'} sub={o.grootsteContract ? `per jaar · ${gamiMaand(o.grootsteContract.maand)}` : ''} minW={140} />
            <StatTile label="Grootste klant" value={gamiVolume(o.grootsteKlantKwh)} sub="per jaar" minW={140} />
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2"><Award size={18} className="text-lime-400" />Volgende mijlpaal</h3>
            <div className="space-y-5">
              {[{ titel: 'Actieve klanten', nu: o.actieveKlanten, doel: data.volgendeMijlpaal?.klanten, toon: (v) => formatNumberRounded(v), rest: (r) => `nog ${formatNumberRounded(r)} klanten` },
                { titel: 'Gecontracteerd volume (actieve portefeuille)', nu: o.actiefKwh / 1e6, doel: data.volgendeMijlpaal?.gwh, toon: (v) => `${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 }).format(v)} GWh`, rest: (r) => `nog ${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 }).format(r)} GWh` }].map((m) => (
                <div key={m.titel}>
                  <div className="flex items-baseline justify-between gap-2 mb-1.5"><span className="text-sm font-medium text-slate-200">{m.titel}</span><span className="text-sm tabular-nums text-slate-300">{m.toon(m.nu)}{m.doel ? <span className="text-slate-500"> / {m.toon(m.doel)}</span> : null}</span></div>
                  {m.doel ? <><GamiBalk waarde={m.nu} max={m.doel} /><div className="text-xs text-slate-500 mt-1.5">{m.rest(Math.max(0, m.doel - m.nu))}</div></> : <div className="text-xs text-slate-500">De hoogste mijlpaal is bereikt.</div>}
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2"><Flame size={18} className="text-lime-400" />Bereikte mijlpalen</h3>
            {!data.tijdlijn?.length ? <TableEmpty message="Nog geen mijlpalen. De eerste komt eraan." icon={Award} /> : (
              <ul className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {data.tijdlijn.map((t) => { const Icoon = GAMI_ICOON[t.key] || Award; return (
                  <li key={t.id} className="flex items-start gap-3"><div className="w-7 h-7 rounded-lg bg-slate-800 border border-slate-700/60 flex items-center justify-center shrink-0"><Icoon size={14} className="text-lime-400" /></div><div className="min-w-0 flex-1"><div className="text-sm text-slate-200 break-words"><span className="font-semibold">{t.titel}</span>{t.tekst ? ` — ${t.tekst}` : ''}</div><div className="text-[11px] text-slate-500">{t.at ? new Date(t.at).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'eerder bereikt'}</div></div></li>
                ); })}
              </ul>
            )}
          </Card>
        </div>
      </>}
    </div>
  );
}

// Account → Meldingen en geluid. Bewaard per gebruiker op de server
// (user_ui_prefs): één keer instellen geldt op elk toestel.
function MeldingenVoorkeuren({ token }) {
  const [mij, setMij] = useState(null);
  const [geluidRuw, zetGeluid] = useUiPref(token, 'gami.geluid', null);
  const [kanalenRuw, zetKanalen] = useUiPref(token, 'gami.kanalen', null);
  const [toestemming, setToestemming] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  useEffect(() => { if (!token) return; fetch('/api/gamification/mij', { headers: gamiHeaders(token) }).then((r) => (r.ok ? r.json() : { actief: false })).then(setMij).catch(() => setMij({ actief: false })); }, [token]);
  if (!mij || !mij.actief) return null;
  const geluid = leesGeluid(geluidRuw); const kanalen = leesKanalen(kanalenRuw);
  const volume = geluid.volume == null ? mij.standaardVolume : geluid.volume;
  const kanaal = (k, v) => zetKanalen({ ...kanalen, [k]: v });
  const uitgeschakeld = <span className="text-xs text-slate-500">uitgeschakeld door de beheerder</span>;
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent || '');
  const alsApp = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const zetBrowser = async (aan) => {
    if (!aan) { kanaal('browser', false); return; }
    if (typeof Notification === 'undefined') { toast.info('Deze browser ondersteunt geen meldingen.'); return; }
    // De browser vraagt zijn toestemming pas NU — bij deze klik, nooit spontaan.
    const p = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
    setToestemming(p);
    if (p === 'granted') kanaal('browser', true);
    else toast.info('Meldingen zijn geblokkeerd in je browser. Sta ze toe via het slotje in de adresbalk.');
  };
  return (
    <Card>
      <h3 className="text-lg font-semibold text-slate-100 mb-1 flex items-center gap-2"><Bell size={20} className="text-lime-400" />Meldingen en geluid</h3>
      <p className="text-xs text-slate-500 mb-3">Je keuzes worden bij je account bewaard en gelden op elk toestel waarop je aanmeldt.</p>
      <GamiRij titel="Geluid" uitleg="Een korte, zachte klank bij een nieuwe sale of mijlpaal. Nooit bij het openen of vernieuwen van de pagina.">
        {mij.soundsBeschikbaar ? <GamiSwitch label="Geluid" checked={geluid.aan} onChange={(v) => zetGeluid({ ...geluid, aan: v })} /> : uitgeschakeld}
      </GamiRij>
      {mij.soundsBeschikbaar && geluid.aan && <GamiRij titel="Volume" uitleg={`${volume} %`}>
        <input type="range" min="5" max="100" step="5" value={volume} onChange={(e) => zetGeluid({ ...geluid, volume: Number(e.target.value) })} aria-label="Volume" className="w-32 sm:w-44 accent-lime-500" />
        <button onClick={() => { ontgrendelAudio(); gamiSpeel('sale', volume, { voorbeeld: true }); }} title="Voorbeeld" className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700"><Play size={15} /></button>
      </GamiRij>}
      <GamiRij titel="Meldingen in het portaal" uitleg="Een kleine melding rechtsboven terwijl je werkt. Uit = je vindt alles nog terug onder Hoogtepunten.">
        <GamiSwitch label="Meldingen in het portaal" checked={kanalen.toasts} onChange={(v) => kanaal('toasts', v)} />
      </GamiRij>
      {mij.categorieen?.length > 1 && <GamiRij titel="Waarover" uitleg="Kies de onderwerpen waarvan je een melding wilt.">
        <div className="flex flex-wrap justify-end gap-x-4 gap-y-1 max-w-xs">{mij.categorieen.map((c) => (
          <label key={c.id} className="flex items-center gap-1.5 text-sm text-slate-300 cursor-pointer"><input type="checkbox" className="w-4 h-4 accent-lime-500" checked={kanalen.categorieen[c.id] !== false} onChange={(e) => zetKanalen({ ...kanalen, categorieen: { ...kanalen.categorieen, [c.id]: e.target.checked } })} />{c.label}</label>
        ))}</div>
      </GamiRij>}
      <GamiRij titel="Browsermeldingen" uitleg={isIos && !alsApp ? 'Op iPhone en iPad werken meldingen alleen in de geïnstalleerde app (Deel → Zet op beginscherm).' : toestemming === 'denied' ? 'Geblokkeerd in je browser. Sta meldingen toe via het slotje in de adresbalk.' : 'Een melding van je toestel wanneer het portaal op de achtergrond open staat. Alleen voor prestaties en mijlpalen.'}>
        {mij.browserBeschikbaar ? <GamiSwitch label="Browsermeldingen" checked={kanalen.browser && toestemming === 'granted'} disabled={toestemming === 'unsupported'} onChange={zetBrowser} /> : uitgeschakeld}
      </GamiRij>
      <GamiRij titel="E-mail bij een mijlpaal" uitleg="Hoogstens één mail per dag, alleen bij iets uitzonderlijks.">
        {mij.mailBeschikbaar ? <GamiSwitch label="E-mail bij een mijlpaal" checked={kanalen.mail} onChange={(v) => kanaal('mail', v)} /> : uitgeschakeld}
      </GamiRij>
      <GamiRij titel="Weekoverzicht per mail" uitleg="Op maandagochtend, alleen als er iets te melden valt.">
        {mij.digestBeschikbaar ? <GamiSwitch label="Weekoverzicht per mail" checked={kanalen.digest} onChange={(v) => kanaal('digest', v)} /> : uitgeschakeld}
      </GamiRij>
      {typeof navigator.vibrate === 'function' && <GamiRij titel="Trillen bij een mijlpaal" uitleg="Alleen op toestellen die dat ondersteunen.">
        <GamiSwitch label="Trillen" checked={kanalen.trillen} onChange={(v) => kanaal('trillen', v)} />
      </GamiRij>}
      <GamiRij titel="Even geen meldingen" uitleg={isGepauzeerd(kanalen) ? 'Gepauzeerd tot morgen. Alles blijft terug te vinden onder Hoogtepunten.' : 'Zet meldingen en geluid uit voor de rest van vandaag.'}>
        <button onClick={() => kanaal('pauzeTot', isGepauzeerd(kanalen) ? null : vandaagIso())} className="px-3 py-1.5 rounded-lg border border-slate-600 text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2">{isGepauzeerd(kanalen) ? <><Bell size={14} />Hervatten</> : <><BellOff size={14} />Pauzeer vandaag</>}</button>
      </GamiRij>
    </Card>
  );
}

// Beheer → Notifications (alleen admin).
const GAMI_ROL_LABEL = { reseller: 'Verkoper', teamleader: 'Teamleader', master: 'Master', et_dossier: 'Dossierbeheerder', admin: 'Admin' };
const GamificationAdminPage = ({ token }) => {
  const [bron, setBron] = useState(null);       // { config, catalogus, categorieen, sounds, rollen, stand }
  const [cfg, setCfg] = useState(null);
  const [bewaren, setBewaren] = useState(false);
  const [open, setOpen] = useState('');
  const [labels, setLabels] = useState([]);
  const [users, setUsers] = useState([]);
  const [log, setLog] = useState(null);
  const [logAantal, setLogAantal] = useState(25);

  const laad = useCallback(async () => {
    try {
      const d = await fetch('/api/gamification/config', { headers: gamiHeaders(token) }).then((r) => { if (!r.ok) throw new Error(); return r.json(); });
      setBron(d); setCfg(d.config);
    } catch (_) { toast.error('De instellingen konden niet geladen worden.'); }
  }, [token]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => {
    if (!token) return;
    fetch('/api/gamification/overzicht', { headers: gamiHeaders(token) }).then((r) => (r.ok ? r.json() : null)).then((d) => setLabels((d && d.labels) || [])).catch(() => {});
    getUsers(token).then((u) => setUsers(Array.isArray(u) ? u : (u && u.users) || [])).catch(() => {});
  }, [token]);

  const gewijzigd = bron && cfg && JSON.stringify(bron.config) !== JSON.stringify(cfg);
  const zet = (pad, waarde) => setCfg((c) => {
    const kopie = JSON.parse(JSON.stringify(c)); let o = kopie;
    for (let i = 0; i < pad.length - 1; i += 1) o = o[pad[i]];
    o[pad[pad.length - 1]] = waarde; return kopie;
  });
  const bewaar = async () => {
    setBewaren(true);
    try {
      const r = await fetch('/api/gamification/config', { method: 'PUT', headers: { ...gamiHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify({ config: cfg }) });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setBron((b) => ({ ...b, config: d.config })); setCfg(d.config);
      toast.success('Instellingen bewaard. Gebruikers merken dit bij hun volgende bezoek of verversing.');
    } catch (_) { toast.error('Bewaren mislukt.'); } finally { setBewaren(false); }
  };
  const proef = async (key) => {
    try {
      ontgrendelAudio();
      const r = await fetch('/api/gamification/test', { method: 'POST', headers: { ...gamiHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify({ key }) });
      const d = await r.json();
      // Met de instellingen zoals ze NU op het scherm staan (ook onbewaard).
      const e = cfg.events[key];
      window.dispatchEvent(new CustomEvent('gami-proef', { detail: { ...d.event, id: Date.now(), tier: e.tier, sound: e.sound } }));
    } catch (_) { toast.error('De proefmelding kon niet gestart worden.'); }
  };
  const laadLog = async () => {
    try { const d = await fetch('/api/gamification/logboek', { headers: gamiHeaders(token) }).then((r) => r.json()); setLog(d.items || []); } catch (_) { setLog([]); }
  };

  if (!cfg || !bron) return <div className="space-y-6"><PageHeader title="Notifications" subtitle="Sounds, mijlpalen en meldingen" /><Card><div className="flex justify-center py-10"><Spinner size={28} /></div></Card></div>;
  const gebruikerOpties = users.filter((u) => !['prospect_label', 'prospect_reseller'].includes(u.role)).map((u) => `${u.name} · ${u.email}`);
  const idVan = (tekst) => (users.find((u) => `${u.name} · ${u.email}` === tekst) || {}).id;
  const tekstVan = (id) => { const u = users.find((x) => x.id === id); return u ? `${u.name} · ${u.email}` : null; };
  const invoer = 'px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-lime-500';

  return (
    <div className="space-y-6">
      <PageHeader title="Notifications" subtitle="Bepaal wat er bestaat; gebruikers kiezen zelf wat ze ervan merken (Account → Meldingen en geluid)">
        <button onClick={bewaar} disabled={!gewijzigd || bewaren} className="px-5 py-2.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-xl transition-all">{bewaren ? 'Bewaren…' : gewijzigd ? 'Wijzigingen bewaren' : 'Bewaard'}</button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Status" value={cfg.enabled ? 'Actief' : 'Uit'} accent={cfg.enabled ? '#14B8A6' : undefined} sub={cfg.enabled ? 'gebeurtenissen worden gemeld' : 'gebeurtenissen worden stil bewaard'} minW={140} />
        <StatTile label="Sale-orders gekend" value={formatNumberRounded(bron.stand?.orders)} sub="basis voor “nieuw”" minW={140} />
        <StatTile label="Gebeurtenissen bewaard" value={formatNumberRounded(bron.stand?.totaal)} sub="inclusief stille" minW={140} />
        <StatTile label="Waarvan gemeld" value={formatNumberRounded(bron.stand?.gemeld)} sub="sinds de start" minW={140} />
      </div>

      <Card>
        <h3 className="text-lg font-semibold text-slate-100 mb-2">Algemeen</h3>
        <GamiRij titel="Gamification" uitleg="De hoofdschakelaar. Uit = geen meldingen, geen bel, geen geluid voor niemand. De detectie loopt op de achtergrond door en bewaart alles stil, zodat er niets alsnog afgaat wanneer je dit aanzet."><GamiSwitch label="Gamification" checked={cfg.enabled} onChange={(v) => zet(['enabled'], v)} /></GamiRij>
        <GamiRij titel="Sounds" uitleg="Hoofdschakelaar voor alle geluid. Een sound duurt hoogstens 1,5 seconde en klinkt nooit bij het laden van een pagina."><GamiSwitch label="Sounds" checked={cfg.soundsEnabled} onChange={(v) => zet(['soundsEnabled'], v)} /></GamiRij>
        <GamiRij titel="Standaardvolume" uitleg={`${cfg.volume} % — voor wie zelf geen volume koos`}>
          <input type="range" min="5" max="100" step="5" value={cfg.volume} onChange={(e) => zet(['volume'], Number(e.target.value))} aria-label="Standaardvolume" className="w-32 sm:w-44 accent-lime-500" />
          <button onClick={() => { ontgrendelAudio(); gamiSpeel('sale', cfg.volume, { voorbeeld: true }); }} title="Voorbeeld" className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700"><Play size={15} /></button>
        </GamiRij>
        <GamiRij titel="Confetti bij een mijlpaal" uitleg="Alleen bij niveau 3, een twintigtal deeltjes binnen de mijlpaalkaart, anderhalve seconde. Standaard uit."><GamiSwitch label="Confetti" checked={cfg.confetti} onChange={(v) => zet(['confetti'], v)} /></GamiRij>
        <GamiRij titel="Maanddoelen" uitleg="Gebruikers kunnen op Sales → Prestaties een maanddoel zetten voor hun eigen niveau."><GamiSwitch label="Maanddoelen" checked={cfg.doelenEnabled} onChange={(v) => zet(['doelenEnabled'], v)} /></GamiRij>
        <GamiRij titel="Verlenging" uitleg="Zelfde klant op dezelfde EAN binnen zoveel dagen na het einde van het vorige contract = verlenging, en dus geen sale.">
          <input type="number" min="0" max="365" value={cfg.verlengingVensterDagen} onChange={(e) => zet(['verlengingVensterDagen'], Number(e.target.value))} className={`${invoer} w-24`} aria-label="Venster verlenging in dagen" /><span className="text-xs text-slate-500">dagen</span>
        </GamiRij>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-slate-100 mb-1">Gebeurtenissen</h3>
        <p className="text-xs text-slate-500 mb-4">Niveau 1 = gewone gebeurtenis (kleine melding), 2 = prestatie, 3 = mijlpaal (mijlpaalkaart). Klik op een rij voor wie het ziet en de drempels. Met het afspeelknopje zie en hoor je een proefmelding in je eigen sessie.</p>
        {bron.categorieen.map((cat) => (
          <div key={cat.id} className="mb-5 last:mb-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">{cat.label}</div>
            <div className="rounded-xl border border-slate-700/50 divide-y divide-slate-700/40">
              {bron.catalogus.filter((d) => d.categorie === cat.id).map((d) => {
                const e = cfg.events[d.key]; const isOpen = open === d.key;
                return (
                  <div key={d.key} className={e.enabled ? '' : 'opacity-60'}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
                      <GamiSwitch label={d.label} checked={e.enabled} onChange={(v) => zet(['events', d.key, 'enabled'], v)} />
                      <button onClick={() => setOpen(isOpen ? '' : d.key)} className="min-w-0 flex-1 basis-56 text-left"><div className="text-sm font-medium text-slate-200 flex items-center gap-1.5">{d.label}{isOpen ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}</div><div className="text-xs text-slate-500">{d.beschrijving}</div></button>
                      <label className="flex items-center gap-1.5 text-xs text-slate-400">Niveau<select value={e.tier} onChange={(ev) => zet(['events', d.key, 'tier'], Number(ev.target.value))} className={`${invoer} py-1`}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option></select></label>
                      <label className="flex items-center gap-1.5 text-xs text-slate-400">Sound<select value={e.sound} onChange={(ev) => zet(['events', d.key, 'sound'], ev.target.value)} className={`${invoer} py-1`}>{bron.sounds.map((s) => <option key={s} value={s}>{s === 'geen' ? 'Geen' : (GAMI_SOUNDS[s]?.label || s)}</option>)}</select></label>
                      <div className="flex items-center gap-3 text-xs text-slate-300">
                        {[['inApp', 'Portaal'], ['browser', 'Browser'], ['mail', 'E-mail']].map(([k, l]) => <label key={k} className="flex items-center gap-1 cursor-pointer"><input type="checkbox" className="w-4 h-4 accent-lime-500" checked={e[k]} onChange={(ev) => zet(['events', d.key, k], ev.target.checked)} />{l}</label>)}
                      </div>
                      <button onClick={() => proef(d.key)} title="Proefmelding in je eigen sessie" className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700"><Play size={14} /></button>
                    </div>
                    {isOpen && <div className="px-3 pb-3 pt-1 grid grid-cols-1 lg:grid-cols-3 gap-4 bg-slate-800/30">
                      {[['rollen', 'Zichtbaar voor', 'Wie deze gebeurtenis in Hoogtepunten ziet.'], ['liveRollen', 'Melding en geluid voor', 'Wie er meteen een melding en geluid bij krijgt. De rest vindt het stil terug onder Hoogtepunten.']].map(([veld, titel, uitleg]) => (
                        <div key={veld}><div className="text-xs font-semibold text-slate-300">{titel}</div><div className="text-[11px] text-slate-500 mb-1.5">{uitleg}</div><div className="flex flex-wrap gap-1.5">{bron.rollen.map((r) => {
                          const aan = e[veld].includes(r); const kan = veld === 'rollen' || e.rollen.includes(r);
                          return <button key={r} disabled={!kan} onClick={() => { const nieuw = aan ? e[veld].filter((x) => x !== r) : [...e[veld], r]; zet(['events', d.key, veld], nieuw); if (veld === 'rollen' && aan) zet(['events', d.key, 'liveRollen'], e.liveRollen.filter((x) => x !== r)); }} className={`px-2.5 py-1 rounded-full text-xs border ${aan ? 'bg-lime-500/15 border-lime-500/40 text-lime-400' : 'border-slate-600 text-slate-400'} ${kan ? '' : 'opacity-30 cursor-not-allowed'}`}>{GAMI_ROL_LABEL[r] || r}</button>;
                        })}</div></div>
                      ))}
                      <div><div className="text-xs font-semibold text-slate-300 mb-1.5">Drempels</div>{!(d.drempels || []).length ? <div className="text-[11px] text-slate-500">Geen drempels voor deze gebeurtenis.</div> : <div className="space-y-2">{d.drempels.map((dr) => (
                        <label key={dr.id} className="block text-[11px] text-slate-400">{dr.label}{dr.type === 'lijst'
                          ? <input defaultValue={(e.drempels[dr.id] || []).join(', ')} onBlur={(ev) => zet(['events', d.key, 'drempels', dr.id], [...new Set(ev.target.value.split(/[;,\s]+/).map(Number).filter((n) => n > 0))].sort((a, b) => a - b))} className={`${invoer} w-full mt-0.5`} />
                          : <input type="number" min="0" value={e.drempels[dr.id]} onChange={(ev) => zet(['events', d.key, 'drempels', dr.id], Number(ev.target.value))} className={`${invoer} w-28 mt-0.5 block`} />}</label>
                      ))}</div>}{d.vereistModule && <div className="text-[11px] text-slate-500 mt-2">Alleen voor gebruikers met de module {d.vereistModule === 'revenue' ? 'Revenue' : d.vereistModule}.</div>}</div>
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-slate-100 mb-2">Kanalen en rust</h3>
          <GamiRij titel="Browsermeldingen" uitleg="Een melding van het toestel wanneer het portaal op de achtergrond open staat. De gebruiker moet dit zelf inschakelen en zijn browser toestemming geven."><GamiSwitch label="Browsermeldingen" checked={cfg.browserEnabled} onChange={(v) => zet(['browserEnabled'], v)} /></GamiRij>
          <GamiRij titel="E-mail bij een mijlpaal" uitleg="Meteen een mail voor gebeurtenissen waar E-mail aangevinkt staat, in de huisstijl van het energielabel van de ontvanger."><GamiSwitch label="E-mail" checked={cfg.mailEnabled} onChange={(v) => zet(['mailEnabled'], v)} /></GamiRij>
          <GamiRij titel="Weekoverzicht per mail" uitleg="Maandag 08:00 (job “Weekoverzicht per mail”). Alleen naar wie iets te melden heeft."><GamiSwitch label="Weekoverzicht" checked={cfg.digestEnabled} onChange={(v) => zet(['digestEnabled'], v)} /></GamiRij>
          <GamiRij titel="Mails alleen tijdens de kantooruren" uitleg="Daarbuiten wacht een mail tot het volgende venster.">
            <GamiSwitch label="Rustige uren" checked={cfg.rustigeUren.actief} onChange={(v) => zet(['rustigeUren', 'actief'], v)} />
          </GamiRij>
          {cfg.rustigeUren.actief && <GamiRij titel="Venster" uitleg="Brusselse tijd">
            <input type="time" lang="nl-BE" value={cfg.rustigeUren.van} onChange={(e) => zet(['rustigeUren', 'van'], e.target.value)} className={invoer} aria-label="Van" /><span className="text-slate-500 text-sm">tot</span><input type="time" lang="nl-BE" value={cfg.rustigeUren.tot} onChange={(e) => zet(['rustigeUren', 'tot'], e.target.value)} className={invoer} aria-label="Tot" />
            <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer"><input type="checkbox" className="w-4 h-4 accent-lime-500" checked={cfg.rustigeUren.alleenWerkdagen} onChange={(e) => zet(['rustigeUren', 'alleenWerkdagen'], e.target.checked)} />werkdagen</label>
          </GamiRij>}
          <GamiRij titel="Plafonds per gebruiker" uitleg="Wat erboven valt, staat stil onder Hoogtepunten. Zo blijft een melding iets bijzonders.">
            <div className="flex flex-wrap justify-end gap-2 text-[11px] text-slate-400">
              {[['toastsPerUur', 'meldingen / uur'], ['browserPerDag', 'browser / dag'], ['mailsPerDag', 'mails / dag']].map(([k, l]) => <label key={k} className="flex flex-col items-end gap-0.5">{l}<input type="number" min="0" value={cfg.plafonds[k]} onChange={(e) => zet(['plafonds', k], Number(e.target.value))} className={`${invoer} w-20`} /></label>)}
            </div>
          </GamiRij>
        </Card>
        <Card>
          <h3 className="text-lg font-semibold text-slate-100 mb-2">Ontvangers</h3>
          <p className="text-xs text-slate-500 mb-3">Per gebeurtenis kies je hierboven welke rollen ze zien. Hier sluit je bovendien hele energielabels of individuele gebruikers uit. Prospect- en demo-accounts doen nooit mee. De tab Sales → Prestaties ken je per gebruiker toe in het gebruikersformulier (subpagina’s).</p>
          <div className="space-y-4">
            <SearchableMultiSelect label="Energielabels zonder gamification" values={cfg.uitgeslotenLabels} onChange={(v) => zet(['uitgeslotenLabels'], v)} options={labels} placeholder="Geen" />
            <SearchableMultiSelect label="Gebruikers zonder gamification" values={cfg.uitgeslotenGebruikers.map(tekstVan).filter(Boolean)} onChange={(v) => zet(['uitgeslotenGebruikers'], v.map(idVan).filter(Boolean))} options={gebruikerOpties} placeholder="Geen" />
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3"><h3 className="text-lg font-semibold text-slate-100">Logboek</h3><button onClick={laadLog} className="px-3 py-1.5 rounded-lg border border-slate-600 text-sm text-slate-200 hover:bg-slate-700">{log ? 'Vernieuwen' : 'Laatste gebeurtenissen tonen'}</button></div>
        {log && (!log.length ? <TableEmpty message="Nog geen gebeurtenissen." icon={Folder} /> : <>
          <div className="overflow-x-auto"><table className="resp-card w-full text-sm"><thead><tr className="text-left text-xs text-slate-400"><th className="px-3 py-2">Wanneer</th><th className="px-3 py-2">Gebeurtenis</th><th className="px-3 py-2">Tekst</th><th className="px-3 py-2">Niveau</th><th className="px-3 py-2">Voor</th><th className="px-3 py-2">Gemeld</th></tr></thead>
            <tbody>{log.slice(0, logAantal).map((e) => (
              <tr key={e.id} className="border-t border-slate-700/40"><td data-label="Wanneer" className="px-3 py-2 whitespace-nowrap tabular-nums text-slate-400">{new Date(e.at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td><td data-label="Gebeurtenis" className="px-3 py-2 text-slate-200">{e.titel}</td><td data-label="Tekst" className="px-3 py-2 text-slate-400">{e.tekst}</td><td data-label="Niveau" className="px-3 py-2 text-slate-400">{e.tier}</td><td data-label="Voor" className="px-3 py-2 text-slate-400">{[e.company, e.salespersonTeam, e.salesperson].filter(Boolean).join(' · ')}</td><td data-label="Gemeld" className="px-3 py-2">{e.stil ? <span className="text-slate-500">stil</span> : <span className="text-lime-400">ja{e.mails ? ` · mail: ${Object.entries(e.mails).map(([s, n]) => `${n} ${s}`).join(', ')}` : ''}</span>}</td></tr>
            ))}</tbody></table></div>
          {log.length > logAantal && <button onClick={() => setLogAantal((n) => n + 50)} className="mt-3 w-full py-2 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800">Meer tonen ({log.length - logAantal})</button>}
        </>)}
      </Card>
    </div>
  );
};

const JobsPage = ({ token }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});     // key → cron-draft (nog niet opgeslagen)
  const [busy, setBusy] = useState({});          // key → bezig (opslaan/starten)
  const [histKey, setHistKey] = useState('');    // uitgeklapte historiek
  const [hist, setHist] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async (silent) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const r = await fetch('/api/jobs', { headers: authHeaders });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      setJobs(d.jobs || []); setError('');
    } catch (e) { if (!silent) setError(e.message); }
    finally { if (!silent) setLoading(false); }
  }, [token, authHeaders]);

  useEffect(() => { load(); }, [load]);
  // Live-verversing: pollt zacht elke 8s zodat lopende runs + laatste-run updaten.
  useEffect(() => { const id = setInterval(() => load(true), 8000); return () => clearInterval(id); }, [load]);

  const saveJob = async (key, patch) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const r = await fetch(`/api/jobs/${encodeURIComponent(key)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(patch) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setJobs(prev => prev.map(j => j.key === key ? { ...j, ...d } : j));
      setDrafts(dr => { const n = { ...dr }; delete n[key]; return n; });
      toast.success('Job bijgewerkt');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const runNow = async (key) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const r = await fetch(`/api/jobs/${encodeURIComponent(key)}/run`, { method: 'POST', headers: authHeaders });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      toast.info('Job gestart — voortgang verschijnt hieronder');
      setTimeout(() => load(true), 800);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const openHistory = async (key) => {
    if (histKey === key) { setHistKey(''); return; }
    setHistKey(key); setHist([]); setHistLoading(true);
    try {
      const r = await fetch(`/api/jobs/${encodeURIComponent(key)}/runs`, { headers: authHeaders });
      const d = await r.json();
      setHist(d.runs || []);
    } catch { /* stil */ } finally { setHistLoading(false); }
  };

  const groups = useMemo(() => {
    const m = new Map();
    for (const j of jobs) { if (!m.has(j.group)) m.set(j.group, { label: j.groupLabel, jobs: [] }); m.get(j.group).jobs.push(j); }
    return [...m.values()];
  }, [jobs]);

  const enabledCount = jobs.filter(j => j.enabled).length;
  const errorCount = jobs.filter(j => j.lastRun && j.lastRun.status === 'error').length;
  const runningCount = jobs.filter(j => j.running).length;

  return (
    <div className="space-y-6">
      <PageHeader title="Jobs — data-imports" subtitle="Beheer de geplande imports van externe data (orders, facturen, revenue, helpdesk, marktprijzen, bill shock). Eén achtergrond-job houdt de cache warm voor álle gebruikers — schermen halen niet meer bij elk bezoek live op.">
        <button onClick={() => load()} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/60 hover:bg-slate-600/60 text-slate-200 text-sm rounded-lg"><RefreshCw size={15} />Vernieuwen</button>
      </PageHeader>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl text-sm">{error}</div>}

      <div className="flex flex-wrap gap-3">
        <StatTile label="Actieve jobs" value={`${enabledCount} / ${jobs.length}`} dot="#14B8A6" />
        <StatTile label="Nu bezig" value={runningCount} dot="#F59E0B" accent={runningCount ? '#F59E0B' : undefined} />
        <StatTile label="Fout bij laatste run" value={errorCount} dot="#F43F5E" accent={errorCount ? '#F43F5E' : undefined} />
      </div>

      {loading ? <div className="flex justify-center py-10"><Spinner size={28} /></div> : groups.map(group => (
        <Card key={group.label} className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700/50 bg-slate-800/40"><h3 className="text-sm font-semibold text-slate-200">{group.label}</h3></div>
          <table className="w-full text-sm resp-card">
            <thead><tr className="border-b border-slate-700/50 text-[11px] text-slate-400 uppercase">
              <th className="text-left px-4 py-2 font-medium">Job</th>
              <th className="text-left px-4 py-2 font-medium">Schema</th>
              <th className="text-center px-4 py-2 font-medium">Aan</th>
              <th className="text-left px-4 py-2 font-medium">Laatste run</th>
              <th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>{group.jobs.map(j => {
              const draft = drafts[j.key];
              const cronVal = draft !== undefined ? draft : j.cron;
              const dirty = draft !== undefined && draft !== j.cron;
              const badge = JOB_MODE_BADGE[j.mode] || JOB_MODE_BADGE.refresh;
              const isBusy = !!busy[j.key];
              return (
                <React.Fragment key={j.key}>
                <tr className="border-b border-slate-700/30 align-top">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-200">{j.label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${badge.cls}`}>{badge.label}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 max-w-md">{j.description}</div>
                    {j.paramsKind === 'fee-mail-targets' && <JobFeeMailDoelen token={token} job={j} onSaved={(d) => setJobs((prev) => prev.map((x) => (x.key === j.key ? { ...x, ...d } : x)))} />}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <input value={cronVal} onChange={e => setDrafts(d => ({ ...d, [j.key]: e.target.value }))} spellCheck={false} className="w-32 px-2 py-1 bg-slate-700/50 border border-slate-600/50 rounded-lg text-xs font-mono text-slate-100 focus:outline-none focus:border-lime-500" />
                      <select value="" onChange={e => { if (e.target.value) setDrafts(d => ({ ...d, [j.key]: e.target.value })); }} className="px-1.5 py-1 bg-slate-700/50 border border-slate-600/50 rounded-lg text-xs text-slate-300 focus:outline-none focus:border-lime-500" title="Kies een preset">
                        <option value="">Preset…</option>
                        {JOB_CRON_PRESETS.map(p => <option key={p.cron} value={p.cron}>{p.label}</option>)}
                      </select>
                      {dirty && <button onClick={() => saveJob(j.key, { cron: cronVal })} disabled={isBusy} className="px-2 py-1 bg-lime-500 hover:bg-lime-400 text-slate-900 text-xs font-semibold rounded-lg disabled:opacity-40">Opslaan</button>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">{j.cronText || j.cron}{dirty ? ' · niet opgeslagen' : ''}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input type="checkbox" checked={j.enabled} disabled={isBusy} onChange={e => saveJob(j.key, { enabled: e.target.checked })} className="w-4 h-4 accent-lime-500 cursor-pointer" />
                  </td>
                  <td className="px-4 py-3">
                    <JobStatusBadge run={j.lastRun} running={j.running} />
                    {j.lastRun && <div className="text-[11px] text-slate-500 mt-1">
                      {jobFmtDateTime(j.lastRun.finishedAt || j.lastRun.startedAt)}
                      {j.lastRun.records != null && <> · {j.lastRun.records.toLocaleString('nl-BE')} records</>}
                      {j.lastRun.durationMs != null && <> · {jobFmtDuration(j.lastRun.durationMs)}</>}
                    </div>}
                    {j.lastRun && j.lastRun.message && <div className={`text-[11px] mt-0.5 max-w-xs truncate ${j.lastRun.status === 'error' ? 'text-red-400/80' : 'text-slate-400'}`} title={j.lastRun.message}>{j.lastRun.message}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => runNow(j.key)} disabled={isBusy || j.running} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-700/60 hover:bg-slate-600/60 text-slate-100 text-xs font-medium rounded-lg disabled:opacity-40"><Play size={13} />Nu uitvoeren</button>
                    <button onClick={() => openHistory(j.key)} className="ml-1 px-2 py-1.5 text-xs text-slate-400 hover:text-slate-200">{histKey === j.key ? 'Verberg' : 'Historiek'}</button>
                  </td>
                </tr>
                {histKey === j.key && <tr className="bg-slate-800/30"><td colSpan={5} className="px-4 py-3">
                  {histLoading ? <div className="text-xs text-slate-500">Laden…</div> : hist.length === 0 ? <div className="text-xs text-slate-500">Nog geen runs.</div> : (
                    <div className="overflow-x-auto"><table className="w-full text-xs">
                      <thead><tr className="text-slate-500"><th className="text-left py-1 font-medium">Gestart</th><th className="text-left py-1 font-medium">Trigger</th><th className="text-left py-1 font-medium">Status</th><th className="text-right py-1 font-medium">Records</th><th className="text-right py-1 font-medium">Duur</th><th className="text-left py-1 font-medium pl-3">Melding</th></tr></thead>
                      <tbody>{hist.map(h => <tr key={h.id} className="border-t border-slate-700/30">
                        <td className="py-1 text-slate-300">{jobFmtDateTime(h.started_at)}</td>
                        <td className="py-1 text-slate-400">{h.trigger || '—'}</td>
                        <td className="py-1"><span className={h.status === 'success' ? 'text-emerald-400' : h.status === 'error' ? 'text-red-400' : 'text-amber-400'}>{h.status}</span></td>
                        <td className="py-1 text-right text-slate-300">{h.records != null ? h.records.toLocaleString('nl-BE') : '—'}</td>
                        <td className="py-1 text-right text-slate-400">{jobFmtDuration(h.duration_ms)}</td>
                        <td className="py-1 pl-3 text-red-400/80 max-w-xs truncate" title={h.message || ''}>{h.message || ''}</td>
                      </tr>)}</tbody>
                    </table></div>
                  )}
                </td></tr>}
                </React.Fragment>
              );
            })}</tbody>
          </table>
        </Card>
      ))}
    </div>
  );
};

// ── Indexparameters (eigen beheerpagina) ─────────────────────────────────────
// Stond tot 01-09-2026 als kaart ONDERAAN "Algemene instellingen". Daar was ze
// niet te vinden: die pagina gaat over API-keys, deep-link-URL's en mailserver —
// technische serverinstellingen die je één keer zet — terwijl deze tabel elke
// maand een nieuwe waarde krijgt. Nu een eigen menu-item onder Beheer, zodat het
// bijwerken van de maandindex één klik is en niet het onderaan scrollen van een
// pagina waar je verder niets te zoeken hebt.
//
// Alleen admin: de route achter deze pagina (PUT /api/index-parameters) staat op
// requireRole(ADMIN). Elke andere rol mag de waarden wél LEZEN — Metering heeft ze
// nodig om een verkoopprijs te tonen — maar niet wijzigen.
const IndexParametersPage = ({ token }) => {
  // ── Indexparameters ───────────────────────────────────────────────────────
  // De maandprijs waarmee een NIET-dynamisch contract afrekent (Belpex_RLP voor
  // afname elektriciteit, Belpex_SPP voor injectie, TTF RLP voor gas). Een
  // dynamisch contract rekent met de echte spotprijs en staat hier dus niet.
  // Waarden in c€/kWh — zoals de index gepubliceerd wordt en zoals ze hier
  // ingetypt worden; de omrekening naar EUR/MWh (× 10) gebeurt bij het rekenen,
  // in src/indexPrijs.js.
  const IDX_MAANDEN = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
  const idxSleutel = (code, jaar, maand) => `${code}|${jaar}-${String(maand).padStart(2, '0')}`;
  // Belgische weergave in het veld (4,81) — bij het opslaan gaat de komma weer
  // naar een punt. Het veld toont wat de admin typt, niet wat de DB opslaat.
  // Minstens twee decimalen: een prijstabel waarin "3,4" naast "3,32" staat leest
  // als een typfout. Maximaal vier, want zo fijn mag de index opgeslagen worden.
  const idxToon = (v) => (v == null || v === ''
    ? ''
    : new Intl.NumberFormat('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: false }).format(Number(v)));
  const idxNaarGetal = (s) => {
    const t = String(s ?? '').trim();
    if (!t) return null;
    const n = Number(t.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const [idxData, setIdxData] = useState(null);
  const [idxInputs, setIdxInputs] = useState({});
  const [idxJaren, setIdxJaren] = useState([]);
  const [idxLoading, setIdxLoading] = useState(false);
  const [idxBusy, setIdxBusy] = useState(false);
  const [idxMsg, setIdxMsg] = useState({ error: '', success: '' });

  const idxVulInputs = (parameters, jaren) => {
    const velden = {};
    for (const c of INDEX_CODES) {
      for (const jaar of jaren) {
        for (let m = 1; m <= 12; m++) {
          const sleutel = idxSleutel(c.code, jaar, m);
          velden[sleutel] = idxToon(parameters?.[c.code]?.[`${jaar}-${String(m).padStart(2, '0')}`]);
        }
      }
    }
    return velden;
  };

  const loadIndex = () => {
    if (!token) return;
    setIdxLoading(true);
    fetch('/api/index-parameters', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(d => {
        // Altijd het huidige en het volgende jaar tonen: een nieuw jaar invullen
        // mag geen knop vereisen die iemand moet vinden.
        const nu = new Date().getFullYear();
        const jaren = [...new Set([...(d.jaren || []), nu, nu + 1])].sort((a, b) => a - b);
        setIdxData(d); setIdxJaren(jaren); setIdxInputs(idxVulInputs(d.parameters, jaren));
        setIdxMsg({ error: '', success: '' });
      })
      .catch(e => setIdxMsg({ error: e.message, success: '' }))
      .finally(() => setIdxLoading(false));
  };
  useEffect(() => { loadIndex(); /* eslint-disable-next-line */ }, [token]);

  // Welke cellen wijken af van wat opgeslagen is? Alleen die gaan naar de server
  // (een jaar dat niemand aanraakte blijft onaangeroerd, ook zijn updated_at),
  // en ze zijn op het scherm gemarkeerd zodat je vóór het opslaan ziet wat er
  // vertrekt — en erna dat alles weg is (vraag 30-10-2026: "niet duidelijk dat
  // het opgeslagen was").
  const idxGewijzigd = useMemo(() => {
    const entries = [];
    const sleutels = new Set();
    for (const c of INDEX_CODES) {
      for (const jaar of idxJaren) {
        for (let m = 1; m <= 12; m++) {
          const maandSleutel = `${jaar}-${String(m).padStart(2, '0')}`;
          const oud = idxData?.parameters?.[c.code]?.[maandSleutel];
          const nieuw = idxNaarGetal(idxInputs[idxSleutel(c.code, jaar, m)]);
          const oudN = oud == null ? null : Number(oud);
          if (nieuw === oudN) continue;
          entries.push({ code: c.code, jaar, maand: m, waarde: nieuw });
          sleutels.add(idxSleutel(c.code, jaar, m));
        }
      }
    }
    return { entries, sleutels };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idxData, idxInputs, idxJaren]);

  const saveIndex = async () => {
    setIdxBusy(true); setIdxMsg({ error: '', success: '' });
    try {
      const { entries } = idxGewijzigd;
      if (!entries.length) { setIdxMsg({ error: '', success: 'Er is niets gewijzigd; alles staat al opgeslagen.' }); toast.info('Niets te bewaren: er is geen cel gewijzigd.'); return; }
      const r = await fetch('/api/index-parameters', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entries }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setIdxData(p => ({ ...(p || {}), parameters: d.parameters }));
      setIdxInputs(idxVulInputs(d.parameters, idxJaren));
      const n = Number(d.opgeslagen) || entries.length;
      const tekst = `${n} ${n === 1 ? 'waarde' : 'waarden'} opgeslagen.`;
      setIdxMsg({ error: '', success: tekst });
      // De knop staat onder drie lange tabellen; de melding bovenaan de kaart
      // viel buiten beeld. Een toast is altijd zichtbaar.
      toast.success(`Indexparameters: ${tekst}`);
    } catch (e) { setIdxMsg({ error: e.message, success: '' }); toast.error(`Opslaan mislukt: ${e.message}`); }
    finally { setIdxBusy(false); }
  };
  const idxAantal = idxGewijzigd.entries.length;

  return (
    <div className="space-y-6">
      <PageHeader title="Indexparameters" subtitle="Maandindex voor niet-dynamische contracten" />


      {/* ── Indexparameters ────────────────────────────────────────────────
          Eén tabel per reeks, maanden als rijen en jaren als kolommen: zo lees je
          een reeks van boven naar beneden zoals ze gepubliceerd wordt, en staan
          twee jaren naast elkaar om te vergelijken. Een lege cel is geen nul maar
          "niet gekend" — de Metering-grafiek laat die maand dan zonder prijs. */}
      <Card>
        <h4 className="text-sm font-semibold text-lime-400 uppercase mb-1 flex items-center gap-2"><TrendingUp size={15} />Indexparameters</h4>
        <p className="text-xs text-slate-400 mb-1">
          De maandprijs waarmee een <strong>niet-dynamisch</strong> contract afrekent. De contractprijs (verkoopprijs) is <span className="font-mono">coeff × prijs + majoratie</span>; bij een dynamische
          prijsformule (Belpex_RLP_Hourly, Belpex_SPP_Hourly, Belpex, Hourly Belpex) is die prijs de echte spotprijs per kwartier, bij de formules hieronder deze maandwaarde.
          Ze voedt de prijsreeks en de contractprijs op de Metering-pagina (module Metering én de tab in Customer 360).
        </p>
        <p className="text-xs text-slate-500 mb-4">
          Waarden in <strong>c€/kWh</strong>, zoals de index gepubliceerd wordt (4,81). Bij het rekenen gaat dat × 10 naar EUR/MWh — de eenheid van de coëfficiënt en de majoratie op de factuur.
          Een <strong>lege cel</strong> betekent "nog niet gekend": die maand krijgt geen prijs, in plaats van een 0.
        </p>
        {/* Een laadfout hoort bovenaan; de uitkomst van "Opslaan" staat naast de knop (onderaan, plakkend) én als toast. */}
        {idxMsg.error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl text-sm mb-3">{idxMsg.error}</div>}
        {idxLoading ? <div className="flex justify-center py-6"><Spinner size={24} /></div> : (
          <div className="space-y-5">
            {INDEX_CODES.map(c => (
              <div key={c.code} className="bg-slate-700/30 border border-slate-600/40 rounded-xl p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <span className="text-sm font-medium text-slate-200">{c.label}</span>
                  <span className="text-[11px] text-slate-500">{c.omschrijving} · c€/kWh</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="text-left font-medium py-1 pr-3">Maand</th>
                        {idxJaren.map(j => <th key={j} className="font-medium py-1 px-1 text-center w-24">{j}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {IDX_MAANDEN.map((naam, i) => (
                        <tr key={naam}>
                          <td className="py-0.5 pr-3 text-slate-400 whitespace-nowrap">{naam}</td>
                          {idxJaren.map(j => (
                            <td key={j} className="py-0.5 px-1">
                              <input
                                value={idxInputs[idxSleutel(c.code, j, i + 1)] ?? ''}
                                onChange={e => { setIdxInputs(p => ({ ...p, [idxSleutel(c.code, j, i + 1)]: e.target.value })); setIdxMsg(m => (m.success || m.error ? { error: '', success: '' } : m)); }}
                                inputMode="decimal"
                                placeholder="—"
                                aria-label={`${c.label} ${naam} ${j}`}
                                title={idxGewijzigd.sleutels.has(idxSleutel(c.code, j, i + 1)) ? 'Gewijzigd, nog niet opgeslagen' : undefined}
                                className={`w-24 px-2 py-1 bg-slate-700/50 border rounded-lg text-sm text-slate-100 text-right placeholder-slate-600 focus:outline-none focus:border-lime-500 tabular-nums ${idxGewijzigd.sleutels.has(idxSleutel(c.code, j, i + 1)) ? 'border-amber-400 bg-amber-400/10' : 'border-slate-600/50'}`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {/* Knoppenrij blijft onderaan in beeld plakken: de tabellen zijn lang en
                de uitkomst van "Opslaan" moet naast de knop staan, niet bovenaan. */}
            <div className="flex flex-wrap gap-2 items-center sticky bottom-0 -mx-1 px-1 py-2 bg-slate-800/95 backdrop-blur-sm border-t border-slate-700/60" style={{ bottom: 'var(--tabbar-h, 0px)' }}>
              <button onClick={saveIndex} disabled={idxBusy} title={idxAantal ? `${idxAantal} gewijzigde cel(len) opslaan` : 'Er is niets gewijzigd'}
                className={`px-4 py-2 font-semibold rounded-lg transition-all disabled:opacity-40 text-sm flex items-center gap-2 ${idxAantal ? 'bg-lime-500 hover:bg-lime-400 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>
                {idxBusy ? <><Spinner size={14} />Bezig…</> : idxAantal ? `Opslaan (${idxAantal})` : 'Opslaan'}
              </button>
              <button onClick={() => setIdxJaren(j => [...j, (j[j.length - 1] || new Date().getFullYear()) + 1])}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-all text-sm flex items-center gap-2"><Plus size={14} />Jaar toevoegen</button>
              <button onClick={loadIndex} disabled={idxBusy} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-all disabled:opacity-40 text-sm">Herstellen</button>
              {idxMsg.success && <span className="text-sm text-lime-400 flex items-center gap-1.5"><Check size={15} />{idxMsg.success}</span>}
              {idxMsg.error && <span className="text-sm text-red-400 flex items-center gap-1.5"><AlertCircle size={15} />{idxMsg.error}</span>}
              {!idxMsg.success && !idxMsg.error && (idxAantal
                ? <span className="text-xs text-amber-400">{idxAantal} {idxAantal === 1 ? 'cel gewijzigd' : 'cellen gewijzigd'}, nog niet opgeslagen.</span>
                : <span className="text-[11px] text-slate-500">Alleen gewijzigde cellen worden opgeslagen.</span>)}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const GeneralSettingsPage = ({ token }) => {

  // Externe API-keys. Status komt van de server zonder de volledige key te tonen.
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysError, setApiKeysError] = useState('');
  const [apiKeyInputs, setApiKeyInputs] = useState({});
  const [apiKeyBusy, setApiKeyBusy] = useState('');
  const [apiKeySuccess, setApiKeySuccess] = useState('');

  const loadApiKeys = () => {
    if (!token) return;
    setApiKeysLoading(true);
    fetch('/api/settings/api-keys', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(d => { setApiKeys(d.keys || []); setApiKeysError(''); })
      .catch(e => setApiKeysError(e.message))
      .finally(() => setApiKeysLoading(false));
  };
  useEffect(() => { loadApiKeys(); /* eslint-disable-next-line */ }, [token]);

  const saveApiKey = async (key, value) => {
    setApiKeyBusy(key); setApiKeysError(''); setApiKeySuccess('');
    try {
      const r = await fetch('/api/settings/api-keys', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: value }),
      });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      const d = await r.json();
      setApiKeys(d.keys || []);
      setApiKeyInputs(p => ({ ...p, [key]: '' }));
      setApiKeySuccess(value.trim() === '' ? 'Key gewist — terug naar env-var fallback.' : 'Key opgeslagen.');
    } catch (err) { setApiKeysError(err.message); }
    finally { setApiKeyBusy(''); }
  };

  // Mailserver-configuratie (admin): toont de huidige config + bewerken + testmail.
  const [mailCfg, setMailCfg] = useState(null);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailMsg, setMailMsg] = useState({ error: '', success: '' });
  const [mailInputs, setMailInputs] = useState(null);
  const [mailBusy, setMailBusy] = useState(false);
  const [mailTestTo, setMailTestTo] = useState('');
  const [mailTesting, setMailTesting] = useState(false);

  const loadMail = () => {
    if (!token) return;
    setMailLoading(true);
    fetch('/api/settings/email', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(d => {
        setMailCfg(d);
        setMailInputs({ mail_provider: d.provider || 'smtp', mail_from: d.from || '', smtp_host: d.smtp.host || '', smtp_port: String(d.smtp.port || 587), smtp_secure: d.smtp.secure ? 'true' : 'false', smtp_user: d.smtp.user || '', smtp_password: '', resend_api_key: '' });
      })
      .catch(e => setMailMsg({ error: e.message, success: '' }))
      .finally(() => setMailLoading(false));
  };
  useEffect(() => { loadMail(); /* eslint-disable-next-line */ }, [token]);

  const saveMail = async () => {
    setMailBusy(true); setMailMsg({ error: '', success: '' });
    try {
      const i = mailInputs;
      const body = { mail_provider: i.mail_provider, mail_from: i.mail_from, smtp_host: i.smtp_host, smtp_port: i.smtp_port, smtp_secure: i.smtp_secure, smtp_user: i.smtp_user };
      if (i.smtp_password.trim()) body.smtp_password = i.smtp_password; // leeg = ongewijzigd
      if (i.resend_api_key.trim()) body.resend_api_key = i.resend_api_key;
      const r = await fetch('/api/settings/email', { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setMailCfg(d);
      setMailInputs(p => ({ ...p, smtp_password: '', resend_api_key: '' }));
      setMailMsg({ error: '', success: 'Mailserver-configuratie opgeslagen.' });
    } catch (e) { setMailMsg({ error: e.message, success: '' }); }
    finally { setMailBusy(false); }
  };

  const testMail = async () => {
    setMailTesting(true); setMailMsg({ error: '', success: '' });
    try {
      const r = await fetch('/api/settings/email/test', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ to: mailTestTo.trim() || undefined }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setMailMsg({ error: '', success: `Testmail verstuurd naar ${d.to}.` });
    } catch (e) { setMailMsg({ error: e.message, success: '' }); }
    finally { setMailTesting(false); }
  };


  return (
    <div className="space-y-6">
      <PageHeader title="Algemene instellingen" subtitle="Label-onafhankelijke configuratie voor het hele platform." />

      <Card>
        <h4 className="text-sm font-semibold text-lime-400 uppercase mb-1 flex items-center gap-2"><KeyRound size={15} />Externe API-keys en koppelingen</h4>
        <p className="text-xs text-slate-400 mb-4">Server-keys voor Energy Together en Lynx, plus het technische partneradres voor de Helpdesk. Een hier ingestelde waarde overschrijft de server-env-var. Wissen valt terug op de env-var. Een geheime key wordt nooit volledig getoond.</p>
        {apiKeysError && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl text-sm mb-3">{apiKeysError}</div>}
        {apiKeySuccess && <div className="bg-lime-500/10 border border-lime-500/30 text-lime-400 px-4 py-2.5 rounded-xl text-sm mb-3 flex items-center gap-2"><Check size={15} />{apiKeySuccess}</div>}
        {apiKeysLoading ? <div className="flex justify-center py-6"><Spinner size={24} /></div> :
        <div className="space-y-4">
          {apiKeys.map(k => <div key={k.key} className="bg-slate-700/30 border border-slate-600/40 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span className="text-sm font-medium text-slate-200">{k.label}</span>
              {k.configured
                ? <span className="inline-flex items-center gap-1.5 text-xs font-medium text-lime-400 bg-lime-500/10 border border-lime-500/30 px-2.5 py-1 rounded-full"><Check size={12} />{k.source === 'db' ? 'Ingesteld via dashboard' : 'Via server-env (fallback)'}{k.last4 ? ` · ••••${k.last4}` : ''}</span>
                : <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full"><AlertCircle size={12} />Niet ingesteld</span>}
            </div>
            {k.hint && <p className="text-xs text-slate-400 mb-2">{k.hint}</p>}
            {k.plain && k.configured && <p className="text-xs text-slate-300 mb-2">Huidige waarde: <span className="font-mono">{k.value}</span></p>}
            {k.source === 'db' && k.updated_at && <p className="text-[11px] text-slate-500 mb-2">Laatst gewijzigd: {new Date(k.updated_at).toLocaleString('nl-NL')}</p>}
            <div className="flex gap-2 flex-wrap">
              <input
                type={k.plain ? 'text' : 'password'}
                autoComplete={k.plain ? 'off' : 'new-password'}
                value={apiKeyInputs[k.key] || ''}
                onChange={e => setApiKeyInputs(p => ({ ...p, [k.key]: e.target.value }))}
                placeholder={k.plain ? 'bv. partnerportaal@voorbeeld.be' : 'Nieuwe key plakken…'}
                className="flex-1 min-w-48 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500 font-mono"
              />
              <button
                onClick={() => saveApiKey(k.key, apiKeyInputs[k.key] || '')}
                disabled={apiKeyBusy === k.key || !(apiKeyInputs[k.key] || '').trim()}
                className="px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg transition-all disabled:opacity-40 text-sm"
              >{apiKeyBusy === k.key ? 'Bezig…' : 'Opslaan'}</button>
              <button
                onClick={() => { if (window.confirm(`${k.label} wissen en terugvallen op de server-env-var?`)) saveApiKey(k.key, ''); }}
                disabled={apiKeyBusy === k.key || k.source !== 'db'}
                title={k.source === 'db' ? 'Wis de dashboard-key (terug naar env-var)' : 'Geen dashboard-key ingesteld'}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-all disabled:opacity-40 text-sm"
              >Wissen</button>
            </div>
          </div>)}
        </div>}
      </Card>

      <Card>
        <h4 className="text-sm font-semibold text-lime-400 uppercase mb-1 flex items-center gap-2"><Send size={15} />Mailserver</h4>
        <p className="text-xs text-slate-400 mb-4">Uitgaande e-mail (uitnodigingen, notificaties). Kies een SMTP-server (bv. Energy Together) of een API-provider (Resend). DB-waarden overschrijven de server-env; wissen valt terug op env. Wachtwoord/API-key worden nooit getoond.</p>
        {mailMsg.error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl text-sm mb-3">{mailMsg.error}</div>}
        {mailMsg.success && <div className="bg-lime-500/10 border border-lime-500/30 text-lime-400 px-4 py-2.5 rounded-xl text-sm mb-3 flex items-center gap-2"><Check size={15} />{mailMsg.success}</div>}
        {mailLoading || !mailInputs ? <div className="flex justify-center py-6"><Spinner size={24} /></div> : <>
          <div className="bg-slate-700/30 border border-slate-600/40 rounded-xl p-3 mb-4 text-xs text-slate-300 flex flex-wrap gap-x-2 gap-y-1">
            <span className="text-slate-500">Huidig actief:</span>
            <span className="font-medium text-slate-100">{mailCfg.provider === 'smtp' ? `SMTP · ${mailCfg.smtp.host || '(geen host)'}:${mailCfg.smtp.port}` : `Resend${mailCfg.resend.configured ? ` · ••••${mailCfg.resend.last4}` : ' (geen key)'}`}</span>
            <span className="text-slate-500">· afzender</span><span className="font-medium text-slate-100">{mailCfg.fromEffective}</span>
            <span className="text-slate-500">· bron {mailCfg.sources.mail_provider === 'db' ? 'dashboard' : 'server-env (fallback)'}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-slate-400">Provider</span>
              <select value={mailInputs.mail_provider} onChange={e => setMailInputs(p => ({ ...p, mail_provider: e.target.value }))} className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
                <option value="smtp">SMTP-server</option>
                <option value="resend">Resend (API)</option>
              </select>
            </label>
            <label className="block"><span className="text-xs text-slate-400">Afzender (from)</span>
              <input value={mailInputs.mail_from} onChange={e => setMailInputs(p => ({ ...p, mail_from: e.target.value }))} placeholder="noreply@energytogether.be" className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500" />
            </label>
          </div>
          {mailInputs.mail_provider === 'smtp' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <p className="sm:col-span-2 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>Op de live omgeving (Render) versturen we via <strong>poort 587</strong> (STARTTLS) met authenticatie. Render draait op AWS en blokkeert uitgaand <strong>poort 25</strong>, dus M365 direct-send op poort 25 werkt daar niet. Laat <strong>"TLS op socket" uit</strong> voor poort 587 (alleen aanvinken bij poort 465).</span>
              </p>
              <label className="block sm:col-span-2"><span className="text-xs text-slate-400">SMTP-host</span>
                <input value={mailInputs.smtp_host} onChange={e => setMailInputs(p => ({ ...p, smtp_host: e.target.value }))} placeholder="energytogether-be.mail.protection.outlook.com" className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500 font-mono" /></label>
              <label className="block"><span className="text-xs text-slate-400">Poort</span>
                <input value={mailInputs.smtp_port} onChange={e => setMailInputs(p => ({ ...p, smtp_port: e.target.value.replace(/[^0-9]/g, '') }))} placeholder="587" className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500" /></label>
              <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={mailInputs.smtp_secure === 'true'} onChange={e => setMailInputs(p => ({ ...p, smtp_secure: e.target.checked ? 'true' : 'false' }))} className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-4 h-4" /><span className="text-xs text-slate-300">TLS op socket — alleen voor poort 465 (uit laten voor 587)</span></label>
              <label className="block"><span className="text-xs text-slate-400">Gebruiker (optioneel)</span>
                <input value={mailInputs.smtp_user} onChange={e => setMailInputs(p => ({ ...p, smtp_user: e.target.value }))} placeholder="leeg = geen auth (direct send)" autoComplete="off" className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500" /></label>
              <label className="block"><span className="text-xs text-slate-400">Wachtwoord {mailCfg.smtp.passwordConfigured ? '(ingesteld)' : ''}</span>
                <input type="password" value={mailInputs.smtp_password} onChange={e => setMailInputs(p => ({ ...p, smtp_password: e.target.value }))} placeholder={mailCfg.smtp.passwordConfigured ? 'Ongewijzigd laten…' : 'Wachtwoord…'} autoComplete="new-password" className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500" /></label>
            </div>
          ) : (
            <div className="mt-3">
              <label className="block"><span className="text-xs text-slate-400">Resend API-key {mailCfg.resend.configured ? `(••••${mailCfg.resend.last4})` : ''}</span>
                <input type="password" value={mailInputs.resend_api_key} onChange={e => setMailInputs(p => ({ ...p, resend_api_key: e.target.value }))} placeholder={mailCfg.resend.configured ? 'Ongewijzigd laten…' : 're_…'} autoComplete="new-password" className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500 font-mono" /></label>
            </div>
          )}
          <div className="flex gap-2 flex-wrap mt-4 items-center">
            <button onClick={saveMail} disabled={mailBusy} className="px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg transition-all disabled:opacity-40 text-sm">{mailBusy ? 'Bezig…' : 'Opslaan'}</button>
            <div className="h-6 w-px bg-slate-600 mx-1" />
            <input value={mailTestTo} onChange={e => setMailTestTo(e.target.value)} placeholder="Test versturen naar… (default jezelf)" className="flex-1 min-w-48 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500" />
            <button onClick={testMail} disabled={mailTesting} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-all disabled:opacity-40 text-sm flex items-center gap-2"><Send size={14} />{mailTesting ? 'Versturen…' : 'Test versturen'}</button>
          </div>
        </>}
      </Card>
    </div>
  );
};

// Rij-achtergrondkleur per CP-status, zodat orderregels visueel meteen
// herkenbaar zijn (gebruikt in de Orders-tabel én de Customer 360 orders-tabel).
//   draft → donkergrijs · ext → rood · in/int_in → oranje · int_out → paars · owned → groen
function cpStatusRowClass(status) {
  const s = (status || '').trim().toLowerCase();
  if (s === 'draft') return 'bg-slate-600/30 hover:bg-slate-600/40';
  if (s === 'ext') return 'bg-red-900/30 hover:bg-red-900/40';
  if (s === 'in' || s === 'int_in') return 'bg-orange-900/30 hover:bg-orange-900/40';
  if (s === 'int_out') return 'bg-purple-900/30 hover:bg-purple-900/40';
  if (s === 'owned') return 'bg-green-900/30 hover:bg-green-900/40';
  return 'hover:bg-slate-700/20';
}

// CP-status-groepen voor de CP-status-filter (Orders + Sales): "Active" = actieve
// aansluitingen, "External" = extern/uitstroom/concept. Andere (onbekende)
// statussen worden apart in de dropdown getoond. Vergelijking altijd op lowercase.
const CP_ACTIVE = new Set(['owned', 'in', 'int_in']);
const CP_EXTERNAL = new Set(['ext', 'external', 'int_out', 'draft']);

// Inhoud van een documentatiepaneel (samenvatting + punten + formules). Wordt
// getoond in de overlay achter het icoontje naast de paginatitel; zie
// PageDocsIcons/DocsModal. Inhoud komt uit moduleDocs.js.
//
// Opmaak van de tekst: de samenvatting staat groter en lichter, een alinea die
// enkel "In het kort:" zegt wordt een klein kopje, en een punt dat begint met
// "Label: …" (kort label, geen punt vóór de dubbele punt) krijgt dat label vet.
// Secties krijgen een titel met een accentstreepje. De technische beschrijving
// gebruikt dezelfde component; daar vallen die regels vanzelf mee (weinig
// "Label:"-punten, geen "In het kort").
const DOCS_LABEL_RE = /^((?:[^.:!?\s]+\s?){1,4}):\s+(.+)$/s;   // hooguit vier woorden vóór de dubbele punt
// Een http(s)-adres in een documentatietekst wordt een klikbare link (nieuw
// tabblad), bv. de verwijzing naar docs/helpdesk-ai.md in de repository. Een
// afsluitend leesteken hoort niet bij het adres.
const DOCS_URL_RE = /(https?:\/\/[^\s<>"')]+?)(?=[.,;:)]*(?:\s|$))/g;
const docsTekst = (tekst) => {
  if (typeof tekst !== 'string' || !/https?:\/\//.test(tekst)) return tekst;
  return tekst.split(DOCS_URL_RE).map((deel, i) => (i % 2 === 1
    ? <a key={i} href={deel} target="_blank" rel="noopener noreferrer" className="text-lime-400 hover:text-lime-300 underline break-all">{deel}</a>
    : deel));
};
const DocsPunt = ({ tekst }) => {
  const m = typeof tekst === 'string' ? tekst.match(DOCS_LABEL_RE) : null;
  if (!m) return <li className="leading-relaxed">{docsTekst(tekst)}</li>;
  return <li className="leading-relaxed"><span className="font-semibold text-slate-200">{m[1]}:</span> {docsTekst(m[2])}</li>;
};
const DocsLijst = ({ punten }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-slate-400 marker:text-lime-500">{punten.map((p, i) => <DocsPunt key={i} tekst={p} />)}</ul>
);
const DocsAlinea = ({ tekst }) => (
  /^in het kort:?$/i.test(String(tekst || '').trim())
    ? <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 pt-1">{tekst}</p>
    : <p className="leading-relaxed">{docsTekst(tekst)}</p>
);
const ModuleDocsSection = ({ data }) => (
  <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 sm:p-5 mt-2 text-sm text-slate-300 space-y-3">
    {data.summary && <p className="text-[15px] leading-relaxed text-slate-200">{data.summary}</p>}
    {/* `paragraphs` voor uitleg die niet in één alinea past (bv. de achtergrond bij
        Bill Shock). Staat tussen de samenvatting en de opsomming. */}
    {Array.isArray(data.paragraphs) && data.paragraphs.map((p, i) => <DocsAlinea key={i} tekst={p} />)}
    {Array.isArray(data.points) && data.points.length > 0 && <DocsLijst punten={data.points} />}
    {/* `outro` voor een afsluitende alinea NA de opsomming. */}
    {Array.isArray(data.outro) && data.outro.map((p, i) => <p key={i} className="leading-relaxed">{p}</p>)}
    {/* `sections` = [{ title, intro?, points }] voor een afgebakend onderwerp NA de
        hoofdopsomming (bv. de Stop Contract-scenario's bij Portfolio en Retention). */}
    {Array.isArray(data.sections) && data.sections.map((s, i) => (
      <div key={i} className="space-y-2 pt-3 mt-1 border-t border-slate-700/40">
        {s.title && (
          <p className="flex items-center gap-2 font-semibold text-slate-100">
            <span className="inline-block w-1 h-4 rounded-full bg-lime-500 shrink-0" aria-hidden="true" />
            {s.title}
          </p>
        )}
        {s.intro && <p className="leading-relaxed">{docsTekst(s.intro)}</p>}
        {Array.isArray(s.points) && s.points.length > 0 && <DocsLijst punten={s.points} />}
      </div>
    ))}
    {Array.isArray(data.formulas) && data.formulas.length > 0 && (
      <div className="bg-slate-950/50 border border-slate-700/50 rounded-lg p-3 font-mono text-xs text-lime-300 space-y-1 overflow-x-auto">
        {data.formulas.map((f, i) => <div key={i}>{f}</div>)}
      </div>
    )}
    {/* `image` = { src, alt, caption } voor een schema dat de tekst ondersteunt
        (bv. de herinnerings- en dropprocedure bij Retention). Staat in public/,
        dus merk-neutraal: statische bestanden zijn niet per rol filterbaar. */}
    {data.image && data.image.src && (
      <figure className="space-y-1 pt-2">
        <a href={data.image.src} target="_blank" rel="noreferrer" title="Openen op ware grootte">
          <img src={data.image.src} alt={data.image.alt || ''} className="w-full rounded-lg border border-slate-700/50 bg-white" />
        </a>
        {data.image.caption && <figcaption className="text-xs text-slate-500">{data.image.caption}</figcaption>}
      </figure>
    )}
  </div>
);

// De gegevens van de LAATST AFGERONDE workflow (kolommen "Workflow — laatste",
// "WL Stop datum", "WL Stop contract" en "WL Stop reason", het bijhorende filter en
// het detailscherm) zijn voorbehouden aan admin en ET-dossierbeheerder. Eén bron,
// zodat kolom, filter, detailvenster én export niet uit elkaar kunnen lopen.
const WL_VELDEN = ['workflowLastFlow', 'workflowLastStep', 'workflowLastLossDate', 'workflowLastStopType', 'workflowLastStopReason'];
const magWorkflowLaatste = (role) => role === 'admin' || role === 'et_dossier';
// Strip de WL-velden uit exportrijen wanneer de gebruiker de kolommen niet ziet.
// De export leidt zijn kolommen af uit de keys van de eerste rij, dus weglaten is
// hier genoeg — en het moet, anders levert één klik op Export alsnog de gegevens.
const zonderWlVelden = (rijen, mag) => (mag ? rijen : rijen.map((o) => {
  const kopie = { ...o };
  for (const k of WL_VELDEN) delete kopie[k];
  return kopie;
}));

// Gedeelde, veld-overschrijdende zoekmatch op een order. Gebruikt in zowel de
// Orders-tabel als de Customer 360-zoeksuggesties. Zoekt op klantnaam, bedrijfs-
// naam, klantnummer, e-mail, telefoon, BTW-nummer, GSRN, adres, verkoper, team,
// ratecard, customer tag en bankrekening. Voor de numerieke velden (telefoon, BTW,
// GSRN, klantnummer, IBAN) ook een cijfer/alfanumeriek-genormaliseerde match,
// zodat formaatverschillen (spaties, +, punten) niet uitmaken.
// `zichtbaar` (optioneel): de customer tags die deze rol mag zien. Zonder die set
// blijven de tags buiten de zoektekst — anders zou een verborgen tag alsnog te
// vinden zijn door hem in te typen.
function orderMatchesSearch(o, q, zichtbaar = null) {
  const ql = String(q || '').trim().toLowerCase();
  if (!ql) return true;
  const text = [
    o.customer, o.customerCompanyName, o.customerReference, o.customerEmail,
    o.customerPhone, o.customerVat, o.connectionPointGSRN, o.connectionPointAddress,
    o.salesperson, o.salespersonTeam, o.ratecardName, zichtbareTagsTekst(o.partnerTags, zichtbaar), o.customerBankAccount,
  ];
  for (const f of text) if (f && String(f).toLowerCase().includes(ql)) return true;
  const qd = ql.replace(/[^a-z0-9]/g, '');
  if (qd.length >= 3) {
    for (const f of [o.customerPhone, o.customerVat, o.connectionPointGSRN, o.customerReference, o.customerBankAccount]) {
      if (f && String(f).toLowerCase().replace(/[^a-z0-9]/g, '').includes(qd)) return true;
    }
  }
  return false;
}

// Filter-persistentie per pagina. Herstelt opgeslagen filters uit de SESSIE-opslag
// (zie filterStorage.js: filters overleven een refresh, maar niet een nieuwe
// aanmelding) bij de EERSTE keer dat currentUser geladen is (dus NA het
// scope-default-effect dat hierboven in de pagina staat — deze hook moet ná dat effect worden aangeroepen,
// zodat het herstel wint). RAW herstel via applySaved (geen clamp): het zijn de
// eigen filters van de gebruiker en de data is server-side gescopet. Bij een latere
// user-wissel (impersonate) wordt NIET hersteld → de pagina valt terug op de
// scope-default van de nieuwe gebruiker. Persisteert `snapshot` bij elke wijziging.
// ── Schermvoorkeur per gebruiker, over sessies heen ──────────────────────────
// Voor een keuze die iemand één keer maakt en overal wil terugzien (bv. "de
// kengetallen op Helpdesk ingeklapt"). Bewaard op de server (/api/ui-prefs, per
// gebruiker) én gespiegeld in localStorage, zodat het scherm meteen in de juiste
// stand opent en de server daarna het laatste woord heeft. Alle hooks delen één
// GET per sessie (uiPrefsLaden), dus een tweede voorkeur kost geen tweede oproep.
let uiPrefsBelofte = null;
const uiPrefsLaden = (token) => {
  if (!uiPrefsBelofte) {
    uiPrefsBelofte = fetch('/api/ui-prefs', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return uiPrefsBelofte;
};
function useUiPref(token, sleutel, standaard) {
  const lsKey = `uiPref:${sleutel}`;
  const [waarde, setWaarde] = useState(() => {
    try { const v = localStorage.getItem(lsKey); return v == null ? standaard : JSON.parse(v); } catch (_) { return standaard; }
  });
  useEffect(() => {
    if (!token) return undefined;
    let weg = false;
    uiPrefsLaden(token).then((alle) => {
      if (weg || !alle || !(sleutel in alle)) return;
      setWaarde(alle[sleutel]);
      try { localStorage.setItem(lsKey, JSON.stringify(alle[sleutel])); } catch (_) { /* vol of geblokkeerd */ }
    });
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sleutel]);
  // De serverwrite wacht even (600 ms): een rekentool roept dit per toetsaanslag
  // aan, en dan hoeft niet elke tussenstand naar de server.
  const timer = useRef(null);
  const zet = useCallback((v) => {
    setWaarde(v);
    try { localStorage.setItem(lsKey, JSON.stringify(v)); } catch (_) { /* vol of geblokkeerd */ }
    uiPrefsBelofte = uiPrefsBelofte ? uiPrefsBelofte.then((alle) => ({ ...(alle || {}), [sleutel]: v })) : null;
    if (!token) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetch(`/api/ui-prefs/${encodeURIComponent(sleutel)}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ value: v }) }).catch(() => {});
    }, 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sleutel]);
  return [waarde, zet];
}

function useFilterPersistence(storageKey, currentUser, snapshot, applySaved) {
  const restored = React.useRef(false);
  React.useEffect(() => {
    if (restored.current) return;
    if (currentUser === null) return; // nog aan het laden — wacht op de scope-velden
    restored.current = true;          // undefined (pagina zonder scope) → meteen herstellen
    const s = readSavedFilters(storageKey);
    if (s) applySaved(migrateSavedFilters(s));   // hernoemde filterwaarden bijwerken
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  const json = JSON.stringify(snapshot);
  React.useEffect(() => {
    if (!restored.current) return;
    writeSavedFilters(storageKey, json);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [json]);
}

// Orderstatussen die als "geannuleerd" tellen. De ET-API levert Engelse codes
// (o.a. 'cancel'); 'geannuleerd' blijft als NL-variant voor de zekerheid.
// Case-insensitief, want de bron kan in casing variëren.
const CANCELLED_ORDER_STATUSES = ['cancel', 'cancelled', 'geannuleerd'];
const isCancelledOrderStatus = (status) => CANCELLED_ORDER_STATUSES.includes(String(status || '').trim().toLowerCase());

// Vrije-tekst zoekfilter voor factuur-/self-bill-records (zelfde velden als de
// Invoices-pagina). Gebruikt door de Revenue-detailtabellen.
function invoiceMatchesSearch(f, q) {
  const term = String(q || '').trim().toLowerCase();
  if (!term) return true;
  const fields = [f.nummer, f.verkoper, f.relatieNaam, f.relatieReferentie, f.relatieBedrijf, f.verkoopteam, f.elGSRN, f.ngGSRN, f.statusBetaling, f.soort];
  return fields.some(v => v && String(v).toLowerCase().includes(term));
}

// "Fee Contracted = Yes": een contracted-stage én niet geannuleerd. Dezelfde
// onderliggende voorwaarde als de Fee-kolommen — ook bruikbaar waar die kolom
// niet getoond wordt (bv. de Orders-pagina).
const CONTRACTED_OPPORTUNITY_STAGES = ['Switch Accepted (st.)', 'Contracted (st.)', 'Switch Accepted (cu.)', 'Contracted (cu.)'];
const isContractedOrder = (o) => CONTRACTED_OPPORTUNITY_STAGES.includes(o.opportunityStage || '') && !isCancelledOrderStatus(o.status);

// Verkoper-/teamcorrectie voor "online" orders. 'Online' en 'OdooBot' zijn geen
// echte verkopers maar tellen WEL als een geldige waarde (een ingevulde verkoper).
const ONLINE_TEAM = 'Online';
const isOnlineSeller = (s) => { const v = String(s || '').trim().toLowerCase(); return v === 'online' || v === 'odoobot'; };
const hasBlankTeam = (o) => !String(o.salespersonTeam || '').trim();
const hasBlankSeller = (o) => !String(o.salesperson || '').trim();
// Regels per order (idempotent — geeft hetzelfde object terug als er niets wijzigt,
// behoudt zo object-identiteit elders):
//   1. lege verkoper → 'Online'
//   2. leeg verkoopteam ÉN verkoper Online/OdooBot → verkoopteam 'Online'
// Regel 1 draait vóór 2, zodat een order zonder verkoper (nu 'Online') ook regel 2
// triggert. Zo verschijnen die orders in "PARTNERVERGOEDING — DETAIL ORDERS" i.p.v.
// weg te vallen op een lege verkoper/leeg verkoopteam.
const applyOnlineOrderRules = (o) => {
  const salesperson = hasBlankSeller(o) ? ONLINE_TEAM : o.salesperson; // ONLINE_TEAM === 'Online'
  const setTeam = hasBlankTeam(o) && isOnlineSeller(salesperson);
  if (salesperson === o.salesperson && !setTeam) return o;
  const next = { ...o, salesperson };
  if (setTeam) next.salespersonTeam = ONLINE_TEAM;
  return next;
};

// Vast jaarlijks recht per EAN dat Energy Together inhoudt (€/EAN/jaar). Wordt van
// de PriceAddition (jaarbedrag) afgetrokken vóór de Fee Subscription-berekening.
// Negatieve uitkomst is toegestaan (gebruikerskeuze); enkel orders ZONDER toeslag
// (PriceAddition 0/leeg) krijgen geen fee en dus geen losse -€30.
const FIXED_ANNUAL_RIGHT_PER_EAN = 30;

// Bepaalt of de Fee Subscription per VOL JAAR (Yearly-tak) of pro rata per maand
// (ProRata-tak) berekend wordt, o.b.v. order.priceAdditionsUnit (ET-veld
// price_additions.per_unit). Echte ET-waarden: 'year' = "Jaar/Year" → Yearly;
// de 'yearprs...'-varianten ("Yearly ProRata Settlement Full First 6 Months",
// FullFirstYear, FullLastYear, …) én leeg → ProRata. Alleen exact year/jaar/yearly
// telt als jaarlijks (NIET startsWith, want 'yearprs...' begint óók met 'year').
const YEARLY_FEE_UNITS = new Set(['year', 'jaar', 'yearly', 'jaar/year', 'year/jaar']);
const isYearlyFeeUnit = (unit) => YEARLY_FEE_UNITS.has(String(unit || '').trim().toLowerCase());

// Voluit geschreven betekenis van de afgekorte ET-waarden van price_additions.
// per_unit, voor de tooltip op de kolom "PriceAddition Unit" — "yearprsff6m"
// zegt op zichzelf niets. Onbekende waarde → geen vertaling, dan tonen we enkel
// de rekenwijze (Yearly of ProRata).
const PRICE_ADDITION_UNIT_LABELS = {
  year: 'Jaar / Year',
  jaar: 'Jaar / Year',
  yearly: 'Jaar / Year',
  yearprs: 'Yearly Pro Rata Settlement',
  yearprsff6m: 'Yearly Pro Rata Settlement — Full First 6 Months',
  yearprsffy: 'Yearly Pro Rata Settlement — Full First Year',
  yearprsfly: 'Yearly Pro Rata Settlement — Full Last Year',
};
// Tooltiptekst: de voluit geschreven naam + hoe de Fee Subscription ermee gerekend
// wordt. Beide delen zijn nuttig; de rekenwijze stond er al.
const priceAdditionUnitTitle = (unit) => {
  const raw = String(unit || '').trim();
  const full = PRICE_ADDITION_UNIT_LABELS[raw.toLowerCase()];
  const wijze = isYearlyFeeUnit(raw) ? 'Jaarlijks → Fee Subscription Yearly' : 'Pro rata → Fee Subscription ProRata';
  if (!raw) return 'Geen eenheid ingevuld — Pro rata → Fee Subscription ProRata';
  return full ? `${full}\n${wijze}` : wijze;
};

// --- Gedeelde Revenue-classificatie (één bron van waarheid) -----------------
// De samenvattingsblokken op de Revenue-pagina moeten een PURE aggregatie zijn
// van de rijen in "PARTNERVERGOEDING — DETAIL ORDERS": exact dezelfde Market- en
// Customer-Segment-velden, dezelfde Customer Payment Status en dezelfde Fee
// Subscription. Daarom leven de classificatie-helpers op module-niveau en worden
// ze door zowel de DETAIL-tabel als de samenvatting gebruikt — zo kan een blok
// altijd gereproduceerd worden door de DETAIL-tabel te filteren (export/controle).

// Markt-klasse uit het ORDER-veld Market: EL (elektriciteit) of GAS.
const orderMarketIsEL = (o) => { const m = String(o.market || '').toUpperCase(); return m.includes('EL') || m.includes('ELEC'); };
// Segment-klasse uit het ORDER-veld Customer Segment.
const orderSegmentClass = (o) => { const s = String(o.segment || '').toUpperCase(); if (s.includes('B2C')) return 'B2C'; if (s.includes('B2B')) return 'B2B'; if (s.includes('CUSTOM')) return 'CUSTOM'; return ''; };
// Samenvattings-cel (Customer Segment × Market) voor de Subscription Fee uit orders.
//
// Een EL-order met Connection Type "Injection" hoort in het INJECTIE-blok van zijn
// segment. Voorheen viel elke Custom+EL-order onder 'Custom - EL Offtake', waardoor
// het blok "Custom - EL Injection" nooit een abonnementsvergoeding toonde terwijl de
// DETAIL ORDERS-tabel die bij dezelfde orders wél liet zien.
//
// "Bidirectional" (afname én injectie op één aansluiting) blijft bewust bij offtake:
// die order heeft één abonnementsvergoeding en die mag maar in ÉÉN blok staan.
// Elke order levert dus precies één sleutel → geen dubbeltelling.
const orderFeeSegmentKey = (o) => {
  const seg = orderSegmentClass(o); if (!seg) return '';
  const el = orderMarketIsEL(o);
  const isInjection = String(o.connectionType || '').trim().toUpperCase() === 'INJECTION';
  // B2C heeft geen eigen injectie-rij in de samenvatting (afspraak), dus ook een
  // B2C-injectie-order houdt zijn abonnementsvergoeding in "B2C - EL". Zo gaat er
  // geen bedrag verloren in een rij die niet bestaat.
  if (seg === 'B2C') return el ? 'B2C - EL' : 'B2C - GAS';
  if (seg === 'B2B') return el ? (isInjection ? 'B2B - EL Injection' : 'B2B - EL') : 'B2B - GAS';
  return el ? (isInjection ? 'Custom - EL Injection' : 'Custom - EL Offtake') : 'Custom - GAS';
};

// Customer Payment Status-toestand "oninbaar deel" (clawback-correctie).
// De waarde zelf staat in filterMigrations.js, samen met de vertaling van de
// vorige benaming — bewaarde filters/favorieten dragen die oude tekst nog.

// Verschuif een ISO-datum (YYYY-MM-DD) met `delta` maanden; lokaal opgebouwd zodat
// er geen tijdzone-dagverschuiving optreedt. Ongeldige input → ongewijzigd terug.
const shiftMonthsIso = (iso, delta) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return String(iso || '');
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setMonth(d.getMonth() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Badge-styling per Customer Payment Status (3 toestanden).
const cpsBadgeClass = (status) =>
  status === 'Goed' ? 'bg-lime-900/50 text-lime-300'
    : status === PAY_STATUS_PARTIAL ? 'bg-amber-900/50 text-amber-300'
      : 'bg-red-900/50 text-red-300';

// Customer Payment Status + oninbaarheidsperiode (clawback) — één definitie,
// gebruikt door de DETAIL-tabellen én de Revenue-samenvatting; bouw 'm uit DEZELFDE
// facturenset zodat alles 1-op-1 reconcilieert.
// Basis: een klant is 'Wanbetaler' zodra hij één verkoopfactuur heeft die onbetaald
// is én de drempel van ZIJN energielabel haalt (labelOverdueDays; default 40 dagen,
// per label instelbaar). De drempel wordt per factuur bepaald op het energielabel
// van die factuur, zodat een klant met facturen onder twee labels elke factuur
// tegen de juiste drempel afmeet. Correctie "Oninbaarheidsperiode wanbetalers"
// (per energielabel, bad_debt_recovery_months; 9999 = volledige terugvordering):
// bij een wanbetaler wordt de vergoeding van de recentste N maanden — t.o.v. zijn
// MEEST RECENTE verkoopfactuur — teruggevorderd (geen commissie). Enkel wie óók
// facturen HEEFT die ouder zijn dan dat venster wordt 'Wanbetaler – oninbaar deel'
// (die oudere facturen blijven 'Goed'/vergoedbaar); wie enkel recente facturen heeft
// blijft volledig 'Wanbetaler'. Levert per klant: status + drempeldatum + maanden.
const computeBadDebt = (facturen, labelConfig = {}) => {
  const statusMap = {};
  const salesDatesByRef = {};   // ref → [factuurdatum, …] van verkoopfacturen
  const labelByRef = {};
  (facturen || []).forEach(f => {
    const ref = f.relatieReferentie || '';
    if (!ref) return;
    const status = (f.statusBetaling || '').toLowerCase();
    const isUnpaid = status !== 'betaald' && status !== 'omgekeerd';
    const isVerkoopfactuur = (f.soort || '').toLowerCase() === 'verkoopfactuur';
    const isOverdue = isRecordBadDebtOverdue(f, labelConfig);
    if (isUnpaid && isVerkoopfactuur && isOverdue) statusMap[ref] = 'Wanbetaler';
    else if (!statusMap[ref]) statusMap[ref] = 'Goed';
    if (isVerkoopfactuur && f.factuurdatum) (salesDatesByRef[ref] || (salesDatesByRef[ref] = [])).push(f.factuurdatum);
    if (!labelByRef[ref]) labelByRef[ref] = f.relatieBedrijf || '';
  });
  const thresholdMap = {};
  const monthsMap = {};
  for (const ref of Object.keys(statusMap)) {
    if (statusMap[ref] !== 'Wanbetaler') continue;
    const months = labelRecoveryMonths(labelConfig, labelByRef[ref]);
    if (months >= BAD_DEBT_RECOVERY_MONTHS_DEFAULT) continue; // volledige terugvordering → blijft 'Wanbetaler'
    const dates = salesDatesByRef[ref] || [];
    if (!dates.length) continue;
    const maxDate = dates.reduce((a, b) => (a > b ? a : b));  // ISO → string-vergelijking volgt chronologie
    const threshold = shiftMonthsIso(maxDate, -months);        // meest recente factuurdatum − N maanden
    if (dates.some(d => d < threshold)) {                      // heeft óók oudere (vergoedbare) facturen
      statusMap[ref] = PAY_STATUS_PARTIAL;
      thresholdMap[ref] = threshold;
      monthsMap[ref] = months;
    }
  }
  return { statusMap, thresholdMap, monthsMap };
};
// Backward-compat: enkel de status-map (geen clawback als labelConfig ontbreekt).
const buildCustomerPaymentStatusMap = (facturen, labelConfig = {}) => computeBadDebt(facturen, labelConfig).statusMap;
const customerPaymentStatusFromMap = (map, ref) => { const r = String(ref || '').trim(); return r ? (map[r] || 'Goed') : 'Goed'; };

// ── Herbruikbare kolom-sortering voor datatabellen ───────────────────────────
// useTableSort: sortConfig-state + toggle. Zelfde kolom opnieuw → wissel asc↔desc;
// andere kolom → nieuwe kolom, asc.
const useTableSort = (defaultKey = null, defaultDir = 'asc') => {
  const [sortConfig, setSortConfig] = useState({ key: defaultKey, dir: defaultDir });
  const handleSort = (key) => setSortConfig(prev => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  return { sortConfig, handleSort, setSortConfig };
};
// sortRows: stabiele kopie-sort van `rows` volgens sortConfig. accessors[key]
// (optioneel) levert de sorteerwaarde voor BEREKENDE kolommen; anders row[key].
// Getallen numeriek, tekst via localeCompare(nl-BE, numeric) zodat "2" < "10".
const sortRows = (rows, sortConfig, accessors = {}) => {
  const key = sortConfig && sortConfig.key;
  if (!key || !Array.isArray(rows)) return rows;
  const get = accessors[key] || ((r) => r[key]);
  const dir = sortConfig.dir === 'desc' ? -1 : 1;
  const norm = (v) => (v == null ? '' : v);
  return [...rows].sort((a, b) => {
    const av = norm(get(a)), bv = norm(get(b));
    const cmp = (typeof av === 'number' && typeof bv === 'number')
      ? av - bv
      : String(av).localeCompare(String(bv), 'nl-BE', { numeric: true, sensitivity: 'base' });
    return cmp * dir;
  });
};
// SortHeader: klikbare kolomkop met op/neer-indicator. `k` = sorteersleutel,
// `align` = 'left'|'right' (right voor getalkolommen). `thClass` overschrijft de
// standaard padding/typografie zodat de kop bij tabellen met eigen stijl past.
const SortHeader = ({ label, k, sortConfig, onSort, align = 'left', className = '', thClass, title }) => (
  <th onClick={() => onSort(k)} title={title} className={`${thClass || 'py-3 px-2 whitespace-nowrap text-xs'} text-${align} text-slate-400 cursor-pointer select-none ${className}`}>
    <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
      {label}
      {sortConfig.key !== k ? <ChevronDown size={13} className="opacity-30" /> : (sortConfig.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
    </span>
  </th>
);

// Cel voor een EAN-attribuut in "GEFACTUREERDE VOLUME PER EAN". Die tabel bundelt
// alle facturen van één aansluiting tot één regel, en segment/majoration/meter
// method zijn daarbinnen niet gegarandeerd constant (gemeten op de echte data:
// majoration wisselt in 145 regels, meter method in 543). We tonen de waarde van de
// recentste factuur, maar markeren dat er meer achter zit — stil één waarde kiezen
// zou een tarief of meetmethode tonen die maar voor een deel van de facturen geldt.
const VolAttrCell = ({ attr, align = 'left', render }) => (
  <td
    className={`py-2 px-2 text-xs whitespace-nowrap text-${align} ${attr.varieert ? 'text-amber-300' : 'text-slate-300'}`}
    title={attr.varieert ? `Wisselt binnen deze aansluiting — alle waarden: ${attr.waarden.join(', ')}` : undefined}
  >
    {render ? render(attr.waarde) : (attr.waarde === '' || attr.waarde == null ? '—' : attr.waarde)}
    {attr.varieert && <span className="text-amber-500/70"> *</span>}
  </td>
);

// GSRN-overlapcontrole: bouwt de Set order-objecten die in een overlap zitten —
// per unieke GSRN twee orders met overlappende leveringsperiode. `contractedOrders`
// moet de reeds op "contracted" gefilterde set zijn. Periode = Effective Start→End
// met fallback op Delivery Start→End per record; ontbreekt de start ook dan
// -oneindig, ontbreekt het eind ook dan +oneindig (open/lopend). Overlap = strikt
// (aansluitend, eindA == startB, telt NIET als overlap). ISO-datums → string-vergelijking.
function computeGsrnOverlapSet(contractedOrders) {
  const set = new Set();
  const byGsrn = new Map();
  for (const o of contractedOrders) {
    const gsrn = (o.connectionPointGSRN || '').trim();
    if (!gsrn) continue;
    const start = (o.effectiveStartDate || o.deliveryStartDate || '') || '0000-01-01';
    const end = (o.effectiveEndDate || o.deliveryEndDate || '') || '9999-12-31';
    if (!byGsrn.has(gsrn)) byGsrn.set(gsrn, []);
    byGsrn.get(gsrn).push({ o, start, end });
  }
  for (const list of byGsrn.values()) {
    if (list.length < 2) continue;
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        if (list[i].start < list[j].end && list[j].start < list[i].end) {
          set.add(list[i].o); set.add(list[j].o);
        }
      }
    }
  }
  return set;
}

// Download + toon de PDF-factuur via de server-proxy (/api/invoices/:id/pdf). Opent
// de PDF in een nieuw tabblad (browser-viewer met download-knop); bij een geblokkeerde
// popup valt het terug op een directe download. Token nodig voor auth. De id mag de
// prefix fact-/credit- dragen (backend stript die).
// Een factuur-PDF openen wanneer het ET-id niet in de rij zit.
//   { nummer }      → EXACT die factuur (Revenue: die records dragen alleen het
//                     factuurnummer; hun rij-id is een React-sleutel).
//   { gsrn, ref }   → de LAATSTE factuur van die aansluiting/klant (Bill Shock).
// De server zoekt het id op in de facturenlijst, rol-gescopet.
async function openLaatsteFactuurPdf({ gsrn, ref, nummer }, token) {
  if (!token || (!gsrn && !ref && !nummer)) return;
  const exact = !!nummer && !gsrn && !ref;
  toast.info(exact ? 'Factuur opzoeken…' : 'Laatste factuur opzoeken…');
  try {
    const q = new URLSearchParams();
    if (nummer) q.set('nummer', nummer);
    if (gsrn) q.set('gsrn', gsrn);
    if (ref) q.set('ref', ref);
    const r = await fetch(`/api/invoices/latest?${q.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 404) {
      toast.error(exact
        ? `Factuur ${nummer} staat niet in de facturenlijst.`
        : 'Geen factuur gevonden voor deze aansluiting of klant.');
      return;
    }
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    const f = await r.json();
    // Bij een zoektocht op aansluiting/klant: zeg WELKE factuur je krijgt. Het is de
    // laatste van deze klant, niet per se de factuur die bij de bill shock hoort —
    // zonder die melding lijkt het een exacte koppeling, en dat is het niet. Bij een
    // nummer-zoektocht is het wél exact en is de melding overbodige ruis.
    if (!exact) toast.info(`Factuur ${f.nummer || f.id}${f.factuurdatum ? ` van ${bsFormatDate(f.factuurdatum)}` : ''} wordt geopend…`);
    await downloadInvoicePdf(f.id, token, f.nummer || f.id);
  } catch (e) {
    toast.error(`Kon de factuur niet openen: ${e.message}`);
  }
}

// soort 'selfbill' haalt de PDF op bij /api/selfbills/:id/pdf (sinds de API-update
// van 09-2026 bestaat die PDF ook voor self bills). Verder identiek: de bytes komen
// via onze eigen server, nooit rechtstreeks van de bron.
async function downloadInvoicePdf(invoiceId, token, label, { soort = 'invoice' } = {}) {
  if (!invoiceId || !token) return;
  const self = soort === 'selfbill';
  const woord = self ? 'Self bill' : 'Factuur';
  toast.info(`${woord}-PDF wordt opgehaald…`);
  try {
    const r = await fetch(`/api/${self ? 'selfbills' : 'invoices'}/${encodeURIComponent(invoiceId)}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { let msg = `HTTP ${r.status}`; try { msg = (await r.json()).error || msg; } catch {} throw new Error(msg); }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (!w) {
      const a = document.createElement('a');
      a.href = url; a.download = `${self ? 'self-bill' : 'factuur'}-${(label || invoiceId).toString().replace(/[^\w.-]+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(`Kon PDF niet openen: ${e.message}`); }
}

// Bouwt het Lynx/Odoo-deeplink-object voor een orderrij (voor het rechtsklikmenu).
// Voor niet-bevoorrechte rollen zijn deze velden server-side al leeg → geen actie.
const orderEtLinks = (o) => ({
  lynxRelation: o.lynxRelationUrl, lynxOrder: o.lynxOrderUrl, lynxGsrn: o.lynxGsrnUrl,
  odooCustomer: o.odooCustomerUrl, odooOrder: o.odooOrderUrl, odooGsrn: o.odooGsrnUrl,
});

// De zes deeplink-acties in het rechtsklikmenu (klant, order en aansluitingspunt,
// telkens in Lynx én Odoo). Ze worden ALTIJD alle zes getoond (voor
// admin/et_dossier); ontbreekt de koppeling in de Energy Together-data, dan staat
// de regel grijs en uitgeschakeld met een uitleg. Bewust geen stilzwijgend
// weglaten meer: ~23% van de orders heeft geen Lynx-relatie, en een verdwenen
// menu-item is niet te onderscheiden van een defect.
const ET_LINK_DEFS = [
  { key: 'lynxRelation', label: 'Lynx Relation', tone: 'text-sky-400', empty: 'Geen Lynx-relatie bekend voor deze klant' },
  { key: 'lynxOrder', label: 'Lynx Order', tone: 'text-sky-400', empty: 'Geen Lynx-order bekend voor deze order' },
  { key: 'lynxGsrn', label: 'Lynx EAN', tone: 'text-sky-400', empty: 'Geen Lynx-aansluitingspunt bekend voor deze EAN' },
  { key: 'odooCustomer', label: 'Odoo Klant', tone: 'text-violet-400', empty: 'Geen Odoo-klant bekend voor deze order' },
  { key: 'odooOrder', label: 'Odoo Order', tone: 'text-violet-400', empty: 'Geen Odoo-order bekend voor deze order' },
  { key: 'odooGsrn', label: 'Odoo EAN', tone: 'text-violet-400', empty: 'Geen Odoo-aansluitingspunt bekend voor deze EAN' },
];

// Deeplinks voor een helpdesk-ticketrij: de klant in Lynx en Odoo, en het ticket
// zelf in Odoo. De URL's komen van de server en zijn er alleen voor
// admin/et_dossier (voor andere rollen stuurt de server ze niet mee).
const TICKET_LINK_DEFS = [
  { key: 'lynxRelation', label: 'Lynx Relation', tone: 'text-sky-400', empty: 'Geen Lynx-relatie bekend voor deze klant' },
  { key: 'odooCustomer', label: 'Odoo Klant', tone: 'text-violet-400', empty: 'Geen Odoo-klant bekend voor dit ticket' },
  { key: 'odooTicket', label: 'Odoo Ticket', tone: 'text-violet-400', empty: 'Geen Odoo-ticket bekend' },
];
const ticketEtLinks = (t) => ({ defs: TICKET_LINK_DEFS, lynxRelation: t.lynx_relation_url, odooCustomer: t.customer_url, odooTicket: t.url });

// Doel voor het rechtsklikmenu: één klant, met ZOWEL zijn naam als zijn
// klantnummer. Beide zijn nodig, want de twee tabellen die naar Customer 360 /
// Metering doorlinken noemen dezelfde klant anders:
//   - een ORDERrij draagt de contactpersoon  ("Viki Reybrouck")
//   - een FACTUURrij draagt de onderneming   ("VIKI'S CHOICE")
// Customer 360 en Metering selecteren op de ordernaam, dus een selectie die van een
// factuur kwam vond bij elke zakelijke klant NIETS — terwijl dezelfde klant via het
// zoekveld van die module wél data toonde. Gemeten: 38% van de facturen heeft een
// relatieNaam die in geen enkele order als klantnaam voorkomt; bij 98,6% daarvan
// matcht het klantnummer wél.
//
// Naam en klantnummer worden geuniëerd, nooit vervangen: één naam kan meer dan één
// klantnummer dragen (747 in de huidige data) en 3.474 orders hebben er geen. Geef
// dus altijd alles mee wat de rij weet; ontbreekt er één, dan draagt de ander het.
// `gsrns` (optioneel): de EAN(s) van de rij waarop geklikt werd (order: één,
// factuur: elektriciteit en/of gas). Metering vinkt dan enkel díe aan; de andere
// EAN's van de klant staan er wel bij, uitgevinkt. Customer 360 negeert het.
const klantTarget = (naam, ref, label, gsrns) => {
  const n = String(naam || '').trim();
  const r = String(ref || '').trim();
  const g = (gsrns || []).map((x) => String(x || '').trim()).filter(Boolean);
  return { type: 'customer', value: n, ref: r, label: label || n || r, ...(g.length ? { gsrns: g } : {}) };
};

// Herbruikbaar rechtsklik-contextmenu met de acties "Customer 360" + "Metering", en
// optioneel "Invoice PDF" voor factuurrijen. Gebruik:
//   const { onContext, menu } = useCustomer360Menu(onOpenCustomer360);
//   <tr onContextMenu={e => onContext(e, klantnaam, klantTarget(klantnaam, klantnummer))}>
//   <tr onContextMenu={e => onContext(e, klantnaam, undefined, { id, label })}>   // + Invoice PDF
//   { menu }  // één keer renderen binnen de tabel/pagina
// Geef ALTIJD een klantTarget mee zodra de rij een klantnummer kent (zie hierboven).
// onOpen mag null zijn (bv. binnen Customer 360 zelf): dan tonen we enkel Invoice PDF.
function useCustomer360Menu(onOpen) {
  const { token, user } = useAuth();
  // Backend-deeplinks (Lynx/Odoo) zijn ENKEL voor admin + et_dossier (ET-merk/host).
  const showEtLinks = seesEtBrand(user?.role);
  const [menu, setMenu] = useState(null); // { x, y, name, target, invoice, links }
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);
  // onContext(e, name[, target[, invoice[, links]]]). Zonder target → standaard een
  // klant-target (type 'customer'). Geef een expliciete target mee (bv. {type:'gsrn',
  // ...}) voor records zonder klantnaam (zoals Bill Shock). `invoice` = { id, label }
  // voegt een "Invoice PDF"-actie toe (factuurrijen), of { lookup: { gsrn, ref } }
  // wanneer het id NIET bekend is en de server de laatste factuur van die
  // aansluiting/klant mag opzoeken (Bill Shock). `links` = het object uit
  // `orderEtLinks(order)` voegt de Lynx/Odoo-deeplinks toe (orderrijen, enkel
  // admin/et_dossier — voor andere rollen zijn de URL's server-side al leeg).
  const onContext = (e, name, target, invoice, links) => {
    const n = String(name || '').trim();
    const canNavigate = !!onOpen && !!n;
    // Het deeplink-blok verschijnt zodra er een orderrij is (links-object) én de rol
    // het mag zien — ook als alle vier de URL's leeg zijn; die regels worden dan
    // grijs getoond i.p.v. weggelaten.
    const hasLinkSection = !!(showEtLinks && links);
    if (!canNavigate && !invoice && !hasLinkSection) return;   // niets te tonen
    e.preventDefault();
    // Klem binnen het venster zodat het menu niet buiten beeld valt.
    const rows = (canNavigate ? 2 : 0) + (invoice ? 1 : 0) + (hasLinkSection ? ((links && links.defs) || ET_LINK_DEFS).length : 0);
    const x = Math.min(e.clientX, window.innerWidth - 240);
    const y = Math.min(e.clientY, window.innerHeight - (30 + rows * 38));
    setMenu({ x, y, name: n, target: target || klantTarget(n), invoice: invoice || null, links: hasLinkSection ? links : null });
  };
  // Klik op "Customer 360" / "Metering". Een klik MET een toets erbij (Ctrl, Cmd,
  // Shift, Alt) laten we met rust: dan vraagt de gebruiker de browser expliciet om
  // een nieuw tabblad of venster, en die weet zelf wel raad met de href.
  //
  // Een gewone linkermuisklik opent een NIEUW TABBLAD (openPortalPage): de lijst
  // waar je op klikte blijft ongemoeid staan — filters, pagina, scrollpositie — en
  // je kunt klant na klant openen zonder telkens terug te navigeren. Dat is de
  // reden van bestaan van dit menu. Werk je in het geïnstalleerde appvenster (PWA),
  // waar een tweede venster niet past, dan navigeert openPortalPage binnen de app.
  //
  // Zo'n tabblad is een verse app-load, maar hoeft NIET op de volledige dataset te
  // wachten: de selectie reist mee in de URL (?sel=…) en die tab haalt eerst enkel
  // de orders van dát doel op (~0,4 s) en stelt het zware achtergrondladen uit tot
  // de pagina staat — zie "Snelle start bij een deep-link" in de App-component.
  const openPagina = (e, dest) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { setMenu(null); return; }
    e.preventDefault();
    const target = menu.target;
    setMenu(null);
    openPortalPage(targetPageUrl(target, dest), () => onOpen(target, dest));
  };
  // Het menu rendert in een PORTAL op document.body. Zonder dat stond het ver van
  // de cursor: "position: fixed" rekent vanaf de dichtstbijzijnde voorouder met een
  // transform/filter/backdrop-filter, en Card draagt backdrop-blur-sm — de
  // clientX/clientY van de klik werden dus als offset BINNEN de kaart gelezen.
  // Zelfde reden als bij DropdownPanel hierboven; gebruik dit voor elk zwevend paneel.
  const menuNode = menu ? ReactDOM.createPortal((
    <div
      style={{ position: 'fixed', top: menu.y, left: menu.x, zIndex: 9999 }}
      onMouseDown={e => e.stopPropagation()}
      className="bg-slate-800 border border-slate-600 rounded-lg shadow-2xl py-1 min-w-[190px] max-w-[calc(100vw-1rem)]"
    >
      <div className="px-3 py-1.5 text-[11px] text-slate-500 truncate max-w-[230px] border-b border-slate-700/60" title={menu.name || (menu.invoice && menu.invoice.label) || ''}>{menu.name || (menu.invoice && menu.invoice.label) || ''}</div>
      {/* Beide acties openen een nieuw tabblad op de pagina met deze klant/GSRN al
          ingevuld (in het PWA-venster: binnen de app). Het blijven <a href>'s: zo
          werken middenklik, Ctrl+klik en "Link openen in…" van de browser gewoon. */}
      {onOpen && menu.name && <>
        <a
          href={targetPageUrl(menu.target)} target="_blank"
          onClick={(e) => openPagina(e)}
          className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2"
        ><Compass size={14} className="text-lime-400" />Customer 360</a>
        <a
          href={targetPageUrl(menu.target, 'metering')} target="_blank"
          onClick={(e) => openPagina(e, 'metering')}
          className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2"
        ><Gauge size={14} className="text-lime-400" />Metering</a>
      </>}
      {menu.invoice && <button
        onClick={() => {
          // `lookup` = het id is niet bekend; de server zoekt de laatste factuur van
          // deze aansluiting/klant op. Anders: het id staat op de rij zelf.
          if (menu.invoice.lookup) openLaatsteFactuurPdf(menu.invoice.lookup, token);
          else downloadInvoicePdf(menu.invoice.id, token, menu.invoice.label, { soort: menu.invoice.soort });
          setMenu(null);
        }}
        className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2"
      ><FileText size={14} className="text-lime-400" />{menu.invoice.soort === 'selfbill' ? 'Self bill PDF' : menu.invoice.lookup && !menu.invoice.lookup.nummer ? 'Laatste factuur (PDF)' : 'Invoice PDF'}</button>}
      {showEtLinks && menu.links && <>
        <div className="border-t border-slate-700/60 my-1" />
        {(menu.links.defs || ET_LINK_DEFS).map(d => {
          const url = menu.links[d.key];
          if (!url) return (
            <div key={d.key} title={d.empty} aria-disabled="true"
              className="w-full px-3 py-2 text-sm text-slate-500 flex items-center gap-2 cursor-not-allowed select-none">
              <ExternalLink size={14} className="text-slate-600 shrink-0" />
              <span className="truncate">{d.label}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-600 shrink-0">niet gekoppeld</span>
            </div>
          );
          return (
            <button key={d.key} onClick={() => { window.open(url, '_blank', 'noopener'); setMenu(null); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2">
              <ExternalLink size={14} className={`${d.tone} shrink-0`} />
              <span className="truncate">{d.label}</span>
            </button>
          );
        })}
      </>}
    </div>
  ), document.body) : null;
  return { onContext, menu: menuNode };
}

// Controle-tabel (ENKEL admin + ET Dossierbeheerder): alle orders die — ná de
// Online/OdooBot→team-correctie — nog steeds GEEN verkoopteam óf GEEN verkoper
// hebben. 'Online' en 'OdooBot' tellen als een geldige waarde (niet leeg) en
// vallen hier dus niet onder. Signaleert data-/mapping-gaten die orders uit de
// partnervergoeding-detailtabel of de team-/verkoper-filters kunnen houden.
const MissingTeamSellerTable = ({ orders = [], onOpenCustomer360 }) => {
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = TABLE_PAGE_SIZE;

  const missing = useMemo(() => orders.filter(o => hasBlankTeam(o) || hasBlankSeller(o)), [orders]);
  const filtered = useMemo(() => searchTerm ? missing.filter(o => orderMatchesSearch(o, searchTerm)) : missing, [missing, searchTerm]);
  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageClamped = Math.min(currentPage, Math.max(1, totalPages));
  const mtsSort = useTableSort();
  const paginated = useMemo(() => { const start = (pageClamped - 1) * pageSize; return sortRows(filtered, mtsSort.sortConfig).slice(start, start + pageSize); }, [filtered, pageClamped, pageSize, mtsSort.sortConfig]);

  const exportCsv = () => {
    const columns = [
      { header: 'Bedrijf', get: o => o.company }, { header: 'Orderdatum', get: o => o.orderDate },
      { header: 'Klantennummer', get: o => o.customerReference }, { header: 'Klant', get: o => o.customer },
      { header: 'E-mail', get: o => o.customerEmail }, { header: 'EAN', get: o => o.connectionPointGSRN, text: true },
      { header: 'Opp. Stage', get: o => o.opportunityStage }, { header: 'Status', get: o => o.status },
      { header: 'Team', get: o => o.salespersonTeam }, { header: 'Verkoper', get: o => o.salesperson },
    ];
    exportRowsToCsv('orders-zonder-team-of-verkoper.csv', columns, filtered);
  };

  const Leeg = () => <span className="text-amber-400 italic">leeg</span>;

  return (
    <Card className="!p-4 !overflow-visible !border-amber-700/50">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-amber-300 flex items-center gap-1.5"><AlertCircle size={18} /> CONTROLE — ORDERS ZONDER VERKOOPTEAM OF VERKOPER</h3><ExportKnop onExport={exportCsv} disabled={filtered.length === 0} count={filtered.length} label="Exporteer de (gefilterde) controlelijst naar Excel" /></div>
      </div>
      <p className="text-xs text-amber-200/80 mb-3">Orders waarbij ná de Online/OdooBot-correctie nog steeds het <strong>verkoopteam</strong> óf de <strong>verkoper</strong> ontbreekt. <em>Online</em> en <em>OdooBot</em> tellen als een geldige waarde. Zulke orders kunnen wegvallen uit "PARTNERVERGOEDING — DETAIL ORDERS" of uit de team-/verkoper-filters. Klik op de klant of EAN (of rechtsklik op de rij) voor Customer 360.</p>
      {missing.length === 0 ? (
        <div className="text-center py-6 text-emerald-400 text-sm flex items-center justify-center gap-2"><Check size={16} />Geen orders zonder verkoopteam of verkoper — alles correct gemapt.</div>
      ) : (<>
        <div className="mb-3 relative max-w-md">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-500" />
          <input type="text" placeholder="Klant, klantnummer, EAN, e-mail, verkoper..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="w-full pl-7 pr-3 py-1.5 bg-amber-900/20 border border-amber-600/40 rounded text-xs text-slate-200 placeholder-slate-400 focus:border-amber-500 focus:outline-none" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead className="bg-slate-800"><tr className="border-b border-slate-700">
              <SortHeader label="Bedrijf" k="company" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="Orderdatum" k="orderDate" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="Klantennummer" k="customerReference" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="Klant" k="customer" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="EAN" k="connectionPointGSRN" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="Opp. Stage" k="opportunityStage" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="Status" k="status" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="Team" k="salespersonTeam" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
              <SortHeader label="Verkoper" k="salesperson" sortConfig={mtsSort.sortConfig} onSort={mtsSort.handleSort} />
            </tr></thead>
            <tbody>{paginated.map(o => <tr key={o.id} onContextMenu={e => onC360Context(e, o.customer, klantTarget(o.customer, o.customerReference, undefined, [o.connectionPointGSRN]), undefined, orderEtLinks(o))} className="border-b border-slate-700/30 hover:bg-slate-700/20">
              <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.company}</td>
              <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs">{o.orderDate}</td>
              <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{o.customerReference}<KlantnrKopieerKnop nr={o.customerReference} /></td>
              <td className="py-2 px-2 truncate max-w-32 text-xs">
                {o.customer && onOpenCustomer360 ? (
                  <button onClick={e => { e.stopPropagation(); onOpenCustomer360(klantTarget(o.customer, o.customerReference, undefined, [o.connectionPointGSRN])); }} className="text-slate-200 hover:text-lime-400 hover:underline cursor-pointer inline-flex items-center gap-1 max-w-full"><Compass size={11} className="text-slate-500 shrink-0" /><span className="truncate">{o.customer}</span></button>
                ) : <span className="text-slate-200">{o.customer || ''}</span>}
              </td>
              <td className="py-2 px-2 text-xs font-mono whitespace-nowrap">
                {o.connectionPointGSRN && onOpenCustomer360 ? (
                  <button onClick={e => { e.stopPropagation(); onOpenCustomer360({ type: 'gsrn', value: o.connectionPointGSRN, label: o.customer ? `${o.customer} · ${o.connectionPointGSRN}` : o.connectionPointGSRN }); }} className="text-slate-500 hover:text-lime-400 hover:underline cursor-pointer">{o.connectionPointGSRN}</button>
                ) : <span className="text-slate-500">{o.connectionPointGSRN || ''}</span>}
                <EanKopieerKnop ean={o.connectionPointGSRN} />
              </td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap"><OppStageText value={o.opportunityStage} /></td>
              <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-24">{o.status}</td>
              <td className="py-2 px-2 text-xs whitespace-nowrap">{hasBlankTeam(o) ? <Leeg /> : <span className="text-slate-300">{o.salespersonTeam}</span>}</td>
              <td className="py-2 px-2 text-xs whitespace-nowrap">{hasBlankSeller(o) ? <Leeg /> : <span className="text-slate-300">{o.salesperson}</span>}</td>
            </tr>)}</tbody>
          </table>
        </div>
        {totalPages > 1 && <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700"><div className="text-xs text-slate-400">Toont {((pageClamped - 1) * pageSize) + 1} - {Math.min(pageClamped * pageSize, filtered.length)} van {filtered.length}</div><div className="flex items-center gap-2"><button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={pageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg">Vorige</button><span className="px-3 py-1.5 bg-amber-500 text-slate-900 font-semibold text-xs rounded-lg">{pageClamped}</span><button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={pageClamped === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg">Volgende</button></div></div>}
      </>)}
      {c360Menu}
    </Card>
  );
};

// ─── Verkoper (ET-reseller) wijzigen ─────────────────────────────────────────
// Rechten (spiegelt de backend RBAC): reseller mag niets; teamleader enkel binnen
// het eigen team; master binnen het eigen label; admin/et_dossier overal — maar
// ALTIJD binnen het energielabel van het order (nooit cross-label). De dropdown-
// opties komen uit de al rol-gescopete salesStructure, dus tonen nooit iets buiten
// de rechten. De harde afdwinging gebeurt server-side (POST /api/orders/:id/reassign).
const canEditSeller = (role) => !!role && role !== 'reseller';

// Modal om de verkoper (+ afgeleid verkoopteam) van één order te wijzigen.
const ReassignSellerModal = ({ order, salesStructure = EMPTY_STRUCTURE, currentUser, onReassignOrder, onClose }) => {
  const label = (order.company || '').trim();
  const role = currentUser?.role;
  // admin/et_dossier/master krijgen een team-filter; teamleader heeft een vast team.
  const showTeamFilter = role === 'admin' || role === 'et_dossier' || role === 'master' || isProspectRol(role);
  const teamsForLabel = useMemo(() => scopeOptions(salesStructure, label, 'all').teams, [salesStructure, label]);
  const fixedTeam = !showTeamFilter && teamsForLabel.length ? teamsForLabel[0] : '';
  const [team, setTeam] = useState(() => {
    if (!showTeamFilter) return fixedTeam || 'all';
    const cur = (order.salespersonTeam || '').trim();
    return cur && teamsForLabel.includes(cur) ? cur : 'all';
  });
  const sellers = useMemo(() => scopeOptions(salesStructure, label, team === 'all' ? 'all' : team).sellers, [salesStructure, label, team]);
  const [seller, setSeller] = useState((order.salesperson || '').trim());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Als de huidige verkoper niet in de (gefilterde) lijst zit, selectie leegmaken.
  useEffect(() => { setSeller((s) => (s && !sellers.includes(s) ? '' : s)); }, [sellers]);

  const unchanged = seller && seller === (order.salesperson || '').trim();
  const submit = async () => {
    if (!seller || busy) return;
    setBusy(true); setError(null);
    try {
      await onReassignOrder(order.id, { seller, team: team === 'all' ? '' : team });
      toast.success(`Verkoper gewijzigd naar ${seller}`);
      onClose();
    } catch (e) { setError(e.message); toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} onContextMenu={(e) => e.preventDefault()}>
      <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2"><User size={16} className="text-lime-400" />Verkoper wijzigen</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-400">Order <span className="font-mono text-slate-300">{String(order.id).replace('order-', '')}</span>{order.customer ? <> · <span className="text-slate-300">{order.customer}</span></> : ''}</div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Energielabel</span>
            <span className="px-2 py-0.5 rounded-md bg-slate-700 border border-slate-600 text-slate-200 text-xs">{label || '—'}</span>
            <span className="text-[10px] text-slate-500">vast — nooit over labels heen</span>
          </div>
          {showTeamFilter && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">Verkoopteam (filtert de verkopers)</label>
              <select value={team} onChange={(e) => setTeam(e.target.value)} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 focus:border-lime-500 focus:outline-none">
                <option value="all">Alle teams</option>
                {teamsForLabel.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          {!showTeamFilter && fixedTeam && (
            <div className="flex items-center gap-2 text-sm"><span className="text-[10px] uppercase tracking-wider text-slate-500">Verkoopteam</span><span className="text-slate-200 text-xs">{fixedTeam}</span></div>
          )}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Nieuwe verkoper</label>
            <select value={seller} onChange={(e) => setSeller(e.target.value)} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 focus:border-lime-500 focus:outline-none">
              <option value="">— Kies verkoper —</option>
              {sellers.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {sellers.length === 0 && <p className="text-xs text-amber-300 mt-1">Geen verkopers beschikbaar binnen je rechten voor dit label/team.</p>}
          </div>
          <div className="text-xs text-slate-500">Huidige verkoper: <span className="text-slate-300">{order.salesperson || '—'}</span>{order.salespersonTeam ? ` · ${order.salespersonTeam}` : ''}</div>
          {error && <div className="text-xs text-red-400 flex items-center gap-1"><AlertCircle size={12} />{error}</div>}
          <div className="text-[11px] text-slate-500">De wijziging wordt ook doorgevoerd in het backend-systeem (order, opportunity en contract).</div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-700">
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg">Annuleren</button>
          <button onClick={submit} disabled={!seller || unchanged || busy} className="px-3 py-1.5 text-xs bg-lime-500 hover:bg-lime-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-lg flex items-center gap-1">{busy ? <Spinner size={12} /> : <Check size={12} />}Wijzigen</button>
        </div>
      </div>
    </div>
  );
};

// Cel die de verkoper toont + (indien toegestaan) een potlood-knop om te wijzigen.
const SellerCell = ({ order, currentUser, salesStructure = EMPTY_STRUCTURE, onReassignOrder }) => {
  const [open, setOpen] = useState(false);
  const role = currentUser?.role;
  const label = (order.company || '').trim();
  const inScopeLabel = (salesStructure?.energy_labels || []).includes(label);
  const editable = canEditSeller(role) && !!onReassignOrder && (
    role === 'admin' || role === 'et_dossier' ||
    (role === 'master' && inScopeLabel) ||
    (role === 'teamleader' && inScopeLabel && (order.salespersonTeam || '') === (currentUser?.salesTeam || ''))
  );
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-slate-300">{order.salesperson || <span className="text-slate-500">—</span>}</span>
      {editable && (
        <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} title="Verkoper wijzigen"
          className="opacity-50 hover:opacity-100 text-slate-500 hover:text-lime-400 transition shrink-0">
          <Edit2 size={12} />
        </button>
      )}
      {open && <ReassignSellerModal order={order} salesStructure={salesStructure} currentUser={currentUser} onReassignOrder={onReassignOrder} onClose={() => setOpen(false)} />}
    </span>
  );
};

// ─── B2B/B2C-weergave + klantnaam ────────────────────────────────────────────
// Bij een zakelijke klant (B2B) tonen we de ONDERNEMINGSNAAM (customerCompanyName)
// als primaire klantnaam, met de contactpersoon eronder; bij een particulier (B2C)
// de persoonsnaam. LET OP: de bedrijfsnaam is NIET het energielabel (o.company =
// leverancier/label); het is de naam van de onderneming van de klant zelf.
// Zakelijk = heeft een ondernemingsnaam én is niet B2C (dus B2B of Custom). Bij
// zulke klanten tonen we de ONDERNEMINGSNAAM (gekoppeld aan het klantnummer)
// i.p.v. de contactpersoon.
const isBusiness = (o) => !!String(o?.customerCompanyName || '').trim() && !String(o?.segment || '').trim().toLowerCase().startsWith('b2c');
// BTW-nummer als vergelijkingssleutel: enkel letters en cijfers, kleine letters.
// "BE 0789.719.065" en "BE0789719065" zijn dezelfde onderneming. Korter dan acht
// tekens is geen BTW-nummer (een streepje, "nvt", …) en telt niet.
const c360NormVat = (v) => { const s = String(v || '').replace(/[^0-9a-z]/gi, '').toLowerCase(); return s.length >= 8 ? s : ''; };
const customerPrimaryName = (o) => (isBusiness(o) ? o.customerCompanyName : (o?.customer || ''));

// Compacte B2B/B2C-badge (icoon + label). 'custom' → CUSTOM.
const SegmentBadge = ({ o }) => {
  const s = String(o?.segment || '').trim().toLowerCase();
  if (!s) return null;
  const b2b = s.startsWith('b2b'), b2c = s.startsWith('b2c');
  const cls = b2b ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
    : b2c ? 'bg-sky-500/15 text-sky-300 border-sky-500/30'
    : 'bg-purple-500/15 text-purple-300 border-purple-500/30';
  const Icon = b2b ? Building2 : User;
  const label = b2b ? 'B2B' : b2c ? 'B2C' : (o.segment || '').toUpperCase();
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border shrink-0 ${cls}`}><Icon size={10} />{label}</span>;
};

// Klant-cel: B2B/B2C-badge + primaire naam (onderneming bij B2B, met contact-
// persoon eronder). Optioneel klikbaar naar Customer 360 (sleutel = persoonsnaam,
// zoals de rest van het platform de klant opzoekt).
// Kopieerknopje naast een EAN in een tabelcel: één klik zet de code op het
// klembord (zonder de rij te openen). Klein icoon met `shrink-0`, want in een
// krappe kolom krimpt een svg anders tot 0 px. Valt terug op een tijdelijk
// tekstveld waar navigator.clipboard niet beschikbaar is (http zonder localhost).
const kopieerNaarKlembord = async (tekst) => {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(tekst); return true; }
  } catch { /* val terug */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = tekst; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(ta); ta.select(); const ok = document.execCommand('copy'); ta.remove();
    return ok;
  } catch { return false; }
};
// Kopieerknopje naast een code (EAN, klantnummer): één klik zet de tekst op het
// klembord zonder de rij te openen. `soort` = het woord in tooltip en melding.
const KopieerKnop = ({ tekst, soort = 'Code', className = '' }) => {
  if (!tekst) return null;
  return (
    <button type="button" onClick={async (e) => { e.stopPropagation(); (await kopieerNaarKlembord(tekst)) ? toast.success(`${soort} ${tekst} gekopieerd`) : toast.error('Kopiëren mislukt'); }}
      title={`${soort} kopiëren naar het klembord`} aria-label={`${soort} kopiëren`}
      className={`inline-flex items-center justify-center shrink-0 w-5 h-5 ml-1 rounded text-slate-500 hover:text-lime-400 hover:bg-slate-700/40 align-middle ${className}`}>
      <Copy size={12} className="shrink-0" />
    </button>
  );
};
// Demo-account: op het klembord komt wat het scherm toont (gemaskeerd), niet de echte waarde.
const EanKopieerKnop = ({ ean, className = '' }) => <KopieerKnop tekst={demoWeergaveActief() ? maskeerTekst(ean) : ean} soort="EAN" className={className} />;
const KlantnrKopieerKnop = ({ nr, className = '' }) => <KopieerKnop tekst={demoWeergaveActief() ? maskeerTekst(nr) : nr} soort="Klantnummer" className={className} />;

const CustomerNameCell = ({ order, onOpenCustomer360 }) => {
  const primary = customerPrimaryName(order);
  const showPerson = isBusiness(order)
    && String(order.customer || '').trim() && order.customer !== order.customerCompanyName;
  const body = (
    <span className="inline-flex flex-col min-w-0 text-left leading-tight">
      <span className="truncate text-slate-200">{primary || '—'}</span>
      {showPerson && <span className="truncate text-[10px] text-slate-500">{order.customer}</span>}
    </span>
  );
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {order.customer && onOpenCustomer360 ? (
        <button onClick={(e) => { e.stopPropagation(); onOpenCustomer360(klantTarget(order.customer, order.customerReference)); }} className="inline-flex items-center gap-1 min-w-0 max-w-full text-slate-200 hover:text-lime-400 hover:underline cursor-pointer">
          <Compass size={11} className="text-slate-500 shrink-0" />{body}
        </button>
      ) : body}
    </div>
  );
};

// Enkelvoudig → meervoudig filter. Customer Tags, Connection Type, Stop Contract en
// Workflow huidig/laatste werden checkbox-filters (04-08-2026); bewaarde snapshots
// (localStorage én gedeelde favorieten) bevatten nog de oude string: 'all'/leeg wordt
// "geen selectie", elke andere waarde blijft als enige selectie behouden.
const asMultiFilter = (next, legacy) => {
  if (Array.isArray(next)) return next;
  return typeof legacy === 'string' && legacy && legacy !== 'all' ? [legacy] : [];
};

// Workflow-filter: de keuzelijst bevat sinds 04-08-2026 de STAP i.p.v. de flow.
// Bewaarde filters/favorieten van vóór die datum bevatten nog een flow-naam ("Stop
// Contract"); die matchen we ook op de flow, zodat zo'n filter alle stappen van die
// flow toont i.p.v. plots een lege tabel.
const matchesWorkflowFilter = (selected, flow, step) =>
  selected.includes(workflowLabel(flow, step)) || (Boolean(flow) && selected.includes(flow));

// Tabs van de Orders-module. Ids identiek aan de subpagina-catalogus (subPages.js).
const ORDERS_TABS = [{ id: 'orders', label: 'Orders' }, { id: 'fases', label: 'Fases' }, { id: 'leads', label: 'Leads' }];

// ── Gedeeld zoekveld met klant-suggesties ────────────────────────────────────
// Customer 360 zette de vorm: je typt, en onder het veld verschijnen de KLANTEN
// (segmentbadge, naam, klantnummer) en GSRN's die matchen, met per regel hoeveel
// records eronder vallen. Orders, Invoices en Metering hadden elk hun eigen,
// kalere variant — drie keer een ander veld voor dezelfde handeling. Dit is de
// gedeelde suggestie-opbouw + component, zodat de vier pagina's er hetzelfde
// uitzien, hetzelfde vinden, en niet opnieuw uit elkaar groeien.
//
// De pagina bepaalt alleen wat een KLIK doet: Customer 360 selecteert de klant en
// opent zijn dossier, de andere drie zetten de zoekterm en filteren hun tabel.
const ZOEK_MAX = 8;                 // regels per sectie
const LEGE_KENMERKEN = { perRef: new Map(), perNaam: new Map() };
const LEGE_SUGGESTIES = { klanten: [], gsrns: [], facturen: [] };

// Klantkenmerken (klantnummer, bedrijfsnaam, segment, e-mail) uit de ORDERS. Een
// factuurrecord draagt segment noch bedrijfsnaam, dus ook de Invoices-pagina haalt
// ze hier op — anders zou daar de segmentbadge altijd ontbreken.
function klantKenmerken(orders) {
  const perRef = new Map();
  const perNaam = new Map();
  for (const o of orders || []) {
    const naam = String(o.customer || '').trim();
    const ref = String(o.customerReference || '').trim();
    const k = {
      naam,
      ref,
      bedrijf: String(o.customerCompanyName || '').trim(),
      segment: String(o.segment || '').trim(),
      email: String(o.customerEmail || '').trim(),
    };
    if (ref && !perRef.has(ref)) perRef.set(ref, k);
    const n = c360Norm(naam);
    if (n && !perNaam.has(n)) perNaam.set(n, k);
  }
  return { perRef, perNaam };
}

// Zakelijk? Dan is de ONDERNEMING de hoofdnaam en de contactpersoon de subregel —
// dezelfde regel als de klantkolom in de tabellen (zie CustomerNameCell).
function klantWeergave(k) {
  const zakelijk = k.bedrijf && !String(k.segment || '').toLowerCase().startsWith('b2c');
  return { display: zakelijk ? k.bedrijf : (k.naam || k.ref), subNaam: zakelijk ? k.naam : '' };
}


// Waarop raakte de zoekterm? Een klantregel toont altijd naam en klantnummer; zocht
// je op iets anders (straat, e-mail, telefoon, BTW, verkoper, …) dan zie je
// anders niet WAAROM een klant in de lijst staat en moet je de waarde volledig
// intypen om zeker te zijn. Geeft { label, waarde } van het eerste andere veld
// dat raakt, of null wanneer naam/bedrijf/klantnummer al de treffer zijn. GSRN en
// factuurnummer hebben hun eigen sectie in het paneel en blijven hier buiten.
const raaktTekst = (v, ql) => !!v && String(v).toLowerCase().includes(ql);
const raaktCijfers = (v, qd) => qd.length >= 3 && !!v && String(v).toLowerCase().replace(/[^a-z0-9]/g, '').includes(qd);
function orderZoekTreffer(o, ql, qd, zichtbaar = null) {
  if ([o.customer, o.customerCompanyName, o.customerReference].some((v) => raaktTekst(v, ql)) || raaktCijfers(o.customerReference, qd)) return null;
  const velden = [
    ['Adres', o.connectionPointAddress, false],
    ['E-mail', o.customerEmail, false],
    ['Telefoon', o.customerPhone, true],
    ['BTW', o.customerVat, true],
    ['IBAN', o.customerBankAccount, true],
    ['Verkoper', o.salesperson, false],
    ['Team', o.salespersonTeam, false],
    ['Ratecard', o.ratecardName, false],
    ['Tag', zichtbareTagsTekst(o.partnerTags, zichtbaar), false],
  ];
  for (const [label, waarde, cijfers] of velden) {
    if (raaktTekst(waarde, ql) || (cijfers && raaktCijfers(waarde, qd))) return { label, waarde: String(waarde).trim() };
  }
  return null;
}
function factuurZoekTreffer(f, ql) {
  if ([f.relatieNaam, f.relatieBedrijf, f.relatieReferentie].some((v) => raaktTekst(v, ql))) return null;
  const velden = [['Verkoper', f.verkoper], ['Team', f.verkoopteam], ['Betaalstatus', f.statusBetaling], ['Soort', f.soort]];
  for (const [label, waarde] of velden) if (raaktTekst(waarde, ql)) return { label, waarde: String(waarde).trim() };
  return null;
}
// Bewaar per klant het eerste geraakte veld met hooguit twee verschillende
// waarden (bv. twee adressen in dezelfde straat) — meer maakt de regel onleesbaar.
function voegTrefferToe(rec, treffer) {
  if (!treffer) return;
  if (!rec.treffer) { rec.treffer = { label: treffer.label, waarden: [treffer.waarde] }; return; }
  if (rec.treffer.label !== treffer.label || rec.treffer.waarden.length >= 2 || rec.treffer.waarden.includes(treffer.waarde)) return;
  rec.treffer.waarden.push(treffer.waarde);
}
// De suggesties bij een zoekterm.
//   bron        'orders' (Orders/Metering/Customer 360) of 'facturen' (Invoices)
//   groepeerOp  'naam' (de pagina zoekt op klantnaam) of 'ref' (Metering werkt
//               per klantnummer, want twee klanten kunnen dezelfde naam dragen)
//   metFacturen aparte sectie met factuurNUMMERS (Invoices)
//   telGsrns    tel per klantregel de UNIEKE GSRN's i.p.v. de records (Metering:
//               daar kies je een klant om zijn aansluitingen te bekijken, dus is
//               het aantal aansluitingen het getal dat de keuze stuurt)
// Het `aantal` per klantregel is standaard het aantal records dat de tabel zou
// tonen — dat is precies waar "aantal resultaten" over gaat.
function bouwZoekSuggesties({
  query, orders = [], facturen = [], kenmerken = LEGE_KENMERKEN,
  bron = 'orders', groepeerOp = 'naam', metFacturen = false, telGsrns = false, zichtbareTags = null,
}) {
  const ql = c360Norm(query);
  if (!ql) return LEGE_SUGGESTIES;
  // Cijfer-genormaliseerde variant: zo vindt "5414 4882" ook een GSRN zonder
  // spaties. Zelfde regel als in orderMatchesSearch.
  const qd = ql.replace(/[^a-z0-9]/g, '');
  const gsrnRaak = (g) => {
    const v = String(g || '').trim();
    return !!v && (v.toLowerCase().includes(ql) || (qd.length >= 4 && v.replace(/[^0-9]/g, '').includes(qd)));
  };

  const klanten = new Map();
  const gsrns = new Map();
  const factuurLijst = [];

  const sleutelVan = (naam, ref, kenmerk) => (groepeerOp === 'ref'
    ? (ref || (kenmerk && kenmerk.ref) || '')
    : c360Norm(naam || (kenmerk && kenmerk.naam) || ''));

  // `gsrnSet` telt altijd mee, ook als de pagina op records telt: het kost niets en
  // zo hoeft de aanroeper alleen nog te zéggen welk van de twee getallen hij toont.
  const telKlant = (naam, ref, gsrn, treffer = null) => {
    const kenmerk = (ref && kenmerken.perRef.get(ref)) || kenmerken.perNaam.get(c360Norm(naam)) || null;
    const sleutel = sleutelVan(naam, ref, kenmerk);
    if (!sleutel) return;
    let rec = klanten.get(sleutel);
    if (!rec) {
      rec = {
        sleutel,
        naam: naam || (kenmerk && kenmerk.naam) || '',
        ref: ref || (kenmerk && kenmerk.ref) || '',
        segment: (kenmerk && kenmerk.segment) || '',
        bedrijf: (kenmerk && kenmerk.bedrijf) || '',
        email: (kenmerk && kenmerk.email) || '',
        records: 0,
        gsrnSet: new Set(),
        treffer: null,
      };
      klanten.set(sleutel, rec);
    }
    rec.records += 1;
    voegTrefferToe(rec, treffer);
    if (gsrn) rec.gsrnSet.add(gsrn);
  };
  const telling = (k) => (telGsrns ? k.gsrnSet.size : k.records);

  if (bron === 'facturen') {
    for (const f of facturen) {
      if (invoiceMatchesSearch(f, ql)) telKlant(String(f.relatieNaam || '').trim(), String(f.relatieReferentie || '').trim(), String(f.elGSRN || f.ngGSRN || '').trim(), factuurZoekTreffer(f, ql));
      const nummer = String(f.nummer || '').trim();
      if (metFacturen && nummer && nummer.toLowerCase().includes(ql) && factuurLijst.length < ZOEK_MAX) {
        factuurLijst.push({ nummer, klant: String(f.relatieNaam || '').trim(), datum: f.factuurdatum || '', soort: f.soort || '' });
      }
      for (const g of [f.elGSRN, f.ngGSRN]) {
        const v = String(g || '').trim();
        if (gsrnRaak(v) && !gsrns.has(v)) gsrns.set(v, { gsrn: v, klant: String(f.relatieNaam || '').trim(), ref: String(f.relatieReferentie || '').trim(), email: '' });
      }
    }
  } else {
    for (const o of orders) {
      const raak = orderMatchesSearch(o, ql, zichtbareTags);
      const naam = String(o.customer || '').trim();
      const ref = String(o.customerReference || '').trim();
      const email = String(o.customerEmail || '').trim();
      const g = String(o.connectionPointGSRN || '').trim();
      if (raak) telKlant(naam, ref, g, orderZoekTreffer(o, ql, qd, zichtbareTags));
      if (gsrnRaak(g) && !gsrns.has(g)) gsrns.set(g, { gsrn: g, klant: naam, ref, email });
    }
    // Een factuurnummer hoort óók naar zijn klant te leiden (Customer 360 zoekt
    // daar expliciet op). Alleen aanvullen: een klant die al via de orders
    // gevonden is, houdt zijn eigen telling.
    if (facturen.length && ql.length >= 3) {
      for (const f of facturen) {
        if (!String(f.nummer || '').toLowerCase().includes(ql)) continue;
        const kenmerk = kenmerken.perRef.get(String(f.relatieReferentie || '').trim());
        if (kenmerk && !klanten.has(sleutelVan(kenmerk.naam, kenmerk.ref, kenmerk))) telKlant(kenmerk.naam, kenmerk.ref);
      }
    }
  }

  return {
    klanten: [...klanten.values()]
      .sort((a, b) => telling(b) - telling(a))
      .slice(0, ZOEK_MAX)
      .map(({ gsrnSet, records, ...k }) => ({ ...k, aantal: telGsrns ? gsrnSet.size : records, ...klantWeergave(k) })),
    gsrns: [...gsrns.values()].slice(0, ZOEK_MAX),
    facturen: factuurLijst,
  };
}


// De zoekterm gemarkeerd in een tekst (hoofdletterongevoelig). Raakt de term
// alleen na cijfer-normalisatie (telefoon met spaties), dan blijft de tekst
// gewoon ongemarkeerd — beter dan een verkeerd stuk oplichten.
const Markeer = ({ tekst, term }) => {
  const t = String(tekst || '');
  const q = String(term || '').trim();
  const i = q ? t.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{t}</>;
  return <>{t.slice(0, i)}<mark className="bg-lime-400/30 text-inherit rounded-sm px-0.5">{t.slice(i, i + q.length)}</mark>{t.slice(i + q.length)}</>;
};
// Kopje boven een sectie in het suggestiepaneel.
const ZoekKop = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">{children}</div>
);

// `telWoord` = het woord achter het aantal per regel: { een: 'order', meer:
// 'orders' } → "12 orders". `onClear` toont het kruisje in het veld.
const KlantZoekVeld = ({
  label, placeholder, value, onChange, onClear, clearActief,
  suggesties = LEGE_SUGGESTIES, onKlant, onGsrn, onFactuur,
  telWoord = { een: 'order', meer: 'orders' }, className = '', inputRef,
}) => {
  const [open, setOpen] = useState(false);
  const eigenRef = useRef(null);
  const veldRef = inputRef || eigenRef;
  const q = String(value || '').trim();
  const aantal = suggesties.klanten.length + suggesties.gsrns.length + suggesties.facturen.length;
  const toonPaneel = open && !!q;
  // onMouseDown i.p.v. onClick: de blur van het invoerveld sluit het paneel, en
  // die vuurt vóór een click. Met mousedown is de keuze al gemaakt.
  const kies = (fn, arg) => { if (fn) { setOpen(false); fn(arg); } };
  const rij = 'w-full text-left px-3 py-2 rounded hover:bg-slate-700 text-sm text-slate-200 flex justify-between items-center gap-2';
  return (
    <div className={className}>
      {label && <label className="block text-xs text-slate-400 mb-1">{label}</label>}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          ref={veldRef}
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className={`w-full h-9 pl-9 ${onClear ? 'pr-9' : 'pr-3'} bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:border-lime-500 focus:outline-none`}
        />
        {onClear && (clearActief ?? !!q) && (
          <button onMouseDown={(e) => { e.preventDefault(); onClear(); }} title="Zoekopdracht wissen"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"><X size={15} /></button>
        )}
      </div>
      {toonPaneel && aantal > 0 && (
        <DropdownPanel anchorRef={veldRef} minWidth={440} className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-80 overflow-y-auto">
          {suggesties.klanten.length > 0 && (
            <div className="p-2">
              <ZoekKop>Klanten</ZoekKop>
              {suggesties.klanten.map((k) => (
                <button key={`k${k.sleutel}`} onMouseDown={() => kies(onKlant, k)} className={rij}>
                  <span className="min-w-0 flex items-center gap-2">
                    {k.segment && <SegmentBadge o={{ segment: k.segment }} />}
                    <span className="min-w-0">
                      <span className="flex items-center gap-2"><span className="truncate">{k.display}</span>{k.ref && <span className="text-[10px] font-mono text-slate-500 shrink-0">#{k.ref}</span>}</span>
                      {k.subNaam && <span className="block text-[10px] text-slate-500 truncate">{k.subNaam}</span>}
                      {k.treffer && <span className="block text-[10px] text-slate-400 truncate" title={`${k.treffer.label}: ${k.treffer.waarden.join(' · ')}`}><span className="text-slate-500">{k.treffer.label}: </span>{k.treffer.waarden.map((w, i) => <React.Fragment key={w}>{i > 0 && ' · '}<Markeer tekst={w} term={q} /></React.Fragment>)}</span>}
                    </span>
                  </span>
                  <span className="text-xs text-slate-500 shrink-0">{k.aantal} {k.aantal === 1 ? telWoord.een : telWoord.meer}</span>
                </button>
              ))}
            </div>
          )}
          {suggesties.gsrns.length > 0 && (
            <div className="p-2 border-t border-slate-700">
              <ZoekKop>EAN</ZoekKop>
              {suggesties.gsrns.map((g) => (
                <button key={`g${g.gsrn}`} onMouseDown={() => kies(onGsrn, g)} className={rij}>
                  <span className="min-w-0 flex items-center gap-2"><Gauge size={13} className="text-slate-500 shrink-0" /><span className="font-mono text-xs truncate">{g.gsrn}</span></span>
                  <span className="text-xs text-slate-500 shrink-0 truncate max-w-[45%]">{g.klant || '—'}</span>
                </button>
              ))}
            </div>
          )}
          {suggesties.facturen.length > 0 && (
            <div className="p-2 border-t border-slate-700">
              <ZoekKop>Factuurnummers</ZoekKop>
              {suggesties.facturen.map((f) => (
                <button key={`f${f.nummer}`} onMouseDown={() => kies(onFactuur, f)} className={rij}>
                  <span className="min-w-0 flex items-center gap-2"><FileText size={13} className="text-slate-500 shrink-0" /><span className="font-mono text-xs truncate">{f.nummer}</span></span>
                  <span className="text-xs text-slate-500 shrink-0 truncate max-w-[45%]">{f.klant || f.soort || '—'}</span>
                </button>
              ))}
            </div>
          )}
        </DropdownPanel>
      )}
      {toonPaneel && aantal === 0 && (
        <DropdownPanel anchorRef={veldRef} minWidth={360} className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-4 text-sm text-slate-400">
          Niets gevonden binnen je rechten.
        </DropdownPanel>
      )}
    </div>
  );
};

const OrdersPage = ({ orders, loading = false, facturen = [], onSettings, onOpenCustomer360, salesStructure = EMPTY_STRUCTURE, visiblePartnerTags, currentUser, onReassignOrder, labelConfig = {} }) => {
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [searchTerm, setSearchTerm] = useState('');
  const [sellerFilter, setSellerFilter] = useState('all');
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [ratecardFilter, setRatecardFilter] = useState('all');
  const [statusFilters, setStatusFilters] = useState([]);
  const [segmentFilters, setSegmentFilters] = useState([]);
  const [marketFilter, setMarketFilter] = useState('all');
  const [deliveryMonthFilter, setDeliveryMonthFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [cpStatusFilter, setCpStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  // Meervoudige (checkbox) filters — zelfde werking als "Opp. Stage": een lege
  // array = geen filtering ("Alle"), anders is elke aangevinkte waarde toegelaten.
  const [partnerTagsFilters, setPartnerTagsFilters] = useState([]);
  const [meterMethodFilters, setMeterMethodFilters] = useState([]);
  const [connectionTypeFilters, setConnectionTypeFilters] = useState([]);
  const [energyProductGroupFilter, setEnergyProductGroupFilter] = useState('all');
  // Workflow-filters (vervangen de oude "Flow Identifier"-filter): apart voor de
  // lopende en de laatst afgeronde workflow, want dat zijn twee verschillende vragen
  // ("waar staat deze order nu?" versus "wat is er laatst gebeurd?").
  const [workflowCurrentFilters, setWorkflowCurrentFilters] = useState([]);
  const [workflowLastFilters, setWorkflowLastFilters] = useState([]);
  const [referralsFilter, setReferralsFilter] = useState('all');
  const [overlapFilter, setOverlapFilter] = useState('all'); // EAN-overlap: 'all' | 'ja' | 'nee' (alleen admin/ET)
  const [sortConfig, setSortConfig] = useState({ key: 'orderDate', dir: 'desc' });
  const [showSegmentDropdown, setShowSegmentDropdown] = useState(false);
  const segmentKnopRef = useRef(null);
  // Dit paneel had nooit een buitenklik-sluiting. Zolang het een absolute div
  // was viel dat nauwelijks op; als portal blijft het anders over de pagina
  // hangen tot je opnieuw op de knop klikt.
  useEffect(() => {
    if (!showSegmentDropdown) return undefined;
    const h = (e) => {
      if (segmentKnopRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-dropdown-panel]")) return;
      setShowSegmentDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showSegmentDropdown]);
  const [currentPage, setCurrentPage] = useState(1);
  // Zoeksuggesties (zelfde veld als Customer 360): klanten met segment, klant-
  // nummer en het aantal orders eronder, plus de GSRN's. Een klik zet de zoekterm,
  // zodat de tabel eronder meteen op die klant/aansluiting staat.
  const zoekKenmerken = useMemo(() => klantKenmerken(orders), [orders]);
  const zoekSuggesties = useMemo(() => bouwZoekSuggesties({
    query: searchTerm, orders, kenmerken: zoekKenmerken, zichtbareTags: visiblePartnerTags,
  }), [searchTerm, orders, zoekKenmerken, visiblePartnerTags]);
  const zoekOp = (waarde) => { setSearchTerm(waarde); setCurrentPage(1); };
  // Horizontaal tab-menu binnen Orders (zoals Revenue/Sales/Customer 360). De
  // filterbalk staat erboven en geldt voor beide tabs: de fases-verdeling hoort
  // dezelfde selectie te tonen als de tabel eronder.
  // Welke van die tabs zichtbaar zijn, is per gebruiker instelbaar (subpagina's).
  const { tabs: ordersTabs, mag: magOrdersTab, leeg: geenOrdersTabs } = useSubPages('orders', currentUser, ORDERS_TABS);
  const [ordersTab, setOrdersTab] = useState(() => {
    return leesSessieWaarde('ordersTab') || 'orders';
  });
  useEffect(() => { schrijfSessieWaarde('ordersTab', ordersTab); }, [ordersTab]);
  // Is de bewaarde tab niet (meer) toegekend, spring dan naar de eerste die dat wel is.
  useEffect(() => {
    if (ordersTabs.length > 0 && !ordersTabs.some(t => t.id === ordersTab)) setOrdersTab(ordersTabs[0].id);
  }, [ordersTabs, ordersTab]);
  // Toont dit inhoudsblok? Tab actief ÉN toegekend — die tweede check zorgt dat
  // een niet-toegekende tab ook niets rendert als de bewaarde waarde nog wacht.
  const toonOrdersTab = (id) => magOrdersTab(id) && ordersTab === id;
  const [selectedOrder, setSelectedOrder] = useState(null);
  const pageSize = TABLE_PAGE_SIZE;
  // GSRN-overlapcontrole (zelfde logica als Revenue): enkel voor admin/ET. De
  // "contracted"-voorwaarde (Fee Contracted = Yes) geldt ook hier, ook al toont
  // deze tabel die kolom niet. Berekend over de volledige (gescopete) orderset,
  // los van de actieve filters, zodat overlappen consistent gemarkeerd blijven.
  const canSeeOverlap = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  const overlapOrderSet = useMemo(() => canSeeOverlap ? computeGsrnOverlapSet(orders.filter(isContractedOrder)) : new Set(), [orders, canSeeOverlap]);
  const hasOverlapTag = (o) => overlapOrderSet.has(o);
  // De laatst-afgeronde-workflow-kolommen + hun filter: enkel admin/ET-dossierbeheerder.
  const toonWlKolommen = magWorkflowLaatste(currentUser?.role);
  // Default cascade-selectie op de scope van de ingelogde gebruiker (master =
  // eigen energielabel, teamleader = +team, reseller = +verkoper; admin/ET =
  // alles). Herhaalt bij user-wissel (impersonate) zodra /api/auth/me de
  // scope-velden levert.
  //
  // Filter-persistentie: bij de EERSTE keer dat currentUser geladen is, herstellen we
  // opgeslagen filters (na refresh of terugkeer naar de pagina) i.p.v. de scope-default.
  // RAW herstel (geen clamp) — het zijn de eigen filters van de gebruiker en de data is
  // sowieso server-side gescopet. Bij een latere user-wissel (impersonate) resetten we
  // wél naar de scope van de nieuwe gebruiker.
  const ORDERS_FILTERS_KEY = 'orders.filters';
  const filtersRestored = useRef(false);
  useEffect(() => {
    if (!currentUser) return;
    const d = defaultScopeSelection(currentUser);
    let saved = null;
    if (!filtersRestored.current) {
      filtersRestored.current = true;
      saved = readSavedFilters(ORDERS_FILTERS_KEY);
      if (saved) saved = migrateSavedFilters(saved);   // hernoemde filterwaarden bijwerken
    }
    if (saved) {
      setCompanyFilter(saved.companyFilter ?? d.company);
      setVerkoopteamFilter(saved.verkoopteamFilter ?? d.team);
      setSellerFilter(saved.sellerFilter ?? d.seller);
      setSearchTerm(saved.searchTerm ?? '');
      setRatecardFilter(saved.ratecardFilter ?? 'all');
      setStatusFilters(Array.isArray(saved.statusFilters) ? saved.statusFilters : []);
      setSegmentFilters(Array.isArray(saved.segmentFilters) ? saved.segmentFilters : []);
      setMarketFilter(saved.marketFilter ?? 'all');
      setDeliveryMonthFilter(saved.deliveryMonthFilter ?? 'all');
      setCpStatusFilter(saved.cpStatusFilter ?? 'all');
      setPaymentStatusFilter(saved.paymentStatusFilter ?? 'all');
      setPartnerTagsFilters(asMultiFilter(saved.partnerTagsFilters, saved.partnerTagsFilter));
      setMeterMethodFilters(asMultiFilter(saved.meterMethodFilters, saved.meterMethodFilter));
      setConnectionTypeFilters(asMultiFilter(saved.connectionTypeFilters, saved.connectionTypeFilter));
      setEnergyProductGroupFilter(saved.energyProductGroupFilter ?? 'all');
      setWorkflowCurrentFilters(asMultiFilter(saved.workflowCurrentFilters, saved.workflowCurrentFilter));
      setWorkflowLastFilters(asMultiFilter(saved.workflowLastFilters, saved.workflowLastFilter));
      setReferralsFilter(saved.referralsFilter ?? 'all');
      setOverlapFilter(saved.overlapFilter ?? 'all');
      if (saved.sortConfig && saved.sortConfig.key) setSortConfig(saved.sortConfig);
      return;
    }
    setCompanyFilter(d.company);
    setVerkoopteamFilter(d.team);
    setSellerFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  const COLORS = [CHART_HUES.blauw, CHART_HUES.oranje, CHART_HUES.violet];

  // Betaalstatus per klantnummer, uit ÁLLE facturen van die klant. Bewust
  // dezelfde functie én dezelfde labelConfig als Revenue, zodat de per-label
  // drempel hier identiek uitvalt. (buildCustomerPaymentStatusMap levert enkel
  // Goed/Wanbetaler — het oninbaar deel is een Revenue-begrip.)
  const customerPaymentStatusMap = useMemo(() => buildCustomerPaymentStatusMap(facturen, labelConfig), [facturen, labelConfig]);

  const getPaymentStatus = (customerReference) => {
    if (!customerReference) return 'Goed';
    return customerPaymentStatusMap[customerReference.trim()] || 'Goed';
  };
  
  // Cascade-opties (energielabel → verkoopteam → verkoper) uit de centrale
  // sales-structuur — identiek in alle modules, rol-gescopet door de backend.
  const { companies, teams: verkoopteams, sellers } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter),
    [salesStructure, companyFilter, verkoopteamFilter]
  );
  // De SCOPE-selectie (energielabel → verkoopteam → verkoper) bakent af naar welke
  // orders je kijkt. ALLE data-afgeleide keuzelijsten hieronder vertrekken daarvan,
  // zodat er nooit een ratecard, segment, workflow of productgroep van een ánder
  // energielabel in een dropdown blijft staan. De overige filters tellen bewust
  // niet mee: die zijn nevengeschikt en zouden elkaars opties laten verdwijnen.
  // ── Tab Leads ──────────────────────────────────────────────────────────────
  // Zelfde tabel en filters als Orders, maar de basis is de leadselectie uit
  // src/leads.js (Opp. Stage "New", EAN-ontdubbeling altijd aan, bron Totaal/
  // Reseller/Online met Online als standaard). `orders` is al gescopet op de
  // rechten van de gebruiker, dus de leads zijn dat ook.
  const [leadsBron, setLeadsBron] = useState(() => leesSessieWaarde('leadsBron') || 'online');
  useEffect(() => { schrijfSessieWaarde('leadsBron', leadsBron); }, [leadsBron]);
  const leads = useMemo(() => (ordersTab === 'leads' ? bepaalLeads(orders, leadsBron) : []), [orders, leadsBron, ordersTab]);
  const isLeadsTab = ordersTab === 'leads';
  const ordersInScope = useMemo(() => {
    let rows = isLeadsTab ? leads : orders;
    if (companyFilter !== 'all') rows = rows.filter(o => o.company === companyFilter);
    if (verkoopteamFilter !== 'all') rows = rows.filter(o => o.salespersonTeam === verkoopteamFilter);
    if (sellerFilter !== 'all') rows = rows.filter(o => o.salesperson === sellerFilter);
    return rows;
  }, [orders, leads, isLeadsTab, companyFilter, verkoopteamFilter, sellerFilter]);
  const ratecards = useMemo(() => [...new Set(ordersInScope.map(o => o.ratecardName).filter(Boolean))].sort(), [ordersInScope]);
  const statuses = useMemo(() => [...new Set(ordersInScope.map(o => o.opportunityStage).filter(Boolean))].sort(), [ordersInScope]);
  const segments = useMemo(() => [...new Set(ordersInScope.map(o => o.segment).filter(Boolean))].sort(), [ordersInScope]);
  const markets = useMemo(() => [...new Set(ordersInScope.map(o => o.market).filter(Boolean))].sort(), [ordersInScope]);
  const deliveryMonths = useMemo(() => [...new Set(ordersInScope.map(o => o.deliveryStartDate?.substring(0, 7)).filter(Boolean))].sort().reverse(), [ordersInScope]);
  // CP-status-filter gegroepeerd: vaste opties "Active"/"External" + eventuele
  // onbekende ruwe statussen apart (zie CP_ACTIVE/CP_EXTERNAL).
  const cpStatuses = useMemo(() => {
    const others = [...new Set(ordersInScope.map(o => o.connectionPointStatus).filter(Boolean))]
      .filter(s => !CP_ACTIVE.has(s.toLowerCase()) && !CP_EXTERNAL.has(s.toLowerCase()))
      .sort();
    return ['Active', 'External', ...others];
  }, [ordersInScope]);
  // Alleen door admin zichtbaar gemaakte customer tags in de filter-dropdown.
  // Alleen de LOSSE customer tags die admin zichtbaar heeft gemaakt — een order met
  // drie tags levert dus tot drie keuzes op, niet één combinatie.
  const partnerTagsList = useMemo(() => [...new Set(ordersInScope.flatMap(o => zichtbareTags(o.partnerTags, visiblePartnerTags)))].sort(), [ordersInScope, visiblePartnerTags]);
  const meterMethods = useMemo(() => [...new Set(ordersInScope.map(o => o.meterMethod).filter(Boolean))].sort(), [ordersInScope]);
  const connectionTypes = useMemo(() => [...new Set(ordersInScope.map(o => o.connectionType).filter(Boolean))].sort(), [ordersInScope]);
  const energyProductGroups = useMemo(() => [...new Set(ordersInScope.map(o => o.energyProductGroup).filter(Boolean))].sort(), [ordersInScope]);
  // Workflow-filters draaien op dezelfde waarde als de kolom toont (de stap, met de
  // flow als terugval) — anders staan er keuzes in de lijst die nergens in de tabel
  // te zien zijn.
  const workflowCurrentOptions = useMemo(() => [...new Set(ordersInScope.map(o => workflowLabel(o.workflowCurrentFlow, o.workflowCurrentStep)).filter(Boolean))].sort(), [ordersInScope]);
  const workflowLastOptions = useMemo(() => [...new Set(ordersInScope.map(o => workflowLabel(o.workflowLastFlow, o.workflowLastStep)).filter(Boolean))].sort(), [ordersInScope]);

  // Wissel je van energielabel, team of verkoper, dan kan een gekozen waarde uit de
  // keuzelijst verdwijnen. Blijft ze staan, dan filtert ze door op iets wat niet
  // meer te zien is: een lege tabel zonder zichtbare oorzaak.
  //
  // Enkel opruimen zolang er orders IN deze scope zitten — anders zou dit tijdens
  // het laden (nog geen orders → lege keuzelijsten) de zojuist herstelde filters
  // wissen. De WORKFLOW-filters blijven er bewust buiten: een bewaarde favoriet van
  // vóór 04-08-2026 bevat daar een flow-naam die niet in de keuzelijst staat maar
  // via matchesWorkflowFilter wél werkt; opruimen zou die stil weggooien.
  useEffect(() => {
    if (ordersInScope.length === 0) return;
    const enkel = (waarde, opties, zet) => { if (waarde !== 'all' && !opties.includes(waarde)) zet('all'); };
    const meer = (waarden, opties, zet) => {
      const over = waarden.filter(v => opties.includes(v));
      if (over.length !== waarden.length) zet(over);
    };
    enkel(ratecardFilter, ratecards, setRatecardFilter);
    enkel(marketFilter, markets, setMarketFilter);
    enkel(deliveryMonthFilter, deliveryMonths, setDeliveryMonthFilter);
    enkel(cpStatusFilter, cpStatuses, setCpStatusFilter);
    enkel(energyProductGroupFilter, energyProductGroups, setEnergyProductGroupFilter);
    meer(statusFilters, statuses, setStatusFilters);
    meer(segmentFilters, segments, setSegmentFilters);
    meer(partnerTagsFilters, partnerTagsList, setPartnerTagsFilters);
    meer(meterMethodFilters, meterMethods, setMeterMethodFilters);
    meer(connectionTypeFilters, connectionTypes, setConnectionTypeFilters);
  }, [ordersInScope, ratecards, markets, deliveryMonths, cpStatuses, energyProductGroups, statuses, segments,
    partnerTagsList, meterMethods, connectionTypes, ratecardFilter, marketFilter, deliveryMonthFilter,
    cpStatusFilter, energyProductGroupFilter, statusFilters, segmentFilters, partnerTagsFilters,
    meterMethodFilters, connectionTypeFilters]);
  // Helper: orderrij heeft minstens één referral-veld ingevuld
  const hasReferralData = (o) => Boolean(o.ambassadorCode || o.referralLinkName || o.referrer || o.referralAmbassadorCode);
  
  const toggleSegment = (segment) => { setSegmentFilters(prev => prev.includes(segment) ? prev.filter(s => s !== segment) : [...prev, segment]); setCurrentPage(1); };
  
  const filteredOrders = useMemo(() => {
    let data = ordersInScope;
    if (searchTerm) data = data.filter(o => orderMatchesSearch(o, searchTerm, visiblePartnerTags));
    if (ratecardFilter !== 'all') data = data.filter(o => o.ratecardName === ratecardFilter);
    if (statusFilters.length > 0) data = data.filter(o => statusFilters.includes(o.opportunityStage));
    if (segmentFilters.length > 0) data = data.filter(o => segmentFilters.includes(o.segment));
    if (marketFilter !== 'all') data = data.filter(o => o.market === marketFilter);
    if (deliveryMonthFilter !== 'all') data = data.filter(o => o.deliveryStartDate?.startsWith(deliveryMonthFilter));
    if (cpStatusFilter === 'Active') data = data.filter(o => CP_ACTIVE.has((o.connectionPointStatus || '').toLowerCase()));
    else if (cpStatusFilter === 'External') data = data.filter(o => CP_EXTERNAL.has((o.connectionPointStatus || '').toLowerCase()));
    else if (cpStatusFilter !== 'all') data = data.filter(o => o.connectionPointStatus === cpStatusFilter);
    if (paymentStatusFilter !== 'all') data = data.filter(o => getPaymentStatus(o.customerReference) === paymentStatusFilter);
    // Gekozen tag(s) → de order matcht zodra hij ÉÉN ervan draagt.
    if (partnerTagsFilters.length > 0) data = data.filter(o => splitCustomerTags(o.partnerTags).some(t => partnerTagsFilters.includes(t)));
    if (meterMethodFilters.length > 0) data = data.filter(o => meterMethodFilters.includes(o.meterMethod));
    if (connectionTypeFilters.length > 0) data = data.filter(o => connectionTypeFilters.includes(o.connectionType));
    if (workflowCurrentFilters.length > 0) data = data.filter(o => matchesWorkflowFilter(workflowCurrentFilters, o.workflowCurrentFlow, o.workflowCurrentStep));
    // Alleen filteren zolang de kolom ook zichtbaar is: een bewaarde filter of een
    // gedeelde favoriet mag bij een partner niet stilzwijgend blijven filteren op
    // iets wat hij nergens op het scherm terugvindt.
    if (toonWlKolommen && workflowLastFilters.length > 0) data = data.filter(o => matchesWorkflowFilter(workflowLastFilters, o.workflowLastFlow, o.workflowLastStep));
    if (referralsFilter !== 'all') data = data.filter(o => referralsFilter === 'ja' ? hasReferralData(o) : !hasReferralData(o));
    if (energyProductGroupFilter !== 'all') data = data.filter(o => o.energyProductGroup === energyProductGroupFilter);
    if (canSeeOverlap && overlapFilter !== 'all') data = data.filter(o => overlapFilter === 'ja' ? overlapOrderSet.has(o) : !overlapOrderSet.has(o));
    // "Enkel overlap" krijgt een eigen, VASTE volgorde. Bij overlap wil je de
    // orders van dezelfde aansluiting naast elkaar kunnen leggen; een gewone
    // kolomsortering trekt die net uit elkaar en dan is de lijst onleesbaar.
    // Volgorde: klant (van de oudste order op die GSRN) → GSRN → startdatum
    // oplopend, zodat je per meter de opeenvolging van contracten van boven naar
    // beneden leest. Startdatum = de effectieve als die er is, anders de geplande.
    if (canSeeOverlap && overlapFilter === 'ja') {
      const start = (o) => o.effectiveStartDate || o.deliveryStartDate || '';
      const groep = new Map();   // EAN → klant van de oudste order
      for (const o of data) {
        const g = o.connectionPointGSRN || '';
        const huidig = groep.get(g);
        if (!huidig || start(o) < huidig.start) groep.set(g, { start: start(o), klant: customerPrimaryName(o) || '' });
      }
      const groepKlant = (o) => groep.get(o.connectionPointGSRN || '')?.klant || '';
      data = [...data].sort((a, b) =>
        groepKlant(a).localeCompare(groepKlant(b), 'nl-BE')
        || String(a.connectionPointGSRN || '').localeCompare(String(b.connectionPointGSRN || ''))
        || start(a).localeCompare(start(b))
        || String(customerPrimaryName(a) || '').localeCompare(String(customerPrimaryName(b) || ''), 'nl-BE'));
      return data;
    }
    data = [...data].sort((a, b) => { const av = a[sortConfig.key] || ''; const bv = b[sortConfig.key] || ''; return sortConfig.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
    return data;
  }, [ordersInScope, searchTerm, ratecardFilter, statusFilters, segmentFilters, marketFilter, deliveryMonthFilter, cpStatusFilter, paymentStatusFilter, partnerTagsFilters, meterMethodFilters, connectionTypeFilters, energyProductGroupFilter, workflowCurrentFilters, workflowLastFilters, toonWlKolommen, referralsFilter, overlapFilter, canSeeOverlap, overlapOrderSet, sortConfig, customerPaymentStatusMap, visiblePartnerTags]);
  
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const paginatedOrders = useMemo(() => { const start = (currentPage - 1) * pageSize; return filteredOrders.slice(start, start + pageSize); }, [filteredOrders, currentPage]);
  
  const handleSort = (key) => { setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' })); setCurrentPage(1); };
  const SortIcon = ({ col }) => sortConfig.key !== col ? <ChevronDown size={14} className="opacity-30" /> : (sortConfig.dir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />);
  
  
  // Verdeling over de verkoopfases (Opp. Stage) van de GEFILTERDE orders, gesplitst
  // naar prijsvorm: tariefkaarten (B2C + B2B) en Prijs op Maat (Custom). Die twee
  // doorlopen een ander verkoopproces, dus ze in één taart samenvatten verbergt
  // precies wat je wil zien.
  //
  // Er zijn ruim dertig fases; een taart met dertig partjes leest niemand. De
  // grafiek toont daarom de acht grootste en vat de rest samen als "Overig"; de
  // tabel ernaast blijft volledig.
  const FASE_TOP_N = 8;
  const bouwFaseVerdeling = useCallback((lijst) => {
    const per = new Map();
    for (const o of lijst) {
      const naam = (o.opportunityStage || '').trim() || '(geen fase)';
      per.set(naam, (per.get(naam) || 0) + 1);
    }
    const totaal = lijst.length;
    const alle = [...per.entries()]
      .map(([naam, aantal]) => ({ naam, aantal, aandeel: totaal ? aantal / totaal : 0 }))
      .sort((a, b) => b.aantal - a.aantal);
    const top = alle.slice(0, FASE_TOP_N);
    const rest = alle.slice(FASE_TOP_N);
    const restAantal = rest.reduce((s, r) => s + r.aantal, 0);
    const grafiek = restAantal > 0
      ? [...top, { naam: `Overig (${rest.length} fases)`, aantal: restAantal, aandeel: totaal ? restAantal / totaal : 0 }]
      : top;
    return { alle, grafiek, totaal };
  }, []);

  const faseTariefkaart = useMemo(
    () => bouwFaseVerdeling(filteredOrders.filter(o => ['B2C', 'B2B'].includes(orderSegmentClass(o)))),
    [filteredOrders, bouwFaseVerdeling]);
  const faseCustom = useMemo(
    () => bouwFaseVerdeling(filteredOrders.filter(o => orderSegmentClass(o) === 'CUSTOM')),
    [filteredOrders, bouwFaseVerdeling]);
  // Orders zonder herkenbaar segment vallen in geen van beide grafieken; dat zeggen
  // we erbij in plaats van ze stil te laten verdwijnen.
  const faseZonderSegment = filteredOrders.length - faseTariefkaart.totaal - faseCustom.totaal;

  // Vaste kleurvolgorde; de negende plek is altijd "Overig" en krijgt de neutrale
  // tint — nooit een negende hue erbij verzinnen. Deze volgorde is met de
  // dataviz-validator nagerekend voor AANGRENZENDE partjes (in een taart raken ze
  // elkaar): slechtste paar ΔE 8,7 bij deutan/tritan en 19,3 bij normaal zicht, in
  // zowel het lichte als het donkere thema. Herschikken? Eerst opnieuw valideren.
  const FASE_KLEUREN = [CHART_HUES.blauw, CHART_HUES.oranje, CHART_HUES.aqua, CHART_HUES.violet,
    CHART_HUES.geel, CHART_HUES.magenta, CHART_HUES.groen, CHART_HUES.rood];
  const faseKleur = (i) => (i < FASE_KLEUREN.length ? FASE_KLEUREN[i] : 'var(--chart-neutral)');

  const exportFases = (bestand, verdeling) => exportRowsToCsv(bestand, [
    { header: 'Verkoopfase', get: r => r.naam },
    { header: 'Aantal', get: r => r.aantal },
    { header: 'Aandeel %', get: r => (r.aandeel * 100).toFixed(1) },
  ], verdeling.alle);

  const clearFilters = () => { setSearchTerm(''); setCompanyFilter('all'); setVerkoopteamFilter('all'); setSellerFilter('all'); setRatecardFilter('all'); setStatusFilters([]); setSegmentFilters([]); setMarketFilter('all'); setDeliveryMonthFilter('all'); setCpStatusFilter('all'); setPaymentStatusFilter('all'); setPartnerTagsFilters([]); setMeterMethodFilters([]); setConnectionTypeFilters([]); setEnergyProductGroupFilter('all'); setWorkflowCurrentFilters([]); setWorkflowLastFilters([]); setReferralsFilter('all'); setOverlapFilter('all'); setCurrentPage(1); };
  const hasActiveFilters = searchTerm || companyFilter !== 'all' || verkoopteamFilter !== 'all' || sellerFilter !== 'all' || ratecardFilter !== 'all' || statusFilters.length > 0 || segmentFilters.length > 0 || marketFilter !== 'all' || deliveryMonthFilter !== 'all' || cpStatusFilter !== 'all' || paymentStatusFilter !== 'all' || partnerTagsFilters.length > 0 || meterMethodFilters.length > 0 || connectionTypeFilters.length > 0 || energyProductGroupFilter !== 'all' || workflowCurrentFilters.length > 0 || (toonWlKolommen && workflowLastFilters.length > 0) || referralsFilter !== 'all' || overlapFilter !== 'all';

  // Favoriete filters: het energielabel hoort MEE in de snapshot — zie favorietScope.
  // Oudere favorieten hebben geen companyFilter; die vallen terug op "Alle", precies
  // het gedrag van vóór deze wijziging.
  const filterSnapshot = {
    companyFilter,
    searchTerm, verkoopteamFilter, sellerFilter, ratecardFilter, statusFilters, segmentFilters,
    marketFilter, deliveryMonthFilter, cpStatusFilter, paymentStatusFilter, partnerTagsFilters,
    meterMethodFilters, connectionTypeFilters, energyProductGroupFilter, workflowCurrentFilters, workflowLastFilters,
    referralsFilter, overlapFilter, sortConfig,
  };
  const applyFavorite = (f) => {
    const scope = favorietScope(salesStructure, { company: f.companyFilter, team: f.verkoopteamFilter, seller: f.sellerFilter });
    setSearchTerm(f.searchTerm ?? '');
    setCompanyFilter(scope.company);
    setVerkoopteamFilter(scope.team);
    setSellerFilter(scope.seller);
    setRatecardFilter(f.ratecardFilter ?? 'all');
    setStatusFilters(Array.isArray(f.statusFilters) ? f.statusFilters : []);
    setSegmentFilters(Array.isArray(f.segmentFilters) ? f.segmentFilters : []);
    setMarketFilter(f.marketFilter ?? 'all');
    setDeliveryMonthFilter(f.deliveryMonthFilter ?? 'all');
    setCpStatusFilter(f.cpStatusFilter ?? 'all');
    setPaymentStatusFilter(f.paymentStatusFilter ?? 'all');
    setPartnerTagsFilters(asMultiFilter(f.partnerTagsFilters, f.partnerTagsFilter));
    setMeterMethodFilters(asMultiFilter(f.meterMethodFilters, f.meterMethodFilter));
    setConnectionTypeFilters(asMultiFilter(f.connectionTypeFilters, f.connectionTypeFilter));
    setEnergyProductGroupFilter(f.energyProductGroupFilter ?? 'all');
    setWorkflowCurrentFilters(asMultiFilter(f.workflowCurrentFilters, f.workflowCurrentFilter));
    setWorkflowLastFilters(asMultiFilter(f.workflowLastFilters, f.workflowLastFilter));
    setReferralsFilter(f.referralsFilter ?? 'all');
    setOverlapFilter(f.overlapFilter ?? 'all');
    if (f.sortConfig && f.sortConfig.key) setSortConfig(f.sortConfig);
    setCurrentPage(1);
  };

  // Persisteer de filters bij elke wijziging (pas nadat het herstel gebeurd is),
  // zodat een refresh of terugkeer naar de pagina dezelfde filtering toont.
  const filtersJson = JSON.stringify(filterSnapshot)   /* companyFilter zit sinds de favorieten-fix in de snapshot */;
  useEffect(() => {
    if (!filtersRestored.current) return;
    writeSavedFilters(ORDERS_FILTERS_KEY, filtersJson);
  }, [filtersJson]);

  // Nog aan het laden → enkel spinner + regel. Pas als het laden klaar is én er
  // écht niets is, tonen we de lege staat met de Instellingen-knop.
  if (!orders.length && loading) return <PageLoading message="Orders worden geladen…" />;
  if (!orders.length) return <EmptyState message="Nog geen orders beschikbaar — data komt automatisch uit de API." onSettings={onSettings} />;

  return <div className="space-y-4">
    {/* De export volgt de tabel: wie de WL-kolommen niet ziet, krijgt ze ook niet
        in het bestand — anders is één klik genoeg om de afscherming te omzeilen. */}
    <PageHeader title="Orders" subtitle="Overzicht van alle verkooporders" />
    <Card className="!p-3 !overflow-visible">
      <CollapsibleFilters active={hasActiveFilters}>
      <div className="mb-3 flex flex-wrap items-end gap-3"><div className="w-[184px]"><label className="block text-xs text-slate-400 mb-1">Energieleverancier</label><select value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setVerkoopteamFilter('all'); setSellerFilter('all'); setRatecardFilter('all'); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${companyFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={companies}>Alle</AllOption>{companies.map(c => <option key={c} value={c}>{c}</option>)}</select></div><div className="self-end"><FilterFavorites pageKey="orders" filters={filterSnapshot} onApply={applyFavorite} /></div><div className="w-[184px]"><label className="block text-xs text-slate-400 mb-1">Verkoopteam</label><select value={verkoopteamFilter} onChange={e => { setVerkoopteamFilter(e.target.value); setSellerFilter('all'); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={verkoopteams}>Alle</AllOption>{verkoopteams.map(t => <option key={t} value={t}>{t}</option>)}</select></div><div><SearchableSelect label="Verkoper" value={sellerFilter} onChange={v => { setSellerFilter(v); setCurrentPage(1); }} options={sellers} placeholder="Alle" /></div><KlantZoekVeld className="w-full sm:w-[280px] shrink-0" label="Zoeken" placeholder="Klant, klantnummer, EAN, BTW, e-mail, telefoon, adres…" value={searchTerm} onChange={zoekOp} onClear={() => zoekOp('')} suggesties={zoekSuggesties} onKlant={(k) => zoekOp(k.naam)} onGsrn={(g) => zoekOp(g.gsrn)} telWoord={{ een: 'order', meer: 'orders' }} /></div>
      <div className="flex flex-wrap items-end gap-3 relative z-40">
        <SearchableSelect label="Delivery" size="sm" value={deliveryMonthFilter} onChange={v => { setDeliveryMonthFilter(v); setCurrentPage(1); }} options={deliveryMonths} placeholder="Alle" />
        <SearchableMultiSelect label="Opp. Stage" values={statusFilters} onChange={v => { setStatusFilters(v); setCurrentPage(1); }} options={statuses} placeholder="Alle" />
        <SearchableSelect label="CP Status" size="sm" value={cpStatusFilter} onChange={v => { setCpStatusFilter(v); setCurrentPage(1); }} options={cpStatuses} placeholder="Alle" />
        <SearchableSelect label="Ratecard" value={ratecardFilter} onChange={v => { setRatecardFilter(v); setCurrentPage(1); }} options={ratecards} placeholder="Alle" />
        <div className="relative shrink-0 w-[132px]"><label className="block text-xs text-slate-400 mb-1">Segment</label><button ref={segmentKnopRef} onClick={() => { setShowSegmentDropdown(!showSegmentDropdown); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs text-left flex items-center justify-between ${segmentFilters.length > 0 ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><span className="truncate">{segmentFilters.length === 0 ? 'Alle' : segmentFilters.join(', ')}</span><ChevronDown size={12} /></button>{showSegmentDropdown && <DropdownPanel anchorRef={segmentKnopRef} minWidth={140} className="bg-slate-800 border border-slate-600 rounded shadow-2xl max-h-72 overflow-y-auto"><div className="p-2 border-b border-slate-600 bg-slate-800"><button onClick={() => setSegmentFilters([])} className="text-xs text-slate-400 hover:text-lime-400">Alles wissen</button></div>{segments.map(segment => <label key={segment} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-700 cursor-pointer bg-slate-800"><input type="checkbox" checked={segmentFilters.includes(segment)} onChange={() => toggleSegment(segment)} className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-3 h-3" /><span className="text-xs text-slate-200">{segment}</span></label>)}</DropdownPanel>}</div>
        <SearchableSelect label="Market" size="sm" value={marketFilter} onChange={v => { setMarketFilter(v); setCurrentPage(1); }} options={markets} placeholder="Alle" />
        {/* Meter Method staat vóór Connection Type: allebei beschrijven ze de
            aansluiting zelf, en zo staan de aansluitings-kenmerken bij elkaar. */}
        <SearchableMultiSelect label="Meter Method" size="sm" values={meterMethodFilters} onChange={v => { setMeterMethodFilters(v); setCurrentPage(1); }} options={meterMethods} placeholder="Alle" />
        <SearchableMultiSelect label="Connection Type" size="sm" values={connectionTypeFilters} onChange={v => { setConnectionTypeFilters(v); setCurrentPage(1); }} options={connectionTypes} placeholder="Alle" />
        <SearchableMultiSelect label="Workflow huidig" values={workflowCurrentFilters} onChange={v => { setWorkflowCurrentFilters(v); setCurrentPage(1); }} options={workflowCurrentOptions} placeholder="Alle" />
        {toonWlKolommen && <SearchableMultiSelect label="Workflow laatste" values={workflowLastFilters} onChange={v => { setWorkflowLastFilters(v); setCurrentPage(1); }} options={workflowLastOptions} placeholder="Alle" />}
        {/* Stop Contract staat bewust naast "Workflow laatste": het stopcontract-type
            hoort bij de laatst afgeronde workflow van de order. */}
        {/* Payment Status en Customer Tags staan hier, vlak vóór Referrals: het zijn
            alle drie kenmerken van de KLANT, geen kenmerken van de aansluiting. */}
        <div className="min-w-[132px]"><label className="block text-xs text-slate-400 mb-1">Payment Status</label><select value={paymentStatusFilter} onChange={e => { setPaymentStatusFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${paymentStatusFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="Goed">Goed</option><option value="Wanbetaler">Wanbetaler</option></select></div>
        <SearchableMultiSelect label="Customer Tags" values={partnerTagsFilters} onChange={v => { setPartnerTagsFilters(v); setCurrentPage(1); }} options={partnerTagsList} placeholder="Alle" />
        <div className="min-w-[132px]"><label className="block text-xs text-slate-400 mb-1">Referrals</label><select value={referralsFilter} onChange={e => { setReferralsFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${referralsFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="ja">Ja</option><option value="nee">Nee</option></select></div>
        <SearchableSelect label="Energy Product" value={energyProductGroupFilter} onChange={v => { setEnergyProductGroupFilter(v); setCurrentPage(1); }} options={energyProductGroups} placeholder="Alle" />
        {canSeeOverlap && <div className="min-w-[132px]"><label className="block text-xs text-red-400 mb-1">⚠ EAN-overlap</label><select value={overlapFilter} onChange={e => { setOverlapFilter(e.target.value); setCurrentPage(1); }} title="Enkel overlap: de tabel groepeert dan op EAN — klant, dan EAN, dan oudste startdatum eerst — zodat de orders van dezelfde aansluiting onder elkaar staan. De kolomsortering staat in die weergave uit." className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${overlapFilter !== 'all' ? 'border-red-500 text-red-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="ja">Enkel overlap</option><option value="nee">Zonder overlap</option></select></div>}
        {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}
      </div>
      </CollapsibleFilters>
      {hasActiveFilters && <div className="mt-2 text-xs text-lime-400">Gefilterd: {filteredOrders.length} van {orders.length} orders</div>}
    </Card>
    {/* Horizontaal tab-menu binnen Orders (zoals Revenue/Sales/Customer 360).
        De filterbalk hierboven geldt voor beide tabs, zodat de fase-verdeling
        precies dezelfde selectie beschrijft als de tabel. Welke tabs er staan,
        hangt af van de subpagina's die deze gebruiker toegekend kreeg. */}
    {geenOrdersTabs && <GeenSubpaginas module="Orders" />}
    <div className="-mx-1 overflow-x-auto no-scrollbar">
      <div className="flex gap-2 px-1 w-max">
        {ordersTabs.map(t => (
          <button key={t.id} onClick={() => setOrdersTab(t.id)}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${ordersTab === t.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}>
            {t.label}
          </button>
        ))}
        {/* Leads: bron van de lead. Online (geen verkoper toegekend) is de
            standaard — dat zijn de klanten die zelf begonnen zijn en die nog
            niemand opgepikt heeft. De EAN-ontdubbeling staat altijd aan. */}
        {toonOrdersTab('leads') && (
          <div className="flex items-center gap-1.5 ml-2 pl-3 border-l border-slate-700/60">
            <span className="text-[11px] text-slate-500 uppercase tracking-wide">Bron</span>
            <div className="inline-flex items-center gap-0.5 bg-slate-800/60 border border-slate-700/60 rounded-full p-0.5">
              {LEADS_BRONNEN.map(b => (
                <button key={b.id} type="button" onClick={() => { setLeadsBron(b.id); setCurrentPage(1); }}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${leadsBron === b.id ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}>{b.label}</button>
              ))}
            </div>
            <InfoTip title="Leads" text={'Orders in Opp. Stage "New (st.)", "Acq. (st.)" of "Acq. (cu.)": klanten die het onboardingformulier begonnen zijn en niet afgemaakt hebben. Een EAN die ook in een andere (niet-lead-)order staat, telt niet mee: dat is een her-invoer, geen lead. Van meerdere leads op dezelfde EAN blijft alleen de meest recente over.\n\nOnline = geen verkoper toegekend. Reseller = wel een verkoper. Totaal = beide.'} />
          </div>
        )}
      </div>
    </div>

    {/* ── Tab: Fases — verdeling van de gefilterde orders over de Opp. Stages ──
        Twee kaarten: tariefkaarten (B2C + B2B) en Prijs op Maat (Custom). Die
        doorlopen een ander verkoopproces, dus samen in één taart verbergt precies
        wat je wil zien. */}
    {/* Voorwaardelijk renderen, geen 'hidden': een Recharts ResponsiveContainer die
        in een display:none-container mount meet breedte 0 en tekent daarna niets
        meer tot er toevallig een resize langskomt. */}
    {toonOrdersTab('fases') && <div className="space-y-4">
      {faseZonderSegment > 0 && (
        <p className="text-xs text-slate-500">
          {formatNumberRounded(faseZonderSegment)} order(s) zonder herkenbaar segment staan in geen van beide grafieken.
        </p>
      )}
      {[
        { sleutel: 'tarief', titel: 'Verkoopfases — Tariefkaarten (B2C + B2B)', verdeling: faseTariefkaart, bestand: 'orders-per-verkoopfase-tariefkaarten.csv' },
        { sleutel: 'custom', titel: 'Verkoopfases — Prijs op Maat (Custom)', verdeling: faseCustom, bestand: 'orders-per-verkoopfase-prijs-op-maat.csv' },
      ].map(({ sleutel, titel, verdeling, bestand }) => (
        <Card key={sleutel}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">{titel} ({formatNumberRounded(verdeling.totaal)} orders)</h3><ExportKnop onExport={() => exportFases(bestand, verdeling)} disabled={verdeling.alle.length === 0} label="Exporteer deze tabel naar Excel" /></div>
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Orders binnen de huidige filters, geteld per Opp. Stage. De grafiek toont de acht grootste fases; de tabel ernaast is volledig.
          </p>
          {verdeling.alle.length === 0 ? <TableEmpty message="Geen orders in dit segment binnen deze filters." icon={Folder} /> : (
            <div className="flex flex-col lg:flex-row items-start gap-6 min-w-0">
              {/* items-start: de fasetabel telt tientallen rijen, dus met
                  items-center zou de donut halverwege die kolom komen zweven. */}
              <div className="w-full lg:w-2/5 min-w-0">
                {/* Geen enter-animatie op de taart: terwijl de data binnenstroomt
                    herrekent de verdeling en herstart Recharts die animatie telkens,
                    waardoor de taart leeg blijft staan. */}
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={verdeling.grafiek} dataKey="aantal" nameKey="naam" cx="50%" cy="50%"
                      innerRadius={55} outerRadius={105} labelLine={false} label={donutPctLabel}
                      isAnimationActive={false}>
                      {verdeling.grafiek.map((d, i) => <Cell key={d.naam} fill={faseKleur(i)} stroke="var(--chart-surface, #1e293b)" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => [`${formatNumberRounded(v)} orders`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legende: identiteit mag nooit alleen aan kleur hangen. */}
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-2">
                  {verdeling.grafiek.map((d, i) => (
                    <span key={d.naam} className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: faseKleur(i) }} />
                      {d.naam}
                    </span>
                  ))}
                </div>
              </div>
              <div className="w-full lg:flex-1 min-w-0 overflow-x-auto">
                <table className="w-full text-xs resp-card">
                  <thead><tr className="border-b border-slate-700 text-slate-400">
                    <th className="text-left py-1.5 px-2">Verkoopfase</th>
                    <th className="text-right py-1.5 px-2">Aantal</th>
                    <th className="text-right py-1.5 px-2">Aandeel</th>
                  </tr></thead>
                  <tbody>{verdeling.alle.map((r, i) => (
                    <tr key={r.naam} className="border-b border-slate-700/30">
                      <td className="py-1.5 px-2 text-slate-200" data-label="Verkoopfase">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: i < FASE_TOP_N ? faseKleur(i) : 'var(--chart-neutral)' }} />
                          <OppStageText value={r.naam} />
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-right text-slate-300 tabular-nums" data-label="Aantal">{formatNumberRounded(r.aantal)}</td>
                      <td className="py-1.5 px-2 text-right text-slate-400 tabular-nums" data-label="Aandeel">{(r.aandeel * 100).toFixed(1)}%</td>
                    </tr>
                  ))}</tbody>
                  <tfoot><tr className="border-t-2 border-slate-600 bg-lime-900/20">
                    <td className="py-1.5 px-2 text-lime-400 font-semibold">Totaal</td>
                    <td className="py-1.5 px-2 text-right text-lime-300 font-semibold tabular-nums">{formatNumberRounded(verdeling.totaal)}</td>
                    <td className="py-1.5 px-2 text-right text-lime-300 tabular-nums">100,0%</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>}

    {/* Zelfde tabel voor de tabs Orders en Leads (src/leads.js bepaalt de basis). */}
    <div className={(toonOrdersTab('orders') || toonOrdersTab('leads')) ? 'grid grid-cols-1 gap-6' : 'hidden'}>
      <div className="bg-slate-800 border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4"><div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">{isLeadsTab ? 'Leads' : 'Orders'} ({filteredOrders.length})</h3><ExportKnop onExport={() => exportToCSV(zonderWlVelden(filteredOrders, toonWlKolommen), isLeadsTab ? 'leads-gefilterd' : 'orders-gefilterd')} disabled={filteredOrders.length === 0} count={filteredOrders.length} label={isLeadsTab ? 'Exporteer de gefilterde leads naar Excel' : 'Exporteer de gefilterde orders naar Excel'} /></div><span className="text-sm text-slate-400">Pagina {currentPage} van {totalPages || 1}</span></div>
        {/* Mobiel: compacte, scanbare kaarten i.p.v. de ~36-koloms tabel. Zelfde
            data (paginatedOrders), dezelfde detail-actie (setSelectedOrder) en dezelfde
            paginatie hieronder — puur een betere mobiele weergave; geen wijziging aan
            data, rechten, filtering of logica. De volledige tabel blijft op desktop. */}
        <div className="md:hidden space-y-2">
          {paginatedOrders.map(o => {
            const overlap = canSeeOverlap && hasOverlapTag(o);
            return (
              <button key={o.id} onClick={() => setSelectedOrder(o)} className={`w-full text-left rounded-xl border p-3 transition-colors bg-slate-800 hover:bg-slate-700 ${overlap ? 'border-red-400' : 'border-slate-700/50'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{o.customer || o.customerReference || '—'}</div>
                    <div className="text-xs text-slate-400 truncate">{[o.company, o.orderDate].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 shrink-0 mt-0.5" />
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <OppStageBadge value={o.opportunityStage} />
                  <span className="text-sm font-semibold text-lime-400 tabular-nums whitespace-nowrap">{o.totaal > 0 ? formatCurrency(o.totaal) : '—'}</span>
                </div>
                {overlap && <div className="mt-1.5 text-[11px] font-medium text-red-500">⚠ EAN-overlap</div>}
              </button>
            );
          })}
        </div>
        <div className="overflow-auto hidden md:block md:max-h-[70vh]">
          <table className="w-full text-sm resp-card">
            <thead className="bg-slate-800 sticky top-0 z-10"><tr className="border-b border-slate-700">
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('company')}><span className="flex items-center gap-1">Energielabel <SortIcon col="company" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('segment')}><span className="flex items-center gap-1">Segment <SortIcon col="segment" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('orderDate')}><span className="flex items-center gap-1">Orderdatum <SortIcon col="orderDate" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('customerReference')}><span className="flex items-center gap-1">Klantennummer <SortIcon col="customerReference" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('customerCompanyName')}><span className="flex items-center gap-1">Bedrijfsnaam <SortIcon col="customerCompanyName" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('customerVat')}><span className="flex items-center gap-1">BTW-nr <SortIcon col="customerVat" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('customer')}><span className="flex items-center gap-1">Klant <SortIcon col="customer" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Telefoon</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">E-mail</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Bank Account</th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('market')}><span className="flex items-center gap-1">Market <SortIcon col="market" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">EAN</th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('meterMethod')}><span className="flex items-center gap-1">Meter Method <SortIcon col="meterMethod" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('connectionType')}><span className="flex items-center gap-1">Connection Type <SortIcon col="connectionType" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Adres</th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('connectionPointStatus')}><span className="flex items-center gap-1">CP Status <SortIcon col="connectionPointStatus" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('opportunityStage')}><span className="flex items-center gap-1">Opp. Stage <SortIcon col="opportunityStage" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('status')}><span className="flex items-center gap-1">Status <SortIcon col="status" /></span></th>
              <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('totaal')}><span className="flex items-center gap-1 justify-end">Potentiële omzet <SortIcon col="totaal" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('deliveryStartDate')}><span className="flex items-center gap-1">Delivery Start <SortIcon col="deliveryStartDate" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Effective Start</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Delivery End</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Effective End</th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('productGroep')}><span className="flex items-center gap-1">Product Groep <SortIcon col="productGroep" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('energyProductGroup')}><span className="flex items-center gap-1">Energy Product <SortIcon col="energyProductGroup" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('ratecardName')}><span className="flex items-center gap-1">Ratecard <SortIcon col="ratecardName" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Team</th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('salesperson')}><span className="flex items-center gap-1">Verkoper <SortIcon col="salesperson" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Contract</th>
              {/* Elke workflow gevolgd door ZIJN eigen stopgegevens. WH = workflow
                  huidig, WL = workflow laatste — de prefix maakt in een brede tabel
                  meteen duidelijk bij welke van de twee een kolom hoort. */}
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs" title="De workflow die nu loopt — de stap (of de flow zolang de stap leeg is)">Workflow — huidig</th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('workflowCurrentLossDate')} title="Stop datum van de workflow die nu loopt"><span className="flex items-center gap-1">WH Stop datum <SortIcon col="workflowCurrentLossDate" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('workflowCurrentStopType')} title="Stop contract-type van de workflow die nu loopt"><span className="flex items-center gap-1">WH Stop contract <SortIcon col="workflowCurrentStopType" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs" title="Toelichting bij het stop contract-type van de lopende workflow">WH Stop reason</th>
              {/* Laatst afgeronde workflow: enkel admin/ET-dossierbeheerder (magWorkflowLaatste). */}
              {toonWlKolommen && <>
                <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs" title="De laatst afgeronde workflow — de stap (of de flow zolang de stap leeg is)">Workflow — laatste</th>
                <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('workflowLastLossDate')} title="Stop datum van de laatst afgeronde workflow"><span className="flex items-center gap-1">WL Stop datum <SortIcon col="workflowLastLossDate" /></span></th>
                <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('workflowLastStopType')} title="Stop contract-type van de laatst afgeronde workflow"><span className="flex items-center gap-1">WL Stop contract <SortIcon col="workflowLastStopType" /></span></th>
                <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs" title="Toelichting bij het stop contract-type van de laatst afgeronde workflow">WL Stop reason</th>
              </>}
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Ambassador Code</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Referral Link</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Referrer</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Referral Amb. Code</th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Customer Tags</th>
              <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('estOfftakeKwh')}><span className="flex items-center gap-1 justify-end">Est. Offtake (kWh) <SortIcon col="estOfftakeKwh" /></span></th>
              <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('estInjectionKwh')}><span className="flex items-center gap-1 justify-end">Est. Injection (kWh) <SortIcon col="estInjectionKwh" /></span></th>
              <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Customer Payment Status</th>
            </tr></thead>
            <tbody>{paginatedOrders.map(o => <tr key={o.id} onClick={() => setSelectedOrder(o)} onContextMenu={e => onC360Context(e, o.customer, klantTarget(o.customer, o.customerReference, undefined, [o.connectionPointGSRN]), undefined, orderEtLinks(o))} title={canSeeOverlap && hasOverlapTag(o) ? 'Overlappende leveringsperiode met een ander actief (contracted) order op dezelfde EAN' : undefined} className={`border-b border-slate-700/30 cursor-pointer ${canSeeOverlap && hasOverlapTag(o) ? 'bg-red-900/40 hover:bg-red-900/50 text-red-300' : cpStatusRowClass(o.connectionPointStatus)}`}>
              <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.company}</td>
              <td className="py-2 px-2"><SegmentBadge o={o} /></td>
              <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs">{o.orderDate}</td>
              <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{o.customerReference}<KlantnrKopieerKnop nr={o.customerReference} /></td>
              <td className="py-2 px-2 text-slate-300 text-xs truncate max-w-40" title={o.customerCompanyName}>{o.customerCompanyName || '—'}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap font-mono">{o.customerVat || '—'}</td>
              <td className="py-2 px-2 truncate max-w-44 text-xs" title={o.customer ? `Open Customer 360° voor ${customerPrimaryName(o)}` : ''}>
                <CustomerNameCell order={o} onOpenCustomer360={onOpenCustomer360} />
              </td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.customerPhone}</td>
              <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-32" title={o.customerEmail}>{o.customerEmail}</td>
              <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.customerBankAccount}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.market}</td>
              <td className="py-2 px-2 text-xs font-mono whitespace-nowrap" title={o.connectionPointGSRN ? `Open Customer 360° voor EAN ${o.connectionPointGSRN}` : ''}>
                {o.connectionPointGSRN && onOpenCustomer360 ? (
                  <button onClick={e => { e.stopPropagation(); onOpenCustomer360({ type: 'gsrn', value: o.connectionPointGSRN, label: o.customer ? `${o.customer} · ${o.connectionPointGSRN}` : o.connectionPointGSRN }); }} className="text-slate-500 hover:text-lime-400 hover:underline cursor-pointer">
                    {o.connectionPointGSRN}
                  </button>
                ) : <span className="text-slate-500">{o.connectionPointGSRN || ''}</span>}
                <EanKopieerKnop ean={o.connectionPointGSRN} />
              </td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.meterMethod}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.connectionType}</td>
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-40" title={o.connectionPointAddress}>{o.connectionPointAddress}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.connectionPointStatus}</td>
              <td className="py-2 px-2"><OppStageBadge value={o.opportunityStage} /></td>
              <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-24">{o.status}</td>
              <td className="py-2 px-2 text-right text-lime-400 text-xs whitespace-nowrap font-medium">{o.totaal > 0 ? formatCurrency(o.totaal) : '—'}</td>
              <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{o.deliveryStartDate}</td>
              <td className="py-2 px-2 text-slate-500 whitespace-nowrap text-xs">{o.effectiveStartDate}</td>
              <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{o.deliveryEndDate}</td>
              <td className="py-2 px-2 text-slate-500 whitespace-nowrap text-xs">{o.effectiveEndDate}</td>
              <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.productGroep}</td>
              <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.energyProductGroup}</td>
              <td className="py-2 px-2 text-slate-300 truncate max-w-32 text-xs" title={o.ratecardName}>{o.ratecardName}</td>
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-20">{o.salespersonTeam}</td>
              <td className="py-2 px-2 whitespace-nowrap text-xs"><SellerCell order={o} currentUser={currentUser} salesStructure={salesStructure} onReassignOrder={onReassignOrder} /></td>
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-24">{o.contract}</td>
              <td className="py-2 px-2 text-xs max-w-40"><WorkflowCell flow={o.workflowCurrentFlow} step={o.workflowCurrentStep} /></td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.workflowCurrentLossDate}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.workflowCurrentStopType}</td>
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-32" title={o.workflowCurrentStopReason}>{o.workflowCurrentStopReason}</td>
              {toonWlKolommen && <>
                <td className="py-2 px-2 text-xs max-w-40"><WorkflowCell flow={o.workflowLastFlow} step={o.workflowLastStep} /></td>
                <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.workflowLastLossDate}</td>
                <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.workflowLastStopType}</td>
                <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-32" title={o.workflowLastStopReason}>{o.workflowLastStopReason}</td>
              </>}
              <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.ambassadorCode}</td>
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-24" title={o.referralLinkName}>{o.referralLinkName}</td>
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-24" title={o.referrer}>{o.referrer}</td>
              <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.referralAmbassadorCode}</td>
              {/* Enkel de tags die deze rol mag zien — een order met drie tags kan
                  er dus twee tonen (zie zichtbareTags bovenaan). */}
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-32" title={zichtbareTagsTekst(o.partnerTags, visiblePartnerTags)}>{zichtbareTagsTekst(o.partnerTags, visiblePartnerTags)}</td>
              <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{o.estOfftakeKwh ? formatNumber(o.estOfftakeKwh) : '—'}</td>
              <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{o.estInjectionKwh ? formatNumber(o.estInjectionKwh) : '—'}</td>
              <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getPaymentStatus(o.customerReference) === 'Wanbetaler' ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{getPaymentStatus(o.customerReference)}</span></td>
            </tr>)}</tbody>
          </table>
        </div>
        {totalPages > 1 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700"><div className="text-sm text-slate-400">Toont {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredOrders.length)} van {filteredOrders.length}</div><div className="flex items-center gap-2"><button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Eerste</button><button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button><span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{currentPage}</span><button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button><button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Laatste</button></div></div>}
        {filteredOrders.length === 0 && <div className="text-center py-8 text-slate-400"><AlertCircle size={32} className="mx-auto mb-2 opacity-50" /><p>Geen orders gevonden</p></div>}
      </div>
    </div>
    {/* Controlelijst (alle gescopete orders, los van de paginafilters) — enkel
        admin + ET Dossierbeheerder (zelfde rolgrens als de overlap-controle).
        Hoort bij de ordertabel, dus enkel op die tab. */}
    {canSeeOverlap && <div className={toonOrdersTab('orders') ? '' : 'hidden'}><MissingTeamSellerTable orders={orders} onOpenCustomer360={onOpenCustomer360} /></div>}
    {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)}
      visiblePartnerTags={visiblePartnerTags} paymentStatus={getPaymentStatus(selectedOrder.customerReference)} />}
    {c360Menu}
  </div>;
};

// De rol komt hier uit de auth-context en niet uit een prop: dit venster wordt op
// zes plekken gemount (Orders, Revenue, Portfolio, Retention, Customer 360…) en die
// zouden anders allemaal dezelfde prop moeten doorgeven — één vergeten call-site is
// meteen een lek.
// (Daarom staat magWorkflowLaatste hieronder op user?.role uit de context.)
// Detailvenster van één order. Toont ALLES wat de Orders-tabel toont, en op een
// desktopscherm past dat in één beeld — daar hoort geen scrollbalk bij. Vandaar:
//   • zes vaste blokken naast elkaar (1 / 2 / 3 kolommen naar schermbreedte),
//     elk blok met een compacte twee-koloms veldenlijst;
//   • kleine typografie (label 10px, waarde 12px) en krappe regelafstand;
//   • geen samenvattingsstrip meer bovenaan — die herhaalde de aansluiting; de
//     kerngegevens staan nu als badges/regel in de kop.
// Op mobiel scrollt het venster gewoon; daar is dat de normale gang van zaken.
//
// `visiblePartnerTags` en `paymentStatus` komen alleen van de Orders-pagina (die
// kent ze). Ontbreken ze, dan valt het veld weg i.p.v. een misleidende "—":
// customer tags zijn ROL-GESCOPET, dus de ruwe tagtekst mag hier nooit zomaar
// gerenderd worden.
const OrderDetailModal = ({ order, onClose, visiblePartnerTags = null, paymentStatus = null }) => {
  const { user } = useAuth();
  const Field = ({ label, value, mono = false, className = '' }) => (
    <div className={`min-w-0 ${className}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 leading-tight">{label}</div>
      <div className={`text-xs text-slate-100 break-words leading-snug ${mono ? 'font-mono' : ''}`}>
        {value === 0 || value ? value : <span className="text-slate-500">—</span>}
      </div>
    </div>
  );
  const Blok = ({ icon: Icon, title, children, className = '' }) => (
    <section className={`bg-slate-800/40 border border-slate-700/50 rounded-lg p-3 ${className}`}>
      <h4 className="text-[11px] font-semibold text-lime-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Icon size={13} />{title}</h4>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">{children}</div>
    </section>
  );
  const tags = visiblePartnerTags ? zichtbareTagsTekst(order.partnerTags, visiblePartnerTags) : null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-6xl max-h-[94vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Kop: wie + de vier kenmerken waarop je een order herkent. */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-700/70 sticky top-0 bg-slate-900 z-10">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base sm:text-lg font-bold text-slate-100 break-words">{customerPrimaryName(order) || 'Order'}</h3>
              <SegmentBadge o={order} />
              <OppStageBadge value={order.opportunityStage} />
              {order.status && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-700/60 text-slate-300 whitespace-nowrap">{order.status}</span>}
            </div>
            <p className="text-xs text-slate-400 mt-0.5 break-words">
              {[order.company, order.orderDate, order.customerReference, order.contract].filter(Boolean).join(' · ')}
            </p>
          </div>
          <button onClick={onClose} aria-label="Sluiten" className="shrink-0 p-1 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-slate-700/50"><X size={18} /></button>
        </div>

        <div className="p-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <Blok icon={User} title="Klant">
            <Field label="Klant" value={order.customer} />
            <Field label="Bedrijfsnaam" value={order.customerCompanyName} />
            <Field label="Klantnummer" value={order.customerReference} mono />
            <Field label="BTW-nummer" value={order.customerVat} mono />
            <Field label="Telefoon" value={order.customerPhone} />
            <Field label="Bankrekening" value={order.customerBankAccount} mono />
            <Field label="E-mail" value={order.customerEmail} className="col-span-2" />
            {paymentStatus && (
              <Field label="Customer payment status" value={
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${paymentStatus === 'Wanbetaler' ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{paymentStatus}</span>
              } />
            )}
          </Blok>

          <Blok icon={Zap} title="Aansluiting">
            <Field label="EAN" value={order.connectionPointGSRN} mono className="col-span-2" />
            <Field label="Market" value={order.market} />
            <Field label="Connection type" value={order.connectionType} />
            <Field label="Meter method" value={order.meterMethod} />
            <Field label="CP status" value={order.connectionPointStatus} />
            <Field label="Adres" value={order.connectionPointAddress} className="col-span-2" />
          </Blok>

          <Blok icon={Package} title="Product & volume">
            <Field label="Product groep" value={order.productGroep} />
            <Field label="Energy product" value={order.energyProductGroup} />
            <Field label="Ratecard" value={order.ratecardName} className="col-span-2" />
            <Field label="Potentiële omzet" value={order.totaal > 0 ? <span className="text-lime-400 font-medium">{formatCurrency(order.totaal)}</span> : null} />
            <Field label="Segment" value={order.segment} />
            <Field label="Est. offtake (kWh)" value={order.estOfftakeKwh ? formatNumber(order.estOfftakeKwh) : null} />
            <Field label="Est. injection (kWh)" value={order.estInjectionKwh ? formatNumber(order.estInjectionKwh) : null} />
          </Blok>

          <Blok icon={CalendarDays} title="Levering">
            <Field label="Orderdatum" value={order.orderDate} />
            <Field label="Status" value={order.status} />
            <Field label="Delivery start" value={order.deliveryStartDate} />
            <Field label="Delivery end" value={order.deliveryEndDate} />
            <Field label="Effective start" value={order.effectiveStartDate} />
            <Field label="Effective end" value={order.effectiveEndDate} />
          </Blok>

          {/* Elke workflow met zijn eigen stopgegevens eronder — zelfde volgorde
              als in de tabel. De laatst afgeronde workflow is enkel voor
              admin/ET-dossierbeheerder (magWorkflowLaatste); dit venster mag daar
              geen achterdeur voor zijn. */}
          <Blok icon={FileText} title="Contract & workflow">
            <Field label="Contract" value={order.contract} className="col-span-2" />
            {/* De STAND van de aansluiting volgens het vertrekmodel (workflow +
                stap + CP-status), neutraal geformuleerd en dus voor elke rol:
                wat er nu geldt, en de eerdere situatie als die er is ("Drop
                geannuleerd op …", "Teruggewonnen na loss op …"). */}
            {(() => {
              const s = vertrekStand(order);
              const status = PORTFOLIO_STATUS_LABELS[STAND_NAAR_STATUS[s.stand]] || PORTFOLIO_STATUS_LABELS.unknown;
              return <>
                <Field label="Stand" value={<span className={`px-2 py-0.5 border rounded text-xs ${status.cls}`}>{status.label}</span>} />
                <Field label="Vertrekdatum" value={s.datum ? mtDatum(s.datum) : ''} />
                <Field label="Vertreksoort" value={s.soort && s.soort !== 'none' ? (DEPARTURE_KIND_LABELS[s.soort] || s.soort) : ''} />
                <Field label="Eerdere situatie" value={s.historiek ? s.historiek.label : ''} />
              </>;
            })()}
            <Field label="Workflow — huidig" value={workflowLabel(order.workflowCurrentFlow, order.workflowCurrentStep)} className="col-span-2" />
            <Field label="WH stop datum" value={order.workflowCurrentLossDate} />
            <Field label="WH stop contract" value={order.workflowCurrentStopType} />
            <Field label="WH stop reason" value={order.workflowCurrentStopReason} className="col-span-2" />
            {magWorkflowLaatste(user?.role) && <>
              <Field label="Workflow — laatste" value={workflowLabel(order.workflowLastFlow, order.workflowLastStep)} className="col-span-2" />
              <Field label="WL stop datum" value={order.workflowLastLossDate} />
              <Field label="WL stop contract" value={order.workflowLastStopType} />
              <Field label="WL stop reason" value={order.workflowLastStopReason} className="col-span-2" />
            </>}
          </Blok>

          <Blok icon={Users} title="Verkoop & referral">
            <Field label="Energielabel" value={order.company} />
            <Field label="Verkoopteam" value={order.salespersonTeam} />
            <Field label="Verkoper" value={order.salesperson} />
            {tags !== null && <Field label="Customer tags" value={tags} />}
            <Field label="Ambassador code" value={order.ambassadorCode} />
            <Field label="Referral amb. code" value={order.referralAmbassadorCode} />
            <Field label="Referral link" value={order.referralLinkName} />
            <Field label="Referrer" value={order.referrer} />
          </Blok>
        </div>
      </div>
    </div>, document.body);
};

const SALES_PATTERNS = ['contracted', 'switch accepted', 'ready for switching'];

// Strikte stage-set voor de Top/Bottom-verkoperstabellen: enkel echt afgesloten
// orders tellen mee (B2C/B2B/Custom-aantallen, Offtake/Injection, omzet, sortering).
// Bewust enger dan SALES_PATTERNS (geen 'Ready for switching'). Match op de exacte,
// kleingeletterde opportunityStage-waarde — de bron levert '(cu.)' in kleine letters.
const SELLER_TABLE_STAGES = new Set([
  'contracted (st.)', 'contracted (cu.)',
  'switch accepted (st.)', 'switch accepted (cu.)',
]);
const isSellerTableOrder = (o) => SELLER_TABLE_STAGES.has((o.opportunityStage || '').trim().toLowerCase());

// ── Verkoopkanaal: reseller vs online ────────────────────────────────────────
// Wie telt als "online" staat al in isOnlineSeller (bovenaan, bij de
// verkoper-/teamcorrectie): 'Online' én Odoo's systeemgebruiker 'OdooBot'.
// Hier alleen het TONEN: OdooBot heet in de interface Online. De ruwe waarde
// blijft OdooBot, want daarop matcht o.salesperson.
const sellerLabel = (naam) => (isOnlineSeller(naam) ? ONLINE_TEAM : naam);
const SALES_KANALEN = [
  { value: 'all', label: 'Totaal' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'online', label: 'Online' },
];

// ── Sales per maand: één aggregator voor beide indelingen ────────────────────
// De tegels lezen de VOLLEDIGE salesset; de grafiek kan dezelfde berekening op
// een kleinere set draaien (zonder de wissel-/verlengingsorders). Daarom als
// functie i.p.v. twee losse useMemo's met dezelfde logica erin.
const SEGMENT_KEYS = ['B2C', 'B2B', 'Custom'];
const METER_KEYS = ['YMR', 'SMR1', 'SMR3', 'MMR', 'AMR', 'Other'];
// De vier eenheden van de grafiek: aantal (geen suffix), omzet, offtake, injection.
const MAAT_SUFFIXEN = ['', '_omzet', '_offtake', '_injection'];
const legeMaandRij = (month, keys) => {
  const rij = { month };
  for (const k of [...keys, 'total']) { rij[k] = 0; rij[`${k}_omzet`] = 0; rij[`${k}_offtake`] = 0; rij[`${k}_injection`] = 0; }
  return rij;
};
const orderMaand = (o, dateBasis) => String((dateBasis === 'orderDate' ? o.orderDate : o.deliveryStartDate) || '').substring(0, 7);
const classifySegment = (o) => {
  const s = (o.segment || '').toUpperCase();
  // Geen match → geen segmentbucket, maar wél meetellen in total (zoals voorheen).
  return s.includes('B2C') ? 'B2C' : s.includes('B2B') ? 'B2B' : s.includes('CUSTOM') ? 'Custom' : null;
};
const classifyMeter = (o) => {
  const m = (o.meterMethod || '').toUpperCase();
  if (m.includes('YMR')) return 'YMR';
  if (m.includes('SMR1') || m === 'SMR 1') return 'SMR1';
  if (m.includes('SMR3') || m === 'SMR 3') return 'SMR3';
  if (m.includes('MMR')) return 'MMR';
  if (m.includes('AMR')) return 'AMR';
  return 'Other';
};
const buildSalesPerMaand = (orders, dateBasis, keys, classify) => {
  const grouped = {};
  for (const o of orders || []) {
    const month = orderMaand(o, dateBasis);
    if (!month) continue;
    const rij = grouped[month] || (grouped[month] = legeMaandRij(month, keys));
    const omzet = o.totaal || 0;
    const offtake = (o.estOfftakeKwh || 0) / 1000;
    const injection = (o.estInjectionKwh || 0) / 1000;
    const k = classify(o);
    if (k) { rij[k]++; rij[`${k}_omzet`] += omzet; rij[`${k}_offtake`] += offtake; rij[`${k}_injection`] += injection; }
    rij.total++; rij.total_omzet += omzet; rij.total_offtake += offtake; rij.total_injection += injection;
  }
  return Object.values(grouped).sort((a, b) => a.month.localeCompare(b.month));
};

// De stapelvolgorde van de meterreeksen is GEVALIDEERD, niet willekeurig: rood
// naast aqua, geel naast oranje en magenta naast rood zakken alle drie onder de
// kleurenblindheid-drempel. Deze volgorde (rood, blauw, oranje, aqua, geel,
// violet) houdt die paren uit elkaar en laat achteraan plaats voor de twee
// bewegingsreeksen (magenta, groen). Herschik dus niet zonder de validator.
const COLORS_METER = {
  YMR: CHART_HUES.rood, SMR1: CHART_HUES.blauw, SMR3: CHART_HUES.oranje,
  MMR: CHART_HUES.aqua, AMR: CHART_HUES.geel, Other: CHART_HUES.violet,
};

// Verbindingsstreepje van een donutsegment naar zijn percentage. Kort en neutraal,
// en alleen waar ook een percentage staat (vanaf 1 %): de standaardlijn van
// Recharts loopt door tot in het label en tekent ook bij segmenten zonder label.
const donutLijn = ({ cx, cy, midAngle, outerRadius, percent }) => {
  if (!(percent >= 0.01)) return <g />;
  const hoek = -midAngle * Math.PI / 180;
  const punt = (r) => `${cx + r * Math.cos(hoek)},${cy + r * Math.sin(hoek)}`;
  return <path className="recharts-pie-label-line" d={`M${punt(outerRadius + 3)}L${punt(outerRadius + 9)}`} fill="none" stroke="#94A3B8" strokeWidth={1} />;
};

// De drie bewegingssoorten van "Wissels & verlengingen", in stapelvolgorde.
const BEWEGING_STAPEL = [
  { key: 'customerSwitches', naam: 'Klantenwissels', kleur: COLORS_BEWEGING.klantenwissel },
  { key: 'switches', naam: 'Productwissels', kleur: COLORS_BEWEGING.productwissel },
  { key: 'renewals', naam: 'Contractverlengingen', kleur: COLORS_BEWEGING.verlenging },
];

// Hoeveel EAN's er in elke Top MWh-ranglijst staan. Eén plek, want het getal staat
// ook in de drie kopjes.
const TOP_MWH_SIZE = 10;

// Eén ranglijst uit Top MWh. Drie keer dezelfde tabel (elektriciteit afname,
// elektriciteit injectie, gas afname) — vandaar één component in plaats van drie
// kopieën die uit elkaar gaan lopen. `accent` kleurt de rangbol en het volume.
const TOP_MWH_ACCENTEN = {
  blue: { bol: 'bg-blue-500 text-slate-900', waarde: 'text-blue-300' },
  green: { bol: 'bg-green-500 text-slate-900', waarde: 'text-green-300' },
  orange: { bol: 'bg-orange-500 text-slate-900', waarde: 'text-orange-300' },
};
const TopMwhTabel = ({ rijen, veld, accent, onContext, onOpenCustomer360 }) => {
  const kleur = TOP_MWH_ACCENTEN[accent] || TOP_MWH_ACCENTEN.blue;
  if (!rijen.length) return <p className="text-sm text-slate-500 italic">Geen data</p>;
  return (
    <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
      <thead><tr className="border-b border-slate-700 text-xs text-slate-400">
        <th className="py-2 text-left w-8">#</th>
        <th className="py-2 text-left">EAN</th>
        <th className="py-2 text-left">Klant</th>
        <th className="py-2 text-right">MWh</th>
      </tr></thead>
      <tbody>{rijen.map((r, i) => (
        <tr key={r.gsrn} className="border-b border-slate-700/30 hover:bg-slate-700/20"
          onContextMenu={e => onContext(e, r.order?.customer, klantTarget(r.order?.customer, r.order?.customerReference), undefined, orderEtLinks(r.order || {}))}>
          <td className="py-2"><div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? kleur.bol : 'bg-slate-700 text-slate-300'}`}>{i + 1}</div></td>
          {/* Het leveringsadres onder de EAN: een GSRN zegt niemand iets, het adres
              maakt meteen duidelijk wélke aansluiting bovenaan staat. Klein en
              grijs, zodat de ranglijst zelf leidend blijft. */}
          <td className="py-2 text-slate-300 font-mono text-xs">
            {r.gsrn}
            {r.order?.connectionPointAddress && (
              <span className="block font-sans text-[10px] text-slate-500 truncate max-w-[220px]" title={r.order.connectionPointAddress}>{r.order.connectionPointAddress}</span>
            )}
          </td>
          <td className="py-2 text-xs truncate max-w-[180px]" title={r.customer}><CustomerNameCell order={r.order} onOpenCustomer360={onOpenCustomer360} /></td>
          <td className={`py-2 text-right font-semibold ${kleur.waarde}`}>{formatNumberRounded(r[veld])}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};

// Sales-tabs. Alleen 'prestaties' is per gebruiker instelbaar (config/subpages.js);
// de overige volgen de module.
const SALES_TABS = [{ id: 'samenvatting', label: 'Samenvatting' }, { id: 'aansluitingen', label: 'Actieve aansluitingen' }, { id: 'topmwh', label: 'Top MWh' }, { id: 'verkopers', label: 'Verkopers' }, { id: 'productgroep', label: 'Sales per Productgroep' }, { id: 'prestaties', label: 'Prestaties' }];
// Eénmalige starttab (mijlpaalkaart → "Bekijk prestaties").
let gamiSalesStartTab = null;
const SalesPage = ({ orders, facturen = [], onSettings, onOpenCustomer360, salesStructure = EMPTY_STRUCTURE, currentUser, activeBranding = null, allBranding = [], allTeamBranding = [], labelConfig = {} }) => {
  // Rechtsklikmenu (Customer 360 / Metering / Lynx-Odoo-deeplinks) voor de
  // klantrijen in Top MWh, net als in Orders en de andere tabellen.
  const { onContext: onSalesC360Context, menu: salesC360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [dateBasis, setDateBasis] = useState('orderDate');
  const [chartType, setChartType] = useState('bar');
  const [chartView, setChartView] = useState('segment');
  const [yAxisMode, setYAxisMode] = useState('count');
  const [pieView, setPieView] = useState('meter');
  const [pieValueMode, setPieValueMode] = useState('count');
  const [marketFilter, setMarketFilter] = useState('all');
  // Verkoopkanaal: 'all' (geen extra filter) | 'reseller' | 'online'. Werkt op
  // dezelfde hoogte als marketFilter, dus op de Samenvatting-tab waar de toggle
  // staat — niet op de tabbladen waar de bediening niet zichtbaar is.
  const [kanaalFilter, setKanaalFilter] = useState('all');
  // Neemt de grafiek Sales per Maand de wissels & verlengingen als aparte reeksen
  // op? Default aan.
  const [inclWissels, setInclWissels] = useState(true);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [verkoperFilter, setVerkoperFilter] = useState('all');
  const [cpStatusFilter, setCpStatusFilter] = useState('all');
  const [showAllSellers, setShowAllSellers] = useState(false);
  const [salesTab, setSalesTab] = useState(() => { const t = gamiSalesStartTab; gamiSalesStartTab = null; return t || 'samenvatting'; }); // horizontaal tab-menu binnen Sales
  const { token: salesToken } = useAuth();
  const { tabs: salesTabs, mag: magSalesTab } = useSubPages('sales', currentUser, SALES_TABS);
  useEffect(() => {
    const open = () => { gamiSalesStartTab = null; setSalesTab('prestaties'); };
    window.addEventListener('gami-open-prestaties', open);
    return () => window.removeEventListener('gami-open-prestaties', open);
  }, []);
  // Omzet (potentiële omzet) is gevoelige info: enkel admin en ET Dossierbeheerder
  // mogen ze zien. Andere rollen krijgen de Omzet-filteropties, -kolommen én
  // -exportvelden niet (Sales per Maand, Actieve aansluitingen, Top/Bottom verkopers).
  const canSeeOmzet = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  // Default cascade-selectie op de scope van de ingelogde gebruiker (master =
  // eigen energielabel, teamleader = +team, reseller = +verkoper; admin/ET =
  // alles). Herhaalt bij user-wissel (impersonate) zodra /api/auth/me de
  // scope-velden levert.
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setCompanyFilter(d.company);
    setVerkoopteamFilter(d.team);
    setVerkoperFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  useFilterPersistence('sales.filters', currentUser,
    { marketFilter, kanaalFilter, companyFilter, verkoopteamFilter, verkoperFilter, cpStatusFilter },
    (s) => { setMarketFilter(s.marketFilter ?? 'all'); setKanaalFilter(s.kanaalFilter ?? 'all'); setCompanyFilter(s.companyFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); setCpStatusFilter(s.cpStatusFilter ?? 'all'); });
  // Cascade-opties (energielabel → verkoopteam → verkoper) uit de centrale
  // sales-structuur — identiek in alle modules, rol-gescopet door de backend.
  const { companies, teams: verkoopteams, sellers: verkopers } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter),
    [salesStructure, companyFilter, verkoopteamFilter]
  );

  // Get unique CP statuses (filtered by company, verkoopteam, verkoper)
  const cpStatuses = useMemo(() => {
    let data = orders;
    if (companyFilter !== 'all') data = data.filter(o => o.company === companyFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(o => o.salespersonTeam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(o => o.salesperson === verkoperFilter);
    return [...new Set(data.map(o => o.connectionPointStatus).filter(Boolean))].sort();
  }, [orders, companyFilter, verkoopteamFilter, verkoperFilter]);

  const handleCompanyChange = (value) => {
    setCompanyFilter(value);
    setVerkoopteamFilter('all');
    setVerkoperFilter('all');
    setCpStatusFilter('all');
  };

  const handleVerkoopteamChange = (value) => {
    setVerkoopteamFilter(value);
    setVerkoperFilter('all');
    setCpStatusFilter('all');
  };

  const handleVerkoperChange = (value) => {
    setVerkoperFilter(value);
    setCpStatusFilter('all');
  };

  const baseFilteredOrders = useMemo(() => { 
    let data = orders;
    if (companyFilter !== 'all') data = data.filter(o => o.company === companyFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(o => o.salespersonTeam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(o => o.salesperson === verkoperFilter);
    if (cpStatusFilter === 'active') data = data.filter(o => CP_ACTIVE.has((o.connectionPointStatus || '').toLowerCase()));
    else if (cpStatusFilter === 'external') data = data.filter(o => CP_EXTERNAL.has((o.connectionPointStatus || '').toLowerCase()));
    else if (cpStatusFilter !== 'all') data = data.filter(o => o.connectionPointStatus === cpStatusFilter);
    return data;
  }, [orders, companyFilter, verkoopteamFilter, verkoperFilter, cpStatusFilter]);
  const filteredOrders = useMemo(() => {
    let data = baseFilteredOrders;
    if (marketFilter !== 'all') data = data.filter(o => (o.market || '').toUpperCase().startsWith(marketFilter));
    if (kanaalFilter === 'online') data = data.filter(o => isOnlineSeller(o.salesperson));
    else if (kanaalFilter === 'reseller') data = data.filter(o => !isOnlineSeller(o.salesperson));
    return data;
  }, [baseFilteredOrders, marketFilter, kanaalFilter]);
  // COLORS_SEGMENT/COLORS_METER komen uit het centrale palet bovenaan dit bestand.

  // De salesset: orders in een verkoopstage. Voedt de tegels, de export, de
  // groeicijfers én de grafiek.
  const salesOrders = useMemo(
    () => filteredOrders.filter(o => SALES_PATTERNS.some(p => (o.opportunityStage || '').toLowerCase().includes(p))),
    [filteredOrders]
  );

  // ── Wissels & verlengingen ─────────────────────────────────────────────────
  // Zelfde detectie als Portfolio Insights: meerdere orders op één actieve GSRN.
  // Draait op filteredOrders (niet op salesOrders), want een keten bestaat juist
  // uit meerdere orders en die hoeven niet allemaal in een verkoopstage te staan.
  const { switches, customerSwitches, renewals } = useMemo(
    () => detectProductSwitchesAndRenewals(filteredOrders),
    [filteredOrders]
  );
  // Het NIEUWE order van elke beweging. Object-identiteit volstaat: detect()
  // duwt dezelfde referenties door die ook in filteredOrders zitten. Elk order is
  // hoogstens één keer een 'new' (per opeenvolgend paar één event), dus wissel-
  // en verlengingsset overlappen nooit — dat is wat dubbeltelling uitsluit.
  const bewegingOrders = useMemo(() => {
    const wissels = new Set();
    const verlengingen = new Set();
    for (const e of switches) if (e.new) wissels.add(e.new);
    for (const e of customerSwitches) if (e.new) wissels.add(e.new);
    for (const e of renewals) if (e.new) verlengingen.add(e.new);
    return { wissels, verlengingen };
  }, [switches, customerSwitches, renewals]);

  // Bewegingen per maand, gebucket op DEZELFDE datumbasis als de rest van deze
  // tab. Portfolio Insights gebruikt hiervoor altijd de orderdatum; hier zou dat
  // betekenen dat een order uit de segmentbalken van maand X verdwijnt en in de
  // wisselbalk van maand Y opduikt zodra je op Delivery Start staat.
  // Per maand zowel de losse tellingen (voor de Portfolio-gelijke grafiek) als de
  // samengevoegde reeksen mét omzet/MWh (voor Sales per Maand, die vier eenheden
  // kent). Zonder die drie extra maten toont de grafiek lege wisselbalken zodra
  // je op Omzet of MWh zet.
  const bewegingenPerMaand = useMemo(() => {
    const merged = {};
    const bucket = (m) => (merged[m] = merged[m] || {
      month: m, switches: 0, customerSwitches: 0, renewals: 0,
      wissels: 0, wissels_omzet: 0, wissels_offtake: 0, wissels_injection: 0,
      verlengingen: 0, verlengingen_omzet: 0, verlengingen_offtake: 0, verlengingen_injection: 0,
    });
    const tel = (events, veld, reeks) => {
      for (const e of events) {
        const o = e.new;
        const m = o ? orderMaand(o, dateBasis) : '';
        if (!m) continue;
        const rij = bucket(m);
        rij[veld]++;
        rij[reeks]++;
        rij[`${reeks}_omzet`] += o.totaal || 0;
        rij[`${reeks}_offtake`] += (o.estOfftakeKwh || 0) / 1000;
        rij[`${reeks}_injection`] += (o.estInjectionKwh || 0) / 1000;
      }
    };
    tel(switches, 'switches', 'wissels');
    tel(customerSwitches, 'customerSwitches', 'wissels');
    tel(renewals, 'renewals', 'verlengingen');
    return Object.values(merged).sort((a, b) => a.month.localeCompare(b.month));
  }, [switches, customerSwitches, renewals, dateBasis]);
  const heeftBewegingen = switches.length + customerSwitches.length + renewals.length > 0;

  // Tegels/groei/export blijven op de VOLLEDIGE salesset — het vinkje hoort de
  // grafiek te sturen, niet de kengetallen erboven.
  const chartDataSegment = useMemo(() => buildSalesPerMaand(salesOrders, dateBasis, SEGMENT_KEYS, classifySegment), [salesOrders, dateBasis]);

  // De grafiek zelf: de wissel-/verlengingsorders gaan UIT de segment-/meter-
  // balken en komen als eigen reeksen terug. Zo staat geen enkel order twee keer
  // in dezelfde grafiek. Vinkje uit → de reeksen verdwijnen en de orders erin ook.
  const grafiekSalesOrders = useMemo(
    () => salesOrders.filter(o => !bewegingOrders.wissels.has(o) && !bewegingOrders.verlengingen.has(o)),
    [salesOrders, bewegingOrders]
  );
  const chartData = useMemo(() => {
    const keys = chartView === 'segment' ? SEGMENT_KEYS : METER_KEYS;
    const rijen = buildSalesPerMaand(grafiekSalesOrders, dateBasis, keys, chartView === 'segment' ? classifySegment : classifyMeter);
    const leegBeweging = { wissels: 0, wissels_omzet: 0, wissels_offtake: 0, wissels_injection: 0, verlengingen: 0, verlengingen_omzet: 0, verlengingen_offtake: 0, verlengingen_injection: 0 };
    const perMaand = new Map(rijen.map(r => [r.month, { ...r, ...leegBeweging }]));
    if (inclWissels) {
      // Maanden samenvoegen: een maand met enkel bewegingen (geen nieuwe verkoop)
      // moet ook een balk krijgen.
      for (const b of bewegingenPerMaand) {
        const rij = perMaand.get(b.month) || { ...legeMaandRij(b.month, keys), ...leegBeweging };
        const { month, switches: _s, customerSwitches: _c, renewals: _r, ...maten } = b;
        perMaand.set(b.month, { ...rij, ...maten });
      }
    }
    // Eigen totaalreeks voor de lijngrafiek: `total` uit buildSalesPerMaand telt
    // enkel de segment-/meterbalken, dus zonder deze optelling zou de stippellijn
    // "Totaal" ónder de zichtbare reeksen liggen zodra het vinkje aan staat.
    return [...perMaand.values()]
      .map(rij => {
        const uit = { ...rij };
        for (const s of MAAT_SUFFIXEN) {
          uit[`grafiekTotal${s}`] = (rij[`total${s}`] || 0) + (rij[`wissels${s}`] || 0) + (rij[`verlengingen${s}`] || 0);
        }
        return uit;
      })
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [chartView, grafiekSalesOrders, dateBasis, inclWissels, bewegingenPerMaand]);

  const detailData = salesOrders;
  const totals = useMemo(() => chartDataSegment.reduce((acc, d) => ({ B2C: acc.B2C + d.B2C, B2B: acc.B2B + d.B2B, Custom: acc.Custom + d.Custom, total: acc.total + d.total, totalOfftakeMwh: acc.totalOfftakeMwh + d.total_offtake, totalInjectionMwh: acc.totalInjectionMwh + d.total_injection, B2C_offtake: acc.B2C_offtake + d.B2C_offtake, B2B_offtake: acc.B2B_offtake + d.B2B_offtake, Custom_offtake: acc.Custom_offtake + d.Custom_offtake, B2C_injection: acc.B2C_injection + d.B2C_injection, B2B_injection: acc.B2B_injection + d.B2B_injection, Custom_injection: acc.Custom_injection + d.Custom_injection }), { B2C: 0, B2B: 0, Custom: 0, total: 0, totalOfftakeMwh: 0, totalInjectionMwh: 0, B2C_offtake: 0, B2B_offtake: 0, Custom_offtake: 0, B2C_injection: 0, B2B_injection: 0, Custom_injection: 0 }), [chartDataSegment]);
  // Top/Bottom-verkoperstabellen: uitsluitend Contracted/Switch Accepted (st./cu.).
  const allSalesData = useMemo(() => baseFilteredOrders.filter(isSellerTableOrder), [baseFilteredOrders]);
  const energyProductBreakdown = useMemo(() => {
    const counts = {};
    filteredOrders.forEach(o => { const k = o.energyProductGroup || 'Onbekend'; counts[k] = (counts[k] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredOrders]);
  const referrerTop = useMemo(() => {
    const stats = {};
    filteredOrders.forEach(o => {
      if (!o.referrer) return;
      if (!stats[o.referrer]) stats[o.referrer] = { name: o.referrer, count: 0, omzet: 0 };
      stats[o.referrer].count++;
      stats[o.referrer].omzet += o.totaal || 0;
    });
    return Object.values(stats).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [filteredOrders]);
  const stopContractBreakdown = useMemo(() => {
    const counts = {};
    filteredOrders.forEach(o => { if (!o.stopContractType) return; counts[o.stopContractType] = (counts[o.stopContractType] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredOrders]);
  const sellerStats = useMemo(() => { 
    const stats = {}; 
    allSalesData.forEach(o => { 
      let seller = o.salesperson || 'Onbekend';
      if (seller === 'OdooBot') seller = 'Online';
      if (!stats[seller]) stats[seller] = { name: seller, team: o.salespersonTeam || '', count: 0, b2c: 0, b2b: 0, custom: 0, firstSaleDate: null, openDebt: 0, vervallenBedrag: 0, totaalGefactureerd: 0, potentieleOmzet: 0, estOfftakeMwh: 0, estInjectionMwh: 0 };
      stats[seller].count++;
      stats[seller].potentieleOmzet += o.totaal || 0;
      stats[seller].estOfftakeMwh += (o.estOfftakeKwh || 0) / 1000;
      stats[seller].estInjectionMwh += (o.estInjectionKwh || 0) / 1000;
      if (!stats[seller].team && o.salespersonTeam) stats[seller].team = o.salespersonTeam;
      const orderDate = o.orderDate;
      if (orderDate && (!stats[seller].firstSaleDate || orderDate < stats[seller].firstSaleDate)) stats[seller].firstSaleDate = orderDate;
      const seg = (o.segment || '').toUpperCase(); 
      if (seg.includes('B2C')) stats[seller].b2c++; 
      else if (seg.includes('B2B')) stats[seller].b2b++; 
      else if (seg.includes('CUSTOM')) stats[seller].custom++; 
    }); 
    // Facturen erbij — dezelfde scope-filters als de orders hierboven, zodat de
    // bedragen bij de getoonde selectie horen. De betaalstatus wordt hier NIET
    // weggefilterd: de noemer van het percentage heeft ook de betaalde facturen
    // nodig.
    let filteredFacturen = facturen;
    if (companyFilter !== 'all') {
      filteredFacturen = filteredFacturen.filter(f => f.relatieBedrijf === companyFilter);
    }
    if (verkoopteamFilter !== 'all') {
      filteredFacturen = filteredFacturen.filter(f => f.verkoopteam === verkoopteamFilter);
    }
    if (verkoperFilter !== 'all') {
      filteredFacturen = filteredFacturen.filter(f => f.verkoper === verkoperFilter);
    }
    filteredFacturen.forEach(f => {
      let seller = f.verkoper || 'Onbekend';
      if (seller === 'OdooBot') seller = 'Online';
      const s = stats[seller];
      if (!s) return;
      const status = (f.statusBetaling || '').toLowerCase();
      const onbetaald = status !== 'betaald' && status !== 'omgekeerd';
      const isVerkoopfactuur = (f.soort || '').toLowerCase() === 'verkoopfactuur';
      // `openDebt` = ALLES wat nog openstaat, ook wat nog niet vervallen is. Staat
      // sinds 01-09-2026 niet meer in de tabel (die toont "Vervallen"), maar blijft
      // in de Excel-export: daar is het naast het vervallen bedrag nuttige context.
      if (onbetaald) s.openDebt += invOutstandingOrTotal(f);
      // Noemer van het %: alles wat deze verkoper GEFACTUREERD heeft (incl. btw),
      // betaald of niet. Enkel verkoopfacturen: creditnota's en self bills zijn
      // geen vordering op de klant en horen dus in geen van beide kanten van de
      // breuk.
      if (isVerkoopfactuur) s.totaalGefactureerd += Number(f.totaal) || 0;
      // Kolom "Vervallen": het nog openstaande bedrag (incl. btw) op de facturen
      // die minstens VERKOPER_VERVALLEN_DAGEN (7) dagen over hun vervaldatum
      // staan. Onbetaald én verkoopfactuur, zodat teller en noemer van het % over
      // dezelfde soort documenten gaan; creditnota's en self bills zijn geen
      // vordering op de klant. Een deels betaalde factuur telt enkel voor haar
      // restbedrag (invOutstandingOrTotal), net als "Openstaand (incl. BTW)"
      // elders in het portaal.
      //
      // Dit is bewust NIET de wanbetaler-toets (isRecordBadDebtOverdue, per label
      // instelbaar, standaard 40 dagen): die is een afrekening, deze kolom een
      // vroeg opvolgingssignaal. Tot 01-09-2026 heette de kolom "Open" en stond er
      // ÁLLE openstaand in — ook een factuur die pas gisteren verstuurd was en dus
      // niets zegt over de opvolging.
      if (onbetaald && isVerkoopfactuur && getDaysOverdue(f.vervaldatum) >= VERKOPER_VERVALLEN_DAGEN) {
        s.vervallenBedrag += invOutstandingOrTotal(f);
      }
    });
    // Vervallen-% per verkoper: het vervallen bedrag gedeeld door alles wat hij
    // gefactureerd heeft (betaald én onbetaald). Vóór 01-09-2026 stond hier het
    // WANBETALER-bedrag in de teller, met een andere dagendrempel dan de kolom
    // ernaast — kolom en percentage vertelden dus twee verschillende verhalen.
    Object.values(stats).forEach(s => {
      s.vervallenPct = s.totaalGefactureerd > 0 ? (s.vervallenBedrag / s.totaalGefactureerd) * 100 : 0;
    });
    // Sort by potentieleOmzet instead of count
    const sorted = Object.values(stats).sort((a, b) => b.potentieleOmzet - a.potentieleOmzet); 
    return { top8: sorted.slice(0, 8), bottom8: sorted.filter(s => s.potentieleOmzet > 0).slice(-8).reverse(), all: sorted, total: sorted.length };
  }, [allSalesData, facturen, companyFilter, verkoopteamFilter, verkoperFilter]);

  // Export-rij in Excel-vriendelijke vorm (Nederlandse decimaalkomma + EUR-formatting).
  // Data is al gescoped door baseFilteredOrders → server filter + client-side
  // scopeFilter — geen extra autorisatie-check nodig.
  const exportSellerStats = () => {
    const rows = sellerStats.all.map((s, idx) => ({
      Rank: idx + 1,
      Verkoper: s.name,
      Verkoopteam: s.team || '',
      'Eerste sale': s.firstSaleDate ? s.firstSaleDate.substring(0, 7) : '',
      'Aantal sales': s.count,
      B2C: s.b2c,
      B2B: s.b2b,
      Custom: s.custom,
      'Offtake (MWh)': Math.round(s.estOfftakeMwh),
      'Injection (MWh)': Math.round(s.estInjectionMwh),
      ...(canSeeOmzet ? { 'Potentiele omzet (EUR)': Math.round(s.potentieleOmzet) } : {}),
      'Openstaand incl. BTW (EUR)': Math.round(s.openDebt),
      [`Vervallen ≥ ${VERKOPER_VERVALLEN_DAGEN} dagen incl. BTW (EUR)`]: Math.round(s.vervallenBedrag),
      'Totaal gefactureerd incl. BTW (EUR)': Math.round(s.totaalGefactureerd),
      'Vervallen %': s.vervallenPct.toFixed(1),
    }));
    const today = new Date().toISOString().substring(0, 10);
    exportToCSV(rows, `verkopers-export-${today}`);
  };
  const growthStats = useMemo(() => { const today = new Date(); const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1); const prevPrevMonthDate = new Date(today.getFullYear(), today.getMonth() - 2, 1); const prevMonthStr = prevMonthDate.toISOString().substring(0, 7); const prevPrevMonthStr = prevPrevMonthDate.toISOString().substring(0, 7); const filtered = filteredOrders.filter(o => SALES_PATTERNS.some(p => (o.opportunityStage || '').toLowerCase().includes(p))); const countInMonth = (monthStr) => { return filtered.filter(o => { const date = dateBasis === 'orderDate' ? o.orderDate : o.deliveryStartDate; return date && date.startsWith(monthStr); }).length; }; const prevMonthCount = countInMonth(prevMonthStr); const prevPrevMonthCount = countInMonth(prevPrevMonthStr); if (prevPrevMonthCount === 0 && prevMonthCount === 0) return null; const growth = prevMonthCount - prevPrevMonthCount; const growthPct = prevPrevMonthCount > 0 ? ((growth / prevPrevMonthCount) * 100).toFixed(1) : (prevMonthCount > 0 ? 100 : 0); return { growth, growthPct, currentMonth: prevMonthStr, previousMonth: prevPrevMonthStr, prevMonthCount, prevPrevMonthCount }; }, [filteredOrders, dateBasis]);
  const bestMonth = useMemo(() => { if (!chartDataSegment.length) return null; return chartDataSegment.reduce((best, curr) => curr.total > best.total ? curr : best, chartDataSegment[0]); }, [chartDataSegment]);
  const getActiveConnections = (ordersData, groupBy) => { const excludedStatuses = ['draft', 'external', 'int_out']; const uniqueGSRN = new Map(); ordersData.forEach(o => { const status = (o.connectionPointStatus || '').toLowerCase(); if (!excludedStatuses.includes(status) && o.connectionPointGSRN) { if (!uniqueGSRN.has(o.connectionPointGSRN)) { uniqueGSRN.set(o.connectionPointGSRN, groupBy === 'meter' ? (o.meterMethod || 'Other') : (o.segment || 'Other')); } } }); if (groupBy === 'meter') { const grouped = { YMR: 0, SMR1: 0, SMR3: 0, MMR: 0, AMR: 0, Other: 0 }; uniqueGSRN.forEach(meter => { const m = (meter || '').toUpperCase(); if (m.includes('YMR')) grouped.YMR++; else if (m.includes('SMR1') || m === 'SMR 1') grouped.SMR1++; else if (m.includes('SMR3') || m === 'SMR 3') grouped.SMR3++; else if (m.includes('MMR')) grouped.MMR++; else if (m.includes('AMR')) grouped.AMR++; else grouped.Other++; }); return Object.entries(grouped).filter(([_, v]) => v > 0).map(([name, value]) => ({ name, value })); } else { const grouped = { B2C: 0, B2B: 0, Custom: 0 }; uniqueGSRN.forEach(seg => { const s = (seg || '').toUpperCase(); if (s.includes('B2C')) grouped.B2C++; else if (s.includes('B2B')) grouped.B2B++; else if (s.includes('CUSTOM')) grouped.Custom++; }); return Object.entries(grouped).filter(([_, v]) => v > 0).map(([name, value]) => ({ name, value })); } };
  const ordersEL = useMemo(() => baseFilteredOrders.filter(o => (o.market || '').toUpperCase().startsWith('EL')), [baseFilteredOrders]);
  const ordersNG = useMemo(() => baseFilteredOrders.filter(o => (o.market || '').toUpperCase().startsWith('NG')), [baseFilteredOrders]);
  // Per-GSRN dedupliceren met meest recente record (op orderDateTime/orderDate).
  // Alleen records met CP Status 'owned', 'in' of 'int_in' tellen als actief.
  // Wordt gebruikt door ALLE aggregaties in "Actieve aansluitingen".
  const activeDedupedAll = useMemo(() => {
    const allowed = new Set(['owned', 'in', 'int_in']);
    const byGSRN = new Map();
    const ts = o => o.orderDateTime || o.orderDate || '';
    for (const o of baseFilteredOrders) {
      const gsrn = o.connectionPointGSRN;
      if (!gsrn) continue;
      if (!allowed.has((o.connectionPointStatus || '').toLowerCase())) continue;
      const existing = byGSRN.get(gsrn);
      if (!existing || ts(o) > ts(existing)) byGSRN.set(gsrn, o);
    }
    return [...byGSRN.values()];
  }, [baseFilteredOrders]);
  const activeDedupedEL = useMemo(() => activeDedupedAll.filter(o => (o.market || '').toUpperCase().startsWith('EL')), [activeDedupedAll]);
  const activeDedupedNG = useMemo(() => activeDedupedAll.filter(o => (o.market || '').toUpperCase().startsWith('NG')), [activeDedupedAll]);

  // Top Grootverbruikers, GESPLITST PER ENERGIESOORT. Eerst stonden afname en
  // injectie van elektriciteit en gas door elkaar in één ranglijst; een gas-EAN van
  // 900 MWh en een elektriciteits-EAN van 900 MWh zijn niet dezelfde klant en niet
  // hetzelfde gesprek. Elektriciteit heeft twee ranglijsten (afname én injectie),
  // gas alleen afname — daar bestaat geen injectie.
  const topGrootverbruikers = useMemo(() => {
    // `order` blijft mee zodat de klantkolom via CustomerNameCell dezelfde
    // B2B/B2C-regel volgt als Orders: bij een zakelijke klant de ONDERNEMINGS-
    // naam, met de contactpersoon eronder.
    const naarRij = (o) => ({
      gsrn: o.connectionPointGSRN,
      order: o,
      customer: customerPrimaryName(o),
      offtakeMwh: (Number(o.estOfftakeKwh) || 0) / 1000,
      injectionMwh: (Number(o.estInjectionKwh) || 0) / 1000,
    });
    // Altijd grootste volume bovenaan; EAN's zonder volume horen niet in een top.
    const top = (orders, veld) => orders.map(naarRij).filter(r => r[veld] > 0)
      .sort((a, b) => b[veld] - a[veld]).slice(0, TOP_MWH_SIZE);
    return {
      elOfftake: top(activeDedupedEL, 'offtakeMwh'),
      elInjectie: top(activeDedupedEL, 'injectionMwh'),
      gasOfftake: top(activeDedupedNG, 'offtakeMwh'),
    };
  }, [activeDedupedEL, activeDedupedNG]);

  const activeConnectionsAll = useMemo(() => getActiveConnections(activeDedupedAll, pieView), [activeDedupedAll, pieView]);
  const activeConnectionsEL = useMemo(() => getActiveConnections(activeDedupedEL, pieView), [activeDedupedEL, pieView]);
  const activeConnectionsNG = useMemo(() => getActiveConnections(activeDedupedNG, pieView), [activeDedupedNG, pieView]);
  const getActiveConnectionsVolume = (ordersData, groupBy, volumeField, divisor = 1000) => { const excludedStatuses = ['draft', 'external', 'int_out']; const uniqueGSRN = new Map(); ordersData.forEach(o => { const status = (o.connectionPointStatus || '').toLowerCase(); if (!excludedStatuses.includes(status) && o.connectionPointGSRN) { if (!uniqueGSRN.has(o.connectionPointGSRN)) { uniqueGSRN.set(o.connectionPointGSRN, { group: groupBy === 'meter' ? (o.meterMethod || 'Other') : (o.segment || 'Other'), value: (o[volumeField] || 0) / divisor }); } } }); if (groupBy === 'meter') { const grouped = { YMR: 0, SMR1: 0, SMR3: 0, MMR: 0, AMR: 0, Other: 0 }; uniqueGSRN.forEach(({ group, value }) => { const m = (group || '').toUpperCase(); if (m.includes('YMR')) grouped.YMR += value; else if (m.includes('SMR1') || m === 'SMR 1') grouped.SMR1 += value; else if (m.includes('SMR3') || m === 'SMR 3') grouped.SMR3 += value; else if (m.includes('MMR')) grouped.MMR += value; else if (m.includes('AMR')) grouped.AMR += value; else grouped.Other += value; }); return Object.entries(grouped).filter(([_, v]) => v > 0).map(([name, value]) => ({ name, value: Math.round(value) })); } else { const grouped = { B2C: 0, B2B: 0, Custom: 0 }; uniqueGSRN.forEach(({ group, value }) => { const s = (group || '').toUpperCase(); if (s.includes('B2C')) grouped.B2C += value; else if (s.includes('B2B')) grouped.B2B += value; else if (s.includes('CUSTOM')) grouped.Custom += value; }); return Object.entries(grouped).filter(([_, v]) => v > 0).map(([name, value]) => ({ name, value: Math.round(value) })); } };
  // Brandstofmix: per energy_product_group de som van offtake MWh over alle
  // actieve records (per-record aggregatie — fuel mix gaat over volume, niet
  // over aantal aansluitingen). Recharts toont de % automatisch.
  const getBrandstofmix = (ordersData) => {
    const excluded = ['draft', 'external', 'int_out'];
    const grouped = new Map();
    for (const o of ordersData) {
      if (excluded.includes((o.connectionPointStatus || '').toLowerCase())) continue;
      const raw = (o.energyProductGroup || '').toLowerCase();
      let name, color;
      if (raw === 'green' || raw.includes('groen')) { name = 'Groen'; color = '#22C55E'; }
      else if (raw === 'grey' || raw === 'gray' || raw.includes('grijs')) { name = 'Grijs'; color = '#94A3B8'; }
      else if (!raw) { name = 'Onbekend'; color = '#475569'; }
      else { name = raw.charAt(0).toUpperCase() + raw.slice(1); color = CHART_HUES.blauw; }
      const offtakeMwh = (Number(o.estOfftakeKwh) || 0) / 1000;
      const existing = grouped.get(name) || { name, value: 0, color };
      existing.value += offtakeMwh;
      grouped.set(name, existing);
    }
    return [...grouped.values()].filter(g => g.value > 0).map(g => ({ ...g, value: Math.round(g.value) }));
  };
  const brandstofmixAll = useMemo(() => getBrandstofmix(activeDedupedAll), [activeDedupedAll]);
  const brandstofmixEL = useMemo(() => getBrandstofmix(activeDedupedEL), [activeDedupedEL]);
  const brandstofmixNG = useMemo(() => getBrandstofmix(activeDedupedNG), [activeDedupedNG]);

  const activeConnectionsAllOfftake = useMemo(() => getActiveConnectionsVolume(activeDedupedAll, pieView, 'estOfftakeKwh'), [activeDedupedAll, pieView]);
  const activeConnectionsELOfftake = useMemo(() => getActiveConnectionsVolume(activeDedupedEL, pieView, 'estOfftakeKwh'), [activeDedupedEL, pieView]);
  const activeConnectionsNGOfftake = useMemo(() => getActiveConnectionsVolume(activeDedupedNG, pieView, 'estOfftakeKwh'), [activeDedupedNG, pieView]);
  const activeConnectionsAllInjection = useMemo(() => getActiveConnectionsVolume(activeDedupedAll, pieView, 'estInjectionKwh'), [activeDedupedAll, pieView]);
  const activeConnectionsELInjection = useMemo(() => getActiveConnectionsVolume(activeDedupedEL, pieView, 'estInjectionKwh'), [activeDedupedEL, pieView]);
  const activeConnectionsNGInjection = useMemo(() => getActiveConnectionsVolume(activeDedupedNG, pieView, 'estInjectionKwh'), [activeDedupedNG, pieView]);
  const activeConnectionsAllOmzet = useMemo(() => getActiveConnectionsVolume(activeDedupedAll, pieView, 'totaal', 1), [activeDedupedAll, pieView]);
  const activeConnectionsELOmzet = useMemo(() => getActiveConnectionsVolume(activeDedupedEL, pieView, 'totaal', 1), [activeDedupedEL, pieView]);
  const activeConnectionsNGOmzet = useMemo(() => getActiveConnectionsVolume(activeDedupedNG, pieView, 'totaal', 1), [activeDedupedNG, pieView]);
  const pieDataAll = useMemo(() => pieView === 'brandstofmix' ? brandstofmixAll : pieValueMode === 'offtake' ? activeConnectionsAllOfftake : pieValueMode === 'injection' ? activeConnectionsAllInjection : pieValueMode === 'omzet' ? activeConnectionsAllOmzet : activeConnectionsAll, [pieView, brandstofmixAll, pieValueMode, activeConnectionsAll, activeConnectionsAllOfftake, activeConnectionsAllInjection, activeConnectionsAllOmzet]);
  const pieDataEL = useMemo(() => pieView === 'brandstofmix' ? brandstofmixEL : pieValueMode === 'offtake' ? activeConnectionsELOfftake : pieValueMode === 'injection' ? activeConnectionsELInjection : pieValueMode === 'omzet' ? activeConnectionsELOmzet : activeConnectionsEL, [pieView, brandstofmixEL, pieValueMode, activeConnectionsEL, activeConnectionsELOfftake, activeConnectionsELInjection, activeConnectionsELOmzet]);
  const pieDataNG = useMemo(() => pieView === 'brandstofmix' ? brandstofmixNG : pieValueMode === 'offtake' ? activeConnectionsNGOfftake : pieValueMode === 'injection' ? activeConnectionsNGInjection : pieValueMode === 'omzet' ? activeConnectionsNGOmzet : activeConnectionsNG, [pieView, brandstofmixNG, pieValueMode, activeConnectionsNG, activeConnectionsNGOfftake, activeConnectionsNGInjection, activeConnectionsNGOmzet]);
  const gsrnPerProductGroup = useMemo(() => {
    const uniqueGSRN = new Map();
    baseFilteredOrders.forEach(o => {
      if (o.connectionPointGSRN && !uniqueGSRN.has(o.connectionPointGSRN)) {
        uniqueGSRN.set(o.connectionPointGSRN, o.productGroep || 'Onbekend');
      }
    });
    const grouped = {};
    uniqueGSRN.forEach(productGroep => {
      const pg = productGroep || 'Onbekend';
      if (!grouped[pg]) grouped[pg] = 0;
      grouped[pg]++;
    });
    return Object.entries(grouped).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [baseFilteredOrders]);
  const COLORS_PRODUCT = [CHART_HUES.blauw, CHART_HUES.oranje, CHART_HUES.aqua, CHART_HUES.geel, CHART_HUES.magenta, CHART_HUES.groen, CHART_HUES.violet, CHART_HUES.rood];
  const COLORS_PIE_METER = METER_KEYS.map(k => COLORS_METER[k]);
  const COLORS_PIE_SEGMENT = SEGMENT_KEYS.map(k => COLORS_SEGMENT[k]);
  const COLORS_PIE = pieView === 'meter' ? COLORS_PIE_METER : COLORS_PIE_SEGMENT;

  // Huisstijl van het rapport: het gekozen ENERGIELABEL, en staat er ook één
  // verkoopteam geselecteerd met een eigen huisstijl, dan gaat die eroverheen —
  // het rapport gaat dan immers alleen over dat team. Zelfde regel als het
  // Partnervergoeding-rapport.
  //
  // COMPLIANCE (zie CLAUDE.md): valt terug op neutraal (null) zodra de branding
  // naar het ET-merk wijst of er geen label gekozen is. Een rapport verlaat het
  // scherm en is daarna niet meer rol-gebonden, dus dat geldt ook voor admin.
  // Voor een niet-admin is `activeBranding` per definitie de eigen huisstijl (die
  // ziet maar één label); admin/ET krijgen de losse records binnen en die worden
  // hier samengevoegd.
  const reportBranding = useMemo(() => {
    const isEt = (v) => /energy\s*together/i.test(String(v || ''));
    if (companyFilter === 'all' || isEt(companyFilter)) return null;
    const label = (allBranding || []).find(b => b.energy_label === companyFilter)
      || (activeBranding?.energy_label === companyFilter ? activeBranding : null)
      || (currentUser?.energyLabel === companyFilter ? activeBranding : null);
    const team = verkoopteamFilter !== 'all'
      ? (allTeamBranding || []).find(b => b.energy_label === companyFilter && b.sales_team === verkoopteamFilter)
      : null;
    const merged = mergeBranding(label, team);
    if (!merged || isEt(merged.energy_label) || isEt(merged.display_name)) return null;
    // Het rapport drukt op wit af, ongeacht het thema waarin de gebruiker zit —
    // dus expliciet de LICHTE variant (brandingForTheme valt in beide richtingen
    // terug als er maar één variant is ingesteld).
    return brandingForTheme(merged, 'light');
  }, [allBranding, allTeamBranding, activeBranding, companyFilter, verkoopteamFilter, currentUser]);

  const exportSalesPDFReport = () => {
    const esc = (v) => String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    // Rapport-tekst: merknaam weg (compliance) en dan pas escapen.
    const rt = (v) => esc(hideEtBrand(String(v == null ? '' : v)));
    const getal = (v) => formatNumberRounded(v);
    const eur = (v) => formatEuroRounded(v);

    const now = new Date();
    const datum = new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(now);

    const primair = reportBranding?.primary_color || '#4f46e5';
    const accent = reportBranding?.accent_color || reportBranding?.secondary_color || primair;
    const labelNaam = reportBranding?.display_name || (companyFilter !== 'all' ? companyFilter : 'Alle energieleveranciers');
    const logo = reportBranding?.logo_data_url
      ? `<img class="logo" src="${esc(reportBranding.logo_data_url)}" alt="${rt(labelNaam)}" />`
      : `<div class="logo-fallback">${rt(labelNaam)}</div>`;

    const filters = [
      ['Energieleverancier', companyFilter !== 'all' ? companyFilter : 'Alle'],
      ['Verkoopteam', verkoopteamFilter !== 'all' ? verkoopteamFilter : 'Alle'],
      ['Verkoper', verkoperFilter !== 'all' ? verkoperFilter : 'Alle'],
      ['CP Status', cpStatusFilter !== 'all' ? cpStatusFilter : 'Alle'],
      ['Markt', marketFilter === 'all' ? 'EL & NG' : marketFilter],
      ['Datumbasis', dateBasis === 'orderDate' ? 'Orderdatum' : 'Delivery start'],
      ['Orders in selectie', getal(filteredOrders.length)],
    ];

    const totalOmzet = chartDataSegment.reduce((s, d) => s + d.total_omzet, 0);
    const totalConnections = activeConnectionsAll.reduce((s, d) => s + d.value, 0);
    const totalConnectionsEL = activeConnectionsEL.reduce((s, d) => s + d.value, 0);
    const totalConnectionsNG = activeConnectionsNG.reduce((s, d) => s + d.value, 0);

    // Kerncijfers als smalle regels i.p.v. grote gekleurde tegels: acht getallen
    // die je in één oogopslag naast elkaar leest in plaats van een halve pagina
    // kleurvlak. Zelfde .kpi-vorm als het Partnervergoeding-rapport.
    const kpi = (label, waarde, sub) => `
      <div class="kpi">
        <div class="kpi-label">${rt(label)}</div>
        <div class="kpi-value">${rt(waarde)}</div>
        ${sub ? `<div class="kpi-sub">${rt(sub)}</div>` : ''}
      </div>`;
    const kpis = [
      kpi('B2C', getal(totals.B2C)),
      kpi('B2B', getal(totals.B2B)),
      kpi('Custom', getal(totals.Custom)),
      kpi('Totaal sales', getal(totals.total)),
      kpi('Est. offtake', `${getal(totals.totalOfftakeMwh)} MWh`),
      kpi('Est. injectie', `${getal(totals.totalInjectionMwh)} MWh`),
      canSeeOmzet ? kpi('Potentiële omzet', eur(totalOmzet)) : '',
      growthStats
        ? kpi('Groei month-over-month', `${growthStats.growth >= 0 ? '+' : ''}${growthStats.growthPct}%`,
          `${growthStats.previousMonth} (${growthStats.prevPrevMonthCount}) → ${growthStats.currentMonth} (${growthStats.prevMonthCount})`)
        : '',
      bestMonth ? kpi('Beste maand', bestMonth.month, `${getal(bestMonth.total)} sales`) : '',
    ].filter(Boolean).join('');

    // Actieve aansluitingen: één tabel met Totaal/EL/Gas naast elkaar in plaats van
    // drie losse kaartjes — zo vergelijk je de drie op dezelfde regel.
    const connNamen = [...new Set([
      ...activeConnectionsAll.map(c => c.name),
      ...activeConnectionsEL.map(c => c.name),
      ...activeConnectionsNG.map(c => c.name),
    ])];
    const connWaarde = (lijst, naam) => (lijst.find(c => c.name === naam)?.value ?? 0);
    const connRijen = connNamen.map(naam => `<tr>
      <td>${rt(naam)}</td>
      <td class="r">${getal(connWaarde(activeConnectionsAll, naam))}</td>
      <td class="r">${getal(connWaarde(activeConnectionsEL, naam))}</td>
      <td class="r">${getal(connWaarde(activeConnectionsNG, naam))}</td>
    </tr>`).join('');

    // ── Grafieken ───────────────────────────────────────────────────────────────
    // Getekend uit dezelfde cijfers als de tabellen eronder (src/reportCharts.js).
    // Het scherm kopiëren kan niet: de tabbladen van Sales staan op `hidden` en een
    // grafiek in een verborgen container meet 0 × 0.
    const segReeksen = SEGMENT_KEYS.map(k => ({ key: k, naam: k, kleur: COLORS_SEGMENT[k] }));
    const maandGrafiek = staafGrafiekSvg({
      rijen: chartDataSegment.map(d => ({ label: d.month, waarden: { B2C: d.B2C, B2B: d.B2B, Custom: d.Custom } })),
      reeksen: segReeksen, breedte: 700, hoogte: 250, eenheid: 'aantal sales',
    });
    const maandLegende = legendeHtml(
      segReeksen.map(r => ({ naam: r.naam, kleur: r.kleur, waarde: totals[r.key] || 0 })),
      { formatteer: getal },
    );

    // De donut gaat ALTIJD over het segment, ook als het scherm op metermethode
    // staat: zo betekent dezelfde grafiek in elk rapport hetzelfde. De tabel
    // ernaast blijft de weergave van het scherm volgen.
    const donutData = (rijen) => rijen.map(r => ({
      naam: r.name, waarde: r.value, kleur: COLORS_SEGMENT[r.name] || CHART_HUES.aqua,
    }));
    const donutBlok = (titel, rijen) => {
      const data = donutData(rijen);
      return `<div class="donut">
        <div class="donut-kop">${rt(titel)}</div>
        <div class="donut-rij">
          ${donutSvg({ segmenten: data, grootte: 132, dikte: 24 })}
          <div class="legende">${legendeHtml(data, { formatteer: getal })}</div>
        </div>
      </div>`;
    };
    const segAll = getActiveConnections(activeDedupedAll, 'segment');
    const segEL = getActiveConnections(activeDedupedEL, 'segment');
    const segNG = getActiveConnections(activeDedupedNG, 'segment');

    const verkoperRijen = sellerStats.top8.map((s, i) => `<tr>
      <td class="r">${i + 1}</td>
      <td>${rt(s.name)}</td>
      <td>${rt(s.team || '—')}</td>
      <td class="c">${s.firstSaleDate ? esc(s.firstSaleDate.substring(0, 7)) : '—'}</td>
      <td class="r">${getal(s.b2c)}</td>
      <td class="r">${getal(s.b2b)}</td>
      <td class="r">${getal(s.custom)}</td>
      <td class="r">${getal(s.estOfftakeMwh)}</td>
      <td class="r">${getal(s.estInjectionMwh)}</td>
      ${canSeeOmzet ? `<td class="r">${eur(s.potentieleOmzet)}</td>` : ''}
      <td class="r">${eur(s.vervallenBedrag)}</td>
      <td class="r">${s.vervallenPct.toFixed(0)}%</td>
      <td class="r strong">${getal(s.count)}</td>
    </tr>`).join('');

    const productTotaal = gsrnPerProductGroup.reduce((s, p) => s + p.value, 0);
    const productRijen = gsrnPerProductGroup.slice(0, 10).map(pg => `<tr>
      <td>${rt(pg.name)}</td>
      <td class="r">${getal(pg.value)}</td>
      <td class="r">${productTotaal > 0 ? ((pg.value / productTotaal) * 100).toFixed(1) : '0,0'}%</td>
    </tr>`).join('');

    const maandRijen = chartDataSegment.map(d => `<tr>
      <td>${esc(d.month)}</td>
      <td class="r">${getal(d.B2C)}</td>
      <td class="r">${getal(d.B2B)}</td>
      <td class="r">${getal(d.Custom)}</td>
      <td class="r strong">${getal(d.total)}</td>
      ${canSeeOmzet ? `<td class="r">${eur(d.B2C_omzet)}</td><td class="r">${eur(d.B2B_omzet)}</td><td class="r">${eur(d.Custom_omzet)}</td><td class="r">${eur(d.total_omzet)}</td>` : ''}
      <td class="r">${getal(d.total_offtake)}</td>
      <td class="r">${getal(d.total_injection)}</td>
    </tr>`).join('');

    const htmlContent = `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>Sales Summary — ${rt(labelNaam)}</title>
<style>
  :root { --primair: ${esc(primair)}; --accent: ${esc(accent)}; }
  * { box-sizing: border-box; }
  body {
    font-family: ${reportBranding?.font_family || "'Segoe UI', system-ui, -apple-system, Arial, sans-serif"};
    color: #1e293b; margin: 0; padding: 0; font-size: 11px; line-height: 1.45;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .vel { max-width: 190mm; margin: 0 auto; padding: 10mm 0 14mm; }

  header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px;
           padding-bottom: 12px; border-bottom: 3px solid var(--primair); }
  .logo { max-height: 54px; max-width: 240px; object-fit: contain; }
  .logo-fallback { font-size: 20px; font-weight: 700; color: var(--primair); }
  .kop-rechts { text-align: right; }
  .kop-rechts h1 { margin: 0; font-size: 19px; color: #0f172a; letter-spacing: -0.01em; }
  .kop-rechts .sub { color: #64748b; font-size: 11px; margin-top: 3px; }

  .meta { display: flex; flex-wrap: wrap; gap: 0; margin: 14px 0 20px;
          border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
  .meta div { flex: 1 1 25%; min-width: 110px; padding: 8px 12px; border-right: 1px solid #e2e8f0; }
  .meta div:last-child { border-right: 0; }
  .meta .k { color: #64748b; font-size: 9.5px; text-transform: uppercase; letter-spacing: .04em; }
  .meta .v { color: #0f172a; font-weight: 600; font-size: 11.5px; margin-top: 2px; }

  h2 { font-size: 13px; color: #0f172a; margin: 22px 0 8px; padding-bottom: 5px;
       border-bottom: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: .05em; }

  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
  .kpi { border: 1px solid #e2e8f0; border-left: 3px solid var(--primair); border-radius: 6px; padding: 9px 11px; }
  .kpi-label { color: #64748b; font-size: 9.5px; }
  .kpi-value { color: #0f172a; font-size: 15px; font-weight: 700; margin-top: 1px; }
  .kpi-sub { color: #64748b; font-size: 8.5px; margin-top: 2px; }

  table { width: 100%; border-collapse: collapse; margin: 0; font-size: 10px; }
  th { background: #f1f5f9; text-align: left; padding: 6px 8px; font-weight: 600;
       color: #334155; border-bottom: 1.5px solid #cbd5e1; white-space: nowrap; }
  td { padding: 5px 8px; border-bottom: 1px solid #eef2f7; }
  th.r, td.r { text-align: right; white-space: nowrap; }
  th.c, td.c { text-align: center; white-space: nowrap; }
  td.strong { font-weight: 600; color: #0f172a; }
  tr.totaal td { background: #eef2f7; font-weight: 700; color: #0f172a; border-top: 1.5px solid #cbd5e1; border-bottom: 0; }
  td.leeg { color: #94a3b8; font-style: italic; text-align: center; padding: 12px; }
  .toelichting { color: #64748b; font-size: 9.5px; margin: -2px 0 6px; }
  .smal { font-size: 9px; }
  .smal th, .smal td { padding: 4px 5px; }
  .twee { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start; }
  .blok { margin-bottom: 16px; }

  /* Grafiek + donuts */
  .grafiek { margin-top: 4px; }
  .grafiek svg { display: block; width: 100%; height: auto; }
  .donuts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 4px; }
  .donut { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; }
  .donut-kop { font-size: 10px; font-weight: 700; color: #334155; text-transform: uppercase;
               letter-spacing: .04em; margin-bottom: 4px; }
  .donut-rij { display: flex; align-items: center; gap: 8px; }
  .donut-rij svg { flex: 0 0 auto; }
  .legende { min-width: 0; }
  .lg-item { display: flex; align-items: center; gap: 5px; font-size: 9px; color: #334155;
             padding: 1px 0; white-space: nowrap; }
  .lg-stip { width: 8px; height: 8px; border-radius: 2px; flex: 0 0 auto; display: inline-block; }
  .lg-naam { color: #475569; }
  .lg-waarde { font-weight: 700; color: #0f172a; }
  .lg-pct { color: #94a3b8; }
  .legende-rij { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 6px; }
  .legende-rij .lg-item { font-size: 9.5px; }

  .disclaimer { margin-top: 22px; border: 1px solid #fcd34d; background: #fffbeb;
                border-radius: 8px; padding: 10px 12px; font-size: 10px; color: #78350f; }
  footer { margin-top: 14px; padding-top: 8px; border-top: 1px solid #e2e8f0;
           color: #94a3b8; font-size: 9px; display: flex; justify-content: space-between; }

  @page { size: A4 portrait; margin: 12mm; }
  @media print {
    .vel { max-width: none; padding: 0; }
    thead { display: table-header-group; }
    /* Een blok blijft HEEL. Elke sectie (kop + tabel of grafiek) zit in een
       .blok, en die mag nooit halverwege afbreken: een titel onderaan bladzijde
       één met zijn tabel op twee, of een donut waarvan de legende op de volgende
       pagina staat, maakt het rapport onleesbaar. Past een blok niet meer op wat
       er over is, dan schuift het in zijn geheel door.
       De maandtabel (.blok-lang) is de uitzondering: die kan langer zijn dan een
       pagina, en dan is afbreken de enige optie. Daar houden we het bij hele
       RIJEN plus een herhaalde kolomkop (thead hierboven). */
    .blok, .kpi, .kpis, .donut, .donuts, .grafiek, .disclaimer, tr { break-inside: avoid; page-break-inside: avoid; }
    .blok-lang { break-inside: auto; page-break-inside: auto; }
    h2 { break-after: avoid; page-break-after: avoid; }
    .pagina-breek { break-before: page; page-break-before: always; }
  }
</style>
</head>
<body>
<div class="vel">

  <header>
    <div>${logo}</div>
    <div class="kop-rechts">
      <h1>Sales Summary</h1>
      <div class="sub">${rt(labelNaam)}</div>
      <div class="sub">Rapport van ${esc(datum)}</div>
    </div>
  </header>

  <div class="meta">
    ${filters.map(([k, v]) => `<div><div class="k">${rt(k)}</div><div class="v">${rt(v)}</div></div>`).join('')}
  </div>

  <section class="blok">
    <h2>Kerncijfers</h2>
    <div class="kpis">${kpis}</div>
  </section>

  <!-- De grafiek staat vóór de tabellen: ze beantwoordt de eerste vraag ("loopt
       het op of terug?") in één oogopslag, waar de maandtabel achteraan het
       naslagwerk is. Getekend uit dezelfde cijfers als die tabel. -->
  <section class="blok">
    <h2>Sales per maand</h2>
    <p class="toelichting">Aantal sales per maand, gestapeld per segment · ${rt(marketFilter === 'all' ? 'EL &amp; NG' : marketFilter)} · ${rt(dateBasis === 'orderDate' ? 'orderdatum' : 'delivery start')}.</p>
    <div class="grafiek">${maandGrafiek}</div>
    <div class="legende-rij">${maandLegende}</div>
  </section>

  <section class="blok">
    <h2>Actieve aansluitingen per segment</h2>
    <p class="toelichting">Unieke aansluitingen (EAN) met CP-status owned, in of int_in — aantallen per segment.</p>
    <div class="donuts">
      ${donutBlok('Totaal', segAll)}
      ${donutBlok('Elektriciteit', segEL)}
      ${donutBlok('Gas', segNG)}
    </div>
  </section>

  <div class="twee" style="margin-top:22px">
    <section class="blok">
      <h2 style="margin-top:0">Actieve aansluitingen</h2>
      <p class="toelichting">Exclusief CP-status draft, external en int_out.</p>
      <table class="smal">
        <thead><tr><th>${pieView === 'meter' ? 'Metermethode' : 'Segment'}</th><th class="r">Totaal</th><th class="r">EL</th><th class="r">Gas</th></tr></thead>
        <tbody>
          ${connRijen || '<tr><td colspan="4" class="leeg">Geen actieve aansluitingen in deze selectie.</td></tr>'}
          <tr class="totaal"><td>Totaal</td><td class="r">${getal(totalConnections)}</td><td class="r">${getal(totalConnectionsEL)}</td><td class="r">${getal(totalConnectionsNG)}</td></tr>
        </tbody>
      </table>
    </section>

    <section class="blok">
      <h2 style="margin-top:0">Sales per productgroep</h2>
      <p class="toelichting">Aantal unieke aansluitingen (EAN), tien grootste.</p>
      <table class="smal">
        <thead><tr><th>Productgroep</th><th class="r">EAN</th><th class="r">Aandeel</th></tr></thead>
        <tbody>${productRijen || '<tr><td colspan="3" class="leeg">Geen gegevens voor deze selectie.</td></tr>'}</tbody>
      </table>
    </section>
  </div>

  <section class="blok">
    <h2>Top 8 verkopers</h2>
    <table class="smal">
      <thead><tr>
        <th class="r">#</th><th>Verkoper</th><th>Team</th><th class="c">Start</th>
        <th class="r">B2C</th><th class="r">B2B</th><th class="r">Custom</th>
        <th class="r">Offtake</th><th class="r">Injectie</th>
        ${canSeeOmzet ? '<th class="r">Omzet</th>' : ''}
        <th class="r">Vervallen</th><th class="r">Verv. %</th><th class="r">Sales</th>
      </tr></thead>
      <tbody>${verkoperRijen || `<tr><td colspan="${canSeeOmzet ? 13 : 12}" class="leeg">Geen verkopers in deze selectie.</td></tr>`}</tbody>
    </table>
    <p style="font-size:9px;color:#64748b;margin-top:6px;">Vervallen = nog openstaand bedrag (incl. btw) op verkoopfacturen die minstens ${VERKOPER_VERVALLEN_DAGEN} dagen over hun vervaldatum staan. Verv. % = dat bedrag t.o.v. alles wat die verkoper gefactureerd heeft (incl. btw, betaald én onbetaald).</p>
  </section>

  <!-- De maandtabel staat ACHTERAAN: het is de breedste en langste tabel van het
       rapport, en wie het opent wil eerst de kerncijfers zien. Zij is het ENIGE
       blok dat over een paginagrens mag lopen (.blok-lang) — bij veel maanden
       past ze niet op één bladzijde, en dan is afbreken op een hele rij met een
       herhaalde kolomkop beter dan haar helemaal doorschuiven. -->
  <section class="blok-lang">
  <h2 class="pagina-breek">Sales per maand — segment</h2>
  <table class="smal">
    <thead><tr>
      <th>Maand</th><th class="r">B2C</th><th class="r">B2B</th><th class="r">Custom</th><th class="r">Totaal</th>
      ${canSeeOmzet ? '<th class="r">B2C omzet</th><th class="r">B2B omzet</th><th class="r">Custom omzet</th><th class="r">Totaal omzet</th>' : ''}
      <th class="r">Offtake (MWh)</th><th class="r">Injectie (MWh)</th>
    </tr></thead>
    <tbody>
      ${maandRijen || `<tr><td colspan="${canSeeOmzet ? 11 : 7}" class="leeg">Geen gegevens voor deze selectie.</td></tr>`}
      <tr class="totaal">
        <td>Totaal</td>
        <td class="r">${getal(totals.B2C)}</td>
        <td class="r">${getal(totals.B2B)}</td>
        <td class="r">${getal(totals.Custom)}</td>
        <td class="r">${getal(totals.total)}</td>
        ${canSeeOmzet ? `<td class="r" colspan="4">${eur(totalOmzet)}</td>` : ''}
        <td class="r">${getal(totals.totalOfftakeMwh)}</td>
        <td class="r">${getal(totals.totalInjectionMwh)}</td>
      </tr>
    </tbody>
  </table>
  </section>

  <div class="disclaimer">
    <strong>Disclaimer:</strong> deze cijfers zijn voorlopig en kunnen nog herberekend worden. De waarden zijn gebaseerd op de data die beschikbaar was op het moment van export en zijn niet definitief. Gebruik ze uitsluitend ter indicatie.
  </div>

  <footer>
    <span>${rt(labelNaam)}</span>
    <span>Sales Summary · ${esc(datum)}</span>
  </footer>

</div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Uitleg bij de kolommen "Vervallen" en "%". Beide gaan over dezelfde set
  // facturen, dus staat de definitie hier één keer en verwijst de tweede tekst
  // ernaar — anders lopen ze bij een volgende wijziging uiteen.
  const vervallenUitleg = `Vervallen: het nog openstaande bedrag (incl. btw) op de facturen van deze verkoper die minstens ${VERKOPER_VERVALLEN_DAGEN} dagen over hun vervaldatum staan.\n\nEen factuur telt mee zodra ze onbetaald is én (vandaag − vervaldatum) ≥ ${VERKOPER_VERVALLEN_DAGEN} dagen. Bij een deelbetaling telt enkel het restbedrag. Enkel verkoopfacturen; creditnota's en self bills blijven erbuiten.\n\nNiet te verwarren met de wanbetaler-badge in Facturen, Aging en Customer 360: die gebruikt de veel latere drempel van het energielabel (standaard ${BAD_DEBT_OVERDUE_DAYS_DEFAULT} dagen). Deze kolom signaleert vroeg wat er blijft liggen.`;
  const vervallenPctUitleg = `Vervallen-%: het bedrag uit de kolom "Vervallen" (openstaand op facturen van minstens ${VERKOPER_VERVALLEN_DAGEN} dagen over de vervaldatum), gedeeld door alles wat deze verkoper gefactureerd heeft — incl. btw, betaald én onbetaald.\n\nBeide kanten van de breuk zijn verkoopfacturen; creditnota's en self bills tellen nergens mee.\n\nOpenstaande facturen die nog niet of nog geen ${VERKOPER_VERVALLEN_DAGEN} dagen vervallen zijn, zitten in geen van beide kolommen.`;

  // Gedeelde tabelopbouw voor de verkoperslijsten (Top 8 / Bottom 8 / alle).
  // De Omzet-kolom wordt enkel getoond aan rollen die ze mogen zien (canSeeOmzet).
  const sellerTableHead = (
    <thead><tr className="border-b border-slate-700 text-xs font-medium text-slate-400 whitespace-nowrap">
      <th className="py-2 pr-2 text-left w-10">#</th>
      <th className="py-2 px-2 text-left">Verkoper</th>
      <th className="py-2 px-2 text-center">Start</th>
      <th className="py-2 px-2 text-right">B2C</th>
      <th className="py-2 px-2 text-right">B2B</th>
      <th className="py-2 px-2 text-right">CU</th>
      <th className="py-2 px-2 text-right">Offtake</th>
      <th className="py-2 px-2 text-right">Injection</th>
      {canSeeOmzet && <th className="py-2 px-2 text-right">Omzet</th>}
      <th className="py-2 px-2 text-right" title={vervallenUitleg}><span className="cursor-help border-b border-dotted border-slate-500">Vervallen</span></th>
      <th className="py-2 px-2 text-right" title={vervallenPctUitleg}><span className="cursor-help border-b border-dotted border-slate-500">%</span></th>
      <th className="py-2 pl-2 text-right">Sales</th>
    </tr></thead>
  );
  const renderSellerRow = (seller, rankNum, badgeClass, salesClass) => (
    <tr key={seller.name} className="border-b border-slate-700/30 hover:bg-slate-700/20 tabular-nums">
      <td className="py-3 pr-2"><div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${badgeClass}`}>{rankNum}</div></td>
      <td className="py-3 px-2"><div className="text-sm text-slate-100 font-semibold">{seller.name}</div><div className="text-xs text-slate-500">{seller.team || '—'}</div></td>
      <td className="py-3 px-2 text-center text-xs text-slate-400 whitespace-nowrap">{seller.firstSaleDate ? seller.firstSaleDate.substring(0, 7) : '—'}</td>
      <td className="py-3 px-2 text-right text-sm font-semibold text-blue-300">{seller.b2c}</td>
      <td className="py-3 px-2 text-right text-sm font-semibold text-lime-300">{seller.b2b}</td>
      <td className="py-3 px-2 text-right text-sm font-semibold text-purple-300">{seller.custom}</td>
      <td className="py-3 px-2 text-right text-sm text-blue-300 whitespace-nowrap">{formatNumberRounded(seller.estOfftakeMwh)}</td>
      <td className="py-3 px-2 text-right text-sm text-green-300 whitespace-nowrap">{formatNumberRounded(seller.estInjectionMwh)}</td>
      {canSeeOmzet && <td className="py-3 px-2 text-right text-sm font-semibold text-lime-300 whitespace-nowrap">{formatEuroRounded(seller.potentieleOmzet)}</td>}
      <td className="py-3 px-2 text-right text-sm font-semibold text-red-300 whitespace-nowrap" title={vervallenUitleg}>{formatEuroRounded(seller.vervallenBedrag)}</td>
      <td className="py-3 px-2 text-right text-sm font-semibold text-orange-400"
        title={`${formatEuroRounded(seller.vervallenBedrag)} vervallen (≥ ${VERKOPER_VERVALLEN_DAGEN} dagen) op ${formatEuroRounded(seller.totaalGefactureerd)} gefactureerd`}>{seller.vervallenPct.toFixed(0)}%</td>
      <td className={`py-3 pl-2 text-right text-base font-bold ${salesClass}`}>{seller.count}</td>
    </tr>
  );
  if (!orders.length) return <EmptyState message="Nog geen orders beschikbaar — data komt automatisch uit de API." onSettings={onSettings} />;
  return <div className="space-y-6 grafiek-modern">
    <PageHeader title="Sales" subtitle="Verkoop per maand en verkoper inzichten"><select value={companyFilter} onChange={e => handleCompanyChange(e.target.value)} className={`px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm bg-slate-700 border rounded-lg ${companyFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={companies}>Alle Energieleveranciers</AllOption>{companies.map(c => <option key={c} value={c}>{c}</option>)}</select><select value={verkoopteamFilter} onChange={e => handleVerkoopteamChange(e.target.value)} className={`px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm bg-slate-700 border rounded-lg ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={verkoopteams}>Alle Teams</AllOption>{verkoopteams.map(t => <option key={t} value={t}>{t}</option>)}</select><select value={verkoperFilter} onChange={e => handleVerkoperChange(e.target.value)} className={`px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm bg-slate-700 border rounded-lg ${verkoperFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={verkopers}>Alle Verkopers</AllOption>{verkopers.map(v => <option key={v} value={v}>{sellerLabel(v)}</option>)}</select><select value={cpStatusFilter} onChange={e => setCpStatusFilter(e.target.value)} className={`px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm bg-slate-700 border rounded-lg ${cpStatusFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle CP Status</option><option value="active">Active</option><option value="external">External</option>{cpStatuses.filter(s => !CP_ACTIVE.has(s.toLowerCase()) && !CP_EXTERNAL.has(s.toLowerCase())).map(s => <option key={s} value={s}>{s}</option>)}</select><ExportButton onExport={exportSalesPDFReport} soort="pdf" label="Download het salesrapport als PDF" /><ExportButton onExport={() => exportToCSV(detailData, 'sales')} label="Exporteer de salescijfers naar Excel" /></PageHeader>
    {/* Horizontaal tab-menu binnen Sales (zoals Market Prices / Revenue). */}
    <div className="-mx-1 overflow-x-auto no-scrollbar">
      <div className="flex gap-2 px-1 w-max">
        {salesTabs.map(st => (
          <button key={st.id} onClick={() => setSalesTab(st.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${salesTab === st.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-600'}`}>
            {st.label}
          </button>
        ))}
      </div>
    </div>

    {/* ── Tab: Samenvatting ── */}
    <div className={salesTab === 'samenvatting' ? 'space-y-6' : 'hidden'}>
    {/* Deze filters (markt EL&NG + order-/deliverydatum) horen bij de Samenvatting
        (StatTiles + Sales per Maand-grafiek) en staan daarom in deze tab. */}
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
      <Toggle options={[{ value: 'all', label: 'EL & NG' }, { value: 'EL', label: 'EL' }, { value: 'NG', label: 'NG' }]} value={marketFilter} onChange={setMarketFilter} />
      <Toggle options={[{ value: 'orderDate', label: 'Orderdatum' }, { value: 'deliveryStartDate', label: 'Delivery Start' }]} value={dateBasis} onChange={setDateBasis} />
      <Toggle options={SALES_KANALEN} value={kanaalFilter} onChange={setKanaalFilter} />
    </div>
    <div className="flex flex-wrap gap-4">
      <StatTile label="B2C" dot={COLORS_SEGMENT.B2C} value={totals.B2C} sub={<><div>Offtake: {formatNumberRounded(totals.B2C_offtake)} MWh</div><div>Injection: {formatNumberRounded(totals.B2C_injection)} MWh</div></>} />
      <StatTile label="B2B" dot={COLORS_SEGMENT.B2B} value={totals.B2B} sub={<><div>Offtake: {formatNumberRounded(totals.B2B_offtake)} MWh</div><div>Injection: {formatNumberRounded(totals.B2B_injection)} MWh</div></>} />
      <StatTile label="Custom" dot={COLORS_SEGMENT.Custom} value={totals.Custom} sub={<><div>Offtake: {formatNumberRounded(totals.Custom_offtake)} MWh</div><div>Injection: {formatNumberRounded(totals.Custom_injection)} MWh</div></>} />
      <StatTile label="Totaal Sales" dot={COLORS_SEGMENT.total} accent={COLORS_SEGMENT.total} value={totals.total} sub={<><div>Offtake: {formatNumberRounded(totals.totalOfftakeMwh)} MWh</div><div>Injection: {formatNumberRounded(totals.totalInjectionMwh)} MWh</div></>} />
      {growthStats && <StatTile label="Groei MoM" minW={140} accent={growthStats.growth >= 0 ? '#10B981' : '#F43F5E'} value={`${growthStats.growth >= 0 ? '+' : ''}${growthStats.growthPct}%`} sub={<div>{growthStats.previousMonth} → {growthStats.currentMonth}</div>} />}
      {bestMonth && <StatTile label="Beste Maand" minW={120} value={bestMonth.month} sub={<div>{bestMonth.total} sales</div>} />}
    </div>
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-slate-100">Sales per Maand</h3>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
          {/* Vinkje vóór de toggles: het bepaalt WAT er in de grafiek staat, de
              toggles enkel hoe het getoond wordt. */}
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none whitespace-nowrap"
            title="Toont wissels (product + klant) en contractverlengingen als aparte reeksen. Die orders worden dan uit de segment-/meterbalken gehaald, zodat geen enkel order twee keer meetelt.">
            <input type="checkbox" checked={inclWissels} onChange={e => setInclWissels(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-700 accent-lime-500 cursor-pointer" />
            incl. wissels &amp; verlengingen
          </label>
          <Toggle options={[{ value: 'count', label: 'Aantal' }, ...(canSeeOmzet ? [{ value: 'omzet', label: 'Omzet' }] : []), { value: 'offtake', label: 'Est. Offtake (MWh)' }, { value: 'injection', label: 'Est. Injection (MWh)' }]} value={yAxisMode} onChange={setYAxisMode} />
          <Toggle options={[{ value: 'segment', label: 'Segment' }, { value: 'meter', label: 'Meter Method' }]} value={chartView} onChange={setChartView} />
          <Toggle options={[{ value: 'bar', label: 'Bar Chart' }, { value: 'line', label: 'Lijn Grafiek' }]} value={chartType} onChange={setChartType} />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={350}>{(() => {
        const suffix = yAxisMode === 'omzet' ? '_omzet' : yAxisMode === 'offtake' ? '_offtake' : yAxisMode === 'injection' ? '_injection' : '';
        const isMwh = yAxisMode === 'offtake' || yAxisMode === 'injection';
        const tickFmt = isMwh ? (v) => `${Math.round(v)} MWh` : yAxisMode === 'omzet' ? (v) => `€${(v / 1000).toFixed(0)}k` : undefined;
        const tooltipFmt = isMwh ? (value) => `${formatNumberRounded(value)} MWh` : yAxisMode === 'omzet' ? (value) => `€${Math.round(value).toLocaleString('nl-BE')}` : (value) => value;
        const reeksen = chartView === 'segment'
          ? SEGMENT_KEYS.map(k => ({ key: k, naam: k, kleur: COLORS_SEGMENT[k] }))
          : METER_KEYS.map(k => ({ key: k, naam: k, kleur: COLORS_METER[k] }));
        // Productwissels en klantenwissels samen in één reeks (één kleur, zoals
        // gevraagd); verlengingen apart. Beide sluiten achteraan de stapel aan.
        const bewegingReeksen = inclWissels ? [
          { key: 'wissels', naam: 'Wissels (product + klant)', kleur: COLORS_BEWEGING.wissels },
          { key: 'verlengingen', naam: 'Contractverlengingen', kleur: COLORS_BEWEGING.verlenging },
        ] : [];
        const alle = [...reeksen, ...bewegingReeksen];
        const assen = <>
          <CartesianGrid vertical={false} stroke="#334155" />
          <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 11 }} angle={-45} textAnchor="end" height={60} {...CHART_AS_X} />
          <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} tickFormatter={tickFmt} {...CHART_AS_Y} />
          <Tooltip contentStyle={CHART_TOOLTIP} formatter={tooltipFmt} />
          <Legend {...CHART_LEGENDA} />
        </>;
        const stapel = alle.map(r => `${r.key}${suffix}`);
        if (chartType === 'bar') return (
          <BarChart data={chartData} barCategoryGap="20%">
            {assen}
            {alle.map((r, i) => (
              <Bar key={r.key} dataKey={stapel[i]} name={r.naam} stackId="a" fill={r.kleur}
                maxBarSize={34} shape={stapelVorm(stapel, i)} />
            ))}
          </BarChart>
        );
        return (
          <LineChart data={chartData}>
            {assen}
            {alle.map(r => (
              <Line key={r.key} type="monotone" dataKey={`${r.key}${suffix}`} name={r.naam} stroke={r.kleur} strokeWidth={2} dot={CHART_LIJN_PUNT} activeDot={CHART_LIJN_PUNT_ACTIEF} />
            ))}
            {/* De totaallijn is geen categorie maar een referentie, en draagt dus
                neutrale inkt i.p.v. een reekskleur. Moest ook wel: in de
                meterweergave zijn alle acht tinten al vergeven, dus elke
                reekskleur zou hier een balk dubbelen. --chart-neutral volgt het
                thema (donker op licht, licht op donker). */}
            <Line type="monotone" dataKey={`grafiekTotal${suffix}`} name="Totaal" stroke="var(--chart-neutral)" strokeWidth={2} strokeDasharray="6 4" dot={CHART_LIJN_PUNT} activeDot={CHART_LIJN_PUNT_ACTIEF} />
          </LineChart>
        );
      })()}</ResponsiveContainer>
      {inclWissels && heeftBewegingen && <p className="text-[11px] text-slate-500 mt-2">
        Wissels en verlengingen staan als eigen reeks in de grafiek en zijn daarom uit de {chartView === 'segment' ? 'segment' : 'meter'}balken gehaald — geen enkel order telt twee keer mee. De kengetallen hierboven blijven op de volledige salesset rekenen.
      </p>}
    </Card>

    {/* Zelfde grafiek als in Portfolio Insights, maar op de scope en de datumbasis
        van Sales — hier uitgesplitst naar de drie bewegingssoorten, terwijl Sales
        per Maand product- en klantenwissels samenneemt. */}
    <Card>
      <h3 className="text-lg font-semibold text-slate-100">Wissels &amp; verlengingen</h3>
      <p className="text-xs text-slate-400 mt-0.5 mb-3">Productwissels, klantenwissels en contractverlengingen — gedetecteerd op actieve EAN's met meerdere verkooporders</p>
      {!heeftBewegingen ? <p className="text-sm text-slate-500 italic">Geen wissels of verlengingen gedetecteerd in de huidige scope.</p> :
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={bewegingenPerMaand} barCategoryGap="20%">
          <CartesianGrid vertical={false} stroke="#334155" />
          <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 11 }} angle={-45} textAnchor="end" height={54} {...CHART_AS_X} />
          <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={false} {...CHART_AS_Y} />
          <Tooltip contentStyle={CHART_TOOLTIP} />
          <Legend {...CHART_LEGENDA} />
          {/* Klantenwissels vóór productwissels: magenta naast groen haalt de
              kleurenblindheid-drempel, geel naast groen niet. Zie het palet. */}
          {BEWEGING_STAPEL.map((r, i) => (
            <Bar key={r.key} dataKey={r.key} name={r.naam} fill={r.kleur} stackId="a"
              maxBarSize={34} shape={stapelVorm(BEWEGING_STAPEL.map(b => b.key), i)} />
          ))}
        </BarChart>
      </ResponsiveContainer>}
    </Card>
    </div>

    {/* ── Tab: Actieve aansluitingen ── */}
    <div className={salesTab === 'aansluitingen' ? '' : 'hidden'}>
    <Card><div className="flex flex-wrap items-center justify-between gap-4 mb-4"><h3 className="text-lg font-semibold text-slate-100">Actieve aansluitingen</h3><div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0"><Toggle options={[{ value: 'count', label: 'Aantal' }, ...(canSeeOmzet ? [{ value: 'omzet', label: 'Omzet' }] : []), { value: 'offtake', label: 'Est. Offtake (MWh)' }, { value: 'injection', label: 'Est. Injection (MWh)' }]} value={pieValueMode} onChange={setPieValueMode} /><Toggle options={[{ value: 'segment', label: 'Segment' }, { value: 'meter', label: 'Meter Method' }, { value: 'brandstofmix', label: 'Brandstofmix' }]} value={pieView} onChange={setPieView} /></div></div><div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {(() => { const isBrand = pieView === 'brandstofmix'; const fmtVal = (v) => isBrand ? `${formatNumberRounded(v)} MWh` : pieValueMode === 'count' ? v : pieValueMode === 'omzet' ? `€${formatNumberRounded(v)}` : `${formatNumberRounded(v)} MWh`; const fmtTotal = (arr, color) => { const total = arr.reduce((sum, d) => sum + d.value, 0); return <span className={`text-xs font-bold ${color}`}>{isBrand ? `${formatNumberRounded(total)} MWh Offtake` : pieValueMode === 'count' ? `${total} totaal` : pieValueMode === 'omzet' ? `€${formatNumberRounded(total)} totaal` : `${formatNumberRounded(total)} MWh totaal`}</span>; }; const tooltipFmt = (value) => isBrand ? `${formatNumberRounded(value)} MWh` : pieValueMode === 'count' ? value : pieValueMode === 'omzet' ? `€${formatNumberRounded(value)}` : `${formatNumberRounded(value)} MWh`; const renderPie = (data, title, titleClass, totalColorClass) => <div><h4 className={`text-sm font-semibold mb-2 text-center ${titleClass}`}>{title}</h4><div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-5 min-w-0"><div className="w-full sm:w-[60%] min-w-0"><ResponsiveContainer width="100%" height={220}><PieChart className="donut-vrij"><Pie data={data} cx="50%" cy="50%" innerRadius={48} outerRadius={74} paddingAngle={2} cornerRadius={3} dataKey="value" labelLine={donutLijn} label={({ cx, cy, midAngle, outerRadius: or, percent }) => { if (percent < 0.01) return null; const r = or + 13; const x = cx + r * Math.cos(-midAngle * Math.PI / 180); const y = cy + r * Math.sin(-midAngle * Math.PI / 180); return <text x={x} y={y} fill="#94A3B8" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={10}>{(percent * 100).toFixed(1)}%</text>; }}>{data.map((d, idx) => <Cell key={idx} fill={d.color || COLORS_PIE[idx % COLORS_PIE.length]} stroke="var(--chart-surface, #ffffff)" strokeWidth={1.5} />)}</Pie><Tooltip contentStyle={CHART_TOOLTIP} formatter={tooltipFmt} /></PieChart></ResponsiveContainer></div><div className="space-y-1.5 w-full sm:w-auto min-w-0">{data.map((item, idx) => <div key={item.name} className="flex items-center gap-2 min-w-0"><div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color || COLORS_PIE[idx % COLORS_PIE.length] }} /><span className="text-xs text-slate-300 truncate">{item.name}</span><span className="text-xs text-slate-100 font-semibold shrink-0 tabular-nums">{fmtVal(item.value)}</span></div>)}<div className="pt-2 mt-2 border-t border-slate-700">{fmtTotal(data, totalColorClass)}</div></div></div></div>; return <>{renderPie(pieDataAll, 'Totaal (Owned & Gaining)', 'text-slate-300', 'text-lime-400')}{renderPie(pieDataEL, <span className="inline-flex items-center gap-1.5"><Zap size={14} className="shrink-0" />EL</span>, 'text-yellow-400', 'text-yellow-400')}{renderPie(pieDataNG, <span className="inline-flex items-center gap-1.5"><Flame size={14} className="shrink-0" />NG</span>, 'text-blue-400', 'text-blue-400')}</>; })()}
    </div></Card>
    </div>

    {/* ── Tab: Top MWh ── */}
    {/* Gesplitst per energiesoort: elektriciteit heeft afname én injectie, gas
        alleen afname. Alle drie de ranglijsten tonen de tien grootste EAN's, altijd
        met het grootste volume bovenaan. Eén record per EAN (de meest recente
        order), zodat een verlenging niet als tweede aansluiting meetelt. */}
    <div className={salesTab === 'topmwh' ? '' : 'hidden'}>
    <Card>
      <h3 className="text-lg font-semibold text-slate-100 mb-1">Top Grootverbruikers</h3>
      <p className="text-xs text-slate-500 mb-4">De {TOP_MWH_SIZE} grootste aansluitingen per energiesoort, op geschat jaarvolume. Alleen actieve aansluitingen.</p>

      <div className="flex items-center gap-2 mb-2">
        <Zap size={15} className="text-blue-300 shrink-0" />
        <h4 className="text-sm font-semibold text-slate-200">Elektriciteit</h4>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h5 className="text-sm font-semibold text-blue-300 mb-2">Top {TOP_MWH_SIZE} Offtake (MWh)</h5>
          <TopMwhTabel rijen={topGrootverbruikers.elOfftake} veld="offtakeMwh" accent="blue"
            onContext={onSalesC360Context} onOpenCustomer360={onOpenCustomer360} />
        </div>
        <div>
          <h5 className="text-sm font-semibold text-green-300 mb-2">Top {TOP_MWH_SIZE} Injectie (MWh)</h5>
          <TopMwhTabel rijen={topGrootverbruikers.elInjectie} veld="injectionMwh" accent="green"
            onContext={onSalesC360Context} onOpenCustomer360={onOpenCustomer360} />
        </div>
      </div>

      {/* Gas kent geen injectie, dus hier één ranglijst. Ze staat onder elektriciteit
          en niet ernaast: een half leeg rooster leest als een ontbrekende tabel. */}
      <div className="flex items-center gap-2 mt-8 mb-2">
        <Flame size={15} className="text-orange-300 shrink-0" />
        <h4 className="text-sm font-semibold text-slate-200">Gas</h4>
      </div>
      <h5 className="text-sm font-semibold text-orange-300 mb-2">Top {TOP_MWH_SIZE} Offtake (MWh)</h5>
      <TopMwhTabel rijen={topGrootverbruikers.gasOfftake} veld="offtakeMwh" accent="orange"
        onContext={onSalesC360Context} onOpenCustomer360={onOpenCustomer360} />

      {salesC360Menu}
    </Card>
    </div>
    {/* ── Tab: Verkopers ── */}
    <div className={salesTab === 'verkopers' ? '' : 'hidden'}>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {showAllSellers ? (
        <Card className="xl:col-span-2"><div className="flex items-center justify-between mb-4"><div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">Alle Verkopers ({sellerStats.total})</h3><ExportButton onExport={exportSellerStats} label="Exporteer de verkopercijfers naar Excel" /></div><div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0"><button onClick={() => setShowAllSellers(false)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm rounded-lg transition-all"><ChevronUp size={14} />Toon Top &amp; Bottom 8</button></div></div>
        <div className="overflow-x-auto">
          <table className="w-full resp-card">
            {sellerTableHead}
            <tbody>{sellerStats.all.map((seller, idx) => renderSellerRow(seller, idx + 1, idx < 3 ? 'bg-lime-500 text-slate-900' : 'bg-slate-700 text-slate-300', 'text-lime-400'))}</tbody>
          </table>
        </div>
      </Card>
      ) : (<>
      <Card><div className="flex items-center justify-between mb-4"><div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">Top 8 Verkopers</h3><ExportButton onExport={exportSellerStats} label="Exporteer de verkopercijfers naar Excel" /></div><div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0"><button onClick={() => setShowAllSellers(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg transition-all"><ChevronDown size={14} />Toon alle ({sellerStats.total})</button></div></div>
        {/* 12 kolommen breed → op mobiel kaarten (resp-card), op tablet scrollt
            de tabel binnen deze wrapper i.p.v. de pagina te verbreden. */}
        <div className="overflow-x-auto">
        <table className="w-full resp-card">
          {sellerTableHead}
          <tbody>{sellerStats.top8.map((seller, idx) => renderSellerRow(seller, idx + 1, idx < 3 ? 'bg-lime-500 text-slate-900' : 'bg-slate-700 text-slate-300', 'text-lime-400'))}</tbody>
        </table>
        </div>
      </Card>
      <Card><div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-slate-100">Bottom 8 Verkopers</h3><div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0"><span className="text-xs text-slate-500">{canSeeOmzet ? 'Verkopers met laagste omzet' : 'Laagst scorende verkopers'}</span><button onClick={() => setShowAllSellers(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg transition-all"><ChevronDown size={14} />Toon alle ({sellerStats.total})</button></div></div>
        <div className="overflow-x-auto">
        <table className="w-full resp-card">
          {sellerTableHead}
          <tbody>{sellerStats.bottom8.map((seller, idx) => renderSellerRow(seller, sellerStats.total - sellerStats.bottom8.length + idx + 1, 'bg-red-900/50 text-red-300', 'text-red-400'))}</tbody>
        </table>
        </div>
      </Card>
      </>)}
    </div>
    </div>

    {/* ── Tab: Sales per Productgroep ── */}
    <div className={salesTab === 'productgroep' ? '' : 'hidden'}>
    <Card>
      <h3 className="text-lg font-semibold text-slate-100 mb-4">Sales per Productgroep</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead className="bg-slate-800"><tr className="border-b border-slate-700 text-xs font-medium">
            <th className="py-3 px-4 text-left text-slate-400">Productgroep</th>
            <th className="py-3 px-4 text-right text-slate-400">Aantal EAN</th>
            <th className="py-3 px-4 text-right text-slate-400">%</th>
          </tr></thead>
          <tbody>
            {gsrnPerProductGroup.map((item, idx) => {
              const totalGSRN = gsrnPerProductGroup.reduce((sum, i) => sum + i.value, 0);
              const pct = totalGSRN > 0 ? ((item.value / totalGSRN) * 100).toFixed(1) : 0;
              return <tr key={item.name} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                <td className="py-2 px-4 text-slate-200"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS_PRODUCT[idx % COLORS_PRODUCT.length] }} />{item.name}</div></td>
                <td className="py-2 px-4 text-right text-slate-100 font-semibold tabular-nums">{item.value}</td>
                <td className="py-2 px-4 text-right text-slate-400 tabular-nums">{pct}%</td>
              </tr>;
            })}
            <tr className="bg-slate-800/50 font-semibold">
              <td className="py-2 px-4 text-lime-400">Totaal</td>
              <td className="py-2 px-4 text-right text-lime-400">{gsrnPerProductGroup.reduce((sum, i) => sum + i.value, 0)}</td>
              <td className="py-2 px-4 text-right text-lime-400">100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
    </div>

    {/* ── Tab: Prestaties (Gamification) — alleen gemount wanneer open ── */}
    {salesTab === 'prestaties' && magSalesTab('prestaties') && <PrestatiesTab token={salesToken} currentUser={currentUser} />}

  </div>;
};

// Tabs van de Invoices-module. Ids identiek aan de subpagina-catalogus (subPages.js).
// Self bills: de facturen die de KLANT aan ons stuurt (injectie) en hun
// creditnota's (negatief bedrag). Eigen feed (/api/selfbills), eigen tabel onder
// de facturen — in Invoices én in Customer 360 → Facturen. Sorteerbaar, 15 rijen
// per pagina, export van de volledige gesorteerde lijst.
const SELFBILL_EXPORT_KOLOMMEN = [
  { header: 'Energielabel', get: r => r.relatieBedrijf || '' },
  { header: 'Verkoper', get: r => r.verkoper || '' },
  { header: 'Verkoopteam', get: r => r.verkoopteam || '' },
  { header: 'Nummer', get: r => r.nummer || '' },
  { header: 'Klantnummer', get: r => r.relatieReferentie || '' },
  { header: 'Klant', get: r => r.relatieNaam || '' },
  { header: 'Factuurdatum', get: r => r.factuurdatum || '' },
  { header: 'Soort', get: r => r.soort || '' },
  { header: 'Vervaldatum', get: r => r.vervaldatum || '' },
  { header: 'Status betaling', get: r => r.statusBetaling || '' },
  { header: 'Totaal excl. BTW', get: r => r.totaalExclBTW || 0 },
  { header: 'Totaal', get: r => r.totaal || 0 },
];
const SelfbillsTabel = ({ rijen, titel = 'Self bills', toelichting, bestandsnaam = 'self-bills', onOpenCustomer360, onRowContext, compact = false }) => {
  const sort = useTableSort('factuurdatum', 'desc');
  const [page, setPage] = useState(1);
  const gesorteerd = useMemo(() => sortRows(rijen || [], sort.sortConfig), [rijen, sort.sortConfig]);
  useEffect(() => { setPage(1); }, [gesorteerd]);
  const th = (label, k, extra) => <SortHeader label={label} k={k} sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" {...extra} />;
  const totaal = (rijen || []).reduce((s, r) => s + (Number(r.totaal) || 0), 0);
  const open = (rijen || []).filter(r => !/^(betaald|omgekeerd)$/i.test(String(r.statusBetaling || ''))).reduce((s, r) => s + (Number(r.totaal) || 0), 0);
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={compact ? 'text-sm font-semibold text-lime-400 uppercase flex items-center gap-2' : 'text-lg font-semibold text-slate-100'}>{compact && <Receipt size={16} />}{titel}</h3>
          <ExportKnop onExport={() => exportRowsToCsv(`${bestandsnaam}.csv`, SELFBILL_EXPORT_KOLOMMEN, gesorteerd)} disabled={!gesorteerd.length} label="Exporteer de self bills naar Excel" count={gesorteerd.length} />
        </div>
        <span className="text-xs text-slate-500">{formatNumberRounded((rijen || []).length)} self bills · totaal {formatCurrency(totaal)}{open ? ` · nog niet betaald ${formatCurrency(open)}` : ''}</span>
      </div>
      {toelichting && <p className="text-xs text-slate-400 mb-3">{toelichting}</p>}
      {(rijen || []).length === 0 ? <p className="text-sm text-slate-500 italic">Geen self bills.</p> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700 text-xs text-slate-400">
            {th('Energielabel', 'relatieBedrijf')}
            {th('Verkoper', 'verkoper')}
            {th('Verkoopteam', 'verkoopteam')}
            {/* Zelfde kolomvolgorde als de Facturen-tabel (Invoices/Customer 360):
                nummer · klantnummer · klant · factuurdatum · soort · vervaldatum · status. */}
            {th('Nummer', 'nummer')}
            {th('Klantnummer', 'relatieReferentie')}
            {th('Klant', 'relatieNaam')}
            {th('Factuurdatum', 'factuurdatum')}
            {th('Soort', 'soort')}
            {th('Vervaldatum', 'vervaldatum')}
            {th('Status betaling', 'statusBetaling')}
            {th('Totaal (excl. BTW)', 'totaalExclBTW', { align: 'right' })}
            {th('Totaal', 'totaal', { align: 'right' })}
          </tr></thead>
          <tbody>{renderPaged(page, setPage, gesorteerd, r => {
            const canOpen = onOpenCustomer360 && r.relatieNaam;
            return (
              <tr key={r.id} onClick={canOpen ? () => onOpenCustomer360(klantTarget(r.relatieNaam, r.relatieReferentie)) : undefined}
                onContextMenu={onRowContext ? (e) => onRowContext(e, r) : undefined}
                className={`border-b border-slate-700/30 hover:bg-slate-700/20 ${canOpen ? 'cursor-pointer' : onRowContext ? 'cursor-context-menu' : ''}`}>
                <td className="py-2 px-2 text-slate-300 text-xs">{r.relatieBedrijf || '—'}</td>
                <td className="py-2 px-2 text-slate-300 text-xs">{r.verkoper || '—'}</td>
                <td className="py-2 px-2 text-slate-400 text-xs">{r.verkoopteam || '—'}</td>
                <td className="py-2 px-2 text-slate-200 font-mono text-xs whitespace-nowrap">{r.nummer || '—'}</td>
                <td className="py-2 px-2 text-slate-400 font-mono text-xs whitespace-nowrap">{r.relatieReferentie || '—'}<KlantnrKopieerKnop nr={r.relatieReferentie} /></td>
                <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-[180px]" title={r.relatieNaam}>{r.relatieNaam || '—'}</td>
                <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{pfFormatDate(r.factuurdatum)}</td>
                <td className="py-2 px-2 text-xs"><span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${/creditnota/i.test(r.soort) ? 'bg-purple-500/15 text-purple-300' : 'bg-teal-500/15 text-teal-300'}`}>{r.soort}</span></td>
                <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{pfFormatDate(r.vervaldatum)}</td>
                <td className="py-2 px-2 text-xs"><span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${/^betaald$/i.test(r.statusBetaling) ? 'bg-lime-500/15 text-lime-300' : /omgekeerd/i.test(r.statusBetaling) ? 'bg-slate-600/30 text-slate-300' : 'bg-amber-500/15 text-amber-300'}`}>{r.statusBetaling || '—'}</span></td>
                <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{formatCurrency(r.totaalExclBTW)}</td>
                <td className={`py-2 px-2 text-xs text-right whitespace-nowrap font-medium ${(Number(r.totaal) || 0) < 0 ? 'text-purple-300' : 'text-slate-100'}`}>{formatCurrency(r.totaal)}</td>
              </tr>
            );
          }, 15)}</tbody>
        </table>
      </div>}
    </Card>
  );
};

const INVOICES_TABS = [{ id: 'facturen', label: 'Facturen' }, { id: 'analyse', label: 'Analyse' }];

const InvoicesPage = ({ facturen, selfbills = [], orders = [], loading = false, onOpenCustomer360, onSettings, apiLoading, apiError, apiInfo, salesStructure = EMPTY_STRUCTURE, currentUser, labelConfig = {} }) => {
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [soortFilter, setSoortFilter] = useState('all');
  const [customerPaymentStatusFilter, setCustomerPaymentStatusFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  // Horizontaal tab-menu binnen Invoices (zoals Orders/Revenue/Customer 360). De
  // filterbalk staat erboven en geldt voor beide tabs, zodat de analyse dezelfde
  // selectie beschrijft als de tabel.
  // Welke van die tabs zichtbaar zijn, is per gebruiker instelbaar (subpagina's).
  const { tabs: invoicesTabs, mag: magInvoicesTab, leeg: geenInvoicesTabs } = useSubPages('invoices', currentUser, INVOICES_TABS);
  const [invoicesTab, setInvoicesTab] = useState(() => {
    return leesSessieWaarde('invoicesTab') || 'facturen';
  });
  useEffect(() => { schrijfSessieWaarde('invoicesTab', invoicesTab); }, [invoicesTab]);
  useEffect(() => {
    if (invoicesTabs.length > 0 && !invoicesTabs.some(t => t.id === invoicesTab)) setInvoicesTab(invoicesTabs[0].id);
  }, [invoicesTabs, invoicesTab]);
  const toonInvoicesTab = (id) => magInvoicesTab(id) && invoicesTab === id;
  const [relatieBedrijfFilter, setRelatieBedrijfFilter] = useState('all');
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [verkoperFilter, setVerkoperFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'factuurdatum', dir: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = TABLE_PAGE_SIZE;
  // Default cascade-selectie op de scope van de ingelogde gebruiker (master =
  // eigen energielabel, teamleader = +team, reseller = +verkoper; admin/ET =
  // alles). Herhaalt bij user-wissel (impersonate) zodra /api/auth/me de
  // scope-velden levert.
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setRelatieBedrijfFilter(d.company);
    setVerkoopteamFilter(d.team);
    setVerkoperFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  useFilterPersistence('invoices.filters', currentUser,
    { relatieBedrijfFilter, verkoopteamFilter, verkoperFilter, searchTerm, statusFilter, soortFilter, customerPaymentStatusFilter, segmentFilter, sortConfig },
    (s) => { setRelatieBedrijfFilter(s.relatieBedrijfFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); setSearchTerm(s.searchTerm ?? ''); setStatusFilter(s.statusFilter ?? 'all'); setSoortFilter(s.soortFilter ?? 'all'); setCustomerPaymentStatusFilter(s.customerPaymentStatusFilter ?? 'all'); setSegmentFilter(s.segmentFilter ?? 'all'); if (s.sortConfig && s.sortConfig.key) setSortConfig(s.sortConfig); });

  // Betaalstatus per klantnummer, uit ÁLLE facturen van die klant. Zelfde
  // functie én labelConfig als Revenue, zodat de per-label drempel hier identiek
  // uitvalt. (Enkel Goed/Wanbetaler — het oninbaar deel is een Revenue-begrip.)
  const customerPaymentStatusMap = useMemo(() => buildCustomerPaymentStatusMap(facturen, labelConfig), [facturen, labelConfig]);

  const getCustomerPaymentStatus = (f) => {
    const customerRef = f.relatieReferentie || '';
    return customerPaymentStatusMap[customerRef] || 'Goed';
  };

  // Invoices toont enkel de brondata (CSV-import én API-import). De berekende
  // energie-fee-kolommen zijn hier verwijderd; die leven nu in de Revenue-module.

  // De SCOPE-selectie (energielabel → verkoopteam → verkoper) bakent af naar welke
  // facturen je kijkt. De keuzelijsten Status en Soort én de tabel vertrekken
  // daarvan, zodat er geen status of factuursoort van een ánder energielabel in de
  // dropdown blijft staan. Segment en Customer Payment hebben een vaste lijst en
  // zijn dus niet data-afgeleid.
  const facturenInScope = useMemo(() => {
    let rows = facturen;
    if (relatieBedrijfFilter !== 'all') rows = rows.filter(f => f.relatieBedrijf === relatieBedrijfFilter);
    if (verkoopteamFilter !== 'all') rows = rows.filter(f => f.verkoopteam === verkoopteamFilter);
    if (verkoperFilter !== 'all') rows = rows.filter(f => f.verkoper === verkoperFilter);
    return rows;
  }, [facturen, relatieBedrijfFilter, verkoopteamFilter, verkoperFilter]);
  const statuses = useMemo(() => [...new Set(facturenInScope.map(f => f.statusBetaling).filter(Boolean))].sort(), [facturenInScope]);
  const soorten = useMemo(() => [...new Set(facturenInScope.map(f => f.soort).filter(Boolean))].sort(), [facturenInScope]);
  // Verdwijnt een gekozen status of soort door een scope-wissel uit de lijst, zet
  // hem dan terug op "Alle" — anders blijft hij filteren op iets wat niet meer te
  // zien is. Enkel zolang er facturen IN de scope zitten, zodat dit tijdens het
  // laden geen zojuist herstelde filters wist.
  useEffect(() => {
    if (facturenInScope.length === 0) return;
    if (statusFilter !== 'all' && !statuses.includes(statusFilter)) setStatusFilter('all');
    if (soortFilter !== 'all' && !soorten.includes(soortFilter)) setSoortFilter('all');
  }, [facturenInScope, statuses, soorten, statusFilter, soortFilter]);
  // Cascade-opties (energielabel → verkoopteam → verkoper) uit de centrale
  // sales-structuur, doorsneden met wat in de facturen voorkomt (factuur-spelling
  // kan licht afwijken van /resellers). Identiek aan Orders/Sales, rol-gescopet.
  const factuurLabels = useMemo(() => facturen.map(f => f.relatieBedrijf).filter(Boolean), [facturen]);
  const factuurTeams = useMemo(() => facturen.map(f => f.verkoopteam).filter(Boolean), [facturen]);
  const factuurSellers = useMemo(() => facturen.map(f => f.verkoper).filter(Boolean), [facturen]);
  const { companies: relatieBedrijven, teams: verkoopteams, sellers: verkopers } = useMemo(
    () => scopeOptions(salesStructure, relatieBedrijfFilter, verkoopteamFilter, { labels: factuurLabels, teams: factuurTeams, sellers: factuurSellers }),
    [salesStructure, relatieBedrijfFilter, verkoopteamFilter, factuurLabels, factuurTeams, factuurSellers]
  );
  
  
  // Zoeksuggesties (zelfde veld als Customer 360). De klantkenmerken — segment,
  // bedrijfsnaam — staan NIET op een factuur, dus die komen uit de orders; het
  // aantal per regel telt de facturen binnen de huidige scope, precies wat de
  // tabel eronder zou tonen. De sectie "Factuurnummers" is hier extra: op deze
  // pagina zoek je even vaak op een factuurnummer als op een klant.
  const zoekKenmerken = useMemo(() => klantKenmerken(orders), [orders]);
  const zoekSuggesties = useMemo(() => bouwZoekSuggesties({
    query: searchTerm, facturen: facturenInScope, kenmerken: zoekKenmerken, bron: 'facturen', metFacturen: true,
  }), [searchTerm, facturenInScope, zoekKenmerken]);
  const zoekOp = (waarde) => { setSearchTerm(waarde); setCurrentPage(1); };

  const getSegment = (f) => {
    const elSeg = (f.elSegment || '').toUpperCase();
    const ngSeg = (f.ngSegment || '').toUpperCase();
    if (elSeg.includes('B2C') || ngSeg.includes('B2C')) return 'B2C';
    if (elSeg.includes('B2B') || ngSeg.includes('B2B')) return 'B2B';
    if (elSeg.includes('CUSTOM') || ngSeg.includes('CUSTOM')) return 'Custom';
    return '';
  };

  // Self bills volgen dezelfde scope- en zoekfilters als de facturen; de
  // soort-/segmentfilters gelden niet (een self bill heeft geen EL/NG-detail).
  const selfbillsGefilterd = useMemo(() => {
    let rows = selfbills || [];
    if (relatieBedrijfFilter !== 'all') rows = rows.filter(f => f.relatieBedrijf === relatieBedrijfFilter);
    if (verkoopteamFilter !== 'all') rows = rows.filter(f => f.verkoopteam === verkoopteamFilter);
    if (verkoperFilter !== 'all') rows = rows.filter(f => f.verkoper === verkoperFilter);
    if (statusFilter !== 'all') rows = rows.filter(f => f.statusBetaling === statusFilter);
    if (searchTerm) rows = rows.filter(f => invoiceMatchesSearch(f, searchTerm));
    return rows;
  }, [selfbills, relatieBedrijfFilter, verkoopteamFilter, verkoperFilter, statusFilter, searchTerm]);
  const filteredFacturen = useMemo(() => {
    let result = [...facturenInScope];
    if (statusFilter !== 'all') result = result.filter(f => f.statusBetaling === statusFilter);
    if (soortFilter !== 'all') result = result.filter(f => f.soort === soortFilter);
    if (customerPaymentStatusFilter !== 'all') result = result.filter(f => getCustomerPaymentStatus(f) === customerPaymentStatusFilter);
    if (segmentFilter !== 'all') result = result.filter(f => getSegment(f) === segmentFilter);
    // Zelfde matcher als de zoeksuggesties (en als de Revenue-detailtabellen),
    // zodat "12 facturen" in de suggestie ook 12 rijen oplevert.
    if (searchTerm) result = result.filter(f => invoiceMatchesSearch(f, searchTerm));
    result.sort((a, b) => {
      // 'openstaand' loopt altijd via invOutstanding (API-restbedrag, met de
      // afleiding uit de betaalstatus als vangnet) — een onbekend restbedrag
      // krijgt -1 en zakt zo onderaan bij aflopend sorteren.
      const lees = (r) => sortConfig.key === 'openstaand' ? (invOutstanding(r) ?? -1) : r[sortConfig.key];
      const av = lees(a), bv = lees(b);
      if (typeof av === 'number' && typeof bv === 'number') return sortConfig.dir === 'asc' ? av - bv : bv - av;
      return sortConfig.dir === 'asc' ? String(av || '').localeCompare(String(bv || '')) : String(bv || '').localeCompare(String(av || ''));
    });
    return result;
  }, [facturenInScope, statusFilter, soortFilter, customerPaymentStatusFilter, customerPaymentStatusMap, segmentFilter, searchTerm, sortConfig]);
  // Het segment op de factuur zelf (elSegment/ngSegment) is in de praktijk bijna
  // alleen op creditnota's gevuld: van de verkoopfacturen heeft ruim 90% er geen.
  // Daarom vallen we terug op het segment van de KLANT, uit zijn orders — die
  // koppelen op klantnummer en dekken vrijwel elke factuur. Zonder die terugval
  // zou de analyse één grote hoop "Zonder segment" tonen.
  const klantSegmentUitOrders = useMemo(() => {
    const map = new Map();
    for (const o of orders || []) {
      const ref = (o.customerReference || '').trim();
      if (!ref || map.has(ref)) continue;
      const seg = orderSegmentClass(o);
      if (seg) map.set(ref, seg === 'CUSTOM' ? 'Custom' : seg);
    }
    return map;
  }, [orders]);
  const segmentVanFactuur = (f) => getSegment(f) || klantSegmentUitOrders.get((f.relatieReferentie || '').trim()) || 'Zonder segment';

  // ── Analyse: betaalstatus per klantsegment ─────────────────────────────────
  // Eén taart per segment (kleine veelvouden) in plaats van één taart met
  // segment × status door elkaar: zo lees je per segment meteen de verhouding
  // betaald / niet betaald, en blijven de segmenten onderling vergelijkbaar.
  //
  // De statussen dragen STATUS-kleuren, geen categorische: groen = betaald,
  // rood = niet betaald, amber = deels. Die betekenis ligt platformbreed vast en
  // mag niet met een reekskleur verward worden.
  const INV_STATUS_VOLGORDE = [
    { sleutel: 'Betaald', kleur: STATUS_ACCENT.goed },
    { sleutel: 'Deels betaald', kleur: STATUS_ACCENT.deels },
    { sleutel: 'Niet betaald', kleur: STATUS_ACCENT.wanbetaler },
    { sleutel: 'Overig', kleur: 'var(--chart-neutral)' },
  ];
  // "Overig" vangt alles wat geen van de drie is (Omgekeerd, in_payment, leeg) —
  // die horen niet bij betaald of niet betaald, maar mogen ook niet wegvallen.
  const invStatusGroep = (f) => {
    const s = (f.statusBetaling || '').trim();
    return ['Betaald', 'Deels betaald', 'Niet betaald'].includes(s) ? s : 'Overig';
  };

  const segmentAnalyse = useMemo(() => {
    const leeg = () => ({ Betaald: 0, 'Deels betaald': 0, 'Niet betaald': 0, Overig: 0, totaal: 0 });
    const per = new Map();
    for (const f of filteredFacturen) {
      const seg = segmentVanFactuur(f);
      const rij = per.get(seg) || leeg();
      rij[invStatusGroep(f)] += 1;
      rij.totaal += 1;
      per.set(seg, rij);
    }
    // Vaste volgorde: de bekende segmenten eerst, "Zonder segment" achteraan.
    const volgorde = ['B2C', 'B2B', 'Custom', 'Zonder segment'].filter(s => per.has(s));
    // De partjes zitten IN de memo: gaf je Recharts bij elke render een nieuwe
    // array, dan herstart hij zijn enter-animatie telkens opnieuw en blijft de
    // taart leeg. Eén stabiele referentie per segment lost dat op.
    const rijen = volgorde.map(seg => {
      const r = { segment: seg, ...per.get(seg) };
      r.slices = INV_STATUS_VOLGORDE.map(s => ({ naam: s.sleutel, aantal: r[s.sleutel], kleur: s.kleur })).filter(s => s.aantal > 0);
      return r;
    });
    const totaal = rijen.reduce((a, r) => ({
      Betaald: a.Betaald + r.Betaald, 'Deels betaald': a['Deels betaald'] + r['Deels betaald'],
      'Niet betaald': a['Niet betaald'] + r['Niet betaald'], Overig: a.Overig + r.Overig, totaal: a.totaal + r.totaal,
    }), leeg());
    return { rijen, totaal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFacturen, klantSegmentUitOrders]);

  const exportSegmentAnalyseCsv = () => exportRowsToCsv('facturen-betaalstatus-per-segment.csv', [
    { header: 'Klantsegment', get: r => r.segment },
    { header: 'Betaald', get: r => r.Betaald },
    { header: 'Deels betaald', get: r => r['Deels betaald'] },
    { header: 'Niet betaald', get: r => r['Niet betaald'] },
    { header: 'Overig', get: r => r.Overig },
    { header: 'Totaal', get: r => r.totaal },
  ], segmentAnalyse.rijen);


  const totalPages = Math.ceil(filteredFacturen.length / pageSize);
  const paginatedFacturen = filteredFacturen.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, soortFilter, customerPaymentStatusFilter, segmentFilter, relatieBedrijfFilter, verkoopteamFilter]);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ col }) => sortConfig.key !== col ? <ChevronDown size={14} className="opacity-30" /> : (sortConfig.dir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />);


  const totals = useMemo(() => ({
    count: filteredFacturen.length,
    totaalExclBTW: filteredFacturen.reduce((sum, f) => sum + (f.totaalExclBTW || 0), 0),
    totaal: filteredFacturen.reduce((sum, f) => sum + (f.totaal || 0), 0)
  }), [filteredFacturen]);

  // Nog aan het laden → enkel spinner + regel, geen knoppen of uitleg.
  if (!facturen.length && (loading || apiLoading)) return <PageLoading message="Facturen worden geladen…" />;
  // Lege staat: er is nog niets geladen. Er is hier geen knop meer om zelf de API
  // aan te spreken — die synchronisatie loopt als geplande job en duurt minuten;
  // ze door een gebruiker laten starten verving de dataset voor iedereen.
  if (!facturen.length) return (
    <div className="space-y-4">
      <PageHeader title="Invoices" subtitle="Nog geen factuur-data beschikbaar" />
      {apiError && currentUser?.role === 'admin' && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">API-fout: {apiError}</div>}
      {apiInfo && !apiError && currentUser?.role === 'admin' && <div className={`px-3 py-1.5 rounded text-xs ${apiLoading ? 'bg-amber-500/10 border border-amber-500/30 text-amber-300' : 'bg-lime-500/10 border border-lime-500/30 text-lime-300'}`}>{apiInfo}</div>}
      <EmptyState message={apiLoading ? 'Facturen worden opgehaald…' : 'Nog geen facturen geladen — de synchronisatie loopt op de achtergrond.'} onSettings={onSettings} />
    </div>
  );

  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setSoortFilter('all'); setCustomerPaymentStatusFilter('all'); setSegmentFilter('all'); setRelatieBedrijfFilter('all'); setVerkoopteamFilter('all'); setVerkoperFilter('all'); setCurrentPage(1); };
  const hasActiveFilters = searchTerm || statusFilter !== 'all' || soortFilter !== 'all' || customerPaymentStatusFilter !== 'all' || segmentFilter !== 'all' || relatieBedrijfFilter !== 'all' || verkoopteamFilter !== 'all';

  // Favoriete filters: het energielabel hoort MEE in de snapshot — zie favorietScope.
  // Onder de sleutel `companyFilter`, gelijk aan de andere pagina's, ook al heet de
  // state hier relatieBedrijfFilter. De keuzelijst van deze pagina is versmald tot
  // labels die in de facturen voorkomen, dus die gaat mee als `zichtbareLabels`.
  const filterSnapshot = {
    companyFilter: relatieBedrijfFilter,
    searchTerm, verkoopteamFilter, verkoperFilter, statusFilter, soortFilter, customerPaymentStatusFilter, segmentFilter, sortConfig,
  };
  const applyFavorite = (f) => {
    const scope = favorietScope(salesStructure, { company: f.companyFilter, team: f.verkoopteamFilter, seller: f.verkoperFilter }, relatieBedrijven);
    setSearchTerm(f.searchTerm ?? '');
    setRelatieBedrijfFilter(scope.company);
    setVerkoopteamFilter(scope.team);
    setVerkoperFilter(scope.seller);
    setStatusFilter(f.statusFilter ?? 'all');
    setSoortFilter(f.soortFilter ?? 'all');
    setCustomerPaymentStatusFilter(f.customerPaymentStatusFilter ?? 'all');
    setSegmentFilter(f.segmentFilter ?? 'all');
    if (f.sortConfig && f.sortConfig.key) setSortConfig(f.sortConfig);
    setCurrentPage(1);
  };

  // De rol bepaalt hoeveel technische ruis deze pagina toont. Een partner heeft
  // niets aan de sync-melding en mag de API-ophaling niet zelf starten (die
  // vervangt de dataset voor iedereen en duurt minuten); die knop is intussen
  // helemaal weg — de synchronisatie loopt als geplande job.
  const isAdminGebruiker = currentUser?.role === 'admin';

  return <div className="space-y-4">
    <PageHeader title="Invoices" subtitle="Overzicht van alle facturen" />
    {apiError && isAdminGebruiker && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">API-fout: {apiError}</div>}
    {/* Sync-melding: enkel voor admin, en klein — het is een technische toelichting,
        geen boodschap voor wie gewoon zijn facturen komt bekijken. */}
    {apiInfo && !apiError && isAdminGebruiker && <div className={`px-3 py-1.5 rounded text-xs ${apiLoading ? 'bg-amber-500/10 border border-amber-500/30 text-amber-300' : 'bg-lime-500/10 border border-lime-500/30 text-lime-300'}`}>{apiInfo}</div>}
    <Card className="!p-3">
      <CollapsibleFilters active={hasActiveFilters}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[184px]"><label className="block text-xs text-slate-400 mb-1">Energieleverancier</label><select value={relatieBedrijfFilter} onChange={e => { setRelatieBedrijfFilter(e.target.value); setVerkoopteamFilter('all'); setVerkoperFilter('all'); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${relatieBedrijfFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={relatieBedrijven}>Alle</AllOption>{relatieBedrijven.map(b => <option key={b} value={b}>{b}</option>)}</select></div>
        <div className="self-end"><FilterFavorites pageKey="invoices" filters={filterSnapshot} onApply={applyFavorite} /></div>
        <div className="w-[184px]"><label className="block text-xs text-slate-400 mb-1">Verkoopteam</label><select value={verkoopteamFilter} onChange={e => { setVerkoopteamFilter(e.target.value); setVerkoperFilter('all'); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={verkoopteams}>Alle</AllOption>{verkoopteams.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
        <div className="w-[184px]"><label className="block text-xs text-slate-400 mb-1">Verkoper</label><select value={verkoperFilter} onChange={e => { setVerkoperFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoperFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={verkopers}>Alle</AllOption>{verkopers.map(v => <option key={v} value={v}>{v}</option>)}</select></div>
        <KlantZoekVeld className="w-full sm:w-[280px] shrink-0" label="Zoeken" placeholder="Klant, klantnummer, factuurnummer, EAN, verkoper…" value={searchTerm} onChange={zoekOp} onClear={() => zoekOp('')} suggesties={zoekSuggesties} onKlant={(k) => zoekOp(k.naam)} onGsrn={(g) => zoekOp(g.gsrn)} onFactuur={(f) => zoekOp(f.nummer)} telWoord={{ een: 'factuur', meer: 'facturen' }} />
        <div className="w-[132px]"><label className="block text-xs text-slate-400 mb-1">Status</label><select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${statusFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
        <div className="w-[132px]"><label className="block text-xs text-slate-400 mb-1">Soort</label><select value={soortFilter} onChange={e => { setSoortFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${soortFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option>{soorten.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
        <div className="w-[132px]"><label className="block text-xs text-slate-400 mb-1">Customer Payment</label><select value={customerPaymentStatusFilter} onChange={e => { setCustomerPaymentStatusFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${customerPaymentStatusFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="Goed">Goed</option><option value="Wanbetaler">Wanbetaler</option></select></div>
        <div className="w-[132px]"><label className="block text-xs text-slate-400 mb-1">Segment</label><select value={segmentFilter} onChange={e => { setSegmentFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${segmentFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="B2C">B2C</option><option value="B2B">B2B</option><option value="Custom">Custom</option></select></div>
        {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}
      </div>
      </CollapsibleFilters>
      {hasActiveFilters && <div className="mt-2 text-xs text-lime-400">Gefilterd: {filteredFacturen.length} van {facturen.length} facturen</div>}
    </Card>
    {/* Horizontaal tab-menu binnen Invoices (zoals Orders/Revenue/Customer 360).
        De filterbalk hierboven geldt voor beide tabs. Welke tabs er staan, hangt
        af van de subpagina's die deze gebruiker toegekend kreeg. */}
    {geenInvoicesTabs && <GeenSubpaginas module="Invoices" />}
    <div className="-mx-1 overflow-x-auto no-scrollbar">
      <div className="flex gap-2 px-1 w-max">
        {invoicesTabs.map(t => (
          <button key={t.id} onClick={() => setInvoicesTab(t.id)}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${invoicesTab === t.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>

    {/* ── Tab: Analyse — betaalstatus per klantsegment ── */}
    {/* Bewust GEEN 'hidden'-tab maar voorwaardelijk renderen: een Recharts
        ResponsiveContainer die in een display:none-container mount, meet breedte 0
        en tekent daarna niets meer tot er toevallig een resize langskomt. Bij een
        tabel merk je dat niet, bij een grafiek zie je een leeg vlak. */}
    {toonInvoicesTab('analyse') && <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">Betaalstatus per klantsegment ({formatNumberRounded(segmentAnalyse.totaal.totaal)} facturen)</h3><ExportKnop onExport={exportSegmentAnalyseCsv} disabled={segmentAnalyse.rijen.length === 0} label="Exporteer deze tabel naar Excel" /></div>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Facturen binnen de huidige filters. Eén taart per segment, zodat de verhouding betaald / niet betaald per segment te vergelijken valt.
          “Overig” is alles wat noch betaald noch onbetaald is (omgekeerd, in behandeling).
        </p>
        {segmentAnalyse.totaal.totaal === 0 ? <TableEmpty message="Geen facturen binnen deze filters." icon={Folder} /> : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {segmentAnalyse.rijen.map(rij => {
                const slices = rij.slices;
                return (
                  <div key={rij.segment} className="min-w-0">
                    <div className="text-center text-sm font-semibold text-slate-200">{rij.segment}</div>
                    <div className="text-center text-[11px] text-slate-500 mb-1">{formatNumberRounded(rij.totaal)} facturen</div>
                    {/* Vaste maat i.p.v. ResponsiveContainer: die meet in dit
                        raster bij de eerste render breedte 0 en tekent dan niets
                        meer tot er toevallig een resize langskomt. Vier kleine
                        taartjes van 180 px hoeven niet mee te schalen; ze staan
                        gecentreerd in hun cel. */}
                    <div className="flex justify-center">
                      <PieChart width={180} height={180}>
                        <Pie data={slices} dataKey="aantal" nameKey="naam" cx="50%" cy="50%"
                          innerRadius={42} outerRadius={78} labelLine={false} label={donutPctLabel}
                          isAnimationActive={false}>
                          {slices.map(s => <Cell key={s.naam} fill={s.kleur} stroke="var(--chart-surface, #1e293b)" strokeWidth={2} />)}
                        </Pie>
                        <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => [`${formatNumberRounded(v)} facturen`, n]} />
                      </PieChart>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Eén legende voor alle taarten — de statuskleuren zijn overal dezelfde. */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-1 mb-4">
              {INV_STATUS_VOLGORDE.map(s => (
                <span key={s.sleutel} className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.kleur }} />
                  {s.sleutel}
                </span>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm resp-card">
                <thead><tr className="border-b border-slate-700 text-slate-400">
                  <th className="text-left py-2 px-2 text-xs">Klantsegment</th>
                  <th className="text-right py-2 px-2 text-xs">Betaald</th>
                  <th className="text-right py-2 px-2 text-xs">Deels betaald</th>
                  <th className="text-right py-2 px-2 text-xs">Niet betaald</th>
                  <th className="text-right py-2 px-2 text-xs">Overig</th>
                  <th className="text-right py-2 px-2 text-xs">Totaal</th>
                  <th className="text-right py-2 px-2 text-xs">% betaald</th>
                </tr></thead>
                <tbody>{segmentAnalyse.rijen.map(r => (
                  <tr key={r.segment} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                    <td className="py-2 px-2 text-slate-200 text-xs" data-label="Klantsegment">{r.segment}</td>
                    <td className="py-2 px-2 text-right text-xs tabular-nums" style={{ color: STATUS_ACCENT.goed }} data-label="Betaald">{formatNumberRounded(r.Betaald)}</td>
                    <td className="py-2 px-2 text-right text-xs tabular-nums" style={{ color: STATUS_ACCENT.deels }} data-label="Deels betaald">{formatNumberRounded(r['Deels betaald'])}</td>
                    <td className="py-2 px-2 text-right text-xs tabular-nums" style={{ color: STATUS_ACCENT.wanbetaler }} data-label="Niet betaald">{formatNumberRounded(r['Niet betaald'])}</td>
                    <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums" data-label="Overig">{formatNumberRounded(r.Overig)}</td>
                    <td className="py-2 px-2 text-right text-slate-200 text-xs tabular-nums font-medium" data-label="Totaal">{formatNumberRounded(r.totaal)}</td>
                    <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="% betaald">{r.totaal ? ((r.Betaald / r.totaal) * 100).toFixed(1) : '0,0'}%</td>
                  </tr>
                ))}</tbody>
                <tfoot><tr className="border-t-2 border-slate-600 bg-lime-900/20">
                  <td className="py-2 px-2 text-lime-400 text-xs font-semibold">Totaal</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(segmentAnalyse.totaal.Betaald)}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(segmentAnalyse.totaal['Deels betaald'])}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(segmentAnalyse.totaal['Niet betaald'])}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(segmentAnalyse.totaal.Overig)}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-bold">{formatNumberRounded(segmentAnalyse.totaal.totaal)}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{segmentAnalyse.totaal.totaal ? ((segmentAnalyse.totaal.Betaald / segmentAnalyse.totaal.totaal) * 100).toFixed(1) : '0,0'}%</td>
                </tr></tfoot>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>}

    <div className={toonInvoicesTab('facturen') ? 'bg-slate-800 border border-slate-700/50 rounded-2xl p-6' : 'hidden'}>
      <div className="flex items-center justify-between mb-4"><div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">Facturen ({filteredFacturen.length})</h3><ExportButton onExport={() => exportToCSV(filteredFacturen.map(f => ({ ...f, openstaandInclBtw: invOutstanding(f) })), 'facturen')} label="Exporteer de gefilterde facturen naar Excel" /></div><div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0"><span className="text-sm text-slate-400">Pagina {currentPage} van {totalPages || 1}</span></div></div>
      {/* Mobiel: compacte kaarten i.p.v. de brede facturentabel. Zelfde data
          (paginatedFacturen) en dezelfde Customer 360°-actie; puur visueel — geen
          wijziging aan data, rechten, filtering of logica. Tabel blijft op desktop. */}
      <div className="md:hidden space-y-2">
        {paginatedFacturen.map(f => {
          const canOpen = onOpenCustomer360 && f.relatieNaam;
          return (
            <button key={f.id} onClick={canOpen ? () => onOpenCustomer360(klantTarget(f.relatieNaam, f.relatieReferentie, undefined, [f.elGSRN, f.ngGSRN])) : undefined} className={`w-full text-left rounded-xl border border-slate-700/50 bg-slate-800 p-3 transition-colors ${canOpen ? 'hover:bg-slate-700' : 'cursor-default'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-100 truncate">{f.relatieNaam || f.relatieBedrijf || '—'}</div>
                  <div className="text-xs text-slate-400 truncate">{[f.nummer, f.factuurdatum].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                {canOpen && <Compass size={15} className="text-slate-400 shrink-0 mt-0.5" />}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${f.statusBetaling === 'Betaald' ? 'bg-lime-900/50 text-lime-300' : f.statusBetaling === 'Niet betaald' ? 'bg-red-900/50 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{f.statusBetaling || '—'}</span>
                <span className="text-sm font-semibold text-lime-400 tabular-nums whitespace-nowrap">{formatCurrency(f.totaal)}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="overflow-auto hidden md:block md:max-h-[70vh]">
        <table className="w-full text-sm resp-card">
          <thead className="bg-slate-800 sticky top-0 z-10"><tr className="border-b border-slate-700">
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('relatieBedrijf')}><span className="flex items-center gap-1">Relatie/Bedrijf <SortIcon col="relatieBedrijf" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('verkoper')}><span className="flex items-center gap-1">Verkoper <SortIcon col="verkoper" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('verkoopteam')}><span className="flex items-center gap-1">Verkoopteam <SortIcon col="verkoopteam" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('nummer')}><span className="flex items-center gap-1">Factuurnummer <SortIcon col="nummer" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Referentie</th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('relatieNaam')}><span className="flex items-center gap-1">Klant <SortIcon col="relatieNaam" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('factuurdatum')}><span className="flex items-center gap-1">Factuurdatum <SortIcon col="factuurdatum" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Customer Payment Status</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Soort</th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('vervaldatum')}><span className="flex items-center gap-1">Vervaldatum <SortIcon col="vervaldatum" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('statusBetaling')}><span className="flex items-center gap-1">Status <SortIcon col="statusBetaling" /></span></th>
            <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('totaalExclBTW')}><span className="flex items-center gap-1 justify-end">Totaal (Excl. BTW) <SortIcon col="totaalExclBTW" /></span></th>
            <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('totaal')}><span className="flex items-center gap-1 justify-end">Totaal <SortIcon col="totaal" /></span></th>
            {/* Openstaand = het restbedrag uit de facturatie-API (zie invOutstanding):
                het bedrag dat de klant nog moet betalen, dus INCL. btw — daarom
                staat de btw-basis expliciet in de kop. */}
            <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('openstaand')} title="Nog te betalen bedrag zoals de facturatie-API het meldt. Incl. btw, want dat is wat de klant verschuldigd is.">
              <span className="flex items-center gap-1 justify-end">Openstaand (incl. BTW) <SortIcon col="openstaand" /></span>
            </th>
          </tr></thead>
          <tbody>{paginatedFacturen.map(f => <tr key={f.id} onContextMenu={e => onC360Context(e, f.relatieNaam, klantTarget(f.relatieNaam, f.relatieReferentie, undefined, [f.elGSRN, f.ngGSRN]), { id: f.id, label: f.nummer })} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-context-menu">
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs truncate max-w-24">{f.relatieBedrijf}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs" title={f.verkoperFactuur ? `Volgt de huidige verkoper van het order; op de factuur zelf staat ${f.verkoperFactuur === 'OdooBot' ? 'Online' : f.verkoperFactuur}` : undefined}>{f.verkoper === 'OdooBot' ? 'Online' : f.verkoper}{f.verkoperFactuur ? <span className="ml-1 text-slate-500" aria-hidden="true">*</span> : null}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs truncate max-w-24">{f.verkoopteam}</td>
            <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs font-medium">{f.nummer}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.relatieReferentie}<KlantnrKopieerKnop nr={f.relatieReferentie} /></td>
            <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={f.relatieNaam}>{f.relatieNaam}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{f.factuurdatum}</td>
            <td className="py-2 px-2">{(() => { const isWanbetaler = isRecordBadDebtOverdue(f, labelConfig); return <span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${isWanbetaler ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{isWanbetaler ? 'Wanbetaler' : 'Goed'}</span>; })()}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.soort}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{f.vervaldatum}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${f.statusBetaling === 'Betaald' ? 'bg-lime-900/50 text-lime-300' : f.statusBetaling === 'Niet betaald' ? 'bg-red-900/50 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{f.statusBetaling}</span></td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{formatCurrency(f.totaalExclBTW)}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{formatCurrency(f.totaal)}</td>
            <td className="py-2 px-2 text-xs text-right whitespace-nowrap">{(() => {
              const open = invOutstanding(f);
              if (open == null) return <span className="text-slate-500 cursor-help" title="Het restbedrag van deze factuur is nog niet opgehaald bij de facturatie-API — hoeveel er nog openstaat is dus niet bekend.">?</span>;
              return <span className={open > 0 ? 'text-amber-300 font-medium' : 'text-slate-500'}>{formatCurrency(open)}</span>;
            })()}</td>
          </tr>)}</tbody>
        </table>
      </div>
      {totalPages > 1 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredFacturen.length)} van {filteredFacturen.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Eerste</button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{currentPage}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Laatste</button>
        </div>
      </div>}
      {filteredFacturen.length === 0 && <div className="text-center py-8 text-slate-400"><AlertCircle size={32} className="mx-auto mb-2 opacity-50" /><p>Geen facturen gevonden</p></div>}
    </div>
    {/* Self bills onder de facturen: aparte feed, aparte tabel, zelfde filters. */}
    {toonInvoicesTab('facturen') && (
      <SelfbillsTabel rijen={selfbillsGefilterd} bestandsnaam="self-bills" onOpenCustomer360={onOpenCustomer360}
        onRowContext={(e, r) => onC360Context(e, r.relatieNaam, klantTarget(r.relatieNaam, r.relatieReferentie, undefined, [r.elGSRN, r.ngGSRN]), { id: r.id, label: r.nummer, soort: 'selfbill' })}
        toelichting="Facturen die de klant aan ons stuurt, meestal voor geïnjecteerde energie, en hun creditnota's (negatief bedrag). Ze tellen niet mee in de facturentabel hierboven, in Aging of in de wanbetalerstatus." />
    )}
    {c360Menu}
  </div>;
};

// Herbruikbare orders-detailtabel (zelfde kolommen + berekende fee-velden als de
// Orders-pagina). Wordt gebruikt op de Revenue-pagina als "Partnervergoeding —
// Detail orders". `orders` is reeds gefilterd door de aanroeper; `facturen`
// voedt de Customer Payment Status; `onOpenCustomer360` optioneel.
const OrdersDetailTable = ({ orders = [], facturen = [], onOpenCustomer360, currentUser, feeNotAllowedTags = new Set(), visiblePartnerTags = null, labelConfig = {}, onRegisterExport, exportScope = '', exportLabel = '', exportStamp = exportTimestamp }) => {
  // GSRN-overlapcontrole is een data-integriteitscheck, enkel voor admin + ET.
  const canSeeOverlap = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  // "Potentiële omzet" = de contractwaarde van de klant; enkel voor admin +
  // ET Dossierbeheerder. Geldt voor de kolom ÉN voor de export (zelfde grens als
  // de Omzet-kolom op de Sales-pagina).
  const canSeeOmzet = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  // Standaard gesorteerd op GSRN, dan orderdatum (zie sortedOrders-tiebreaker).
  const [sortConfig, setSortConfig] = useState({ key: 'connectionPointGSRN', dir: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [marketFilter, setMarketFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [connectionTypeFilter, setConnectionTypeFilter] = useState('all');
  const [meterMethodFilter, setMeterMethodFilter] = useState('all');
  const [overlapFilter, setOverlapFilter] = useState('all'); // 'all' | 'ja' | 'nee' (alleen admin/ET)
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // 'all' | 'Goed' | 'Wanbetaler'
  const [feePartnerTagFilter, setFeePartnerTagFilter] = useState('all'); // 'all' | 'Yes' | 'No'
  const pageSize = TABLE_PAGE_SIZE;

  const badDebt = useMemo(() => computeBadDebt(facturen, labelConfig), [facturen, labelConfig]);
  const customerPaymentStatusMap = badDebt.statusMap;
  const getPaymentStatus = (ref) => customerPaymentStatusFromMap(customerPaymentStatusMap, ref);
  // Oninbaarheidsperiode (maanden) toegepast op een klant (0 = geen correctie).
  const badDebtMonths = (ref) => badDebt.monthsMap[String(ref || '').trim()] || 0;

  // MaxFeeDate = de 1e van de VOLGENDE maand. Bewust als string opgebouwd uit de
  // lokale datum: toISOString() zou in tijdzones vóór UTC een dag terugschuiven.
  const maxFeeDate = useMemo(() => {
    const today = new Date();
    // 1e van de VOLGENDE maand (Date verwerkt de jaarovergang dec→jan correct).
    const d = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  }, []);
  // Fee End Date per order (spec): leeg ("") als de effective start ontbreekt of
  // niet vóór MaxFeeDate ligt; anders de effective end, maar gecapt op MaxFeeDate
  // (en MaxFeeDate wanneer de effective end ontbreekt). ISO-datums (YYYY-MM-DD) →
  // een string-vergelijking volgt de chronologie.
  const getFeeEndDate = (o) => {
    const start = o.effectiveStartDate || '';
    if (!start || start >= maxFeeDate) return '';
    const end = o.effectiveEndDate || '';
    if (!end || end > maxFeeDate) return maxFeeDate;
    return end;
  };
  // Fee Duration = maanden van effective start tot de Fee End Date (0 als er geen
  // Fee End Date is).
  const getFeeDuration = (o) => {
    const feeEnd = getFeeEndDate(o);
    if (!feeEnd || !o.effectiveStartDate) return 0;
    const start = new Date(o.effectiveStartDate);
    const end = new Date(feeEnd);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + (end.getDate() - start.getDate()) / 30;
    return Math.round(Math.max(0, months) * 10) / 10;
  };
  // Fee Duration na aftrek van de oninbaarheidsperiode: bij een 'Wanbetaler – deels
  // oninbaar'-klant zijn de recentste N maanden oninbaar → betaalbare looptijd =
  // Fee Duration − N (niet negatief). Andere klanten: ongewijzigd.
  const isFeeDurationAdjusted = (o) => getPaymentStatus(o.customerReference) === PAY_STATUS_PARTIAL && badDebtMonths(o.customerReference) > 0;
  const getFeeDurationAdjusted = (o) => {
    const base = getFeeDuration(o);
    return isFeeDurationAdjusted(o) ? Math.max(0, Math.round((base - badDebtMonths(o.customerReference)) * 10) / 10) : base;
  };
  const CONTRACTED_STAGES = ['Switch Accepted (st.)', 'Contracted (st.)', 'Switch Accepted (cu.)', 'Contracted (cu.)'];
  const getContracted = (o) => (CONTRACTED_STAGES.includes(o.opportunityStage || '') && !isCancelledOrderStatus(o.status)) ? 'Yes' : 'No';
  // Fee Customer Tag = No zodra de klant ÉÉN tag heeft die niet fee-allowed is
  // (data-gedreven config in Revenue → Admin → Customer Tags). partnerTags is
  // komma-gescheiden (bv. "BE- AFBETALINGSPLAN, BE- SOC TARIEF") → per tag checken.
  const getFeePartnerTag = (partnerTags) => {
    if (!partnerTags) return 'Yes';
    const tags = String(partnerTags).split(',').map(t => t.trim()).filter(Boolean);
    return tags.some(t => feeNotAllowedTags.has(t)) ? 'No' : 'Yes';
  };
  const getPriceAddition = (o) => {
    const el = o.priceAdditionsEL;
    const elNum = parseFloat(String(el).replace(',', '.')) || 0;
    if (el && el !== '' && elNum !== 0) return el;
    return o.priceAdditionsNG || '';
  };
  const getPriceAdditionNumeric = (o) => {
    const val = getPriceAddition(o);
    if (!val || val === '') return 0;
    return parseFloat(String(val).replace(',', '.')) || 0;
  };
  // Fee Subscription = 0 bij Fee Contracted = No ÓF Fee Customer Tag = No. Beide
  // gates moeten exact gelijk zijn aan de RevenuePage-samenvatting (getFeeSubscription
  // daar), zodat een (gefilterde) som van deze kolom 1-op-1 reconcilieert met de
  // "GOEDE/WANBETALER"-blokken. Eerder rekende de detail-tabel enkel op Contracted af
  // → een contracted order met een niet-fee-allowed partner-tag toonde toch een fee
  // (intern tegenstrijdig met de eigen "Fee Customer Tag = No"-kolom én hoger dan de
  // samenvatting). Nu consistent.
  const feeAllowedForOrder = (o) => getContracted(o) === 'Yes' && getFeePartnerTag(o.partnerTags) === 'Yes';
  // Netto jaarbedrag = PriceAddition minus het vaste ET-recht (€30/EAN/jaar).
  // Negatief toegestaan; géén toeslag (0/leeg) → geen fee (geen losse -€30).
  const getFeeSubscriptionYearly = (o) => {
    if (!feeAllowedForOrder(o)) return 0;
    if (!isYearlyFeeUnit(o.priceAdditionsUnit)) return 0;
    const priceAddition = getPriceAdditionNumeric(o);
    if (!priceAddition) return 0;
    const netAnnual = priceAddition - FIXED_ANNUAL_RIGHT_PER_EAN;
    const feeDuration = getFeeDurationAdjusted(o);
    const years = feeDuration > 0 ? Math.ceil(feeDuration / 12) : 0;
    return netAnnual * Math.max(years, feeDuration > 0 ? 1 : 0);
  };
  const getFeeSubscriptionProRata = (o) => {
    if (!feeAllowedForOrder(o)) return 0;
    if (isYearlyFeeUnit(o.priceAdditionsUnit)) return 0;
    const priceAddition = getPriceAdditionNumeric(o);
    if (!priceAddition) return 0;
    const netAnnual = priceAddition - FIXED_ANNUAL_RIGHT_PER_EAN;
    return (netAnnual / 12) * getFeeDurationAdjusted(o);
  };
  const getFeeSubscription = (o) => {
    if (!feeAllowedForOrder(o)) return 0;
    return getFeeSubscriptionYearly(o) + getFeeSubscriptionProRata(o);
  };

  // Basis: enkel "contracted" orders (Fee Contracted = Yes): contracted-stage én
  // niet geannuleerd.
  const contractedOrders = useMemo(() => orders.filter(o => getContracted(o) === 'Yes'), [orders]);
  // GSRN-overlapcontrole: per unieke GSRN mag er nooit een overlappende
  // leveringsperiode zijn tussen twee actieve (Fee Contracted = Yes) orders.
  // Periode = Effective Start→End, met fallback op Delivery Start→End per record.
  // Ontbreekt de start ook dan → -oneindig; ontbreekt de eind ook dan → +oneindig
  // (open/lopend contract). Overlap = strikt (aansluitende periodes, einde == volgende
  // start, tellen NIET als overlap). Resultaat = een (onzichtbare) tag: een Set met
  // de order-objecten die in een overlap zitten. Enkel berekend voor admin/ET, want
  // alleen zij zien de rode markering + de overlap-filter.
  const overlapOrderSet = useMemo(() => canSeeOverlap ? computeGsrnOverlapSet(contractedOrders) : new Set(), [contractedOrders, canSeeOverlap]);
  const hasOverlapTag = (o) => overlapOrderSet.has(o);
  // Filteropties uit de zichtbare (contracted) set.
  const markets = useMemo(() => [...new Set(contractedOrders.map(o => o.market).filter(Boolean))].sort(), [contractedOrders]);
  const segments = useMemo(() => [...new Set(contractedOrders.map(o => o.segment).filter(Boolean))].sort(), [contractedOrders]);
  const connectionTypes = useMemo(() => [...new Set(contractedOrders.map(o => o.connectionType).filter(Boolean))].sort(), [contractedOrders]);
  const meterMethods = useMemo(() => [...new Set(contractedOrders.map(o => o.meterMethod).filter(Boolean))].sort(), [contractedOrders]);
  // Zoek + filters + sortering. Voedt zowel de tabel als de CSV-export, dus de
  // export bevat exact de gefilterde rijen.
  const sortedOrders = useMemo(() => {
    let data = contractedOrders;
    if (searchTerm) data = data.filter(o => orderMatchesSearch(o, searchTerm, visiblePartnerTags));
    if (marketFilter !== 'all') data = data.filter(o => o.market === marketFilter);
    if (segmentFilter !== 'all') data = data.filter(o => o.segment === segmentFilter);
    if (connectionTypeFilter !== 'all') data = data.filter(o => o.connectionType === connectionTypeFilter);
    if (meterMethodFilter !== 'all') data = data.filter(o => o.meterMethod === meterMethodFilter);
    if (canSeeOverlap && overlapFilter !== 'all') data = data.filter(o => overlapFilter === 'ja' ? overlapOrderSet.has(o) : !overlapOrderSet.has(o));
    if (paymentStatusFilter !== 'all') data = data.filter(o => getPaymentStatus(o.customerReference) === paymentStatusFilter);
    if (feePartnerTagFilter !== 'all') data = data.filter(o => getFeePartnerTag(o.partnerTags) === feePartnerTagFilter);
    return [...data].sort((a, b) => {
      const av = a[sortConfig.key] || ''; const bv = b[sortConfig.key] || '';
      const primary = sortConfig.dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      // Secundair altijd op orderdatum (oudste eerst), zodat orders per GSRN
      // chronologisch staan.
      return primary !== 0 ? primary : String(a.orderDate || '').localeCompare(String(b.orderDate || ''));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractedOrders, searchTerm, marketFilter, segmentFilter, connectionTypeFilter, meterMethodFilter, overlapFilter, canSeeOverlap, overlapOrderSet, sortConfig, paymentStatusFilter, feePartnerTagFilter, customerPaymentStatusMap, feeNotAllowedTags, visiblePartnerTags]);
  const totalPages = Math.ceil(sortedOrders.length / pageSize);
  const pageClamped = Math.min(currentPage, Math.max(1, totalPages));
  const paginatedOrders = useMemo(() => { const start = (pageClamped - 1) * pageSize; return sortedOrders.slice(start, start + pageSize); }, [sortedOrders, pageClamped, pageSize]);
  const handleSort = (key) => { setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' })); setCurrentPage(1); };
  const SortIcon = ({ col }) => sortConfig.key !== col ? <ChevronDown size={14} className="opacity-30" /> : (sortConfig.dir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />);

  // Export: alle (gefilterde) orderregels incl. berekende fee-velden naar CSV.
  const exportOrdersCsv = (collect) => {
    const columns = [
      { header: 'Bedrijf', get: o => o.company }, { header: 'Orderdatum', get: o => o.orderDate },
      { header: 'Klantennummer', get: o => o.customerReference }, { header: 'Klant', get: o => o.customer },
      { header: 'Telefoon', get: o => o.customerPhone }, { header: 'E-mail', get: o => o.customerEmail },
      { header: 'Bank Account', get: o => o.customerBankAccount }, { header: 'Market', get: o => o.market },
      { header: 'EAN', get: o => o.connectionPointGSRN, text: true }, { header: 'Meter Method', get: o => o.meterMethod },
      { header: 'Connection Type', get: o => o.connectionType }, { header: 'Adres', get: o => o.connectionPointAddress },
      { header: 'CP Status', get: o => o.connectionPointStatus }, { header: 'Opp. Stage', get: o => o.opportunityStage },
      { header: 'Status', get: o => o.status },
      // Potentiële omzet ook uit de EXPORT houden voor rollen die de kolom niet zien.
      ...(canSeeOmzet ? [{ header: 'Potentiele omzet', get: o => o.totaal }] : []),
      { header: 'Delivery Start', get: o => o.deliveryStartDate }, { header: 'Effective Start', get: o => o.effectiveStartDate },
      { header: 'Delivery End', get: o => o.deliveryEndDate }, { header: 'Effective End', get: o => o.effectiveEndDate },
      { header: 'Product Groep', get: o => o.productGroep }, { header: 'Energy Product', get: o => o.energyProductGroup },
      { header: 'Ratecard', get: o => o.ratecardName }, { header: 'Segment', get: o => o.segment },
      { header: 'Team', get: o => o.salespersonTeam }, { header: 'Verkoper', get: o => o.salesperson },
      { header: 'Contract', get: o => o.contract },
      { header: 'Ambassador Code', get: o => o.ambassadorCode },
      { header: 'Referral Link', get: o => o.referralLinkName }, { header: 'Referrer', get: o => o.referrer },
      { header: 'Referral Amb. Code', get: o => o.referralAmbassadorCode }, { header: 'Customer Tags', get: o => zichtbareTagsTekst(o.partnerTags, visiblePartnerTags) },
      { header: 'Est. Offtake (kWh)', get: o => o.estOfftakeKwh }, { header: 'Est. Injection (kWh)', get: o => o.estInjectionKwh },
      { header: 'Customer Payment Status', get: o => getPaymentStatus(o.customerReference) },
      { header: 'Fee End Date', get: o => getFeeEndDate(o) }, { header: 'Fee Duration', get: o => getFeeDurationAdjusted(o) },
      { header: 'Fee Contracted', get: o => getContracted(o) }, { header: 'Fee Customer Tag', get: o => getFeePartnerTag(o.partnerTags) },
      { header: 'PriceAddition', get: o => getPriceAddition(o) },
      { header: 'PriceAddition Unit', get: o => o.priceAdditionsUnit },
      { header: 'Fee Subscription Yearly', get: o => getFeeSubscriptionYearly(o) },
      { header: 'Fee Subscription ProRata', get: o => getFeeSubscriptionProRata(o) },
      { header: 'Fee Subscription', get: o => getFeeSubscription(o) },
    ];
    // Onzichtbare overlap-tag enkel mee-exporteren voor admin/ET.
    if (canSeeOverlap) columns.push({ header: 'EAN Overlap', get: o => hasOverlapTag(o) ? 'Ja' : 'Nee' });
    return exportRowsToCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: "partnervergoeding-orders", scope: exportScope }), columns, sortedOrders, collect);
  };

  // Meld deze export aan bij de pagina, zodat "Export All" op Revenue dezelfde
  // kolommen en dezelfde gefilterde rijen wegschrijft als de knop hierboven — de
  // definitie staat op één plek. De ref houdt altijd de nieuwste functie vast, zodat
  // de registratie zelf niet bij elke render opnieuw hoeft te gebeuren.
  const exportOrdersRef = useRef(exportOrdersCsv);
  exportOrdersRef.current = exportOrdersCsv;
  useEffect(() => {
    if (!onRegisterExport) return undefined;
    onRegisterExport('orders', (collect) => exportOrdersRef.current(collect));
    return () => onRegisterExport('orders', null);
  }, [onRegisterExport]);

  return (
    <Card className="!p-4 !overflow-visible">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100 flex items-center gap-1.5">PARTNERVERGOEDING — DETAIL ORDERS</h3><ExportKnop onExport={() => exportOrdersCsv()} disabled={sortedOrders.length === 0} count={sortedOrders.length} label="Exporteer alle (gefilterde) orderregels incl. berekende fee-velden naar Excel/CSV" /></div>
      </div>
      <div className="mb-3 flex flex-wrap items-end gap-3 relative z-40">
        <div className="w-full sm:w-[280px] shrink-0">
          <label className="block text-xs text-emerald-400 mb-1">🔍 Zoeken</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-emerald-500" />
            <input type="text" placeholder="Klant, klantnummer, EAN, BTW, e-mail, telefoon, verkoper, adres, tag..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="w-full pl-7 pr-3 py-1.5 bg-emerald-900/30 border border-emerald-600/50 rounded text-xs text-slate-200 placeholder-slate-400 focus:border-emerald-500 focus:outline-none" />
          </div>
        </div>
        <SearchableSelect label="Market" size="sm" value={marketFilter} onChange={v => { setMarketFilter(v); setCurrentPage(1); }} options={markets} placeholder="Alle" />
        <SearchableSelect label="Customer Segment" size="sm" value={segmentFilter} onChange={v => { setSegmentFilter(v); setCurrentPage(1); }} options={segments} placeholder="Alle" />
        <SearchableSelect label="Connection Type" size="sm" value={connectionTypeFilter} onChange={v => { setConnectionTypeFilter(v); setCurrentPage(1); }} options={connectionTypes} placeholder="Alle" />
        <SearchableSelect label="Meter Method" size="sm" value={meterMethodFilter} onChange={v => { setMeterMethodFilter(v); setCurrentPage(1); }} options={meterMethods} placeholder="Alle" />
        <SearchableSelect label="Customer Payment Status" size="sm" value={paymentStatusFilter} onChange={v => { setPaymentStatusFilter(v); setCurrentPage(1); }} options={['Goed', 'Wanbetaler', ...(Object.keys(labelConfig).some(l => labelRecoveryMonths(labelConfig, l) < BAD_DEBT_RECOVERY_MONTHS_DEFAULT) ? [PAY_STATUS_PARTIAL] : [])]} placeholder="Alle" />
        <SearchableSelect label="Fee Customer Tag" value={feePartnerTagFilter} onChange={v => { setFeePartnerTagFilter(v); setCurrentPage(1); }} options={['Yes', 'No']} placeholder="Alle" />
        {canSeeOverlap && <div className="min-w-[132px]"><label className="block text-xs text-red-400 mb-1">⚠ EAN-overlap</label><select value={overlapFilter} onChange={e => { setOverlapFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${overlapFilter !== 'all' ? 'border-red-500 text-red-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="ja">Enkel overlap</option><option value="nee">Zonder overlap</option></select></div>}
        {(searchTerm || marketFilter !== 'all' || segmentFilter !== 'all' || connectionTypeFilter !== 'all' || meterMethodFilter !== 'all' || overlapFilter !== 'all' || paymentStatusFilter !== 'all' || feePartnerTagFilter !== 'all') && <button onClick={() => { setSearchTerm(''); setMarketFilter('all'); setSegmentFilter('all'); setConnectionTypeFilter('all'); setMeterMethodFilter('all'); setOverlapFilter('all'); setPaymentStatusFilter('all'); setFeePartnerTagFilter('all'); setCurrentPage(1); }} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}
      </div>
      {sortedOrders.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen orders voor de huidige filters.</div> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead className="bg-slate-800"><tr className="border-b border-slate-700">
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('company')}><span className="flex items-center gap-1">Bedrijf <SortIcon col="company" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('orderDate')}><span className="flex items-center gap-1">Orderdatum <SortIcon col="orderDate" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('customerReference')}><span className="flex items-center gap-1">Klantennummer <SortIcon col="customerReference" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('customer')}><span className="flex items-center gap-1">Klant <SortIcon col="customer" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Telefoon</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">E-mail</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Bank Account</th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('market')}><span className="flex items-center gap-1">Market <SortIcon col="market" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">EAN</th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('meterMethod')}><span className="flex items-center gap-1">Meter Method <SortIcon col="meterMethod" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('connectionType')}><span className="flex items-center gap-1">Connection Type <SortIcon col="connectionType" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Adres</th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('connectionPointStatus')}><span className="flex items-center gap-1">CP Status <SortIcon col="connectionPointStatus" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('opportunityStage')}><span className="flex items-center gap-1">Opp. Stage <SortIcon col="opportunityStage" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('status')}><span className="flex items-center gap-1">Status <SortIcon col="status" /></span></th>
            {canSeeOmzet && <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('totaal')}><span className="flex items-center gap-1 justify-end">Potentiële omzet <SortIcon col="totaal" /></span></th>}
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('deliveryStartDate')}><span className="flex items-center gap-1">Delivery Start <SortIcon col="deliveryStartDate" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Effective Start</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Delivery End</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Effective End</th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('productGroep')}><span className="flex items-center gap-1">Product Groep <SortIcon col="productGroep" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('energyProductGroup')}><span className="flex items-center gap-1">Energy Product <SortIcon col="energyProductGroup" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('ratecardName')}><span className="flex items-center gap-1">Ratecard <SortIcon col="ratecardName" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('segment')}><span className="flex items-center gap-1">Segment <SortIcon col="segment" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Team</th>
            <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('salesperson')}><span className="flex items-center gap-1">Verkoper <SortIcon col="salesperson" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Contract</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Ambassador Code</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Referral Link</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Referrer</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Referral Amb. Code</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Customer Tags</th>
            <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('estOfftakeKwh')}><span className="flex items-center gap-1 justify-end">Est. Offtake (kWh) <SortIcon col="estOfftakeKwh" /></span></th>
            <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('estInjectionKwh')}><span className="flex items-center gap-1 justify-end">Est. Injection (kWh) <SortIcon col="estInjectionKwh" /></span></th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Customer Payment Status</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Fee End Date</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Fee Duration</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Fee Contracted</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Fee Customer Tag</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">PriceAddition</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">PriceAddition Unit</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Fee Subscription_ Yearly</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Fee Subscription_ ProRataFee</th>
            <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Fee Subscription</th>
          </tr></thead>
          <tbody>{paginatedOrders.map(o => <tr key={o.id} onClick={() => setSelectedOrder(o)} onContextMenu={e => onC360Context(e, o.customer, klantTarget(o.customer, o.customerReference, undefined, [o.connectionPointGSRN]), undefined, orderEtLinks(o))} title={canSeeOverlap && hasOverlapTag(o) ? 'Overlappende leveringsperiode met een ander actief order op dezelfde EAN' : undefined} className={`border-b cursor-pointer ${canSeeOverlap && hasOverlapTag(o) ? 'bg-red-900/40 hover:bg-red-900/50 border-red-700/50 text-red-300' : getPaymentStatus(o.customerReference) === 'Wanbetaler' ? 'bg-red-900/30 hover:bg-red-900/40 border-red-800/40' : 'border-slate-700/30 hover:bg-slate-700/20'}`}>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.company}</td>
            <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs">{o.orderDate}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{o.customerReference}<KlantnrKopieerKnop nr={o.customerReference} /></td>
            <td className="py-2 px-2 truncate max-w-32 text-xs" title={o.customer ? `Open Customer 360° voor ${o.customer}` : ''}>
              {o.customer && onOpenCustomer360 ? (
                <button onClick={e => { e.stopPropagation(); onOpenCustomer360(klantTarget(o.customer, o.customerReference, undefined, [o.connectionPointGSRN])); }} className="text-slate-200 hover:text-lime-400 hover:underline cursor-pointer inline-flex items-center gap-1 max-w-full">
                  <Compass size={11} className="text-slate-500 shrink-0" />
                  <span className="truncate">{o.customer}</span>
                </button>
              ) : <span className="text-slate-200">{o.customer || ''}</span>}
            </td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.customerPhone}</td>
            <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-32" title={o.customerEmail}>{o.customerEmail}</td>
            <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.customerBankAccount}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.market}</td>
            <td className="py-2 px-2 text-xs font-mono whitespace-nowrap" title={o.connectionPointGSRN ? `Open Customer 360° voor EAN ${o.connectionPointGSRN}` : ''}>
              {o.connectionPointGSRN && onOpenCustomer360 ? (
                <button onClick={e => { e.stopPropagation(); onOpenCustomer360({ type: 'gsrn', value: o.connectionPointGSRN, label: o.customer ? `${o.customer} · ${o.connectionPointGSRN}` : o.connectionPointGSRN }); }} className="text-slate-500 hover:text-lime-400 hover:underline cursor-pointer">
                  {o.connectionPointGSRN}
                </button>
              ) : <span className="text-slate-500">{o.connectionPointGSRN || ''}</span>}
              <EanKopieerKnop ean={o.connectionPointGSRN} />
            </td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.meterMethod}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.connectionType}</td>
            <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-40" title={o.connectionPointAddress}>{o.connectionPointAddress}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.connectionPointStatus}</td>
            <td className="py-2 px-2"><OppStageBadge value={o.opportunityStage} /></td>
            <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-24">{o.status}</td>
            {canSeeOmzet && <td className="py-2 px-2 text-right text-lime-400 text-xs whitespace-nowrap font-medium">{o.totaal > 0 ? formatCurrency(o.totaal) : '—'}</td>}
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{o.deliveryStartDate}</td>
            <td className="py-2 px-2 text-slate-500 whitespace-nowrap text-xs">{o.effectiveStartDate}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{o.deliveryEndDate}</td>
            <td className="py-2 px-2 text-slate-500 whitespace-nowrap text-xs">{o.effectiveEndDate}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.productGroep}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.energyProductGroup}</td>
            <td className="py-2 px-2 text-slate-300 truncate max-w-32 text-xs" title={o.ratecardName}>{o.ratecardName}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium ${o.segment?.includes('B2C') ? 'bg-blue-900/50 text-blue-300' : o.segment?.includes('B2B') ? 'bg-lime-900/50 text-lime-300' : 'bg-purple-900/50 text-purple-300'}`}>{o.segment}</span></td>
            <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-20">{o.salespersonTeam}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{o.salesperson}</td>
            <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-24">{o.contract}</td>
            <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.ambassadorCode}</td>
            <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-24" title={o.referralLinkName}>{o.referralLinkName}</td>
            <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-24" title={o.referrer}>{o.referrer}</td>
            <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.referralAmbassadorCode}</td>
            <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-32" title={zichtbareTagsTekst(o.partnerTags, visiblePartnerTags)}>{zichtbareTagsTekst(o.partnerTags, visiblePartnerTags)}</td>
            <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{o.estOfftakeKwh ? formatNumber(o.estOfftakeKwh) : '—'}</td>
            <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{o.estInjectionKwh ? formatNumber(o.estInjectionKwh) : '—'}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cpsBadgeClass(getPaymentStatus(o.customerReference))}`}>{getPaymentStatus(o.customerReference)}</span></td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{getFeeEndDate(o)}</td>
            <td className={`py-2 px-2 text-xs whitespace-nowrap ${isFeeDurationAdjusted(o) ? 'text-amber-400 font-semibold' : 'text-slate-400'}`} title={isFeeDurationAdjusted(o) ? `Oninbaarheidsperiode wanbetalers: ${getFeeDuration(o)} − ${badDebtMonths(o.customerReference)} mnd` : undefined}>{getFeeDurationAdjusted(o)}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getContracted(o) === 'Yes' ? 'bg-lime-900/50 text-lime-300' : 'bg-slate-700 text-slate-300'}`}>{getContracted(o)}</span></td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getFeePartnerTag(o.partnerTags) === 'Yes' ? 'bg-lime-900/50 text-lime-300' : 'bg-red-900/50 text-red-300'}`}>{getFeePartnerTag(o.partnerTags)}</span></td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{getPriceAddition(o)}</td>
            <td className="py-2 px-2 text-xs whitespace-nowrap"><span className={`${isYearlyFeeUnit(o.priceAdditionsUnit) ? 'text-lime-300' : 'text-slate-400'} cursor-help underline decoration-dotted decoration-slate-600 underline-offset-2`} title={priceAdditionUnitTitle(o.priceAdditionsUnit)}>{o.priceAdditionsUnit || '—'}</span></td>
            <td className="py-2 px-2 text-lime-400 text-xs whitespace-nowrap">{getFeeSubscriptionYearly(o) > 0 ? formatCurrency(getFeeSubscriptionYearly(o)) : '—'}</td>
            <td className="py-2 px-2 text-lime-400 text-xs whitespace-nowrap">{getFeeSubscriptionProRata(o) > 0 ? formatCurrency(getFeeSubscriptionProRata(o)) : '—'}</td>
            <td className="py-2 px-2 text-lime-400 text-xs whitespace-nowrap font-medium">{getFeeSubscription(o) > 0 ? formatCurrency(getFeeSubscription(o)) : '—'}</td>
          </tr>)}</tbody>
        </table>
      </div>}
      {sortedOrders.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((pageClamped - 1) * pageSize) + 1} - {Math.min(pageClamped * pageSize, sortedOrders.length)} van {sortedOrders.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={pageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{pageClamped} / {totalPages || 1}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={pageClamped === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
      {c360Menu}
    </Card>
  );
};

// Waarschuwingstabel (ENKEL admin + ET Dossierbeheerder): GSRN's waarvoor wél een
// partnervergoeding-factuur bestaat, maar die NIET voorkomen in "PARTNERVERGOEDING —
// DETAIL ORDERS" hierboven (d.w.z. geen actief contracted order op die GSRN binnen
// de huidige filters). Signaleert een mogelijke commissie-leak of order-mismatch.
// Klik op de GSRN/klant of rechtsklik op de rij → Customer 360.
const MissingCommissionGsrnTable = ({ orders = [], facturen = [], onOpenCustomer360, currentUser }) => {
  const canSee = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = TABLE_PAGE_SIZE;

  // GSRN's die in de DETAIL ORDERS-tabel staan = GSRN's van de contracted orders
  // (exact dezelfde definitie als die tabel, via de module-level isContractedOrder).
  const orderGsrns = useMemo(() => {
    const s = new Set();
    for (const o of orders) {
      if (!isContractedOrder(o)) continue;
      const g = (o.connectionPointGSRN || '').trim();
      if (g) s.add(g);
    }
    return s;
  }, [orders]);

  // Gefactureerde GSRN's (uit de partnervergoeding-records, EL + NG) die NIET in die
  // set zitten. Dedup per GSRN; tel hoeveel facturen ernaar verwijzen.
  const missingRows = useMemo(() => {
    if (!canSee) return [];
    const byGsrn = new Map();
    const add = (gsrn, type, f) => {
      const g = (gsrn || '').trim();
      if (!g || orderGsrns.has(g)) return;
      let e = byGsrn.get(g);
      if (!e) {
        e = { gsrn: g, type, klant: f.relatieNaam || '', klantnummer: f.relatieReferentie || '', label: f.relatieBedrijf || '', verkoper: f.verkoper || '', team: f.verkoopteam || '', soort: f.soort || '', status: f.statusBetaling || '', nummer: f.nummer || '', count: 0 };
        byGsrn.set(g, e);
      }
      e.count++;
      if (!e.klant && f.relatieNaam) e.klant = f.relatieNaam;
      if (!e.klantnummer && f.relatieReferentie) e.klantnummer = f.relatieReferentie;
      if (!e.verkoper && f.verkoper) e.verkoper = f.verkoper;
    };
    for (const f of facturen) { add(f.elGSRN, 'EL', f); add(f.ngGSRN, 'NG', f); }
    return [...byGsrn.values()].sort((a, b) => b.count - a.count || a.gsrn.localeCompare(b.gsrn));
  }, [facturen, orderGsrns, canSee]);

  const filtered = useMemo(() => {
    if (!searchTerm) return missingRows;
    const t = searchTerm.toLowerCase();
    return missingRows.filter(r => [r.gsrn, r.klant, r.klantnummer, r.label, r.verkoper, r.team, r.nummer].some(v => String(v || '').toLowerCase().includes(t)));
  }, [missingRows, searchTerm]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageClamped = Math.min(currentPage, Math.max(1, totalPages));
  const mcgSort = useTableSort();
  const paginated = useMemo(() => { const s = (pageClamped - 1) * pageSize; return sortRows(filtered, mcgSort.sortConfig).slice(s, s + pageSize); }, [filtered, pageClamped, pageSize, mcgSort.sortConfig]);

  const exportCsv = () => {
    const columns = [
      { header: 'EAN', get: r => r.gsrn, text: true }, { header: 'Type', get: r => r.type },
      { header: 'Klant', get: r => r.klant }, { header: 'Klantnummer', get: r => r.klantnummer },
      { header: 'Energielabel', get: r => r.label }, { header: 'Verkoper', get: r => r.verkoper },
      { header: 'Team', get: r => r.team }, { header: 'Soort', get: r => r.soort },
      { header: 'Status betaling', get: r => r.status }, { header: 'Aantal facturen', get: r => r.count },
      { header: 'Voorbeeld factuurnr', get: r => r.nummer },
    ];
    exportRowsToCsv('partnervergoeding-gsrn-zonder-order.csv', columns, filtered);
  };

  if (!canSee) return null;

  return (
    <Card className="!p-4 !overflow-visible border border-amber-600/40">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-amber-300 flex items-center gap-1.5"><AlertCircle size={18} />EAN MET FACTUUR ZONDER ORDER</h3><ExportKnop onExport={exportCsv} disabled={filtered.length === 0} count={filtered.length} label="Exporteer alle (gefilterde) rijen naar Excel/CSV" /></div>
      </div>
      <div className="mb-3 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-200/90">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-amber-400" />
        <span>Deze EAN's worden <strong>wél gefactureerd</strong> (er bestaat een partnervergoeding-factuur) maar komen <strong>niet voor</strong> in "PARTNERVERGOEDING — DETAIL ORDERS" hierboven — er is geen actief <em>contracted</em> order op die EAN binnen de huidige filters. Mogelijk ontbreekt commissie of is er een order-mismatch. Klik op de EAN of klant, of rechtsklik op een rij, om naar Customer 360 te gaan.</span>
      </div>
      {missingRows.length > 0 && <div className="mb-3 max-w-md">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-500" />
          <input type="text" placeholder="EAN, klant, klantnummer, verkoper, factuurnr..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="w-full pl-7 pr-3 py-1.5 bg-amber-900/20 border border-amber-600/40 rounded text-xs text-slate-200 placeholder-slate-400 focus:border-amber-500 focus:outline-none" />
        </div>
      </div>}
      {filtered.length === 0 ? (
        <div className="text-center py-6 text-sm text-emerald-300/80 flex items-center justify-center gap-2"><Check size={16} />{missingRows.length === 0 ? 'Alle gefactureerde EAN’s komen voor in de detail orders. Geen mismatch.' : 'Geen resultaten voor deze zoekterm.'}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead className="bg-slate-800"><tr className="border-b border-slate-700">
              <SortHeader label="EAN" k="gsrn" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Type" k="type" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Klant" k="klant" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Klantnummer" k="klantnummer" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Energielabel" k="label" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Verkoper" k="verkoper" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Team" k="team" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Soort" k="soort" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="Status" k="status" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
              <SortHeader label="# facturen" k="count" align="right" sortConfig={mcgSort.sortConfig} onSort={mcgSort.handleSort} />
            </tr></thead>
            <tbody>{paginated.map(r => <tr key={r.gsrn} onContextMenu={e => onC360Context(e, r.klant || r.gsrn, { type: 'gsrn', value: r.gsrn, label: r.klant ? `${r.klant} · ${r.gsrn}` : r.gsrn })} className="border-b border-amber-800/20 hover:bg-amber-900/10">
              <td className="py-2 px-2 text-xs font-mono whitespace-nowrap" title={`Open Customer 360° voor EAN ${r.gsrn}`}>
                {onOpenCustomer360 ? (
                  <button onClick={e => { e.stopPropagation(); onOpenCustomer360({ type: 'gsrn', value: r.gsrn, label: r.klant ? `${r.klant} · ${r.gsrn}` : r.gsrn }); }} className="text-amber-300 hover:text-lime-400 hover:underline cursor-pointer">{r.gsrn}</button>
                ) : <span className="text-amber-300">{r.gsrn}</span>}
              </td>
              <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.type === 'EL' ? 'bg-blue-900/50 text-blue-300' : 'bg-orange-900/50 text-orange-300'}`}>{r.type}</span></td>
              <td className="py-2 px-2 truncate max-w-40 text-xs" title={r.klant ? `Open Customer 360° voor ${r.klant}` : ''}>
                {r.klant && onOpenCustomer360 ? (
                  <button onClick={e => { e.stopPropagation(); onOpenCustomer360(klantTarget(r.klant, r.klantnummer)); }} className="text-slate-200 hover:text-lime-400 hover:underline cursor-pointer inline-flex items-center gap-1 max-w-full"><Compass size={11} className="text-slate-500 shrink-0" /><span className="truncate">{r.klant}</span></button>
                ) : <span className="text-slate-500">{r.klant || '—'}</span>}
              </td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{r.klantnummer}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{r.label}</td>
              <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{r.verkoper}</td>
              <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-24">{r.team}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{r.soort}</td>
              <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${r.status === 'Betaald' ? 'bg-lime-900/50 text-lime-300' : 'bg-slate-700 text-slate-300'}`}>{r.status}</span></td>
              <td className="py-2 px-2 text-right text-slate-300 text-xs">{r.count}</td>
            </tr>)}</tbody>
          </table>
        </div>
      )}
      {filtered.length > pageSize && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((pageClamped - 1) * pageSize) + 1} - {Math.min(pageClamped * pageSize, filtered.length)} van {filtered.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={pageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-amber-500 text-slate-900 font-semibold text-sm rounded-lg">{pageClamped} / {totalPages || 1}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={pageClamped === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
      {c360Menu}
    </Card>
  );
};

// ── Verkopersvergoeding op maat ──────────────────────────────────────────────
// Elk energielabel rekent zijn verkopersvergoeding anders af dan Energy Together.
// Voor labels met zo'n eigen methodiek verschijnt op Revenue een eigen tab
// ("Commissies <label>"); staat een label niet in dit register, dan is er geen
// tab. De methodiek zelf is puur rekenwerk en staat in src/customFee.js, zodat ze
// na te rekenen is.
//
// De tab-id's staan ÓÓK in de subpagina-catalogus (src/subPages.js en
// backend/src/config/subpages.js): daar bepaalt een beheerder per gebruiker wie de
// pagina ziet. Standaard aan voor een master, uit voor teamleader en verkoper.
// Hernoem je hier een tab-id, hernoem het daar mee — anders valt de pagina stil
// weg uit ieders menu.
//
// Bewerken van de tarieven blijft admin-only: deze getallen bepalen een
// uitbetaling, dus een partner hoort zijn eigen vergoeding niet bij te stellen.
const CUSTOM_FEE_PAGINAS = [
  { label: 'Servolt Energie', tab: 'commissies-servolt', titel: 'Commissies Servolt' },
  { label: 'Evident Energie', tab: 'commissies-evident', titel: 'Commissies Evident' },
  // Hetzelfde cijfer, andere weergave: een verkoopdashboard met de projectie erbij.
  // Ook voor teamleader en verkoper toe te kennen, maar standaard voor iedereen uit.
  { label: 'Evident Energie', tab: 'commissie-overzicht-evident', titel: 'Commissie overzicht' },
];
const customFeePaginas = (label) => CUSTOM_FEE_PAGINAS.filter(p => p.label === label);

// Eén getal in de tarieventabel. Smal en zonder eigen label: de betekenis komt uit
// de rij en de kolom eromheen, zodat de hele tabel op één kaart past.
const TariefInput = ({ waarde, onChange, readOnly }) => (
  <input type="number" min="0" step="any" value={waarde} disabled={readOnly}
    onChange={e => onChange(e.target.value)}
    className="w-14 px-1.5 py-0.5 bg-slate-700/60 border border-slate-600/50 rounded text-right text-slate-100 text-xs disabled:opacity-60 disabled:cursor-not-allowed focus:border-lime-500 focus:outline-none" />
);

// De volledige pagina "Commissies Servolt" (tot 18-08-2026 "Custom made").
//
// Twee tabellen, allebei per maand en per verkoper:
//   1. VERGOEDING AANTAL EAN — elke EAN die in de gekozen maand gestart is, met
//      zijn categorie (op Est. Offtake) en de bijbehorende vaste vergoeding, plus
//      de eenmalige bonus als de klant twaalf maanden actief bleef. Daaronder de
//      optelling per categorie.
//   2. VERGOEDING GEFACTUREERDE KWH — dezelfde kolommen als "Vergoeding per
//      verkoper" op de tab Per Verkoper(team), maar over de facturen van de maand
//      VÓÓR de gekozen maand (zo spreken de volumes en de activaties elkaar niet
//      tegen: een EAN die in mei start, factureert pas erna).
const ServoltCustomFee = ({ orders, facturenGoed, selfBillsGoed, calcSummaryStats, token, energielabel, currentUser, onRegisterExport, exportStamp, exportLabel = '', exportScope = '' }) => {
  const magBewerken = currentUser?.role === 'admin';
  const [tarieven, setTarieven] = useState(SERVOLT_DEFAULT_TARIEVEN);
  const [concept, setConcept] = useState(SERVOLT_DEFAULT_TARIEVEN);
  const [bewaren, setBewaren] = useState(false);
  const [fout, setFout] = useState('');
  const [verlengingenTellen, setVerlengingenTellen] = useState(true);
  const [verkoper, setVerkoper] = useState('all');

  // Maanden waarin er een EAN gestart is, nieuwste eerst. Toekomstige maanden
  // horen erbij (er staan contracten met een startdatum in de toekomst), maar de
  // pagina opent op de laatste maand die al begonnen is — daar gaat een afrekening
  // over.
  const maanden = useMemo(() => {
    const s = new Set();
    for (const o of orders || []) { const m = maandVan(orderStartDatum(o)); if (m) s.add(m); }
    return [...s].sort().reverse();
  }, [orders]);
  const [maand, setMaand] = useState('');
  useEffect(() => {
    if (maand || !maanden.length) return;
    const nu = new Date().toISOString().slice(0, 7);
    setMaand(maanden.find(m => m <= nu) || maanden[maanden.length - 1]);
  }, [maanden, maand]);

  useEffect(() => {
    if (!token || !energielabel) return;
    let af = false;
    fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (af || !d) return; const t = normaliseerTarieven(d.settings); setTarieven(t); setConcept(t); })
      .catch(() => { /* niet kritisch: de standaardtarieven blijven staan */ });
    return () => { af = true; };
  }, [token, energielabel]);

  const bewaarTarieven = async () => {
    setBewaren(true); setFout('');
    try {
      const r = await fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: concept }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      const t = normaliseerTarieven(d.settings);
      setTarieven(t); setConcept(t);
      toast.success('Tarieven opgeslagen.');
    } catch (e) { setFout(e.message); }
    finally { setBewaren(false); }
  };

  const verkopers = useMemo(() => [...new Set((orders || []).map(o => o.salesperson).filter(Boolean))].sort(), [orders]);

  const eanRijen = useMemo(() => bouwEanRijen(orders, {
    maand, verkoper: verkoper === 'all' ? null : verkoper, tarieven, verlengingenTellen,
  }), [orders, maand, verkoper, tarieven, verlengingenTellen]);
  const catTotalen = useMemo(() => feePerCategorie(eanRijen, tarieven), [eanRijen, tarieven]);

  // ── Tabel 2: gefactureerde kWh van de maand ervóór ─────────────────────────
  const factuurMaand = useMemo(() => vorigeMaand(maand), [maand]);
  const kwhRijen = useMemo(() => {
    if (!factuurMaand) return [];
    const norm = (v) => { const n = (v || '').trim(); return (!n || n === 'OdooBot') ? 'Online' : n; };
    const inMaand = (f) => String(f.factuurdatum || '').startsWith(factuurMaand);
    const fBy = {}; const sbBy = {};
    (facturenGoed || []).filter(inMaand).forEach(f => { const s = norm(f.verkoper); (fBy[s] || (fBy[s] = [])).push(f); });
    (selfBillsGoed || []).filter(inMaand).forEach(f => { const s = norm(f.verkoper); (sbBy[s] || (sbBy[s] = [])).push(f); });
    const namen = new Set([...Object.keys(fBy), ...Object.keys(sbBy)]);
    const rows = [...namen]
      .filter(s => verkoper === 'all' || norm(verkoper) === s)
      .map(s => {
        const stats = calcSummaryStats(fBy[s] || [], {}, sbBy[s] || []);
        const kwh = (l) => (stats.find(r => r.label === l)?.kwhOfftake) || 0;
        return {
          verkoper: s,
          kwhB2cEl: kwh('B2C - EL'), kwhB2bEl: kwh('B2B - EL'),
          kwhCustomElOfftake: kwh('Custom - EL Offtake'), kwhCustomElInjection: kwh('Custom - EL Injection'),
          kwhB2cGas: kwh('B2C - GAS'), kwhB2bGas: kwh('B2B - GAS'), kwhCustomGas: kwh('Custom - GAS'),
        };
      });
    const tot = (r) => r.kwhB2cEl + r.kwhB2bEl + r.kwhCustomElOfftake + r.kwhCustomElInjection + r.kwhB2cGas + r.kwhB2bGas + r.kwhCustomGas;
    rows.sort((a, b) => tot(b) - tot(a) || a.verkoper.localeCompare(b.verkoper));
    return rows;
  }, [facturenGoed, selfBillsGoed, calcSummaryStats, factuurMaand, verkoper]);
  const kwhTotalen = useMemo(() => kwhRijen.reduce((a, r) => ({
    kwhB2cEl: a.kwhB2cEl + r.kwhB2cEl, kwhB2bEl: a.kwhB2bEl + r.kwhB2bEl,
    kwhCustomElOfftake: a.kwhCustomElOfftake + r.kwhCustomElOfftake, kwhCustomElInjection: a.kwhCustomElInjection + r.kwhCustomElInjection,
    kwhB2cGas: a.kwhB2cGas + r.kwhB2cGas, kwhB2bGas: a.kwhB2bGas + r.kwhB2bGas, kwhCustomGas: a.kwhCustomGas + r.kwhCustomGas,
  }), { kwhB2cEl: 0, kwhB2bEl: 0, kwhCustomElOfftake: 0, kwhCustomElInjection: 0, kwhB2cGas: 0, kwhB2bGas: 0, kwhCustomGas: 0 }), [kwhRijen]);

  // ── Exports ────────────────────────────────────────────────────────────────
  // Zelfde naamgeving als de andere Revenue-exports: tijdstempel, dan het
  // energielabel met de scope (verkoopteam/verkoper) van de paginafilters erachter,
  // dan waar het bestand over gaat.
  const naam = (basis) => exportBestandsnaam({ stamp: exportStamp ? exportStamp() : "", label: exportLabel, scope: exportScope, basis });
  const eanKolommen = [
    { header: 'Startdatum', get: r => r.start }, { header: 'EAN', get: r => r.gsrn },
    { header: 'Klant', get: r => r.klant }, { header: 'Klantnummer', get: r => r.klantnummer },
    { header: 'Verkoper', get: r => r.verkoper }, { header: 'Verkoopteam', get: r => r.verkoopteam },
    { header: 'Markt', get: r => r.markt }, { header: 'Segment', get: r => r.segment },
    { header: 'Soort', get: r => r.soort }, { header: 'Est. Offtake (MWh)', get: r => r.estMwh.toFixed(2) },
    { header: 'Categorie', get: r => r.categorie }, { header: 'Activatie', get: r => r.activatie },
    { header: 'Bonus 12m', get: r => r.bonus }, { header: 'Totaal', get: r => r.totaal },
  ];
  const catKolommen = [
    { header: 'Categorie', get: r => r.categorie }, { header: 'Tarief', get: r => r.tarief },
    { header: 'Aantal EAN', get: r => r.aantal }, { header: 'Est. Offtake (MWh)', get: r => Math.round(r.mwh) },
    { header: 'Activatie', get: r => r.activatie }, { header: 'Bonus 12m', get: r => r.bonus },
    { header: 'Totaal', get: r => r.totaal },
  ];
  const kwhKolommen = [
    { header: 'Verkoper', get: r => r.verkoper },
    { header: 'Kwh_B2C_El', get: r => Math.round(r.kwhB2cEl) }, { header: 'Kwh_B2B_EL', get: r => Math.round(r.kwhB2bEl) },
    { header: 'Kwh_Custom_El Offtake', get: r => Math.round(r.kwhCustomElOfftake) },
    { header: 'Kwh_Custom_El Injection', get: r => Math.round(r.kwhCustomElInjection) },
    { header: 'Kwh_B2C_Gas', get: r => Math.round(r.kwhB2cGas) }, { header: 'Kwh_B2B_Gas', get: r => Math.round(r.kwhB2bGas) },
    { header: 'Kwh_Custom_Gas', get: r => Math.round(r.kwhCustomGas) },
  ];
  const exportEan = () => exportRowsToCsv(naam(`vergoeding-aantal-ean-${maand}`), eanKolommen, eanRijen);
  const exportCat = () => exportRowsToCsv(naam(`vergoeding-per-categorie-${maand}`), catKolommen, catTotalen.rows);
  const exportKwh = () => exportRowsToCsv(naam(`vergoeding-gefactureerde-kwh-${factuurMaand}`), kwhKolommen, kwhRijen);
  // Meld de tabellen aan bij "Export All", zoals de andere Revenue-tabellen.
  useEffect(() => {
    if (!onRegisterExport) return undefined;
    const af = [
      onRegisterExport(`vergoeding-aantal-ean-${maand}`, () => ({ columns: eanKolommen, rows: eanRijen })),
      onRegisterExport(`vergoeding-per-categorie-${maand}`, () => ({ columns: catKolommen, rows: catTotalen.rows })),
      onRegisterExport(`vergoeding-gefactureerde-kwh-${factuurMaand}`, () => ({ columns: kwhKolommen, rows: kwhRijen })),
    ];
    return () => af.forEach(f => typeof f === 'function' && f());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maand, factuurMaand, eanRijen, catTotalen, kwhRijen]);

  const euro = (n) => formatEuroRounded(n);
  // DD/MM/YYYY zoals overal op het platform (zie CLAUDE.md).
  const datumBE = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
  const maandLabel = (m) => (m ? new Intl.DateTimeFormat('nl-BE', { month: 'long', year: 'numeric' }).format(new Date(`${m}-01T00:00:00`)) : '—');

  return (
    <div className="space-y-4">
      {/* Keuzes: maand + verkoper. De maand stuurt beide tabellen; de kWh-tabel
          kijkt naar de maand ervóór (zie de uitleg bij die tabel). */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Maand (start EAN)</label>
            <select value={maand} onChange={e => setMaand(e.target.value)}
              className="px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
              {maanden.map(m => <option key={m} value={m}>{maandLabel(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Verkoper</label>
            <select value={verkoper} onChange={e => setVerkoper(e.target.value)}
              className="px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
              <option value="all">Alle verkopers</option>
              {verkopers.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300 pb-2 cursor-pointer">
            <input type="checkbox" checked={verlengingenTellen} onChange={e => setVerlengingenTellen(e.target.checked)} className="w-4 h-4 accent-lime-500" />
            Verlengingen tellen mee voor de activatievergoeding
          </label>
        </div>
      </Card>
      {/* De optelling per categorie met de tarieven ernaast: links het bedrag waar
          het om gaat, rechts de knoppen waarmee dat bedrag tot stand komt. Onder
          `xl` stapelen ze, want naast elkaar wordt de tabel daar te smal. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="!p-4 xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0"><h3 className="text-base font-semibold text-slate-100">TOTALE FEE PER CATEGORIE</h3><ExportKnop onExport={exportCat} label="Exporteer deze tabel naar Excel" /></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm resp-card">
              <thead><tr className="border-b border-slate-700">
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Categorie</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Tarief</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Aantal EAN</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Est. Offtake (MWh)</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Activatie</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Bonus 12m</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Totaal</th>
              </tr></thead>
              <tbody>{catTotalen.rows.map(r => (
                <tr key={r.categorie} className="border-b border-slate-700/30">
                  <td className="py-2 px-2 text-slate-200 text-xs" data-label="Categorie">Categorie {r.categorie}</td>
                  <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums" data-label="Tarief">{euro(r.tarief)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Aantal EAN">{r.aantal}</td>
                  <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums" data-label="Est. Offtake (MWh)">{formatNumberRounded(r.mwh)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Activatie">{euro(r.activatie)}</td>
                  <td className="py-2 px-2 text-right text-cyan-300 text-xs tabular-nums" data-label="Bonus 12m">{euro(r.bonus)}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-medium" data-label="Totaal">{euro(r.totaal)}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr className="border-t-2 border-slate-600 bg-lime-900/20">
                <td className="py-2 px-2 text-lime-400 text-xs font-semibold" colSpan="2">Totaal</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs font-semibold tabular-nums">{catTotalen.totaal.aantal}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(catTotalen.totaal.mwh)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro(catTotalen.totaal.activatie)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro(catTotalen.totaal.bonus)}</td>
                <td className="py-2 px-2 text-right text-lime-400 text-xs font-bold tabular-nums">{euro(catTotalen.totaal.totaal)}</td>
              </tr></tfoot>
            </table>
          </div>
        </Card>

        {/* Tarieven: één regel per categorie, met elektriciteit en gas naast elkaar.
            Elk getal heeft precies één invoerveld; waar dezelfde grens een tweede
            keer voorkomt (de bovengrens van categorie 1 is de ondergrens van 2)
            staat ze als tekst, zodat er nooit twee velden dezelfde waarde bewaken.
            Bewerkbaar door admin; master en ET-dossierbeheerder lezen mee. */}
        <Card className="!p-4">
          <h3 className="text-base font-semibold text-slate-100 mb-1">TARIEVEN</h3>
          <p className="text-xs text-slate-400 mb-3">
            Vast bedrag per geactiveerde EAN, op geschat jaarverbruik (Est. Offtake). De grens hoort bij de hogere categorie.
            {!magBewerken && ' Alleen een beheerder kan dit wijzigen.'}
          </p>
          {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm mb-3">{fout}</div>}
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-700/60">
              <th className="py-1.5 pr-2 text-left text-slate-400 font-medium">Cat.</th>
              <th className="py-1.5 px-1 text-center text-slate-400 font-medium">Elek (MWh)</th>
              <th className="py-1.5 px-1 text-center text-slate-400 font-medium">Gas (MWh)</th>
              <th className="py-1.5 pl-2 text-right text-slate-400 font-medium">Vergoeding</th>
            </tr></thead>
            <tbody>
              <tr className="border-b border-slate-700/30">
                <td className="py-1.5 pr-2 text-slate-300">1</td>
                <td className="py-1.5 px-1 text-center whitespace-nowrap"><span className="text-slate-500 mr-1">&lt;</span><TariefInput waarde={concept.elT1} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, elT1: v }))} /></td>
                <td className="py-1.5 px-1 text-center whitespace-nowrap"><span className="text-slate-500 mr-1">&lt;</span><TariefInput waarde={concept.ngT1} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, ngT1: v }))} /></td>
                <td className="py-1.5 pl-2 text-right whitespace-nowrap"><TariefInput waarde={concept.cat1} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, cat1: v }))} /><span className="text-slate-500 ml-1">€</span></td>
              </tr>
              <tr className="border-b border-slate-700/30">
                <td className="py-1.5 pr-2 text-slate-300">2</td>
                <td className="py-1.5 px-1 text-center whitespace-nowrap"><span className="text-slate-500 mr-1">{concept.elT1}–</span><TariefInput waarde={concept.elT2} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, elT2: v }))} /></td>
                <td className="py-1.5 px-1 text-center whitespace-nowrap"><span className="text-slate-500 mr-1">{concept.ngT1}–</span><TariefInput waarde={concept.ngT2} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, ngT2: v }))} /></td>
                <td className="py-1.5 pl-2 text-right whitespace-nowrap"><TariefInput waarde={concept.cat2} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, cat2: v }))} /><span className="text-slate-500 ml-1">€</span></td>
              </tr>
              <tr>
                <td className="py-1.5 pr-2 text-slate-300">3</td>
                <td className="py-1.5 px-1 text-center text-slate-400 whitespace-nowrap">&gt; {concept.elT2}</td>
                <td className="py-1.5 px-1 text-center text-slate-400 whitespace-nowrap">&gt; {concept.ngT2}</td>
                <td className="py-1.5 pl-2 text-right whitespace-nowrap"><TariefInput waarde={concept.cat3} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, cat3: v }))} /><span className="text-slate-500 ml-1">€</span></td>
              </tr>
            </tbody>
          </table>
          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-700/60 text-xs">
            <span className="text-slate-400">Bonus na 12 maanden actief</span>
            <span className="whitespace-nowrap"><TariefInput waarde={concept.bonus12m} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, bonus12m: v }))} /><span className="text-slate-500 ml-1">€</span></span>
          </div>
          {magBewerken && (
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setConcept(tarieven)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg">Herstel</button>
              <button onClick={bewaarTarieven} disabled={bewaren} className="px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-60 text-slate-900 text-xs font-semibold rounded-lg">Opslaan</button>
            </div>
          )}
        </Card>
      </div>

      {/* De onderliggende regels bij de optelling hierboven. */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0"><h3 className="text-base font-semibold text-slate-100">VERGOEDING AANTAL EAN</h3><ExportKnop onExport={exportEan} disabled={eanRijen.length === 0} label="Exporteer deze tabel naar Excel" /></div>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Elke EAN die in {maandLabel(maand)} gestart is — effectieve startdatum, of de geplande leveringsstart als die ontbreekt.
          Verlengingen en productwissels staan erbij en zijn herkenbaar aan de kolom Soort.
        </p>
        {eanRijen.length === 0 ? <TableEmpty message="Geen EAN's gestart in deze maand." icon={Folder} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm resp-card">
              <thead><tr className="border-b border-slate-700">
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Start</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Klant</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">EAN</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Verkoper</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Markt</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Soort</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Est. Offtake (MWh)</th>
                <th className="py-2 px-2 text-center text-slate-400 text-xs">Cat.</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Activatie</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs" title="Eenmalig, als de klant twaalf maanden actief bleef">Bonus 12m</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Totaal</th>
              </tr></thead>
              <tbody>{eanRijen.map(r => (
                <tr key={`${r.gsrn}|${r.start}`} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap" data-label="Start">{datumBE(r.start)}</td>
                  <td className="py-2 px-2 text-slate-200 text-xs" data-label="Klant">{r.klant}</td>
                  <td className="py-2 px-2 text-slate-500 text-xs font-mono" data-label="EAN">{r.gsrn}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs" data-label="Verkoper">{r.verkoper || '—'}</td>
                  <td className="py-2 px-2 text-xs" data-label="Markt"><span className={`px-1.5 py-0.5 rounded ${r.markt === 'NG' ? 'bg-amber-900/40 text-amber-300' : 'bg-sky-900/40 text-sky-300'}`}>{r.markt}</span></td>
                  <td className="py-2 px-2 text-xs" data-label="Soort"><span className={`px-1.5 py-0.5 rounded ${r.soort === 'Nieuw' ? 'bg-lime-900/40 text-lime-300' : 'bg-slate-700 text-slate-300'}`}>{r.soort}</span></td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Est. Offtake (MWh)">{r.estMwh.toFixed(1)}</td>
                  <td className="py-2 px-2 text-center text-slate-300 text-xs" data-label="Cat.">{r.categorie}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Activatie">{euro(r.activatie)}</td>
                  <td className="py-2 px-2 text-right text-cyan-300 text-xs tabular-nums" data-label="Bonus 12m">{r.bonus ? euro(r.bonus) : '—'}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-medium" data-label="Totaal">{euro(r.totaal)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Gefactureerde volumes van de maand ervóór. */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0"><h3 className="text-base font-semibold text-slate-100">VERGOEDING GEFACTUREERDE KWH</h3><ExportKnop onExport={exportKwh} disabled={kwhRijen.length === 0} label="Exporteer deze tabel naar Excel" /></div>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Gefactureerde volumes van goede klanten met factuurdatum in {maandLabel(factuurMaand)} — de maand vóór de gekozen startmaand.
          Zelfde kolommen als “Vergoeding per verkoper”, maar beperkt tot die ene factuurmaand.
        </p>
        {kwhRijen.length === 0 ? <TableEmpty message="Geen gefactureerde volumes in deze maand." icon={Folder} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm resp-card">
              <thead><tr className="border-b border-slate-700">
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Verkoper</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">B2C EL</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">B2B EL</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Custom EL Offtake</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Custom EL Injection</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">B2C Gas</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">B2B Gas</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Custom Gas</th>
              </tr></thead>
              <tbody>{kwhRijen.map(r => (
                <tr key={r.verkoper} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="py-2 px-2 text-slate-200 text-xs" data-label="Verkoper">{r.verkoper}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="B2C EL">{formatNumberRounded(r.kwhB2cEl)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="B2B EL">{formatNumberRounded(r.kwhB2bEl)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Custom EL Offtake">{formatNumberRounded(r.kwhCustomElOfftake)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Custom EL Injection">{formatNumberRounded(r.kwhCustomElInjection)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="B2C Gas">{formatNumberRounded(r.kwhB2cGas)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="B2B Gas">{formatNumberRounded(r.kwhB2bGas)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Custom Gas">{formatNumberRounded(r.kwhCustomGas)}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr className="border-t-2 border-slate-600 bg-lime-900/20">
                <td className="py-2 px-2 text-lime-400 text-xs font-semibold">Totaal</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(kwhTotalen.kwhB2cEl)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(kwhTotalen.kwhB2bEl)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(kwhTotalen.kwhCustomElOfftake)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(kwhTotalen.kwhCustomElInjection)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(kwhTotalen.kwhB2cGas)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(kwhTotalen.kwhB2bGas)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(kwhTotalen.kwhCustomGas)}</td>
              </tr></tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* De afspraak zelf, als PDF — zelfde blok als op de andere commissiepagina. */}
      <CommissieDocumenten token={token} energielabel={energielabel} />
    </div>
  );
};

// ── Documenten bij een commissieafspraak ─────────────────────────────────────
// De PDF's waarin staat wat een verkoper verdient, bij de pagina waar die
// bedragen uitgerekend worden. Lezen/openen mag iedereen die de pagina ziet
// (admin, ET-dossierbeheerder en de master van dat label); toevoegen en
// verwijderen is beheerderswerk — dit is het stuk waar een uitbetaling op
// teruggaat. De server bewaakt dat allebei opnieuw (routes/custom-fee.js).
const CommissieDocumenten = ({ token, energielabel }) => {
  const [docs, setDocs] = useState([]);
  const [magBeheren, setMagBeheren] = useState(false);
  const [laden, setLaden] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');
  const bestandRef = useRef(null);

  const laad = useMemo(() => async () => {
    if (!token || !energielabel) return;
    setLaden(true);
    try {
      const r = await fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}/docs`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      setDocs(Array.isArray(d.docs) ? d.docs : []);
      setMagBeheren(!!d.mag_beheren);
      setFout('');
    } catch (e) { setFout(e.message); }
    finally { setLaden(false); }
  }, [token, energielabel]);
  useEffect(() => { laad(); }, [laad]);

  const upload = async (file) => {
    if (!file) return;
    setBezig(true); setFout('');
    try {
      const body = new FormData();
      body.append('file', file);
      const r = await fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}/docs`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      toast.success(`${file.name} toegevoegd.`);
      await laad();
    } catch (e) { setFout(e.message); toast.error(`Uploaden mislukt: ${e.message}`); }
    finally { setBezig(false); if (bestandRef.current) bestandRef.current.value = ''; }
  };

  // Openen gaat via fetch + blob en niet via een gewone link: het token zit in
  // localStorage, niet in een cookie, dus een <a href> zou zonder Authorization
  // vertrekken en een 401 opleveren.
  const open = async (d) => {
    try {
      const r = await fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}/docs/${d.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const url = URL.createObjectURL(await r.blob());
      const w = window.open(url, '_blank');
      if (!w) { // popup geblokkeerd → dan maar rechtstreeks downloaden
        const a = document.createElement('a');
        a.href = url; a.download = d.original_name;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(`Kon het document niet openen: ${e.message}`); }
  };

  const verwijder = async (d) => {
    if (!window.confirm(`"${d.original_name}" verwijderen?`)) return;
    try {
      const r = await fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}/docs/${d.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      toast.success('Document verwijderd.');
      await laad();
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
  };

  const kb = (n) => `${Math.max(1, Math.round((Number(n) || 0) / 1024))} kB`;
  const datumTijd = (v) => (v ? new Date(v).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

  return (
    <Card className="!p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-base font-semibold text-slate-100">COMMISSIEAFSPRAAK (PDF)</h3>
        {magBeheren && (
          <div className="flex items-center gap-2">
            <input ref={bestandRef} type="file" accept="application/pdf" className="hidden"
              onChange={e => upload(e.target.files && e.target.files[0])} />
            <button onClick={() => bestandRef.current && bestandRef.current.click()} disabled={bezig}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-60 text-slate-900 text-xs font-semibold rounded-lg">
              <Upload size={14} />{bezig ? 'Bezig…' : 'PDF toevoegen'}
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400 mb-3">
        De afspraak waarop de bedragen op deze pagina teruggaan. Zichtbaar voor de beheerders en voor de master van dit energielabel.
        {!magBeheren && ' Toevoegen of verwijderen kan alleen een beheerder.'}
      </p>
      {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm mb-3">{fout}</div>}
      {laden ? <div className="py-4 flex justify-center"><Spinner size={20} /></div>
        : docs.length === 0 ? <TableEmpty message="Nog geen document toegevoegd." icon={Folder} /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead><tr className="border-b border-slate-700">
              <th className="py-2 px-2 text-left text-slate-400 text-xs">Bestand</th>
              <th className="py-2 px-2 text-right text-slate-400 text-xs">Grootte</th>
              <th className="py-2 px-2 text-left text-slate-400 text-xs">Toegevoegd</th>
              <th className="py-2 px-2 text-right text-slate-400 text-xs">Actie</th>
            </tr></thead>
            <tbody>{docs.map(d => (
              <tr key={d.id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                <td className="py-2 px-2 text-slate-200 text-xs" data-label="Bestand">
                  <button onClick={() => open(d)} className="inline-flex items-center gap-1.5 text-lime-300 hover:text-lime-200">
                    <FileText size={14} className="shrink-0 w-4" /><span className="truncate">{d.original_name}</span>
                  </button>
                </td>
                <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums" data-label="Grootte">{kb(d.size_bytes)}</td>
                <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap" data-label="Toegevoegd">
                  {datumTijd(d.uploaded_at)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''}
                </td>
                <td className="py-2 px-2 text-right text-xs" data-label="Actie">
                  <span className="inline-flex items-center gap-2 justify-end">
                    <button onClick={() => open(d)} className="text-slate-300 hover:text-lime-300" title="Openen"><Download size={14} className="shrink-0 w-4" /></button>
                    {magBeheren && <button onClick={() => verwijder(d)} className="text-slate-500 hover:text-red-400" title="Verwijderen"><Trash2 size={14} className="shrink-0 w-4" /></button>}
                  </span>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ── Evident Energie: commissie per gefactureerde MWh ─────────────────────────
// Anders dan bij Servolt (vast bedrag per geactiveerde EAN) rekent Evident af op
// wat er écht gefactureerd is. Twee prijszettingen naast elkaar — het vaste
// tarief voor residentieel + professioneel, en een trappenschema op de
// verkoopformule voor Custom-aansluitingen met een AMR/MMR-meter. Het rekenwerk
// staat in src/customFee.js (met tests); hier staat alleen de weergave.
//
// Drie blokken: de twee configuratietabellen, "KWH COMMISSIE PER VERKOPER" (de
// gefactureerde kWh én de commissie per prijszetting op één regel — dat waren
// eerst twee tabellen naast elkaar) en daaronder de gefactureerde volumes per
// EAN met het tarief en de commissie erbij.
// ── Gedeelde bouwstenen van de Evident-commissie ─────────────────────────────
// Beide Evident-pagina's ("Commissies Evident" en "Commissie overzicht") rekenen
// op EXACT dezelfde regels: het overzicht is een andere weergave van hetzelfde
// getal, geen tweede berekening. Vandaar deze twee helpers op moduleniveau.
const evidentNormVerkoper = (v) => { const n = (v || '').trim(); return (!n || n === 'OdooBot') ? 'Online' : n; };

// De injectie per aansluiting, uit de self bills. Die staan in een aparte feed
// (partner_commission_export_SB) en niet in de facturen, dus ze worden per
// (verkoper · klant · EL-GSRN) opgeteld en bij de bijhorende EAN-regel gelegd.
// Zonder GSRN valt een self bill weg: er is dan geen aansluiting om hem aan te
// hangen, en samengooien onder één klant zou de commissie op een andere
// prijszetting laten landen.
const evidentInjSleutel = (verkoper, klant, gsrn) => [verkoper, String(klant || '').trim().toLowerCase(), String(gsrn || '').trim()].join('|');
function evidentInjectiePerEan(selfBills, meterMethod) {
  const per = new Map();
  for (const f of selfBills || []) {
    const gsrn = String(f.elGSRN || '').trim();
    if (!gsrn) continue;
    const verkoper = evidentNormVerkoper(f.verkoper);
    const k = evidentInjSleutel(verkoper, f.relatieReferentie || f.relatieNaam, gsrn);
    const p = per.get(k) || { kwh: 0, maj: 0, segment: '', meter: '', klant: f.relatieNaam, klantnummer: f.relatieReferentie, verkoper, verkoopteam: f.verkoopteam, elGSRN: gsrn, aantal: 0, datum: '' };
    p.kwh += Number(f.elKwhInjection) || 0;
    p.aantal += 1;
    // Opslag, segment en meetmethode van de RECENTSTE self bill — zelfde regel
    // als de attribuutkolommen van de facturen. De meetmethode komt uit dezelfde
    // helper als daar (record zelf, anders opgezocht via de GSRN), zodat een
    // aansluiting die alleen injecteert tóch in de juiste prijszetting valt.
    const d = String(f.factuurdatum || '');
    if (d >= p.datum) {
      p.datum = d;
      p.maj = Number(f.elMajorationInjection) || 0;
      p.segment = f.elSegment || '';
      p.meter = meterMethod ? (meterMethod(f) || '') : '';
    }
    per.set(k, p);
  }
  return per;
}

// De EAN-regels mét commissie: facturen gebundeld per aansluiting (eanVolume.js),
// de injectie eraan gekoppeld, en daarop de methodiek uit customFee.js.
function evidentBouwEanRijen({ facturen, selfBills, meterMethod, tarieven }) {
  const groepen = groepeerEanVolumes(facturen || [], meterMethod || (() => ''));
  const injectie = evidentInjectiePerEan(selfBills, meterMethod);
  const gebruikt = new Set();
  const basis = groepen.map(g => ({
    sleutel: g._sleutel,
    klant: g.relatieNaam,
    klantnummer: g.relatieReferentie,
    verkoper: evidentNormVerkoper(g.verkoper),
    verkoopteam: g.verkoopteam,
    aantalFacturen: g.aantalFacturen,
    elGSRN: g.elGSRN,
    elKwh: g.totaalElKwh,
    elSegment: g._elSegment.waarde,
    elMaj: Number(g._elMaj.waarde) || 0,
    ngGSRN: g.ngGSRN,
    ngKwh: g.totaalNgKwh,
    ngSegment: g._ngSegment.waarde,
    ngMaj: Number(g._ngMaj.waarde) || 0,
    meterMethod: g._meter.waarde,
    // De attribuutobjecten houden we bij voor de cellen die tonen dat een waarde
    // over de gebundelde facturen wisselt (amber met een *).
    _elSegment: g._elSegment, _ngSegment: g._ngSegment, _elMaj: g._elMaj, _ngMaj: g._ngMaj, _meter: g._meter,
  }));
  for (const r of basis) {
    if (!r.elGSRN) continue;
    const k = evidentInjSleutel(r.verkoper, r.klantnummer || r.klant, r.elGSRN);
    const inj = injectie.get(k);
    if (!inj) continue;
    gebruikt.add(k);
    r.injKwh = inj.kwh;
    r.injMaj = inj.maj;
    r.injSegment = inj.segment;
    r.aantalSelfBills = inj.aantal;
  }
  // Een aansluiting die in deze periode alleen injecteerde past bij geen enkele
  // factuurregel en krijgt een eigen regel — anders verdwijnt die commissie.
  for (const [k, inj] of injectie) {
    if (gebruikt.has(k)) continue;
    basis.push({
      sleutel: `sb|${k}`,
      klant: inj.klant, klantnummer: inj.klantnummer, verkoper: inj.verkoper, verkoopteam: inj.verkoopteam,
      aantalFacturen: 0, aantalSelfBills: inj.aantal,
      elGSRN: inj.elGSRN, elKwh: null, elSegment: inj.segment, elMaj: 0,
      ngGSRN: '', ngKwh: null, ngSegment: '', ngMaj: 0,
      meterMethod: inj.meter,
      injKwh: inj.kwh, injMaj: inj.maj, injSegment: inj.segment,
      _elSegment: { waarde: inj.segment, varieert: false, waarden: [] },
      _ngSegment: { waarde: '', varieert: false, waarden: [] },
      _elMaj: { waarde: '', varieert: false, waarden: [] },
      _ngMaj: { waarde: '', varieert: false, waarden: [] },
      _meter: { waarde: inj.meter, varieert: false, waarden: [] },
    });
  }
  return evidentEanRijen(basis, tarieven);
}

const EvidentCommissies = ({ facturenGoed, selfBillsGoed, calcSummaryStats, meterMethod, token, energielabel, currentUser, onRegisterExport, exportStamp, exportLabel = '', exportScope = '' }) => {
  const magBewerken = currentUser?.role === 'admin';
  const [tarieven, setTarieven] = useState(EVIDENT_DEFAULT_TARIEVEN);
  const [concept, setConcept] = useState(EVIDENT_DEFAULT_TARIEVEN);
  const [bewaren, setBewaren] = useState(false);
  const [fout, setFout] = useState('');
  const [verkoper, setVerkoper] = useState('all');
  const [eanPage, setEanPage] = useState(1);

  // Factuurmaanden waarin er iets gefactureerd is, nieuwste eerst. 'all' = alles
  // wat binnen de paginafilters valt.
  const maanden = useMemo(() => {
    const s = new Set();
    for (const f of facturenGoed || []) { const m = String(f.factuurdatum || '').slice(0, 7); if (/^\d{4}-\d{2}$/.test(m)) s.add(m); }
    return [...s].sort().reverse();
  }, [facturenGoed]);
  const [maand, setMaand] = useState('');
  useEffect(() => {
    if (maand || !maanden.length) return;
    const nu = new Date().toISOString().slice(0, 7);
    setMaand(maanden.find(m => m <= nu) || maanden[0]);
  }, [maanden, maand]);

  useEffect(() => {
    if (!token || !energielabel) return undefined;
    let af = false;
    fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (af || !d) return; const t = normaliseerEvident(d.settings); setTarieven(t); setConcept(t); })
      .catch(() => { /* niet kritisch: de standaardtarieven blijven staan */ });
    return () => { af = true; };
  }, [token, energielabel]);

  const bewaarTarieven = async () => {
    setBewaren(true); setFout('');
    try {
      const r = await fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: concept }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      const t = normaliseerEvident(d.settings);
      setTarieven(t); setConcept(t);
      toast.success('Commissietarieven opgeslagen.');
    } catch (e) { setFout(e.message); }
    finally { setBewaren(false); }
  };

  const normVerkoper = (v) => { const n = (v || '').trim(); return (!n || n === 'OdooBot') ? 'Online' : n; };
  const verkopers = useMemo(
    () => [...new Set((facturenGoed || []).map(f => normVerkoper(f.verkoper)))].sort(),
    [facturenGoed]
  );

  // De facturen waarover deze pagina rekent: binnen de gekozen maand en, als er
  // een verkoper gekozen is, van die verkoper.
  const facturenSelectie = useMemo(() => (facturenGoed || []).filter(f => {
    if (maand && maand !== 'all' && !String(f.factuurdatum || '').startsWith(maand)) return false;
    if (verkoper !== 'all' && normVerkoper(f.verkoper) !== verkoper) return false;
    return true;
  }), [facturenGoed, maand, verkoper]);

  // ── Gefactureerde kWh per verkoper (voedt de gecombineerde tabel hieronder) ─
  const kwhRijen = useMemo(() => {
    const inSelectie = (f) => (!maand || maand === 'all' || String(f.factuurdatum || '').startsWith(maand));
    const fBy = {}; const sbBy = {};
    (facturenGoed || []).filter(inSelectie).forEach(f => { const s = normVerkoper(f.verkoper); (fBy[s] || (fBy[s] = [])).push(f); });
    (selfBillsGoed || []).filter(inSelectie).forEach(f => { const s = normVerkoper(f.verkoper); (sbBy[s] || (sbBy[s] = [])).push(f); });
    const namen = new Set([...Object.keys(fBy), ...Object.keys(sbBy)]);
    const rows = [...namen]
      .filter(s => verkoper === 'all' || verkoper === s)
      .map(s => {
        const stats = calcSummaryStats(fBy[s] || [], {}, sbBy[s] || []);
        const kwh = (l) => (stats.find(r => r.label === l)?.kwhOfftake) || 0;
        return {
          verkoper: s,
          kwhB2cEl: kwh('B2C - EL'), kwhB2bEl: kwh('B2B - EL'),
          kwhCustomElOfftake: kwh('Custom - EL Offtake'), kwhCustomElInjection: kwh('Custom - EL Injection'),
          kwhB2cGas: kwh('B2C - GAS'), kwhB2bGas: kwh('B2B - GAS'), kwhCustomGas: kwh('Custom - GAS'),
        };
      });
    const tot = (r) => r.kwhB2cEl + r.kwhB2bEl + r.kwhCustomElOfftake + r.kwhCustomElInjection + r.kwhB2cGas + r.kwhB2bGas + r.kwhCustomGas;
    rows.sort((a, b) => tot(b) - tot(a) || a.verkoper.localeCompare(b.verkoper));
    return rows;
  }, [facturenGoed, selfBillsGoed, calcSummaryStats, maand, verkoper]);

  // De self bills binnen dezelfde selectie: die dragen de injectievolumes.
  const selfBillsSelectie = useMemo(() => (selfBillsGoed || []).filter(f => {
    if (maand && maand !== 'all' && !String(f.factuurdatum || '').startsWith(maand)) return false;
    if (verkoper !== 'all' && normVerkoper(f.verkoper) !== verkoper) return false;
    return true;
  }), [selfBillsGoed, maand, verkoper]);

  // ── Tabel: gefactureerd volume per EAN, met de commissie erbij ─────────────
  // Facturen gebundeld per aansluiting (src/eanVolume.js), de injectie uit de
  // self bills eraan gekoppeld en daarop de methodiek uit customFee.js —
  // allemaal in evidentBouwEanRijen, zodat het overzicht hiernaast op precies
  // dezelfde regels rekent.
  const eanRijen = useMemo(() => {
    const berekend = evidentBouwEanRijen({ facturen: facturenSelectie, selfBills: selfBillsSelectie, meterMethod, tarieven });
    return berekend.sort((a, b) => (b.commissie || 0) - (a.commissie || 0) || String(a.klant || '').localeCompare(String(b.klant || '')));
  }, [facturenSelectie, selfBillsSelectie, meterMethod, tarieven]);
  useEffect(() => { setEanPage(1); }, [maand, verkoper]);
  const eanTotalen = useMemo(() => eanRijen.reduce((a, r) => ({
    elMwh: a.elMwh + (r.elMwh || 0), elCommissie: a.elCommissie + (r.elCommissie || 0),
    injMwh: a.injMwh + (r.injMwh || 0), injCommissie: a.injCommissie + (r.injCommissie || 0),
    ngMwh: a.ngMwh + (r.ngMwh || 0), ngCommissie: a.ngCommissie + (r.ngCommissie || 0),
    commissie: a.commissie + (r.commissie || 0),
  }), { elMwh: 0, elCommissie: 0, injMwh: 0, injCommissie: 0, ngMwh: 0, ngCommissie: 0, commissie: 0 }), [eanRijen]);

  // ── Tabel: KWH COMMISSIE PER VERKOPER ──────────────────────────────────────
  // Eén regel per verkoper, met links wat hij factureerde (dezelfde kolommen als
  // "Vergoeding per verkoper") en rechts wat dat aan commissie oplevert, gesplitst
  // per prijszetting. Dat waren twee tabellen; naast elkaar in één regel lees je
  // meteen of een verkoper zijn volume uit residentieel of uit custom pricing
  // haalt — met twee tabellen moest je dat zelf naast elkaar leggen.
  //
  // De kWh-kolommen zijn NIET per prijszetting te splitsen: ze komen uit de
  // factuurrecords (per segment), en één factuur kan elektriciteit onder de custom
  // pricing hebben en gas onder het vaste tarief. Ze horen dus bij de verkoper,
  // niet bij een van de twee blokken.
  const perVerkoper = useMemo(() => evidentPerVerkoper(eanRijen), [eanRijen]);
  const LEEG_GROEP = { aantalEan: 0, elMwh: 0, elCommissie: 0, injMwh: 0, injCommissie: 0, ngMwh: 0, ngCommissie: 0, commissie: 0 };
  const verkoperRijen = useMemo(() => {
    const per = new Map();
    const zorg = (v) => {
      if (!per.has(v)) {
        per.set(v, {
          verkoper: v,
          kwhB2cEl: 0, kwhB2bEl: 0, kwhCustomElOfftake: 0, kwhCustomElInjection: 0,
          kwhB2cGas: 0, kwhB2bGas: 0, kwhCustomGas: 0,
          res: { ...LEEG_GROEP }, custom: { ...LEEG_GROEP },
          aantalEan: 0, commissie: 0,
        });
      }
      return per.get(v);
    };
    for (const r of kwhRijen) Object.assign(zorg(r.verkoper), r);
    for (const r of perVerkoper.rows) {
      const p = zorg(r.verkoper);
      p[r.groep] = { aantalEan: r.aantalEan, elMwh: r.elMwh, elCommissie: r.elCommissie, injMwh: r.injMwh, injCommissie: r.injCommissie, ngMwh: r.ngMwh, ngCommissie: r.ngCommissie, commissie: r.commissie };
      p.aantalEan += r.aantalEan;
      p.commissie += r.commissie;
    }
    // Hoogste commissie eerst; verkopers zonder commissie (wel volume, geen
    // toegekende EAN-regels) blijven staan, onderaan.
    return [...per.values()].sort((a, b) => b.commissie - a.commissie || a.verkoper.localeCompare(b.verkoper));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kwhRijen, perVerkoper]);
  const verkoperTotaal = useMemo(() => {
    const nul = () => ({ ...LEEG_GROEP });
    const t = {
      kwhB2cEl: 0, kwhB2bEl: 0, kwhCustomElOfftake: 0, kwhCustomElInjection: 0,
      kwhB2cGas: 0, kwhB2bGas: 0, kwhCustomGas: 0,
      res: nul(), custom: nul(), aantalEan: 0, commissie: 0,
    };
    for (const r of verkoperRijen) {
      for (const k of ['kwhB2cEl', 'kwhB2bEl', 'kwhCustomElOfftake', 'kwhCustomElInjection', 'kwhB2cGas', 'kwhB2bGas', 'kwhCustomGas']) t[k] += r[k] || 0;
      for (const g of ['res', 'custom']) for (const k of Object.keys(LEEG_GROEP)) t[g][k] += r[g][k] || 0;
      t.aantalEan += r.aantalEan;
      t.commissie += r.commissie;
    }
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verkoperRijen]);

  // ── Exports ────────────────────────────────────────────────────────────────
  const naam = (basis) => exportBestandsnaam({ stamp: exportStamp ? exportStamp() : '', label: exportLabel, scope: exportScope, basis });
  const maandStuk = maand && maand !== 'all' ? maand : 'alle-maanden';
  const kwhKolommen = [
    { header: 'Verkoper', get: r => r.verkoper },
    { header: 'Kwh_B2C_El', get: r => Math.round(r.kwhB2cEl) }, { header: 'Kwh_B2B_EL', get: r => Math.round(r.kwhB2bEl) },
    { header: 'Kwh_Custom_El Offtake', get: r => Math.round(r.kwhCustomElOfftake) },
    { header: 'Kwh_Custom_El Injection', get: r => Math.round(r.kwhCustomElInjection) },
    { header: 'Kwh_B2C_Gas', get: r => Math.round(r.kwhB2cGas) }, { header: 'Kwh_B2B_Gas', get: r => Math.round(r.kwhB2bGas) },
    { header: 'Kwh_Custom_Gas', get: r => Math.round(r.kwhCustomGas) },
    { header: 'Res+prof EAN-regels', get: r => r.res.aantalEan },
    { header: 'Res+prof EL MWh', get: r => r.res.elMwh.toFixed(1) },
    { header: 'Res+prof Commissie EL (EUR)', get: r => r.res.elCommissie.toFixed(2) },
    { header: 'Res+prof Injectie MWh', get: r => r.res.injMwh.toFixed(1) },
    { header: 'Res+prof Commissie injectie (EUR)', get: r => r.res.injCommissie.toFixed(2) },
    { header: 'Res+prof NG MWh', get: r => r.res.ngMwh.toFixed(1) },
    { header: 'Res+prof Commissie NG (EUR)', get: r => r.res.ngCommissie.toFixed(2) },
    { header: 'Custom pricing EAN-regels', get: r => r.custom.aantalEan },
    { header: 'Custom pricing EL MWh', get: r => r.custom.elMwh.toFixed(1) },
    { header: 'Custom pricing Commissie EL (EUR)', get: r => r.custom.elCommissie.toFixed(2) },
    { header: 'Custom pricing Injectie MWh', get: r => r.custom.injMwh.toFixed(1) },
    { header: 'Custom pricing Commissie injectie (EUR)', get: r => r.custom.injCommissie.toFixed(2) },
    { header: 'Custom pricing NG MWh', get: r => r.custom.ngMwh.toFixed(1) },
    { header: 'Custom pricing Commissie NG (EUR)', get: r => r.custom.ngCommissie.toFixed(2) },
    { header: 'EAN-regels totaal', get: r => r.aantalEan },
    { header: 'Commissie totaal (EUR)', get: r => r.commissie.toFixed(2) },
  ];
  const eanKolommen = [
    { header: 'Relatie/Bedrijf', get: r => energielabel }, { header: 'Verkoper', get: r => r.verkoper },
    { header: 'Verkoopteam', get: r => r.verkoopteam }, { header: 'Relatie', get: r => r.klant },
    { header: 'Klantnummer', get: r => r.klantnummer }, { header: 'Facturen', get: r => r.aantalFacturen },
    { header: 'EL EAN', get: r => r.elGSRN }, { header: 'EL Segment', get: r => r.elSegment },
    { header: 'EL Maj Offtake', get: r => r.elMaj }, { header: 'Meter Method', get: r => r.meterMethod },
    { header: 'NG EAN', get: r => r.ngGSRN }, { header: 'NG Segment', get: r => r.ngSegment },
    { header: 'NG Maj Offtake', get: r => r.ngMaj },
    { header: 'Total Invoice EL kWh', get: r => (r.elKwh == null ? '' : Math.round(r.elKwh)) },
    { header: 'Total Invoice NG kWh', get: r => (r.ngKwh == null ? '' : Math.round(r.ngKwh)) },
    { header: 'Self bills', get: r => (r.aantalSelfBills || '') },
    { header: 'Injectie kWh (self bills)', get: r => (r.injKwh == null ? '' : Math.round(r.injKwh)) },
    { header: 'Maj Injection EL', get: r => (r.injKwh == null ? '' : r.injMaj) },
    { header: 'Commissie injectie (EUR/MWh)', get: r => (r.injTarief == null ? '' : r.injTarief) },
    { header: 'Commissie injectie (EUR)', get: r => (r.injCommissie == null ? '' : r.injCommissie.toFixed(2)) },
    { header: 'Prijszetting EL', get: r => (r.elGroep ? EVIDENT_GROEP_LABEL[r.elGroep] : '') },
    { header: 'Commissie EL (EUR/MWh)', get: r => (r.elTarief == null ? '' : r.elTarief) },
    { header: 'Commissie EL (EUR)', get: r => (r.elCommissie == null ? '' : r.elCommissie.toFixed(2)) },
    { header: 'Prijszetting NG', get: r => (r.ngGroep ? EVIDENT_GROEP_LABEL[r.ngGroep] : '') },
    { header: 'Commissie NG (EUR/MWh)', get: r => (r.ngTarief == null ? '' : r.ngTarief) },
    { header: 'Commissie NG (EUR)', get: r => (r.ngCommissie == null ? '' : r.ngCommissie.toFixed(2)) },
    { header: 'Commissie totaal (EUR)', get: r => (r.commissie || 0).toFixed(2) },
  ];
  const exportVerkoper = () => exportRowsToCsv(naam(`kwh-commissie-per-verkoper-${maandStuk}`), kwhKolommen, verkoperRijen);
  const exportEan = () => exportRowsToCsv(naam(`commissie-per-ean-${maandStuk}`), eanKolommen, eanRijen);
  useEffect(() => {
    if (!onRegisterExport) return undefined;
    const af = [
      onRegisterExport(`kwh-commissie-per-verkoper-${maandStuk}`, () => ({ columns: kwhKolommen, rows: verkoperRijen })),
      onRegisterExport(`commissie-per-ean-${maandStuk}`, () => ({ columns: eanKolommen, rows: eanRijen })),
    ];
    return () => af.forEach(f => typeof f === 'function' && f());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maandStuk, verkoperRijen, eanRijen]);

  const euro = (n) => formatEuroRounded(n);
  const euro2 = (n) => formatCurrency(n);
  const maandLabel = (m) => (!m || m === 'all' ? 'alle maanden' : new Intl.DateTimeFormat('nl-BE', { month: 'long', year: 'numeric' }).format(new Date(`${m}-01T00:00:00`)));
  // Kort op de badge, de volledige naam in de tooltip: "Custom pricing" dekt sinds
  // 18-08-2026 zowel elektriciteit met een AMR/MMR-meter als élke Custom-gasklant,
  // dus "Custom AMR/MMR" op de badge zou voor gas niet meer kloppen.
  const groepBadge = (g) => (!g ? <span className="text-slate-600">—</span> : (
    <span title={EVIDENT_GROEP_LABEL[g]}
      className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${g === 'custom' ? 'bg-violet-900/40 text-violet-300' : 'bg-slate-700 text-slate-300'}`}>
      {g === 'custom' ? 'Custom pricing' : 'Res + prof'}
    </span>
  ));

  // Eén trappentabel (elektriciteit, gas of injectie) in het configuratieblok.
  const TrapRij = ({ soort, i }) => (
    <tr className="border-b border-slate-700/30">
      <td className="py-1.5 pr-2 text-slate-400 text-xs whitespace-nowrap">vanaf</td>
      <td className="py-1.5 px-1 text-center whitespace-nowrap">
        <TariefInput waarde={concept[`cust${soort}Maj${i}`]} readOnly={!magBewerken}
          onChange={v => setConcept(c => ({ ...c, [`cust${soort}Maj${i}`]: v }))} />
      </td>
      <td className="py-1.5 px-1 text-center text-slate-500 text-xs">→</td>
      <td className="py-1.5 pl-2 text-right whitespace-nowrap">
        <TariefInput waarde={concept[`cust${soort}Tar${i}`]} readOnly={!magBewerken}
          onChange={v => setConcept(c => ({ ...c, [`cust${soort}Tar${i}`]: v }))} />
        <span className="text-slate-500 ml-1 text-xs">€</span>
      </td>
    </tr>
  );
  // De eenheid staat in de kop en niet achter elk veld: met vier regels naast
  // elkaar wordt "€/MWh" per rij te breed en breekt de kolom af.
  const TrapTabel = ({ soort, titel, hint }) => (
    <div>
      <div className="text-xs font-semibold text-slate-300 tracking-wide">{titel}</div>
      <div className="text-[11px] text-slate-500 mb-1">{hint} · opslag → €/MWh</div>
      <table className="w-full text-xs">
        <tbody>{[1, 2, 3, 4].map(i => <TrapRij key={i} soort={soort} i={i} />)}</tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Keuzes: factuurmaand + verkoper. Ze sturen alle drie de tabellen. */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Factuurmaand</label>
            <select value={maand} onChange={e => setMaand(e.target.value)}
              className="px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
              <option value="all">Alle maanden</option>
              {maanden.map(m => <option key={m} value={m}>{maandLabel(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Verkoper</label>
            <select value={verkoper} onChange={e => setVerkoper(e.target.value)}
              className="px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
              <option value="all">Alle verkopers</option>
              {verkopers.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="text-xs text-slate-400 pb-2">
            Alle tabellen rekenen op de facturen van <strong className="text-slate-300">goede klanten</strong> binnen de filters van deze pagina.
          </div>
        </div>
      </Card>

      {/* De twee configuratietabellen naast elkaar: links het vaste tarief, rechts
          het trappenschema. Bewerkbaar door admin; master en ET-dossierbeheerder
          lezen mee — deze getallen bepalen een uitbetaling. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="!p-4">
          <h3 className="text-base font-semibold text-slate-100 mb-1 tracking-[0.15em]">R E S I D E N T I E E L &nbsp;+&nbsp; P R O F E S S I O N E E L</h3>
          <p className="text-xs text-slate-400 mb-3">
            Commissie per MWh voor alles wat niet onder de custom pricing valt — dus ook een Custom-klant zonder AMR- of MMR-meter.
            {!magBewerken && ' Alleen een beheerder kan dit wijzigen.'}
          </p>
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-700/60">
              <th className="py-1.5 pr-2 text-left text-slate-400 font-medium">Energie</th>
              <th className="py-1.5 pl-2 text-right text-slate-400 font-medium">Commissie per MWh</th>
            </tr></thead>
            <tbody>
              <tr className="border-b border-slate-700/30">
                <td className="py-2 pr-2 text-slate-300">Elektriciteit</td>
                <td className="py-2 pl-2 text-right whitespace-nowrap">
                  <TariefInput waarde={concept.resEl} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, resEl: v }))} />
                  <span className="text-slate-500 ml-1">€/MWh</span>
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-2 text-slate-300">Gas</td>
                <td className="py-2 pl-2 text-right whitespace-nowrap">
                  <TariefInput waarde={concept.resNg} readOnly={!magBewerken} onChange={v => setConcept(c => ({ ...c, resNg: v }))} />
                  <span className="text-slate-500 ml-1">€/MWh</span>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>

        {/* Twee derde van de breedte: drie trappenreeksen naast elkaar hebben die
            ruimte nodig, anders breken de invoervelden en hun eenheid af. */}
        <Card className="!p-4 xl:col-span-2">
          <h3 className="text-base font-semibold text-slate-100 mb-1 tracking-[0.12em]">C U S T O M &nbsp; P R I C I N G &nbsp; ( A M R&nbsp;E L / M M R&nbsp;E L / C U S T O M&nbsp;G A S )</h3>
          <p className="text-xs text-slate-400 mb-3">
            De commissie hangt hier aan de verkoopformule: de opslag op de aansluiting. Elektriciteit (afname én injectie) valt
            hieronder bij een Custom-klant mét een AMR- of MMR-meter; <strong className="text-slate-300">gas van een Custom-klant valt er
            altijd onder</strong>, ongeacht de meetmethode. De drempel zelf telt mee (opslag 25 → het tarief van “vanaf 25”); onder de
            laagste drempel is er geen commissie.
          </p>
          {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm mb-3">{fout}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TrapTabel soort="El" titel="ELEKTRICITEIT (BELPEX)" hint="Maj Offtake El" />
            <TrapTabel soort="Ng" titel="GAS (TTF)" hint="Maj Offtake NG" />
            <TrapTabel soort="Inj" titel="INJECTIE ELEKTRICITEIT" hint="Maj Injection EL" />
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            Injectie komt uit de self bills en telt mee vanaf 18-08-2026; de trappen staan standaard gelijk aan die van de afname van
            elektriciteit. De opslag op injectie staat negatief in de data — er wordt op de absolute waarde getoetst, en een lege
            waarde valt terug op de contractdefault −25 (zoals overal in het platform).
          </p>
        </Card>
      </div>

      {magBewerken && (
        <div className="flex justify-end gap-2">
          <button onClick={() => setConcept(tarieven)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg">Herstel</button>
          <button onClick={bewaarTarieven} disabled={bewaren} className="px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-60 text-slate-900 text-xs font-semibold rounded-lg">Tarieven opslaan</button>
        </div>
      )}

      {/* Gefactureerde kWh én de commissie in ÉÉN tabel per verkoper. Dit waren
          twee tabellen ("Vergoeding gefactureerde kWh" en "Commissie per
          verkoper"); naast elkaar op één regel lees je meteen of iemand zijn
          volume uit residentieel of uit custom pricing haalt. */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0"><h3 className="text-base font-semibold text-slate-100">KWH COMMISSIE PER VERKOPER</h3><ExportKnop onExport={exportVerkoper} disabled={verkoperRijen.length === 0} label="Exporteer deze tabel naar Excel" /></div>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Per verkoper: links de gefactureerde volumes van goede klanten met factuurdatum in {maandLabel(maand)} (zelfde kolommen als
          “Vergoeding per verkoper”), rechts de commissie uit de tabel hieronder — afname, injectie en gas — gesplitst per
          prijszetting. De kWh-kolommen zijn bewust niet per prijszetting gesplitst: één factuur kan elektriciteit onder de custom
          pricing hebben en gas onder het vaste tarief, dus ze horen bij de verkoper en niet bij één van de twee blokken.
        </p>
        {verkoperRijen.length === 0 ? <TableEmpty message="Geen gefactureerde volumes in deze maand." icon={Folder} /> : (
          <div className="overflow-x-auto">
            {/* Bewust ZONDER `resp-card`: die mobiele kaartweergave leidt haar
                labels af uit één kopregel, en deze tabel heeft er twee. Twintig
                kolommen gestapeld leest trouwens toch niet — horizontaal scrollen
                is hier de betere weergave. */}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide">
                  <th className="py-1 px-2" />
                  <th className="py-1 px-2 text-center text-slate-500 border-l border-slate-700/60" colSpan="7">Gefactureerde kWh</th>
                  <th className="py-1 px-2 text-center text-slate-300 border-l border-slate-700/60" colSpan="7">Residentieel + professioneel</th>
                  <th className="py-1 px-2 text-center text-violet-300 border-l border-slate-700/60" colSpan="7">Custom pricing (AMR EL/MMR EL/Custom GAS)</th>
                  <th className="py-1 px-2 text-center text-lime-300 border-l border-slate-700/60" colSpan="2">Totaal</th>
                </tr>
                <tr className="border-b border-slate-700">
                  <th className="py-2 px-2 text-left text-slate-400 text-xs">Verkoper</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs border-l border-slate-700/60">B2C EL</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">B2B EL</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Custom EL Offtake</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Custom EL Injection</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">B2C Gas</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">B2B Gas</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Custom Gas</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs border-l border-slate-700/60">EAN-regels</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">EL MWh</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie EL (€)</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs" title="Injectie uit de self bills — alle segmenten. De kolom Custom EL Injection links toont enkel het Custom-deel.">Injectie MWh</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie inj. (€)</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">NG MWh</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie NG (€)</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs border-l border-slate-700/60">EAN-regels</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">EL MWh</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie EL (€)</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs" title="Injectie uit de self bills — alle segmenten. De kolom Custom EL Injection links toont enkel het Custom-deel.">Injectie MWh</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie inj. (€)</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">NG MWh</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie NG (€)</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs border-l border-slate-700/60">EAN-regels</th>
                  <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie (€)</th>
                </tr>
              </thead>
              <tbody>{verkoperRijen.map(r => (
                <tr key={r.verkoper} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="py-2 px-2 text-slate-200 text-xs whitespace-nowrap">{r.verkoper}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums border-l border-slate-700/60">{formatNumberRounded(r.kwhB2cEl)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.kwhB2bEl)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.kwhCustomElOfftake)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.kwhCustomElInjection)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.kwhB2cGas)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.kwhB2bGas)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.kwhCustomGas)}</td>
                  <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums border-l border-slate-700/60">{r.res.aantalEan || ''}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.res.elMwh)}</td>
                  <td className="py-2 px-2 text-right text-slate-200 text-xs tabular-nums">{euro2(r.res.elCommissie)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.res.injMwh)}</td>
                  <td className="py-2 px-2 text-right text-slate-200 text-xs tabular-nums">{euro2(r.res.injCommissie)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums">{formatNumberRounded(r.res.ngMwh)}</td>
                  <td className="py-2 px-2 text-right text-slate-200 text-xs tabular-nums">{euro2(r.res.ngCommissie)}</td>
                  <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums border-l border-slate-700/60">{r.custom.aantalEan || ''}</td>
                  <td className="py-2 px-2 text-right text-violet-200 text-xs tabular-nums">{formatNumberRounded(r.custom.elMwh)}</td>
                  <td className="py-2 px-2 text-right text-violet-200 text-xs tabular-nums">{euro2(r.custom.elCommissie)}</td>
                  <td className="py-2 px-2 text-right text-violet-200 text-xs tabular-nums">{formatNumberRounded(r.custom.injMwh)}</td>
                  <td className="py-2 px-2 text-right text-violet-200 text-xs tabular-nums">{euro2(r.custom.injCommissie)}</td>
                  <td className="py-2 px-2 text-right text-violet-200 text-xs tabular-nums">{formatNumberRounded(r.custom.ngMwh)}</td>
                  <td className="py-2 px-2 text-right text-violet-200 text-xs tabular-nums">{euro2(r.custom.ngCommissie)}</td>
                  <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums border-l border-slate-700/60">{r.aantalEan || ''}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-medium">{euro2(r.commissie)}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr className="border-t-2 border-slate-600 bg-lime-900/20">
                <td className="py-2 px-2 text-lime-400 text-xs font-semibold">Totaal</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums border-l border-slate-700/60">{formatNumberRounded(verkoperTotaal.kwhB2cEl)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.kwhB2bEl)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.kwhCustomElOfftake)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.kwhCustomElInjection)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.kwhB2cGas)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.kwhB2bGas)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.kwhCustomGas)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums border-l border-slate-700/60">{verkoperTotaal.res.aantalEan}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.res.elMwh)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(verkoperTotaal.res.elCommissie)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.res.injMwh)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(verkoperTotaal.res.injCommissie)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.res.ngMwh)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(verkoperTotaal.res.ngCommissie)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums border-l border-slate-700/60">{verkoperTotaal.custom.aantalEan}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.custom.elMwh)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(verkoperTotaal.custom.elCommissie)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.custom.injMwh)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(verkoperTotaal.custom.injCommissie)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(verkoperTotaal.custom.ngMwh)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(verkoperTotaal.custom.ngCommissie)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums border-l border-slate-700/60">{verkoperTotaal.aantalEan}</td>
                <td className="py-2 px-2 text-right text-lime-400 text-xs font-bold tabular-nums">{euro2(verkoperTotaal.commissie)}</td>
              </tr></tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Gefactureerd volume per EAN, met de commissie erbij. */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0"><h3 className="text-base font-semibold text-slate-100">GEFACTUREERDE VOLUME PER EAN</h3><ExportKnop onExport={exportEan} disabled={eanRijen.length === 0} count={eanRijen.length} label="Exporteer deze tabel naar Excel" /></div>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Eén regel per aansluiting per klant, zoals op de Facturen-pagina, met daarachter de injectie uit de self bills en het tarief
          uit de configuratie met de commissie erop. Segment, opslag en meetmethode komen van de recentste factuur; wisselen ze over de
          gebundelde facturen, dan staat de cel in amber met een <span className="text-amber-300">*</span>. Een aansluiting die in deze
          maand alleen injecteerde krijgt een eigen regel (Facturen = 0).
        </p>
        {eanRijen.length === 0 ? <TableEmpty message="Geen facturen binnen deze selectie." icon={Folder} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm resp-card">
              <thead><tr className="border-b border-slate-700">
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Relatie</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Verkoper</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Facturen</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">EL EAN</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">EL Segment</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">EL Maj Offtake</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Meter Method</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">NG EAN</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">NG Segment</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">NG Maj Offtake</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Total Invoice EL kWh</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Total Invoice NG kWh</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs" title="EL kWh Injection uit de self bills">Injectie kWh</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs" title="Maj Injection EL — leeg = de contractdefault −25">Maj Inj</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie inj. (€/MWh)</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie inj. (€)</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Prijszetting EL</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie EL (€/MWh)</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie EL (€)</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Prijszetting NG</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie NG (€/MWh)</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie NG (€)</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Commissie (€)</th>
              </tr></thead>
              <tbody>{renderPaged(eanPage, setEanPage, eanRijen, r => (
                <tr key={r.sleutel} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={r.klant} data-label="Relatie">{r.klant}</td>
                  <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap" data-label="Verkoper">{r.verkoper}</td>
                  <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums" data-label="Facturen">{r.aantalFacturen}</td>
                  <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap" data-label="EL EAN">{r.elGSRN}</td>
                  <VolAttrCell attr={r._elSegment} render={v => v || ''} />
                  <VolAttrCell attr={r._elMaj} align="right" render={v => v || ''} />
                  <VolAttrCell attr={r._meter} render={v => v || '—'} />
                  <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap" data-label="NG EAN">{r.ngGSRN}</td>
                  <VolAttrCell attr={r._ngSegment} render={v => v || ''} />
                  <VolAttrCell attr={r._ngMaj} align="right" render={v => v || ''} />
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-medium" data-label="Total Invoice EL kWh">{r.elKwh != null ? formatNumberRounded(r.elKwh) : ''}</td>
                  <td className="py-2 px-2 text-right text-orange-300 text-xs tabular-nums font-medium" data-label="Total Invoice NG kWh">{r.ngKwh != null ? formatNumberRounded(r.ngKwh) : ''}</td>
                  <td className="py-2 px-2 text-right text-cyan-300 text-xs tabular-nums font-medium" data-label="Injectie kWh">{r.injKwh != null ? formatNumberRounded(r.injKwh) : ''}</td>
                  <td className="py-2 px-2 text-right text-slate-400 text-xs tabular-nums" data-label="Maj Inj">{r.injKwh == null ? '' : (r.injMaj || <span className="text-amber-300" title="Leeg in de data → contractdefault −25">−25*</span>)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Commissie inj. (€/MWh)">{r.injTarief == null ? '' : euro(r.injTarief)}</td>
                  <td className="py-2 px-2 text-right text-slate-200 text-xs tabular-nums" data-label="Commissie inj. (€)">{r.injCommissie == null ? '' : euro2(r.injCommissie)}</td>
                  <td className="py-2 px-2 text-xs" data-label="Prijszetting EL">{groepBadge(r.elGroep)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Commissie EL (€/MWh)">{r.elTarief == null ? '' : euro(r.elTarief)}</td>
                  <td className="py-2 px-2 text-right text-slate-200 text-xs tabular-nums" data-label="Commissie EL (€)">{r.elCommissie == null ? '' : euro2(r.elCommissie)}</td>
                  <td className="py-2 px-2 text-xs" data-label="Prijszetting NG">{groepBadge(r.ngGroep)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs tabular-nums" data-label="Commissie NG (€/MWh)">{r.ngTarief == null ? '' : euro(r.ngTarief)}</td>
                  <td className="py-2 px-2 text-right text-slate-200 text-xs tabular-nums" data-label="Commissie NG (€)">{r.ngCommissie == null ? '' : euro2(r.ngCommissie)}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-medium" data-label="Commissie (€)">{euro2(r.commissie)}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr className="border-t-2 border-slate-600 bg-lime-900/20">
                <td className="py-2 px-2 text-lime-400 text-xs font-semibold" colSpan="10">Totaal ({eanRijen.length} regels)</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(eanTotalen.elMwh * 1000)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(eanTotalen.ngMwh * 1000)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{formatNumberRounded(eanTotalen.injMwh * 1000)}</td>
                <td className="py-2 px-2" colSpan="2" />
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(eanTotalen.injCommissie)}</td>
                <td className="py-2 px-2" colSpan="2" />
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(eanTotalen.elCommissie)}</td>
                <td className="py-2 px-2" colSpan="2" />
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(eanTotalen.ngCommissie)}</td>
                <td className="py-2 px-2 text-right text-lime-400 text-xs font-bold tabular-nums">{euro2(eanTotalen.commissie)}</td>
              </tr></tfoot>
            </table>
          </div>
        )}
      </Card>


      <CommissieDocumenten token={token} energielabel={energielabel} />
    </div>
  );
};

// ── Evident: "Commissie overzicht" ───────────────────────────────────────────
// Dezelfde cijfers als "Commissies Evident", maar dan als verkoopdashboard: wat
// staat er al, wat komt er nog, en hoe ver staat iemand van zijn doel.
//
// De HUIDIGE commissie komt uit exact dezelfde pijplijn (evidentBouwEanRijen) —
// dit is een weergave, geen tweede berekening. Ze wordt per MAAND berekend en
// opgeteld, zodat elke maand hier gelijk is aan wat de tabel "KWH COMMISSIE PER
// VERKOPER" voor die maand toont.
//
// De PROJECTIE komt uit de orders: elk order draagt een geschat jaarvolume
// (Est. Offtake voor elektriciteit of gas, Est. Injection voor injectie). Wat
// daarvan nog niet gefactureerd is, levert straks nog commissie op — aan dezelfde
// prijszetting. Zie evidentProjectie in src/customFee.js (met tests).
const MAAND_KORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// Kleine trendlijn in een tabelcel: puur SVG, want een Recharts-grafiek per rij
// kost te veel voor een lijst van tientallen verkopers.
const MiniTrend = ({ waarden = [], kleur = '#84cc16', breedte = 72, hoogte = 22 }) => {
  const n = waarden.length;
  if (!n) return <span className="text-slate-600 text-xs">—</span>;
  const max = Math.max(...waarden, 1);
  const stap = n > 1 ? breedte / (n - 1) : breedte;
  const punt = (v, i) => `${(i * stap).toFixed(1)},${(hoogte - (v / max) * (hoogte - 2) - 1).toFixed(1)}`;
  const lijn = waarden.map(punt).join(' ');
  const vlak = `0,${hoogte} ${lijn} ${breedte},${hoogte}`;
  const stijgt = n > 1 && waarden[n - 1] >= waarden[n - 2];
  return (
    <svg width={breedte} height={hoogte} viewBox={`0 0 ${breedte} ${hoogte}`} className="shrink-0" aria-hidden="true">
      <polygon points={vlak} fill={kleur} opacity="0.15" />
      <polyline points={lijn} fill="none" stroke={kleur} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {n > 1 && <circle cx={breedte} cy={hoogte - (waarden[n - 1] / max) * (hoogte - 2) - 1} r="2" fill={stijgt ? kleur : '#f43f5e'} />}
    </svg>
  );
};

// Voortgangsbalk met het doel als ijkpunt. `extra` (de projectie) staat er in een
// lichtere tint bovenop: zo zie je meteen dat wat nog binnenkomt je over de streep
// trekt.
const DoelBalk = ({ nu = 0, extra = 0, doel = 0, hoogte = 'h-2' }) => {
  if (!doel) return <span className="text-slate-500 text-xs">geen doel</span>;
  const pctNu = Math.max(0, Math.min(100, (nu / doel) * 100));
  const pctExtra = Math.max(0, Math.min(100 - pctNu, (extra / doel) * 100));
  const gehaald = nu >= doel;
  return (
    <div className={`w-full ${hoogte} rounded-full bg-slate-700/60 overflow-hidden flex`} title={`${Math.round((nu / doel) * 100)}% van het doel gehaald`}>
      <div className={`${gehaald ? 'bg-emerald-400' : 'bg-lime-500'} h-full`} style={{ width: `${pctNu}%` }} />
      <div className="bg-lime-500/35 h-full" style={{ width: `${pctExtra}%` }} />
    </div>
  );
};

const EvidentCommissieOverzicht = ({ facturenGoed, selfBillsGoed, orders = [], meterMethod, token, energielabel, currentUser, onRegisterExport, exportStamp, exportLabel = '', exportScope = '' }) => {
  const magBewerken = currentUser?.role === 'admin';
  const [tarieven, setTarieven] = useState(EVIDENT_DEFAULT_TARIEVEN);
  const [doelConcept, setDoelConcept] = useState({ doelJaarLabel: 0, doelJaarVerkoper: 0 });
  const [doelBezig, setDoelBezig] = useState(false);
  const [team, setTeam] = useState('all');
  const [verkoper, setVerkoper] = useState('all');
  const [toonDoelen, setToonDoelen] = useState(false);

  useEffect(() => {
    if (!token || !energielabel) return undefined;
    let af = false;
    fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (af || !d) return;
        const t = normaliseerEvident(d.settings);
        setTarieven(t);
        setDoelConcept({ doelJaarLabel: t.doelJaarLabel, doelJaarVerkoper: t.doelJaarVerkoper });
      })
      .catch(() => { /* niet kritisch: de standaardtarieven blijven staan */ });
    return () => { af = true; };
  }, [token, energielabel]);

  const bewaarDoelen = async () => {
    setDoelBezig(true);
    try {
      const nieuw = { ...tarieven, doelJaarLabel: Number(doelConcept.doelJaarLabel) || 0, doelJaarVerkoper: Number(doelConcept.doelJaarVerkoper) || 0 };
      const r = await fetch(`/api/custom-fee/${encodeURIComponent(energielabel)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: nieuw }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      const t = normaliseerEvident(d.settings);
      setTarieven(t);
      setDoelConcept({ doelJaarLabel: t.doelJaarLabel, doelJaarVerkoper: t.doelJaarVerkoper });
      setToonDoelen(false);
      toast.success('Doelstellingen opgeslagen.');
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); }
    finally { setDoelBezig(false); }
  };

  // ── Periode ────────────────────────────────────────────────────────────────
  // Een kalenderJAAR, niet een maand: de projectie rekent met het geschatte
  // JAARvolume van een order, en dat naast één maand facturen leggen zegt niets.
  const jaren = useMemo(() => {
    const s = new Set();
    for (const f of facturenGoed || []) { const j = String(f.factuurdatum || '').slice(0, 4); if (/^\d{4}$/.test(j)) s.add(j); }
    return [...s].sort().reverse();
  }, [facturenGoed]);
  const [jaar, setJaar] = useState('');
  useEffect(() => {
    if (jaar || !jaren.length) return;
    const nu = String(new Date().getFullYear());
    setJaar(jaren.includes(nu) ? nu : jaren[0]);
  }, [jaren, jaar]);

  const teams = useMemo(() => [...new Set((facturenGoed || []).map(f => (f.verkoopteam || '').trim()).filter(Boolean))].sort(), [facturenGoed]);
  const verkopers = useMemo(() => [...new Set((facturenGoed || [])
    .filter(f => team === 'all' || (f.verkoopteam || '').trim() === team)
    .map(f => evidentNormVerkoper(f.verkoper)))].sort(), [facturenGoed, team]);
  useEffect(() => { if (verkoper !== 'all' && !verkopers.includes(verkoper)) setVerkoper('all'); }, [verkopers, verkoper]);

  const inScope = useMemo(() => (f) => {
    if (jaar && !String(f.factuurdatum || '').startsWith(jaar)) return false;
    if (team !== 'all' && (f.verkoopteam || '').trim() !== team) return false;
    if (verkoper !== 'all' && evidentNormVerkoper(f.verkoper) !== verkoper) return false;
    return true;
  }, [jaar, team, verkoper]);

  // ── Gerealiseerde commissie, per maand ─────────────────────────────────────
  // Per maand door dezelfde pijplijn als de commissietabel, zodat elke maand hier
  // hetzelfde getal geeft als die tabel. Het jaartotaal is de som daarvan.
  const perMaand = useMemo(() => {
    if (!jaar) return [];
    const facturen = (facturenGoed || []).filter(inScope);
    const selfBills = (selfBillsGoed || []).filter(inScope);
    const uit = [];
    for (let m = 0; m < 12; m += 1) {
      const sleutel = `${jaar}-${String(m + 1).padStart(2, '0')}`;
      const fMaand = facturen.filter(f => String(f.factuurdatum || '').startsWith(sleutel));
      const sbMaand = selfBills.filter(f => String(f.factuurdatum || '').startsWith(sleutel));
      const rijen = (fMaand.length || sbMaand.length)
        ? evidentBouwEanRijen({ facturen: fMaand, selfBills: sbMaand, meterMethod, tarieven })
        : [];
      const perVerkoper = new Map();
      let totaal = 0;
      for (const r of rijen) {
        totaal += r.commissie || 0;
        perVerkoper.set(r.verkoper, (perVerkoper.get(r.verkoper) || 0) + (r.commissie || 0));
      }
      uit.push({ maand: sleutel, index: m, label: MAAND_KORT[m], commissie: totaal, perVerkoper, rijen });
    }
    return uit;
  }, [facturenGoed, selfBillsGoed, inScope, jaar, meterMethod, tarieven]);

  const alleRijen = useMemo(() => perMaand.flatMap(m => m.rijen), [perMaand]);
  const huidig = useMemo(() => perMaand.reduce((s, m) => s + m.commissie, 0), [perMaand]);

  // Tot en met welke maand is er iets gefactureerd? Daar stopt de opgebouwde lijn
  // en begint de projectie. Bewust de laatste maand MET facturen en niet de
  // kalendermaand: facturen lopen achter, dus de lopende maand staat vaak nog op
  // nul en dat zou de grafiek met een val naar beneden laten eindigen.
  // −1 = dit jaar nog niets gefactureerd.
  const laatsteMaand = useMemo(() => {
    let i = -1;
    perMaand.forEach((m, idx) => { if (m.commissie > 0) i = idx; });
    return i;
  }, [perMaand]);

  // ── Projectie op het nog te factureren volume ──────────────────────────────
  const gefactureerdPerGsrn = useMemo(() => {
    const per = new Map();
    const bij = (gsrn, veld, kwh) => {
      const g = String(gsrn || '').trim();
      if (!g || !kwh) return;
      const p = per.get(g) || { el: 0, ng: 0, inj: 0 };
      p[veld] += Number(kwh) || 0;
      per.set(g, p);
    };
    for (const r of alleRijen) {
      bij(r.elGSRN, 'el', r.elKwh);
      bij(r.elGSRN, 'inj', r.injKwh);
      bij(r.ngGSRN, 'ng', r.ngKwh);
    }
    return per;
  }, [alleRijen]);

  // De prijsformule (opslag) per aansluiting, uit de recentste factuurregel. Een
  // order zonder factuurgeschiedenis kent die niet — daar valt de projectie terug
  // op het gemiddeld gerealiseerde tarief van zijn prijszetting.
  const formulePerGsrn = useMemo(() => {
    const per = new Map();
    for (const r of alleRijen) {
      if (r.elGSRN) { const p = per.get(r.elGSRN) || {}; if (r.elMaj) p.elMaj = r.elMaj; if (r.injMaj) p.injMaj = r.injMaj; per.set(r.elGSRN, p); }
      if (r.ngGSRN) { const p = per.get(r.ngGSRN) || {}; if (r.ngMaj) p.ngMaj = r.ngMaj; per.set(r.ngGSRN, p); }
    }
    return per;
  }, [alleRijen]);

  const gemiddeldTarief = useMemo(() => evidentGemiddeldTarief(alleRijen), [alleRijen]);

  const projectieRijen = useMemo(() => {
    if (!jaar) return [];
    const van = `${jaar}-01-01`;
    const tot = `${jaar}-12-31`;
    const kandidaten = (orders || [])
      .filter(o => (o.company || '').trim() === energielabel)
      // ALLEEN actieve aansluitingen (CP_ACTIVE — dezelfde groepering als de
      // CP-statusfilter op Orders en Sales). Offertes, concepten en vertrokken
      // EANs dragen soms enorme geschatte volumes die nooit gefactureerd worden:
      // gemeten op 500 Evident-orders staat er 124 GWh geschat volume tegenover
      // 8 GWh op de actieve aansluitingen. Zonder deze grens is de projectie een
      // fantasiegetal.
      .filter(o => CP_ACTIVE.has((o.connectionPointStatus || '').toLowerCase()))
      .filter(o => team === 'all' || (o.salespersonTeam || '').trim() === team)
      .filter(o => verkoper === 'all' || evidentNormVerkoper(o.salesperson) === verkoper)
      .map(o => ({
        gsrn: o.connectionPointGSRN,
        market: o.market,
        segment: o.segment,
        meterMethod: o.meterMethod,
        verkoper: evidentNormVerkoper(o.salesperson),
        verkoopteam: o.salespersonTeam,
        klant: o.customer,
        start: o.effectiveStartDate || o.deliveryStartDate || '',
        eind: o.effectiveEndDate || o.deliveryEndDate || '',
        estOfftakeKwh: o.estOfftakeKwh,
        estInjectionKwh: o.estInjectionKwh,
      }));
    const actief = evidentActieveOrders(kandidaten, { van, tot });
    return evidentProjectie(actief, { tarieven, gefactureerd: gefactureerdPerGsrn, formule: formulePerGsrn, gemiddeld: gemiddeldTarief });
  }, [orders, energielabel, team, verkoper, jaar, tarieven, gefactureerdPerGsrn, formulePerGsrn, gemiddeldTarief]);

  const projectie = useMemo(() => projectieRijen.reduce((s, r) => s + r.commissie, 0), [projectieRijen]);
  const projectiePerVerkoper = useMemo(() => evidentProjectiePerVerkoper(projectieRijen), [projectieRijen]);
  const geschatteRegels = useMemo(() => projectieRijen.filter(r => r.geschatTarief).length, [projectieRijen]);
  const restMwh = useMemo(() => projectieRijen.reduce((s, r) => s + r.restMwh, 0), [projectieRijen]);

  const doel = verkoper !== 'all' ? tarieven.doelJaarVerkoper : tarieven.doelJaarLabel;
  const verwacht = huidig + projectie;

  // ── De grafiek: opgebouwd vs. geprojecteerd ────────────────────────────────
  // Tot de laatste gefactureerde maand de echte cijfers, cumulatief. Daarna loopt
  // de projectielijn door tot het jaareinde: de resterende commissie gelijk
  // verdeeld over de maanden die nog komen.
  const grafiek = useMemo(() => {
    const vanaf = Math.max(laatsteMaand, 0);
    const resterend = Math.max(0, 11 - vanaf);
    const perRestMaand = resterend > 0 ? projectie / resterend : 0;
    let cum = 0;
    return perMaand.map((m, i) => {
      cum += m.commissie;
      const rij = { label: m.label, maand: m.commissie, opgebouwd: i <= laatsteMaand ? cum : null };
      if (i === vanaf) rij.verwacht = cum;
      if (i > vanaf) rij.verwacht = cum + perRestMaand * (i - vanaf);
      return rij;
    });
  }, [perMaand, laatsteMaand, projectie]);

  // Groei: deze maand tegenover de vorige, als motiverende delta.
  const delta = useMemo(() => {
    const nu = perMaand[laatsteMaand]?.commissie || 0;
    const vorige = perMaand[laatsteMaand - 1]?.commissie || 0;
    if (!vorige) return null;
    return ((nu - vorige) / vorige) * 100;
  }, [perMaand, laatsteMaand]);

  // ── Ranglijst per verkoper ─────────────────────────────────────────────────
  const ranglijst = useMemo(() => {
    const per = new Map();
    const zorg = (naam) => {
      if (!per.has(naam)) per.set(naam, { verkoper: naam, huidig: 0, projectie: 0, maanden: new Array(12).fill(0) });
      return per.get(naam);
    };
    perMaand.forEach((m, i) => {
      for (const [naam, bedrag] of m.perVerkoper) { const p = zorg(naam); p.huidig += bedrag; p.maanden[i] = bedrag; }
    });
    for (const p of projectiePerVerkoper) zorg(p.verkoper).projectie += p.commissie;
    return [...per.values()]
      .map(p => ({ ...p, verwacht: p.huidig + p.projectie }))
      .sort((a, b) => b.verwacht - a.verwacht || a.verkoper.localeCompare(b.verkoper));
  }, [perMaand, projectiePerVerkoper]);

  // ── Export ─────────────────────────────────────────────────────────────────
  const naam = (basis) => exportBestandsnaam({ stamp: exportStamp ? exportStamp() : '', label: exportLabel, scope: exportScope, basis });
  const ranglijstKolommen = [
    { header: 'Verkoper', get: r => r.verkoper },
    { header: 'Huidige commissie (EUR)', get: r => r.huidig.toFixed(2) },
    { header: 'Nog te verdienen (EUR)', get: r => r.projectie.toFixed(2) },
    { header: 'Geprojecteerde commissie (EUR)', get: r => r.verwacht.toFixed(2) },
    { header: 'Doel (EUR)', get: () => (tarieven.doelJaarVerkoper || '') },
    { header: 'Voortgang (%)', get: r => (tarieven.doelJaarVerkoper ? Math.round((r.huidig / tarieven.doelJaarVerkoper) * 100) : '') },
    ...MAAND_KORT.map((m, i) => ({ header: `${m} (EUR)`, get: r => r.maanden[i].toFixed(2) })),
  ];
  const exportRanglijst = () => exportRowsToCsv(naam(`commissie-overzicht-${jaar}`), ranglijstKolommen, ranglijst);
  useEffect(() => {
    if (!onRegisterExport) return undefined;
    const af = onRegisterExport(`commissie-overzicht-${jaar}`, () => ({ columns: ranglijstKolommen, rows: ranglijst }));
    return () => { if (typeof af === 'function') af(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jaar, ranglijst]);

  const euro0 = (n) => formatEuroRounded(n);
  const euro2 = (n) => formatCurrency(n);
  const pct = (n) => `${Math.round(n)}%`;

  // Eén KPI-tegel. Compact, met plaats voor een trendlijn of een balk eronder.
  const Kpi = ({ label, waarde, sub, accent = 'text-slate-100', icoon: Icoon, onder }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl px-3 py-2.5 min-w-0">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 uppercase tracking-wide">
        {Icoon && <Icoon size={12} className="shrink-0 w-3" />}<span className="truncate">{label}</span>
      </div>
      <div className={`text-xl font-bold tabular-nums leading-tight mt-0.5 ${accent}`}><Kengetal>{waarde}</Kengetal></div>
      {sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}
      {onder && <div className="mt-1.5">{onder}</div>}
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Filterbalk — compact, alles op één regel zodra er plaats is. */}
      <Card className="!p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] text-slate-400 mb-0.5">Jaar</label>
            <select value={jaar} onChange={e => setJaar(e.target.value)}
              className="px-2.5 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
              {jaren.map(j => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
          {teams.length > 1 && (
            <div>
              <label className="block text-[11px] text-slate-400 mb-0.5">Verkoopteam</label>
              <select value={team} onChange={e => { setTeam(e.target.value); setVerkoper('all'); }}
                className="px-2.5 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
                <option value="all">Alle teams</option>
                {teams.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[11px] text-slate-400 mb-0.5">Verkoper</label>
            <select value={verkoper} onChange={e => setVerkoper(e.target.value)}
              className="px-2.5 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500">
              <option value="all">Alle verkopers</option>
              {verkopers.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {geschatteRegels > 0 && (
              <span className="text-[11px] text-amber-300/90" title="Orders zonder factuurgeschiedenis kennen hun prijsformule niet; daar rekent de projectie met het gemiddeld gerealiseerde tarief van hun prijszetting.">
                {geschatteRegels} projectieregel(s) op een geschat tarief
              </span>
            )}
            {magBewerken && (
              <button onClick={() => setToonDoelen(v => !v)}
                className="px-2.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded-lg whitespace-nowrap">
                Doelen instellen
              </button>
            )}
          </div>
        </div>
        {toonDoelen && magBewerken && (
          <div className="mt-3 pt-3 border-t border-slate-700/60 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[11px] text-slate-400 mb-0.5">Doel per jaar — hele energielabel (€)</label>
              <input type="number" min="0" step="any" value={doelConcept.doelJaarLabel}
                onChange={e => setDoelConcept(c => ({ ...c, doelJaarLabel: e.target.value }))}
                className="w-36 px-2 py-1.5 bg-slate-700/60 border border-slate-600/50 rounded text-right text-slate-100 text-sm focus:border-lime-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-[11px] text-slate-400 mb-0.5">Doel per jaar — per verkoper (€)</label>
              <input type="number" min="0" step="any" value={doelConcept.doelJaarVerkoper}
                onChange={e => setDoelConcept(c => ({ ...c, doelJaarVerkoper: e.target.value }))}
                className="w-36 px-2 py-1.5 bg-slate-700/60 border border-slate-600/50 rounded text-right text-slate-100 text-sm focus:border-lime-500 focus:outline-none" />
            </div>
            <button onClick={bewaarDoelen} disabled={doelBezig}
              className="px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-60 text-slate-900 text-xs font-semibold rounded-lg">Opslaan</button>
            <span className="text-[11px] text-slate-500">0 = geen doel. Het label-doel geldt zolang er geen verkoper gekozen is.</span>
          </div>
        )}
      </Card>

      {/* KPI-strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
        <Kpi label="Huidige commissie" waarde={euro0(huidig)} accent="text-lime-300" icoon={Coins}
          sub={`gefactureerd in ${jaar}`}
          onder={<MiniTrend waarden={perMaand.slice(0, laatsteMaand + 1).map(m => m.commissie)} />} />
        <Kpi label="Geprojecteerd" waarde={euro0(verwacht)} accent="text-emerald-300" icoon={TrendingUp}
          sub={huidig > 0 ? `+${pct(((verwacht - huidig) / Math.max(1, huidig)) * 100)} t.o.v. nu` : 'incl. verwacht volume'} />
        <Kpi label="Nog te verdienen" waarde={euro0(projectie)} accent="text-cyan-300" icoon={Zap}
          sub={`${formatNumberRounded(restMwh)} MWh nog te factureren`} />
        <Kpi label="Doelstelling" waarde={doel ? euro0(doel) : '—'} accent="text-violet-300" icoon={Star}
          sub={verkoper !== 'all' ? 'doel van deze verkoper' : 'doel van het energielabel'} />
        <Kpi label="Voortgang naar doel" waarde={doel ? pct((huidig / doel) * 100) : '—'} icoon={BarChart3}
          accent={doel && huidig >= doel ? 'text-emerald-300' : 'text-slate-100'}
          sub={doel ? (huidig >= doel ? 'doel gehaald' : `nog ${euro0(Math.max(0, doel - huidig))} te gaan`) : 'geen doel ingesteld'}
          onder={<DoelBalk nu={huidig} extra={projectie} doel={doel} />} />
      </div>

      {/* De grafiek: opgebouwde commissie tot vandaag, projectie tot jaareinde. */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <h3 className="text-base font-semibold text-slate-100">COMMISSIE-OPBOUW {jaar}</h3>
          <div className="flex items-center gap-3 text-[11px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-lime-500 inline-block" />Opgebouwd</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-emerald-400 inline-block" />Geprojecteerd</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-500/60 inline-block" />Per maand</span>
          </div>
        </div>
        {delta != null && (
          <p className={`text-xs mb-2 ${delta >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
            {delta >= 0 ? '▲' : '▼'} {pct(Math.abs(delta))} tegenover de vorige maand — elke nieuwe verkoop tilt de projectie mee omhoog.
          </p>
        )}
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={grafiek} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="evOpbouw" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#84cc16" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#84cc16" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={70}
                tickFormatter={(v) => formatEuroRounded(v)} />
              <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => [formatCurrency(v), n]} />
              <Bar dataKey="maand" name="Per maand" fill="#64748b" fillOpacity={0.35} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              <Area type="monotone" dataKey="opgebouwd" name="Opgebouwd" stroke="#84cc16" strokeWidth={2.5} fill="url(#evOpbouw)" connectNulls isAnimationActive={false} />
              <Line type="monotone" dataKey="verwacht" name="Geprojecteerd" stroke="#34d399" strokeWidth={2.5}
                strokeDasharray="6 4" dot={false} connectNulls isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Ranglijst per verkoper */}
      <Card className="!p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-base font-semibold text-slate-100">RANGLIJST PER VERKOPER</h3>
            <ExportKnop onExport={exportRanglijst} disabled={ranglijst.length === 0} label="Exporteer deze tabel naar Excel" />
          </div>
          <span className="text-[11px] text-slate-500">Gesorteerd op geprojecteerde commissie</span>
        </div>
        {ranglijst.length === 0 ? <TableEmpty message="Geen commissie in dit jaar." icon={Folder} /> : (
          <div className="overflow-x-auto">
            {/* `resp-card`: op een telefoon wordt elke regel een kaartje met de
                kolomnaam ervoor — één kopregel, dus die weergave klopt hier wél. */}
            <table className="w-full text-sm resp-card">
              <thead><tr className="border-b border-slate-700">
                <th className="py-2 px-2 text-left text-slate-400 text-xs w-8">#</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Verkoper</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Huidige commissie</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Nog te verdienen</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Geprojecteerd</th>
                <th className="py-2 px-2 text-left text-slate-400 text-xs min-w-[140px]">Voortgang</th>
                <th className="py-2 px-2 text-center text-slate-400 text-xs">Trend</th>
              </tr></thead>
              <tbody>{ranglijst.map((r, i) => (
                <tr key={r.verkoper} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="py-2 px-2 text-slate-500 text-xs tabular-nums" data-label="#">{i + 1}</td>
                  <td className="py-2 px-2 text-slate-200 text-xs font-medium whitespace-nowrap" data-label="Verkoper">{r.verkoper}</td>
                  <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-medium" data-label="Huidige commissie">{euro2(r.huidig)}</td>
                  <td className="py-2 px-2 text-right text-cyan-300 text-xs tabular-nums" data-label="Nog te verdienen">{r.projectie ? `+ ${euro2(r.projectie)}` : '—'}</td>
                  <td className="py-2 px-2 text-right text-emerald-300 text-xs tabular-nums font-semibold" data-label="Geprojecteerd">{euro2(r.verwacht)}</td>
                  <td className="py-2 px-2" data-label="Voortgang">
                    <div className="flex items-center gap-2">
                      <DoelBalk nu={r.huidig} extra={r.projectie} doel={tarieven.doelJaarVerkoper} hoogte="h-1.5" />
                      {tarieven.doelJaarVerkoper > 0 && (
                        <span className={`text-[11px] tabular-nums whitespace-nowrap ${r.huidig >= tarieven.doelJaarVerkoper ? 'text-emerald-300' : 'text-slate-400'}`}>
                          {pct((r.huidig / tarieven.doelJaarVerkoper) * 100)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-2" data-label="Trend">
                    <div className="flex justify-center"><MiniTrend waarden={r.maanden.slice(0, laatsteMaand + 1)} /></div>
                  </td>
                </tr>
              ))}</tbody>
              <tfoot><tr className="border-t-2 border-slate-600 bg-lime-900/20">
                <td className="py-2 px-2" />
                <td className="py-2 px-2 text-lime-400 text-xs font-semibold">Totaal</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums font-semibold">{euro2(huidig)}</td>
                <td className="py-2 px-2 text-right text-lime-300 text-xs tabular-nums">{euro2(projectie)}</td>
                <td className="py-2 px-2 text-right text-lime-400 text-xs tabular-nums font-bold">{euro2(verwacht)}</td>
                <td className="py-2 px-2" colSpan="2" />
              </tr></tfoot>
            </table>
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-2">
          De huidige commissie komt regel voor regel uit dezelfde berekening als “KWH COMMISSIE PER VERKOPER” op de pagina hiernaast.
          De projectie telt per aansluiting het geschatte jaarvolume (Est. Offtake en Est. Injection) min wat er dit jaar al
          gefactureerd is, aan de prijszetting van die aansluiting; ontbreekt de schatting, dan projecteren we niets.
        </p>
      </Card>
    </div>
  );
};

// ── Partnervergoeding van één energielabel ophalen (gedeeld) ────────────────
// Gebruikt door de Revenue-pagina én door de opstart-prefetch in de app-root:
// Revenue hoort meteen cijfers te tonen, niet pas na een ophaal die begint zodra
// je de pagina opent.
//
// De server antwoordt uit zijn eigen cache. Alleen wanneer die nog LEEG is
// (`ready: false`) valt er iets te wachten — dan loopt de allereerste sync en
// pollen we de voortgang. Een sync die NAAST een gevulde cache loopt houdt niemand
// tegen; daar leest de server gewoon de bestaande rijen.
const fmtEta = (sec) => (sec >= 90 ? `~${Math.round(sec / 60)} min` : `~${Math.max(1, sec)}s`);

async function haalPartnervergoeding({ label, token, onVoortgang, geannuleerd }) {
  const gestopt = () => (typeof geannuleerd === 'function' && geannuleerd());
  const status = async () => {
    const r = await fetch(`/api/commission/data?label=${encodeURIComponent(label)}&limit=1`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    return r.json();
  };
  let d = await status();
  const t0 = Date.now();
  while (d.syncing && !d.ready && !gestopt()) {
    const avgMs = d.fetched > 0 ? (Date.now() - t0) / d.fetched : 0;
    const remSec = (d.total && d.fetched && avgMs) ? Math.round((d.total - d.fetched) * avgMs / 1000) : 0;
    onVoortgang?.(`Synchroniseren met API: ${d.fetched || 0}${d.total ? '/' + d.total : ''} records${remSec > 0 ? ` · ${fmtEta(remSec)} resterend` : ''}…`);
    await new Promise((r) => setTimeout(r, 2500));
    d = await status();
  }
  if (gestopt()) return null;
  onVoortgang?.('Gegevens laden…');
  const haal = (kind) => fetchPaginated(`/api/commission/data?label=${encodeURIComponent(label)}&kind=${kind}`, token);
  const [apiInvoices, apiSelfbills] = await Promise.all([haal('invoices'), haal('selfbills')]);
  const salesRecs = [];
  const creditRecs = [];
  for (const it of apiInvoices) {
    const isCredit = it.type === 'out_refund';
    (isCredit ? creditRecs : salesRecs).push(mapApiCommissionItem(it, { isCredit, label }));
  }
  // Self bills = alle klantensegmenten (B2C, B2B én Custom). De samenvattings-
  // blokken "Custom - EL Injection" her-filteren zelf op CUSTOM; de SELF BILLS-
  // tabel toont bewust alle segmenten.
  const sbRecs = apiSelfbills.map((it) => mapApiCommissionItem(it, { isCredit: false, label }));
  return {
    label,
    sales: salesRecs.length ? [{ name: `API · ${label}`, records: salesRecs, _apiLabel: label }] : [],
    credit: creditRecs.length ? [{ name: `API · ${label} (credit)`, records: creditRecs, _apiLabel: label }] : [],
    selfbill: sbRecs.length ? [{ name: `API · ${label} (SB)`, records: sbRecs, _apiLabel: label }] : [],
    info: { label, sales: salesRecs.length, credit: creditRecs.length, selfbill: sbRecs.length, syncedAt: d.synced_at },
  };
}

// ─── Partnervergoeding mailen (Revenue, alleen admin) ────────────────────────
// De knop met het mail-icoon naast "Export All" verstuurt de zip van de
// geselecteerde data naar de partner, met het sjabloon 'revenue_partner_fee'
// (Mailsjablonen, module Revenue) vanaf finadmin@energytogether.be in het
// ET-merk. Ontvangers komen als standaard uit Revenue → Admin → "Ontvanger Mail
// Partnervergoeding" (per energielabel of per verkoopteam); wijkt de admin
// daarvan af, dan bevestigt hij dat een tweede keer. Server: routes/revenueFeeMail.js.

const FEE_MAIL_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Vrije tekst → adressen (komma, puntkomma, spatie of nieuwe regel), ontdubbeld.
const feeMailParse = (tekst) => {
  const geldig = []; const ongeldig = [];
  for (const x of String(tekst || '').split(/[,;\s]+/)) {
    const a = x.trim().toLowerCase();
    if (!a) continue;
    if (!FEE_MAIL_EMAIL_RE.test(a)) { if (!ongeldig.includes(a)) ongeldig.push(a); continue; }
    if (!geldig.includes(a)) geldig.push(a);
  }
  return { geldig, ongeldig };
};
const feeMailZelfdeSet = (a, b) => {
  const A = new Set((a || []).map((x) => x.toLowerCase())); const B = new Set((b || []).map((x) => x.toLowerCase()));
  return A.size === B.size && [...A].every((x) => B.has(x));
};
const feeMailApi = async (token, pad, opties = {}) => {
  const r = await fetch(`/api/revenue/fee-mail${pad}`, { ...opties, headers: { Authorization: `Bearer ${token}`, ...(opties.body && !(opties.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}), ...(opties.headers || {}) } });
  const b = await r.json().catch(() => null);
  if (!r.ok) { const e = new Error((b && b.error) || `HTTP ${r.status}`); e.status = r.status; e.body = b; throw e; }
  return b;
};

// Invoerveld voor meerdere adressen: chips + een tekstvak; Enter, komma of
// blur voegt toe. Ongeldige invoer blijft in het vak staan met een melding.
const FeeMailAdresVeld = ({ label, waarde, onChange, placeholder, hint }) => {
  const [tekst, setTekst] = useState('');
  const [fout, setFout] = useState(null);
  const neemOp = () => {
    if (!tekst.trim()) return;
    const { geldig, ongeldig } = feeMailParse(tekst);
    if (geldig.length) onChange([...waarde, ...geldig.filter((a) => !waarde.includes(a))]);
    setTekst(ongeldig.join(', '));
    setFout(ongeldig.length ? `Geen geldig adres: ${ongeldig.join(', ')}` : null);
  };
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}{hint && <span className="text-slate-500"> — {hint}</span>}</label>
      <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg min-h-[38px]">
        {waarde.map((a) => (
          <span key={a} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-slate-600/60 border border-slate-500/50 rounded text-xs text-slate-100">
            {a}<button type="button" onClick={() => onChange(waarde.filter((x) => x !== a))} title="Verwijderen" className="text-slate-400 hover:text-red-400"><X size={12} /></button>
          </span>
        ))}
        <input value={tekst} onChange={(e) => { setTekst(e.target.value); setFout(null); }} onBlur={neemOp}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); neemOp(); } if (e.key === 'Backspace' && !tekst && waarde.length) onChange(waarde.slice(0, -1)); }}
          placeholder={waarde.length ? 'Nog een adres…' : placeholder} className="flex-1 min-w-[160px] bg-transparent text-sm text-slate-100 outline-none" />
      </div>
      {fout && <div className="text-xs text-red-300 mt-1">{fout}</div>}
    </div>
  );
};

// Het verzendvenster. Stappen: opstellen (ontvangers, sjabloon, onderwerp en
// inhoud — bewerkbaar — met een voorbeeld van de mail) → bij een afwijking van
// de standaardontvangers eerst een waarschuwing → versturen.
// Sjabloon: elk actief sjabloon van module Revenue dat bij dit label/team past;
// het huidige (meest specifieke voor 'revenue_partner_fee') staat als standaard.
const FeeMailModal = ({ open, onClose, token, zip, selectie, onSent }) => {
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [standaard, setStandaard] = useState(null); // { to, bcc, bron }
  const [preview, setPreview] = useState(null);    // { subject, html, from, fromName, templateName, … }
  const [to, setTo] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [stap, setStap] = useState('opstellen');   // 'opstellen' | 'bevestigen'
  const [bezig, setBezig] = useState(false);
  // Sjabloonkeuze + bewerkbare tekst. `basis` = de inhoud zoals de editor ze na
  // het laden normaliseert; alleen een verschil daarmee gaat als bewerking mee.
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [subjectBasis, setSubjectBasis] = useState('');
  const [inhoud, setInhoud] = useState('');
  const [basis, setBasis] = useState('');
  const [editorVersie, setEditorVersie] = useState(0);
  const [weergave, setWeergave] = useState('inhoud'); // 'inhoud' | 'voorbeeld'
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewBezig, setPreviewBezig] = useState(false);
  const { energyLabel, salesTeam, salesperson } = selectie || {};
  const q = useMemo(() => new URLSearchParams({ label: energyLabel || '', team: salesTeam || '', seller: salesperson || '', file: zip?.naam || '' }), [energyLabel, salesTeam, salesperson, zip?.naam]);

  const inhoudBewerkt = inhoud !== basis;
  const subjectBewerkt = subject.trim() !== subjectBasis.trim();

  // Sjabloon (opnieuw) laden: tekst en voorbeeld terug naar het sjabloon.
  const laadSjabloon = async (id) => {
    const p = await feeMailApi(token, `/preview?${q}${id ? `&templateId=${id}` : ''}`);
    setPreview(p); setPreviewHtml(p.html);
    setSubject(p.subject); setSubjectBasis(p.subject);
    setInhoud(p.contentHtml); setBasis(p.contentHtml);
    setEditorVersie((v) => v + 1);
    setTemplateId(String(p.templateId));
    return p;
  };

  useEffect(() => {
    if (!open) return;
    let weg = false;
    setLaden(true); setFout(null); setStap('opstellen'); setPreview(null); setWeergave('inhoud');
    Promise.allSettled([feeMailApi(token, `/defaults?${q}`), feeMailApi(token, `/templates?${q}`)])
      .then(async ([d, t]) => {
        if (weg) return;
        // De standaard blijft staan, ook als het sjabloon niet lukt: dan weet de
        // admin tenminste naar wie het zou gaan.
        if (d.status === 'fulfilled') { setStandaard(d.value); setTo(d.value.to || []); setBcc(d.value.bcc || []); } else setFout(d.reason.message);
        if (t.status === 'fulfilled') {
          setTemplates(t.value.templates || []);
          try { await laadSjabloon(t.value.defaultId); } catch (e) { if (!weg) setFout(e.message); }
        } else setFout(t.reason.message);
      })
      .finally(() => { if (!weg) setLaden(false); });
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token, q]);

  // Voorbeeld van de BEWERKTE tekst, opgehaald bij het omschakelen naar de
  // voorbeeld-weergave (niet bij elke toetsaanslag).
  const toonVoorbeeld = async () => {
    setWeergave('voorbeeld');
    if (!subjectBewerkt && !inhoudBewerkt) return;
    setPreviewBezig(true);
    try {
      const p = await feeMailApi(token, '/preview', { method: 'POST', body: JSON.stringify({ label: energyLabel, team: salesTeam, seller: salesperson, file: zip?.naam, templateId, subject: subjectBewerkt ? subject : undefined, contentHtml: inhoudBewerkt ? inhoud : undefined }) });
      setPreviewHtml(p.html);
    } catch (e) { setFout(e.message); }
    finally { setPreviewBezig(false); }
  };
  const wisselSjabloon = async (id) => {
    if (id === templateId) return;
    if ((subjectBewerkt || inhoudBewerkt) && !window.confirm('Je bewerkingen aan onderwerp en inhoud gaan verloren bij het wisselen van sjabloon. Doorgaan?')) return;
    setFout(null);
    try { await laadSjabloon(id); } catch (e) { setFout(e.message); }
  };
  const herstel = async () => {
    if (!window.confirm('Onderwerp en inhoud terugzetten naar het sjabloon?')) return;
    try { await laadSjabloon(templateId); } catch (e) { setFout(e.message); }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !bezig) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, bezig]);

  if (!open) return null;
  const afwijkend = !standaard || !standaard.bron || !feeMailZelfdeSet(to, standaard.to) || !feeMailZelfdeSet(bcc, standaard.bcc);
  const kanVersturen = !laden && !bezig && !!preview && to.length > 0 && !!zip && subject.trim().length > 0;
  const bronTekst = standaard?.bron === 'team' ? `ingesteld voor verkoopteam ${salesTeam}` : standaard?.bron === 'label' ? `ingesteld voor energielabel ${energyLabel}` : 'geen standaardontvanger ingesteld (Revenue → Admin → Ontvanger Mail Partnervergoeding)';

  const verstuur = async () => {
    if (stap === 'opstellen' && afwijkend) { setStap('bevestigen'); return; }
    setBezig(true); setFout(null);
    try {
      const fd = new FormData();
      fd.append('file', new window.File([zip.blob], zip.naam, { type: 'application/zip' }));
      fd.append('energy_label', energyLabel || '');
      fd.append('sales_team', salesTeam || '');
      fd.append('salesperson', salesperson || '');
      fd.append('to', JSON.stringify(to));
      fd.append('bcc', JSON.stringify(bcc));
      fd.append('bevestigdAfwijkend', afwijkend ? 'true' : 'false');
      fd.append('templateId', templateId || '');
      // Alleen wat écht bewerkt is gaat mee; anders blijft het sjabloon de bron.
      if (subjectBewerkt) fd.append('subject', subject);
      if (inhoudBewerkt) fd.append('contentHtml', inhoud);
      const r = await feeMailApi(token, '/send', { method: 'POST', body: fd });
      toast.success(`Partnervergoeding verstuurd naar ${r.to.join(', ')}${r.bcc?.length ? ` (bcc ${r.bcc.join(', ')})` : ''}.`);
      onSent?.(r);
      onClose();
    } catch (e) {
      if (e.status === 409 && e.body?.afwijkend) { setStandaard(e.body.standaard); setStap('bevestigen'); }
      else setFout(e.message);
    } finally { setBezig(false); }
  };

  const grootte = zip ? (zip.blob.size >= 1024 * 1024 ? `${(zip.blob.size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(zip.blob.size / 1024))} KB`) : '';
  const tabKnop = (id, label) => (
    <button type="button" onClick={() => (id === 'voorbeeld' ? toonVoorbeeld() : setWeergave(id))}
      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${weergave === id ? 'bg-lime-500 text-slate-900' : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600/60'}`}>{label}</button>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-3 sm:p-6">
      <button className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" aria-label="Sluiten" onClick={() => !bezig && onClose()} />
      <div className="relative w-full max-w-4xl my-2 flex flex-col max-h-[92vh] bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-5 py-3.5 border-b border-slate-700/70 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-100 uppercase tracking-wide flex items-center gap-2"><Mail size={15} className="text-lime-400" />Partnervergoeding mailen</h3>
            <p className="text-xs text-slate-400 mt-0.5">{[energyLabel, salesTeam, salesperson].filter(Boolean).join(' · ')} — zip van de huidige selectie als bijlage</p>
          </div>
          <button type="button" onClick={onClose} disabled={bezig} aria-label="Sluiten" className="shrink-0 text-slate-400 hover:text-slate-100"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{fout}</div>}
          {laden ? <div className="flex items-center gap-2 text-sm text-slate-400"><Spinner size={16} />Standaardontvangers en sjabloon laden…</div> : stap === 'bevestigen' ? (
            <div className="space-y-3">
              <div className="bg-amber-500/10 border border-amber-500/40 text-amber-200 px-4 py-3 rounded-lg text-sm space-y-2">
                <div className="flex items-center gap-2 font-semibold"><AlertCircle size={16} />De ontvangers wijken af van de ingestelde standaard</div>
                <p className="text-amber-100/90">De zip bevat de partnervergoeding van <strong>{[energyLabel, salesTeam, salesperson].filter(Boolean).join(' · ')}</strong>. Controleer of ze naar de juiste partner gaat.</p>
                <div className="grid sm:grid-cols-2 gap-3 text-xs">
                  <div className="bg-slate-900/40 rounded p-2">
                    <div className="text-slate-400 mb-1">Ingesteld ({standaard?.bron ? bronTekst : 'niets'})</div>
                    <div className="text-slate-200">{standaard?.to?.length ? standaard.to.join(', ') : '—'}</div>
                    {standaard?.bcc?.length > 0 && <div className="text-slate-400 mt-0.5">bcc: {standaard.bcc.join(', ')}</div>}
                  </div>
                  <div className="bg-slate-900/40 rounded p-2">
                    <div className="text-slate-400 mb-1">Je verstuurt naar</div>
                    <div className="text-slate-100 font-medium">{to.join(', ')}</div>
                    {bcc.length > 0 && <div className="text-slate-400 mt-0.5">bcc: {bcc.join(', ')}</div>}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <FeeMailAdresVeld label="Aan" waarde={to} onChange={setTo} placeholder="adres@partner.be" hint={standaard?.bron ? 'standaard ingevuld' : 'geen standaard ingesteld'} />
                <FeeMailAdresVeld label="Bcc" waarde={bcc} onChange={setBcc} placeholder="optioneel" />
              </div>
              <div className="text-xs text-slate-500">Standaard: {bronTekst}.{afwijkend && standaard?.bron ? ' Je wijkt hiervan af — je krijgt nog een bevestiging.' : ''}</div>

              <div className="grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 border-t border-slate-700/60 pt-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Sjabloon <span className="text-slate-500">(module Revenue)</span></label>
                  <select value={templateId} onChange={(e) => wisselSjabloon(e.target.value)} className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100">
                    {templates.map((t) => <option key={t.id} value={String(t.id)}>{t.name}{t.energy_label ? ` · ${t.energy_label}${t.sales_team ? ` / ${t.sales_team}` : ''}` : ''}{t.language && t.language !== 'nl' ? ` (${t.language})` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Onderwerp{subjectBewerkt && <span className="text-amber-300"> — aangepast</span>}</label>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={300} className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100" />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {tabKnop('inhoud', 'Inhoud bewerken')}
                {tabKnop('voorbeeld', 'Voorbeeld')}
                {inhoudBewerkt && <span className="text-xs text-amber-300">Inhoud aangepast t.o.v. het sjabloon</span>}
                {(subjectBewerkt || inhoudBewerkt) && <button type="button" onClick={herstel} className="text-xs text-slate-400 hover:text-lime-300 underline">Terug naar het sjabloon</button>}
                <span className="ml-auto text-[11px] text-slate-500">De bewerking geldt alleen voor deze verzending; het sjabloon zelf blijft ongewijzigd.</span>
              </div>
              {/* Beide weergaven blijven gemount (hidden): een editor die opnieuw mount
                  zou zijn inhoud opnieuw als basis nemen en de bewerking "vergeten". */}
              <div className={weergave === 'inhoud' ? '' : 'hidden'}>
                <HdVisueleEditor html={inhoud} versie={editorVersie} onChange={setInhoud} onSync={(h) => { setBasis(h); setInhoud(h); }} />
              </div>
              {weergave === 'voorbeeld' && (
                <div className="relative">
                  {previewBezig && <div className="absolute inset-0 flex items-center justify-center bg-slate-900/40 rounded-lg"><Spinner size={18} /></div>}
                  <iframe title="Voorbeeld van de mail" srcDoc={previewHtml || ''} sandbox="" className="w-full h-[380px] bg-white rounded-lg border border-slate-700" />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 border-t border-slate-700/60 pt-3">
                <span>Van: <span className="text-slate-200">{preview?.fromName} &lt;{preview?.from}&gt;</span></span>
                {preview?.replyTo && <span>Antwoord aan: <span className="text-slate-200">{preview.replyTo}</span></span>}
                <span className="inline-flex items-center gap-1"><FileArchive size={12} />Bijlage: <span className="text-slate-200">{zip?.naam}</span> ({grootte})</span>
              </div>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-3 border-t border-slate-700/70 shrink-0">
          {stap === 'bevestigen' && <button type="button" onClick={() => setStap('opstellen')} disabled={bezig} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm rounded-lg">Terug</button>}
          <button type="button" onClick={onClose} disabled={bezig} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm rounded-lg">Annuleren</button>
          <button type="button" onClick={verstuur} disabled={!kanVersturen}
            className={`flex items-center gap-2 px-4 py-1.5 font-semibold text-sm rounded-lg disabled:opacity-50 ${stap === 'bevestigen' ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' : 'bg-lime-500 hover:bg-lime-400 text-slate-900'}`}>
            {bezig ? <Spinner size={14} /> : <Send size={14} />}{bezig ? 'Versturen…' : stap === 'bevestigen' ? 'Ja, toch versturen' : 'Versturen'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Beheertabel "Ontvanger Mail Partnervergoeding" (Revenue → Admin): per
// energielabel of per (energielabel, verkoopteam) de ontvangers en bcc.
const FeeMailRecipientsCard = ({ token, labels = [], salesStructure = EMPTY_STRUCTURE }) => {
  const [rijen, setRijen] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);
  const leeg = { energy_label: '', sales_team: '', to: [], bcc: [] };
  const [form, setForm] = useState(leeg);
  const [bewerkId, setBewerkId] = useState(null);
  const teams = useMemo(() => (form.energy_label ? scopeOptions(salesStructure, form.energy_label, 'all').teams : []), [salesStructure, form.energy_label]);

  const load = async () => {
    setLaden(true); setFout(null);
    try { setRijen((await feeMailApi(token, '/recipients')).recipients || []); }
    catch (e) { setFout(e.message); }
    finally { setLaden(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const bewaar = async () => {
    setBezig(true); setFout(null);
    try {
      await feeMailApi(token, '/recipients', { method: 'PUT', body: JSON.stringify({ energy_label: form.energy_label, sales_team: form.sales_team || null, to: form.to, bcc: form.bcc }) });
      toast.success('Ontvanger bewaard.');
      setForm(leeg); setBewerkId(null);
      load();
    } catch (e) { setFout(e.message); }
    finally { setBezig(false); }
  };
  const verwijder = async (r) => {
    if (!window.confirm(`Ontvanger voor ${r.energy_label}${r.sales_team ? ` · ${r.sales_team}` : ''} verwijderen?`)) return;
    try { await feeMailApi(token, `/recipients/${r.id}`, { method: 'DELETE' }); load(); }
    catch (e) { setFout(e.message); }
  };
  const bewerk = (r) => { setBewerkId(r.id); setForm({ energy_label: r.energy_label, sales_team: r.sales_team || '', to: [...r.to], bcc: [...r.bcc] }); };

  return (
    <Card className="!p-4">
      <h3 className="text-base font-semibold text-slate-100 mb-1 flex items-center gap-1.5">
        ONTVANGER MAIL PARTNERVERGOEDING
        <InfoTip title="Ontvanger Mail Partnervergoeding" text={'Alleen admin. De knop met het mail-icoon naast Export All op deze pagina verstuurt de zip van de geselecteerde data naar de partner, vanaf finadmin@energytogether.be met het sjabloon "Partnervergoeding — nieuwe vergoeding beschikbaar" (Mailsjablonen, module Revenue).\n\nHier stel je per energielabel — of specifieker per verkoopteam — in naar wie die mail standaard gaat, met eventueel een bcc. Staat de selectie op een verkoopteam met een eigen rij, dan wint die rij; anders geldt de rij van het energielabel. De adressen worden bij het versturen als standaard ingevuld en kunnen nog aangepast worden; wijkt de admin af van de standaard, dan vraagt het portaal een tweede bevestiging.'} />
      </h3>
      <p className="text-xs text-slate-400 mb-3">Standaardontvangers van de partnervergoeding-mail (zip), per energielabel of per verkoopteam. Meerdere adressen mogen, gescheiden door een komma.</p>
      {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm mb-3">{fout}</div>}

      <div className="grid md:grid-cols-[184px_184px_1fr_1fr_auto] gap-2 items-end mb-3">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Energielabel</label>
          <select value={form.energy_label} onChange={(e) => setForm((f) => ({ ...f, energy_label: e.target.value, sales_team: '' }))} disabled={bewerkId != null}
            className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 disabled:opacity-60">
            <option value="">Kies energielabel…</option>
            {labels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Verkoopteam <span className="text-slate-500">(leeg = hele label)</span></label>
          <select value={form.sales_team} onChange={(e) => setForm((f) => ({ ...f, sales_team: e.target.value }))} disabled={bewerkId != null || !form.energy_label}
            className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 disabled:opacity-60">
            <option value="">Alle verkoopteams</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <FeeMailAdresVeld label="Ontvangers" waarde={form.to} onChange={(v) => setForm((f) => ({ ...f, to: v }))} placeholder="finance@partner.be" />
        <FeeMailAdresVeld label="Bcc" waarde={form.bcc} onChange={(v) => setForm((f) => ({ ...f, bcc: v }))} placeholder="optioneel" />
        <div className="flex items-center gap-2">
          <button onClick={bewaar} disabled={bezig || !form.energy_label || !form.to.length}
            className="flex items-center gap-2 px-4 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-slate-900 font-medium text-sm rounded-lg">
            {bewerkId != null ? <Check size={16} /> : <Plus size={16} />}{bewerkId != null ? 'Bewaren' : 'Toevoegen'}
          </button>
          {bewerkId != null && <button onClick={() => { setBewerkId(null); setForm(leeg); }} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm rounded-lg">Annuleren</button>}
        </div>
      </div>

      {laden ? <ListSkeleton rows={3} /> : rijen.length === 0 ? (
        <div className="text-sm text-slate-500">Nog geen ontvanger ingesteld — bij het versturen moet de admin dan zelf een adres invullen (met bevestiging).</div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
          <thead className="bg-slate-800 text-slate-400 text-xs"><tr>
            <th className="px-3 py-2 font-medium text-left">Energielabel</th>
            <th className="px-3 py-2 font-medium text-left">Verkoopteam</th>
            <th className="px-3 py-2 font-medium text-left">Ontvangers</th>
            <th className="px-3 py-2 font-medium text-left">Bcc</th>
            <th className="px-3 py-2 font-medium text-left">Gewijzigd</th>
            <th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {rijen.map((r) => (
              <tr key={r.id} className={`border-t border-slate-700/40 ${bewerkId === r.id ? 'bg-lime-500/5' : ''}`}>
                <td className="px-3 py-2 text-slate-200 whitespace-nowrap">{r.energy_label}</td>
                <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.sales_team || <span className="text-slate-500">Alle verkoopteams</span>}</td>
                <td className="px-3 py-2 text-slate-200">{r.to.join(', ')}</td>
                <td className="px-3 py-2 text-slate-400">{r.bcc.length ? r.bcc.join(', ') : '—'}</td>
                <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{fbFmt(r.updated_at)}{r.updated_by_name ? ` · ${r.updated_by_name}` : ''}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => bewerk(r)} title="Bewerken" className="text-slate-400 hover:text-lime-300 mr-2"><Pencil size={15} /></button>
                  <button onClick={() => verwijder(r)} title="Verwijderen" className="text-slate-500 hover:text-red-400"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
};

const RevenuePage = ({ facturen, selfBills = [], orders = [], ordersLoading = false, onOpenCustomer360, onSettings, currentUser, salesStructure = EMPTY_STRUCTURE, onCommissionFromApi, onClearAllCommission, commissionMeta = {}, token, feeNotAllowedTags = new Set(), visiblePartnerTags = null, partnerTagAll = [], onPartnerTagsChanged, activeBranding = null, allBranding = [], allTeamBranding = [], labelConfig = {}, onLabelConfigChanged }) => {
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [showDocs, setShowDocs] = useState(false);
  const [showTechDocs, setShowTechDocs] = useState(false);
  const [showMenukaart, setShowMenukaart] = useState(false);
  // Revenue heeft eigen, rol-bewuste documentatie (RevenueDocs/RevenueTechDocs)
  // i.p.v. de generieke uit moduleDocs.js. Zelfde vorm als elders: icoontjes naast
  // de titel + overlay. `padded={false}` want beide panelen brengen hun eigen kaart
  // mee. Beide returns van deze pagina gebruiken dezelfde twee fragmenten.
  //
  // LET OP: dit zijn FUNCTIES, geen kant-en-klare JSX-constanten. De panelen lezen
  // `energielabelFilter`, dat verderop in deze component pas met useState wordt
  // aangemaakt; als losse const zou dat blok bij het renderen meteen worden
  // uitgevoerd en op een temporal-dead-zone-fout knallen. Als functie wordt de
  // inhoud pas bij het opbouwen van de return geëvalueerd, dus ná alle state.

  // De FUNCTIONELE beschrijving staat per gebruiker aan of uit (users.revenue_docs,
  // in te stellen bij Gebruikers). Standaard: aan voor admin, ET-dossierbeheerder
  // en elke master behalve die van HOA Energy; uit voor teamleader en verkoper.
  // Zie toontRevenueDocs in revenueDocs.js. Staat hij uit, dan komt noch het
  // icoontje noch het paneel in de DOM. De Menukaart en de technische beschrijving
  // volgen hun eigen regel en blijven ongemoeid.
  const magDocsZien = toontRevenueDocs(currentUser);
  const revenueDocsIcons = () => <>
    {magDocsZien && <RevenueDocsButton onToggle={() => setShowDocs(true)} />}
    <RevenueMenukaartButton onToggle={() => setShowMenukaart(true)} />
    <RevenueTechDocsButton onToggle={() => setShowTechDocs(true)} currentUser={currentUser} />
  </>;
  const revenueDocsModals = () => <>
    {/* energyLabel: de functionele beschrijving toont ALLEEN de logica van het
        geselecteerde energielabel, nooit die van andere labels. */}
    <DocsModal open={magDocsZien && showDocs} onClose={() => setShowDocs(false)} title="Functionele beschrijving" subtitle="Partnervergoeding — velden en berekeningen" padded={false}>
      <RevenueDocs open currentUser={currentUser} energyLabel={energielabelFilter} />
    </DocsModal>
    <DocsModal open={showMenukaart} onClose={() => setShowMenukaart(false)} title="Menukaart" subtitle="Alle fees, excl. btw" padded={false}>
      <RevenueMenukaartDocs open currentUser={currentUser} energyLabel={energielabelFilter} />
    </DocsModal>
    <DocsModal open={showTechDocs} onClose={() => setShowTechDocs(false)} title="Technische beschrijving" subtitle="Alleen zichtbaar voor beheerders" padded={false}>
      <RevenueTechDocs open currentUser={currentUser} />
    </DocsModal>
  </>;
  // Partnervergoeding via de ET API (alternatief voor de bestand-upload).
  const [apiBusy, setApiBusy] = useState(false);
  const [apiProgress, setApiProgress] = useState('');
  const [apiError, setApiError] = useState('');
  const cancelApiRef = useRef(false);
  // GDPR-KRITISCH: data mag NOOIT tussen energielabels gedeeld worden. De
  // API-label-keuze toont daarom voor élke rol BEHALVE admin en ET
  // Dossierbeheerder uitsluitend het aan de gebruiker gekoppelde energielabel.
  // We leiden dit hard af uit currentUser (niet uit salesStructure), en de
  // backend dwingt de scope nogmaals af bij /api/commission/page.
  const apiLabelOptions = useMemo(() => {
    // "Energy Together B.V." is de Odoo-entiteit (geen echt energielabel) en komt
    // als reseller-bedrijf mee in de structuur — niet tonen in de Revenue-filter.
    const isOdooEntity = (l) => (l || '').trim().toLowerCase().startsWith('energy together');
    // Belvus Energie hoort niet in Revenue (op verzoek), net zoals het al uit Sales
    // en Portfolio Insights gehouden wordt. Zie ook REVENUE_EXCLUDED_LABELS in de
    // backend: daar wordt de partnervergoeding voor dit label ook niet gesynct, dus
    // hem hier tonen zou een lege pagina opleveren.
    const uitgesloten = (l) => isOdooEntity(l) || (l || '').trim().toLowerCase() === 'belvus energie';
    const fullAccess = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
    if (fullAccess) return [...(salesStructure?.energy_labels || [])].filter(l => !uitgesloten(l)).sort();
    return currentUser?.energyLabel && !uitgesloten(currentUser.energyLabel) ? [currentUser.energyLabel] : [];
  }, [currentUser, salesStructure]);
  // Ophaal-knoppen. Bewust compact en direct naast de paginatitel: het zijn
  // secundaire acties bij het energielabel, geen paginabrede werkbalk. De
  // statusregel staat apart (CommissionStatus) — die kan lang worden en hoort niet
  // in een titelbalk.
  const CommissionButtons = () => {
    // Enkel de API-ophaal (bestand-upload is verwijderd — er wordt altijd via de
    // API/jobs gewerkt). Alleen admin: het ophalen vervangt de cache voor iedereen
    // met dat energielabel en duurt minuten. Partners krijgen de data via de
    // geplande synchronisatie; zij zien enkel de laadstatus.
    if (currentUser?.role !== 'admin') return null;
    if (!onCommissionFromApi) return null;
    const kanOphalen = apiLabelOptions.includes(energielabelFilter);
    return (
      <div className="flex items-center gap-1.5">
        <button onClick={apiBusy ? cancelApi : () => fetchCommissionViaApi()}
          disabled={!apiBusy && !kanOphalen}
          title={!kanOphalen
            ? 'Kies eerst een concreet energielabel in de filter hieronder om de data op te halen'
            : 'Laadt de partnervergoeding voor het gekozen energielabel uit de lokale cache (instant). Nog niet gesynct? Dan haalt de server het eenmalig uit de API.'}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${apiBusy ? 'bg-amber-700/50 hover:bg-red-700/50 text-amber-100' : 'bg-blue-700/40 hover:bg-blue-700/60 text-blue-100'}`}>
          {apiBusy ? <X size={13} /> : <Download size={13} />}{apiBusy ? 'Annuleren' : 'Data ophalen'}
        </button>
      </div>
    );
  };

  // De abonnementsvergoeding komt uit de ORDERS, niet uit de facturen. Zolang die
  // nog binnenkomen is dat deel van de cijfers onvolledig; dat zeggen we expliciet
  // in plaats van een half totaal te tonen alsof het klaar is.
  const OrdersLaadStatus = () => {
    if (!ordersLoading) return null;
    return (
      <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-1.5">
        <Spinner size={12} />
        Orders worden nog geladen — de abonnementsvergoeding is nog niet compleet.
      </div>
    );
  };

  const CommissionStatus = () => {
    const apiLoaded = commissionMeta.source === 'api';
    const a = commissionMeta.api;
    if (!onCommissionFromApi) return null;
    if (apiError) return <div className="text-xs text-red-400">{apiError}</div>;
    if (apiBusy) return <div className="text-xs text-slate-400 flex items-center gap-2"><Spinner size={12} />{apiProgress}</div>;
    if (apiLoaded && a) return <div className="text-xs text-blue-300 flex items-center gap-1.5 flex-wrap"><Download size={12} />Geladen via API{a.labels?.length ? ` — ${a.labels.join(', ')}` : ''} · {commissionMeta.salesCount} invoices, {commissionMeta.creditCount} credit, {commissionMeta.selfbillCount} self-bills{a.syncedAt ? ` · cache van ${a.syncedAt}` : ''}{onClearAllCommission && <button onClick={onClearAllCommission} title="Alle via API geladen data wissen" className="ml-1 text-slate-400 hover:text-red-400 underline">wissen</button>}</div>;
    return null;
  };
  // De vroegere "Fee Menu rapport"-download is vervangen door de Menukaart-sectie
  // in de Functionele specificatie (revenueDocs.js).
  const [verkoperSegPage, setVerkoperSegPage] = useState(1);
  const [detailPage, setDetailPage] = useState(1);
  // Tabel "Gefactureerde Volume per EAN" (onder DETAIL PER FACTUUR): eigen
  // paginatie en sortering, maar dezelfde gefilterde rijen.
  const [volumePage, setVolumePage] = useState(1);
  const [detailEtPage, setDetailEtPage] = useState(1);
  const [selfBillPage, setSelfBillPage] = useState(1);
  const [selfBillEtPage, setSelfBillEtPage] = useState(1);
  // Kolom-sortering per Revenue-datatabel (klik op kolomkop).
  const detailSort = useTableSort();
  // Volume-tabel start op EL GSRN: de facturen van dezelfde aansluiting horen
  // onder elkaar te staan, anders zegt het totaal in de laatste kolom niets.
  const volumeSort = useTableSort('elGSRN');
  const detEtSort = useTableSort();
  const sbSort = useTableSort();
  const sbEtSort = useTableSort();
  const vkSegSort = useTableSort();
  // Weergave-switch voor de "Vergelijking per categorie"-grafiek (bestaande data).
  const [comparisonMode, setComparisonMode] = useState('vergoeding'); // 'vergoeding' | 'volume'
  // Extra filters voor de detail-per-factuur tabel.
  const [detailSearch, setDetailSearch] = useState('');
  // Soort factuur (Verkoopfactuur / Creditnota / Selfbill) — zelfde mechaniek als
  // het gelijknamige filter op de Facturen-pagina: opties uit de data, en terug
  // naar "Alle" zodra de gekozen soort door een scope-wissel verdwijnt.
  const [detailSoortFilter, setDetailSoortFilter] = useState('all');
  const [detailStatusFilter, setDetailStatusFilter] = useState('all');
  const [detailCpsFilter, setDetailCpsFilter] = useState('all');
  const [detailSegmentFilter, setDetailSegmentFilter] = useState('all'); // B2C/B2B/Custom
  const [detailMarketFilter, setDetailMarketFilter] = useState('all');   // EL/NG
  const [detailFeeTagFilter, setDetailFeeTagFilter] = useState('all');   // Yes/No
  // Extra filters voor de self bills (elektriciteit) tabel.
  const [sbSearch, setSbSearch] = useState('');
  const [sbStatusFilter, setSbStatusFilter] = useState('all');
  const [sbCpsFilter, setSbCpsFilter] = useState('all');
  const [sbSegmentFilter, setSbSegmentFilter] = useState('all'); // B2C/B2B/Custom
  const [sbFeeTagFilter, setSbFeeTagFilter] = useState('all');   // Yes/No
  const [energielabelFilter, setEnergielabelFilter] = useState('Evident Energie');
  const [revTab, setRevTab] = useState('samenvatting'); // horizontaal tab-menu binnen Revenue
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [verkoperFilter, setVerkoperFilter] = useState('all');
  // Scope-deel van ELKE bestandsnaam op deze pagina (losse exports, de zip van
  // "Export All" en het PDF-rapport erin): het gekozen energielabel, met
  // verkoopteam en verkoper er meteen achter zodra die de data beperken →
  // "Evident Energie-HOA Team-Jan Jansen". Bij "Alle energielabels" blijft de
  // eerste positie gevuld ('alle'), zodat de volgorde label-team-verkoper klopt.
  // Exportnamen: <timestamp>_<energielabel>[-team][-verkoper]_<basis> — zie
  // src/exportNaam.js, waar die volgorde is vastgelegd en getest.
  // Het RUWE label; exportBestandsnaam zet er zelf de underscore achter.
  const exportLabel = energielabelFilter === 'all' ? '' : energielabelFilter;
  // 'all' in de filter betekent niet "alle data": een teamleader ziet met "Alle
  // verkoopteams" nog altijd enkel zijn eigen team, een reseller enkel zijn eigen
  // verkopen. Zonder deze terugval zou hun export "Evident Energie" heten terwijl
  // er maar één team in zit, en zou het rapport "Verkoopteam: Alle" tonen —
  // precies de verwarring die we willen vermijden. Voor admin/ET Dossierbeheerder
  // staan die velden leeg en verandert er niets zolang ze niet filteren.
  // Leeg = werkelijk alles; deze twee sturen zowel de bestandsnamen als de
  // filterregel bovenaan het PDF-rapport, zodat die elkaar niet kunnen tegenspreken.
  const [effectiefTeam, effectieveVerkoper] = useMemo(() => {
    const eigen = defaultScopeSelection(currentUser);
    return [
      verkoopteamFilter !== 'all' ? verkoopteamFilter : (eigen.team !== 'all' ? eigen.team : ''),
      verkoperFilter !== 'all' ? verkoperFilter : (eigen.seller !== 'all' ? eigen.seller : ''),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verkoopteamFilter, verkoperFilter, currentUser?.role, currentUser?.salesTeam, currentUser?.sellerName]);
  const exportScope = useMemo(
    () => exportScopeStaart([effectiefTeam, effectieveVerkoper]),
    [effectiefTeam, effectieveVerkoper]
  );
  // Tijdstempel vooraan elke exportnaam. Tijdens een "Export All" staat er één
  // stempel vast in de ref: anders zouden de zip, de CSV's erin en het PDF-rapport
  // (dat pas 300 ms later opent) op een minuutgrens uit elkaar kunnen lopen.
  // Een losse tabel-export heeft geen ref en stempelt gewoon het moment zelf.
  const exportStampRef = useRef(null);
  const exportStamp = () => exportStampRef.current || exportTimestamp();
  // "Export All" wacht nu op de server die het PDF-rapport rendert (~1-3 s), dus
  // de knop moet tonen dat er iets loopt en niet twee keer te starten zijn.
  const [exportBusy, setExportBusy] = useState(false);
  // Partnervergoeding mailen: de zip + selectie voor het verzendvenster (alleen admin).
  const [feeMail, setFeeMail] = useState(null); // { zip: { blob, naam }, selectie }
  const [feeMailBusy, setFeeMailBusy] = useState(false);
  // Default cascade-selectie op de scope van de ingelogde gebruiker. Admin/ET
  // behouden 'Evident Energie' als start-label (bestaand gedrag); lagere rollen
  // krijgen hun eigen energielabel voorgeselecteerd (GDPR: ze mogen enkel dat
  // label) + team (teamleader) + verkoper (reseller). Herhaalt bij user-wissel
  // (impersonate) zodra /api/auth/me de scope-velden levert.
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    if (d.company !== 'all') setEnergielabelFilter(d.company);
    setVerkoopteamFilter(d.team);
    setVerkoperFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  useFilterPersistence('revenue.filters', currentUser,
    { energielabelFilter, verkoopteamFilter, verkoperFilter, detailSearch, detailStatusFilter, detailSoortFilter, detailCpsFilter, detailSegmentFilter, detailMarketFilter, detailFeeTagFilter, sbSearch, sbStatusFilter, sbCpsFilter, sbSegmentFilter, sbFeeTagFilter },
    (s) => { setEnergielabelFilter(s.energielabelFilter ?? 'Evident Energie'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); setDetailSearch(s.detailSearch ?? ''); setDetailStatusFilter(s.detailStatusFilter ?? 'all'); setDetailSoortFilter(s.detailSoortFilter ?? 'all'); setDetailCpsFilter(s.detailCpsFilter ?? 'all'); setDetailSegmentFilter(s.detailSegmentFilter ?? 'all'); setDetailMarketFilter(s.detailMarketFilter ?? 'all'); setDetailFeeTagFilter(s.detailFeeTagFilter ?? 'all'); setSbSearch(s.sbSearch ?? ''); setSbStatusFilter(s.sbStatusFilter ?? 'all'); setSbCpsFilter(s.sbCpsFilter ?? 'all'); setSbSegmentFilter(s.sbSegmentFilter ?? 'all'); setSbFeeTagFilter(s.sbFeeTagFilter ?? 'all'); });

  // Oninbaarheidsperiode wanbetalers per energielabel (alleen afwijkende waarden;
  // default 9999 wordt niet teruggegeven). GDPR-gescopet door de backend. Voor een
  // in beeld zijnd label ≠ 9999 tonen we een kleine noot onder de filters.
  // labelConfig komt als prop uit App (één fetch, gedeeld met Orders/Facturen/
  // Aging/Customer 360) — zie de comment bij die state.
  const badDebtNotes = useMemo(() => {
    const out = [];
    for (const label of Object.keys(labelConfig)) {
      const months = labelRecoveryMonths(labelConfig, label);
      if (months >= BAD_DEBT_RECOVERY_MONTHS_DEFAULT) continue;
      if (energielabelFilter === 'all' || energielabelFilter === label) out.push({ label, months });
    }
    return out;
  }, [labelConfig, energielabelFilter]);
  // Labels in beeld met een niet-standaard wanbetaler-drempel (voor de InfoTip
  // op het WANBETALERS-blok, zodat de lezer ziet welke drempel geldt).
  const overdueDayNotes = useMemo(() => {
    const out = [];
    for (const label of Object.keys(labelConfig)) {
      const days = labelOverdueDays(labelConfig, label);
      if (days === BAD_DEBT_OVERDUE_DAYS_DEFAULT) continue;
      if (energielabelFilter === 'all' || energielabelFilter === label) out.push({ label, days });
    }
    return out;
  }, [labelConfig, energielabelFilter]);

  // ── Admin: wanbetaler-instellingen per energielabel ────────────────────────
  // De tabel toont UITSLUITEND labels met een eigen rij. Een label zonder rij
  // volgt de defaults (40 dagen / 9999 maanden) en hoort hier dus niet in de
  // lijst — anders lijkt elk label ingesteld terwijl er niets bewaard is.
  // Toevoegen gebeurt met de kiezer eronder; verwijderen zet het label terug op
  // de defaults (er is geen sentinelwaarde die "standaard" betekent).
  const [wbDrafts, setWbDrafts] = useState({});   // { label: {months?, days?} }
  const [wbBusy, setWbBusy] = useState('');
  const [wbError, setWbError] = useState('');
  const [wbSuccess, setWbSuccess] = useState('');
  const [wbNieuwLabel, setWbNieuwLabel] = useState('');
  const [wbNieuwMaanden, setWbNieuwMaanden] = useState(String(BAD_DEBT_RECOVERY_MONTHS_DEFAULT));
  const [wbNieuwDagen, setWbNieuwDagen] = useState(String(BAD_DEBT_OVERDUE_DAYS_DEFAULT));
  const wbLabels = useMemo(() => Object.keys(labelConfig).sort(), [labelConfig]);
  // Bewust NIET paidEditLabels hergebruiken: die const staat verderop in deze
  // component, en een useMemo-dep die naar een latere const wijst crasht op de
  // temporal dead zone bij het renderen.
  const wbToeTeVoegenLabels = useMemo(
    () => scopeOptions(salesStructure, 'all', 'all').companies.filter(l => !(l in labelConfig)),
    [salesStructure, labelConfig]
  );
  const wbMaanden = (label) => labelRecoveryMonths(labelConfig, label);
  const wbDagen = (label) => labelOverdueDays(labelConfig, label);
  const wbDraftMaanden = (label) => (wbDrafts[label]?.months ?? String(wbMaanden(label)));
  const wbDraftDagen = (label) => (wbDrafts[label]?.days ?? String(wbDagen(label)));
  const wbSetDraft = (label, key, value) => setWbDrafts(p => ({ ...p, [label]: { ...(p[label] || {}), [key]: value } }));
  const wbDirty = (label) =>
    (wbDrafts[label]?.months !== undefined && Number(wbDrafts[label].months) !== wbMaanden(label)) ||
    (wbDrafts[label]?.days !== undefined && Number(wbDrafts[label].days) !== wbDagen(label));
  const wbValideer = (maanden, dagen) => {
    if (!Number.isInteger(maanden) || maanden < 0 || maanden > 9999) return 'Oninbaarheidsperiode: geheel getal tussen 0 en 9999 (9999 = volledige terugvordering).';
    if (!Number.isInteger(dagen) || dagen < 1 || dagen > 3650) return 'Drempel wanbetaler: geheel aantal dagen tussen 1 en 3650.';
    return '';
  };
  const wbBewaar = async (label, maanden, dagen) => {
    const fout = wbValideer(maanden, dagen);
    if (fout) { setWbError(fout); return false; }
    setWbBusy(label); setWbError(''); setWbSuccess('');
    try {
      const r = await fetch(`/api/label-config/${encodeURIComponent(label)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ months: maanden, overdue_days: dagen }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setWbDrafts(p => { const n = { ...p }; delete n[label]; return n; });
      setWbSuccess(`Opgeslagen voor ${label}.`);
      await onLabelConfigChanged?.();
      return true;
    } catch (err) { setWbError(err.message); return false; }
    finally { setWbBusy(''); }
  };
  const wbVerwijder = async (label) => {
    setWbBusy(label); setWbError(''); setWbSuccess('');
    try {
      const r = await fetch(`/api/label-config/${encodeURIComponent(label)}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok && r.status !== 204) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setWbDrafts(p => { const n = { ...p }; delete n[label]; return n; });
      setWbSuccess(`${label} staat weer op de standaardwaarden.`);
      await onLabelConfigChanged?.();
    } catch (err) { setWbError(err.message); }
    finally { setWbBusy(''); }
  };
  const wbVoegToe = async () => {
    if (!wbNieuwLabel) { setWbError('Kies eerst een energielabel.'); return; }
    const ok = await wbBewaar(wbNieuwLabel, Number(wbNieuwMaanden), Number(wbNieuwDagen));
    if (ok) {
      setWbNieuwLabel('');
      setWbNieuwMaanden(String(BAD_DEBT_RECOVERY_MONTHS_DEFAULT));
      setWbNieuwDagen(String(BAD_DEBT_OVERDUE_DAYS_DEFAULT));
    }
  };

  // --- Partnervergoeding ophalen via de ET API i.p.v. bestand-upload ---
  // Gericht per geselecteerd energielabel (+ optioneel verkoopteam/verkoper),
  // omdat het ET-endpoint zwaar is. Pagineert pagina-per-pagina met voortgang;
  // server dwingt de RBAC-scope af. MAX_PAGES = veiligheidsplafond (5000 records).
  const cancelApi = () => { cancelApiRef.current = true; };
  // Revenue leest de partnervergoeding nu uit de DB-CACHE (/api/commission/data):
  // is het label al gesynct → instant; anders start de backend eenmalig een
  // achtergrond-sync (uit ET, traag) en pollen we de voortgang.
  //
  // Bewust ZONDER refresh=1: een (her)sync forceren gebeurt enkel via de jobs
  // (Revenue — nieuwe (delta) / Revenue — volledige sync), niet met een knop op
  // deze pagina. De vroegere knop "Vernieuwen" is daarom verwijderd.
  //
  // Het ophalen zelf staat module-level (haalPartnervergoeding), zodat de
  // opstart-prefetch in de app-root exact hetzelfde doet. Hier blijft alleen de
  // schermstaat: bezig, voortgang, fout.
  const fetchCommissionViaApi = async () => {
    const label = energielabelFilter;
    if (!label || label === 'all') { setApiError('Kies eerst één energielabel in de filter.'); return; }
    // GDPR-guard: nooit een energielabel buiten de rechten van de gebruiker ophalen.
    if (!apiLabelOptions.includes(label)) { setApiError('Dit energielabel valt buiten je rechten.'); return; }
    cancelApiRef.current = false;
    setApiBusy(true); setApiError(''); setApiProgress('Laden…');
    try {
      const res = await haalPartnervergoeding({
        label, token, onVoortgang: setApiProgress, geannuleerd: () => cancelApiRef.current,
      });
      if (!res) { setApiProgress('Geannuleerd (sync loopt op de achtergrond door).'); return; }
      onCommissionFromApi?.(res);
      setApiProgress('');
    } catch (e) {
      setApiError(`API-laden mislukt: ${e.message}`);
      setApiProgress('');
    } finally {
      setApiBusy(false);
    }
  };

  // Reeds betaalde partnervergoeding (per energielabel + verkoopteam). Read-only
  // voor iedereen; alleen admin kan bedragen instellen via de admin-editor.
  const isAdmin = currentUser?.role === 'admin';
  // "Commissies <label>": de verkopersvergoeding zoals DIT energielabel ze
  // afrekent. Twee voorwaarden:
  //   1. voor het gekozen energielabel bestaat er een methodiek (CUSTOM_FEE_PAGINAS);
  //   2. de gebruiker heeft die subpagina toegekend gekregen. Admin en
  //      ET-dossierbeheerder zien altijd alles; een master krijgt de pagina
  //      standaard, een teamleader of verkoper alleen als een beheerder ze aanvinkt.
  // Eén energielabel kan meer dan één commissiepagina hebben (Evident heeft er
  // twee: de berekening en het overzicht); elke tab hangt aan zijn eigen vinkje.
  const revSubPages = useMemo(() => subPageSetForUser(currentUser), [currentUser]);
  const customPaginas = useMemo(
    () => customFeePaginas(energielabelFilter)
      .filter(p => revSubPages === null || revSubPages.has(subPageKey('revenue', p.tab))),
    [energielabelFilter, revSubPages]
  );
  // Wisselt het label (of verdwijnt het recht), dan verdwijnt de tab; blijf er dan
  // niet op hangen. Geldt voor élke commissietab, ook die van een ander label.
  // Betalingen: de betaalde partnervergoeding (subpagina revenue:betalingen —
  // standaard aan voor master en teamleader, admin/ET altijd, een verkoper enkel
  // na toekenning). De server scopet wat erin staat.
  const magBetalingen = revSubPages === null || revSubPages.has(subPageKey('revenue', 'betalingen'));
  useEffect(() => { if (revTab === 'betalingen' && !magBetalingen) setRevTab('samenvatting'); }, [revTab, magBetalingen]);
  const customTabIds = useMemo(() => new Set(CUSTOM_FEE_PAGINAS.map(p => p.tab)), []);
  useEffect(() => {
    if (customTabIds.has(revTab) && !customPaginas.some(p => p.tab === revTab)) setRevTab('samenvatting');
  }, [revTab, customPaginas, customTabIds]);

  // Betaalde partnervergoeding: een LIJST van bedragen per energielabel/verkoopteam/
  // verkoper met omschrijving (subpagina Betalingen). `paidRechten` komt van de
  // server en zegt op welk niveau deze gebruiker mag instellen.
  const [paidCommission, setPaidCommission] = useState([]);
  const [paidRechten, setPaidRechten] = useState({ magSchrijven: false, schrijfNiveau: null });
  const [paidCommissionError, setPaidCommissionError] = useState('');
  const [paidSaving, setPaidSaving] = useState(false);
  const pageSize = TABLE_PAGE_SIZE;

  // Alle kostcomponenten komen uit src/menukaart.js (MENUKAART) — dezelfde bron
  // als het Menukaart-paneel en de Revenue Calculator. Wijzig de cijfers DAAR.
  const SUPPLIER_COST_EL = MENUKAART.el.marge;
  // BRP Cost EL hangt af van klantensegment, energielabel én (voor Power2You-B2C)
  // verkoopteam:
  //   - B2B en Custom/aangepast: laag tarief voor ALLE energielabels.
  //   - B2C (particulier): laag voor de labels in MENUKAART.el.brp.laagLabels;
  //     Power2You per verkoopteam (p2yB2cLaagTeams); alle andere labels (bv. HOA
  //     Energy) én elk NIEUW label of team: hoog tarief (bevestigd 2026-07-29 voor
  //     "Prijspunten Energie België"). Dit is geen vergeten label.
  // bronLabel: een prospect ziet zijn eigen labelnaam, maar rekent als het bronlabel.
  const brpCostFor = (label, segment, team) => brpCostVoor(label, segment, team, bronLabel);
  // ── Imbalance Cost EL — SELF BILLS (injectie) ───────────────────────────────
  // Twee tariefgroepen per energielabel (bevestigd 2026-07-29):
  //   groep A → 18 / 14 €/MWh: Smappee Smiles, Servolt Energie, Power2You, Evident Energie
  //   groep B → 20 / 15 €/MWh: HOA Energy, Prijspunten Energie België én ELK NIEUW label
  // Een nieuw label staat hier bewust NIET in de allowlist en valt dus automatisch
  // op groep B — net zoals bij de BRP-kost hierboven. Dit is geen vergeten label.
  //
  // Beslisvolgorde (geldt ALLEEN voor de SELF BILLS — ELEKTRICITEIT-tabel):
  //   1. Meter Method "AMR" ÉN segment "Custom" → 0 €/MWh. Deze regel WINT altijd:
  //      grootverbruik met continumeting draagt geen onbalanskost.
  //   2. Segment "Custom" of "B2B" → het hoge tarief als de meting géén AMR is ÉN
  //      het EL-prijsformuletype (offtake óf injectie) "RLP" of "SPP" bevat;
  //      anders het lage tarief.
  //   3. Al het overige (B2C of geen segment) → 20 €/MWh. Komt in self bills in de
  //      praktijk niet voor; dit is de afgesproken terugvalwaarde.
  // De tarieven zelf komen uit revenueDocs.js (sbImbalanceRatesForLabel), zodat de
  // berekening en de getoonde documentatie gegarandeerd dezelfde bron gebruiken.
  const SB_IMBALANCE_FALLBACK = MENUKAART.el.onbalansInjectie.terugval;
  const sbImbalanceCostFor = (label, segment, meterMethod, isRlpSpp) => {
    const seg = (segment || '').toUpperCase();
    const isCustom = seg.includes('CUSTOM');
    const isAMR = (meterMethod || '').toUpperCase().replace(/\s/g, '') === 'AMR';
    if (isAMR && isCustom) return 0;
    if (!isCustom && !seg.includes('B2B')) return SB_IMBALANCE_FALLBACK;
    const { high, low } = sbImbalanceRatesForLabel(bronLabel(label));
    return (!isAMR && isRlpSpp) ? high : low;
  };

  const SUPPLIER_COST_GAS = MENUKAART.gas.marge;
  const SHIPPER_COST_GAS = MENUKAART.gas.shipper;
  const CONTRACTED_STAGES = ['Switch Accepted (st.)', 'Contracted (st.)', 'Switch Accepted (cu.)', 'Contracted (cu.)'];

  // Groene EL-ratecards, afgeleid uit de orders: een ratecard is "green" als de
  // bijhorende order energy_product_group === 'green' heeft. De ratecard bepaalt
  // dit eenduidig (geen ratecard komt zowel green als grey voor). Green Cost EL
  // = 0,5 €/MWh voor green-ratecards, anders 0. (Eerder een handmatige lijst.)
  const greenRatecards = useMemo(() => {
    const s = new Set();
    for (const o of (orders || [])) {
      if ((o.energyProductGroup || '').toLowerCase() === 'green' && o.ratecardName) s.add(o.ratecardName);
    }
    return s;
  }, [orders]);
  const greenCostFor = (ratecard) => (greenRatecards.has(ratecard) ? MENUKAART.el.groenkost : 0);

  // Cascade-opties (energielabel → verkoopteam → verkoper) uit de centrale
  // sales-structuur, doorsneden met wat in de facturen voorkomt (de factuur-
  // team/verkoper-spelling kan licht afwijken van /resellers). Zo overal dezelfde
  // cascade zonder dropdown-opties die 0 facturen opleveren.
  // Intersect-set bevat zowel partnervergoeding-facturen als self bills, zodat de
  // cascade-dropdowns ook labels/teams/verkopers tonen die enkel in self bills
  // voorkomen (bv. wanneer alleen het SB-bestand is geïmporteerd).
  // Team/verkoper-opties = de VOLLEDIGE (rol-gescopete) cascade — NIET beperkt tot
  // de geladen factuurdata, zodat (a) admin alles ziet en (b) de filters ook als
  // input voor de API-ophaal dienen (een nog-niet-geladen team/verkoper kiezen).
  // De energielabel-opties komen uit `apiLabelOptions` (GDPR-hard: admin alle,
  // overige enkel eigen label).
  const { teams: verkoopteams, sellers: verkopers } = useMemo(
    () => scopeOptions(salesStructure, energielabelFilter, verkoopteamFilter),
    [salesStructure, energielabelFilter, verkoopteamFilter]
  );

  // Filter facturen by energielabel, verkoopteam and verkoper
  // Facturen splitsen: klantfacturen vs leveranciersfacturen. Een KLANTfactuur
  // begint altijd met "VO" (verkoopfactuur) of "VK" (creditnota). Alles wat daar
  // niet mee begint is geen klantfactuur en hoort in de INVOICE ET-tabel — dus
  // niet alleen "VET…", maar ook "RVET…" en losse "SBEVI…"-nummers die in de
  // facturen-feed opduiken. Eerder werd enkel op "VET" gefilterd, waardoor die
  // rijen in DETAIL PER FACTUUR bleven staan en meetelden in de samenvattingen.
  // De DETAIL PER FACTUUR-tabel én alle vergoeding-samenvattingen draaien enkel
  // op de klantfacturen; de rest komt in INVOICE ET (admin / ET-dossierbeheerder
  // / master). Een record zonder nummer telt als niet-klantfactuur.
  const isCustomerInvoiceNumber = (nummer) => /^V[OK]/.test(String(nummer || '').toUpperCase().trim());
  // Self bills duiken ook in de FACTUREN-feed op. ET leverde bv. SBEVI/202506/00004
  // via /invoices i.p.v. via /selfbills; die rijen kwamen daardoor in INVOICE ET
  // terecht, waar alleen ET-leveranciersfacturen (VET…, en RVET… voor een
  // terugboeking) horen. Ze horen bij de self bills — geverifieerd dat ze NIET ook
  // al in de self-bills-feed zitten, dus verplaatsen voegt data toe en dubbelt niet.
  const isSelfBillNumber = (nummer) => /^R?SB/.test(String(nummer || '').toUpperCase().trim());
  const facturenCustomer = useMemo(() => facturen.filter(f => isCustomerInvoiceNumber(f.nummer)), [facturen]);
  // INVOICE ET blijft bewust de restcategorie: niet-klant én niet-self-bill. Vandaag
  // is dat exact VET/VETEN/RVET/RVETEN. Zou ET morgen een nieuw leveranciersprefix
  // introduceren, dan verschijnt dat hier i.p.v. stilletjes uit alle tabellen te
  // verdwijnen.
  const facturenEt = useMemo(
    () => facturen.filter(f => !isCustomerInvoiceNumber(f.nummer) && !isSelfBillNumber(f.nummer)),
    [facturen]);
  const filteredFacturen = useMemo(() => {
    let data = facturenCustomer;
    if (energielabelFilter !== 'all') data = data.filter(f => f.relatieBedrijf === energielabelFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(f => f.verkoopteam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(f => f.verkoper === verkoperFilter);
    return data;
  }, [facturenCustomer, energielabelFilter, verkoopteamFilter, verkoperFilter]);
  const filteredFacturenEt = useMemo(() => {
    let data = facturenEt;
    if (energielabelFilter !== 'all') data = data.filter(f => f.relatieBedrijf === energielabelFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(f => f.verkoopteam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(f => f.verkoper === verkoperFilter);
    return data;
  }, [facturenEt, energielabelFilter, verkoopteamFilter, verkoperFilter]);

  const handleEnergielabelChange = (value) => {
    setEnergielabelFilter(value);
    setVerkoopteamFilter('all');
    setVerkoperFilter('all');
  };

  // Een energielabel kiezen IS de opdracht om die data te tonen — daar hoort geen
  // tweede klik bij. Zodra er een concreet, toegestaan label staat halen we het
  // automatisch op; ook meteen bij het openen van de pagina, want dan staat er al
  // een label uit de bewaarde filters. Het lezen komt uit de lokale cache en duurt
  // milliseconden; enkel een nog niet gesynct label start (net als vroeger de knop)
  // een achtergrond-sync.
  //
  // De ref onthoudt voor welk label dit al gebeurd is. Zonder die guard zou de
  // effect-lus na "wissen" meteen opnieuw ophalen — dan zou die knop niets doen —
  // en zou elke render die apiLabelOptions vernieuwt een extra ophaal starten.
  const autoFetchedLabel = useRef(null);
  useEffect(() => {
    if (!onCommissionFromApi || apiBusy) return undefined;
    if (energielabelFilter === 'all' || !apiLabelOptions.includes(energielabelFilter)) return undefined;
    if (autoFetchedLabel.current === energielabelFilter) return undefined;
    // Al binnengehaald door de prefetch bij het opstarten (of door een eerder
    // bezoek in dezelfde sessie): niets opnieuw ophalen, wel de ref zetten zodat
    // een latere labelwissel en terug weer werkt.
    const alGeladen = commissionMeta.source === 'api'
      && (commissionMeta.api?.labels || []).includes(energielabelFilter);
    if (alGeladen) { autoFetchedLabel.current = energielabelFilter; return undefined; }
    // Kort uitstel met annulering. Bij het openen van de pagina zetten de bewaarde
    // filters het label pas ná deze render: zonder uitstel haalt dit effect eerst
    // het standaardlabel op en meteen daarna het echte — twee ophaalacties, en heel
    // even de data van het verkeerde label in beeld. De cleanup annuleert de
    // geplande ophaal zodra het label binnen die tijd nog verandert.
    const t = setTimeout(() => {
      autoFetchedLabel.current = energielabelFilter;
      fetchCommissionViaApi();
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energielabelFilter, apiLabelOptions, apiBusy]);

  const handleVerkoopteamChange = (value) => {
    setVerkoopteamFilter(value);
    setVerkoperFilter('all');
  };

  const hasActiveFilters = energielabelFilter !== 'all' || verkoopteamFilter !== 'all' || verkoperFilter !== 'all';
  const clearFilters = () => { setEnergielabelFilter('all'); setVerkoopteamFilter('all'); setVerkoperFilter('all'); };

  // --- Reeds betaalde partnervergoeding -------------------------------------
  const loadPaidCommission = useCallback(() => {
    if (!token) return;
    fetch('/api/paid-commission', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('laden mislukt')))
      .then(d => {
        setPaidCommission(Array.isArray(d.all) ? d.all : []);
        setPaidRechten(d.rechten || { magSchrijven: false, schrijfNiveau: null });
        setPaidCommissionError('');
      })
      .catch(() => setPaidCommissionError('Betaalde vergoeding kon niet worden geladen.'));
  }, [token]);
  useEffect(() => { loadPaidCommission(); }, [loadPaidCommission]);

  // Alle ingestelde "betaalde vergoeding"-regels binnen de actieve TAK van de
  // verkoopstructuur (label → label + teams + verkopers; team → team + verkopers;
  // verkoper → enkel die verkoper). Dit is wat de drie tabellen op de subpagina
  // Betalingen tonen. Het KENGETAL op de Samenvatting telt hier NIET alles van op —
  // zie paidSelectieNiveau / betaaldeVergoeding hieronder.
  const paidCommissionFiltered = useMemo(() => (paidCommission || []).filter(r => {
    if (energielabelFilter !== 'all' && r.energy_label !== energielabelFilter) return false;
    if (verkoopteamFilter !== 'all' && r.sales_team !== verkoopteamFilter) return false;
    if (verkoperFilter !== 'all' && r.seller_name !== verkoperFilter) return false;
    return true;
  }), [paidCommission, energielabelFilter, verkoopteamFilter, verkoperFilter]);
  // Niveau van een regel: aan wie is het bedrag toegewezen?
  const paidNiveauVan = (r) => (r.seller_name ? 'seller' : r.sales_team ? 'team' : 'label');
  // Niveau van de SELECTIE: de fijnste filter die de data beperkt. Een teamleader
  // zit ook met "Alle verkoopteams" op zijn eigen team en een reseller op zichzelf
  // (effectiefTeam/effectieveVerkoper), dus die tellen mee.
  const paidSelectieNiveau = effectieveVerkoper ? 'seller' : effectiefTeam ? 'team' : 'label';
  // Kengetal "Betaalde vergoeding": ÉÉN-OP-ÉÉN met de invoer op het niveau van de
  // selectie. Energielabel gekozen → enkel de bedragen die aan het label zelf zijn
  // toegewezen (niet die van zijn teams of verkopers); verkoopteam → enkel de
  // teambedragen; verkoper → enkel de bedragen van die verkoper. Geen optelling
  // van de onderliggende niveaus en geen terugval naar boven.
  const betaaldeVergoeding = useMemo(
    () => paidCommissionFiltered.filter(r => paidNiveauVan(r) === paidSelectieNiveau).reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paidCommissionFiltered, paidSelectieNiveau]
  );

  // ── Betalingen: drie tabellen (energielabel / verkoopteam / verkoper) ──────────
  // Elk niveau heeft zijn eigen tabel met een eigen invoerregel. Wie mag zetten:
  //   label    → admin
  //   team     → admin, master van dat energielabel
  //   verkoper → admin, master van dat energielabel, teamleader van dat team
  // Dat is precies `paidRechten.schrijfNiveau` van de server (label ⊃ team ⊃
  // verkoper): een rol mag op zijn eigen niveau en alles daaronder.
  const paidNiveau = paidRechten.schrijfNiveau;
  const PAID_NIVEAU_RANG = { label: 0, team: 1, seller: 2 };
  const PAID_NIVEAU_TITEL = { label: 'Energielabel', team: 'Verkoopteam', seller: 'Verkoper' };
  const magPaidNiveau = (niveau) => paidRechten.magSchrijven && paidNiveau != null && PAID_NIVEAU_RANG[niveau] >= PAID_NIVEAU_RANG[paidNiveau];

  // De tabellen volgen de filters van de pagina, net als de Samenvatting: dezelfde
  // tak van de verkoopstructuur, zodat het kengetal en de tabellen bij elkaar horen.
  // Gesorteerd op naam en daarbinnen op laatst gewijzigd (meest recente bovenaan).
  const paidPerNiveau = useMemo(() => {
    const uit = { label: [], team: [], seller: [] };
    for (const r of paidCommissionFiltered) uit[paidNiveauVan(r)].push(r);
    const naam = (r) => (r.seller_name || r.sales_team || r.energy_label || '');
    const sleutel = (r) => `${r.energy_label}|${r.sales_team}|${r.seller_name}`;
    const bijgewerkt = (r) => new Date(r.updated_at || r.created_at || 0).getTime();
    const groepeer = (rijen) => {
      const m = new Map();
      for (const r of rijen) {
        let g = m.get(sleutel(r));
        if (!g) { g = { sleutel: sleutel(r), energy_label: r.energy_label, sales_team: r.sales_team, seller_name: r.seller_name, naam: naam(r), rijen: [], totaal: 0, laatst: 0 }; m.set(sleutel(r), g); }
        g.rijen.push(r); g.totaal += Number(r.amount) || 0; g.laatst = Math.max(g.laatst, bijgewerkt(r));
      }
      const groepen = [...m.values()];
      for (const g of groepen) g.rijen.sort((a, b) => bijgewerkt(b) - bijgewerkt(a));
      // Naam, dan (bij gelijke naam in een ander label/team) het label, dan laatst gewijzigd.
      groepen.sort((a, b) => a.naam.localeCompare(b.naam, 'nl') || a.energy_label.localeCompare(b.energy_label, 'nl') || (a.sales_team || '').localeCompare(b.sales_team || '', 'nl') || b.laatst - a.laatst);
      return groepen;
    };
    return { label: groepeer(uit.label), team: groepeer(uit.team), seller: groepeer(uit.seller) };
  }, [paidCommissionFiltered]);
  // Uitgeklapte groepen (sleutel → true). Standaard ingeklapt: je ziet de som.
  const [paidOpen, setPaidOpen] = useState({});
  const togglePaidGroep = (k) => setPaidOpen((o) => ({ ...o, [k]: !o[k] }));

  // Invoerregel per tabel: één formulier-object per niveau. Het energielabel en
  // team volgen de filters van de pagina (of de enige keuze), zodat je meestal
  // alleen nog het bedrag en de omschrijving hoeft in te typen.
  const leegPaidForm = () => ({ label: 'all', team: '', seller: '', amount: '', note: '' });
  const [paidForm, setPaidForm] = useState({ label: leegPaidForm(), team: leegPaidForm(), seller: leegPaidForm() });
  const zetPaidForm = (niveau, patch) => setPaidForm((f) => ({ ...f, [niveau]: { ...f[niveau], ...patch } }));
  const paidEditLabels = useMemo(() => scopeOptions(salesStructure, 'all', 'all').companies, [salesStructure]);
  const paidTeamsVoor = (label) => (label && label !== 'all' ? scopeOptions(salesStructure, label, 'all').teams : []);
  const paidSellersVoor = (label, team) => (label && label !== 'all' && team ? scopeOptions(salesStructure, label, team).sellers : []);
  useEffect(() => {
    // Vooraf invullen vanuit de filters; wie maar één label/team heeft, krijgt dat.
    const label = energielabelFilter !== 'all' ? energielabelFilter : (paidEditLabels.length === 1 ? paidEditLabels[0] : 'all');
    const teams = paidTeamsVoor(label);
    const team = verkoopteamFilter !== 'all' && teams.includes(verkoopteamFilter) ? verkoopteamFilter : (teams.length === 1 ? teams[0] : '');
    setPaidForm((f) => ({
      label: { ...f.label, label },
      team: { ...f.team, label, team: f.team.team && teams.includes(f.team.team) ? f.team.team : team },
      seller: { ...f.seller, label, team: f.seller.team && teams.includes(f.seller.team) ? f.seller.team : team, seller: '' },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energielabelFilter, verkoopteamFilter, paidEditLabels.join('|'), salesStructure]);
  const paidFormKlaar = (niveau) => {
    const f = paidForm[niveau];
    if (!f.label || f.label === 'all') return false;
    if (niveau !== 'label' && !f.team) return false;
    if (niveau === 'seller' && !f.seller) return false;
    return String(f.amount).trim() !== '';
  };
  const parsePaidBedrag = (s) => {
    const t = String(s || '').replace(/\s/g, '');
    return parseFloat(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  };
  const addPaidCommission = async (niveau) => {
    if (!magPaidNiveau(niveau) || !paidFormKlaar(niveau)) return;
    const f = paidForm[niveau];
    const amount = parsePaidBedrag(f.amount);
    if (!Number.isFinite(amount) || amount < 0) { setPaidCommissionError('Vul een geldig bedrag (≥ 0) in.'); return; }
    setPaidSaving(true); setPaidCommissionError('');
    try {
      const r = await fetch('/api/paid-commission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ energy_label: f.label, sales_team: niveau === 'label' ? '' : f.team, seller_name: niveau === 'seller' ? f.seller : '', amount, omschrijving: f.note }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'opslaan mislukt');
      zetPaidForm(niveau, { amount: '', note: '' });
      toast.success('Bedrag toegevoegd');
      loadPaidCommission();
    } catch (err) { setPaidCommissionError(`Opslaan mislukt: ${err.message}`); }
    finally { setPaidSaving(false); }
  };

  // Inline bewerken: bedrag en omschrijving in de rij zelf; Enter slaat op, Esc annuleert.
  const [paidEdit, setPaidEdit] = useState(null); // { id, amount, note }
  const startPaidEdit = (r) => setPaidEdit({ id: r.id, amount: String(r.amount).replace('.', ','), note: r.omschrijving || '' });
  const savePaidEdit = async () => {
    if (!paidEdit) return;
    const amount = parsePaidBedrag(paidEdit.amount);
    if (!Number.isFinite(amount) || amount < 0) { setPaidCommissionError('Vul een geldig bedrag (≥ 0) in.'); return; }
    setPaidSaving(true); setPaidCommissionError('');
    try {
      const r = await fetch(`/api/paid-commission/${paidEdit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount, omschrijving: paidEdit.note }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'opslaan mislukt');
      setPaidEdit(null);
      toast.success('Bedrag bijgewerkt');
      loadPaidCommission();
    } catch (err) { setPaidCommissionError(`Opslaan mislukt: ${err.message}`); }
    finally { setPaidSaving(false); }
  };
  const deletePaidCommission = async (id) => {
    if (!paidRechten.magSchrijven) return;
    setPaidCommissionError('');
    try {
      const r = await fetch(`/api/paid-commission/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'verwijderen mislukt');
      if (paidEdit && paidEdit.id === id) setPaidEdit(null);
      toast.success('Bedrag verwijderd');
      loadPaidCommission();
    } catch (err) { setPaidCommissionError(`Verwijderen mislukt: ${err.message}`); }
  };

  // Filter orders by energielabel (company), verkoopteam and verkoper (salesperson)
  const filteredOrders = useMemo(() => {
    let data = orders;
    if (energielabelFilter !== 'all') data = data.filter(o => o.company === energielabelFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(o => o.salespersonTeam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(o => o.salesperson === verkoperFilter);
    return data;
  }, [orders, energielabelFilter, verkoopteamFilter, verkoperFilter]);

  // Fee calculation functions for orders
  // MaxFeeDate = de 1e van de VOLGENDE maand (TZ-veilig als string opgebouwd).
  const maxFeeDate = useMemo(() => {
    const today = new Date();
    // 1e van de VOLGENDE maand (Date verwerkt de jaarovergang dec→jan correct).
    const d = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  }, []);

  // Fee End Date per order (spec): leeg ("") als de effective start ontbreekt of
  // niet vóór MaxFeeDate ligt; anders de effective end, gecapt op MaxFeeDate (en
  // MaxFeeDate wanneer de effective end ontbreekt).
  const getFeeEndDate = (o) => {
    const start = o.effectiveStartDate || '';
    if (!start || start >= maxFeeDate) return '';
    const end = o.effectiveEndDate || '';
    if (!end || end > maxFeeDate) return maxFeeDate;
    return end;
  };

  const getFeeDuration = (o) => {
    const feeEnd = getFeeEndDate(o);
    if (!feeEnd || !o.effectiveStartDate) return 0;
    const start = new Date(o.effectiveStartDate);
    const end = new Date(feeEnd);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + (end.getDate() - start.getDate()) / 30;
    return Math.round(Math.max(0, months) * 10) / 10;
  };

  const getContracted = (o) => {
    const stage = o.opportunityStage || '';
    if (CONTRACTED_STAGES.includes(stage) && !isCancelledOrderStatus(o.status)) return 'Yes';
    return 'No';
  };

  // Fee Customer Tag = No zodra de klant ÉÉN niet-fee-allowed tag heeft (config in
  // Revenue → Admin → Customer Tags). partnerTags is komma-gescheiden → per tag checken.
  const getFeePartnerTag = (partnerTags) => {
    if (!partnerTags) return 'Yes';
    const tags = String(partnerTags).split(',').map(t => t.trim()).filter(Boolean);
    return tags.some(t => feeNotAllowedTags.has(t)) ? 'No' : 'Yes';
  };

  const getPriceAdditionNumeric = (o) => {
    const el = o.priceAdditionsEL;
    const elNum = parseFloat(String(el).replace(',', '.')) || 0;
    if (el && el !== '' && elNum !== 0) return elNum;
    const ng = o.priceAdditionsNG;
    return parseFloat(String(ng).replace(',', '.')) || 0;
  };

  // `negeerPartnerTag` = reken de abonnementsvergoeding uit alsof de Fee Partner
  // Tag niet blokkeert. Alleen voor de admin-analyse "geblokkeerd door customer tag":
  // daar is de vraag juist hoeveel er wegvalt. Alle andere aanroepen laten deze
  // optie weg en houden dus het normale gedrag (tag = No → 0).
  const getFeeSubscription = (o, { negeerPartnerTag = false } = {}) => {
    if (getContracted(o) === 'No') return 0;
    if (!negeerPartnerTag && getFeePartnerTag(o.partnerTags) === 'No') return 0;
    const priceAddition = getPriceAdditionNumeric(o);
    if (!priceAddition) return 0;
    // Netto jaarbedrag = PriceAddition minus het vaste ET-recht (€30/EAN/jaar).
    const netAnnual = priceAddition - FIXED_ANNUAL_RIGHT_PER_EAN;
    const feeDuration = getFeeDurationAdjusted(o);
    if (isYearlyFeeUnit(o.priceAdditionsUnit)) {
      const years = feeDuration > 0 ? Math.ceil(feeDuration / 12) : 0;
      const aantalJaren = Math.max(years, feeDuration > 0 ? 1 : 0);
      return netAnnual * aantalJaren;
    }
    return (netAnnual / 12) * feeDuration;
  };

  // Customer Payment Status uit DEZELFDE volledige facturenset + logica als de
  // DETAIL ORDERS-tabel (de `facturen`-prop, NIET de team/verkoper-gefilterde
  // `filteredFacturen`), zodat alle samenvattingsblokken 1-op-1 reconciliëren met
  // die tabel. De wanbetaler-status is een eigenschap van de klant (al z'n
  // facturen), niet van het actieve team/verkoper-filter.
  const badDebt = useMemo(() => computeBadDebt(facturen, labelConfig), [facturen, labelConfig]);
  const customerPaymentStatusMap = badDebt.statusMap;
  const getCustomerPaymentStatus = (f) => customerPaymentStatusFromMap(customerPaymentStatusMap, f.relatieReferentie);
  const getOrderPaymentStatus = (o) => customerPaymentStatusFromMap(customerPaymentStatusMap, o.customerReference);
  const badDebtMonthsForRef = (ref) => badDebt.monthsMap[String(ref || '').trim()] || 0;
  // Per-factuur/self-bill EFFECTIEVE status bij een 'oninbaar deel'-klant: facturen
  // ouder dan de drempel (buiten het clawback-venster) → 'Goed' (vergoedbaar); recente
  // facturen (binnen het venster) → 'Wanbetaler – oninbaar deel' (teruggevorderd, geen
  // commissie). Andere klanten: hun klantstatus ongewijzigd.
  const getRecordEffStatus = (f) => {
    const ref = String(f.relatieReferentie || '').trim();
    const base = customerPaymentStatusMap[ref] || 'Goed';
    if (base !== PAY_STATUS_PARTIAL) return base;
    const threshold = badDebt.thresholdMap[ref];
    const d = f.factuurdatum || '';
    return (threshold && d && d < threshold) ? 'Goed' : PAY_STATUS_PARTIAL;
  };
  // Fee Duration na aftrek van de oninbaarheidsperiode voor een order (deels-oninbaar
  // klant → recentste N maanden oninbaar → betaalbare looptijd = Fee Duration − N ≥ 0).
  const getFeeDurationAdjusted = (o) => {
    const base = getFeeDuration(o);
    const n = getOrderPaymentStatus(o) === PAY_STATUS_PARTIAL ? badDebtMonthsForRef(o.customerReference) : 0;
    return n > 0 ? Math.max(0, Math.round((base - n) * 10) / 10) : base;
  };

  // Klanten met Fee Customer Tag = No (niet-fee-allowed). Partner-tags zitten ALLEEN
  // op orders, dus we leiden per klant (customer reference) af of die fee-allowed is:
  // 'No' zodra één van zijn orders een niet-fee-allowed tag draagt. Hiermee weren we
  // die klanten VOLLEDIG uit de samenvattingsblokken (niet enkel hun Subscription Fee
  // = 0, maar ook hun Invoiced kWh / Factor Fee / Volume Fee uit de facturen).
  const feeNotAllowedCustomers = useMemo(() => {
    const set = new Set();
    (orders || []).forEach(o => {
      const ref = (o.customerReference || '').trim();
      if (ref && getFeePartnerTag(o.partnerTags) === 'No') set.add(ref);
    });
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, feeNotAllowedTags]);
  const customerFeeAllowed = (ref) => !feeNotAllowedCustomers.has(String(ref || '').trim());
  // Fee Customer Tag (Yes/No) voor een factuur-/self-bill-record, via de klant.
  const getFactuurFeePartnerTag = (f) => customerFeeAllowed(f.relatieReferentie) ? 'Yes' : 'No';
  // Segment-/markt-classificatie voor factuurrecords (om de detailtabellen te kunnen
  // filteren op exact dezelfde dimensies als de samenvattingsblokken). Een factuur
  // kan een EL- én een NG-lijn dragen; segment matcht op één van beide.
  const segmentClassOf = (seg) => { const s = String(seg || '').toUpperCase(); if (s.includes('B2C')) return 'B2C'; if (s.includes('B2B')) return 'B2B'; if (s.includes('CUSTOM')) return 'CUSTOM'; return ''; };
  const factuurMatchesSegment = (f, cls) => segmentClassOf(f.elSegment) === cls || segmentClassOf(f.ngSegment) === cls;
  const factuurHasEL = (f) => !!(f.elGSRN || f.elRatecard || f.elKwhOfftake);
  const factuurHasNG = (f) => !!(f.ngGSRN || f.ngRatecard || f.ngKwhOfftake);

  // ── Reseller Fee % → "Totale Fee R." ───────────────────────────────────────
  // teamleader/reseller: hun eigen % (default 75) — Totale Fee R. = % × Totale Fee.
  // master: 100% (geen reseller-fee). admin/et: 100%, BEHALVE wanneer gefilterd op
  // een verkoopteam en/of verkoper → dan het % van die team/verkoper (verkoper wint),
  // opgehaald uit /api/reference/reseller-fees. Geldt voor alle energielabels.
  // Op één en ander bestaan contractuele uitzonderingen per (energielabel, team):
  // zie src/resellerFee.js. Die gaan vóór op beide bronnen hierboven.
  const isAdminOrEt = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  const [resellerFeeMap, setResellerFeeMap] = useState({ teams: {}, sellers: {}, default: 75 });
  useEffect(() => {
    if (!token || !isAdminOrEt) return;
    fetch('/api/reference/reseller-fees', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setResellerFeeMap({ teams: d.teams || {}, sellers: d.sellers || {}, default: d.default != null ? d.default : 75 }); })
      .catch(() => {});
  }, [token, isAdminOrEt]);

  const { showResellerFee, resellerFeePct } = useMemo(() => {
    const role = currentUser?.role;
    if (role === 'teamleader' || role === 'reseller') {
      // Een contractuele uitzondering (zie RESELLER_FEE_UITZONDERINGEN) gaat vóór
      // op het percentage dat op het gebruikersprofiel staat.
      const uitz = resellerFeeUitzondering(currentUser.energyLabel, currentUser.salesTeam);
      const pct = uitz != null ? uitz : (currentUser.resellerFeePct == null ? 75 : Number(currentUser.resellerFeePct));
      return { showResellerFee: true, resellerFeePct: pct };
    }
    if (isAdminOrEt) {
      const hasTeam = verkoopteamFilter && verkoopteamFilter !== 'all';
      const hasSeller = verkoperFilter && verkoperFilter !== 'all';
      if (hasSeller || hasTeam) {
        const uitz = (hasTeam ? resellerFeeUitzondering(energielabelFilter, verkoopteamFilter) : null)
          ?? (hasSeller ? resellerFeeUitzonderingViaVerkoper(salesStructure, energielabelFilter, verkoperFilter) : null);
        if (uitz != null) return { showResellerFee: true, resellerFeePct: uitz };
        let pct = resellerFeeMap.default != null ? resellerFeeMap.default : 75;
        if (hasSeller && resellerFeeMap.sellers[verkoperFilter] != null) pct = resellerFeeMap.sellers[verkoperFilter];
        else if (hasTeam && resellerFeeMap.teams[verkoopteamFilter] != null) pct = resellerFeeMap.teams[verkoopteamFilter];
        return { showResellerFee: true, resellerFeePct: Number(pct) };
      }
    }
    return { showResellerFee: false, resellerFeePct: 100 };
  }, [currentUser, isAdminOrEt, energielabelFilter, verkoopteamFilter, verkoperFilter, resellerFeeMap, salesStructure]);
  const resellerFeeFactor = showResellerFee ? resellerFeePct / 100 : 1;

  // Calculate subscription fees grouped by segment and payment status
  const subscriptionFeesBySegment = useMemo(() => {
    // Alle sleutels die orderFeeSegmentKey kan opleveren — inclusief de injectie-
    // blokken van B2C/B2B, anders zou `fees[key] += fee` op undefined NaN geven.
    const emptyFees = () => ({ 'B2C - EL': 0, 'B2C - GAS': 0, 'B2B - EL': 0, 'B2B - EL Injection': 0, 'B2B - GAS': 0, 'Custom - EL Offtake': 0, 'Custom - EL Injection': 0, 'Custom - GAS': 0 });
    const fees = { goed: emptyFees(), wanbetaler: emptyFees(), openstaand: emptyFees(), deelsOninbaar: emptyFees() };

    filteredOrders.forEach(o => {
      const subscriptionFee = getFeeSubscription(o);
      if (subscriptionFee === 0) return;

      const paymentStatus = getOrderPaymentStatus(o);
      // Zelfde Market/Customer-Segment-classificatie als de DETAIL ORDERS-tabel.
      const segmentKey = orderFeeSegmentKey(o);
      if (!segmentKey) return;

      if (paymentStatus === 'Wanbetaler') {
        fees.wanbetaler[segmentKey] += subscriptionFee;
      } else {
        // 'Goed' óf 'Wanbetaler – oninbaar deel': de reeds via getFeeDurationAdjusted
        // verlaagde order-fee = het INBARE deel → blijft in de goed-bucket en telt mee
        // voor 'Te ontvangen' (variant A). Het deels-oninbaar-blok toont enkel de recente
        // oninbare facturen/self bills (geen order-abonnementsvergoeding → deelsOninbaar blijft 0).
        fees.goed[segmentKey] += subscriptionFee;
      }
      // Openstaand + deelsOninbaar (order-fee) blijven 0
    });

    return fees;
  }, [filteredOrders, badDebt, feeNotAllowedTags]);

  // De samenvattingsblokken weren klanten met Fee Customer Tag = No VOLLEDIG
  // (customerFeeAllowed) — niet enkel hun Subscription Fee. De DETAIL PER FACTUUR-
  // en SELF BILLS-tabellen hieronder tonen ze nog wél, met een Fee Customer Tag-kolom.
  const facturenGoed = useMemo(() => filteredFacturen.filter(f => {
    const status = (f.statusBetaling || '').toLowerCase();
    const isGoodStatus = status === 'betaald' || status === 'omgekeerd';
    const paymentStatus = getRecordEffStatus(f);
    return isGoodStatus && paymentStatus === 'Goed' && customerFeeAllowed(f.relatieReferentie);
  }), [filteredFacturen, badDebt, feeNotAllowedCustomers]);

  const facturenWanbetaler = useMemo(() => filteredFacturen.filter(f => {
    const paymentStatus = getRecordEffStatus(f);
    return paymentStatus === 'Wanbetaler' && customerFeeAllowed(f.relatieReferentie);
  }), [filteredFacturen, badDebt, feeNotAllowedCustomers]);

  // Recente (teruggevorderde) facturen van 'Wanbetaler – oninbaar deel'-klanten.
  const facturenDeelsOninbaar = useMemo(() => filteredFacturen.filter(f => {
    const paymentStatus = getRecordEffStatus(f);
    return paymentStatus === PAY_STATUS_PARTIAL && customerFeeAllowed(f.relatieReferentie);
  }), [filteredFacturen, badDebt, feeNotAllowedCustomers]);

  const facturenOverig = useMemo(() => filteredFacturen.filter(f => {
    const status = (f.statusBetaling || '').toLowerCase();
    const isGoodStatus = status === 'betaald' || status === 'omgekeerd';
    const paymentStatus = getRecordEffStatus(f);
    return !isGoodStatus && paymentStatus === 'Goed' && customerFeeAllowed(f.relatieReferentie);
  }), [filteredFacturen, badDebt, feeNotAllowedCustomers]);

  // Openstaand factuurbedrag INCL. BTW binnen de actieve filters (energielabel /
  // verkoopteam / verkoper), gesplitst zoals de KPI-tegels eronder staan:
  // wanbetalers apart van de rest. Bewust een andere grootheid dan de blokken
  // erboven — dat zijn vergoedingen excl. btw, dit is wat de eindklanten nog
  // effectief moeten betalen. Daarom ook zonder de Fee Customer Tag-uitsluiting
  // die de fee-blokken wél toepassen: het gaat over openstaand geld, niet over
  // commissierecht.
  //
  // De indeling volgt de KLANT-status (een klant is wanbetaler of niet), niet de
  // status per factuur. "Wanbetaler – oninbaar deel" telt dus bij de wanbetalers:
  // die klant ís een wanbetaler, alleen is een deel van zijn historiek nog
  // vergoedbaar — dat onderscheid slaat op de vergoeding, niet op wat hij nog moet
  // betalen.
  //
  // Sommeren per UNIEK factuurnummer: een factuur is over meerdere regels (per
  // GSRN) verdeeld en draagt op elke regel hetzelfde totaal. Creditnota's staan
  // negatief in de data en verlagen het bedrag dus vanzelf.
  const openstaandInclBtw = useMemo(() => {
    const perFactuur = new Map();
    for (const f of filteredFacturen) {
      const status = (f.statusBetaling || '').toLowerCase();
      if (status === 'betaald' || status === 'omgekeerd') continue;
      const sleutel = String(f.nummer || '').trim() || `#${f.id}`;
      if (perFactuur.has(sleutel)) continue;
      const klantStatus = customerPaymentStatusMap[String(f.relatieReferentie || '').trim()] || 'Goed';
      perFactuur.set(sleutel, { bedrag: f.totaal || 0, wanbetaler: klantStatus.startsWith('Wanbetaler') });
    }
    const som = { wanbetalers: 0, goed: 0 };
    for (const { bedrag, wanbetaler } of perFactuur.values()) {
      if (wanbetaler) som.wanbetalers += bedrag; else som.goed += bedrag;
    }
    return som;
  }, [filteredFacturen, customerPaymentStatusMap]);

  // GSRN → meest recente meter method, afgeleid uit de Order-tabel. De
  // partner-commission-records (zeker de API-variant) dragen zelf geen meter
  // method; we zoeken die op via de EL GSRN en nemen bij meerdere orders de
  // meest recente waarde (effectieve start > leveringsstart > orderdatum).
  const gsrnMeterMethod = useMemo(() => {
    const best = new Map(); // gsrn → { method, date }
    for (const o of (orders || [])) {
      const gsrn = (o.connectionPointGSRN || '').trim();
      const method = (o.meterMethod || '').trim();
      if (!gsrn || !method) continue;
      const date = String(o.effectiveStartDate || o.deliveryStartDate || o.orderDateTime || o.orderDate || '');
      const cur = best.get(gsrn);
      if (!cur || date > cur.date) best.set(gsrn, { method, date });
    }
    const m = new Map();
    for (const [g, v] of best) m.set(g, v.method);
    return m;
  }, [orders]);

  // Effectieve meter method voor een factuur-record: gebruik de waarde uit het
  // record zelf (bestand-upload) en val anders terug op de via GSRN opgezochte
  // Order-waarde (API-variant).
  const meterMethodFor = (f) => {
    const own = (f.elMeterMethod || '').trim();
    if (own) return own;
    return gsrnMeterMethod.get((f.elGSRN || '').trim()) || '';
  };

  // Imbalance Cost EL (€/MWh). Onderscheid klein-/grootverbruik op segment
  // (grootverbruik = "Custom"). Logica:
  //   Kleinverbruik : type Belpex_RLP / Belpex_RLP_Hourly → 4, anders 2.
  //   Grootverbruik : type Belpex / Hourly Belpex ÉN meter method AMR → 0,
  //                   anders 4.
  const elImbalanceCost = (f) => {
    const typeL = (f.elTypeOfftake || '').toLowerCase();
    const isGrootverbruik = (f.elSegment || '').toUpperCase().includes('CUSTOM');
    const onb = MENUKAART.el.onbalansAfname;
    if (isGrootverbruik) {
      const isBelpexNonRlp = typeL.includes('belpex') && !typeL.includes('rlp');
      return (isBelpexNonRlp && meterMethodFor(f).toUpperCase() === 'AMR') ? onb.gvAmr : onb.gvOverig;
    }
    return typeL.includes('belpex_rlp') ? onb.rlp : onb.smr3;
  };

  const calcEL = (f) => {
    const kwhMWh = (f.elKwhOfftake || 0) / 1000;
    const coeff = parseFloat(f.elCoeffOfftake) || 1;
    const maj = f.elMajorationOfftake || 0;
    const amount = f.elAmountOfftake || 0;
    const avgEnergyPrice = amount === 0 || kwhMWh === 0 ? 0 : ((amount / kwhMWh) - maj) / coeff;
    const factorFee = (coeff - 1) * avgEnergyPrice * kwhMWh;
    const imbalanceCost = elImbalanceCost(f);
    const greenCost = greenCostFor(f.elRatecard);
    const brpCost = brpCostFor(f.relatieBedrijf, f.elSegment, f.verkoopteam);
    const volumeFee = kwhMWh * (maj - (SUPPLIER_COST_EL + brpCost + imbalanceCost + greenCost));
    const energyFee = factorFee + volumeFee;
    return { factorFee, volumeFee, energyFee };
  };

  const calcNG = (f) => {
    const kwhMWh = (f.ngKwhOfftake || 0) / 1000;
    const coeff = parseFloat(f.ngCoeffOfftake) || 1;
    const maj = f.ngMajorationOfftake || 0;
    const amount = f.ngAmountOfftake || 0;
    const avgEnergyPrice = amount === 0 || kwhMWh === 0 ? 0 : ((amount / kwhMWh) - maj) / coeff;
    const factorFee = (coeff - 1) * avgEnergyPrice * kwhMWh;
    const volumeFee = kwhMWh * (maj - (SUPPLIER_COST_GAS + SHIPPER_COST_GAS));
    const energyFee = factorFee + volumeFee;
    return { factorFee, volumeFee, energyFee };
  };

  // Detail-varianten die ALLE weergavekolommen teruggeven (AVG Energy Price,
  // imbalance/green/supplier/brp/shipper-kosten). Zelfde berekeningswijze als
  // de oude Invoice-detailtabel, nu gevoed met de partner_commission-records.
  const calcELDetail = (f) => {
    const kwhMWh = (f.elKwhOfftake || 0) / 1000;
    const coeff = parseFloat(f.elCoeffOfftake) || 1;
    const maj = f.elMajorationOfftake || 0;
    const amount = f.elAmountOfftake || 0;
    const avgEnergyPrice = amount === 0 || kwhMWh === 0 ? 0 : ((amount / kwhMWh) - maj) / coeff;
    const factorFee = (coeff - 1) * avgEnergyPrice * kwhMWh;
    const imbalanceCost = elImbalanceCost(f);
    const greenCost = greenCostFor(f.elRatecard);
    const brpCost = brpCostFor(f.relatieBedrijf, f.elSegment, f.verkoopteam);
    const volumeFee = kwhMWh * (maj - (SUPPLIER_COST_EL + brpCost + imbalanceCost + greenCost));
    const energyFee = factorFee + volumeFee;
    return { avgEnergyPrice, factorFee, meterMethod: meterMethodFor(f), imbalanceCost, greenCost, supplierCost: SUPPLIER_COST_EL, brpCost, volumeFee, energyFee };
  };

  // Self bills (Custom EL injectie): Volume/Energy Fee zoals calcELDetail, maar op de
  // INJECTIE-kWh (EL kWh Injection) en met de ABSOLUTE injection-majoration i.p.v. de
  // offtake-majoration. Lege injection-majoration → contractdefault -25 (in de UI
  // donkeroranje gemarkeerd).
  // Factor Fee (sinds 09-09-2026) op de INJECTIE-coëfficiënt (EL Coeff Injection):
  // de klant krijgt coeff × spot − |maj| uitbetaald, dus wat de leverancier op de
  // energieprijs inhoudt is (1 − coeff). Bij coeff 0,95 → 5% van de gemiddelde
  // energieprijs × injectie-MWh, POSITIEF — het omgekeerde teken van offtake, waar
  // (coeff − 1) geldt. AVG-prijs injectie = ((€ injectie ÷ MWh) + |injMaj|) ÷ coeff.
  // Energy Fee = deze Factor Fee + Volume Fee.
  // Imbalance Cost EL (Self Bills) is PER ENERGIELABEL verschillend — zie
  // sbImbalanceCostFor hierboven voor de volledige beslisvolgorde en de twee
  // tariefgroepen. "RLP"/"SPP" wordt getoetst op het EL-prijsformuletype van
  // offtake ÓF injectie (bv. Belpex_RLP, Belpex-SPP).
  // EL Coeff Injection komt uit de bron als 0.9500000000000001 (float): op het
  // scherm en in de export hooguit 3 cijfers na de komma, leeg/0 blijft leeg.
  const coeff3 = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n !== 0 ? Number(n.toFixed(3)) : ''; };
  const calcELDetailSB = (f) => {
    const el = calcELDetail(f);
    const hasRlpSpp = (t) => { const s = (t || '').toUpperCase(); return s.includes('RLP') || s.includes('SPP'); };
    const isRlpSpp = hasRlpSpp(f.elTypeOfftake) || hasRlpSpp(f.elTypeInjection);
    const imbalanceCost = sbImbalanceCostFor(f.relatieBedrijf, f.elSegment, el.meterMethod, isRlpSpp);
    const injMajIsFix = !f.elMajorationInjection;
    const injMaj = injMajIsFix ? -25 : f.elMajorationInjection;
    const kwhMWh = (f.elKwhInjection || 0) / 1000;
    const volumeFee = kwhMWh * (Math.abs(injMaj) - (el.supplierCost + el.brpCost + imbalanceCost + el.greenCost));
    const coeffInj = parseFloat(f.elCoeffInjection) || 1;
    const amountInj = f.elAmountInjection || 0;
    const avgEnergyPrice = amountInj === 0 || kwhMWh === 0 ? 0 : ((amountInj / kwhMWh) + Math.abs(injMaj)) / coeffInj;
    const factorFee = (1 - coeffInj) * avgEnergyPrice * kwhMWh;
    return { ...el, avgEnergyPrice, factorFee, imbalanceCost, volumeFee, energyFee: factorFee + volumeFee, injMaj, injMajIsFix };
  };

  const calcNGDetail = (f) => {
    const kwhMWh = (f.ngKwhOfftake || 0) / 1000;
    const coeff = parseFloat(f.ngCoeffOfftake) || 1;
    const maj = f.ngMajorationOfftake || 0;
    const amount = f.ngAmountOfftake || 0;
    const avgEnergyPrice = amount === 0 || kwhMWh === 0 ? 0 : ((amount / kwhMWh) - maj) / coeff;
    const factorFee = (coeff - 1) * avgEnergyPrice * kwhMWh;
    const volumeFee = kwhMWh * (maj - (SUPPLIER_COST_GAS + SHIPPER_COST_GAS));
    const energyFee = factorFee + volumeFee;
    return { avgEnergyPrice, factorFee, supplierCost: SUPPLIER_COST_GAS, shipperCost: SHIPPER_COST_GAS, volumeFee, energyFee };
  };

  // Gecombineerde facturen + creditfacturen (de `facturen`-prop bevat al beide:
  // partner_commission_export + partner_commission_Credit_export), gepagineerd
  // op 15 per pagina.
  // Detailtabel respecteert de paginafilters (energielabel/verkoopteam/verkoper)
  // op factuurvelden, zodat "wat je ziet" overeenkomt met wat je exporteert.
  const detailFacturen = useMemo(() => {
    let data = facturenCustomer;
    if (energielabelFilter !== 'all') data = data.filter(f => f.relatieBedrijf === energielabelFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(f => f.verkoopteam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(f => f.verkoper === verkoperFilter);
    if (detailSearch) data = data.filter(f => invoiceMatchesSearch(f, detailSearch));
    if (detailSoortFilter !== 'all') data = data.filter(f => f.soort === detailSoortFilter);
    if (detailStatusFilter !== 'all') data = data.filter(f => f.statusBetaling === detailStatusFilter);
    if (detailCpsFilter !== 'all') data = data.filter(f => getRecordEffStatus(f) === detailCpsFilter);
    if (detailSegmentFilter !== 'all') data = data.filter(f => factuurMatchesSegment(f, detailSegmentFilter));
    if (detailMarketFilter !== 'all') data = data.filter(f => detailMarketFilter === 'EL' ? factuurHasEL(f) : factuurHasNG(f));
    if (detailFeeTagFilter !== 'all') data = data.filter(f => getFactuurFeePartnerTag(f) === detailFeeTagFilter);
    // Sorteer per GSRN (EL, anders NG), dan op factuurdatum.
    return [...data].sort((a, b) => {
      const ga = (a.elGSRN || a.ngGSRN || ''), gb = (b.elGSRN || b.ngGSRN || '');
      return ga.localeCompare(gb) || String(a.factuurdatum || '').localeCompare(String(b.factuurdatum || ''));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturenCustomer, energielabelFilter, verkoopteamFilter, verkoperFilter, detailSearch, detailStatusFilter, detailSoortFilter, detailCpsFilter, detailSegmentFilter, detailMarketFilter, detailFeeTagFilter, customerPaymentStatusMap, feeNotAllowedCustomers]);
  const detailStatuses = useMemo(() => [...new Set(facturenCustomer.map(f => f.statusBetaling).filter(Boolean))].sort(), [facturenCustomer]);
  const detailSoorten = useMemo(() => [...new Set(facturenCustomer.map(f => f.soort).filter(Boolean))].sort(), [facturenCustomer]);
  // Verdwijnt de gekozen soort door een scope-wissel uit de lijst, zet hem terug op
  // "Alle" — anders filtert hij op iets wat niet meer te zien is. Enkel zolang er
  // facturen IN de scope zitten, zodat dit tijdens het laden niets wist.
  useEffect(() => {
    if (facturenCustomer.length === 0) return;
    if (detailSoortFilter !== 'all' && !detailSoorten.includes(detailSoortFilter)) setDetailSoortFilter('all');
  }, [facturenCustomer, detailSoorten, detailSoortFilter]);
  const detailTotalPages = Math.max(1, Math.ceil(detailFacturen.length / pageSize));
  const detailPageClamped = Math.min(detailPage, detailTotalPages);
  // Accessors voor berekende kolommen (fee-berekeningen + status) zodat ook die
  // sorteerbaar zijn; veld-gebaseerde kolommen sorteren rechtstreeks op row[key].
  const detailSortAcc = {
    cps: f => getRecordEffStatus(f), feeTag: f => getFactuurFeePartnerTag(f),
    elAvg: f => calcELDetail(f).avgEnergyPrice, elFactor: f => calcELDetail(f).factorFee, elMeter: f => calcELDetail(f).meterMethod,
    elImbalance: f => calcELDetail(f).imbalanceCost, elGreen: f => calcELDetail(f).greenCost, elSupplier: f => calcELDetail(f).supplierCost,
    elBrp: f => calcELDetail(f).brpCost, elVolume: f => calcELDetail(f).volumeFee, elEnergy: f => calcELDetail(f).energyFee,
    ngAvg: f => calcNGDetail(f).avgEnergyPrice, ngFactor: f => calcNGDetail(f).factorFee, ngSupplier: f => calcNGDetail(f).supplierCost,
    ngShipper: f => calcNGDetail(f).shipperCost, ngVolume: f => calcNGDetail(f).volumeFee, ngEnergy: f => calcNGDetail(f).energyFee,
  };
  const paginatedDetail = sortRows(detailFacturen, detailSort.sortConfig, detailSortAcc).slice((detailPageClamped - 1) * pageSize, detailPageClamped * pageSize);

  // ── Gefactureerde Volume per EAN ────────────────────────────────────────────
  // ÉÉN regel per aansluiting per klant in plaats van per factuur. De sleutel en
  // de sommaties staan in src/eanVolume.js, met tests — de keuze van die sleutel
  // is nagemeten op de echte data en valt niet met het oog te controleren.
  const volumeGroepen = useMemo(
    () => groepeerEanVolumes(detailFacturen, (f) => calcELDetail(f).meterMethod),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailFacturen]
  );

  // Standaard op EL GSRN, zodat de aansluitingen van dezelfde klant onder elkaar
  // staan. De attribuutkolommen sorteren op de getoonde waarde.
  const volumeSortAcc = {
    elMeter: r => r._meter.waarde,
    elSegment: r => r._elSegment.waarde,
    ngSegment: r => r._ngSegment.waarde,
    elMajorationOfftake: r => Number(r._elMaj.waarde) || 0,
    ngMajorationOfftake: r => Number(r._ngMaj.waarde) || 0,
  };
  const volumeRijen = useMemo(
    () => sortRows(volumeGroepen, volumeSort.sortConfig, volumeSortAcc),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [volumeGroepen, volumeSort.sortConfig]
  );
  const volumeTotalPages = Math.max(1, Math.ceil(volumeRijen.length / pageSize));
  const volumePageClamped = Math.min(volumePage, volumeTotalPages);
  const paginatedVolume = volumeRijen.slice((volumePageClamped - 1) * pageSize, volumePageClamped * pageSize);
  useEffect(() => { setVolumePage(1); }, [detailFacturen]);

  // Export alle (gefilterde) detailregels naar CSV — alle kolommen incl. de
  // berekende EL/NG-velden, zodat de download exact overeenkomt met de tabel.
  const exportDetailCsv = (collect) => {
    const headers = [
      'Relatie/Bedrijf', 'Verkoper', 'Verkoopteam', 'Nummer', 'Factuurdatum', 'Relatie', 'Soort', 'Status betaling', 'Customer Payment Status', 'Fee Customer Tag',
      'EL EAN', 'EL Ratecard', 'EL Segment', 'EL kWh Offtake', 'EL EUR Offtake', 'EL Coeff Offtake', 'EL Type Offtake', 'EL Maj Offtake',
      'AVG Energy Price EL', 'Factor Fee EL', 'Meter Method', 'Imbalance Cost EL', 'Green Cost EL', 'Supplier Cost EL', 'BRP Cost EL', 'Volume Fee EL', 'Energy Fee EL',
      'NG EAN', 'NG Ratecard', 'NG Segment', 'NG kWh Offtake', 'NG EUR Offtake', 'NG Coeff Offtake', 'NG Type Offtake', 'NG Maj Offtake',
      'AVG Energy Price Gas', 'Factor Fee NG', 'Supplier Cost Gas', 'Shipper Cost Gas', 'Volume Fee Gas', 'Energy Fee Gas',
      'Totaal excl. BTW', 'Totaal',
    ];
    const num = v => (v == null || Number.isNaN(Number(v))) ? '' : String(Number(v)).replace('.', ',');
    const txt = v => v == null ? '' : String(v).replace(/[\r\n;]/g, ' ');
    const lines = [headers.join(';')];
    for (const f of detailFacturen) {
      const el = calcELDetail(f); const ng = calcNGDetail(f);
      lines.push([
        txt(f.relatieBedrijf), txt(f.verkoper === 'OdooBot' ? 'Online' : f.verkoper), txt(f.verkoopteam), txt(f.nummer), txt(f.factuurdatum), txt(f.relatieNaam), txt(f.soort), txt(f.statusBetaling), txt(getRecordEffStatus(f)), txt(getFactuurFeePartnerTag(f)),
        csvTextCell(f.elGSRN), txt(f.elRatecard), txt(f.elSegment), num(f.elKwhOfftake), num(f.elAmountOfftake), txt(f.elCoeffOfftake), txt(f.elTypeOfftake), num(f.elMajorationOfftake),
        num(el.avgEnergyPrice), num(el.factorFee), txt(el.meterMethod), num(el.imbalanceCost), num(el.greenCost), num(el.supplierCost), num(el.brpCost), num(el.volumeFee), num(el.energyFee),
        csvTextCell(f.ngGSRN), txt(f.ngRatecard), txt(f.ngSegment), num(f.ngKwhOfftake), num(f.ngAmountOfftake), txt(f.ngCoeffOfftake), txt(f.ngTypeOfftake), num(f.ngMajorationOfftake),
        num(ng.avgEnergyPrice), num(ng.factorFee), num(ng.supplierCost), num(ng.shipperCost), num(ng.volumeFee), num(ng.energyFee),
        num(f.totaalExclBTW), num(f.totaal),
      ].join(';'));
    }
    const csv = '﻿' + lines.join('\r\n');
    return leverCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: "partnervergoeding-facturen", scope: exportScope }), csv, collect);
  };

  // Export van "Gefactureerde Volume per EAN" — exact de kolommen van die tabel,
  // in dezelfde volgorde als op het scherm (platformconventie: wat je ziet is wat
  // je exporteert), inclusief de twee totaalkolommen per GSRN.
  const exportVolumePerEanCsv = (collect) => {
    const headers = [
      'Relatie/Bedrijf', 'Verkoper', 'Verkoopteam', 'Relatie', 'Klantnr.', 'Facturen',
      'EL EAN', 'EL Segment', 'EL Maj Offtake', 'Meter Method',
      'NG EAN', 'NG Segment', 'NG Maj Offtake',
      'Total Invoice EL kWh', 'Total Invoice NG kWh',
    ];
    const num = v => (v == null || Number.isNaN(Number(v))) ? '' : String(Number(v)).replace('.', ',');
    const txt = v => v == null ? '' : String(v).replace(/[\r\n;]/g, ' ');
    // Wisselt een attribuut binnen de regel, dan gaan ALLE waarden mee in de export
    // ("35 | 40"). In een cel afkappen tot de recentste zou een tarief tonen dat
    // maar voor een deel van de facturen geldt, en dat merk je in Excel niet.
    const attr = (a, formatteer = txt) => a.varieert ? txt(a.waarden.join(' | ')) : formatteer(a.waarde);
    const lines = [headers.join(';')];
    for (const r of volumeRijen) {
      lines.push([
        txt(r.relatieBedrijf), txt(r.verkoper === 'OdooBot' ? 'Online' : r.verkoper), txt(r.verkoopteam), txt(r.relatieNaam),
        csvTextCell(r.relatieReferentie), num(r.aantalFacturen),
        csvTextCell(r.elGSRN), attr(r._elSegment), attr(r._elMaj, num), attr(r._meter),
        csvTextCell(r.ngGSRN), attr(r._ngSegment), attr(r._ngMaj, num),
        num(r.totaalElKwh), num(r.totaalNgKwh),
      ].join(';'));
    }
    const csv = '﻿' + lines.join('\r\n');
    return leverCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: "gefactureerde-volume-per-ean", scope: exportScope }), csv, collect);
  };

  // Self bills (partner_commission_export_SB) — alleen elektriciteit. Respecteren
  // EXACT dezelfde energielabel/verkoopteam/verkoper-filters (en RBAC-scoping via
  // de prop) als de detailtabel hierboven, zodat "wat je ziet = wat je exporteert".
  // Self bills splitsen: klant-self-bills (klantfacturen) vs ET-leveranciersfacturen.
  // Leveranciersfacturen herken je aan een factuurnummer dat begint met "SBET"
  // (bv. "SBET1/2025/07/0001"). De SELF BILLS-hoofdtabel én alle vergoeding-
  // berekeningen draaien enkel op de klant-self-bills; de SBET-rijen komen in een
  // aparte SELF BILLS ET-tabel (enkel admin / ET-dossierbeheerder / master).
  // De self bills uit de facturen-feed (zie isSelfBillNumber hierboven) horen er
  // gewoon bij: één stroom, daarna pas de splitsing klant vs leverancier. Zo tellen
  // ze ook mee in de vergoeding-berekening, net als elke andere self bill.
  const selfBillsAlle = useMemo(
    () => [...selfBills, ...facturen.filter(f => isSelfBillNumber(f.nummer))],
    [selfBills, facturen]);   // eslint-disable-line react-hooks/exhaustive-deps
  const selfBillsCustomer = useMemo(() => selfBillsAlle.filter(f => !(f.nummer || '').toUpperCase().startsWith('SBET')), [selfBillsAlle]);
  const selfBillsEt = useMemo(() => selfBillsAlle.filter(f => (f.nummer || '').toUpperCase().startsWith('SBET')), [selfBillsAlle]);
  const filteredSelfBills = useMemo(() => {
    let data = selfBillsCustomer;
    if (energielabelFilter !== 'all') data = data.filter(f => f.relatieBedrijf === energielabelFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(f => f.verkoopteam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(f => f.verkoper === verkoperFilter);
    return data;
  }, [selfBillsCustomer, energielabelFilter, verkoopteamFilter, verkoperFilter]);
  const filteredSelfBillsEt = useMemo(() => {
    let data = selfBillsEt;
    if (energielabelFilter !== 'all') data = data.filter(f => f.relatieBedrijf === energielabelFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(f => f.verkoopteam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(f => f.verkoper === verkoperFilter);
    return data;
  }, [selfBillsEt, energielabelFilter, verkoopteamFilter, verkoperFilter]);

  // Tabelweergave van de self bills = filteredSelfBills + de tabel-eigen filters
  // (zoek/status/CPS). BELANGRIJK: dit voedt ALLEEN de tabel + export, NIET de
  // samenvatting (selfBillsGoed/Wanbetaler/Overig blijven op filteredSelfBills).
  const sbStatuses = useMemo(() => [...new Set(selfBillsCustomer.map(f => f.statusBetaling).filter(Boolean))].sort(), [selfBillsCustomer]);
  const selfBillsTable = useMemo(() => {
    let data = filteredSelfBills;
    if (sbSearch) data = data.filter(f => invoiceMatchesSearch(f, sbSearch));
    if (sbStatusFilter !== 'all') data = data.filter(f => f.statusBetaling === sbStatusFilter);
    if (sbCpsFilter !== 'all') data = data.filter(f => getRecordEffStatus(f) === sbCpsFilter);
    if (sbSegmentFilter !== 'all') data = data.filter(f => factuurMatchesSegment(f, sbSegmentFilter));
    if (sbFeeTagFilter !== 'all') data = data.filter(f => getFactuurFeePartnerTag(f) === sbFeeTagFilter);
    // Sorteer per GSRN (self bills = elektriciteit), dan op factuurdatum.
    return [...data].sort((a, b) => {
      const ga = (a.elGSRN || a.ngGSRN || ''), gb = (b.elGSRN || b.ngGSRN || '');
      return ga.localeCompare(gb) || String(a.factuurdatum || '').localeCompare(String(b.factuurdatum || ''));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSelfBills, sbSearch, sbStatusFilter, sbCpsFilter, sbSegmentFilter, sbFeeTagFilter, customerPaymentStatusMap, feeNotAllowedCustomers]);

  // Self bills opgesplitst naar dezelfde betaalstatus-buckets als de facturen
  // (zelfde klant-Wanbetaler-bepaling + factuurstatus). Voeden ENKEL de
  // 'Custom - EL Injection'-rij van de samenvattingsblokken + de losse Custom EL
  // Injection-tabel; alle andere segmenten blijven op de partnervergoeding-data.
  const selfBillsGoed = useMemo(() => filteredSelfBills.filter(f => {
    const status = (f.statusBetaling || '').toLowerCase();
    const isGoodStatus = status === 'betaald' || status === 'omgekeerd';
    return isGoodStatus && getRecordEffStatus(f) === 'Goed' && customerFeeAllowed(f.relatieReferentie);
  }), [filteredSelfBills, badDebt, feeNotAllowedCustomers]);

  const selfBillsWanbetaler = useMemo(() => filteredSelfBills.filter(f =>
    getRecordEffStatus(f) === 'Wanbetaler' && customerFeeAllowed(f.relatieReferentie)
  ), [filteredSelfBills, badDebt, feeNotAllowedCustomers]);

  const selfBillsDeelsOninbaar = useMemo(() => filteredSelfBills.filter(f =>
    getRecordEffStatus(f) === PAY_STATUS_PARTIAL && customerFeeAllowed(f.relatieReferentie)
  ), [filteredSelfBills, badDebt, feeNotAllowedCustomers]);

  const selfBillsOverig = useMemo(() => filteredSelfBills.filter(f => {
    const status = (f.statusBetaling || '').toLowerCase();
    const isGoodStatus = status === 'betaald' || status === 'omgekeerd';
    return !isGoodStatus && getRecordEffStatus(f) === 'Goed' && customerFeeAllowed(f.relatieReferentie);
  }), [filteredSelfBills, badDebt, feeNotAllowedCustomers]);

  const selfBillTotalPages = Math.max(1, Math.ceil(selfBillsTable.length / pageSize));
  const selfBillPageClamped = Math.min(selfBillPage, selfBillTotalPages);
  const sbSortAcc = {
    cps: f => getRecordEffStatus(f), feeTag: f => getFactuurFeePartnerTag(f),
    sbInjMaj: f => calcELDetailSB(f).injMaj, sbFactor: f => calcELDetailSB(f).factorFee, sbMeter: f => calcELDetailSB(f).meterMethod,
    sbImbalance: f => calcELDetailSB(f).imbalanceCost, sbGreen: f => calcELDetailSB(f).greenCost, sbSupplier: f => calcELDetailSB(f).supplierCost,
    sbBrp: f => calcELDetailSB(f).brpCost, sbVolume: f => calcELDetailSB(f).volumeFee, sbEnergy: f => calcELDetailSB(f).energyFee,
  };
  const paginatedSelfBills = sortRows(selfBillsTable, sbSort.sortConfig, sbSortAcc).slice((selfBillPageClamped - 1) * pageSize, selfBillPageClamped * pageSize);
  const selfBillTotals = useMemo(() => selfBillsTable.reduce((acc, f) => {
    const el = calcELDetailSB(f);
    acc.kwhOfftake += f.elKwhOfftake || 0;
    acc.amountOfftake += f.elAmountOfftake || 0;
    acc.energyFee += el.energyFee || 0;
    acc.totaal += f.totaal || 0;
    return acc;
  }, { kwhOfftake: 0, amountOfftake: 0, energyFee: 0, totaal: 0 }), [selfBillsTable]);

  const exportSelfBillsCsv = (collect) => {
    const headers = [
      'Relatie/Bedrijf', 'Verkoper', 'Verkoopteam', 'Nummer', 'Factuurdatum', 'Relatie', 'Soort', 'Status betaling', 'Customer Payment Status', 'Fee Customer Tag',
      'EL EAN', 'EL Ratecard', 'EL Segment', 'EL kWh Offtake', 'EL EUR Offtake', 'EL Coeff Injection', 'EL Type Offtake', 'EL Type Injection', 'EL Maj Offtake',
      'AVG Energy Price EL', 'Factor Fee EL', 'Meter Method', 'Imbalance Cost EL', 'Green Cost EL', 'Supplier Cost EL', 'BRP Cost EL', 'Volume Fee EL', 'Energy Fee EL',
      'EL kWh Injection', 'EL EUR Injection', 'EL Maj Injection', 'Imbalance kWh', 'Imbalance EUR',
      'Totaal excl. BTW', 'Totaal',
    ];
    const num = v => (v == null || Number.isNaN(Number(v))) ? '' : String(Number(v)).replace('.', ',');
    const txt = v => v == null ? '' : String(v).replace(/[\r\n;]/g, ' ');
    const lines = [headers.join(';')];
    for (const f of selfBillsTable) {
      const el = calcELDetailSB(f);
      lines.push([
        txt(f.relatieBedrijf), txt(f.verkoper === 'OdooBot' ? 'Online' : f.verkoper), txt(f.verkoopteam), txt(f.nummer), txt(f.factuurdatum), txt(f.relatieNaam), txt(f.soort), txt(f.statusBetaling), txt(getRecordEffStatus(f)), txt(getFactuurFeePartnerTag(f)),
        csvTextCell(f.elGSRN), txt(f.elRatecard), txt(f.elSegment), num(f.elKwhOfftake), num(f.elAmountOfftake), num(coeff3(f.elCoeffInjection)), txt(f.elTypeOfftake), txt(f.elTypeInjection), num(f.elMajorationOfftake),
        num(el.avgEnergyPrice), num(el.factorFee), txt(el.meterMethod), num(el.imbalanceCost), num(el.greenCost), num(el.supplierCost), num(el.brpCost), num(el.volumeFee), num(el.energyFee),
        num(f.elKwhInjection), num(f.elAmountInjection), num(f.elMajorationInjection || -25), num(f.elKwhImbalance), num(f.elAmountImbalance),
        num(f.totaalExclBTW), num(f.totaal),
      ].join(';'));
    }
    const csv = '﻿' + lines.join('\r\n');
    return leverCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: "partnervergoeding-selfbills", scope: exportScope }), csv, collect);
  };

  // ── SELF BILLS ET (leveranciersfacturen, factuurnr. "SBET…") ─────────────
  // Enkel zichtbaar voor admin / ET-dossierbeheerder / master. De titel toont het
  // ET-merk enkel aan admin/ET (compliance: "ET" verborgen voor master).
  const canSeeEtSelfBills = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier' || currentUser?.role === 'master';
  const etSbShowBrand = seesEtBrand(currentUser?.role);
  const etSbTitle = etSbShowBrand ? 'SELF BILLS ET' : 'SELF BILLS LEVERANCIER';
  // Compliance: ook de BESTANDSNAAM mag "ET" enkel dragen voor admin/ET-dossier —
  // master ziet deze tabellen wél, maar krijgt de merkloze variant, net als de titel.
  const etFilePrefix = etSbShowBrand ? 'ET' : 'leverancier';
  // Compliance: leveranciersfacturen kunnen de ET-entiteit als tegenpartij hebben.
  // Voor master (geen ET-merkrol) elk tekstveld door hideEtBrand halen — dit raakt
  // functionele codes zoals het factuurnummer "SBET…" of GSRN's niet aan.
  const etText = v => etSbShowBrand ? v : hideEtBrand(v);
  const selfBillsEtTable = useMemo(
    () => [...filteredSelfBillsEt].sort((a, b) =>
      String(a.factuurdatum || '').localeCompare(String(b.factuurdatum || '')) ||
      String(a.nummer || '').localeCompare(String(b.nummer || ''))),
    [filteredSelfBillsEt]
  );
  const selfBillEtTotalPages = Math.max(1, Math.ceil(selfBillsEtTable.length / pageSize));
  const selfBillEtPageClamped = Math.min(selfBillEtPage, selfBillEtTotalPages);
  const paginatedSelfBillsEt = sortRows(selfBillsEtTable, sbEtSort.sortConfig).slice((selfBillEtPageClamped - 1) * pageSize, selfBillEtPageClamped * pageSize);
  const selfBillEtTotals = useMemo(() => selfBillsEtTable.reduce((acc, f) => {
    acc.totaalExclBTW += f.totaalExclBTW || 0;
    acc.totaal += f.totaal || 0;
    return acc;
  }, { totaalExclBTW: 0, totaal: 0 }), [selfBillsEtTable]);
  const exportSelfBillsEtCsv = (collect) => {
    const headers = ['Relatie/Bedrijf', 'Verkoper', 'Verkoopteam', 'Nummer', 'Factuurdatum', 'Relatie', 'Soort', 'Status betaling', 'EL EAN', 'NG EAN', 'Totaal excl. BTW', 'Totaal'];
    const num = v => (v == null || Number.isNaN(Number(v))) ? '' : String(Number(v)).replace('.', ',');
    const txt = v => v == null ? '' : String(v).replace(/[\r\n;]/g, ' ');
    const lines = [headers.join(';')];
    for (const f of selfBillsEtTable) {
      lines.push([
        txt(etText(f.relatieBedrijf)), txt(f.verkoper === 'OdooBot' ? 'Online' : etText(f.verkoper)), txt(etText(f.verkoopteam)), txt(etText(f.nummer)), txt(f.factuurdatum), txt(etText(f.relatieNaam)), txt(etText(f.soort)), txt(f.statusBetaling),
        csvTextCell(f.elGSRN), csvTextCell(f.ngGSRN), num(f.totaalExclBTW), num(f.totaal),
      ].join(';'));
    }
    const csv = '﻿' + lines.join('\r\n');
    return leverCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: `${etFilePrefix}-selfbills`, scope: exportScope }), csv, collect);
  };

  // ── INVOICE ET (leveranciersfacturen, factuurnr. "VET…") ─────────────────
  // Zelfde rol-gating + compliance als SELF BILLS ET (canSeeEtSelfBills /
  // etSbShowBrand / etText). Titel toont "ET" enkel aan admin/ET, niet aan master.
  const etInvTitle = etSbShowBrand ? 'INVOICE ET' : 'INVOICE LEVERANCIER';
  const detailFacturenEt = useMemo(
    () => [...filteredFacturenEt].sort((a, b) =>
      String(a.factuurdatum || '').localeCompare(String(b.factuurdatum || '')) ||
      String(a.nummer || '').localeCompare(String(b.nummer || ''))),
    [filteredFacturenEt]
  );
  const detailEtTotalPages = Math.max(1, Math.ceil(detailFacturenEt.length / pageSize));
  const detailEtPageClamped = Math.min(detailEtPage, detailEtTotalPages);
  const paginatedDetailEt = sortRows(detailFacturenEt, detEtSort.sortConfig).slice((detailEtPageClamped - 1) * pageSize, detailEtPageClamped * pageSize);
  const detailEtTotals = useMemo(() => detailFacturenEt.reduce((acc, f) => {
    acc.totaalExclBTW += f.totaalExclBTW || 0;
    acc.totaal += f.totaal || 0;
    return acc;
  }, { totaalExclBTW: 0, totaal: 0 }), [detailFacturenEt]);
  const exportInvoiceEtCsv = (collect) => {
    const headers = ['Relatie/Bedrijf', 'Verkoper', 'Verkoopteam', 'Nummer', 'Factuurdatum', 'Relatie', 'Soort', 'Status betaling', 'EL EAN', 'NG EAN', 'Totaal excl. BTW', 'Totaal'];
    const num = v => (v == null || Number.isNaN(Number(v))) ? '' : String(Number(v)).replace('.', ',');
    const txt = v => v == null ? '' : String(v).replace(/[\r\n;]/g, ' ');
    const lines = [headers.join(';')];
    for (const f of detailFacturenEt) {
      lines.push([
        txt(etText(f.relatieBedrijf)), txt(f.verkoper === 'OdooBot' ? 'Online' : etText(f.verkoper)), txt(etText(f.verkoopteam)), txt(etText(f.nummer)), txt(f.factuurdatum), txt(etText(f.relatieNaam)), txt(etText(f.soort)), txt(f.statusBetaling),
        csvTextCell(f.elGSRN), csvTextCell(f.ngGSRN), num(f.totaalExclBTW), num(f.totaal),
      ].join(';'));
    }
    const csv = '﻿' + lines.join('\r\n');
    return leverCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: `${etFilePrefix}-invoice`, scope: exportScope }), csv, collect);
  };

  const calcSummaryStats = (data, subscriptionFees = {}, injectionData = []) => {
    const elStatsCalc = {};
    const ngStatsCalc = {};
    const customElOfftakeStatsCalc = {};
    const customElInjectionStatsCalc = {};
    const b2bElInjectionStatsCalc = {};
    const customGasStatsCalc = {};

    data.forEach(f => {
      // EL Stats
      const elSegment = (f.elSegment || '').toUpperCase();
      const elRatecard = f.elRatecard || '';
      if (elRatecard && (elSegment.includes('B2C') || elSegment.includes('B2B'))) {
        const segmentLabel = elSegment.includes('B2C') ? 'B2C' : 'B2B';
        const key = `${elRatecard}-${segmentLabel}`;
        if (!elStatsCalc[key]) elStatsCalc[key] = { segment: segmentLabel, kwhOfftake: 0, factorFee: 0, volumeFee: 0 };
        const el = calcEL(f);
        elStatsCalc[key].kwhOfftake += f.elKwhOfftake || 0;
        elStatsCalc[key].factorFee += el.factorFee || 0;
        elStatsCalc[key].volumeFee += el.volumeFee || 0;
      }

      // NG Stats
      const ngSegment = (f.ngSegment || '').toUpperCase();
      const ngRatecard = f.ngRatecard || '';
      if (ngRatecard && (ngSegment.includes('B2C') || ngSegment.includes('B2B'))) {
        const segmentLabel = ngSegment.includes('B2C') ? 'B2C' : 'B2B';
        const key = `${ngRatecard}-${segmentLabel}`;
        if (!ngStatsCalc[key]) ngStatsCalc[key] = { segment: segmentLabel, kwhOfftake: 0, factorFee: 0, volumeFee: 0 };
        const ng = calcNG(f);
        ngStatsCalc[key].kwhOfftake += f.ngKwhOfftake || 0;
        ngStatsCalc[key].factorFee += ng.factorFee || 0;
        ngStatsCalc[key].volumeFee += ng.volumeFee || 0;
      }

      // Custom EL Offtake
      if (elRatecard && elSegment.includes('CUSTOM')) {
        if (!customElOfftakeStatsCalc[elRatecard]) customElOfftakeStatsCalc[elRatecard] = { kwhOfftake: 0, factorFee: 0, volumeFee: 0 };
        const el = calcEL(f);
        customElOfftakeStatsCalc[elRatecard].kwhOfftake += f.elKwhOfftake || 0;
        customElOfftakeStatsCalc[elRatecard].factorFee += el.factorFee || 0;
        customElOfftakeStatsCalc[elRatecard].volumeFee += el.volumeFee || 0;
      }

      // Custom Gas
      if (ngRatecard && ngSegment.includes('CUSTOM')) {
        if (!customGasStatsCalc[ngRatecard]) customGasStatsCalc[ngRatecard] = { kwhOfftake: 0, factorFee: 0, volumeFee: 0 };
        const ng = calcNG(f);
        customGasStatsCalc[ngRatecard].kwhOfftake += f.ngKwhOfftake || 0;
        customGasStatsCalc[ngRatecard].factorFee += ng.factorFee || 0;
        customGasStatsCalc[ngRatecard].volumeFee += ng.volumeFee || 0;
      }
    });

    // Custom EL Injection — UIT de self bills (injectionData), niet uit `data`.
    // De rest van de segmenten blijft op de partnervergoeding-facturen. We gebruiken
    // EXACT dezelfde berekening als de SELF BILLS — ELEKTRICITEIT-tabel (calcELDetailSB)
    // i.p.v. de oude calcELInjection, zodat dit blok een pure rollup van die tabel is
    // (injection-volumefee met kosten-aftrek + SMR3-onbalans + offtake-factorfee).
    injectionData.forEach(f => {
      const elSegment = (f.elSegment || '').toUpperCase();
      const elRatecard = f.elRatecard || '';
      if (!elRatecard) return;
      // B2C-injectie krijgt bewust GEEN eigen samenvattingsrij (afspraak): die
      // self-bill-regels worden hier dus niet geaggregeerd. Ze blijven wel gewoon
      // zichtbaar in de SELF BILLS — ELEKTRICITEIT-tabel.
      const store = elSegment.includes('CUSTOM') ? customElInjectionStatsCalc
        : elSegment.includes('B2B') ? b2bElInjectionStatsCalc : null;
      if (!store) return;
      if (!store[elRatecard]) store[elRatecard] = { kwhInjection: 0, factorFee: 0, volumeFee: 0 };
      const el = calcELDetailSB(f);
      store[elRatecard].kwhInjection += f.elKwhInjection || 0;
      store[elRatecard].factorFee += el.factorFee || 0;
      store[elRatecard].volumeFee += el.volumeFee || 0;
    });

    const elStatsArr = Object.values(elStatsCalc);
    const ngStatsArr = Object.values(ngStatsCalc);
    const customElOfftakeArr = Object.values(customElOfftakeStatsCalc);
    const customElInjectionArr = Object.values(customElInjectionStatsCalc);
    const b2bElInjectionArr = Object.values(b2bElInjectionStatsCalc);
    const customGasArr = Object.values(customGasStatsCalc);

    // Rij-volgorde: eerst alle EL-segmenten, daarna alle Gas-segmenten.
    return [
      { label: 'B2C - EL', color: '#3b82f6', kwhOfftake: elStatsArr.filter(r => r.segment === 'B2C').reduce((s, r) => s + r.kwhOfftake, 0), factorFee: elStatsArr.filter(r => r.segment === 'B2C').reduce((s, r) => s + r.factorFee, 0), volumeFee: elStatsArr.filter(r => r.segment === 'B2C').reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['B2C - EL'] || 0 },
      { label: 'B2B - EL', color: '#22c55e', kwhOfftake: elStatsArr.filter(r => r.segment === 'B2B').reduce((s, r) => s + r.kwhOfftake, 0), factorFee: elStatsArr.filter(r => r.segment === 'B2B').reduce((s, r) => s + r.factorFee, 0), volumeFee: elStatsArr.filter(r => r.segment === 'B2B').reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['B2B - EL'] || 0 },
      { label: 'Custom - EL Offtake', color: '#a855f7', kwhOfftake: customElOfftakeArr.reduce((s, r) => s + r.kwhOfftake, 0), factorFee: customElOfftakeArr.reduce((s, r) => s + r.factorFee, 0), volumeFee: customElOfftakeArr.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['Custom - EL Offtake'] || 0 },
      { label: 'Custom - EL Injection', color: '#ec4899', kwhOfftake: customElInjectionArr.reduce((s, r) => s + r.kwhInjection, 0), factorFee: customElInjectionArr.reduce((s, r) => s + r.factorFee, 0), volumeFee: customElInjectionArr.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['Custom - EL Injection'] || 0 },
      { label: 'B2B - EL Injection', color: '#8b5cf6', kwhOfftake: b2bElInjectionArr.reduce((s, r) => s + r.kwhInjection, 0), factorFee: b2bElInjectionArr.reduce((s, r) => s + r.factorFee, 0), volumeFee: b2bElInjectionArr.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['B2B - EL Injection'] || 0 },
      { label: 'B2C - GAS', color: '#f97316', kwhOfftake: ngStatsArr.filter(r => r.segment === 'B2C').reduce((s, r) => s + r.kwhOfftake, 0), factorFee: ngStatsArr.filter(r => r.segment === 'B2C').reduce((s, r) => s + r.factorFee, 0), volumeFee: ngStatsArr.filter(r => r.segment === 'B2C').reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['B2C - GAS'] || 0 },
      { label: 'B2B - GAS', color: '#eab308', kwhOfftake: ngStatsArr.filter(r => r.segment === 'B2B').reduce((s, r) => s + r.kwhOfftake, 0), factorFee: ngStatsArr.filter(r => r.segment === 'B2B').reduce((s, r) => s + r.factorFee, 0), volumeFee: ngStatsArr.filter(r => r.segment === 'B2B').reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['B2B - GAS'] || 0 },
      { label: 'Custom - GAS', color: '#14b8a6', kwhOfftake: customGasArr.reduce((s, r) => s + r.kwhOfftake, 0), factorFee: customGasArr.reduce((s, r) => s + r.factorFee, 0), volumeFee: customGasArr.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: subscriptionFees['Custom - GAS'] || 0 },
    ].map(r => ({ ...r, totalFee: (r.factorFee + r.volumeFee + r.subscriptionFee) * resellerFeeFactor }));
  };

  const summaryStatsGoed = useMemo(() => calcSummaryStats(facturenGoed, subscriptionFeesBySegment.goed, selfBillsGoed), [facturenGoed, subscriptionFeesBySegment, selfBillsGoed, resellerFeeFactor]);
  const summaryStatsWanbetaler = useMemo(() => calcSummaryStats(facturenWanbetaler, subscriptionFeesBySegment.wanbetaler, selfBillsWanbetaler), [facturenWanbetaler, subscriptionFeesBySegment, selfBillsWanbetaler, resellerFeeFactor]);
  const summaryStatsOverig = useMemo(() => calcSummaryStats(facturenOverig, subscriptionFeesBySegment.openstaand, selfBillsOverig), [facturenOverig, subscriptionFeesBySegment, selfBillsOverig, resellerFeeFactor]);
  const summaryStatsDeelsOninbaar = useMemo(() => calcSummaryStats(facturenDeelsOninbaar, subscriptionFeesBySegment.deelsOninbaar, selfBillsDeelsOninbaar), [facturenDeelsOninbaar, subscriptionFeesBySegment, selfBillsDeelsOninbaar, resellerFeeFactor]);

  const summaryTotalsGoed = { kwhOfftake: summaryStatsGoed.reduce((s, r) => s + r.kwhOfftake, 0), factorFee: summaryStatsGoed.reduce((s, r) => s + r.factorFee, 0), volumeFee: summaryStatsGoed.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: summaryStatsGoed.reduce((s, r) => s + r.subscriptionFee, 0), totalFee: summaryStatsGoed.reduce((s, r) => s + r.totalFee, 0) };
  const summaryTotalsWanbetaler = { kwhOfftake: summaryStatsWanbetaler.reduce((s, r) => s + r.kwhOfftake, 0), factorFee: summaryStatsWanbetaler.reduce((s, r) => s + r.factorFee, 0), volumeFee: summaryStatsWanbetaler.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: summaryStatsWanbetaler.reduce((s, r) => s + r.subscriptionFee, 0), totalFee: summaryStatsWanbetaler.reduce((s, r) => s + r.totalFee, 0) };
  const summaryTotalsOverig = { kwhOfftake: summaryStatsOverig.reduce((s, r) => s + r.kwhOfftake, 0), factorFee: summaryStatsOverig.reduce((s, r) => s + r.factorFee, 0), volumeFee: summaryStatsOverig.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: summaryStatsOverig.reduce((s, r) => s + r.subscriptionFee, 0), totalFee: summaryStatsOverig.reduce((s, r) => s + r.totalFee, 0) };
  const summaryTotalsDeelsOninbaar = { kwhOfftake: summaryStatsDeelsOninbaar.reduce((s, r) => s + r.kwhOfftake, 0), factorFee: summaryStatsDeelsOninbaar.reduce((s, r) => s + r.factorFee, 0), volumeFee: summaryStatsDeelsOninbaar.reduce((s, r) => s + r.volumeFee, 0), subscriptionFee: summaryStatsDeelsOninbaar.reduce((s, r) => s + r.subscriptionFee, 0), totalFee: summaryStatsDeelsOninbaar.reduce((s, r) => s + r.totalFee, 0) };
  // ── ADMIN-ANALYSE: wat blokkeren de customer tags? ──────────────────────────
  // Klanten met een tag die op Fee Allowed = No staat vallen VOLLEDIG uit de vier
  // samenvattingsblokken. Deze aggregatie maakt zichtbaar hoeveel dat is, per
  // blokkerende tag en gesplitst op wanbetaler-status.
  //
  // ⛔ INTERN — enkel voor de rol admin, op de Admin-tab. Deze cijfers horen NIET
  // in een export, NIET in de zip van "Export All" en NIET in het PDF-rapport.
  // Registreer hier dus geen tabel-export en neem deze waarden niet op in
  // buildRevenueReportHtml.
  //
  // Twee aggregaties naast elkaar:
  //   perTag    — een klant met twee blokkerende tags telt in BEIDE rijen mee.
  //   perStatus — elke klant precies één keer; dit voedt de totaalrij en het
  //               kengetal, zodat het totaal niet dubbeltelt.
  const feeGeblokkeerd = useMemo(() => {
    const leeg = () => ({ kwh: 0, factorFee: 0, volumeFee: 0, subscriptionFee: 0, klanten: new Set() });
    // Blokkerende tags per klant — uit ALLE orders, net als feeNotAllowedCustomers,
    // zodat deze analyse exact dezelfde klanten dekt als de uitsluiting.
    const tagsPerKlant = new Map();
    for (const o of (orders || [])) {
      const ref = String(o.customerReference || '').trim();
      if (!ref) continue;
      const blokkerend = String(o.partnerTags || '').split(',').map(t => t.trim())
        .filter(t => t && feeNotAllowedTags.has(t));
      if (!blokkerend.length) continue;
      const set = tagsPerKlant.get(ref) || new Set();
      for (const t of blokkerend) set.add(t);
      tagsPerKlant.set(ref, set);
    }
    if (!tagsPerKlant.size) return { rijen: [], totalen: {}, kengetal: 0, tags: [] };

    const perTag = new Map();      // `${tag}|${status}` → bedragen
    const perStatus = new Map();   // status → bedragen (klant telt één keer)
    const isWanbetaler = (ref) => String(customerPaymentStatusMap[String(ref || '').trim()] || 'Goed').startsWith('Wanbetaler');
    const boek = (ref, waarden) => {
      const tags = tagsPerKlant.get(String(ref || '').trim());
      if (!tags) return;
      const status = isWanbetaler(ref) ? 'Wanbetaler' : 'Geen wanbetaler';
      for (const tag of tags) {
        const k = `${tag}|${status}`;
        if (!perTag.has(k)) perTag.set(k, { tag, status, ...leeg() });
        const r = perTag.get(k);
        r.kwh += waarden.kwh; r.factorFee += waarden.factorFee; r.volumeFee += waarden.volumeFee; r.subscriptionFee += waarden.subscriptionFee;
        r.klanten.add(String(ref).trim());
      }
      if (!perStatus.has(status)) perStatus.set(status, { status, ...leeg() });
      const s = perStatus.get(status);
      s.kwh += waarden.kwh; s.factorFee += waarden.factorFee; s.volumeFee += waarden.volumeFee; s.subscriptionFee += waarden.subscriptionFee;
      s.klanten.add(String(ref).trim());
    };

    // Facturen: elektriciteit + gas, exact zoals calcSummaryStats rekent.
    for (const f of filteredFacturen) {
      const el = calcEL(f); const ng = calcNG(f);
      boek(f.relatieReferentie, {
        kwh: (f.elKwhOfftake || 0) + (f.ngKwhOfftake || 0),
        factorFee: (el.factorFee || 0) + (ng.factorFee || 0),
        volumeFee: (el.volumeFee || 0) + (ng.volumeFee || 0),
        subscriptionFee: 0,
      });
    }
    // Self bills: de injectie-regels (B2B/Custom), zoals in de blokken.
    for (const f of filteredSelfBills) {
      const seg = (f.elSegment || '').toUpperCase();
      if (!seg.includes('CUSTOM') && !seg.includes('B2B')) continue;
      if (!f.elRatecard) continue;
      const el = calcELDetailSB(f);
      boek(f.relatieReferentie, {
        kwh: f.elKwhInjection || 0,
        factorFee: el.factorFee || 0,
        volumeFee: el.volumeFee || 0,
        subscriptionFee: 0,
      });
    }
    // Abonnementsvergoeding uit de orders — berekend alsóf de tag niet blokkeerde,
    // want dat is precies het bedrag dat door de tag wegvalt.
    for (const o of filteredOrders) {
      const bedrag = getFeeSubscription(o, { negeerPartnerTag: true });
      if (!bedrag) continue;
      boek(o.customerReference, { kwh: 0, factorFee: 0, volumeFee: 0, subscriptionFee: bedrag });
    }

    const afwerken = (r) => ({
      ...r,
      aantalKlanten: r.klanten.size,
      totalFee: (r.factorFee + r.volumeFee + r.subscriptionFee) * resellerFeeFactor,
    });
    const rijen = [...perTag.values()].map(afwerken)
      .sort((a, b) => a.tag.localeCompare(b.tag) || a.status.localeCompare(b.status));
    const perStatusRijen = [...perStatus.values()].map(afwerken);
    const totalen = perStatusRijen.reduce((acc, r) => ({
      kwh: acc.kwh + r.kwh, factorFee: acc.factorFee + r.factorFee, volumeFee: acc.volumeFee + r.volumeFee,
      subscriptionFee: acc.subscriptionFee + r.subscriptionFee, totalFee: acc.totalFee + r.totalFee,
      aantalKlanten: acc.aantalKlanten + r.aantalKlanten,
    }), { kwh: 0, factorFee: 0, volumeFee: 0, subscriptionFee: 0, totalFee: 0, aantalKlanten: 0 });
    return { rijen, perStatus: perStatusRijen, totalen, kengetal: totalen.totalFee, tags: [...new Set(rijen.map(r => r.tag))] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, filteredFacturen, filteredSelfBills, filteredOrders, feeNotAllowedTags, customerPaymentStatusMap, resellerFeeFactor, labelConfig]);

  // Toon het "oninbaar deel"-blok + de CPS-filteroptie enkel als een energielabel in
  // beeld een Oninbaarheidsperiode wanbetalers (≠ 9999) heeft ingesteld.
  const hasBadDebtPeriod = badDebtNotes.length > 0;
  // Uitleg bij het WANBETALERS-blok. Noemt de drempel die voor de HUIDIGE selectie
  // geldt: één label in beeld → dat getal; meerdere labels met afwijkende drempels
  // → ze staan er allemaal bij, anders leest de tekst als één regel die niet klopt.
  const wanbetalerTip = useMemo(() => {
    // Eén label in beeld met een eigen drempel → gewoon dat getal. Anders de
    // standaard, met de afwijkende labels tussen haakjes erachter. Bewust NA het
    // woord "vervallen", anders leest de kopregel als "vanaf 40 dagen, behalve
    // Evident Energie: 55 vervallen".
    const eigenDrempel = overdueDayNotes.length === 1 && energielabelFilter !== 'all'
      ? overdueDayNotes[0].days
      : null;
    const dagen = eigenDrempel ?? BAD_DEBT_OVERDUE_DAYS_DEFAULT;
    const uitzonderingen = eigenDrempel != null ? [] : overdueDayNotes;
    const staart = uitzonderingen.length
      ? ` (${uitzonderingen.map(n => `${n.label}: ${n.days} dagen`).join(' · ')})`
      : '';
    return {
      // Kopregel van de tooltip: meteen het getal dat nú geldt, zodat je de
      // drempel ziet zonder de hele uitleg te lezen.
      titel: `WANBETALERS — vanaf ${dagen} dagen vervallen${staart}`,
      // Alleen de kernregel. De rest van de uitleg — de drempel per energielabel,
      // dat de status bij de KLANT hoort en dat ze over álle facturen gerekend
      // wordt — staat in de TECHNISCHE beschrijving, die enkel admin ziet. In een
      // tooltip las dat als een muur tekst waar een partner niets aan heeft.
      tekst: `Een klant komt in dit blok zodra minstens één van zijn verkoopfacturen onbetaald is én ${dagen} dagen of langer vervallen is, gerekend vanaf de vervaldatum.${uitzonderingen.length ? ` Afwijkend ingesteld: ${uitzonderingen.map(n => `${n.label} ${n.days} dagen`).join(', ')}.` : ''}`,
    };
  }, [overdueDayNotes, energielabelFilter]);

  // Vergoeding per verkoopteam: per team de Totale Fee voor de drie betaalstatus-
  // categorieën (Betaald & Goed / Wanbetaler / Openstaand) + de ingestelde
  // betaalde vergoeding en het verschil (te betalen). Volgt de actieve filters.
  // Hergebruikt calcSummaryStats per team-subset zodat de bedragen 1-op-1
  // overeenstemmen met de status-blokken hierboven.
  const teamRevenueRows = useMemo(() => {
    const teamKey = (v) => ((v || '').trim() || 'Online');
    // Abonnementsvergoeding per team → status → segment (uit de orders).
    const subs = {};
    const ensureSubs = (t) => (subs[t] || (subs[t] = { goed: {}, wanbetaler: {}, openstaand: {} }));
    filteredOrders.forEach(o => {
      const fee = getFeeSubscription(o); if (!fee) return;
      const seg = orderFeeSegmentKey(o); // zelfde classificatie als de DETAIL-tabel
      if (!seg) return;
      const status = getOrderPaymentStatus(o);
      const tgt = status === 'Goed' ? 'goed' : status === 'Wanbetaler' ? 'wanbetaler' : null;
      if (!tgt) return; // Openstaand: geen abonnementsvergoeding (zelfde regel als de blokken)
      const bucket = ensureSubs(teamKey(o.salespersonTeam));
      bucket[tgt][seg] = (bucket[tgt][seg] || 0) + fee;
    });
    // Facturen + self-bills per team groeperen, per status.
    const groups = {};
    const ensure = (t) => (groups[t] || (groups[t] = { goedF: [], wanF: [], openF: [], goedSB: [], wanSB: [], openSB: [] }));
    facturenGoed.forEach(f => ensure(teamKey(f.verkoopteam)).goedF.push(f));
    facturenWanbetaler.forEach(f => ensure(teamKey(f.verkoopteam)).wanF.push(f));
    facturenOverig.forEach(f => ensure(teamKey(f.verkoopteam)).openF.push(f));
    selfBillsGoed.forEach(f => ensure(teamKey(f.verkoopteam)).goedSB.push(f));
    selfBillsWanbetaler.forEach(f => ensure(teamKey(f.verkoopteam)).wanSB.push(f));
    selfBillsOverig.forEach(f => ensure(teamKey(f.verkoopteam)).openSB.push(f));
    Object.keys(subs).forEach(ensure);
    // Betaalde vergoeding per team: ÉÉN-OP-ÉÉN met de invoer op teamniveau (de
    // verkoperbedragen van dat team tellen NIET mee). Staat er een verkoper-filter,
    // dan stelt de rij die verkoper binnen zijn team voor en tonen we zijn bedragen.
    const paidRijNiveau = paidSelectieNiveau === 'seller' ? 'seller' : 'team';
    const paidPerTeam = paidCommissionFiltered.filter(r => r.sales_team && paidNiveauVan(r) === paidRijNiveau);
    paidPerTeam.forEach(r => ensure(teamKey(r.sales_team)));

    const sumTotal = (arr) => arr.reduce((s, r) => s + r.totalFee, 0);
    const rows = Object.keys(groups).map(team => {
      const g = groups[team];
      const sub = subs[team] || { goed: {}, wanbetaler: {}, openstaand: {} };
      const goed = sumTotal(calcSummaryStats(g.goedF, sub.goed, g.goedSB));
      const wan = sumTotal(calcSummaryStats(g.wanF, sub.wanbetaler, g.wanSB));
      const open = sumTotal(calcSummaryStats(g.openF, sub.openstaand, g.openSB));
      const betaald = paidPerTeam.filter(r => teamKey(r.sales_team) === team).reduce((s, r) => s + (Number(r.amount) || 0), 0);
      return { team, goed, wan, open, betaald, teBetalen: goed - betaald };
    });
    rows.sort((a, b) => b.goed - a.goed || a.team.localeCompare(b.team));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturenGoed, facturenWanbetaler, facturenOverig, selfBillsGoed, selfBillsWanbetaler, selfBillsOverig, filteredOrders, paidCommissionFiltered, paidSelectieNiveau, customerPaymentStatusMap, feeNotAllowedTags, resellerFeeFactor]);

  // Totaalrij = de som van de rijen. De betaalde-vergoeding-kolom is dus de som
  // van de TEAMbedragen en kan afwijken van het kengetal "Betaalde vergoeding" op
  // de Samenvatting: dat toont bij een energielabel-selectie enkel de bedragen die
  // aan het label zelf zijn toegewezen (één-op-één met de invoer).
  const teamRevenueTotals = useMemo(() => {
    const t = teamRevenueRows.reduce((acc, r) => ({ goed: acc.goed + r.goed, wan: acc.wan + r.wan, open: acc.open + r.open, betaald: acc.betaald + r.betaald }), { goed: 0, wan: 0, open: 0, betaald: 0 });
    return { ...t, teBetalen: t.goed - t.betaald };
  }, [teamRevenueRows]);

  // VERGOEDING PER VERKOPER: per verkoper de Invoiced kWh per segment (ENKEL Goede
  // klanten — identiek aan het blok "GOEDE KLANTEN", via calcSummaryStats per
  // verkoper-subset) + het aantal unieke EAN's (GSRN) per customer-segment uit
  // "PARTNERVERGOEDING — DETAIL ORDERS" (de contracted orders). De verkopers volgen
  // uit de reeds rol-gescopete facturen/self-bills/orders → alleen zichtbare verkopers.
  const verkoperSegmentRows = useMemo(() => {
    // Geen verkoper (leeg) én OdooBot worden samen onder "Online" geaggregeerd,
    // zodat élke factuur/order in een rij valt en het tabeltotaal sluit op de som
    // van alle verkopers (geen aparte "Onbekend"-restpost meer).
    const norm = (v) => { const n = (v || '').trim(); return (!n || n === 'OdooBot') ? 'Online' : n; };
    // kWh-bron: Goede-klanten-facturen (segmenten) + self-bills (Custom EL Injection), per verkoper.
    const fBySeller = {}; const sbBySeller = {};
    facturenGoed.forEach(f => { const s = norm(f.verkoper); (fBySeller[s] || (fBySeller[s] = [])).push(f); });
    selfBillsGoed.forEach(f => { const s = norm(f.verkoper); (sbBySeller[s] || (sbBySeller[s] = [])).push(f); });
    // EAN-bron: contracted orders (zelfde set als de DETAIL ORDERS-tabel), unieke GSRN per segment.
    const eanBySeller = {};
    filteredOrders.forEach(o => {
      if (!isContractedOrder(o)) return;
      const gsrn = o.connectionPointGSRN; if (!gsrn) return;
      const seg = orderSegmentClass(o); if (!seg) return; // 'B2C' | 'B2B' | 'CUSTOM'
      const s = norm(o.salesperson);
      const e = eanBySeller[s] || (eanBySeller[s] = { B2C: new Set(), B2B: new Set(), CUSTOM: new Set() });
      e[seg].add(gsrn);
    });
    const sellers = new Set([...Object.keys(fBySeller), ...Object.keys(sbBySeller), ...Object.keys(eanBySeller)]);
    const rows = [...sellers].map(s => {
      const stats = calcSummaryStats(fBySeller[s] || [], {}, sbBySeller[s] || []);
      const kwh = (label) => (stats.find(r => r.label === label)?.kwhOfftake) || 0;
      const e = eanBySeller[s] || { B2C: new Set(), B2B: new Set(), CUSTOM: new Set() };
      return {
        verkoper: s,
        kwhB2cEl: kwh('B2C - EL'),
        kwhB2bEl: kwh('B2B - EL'),
        kwhCustomElOfftake: kwh('Custom - EL Offtake'),
        kwhCustomElInjection: kwh('Custom - EL Injection'),
        kwhB2cGas: kwh('B2C - GAS'),
        kwhB2bGas: kwh('B2B - GAS'),
        kwhCustomGas: kwh('Custom - GAS'),
        eanB2c: e.B2C.size,
        eanB2b: e.B2B.size,
        eanCustom: e.CUSTOM.size,
      };
    });
    const totKwh = (r) => r.kwhB2cEl + r.kwhB2bEl + r.kwhCustomElOfftake + r.kwhCustomElInjection + r.kwhB2cGas + r.kwhB2bGas + r.kwhCustomGas;
    rows.sort((a, b) => totKwh(b) - totKwh(a) || a.verkoper.localeCompare(b.verkoper));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturenGoed, selfBillsGoed, filteredOrders, resellerFeeFactor]);

  const verkoperSegmentTotals = useMemo(() => verkoperSegmentRows.reduce((acc, r) => ({
    kwhB2cEl: acc.kwhB2cEl + r.kwhB2cEl, kwhB2bEl: acc.kwhB2bEl + r.kwhB2bEl,
    kwhCustomElOfftake: acc.kwhCustomElOfftake + r.kwhCustomElOfftake, kwhCustomElInjection: acc.kwhCustomElInjection + r.kwhCustomElInjection,
    kwhB2cGas: acc.kwhB2cGas + r.kwhB2cGas, kwhB2bGas: acc.kwhB2bGas + r.kwhB2bGas, kwhCustomGas: acc.kwhCustomGas + r.kwhCustomGas,
    eanB2c: acc.eanB2c + r.eanB2c, eanB2b: acc.eanB2b + r.eanB2b, eanCustom: acc.eanCustom + r.eanCustom,
  }), { kwhB2cEl: 0, kwhB2bEl: 0, kwhCustomElOfftake: 0, kwhCustomElInjection: 0, kwhB2cGas: 0, kwhB2bGas: 0, kwhCustomGas: 0, eanB2c: 0, eanB2b: 0, eanCustom: 0 }), [verkoperSegmentRows]);

  const verkoperSegPageSize = 15;
  const verkoperSegTotalPages = Math.max(1, Math.ceil(verkoperSegmentRows.length / verkoperSegPageSize));
  const verkoperSegClamped = Math.min(verkoperSegPage, verkoperSegTotalPages);
  const paginatedVerkoperSegmentRows = sortRows(verkoperSegmentRows, vkSegSort.sortConfig).slice((verkoperSegClamped - 1) * verkoperSegPageSize, verkoperSegClamped * verkoperSegPageSize);

  // Export VERGOEDING PER VERKOPER → CSV (alle verkopers, alle kolommen).
  const exportVerkoperSegmentCsv = () => {
    const columns = [
      { header: 'Verkoper', get: r => r.verkoper },
      { header: 'Kwh_B2C_El', get: r => Math.round(r.kwhB2cEl) },
      { header: 'Kwh_B2B_EL', get: r => Math.round(r.kwhB2bEl) },
      { header: 'Kwh_Custom_El Offtake', get: r => Math.round(r.kwhCustomElOfftake) },
      { header: 'Kwh_Custom_El Injection', get: r => Math.round(r.kwhCustomElInjection) },
      { header: 'Kwh_B2C_Gas', get: r => Math.round(r.kwhB2cGas) },
      { header: 'Kwh_B2B_Gas', get: r => Math.round(r.kwhB2bGas) },
      { header: 'Kwh_Custom_Gas', get: r => Math.round(r.kwhCustomGas) },
      { header: 'EAN B2C', get: r => r.eanB2c },
      { header: 'EAN B2B', get: r => r.eanB2b },
      { header: 'EAN Custom', get: r => r.eanCustom },
    ];
    // Sloot als enige Revenue-export niet aan op de naamgeving; nu wel.
    exportRowsToCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: 'vergoeding-per-verkoper', scope: exportScope }), columns, verkoperSegmentRows);
  };

  // ── Hooks voor het PDF-rapport en Export All ─────────────────────────────────
  // Staan hier BEWUST vóór de lege-staat-return verderop: hooks moeten in elke
  // render in dezelfde volgorde draaien, dus ze mogen niet achter een `return`.
  //
  // COMPLIANCE (zie CLAUDE.md): in het rapport mag "Energy Together" nergens
  // voorkomen — ook niet voor admin, want een rapport verlaat het scherm en is dan
  // niet meer rol-gebonden. Laag 1: de branding valt terug op neutraal zodra ze
  // naar het ET-merk wijst. Laag 2 zit in het rapport zelf (`rt()` → hideEtBrand).
  const reportBranding = useMemo(() => {
    const isEt = (v) => /energy\s*together/i.test(String(v || ''));
    if (energielabelFilter === 'all' || isEt(energielabelFilter)) return null;
    // Admin krijgt alle brandings binnen; niet-admins enkel de eigen huisstijl —
    // en dat is per definitie het enige label dat ze kunnen kiezen (GDPR-regel op
    // deze pagina), dus voor hen is activeBranding altijd de juiste.
    const found = (allBranding || []).find(b => b.energy_label === energielabelFilter)
      || (activeBranding?.energy_label === energielabelFilter ? activeBranding : null)
      || (currentUser?.energyLabel === energielabelFilter ? activeBranding : null);
    // Heeft het gekozen VERKOOPTEAM een eigen huisstijl, dan gaat die eroverheen —
    // het rapport gaat immers alleen over dat team. Voor een teamleader/verkoper is
    // dat al door de server gedaan (activeBranding is dan de samengevoegde rij);
    // deze regel dekt admin/ET, die de losse teamrecords binnenkrijgen.
    const team = verkoopteamFilter !== 'all'
      ? (allTeamBranding || []).find(b => b.energy_label === energielabelFilter && b.sales_team === verkoopteamFilter)
      : null;
    const merged = mergeBranding(found, team);
    if (!merged || isEt(merged.energy_label) || isEt(merged.display_name)) return null;
    return merged;
  }, [allBranding, allTeamBranding, activeBranding, energielabelFilter, verkoopteamFilter, currentUser]);

  // Tabellen die in een child-component leven (Orders-tab) registreren hun export
  // hier, zodat "Export All" ze kan aanroepen zonder de kolomdefinities te kopiëren.
  const tableExports = useRef({});
  const registerTableExport = useCallback((key, fn) => {
    if (fn) tableExports.current[key] = fn; else delete tableExports.current[key];
  }, []);

  // Filterbalk (energielabel → verkoopteam → verkoper). Herbruikt in de lege-staat
  // én de volledige pagina, zodat de filters ALTIJD zichtbaar zijn — ook zonder
  // data — en als input voor de API-ophaal kunnen dienen.
  const filterBar = (
    <Card className="!p-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Energieleverancier</label>
          <select value={energielabelFilter} onChange={e => handleEnergielabelChange(e.target.value)} className={`w-full px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm bg-slate-700 border rounded-lg ${energielabelFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <AllOption options={apiLabelOptions}>Alle Energieleveranciers</AllOption>
            {apiLabelOptions.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Verkoopteam</label>
          <select value={verkoopteamFilter} onChange={e => handleVerkoopteamChange(e.target.value)} className={`w-full px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm bg-slate-700 border rounded-lg ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <AllOption options={verkoopteams}>Alle Teams</AllOption>
            {verkoopteams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Verkoper</label>
          <select value={verkoperFilter} onChange={e => setVerkoperFilter(e.target.value)} className={`w-full px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm bg-slate-700 border rounded-lg ${verkoperFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <AllOption options={verkopers}>Alle Verkopers</AllOption>
            {verkopers.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 px-3 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-sm rounded-lg"><X size={14} />Wissen</button>}
        {hasActiveFilters && filteredFacturen.length > 0 && <div className="text-sm text-lime-400">Gefilterd: {filteredFacturen.length} facturen</div>}
      </div>
      {badDebtNotes.length > 0 && (
        <div className="mt-3 text-xs text-slate-400 space-y-0.5">
          {badDebtNotes.map(n => (
            <div key={n.label}>{energielabelFilter === 'all' ? `${n.label}: ` : ''}Oninbaarheidsperiode wanbetalers = {n.months} maanden</div>
          ))}
        </div>
      )}
    </Card>
  );

  // Haak voor de geplande partnervergoeding-mail — de hooks staan hier, vóór de
  // vroege return hieronder; de functies zelf worden verderop per render in de
  // ref gezet (feeMailJobRef.current = …), naast bouwRevenueZip.
  const feeMailJobRef = useRef(null);
  useEffect(() => {
    if (!isAdmin || !IS_FEE_MAIL_JOB) return undefined;
    const slaap = (ms) => new Promise((r) => setTimeout(r, ms));
    const naarBase64 = (blob) => new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
      fr.onerror = () => reject(fr.error || new Error('zip lezen mislukt'));
      fr.readAsDataURL(blob);
    });
    window.__feeMailJob = {
      bouwVoor: async (label, team, timeoutMs = 240000) => {
        // De pagina staat er al vóór de facturen binnen zijn (vroege return hierboven);
        // wacht tot ze data heeft en de ref gevuld is.
        const t00 = Date.now();
        while (!feeMailJobRef.current && Date.now() - t00 < timeoutMs) await slaap(500);
        const r = feeMailJobRef.current;
        if (!r) throw new Error('Revenue-pagina heeft geen data (facturen niet geladen)');
        // Eerst terug naar "geen selectie": een halve laadbeurt van het vorige
        // label mag nooit in deze zip belanden.
        r.kies('all', null);
        await slaap(400);
        feeMailJobRef.current.kies(label, team);
        const t0 = Date.now();
        let s = feeMailJobRef.current.staat();
        while (Date.now() - t0 < timeoutMs) {
          s = feeMailJobRef.current.staat();
          if (s.fout) throw new Error(`Partnervergoeding van ${label} laden mislukt: ${s.fout}`);
          if (s.label === label && s.team === (team || '') && s.gereed) break;
          await slaap(500);
        }
        if (!(s.label === label && s.team === (team || '') && s.gereed)) throw new Error(`Data van ${label} niet klaar binnen ${Math.round(timeoutMs / 1000)} s`);
        // Nog even laten uitrenderen: de afgeleide tabellen (useMemo) van de
        // nieuwe filters moeten in de laatste sluiting zitten.
        await slaap(1500);
        const z = await feeMailJobRef.current.bouw();
        const na = feeMailJobRef.current.staat();
        return { label: na.label, team: na.team, naam: z.naam, aantal: z.aantal, pdfFout: z.pdfFout, mislukt: z.mislukt, base64: await naarBase64(z.blob) };
      },
    };
    return () => { delete window.__feeMailJob; };
  }, [isAdmin]);
  if (!facturen.length && !selfBills.length) return (
    <div className="space-y-4">
      <PageHeader title="Partnervergoeding" subtitle="Alle bedragen excl. BTW" docs={<>{revenueDocsIcons()}<CommissionButtons /></>} />
      <OrdersLaadStatus />
      <CommissionStatus />
      {commissionMeta.unmappedCount > 0 && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-2 rounded text-sm">{commissionMeta.unmappedCount} record(s) zonder herkend energielabel (factuurnummer-prefix) — deze worden niet getoond.</div>}
      {revenueDocsModals()}
      {filterBar}
      <Card>
        {/* De lege staat moet zeggen wat er ÉCHT aan de hand is. De oude tekst
            verwees naar de knop "Ophalen via API" en naar een bestand-import —
            allebei bestaan ze alleen voor admin, dus een teamleader las een
            instructie die hij onmogelijk kon uitvoeren en die bovendien het
            verkeerde vermoeden wekte (data zou niet automatisch laden).
            Er zijn drie verschillende situaties, en die zien er identiek uit
            zolang je ze niet uit elkaar houdt: */}
        {(() => {
          const geladenRecords = (commissionMeta.salesCount || 0) + (commissionMeta.creditCount || 0) + (commissionMeta.selfbillCount || 0);
          const mijnTeam = currentUser?.salesTeam;
          const mijnVerkoper = currentUser?.sellerName;
          // 1. Er kwamen wél records binnen voor dit energielabel, maar na de
          //    scope-filter blijft er niets over. Dan hangt het niet op de sync
          //    maar op de koppeling verkoper → verkoopteam: een record waarvan de
          //    verkoper in de sales-structuur geen team heeft, valt buiten elke
          //    teamscope. Benoem dat, anders zoekt iedereen in de verkeerde hoek.
          if (geladenRecords > 0) return (
            <div className="py-6 text-sm text-slate-400 space-y-2">
              <div className="text-slate-300">Er zijn <strong>{formatNumberRounded(geladenRecords)}</strong> records opgehaald voor {energielabelFilter}, maar geen enkele valt binnen je eigen scope{mijnTeam ? ` (verkoopteam ${mijnTeam}${mijnVerkoper ? `, verkoper ${mijnVerkoper}` : ''})` : ''}.</div>
              <div className="text-xs">Dat wijst op de koppeling <strong>verkoper → verkoopteam</strong>: staat de verkoper van deze facturen niet onder jouw team in de sales-structuur, dan blijft de pagina leeg. Meld dit aan de beheerder — die kan de verkoper aan het juiste verkoopteam koppelen.</div>
            </div>
          );
          // 2. Niet-admin: er valt hier niets te klikken. De data komt uit de
          //    geplande synchronisatie en wordt bij het openen automatisch gelezen.
          if (currentUser?.role !== 'admin') return (
            <div className="py-6 text-sm text-slate-400 space-y-2">
              <div className="text-slate-300">Voor {energielabelFilter === 'all' ? 'dit energielabel' : energielabelFilter} staat er nog geen partnervergoeding klaar.</div>
              <div className="text-xs">De cijfers worden automatisch geladen zodra de synchronisatie ze heeft opgehaald; je hoeft hier niets te doen. Blijft dit langer dan een dag staan, meld het dan aan de beheerder.</div>
            </div>
          );
          // 3. Admin: die heeft de knop wél.
          return <div className="text-center py-8 text-slate-500 text-sm">Nog geen Revenue-data geladen. Kies hierboven een energielabel (+ optioneel verkoopteam/verkoper) en klik "Ophalen via API".</div>;
        })()}
      </Card>
    </div>
  );

  // Harde spatie ( ) tussen € en bedrag → euroteken blijft altijd bij het getal.
  const formatCurrencyRounded = (val) => `€ ${Math.round(val || 0).toLocaleString('nl-BE')}`;

  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
  };

  // Samenvattingsblok: de segmentrijen zijn gegroepeerd per energietype met een
  // eigen kop, eigen subtotaal en een dikkere scheidingslijn vóór de injectie-
  // rijen. De bolletjes zijn gecodeerd per segmentTYPE (B2C/B2B/Custom/Injectie)
  // i.p.v. één kleur per rij, zodat dezelfde soort klant overal dezelfde kleur
  // heeft; de legende onderaan verklaart de codering.
  // `accent` = de statuskleur van dit blok. Eén hex-waarde voedt het icoon, de
  // linkerrand én de achtergrond van de totaalrij, en is dezelfde kleur als de
  // reeks in de grafiek "Vergelijking per categorie" — zo is in één oogopslag te
  // zien welk blok bij welke balk hoort.
  // `accent` = de statuskleur van dit blok. Eén hex-waarde voedt het icoon, de
  // linkerrand én de achtergrond van de totaalrij, en is dezelfde kleur als de
  // reeks in de grafiek "Vergelijking per categorie".
  //
  // Opbouw: elke energiesoort staat in een eigen omkaderd blok (Elektriciteit,
  // Gas) met een eigen subtotaal; het eindtotaal staat in een derde kader. Het
  // zijn drie APARTE tabellen met dezelfde kolombreedtes (table-fixed + colgroup),
  // zodat de kolommen over de blokken heen uitgelijnd blijven zónder de
  // kolomkoppen te moeten herhalen.
  // `subscriptionTip` = optionele uitleg achter een (i) bij de kolom Subscription
  // Fee. Enkel het blok OPENSTAANDE FACTUREN gebruikt hem: daar staat er altijd 0,
  // en zonder uitleg leest dat als een fout in plaats van een keuze.
  const SummaryTable = ({ title, Icon, accent, stats, totals, subscriptionTip, titleTip, titleTipTitel }) => {
    const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
    const isGas = (r) => /-\s*GAS/i.test(r.label);
    const isInjection = (r) => /injection/i.test(r.label);
    const elRows = stats.filter(r => !isGas(r));
    const gasRows = stats.filter(isGas);
    const subtotal = (rows) => ({
      kwhOfftake: sum(rows, 'kwhOfftake'), factorFee: sum(rows, 'factorFee'),
      volumeFee: sum(rows, 'volumeFee'), subscriptionFee: sum(rows, 'subscriptionFee'),
      totalFee: sum(rows, 'totalFee'),
    });
    // Het gebruikte reseller-percentage staat in de kolomkop zelf. De blokken
    // worden los gelezen, gescreenshot en doorgestuurd, en dan is "Totale Fee R."
    // zonder getal niet na te rekenen — zeker nu niet elk team hetzelfde
    // percentage heeft (zie src/resellerFee.js).
    // ENKEL op verkoopteam-/verkoperniveau: op energielabel-niveau is er geen
    // reseller-fee en heet de kolom gewoon "Totale Fee". `showResellerFee` dekt
    // precies dat onderscheid (admin/ET krijgt hem pas bij een team-/verkoperfilter).
    const kopTekst = showResellerFee ? `Totale Fee R. (${resellerFeePct}%)` : 'Totale Fee';

    // Vaste kolombreedtes → alle drie de tabellen lijnen uit.
    const colGroup = (
      <colgroup>
        <col style={{ width: '26%' }} />
        <col style={{ width: '15.2%' }} /><col style={{ width: '14.7%' }} />
        <col style={{ width: '14.7%' }} /><col style={{ width: '14.7%' }} />
        <col style={{ width: '14.7%' }} />
      </colgroup>
    );
    const kopCellen = ['Segment', 'Invoiced MWh', 'Factor Fee', 'Volume Fee', 'Subscription Fee', kopTekst];

    const segmentRij = (r, dikkeLijn) => (
      <tr key={r.label} className={`sum-row hover:bg-slate-700/20 ${dikkeLijn ? 'sum-split' : ''}`}>
        <td className="py-1.5 px-3 text-slate-200 text-xs font-medium">
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: summaryDotColor(r.label) }}></span>{r.label}
          </span>
        </td>
        <td className="py-1.5 px-3 text-slate-300 text-xs text-right tabular-nums">{formatMwhFromKwh(r.kwhOfftake)}</td>
        <td className="py-1.5 px-3 text-slate-300 text-xs text-right tabular-nums">{formatCurrencyRounded(r.factorFee)}</td>
        <td className="py-1.5 px-3 text-slate-300 text-xs text-right tabular-nums">{formatCurrencyRounded(r.volumeFee)}</td>
        <td className="py-1.5 px-3 text-cyan-400 text-xs text-right tabular-nums">{formatCurrencyRounded(r.subscriptionFee)}</td>
        <td className="py-1.5 px-3 text-lime-400 text-xs text-right tabular-nums font-medium">{formatCurrencyRounded(r.totalFee)}</td>
      </tr>
    );

    const totaalRij = (label, t, rowClass, labelKlasse, style) => (
      <tr className={rowClass} style={style}>
        <td className={`py-2 px-3 text-xs font-semibold ${labelKlasse}`}>{label}</td>
        <td className="py-2 px-3 text-slate-100 text-xs text-right font-semibold tabular-nums">{formatMwhFromKwh(t.kwhOfftake)}</td>
        <td className="py-2 px-3 text-slate-100 text-xs text-right font-semibold tabular-nums">{formatCurrencyRounded(t.factorFee)}</td>
        <td className="py-2 px-3 text-slate-100 text-xs text-right font-semibold tabular-nums">{formatCurrencyRounded(t.volumeFee)}</td>
        <td className="py-2 px-3 text-cyan-300 text-xs text-right font-semibold tabular-nums">{formatCurrencyRounded(t.subscriptionFee)}</td>
        <td className="py-2 px-3 text-lime-300 text-xs text-right font-semibold tabular-nums">{formatCurrencyRounded(t.totalFee)}</td>
      </tr>
    );

    // Eén omkaderde sectie. `toonKoppen=false` (gas) verbergt de kolomkoppen
    // visueel maar houdt ze in de DOM: de mobiele kaartweergave leest de labels
    // uit de thead. Vandaar `hidden` i.p.v. de thead weglaten.
    const sectie = (SectieIcon, naam, rows, dikkeLijnIndex, toonKoppen, boxKlasse, tintKlasse, tekstKlasse, subtotaalLabel) => (
      <div className={`rounded-xl border overflow-hidden ${boxKlasse}`}>
        <div className={`flex items-center gap-2 px-3 py-2 ${tintKlasse}`}>
          <span className={tekstKlasse}><SectieIcon size={15} /></span>
          <span className={`text-xs font-bold uppercase tracking-wide ${tekstKlasse}`}>{naam}</span>
        </div>
        <table className="w-full table-fixed text-sm resp-card">
          {colGroup}
          <thead className={toonKoppen ? '' : 'hidden'}>
            <tr className="sum-row">
              {kopCellen.map((k, i) => (
                <th key={k} className={`py-1.5 px-3 text-[11px] font-medium text-slate-400 ${i === 0 ? 'text-left' : 'text-right'}`}>
                  {subscriptionTip && k === 'Subscription Fee'
                    ? <span className="inline-flex items-center gap-1">{k}<InfoTip title="Subscription Fee" text={subscriptionTip} /></span>
                    : k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => segmentRij(r, i === dikkeLijnIndex))}
            {totaalRij(subtotaalLabel, subtotal(rows), `sum-subtotal ${tintKlasse}`, tekstKlasse)}
          </tbody>
        </table>
      </div>
    );

    // Index van de eerste injectie-rij → daar komt de zwaardere scheidingslijn.
    const splitIndex = elRows.findIndex((r, i) => isInjection(r) && i > 0 && !isInjection(elRows[i - 1]));

    return (
      <Card className="border-l-4" style={{ borderLeftColor: accent }}>
        <div className="flex items-center gap-2.5 mb-3">
          {Icon && <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: accent }}><Icon size={17} /></div>}
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-1.5">
              {title}{titleTip && <InfoTip title={titleTipTitel || title} text={titleTip} />}
            </h3>
            <p className="text-[11px] text-slate-500">Alle bedragen excl. BTW{showResellerFee ? ` · Totale Fee R. = ${resellerFeePct}% × Totale Fee` : ''}</p>
          </div>
        </div>
        <div className="overflow-x-auto space-y-2">
          {elRows.length > 0 && sectie(Zap, 'Elektriciteit', elRows, splitIndex, true, 'sum-box-el', 'sum-tint-el', 'sum-txt-el', 'Totaal elektriciteit')}
          {gasRows.length > 0 && sectie(Flame, 'Gas', gasRows, -1, false, 'sum-box-gas', 'sum-tint-gas', 'sum-txt-gas', 'Totaal gas')}
          {/* Eindtotaal in een eigen kader, in de accentkleur van dit blok. */}
          <div className="rounded-xl border sum-box-total overflow-hidden">
            <table className="w-full table-fixed text-sm resp-card">
              {colGroup}
              <thead className="hidden"><tr>{kopCellen.map(k => <th key={k}>{k}</th>)}</tr></thead>
              <tbody>{totaalRij('Totaal', totals, '', 'text-slate-100', { backgroundColor: `${accent}1F` })}</tbody>
            </table>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-400">
          {Object.entries(SUMMARY_DOT_COLORS).map(([naam, kleur]) => (
            <span key={naam} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: kleur }}></span>{naam}
            </span>
          ))}
        </div>
      </Card>
    );
  };

  const pieData = summaryStatsGoed.filter(r => r.totalFee > 0);
  const pieTotal = pieData.reduce((s, r) => s + Math.abs(r.totalFee), 0);
  let pieAngle = 0;
  const pieSlices = pieData.map(r => {
    const angle = (Math.abs(r.totalFee) / pieTotal) * 360;
    const startAngle = pieAngle;
    pieAngle += angle;
    return { ...r, startAngle, angle };
  });

  // ── PDF-rapport + Export All ────────────────────────────────────────────────
  // Het rapport toont ALLES uit de tabs "Samenvatting" en "Per Verkoper(team)",
  // in de huisstijl van het GESELECTEERDE energielabel. De hooks die het nodig
  // heeft staan hoger in de component, vóór de lege-staat-return — hooks moeten in
  // elke render in dezelfde volgorde lopen.
  const buildRevenueReportHtml = () => {
    const esc = (v) => String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    // Rapport-tekst: merknaam weg (compliance) en dan pas escapen.
    const rt = (v) => esc(hideEtBrand(String(v == null ? '' : v)));
    const eur = (v) => formatCurrencyRounded(v);
    const mwh = (v) => formatMwhFromKwh(v);
    const getal = (v) => formatNumberRounded(v);

    const primair = reportBranding?.primary_color || '#4f46e5';
    const accent = reportBranding?.accent_color || reportBranding?.secondary_color || primair;
    const labelNaam = reportBranding?.display_name || (energielabelFilter !== 'all' ? energielabelFilter : 'Alle energieleveranciers');
    const logo = reportBranding?.logo_data_url
      ? `<img class="logo" src="${esc(reportBranding.logo_data_url)}" alt="${rt(labelNaam)}" />`
      : `<div class="logo-fallback">${rt(labelNaam)}</div>`;

    const now = new Date();
    const datum = new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(now);
    // Verkoopteam en verkoper komen uit de EFFECTIEVE scope, niet rechtstreeks uit
    // de filterwaarde: een teamleader die op "Alle verkoopteams" staat, ziet nog
    // altijd enkel zijn eigen team, en dan hoort daar niet "Alle" te staan.
    const filters = [
      ['Energieleverancier', energielabelFilter !== 'all' ? energielabelFilter : 'Alle'],
      ['Verkoopteam', effectiefTeam || 'Alle'],
      ['Verkoper', effectieveVerkoper || 'Alle'],
    ];

    // Zelfde kop als op het scherm (inclusief het percentage) — het rapport hoort
    // een getrouwe afdruk van de Samenvatting te zijn.
    const feeKop = showResellerFee ? `Totale Fee R. (${resellerFeePct}%)` : 'Totale Fee';

    // ── Blok per betaalstatus: segmenten gesplitst in Elektriciteit / Gas met een
    //    subtotaal per sectie, precies zoals de tabellen op het scherm.
    const statusBlok = (titel, kleur, stats, totals) => {
      const isGas = (r) => /-\s*GAS/i.test(r.label);
      const som = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
      const el = stats.filter(r => !isGas(r));
      const gas = stats.filter(isGas);
      const rij = (r) => `<tr>
        <td>${rt(r.label)}</td>
        <td class="r">${mwh(r.kwhOfftake)}</td>
        <td class="r">${eur(r.factorFee)}</td>
        <td class="r">${eur(r.volumeFee)}</td>
        <td class="r">${eur(r.subscriptionFee)}</td>
        <td class="r strong">${eur(r.totalFee)}</td>
      </tr>`;
      const subtotaal = (naam, rows) => `<tr class="sub">
        <td>${rt(naam)}</td>
        <td class="r">${mwh(som(rows, 'kwhOfftake'))}</td>
        <td class="r">${eur(som(rows, 'factorFee'))}</td>
        <td class="r">${eur(som(rows, 'volumeFee'))}</td>
        <td class="r">${eur(som(rows, 'subscriptionFee'))}</td>
        <td class="r">${eur(som(rows, 'totalFee'))}</td>
      </tr>`;
      const secties = [
        el.length ? el.map(rij).join('') + (gas.length ? subtotaal('Subtotaal elektriciteit', el) : '') : '',
        gas.length ? gas.map(rij).join('') + (el.length ? subtotaal('Subtotaal gas', gas) : '') : '',
      ].join('');
      return `
      <section class="blok">
        <h3 style="border-left-color:${esc(kleur)}">${rt(titel)}</h3>
        <table>
          <thead><tr>
            <th>Segment</th><th class="r">Invoiced MWh</th><th class="r">Factor Fee</th>
            <th class="r">Volume Fee</th><th class="r">Subscription Fee</th><th class="r">${rt(feeKop)}</th>
          </tr></thead>
          <tbody>
            ${secties || '<tr><td colspan="6" class="leeg">Geen gegevens voor deze selectie.</td></tr>'}
            <tr class="totaal">
              <td>Totaal</td>
              <td class="r">${mwh(totals.kwhOfftake)}</td>
              <td class="r">${eur(totals.factorFee)}</td>
              <td class="r">${eur(totals.volumeFee)}</td>
              <td class="r">${eur(totals.subscriptionFee)}</td>
              <td class="r">${eur(totals.totalFee)}</td>
            </tr>
          </tbody>
        </table>
      </section>`;
    };

    // ── Vergelijking per categorie: dezelfde segmenten, per betaalstatus naast
    //    elkaar. Altijd in euro — een rapport heeft geen schakelaar, dus de
    //    volume-weergave van het scherm hoort hier niet thuis.
    const vergelijkingRijen = summaryStatsGoed.map((g, i) => {
      const w = summaryStatsWanbetaler[i] || {}; const o = summaryStatsOverig[i] || {};
      const d = hasBadDebtPeriod ? (summaryStatsDeelsOninbaar[i] || {}) : null;
      const totaal = (g.totalFee || 0) + (w.totalFee || 0) + (o.totalFee || 0);
      return `<tr>
        <td>${rt(g.label)}</td>
        <td class="r">${eur(g.totalFee)}</td>
        <td class="r">${eur(o.totalFee)}</td>
        <td class="r">${eur(w.totalFee)}</td>
        ${d ? `<td class="r">${eur(d.totalFee)}</td>` : ''}
        <td class="r strong">${eur(totaal)}</td>
      </tr>`;
    }).join('');

    const donutTotaal = statusDonutTotal || 0;
    const statusRijen = statusDonutData.map(d => `<tr>
      <td><span class="dot" style="background:${esc(d.color)}"></span>${rt(d.name)}</td>
      <td class="r">${eur(d.value)}</td>
      <td class="r">${donutTotaal > 0 ? (d.value / donutTotaal * 100).toFixed(1) : '0,0'}%</td>
    </tr>`).join('');

    const teamRijen = teamRevenueRows.map(r => `<tr>
      <td>${rt(r.team)}</td>
      <td class="r">${eur(r.goed)}</td>
      <td class="r">${eur(r.wan)}</td>
      <td class="r">${eur(r.open)}</td>
      <td class="r">${eur(r.betaald)}</td>
      <td class="r strong">${eur(r.teBetalen)}</td>
    </tr>`).join('');

    const verkoperRijen = verkoperSegmentRows.map(r => `<tr>
      <td>${rt(r.verkoper)}</td>
      <td class="r">${getal(r.kwhB2cEl)}</td>
      <td class="r">${getal(r.kwhB2bEl)}</td>
      <td class="r">${getal(r.kwhCustomElOfftake)}</td>
      <td class="r">${getal(r.kwhCustomElInjection)}</td>
      <td class="r">${getal(r.kwhB2cGas)}</td>
      <td class="r">${getal(r.kwhB2bGas)}</td>
      <td class="r">${getal(r.kwhCustomGas)}</td>
      <td class="r ean">${getal(r.eanB2c)}</td>
      <td class="r ean">${getal(r.eanB2b)}</td>
      <td class="r ean">${getal(r.eanCustom)}</td>
    </tr>`).join('');

    const kpiTegels = kpiCards.map(k => `
      <div class="kpi">
        <div class="kpi-label">${rt(k.label)}</div>
        <div class="kpi-value">${eur(k.value)}</div>
        ${k.sub ? `<div class="kpi-sub">${rt(k.sub.label)}: <strong>${rt(k.sub.value)}</strong></div>` : ''}
      </div>`).join('');

    // De <title> is meteen de voorgestelde bestandsnaam wanneer het rapport via
    // het printvenster wordt opgeslagen (de terugval als de PDF-render faalt),
    // dus dezelfde scope als de zip: label, dan verkoopteam/verkoper.
    return `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>${exportStamp()}${rt(`Partnervergoeding — ${labelNaam}${exportScope}`)}</title>
<style>
  :root { --primair: ${esc(primair)}; --accent: ${esc(accent)}; }
  * { box-sizing: border-box; }
  body {
    font-family: ${reportBranding?.font_family || "'Segoe UI', system-ui, -apple-system, Arial, sans-serif"};
    color: #1e293b; margin: 0; padding: 0; font-size: 11px; line-height: 1.45;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .vel { max-width: 190mm; margin: 0 auto; padding: 10mm 0 14mm; }

  /* Kop: logo van het energielabel links, rapportgegevens rechts. */
  header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px;
           padding-bottom: 12px; border-bottom: 3px solid var(--primair); }
  .logo { max-height: 54px; max-width: 240px; object-fit: contain; }
  .logo-fallback { font-size: 20px; font-weight: 700; color: var(--primair); }
  .kop-rechts { text-align: right; }
  .kop-rechts h1 { margin: 0; font-size: 19px; color: #0f172a; letter-spacing: -0.01em; }
  .kop-rechts .sub { color: #64748b; font-size: 11px; margin-top: 3px; }

  .meta { display: flex; flex-wrap: wrap; gap: 0; margin: 14px 0 20px;
          border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
  .meta div { flex: 1 1 25%; min-width: 110px; padding: 8px 12px; border-right: 1px solid #e2e8f0; }
  .meta div:last-child { border-right: 0; }
  .meta .k { color: #64748b; font-size: 9.5px; text-transform: uppercase; letter-spacing: .04em; }
  .meta .v { color: #0f172a; font-weight: 600; font-size: 11.5px; margin-top: 2px; }

  h2 { font-size: 13px; color: #0f172a; margin: 22px 0 8px; padding-bottom: 5px;
       border-bottom: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: .05em; }
  h3 { font-size: 11.5px; color: #0f172a; margin: 0 0 6px; padding-left: 8px;
       border-left: 4px solid var(--primair); text-transform: uppercase; letter-spacing: .04em; }

  .kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .kpi { border: 1px solid #e2e8f0; border-left: 3px solid var(--primair); border-radius: 6px; padding: 9px 11px; }
  .kpi-label { color: #64748b; font-size: 9.5px; }
  .kpi-value { color: #0f172a; font-size: 15px; font-weight: 700; margin-top: 1px; }
  .kpi-sub { color: #64748b; font-size: 8.5px; margin-top: 2px; }

  table { width: 100%; border-collapse: collapse; margin: 0; font-size: 10px; }
  th { background: #f1f5f9; text-align: left; padding: 6px 8px; font-weight: 600;
       color: #334155; border-bottom: 1.5px solid #cbd5e1; white-space: nowrap; }
  td { padding: 5px 8px; border-bottom: 1px solid #eef2f7; }
  th.r, td.r { text-align: right; white-space: nowrap; }
  td.strong { font-weight: 600; color: #0f172a; }
  td.ean { color: var(--accent); }
  tr.sub td { background: #f8fafc; font-weight: 600; color: #475569; }
  tr.totaal td { background: #eef2f7; font-weight: 700; color: #0f172a; border-top: 1.5px solid #cbd5e1; border-bottom: 0; }
  td.leeg { color: #94a3b8; font-style: italic; text-align: center; padding: 12px; }
  .dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 6px; }

  .blok { margin-bottom: 16px; }
  .twee { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start; }
  .smal { font-size: 9px; }
  .smal th, .smal td { padding: 4px 5px; }

  .disclaimer { margin-top: 22px; border: 1px solid #fcd34d; background: #fffbeb;
                border-radius: 8px; padding: 10px 12px; font-size: 10px; color: #78350f; }
  footer { margin-top: 14px; padding-top: 8px; border-top: 1px solid #e2e8f0;
           color: #94a3b8; font-size: 9px; display: flex; justify-content: space-between; }

  /* Print: tabellen niet midden in een rij afbreken en koppen herhalen. */
  @page { size: A4 portrait; margin: 12mm; }
  @media print {
    .vel { max-width: none; padding: 0; }
    thead { display: table-header-group; }
    tr, .kpi, .blok { break-inside: avoid; page-break-inside: avoid; }
    h2 { break-after: avoid; page-break-after: avoid; }
    .pagina-breek { break-before: page; page-break-before: always; }
  }
</style>
</head>
<body>
<div class="vel">

  <header>
    <div>${logo}</div>
    <div class="kop-rechts">
      <h1>Partnervergoeding</h1>
      <div class="sub">${rt(labelNaam)}</div>
      <div class="sub">Rapport van ${esc(datum)}</div>
    </div>
  </header>

  <div class="meta">
    ${filters.map(([k, v]) => `<div><div class="k">${rt(k)}</div><div class="v">${rt(v)}</div></div>`).join('')}
    <div><div class="k">Facturen</div><div class="v">${getal(filteredFacturen.length)}</div></div>
    ${reportBranding?.mail_from ? `<div><div class="k">Contact</div><div class="v">${rt(reportBranding.mail_from)}</div></div>` : ''}
  </div>

  <h2>Kerncijfers</h2>
  <div class="kpis">${kpiTegels}</div>
  ${showResellerFee ? `<p class="sub" style="color:#64748b;margin-top:8px">Reseller Fee ${rt(resellerFeePct)}% verrekend in de kolom &quot;Totale Fee R.&quot;.</p>` : ''}

  <h2>Statusverdeling vergoeding</h2>
  <table>
    <thead><tr><th>Status</th><th class="r">Vergoeding</th><th class="r">Aandeel</th></tr></thead>
    <tbody>
      ${statusRijen}
      <tr class="totaal"><td>Totaal</td><td class="r">${eur(donutTotaal)}</td><td class="r">100,0%</td></tr>
    </tbody>
  </table>

  <h2>Vergelijking per categorie</h2>
  <table>
    <thead><tr>
      <th>Segment</th><th class="r">Goede klanten</th><th class="r">Openstaand</th>
      <th class="r">Wanbetalers</th>${hasBadDebtPeriod ? '<th class="r">Oninbaar deel</th>' : ''}<th class="r">Totaal</th>
    </tr></thead>
    <tbody>${vergelijkingRijen || `<tr><td colspan="${hasBadDebtPeriod ? 6 : 5}" class="leeg">Geen gegevens voor deze selectie.</td></tr>`}</tbody>
  </table>

  <h2>Detail per betaalstatus</h2>
  ${statusBlok(`Goede klanten — ${facturenGoed.length} facturen`, STATUS_ACCENT.goed, summaryStatsGoed, summaryTotalsGoed)}
  ${statusBlok(`Wanbetalers — ${facturenWanbetaler.length} facturen`, STATUS_ACCENT.wanbetaler, summaryStatsWanbetaler, summaryTotalsWanbetaler)}
  ${hasBadDebtPeriod ? statusBlok('Wanbetaler — oninbaar deel', STATUS_ACCENT.deels, summaryStatsDeelsOninbaar, summaryTotalsDeelsOninbaar) : ''}
  ${statusBlok(`Openstaande facturen — ${facturenOverig.length} facturen`, STATUS_ACCENT.openstaand, summaryStatsOverig, summaryTotalsOverig)}

  <h2 class="pagina-breek">Vergoeding per verkoopteam</h2>
  <table>
    <thead><tr>
      <th>Verkoopteam</th><th class="r">Goede klanten</th><th class="r">Wanbetalers</th>
      <th class="r">Openstaand</th><th class="r">Betaalde vergoeding</th><th class="r">Te betalen</th>
    </tr></thead>
    <tbody>
      ${teamRijen || '<tr><td colspan="6" class="leeg">Geen gegevens voor deze selectie.</td></tr>'}
      <tr class="totaal">
        <td>Totaal</td>
        <td class="r">${eur(teamRevenueTotals.goed)}</td>
        <td class="r">${eur(teamRevenueTotals.wan)}</td>
        <td class="r">${eur(teamRevenueTotals.open)}</td>
        <td class="r">${eur(teamRevenueTotals.betaald)}</td>
        <td class="r">${eur(teamRevenueTotals.teBetalen)}</td>
      </tr>
    </tbody>
  </table>

  <h2>Vergoeding per verkoper</h2>
  <p style="color:#64748b;margin:0 0 6px">Invoiced kWh per segment — enkel goede klanten. De EAN-kolommen tonen het aantal unieke EAN's per segment uit de detail-orders.</p>
  <table class="smal">
    <thead><tr>
      <th>Verkoper</th>
      <th class="r">B2C El</th><th class="r">B2B El</th><th class="r">Custom El Offtake</th><th class="r">Custom El Injection</th>
      <th class="r">B2C Gas</th><th class="r">B2B Gas</th><th class="r">Custom Gas</th>
      <th class="r">EAN B2C</th><th class="r">EAN B2B</th><th class="r">EAN Custom</th>
    </tr></thead>
    <tbody>
      ${verkoperRijen || '<tr><td colspan="11" class="leeg">Geen gegevens voor deze selectie.</td></tr>'}
      <tr class="totaal">
        <td>Totaal</td>
        <td class="r">${getal(verkoperSegmentTotals.kwhB2cEl)}</td>
        <td class="r">${getal(verkoperSegmentTotals.kwhB2bEl)}</td>
        <td class="r">${getal(verkoperSegmentTotals.kwhCustomElOfftake)}</td>
        <td class="r">${getal(verkoperSegmentTotals.kwhCustomElInjection)}</td>
        <td class="r">${getal(verkoperSegmentTotals.kwhB2cGas)}</td>
        <td class="r">${getal(verkoperSegmentTotals.kwhB2bGas)}</td>
        <td class="r">${getal(verkoperSegmentTotals.kwhCustomGas)}</td>
        <td class="r">${getal(verkoperSegmentTotals.eanB2c)}</td>
        <td class="r">${getal(verkoperSegmentTotals.eanB2b)}</td>
        <td class="r">${getal(verkoperSegmentTotals.eanCustom)}</td>
      </tr>
    </tbody>
  </table>

  <div class="disclaimer">
    <strong>Voorlopige cijfers.</strong> De bedragen in dit rapport zijn gebaseerd op de data
    die beschikbaar was op het moment van export en kunnen nog herberekend worden.
    Alle bedragen zijn exclusief btw. Gebruik dit rapport ter indicatie.
  </div>

  <footer>
    <span>${rt(labelNaam)}</span>
    <span>Gegenereerd op ${esc(datum)}</span>
  </footer>

</div>
</body>
</html>`;
  };

  const exportPDFReport = () => {
    const venster = window.open('', '_blank');
    if (!venster) { toast.error('Sta pop-ups toe om het rapport te openen.'); return false; }
    venster.document.write(buildRevenueReportHtml());
    venster.document.close();
    venster.focus();
    setTimeout(() => venster.print(), 300);
    return true;
  };

  // De vier samenvattingsblokken (GOEDE KLANTEN / WANBETALERS / eventueel
  // WANBETALER – ONINBAAR DEEL / OPENSTAANDE FACTUREN) als één CSV: exact de
  // rijen die op de tab Samenvatting in die tabellen staan — segmenten gesplitst
  // in elektriciteit en gas met hun subtotaal, plus de totaalrij per blok. De
  // rest van de tab (KPI's, grafieken, per verkoper) hoort hier bewust NIET in.
  // Bedragen blijven ruwe getallen (komma-decimaal) zodat Excel ermee rekent;
  // het volume staat in MWh, net als de kolomkop op het scherm.
  const exportSummaryCsv = (collect) => {
    const blokken = [
      ['Goede klanten', summaryStatsGoed, summaryTotalsGoed],
      ['Wanbetalers', summaryStatsWanbetaler, summaryTotalsWanbetaler],
      ...(hasBadDebtPeriod ? [['Wanbetaler - oninbaar deel', summaryStatsDeelsOninbaar, summaryTotalsDeelsOninbaar]] : []),
      ['Openstaande facturen', summaryStatsOverig, summaryTotalsOverig],
    ];
    const feeKop = showResellerFee ? `Totale Fee R. (${resellerFeePct}%)` : 'Totale Fee';
    const num = v => (v == null || Number.isNaN(Number(v))) ? '' : String(Number(v)).replace('.', ',');
    const txt = v => v == null ? '' : String(v).replace(/[\r\n;]/g, ' ');
    const isGas = (r) => /-\s*GAS/i.test(r.label);
    const som = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
    const lines = [['Blok', 'Sectie', 'Segment', 'Invoiced MWh', 'Factor Fee', 'Volume Fee', 'Subscription Fee', feeKop].join(';')];
    const rij = (blok, sectie, segment, r) => lines.push([
      txt(blok), txt(sectie), txt(segment),
      num((r.kwhOfftake || 0) / 1000), num(r.factorFee), num(r.volumeFee), num(r.subscriptionFee), num(r.totalFee),
    ].join(';'));
    for (const [blok, stats, totals] of blokken) {
      const el = (stats || []).filter(r => !isGas(r));
      const gas = (stats || []).filter(isGas);
      for (const [sectie, rows, subtotaalLabel] of [['Elektriciteit', el, 'Totaal elektriciteit'], ['Gas', gas, 'Totaal gas']]) {
        if (!rows.length) continue;
        rows.forEach(r => rij(blok, sectie, r.label, r));
        rij(blok, sectie, subtotaalLabel, {
          kwhOfftake: som(rows, 'kwhOfftake'), factorFee: som(rows, 'factorFee'),
          volumeFee: som(rows, 'volumeFee'), subscriptionFee: som(rows, 'subscriptionFee'), totalFee: som(rows, 'totalFee'),
        });
      }
      rij(blok, 'Totaal', 'Totaal', totals);
    }
    const csv = '﻿' + lines.join('\r\n');
    return leverCsv(exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: "partnervergoeding-Summary", scope: exportScope }), csv, collect);
  };

  // Het rapport als échte PDF ophalen. De browser kan zijn eigen print-to-PDF niet
  // uitlezen (die bytes komen nooit bij de pagina terug), dus rendert de server
  // dezelfde HTML met headless Chromium — zie backend/src/services/htmlToPdf.js.
  const fetchReportPdf = async () => {
    const r = await fetch('/api/commission/report-pdf', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/html; charset=utf-8' },
      body: buildRevenueReportHtml(),
    });
    if (!r.ok) {
      const b = await r.json().catch(() => null);
      throw new Error((b && b.error) || `HTTP ${r.status}`);
    }
    return new Uint8Array(await r.arrayBuffer());
  };

  // Bouwt de zip van "Export All": het PDF-rapport plus elke datatabel. Gedeeld
  // door de download-knop en de partnervergoeding-mail (die dezelfde zip als
  // bijlage stuurt), zodat wat de partner krijgt precies is wat de admin zelf
  // zou downloaden. Geeft { blob, naam, aantal, pdfFout, mislukt } terug.
  const bouwRevenueZip = async () => {
    // Eén tijdstempel voor de hele export — zie exportStamp.
    exportStampRef.current = exportTimestamp();
    try {
      const bestanden = [];
      const verzamel = (naam, csv) => { bestanden.push({ name: naam, content: csv }); };
      const taken = [
        ['samenvatting', (c) => exportSummaryCsv(c)],
        ['orders', (c) => tableExports.current.orders?.(c)],
        ['detail per factuur', (c) => exportDetailCsv(c)],
        ['self bills elektriciteit', (c) => exportSelfBillsCsv(c)],
        ...(canSeeEtSelfBills ? [
          ['self bills leverancier', (c) => exportSelfBillsEtCsv(c)],
          ['facturen leverancier', (c) => exportInvoiceEtCsv(c)],
        ] : []),
      ];
      const mislukt = [];
      for (const [naam, fn] of taken) {
        try { fn(verzamel); } catch (e) { mislukt.push(naam); console.error(`Export "${naam}" mislukt:`, e); }
      }

      // Het rapport gaat als PDF mee in de zip. Lukt het renderen niet, dan blijft
      // de rest van de export gewoon doorgaan — beter een zip zonder PDF dan geen zip.
      let pdfFout = null;
      try {
        bestanden.unshift({
          name: exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: "partnervergoeding-rapport", scope: exportScope, ext: "pdf" }),
          content: await fetchReportPdf(),
        });
      } catch (e) {
        pdfFout = e.message;
        console.error('PDF-rapport renderen mislukt:', e);
      }
      return {
        blob: makeZip(bestanden),
        naam: exportBestandsnaam({ stamp: exportStamp(), label: exportLabel, basis: "revenue-export", scope: exportScope, ext: "zip" }),
        aantal: bestanden.length, pdfFout, mislukt,
      };
    } finally {
      exportStampRef.current = null; // vanaf hier stempelt elke losse export weer het moment zelf
    }
  };

  // "Export All": het PDF-rapport plus elke datatabel als ÉÉN zip.
  //
  // Waarom een zip en geen losse bestanden: een browser staat per klik maar één
  // automatische download toe. De vorige opzet vuurde er drie af met 400 ms ertussen
  // en dat hielp niet — gemeten in de app kwamen alle drie de exports zonder fout
  // door de code, maar leverde Chrome er twee af en blokkeerde de rest stil. Eén
  // download is er altijd één.
  //
  // De leveranciersfactuur-tabellen zitten er nu WEL bij (op vraag), maar enkel voor
  // wie ze mag zien — `canSeeEtSelfBills` gatet ze, net als op het scherm.
  const exportAllRevenue = async () => {
    setExportBusy(true);
    try {
      const z = await bouwRevenueZip();
      if (z.aantal) downloadBlob(z.naam, z.blob);
      if (z.pdfFout) {
        toast.error(`Zip met ${z.aantal} tabellen gedownload; het PDF-rapport is niet gelukt (${z.pdfFout}) — het printvenster opent nu.`);
        // Printvenster pas ná de download: het neemt de focus over en zou de
        // download midden in kunnen onderbreken.
        setTimeout(() => exportPDFReport(), 300);
      } else if (z.mislukt.length) {
        toast.error(`Rapport geëxporteerd; ${z.mislukt.length} tabel(len) niet: ${z.mislukt.join(', ')}.`);
      } else {
        toast.success(`Rapport en ${z.aantal - 1} tabellen geëxporteerd.`);
      }
    } finally {
      setExportBusy(false);
    }
  };

  // Partnervergoeding mailen (alleen admin): dezelfde zip, maar als bijlage naar
  // de partner. Vereist één gekozen energielabel — de zip van "alle labels" bevat
  // de vergoeding van élke partner en gaat dus nooit naar één van hen.
  const feeMailMogelijk = isAdmin && energielabelFilter !== 'all';
  const openFeeMail = async () => {
    if (!feeMailMogelijk) return;
    setFeeMailBusy(true);
    try {
      const z = await bouwRevenueZip();
      if (z.pdfFout) toast.error(`Het PDF-rapport kon niet gerenderd worden (${z.pdfFout}); de zip bevat enkel de datatabellen.`);
      if (z.mislukt.length) toast.error(`${z.mislukt.length} tabel(len) ontbreken in de zip: ${z.mislukt.join(', ')}.`);
      setFeeMail({
        zip: { blob: z.blob, naam: z.naam },
        selectie: { energyLabel: energielabelFilter, salesTeam: effectiefTeam || null, salesperson: effectieveVerkoper || null },
      });
    } finally { setFeeMailBusy(false); }
  };

  // ── Haak voor de geplande partnervergoeding-mail (backend/src/services/feeMailJob.js) ──
  // De job opent deze pagina in headless Chromium met ?feeMailJob=1 en vraagt per
  // doel een zip: window.__feeMailJob.bouwVoor(label, team). Dat zet de filters,
  // wacht tot de partnervergoeding van DAT label geladen is en bouwt dan dezelfde
  // zip als "Export All". De zip gaat terug mét het label en team waarop ze
  // gebouwd is — de job weigert te versturen als dat niet klopt met het doel.
  // De ref houdt altijd de functies van de LAATSTE render, zodat de zip nooit uit
  // een verouderde sluiting komt.
  feeMailJobRef.current = {
    kies: (label, team) => { setEnergielabelFilter(label); setVerkoopteamFilter(team || 'all'); setVerkoperFilter('all'); setRevTab('samenvatting'); },
    staat: () => ({
      label: energielabelFilter,
      team: effectiefTeam || '',
      gereed: !apiBusy && !ordersLoading && !apiError
        && commissionMeta.source === 'api' && (commissionMeta.api?.labels || []).includes(energielabelFilter),
      fout: apiError || null,
    }),
    bouw: bouwRevenueZip,
  };

  // ── Dashboard-afgeleiden — UITSLUITEND bestaande waarden, geen nieuwe berekeningen ──
  const teOntvangen = summaryTotalsGoed.totalFee - betaaldeVergoeding;
  const KPI_TONE = {
    blue: { tile: 'bg-blue-900/20 border-blue-700/50', icon: 'bg-blue-500', txt: 'text-blue-300', accent: '#2563eb' },
    violet: { tile: 'bg-violet-900/20 border-violet-700/50', icon: 'bg-violet-500', txt: 'text-violet-300', accent: '#7c3aed' },
    green: { tile: 'bg-emerald-900/20 border-emerald-700/50', icon: 'bg-emerald-500', txt: 'text-emerald-300', accent: '#16a34a' },
    red: { tile: 'bg-red-900/20 border-red-700/50', icon: 'bg-red-500', txt: 'text-red-300', accent: '#dc2626' },
    lime: { tile: 'bg-lime-900/20 border-lime-700/50', icon: 'bg-lime-500', txt: 'text-lime-300', accent: '#65a30d' },
    amber: { tile: 'bg-amber-900/20 border-amber-700/50', icon: 'bg-amber-500', txt: 'text-amber-300', accent: '#d97706' },
  };
  const kpiCards = [
    { key: 'betaald', label: 'Betaalde vergoeding', value: betaaldeVergoeding, Icon: Wallet, tone: 'blue' },
    { key: 'teont', label: 'Te ontvangen', value: teOntvangen, Icon: Coins, tone: 'violet' },
    { key: 'goed', label: 'Goede klanten', value: summaryTotalsGoed.totalFee, Icon: UserCheck, tone: 'green' },
    // Onder het fee-bedrag van beide tegels: wat er bij die klanten nog openstaat
    // (incl. btw). Andere grootheid dan de fee erboven, dus expliciet gelabeld.
    {
      key: 'open', label: 'Openstaande facturen', value: summaryTotalsOverig.totalFee, Icon: FileText, tone: 'lime',
      sub: {
        label: 'Openstaand incl. btw',
        value: formatCurrencyRounded(openstaandInclBtw.goed),
        tip: 'Dit is het totaal bedrag dat goede klanten nog moeten betalen, niet een vergoeding.',
      },
    },
    {
      key: 'wan', label: 'Wanbetalers', value: summaryTotalsWanbetaler.totalFee, Icon: AlertCircle, tone: 'red',
      sub: {
        label: 'Openstaand incl. btw',
        value: formatCurrencyRounded(openstaandInclBtw.wanbetalers),
        tip: 'Dit is het totaal bedrag dat wanbetalers nog moeten betalen, niet een vergoeding.',
      },
    },
    ...(hasBadDebtPeriod ? [{ key: 'deels', label: 'Wanbetaler – oninbaar deel', value: summaryTotalsDeelsOninbaar.totalFee, Icon: AlertCircle, tone: 'amber' }] : []),
  ];
  const statusDonutData = [
    { name: 'Goede klanten', value: summaryTotalsGoed.totalFee, color: '#22c55e' },
    { name: 'Wanbetalers', value: summaryTotalsWanbetaler.totalFee, color: '#ef4444' },
    ...(hasBadDebtPeriod ? [{ name: 'Oninbaar deel', value: summaryTotalsDeelsOninbaar.totalFee, color: '#f59e0b' }] : []),
    { name: 'Openstaande facturen', value: summaryTotalsOverig.totalFee, color: '#84cc16' },
    // "Te ontvangen" hoort hier NIET bij: dat is geen status maar een afgeleide
    // (Goede klanten − Betaalde vergoeding). Als segment in deze verdeling telde
    // het bedrag dubbel mee. Het staat nog wel als eigen KPI-tegel bovenaan.
  ].filter(d => d.value > 0);
  const statusDonutTotal = statusDonutData.reduce((s, d) => s + d.value, 0);
  const totaleVergoeding = summaryTotalsGoed.totalFee + summaryTotalsWanbetaler.totalFee + summaryTotalsOverig.totalFee + (hasBadDebtPeriod ? summaryTotalsDeelsOninbaar.totalFee : 0);
  const compMetric = (r) => !r ? 0 : (comparisonMode === 'volume' ? (r.kwhOfftake || 0) : (r.totalFee || 0));
  // De 3 EL-injectie-segmenten (Custom/B2C/B2B) worden gesommeerd tot één "EL Injection".
  const COMP_MERGE = (label) => /EL Injection/i.test(label) ? 'EL Injection' : label;
  const COMP_ORDER = ['B2C - EL', 'B2B - EL', 'Custom - EL Offtake', 'EL Injection', 'B2C - GAS', 'B2B - GAS', 'Custom - GAS'];
  const comparisonData = (() => {
    const acc = {};
    summaryStatsGoed.forEach((g, i) => {
      const seg = COMP_MERGE(g.label);
      if (!acc[seg]) acc[seg] = { segment: seg, goed: 0, wanbetaler: 0, openstaand: 0, ...(hasBadDebtPeriod ? { deels: 0 } : {}) };
      acc[seg].goed += compMetric(g);
      acc[seg].wanbetaler += compMetric(summaryStatsWanbetaler[i]);
      acc[seg].openstaand += compMetric(summaryStatsOverig[i]);
      if (hasBadDebtPeriod) acc[seg].deels += compMetric(summaryStatsDeelsOninbaar[i]);
    });
    return COMP_ORDER.filter(s => acc[s]).map(s => acc[s]);
  })();

  // Reeksen in de gevraagde stapelvolgorde: goed → openstaand → wanbetaler →
  // oninbaar deel. Kleuren uit STATUS_ACCENT, dus identiek aan het icoon en de
  // totaalrij van het bijbehorende samenvattingsblok.
  const compReeksen = [
    { key: 'goed', name: 'Goede klanten', color: STATUS_ACCENT.goed },
    { key: 'openstaand', name: 'Openstaande facturen', color: STATUS_ACCENT.openstaand },
    { key: 'wanbetaler', name: 'Wanbetalers', color: STATUS_ACCENT.wanbetaler },
    ...(hasBadDebtPeriod ? [{ key: 'deels', name: 'Oninbaar deel', color: STATUS_ACCENT.deels }] : []),
  ];
  // Gedeeld X-maximum over beide secties, zodat een balk in de gassectie even
  // lang is als eenzelfde bedrag in de elektriciteitssectie.
  const compMax = Math.max(1, ...comparisonData.map(r => compReeksen.reduce((s, x) => s + (r[x.key] || 0), 0)));
  const compSecties = [
    { naam: 'Elektriciteit', Icon: Zap, boxKlasse: 'sum-box-el', tintKlasse: 'sum-tint-el', tekstKlasse: 'sum-txt-el', rows: comparisonData.filter(r => !/GAS/i.test(r.segment)) },
    { naam: 'Gas', Icon: Flame, boxKlasse: 'sum-box-gas', tintKlasse: 'sum-tint-gas', tekstKlasse: 'sum-txt-gas', rows: comparisonData.filter(r => /GAS/i.test(r.segment)) },
  ].filter(s => s.rows.length);
  const compFmt = (v) => comparisonMode === 'volume' ? formatNumberRounded(v) : formatCurrencyRounded(v);

  return <div className="space-y-6">
    <PageHeader title="Partnervergoeding" subtitle="Alle bedragen excl. BTW" docs={<>{revenueDocsIcons()}<CommissionButtons /></>}>
      <ExportButton onExport={exportPDFReport} soort="pdf" label="Download het partnervergoeding-rapport als PDF" />
      {/* Alles in één keer: het rapport + de datatabellen van Orders en Facturen. */}
      <ExportKnop onExport={exportAllRevenue} busy={exportBusy} soort="zip" label="Download het PDF-rapport én alle datatabellen samen in één zip" />
      {/* Alleen admin: dezelfde zip als bijlage naar de partner (sjabloon Revenue). */}
      {isAdmin && <ExportKnop onExport={openFeeMail} busy={feeMailBusy} disabled={!feeMailMogelijk} soort="mail" label={feeMailMogelijk ? `Mail de partnervergoeding (zip) naar de partner van ${energielabelFilter}` : "Kies eerst één energielabel om de partnervergoeding te mailen"} />}
    </PageHeader>
    <OrdersLaadStatus />
    <CommissionStatus />
    {commissionMeta.unmappedCount > 0 && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-2 rounded text-sm">{commissionMeta.unmappedCount} record(s) zonder herkend energielabel worden niet meegerekend.</div>}
    {revenueDocsModals()}
    {isAdmin && <FeeMailModal open={!!feeMail} onClose={() => setFeeMail(null)} token={token} zip={feeMail?.zip} selectie={feeMail?.selectie} />}

    {filterBar}

    {/* Horizontaal tab-menu (zoals Market Prices): minder info onder elkaar; compacte
        pill-rij, scrollbaar op mobiel. De Admin-tab verschijnt enkel voor admin. */}
    <div className="-mx-1 overflow-x-auto no-scrollbar">
      <div className="flex gap-2 px-1 w-max">
        {[{ id: 'samenvatting', label: 'Samenvatting' }, { id: 'perverkoper', label: 'Per Verkoper(team)' }, { id: 'orders', label: 'Orders' }, { id: 'facturen', label: 'Facturen' }, ...(magBetalingen ? [{ id: 'betalingen', label: 'Betalingen' }] : []), ...customPaginas.map(p => ({ id: p.tab, label: p.titel })), ...(isAdmin ? [{ id: 'admin', label: 'Admin' }] : [])].map(rt => (
          <button key={rt.id} onClick={() => setRevTab(rt.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${revTab === rt.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-600'}`}>
            {rt.label}
          </button>
        ))}
      </div>
    </div>

    {/* ── Tab: Samenvatting ── */}
    <div className={revTab === 'samenvatting' ? 'space-y-4' : 'hidden'}>
    {/* ── Dashboard (layout volgens referentie): KPI-rij · grafiek+status/totaal · detail 2×2 ── */}
    <div className="space-y-4">

      {/* Rij 1 — KPI-kaarten horizontaal (full-width, uitgelijnd). Onder 640px één
          kolom: naast het 44px-icoon bleef in twee kolommen te weinig over voor het
          bedrag, dat dan werd afgekapt. Vanaf sm ongewijzigd. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {kpiCards.map(k => { const t = KPI_TONE[k.tone]; const Icon = k.Icon; return (
          <div key={k.key} style={{ '--kpi-accent': t.accent }} className={`revenue-kpi rounded-xl border border-l-4 p-4 flex items-center gap-3.5 ${t.tile}`}>
            <div className={`revenue-kpi-icon w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0 ${t.icon}`}><Icon size={22} /></div>
            <div className="min-w-0">
              <div className={`revenue-kpi-title text-xs font-medium ${t.txt}`}>{k.label}</div>
              <div className={`revenue-kpi-value text-xl font-bold ${t.txt}`} title={formatCurrencyRounded(k.value)}><Kengetal>{formatCurrencyRounded(k.value)}</Kengetal></div>
              {/* Subregel: label en bedrag onder elkaar, niet naast elkaar. Gemeten op
                  1440px is de tekstkolom van een tegel maar ~79px breed — één regel
                  "label: bedrag" (197px nodig) werd daar afgekapt tot onleesbaar. */}
              {k.sub && (
                <div className="revenue-kpi-sub mt-1.5 pt-1.5 border-t border-slate-600/40 min-w-0">
                  <div className="text-[10px] leading-tight text-slate-400">{k.sub.label}</div>
                  <div className="text-sm font-semibold text-slate-200 leading-tight flex items-center gap-1 min-w-0">
                    <Kengetal className="flex-1" title={k.sub.value}>{k.sub.value}</Kengetal>
                    {k.sub.tip && <InfoTip title={k.sub.label} text={k.sub.tip} />}
                  </div>
                </div>
              )}
            </div>
          </div>
        ); })}
      </div>

      {(showResellerFee || paidCommissionError) && <div className="space-y-1">
        {showResellerFee && <div className="text-[11px] text-slate-500">Reseller Fee {resellerFeePct}% verrekend (Totale Fee R.).</div>}
        {paidCommissionError && <div className="text-xs text-red-300">{paidCommissionError}</div>}
      </div>}

      {/* Rij 2 — vergelijking (breed) + statusverdeling & totaal (smal) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        <Card className="lg:col-span-2 lg:order-2 !p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-1.5">Vergelijking per categorie <InfoTip title={REVENUE_TABLE_DOCS.totaal.title} text={REVENUE_TABLE_DOCS.totaal.text} /></h3>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Statuslegende: dezelfde kleuren als de samenvattingsblokken eronder. */}
              <div className="flex items-center gap-3 text-[11px] flex-wrap">
                {compReeksen.map(s => (
                  <span key={s.key} className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }}></span>
                    <span className="text-slate-400">{s.name}</span>
                  </span>
                ))}
              </div>
              <div className="inline-flex rounded-lg bg-slate-800/60 border border-slate-700 p-0.5 text-xs">
                {['vergoeding', 'volume'].map(m => (
                  <button key={m} onClick={() => setComparisonMode(m)} className={`px-3 py-1 rounded-md font-medium transition ${comparisonMode === m ? 'bg-lime-500 text-slate-900' : 'text-slate-300 hover:text-slate-100'}`}>{m === 'vergoeding' ? 'Vergoeding' : 'Volume'}</button>
                ))}
              </div>
            </div>
          </div>
          {/* Twee omkaderde secties (Elektriciteit / Gas) met dezelfde koppen, kaders
              en bolletjes-codering als de samenvattingsblokken eronder. Beide charts
              delen hetzelfde X-domein zodat de balken onderling vergelijkbaar zijn;
              de as staat één keer, onder de laatste sectie. */}
          <div className="overflow-x-auto space-y-2">
            <div style={{ minWidth: 420 }}>
              {compSecties.map((sec, secIdx) => {
                const laatste = secIdx === compSecties.length - 1;
                return (
                  <div key={sec.naam} className={`rounded-xl border overflow-hidden ${sec.boxKlasse} ${secIdx > 0 ? 'mt-2' : ''}`}>
                    <div className={`flex items-center gap-2 px-3 py-2 ${sec.tintKlasse}`}>
                      <span className={sec.tekstKlasse}><sec.Icon size={15} /></span>
                      <span className={`text-xs font-bold uppercase tracking-wide ${sec.tekstKlasse}`}>{sec.naam}</span>
                    </div>
                    <ResponsiveContainer width="100%" height={sec.rows.length * 34 + (laatste ? 34 : 10)}>
                      <BarChart layout="vertical" data={sec.rows} margin={{ top: 6, right: 16, left: 4, bottom: laatste ? 4 : 0 }} barSize={14}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                        <XAxis type="number" domain={[0, compMax]} allowDataOverflow hide={!laatste}
                          tick={{ fill: '#94A3B8', fontSize: 11 }} tickFormatter={compFmt} />
                        <YAxis type="category" dataKey="segment" width={130} interval={0}
                          tick={<CompCatTick />} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => [compFmt(v), n]} cursor={{ fill: 'rgba(148,163,184,.08)' }} />
                        {compReeksen.map((s, i) => (
                          <Bar key={s.key} dataKey={s.key} name={s.name} stackId="a" fill={s.color}
                            radius={i === compReeksen.length - 1 ? [0, 4, 4, 0] : 0} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <Card>
            <h3 className="text-base font-semibold text-slate-100 mb-2">Goede klanten per segment</h3>
            <div className="flex flex-col items-start gap-5 sm:flex-row sm:gap-6">
              <svg viewBox="0 0 200 200" className="w-32 h-32 flex-shrink-0">
                {pieSlices.map((slice, i) => (
                  <path key={i} d={describeArc(100, 100, 80, slice.startAngle, slice.startAngle + slice.angle)} fill={slice.color} className="hover:opacity-80 transition-opacity" />
                ))}
                <circle cx="100" cy="100" r="40" fill="#1e293b" />
                <text x="100" y="95" textAnchor="middle" className="fill-slate-400 text-xs">Totaal</text>
                <text x="100" y="112" textAnchor="middle" className="fill-lime-400 text-sm font-bold">{formatCurrencyRounded(summaryTotalsGoed.totalFee)}</text>
              </svg>
              <div className="flex flex-col gap-2 w-full min-w-0">
                {summaryStatsGoed.map(r => <div key={r.label} className="flex items-center gap-2 text-xs">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }}></span>
                  <span className="text-slate-400 whitespace-nowrap">{r.label}</span>
                  <span className="text-slate-200 ml-auto font-medium">{formatCurrencyRounded(r.totalFee)}</span>
                </div>)}
              </div>
            </div>
          </Card>
          <Card className="!p-4 !overflow-visible flex-1">
            <h3 className="text-base font-semibold text-slate-100">Statusverdeling vergoeding</h3>
            <p className="text-[11px] text-slate-500 mb-2">Op basis van totaal vergoeding</p>
            <div className="flex items-start gap-3">
              <div className="shrink-0"><ResponsiveContainer width={128} height={128}>
                <PieChart>
                  <Pie data={statusDonutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={62} paddingAngle={1} stroke="none">
                    {statusDonutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => [formatCurrencyRounded(v), n]} />
                </PieChart>
              </ResponsiveContainer></div>
              <div className="flex-1 min-w-0 space-y-1.5">
                {statusDonutData.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }}></span>
                    <span className="text-slate-400 truncate">{d.name}</span>
                    <span className="text-slate-200 ml-auto font-medium">{statusDonutTotal > 0 ? (d.value / statusDonutTotal * 100).toFixed(1) : '0.0'}%</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Rij 3 — detailkaarten per categorie (2×2, uitgelijnd) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* accent = exact de reekskleur uit "Vergelijking per categorie"
            (STATUS_ACCENT), zodat icoon, linkerrand, totaalrij en grafiekbalk
            per status dezelfde kleur dragen. */}
        <SummaryTable
          title="GOEDE KLANTEN"
          Icon={UserCheck} accent={STATUS_ACCENT.goed}
          stats={summaryStatsGoed}
          totals={summaryTotalsGoed}
        />
        <SummaryTable
          title="WANBETALERS"
          Icon={AlertCircle} accent={STATUS_ACCENT.wanbetaler}
          stats={summaryStatsWanbetaler}
          totals={summaryTotalsWanbetaler}
          titleTip={wanbetalerTip.tekst}
          titleTipTitel={wanbetalerTip.titel}
        />
        {hasBadDebtPeriod && <SummaryTable
          title="WANBETALER – ONINBAAR DEEL"
          Icon={AlertCircle} accent={STATUS_ACCENT.deels}
          stats={summaryStatsDeelsOninbaar}
          totals={summaryTotalsDeelsOninbaar}
        />}
        <SummaryTable
          title="OPENSTAANDE FACTUREN"
          Icon={FileText} accent={STATUS_ACCENT.openstaand}
          stats={summaryStatsOverig}
          totals={summaryTotalsOverig}
          subscriptionTip="De subscription Fee voor klanten met onbetaalde facturen is opgenomen onder goede klanten"
        />
      </div>
    </div>
    </div>

    {/* ── Tab: Per Verkoper(team) ── */}
    <div className={revTab === 'perverkoper' ? 'space-y-4' : 'hidden'}>
    <Card className="!p-4">
      <h3 className="text-lg font-semibold text-slate-100 mb-1 flex items-center gap-1.5">VERGOEDING PER TEAM</h3>
      <p className="text-xs text-slate-400 mb-4">Per verkoopteam de Totale Fee per betaalstatus, de op teamniveau ingevoerde betaalde vergoeding (verkoperbedragen tellen niet mee) en wat er nog te betalen is (Goede klanten − betaalde vergoeding). Volgt de actieve filters; bij een verkoper-filter staan de bedragen van die verkoper.</p>
      {teamRevenueRows.length === 0 ? <TableEmpty message="Geen gegevens voor de huidige selectie." /> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <th className="py-2 px-3 text-left text-slate-400 text-xs">Verkoopteam</th>
            <th className="py-2 px-3 text-right text-slate-400 text-xs">Goede klanten</th>
            <th className="py-2 px-3 text-right text-slate-400 text-xs">Wanbetalers</th>
            <th className="py-2 px-3 text-right text-slate-400 text-xs">Openstaande facturen</th>
            <th className="py-2 px-3 text-right text-slate-400 text-xs">Betaalde vergoeding</th>
            <th className="py-2 px-3 text-right text-slate-400 text-xs">Te betalen vergoeding</th>
          </tr></thead>
          <tbody>
            {teamRevenueRows.map(r => <tr key={`tr-${r.team}`} className="border-b border-slate-700/30 hover:bg-slate-700/20">
              <td className="py-2 px-3 text-slate-200 text-xs">{r.team}</td>
              <td className="py-2 px-3 text-lime-300 text-xs text-right">{formatCurrencyRounded(r.goed)}</td>
              <td className="py-2 px-3 text-red-300 text-xs text-right">{formatCurrencyRounded(r.wan)}</td>
              <td className="py-2 px-3 text-amber-300 text-xs text-right">{formatCurrencyRounded(r.open)}</td>
              <td className="py-2 px-3 text-sky-300 text-xs text-right">{formatCurrencyRounded(r.betaald)}</td>
              <td className={`py-2 px-3 text-xs text-right font-medium ${r.teBetalen < 0 ? 'text-amber-300' : 'text-violet-200'}`}>{formatCurrencyRounded(r.teBetalen)}</td>
            </tr>)}
            <tr className="border-t-2 border-slate-600 bg-slate-800/50 font-semibold">
              <td className="py-2 px-3 text-slate-200 text-xs">Totaal</td>
              <td className="py-2 px-3 text-lime-300 text-xs text-right">{formatCurrencyRounded(teamRevenueTotals.goed)}</td>
              <td className="py-2 px-3 text-red-300 text-xs text-right">{formatCurrencyRounded(teamRevenueTotals.wan)}</td>
              <td className="py-2 px-3 text-amber-300 text-xs text-right">{formatCurrencyRounded(teamRevenueTotals.open)}</td>
              <td className="py-2 px-3 text-sky-300 text-xs text-right">{formatCurrencyRounded(teamRevenueTotals.betaald)}</td>
              <td className={`py-2 px-3 text-xs text-right ${teamRevenueTotals.teBetalen < 0 ? 'text-amber-300' : 'text-violet-200'}`}>{formatCurrencyRounded(teamRevenueTotals.teBetalen)}</td>
            </tr>
          </tbody>
        </table>
      </div>}
      {paidCommissionError && <div className="text-xs text-red-300 mt-3">{paidCommissionError}</div>}
    </Card>

    <Card className="!p-4 !overflow-visible">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center text-white shrink-0 mt-0.5"><BarChart3 size={19} /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">VERGOEDING PER VERKOPER</h3><ExportKnop onExport={exportVerkoperSegmentCsv} disabled={verkoperSegmentRows.length === 0} count={verkoperSegmentRows.length} label="Exporteer alle verkopers (Invoiced kWh + unieke EAN's per segment) naar Excel" /></div>
            <p className="text-xs text-slate-400">Invoiced kWh per segment — <strong>enkel Goede klanten</strong>. De EAN-kolommen tonen het aantal unieke EAN's per customer-segment uit "PARTNERVERGOEDING — DETAIL ORDERS".</p>
          </div>
        </div>
      </div>
      {verkoperSegmentRows.length === 0 ? <TableEmpty message="Geen gegevens voor de huidige selectie." /> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th rowSpan={2} className="py-2.5 px-3 text-left text-slate-400 text-xs align-middle whitespace-nowrap border-b border-r border-slate-700/60">
                <span className="flex items-center gap-1.5"><User size={14} className="text-slate-400" />Verkoper</span>
              </th>
              <th colSpan={7} className="py-2 px-3 text-center text-slate-300 text-xs font-semibold uppercase tracking-wide border-b border-slate-700/60">kWh per segment</th>
              <th colSpan={3} className="py-2 px-3 text-center text-emerald-400 text-xs font-semibold uppercase tracking-wide border-b border-l border-slate-700/60">Unieke EAN's</th>
            </tr>
            <tr className="border-b border-slate-700">
              <SortHeader label="kWh_B2C_El" k="kwhB2cEl" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="kWh_B2B_El" k="kwhB2bEl" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="kWh_Custom_El Offtake" k="kwhCustomElOfftake" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="kWh_Custom_El Injection" k="kwhCustomElInjection" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="kWh_B2C_Gas" k="kwhB2cGas" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="kWh_B2B_Gas" k="kwhB2bGas" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="kWh_Custom_Gas" k="kwhCustomGas" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="EAN B2C" k="eanB2c" align="right" className="border-l border-slate-700/60" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="EAN B2B" k="eanB2b" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
              <SortHeader label="EAN Custom" k="eanCustom" align="right" sortConfig={vkSegSort.sortConfig} onSort={vkSegSort.handleSort} />
            </tr>
          </thead>
          <tbody>
            {paginatedVerkoperSegmentRows.map(r => <tr key={`vseg-${r.verkoper}`} className="border-b border-slate-700/30 hover:bg-slate-700/20">
              <td className="py-2 px-3 text-slate-200 text-xs font-medium border-r border-slate-700/50">{r.verkoper}</td>
              <td className="py-2 px-2 text-slate-300 text-xs text-right">{formatNumberRounded(r.kwhB2cEl)}</td>
              <td className="py-2 px-2 text-slate-300 text-xs text-right">{formatNumberRounded(r.kwhB2bEl)}</td>
              <td className="py-2 px-2 text-slate-300 text-xs text-right">{formatNumberRounded(r.kwhCustomElOfftake)}</td>
              <td className="py-2 px-2 text-slate-300 text-xs text-right">{formatNumberRounded(r.kwhCustomElInjection)}</td>
              <td className="py-2 px-2 text-slate-300 text-xs text-right">{formatNumberRounded(r.kwhB2cGas)}</td>
              <td className="py-2 px-2 text-slate-300 text-xs text-right">{formatNumberRounded(r.kwhB2bGas)}</td>
              <td className="py-2 px-2 text-slate-300 text-xs text-right">{formatNumberRounded(r.kwhCustomGas)}</td>
              <td className="py-2 px-2 text-sky-300 text-xs text-right border-l border-slate-700/50">{r.eanB2c}</td>
              <td className="py-2 px-2 text-sky-300 text-xs text-right">{r.eanB2b}</td>
              <td className="py-2 px-2 text-sky-300 text-xs text-right">{r.eanCustom}</td>
            </tr>)}
            <tr className="border-t-2 border-slate-600 bg-slate-800/50 font-semibold">
              <td className="py-2 px-3 text-slate-200 text-xs border-r border-slate-700/50"><span className="inline-flex items-center gap-1">Totaal <InfoTip title={REVENUE_TABLE_DOCS.verkoper.title} text={REVENUE_TABLE_DOCS.verkoper.text} /></span></td>
              <td className="py-2 px-2 text-slate-200 text-xs text-right">{formatNumberRounded(verkoperSegmentTotals.kwhB2cEl)}</td>
              <td className="py-2 px-2 text-slate-200 text-xs text-right">{formatNumberRounded(verkoperSegmentTotals.kwhB2bEl)}</td>
              <td className="py-2 px-2 text-slate-200 text-xs text-right">{formatNumberRounded(verkoperSegmentTotals.kwhCustomElOfftake)}</td>
              <td className="py-2 px-2 text-slate-200 text-xs text-right">{formatNumberRounded(verkoperSegmentTotals.kwhCustomElInjection)}</td>
              <td className="py-2 px-2 text-slate-200 text-xs text-right">{formatNumberRounded(verkoperSegmentTotals.kwhB2cGas)}</td>
              <td className="py-2 px-2 text-slate-200 text-xs text-right">{formatNumberRounded(verkoperSegmentTotals.kwhB2bGas)}</td>
              <td className="py-2 px-2 text-slate-200 text-xs text-right">{formatNumberRounded(verkoperSegmentTotals.kwhCustomGas)}</td>
              <td className="py-2 px-2 text-sky-300 text-xs text-right border-l border-slate-700/50">{verkoperSegmentTotals.eanB2c}</td>
              <td className="py-2 px-2 text-sky-300 text-xs text-right">{verkoperSegmentTotals.eanB2b}</td>
              <td className="py-2 px-2 text-sky-300 text-xs text-right">{verkoperSegmentTotals.eanCustom}</td>
            </tr>
          </tbody>
        </table>
      </div>}
      {verkoperSegTotalPages > 1 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((verkoperSegClamped - 1) * verkoperSegPageSize) + 1} - {Math.min(verkoperSegClamped * verkoperSegPageSize, verkoperSegmentRows.length)} van {verkoperSegmentRows.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setVerkoperSegPage(p => Math.max(1, p - 1))} disabled={verkoperSegClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <button onClick={() => setVerkoperSegPage(p => Math.min(verkoperSegTotalPages, p + 1))} disabled={verkoperSegClamped === verkoperSegTotalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
    </Card>

    </div>

    {/* ── Tab: Orders ── */}
    <div className={revTab === 'orders' ? '' : 'hidden'}>
    {/* Orders dragen bij aan de Revenue (abonnementsvergoeding). Zelfde tabel +
        berekende fee-velden als de Orders-pagina, gefilterd op de paginafilters. */}
    <OrdersDetailTable orders={filteredOrders} facturen={facturen} onOpenCustomer360={onOpenCustomer360} currentUser={currentUser} feeNotAllowedTags={feeNotAllowedTags} visiblePartnerTags={visiblePartnerTags} labelConfig={labelConfig} onRegisterExport={registerTableExport} exportScope={exportScope} exportLabel={exportLabel} exportStamp={exportStamp} />
    </div>

    {/* ── Tab: Facturen ── */}
    <div className={revTab === 'facturen' ? 'space-y-4' : 'hidden'}>
    <Card className="!p-4 !overflow-visible">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100 flex items-center gap-1.5">PARTNERVERGOEDING — DETAIL PER FACTUUR</h3><ExportKnop onExport={() => exportDetailCsv()} disabled={detailFacturen.length === 0} count={detailFacturen.length} label="Exporteer alle (gefilterde) detailregels — alle kolommen — naar Excel/CSV" /></div>
      </div>
      {facturenCustomer.length > 0 && <div className="mb-3 flex flex-wrap items-end gap-3 relative z-30">
        <div className="w-full sm:w-[280px] shrink-0">
          <label className="block text-xs text-emerald-400 mb-1">🔍 Zoeken</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-emerald-500" />
            <input type="text" placeholder="Nummer, klant, klantnr, EAN, verkoper, team…" value={detailSearch} onChange={e => { setDetailSearch(e.target.value); setDetailPage(1); }} className="w-full pl-7 pr-3 py-1.5 bg-emerald-900/30 border border-emerald-600/50 rounded text-xs text-slate-200 placeholder-slate-400 focus:border-emerald-500 focus:outline-none" />
          </div>
        </div>
        <SearchableSelect label="Soort" size="sm" value={detailSoortFilter} onChange={v => { setDetailSoortFilter(v); setDetailPage(1); }} options={detailSoorten} placeholder="Alle" />
        <SearchableSelect label="Status" size="sm" value={detailStatusFilter} onChange={v => { setDetailStatusFilter(v); setDetailPage(1); }} options={detailStatuses} placeholder="Alle" />
        <div className="min-w-[132px]"><label className="block text-xs text-slate-400 mb-1">Customer Payment Status</label><select value={detailCpsFilter} onChange={e => { setDetailCpsFilter(e.target.value); setDetailPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${detailCpsFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="Goed">Goed</option><option value="Wanbetaler">Wanbetaler</option>{hasBadDebtPeriod && <option value={PAY_STATUS_PARTIAL}>{PAY_STATUS_PARTIAL}</option>}</select></div>
        <div className="min-w-[132px]"><label className="block text-xs text-slate-400 mb-1">Segment</label><select value={detailSegmentFilter} onChange={e => { setDetailSegmentFilter(e.target.value); setDetailPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${detailSegmentFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="B2C">B2C</option><option value="B2B">B2B</option><option value="CUSTOM">Custom</option></select></div>
        <div className="min-w-[132px]"><label className="block text-xs text-slate-400 mb-1">Market</label><select value={detailMarketFilter} onChange={e => { setDetailMarketFilter(e.target.value); setDetailPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${detailMarketFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="EL">EL</option><option value="NG">NG</option></select></div>
        <div className="min-w-[184px]"><label className="block text-xs text-slate-400 mb-1">Fee Customer Tag</label><select value={detailFeeTagFilter} onChange={e => { setDetailFeeTagFilter(e.target.value); setDetailPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${detailFeeTagFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="Yes">Yes</option><option value="No">No</option></select></div>
        {(detailSearch || detailSoortFilter !== 'all' || detailStatusFilter !== 'all' || detailCpsFilter !== 'all' || detailSegmentFilter !== 'all' || detailMarketFilter !== 'all' || detailFeeTagFilter !== 'all') && <button onClick={() => { setDetailSearch(''); setDetailSoortFilter('all'); setDetailStatusFilter('all'); setDetailCpsFilter('all'); setDetailSegmentFilter('all'); setDetailMarketFilter('all'); setDetailFeeTagFilter('all'); setDetailPage(1); }} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}
      </div>}
      {facturenCustomer.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Importeer partner_commission_export om de detailtabel te tonen.</div> :
      detailFacturen.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen facturen voor de huidige filters.</div> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <SortHeader label="Relatie/Bedrijf" k="relatieBedrijf" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Verkoper" k="verkoper" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Verkoopteam" k="verkoopteam" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Nummer" k="nummer" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Factuurdatum" k="factuurdatum" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Relatie" k="relatieNaam" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Soort" k="soort" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Status" k="statusBetaling" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Customer Payment Status" k="cps" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Fee Customer Tag" k="feeTag" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL EAN" k="elGSRN" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL Ratecard" k="elRatecard" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL Segment" k="elSegment" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL kWh Offtake" k="elKwhOfftake" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL € Offtake" k="elAmountOfftake" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL Coeff Offtake" k="elCoeffOfftake" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL Type Offtake" k="elTypeOfftake" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="EL Maj Offtake" k="elMajorationOfftake" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="AVG Energy Price EL" k="elAvg" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Factor Fee EL" k="elFactor" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Meter Method" k="elMeter" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Imbalance Cost EL" k="elImbalance" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Green Cost EL" k="elGreen" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Supplier Cost EL" k="elSupplier" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="BRP Cost EL" k="elBrp" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Volume Fee EL" k="elVolume" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Energy Fee EL" k="elEnergy" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG EAN" k="ngGSRN" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG Ratecard" k="ngRatecard" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG Segment" k="ngSegment" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG kWh Offtake" k="ngKwhOfftake" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG € Offtake" k="ngAmountOfftake" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG Coeff Offtake" k="ngCoeffOfftake" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG Type Offtake" k="ngTypeOfftake" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="NG Maj Offtake" k="ngMajorationOfftake" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="AVG Energy Price Gas" k="ngAvg" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Factor Fee NG" k="ngFactor" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Supplier Cost Gas" k="ngSupplier" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Shipper Cost Gas" k="ngShipper" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Volume Fee Gas" k="ngVolume" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Energy Fee Gas" k="ngEnergy" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Excl. BTW" k="totaalExclBTW" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
            <SortHeader label="Totaal" k="totaal" align="right" sortConfig={detailSort.sortConfig} onSort={detailSort.handleSort} />
          </tr></thead>
          <tbody>{paginatedDetail.map(f => { const el = calcELDetail(f); const ng = calcNGDetail(f); const cps = getRecordEffStatus(f); const isWanbetaler = cps !== 'Goed'; return <tr key={f.id} onContextMenu={e => onC360Context(e, f.relatieNaam, klantTarget(f.relatieNaam, f.relatieReferentie, undefined, [f.elGSRN, f.ngGSRN]))} className={`border-b cursor-context-menu ${isWanbetaler ? 'bg-red-900/30 hover:bg-red-900/40 border-red-800/40' : 'border-slate-700/30 hover:bg-slate-700/20'}`}>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs truncate max-w-24">{f.relatieBedrijf}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs" title={f.verkoperFactuur ? `Volgt de huidige verkoper van het order; op de factuur zelf staat ${f.verkoperFactuur === 'OdooBot' ? 'Online' : f.verkoperFactuur}` : undefined}>{f.verkoper === 'OdooBot' ? 'Online' : f.verkoper}{f.verkoperFactuur ? <span className="ml-1 text-slate-500" aria-hidden="true">*</span> : null}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs truncate max-w-24">{f.verkoopteam}</td>
            <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs font-medium">{f.nummer}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{f.factuurdatum}</td>
            <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={f.relatieNaam}>{f.relatieNaam}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.soort}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${f.statusBetaling === 'Betaald' ? 'bg-lime-900/50 text-lime-300' : f.statusBetaling === 'Niet betaald' ? 'bg-red-900/50 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{f.statusBetaling}</span></td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cpsBadgeClass(cps)}`}>{cps}</span></td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getFactuurFeePartnerTag(f) === 'No' ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{getFactuurFeePartnerTag(f)}</span></td>
            <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap">{f.elGSRN}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.elRatecard}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs ${f.elSegment?.includes('B2C') ? 'bg-blue-900/50 text-blue-300' : f.elSegment?.includes('B2B') ? 'bg-lime-900/50 text-lime-300' : ''}`}>{f.elSegment}</span></td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{f.elKwhOfftake ? formatNumber(f.elKwhOfftake) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{f.elAmountOfftake ? formatCurrency(f.elAmountOfftake) : ''}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.elCoeffOfftake}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.elTypeOfftake}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{f.elMajorationOfftake || ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{el.avgEnergyPrice ? el.avgEnergyPrice.toFixed(2) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{el.factorFee ? formatCurrency(el.factorFee) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{el.meterMethod || '—'}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.imbalanceCost}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.greenCost}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.supplierCost}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.brpCost}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{el.volumeFee ? formatCurrency(el.volumeFee) : ''}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{el.energyFee ? formatCurrency(el.energyFee) : ''}</td>
            <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap">{f.ngGSRN}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.ngRatecard}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs ${f.ngSegment?.includes('B2C') ? 'bg-blue-900/50 text-blue-300' : f.ngSegment?.includes('B2B') ? 'bg-lime-900/50 text-lime-300' : ''}`}>{f.ngSegment}</span></td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{f.ngKwhOfftake ? formatNumber(f.ngKwhOfftake) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{f.ngAmountOfftake ? formatCurrency(f.ngAmountOfftake) : ''}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.ngCoeffOfftake}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.ngTypeOfftake}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{f.ngMajorationOfftake || ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{ng.avgEnergyPrice ? ng.avgEnergyPrice.toFixed(2) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{ng.factorFee ? formatCurrency(ng.factorFee) : ''}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{ng.supplierCost}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{ng.shipperCost}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{ng.volumeFee ? formatCurrency(ng.volumeFee) : ''}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{ng.energyFee ? formatCurrency(ng.energyFee) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{formatCurrency(f.totaalExclBTW)}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{formatCurrency(f.totaal)}</td>
          </tr>; })}</tbody>
        </table>
      </div>}
      {detailFacturen.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((detailPageClamped - 1) * pageSize) + 1} - {Math.min(detailPageClamped * pageSize, detailFacturen.length)} van {detailFacturen.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDetailPage(p => Math.max(1, p - 1))} disabled={detailPageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{detailPageClamped} / {detailTotalPages}</span>
          <button onClick={() => setDetailPage(p => Math.min(detailTotalPages, p + 1))} disabled={detailPageClamped === detailTotalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
    </Card>

    {/* Gefactureerde Volume per EAN — dezelfde gefilterde regels als DETAIL PER
        FACTUUR, teruggebracht tot de volumekolommen en aangevuld met het TOTAAL
        gefactureerde kWh per EAN. Staat bewust ONDER de detailtabel: het is een
        andere lezing van dezelfde data, geen andere selectie. */}
    <Card className="!p-4 !overflow-visible">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">GEFACTUREERDE VOLUME PER EAN</h3><ExportKnop onExport={() => exportVolumePerEanCsv()} disabled={volumeRijen.length === 0} count={volumeRijen.length} label="Exporteer alle (gefilterde) regels van deze tabel naar Excel/CSV" /></div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Zelfde selectie en filters als de detailtabel hierboven, maar <strong>één regel per aansluiting per klant</strong> in plaats van per factuur. De twee laatste kolommen tellen alle gefactureerde kWh van die regel op. Segment, majoration en meter method komen van de recentste factuur; wisselen ze over de gebundelde facturen, dan staat de cel in amber met een <span className="text-amber-300">*</span> en toont de tooltip alle waarden.
      </p>
      {volumeRijen.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen facturen voor de huidige filters.</div> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <SortHeader label="Relatie/Bedrijf" k="relatieBedrijf" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="Verkoper" k="verkoper" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="Verkoopteam" k="verkoopteam" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="Relatie" k="relatieNaam" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            {/* Hoeveel facturen er in deze ene regel zitten. Zonder dat getal weet je
                niet of een totaal uit één factuur komt of uit twaalf. */}
            <SortHeader label="Facturen" k="aantalFacturen" align="right" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="EL EAN" k="elGSRN" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="EL Segment" k="elSegment" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="EL Maj Offtake" k="elMajorationOfftake" align="right" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="Meter Method" k="elMeter" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="NG EAN" k="ngGSRN" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="NG Segment" k="ngSegment" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="NG Maj Offtake" k="ngMajorationOfftake" align="right" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            {/* De twee totaalkolommen achteraan en zichtbaar anders gekleurd: ze
                dragen het volume van de hele regel. */}
            <SortHeader label="Total Invoice EL kWh" k="totaalElKwh" align="right" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
            <SortHeader label="Total Invoice NG kWh" k="totaalNgKwh" align="right" sortConfig={volumeSort.sortConfig} onSort={volumeSort.handleSort} />
          </tr></thead>
          <tbody>{paginatedVolume.map(r => {
            const segBadge = (v) => <span className={`px-1.5 py-0.5 rounded text-xs ${v?.includes('B2C') ? 'bg-blue-900/50 text-blue-300' : v?.includes('B2B') ? 'bg-lime-900/50 text-lime-300' : ''}`}>{v}</span>;
            return <tr key={r._sleutel} onContextMenu={e => onC360Context(e, r.relatieNaam, klantTarget(r.relatieNaam, r.relatieReferentie))} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-context-menu">
              <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs truncate max-w-24">{r.relatieBedrijf}</td>
              <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{r.verkoper === 'OdooBot' ? 'Online' : r.verkoper}</td>
              <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs truncate max-w-24">{r.verkoopteam}</td>
              <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={r.relatieNaam}>{r.relatieNaam}</td>
              <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap" title={`${r.aantalFacturen} factuurregel(s) samengeteld in deze regel`}>{r.aantalFacturen}</td>
              <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap">{r.elGSRN}</td>
              <VolAttrCell attr={r._elSegment} render={segBadge} />
              <VolAttrCell attr={r._elMaj} align="right" render={v => v || ''} />
              <VolAttrCell attr={r._meter} render={v => v || '—'} />
              <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap">{r.ngGSRN}</td>
              <VolAttrCell attr={r._ngSegment} render={segBadge} />
              <VolAttrCell attr={r._ngMaj} align="right" render={v => v || ''} />
              <td className="py-2 px-2 text-lime-300 text-xs text-right whitespace-nowrap font-medium">{r.totaalElKwh != null ? formatNumber(Math.round(r.totaalElKwh)) : ''}</td>
              <td className="py-2 px-2 text-orange-300 text-xs text-right whitespace-nowrap font-medium">{r.totaalNgKwh != null ? formatNumber(Math.round(r.totaalNgKwh)) : ''}</td>
            </tr>; })}</tbody>
        </table>
      </div>}
      {volumeRijen.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((volumePageClamped - 1) * pageSize) + 1} - {Math.min(volumePageClamped * pageSize, volumeRijen.length)} van {volumeRijen.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setVolumePage(p => Math.max(1, p - 1))} disabled={volumePageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{volumePageClamped} / {volumeTotalPages}</span>
          <button onClick={() => setVolumePage(p => Math.min(volumeTotalPages, p + 1))} disabled={volumePageClamped === volumeTotalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
    </Card>

    {/* Self bills — alleen elektriciteit (partner_commission_export_SB), in een
        aparte tabel onder de facturen-detailtabel. Zelfde filters/scoping. */}
    <Card className="!p-4 !overflow-visible">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100 flex items-baseline gap-2">SELF BILLS — ELEKTRICITEIT <span className="text-xs font-normal text-slate-400">Alle klantensegmenten (B2C, B2B, Custom)</span></h3><ExportKnop onExport={() => exportSelfBillsCsv()} disabled={selfBillsTable.length === 0} count={selfBillsTable.length} label="Exporteer alle (gefilterde) self-bill regels naar Excel/CSV" /></div>
      </div>
      {commissionMeta.selfbillUnmappedCount > 0 && <div className="mb-3 bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-2 rounded text-sm">{commissionMeta.selfbillUnmappedCount} self-bill record(s) zonder herkend energielabel (factuurnummer-prefix) — deze worden niet getoond.</div>}
      {selfBillsCustomer.length > 0 && <div className="mb-3 flex flex-wrap items-end gap-3 relative z-30">
        <div className="w-full sm:w-[280px] shrink-0">
          <label className="block text-xs text-emerald-400 mb-1">🔍 Zoeken</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-emerald-500" />
            <input type="text" placeholder="Nummer, klant, klantnr, EAN, verkoper, team…" value={sbSearch} onChange={e => { setSbSearch(e.target.value); setSelfBillPage(1); }} className="w-full pl-7 pr-3 py-1.5 bg-emerald-900/30 border border-emerald-600/50 rounded text-xs text-slate-200 placeholder-slate-400 focus:border-emerald-500 focus:outline-none" />
          </div>
        </div>
        <SearchableSelect label="Status" size="sm" value={sbStatusFilter} onChange={v => { setSbStatusFilter(v); setSelfBillPage(1); }} options={sbStatuses} placeholder="Alle" />
        <div className="min-w-[132px]"><label className="block text-xs text-slate-400 mb-1">Customer Payment Status</label><select value={sbCpsFilter} onChange={e => { setSbCpsFilter(e.target.value); setSelfBillPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${sbCpsFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="Goed">Goed</option><option value="Wanbetaler">Wanbetaler</option>{hasBadDebtPeriod && <option value={PAY_STATUS_PARTIAL}>{PAY_STATUS_PARTIAL}</option>}</select></div>
        {/* Geen B2C-optie: self bills zijn injectie-afrekeningen en komen in de
            praktijk alleen voor bij B2B en Custom. */}
        <div className="min-w-[132px]"><label className="block text-xs text-slate-400 mb-1">Segment</label><select value={sbSegmentFilter} onChange={e => { setSbSegmentFilter(e.target.value); setSelfBillPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${sbSegmentFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="B2B">B2B</option><option value="CUSTOM">Custom</option></select></div>
        <div className="min-w-[184px]"><label className="block text-xs text-slate-400 mb-1">Fee Customer Tag</label><select value={sbFeeTagFilter} onChange={e => { setSbFeeTagFilter(e.target.value); setSelfBillPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${sbFeeTagFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="Yes">Yes</option><option value="No">No</option></select></div>
        {(sbSearch || sbStatusFilter !== 'all' || sbCpsFilter !== 'all' || sbSegmentFilter !== 'all' || sbFeeTagFilter !== 'all') && <button onClick={() => { setSbSearch(''); setSbStatusFilter('all'); setSbCpsFilter('all'); setSbSegmentFilter('all'); setSbFeeTagFilter('all'); setSelfBillPage(1); }} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}
      </div>}
      {selfBillsCustomer.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Importeer partner_commission_export_SB (Self Bills) om deze tabel te tonen.</div> :
      selfBillsTable.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen self bills voor de huidige filters.</div> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <SortHeader label="Relatie/Bedrijf" k="relatieBedrijf" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Verkoper" k="verkoper" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Verkoopteam" k="verkoopteam" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Nummer" k="nummer" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Factuurdatum" k="factuurdatum" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Relatie" k="relatieNaam" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Soort" k="soort" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Status" k="statusBetaling" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Customer Payment Status" k="cps" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Fee Customer Tag" k="feeTag" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL EAN" k="elGSRN" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL Ratecard" k="elRatecard" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL Segment" k="elSegment" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL kWh Injection" k="elKwhInjection" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL € Injection" k="elAmountInjection" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL Coeff Injection" k="elCoeffInjection" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL Type Offtake" k="elTypeOfftake" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL Type Injection" k="elTypeInjection" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="EL Maj Injection" k="sbInjMaj" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Meter Method" k="sbMeter" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Imbalance Cost EL" k="sbImbalance" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Green Cost EL" k="sbGreen" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Supplier Cost EL" k="sbSupplier" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="BRP Cost EL" k="sbBrp" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Factor Fee EL" k="sbFactor" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Volume Fee EL" k="sbVolume" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Energy Fee EL" k="sbEnergy" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Excl. BTW" k="totaalExclBTW" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
            <SortHeader label="Totaal" k="totaal" align="right" sortConfig={sbSort.sortConfig} onSort={sbSort.handleSort} />
          </tr></thead>
          <tbody>{paginatedSelfBills.map(f => { const el = calcELDetailSB(f); const cps = getRecordEffStatus(f); const isWanbetaler = cps !== 'Goed'; return <tr key={f.id} onContextMenu={e => onC360Context(e, f.relatieNaam, klantTarget(f.relatieNaam, f.relatieReferentie, undefined, [f.elGSRN, f.ngGSRN]))} className={`border-b cursor-context-menu ${isWanbetaler ? 'bg-red-900/30 hover:bg-red-900/40 border-red-800/40' : 'border-slate-700/30 hover:bg-slate-700/20'}`}>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs truncate max-w-24">{f.relatieBedrijf}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs" title={f.verkoperFactuur ? `Volgt de huidige verkoper van het order; op de factuur zelf staat ${f.verkoperFactuur === 'OdooBot' ? 'Online' : f.verkoperFactuur}` : undefined}>{f.verkoper === 'OdooBot' ? 'Online' : f.verkoper}{f.verkoperFactuur ? <span className="ml-1 text-slate-500" aria-hidden="true">*</span> : null}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs truncate max-w-24">{f.verkoopteam}</td>
            <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs font-medium">{f.nummer}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{f.factuurdatum}</td>
            <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={f.relatieNaam}>{f.relatieNaam}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.soort}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${f.statusBetaling === 'Betaald' ? 'bg-lime-900/50 text-lime-300' : f.statusBetaling === 'Niet betaald' ? 'bg-red-900/50 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{f.statusBetaling}</span></td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cpsBadgeClass(cps)}`}>{cps}</span></td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getFactuurFeePartnerTag(f) === 'No' ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{getFactuurFeePartnerTag(f)}</span></td>
            <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap">{f.elGSRN}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.elRatecard}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs ${f.elSegment?.includes('B2C') ? 'bg-blue-900/50 text-blue-300' : f.elSegment?.includes('B2B') ? 'bg-lime-900/50 text-lime-300' : ''}`}>{f.elSegment}</span></td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{f.elKwhInjection ? formatNumber(f.elKwhInjection) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{f.elAmountInjection ? formatCurrency(f.elAmountInjection) : ''}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{coeff3(f.elCoeffInjection)}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.elTypeOfftake}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.elTypeInjection}</td>
            <td className={`py-2 px-2 text-xs text-right whitespace-nowrap ${el.injMajIsFix ? 'text-orange-600 font-semibold' : 'text-slate-400'}`} title={el.injMajIsFix ? 'Ontbrekende data/contractafspraak — ingevuld als -25' : undefined}>{el.injMaj}</td>
            <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{el.meterMethod || '—'}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.imbalanceCost}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.greenCost}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.supplierCost}</td>
            <td className="py-2 px-2 text-slate-400 text-xs text-right whitespace-nowrap">{el.brpCost}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{el.factorFee ? formatCurrency(el.factorFee) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{el.volumeFee ? formatCurrency(el.volumeFee) : ''}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{el.energyFee ? formatCurrency(el.energyFee) : ''}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{formatCurrency(f.totaalExclBTW)}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{formatCurrency(f.totaal)}</td>
          </tr>; })}
          <tr className="border-t-2 border-slate-600 bg-slate-800/50 font-semibold">
            <td className="py-2 px-2 text-slate-200 text-xs" colSpan={12}>TOTAAL ({selfBillsTable.length} regels)</td>
            <td className="py-2 px-2 text-slate-200 text-xs text-right whitespace-nowrap">{formatNumber(selfBillTotals.kwhOfftake)}</td>
            <td className="py-2 px-2 text-slate-200 text-xs text-right whitespace-nowrap">{formatCurrency(selfBillTotals.amountOfftake)}</td>
            <td className="py-2 px-2 text-xs" colSpan={14}></td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap">{formatCurrency(selfBillTotals.energyFee)}</td>
            <td className="py-2 px-2 text-xs" colSpan={1}></td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap">{formatCurrency(selfBillTotals.totaal)}</td>
          </tr></tbody>
        </table>
      </div>}
      {selfBillsTable.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((selfBillPageClamped - 1) * pageSize) + 1} - {Math.min(selfBillPageClamped * pageSize, selfBillsTable.length)} van {selfBillsTable.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSelfBillPage(p => Math.max(1, p - 1))} disabled={selfBillPageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{selfBillPageClamped} / {selfBillTotalPages}</span>
          <button onClick={() => setSelfBillPage(p => Math.min(selfBillTotalPages, p + 1))} disabled={selfBillPageClamped === selfBillTotalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
    </Card>

    {/* INVOICE ET — leveranciersfacturen (alles wat niet met VO of VK begint), afgesplitst van de
        klantfacturen hierboven. Enkel admin / ET-dossierbeheerder / master;
        titel toont het ET-merk niet aan master (compliance). */}
    {canSeeEtSelfBills && <Card className="!p-4 !overflow-visible">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100 flex items-baseline gap-2">{etInvTitle} <span className="text-xs font-normal text-slate-400">{etSbShowBrand ? 'Leveranciersfacturen — factuurnr. VET…' : 'Leveranciersfacturen'}</span></h3><ExportKnop onExport={() => exportInvoiceEtCsv()} disabled={detailFacturenEt.length === 0} count={detailFacturenEt.length} label="Exporteer alle (gefilterde) regels naar Excel/CSV" /></div>
      </div>
      {facturenEt.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen leveranciersfacturen in de data (alle factuurnummers beginnen met VO of VK).</div> :
      detailFacturenEt.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen leveranciersfacturen voor de huidige filters.</div> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <SortHeader label="Relatie/Bedrijf" k="relatieBedrijf" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Verkoper" k="verkoper" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Verkoopteam" k="verkoopteam" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Nummer" k="nummer" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Factuurdatum" k="factuurdatum" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Relatie" k="relatieNaam" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Soort" k="soort" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Status" k="statusBetaling" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="EAN" k="elGSRN" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Excl. BTW" k="totaalExclBTW" align="right" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
            <SortHeader label="Totaal" k="totaal" align="right" sortConfig={detEtSort.sortConfig} onSort={detEtSort.handleSort} />
          </tr></thead>
          <tbody>{paginatedDetailEt.map(f => <tr key={f.id} onContextMenu={e => onC360Context(e, f.relatieNaam, klantTarget(f.relatieNaam, f.relatieReferentie, undefined, [f.elGSRN, f.ngGSRN]), { lookup: { nummer: f.nummer }, label: f.nummer })} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-context-menu">
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs truncate max-w-24">{etText(f.relatieBedrijf)}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{f.verkoper === 'OdooBot' ? 'Online' : etText(f.verkoper)}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs truncate max-w-24">{etText(f.verkoopteam)}</td>
            <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs font-medium">{etText(f.nummer)}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{f.factuurdatum}</td>
            <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={etText(f.relatieNaam)}>{etText(f.relatieNaam)}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{etText(f.soort)}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${f.statusBetaling === 'Betaald' ? 'bg-lime-900/50 text-lime-300' : f.statusBetaling === 'Niet betaald' ? 'bg-red-900/50 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{f.statusBetaling}</span></td>
            <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap">{f.elGSRN || f.ngGSRN}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{formatCurrency(f.totaalExclBTW)}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{formatCurrency(f.totaal)}</td>
          </tr>)}
          <tr className="border-t-2 border-slate-600 bg-slate-800/50 font-semibold">
            <td className="py-2 px-2 text-slate-200 text-xs" colSpan={9}>TOTAAL ({detailFacturenEt.length} regels)</td>
            <td className="py-2 px-2 text-slate-200 text-xs text-right whitespace-nowrap">{formatCurrency(detailEtTotals.totaalExclBTW)}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap">{formatCurrency(detailEtTotals.totaal)}</td>
          </tr></tbody>
        </table>
      </div>}
      {detailFacturenEt.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((detailEtPageClamped - 1) * pageSize) + 1} - {Math.min(detailEtPageClamped * pageSize, detailFacturenEt.length)} van {detailFacturenEt.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDetailEtPage(p => Math.max(1, p - 1))} disabled={detailEtPageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{detailEtPageClamped} / {detailEtTotalPages}</span>
          <button onClick={() => setDetailEtPage(p => Math.min(detailEtTotalPages, p + 1))} disabled={detailEtPageClamped === detailEtTotalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
    </Card>}

    {/* SELF BILLS ET — leveranciersfacturen (factuurnr. "SBET…"), afgesplitst van
        de klant-self-bills hierboven. Enkel admin / ET-dossierbeheerder / master;
        titel toont het ET-merk niet aan master (compliance). */}
    {canSeeEtSelfBills && <Card className="!p-4 !overflow-visible">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100 flex items-baseline gap-2">{etSbTitle} <span className="text-xs font-normal text-slate-400">{etSbShowBrand ? 'Leveranciersfacturen — factuurnr. SBET…' : 'Leveranciersfacturen'}</span></h3><ExportKnop onExport={() => exportSelfBillsEtCsv()} disabled={selfBillsEtTable.length === 0} count={selfBillsEtTable.length} label="Exporteer alle (gefilterde) regels naar Excel/CSV" /></div>
      </div>
      {selfBillsEt.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen leveranciersfacturen (SBET) in de data.</div> :
      selfBillsEtTable.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">Geen leveranciersfacturen voor de huidige filters.</div> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <SortHeader label="Relatie/Bedrijf" k="relatieBedrijf" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Verkoper" k="verkoper" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Verkoopteam" k="verkoopteam" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Nummer" k="nummer" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Factuurdatum" k="factuurdatum" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Relatie" k="relatieNaam" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Soort" k="soort" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Status" k="statusBetaling" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="EAN" k="elGSRN" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Excl. BTW" k="totaalExclBTW" align="right" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
            <SortHeader label="Totaal" k="totaal" align="right" sortConfig={sbEtSort.sortConfig} onSort={sbEtSort.handleSort} />
          </tr></thead>
          <tbody>{paginatedSelfBillsEt.map(f => <tr key={f.id} onContextMenu={e => onC360Context(e, f.relatieNaam, klantTarget(f.relatieNaam, f.relatieReferentie, undefined, [f.elGSRN, f.ngGSRN]))} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-context-menu">
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs truncate max-w-24">{etText(f.relatieBedrijf)}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{f.verkoper === 'OdooBot' ? 'Online' : etText(f.verkoper)}</td>
            <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs truncate max-w-24">{etText(f.verkoopteam)}</td>
            <td className="py-2 px-2 text-slate-200 whitespace-nowrap text-xs font-medium">{etText(f.nummer)}</td>
            <td className="py-2 px-2 text-slate-300 whitespace-nowrap text-xs">{f.factuurdatum}</td>
            <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={etText(f.relatieNaam)}>{etText(f.relatieNaam)}</td>
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{etText(f.soort)}</td>
            <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${f.statusBetaling === 'Betaald' ? 'bg-lime-900/50 text-lime-300' : f.statusBetaling === 'Niet betaald' ? 'bg-red-900/50 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{f.statusBetaling}</span></td>
            <td className="py-2 px-2 text-slate-500 text-xs font-mono whitespace-nowrap">{f.elGSRN || f.ngGSRN}</td>
            <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{formatCurrency(f.totaalExclBTW)}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap font-medium">{formatCurrency(f.totaal)}</td>
          </tr>)}
          <tr className="border-t-2 border-slate-600 bg-slate-800/50 font-semibold">
            <td className="py-2 px-2 text-slate-200 text-xs" colSpan={9}>TOTAAL ({selfBillsEtTable.length} regels)</td>
            <td className="py-2 px-2 text-slate-200 text-xs text-right whitespace-nowrap">{formatCurrency(selfBillEtTotals.totaalExclBTW)}</td>
            <td className="py-2 px-2 text-lime-400 text-xs text-right whitespace-nowrap">{formatCurrency(selfBillEtTotals.totaal)}</td>
          </tr></tbody>
        </table>
      </div>}
      {selfBillsEtTable.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700">
        <div className="text-sm text-slate-400">Toont {((selfBillEtPageClamped - 1) * pageSize) + 1} - {Math.min(selfBillEtPageClamped * pageSize, selfBillsEtTable.length)} van {selfBillsEtTable.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSelfBillEtPage(p => Math.max(1, p - 1))} disabled={selfBillEtPageClamped === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button>
          <span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{selfBillEtPageClamped} / {selfBillEtTotalPages}</span>
          <button onClick={() => setSelfBillEtPage(p => Math.min(selfBillEtTotalPages, p + 1))} disabled={selfBillEtPageClamped === selfBillEtTotalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button>
        </div>
      </div>}
    </Card>}
    </div>

    {/* ── Tab: Commissies <label> — de eigen vergoedingsmethodiek van dit
        energielabel. Elk label heeft zijn eigen pagina; ze delen alleen de
        opslag van de tarieven (/api/custom-fee) en het documentenblok. ── */}
    {customPaginas.map(p => (
      <div key={p.tab} className={revTab === p.tab ? 'space-y-4' : 'hidden'}>
        {p.tab === 'commissie-overzicht-evident' ? (
          <EvidentCommissieOverzicht
            facturenGoed={facturenGoed}
            selfBillsGoed={selfBillsGoed}
            orders={filteredOrders}
            meterMethod={meterMethodFor}
            token={token}
            energielabel={energielabelFilter}
            currentUser={currentUser}
            onRegisterExport={registerTableExport}
            exportStamp={exportStamp}
            exportLabel={exportLabel}
            exportScope={exportScope}
          />
        ) : p.tab === 'commissies-evident' ? (
          <EvidentCommissies
            facturenGoed={facturenGoed}
            selfBillsGoed={selfBillsGoed}
            calcSummaryStats={calcSummaryStats}
            meterMethod={meterMethodFor}
            token={token}
            energielabel={energielabelFilter}
            currentUser={currentUser}
            onRegisterExport={registerTableExport}
            exportStamp={exportStamp}
            exportLabel={exportLabel}
            exportScope={exportScope}
          />
        ) : (
          <ServoltCustomFee
            orders={filteredOrders}
            facturenGoed={facturenGoed}
            selfBillsGoed={selfBillsGoed}
            calcSummaryStats={calcSummaryStats}
            token={token}
            energielabel={energielabelFilter}
            currentUser={currentUser}
            onRegisterExport={registerTableExport}
            exportStamp={exportStamp}
            exportLabel={exportLabel}
            exportScope={exportScope}
          />
        )}
      </div>
    ))}

    {/* ── Tab: Betalingen ── betaalde partnervergoeding in DRIE tabellen: per
        energielabel, per verkoopteam en per verkoper. Elke tabel heeft een eigen
        invoerregel (alleen voor wie op dat niveau mag zetten), groepeert meerdere
        bedragen van dezelfde naam tot een som met inklapbare deelbedragen, en
        volgt de filters van de pagina. Bedrag en omschrijving zijn in de rij zelf
        te bewerken (Enter = opslaan, Esc = annuleren). De server bewaakt de rechten. */}
    <div className={revTab === 'betalingen' ? 'space-y-4' : 'hidden'}>
    {magBetalingen && (() => {
      const inputKlasse = 'h-8 px-2.5 bg-slate-700/70 border border-slate-600/70 rounded-md text-xs text-slate-200 disabled:opacity-60';
      const selKlasse = `${inputKlasse} w-full`;
      const knopKlein = 'px-2 py-1 text-xs rounded';
      const opEnter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); fn(); } };
      const opEditToets = (e) => { if (e.key === 'Enter') { e.preventDefault(); savePaidEdit(); } else if (e.key === 'Escape') { setPaidEdit(null); } };
      // Kolommen per tabel: de naamkolommen verschillen, de rest is gelijk.
      const naamKoppen = { label: ['Energielabel'], team: ['Energielabel', 'Verkoopteam'], seller: ['Energielabel', 'Verkoopteam', 'Verkoper'] };
      const naamCellen = (niveau, g) => [g.energy_label, ...(niveau !== 'label' ? [g.sales_team] : []), ...(niveau === 'seller' ? [g.seller_name] : [])];

      const invoerRegel = (niveau) => {
        if (!magPaidNiveau(niveau)) return null;
        const f = paidForm[niveau];
        const teams = paidTeamsVoor(f.label);
        const sellers = paidSellersVoor(f.label, f.team);
        const klaar = paidFormKlaar(niveau);
        return (
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div className="w-[184px]">
              <label className="block text-[11px] text-slate-400 mb-1">Energielabel</label>
              <select value={f.label} onChange={e => zetPaidForm(niveau, { label: e.target.value, team: '', seller: '' })} disabled={paidEditLabels.length === 1} className={selKlasse}>
                <option value="all">Kies energielabel…</option>
                {paidEditLabels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            {niveau !== 'label' && <div className="w-[184px]">
              <label className="block text-[11px] text-slate-400 mb-1">Verkoopteam</label>
              <select value={f.team} onChange={e => zetPaidForm(niveau, { team: e.target.value, seller: '' })} disabled={f.label === 'all' || (paidNiveau === 'seller' && teams.length === 1)} className={selKlasse}>
                <option value="">Kies verkoopteam…</option>
                {teams.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>}
            {niveau === 'seller' && <div className="w-[184px]">
              <label className="block text-[11px] text-slate-400 mb-1">Verkoper</label>
              <select value={f.seller} onChange={e => zetPaidForm(niveau, { seller: e.target.value })} disabled={!f.team} className={selKlasse}>
                <option value="">Kies verkoper…</option>
                {sellers.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>}
            <div className="w-[132px]">
              <label className="block text-[11px] text-slate-400 mb-1 whitespace-nowrap">Bedrag excl. BTW (€)</label>
              <input type="text" inputMode="decimal" value={f.amount} onChange={e => zetPaidForm(niveau, { amount: e.target.value })} onKeyDown={opEnter(() => addPaidCommission(niveau))} placeholder="0,00" className={`${inputKlasse} w-full`} />
            </div>
            <div className="w-[280px]">
              <label className="block text-[11px] text-slate-400 mb-1">Omschrijving <span className="text-slate-500">(bv. vergoeding 2026/08)</span></label>
              <input type="text" value={f.note} onChange={e => zetPaidForm(niveau, { note: e.target.value })} onKeyDown={opEnter(() => addPaidCommission(niveau))} maxLength={200} placeholder="vergoeding 2026/08" className={`${inputKlasse} w-full`} />
            </div>
            <button onClick={() => addPaidCommission(niveau)} disabled={paidSaving || !klaar} title={klaar ? 'Toevoegen (Enter)' : 'Vul niveau en bedrag in'} className="inline-flex items-center gap-1.5 h-8 px-3 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-slate-900 font-medium text-xs rounded-md"><Plus size={14} />Toevoegen</button>
          </div>
        );
      };

      // Eén deelbedrag (of het enige bedrag) als rij, eventueel in bewerking.
      const bedragRij = (niveau, r, sub) => {
        const magRij = magPaidNiveau(niveau);
        const inEdit = paidEdit && paidEdit.id === r.id;
        const inspringing = sub ? 'pl-7' : '';
        const kolommen = naamKoppen[niveau].length;
        return (
          <tr key={r.id} className={`border-b border-slate-700/30 ${sub ? 'bg-slate-500/[.06]' : 'hover:bg-slate-700/20'} ${inEdit ? '!bg-lime-500/10' : ''}`}>
            <td className={`py-1.5 px-3 text-xs text-slate-400 ${inspringing}`} colSpan={kolommen} data-label="Naam">{sub ? <span className="text-slate-500">deelbedrag</span> : naamCellen(niveau, r).join(' · ')}</td>
            <td className="py-1.5 px-3 text-xs" data-label="Omschrijving">
              {inEdit
                ? <input autoFocus type="text" value={paidEdit.note} onChange={e => setPaidEdit(p => ({ ...p, note: e.target.value }))} onKeyDown={opEditToets} maxLength={200} placeholder="vergoeding 2026/08" className={`${inputKlasse} w-full min-w-[200px]`} />
                : (magRij
                  ? <button onClick={() => startPaidEdit(r)} title="Omschrijving en bedrag bewerken" className="text-left text-slate-300 hover:text-slate-100 hover:underline decoration-dotted underline-offset-2 w-full">{r.omschrijving || <span className="text-slate-500 italic">omschrijving toevoegen…</span>}</button>
                  : <span className="text-slate-300">{r.omschrijving || <span className="text-slate-500">—</span>}</span>)}
            </td>
            <td className="py-1.5 px-3 text-xs text-right tabular-nums" data-label="Bedrag excl. BTW">
              {inEdit
                ? <input type="text" inputMode="decimal" value={paidEdit.amount} onChange={e => setPaidEdit(p => ({ ...p, amount: e.target.value }))} onKeyDown={opEditToets} className={`${inputKlasse} w-[110px] text-right`} />
                : <span className="text-sky-300 font-medium">{formatCurrency(r.amount)}</span>}
            </td>
            <td className="py-1.5 px-3 text-xs text-right tabular-nums text-slate-400" data-label="Bedrag incl. BTW">{formatCurrency(metBtw(r.amount))}</td>
            <td className="py-1.5 px-3 text-xs text-slate-300" data-label="Laatst gewijzigd door" title={r.updated_by_email || ''}>{r.updated_by_name || <span className="text-slate-500">—</span>}</td>
            <td className="py-1.5 px-3 text-xs text-slate-300 whitespace-nowrap tabular-nums" data-label="Laatst gewijzigd op">{fmtMomentBE(r.updated_at)}</td>
            {paidRechten.magSchrijven && <td className="py-1.5 px-3 text-right whitespace-nowrap" data-label="Actie">
              {inEdit
                ? <>
                  <button onClick={savePaidEdit} disabled={paidSaving} title="Opslaan (Enter)" className={`${knopKlein} bg-lime-500 hover:bg-lime-400 text-slate-900 font-medium mr-1`}>Opslaan</button>
                  <button onClick={() => setPaidEdit(null)} title="Annuleren (Esc)" className={`${knopKlein} bg-slate-700 hover:bg-slate-600 text-slate-200`}>Annuleer</button>
                </>
                : magRij && <>
                  <button onClick={() => startPaidEdit(r)} className={`${knopKlein} bg-slate-700 hover:bg-slate-600 text-slate-200 mr-1`}>Wijzig</button>
                  <button onClick={() => deletePaidCommission(r.id)} className={`${knopKlein} bg-red-900/40 hover:bg-red-900/60 text-red-300`}>Wis</button>
                </>}
            </td>}
          </tr>
        );
      };

      // Groep: één bedrag → gewone rij; meerdere → somrij met pijltje en daaronder
      // (ingeklapt) de deelbedragen, meest recente bovenaan.
      const groepRijen = (niveau, g) => {
        if (g.rijen.length === 1) return [bedragRij(niveau, g.rijen[0], false)];
        const open = !!paidOpen[g.sleutel];
        const kolommen = naamKoppen[niveau].length;
        const laatste = g.rijen[0];
        const kop = (
          <tr key={`g${g.sleutel}`} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer" onClick={() => togglePaidGroep(g.sleutel)}>
            <td className="py-1.5 px-3 text-xs text-slate-200 font-medium" colSpan={kolommen} data-label="Naam">
              <span className="inline-flex items-center gap-1.5">{open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}{naamCellen(niveau, g).join(' · ')}</span>
            </td>
            <td className="py-1.5 px-3 text-xs text-slate-400" data-label="Omschrijving">{g.rijen.length} bedragen · <span className="text-slate-500">{open ? 'inklappen' : 'toon deelbedragen'}</span></td>
            <td className="py-1.5 px-3 text-xs text-right tabular-nums text-sky-300 font-semibold" data-label="Totaal excl. BTW">{formatCurrency(g.totaal)}</td>
            <td className="py-1.5 px-3 text-xs text-right tabular-nums text-slate-400" data-label="Totaal incl. BTW">{formatCurrency(metBtw(g.totaal))}</td>
            <td className="py-1.5 px-3 text-xs text-slate-300" data-label="Laatst gewijzigd door" title={laatste.updated_by_email || ''}>{laatste.updated_by_name || <span className="text-slate-500">—</span>}</td>
            <td className="py-1.5 px-3 text-xs text-slate-300 whitespace-nowrap tabular-nums" data-label="Laatst gewijzigd op">{fmtMomentBE(laatste.updated_at)}</td>
            {paidRechten.magSchrijven && <td className="py-1.5 px-3" />}
          </tr>
        );
        return open ? [kop, ...g.rijen.map((r) => bedragRij(niveau, r, true))] : [kop];
      };

      const tabel = (niveau, titel, uitleg) => {
        const groepen = paidPerNiveau[niveau];
        const totaal = groepen.reduce((s, g) => s + g.totaal, 0);
        const koppen = naamKoppen[niveau];
        return (
          <Card key={niveau} className="!p-4">
            <h3 className="text-base font-semibold text-slate-100 mb-1 flex items-center gap-1.5">BETAALDE PARTNERVERGOEDING – {titel}
              <InfoTip title={`Betaalde partnervergoeding – ${titel}`} text={uitleg} />
            </h3>
            <p className="text-xs text-slate-400 mb-3">{magPaidNiveau(niveau) ? 'Meerdere bedragen per naam zijn mogelijk; de som telt. ' : 'Alleen lezen op dit niveau. '}Bedragen excl. BTW; sortering op naam en daarna op laatst gewijzigd.</p>
            {invoerRegel(niveau)}
            {groepen.length === 0 ? <div className="text-sm text-slate-500">Nog geen bedragen{paidRechten.magSchrijven ? '' : ' binnen je rechten'} in deze selectie.</div> :
            <div className="overflow-x-auto">
              <table className="w-full text-sm resp-card">
                <thead><tr className="border-b border-slate-700">
                  <th className="py-2 px-3 text-left text-slate-400 text-xs" colSpan={koppen.length}>{koppen.join(' · ')}</th>
                  <th className="py-2 px-3 text-left text-slate-400 text-xs">Omschrijving</th>
                  <th className="py-2 px-3 text-right text-slate-400 text-xs">Bedrag excl. BTW</th>
                  <th className="py-2 px-3 text-right text-slate-400 text-xs">Bedrag incl. BTW</th>
                  <th className="py-2 px-3 text-left text-slate-400 text-xs">Laatst gewijzigd door</th>
                  <th className="py-2 px-3 text-left text-slate-400 text-xs">Laatst gewijzigd op</th>
                  {paidRechten.magSchrijven && <th className="py-2 px-3 text-right text-slate-400 text-xs">Actie</th>}
                </tr></thead>
                <tbody>{groepen.flatMap((g) => groepRijen(niveau, g))}</tbody>
                <tfoot><tr className="border-t border-slate-600 font-semibold">
                  <td className="py-2 px-3 text-slate-200 text-xs" colSpan={koppen.length + 1}>Totaal {titel.toLowerCase()} in deze selectie</td>
                  <td className="py-2 px-3 text-sky-300 text-xs text-right tabular-nums">{formatCurrency(totaal)}</td>
                  <td className="py-2 px-3 text-slate-400 text-xs text-right tabular-nums">{formatCurrency(metBtw(totaal))}</td>
                  <td colSpan={paidRechten.magSchrijven ? 3 : 2} />
                </tr></tfoot>
              </table>
            </div>}
          </Card>
        );
      };

      return (
        <>
          {paidCommissionError && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2.5 rounded-xl text-sm">{paidCommissionError}</div>}
          <div className="text-xs text-slate-400 px-1">Kengetal "Betaalde vergoeding" op de Samenvatting voor de actieve selectie: <span className="text-sky-300 font-semibold">{formatCurrency(betaaldeVergoeding)}</span> excl. BTW — dat is één-op-één het totaal van de tabel <span className="text-slate-300">{PAID_NIVEAU_TITEL[paidSelectieNiveau]}</span> hieronder (het niveau van je selectie); de andere niveaus tellen er niet in mee.</div>
          {tabel('label', 'Energielabel', 'Bedragen die aan het energielabel zelf zijn uitbetaald, los van een verkoopteam of verkoper. Alleen admin stelt ze in. Enkel deze bedragen vormen het kengetal "Betaalde vergoeding" wanneer je alleen een energielabel selecteert; de team- en verkoperbedragen eronder tellen daar niet in mee.')}
          {tabel('team', 'Verkoopteam', 'Bedragen die aan een verkoopteam zijn uitbetaald. Instelbaar door admin en door de master van het energielabel van dat team. Enkel deze bedragen vormen het kengetal "Betaalde vergoeding" bij een verkoopteam-selectie; de verkoperbedragen van het team tellen daar niet in mee en het team telt niet mee bij het energielabel erboven.')}
          {tabel('seller', 'Verkoper', 'Bedragen die aan één verkoper zijn uitbetaald. Instelbaar door admin, de master van het energielabel en de teamleader van het team van die verkoper. Enkel deze bedragen vormen het kengetal "Betaalde vergoeding" bij een verkoper-selectie. Een verkoper ziet enkel zijn eigen bedragen.')}
        </>
      );
    })()}
    </div>

    {/* ── Tab: Admin (enkel voor admin) ── */}
    <div className={revTab === 'admin' ? 'space-y-4' : 'hidden'}>
    {/* Customer Tags-config staat hier, direct boven het blok dat toont wat een tag
        met Fee Allowed = No kost. Voorheen een aparte pagina onder Instellingen. */}
    {isAdmin && <CustomerTagsConfigCard token={token} partnerTagAll={partnerTagAll} onPartnerTagsChanged={onPartnerTagsChanged} />}
    {isAdmin && <FeeMailRecipientsCard token={token} labels={apiLabelOptions} salesStructure={salesStructure} />}
    {isAdmin && <Card className="!p-4">
      <h3 className="text-base font-semibold text-slate-100 mb-1 flex items-center gap-1.5">
        WANBETALERS PER ENERGIELABEL
        <InfoTip title="Wanbetalers per energielabel" text={`Twee instellingen, allebei per energielabel te overschrijven.\n\nDrempel wanbetaler (dagen): hoelang een onbetaalde VERKOOPFACTUUR vervallen moet zijn voor de klant de status Wanbetaler krijgt. Standaard ${BAD_DEBT_OVERDUE_DAYS_DEFAULT} dagen, gerekend vanaf de vervaldatum. Enkel verkoopfacturen tellen mee — creditnota's en self bills niet.\n\nOninbaarheidsperiode (maanden): over hoeveel maanden ontvangen marge kan worden teruggevorderd bij een oninbare vordering. ${BAD_DEBT_RECOVERY_MONTHS_DEFAULT} = 100% terugvordering.\n\nDeze tabel toont alleen energielabels met een eigen instelling. Alle andere labels volgen de standaardwaarden; die hoeven hier niet in te staan. Verwijderen zet een label terug op de standaard.`} />
      </h3>
      <p className="text-xs text-slate-400 mb-3">
        Standaard: {BAD_DEBT_OVERDUE_DAYS_DEFAULT} dagen drempel en {BAD_DEBT_RECOVERY_MONTHS_DEFAULT} maanden oninbaarheidsperiode (volledige terugvordering).
        Alleen labels die daarvan afwijken staan hieronder.
      </p>
      {wbError && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm mb-3">{wbError}</div>}
      {wbSuccess && <div className="bg-lime-500/10 border border-lime-500/30 text-lime-300 px-3 py-2 rounded text-sm mb-3 flex items-center gap-2"><Check size={15} />{wbSuccess}</div>}

      {/* Toevoegen — zelfde vorm als de kiezer bij de betaalde partnervergoeding
          hieronder, zodat de twee beheerblokken op deze tab hetzelfde lezen. */}
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Energielabel toevoegen</label>
          <select value={wbNieuwLabel} onChange={e => setWbNieuwLabel(e.target.value)} className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200">
            <option value="">Kies energielabel…</option>
            {wbToeTeVoegenLabels.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="w-[150px]">
          <label className="block text-xs text-slate-400 mb-1">Drempel (dagen)</label>
          <input type="number" min="1" max="3650" step="1" value={wbNieuwDagen} onChange={e => setWbNieuwDagen(e.target.value)}
            className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200" />
        </div>
        <div className="w-[180px]">
          <label className="block text-xs text-slate-400 mb-1">Oninbaarheidsperiode (mnd)</label>
          <input type="number" min="0" max="9999" step="1" value={wbNieuwMaanden} onChange={e => setWbNieuwMaanden(e.target.value)}
            className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200" />
        </div>
        <button onClick={wbVoegToe} disabled={!wbNieuwLabel || wbBusy === wbNieuwLabel}
          className="flex items-center gap-2 px-4 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-slate-900 font-medium text-sm rounded-lg"><Plus size={16} />Toevoegen</button>
      </div>

      {wbLabels.length === 0 ? <div className="text-sm text-slate-500">Nog geen energielabel met een eigen instelling — alles volgt de standaardwaarden.</div> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <th className="py-2 px-3 text-left text-slate-400 text-xs">Energielabel</th>
            <th className="py-2 px-3 text-left text-slate-400 text-xs">Drempel (dagen)</th>
            <th className="py-2 px-3 text-left text-slate-400 text-xs">Oninbaarheidsperiode (mnd)</th>
            <th className="py-2 px-3 text-right text-slate-400 text-xs">Actie</th>
          </tr></thead>
          <tbody>
            {wbLabels.map(label => (
              <tr key={label} className="border-t border-slate-700/40">
                <td className="py-2 px-3 text-slate-200">{label}</td>
                <td className="py-2 px-3">
                  <input type="number" min="1" max="3650" step="1" value={wbDraftDagen(label)}
                    onChange={e => wbSetDraft(label, 'days', e.target.value)}
                    className="w-24 px-2 py-1 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500" />
                </td>
                <td className="py-2 px-3">
                  <input type="number" min="0" max="9999" step="1" value={wbDraftMaanden(label)}
                    onChange={e => wbSetDraft(label, 'months', e.target.value)}
                    className="w-24 px-2 py-1 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-lime-500" />
                </td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <button onClick={() => wbBewaar(label, Number(wbDraftMaanden(label)), Number(wbDraftDagen(label)))}
                    disabled={wbBusy === label || !wbDirty(label)}
                    className="px-3 py-1 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-xs transition-all disabled:opacity-40">
                    {wbBusy === label ? 'Bezig…' : 'Opslaan'}
                  </button>
                  <button onClick={() => wbVerwijder(label)} disabled={wbBusy === label}
                    title="Zet dit energielabel terug op de standaardwaarden"
                    className="ml-2 px-3 py-1 bg-slate-700 hover:bg-slate-600 text-red-300 rounded-lg text-xs transition-all disabled:opacity-40">
                    Verwijderen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
    </Card>}

    {/* ⛔ INTERN — enkel rol admin. Wat valt er weg door customer tags met Fee
        Allowed = No? Bewust ZONDER exportknop: deze cijfers mogen niet in een
        export, niet in de zip van "Export All" en niet in het PDF-rapport. */}
    {isAdmin && <Card className="!p-4">
      <h3 className="text-base font-semibold text-slate-100 mb-1 flex items-center gap-1.5">
        GEBLOKKEERD DOOR CUSTOMER TAG
        <InfoTip title="Geblokkeerd door Customer Tag" text={'Alleen admin, intern. Klanten met een customer tag die op Fee Allowed = No staat vallen volledig uit de samenvattingsblokken — kWh, Factor Fee, Volume Fee én abonnementsvergoeding. Deze tabel toont wat dat is, per blokkerende tag en gesplitst op wanbetaler-status. De abonnementsvergoeding is hier berekend alsóf de tag niet blokkeerde; dat is immers het bedrag dat wegvalt.\n\nEen klant met twee blokkerende tags telt in beide tagrijen mee; de totaalrij telt elke klant één keer, dus die is niet de som van de rijen erboven. Volgt de filters energielabel/verkoopteam/verkoper. Komt bewust niet in exports of het PDF-rapport.'} />
      </h3>
      <p className="text-xs text-slate-400 mb-3">Vergoeding die wegvalt doordat de klant een tag draagt met Fee Allowed = No.</p>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <StatTile label="Totaal geblokkeerde vergoeding" value={formatCurrencyRounded(feeGeblokkeerd.kengetal)} accent="text-amber-300" dot={STATUS_ACCENT.deels} sub={`${formatNumberRounded(feeGeblokkeerd.totalen.aantalKlanten || 0)} klanten · ${feeGeblokkeerd.tags.length} tag(s)`} minW={260} />
      </div>
      {feeGeblokkeerd.rijen.length === 0 ? (
        <TableEmpty message="Geen klanten geblokkeerd door een customer tag binnen deze filters." icon={Folder} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead><tr className="border-b border-slate-700">
              <th className="py-2 px-3 text-left text-slate-400 text-xs">Customer Tag</th>
              <th className="py-2 px-3 text-left text-slate-400 text-xs">Status</th>
              <th className="py-2 px-3 text-right text-slate-400 text-xs">Klanten</th>
              <th className="py-2 px-3 text-right text-slate-400 text-xs">Invoiced MWh</th>
              <th className="py-2 px-3 text-right text-slate-400 text-xs">Factor Fee</th>
              <th className="py-2 px-3 text-right text-slate-400 text-xs">Volume Fee</th>
              <th className="py-2 px-3 text-right text-slate-400 text-xs">Subscription Fee</th>
              <th className="py-2 px-3 text-right text-slate-400 text-xs">Totale Fee</th>
            </tr></thead>
            <tbody>
              {feeGeblokkeerd.rijen.map(r => (
                <tr key={`${r.tag}|${r.status}`} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="py-2 px-3 text-slate-200 text-xs" data-label="Customer Tag">{r.tag}</td>
                  <td className="py-2 px-3 text-xs" data-label="Status">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${r.status === 'Wanbetaler' ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{r.status}</span>
                  </td>
                  <td className="py-2 px-3 text-slate-400 text-xs text-right tabular-nums" data-label="Klanten">{formatNumberRounded(r.aantalKlanten)}</td>
                  <td className="py-2 px-3 text-slate-300 text-xs text-right tabular-nums" data-label="Invoiced MWh">{formatMwhFromKwh(r.kwh)}</td>
                  <td className="py-2 px-3 text-slate-300 text-xs text-right tabular-nums" data-label="Factor Fee">{formatCurrencyRounded(r.factorFee)}</td>
                  <td className="py-2 px-3 text-slate-300 text-xs text-right tabular-nums" data-label="Volume Fee">{formatCurrencyRounded(r.volumeFee)}</td>
                  <td className="py-2 px-3 text-cyan-400 text-xs text-right tabular-nums" data-label="Subscription Fee">{formatCurrencyRounded(r.subscriptionFee)}</td>
                  <td className="py-2 px-3 text-amber-300 text-xs text-right tabular-nums font-medium" data-label="Totale Fee">{formatCurrencyRounded(r.totalFee)}</td>
                </tr>
              ))}
              {/* Tussenrij per status — elke klant één keer, dus optelbaar. */}
              {(feeGeblokkeerd.perStatus || []).map(r => (
                <tr key={`totaal-${r.status}`} className="border-t border-slate-600/60 bg-slate-800/40">
                  <td className="py-2 px-3 text-slate-400 text-xs italic" data-label="Customer Tag">Alle tags samen</td>
                  <td className="py-2 px-3 text-slate-300 text-xs font-medium" data-label="Status">{r.status}</td>
                  <td className="py-2 px-3 text-slate-300 text-xs text-right tabular-nums" data-label="Klanten">{formatNumberRounded(r.aantalKlanten)}</td>
                  <td className="py-2 px-3 text-slate-200 text-xs text-right tabular-nums" data-label="Invoiced MWh">{formatMwhFromKwh(r.kwh)}</td>
                  <td className="py-2 px-3 text-slate-200 text-xs text-right tabular-nums" data-label="Factor Fee">{formatCurrencyRounded(r.factorFee)}</td>
                  <td className="py-2 px-3 text-slate-200 text-xs text-right tabular-nums" data-label="Volume Fee">{formatCurrencyRounded(r.volumeFee)}</td>
                  <td className="py-2 px-3 text-cyan-300 text-xs text-right tabular-nums" data-label="Subscription Fee">{formatCurrencyRounded(r.subscriptionFee)}</td>
                  <td className="py-2 px-3 text-amber-200 text-xs text-right tabular-nums font-semibold" data-label="Totale Fee">{formatCurrencyRounded(r.totalFee)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-500/60 bg-slate-800/60">
                <td className="py-2 px-3 text-slate-100 text-xs font-semibold" colSpan={2} data-label="Totaal">Totaal (elke klant één keer)</td>
                <td className="py-2 px-3 text-slate-100 text-xs text-right font-semibold tabular-nums" data-label="Klanten">{formatNumberRounded(feeGeblokkeerd.totalen.aantalKlanten)}</td>
                <td className="py-2 px-3 text-slate-100 text-xs text-right font-semibold tabular-nums" data-label="Invoiced MWh">{formatMwhFromKwh(feeGeblokkeerd.totalen.kwh)}</td>
                <td className="py-2 px-3 text-slate-100 text-xs text-right font-semibold tabular-nums" data-label="Factor Fee">{formatCurrencyRounded(feeGeblokkeerd.totalen.factorFee)}</td>
                <td className="py-2 px-3 text-slate-100 text-xs text-right font-semibold tabular-nums" data-label="Volume Fee">{formatCurrencyRounded(feeGeblokkeerd.totalen.volumeFee)}</td>
                <td className="py-2 px-3 text-cyan-300 text-xs text-right font-semibold tabular-nums" data-label="Subscription Fee">{formatCurrencyRounded(feeGeblokkeerd.totalen.subscriptionFee)}</td>
                <td className="py-2 px-3 text-amber-200 text-xs text-right font-semibold tabular-nums" data-label="Totale Fee">{formatCurrencyRounded(feeGeblokkeerd.totalen.totalFee)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-2">Intern cijfer — komt bewust niet in exports of het PDF-rapport.</p>
    </Card>}

    {/* Waarschuwingstabel (admin + ET Dossierbeheerder): gefactureerde GSRN's die
        niet in de DETAIL ORDERS-tabel voorkomen → mogelijke commissie-leak. */}
    <MissingCommissionGsrnTable orders={filteredOrders} facturen={facturen} onOpenCustomer360={onOpenCustomer360} currentUser={currentUser} />
    </div>

    {c360Menu}
  </div>;
};

// Ouderdomsanalyse: de openstaande facturen in vaste vervaldag-emmers. Staat op
// module-niveau omdat zowel de pagina Aging als het klantdossier (Customer 360 →
// Facturen) dezelfde tabel toont — één berekening, dus de cijfers kunnen
// niet uit elkaar lopen.
//
// Gerekend wordt met het RESTBEDRAG (invOutstandingOrTotal, incl. btw), niet met
// het factuurtotaal: een deels betaalde factuur is nog maar voor haar restbedrag
// een vordering. Lege emmers vallen weg.
const AGING_BUCKETS = [
  { key: 'nietVervallen', label: '< 0 dagen', color: 'bg-slate-500', minDays: -Infinity },
  { key: 'd1_30', label: '1-30 dagen', color: 'bg-lime-500', minDays: 0 },
  { key: 'd31_60', label: '31-60 dagen', color: 'bg-blue-500', minDays: 30 },
  { key: 'd61_90', label: '61-90 dagen', color: 'bg-yellow-500', minDays: 60 },
  { key: 'd91_120', label: '91-120 dagen', color: 'bg-orange-500', minDays: 90 },
  { key: 'd121_150', label: '121-150 dagen', color: 'bg-orange-600', minDays: 120 },
  { key: 'd151_180', label: '151-180 dagen', color: 'bg-red-500', minDays: 150 },
  { key: 'd181_365', label: '180-365 dagen', color: 'bg-red-600', minDays: 180 },
  { key: 'd1_2jaar', label: '1-2 jaar', color: 'bg-red-700', minDays: 365 },
  { key: 'd2_5jaar', label: '2-5 jaar', color: 'bg-red-800', minDays: 730 },
  { key: 'ouder5jaar', label: '> 5 jaar', color: 'bg-red-900', minDays: 1825 },
];

// `openInvoices` = facturen waarop nog iets openstaat. Elke factuur draagt een
// `agingCategory`; ontbreekt die, dan leiden we ze af uit daysOverdue.
// Cumulatief = deze emmer plus alle OUDERE emmers, behalve bij "< 0 dagen" — dat
// is de niet-vervallen rest en die toont het totaal van alles.
function berekenAgingBuckets(openInvoices) {
  const cats = new Map(AGING_BUCKETS.map(b => [b.key, { ...b, total: 0, count: 0, thresholdDays: b.minDays }]));
  for (const inv of openInvoices || []) {
    const key = inv.agingCategory || getAgingCategory(Number(inv.daysOverdue) || 0);
    const c = cats.get(key);
    if (!c) continue;
    c.total += invOutstandingOrTotal(inv);
    c.count += 1;
  }
  const result = [...cats.values()].filter(c => c.count > 0);
  const grandTotal = result.reduce((s, c) => s + c.total, 0);
  const grandCount = result.reduce((s, c) => s + c.count, 0);
  for (const cat of result) {
    if (cat.minDays === -Infinity) { cat.cumulative = grandTotal; cat.cumulativeCount = grandCount; }
    else {
      cat.cumulative = result.filter(c => c.minDays >= cat.minDays).reduce((s, c) => s + c.total, 0);
      cat.cumulativeCount = result.filter(c => c.minDays >= cat.minDays).reduce((s, c) => s + c.count, 0);
    }
  }
  return result;
}

// De tabel zelf, gedeeld tussen Aging en Customer 360. `onPick` maakt de rijen
// klikbaar (Aging filtert daarmee zijn factuurtabel); zonder die callback is de
// tabel puur informatief, zoals in het klantdossier.
const AgingBucketTable = ({ categories, actief = 'all', onPick = null, balk = true }) => {
  const totaal = categories.reduce((s, c) => s + c.total, 0);
  const aantal = categories.reduce((s, c) => s + c.count, 0);
  return (
    <>
      {/* Mobiel kaarten (resp-card); daarboven scrollt de tabel binnen haar eigen
          wrapper i.p.v. de pagina te verbreden. */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700">
            <th className="py-2 px-2 text-left text-slate-400 text-xs">Periode</th>
            <th className="py-2 px-2 text-center text-slate-400 text-xs">Aantal</th>
            {/* Bedragen zijn restbedragen, geen factuurtotalen. */}
            <th className="py-2 px-2 text-right text-slate-400 text-xs" title="Nog openstaand bedrag (incl. btw) van de facturen in deze periode">Openstaand</th>
            <th className="py-2 px-2 text-right text-slate-400 text-xs" title="Openstaand van deze periode en alle oudere periodes samen">Cumulatief</th>
          </tr></thead>
          <tbody>{categories.map((cat) => (
            <tr key={cat.key} onClick={onPick ? () => onPick(actief === cat.key ? 'all' : cat.key) : undefined}
              className={`border-b border-slate-700/30 ${onPick ? 'cursor-pointer hover:bg-slate-700/30' : ''} ${actief === cat.key ? 'bg-lime-500/10' : ''}`}>
              <td className="py-2 px-2" data-label="Periode"><div className="flex items-center gap-2"><div className={`w-3 h-3 rounded-full shrink-0 ${cat.color}`} /><span className="text-slate-200">{cat.label}</span></div></td>
              <td className="py-2 px-2 text-center text-slate-400" data-label="Aantal">{cat.count}</td>
              <td className="py-2 px-2 text-right text-slate-200 font-medium" data-label="Openstaand">{formatEuroRounded(cat.total)}</td>
              <td className="py-2 px-2 text-right text-red-400 font-medium" data-label="Cumulatief">{formatEuroRounded(cat.cumulative)}</td>
            </tr>
          ))}</tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-600 bg-lime-900/20">
              <td className="py-2 px-2 text-lime-400 font-semibold" colSpan="2">Totaal Openstaand</td>
              <td className="py-2 px-2 text-right text-lime-400 font-bold" colSpan="2">{formatEuroRounded(totaal)} <span className="text-slate-500 font-normal">({aantal})</span></td>
            </tr>
          </tfoot>
        </table>
      </div>
      {balk && totaal > 0 && (
        <div className="mt-4"><div className="flex h-3 rounded-full overflow-hidden">
          {categories.map(c => <div key={c.key} className={c.color} style={{ width: `${(c.total / totaal) * 100}%` }} title={`${c.label}: ${formatEuroRounded(c.total)}`} />)}
        </div></div>
      )}
    </>
  );
};

const BillingPage = ({ facturen, loading = false, onOpenCustomer360, onSettings, salesStructure = EMPTY_STRUCTURE, currentUser, labelConfig = {} }) => {
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [searchTerm, setSearchTerm] = useState('');
  // Default-selectie op de scope van de ingelogde gebruiker: master ziet meteen
  // zijn energielabel voorgeselecteerd, teamleader óók zijn team, reseller óók
  // zijn verkoper. Admin/ET = 'all'.
  const scopeDefaults = defaultScopeSelection(currentUser);
  const [companyFilter, setCompanyFilter] = useState(scopeDefaults.company);
  const [sellerFilter, setSellerFilter] = useState(scopeDefaults.seller);
  const [verkoopteamFilter, setVerkoopteamFilter] = useState(scopeDefaults.team);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agingFilter, setAgingFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'daysOverdue', dir: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = TABLE_PAGE_SIZE;
  // Herhaal de scope-default wanneer de gebruiker wisselt (bv. impersonate) en de
  // scope-velden uit /api/auth/me alsnog binnenkomen. Reageert enkel op de
  // scope-scalairen, dus handmatige filterwijzigingen blijven behouden.
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setCompanyFilter(d.company);
    setVerkoopteamFilter(d.team);
    setSellerFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  useFilterPersistence('billing.filters', currentUser,
    { searchTerm, companyFilter, sellerFilter, verkoopteamFilter, customerFilter, statusFilter, agingFilter, sortConfig },
    (s) => { setSearchTerm(s.searchTerm ?? ''); setCompanyFilter(s.companyFilter ?? 'all'); setSellerFilter(s.sellerFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setCustomerFilter(s.customerFilter ?? 'all'); setStatusFilter(s.statusFilter ?? 'all'); setAgingFilter(s.agingFilter ?? 'all'); if (s.sortConfig && s.sortConfig.key) setSortConfig(s.sortConfig); });
  // Safety-net: reset paginatie wanneer welk filter dan ook wijzigt. Sommige
  // filter-setters doen het al inline, deze useEffect dekt de rest.
  useEffect(() => { setCurrentPage(1); }, [searchTerm, companyFilter, sellerFilter, verkoopteamFilter, customerFilter, statusFilter, agingFilter]);

  // Filter facturen where statusBetaling is NOT "betaald" and NOT "omgekeerd", then map to invoice format
  const invoices = useMemo(() => {
    return facturen
      .filter(f => {
        const status = (f.statusBetaling || '').toLowerCase();
        return status !== 'betaald' && status !== 'omgekeerd';
      })
      .map((f, idx) => {
        const dueDate = f.vervaldatum || '';
        const daysOverdue = getDaysOverdue(dueDate);
        return {
          id: `inv-${idx}`,
          etInvoiceId: f.id,   // originele factuur-id (fact-/credit-) voor de PDF-download
          relatieBedrijf: f.relatieBedrijf || '',
          verkoper: f.verkoper || '',
          verkoopteam: f.verkoopteam || '',
          relatieReferentie: f.relatieReferentie || '',
          isEenBedrijf: f.isCompany || '',
          elContractNaam: f.elContract || '',
          ngContractNaam: f.ngContract || '',
          invoiceNumber: f.nummer || '',
          factuurdatum: f.factuurdatum || '',
          customerName: f.relatieNaam || '',
          soort: f.soort || '',
          dueDate,
          daysOverdue,
          agingCategory: getAgingCategory(daysOverdue),
          paymentStatus: f.statusBetaling || '',
          elOppStage: f.elOpportunityStage || '',
          elDeliveryStart: f.elDeliveryStart || '',
          elGSRN: f.elGSRN || '',
          elMeterMethod: f.elMeterMethod || '',
          elCPStatus: f.elCPStatus || '',
          elProductGroep: f.elProductGroep || '',
          elRatecard: f.elRatecard || '',
          elSegment: f.elSegment || '',
          elPriceAdditions: f.elPriceAdditions || 0,
          elKwhOfftake: f.elKwhOfftake || 0,
          elAmountOfftake: f.elAmountOfftake || 0,
          elCoeffOfftake: f.elCoeffOfftake || '',
          elTypeOfftake: f.elTypeOfftake || '',
          elMajOfftake: f.elMajorationOfftake || 0,
          elKwhInjection: f.elKwhInjection || 0,
          elAmountInjection: f.elAmountInjection || 0,
          elCoeffInjection: f.elCoeffInjection || '',
          elTypeInjection: f.elTypeInjection || '',
          elMajInjection: f.elMajorationInjection || 0,
          elKwhImbalance: f.elKwhImbalance || 0,
          elAmountImbalance: f.elAmountImbalance || 0,
          ngOppStage: f.ngOpportunityStage || '',
          ngDeliveryStart: f.ngDeliveryStart || '',
          ngGSRN: f.ngGSRN || '',
          ngCPStatus: f.ngCPStatus || '',
          ngProductGroep: f.ngProductGroep || '',
          ngRatecard: f.ngRatecard || '',
          ngSegment: f.ngSegment || '',
          ngPriceAdditions: f.ngPriceAdditions || 0,
          ngKwhOfftake: f.ngKwhOfftake || 0,
          ngAmountOfftake: f.ngAmountOfftake || 0,
          ngCoeffOfftake: f.ngCoeffOfftake || '',
          ngTypeOfftake: f.ngTypeOfftake || '',
          ngMajOfftake: f.ngMajorationOfftake || 0,
          totalExclBTW: f.totaalExclBTW || 0,
          total: f.totaal || 0,
          openstaand: f.openstaand,   // restbedrag uit de API; zie invOutstanding
          seller: f.verkoper || '',
          company: f.relatieBedrijf || ''
        };
      });
  }, [facturen]);
  
  // Cascade-opties (energielabel → verkoopteam → verkoper) uit de centrale
  // sales-structuur, doorsneden met wat in deze facturen voorkomt. Identiek in
  // alle modules, rol-gescopet door de backend.
  const invoiceLabels = useMemo(() => invoices.map(i => i.relatieBedrijf).filter(Boolean), [invoices]);
  const invoiceTeams = useMemo(() => invoices.map(i => i.verkoopteam).filter(Boolean), [invoices]);
  const invoiceSellers = useMemo(() => invoices.map(i => i.verkoper).filter(Boolean), [invoices]);
  const { companies, teams: verkoopteams, sellers } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter, { labels: invoiceLabels, teams: invoiceTeams, sellers: invoiceSellers }),
    [salesStructure, companyFilter, verkoopteamFilter, invoiceLabels, invoiceTeams, invoiceSellers]
  );
  const customers = useMemo(() => { const filtered = companyFilter !== 'all' ? invoices.filter(i => i.relatieBedrijf === companyFilter) : invoices; return [...new Set(filtered.map(i => i.customerName).filter(Boolean))].sort(); }, [invoices, companyFilter]);
  const statuses = useMemo(() => [...new Set(invoices.map(i => i.paymentStatus).filter(Boolean))], [invoices]);
  
  const handleCompanyChange = (value) => { setCompanyFilter(value); setSellerFilter('all'); setCustomerFilter('all'); setCurrentPage(1); };
  
  const baseFilteredInvoices = useMemo(() => {
    let data = invoices;
    if (searchTerm) { const term = searchTerm.toLowerCase(); data = data.filter(i => i.customerName?.toLowerCase().includes(term) || i.invoiceNumber?.toLowerCase().includes(term) || i.verkoper?.toLowerCase().includes(term) || i.relatieReferentie?.toLowerCase().includes(term) || i.relatieBedrijf?.toLowerCase().includes(term) || i.verkoopteam?.toLowerCase().includes(term)); }
    if (companyFilter !== 'all') data = data.filter(i => i.relatieBedrijf === companyFilter);
    if (sellerFilter !== 'all') data = data.filter(i => i.verkoper === sellerFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(i => i.verkoopteam === verkoopteamFilter);
    if (customerFilter !== 'all') data = data.filter(i => i.customerName === customerFilter);
    if (statusFilter !== 'all') data = data.filter(i => i.paymentStatus === statusFilter);
    return data;
  }, [invoices, searchTerm, companyFilter, sellerFilter, verkoopteamFilter, customerFilter, statusFilter]);
  
  const filteredInvoices = useMemo(() => {
    let data = baseFilteredInvoices;
    if (agingFilter !== 'all') data = data.filter(i => i.agingCategory === agingFilter);
    // 'openstaand' loopt via invOutstanding (API-restbedrag, afleiding als
    // vangnet); onbekend → -1, dus onderaan bij aflopend sorteren.
    data = [...data].sort((a, b) => { const lees = (r) => sortConfig.key === 'openstaand' ? (invOutstanding(r) ?? -1) : r[sortConfig.key]; const av = lees(a), bv = lees(b); if (typeof av === 'number') return sortConfig.dir === 'asc' ? av - bv : bv - av; return sortConfig.dir === 'asc' ? String(av || '').localeCompare(String(bv || '')) : String(bv || '').localeCompare(String(av || '')); });
    return data;
  }, [baseFilteredInvoices, agingFilter, sortConfig]);
  
  const totalPages = Math.ceil(filteredInvoices.length / pageSize);
  const paginatedInvoices = useMemo(() => { const start = (currentPage - 1) * pageSize; return filteredInvoices.slice(start, start + pageSize); }, [filteredInvoices, currentPage]);
  
  const handleSort = (key) => { setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' })); setCurrentPage(1); };
  const SortIcon = ({ col }) => sortConfig.key !== col ? <ChevronDown size={14} className="opacity-30" /> : (sortConfig.dir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />);
  
  // Deze pagina rekent met wat er nog OPENSTAAT (restbedrag uit de facturatie-API,
  // incl. btw), niet met het volledige factuurbedrag: een deels betaalde factuur is
  // maar voor haar restbedrag nog een vordering.
  //
  // Daardoor is "openstaand" ook rúimer dan de oude test op de status "niet
  // betaald": een DEELS betaalde factuur viel toen volledig buiten de KPI's, de
  // buckets en de verkoperslijst, terwijl ze wél in de tabel stond. Nu telt elke
  // factuur mee waarvoor nog een bedrag openstaat, voor precies dat bedrag.
  const openInvoices = useMemo(() => baseFilteredInvoices.filter(i => invOutstandingOrTotal(i) !== 0), [baseFilteredInvoices]);
  const topSellers = useMemo(() => { const g = {}; openInvoices.forEach(i => { const s = i.verkoper || 'Onbekend'; if (!g[s]) g[s] = { seller: s, count: 0, total: 0 }; g[s].count++; g[s].total += invOutstandingOrTotal(i); }); return Object.values(g).sort((a, b) => b.total - a.total).slice(0, 8); }, [openInvoices]);
  
  const agingCategories = useMemo(() => berekenAgingBuckets(openInvoices), [openInvoices]);
  
  const totalFiltered = useMemo(() => baseFilteredInvoices.reduce((s, i) => s + invOutstandingOrTotal(i), 0), [baseFilteredInvoices]);
  const totalOpen = useMemo(() => openInvoices.reduce((s, i) => s + invOutstandingOrTotal(i), 0), [openInvoices]);
  
  const getAgingBadgeColor = (days) => { if (days < 0) return 'bg-slate-700 text-slate-300'; if (days <= 30) return 'bg-lime-900/50 text-lime-300'; if (days <= 60) return 'bg-blue-900/50 text-blue-300'; if (days <= 90) return 'bg-yellow-900/50 text-yellow-300'; if (days <= 120) return 'bg-orange-900/50 text-orange-300'; if (days <= 180) return 'bg-red-900/50 text-red-300'; return 'bg-red-900 text-red-100'; };
  const clearFilters = () => { setSearchTerm(''); setCompanyFilter('all'); setSellerFilter('all'); setVerkoopteamFilter('all'); setCustomerFilter('all'); setStatusFilter('all'); setAgingFilter('all'); setCurrentPage(1); };
  const hasActiveFilters = searchTerm || companyFilter !== 'all' || sellerFilter !== 'all' || verkoopteamFilter !== 'all' || customerFilter !== 'all' || statusFilter !== 'all';
  const hasAgingFilter = agingFilter !== 'all';
  
  if (!facturen.length && loading) return <PageLoading message="Facturen worden geladen…" />;
  if (!facturen.length) return <EmptyState message="Nog geen facturen beschikbaar — data komt uit de API." onSettings={onSettings} />;
  
  return <div className="space-y-6">
    <PageHeader title="Aging" subtitle="Debiteurenbeheer en ouderdomsanalyse" />
    <Card className="!p-4"><CollapsibleFilters active={hasActiveFilters}><div className="flex flex-wrap items-end gap-3"><div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Energieleverancier</label><select value={companyFilter} onChange={e => handleCompanyChange(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${companyFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={companies}>Alle</AllOption>{companies.map(c => <option key={c} value={c}>{c}</option>)}</select></div><div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Verkoopteam</label><select value={verkoopteamFilter} onChange={e => { setVerkoopteamFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={verkoopteams}>Alle</AllOption>{verkoopteams.map(t => <option key={t} value={t}>{t}</option>)}</select></div><div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Verkoper</label><select value={sellerFilter} onChange={e => { setSellerFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${sellerFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={sellers}>Alle</AllOption>{sellers.map(s => <option key={s} value={s}>{s}</option>)}</select></div><div className="w-full sm:w-[280px] shrink-0"><label className="block text-xs text-emerald-400 mb-1">🔍 Zoeken</label><div className="relative"><Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-emerald-500" /><input type="text" placeholder="Klant, factuur, verkoper..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="w-full pl-7 pr-3 py-1.5 bg-emerald-900/30 border border-emerald-600/50 rounded text-xs text-slate-200 placeholder-slate-400 focus:border-emerald-500 focus:outline-none" /></div></div><div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Klant</label><select value={customerFilter} onChange={e => { setCustomerFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${customerFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option>{customers.map(c => <option key={c} value={c}>{c}</option>)}</select></div><div className="w-[132px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Status</label><select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${statusFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</select></div>{hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}</div></CollapsibleFilters>{hasActiveFilters && <div className="mt-3 p-3 bg-lime-900/20 border border-lime-700/50 rounded-lg flex items-center justify-between"><span className="text-lime-400 text-sm">Gefilterd: {baseFilteredInvoices.length} van {invoices.length} facturen</span><span className="text-lime-400 font-bold" title="Nog openstaand bedrag (incl. btw) van de gefilterde facturen">{formatCurrency(totalFiltered)}</span></div>}</Card>
    <div className="flex flex-wrap gap-4"><div className="flex-1 min-w-[140px] bg-slate-800/50 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Totaal Facturen</div><div className="text-2xl font-bold text-slate-100"><Kengetal>{baseFilteredInvoices.length}</Kengetal></div></div><div className="flex-1 min-w-[140px] bg-slate-800/50 border border-slate-700/50 rounded-xl p-4" title="Facturen waarvoor nog een bedrag openstaat — een deels betaalde factuur telt dus mee."><div className="text-xs text-slate-500 mb-1">Openstaand</div><div className="text-2xl font-bold text-red-400"><Kengetal>{openInvoices.length}</Kengetal></div></div><div className="flex-1 min-w-[140px] bg-slate-800/50 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Gem. Dagen Achterstallig</div><div className="text-2xl font-bold text-lime-400"><Kengetal>{openInvoices.length > 0 ? Math.round(openInvoices.reduce((s, i) => s + Math.max(0, i.daysOverdue), 0) / openInvoices.length) : 0}</Kengetal></div></div><div className="flex-1 min-w-[140px] bg-lime-900/30 border border-lime-700/50 rounded-xl p-4" title="Som van de nog openstaande bedragen (incl. btw), niet van de factuurtotalen."><div className="text-xs text-lime-400 mb-1">Totaal Openstaand</div><div className="text-2xl font-bold text-lime-400"><Kengetal>{formatEuroRounded(totalOpen)}</Kengetal></div></div></div>
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <Card className="xl:col-span-2"><h3 className="text-lg font-semibold text-slate-100 mb-4">Ouderdomsanalyse</h3>
        {/* Zelfde tabel als in Customer 360 → Facturen (AgingBucketTable).
            Hier zijn de rijen klikbaar: ze filteren de factuurtabel eronder. */}
        <AgingBucketTable categories={agingCategories} actief={agingFilter}
          onPick={(k) => { setAgingFilter(k); setCurrentPage(1); }} />
      </Card>
      <Card><h3 className="text-lg font-semibold text-slate-100 mb-4">Top 8 Verkopers</h3><div className="space-y-2">{topSellers.length > 0 ? topSellers.map((s, idx) => <div key={s.seller} onClick={() => { setSellerFilter(sellerFilter === s.seller ? 'all' : s.seller); setCurrentPage(1); }} className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${sellerFilter === s.seller ? 'bg-lime-500/20 border border-lime-500/50' : 'bg-slate-900/50 hover:bg-slate-900'}`}><div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0"><div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx < 3 ? 'bg-lime-500 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>{idx + 1}</div><div><div className="text-slate-200 text-sm font-medium truncate max-w-24">{s.seller}</div><div className="text-slate-500 text-xs">{s.count} fact.</div></div></div><span className="text-lime-400 font-semibold text-sm">{formatCurrency(s.total)}</span></div>) : <div className="text-center py-4 text-slate-500">Geen data</div>}</div></Card>
    </div>
    <Card><div className="flex flex-wrap items-center justify-between gap-4 mb-4"><div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0"><div className="flex items-center gap-2 min-w-0"><h3 className="text-lg font-semibold text-slate-100">Alle Onbetaalde Facturen ({filteredInvoices.length})</h3><ExportButton onExport={() => exportToCSV(filteredInvoices.map(i => ({ ...i, openstaandInclBtw: invOutstanding(i) })), 'gefilterde-facturen')} label="Exporteer de gefilterde facturen naar Excel" /></div><select value={agingFilter} onChange={e => { setAgingFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200"><option value="all">Alle Leeftijden</option><option value="nietVervallen">&lt; 0 dagen</option><option value="d1_30">1-30 dagen</option><option value="d31_60">31-60 dagen</option><option value="d61_90">61-90 dagen</option><option value="d91_120">91-120 dagen</option><option value="d121_150">121-150 dagen</option><option value="d151_180">151-180 dagen</option><option value="d181_365">180-365 dagen</option><option value="d1_2jaar">1-2 jaar</option><option value="d2_5jaar">2-5 jaar</option><option value="ouder5jaar">&gt; 5 jaar</option></select>{hasAgingFilter && <button onClick={() => { setAgingFilter('all'); setCurrentPage(1); }} className="text-xs text-red-400 hover:text-red-300">× Reset aging</button>}</div></div><div className="md:hidden space-y-2">{paginatedInvoices.map(inv => { const canOpen = onOpenCustomer360 && inv.customerName; return (<button key={inv.id} onClick={canOpen ? () => onOpenCustomer360(klantTarget(inv.customerName, inv.relatieReferentie)) : undefined} className={`w-full text-left rounded-xl border border-slate-700/50 bg-slate-800 p-3 transition-colors ${canOpen ? 'hover:bg-slate-700' : 'cursor-default'}`}><div className="flex items-start justify-between gap-2"><div className="min-w-0"><div className="font-semibold text-slate-100 truncate">{inv.customerName || inv.relatieBedrijf || '—'}</div><div className="text-xs text-slate-400 truncate">{[inv.invoiceNumber, inv.dueDate].filter(Boolean).join(' · ') || '—'}</div></div><span className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${getAgingBadgeColor(inv.daysOverdue)}`}>{inv.daysOverdue < 0 ? `${Math.abs(inv.daysOverdue)}d` : `${inv.daysOverdue}d`}</span></div><div className="mt-2 flex items-center justify-between gap-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium ${inv.paymentStatus?.toLowerCase().includes('niet betaald') ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{inv.paymentStatus || '—'}</span><span className="text-sm font-semibold text-lime-400 tabular-nums whitespace-nowrap" title="Nog openstaand (incl. btw)">{formatCurrency(invOutstandingOrTotal(inv))}</span></div></button>); })}</div><div className="overflow-auto hidden md:block md:max-h-[70vh]"><table className="w-full text-sm resp-card"><thead className="sticky top-0 bg-slate-800 z-10"><tr className="border-b border-slate-700">
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('relatieBedrijf')}><span className="flex items-center gap-1">Relatie/Bedrijf <SortIcon col="relatieBedrijf" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('verkoper')}><span className="flex items-center gap-1">Verkoper <SortIcon col="verkoper" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('verkoopteam')}><span className="flex items-center gap-1">Verkoopteam <SortIcon col="verkoopteam" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('invoiceNumber')}><span className="flex items-center gap-1">Factuurnummer <SortIcon col="invoiceNumber" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('relatieReferentie')}><span className="flex items-center gap-1">Referentie <SortIcon col="relatieReferentie" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('customerName')}><span className="flex items-center gap-1">Klant <SortIcon col="customerName" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('factuurdatum')}><span className="flex items-center gap-1">Factuurdatum <SortIcon col="factuurdatum" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Customer Payment Status</th>
      <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Soort</th>
      <th className="py-3 px-2 text-left text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('dueDate')}><span className="flex items-center gap-1">Vervaldatum <SortIcon col="dueDate" /></span></th>
      <th className="py-3 px-2 text-left text-slate-400 whitespace-nowrap text-xs">Status</th>
      <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('totalExclBTW')}><span className="flex items-center justify-end gap-1">Totaal (Excl. BTW) <SortIcon col="totalExclBTW" /></span></th>
      <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('total')}><span className="flex items-center justify-end gap-1">Totaal <SortIcon col="total" /></span></th>
      {/* Restbedrag uit de facturatie-API (zie invOutstanding). Incl. btw — dat is
          wat de klant nog verschuldigd is; de btw-basis staat daarom in de kop. */}
      <th className="py-3 px-2 text-right text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('openstaand')} title="Nog te betalen bedrag zoals de facturatie-API het meldt. Incl. btw, want dat is wat de klant verschuldigd is.">
        <span className="flex items-center justify-end gap-1">Openstaand (incl. BTW) <SortIcon col="openstaand" /></span>
      </th>
      {/* Aging blijft staan: het is de kernkolom van deze module. */}
      <th className="py-3 px-2 text-center text-slate-400 cursor-pointer whitespace-nowrap text-xs" onClick={() => handleSort('daysOverdue')}><span className="flex items-center justify-center gap-1">Aging <SortIcon col="daysOverdue" /></span></th>
    </tr></thead><tbody>{paginatedInvoices.map(inv => <tr key={inv.id} onContextMenu={e => onC360Context(e, inv.customerName, klantTarget(inv.customerName, inv.relatieReferentie), { id: inv.etInvoiceId, label: inv.invoiceNumber })} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-context-menu">
      <td className="py-2 px-2 text-slate-300 text-xs truncate max-w-24">{inv.relatieBedrijf}</td>
      <td className="py-2 px-2 text-slate-300 text-xs truncate max-w-24">{inv.verkoper}</td>
      <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-24">{inv.verkoopteam}</td>
      <td className="py-2 px-2 text-slate-200 text-xs font-mono whitespace-nowrap">{inv.invoiceNumber}</td>
      <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-24">{inv.relatieReferentie}</td>
      <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-32" title={inv.customerName}>{inv.customerName}</td>
      <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{inv.factuurdatum}</td>
      <td className="py-2 px-2">{(() => { const isWanbetaler = isRecordBadDebtOverdue(inv, labelConfig); return <span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${isWanbetaler ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{isWanbetaler ? 'Wanbetaler' : 'Goed'}</span>; })()}</td>
      <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-20">{inv.soort}</td>
      <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{inv.dueDate}</td>
      <td className="py-2 px-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium ${inv.paymentStatus?.toLowerCase().includes('niet betaald') ? 'bg-red-900/50 text-red-300' : 'bg-lime-900/50 text-lime-300'}`}>{inv.paymentStatus}</span></td>
      <td className="py-2 px-2 text-right text-slate-300 text-xs">{formatCurrency(inv.totalExclBTW)}</td>
      <td className="py-2 px-2 text-right font-medium text-lime-400 text-xs">{formatCurrency(inv.total)}</td>
      <td className="py-2 px-2 text-right text-xs whitespace-nowrap">{(() => {
        const open = invOutstanding(inv);
        if (open == null) return <span className="text-slate-500 cursor-help" title="Het restbedrag van deze factuur is nog niet opgehaald bij de facturatie-API — hoeveel er nog openstaat is dus niet bekend.">?</span>;
        return <span className={open > 0 ? 'text-amber-300 font-medium' : 'text-slate-500'}>{formatCurrency(open)}</span>;
      })()}</td>
      <td className="py-2 px-2 text-center"><span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getAgingBadgeColor(inv.daysOverdue)}`}>{inv.daysOverdue < 0 ? `${Math.abs(inv.daysOverdue)}d` : `${inv.daysOverdue}d`}</span></td>
    </tr>)}</tbody></table></div>{filteredInvoices.length === 0 && <div className="text-center py-8 text-slate-400"><AlertCircle size={32} className="mx-auto mb-2 opacity-50" /><p>Geen facturen gevonden</p></div>}{totalPages > 1 && <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-700"><div className="text-sm text-slate-400">Toont {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredInvoices.length)} van {filteredInvoices.length}</div><div className="flex items-center gap-2"><button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Eerste</button><button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Vorige</button><span className="px-3 py-1.5 bg-lime-500 text-slate-900 font-semibold text-sm rounded-lg">{currentPage}</span><button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Volgende</button><button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-sm rounded-lg">Laatste</button></div></div>}</Card>
    {c360Menu}
  </div>;
};

// ─── Market Prices module (Lynx + Elia + ENTSO-E) ──────────────────────────────
// Geport uit customer-portal/src/pages/MarketPage.jsx. Live + historische spot-
// en onbalansprijzen per kwartier, near-real-time minuut-data met SI-forecast,
// dagprofiel-aggregatie, CSV-export en forward-quotaties via Lynx.

const MP_COLORS = {
  belpex: '#38bdf8',    // sky-400 — Spot prijs (€/MWh)
  elia15: '#ec4899',    // pink-500 — Onbalans prijs (€/MWh)
  elia1: '#f97316',     // orange-500 — minuut-chart actual
  eliaForecast: '#a78bfa', // violet-400 — minuut-chart forecast
  // Onbalans en zon zijn onderling van kleur gewisseld (gebruikersvoorkeur):
  // onbalans is nu roze, zon amber. Beide houden hoge tonale afstand t.o.v.
  // sky (spot) en teal (wind), dus de vier lijnen blijven onderscheidbaar.
  solar: '#fbbf24',  // amber-400 — Elia ods032/ods087 solar productie (MW)
  wind: '#14b8a6',   // teal-500 — Elia ods031/ods086 wind productie (MW)
};
// De twee forward-grafieken op Market Prices: Power BE en TTF Gas. Elke grafiek
// toont alle kalenderjaar-prijzen (Cal-YY) over de laatste 60 dagen.
const MP_FORWARD_CHARTS = [
  // kort/maandLabel: kolomkop en bestandsnaamdeel van de export "Maandprijzen".
  { product: 'ICE_Endex_Power_BE_BaseLoadFutures', label: 'Power BE — Baseload', unit: '€/MWh', kort: 'power', maandLabel: 'Power BE Base' },
  { product: 'ICE_Endex_TTFGas_NL_BaseLoadFutures', label: 'TTF Gas NL — Baseload', unit: '€/MWh', kort: 'gas', maandLabel: 'TTF Gas Base' },
];

// Kleurenpalet voor de prijslijnen (Cal's krijgen de eerste, verzadigde tinten;
// Q's de volgende). Cyclisch indien meer lijnen dan kleuren.
const MP_FORWARD_LINE_COLORS = ['#84cc16', '#38bdf8', '#fbbf24', '#f472b6', '#a78bfa', '#fb923c', '#22d3ee', '#e879f9', '#4ade80', '#60a5fa', '#facc15', '#fb7185', '#c084fc', '#2dd4bf', '#f59e0b', '#34d399'];

// Periode-opties voor de forward-grafieken (label → aantal dagen). Default 60d.
const MP_FORWARD_PERIODS = [
  { value: 30, label: '30d' },
  { value: 60, label: '60d' },
  { value: 91, label: '3m' },
  { value: 182, label: '6m' },
  { value: 365, label: '1j' },
];

// Alle reeksen van een product, per soort chronologisch: maanden (Nov-26), dan
// kwartalen (Q1-27), dan Cal's (Cal-27). Seizoenen/strips vallen weg.
// Regels + tests: src/forwardMaandprijzen.js.
function mpForwardSeries(productSeries) {
  return sorteerContracten(productSeries || []);
}

// Gedeelde reeksenset voor BEIDE grafieken: de series die in elk product
// voorkomen (= doorgaans de smalste, Power BE-set). Hierdoor tonen Power BE en
// TTF Gas exact dezelfde ladder, en blijven verre TTF-jaren (Cal+3 e.v.) en de
// vijf jaar gasmaanden uit beeld. De volledige data blijft beschikbaar via Export.
function mpSharedForwardSeries(payloads) {
  const lists = (payloads || []).filter(Boolean).map(p => mpForwardSeries(p.productSeries));
  if (lists.length === 0) return [];
  return lists[0].filter(name => lists.every(l => l.includes(name)));
}

// Laatste niet-lege waarde van een serie in de chart-rijen (= meest recente
// gekende prijs).
function mpLastKnown(chart, seriesName) {
  if (!chart || !seriesName) return null;
  for (let i = chart.length - 1; i >= 0; i--) {
    const v = chart[i][seriesName];
    if (v != null) return { value: v, label: chart[i].tradeDateLabel };
  }
  return null;
}

const MP_BRUSSELS_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' });
const MP_BRUSSELS_TIME_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hour12: false });
const MP_NL_LONG_FMT = new Intl.DateTimeFormat('nl-BE', { timeZone: 'Europe/Brussels', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const MP_NL_WEEKDAY_FMT = new Intl.DateTimeFormat('nl-BE', { timeZone: 'Europe/Brussels', weekday: 'long' });
const MP_NL_SHORT_FMT = new Intl.DateTimeFormat('nl-BE', { timeZone: 'Europe/Brussels', weekday: 'short', day: '2-digit', month: '2-digit' });

function mpCapFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
function mpFormatWeekday(iso) { const [y, m, d] = iso.split('-').map(Number); return mpCapFirst(MP_NL_WEEKDAY_FMT.format(new Date(Date.UTC(y, m - 1, d, 12)))); }
// Nooit gooien: de tooltip van Recharts kan een label van de VORIGE weergave
// aanreiken (bv. "12:30" na een wissel dag→week). Intl.format op een ongeldige
// datum gooit een RangeError en dat is een wit scherm; toon dan de tekst zelf.
function mpFormatShortDate(iso) {
  const [y, m, d] = String(iso ?? '').split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return Number.isNaN(dt.getTime()) ? String(iso ?? '') : MP_NL_SHORT_FMT.format(dt);
}
function mpWeekRangeIso(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(dt); mon.setUTCDate(dt.getUTCDate() + offset);
  const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6);
  return { from: MP_BRUSSELS_FMT.format(mon), to: MP_BRUSSELS_FMT.format(sun) };
}
function mpTodayIso() { return MP_BRUSSELS_FMT.format(new Date()); }
function mpTomorrowIso() { const d = new Date(); d.setUTCDate(d.getUTCDate() + 1); return MP_BRUSSELS_FMT.format(d); }
function mpShiftIso(iso, days) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + days);
  return MP_BRUSSELS_FMT.format(dt);
}
function mpDaysBetweenIso(a, b) {
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000);
}
function mpFormatLongDate(iso) { const [y, m, d] = iso.split('-').map(Number); return MP_NL_LONG_FMT.format(new Date(Date.UTC(y, m - 1, d, 12))); }
// Korte Belgische datum (DD/MM/YYYY) uit een ISO-dag — voor tabellen en exports,
// waar de lange vorm ("maandag 17 augustus 2026") te breed is. Zie de
// datumnotatie-regel in CLAUDE.md.
function mpFormatIsoBe(iso) { const p = String(iso).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso); }
function mpIsoWeekNumber(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 4 - (dt.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
}
function mpStatsFromItems(items, key) {
  if (!items?.length) return null;
  const prices = items.map(i => i[key]).filter(v => v != null && !Number.isNaN(v));
  if (!prices.length) return null;
  const avg = prices.reduce((a, c) => a + c, 0) / prices.length;
  return { avg: Math.round(avg * 10) / 10, min: Math.min(...prices), max: Math.max(...prices) };
}

const MpSection = ({ title, subtitle, stale, right, children }) => (
  <div className="mt-6 bg-slate-900/60 border border-slate-800/70 rounded-xl p-4">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
          {stale && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">stale cache</span>}
        </div>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

// Eigen datumveld dat ALTIJD DD/MM/YYYY toont, onafhankelijk van de browser-taal
// (native <input type=date> volgt de browser → en-US toont MM/DD). Waarde in/uit is
// ISO 'YYYY-MM-DD'. Zie de datumnotatie-regel in CLAUDE.md.
const BD_WEEKDAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const BD_MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const BD_ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const bdIsoToDmy = (iso) => { if (!iso || !BD_ISO_RE.test(iso)) return ''; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
const bdTodayIso = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };

const BeDateInput = ({ value, onChange, min, max, className, placeholder = 'dd/mm/jjjj', allowClear = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const knopRef = useRef(null);
  const baseIso = () => (value && BD_ISO_RE.test(value)) ? value : (max && BD_ISO_RE.test(max) ? max : bdTodayIso());
  const [vy, setVy] = useState(() => Number(baseIso().split('-')[0]));
  const [vm, setVm] = useState(() => Number(baseIso().split('-')[1]) - 1); // 0-based maand
  // Bij heropenen: kalender op de maand van de huidige waarde zetten.
  useEffect(() => { if (open) { const b = baseIso(); setVy(Number(b.split('-')[0])); setVm(Number(b.split('-')[1]) - 1); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Klik buiten → sluiten.
  useEffect(() => {
    if (!open) return undefined;
    // De kalender rendert in een portal en zit dus buiten `ref`.
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-dropdown-panel]")) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const pad = (n) => String(n).padStart(2, '0');
  const mkIso = (y, m0, d) => `${y}-${pad(m0 + 1)}-${pad(d)}`;
  const daysInMonth = new Date(vy, vm + 1, 0).getDate();
  const firstDow = (new Date(vy, vm, 1).getDay() + 6) % 7; // maandag-eerst
  const todayIso = bdTodayIso();
  const isDisabled = (iso) => (min && BD_ISO_RE.test(min) && iso < min) || (max && BD_ISO_RE.test(max) && iso > max);
  const prevMonth = () => { if (vm <= 0) { setVm(11); setVy(vy - 1); } else setVm(vm - 1); };
  const nextMonth = () => { if (vm >= 11) { setVm(0); setVy(vy + 1); } else setVm(vm + 1); };

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const triggerCls = className || 'bg-slate-800 border border-slate-700 hover:border-lime-500 focus:border-lime-500 outline-none text-slate-200 px-3 py-1.5 rounded-lg text-sm text-left';
  return (
    <div className={`relative inline-block ${(className || '').includes('w-full') ? 'w-full' : ''}`} ref={ref}>
      <button ref={knopRef} type="button" onClick={() => setOpen((o) => !o)} className={triggerCls}>
        <span className="inline-flex items-center gap-2">
          <CalendarDays size={14} className="text-slate-400 shrink-0" />
          {value && BD_ISO_RE.test(value) ? bdIsoToDmy(value) : <span className="text-slate-500">{placeholder}</span>}
        </span>
      </button>
      {open && (
        <DropdownPanel anchorRef={knopRef} width={256} className="p-2 bg-slate-900 border border-slate-700 rounded-xl shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-300" aria-label="Vorige maand"><ChevronLeft size={16} /></button>
            <div className="text-sm font-medium text-slate-200">{BD_MONTHS[vm]} {vy}</div>
            <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-300" aria-label="Volgende maand"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {BD_WEEKDAYS.map((w) => <div key={w} className="text-center text-[10px] uppercase text-slate-500 py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d == null) return <div key={`e${i}`} />;
              const iso = mkIso(vy, vm, d);
              const dis = isDisabled(iso);
              const sel = iso === value;
              const isToday = iso === todayIso;
              return (
                <button key={iso} type="button" disabled={dis} onClick={() => { onChange(iso); setOpen(false); }}
                  className={`h-8 rounded-lg text-sm ${sel ? 'bg-lime-500 text-slate-900 font-semibold' : dis ? 'text-slate-600 cursor-not-allowed' : isToday ? 'bg-slate-800 text-lime-400 hover:bg-slate-700' : 'text-slate-200 hover:bg-slate-800'}`}>
                  {d}
                </button>
              );
            })}
          </div>
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-700">
            <button type="button" onClick={() => { if (!isDisabled(todayIso)) { onChange(todayIso); setOpen(false); } }} disabled={isDisabled(todayIso)} className="text-xs text-lime-400 hover:underline disabled:text-slate-600 disabled:no-underline">Vandaag</button>
            {allowClear && value ? <button type="button" onClick={() => { onChange(''); setOpen(false); }} className="text-xs text-slate-400 hover:underline">Wissen</button> : null}
          </div>
        </DropdownPanel>
      )}
    </div>
  );
};

// Op mobiel moet deze hele rij (datum + Vandaag/Morgen + Dag/Week + export) op ÉÉN
// regel passen: elke extra regel gaat af van de grafiek, en die moet zonder scrollen
// in beeld staan. Vandaar kleinere tekst, strakkere padding en `flex-nowrap`;
// vanaf `sm` staat alles weer op de gewone maat.
const MpDateSelector = ({ value, onChange }) => {
  const today = mpTodayIso();
  const tomorrow = mpTomorrowIso();
  const pill = (actief) => `px-1.5 sm:px-3 py-1 sm:py-1.5 rounded-md text-[11px] sm:text-xs font-medium whitespace-nowrap ${actief ? 'bg-lime-500 text-slate-900' : 'text-slate-400 hover:text-slate-200'}`;
  return (
    <div className="flex items-center gap-1 sm:gap-2 min-w-0">
      <button onClick={() => onChange(mpShiftIso(value, -1))} className="p-1 sm:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 shrink-0" aria-label="Vorige dag"><ChevronLeft size={15} /></button>
      <BeDateInput value={value} onChange={onChange}
        className="bg-slate-800 border border-slate-700 hover:border-lime-500 outline-none text-slate-200 px-1.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-sm text-left whitespace-nowrap" />
      <button onClick={() => onChange(mpShiftIso(value, 1))} className="p-1 sm:p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 shrink-0" aria-label="Volgende dag"><ChevronRight size={15} /></button>
      <div className="flex bg-slate-800 border border-slate-700 rounded-lg p-0.5 shrink-0">
        <button onClick={() => onChange(today)} className={pill(value === today)}>Vandaag</button>
        <button onClick={() => onChange(tomorrow)} className={pill(value === tomorrow)}>Morgen</button>
      </div>
    </div>
  );
};

const MpWeekSelector = ({ value, onChange }) => {
  const week = mpWeekRangeIso(value);
  const currentWeek = mpWeekRangeIso(mpTodayIso());
  const lastWeek = mpWeekRangeIso(mpShiftIso(mpTodayIso(), -7));
  const isCurrentWeek = week.from === currentWeek.from;
  const isLastWeek = week.from === lastWeek.from;
  const nextWeek = mpWeekRangeIso(mpShiftIso(value, 7));
  const tomorrow = mpTomorrowIso();
  const forwardAllowed = nextWeek.from <= tomorrow;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button onClick={() => onChange(mpShiftIso(value, -7))} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300" aria-label="Vorige week"><ChevronLeft size={16} /></button>
      <div className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 min-w-[200px] text-center">
        <span className="text-slate-500 text-xs mr-2">Week {mpIsoWeekNumber(week.from)}</span>
        {mpFormatShortDate(week.from)} – {mpFormatShortDate(week.to)}
      </div>
      <button onClick={() => onChange(mpShiftIso(value, 7))} disabled={!forwardAllowed} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300" aria-label="Volgende week"><ChevronRight size={16} /></button>
      <div className="flex gap-1 ml-1">
        <button onClick={() => onChange(mpShiftIso(mpTodayIso(), -7))} className={`px-2.5 py-1.5 rounded-lg text-xs ${isLastWeek ? 'bg-lime-500/15 text-lime-400 border border-lime-500/30' : 'bg-slate-800 text-slate-300 border border-transparent hover:border-slate-600'}`}>Vorige week</button>
        <button onClick={() => onChange(mpTodayIso())} className={`px-2.5 py-1.5 rounded-lg text-xs ${isCurrentWeek ? 'bg-lime-500/15 text-lime-400 border border-lime-500/30' : 'bg-slate-800 text-slate-300 border border-transparent hover:border-slate-600'}`}>Deze week</button>
      </div>
    </div>
  );
};

const MpStat = ({ label, value, accent = 'text-slate-100', accentStyle }) => (
  <div className="bg-slate-950/60 border border-slate-800/60 rounded-lg p-2.5">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-0.5 text-lg font-semibold ${accentStyle ? '' : accent}`} style={accentStyle}><Kengetal>{value}</Kengetal></div>
  </div>
);

// Compacte KPI-kolom voor de samenvattingsbalk boven de grafiek: kleine titel +
// gekleurde stip, grote waarde, eenheid. Waarde null → '—'.
// Statistiek-strip boven de grafiek. Eén blok per GESELECTEERDE prijsreeks, in de
// kleur van die reeks in de grafiek. Verving de losse KPI-balk bovenaan de pagina:
// die stond los van de grafiek en veranderde niet mee met de reeks-selectie.
const mpNum = (v, decimals = 1) =>
  (v == null || Number.isNaN(Number(v))) ? '—' : Number(v).toFixed(decimals);

// Zon/wind komen in MW binnen (duizenden). Voor de as en de tooltip tonen we GW:
// deel door 1000 en houd één decimaal, want een hele GW is een grove stap.
const mpGw = (v) => (v == null || Number.isNaN(Number(v))) ? '—' : (Number(v) / 1000).toFixed(1);

// Breekpunt in JS. Nodig omdat Recharts-props (zoals `hide` op een as) geen CSS-
// klassen kennen: een `sm:`-classe kan een as niet verbergen. 640px = Tailwind
// `sm`, hetzelfde punt waarop de rest van deze pagina van de compacte mobiele
// opmaak naar de ruime schakelt — zo verschijnen as en layout tegelijk.
const useMinWidth = (px) => {
  const query = `(min-width: ${px}px)`;
  const [match, setMatch] = useState(() => { try { return window.matchMedia(query).matches; } catch { return true; } });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(query); } catch { return undefined; }
    const h = (e) => setMatch(e.matches);
    setMatch(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [query]);
  return match;
};

// Mobiel staan label en waarde NAAST elkaar op één regel; vanaf `sm` weer onder
// elkaar met een groot cijfer. Zo past een hele reeks (nu · gemiddelde · min/max)
// op één regel en is er geen horizontale schuifbalk meer nodig.
// `short` is het mobiele label: met de volle tekst ("Spotprijs nu", "Gemiddelde")
// passen de cellen niet op één regel. `desktopOnly` laat een cel op mobiel helemaal
// weg (min/max) — daar telt enkel de actuele prijs en het gemiddelde.
const MpStatCell = ({ label, short, value, color, desktopOnly }) => (
  <div className={`${desktopOnly ? 'hidden sm:block' : 'flex items-baseline gap-1 sm:block'} px-2 sm:px-4 py-0.5 sm:py-2 sm:min-w-[104px] sm:flex-1`}>
    <div className="text-[10px] sm:text-[11px] text-slate-500 whitespace-nowrap">
      <span className="sm:hidden">{short || label}</span><span className="hidden sm:inline">{label}</span>
    </div>
    <div className="min-w-0 text-[13px] sm:text-lg md:text-xl font-semibold leading-tight sm:mt-0.5 tabular-nums whitespace-nowrap" style={{ color }}><Kengetal>{value}</Kengetal></div>
  </div>
);

// `cells` = [{ label, value }]. De kop draagt de reekskleur zodat het blok
// visueel aan de bijbehorende lijn in de grafiek vastzit. Op mobiel staat de kop
// op dezelfde regel als de waarden — de kleurstip volstaat daar als koppeling.
const MpStatGroup = ({ title, color, cells }) => (
  // Mobiel: kop én waarden op ÉÉN regel — dat scheelt per reeks een hele regel en
  // houdt de grafiek boven de vouw. De eenheid (€/MWh) valt daar weg; die staat al
  // als label bij de linker as. Vanaf `sm` de gewone opbouw met kop erboven.
  <div className="flex-1 min-w-0 sm:min-w-[260px] py-1 sm:py-1.5">
    <div className="flex items-center gap-1 sm:block">
      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 pt-0 sm:pt-1.5 shrink-0">
        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span className="text-[11px] sm:text-sm font-semibold whitespace-nowrap" style={{ color }}>{title}</span>
        <span className="hidden sm:inline text-[11px] text-slate-500">(€/MWh)</span>
      </div>
      <div className="flex flex-wrap sm:flex-nowrap mt-0 sm:mt-0.5 min-w-0">
        {cells.map(c => <MpStatCell key={c.label} label={c.label} short={c.short} value={c.value} color={color} desktopOnly={c.desktopOnly} />)}
      </div>
    </div>
  </div>
);

// `short` houdt de vier reeksen op mobiel op één regel ("Spot"/"Onbalans" i.p.v.
// "Spotprijs"/"Onbalansprijs"); op desktop blijft het volledige woord staan.
const MpSeriesToggle = ({ label, short, color, checked, onChange }) => (
  <label className="inline-flex items-center gap-1.5 sm:gap-2 cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-slate-500 bg-slate-700 text-lime-500 focus:ring-lime-500 focus:ring-offset-0" />
    <span className="inline-flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-slate-200 whitespace-nowrap">
      <span className="inline-block w-2.5 sm:w-3 h-0.5 shrink-0" style={{ backgroundColor: color }} />
      <span className="sm:hidden">{short || label}</span><span className="hidden sm:inline">{label}</span>
    </span>
  </label>
);

// Mobiel: in plaats van alle grafieken onder elkaar te stapelen, kan je per
// tab tussen de grafieken wisselen (de grafiek is op smartphone het belangrijkst).
// Op md+ tonen we alles gewoon gestapeld; de tabbalk is dan verborgen.
const MP_TABS = [
  { id: 'actueel', label: 'Elektriciteitsprijzen (Spot)' },
  // Gas staat meteen na de elektriciteitsprijzen: het is dezelfde vraag ("wat kost
  // energie vandaag"), maar voor de andere energiedrager.
  { id: 'gas', label: 'Gasprijzen (Spot)' },
  { id: 'forward', label: 'Forward' },
  { id: 'live', label: 'Live onbalans' },
  { id: 'profiel', label: 'Dagprofiel' },
];

// Compacte export-knop met eigen popover. Kiest een EIGEN van–tot periode, los van
// de weergaveperiode van "Actuele energieprijzen". Zo blijft de grote export-balk weg.
const MpExportPopover = ({ from, to, setFrom, setTo, rangeDays, rangeValid, exporting, error, onExport }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const knopRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    // Zowel dit paneel als de BeDateInput-kalenders erin renderen in een
    // portal, dus buiten `ref`. Een klik op een datum mag de popover niet sluiten.
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-dropdown-panel]")) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const dateCls = 'bg-slate-800 border border-slate-700 hover:border-lime-500 outline-none text-slate-200 px-3 py-1.5 rounded-lg text-sm text-left w-full';
  return (
    <div className="relative" ref={ref}>
      {/* Zelfde icoonknop als overal; hij opent hier een paneel met een eigen
          periodekeuze i.p.v. meteen te downloaden. */}
      <ExportKnop onExport={() => setOpen(o => !o)} label="Exporteer spot + onbalans per kwartier naar Excel (eigen periode)" className="!bg-slate-800 !border-slate-700 hover:!border-lime-500" innerRef={knopRef} />
      {open && (
        <DropdownPanel anchorRef={knopRef} width={288} className="p-3 bg-slate-900 border border-slate-700 rounded-xl shadow-xl">
          <div className="text-sm font-semibold text-slate-100 flex items-center gap-2"><Download size={15} className="text-lime-400" />Export naar Excel</div>
          <p className="text-[11px] text-slate-500 mt-0.5 mb-3">Onbalans + spot per kwartier, eigen periode (los van de weergave); de einddag zelf telt niet mee. Max 1 jaar.</p>
          <div className="flex flex-col gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Van</div>
              <BeDateInput value={from} onChange={setFrom} max={laatsteDag(to) || undefined} className={dateCls} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Tot</div>
              <BeDateInput value={to} onChange={setTo} min={totNa(from) || undefined} max={mpTomorrowIso()} className={dateCls} />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 mt-3">
            <span className="text-xs text-slate-500">{rangeDays} dag{rangeDays === 1 ? '' : 'en'}</span>
            <button onClick={onExport} disabled={!rangeValid || exporting} className="inline-flex items-center gap-2 px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg text-sm">
              <Download size={14} />{exporting ? 'Exporteren…' : 'Download'}
            </button>
          </div>
          {!rangeValid && <div className="mt-2 text-xs text-amber-400">Periode te lang (max 366 dagen) of ongeldig.</div>}
          {error && <div className="mt-2 text-xs text-red-300">{error}</div>}
        </DropdownPanel>
      )}
    </div>
  );
};

const MarketPricesPage = ({ token, currentUser, branding = null }) => {
  const apiFetch = useMemo(() => async (url) => {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      let msg = `HTTP ${r.status}`;
      try { msg = (await r.json()).error || msg; } catch (_) { /* niet-JSON antwoord */ }
      throw new Error(msg);
    }
    return r.json();
  }, [token]);

  const [date, setDate] = useState(() => mpTodayIso());
  const [combinedView, setCombinedView] = useState('day');
  const [showSpot, setShowSpot] = useState(true);
  const [showImbalance, setShowImbalance] = useState(true);
  const [showSolar, setShowSolar] = useState(true);
  const [showWind, setShowWind] = useState(true);
  const [spot, setSpot] = useState(null);
  const [imbalance, setImbalance] = useState(null);
  const [solar, setSolar] = useState(null);    // {items: [{time, valueMw}]}
  const [wind, setWind] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [combinedErr, setCombinedErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const week = useMemo(() => mpWeekRangeIso(date), [date]);

  useEffect(() => {
    setCombinedErr(null);
    setLoading(true);
    // Wis vorige data ONMIDDELLIJK bij elke datum/view-wissel. Anders blijft de
    // vorige dag/week in beeld zolang de (soms trage) Elia/Lynx-fetch loopt —
    // dan "lijkt de grafiek niet geüpdatet" / blijft de vorige periode staan.
    setSpot(null); setImbalance(null); setSolar(null); setWind(null); setWeekData(null);
    // Maand/Jaar tekenen gemiddelden uit het prijsarchief (zie maandGem/jaarGem
    // hieronder); daar hoort geen kwartierophaal bij.
    if (combinedView === 'month' || combinedView === 'year') { setLoading(false); return; }
    if (combinedView === 'day') {
      // Vier parallelle calls; renewable-endpoints aggregeren server-side per
      // timestamp (som over alle Belgische regio's en grid-types). ELKE call
      // apart afvangen: een ontbrekende/falende serie (bv. day-ahead spot of
      // onbalans is voor een toekomstige dag nog niet beschikbaar) mag de andere
      // niet blokkeren. We zetten de state ALTIJD (null = geen data) zodat de
      // vorige dag NOOIT in beeld blijft staan — werkelijke onbalans- en
      // spotprijzen bestaan nooit voor een datum waarvoor er geen data is.
      Promise.all([
        apiFetch(`/api/lynx/spot?date=${date}&spotType=EPEXSPOTBE_15M`).catch(() => null),
        apiFetch(`/api/market/imbalance?date=${date}`).catch(() => null),
        apiFetch(`/api/market/solar?date=${date}`).catch(() => null),
        apiFetch(`/api/market/wind?date=${date}`).catch(() => null),
      ]).then(([s, i, so, wi]) => {
        setSpot(s); setImbalance(i); setSolar(so); setWind(wi); setWeekData(null);
      }).finally(() => setLoading(false));
    } else {
      // Week-mode: één /export call met ?include=renewable. Backend pakt zon+wind
      // per dag uit dezelfde 15-min cache als de dag-modus, dus na 1× ophalen
      // is alles warm.
      apiFetch(`/api/market/export?from=${week.from}&to=${week.to}&include=renewable`)
        .then(r => { setWeekData(r); setSpot(null); setImbalance(null); setSolar(null); setWind(null); })
        .catch(e => { setCombinedErr(e.message); setWeekData(null); })
        .finally(() => setLoading(false));
    }
  }, [apiFetch, date, combinedView, week.from, week.to]);

  const merged = useMemo(() => {
    if (combinedView === 'week') {
      // Per-row actual/forecast split voor zon+wind, zelfde regel als day-mode:
      // source 'forecast' → stippellijn-veld, anders volle lijn. We doen één
      // pass in volgorde van date+time en plaatsen het laatste actual-punt ook
      // in het forecast-veld als bridge (één-punt-overlap).
      const rows = (weekData?.rows || []).map(r => ({
        key: `${r.date} ${r.time}`, date: r.date, time: r.time,
        spotEurMwh: r.spotEurMwh, imbalanceEurMwh: r.imbalanceEurMwh,
        // Plat opslaan zodat we de split-pass eronder uitvoeren
        _solarMw: r.solarMw, _solarSource: r.solarSource,
        _windMw: r.windMw, _windSource: r.windSource,
        solarMwActual: null, solarMwForecast: null,
        windMwActual: null, windMwForecast: null,
      }));
      const splitInPlace = (valKey, srcKey, actualKey, forecastKey) => {
        let lastActual = null, bridged = false;
        for (const row of rows) {
          if (row[valKey] == null) continue;
          if (row[srcKey] === 'forecast') {
            row[forecastKey] = row[valKey];
            if (!bridged && lastActual) { lastActual[forecastKey] = lastActual[actualKey]; bridged = true; }
          } else {
            row[actualKey] = row[valKey];
            lastActual = row; bridged = false;
          }
        }
      };
      splitInPlace('_solarMw', '_solarSource', 'solarMwActual', 'solarMwForecast');
      splitInPlace('_windMw', '_windSource', 'windMwActual', 'windMwForecast');
      return rows;
    }
    const byTime = new Map();
    const ensure = t => {
      let p = byTime.get(t);
      if (!p) {
        p = {
          key: t, time: t,
          // Twee aparte velden per prijs-serie: actual = volle lijn,
          // forecast = stippellijn (backend kan per point source='forecast' geven).
          spotEurMwh: null, spotForecastEurMwh: null,
          imbalanceEurMwh: null, imbalanceForecastEurMwh: null,
          // Idem voor zon/wind (MW).
          solarMwActual: null, solarMwForecast: null,
          windMwActual: null, windMwForecast: null,
        };
        byTime.set(t, p);
      }
      return p;
    };
    // Prijs-series splitsen in actual/forecast op basis van point.source, met
    // één-punt-overlap (bridge) zodat de stippellijn aansluit op de volle lijn.
    const splitPriceSeries = (items, valKey, actualKey, forecastKey) => {
      const sorted = (items || []).slice().sort((a, b) => a.time.localeCompare(b.time));
      let lastActualTime = null, bridged = false;
      for (const it of sorted) {
        const v = it[valKey];
        if (v == null) continue;
        const row = ensure(it.time);
        if (it.source === 'forecast') {
          row[forecastKey] = v;
          if (!bridged && lastActualTime != null) {
            const lastRow = byTime.get(lastActualTime);
            if (lastRow) lastRow[forecastKey] = lastRow[actualKey];
            bridged = true;
          }
        } else {
          row[actualKey] = v;
          lastActualTime = it.time;
          bridged = false;
        }
      }
    };
    splitPriceSeries(spot?.items, 'priceEurMwh', 'spotEurMwh', 'spotForecastEurMwh');
    splitPriceSeries(imbalance?.items, 'imbalanceEurMwh', 'imbalanceEurMwh', 'imbalanceForecastEurMwh');

    // Solar+wind: splitsen per timestamp in actual vs forecast veld zodat we
    // twee Lines kunnen renderen (volle + stippel) met dezelfde kleur. Om de
    // visuele aansluiting te bewaren laten we het laatste actual-punt óók
    // verschijnen aan het begin van de forecast-serie (één-punt-overlap).
    const splitSeries = (items, actualKey, forecastKey) => {
      const sorted = (items || []).slice().sort((a, b) => a.time.localeCompare(b.time));
      let lastActualTime = null;
      let bridged = false;
      for (const p of sorted) {
        const row = ensure(p.time);
        if (p.source === 'forecast') {
          row[forecastKey] = p.valueMw;
          if (!bridged && lastActualTime != null) {
            // Bridge: zet laatste actual-waarde ook in forecast-veld zodat de
            // stippellijn aansluit op het einde van de volle lijn.
            const lastRow = byTime.get(lastActualTime);
            if (lastRow) lastRow[forecastKey] = lastRow[actualKey];
            bridged = true;
          }
        } else {
          row[actualKey] = p.valueMw;
          lastActualTime = p.time;
          bridged = false; // resetting laat herhalende actual→forecast switches netjes hertekenen
        }
      }
    };
    splitSeries(solar?.items, 'solarMwActual', 'solarMwForecast');
    splitSeries(wind?.items, 'windMwActual', 'windMwForecast');

    return [...byTime.values()].sort((a, b) => a.time.localeCompare(b.time));
  }, [combinedView, weekData, spot, imbalance, solar, wind]);

  // Y-as domeinen berekenen zodat de '0'-lijn op beide assen op exact dezelfde
  // verticale positie ligt. Strategie: van elke as bereken de fractie negatieve
  // ruimte, pak de grootste, en strek de andere as evenredig naar onder (negatief
  // MW heeft fysisch geen betekenis maar we hide die ticks via tickFormatter).
  const yAxisDomains = useMemo(() => {
    let pMin = 0, pMax = 0, mMax = 0, priceSeen = false;
    for (const r of merged) {
      for (const v of [r.spotEurMwh, r.spotForecastEurMwh, r.imbalanceEurMwh, r.imbalanceForecastEurMwh]) if (v != null && !Number.isNaN(v)) { priceSeen = true; if (v < pMin) pMin = v; if (v > pMax) pMax = v; }
      for (const v of [r.solarMwActual, r.solarMwForecast, r.windMwActual, r.windMwForecast]) if (v != null && !Number.isNaN(v) && v > mMax) mMax = v;
    }
    // Nette stapgrootte (1 / 2 / 2.5 / 5 / 10 × 10ⁿ) voor ~5 intervallen, zodat
    // de assen ronde getallen tonen i.p.v. ruwe min/max-waarden.
    const niceStep = (range, count) => {
      const raw = (range || 1) / count;
      const mag = Math.pow(10, Math.floor(Math.log10(raw)));
      const n = raw / mag;
      const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
      return step * mag;
    };
    const TARGET = 5;
    const pStep = niceStep(pMax, TARGET);
    const mStep = niceStep(mMax, TARGET);
    // Zelfde aantal intervallen boven/onder nul op beide assen → de 0-lijn en
    // alle rasterlijnen vallen samen, terwijl elke as eigen ronde ticks houdt.
    const posTicks = Math.max(Math.ceil(pMax / pStep), Math.ceil(mMax / mStep), 1);
    const negTicks = pMin < 0 ? Math.ceil(-pMin / pStep) : 0;
    const mkTicks = (step) => {
      const t = [];
      for (let i = -negTicks; i <= posTicks; i++) t.push(Math.round(i * step * 1e6) / 1e6);
      return t;
    };
    return {
      price: { domain: [-negTicks * pStep, posTicks * pStep], ticks: mkTicks(pStep), hasData: priceSeen },
      mw: { domain: [-negTicks * mStep, posTicks * mStep], ticks: mkTicks(mStep), hasData: mMax > 0 },
    };
  }, [merged]);

  // Welke series hebben überhaupt data voor de gekozen dag? Zo tekenen we geen
  // misleidende lege lijn en kunnen we per serie "geen data" melden. De grafiek
  // verschijnt zodra er ÉNIGE data is (`hasAnyData`) — voor een toekomstige dag
  // bestaat day-ahead/onbalans nog niet, maar de zon/wind-forecast wél, en die
  // tonen we dan (gestippeld). `hasPriceData` stuurt enkel of de prijs-as/stats
  // zinvol zijn.
  const seriesPresence = useMemo(() => {
    const has = (k) => merged.some(r => r[k] != null && !Number.isNaN(r[k]));
    const spotA = has('spotEurMwh'), spotF = has('spotForecastEurMwh');
    const imbA = has('imbalanceEurMwh'), imbF = has('imbalanceForecastEurMwh');
    const solarA = has('solarMwActual'), solarF = has('solarMwForecast');
    const windA = has('windMwActual'), windF = has('windMwForecast');
    return {
      spot: spotA || spotF, spotActual: spotA, spotForecast: spotF,
      imbalance: imbA || imbF, imbalanceActual: imbA, imbalanceForecast: imbF,
      solar: solarA || solarF, wind: windA || windF,
      solarForecast: solarF, windForecast: windF,
      hasPriceData: spotA || spotF || imbA || imbF,
      hasAnyData: spotA || spotF || imbA || imbF || solarA || solarF || windA || windF,
    };
  }, [merged]);

  const spotStats = useMemo(() => mpStatsFromItems(
    combinedView === 'week'
      ? (weekData?.rows || []).filter(r => r.spotEurMwh != null).map(r => ({ priceEurMwh: r.spotEurMwh }))
      : spot?.items, 'priceEurMwh'
  ), [combinedView, weekData, spot]);
  const imbStats = useMemo(() => mpStatsFromItems(
    combinedView === 'week'
      ? (weekData?.rows || []).filter(r => r.imbalanceEurMwh != null)
      : imbalance?.items, 'imbalanceEurMwh'
  ), [combinedView, weekData, imbalance]);
  // Bewust geen stats voor zon/wind: gebruiker wil enkel de lijn, niet de cijfers.

  // Forward: twee grafieken (Power BE + TTF Gas) met maand-, Q- en Cal-prijzen.
  // Periode instelbaar (default 60d). Welke contracten getekend worden bepaalt
  // de "ladder" (x maanden → y kwartalen erna → Cal's erna, forwardLadder);
  // daarbinnen per lijn aan/uit via hiddenForward.
  const [lynxForward, setLynxForward] = useState({}); // product → payload
  const [lynxForwardErr, setLynxForwardErr] = useState(null);
  const [lynxForwardDays, setLynxForwardDays] = useState(60);
  const [forwardLadderKeuze, setForwardLadderKeuze] = useState(LADDER_STANDAARD);
  // Set van verborgen series-keys "product|seriesName" (handmatige klik).
  const [hiddenForward, setHiddenForward] = useState(() => new Set());

  useEffect(() => {
    setLynxForwardErr(null);
    let cancelled = false;
    Promise.all(MP_FORWARD_CHARTS.map(c =>
      apiFetch(`/api/lynx/forward?product=${c.product}&days=${lynxForwardDays}`)
        .then(d => [c.product, d])
        .catch(e => { if (!cancelled) setLynxForwardErr(e.message); return [c.product, null]; })
    )).then(pairs => { if (!cancelled) setLynxForward(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [apiFetch, lynxForwardDays]);

  const toggleForwardSeries = (product, name) => {
    setHiddenForward(prev => {
      const next = new Set(prev || []);
      const k = `${product}|${name}`;
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // Export: ALLE forward-data (beide producten, álle Cal- én Q-reeksen — niet
  // beperkt tot de gedeelde grafiek-filterset) als CSV, per trade date.
  const exportForwardCsv = () => {
    const rows = [];
    for (const cfg of MP_FORWARD_CHARTS) {
      const data = lynxForward[cfg.product];
      if (!data) continue;
      const series = mpForwardSeries(data.productSeries);
      for (const td of (data.chart || [])) {
        for (const name of series) {
          const v = td[name];
          if (v == null) continue;
          rows.push({ product: cfg.label, tradeDate: td.tradeDateLabel, contract: name, value: v });
        }
      }
    }
    const fmt = v => v == null ? '' : String(v).replace('.', ',');
    const lines = ['Product;Trade date;Contract;Prijs (EUR/MWh)'];
    for (const r of rows) lines.push([r.product, r.tradeDate, r.contract, fmt(r.value)].join(';'));
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `forward-market-${lynxForwardDays}d.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Export "Maandprijzen": één prijs per leveringsmaand, van deze maand tot
  // drie jaar vooruit, voor Power BE Base en TTF Gas Base. Per maand de fijnste
  // notering die bestaat (maand → kwartaal → Cal), altijd van de MEEST RECENTE
  // handelsdag in de geladen reeks; die datum staat in de bestandsnaam.
  // Regels + tests: src/forwardMaandprijzen.js.
  // De CALC-kolom verdeelt een kwartaal-/Cal-prijs volgens het profiel van de
  // laatst gekende maandprijzen; dat profiel (verlopen maandcontracten, 15
  // maanden terug) komt per product van /api/lynx/forward/maandprofiel.
  const forwardMaandProducten = () => MP_FORWARD_CHARTS.map(c => ({
    id: c.product, kort: c.kort, label: c.maandLabel, notering: laatsteNotering(lynxForward[c.product]),
  }));
  const [maandprijzenBezig, setMaandprijzenBezig] = useState(false);

  // ── Tabel "Maandprijzen" onder de forward-grafieken (22-10-2026) ────────────
  // Dezelfde cijfers als de export Maandprijzen: per maand de fijnste notering
  // (maand → kwartaal → Cal) van de meest recente handelsdag, plus CALC. Het
  // maandprofiel per product (verlopen maandcontracten) wordt één keer geladen
  // zodra de Forward-tab open staat; het dient voor CALC én voor de historische
  // maanden (toggle), die hun eigen laatste notering tonen.
  const [forwardProfielen, setForwardProfielen] = useState({});
  const [forwardProfielFout, setForwardProfielFout] = useState(null);
  const [forwardHistorisch, setForwardHistorisch] = useState(false);
  // Laadt zodra de forward-reeksen er zijn (twee kleine, server-side gecachete
  // antwoorden). Niet aan de actieve tab koppelen: die state wordt verderop pas
  // gedeclareerd en een dependency-array wordt tijdens het renderen gelezen (TDZ).
  useEffect(() => {
    if (!MP_FORWARD_CHARTS.some(c => lynxForward[c.product])) return undefined;
    let weg = false;
    Promise.all(MP_FORWARD_CHARTS.map(c => apiFetch(`/api/lynx/forward/maandprofiel?product=${c.product}`).then(d => [c.product, d])))
      .then(pairs => { if (!weg) { setForwardProfielen(Object.fromEntries(pairs)); setForwardProfielFout(null); } })
      .catch(e => { if (!weg) setForwardProfielFout(e.message); });
    return () => { weg = true; };
  }, [apiFetch, lynxForward]);
  const forwardTabelProducten = useMemo(() => MP_FORWARD_CHARTS.map(c => {
    const notering = laatsteNotering(lynxForward[c.product]);
    return { id: c.product, kort: c.kort, label: c.maandLabel, notering, profiel: forwardProfielen[c.product] ? bouwProfiel(forwardProfielen[c.product], notering) : null };
  }), [lynxForward, forwardProfielen]);
  const forwardTabelRijen = useMemo(() => {
    if (!forwardTabelProducten.some(p => p.notering)) return [];
    const toekomst = bouwMaandprijzen(forwardTabelProducten);
    return forwardHistorisch ? [...historischeMaandprijzen(forwardTabelProducten), ...toekomst] : toekomst;
  }, [forwardTabelProducten, forwardHistorisch]);
  const forwardHuidigeMaand = mpTodayIso().slice(0, 7);

  const exportForwardMaandprijzen = async () => {
    const producten = forwardMaandProducten();
    if (!producten.some(p => p.notering)) { toast.info('Geen recente notering beschikbaar om te exporteren.'); return; }
    setMaandprijzenBezig(true);
    try {
      const profielen = await Promise.all(producten.map(p => apiFetch(`/api/lynx/forward/maandprofiel?product=${p.id}`)));
      const metProfiel = producten.map((p, i) => ({ ...p, profiel: bouwProfiel(profielen[i], p.notering) }));
      exportRowsToCsv(maandprijzenBestandsnaam(metProfiel), maandprijzenKolommen(metProfiel), bouwMaandprijzen(metProfiel));
    } catch (e) {
      toast.error(`De maandprijzen uit het verleden konden niet opgehaald worden: ${e.message}`);
    } finally {
      setMaandprijzenBezig(false);
    }
  };

  const [minute, setMinute] = useState(null);
  const [minuteErr, setMinuteErr] = useState(null);

  useEffect(() => {
    let timer;
    const load = () => apiFetch('/api/market/imbalance/minute').then(setMinute).catch(e => setMinuteErr(e.message));
    load();
    timer = setInterval(load, 10 * 1000);
    return () => clearInterval(timer);
  }, [apiFetch]);

  const { latestActual, upcomingForecasts } = useMemo(() => {
    const items = minute?.items || [];
    let lastActualIdx = -1;
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].imbalanceEurMwh != null) { lastActualIdx = i; break; }
    }
    const latest = lastActualIdx >= 0 ? items[lastActualIdx] : null;
    const forecasts = items
      .filter(i => i.forecastPriceEurMwh != null)
      .sort((a, b) => (a.datetime || a.time).localeCompare(b.datetime || b.time))
      .slice(0, 6);
    return { latestActual: latest, upcomingForecasts: forecasts };
  }, [minute]);

  // Alleen de laatste 30 minuten tonen (venster eindigt op het nieuwste punt —
  // actual of korte forecast vooruit). Houdt de live-grafiek compact.
  const minuteWindow = useMemo(() => {
    const items = minute?.items || [];
    if (items.length === 0) return [];
    let endMs = 0;
    for (const it of items) { const ms = Date.parse(it.datetime); if (!Number.isNaN(ms) && ms > endMs) endMs = ms; }
    if (!endMs) return items.slice(-30); // fallback als datetime niet parsebaar is
    const cutoff = endMs - 30 * 60 * 1000;
    return items.filter(it => { const ms = Date.parse(it.datetime); return Number.isNaN(ms) || ms >= cutoff; });
  }, [minute]);

  const todayBelgium = mpTodayIso();
  const nowQuarter = useMemo(() => {
    if (date !== todayBelgium) return null;
    const hm = MP_BRUSSELS_TIME_FMT.format(new Date()).split(':');
    const h = parseInt(hm[0], 10), m = parseInt(hm[1], 10);
    const qm = Math.floor(m / 15) * 15;
    return `${String(h).padStart(2, '0')}:${String(qm).padStart(2, '0')}`;
  }, [date, todayBelgium]);

  // Huidige spotprijs voor de KPI-balk: op het actuele kwartier (vandaag), anders
  // de laatst gekende spotwaarde van de getoonde reeks.
  const spotNow = useMemo(() => {
    if (nowQuarter) { const r = merged.find(m => m.time === nowQuarter); if (r && r.spotEurMwh != null) return r.spotEurMwh; }
    for (let i = merged.length - 1; i >= 0; i--) { if (merged[i].spotEurMwh != null) return merged[i].spotEurMwh; }
    return null;
  }, [merged, nowQuarter]);

  // Welke dag toont de prijsdetail-tabel? Enkel relevant in weekweergave; bij een
  // wissel van periode/weergave terug naar de eerste dag.
  const [detailDag, setDetailDag] = useState(0);
  useEffect(() => { setDetailDag(0); }, [date, combinedView]);

  const [expFrom, setExpFrom] = useState(() => mpShiftIso(mpTodayIso(), -7));
  // "Tot" telt niet mee (src/datumFilter.js): morgen = tot en met vandaag.
  const [expTo, setExpTo] = useState(() => mpTomorrowIso());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  // Actieve tab (Actuele prijzen / Forward / Live onbalans / Dagprofiel) — nu op ALLE
  // schermformaten, i.p.v. gestapelde secties op desktop.
  // Welke van die tabs zichtbaar zijn, is per gebruiker instelbaar (subpagina's).
  const { tabs: mpTabs, mag: magMpTab, leeg: geenMpTabs } = useSubPages('market', currentUser, MP_TABS);
  const [mobileTab, setMobileTab] = useState('actueel');
  useEffect(() => {
    if (mpTabs.length > 0 && !mpTabs.some(t => t.id === mobileTab)) setMobileTab(mpTabs[0].id);
  }, [mpTabs, mobileTab]);
  const toonMpTab = (id) => magMpTab(id) && mobileTab === id;
  // Zon/wind-as (GW) tonen vanaf tablet; op een telefoon verbergen we ze.
  const toonGwAs = useMinWidth(640);

  const handleExport = async () => {
    setExporting(true); setExportError(null);
    try {
      // De API verwacht de laatste dag die meetelt; het scherm vraagt "tot".
      const data = await apiFetch(`/api/market/export?from=${expFrom}&to=${laatsteDag(expTo)}`);
      // Datum + Tijd zijn Belgische (lokale) tijd; "Start (UTC)" staat ernaast
      // zodat deze export kwartier voor kwartier naast de Metering-export
      // (zelfde kolom) te leggen is, ook over een zomertijdwissel heen.
      const headers = ['Datum', 'Tijd', 'Start (UTC)', 'Spotprijs (EUR/MWh)', 'Onbalansprijs (EUR/MWh)'];
      const lines = [headers.join(';')];
      for (const r of data.rows || []) {
        const fmt = (v) => v == null ? '' : String(v).replace('.', ',');
        lines.push([r.date, r.time, r.fromUtc || '', fmt(r.spotEurMwh), fmt(r.imbalanceEurMwh)].join(';'));
      }
      const csv = '﻿' + lines.join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `marktprijzen-${expFrom}-tot-${expTo}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setExportError(e.message); }
    finally { setExporting(false); }
  };

  const expRangeDays = useMemo(() => dagenInVenster(expFrom, expTo) ?? 0, [expFrom, expTo]);
  const expRangeValid = expRangeDays >= 1 && expRangeDays <= 366;

  const [profileFrom, setProfileFrom] = useState(() => mpShiftIso(mpTodayIso(), -30));
  // "Tot" telt niet mee (src/datumFilter.js): vandaag = tot en met gisteren, de
  // laatste volledige dag.
  const [profileTo, setProfileTo] = useState(() => mpTodayIso());
  const [profileRaw, setProfileRaw] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const profileDays = useMemo(() => dagenInVenster(profileFrom, profileTo) ?? 0, [profileFrom, profileTo]);
  const profileValid = profileDays >= 1 && profileDays <= 366;

  const loadProfile = async () => {
    if (!profileValid) return;
    setProfileLoading(true); setProfileError(null);
    try {
      // De API verwacht de laatste dag die meetelt; het scherm vraagt "tot".
      const data = await apiFetch(`/api/market/export?from=${profileFrom}&to=${laatsteDag(profileTo)}`);
      setProfileRaw(data);
    } catch (e) { setProfileError(e.message); setProfileRaw(null); }
    finally { setProfileLoading(false); }
  };

  useEffect(() => {
    if (!profileValid) return;
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileFrom, profileTo]);

  const profileChart = useMemo(() => {
    const rows = profileRaw?.rows || [];
    const buckets = new Map();
    for (let h = 0; h < 24; h++) {
      for (let q = 0; q < 60; q += 15) {
        const time = `${String(h).padStart(2, '0')}:${String(q).padStart(2, '0')}`;
        buckets.set(time, { time, spotSum: 0, spotN: 0, imbSum: 0, imbN: 0 });
      }
    }
    for (const r of rows) {
      const b = buckets.get(r.time);
      if (!b) continue;
      if (r.spotEurMwh != null) { b.spotSum += r.spotEurMwh; b.spotN++; }
      if (r.imbalanceEurMwh != null) { b.imbSum += r.imbalanceEurMwh; b.imbN++; }
    }
    return [...buckets.values()].map(b => ({
      time: b.time,
      belpexAvg: b.spotN > 0 ? Math.round((b.spotSum / b.spotN) * 100) / 100 : null,
      eliaAvg: b.imbN > 0 ? Math.round((b.imbSum / b.imbN) * 100) / 100 : null,
      n: Math.max(b.spotN, b.imbN),
    }));
  }, [profileRaw]);

  // ── Gasprijzen (tab "Gasprijzen") ──────────────────────────────────────────
  // Day-ahead gas voor twee hubs: TTF (NL) en ZTP (BE), in de EEX-notering. Het is
  // één prijs per gasdag — geen kwartieren — dus de reeks komt in één call per type
  // over het hele venster binnen (/api/lynx/gas/dayahead).
  //
  // Een reeks die leeg terugkomt verdwijnt niet stil: ze houdt haar regel bij
  // "Beschikbaarheid" mét de reden. Zo is het verschil tussen "geen prijzen in deze
  // periode" en "er ging iets mis" op het scherm te zien.
  const [gasVan, setGasVan] = useState(() => mpShiftIso(mpTodayIso(), -89));
  // "Tot" telt niet mee (src/datumFilter.js): morgen = tot en met vandaag.
  const [gasTot, setGasTot] = useState(() => mpTomorrowIso());
  const [gasData, setGasData] = useState(null);
  const [gasLaadt, setGasLaadt] = useState(false);
  const [gasFout, setGasFout] = useState(null);
  const gasDagen = useMemo(() => dagenInVenster(gasVan, gasTot) ?? 0, [gasVan, gasTot]);
  const gasGeldig = gasDagen >= 1 && gasDagen <= 1830;

  const laadGas = useCallback(async () => {
    if (!gasGeldig) return;
    setGasLaadt(true); setGasFout(null);
    try {
      // De API verwacht de laatste dag die meetelt; het scherm vraagt "tot".
      setGasData(await apiFetch(`/api/lynx/gas/dayahead?from=${gasVan}&to=${laatsteDag(gasTot)}`));
    } catch (e) { setGasFout(e.message); setGasData(null); }
    finally { setGasLaadt(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gasVan, gasTot, gasGeldig]);

  // Alleen ophalen wanneer de tab ook echt open staat: vier Lynx-calls voor een
  // tab die niemand bekijkt is verspilling, en de meeste bezoekers komen voor de
  // elektriciteitsprijzen.
  useEffect(() => {
    if (mobileTab !== 'gas' || !gasGeldig) return;
    laadGas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileTab, gasVan, gasTot]);

  const gasSeries = useMemo(() => gasData?.series || [], [gasData]);
  const gasMetData = useMemo(() => gasSeries.filter((s) => (s.items || []).length > 0), [gasSeries]);
  const gasChartRijen = useMemo(() => gasRijen(gasMetData), [gasMetData]);
  const gasVergelijking = useMemo(() => gasVergelijkingen(gasMetData), [gasMetData]);

  const exportGas = () => {
    const kolommen = [
      { header: 'Datum', get: (r) => mpFormatIsoBe(r.date), text: true },
      { header: 'Sleutel (ISO)', get: (r) => r.date, text: true },
      ...gasMetData.map((s) => ({
        header: `${gasSerieMeta(s.type)?.label || s.type} (EUR/MWh)`,
        get: (r) => (r[s.type] == null ? null : r[s.type]),
      })),
    ];
    exportRowsToCsv(`gasprijzen-${gasVan}_${gasTot}.csv`, kolommen, gasChartRijen);
  };

  // ── Maandgemiddelden spot + onbalans sinds januari 2024 (tab Elektriciteit) ──
  // Komt uit het prijsarchief van de server (/api/market/maandgemiddelden, ~35
  // kleine rijen) — geen kwartierdata naar de browser. Een maand die de jobs nog
  // niet opgehaald hebben, blijft leeg; dat zegt het scherm er ook bij.
  const [maandGem, setMaandGem] = useState(null);
  const [maandGemFout, setMaandGemFout] = useState(null);
  useEffect(() => {
    let weg = false;
    apiFetch('/api/market/maandgemiddelden?van=2024-01')
      .then((d) => { if (!weg) { setMaandGem(d); setMaandGemFout(null); } })
      .catch((e) => { if (!weg) setMaandGemFout(e.message); });
    return () => { weg = true; };
  }, [apiFetch]);
  const maandGemRijen = useMemo(() => (maandGem?.maanden || []).map((m) => ({
    ...m, label: maandNaamKort(m.maand), spotGem: m.spot ? m.spot.gem : null, onbalansGem: m.onbalans ? m.onbalans.gem : null,
  })), [maandGem]);
  const maandGemMetData = useMemo(() => maandGemRijen.filter((m) => m.spot || m.onbalans), [maandGemRijen]);
  // Jaargemiddelden (resolutie "Jaar" van de grafiek): zelfde endpoint, per kalenderjaar.
  const [jaarGem, setJaarGem] = useState(null);
  const [jaarGemFout, setJaarGemFout] = useState(null);
  useEffect(() => {
    let weg = false;
    apiFetch('/api/market/maandgemiddelden?van=2024-01&per=jaar')
      .then((d) => { if (!weg) { setJaarGem(d); setJaarGemFout(null); } })
      .catch((e) => { if (!weg) setJaarGemFout(e.message); });
    return () => { weg = true; };
  }, [apiFetch]);
  // Resolutie Maand/Jaar: de grafiek toont gemiddelden per periode (staven) in
  // plaats van kwartieren. Zelfde rijvorm voor beide, zodat grafiek, kerncijfers
  // en rapport één pad delen.
  const periodiek = combinedView === 'month' || combinedView === 'year';
  const periodiekRijen = useMemo(() => {
    if (combinedView === 'month') return maandGemRijen.map((m) => ({ key: m.maand, label: m.label, spotGem: m.spotGem, onbalansGem: m.onbalansGem, spot: m.spot, onbalans: m.onbalans, dagenIn: m.dagenInMaand }));
    if (combinedView === 'year') return (jaarGem?.jaren || []).map((j) => ({ key: j.jaar, label: j.jaar, spotGem: j.spot ? j.spot.gem : null, onbalansGem: j.onbalans ? j.onbalans.gem : null, spot: j.spot, onbalans: j.onbalans, dagenIn: j.dagenInJaar }));
    return [];
  }, [combinedView, maandGemRijen, jaarGem]);
  const periodiekMetData = periodiekRijen.filter((r) => r.spotGem != null || r.onbalansGem != null);
  const periodiekStats = (sleutel) => {
    const v = periodiekRijen.map((r) => r[sleutel]).filter((x) => x != null);
    if (!v.length) return null;
    return { avg: Math.round((v.reduce((a, x) => a + x, 0) / v.length) * 10) / 10, min: Math.min(...v), max: Math.max(...v), laatste: v[v.length - 1] };
  };
  // Wat de kerncijferbalk toont: kwartierstatistiek (dag/week) of periodestatistiek (maand/jaar).
  const kpiSpotStats = periodiek ? periodiekStats('spotGem') : spotStats;
  const kpiImbStats = periodiek ? periodiekStats('onbalansGem') : imbStats;
  const kpiSpotNu = periodiek ? (kpiSpotStats ? kpiSpotStats.laatste : null) : spotNow;
  const periodeWoord = combinedView === 'month' ? 'maand' : 'jaar';
  const exportMaandGem = () => exportRowsToCsv(`maandgemiddelden-spot-onbalans_${maandGem?.van || '2024-01'}_${maandGem?.tot || ''}.csv`, [
    { header: 'Maand', get: (r) => r.maand, text: true },
    { header: 'Spot gemiddeld (EUR/MWh)', get: (r) => r.spotGem },
    { header: 'Spot laagste (EUR/MWh)', get: (r) => (r.spot ? r.spot.min : null) },
    { header: 'Spot hoogste (EUR/MWh)', get: (r) => (r.spot ? r.spot.max : null) },
    { header: 'Dagen spot', get: (r) => (r.spot ? r.spot.dagen : null) },
    { header: 'Onbalans gemiddeld (EUR/MWh)', get: (r) => r.onbalansGem },
    { header: 'Onbalans laagste (EUR/MWh)', get: (r) => (r.onbalans ? r.onbalans.min : null) },
    { header: 'Onbalans hoogste (EUR/MWh)', get: (r) => (r.onbalans ? r.onbalans.max : null) },
    { header: 'Dagen onbalans', get: (r) => (r.onbalans ? r.onbalans.dagen : null) },
    { header: 'Dagen in maand', get: (r) => r.dagenInMaand },
  ], maandGemRijen);

  // ── PDF-rapport per tabblad (22-10-2026) ────────────────────────────────────
  // Zelfde weg als het meetrapport van Metering: de rapportmodule bouwt HTML uit
  // precies wat er op het scherm staat (src/marktRapport.js), de server maakt er
  // een PDF van (POST /api/report/pdf). Huisstijl = de eigen huisstijl van de
  // gebruiker; voor admin/ET (geen partnerhuisstijl) blijft het rapport merkloos.
  const [mpPdfBezig, setMpPdfBezig] = useState(null);
  const mpMerk = () => {
    const isEt = (v) => /energy\s*together/i.test(String(v || ''));
    const eigen = branding && !isEt(branding.energy_label) && !isEt(branding.display_name) ? branding : null;
    return {
      primair: eigen?.primary_color || '#4f46e5', font: eigen?.font_family || null, logoDataUrl: eigen?.logo_data_url || null,
      labelNaam: eigen?.display_name || eigen?.energy_label || hideEtBrand(currentUser?.energyLabel || ''),
    };
  };
  const bouwMpRapport = (tab) => {
    const merk = mpMerk();
    const nu = new Intl.DateTimeFormat('nl-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
    if (tab === 'actueel') {
      return actueelRapport({
        merk, nu, weergave: combinedView, datum: date, datumLang: mpFormatLongDate(date), week,
        // Maand/Jaar: de periodegemiddelden in de rijvorm van het rapport (staven).
        rijen: periodiek
          ? periodiekRijen.map(r => ({ key: r.key, label: r.label, spotEurMwh: r.spotGem, imbalanceEurMwh: r.onbalansGem, spot: r.spot, onbalans: r.onbalans, dagenIn: r.dagenIn }))
          : merged,
        toon: { spot: showSpot, onbalans: showImbalance, zon: showSolar && !periodiek, wind: showWind && !periodiek },
        aanwezig: periodiek ? { spotActual: showSpot, imbalanceActual: showImbalance } : seriesPresence,
        stats: { spot: kpiSpotStats, onbalans: kpiImbStats }, spotNu: kpiSpotNu, laatsteOnbalans: periodiek ? null : latestActual, nuKwartier: nowQuarter,
        verouderd: !!(spot?.stale || imbalance?.stale || weekData?.failedDays > 0), maanden: maandGem?.maanden || [],
      });
    }
    if (tab === 'gas') return gasRapport({ merk, nu, van: gasVan, tot: gasTot, dagen: gasDagen, reeksen: gasSeries, metData: gasMetData, rijen: gasChartRijen, vergelijking: gasVergelijking });
    if (tab === 'forward') {
      // Zelfde ladder en kleuren als de twee grafieken op het scherm.
      const contracten = forwardLadder(mpSharedForwardSeries(MP_FORWARD_CHARTS.map(c => lynxForward[c.product])), forwardLadderKeuze);
      return forwardRapport({
        merk, nu, dagen: lynxForwardDays, ladder: forwardLadderKeuze, contracten, kleuren: MP_FORWARD_LINE_COLORS,
        grafieken: MP_FORWARD_CHARTS.map(c => ({
          label: c.label, unit: c.unit, chart: lynxForward[c.product]?.chart || [],
          verborgen: contracten.filter(n => hiddenForward != null && hiddenForward.has(`${c.product}|${n}`)),
        })),
        // De tabel Maandprijzen zoals ze op het scherm staat (met of zonder historische maanden).
        maandprijzen: { rijen: forwardTabelRijen, producten: forwardTabelProducten.map(p => ({ id: p.id, label: p.label, notering: p.notering?.datum || '' })), huidigeMaand: forwardHuidigeMaand },
      });
    }
    if (tab === 'live') return liveRapport({ merk, nu, kalibratie: minute?.calibration || null, verouderd: !!minute?.stale, laatste: latestActual, forecasts: upcomingForecasts, venster: minuteWindow, nuTijd: minute?.nowDatetime || null });
    return profielRapport({ merk, nu, van: profileFrom, tot: profileTo, dagen: profileDays, datapunten: profileRaw?.rows?.length || 0, gefaald: profileRaw?.failedDays || 0, rijen: profileChart });
  };
  const exportMpRapport = async (tab) => {
    if (mpPdfBezig) return;
    setMpPdfBezig(tab);
    try {
      const r = await fetch('/api/report/pdf', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/html; charset=utf-8' },
        body: bouwMpRapport(tab),
      });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || `HTTP ${r.status}`); }
      const naam = { actueel: 'elektriciteitsprijzen', gas: 'gasprijzen', forward: 'forward', live: 'live-onbalans', profiel: 'dagprofiel' }[tab];
      const periode = tab === 'actueel' ? (combinedView === 'week' ? `${week.from}_${week.to}` : date)
        : tab === 'gas' ? `${gasVan}_${gasTot}` : tab === 'profiel' ? `${profileFrom}_${profileTo}`
          : tab === 'forward' ? `${lynxForwardDays}d_${mpTodayIso()}` : mpTodayIso();
      downloadBlob(`${exportTimestamp()}marktprijzen-${naam}_${periode}.pdf`, await r.blob());
      toast.success('Rapport als PDF gedownload');
    } catch (e) {
      toast.error(`PDF maken mislukt: ${e.message}`);
    } finally { setMpPdfBezig(null); }
  };

  return (
    <div className="grafiek-modern">
      {/* Eigen titelrij i.p.v. PageHeader (die zet mb-6 en zou de grafiek op een
          telefoon onder de vouw duwen). De docs-iconen en de feedbackknop staan
          er wél bij, net als op elke andere paginatitel: de functionele en de
          technische beschrijving komen uit moduleDocs.js (sleutel `market`). */}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <h1 className="text-2xl font-bold text-slate-100">Marktprijzen</h1>
        <PageDocsIcons />
        <FeedbackKnop />
      </div>

      {/* Tabbalk — op ALLE schermformaten: horizontale pill-rij (scrollbaar op mobiel,
          compact op desktop, neemt niet de volle breedte in). Welke tabs er staan,
          hangt af van de subpagina's die deze gebruiker toegekend kreeg. */}
      {geenMpTabs && <div className="mt-3"><GeenSubpaginas module="Market Prices" /></div>}
      <div className="mt-3 -mx-1 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 px-1 w-max">
          {mpTabs.map(t => (
            <button key={t.id} onClick={() => setMobileTab(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${mobileTab === t.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-600'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className={toonMpTab('actueel') ? '' : 'hidden'}>

        {/* ── Dashboardsectie: bediening + legenda + grafiek + export in één kaart ── */}
        <div className="mt-4 bg-slate-900/40 border border-slate-800 rounded-xl p-3 md:p-4">
        {/* Bediening boven de grafiek: prev · datum · next · Vandaag/Morgen ·
            Dag/Week · export. Dag/Week staat direct naast Vandaag/Morgen (geen
            ml-auto meer); de export staat rechtsboven in het blok i.p.v. onder
            de grafiek. */}
        {/* Op mobiel BEWUST geen flex-wrap: datum, Vandaag/Morgen, Dag/Week en de
            export horen op één regel. Zo blijft er verticaal genoeg over om de
            grafiek meteen in beeld te hebben. Vanaf `sm` mag het weer wrappen. */}
        <div className="flex flex-nowrap sm:flex-wrap items-center gap-1 sm:gap-2">
          {combinedView === 'day' ? <MpDateSelector value={date} onChange={setDate} />
            : combinedView === 'week' ? <MpWeekSelector value={date} onChange={setDate} />
              : <span className="text-[11px] sm:text-xs text-slate-400 px-1 whitespace-nowrap" title="Gemiddelden per periode uit het prijsarchief, vanaf januari 2024">Gemiddelden sinds jan 2024</span>}
          {/* Resolutie: Dag en Week tekenen kwartieren; Maand en Jaar het gemiddelde
              per periode (staven) uit het prijsarchief. */}
          <div className="flex bg-slate-800 border border-slate-700 rounded-lg p-0.5 text-[11px] sm:text-xs shrink-0">
            {[['day', 'Dag'], ['week', 'Week'], ['month', 'Maand'], ['year', 'Jaar']].map(([v, l]) => (
              <button key={v} onClick={() => setCombinedView(v)} className={`px-1.5 sm:px-3 py-1 sm:py-1.5 rounded-md font-medium ${combinedView === v ? 'bg-lime-500 text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}>{l}</button>
            ))}
          </div>
          <div className="ml-auto shrink-0 flex items-center gap-1.5">
            <MpExportPopover from={expFrom} to={expTo} setFrom={setExpFrom} setTo={setExpTo}
              rangeDays={expRangeDays} rangeValid={expRangeValid} exporting={exporting} error={exportError} onExport={handleExport} />
            <ExportKnop soort="pdf" onExport={() => exportMpRapport('actueel')} busy={mpPdfBezig === 'actueel'} disabled={loading || (periodiek ? !periodiekMetData.length : !seriesPresence.hasAnyData)}
              label="Rapport als PDF: kerncijfers, grafiek, maandgemiddelden en prijsdetail van de getoonde periode" />
          </div>
        </div>

        {/* ── Statistieken van de GESELECTEERDE prijsreeksen ──
            Vervangt de vroegere losse KPI-balk bovenaan de pagina. Staat nu in
            dezelfde kaart als de grafiek en volgt de reeks-selectie: enkel de
            aangevinkte prijsreeksen krijgen een blok. Zon en wind blijven bewust
            zonder cijfers (dat zijn MW-reeksen, geen prijzen). */}
        {(() => {
          const groepen = [];
          // Maand/Jaar: de cijfers gaan over de periodegemiddelden (laatste
          // periode, gemiddelde van de gemiddelden, laagste/hoogste periode).
          if (showSpot && (kpiSpotStats || kpiSpotNu != null)) groepen.push(
            <MpStatGroup key="spot" title="Spotprijs" color={MP_COLORS.belpex} cells={[
              periodiek
                ? { label: `Laatste ${periodeWoord}`, short: 'Laatste', value: mpNum(kpiSpotNu, 2) }
                : { label: 'Spotprijs nu', short: 'Nu', value: mpNum(kpiSpotNu) },
              { label: 'Gemiddelde', short: 'Gem.', value: mpNum(kpiSpotStats?.avg) },
              { label: periodiek ? `Laagste / hoogste ${periodeWoord}` : 'Min / max', desktopOnly: true, value: `${mpNum(kpiSpotStats?.min, 2)} / ${mpNum(kpiSpotStats?.max, 2)}` },
            ]} />
          );
          if (showImbalance && (kpiImbStats || (!periodiek && latestActual))) groepen.push(
            <MpStatGroup key="imb" title="Onbalansprijs" color={MP_COLORS.elia15} cells={[
              // Tijdstip in het label i.p.v. een aparte cel: dat zou exact hetzelfde
              // bedrag herhalen als "Onbalans nu".
              periodiek
                ? { label: `Laatste ${periodeWoord}`, short: 'Laatste', value: mpNum(kpiImbStats?.laatste, 2) }
                : { label: latestActual ? `Onbalans nu · ${latestActual.time}` : 'Onbalans nu', short: latestActual ? `Nu ${latestActual.time}` : 'Nu', value: mpNum(latestActual?.imbalanceEurMwh, 2) },
              { label: 'Gemiddelde', short: 'Gem.', value: mpNum(kpiImbStats?.avg) },
              { label: periodiek ? `Laagste / hoogste ${periodeWoord}` : 'Min / max', desktopOnly: true, value: `${mpNum(kpiImbStats?.min, 2)} / ${mpNum(kpiImbStats?.max, 2)}` },
            ]} />
          );
          if (!groepen.length) return null;
          return (
            // Mobiel: de reeksen ONDER elkaar (elk één compacte regel) i.p.v. naast
            // elkaar met een schuifbalk — twee kolommen van 260px passen simpelweg
            // niet op een telefoon. Vanaf `sm` weer naast elkaar.
            <div className="mt-2 sm:mt-3 border border-slate-800 rounded-lg">
              <div className="flex flex-col sm:flex-row items-stretch divide-y sm:divide-y-0 sm:divide-x divide-slate-800 min-w-0">{groepen}</div>
            </div>
          );
        })()}

        {/* ── Legenda / reeks-selectie: korte labels, direct boven de grafiek ── */}
        <div className="mt-2 sm:mt-3 flex flex-wrap gap-x-3 sm:gap-x-5 gap-y-1 sm:gap-y-2 items-center">
          <MpSeriesToggle label="Spotprijs" short="Spot" color={MP_COLORS.belpex} checked={showSpot} onChange={setShowSpot} />
          <MpSeriesToggle label="Onbalansprijs" short="Onbalans" color={MP_COLORS.elia15} checked={showImbalance} onChange={setShowImbalance} />
          {!periodiek && <MpSeriesToggle label="Zon" color={MP_COLORS.solar} checked={showSolar} onChange={setShowSolar} />}
          {!periodiek && <MpSeriesToggle label="Wind" color={MP_COLORS.wind} checked={showWind} onChange={setShowWind} />}
          {periodiek && <span className="text-[11px] text-slate-500">gemiddelde per {periodeWoord} over alle kwartieren · een ontbrekende staaf zit nog niet in het prijsarchief</span>}
          {(spot?.stale || imbalance?.stale || weekData?.failedDays > 0) && <span className="text-[11px] text-amber-400">verouderde data</span>}
        </div>

        {combinedErr && <div className="mt-3 text-red-300 text-sm">{combinedErr}</div>}
        {loading && <div className="mt-3 text-slate-400 text-sm">Laden…</div>}

        {/* ── Maand/Jaar: staven met het gemiddelde per periode ── */}
        {periodiek && (periodiekMetData.length > 0 ? (
          <div className="h-[340px] md:h-[460px] lg:h-[500px] mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={periodiekRijen} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#1e293b" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} {...CHART_AS_X} interval={periodiekRijen.length > 18 ? 1 : 0} />
                <YAxis stroke="#64748b" fontSize={11} width={44} {...CHART_AS_Y} />
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, name) => [v == null ? '—' : `${mpNum(v, 2)} €/MWh`, name]} />
                <Legend {...CHART_LEGENDA} />
                <ReferenceLine y={0} stroke="#475569" />
                {showSpot && <Bar dataKey="spotGem" name={`Spotprijs (gemiddelde per ${periodeWoord})`} fill={MP_COLORS.belpex} maxBarSize={combinedView === 'year' ? 64 : 22} radius={[3, 3, 0, 0]} />}
                {showImbalance && <Bar dataKey="onbalansGem" name={`Onbalansprijs (gemiddelde per ${periodeWoord})`} fill={MP_COLORS.elia15} maxBarSize={combinedView === 'year' ? 64 : 22} radius={[3, 3, 0, 0]} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="mt-3 text-slate-400 text-sm">
            {maandGemFout || jaarGemFout || (maandGem && (combinedView === 'month' || jaarGem) ? 'Nog geen gemiddelden in het prijsarchief. Laat de jobs "historiek ophalen" draaien.' : 'Laden…')}
          </div>
        ))}

        {/* Geen grafiek pas als er HELEMAAL geen data is (ook geen zon/wind-
            forecast). Pas tonen nadat het laden klaar is, zodat we nooit de
            vorige dag of een misleidende lijn tonen. */}
        {!periodiek && !loading && !combinedErr && !seriesPresence.hasAnyData && (
          <div className="mt-3 text-slate-400 text-sm">
            {combinedView === 'day' && date > mpTodayIso()
              ? 'Day-ahead spot en onbalansprijzen zijn nog niet gepubliceerd voor deze datum.'
              : 'Geen prijsdata beschikbaar voor deze datum.'}
          </div>
        )}

        {seriesPresence.hasAnyData && merged.length > 0 && (
          <>
          {/* As-eenheden boven de grafiek: €/MWh links, GW rechts (zon/wind). Op een
              telefoon valt het GW-bijschrift weg, want daar is de rechteras zelf ook
              verborgen. Zon/wind komen in MW binnen (duizenden); ÷1000 naar GW leest
              rustiger. De onderliggende data blijft MW — enkel de weergave verandert. */}
          <div className="mt-2 flex justify-between text-[11px] text-slate-500 px-1">
            <span>€/MWh</span><span className="hidden sm:inline">GW</span>
          </div>
          <div className="h-[340px] md:h-[460px] lg:h-[500px] mt-0.5">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged} margin={{ top: 5, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#1e293b" />
                {combinedView === 'day' ? (
                  // Toon alleen het uur op het volle uur, zonder ":00" (bv. "01"
                  // i.p.v. "01:00") — scheelt ruimte en oogt rustiger.
                  <XAxis tickLine={false} dataKey="time" stroke="#64748b" fontSize={11} tickMargin={6} tickFormatter={t => t.endsWith(':00') ? t.slice(0, 2) : ''} interval={0} />
                ) : (
                  <XAxis tickLine={false} dataKey="key" stroke="#64748b" fontSize={10} tickMargin={6} tickFormatter={k => k.endsWith(' 00:00') ? mpFormatShortDate(k.substring(0, 10)) : ''} interval={0} />
                )}
                {/* Prijs-as links (€/MWh), MW-as rechts. Verschillende eenheden
                    blijven op aparte assen want de schalen lopen ver uiteen.
                    Beide gebruiken expliciete ronde ticks (zie yAxisDomains). */}
                {/* Prijs-as leeg laten als er geen prijsdata is (bv. toekomstige
                    dag met enkel zon/wind-forecast) — anders toont recharts
                    misleidende mini-ticks afgeleid van de MW-as. */}
                <YAxis axisLine={false} tickLine={false} yAxisId="price" orientation="left" stroke="#64748b" fontSize={11} width={44}
                  domain={yAxisDomains.price.domain} ticks={yAxisDomains.price.ticks} allowDataOverflow={false}
                  tickFormatter={v => yAxisDomains.price.hasData ? `${v}` : ''} />
                {/* Rechteras (zon/wind) in GW. Op een TELEFOON verborgen — daar
                    telt elke pixel breedte en is de exacte productiewaarde
                    bijzaak (de tooltip noemt ze wél). Op tablet/desktop gewoon
                    zichtbaar. De as schaalt de reeksen in beide gevallen.
                    MW kan fysisch niet negatief zijn voor zon/wind: het negatieve
                    as-segment is enkel padding om de 0-lijn met de prijs-as te
                    laten samenvallen. Vandaar geen negatieve ticks, geen gedeelde
                    0 (die toont de prijs-as al) en niets als er geen data is. */}
                <YAxis axisLine={false} tickLine={false} yAxisId="mw" orientation="right" hide={!toonGwAs} stroke="#64748b" fontSize={11} width={44}
                  domain={yAxisDomains.mw.domain} ticks={yAxisDomains.mw.ticks} allowDataOverflow={false}
                  tickFormatter={v => (yAxisDomains.mw.hasData && v > 0) ? mpGw(v) : ''} />
                <Tooltip contentStyle={CHART_TOOLTIP}
                  formatter={(v, name) => {
                    if (v == null) return ['—', name];
                    if (name.startsWith('Zon') || name.startsWith('Wind')) return [`${mpGw(v)} GW`, name];
                    return [`${v} €/MWh`, name];
                  }}
                  labelFormatter={k => combinedView === 'week' ? `${mpFormatShortDate(k.substring(0, 10))} ${k.substring(11)}` : `Kwartier ${k}`} />
                <ReferenceLine yAxisId="price" y={0} stroke="#475569" />
                {combinedView === 'day' && nowQuarter && merged.some(m => m.time === nowQuarter) && (
                  <ReferenceLine yAxisId="price" x={nowQuarter} stroke="#84cc16" strokeWidth={1.5} strokeDasharray="4 3" label={{ value: 'nu', position: 'insideTopRight', offset: 6, fill: '#84cc16', fontSize: 11, fontWeight: 600 }} />
                )}
                {showSpot && seriesPresence.spotActual && <Line yAxisId="price" type="stepAfter" name="Spotprijs" dataKey="spotEurMwh" stroke={MP_COLORS.belpex} strokeWidth={2} dot={false} connectNulls={false} />}
                {showSpot && seriesPresence.spotForecast && <Line yAxisId="price" type="stepAfter" name="Spotprijs (forecast)" dataKey="spotForecastEurMwh" stroke={MP_COLORS.belpex} strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls={false} />}
                {showImbalance && seriesPresence.imbalanceActual && <Line yAxisId="price" type="stepAfter" name="Onbalansprijs (Elia)" dataKey="imbalanceEurMwh" stroke={MP_COLORS.elia15} strokeWidth={2} dot={false} connectNulls={false} />}
                {showImbalance && seriesPresence.imbalanceForecast && <Line yAxisId="price" type="stepAfter" name="Onbalansprijs (forecast)" dataKey="imbalanceForecastEurMwh" stroke={MP_COLORS.elia15} strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls={false} />}
                {/* Solar/wind: twee lijnen per series — volle lijn voor measured/realtime,
                    stippellijn voor forecast (toekomstige data). Dezelfde kleur, dezelfde
                    legend-naam is OK want recharts groepeert ze niet automatisch — we voegen
                    " (forecast)" toe aan de naam zodat de legend duidelijk blijft. */}
                {showSolar && <Line yAxisId="mw" type="monotone" name="Zon" dataKey="solarMwActual" stroke={MP_COLORS.solar} strokeWidth={2} dot={false} connectNulls={false} />}
                {showSolar && <Line yAxisId="mw" type="monotone" name="Zon (forecast)" dataKey="solarMwForecast" stroke={MP_COLORS.solar} strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls={false} />}
                {showWind && <Line yAxisId="mw" type="monotone" name="Wind" dataKey="windMwActual" stroke={MP_COLORS.wind} strokeWidth={2} dot={false} connectNulls={false} />}
                {showWind && <Line yAxisId="mw" type="monotone" name="Wind (forecast)" dataKey="windMwForecast" stroke={MP_COLORS.wind} strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls={false} />}
              </LineChart>
            </ResponsiveContainer>
          </div>
          </>
        )}

        </div>

        {/* ── Prijsdetail: spot + onbalans per kwartier, naast elkaar ──
            Vervangt de vroegere twee blokken met losse gemiddelden/min/max (die
            staan nu boven de grafiek). In weekweergave bevat `merged` meerdere
            dagen; dan blader je per dag met vorige/volgende. De kwartieren staan
            in vier kolommen naast elkaar, zodat een volledige dag (96 rijen) in
            één scherm past. */}
        {(() => {
          if (periodiek) return null; // Maand/Jaar: de maandtabel staat eronder.
          const dagen = combinedView === 'week'
            ? [...new Set(merged.map(r => r.date).filter(Boolean))].sort()
            : [date];
          if (!dagen.length || !merged.length) return null;
          const idx = Math.min(detailDag, dagen.length - 1);
          const actieveDag = dagen[idx];
          const rijen = combinedView === 'week' ? merged.filter(r => r.date === actieveDag) : merged;
          const waarde = (r, a, f) => (r[a] != null ? r[a] : (r[f] != null ? r[f] : null));
          const fmt = (v) => v == null ? '—' : Number(v).toFixed(2);
          // In vier gelijke stukken hakken → vier kolommen naast elkaar.
          const stukGrootte = Math.ceil(rijen.length / 4);
          const stukken = [0, 1, 2, 3]
            .map(i => rijen.slice(i * stukGrootte, (i + 1) * stukGrootte))
            .filter(s => s.length);

          return (
            <details className="mt-3 group bg-slate-900/40 border border-slate-800 rounded-xl">
              <summary className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-200"><TrendingUp size={16} className="text-lime-400" />Prijsdetail per kwartier</span>
                <ChevronDown size={16} className="text-slate-500" />
              </summary>
              <div className="px-3 pb-3 pt-2 border-t border-slate-800">
                {/* Dagnavigatie: enkel zinvol wanneer er meerdere dagen in beeld zijn. */}
                {dagen.length > 1 && (
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <button onClick={() => setDetailDag(Math.max(0, idx - 1))} disabled={idx === 0}
                      className="px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-slate-300 disabled:opacity-40 hover:border-slate-600"
                      aria-label="Vorige dag"><ChevronLeft size={14} /></button>
                    <span className="text-xs font-semibold text-slate-200 tabular-nums">{mpFormatLongDate(actieveDag)}</span>
                    <button onClick={() => setDetailDag(Math.min(dagen.length - 1, idx + 1))} disabled={idx === dagen.length - 1}
                      className="px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-slate-300 disabled:opacity-40 hover:border-slate-600"
                      aria-label="Volgende dag"><ChevronRight size={14} /></button>
                    <span className="text-[11px] text-slate-500">dag {idx + 1} van {dagen.length}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-3 gap-y-2">
                  {stukken.map((stuk, i) => (
                    <table key={i} className="w-full text-[11px] tabular-nums">
                      <thead>
                        <tr className="border-b border-slate-700/70">
                          <th className="py-1 pr-2 text-left font-medium text-slate-500">Tijd</th>
                          <th className="py-1 px-1 text-right font-medium" style={{ color: MP_COLORS.belpex }}>Spot</th>
                          <th className="py-1 pl-1 text-right font-medium" style={{ color: MP_COLORS.elia15 }}>Onbalans</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stuk.map(r => (
                          <tr key={r.key} className="border-b border-slate-800/50">
                            <td className="py-[3px] pr-2 text-slate-400">{r.time}</td>
                            <td className="py-[3px] px-1 text-right" style={{ color: MP_COLORS.belpex }}>{fmt(waarde(r, 'spotEurMwh', 'spotForecastEurMwh'))}</td>
                            <td className="py-[3px] pl-1 text-right" style={{ color: MP_COLORS.elia15 }}>{fmt(waarde(r, 'imbalanceEurMwh', 'imbalanceForecastEurMwh'))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-slate-500">Alle prijzen in €/MWh. Ontbrekende waarden zijn nog niet gepubliceerd.</p>
              </div>
            </details>
          );
        })()}

        {/* ── Maandgemiddelden sinds januari 2024 ──
            Per kalendermaand het gemiddelde van spot en onbalans, als staven met
            de tabel eronder. De cijfers komen uit het prijsarchief op de server;
            een maand die de jobs nog niet opgehaald hebben, blijft een gat. */}
        <div className="mt-3 bg-slate-900/40 border border-slate-800 rounded-xl p-3 md:p-4">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2 min-w-0"><BarChart3 size={16} className="text-lime-400 shrink-0" />Maandgemiddelden sinds januari 2024</h3>
            <ExportKnop onExport={exportMaandGem} disabled={!maandGemMetData.length} count={maandGemMetData.length} label="Exporteer de maandgemiddelden van spot en onbalans naar Excel" />
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">Gemiddelde spot- en onbalansprijs per kalendermaand (€/MWh), berekend over alle kwartieren van de maand. Een maand zonder staaf staat nog niet in het prijsarchief.</p>
          {maandGemFout && <div className="mt-3 text-red-300 text-sm">{maandGemFout}</div>}
          {!maandGem && !maandGemFout && <div className="mt-3 text-slate-400 text-sm">Laden…</div>}
          {maandGem && !maandGemMetData.length && <div className="mt-2"><TableEmpty message="Nog geen historiek in het archief voor deze maanden." icon={Folder} /></div>}
          {maandGemMetData.length > 0 && (
            <>
              <div className="h-56 sm:h-64 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={maandGemRijen} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#1e293b" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={10} {...CHART_AS_X} interval={maandGemRijen.length > 18 ? 1 : 0} />
                    <YAxis stroke="#64748b" fontSize={11} width={44} {...CHART_AS_Y} />
                    <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, name) => [v == null ? '—' : `${mpNum(v, 2)} €/MWh`, name]} />
                    <Legend {...CHART_LEGENDA} />
                    <ReferenceLine y={0} stroke="#475569" />
                    <Bar dataKey="spotGem" name="Spotprijs (maandgemiddelde)" fill={MP_COLORS.belpex} maxBarSize={18} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="onbalansGem" name="Onbalansprijs (maandgemiddelde)" fill={MP_COLORS.elia15} maxBarSize={18} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 max-h-80 overflow-auto border border-slate-800 rounded-lg">
                <table className="w-full text-xs tabular-nums">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="text-slate-400">
                      <th className="text-left px-3 py-1.5 font-medium">Maand</th>
                      <th className="text-right px-3 py-1.5 font-medium" style={{ color: MP_COLORS.belpex }}>Spot gem.</th>
                      <th className="text-right px-3 py-1.5 font-medium" style={{ color: MP_COLORS.elia15 }}>Onbalans gem.</th>
                      <th className="text-right px-3 py-1.5 font-medium">Dagen spot</th>
                      <th className="text-right px-3 py-1.5 font-medium">Dagen onbalans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...maandGemRijen].reverse().map((m) => {
                      const dekking = (r) => (r ? <span className={r.volledig ? '' : 'text-amber-300/90'} title={r.volledig ? 'volledige maand' : 'onvolledige maand: het gemiddelde gaat over een deel van de dagen'}>{r.dagen}/{m.dagenInMaand}</span> : <span className="text-slate-600">—</span>);
                      return (
                        <tr key={m.maand} className={`border-t border-slate-800/60 ${m.spot || m.onbalans ? 'text-slate-300' : 'text-slate-600'}`}>
                          <td className="px-3 py-1 whitespace-nowrap">{m.label}</td>
                          <td className="px-3 py-1 text-right" style={m.spot ? { color: MP_COLORS.belpex } : undefined}>{mpNum(m.spotGem, 2)}</td>
                          <td className="px-3 py-1 text-right" style={m.onbalans ? { color: MP_COLORS.elia15 } : undefined}>{mpNum(m.onbalansGem, 2)}</td>
                          <td className="px-3 py-1 text-right">{dekking(m.spot)}</td>
                          <td className="px-3 py-1 text-right">{dekking(m.onbalans)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">Nieuwste maand bovenaan. "Dagen" = het aantal dagen met prijzen in het archief tegenover het aantal dagen in de maand; oranje = onvolledig.</p>
            </>
          )}
        </div>
      </div>

      {/* ── Gasprijzen: TTF en ZTP day-ahead (EEX) ─────────────────────────────
          Eén prijs per gasdag (06:00→06:00 lokaal), dus dagpunten i.p.v.
          kwartieren. Kleur = hub (TTF oranje, ZTP blauw) — twee hues die ook
          zonder kleurzicht uit elkaar te houden zijn. Beide reeksen delen één
          y-as: het is dezelfde grootheid in dezelfde eenheid. */}
      <div className={toonMpTab('gas') ? '' : 'hidden'}>
      <MpSection
        title="Gasprijzen — day-ahead"
        subtitle={`TTF (NL) en ZTP (BE), EEX-notering · ${mpFormatIsoBe(gasVan)} tot ${mpFormatIsoBe(gasTot)} (${gasDagen} dagen) · prijzen per gasdag (06:00–06:00) in €/MWh`}
        right={
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Van</div>
              <BeDateInput value={gasVan} onChange={setGasVan} max={laatsteDag(gasTot) || undefined} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Tot</div>
              <BeDateInput value={gasTot} onChange={setGasTot} min={totNa(gasVan) || undefined} max={mpTomorrowIso()} />
            </div>
            {/* Vaste vensters: de vraag is bijna altijd "de laatste maanden" of
                "vorig jaar erbij", niet een precieze datum. */}
            <div className="flex items-center gap-1">
              {[{ l: '30 d', n: 29 }, { l: '90 d', n: 89 }, { l: '1 jaar', n: 364 }, { l: '3 jaar', n: 1094 }].map(({ l, n }) => (
                <button key={l} onClick={() => { const t = mpTodayIso(); setGasVan(mpShiftIso(t, -n)); setGasTot(totNa(t)); }}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 border border-slate-700 text-slate-300 hover:border-lime-500">{l}</button>
              ))}
            </div>
            <button onClick={laadGas} disabled={!gasGeldig || gasLaadt}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg text-sm">
              {gasLaadt ? 'Ophalen…' : 'Vernieuwen'}
            </button>
            <ExportKnop onExport={exportGas} disabled={!gasChartRijen.length} label="Exporteer de gasprijzen naar Excel" />
            <ExportKnop soort="pdf" onExport={() => exportMpRapport('gas')} busy={mpPdfBezig === 'gas'} disabled={!gasData || gasLaadt}
              label="Rapport als PDF: kerncijfers, grafiek, vergelijking, beschikbaarheid en dagwaarden van deze periode" />
          </div>
        }
      >
        {!gasGeldig && <div className="mt-3 text-amber-400 text-sm">Periode ongeldig of te lang (max 1830 dagen).</div>}
        {gasFout && <div className="mt-3 text-red-300 text-sm">{gasFout}</div>}
        {gasLaadt && !gasData && <div className="mt-3 flex justify-center py-8"><Spinner /></div>}

        {gasData && (
          <>
            {/* Kengetallen per reeks, in de kleur van haar lijn. */}
            <div className="mt-3 flex flex-wrap items-stretch divide-y sm:divide-y-0 sm:divide-x divide-slate-800 bg-slate-900/40 border border-slate-800 rounded-xl">
              {gasMetData.map((s) => {
                const meta = gasSerieMeta(s.type);
                return (
                  <MpStatGroup key={s.type} title={meta?.label || s.type} color={meta?.kleur} cells={[
                    { label: 'Laatste', short: 'Laatste', value: mpNum(s.stats?.laatste, 2) },
                    { label: 'Gemiddelde', short: 'Gem.', value: mpNum(s.stats?.gemiddelde, 2) },
                    { label: 'Min / max', desktopOnly: true, value: `${mpNum(s.stats?.min, 2)} / ${mpNum(s.stats?.max, 2)}` },
                  ]} />
                );
              })}
              {!gasMetData.length && <div className="px-4 py-3 text-sm text-slate-400">Geen gasprijzen in deze periode.</div>}
            </div>

            {gasChartRijen.length > 0 && (
              <div className="h-72 sm:h-80 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={gasChartRijen} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#1e293b" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} minTickGap={28}
                      tickFormatter={(d) => mpFormatIsoBe(d).substring(0, 5)} />
                    <YAxis axisLine={false} tickLine={false} stroke="#64748b" fontSize={11} unit=" €/MWh" width={78} domain={['auto', 'auto']} />
                    <Tooltip contentStyle={CHART_TOOLTIP}
                      formatter={(v, name) => [v == null ? '—' : `${Number(v).toFixed(3)} €/MWh`, name]}
                      labelFormatter={(d) => `Gasdag ${mpFormatIsoBe(d)}`} />
                    <Legend {...CHART_LEGENDA_LIJN} />
                    {gasMetData.map((s) => {
                      const meta = gasSerieMeta(s.type);
                      return (
                        <Line key={s.type} type="monotone" dataKey={s.type} name={meta?.label || s.type}
                          stroke={meta?.kleur} strokeWidth={2}
                          dot={false} connectNulls={false} />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Vergelijking: verschillen TTF en ZTP van elkaar? Dat is de vraag
                waarvoor deze tab bestaat, dus ze staat als cijfer op het scherm en
                niet als iets wat de lezer uit twee bijna gelijke lijnen moet
                afleiden. */}
            {gasVergelijking.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-slate-200">Vergelijking</h3>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-sm resp-card">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <th className="text-left font-medium py-1.5 pr-3">Paar</th>
                        <th className="text-right font-medium py-1.5 px-2">Dagen</th>
                        <th className="text-right font-medium py-1.5 px-2">Gem. verschil</th>
                        <th className="text-right font-medium py-1.5 px-2">Min</th>
                        <th className="text-right font-medium py-1.5 px-2">Max</th>
                        <th className="text-left font-medium py-1.5 pl-2">Grootste afwijking</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {gasVergelijking.map((v) => (
                        <tr key={v.id} className="border-b border-slate-800/60">
                          <td className="py-1.5 pr-3">{v.label}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{v.n}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{mpNum(v.gemiddelde, 3)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{mpNum(v.min, 3)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{mpNum(v.max, 3)}</td>
                          <td className="py-1.5 pl-2">
                            {v.identiek
                              ? <span className="text-lime-400">identieke waarden</span>
                              : <>{mpNum(v.grootsteAfwijking, 3)} €/MWh <span className="text-slate-500">op {mpFormatIsoBe(v.grootsteAfwijkingDag)}</span></>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Verschil = tweede reeks − eerste reeks, over de dagen waarop beide een prijs hebben.</p>
              </div>
            )}

            {/* Beschikbaarheid per reeks — de tweede open vraag: welke notering
                bestaat over welke periode? Ook de reeksen ZONDER data staan hier,
                mét reden; anders lijkt een ontbrekende reeks een storing. */}
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-slate-200">Beschikbaarheid per reeks</h3>
              <ul className="mt-2 space-y-1 text-xs">
                {gasSeries.map((s) => {
                  const meta = gasSerieMeta(s.type);
                  const dekking = gasDekking(s);
                  return (
                    <li key={s.type} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="inline-flex items-center gap-1.5 shrink-0">
                        <span className="w-3 h-0.5 rounded" style={{ background: meta?.kleur }} />
                        <span className="text-slate-300">{meta?.label || s.type}</span>
                        <span className="font-mono text-slate-600">{s.type}</span>
                      </span>
                      {dekking.dagen > 0 ? (
                        <span className="text-slate-500">
                          {dekking.dagen} dag{dekking.dagen === 1 ? '' : 'en'} · {mpFormatIsoBe(dekking.van)} t.e.m. {mpFormatIsoBe(dekking.tot)}
                          {dekking.ontbrekend > 0 && <span className="text-amber-300/90"> · {dekking.ontbrekend} dag(en) zonder prijs ({dekking.ontbrekendeDagen.map(mpFormatIsoBe).join(', ')}{dekking.ontbrekend > dekking.ontbrekendeDagen.length ? '…' : ''})</span>}
                        </span>
                      ) : (
                        <span className="text-amber-300/90">{s.reden || 'geen data'}</span>
                      )}
                      {(s.waarschuwingen || []).map((w, i) => <span key={i} className="text-rose-300/90">{w}</span>)}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* De dagwaarden zelf, ingeklapt: de grafiek is het antwoord, de tabel
                het controle-instrument. Nieuwste dag bovenaan. */}
            {gasChartRijen.length > 0 && (
              <details className="mt-4 group">
                <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200 select-none">
                  Dagwaarden tonen ({gasChartRijen.length} dagen)
                </summary>
                <div className="mt-2 max-h-96 overflow-auto">
                  <table className="w-full text-sm resp-card">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <th className="text-left font-medium py-1.5 pr-3">Gasdag</th>
                        {gasMetData.map((s) => (
                          <th key={s.type} className="text-right font-medium py-1.5 px-2" style={{ color: gasSerieMeta(s.type)?.kleur }}>
                            {gasSerieMeta(s.type)?.label || s.type}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {[...gasChartRijen].reverse().map((r) => (
                        <tr key={r.date} className="border-b border-slate-800/60">
                          <td className="py-1 pr-3 whitespace-nowrap">{mpFormatIsoBe(r.date)}</td>
                          {gasMetData.map((s) => (
                            <td key={s.type} className="py-1 px-2 text-right tabular-nums">{r[s.type] == null ? '—' : Number(r[s.type]).toFixed(3)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="mt-3 text-[11px] text-slate-500">
              Day-ahead-noteringen (EEX) per prijstype. Een gasdag loopt van 06:00 tot 06:00 lokale tijd; de datum is de dag waarop hij begint.
            </p>
          </>
        )}
      </MpSection>
      </div>

      <div className={toonMpTab('forward') ? '' : 'hidden'}>
      <MpSection
        title="Forward Market"
        subtitle="Evolutie van de maand-, kwartaal- en Cal-prijzen baseload · klik een prijs aan/uit"
        right={
          <div className="flex flex-wrap items-center gap-2">
            {/* Ladder: eerst x maanden, dan y kwartalen die erna beginnen, dan de
                Cal's erna. Twee keuzevelden i.p.v. een knop per contract: gas
                noteert 60+ maanden. */}
            <div className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-lg px-2 py-0.5 text-xs text-slate-300">
              <label className="flex items-center gap-1">Maanden
                <select value={forwardLadderKeuze.maanden} onChange={e => setForwardLadderKeuze(k => ({ ...k, maanden: Number(e.target.value) }))} className="bg-transparent text-slate-100 font-medium focus:outline-none" aria-label="Aantal maandprijzen in de grafiek">
                  {LADDER_MAAND_OPTIES.map(n => <option key={n} value={n} className="bg-slate-800">{n}</option>)}
                </select>
              </label>
              <span className="text-slate-600">·</span>
              <label className="flex items-center gap-1">Kwartalen
                <select value={forwardLadderKeuze.kwartalen} onChange={e => setForwardLadderKeuze(k => ({ ...k, kwartalen: Number(e.target.value) }))} className="bg-transparent text-slate-100 font-medium focus:outline-none" aria-label="Aantal kwartaalprijzen in de grafiek, na de laatste maand">
                  {LADDER_KWARTAAL_OPTIES.map(n => <option key={n} value={n} className="bg-slate-800">{n}</option>)}
                </select>
              </label>
              <span className="text-slate-600">·</span>
              <span title="Alle kalenderjaren die na het laatste gekozen contract beginnen">Cal erna</span>
            </div>
            <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-lg p-0.5">
              {MP_FORWARD_PERIODS.map(p => (
                <button
                  key={p.value}
                  onClick={() => setLynxForwardDays(p.value)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${lynxForwardDays === p.value ? 'bg-lime-500 text-slate-900' : 'text-slate-300 hover:bg-slate-700'}`}
                >{p.label}</button>
              ))}
            </div>
            <ExportKnop
              onExport={exportForwardCsv}
              disabled={!MP_FORWARD_CHARTS.some(c => lynxForward[c.product])}
              label="Exporteer ALLE forward-data (beide producten, alle Cal- én Q-reeksen) naar Excel"
            />
            <ExportKnop
              onExport={exportForwardMaandprijzen}
              soort="maand"
              busy={maandprijzenBezig}
              disabled={!MP_FORWARD_CHARTS.some(c => lynxForward[c.product]?.tradeDates?.length)}
              count={AANTAL_MAANDEN}
              label="Maandprijzen: één Power BE Base- en één TTF Gas Base-prijs per maand, van deze maand tot 3 jaar vooruit (maand → kwartaal → Cal), op de meest recente notering. CALC-kolom: kwartaal- of Cal-prijs verdeeld volgens het profiel van de laatst gekende maandprijzen"
            />
            <ExportKnop soort="pdf" onExport={() => exportMpRapport('forward')} busy={mpPdfBezig === 'forward'}
              disabled={!MP_FORWARD_CHARTS.some(c => lynxForward[c.product]?.chart?.length)}
              label="Rapport als PDF: Power BE en TTF Gas met de contracten van de ladder, laatst gekende prijzen, verschil over de periode en beide grafieken" />
          </div>
        }
      >
        {lynxForwardErr && <div className="mt-3 text-red-300 text-sm">{lynxForwardErr}</div>}
        {(() => {
        // Gedeelde reeksen (intersectie = Power BE-set) → ladder: dezelfde
        // contracten voor BEIDE grafieken. Verre TTF-jaren en de vijf jaar
        // gasmaanden blijven uit de grafiek; volledige data zit in de Export.
        const sharedSeries = forwardLadder(mpSharedForwardSeries(MP_FORWARD_CHARTS.map(c => lynxForward[c.product])), forwardLadderKeuze);
        const colorOf = (name) => MP_FORWARD_LINE_COLORS[Math.max(0, sharedSeries.indexOf(name)) % MP_FORWARD_LINE_COLORS.length];
        return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3 items-start">
          {MP_FORWARD_CHARTS.map(cfg => {
            const data = lynxForward[cfg.product];
            const isHidden = (name) => hiddenForward != null && hiddenForward.has(`${cfg.product}|${name}`);
            const visible = sharedSeries.filter(n => !isHidden(n));
            const lastDate = data && sharedSeries.length ? (mpLastKnown(data.chart, sharedSeries[0])?.label) : null;
            return (
              <div key={cfg.product} className="bg-slate-800/50 border border-slate-800/70 rounded-lg p-4 flex flex-col">
                <div className="mb-3">
                  <div className="text-sm font-medium text-slate-200">{cfg.label}</div>
                  <div className="text-[11px] text-slate-500">Laatst gekende prijzen{lastDate ? ` · ${lastDate}` : ''} · {cfg.unit} · klik om te tonen/verbergen</div>
                </div>
                {/* Kerngetallen + toggle per prijslijn — vaste hoogte zodat beide
                    grafieken op dezelfde y-positie starten (uitlijning). */}
                <div className="flex flex-wrap gap-2 mb-3 min-h-[112px] content-start">
                  {data && sharedSeries.map(name => {
                    const last = mpLastKnown(data.chart, name);
                    const hidden = isHidden(name);
                    const col = colorOf(name);
                    return (
                      <button
                        key={name}
                        onClick={() => toggleForwardSeries(cfg.product, name)}
                        title={hidden ? 'Klik om te tonen' : 'Klik om te verbergen'}
                        className={`text-left px-2.5 py-1.5 rounded-lg border w-[88px] transition-opacity ${hidden ? 'bg-slate-900/30 border-slate-800 opacity-45' : 'bg-slate-900/60 border-slate-700/50'}`}
                      >
                        <div className="flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: col }} />
                          <span className="text-[10px] uppercase tracking-wide" style={{ color: col }}>{name}</span>
                        </div>
                        <div className="text-base font-bold tabular-nums text-slate-100">{last ? last.value.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                      </button>
                    );
                  })}
                </div>
                {!data && !lynxForwardErr && <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Laden…</div>}
                {data && (sharedSeries.length === 0 || data.chart.length === 0) && (
                  <div className="h-64 flex items-center justify-center text-center text-slate-400 text-sm px-4">Geen forward data beschikbaar. Mogelijk heeft deze API key (nog) geen forward-abonnement of zijn er geen recente trade dates.</div>
                )}
                {data && sharedSeries.length > 0 && data.chart.length > 0 && (
                  <div className="h-64 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.chart} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="#1e293b" />
                        <XAxis tickLine={false} dataKey="tradeDateLabel" stroke="#64748b" fontSize={10} minTickGap={24} />
                        <YAxis axisLine={false} tickLine={false} stroke="#64748b" fontSize={11} unit=" €" width={48} domain={['auto', 'auto']} />
                        <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, name) => v != null ? [`${v} €/MWh`, name] : ['—', name]} />
                        {visible.map(name => (
                          <Line key={name} type="monotone" dataKey={name} stroke={colorOf(name)} strokeWidth={2} dot={false} connectNulls />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                    {visible.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs">Alle lijnen verborgen — klik een prijs hierboven aan.</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        );
        })()}

        {/* ── Maandprijzen: de meest recente notering per leveringsmaand ──
            Zelfde cijfers als de export "Maandprijzen": per maand de fijnste
            notering (maand → kwartaal → Cal) van de laatste handelsdag, met
            CALC. Chronologisch; met de toggle ook de verlopen maanden met hun
            eigen laatste notering. De lopende maand is gemarkeerd. */}
        <div className="mt-4 bg-slate-800/50 border border-slate-800/70 rounded-lg p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-sm font-medium text-slate-200">Maandprijzen — meest recente notering</h3>
                <ExportKnop onExport={exportForwardMaandprijzen} soort="maand" busy={maandprijzenBezig} disabled={!forwardTabelRijen.length} count={AANTAL_MAANDEN}
                  label="Maandprijzen naar Excel: één Power BE Base- en één TTF Gas Base-prijs per maand, van deze maand tot 3 jaar vooruit, met CALC" />
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Per leveringsmaand de fijnste notering: eerst het maandcontract, anders het kwartaal, anders het kalenderjaar (Cal). CALC verdeelt een kwartaal- of Cal-prijs volgens het profiel van de laatst gekende maandprijzen. €/MWh
                {forwardTabelProducten.filter(p => p.notering).map(p => <span key={p.id} className="ml-2 text-slate-400">{p.label}: notering {mpFormatIsoBe(p.notering.datum)}</span>)}
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
              <input type="checkbox" className="accent-lime-500" checked={forwardHistorisch} onChange={e => setForwardHistorisch(e.target.checked)} />
              Toon ook historische maanden
            </label>
          </div>
          {forwardProfielFout && <div className="mt-2 text-[11px] text-amber-300/90">CALC en historische maanden niet beschikbaar: {forwardProfielFout}</div>}
          {!forwardTabelRijen.length ? (
            <div className="mt-3 text-slate-400 text-sm">{lynxForwardErr ? 'Geen forward data.' : 'Laden…'}</div>
          ) : (
            <div className="mt-3 max-h-[28rem] overflow-auto border border-slate-800 rounded-lg">
              <table className="w-full text-xs tabular-nums">
                <thead className="sticky top-0 bg-slate-800 z-10">
                  <tr className="text-slate-400">
                    <th rowSpan={2} className="text-left px-3 py-1.5 font-medium align-bottom">Maand</th>
                    {forwardTabelProducten.map((p, i) => (
                      <th key={p.id} colSpan={3} className={`text-center px-3 py-1 font-semibold ${i ? 'border-l border-slate-700' : ''}`} style={{ color: MP_FORWARD_LINE_COLORS[i] }}>{p.label}</th>
                    ))}
                  </tr>
                  <tr className="text-slate-500 text-[10px] uppercase tracking-wide">
                    {forwardTabelProducten.map((p, i) => (
                      <React.Fragment key={p.id}>
                        <th className={`text-right px-3 py-1 font-medium ${i ? 'border-l border-slate-700' : ''}`}>Prijs</th>
                        <th className="text-right px-3 py-1 font-medium">CALC</th>
                        <th className="text-left px-3 py-1 font-medium">Contract</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {forwardTabelRijen.map(r => {
                    const huidig = r.periode === forwardHuidigeMaand;
                    return (
                      <tr key={r.periode} className={`border-t border-slate-800/60 ${huidig ? 'bg-lime-500/10' : r.historisch ? 'text-slate-400' : 'text-slate-300'}`}>
                        <td className="px-3 py-1 whitespace-nowrap font-medium">
                          {r.periode}{huidig && <span className="ml-2 text-[10px] uppercase tracking-wide text-lime-400">nu</span>}{r.historisch && <span className="ml-2 text-[10px] text-slate-600">historisch</span>}
                        </td>
                        {forwardTabelProducten.map((p, i) => {
                          const prijs = r.prijzen[p.id], calc = r.calc[p.id];
                          return (
                            <React.Fragment key={p.id}>
                              <td className={`px-3 py-1 text-right ${i ? 'border-l border-slate-800' : ''}`}>{prijs ? prijs.waarde.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : <span className="text-slate-600">—</span>}</td>
                              <td className="px-3 py-1 text-right" title={calc?.basis || undefined}>
                                {calc ? <span className={calc.basis && !/ongewijzigd/.test(calc.basis) ? 'text-lime-300' : ''}>{calc.waarde.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> : <span className="text-slate-600">—</span>}
                              </td>
                              <td className="px-3 py-1 whitespace-nowrap">
                                {prijs ? <>
                                  <span className="font-mono">{prijs.contract}</span>
                                  <span className="ml-1.5 text-[10px] text-slate-500">{SOORT_LABEL[prijs.soort]}</span>
                                  {r.historisch && prijs.notering && <span className="ml-1.5 text-[10px] text-slate-500">· {mpFormatIsoBe(prijs.notering)}</span>}
                                </> : <span className="text-slate-600">geen notering</span>}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-[11px] text-slate-500">Groene CALC-waarde = kwartaal- of Cal-prijs verdeeld over de maanden (de uitleg staat in de tooltip). Historische maanden tonen de laatste notering van hun eigen maandcontract, met die datum erbij.</p>
        </div>
      </MpSection>
      </div>

      <div className={toonMpTab('live') ? '' : 'hidden'}>
      <MpSection
        title="Actuele onbalans & forecast"
        subtitle={minute ? `Live minuut-data + prijsforecast (alpha ${minute.calibration?.alpha ?? '—'}, marg. inc ${minute.calibration?.marginalInc ?? '—'}, marg. dec ${minute.calibration?.marginalDec ?? '—'})` : ' '}
        stale={minute?.stale}
        right={<ExportKnop soort="pdf" onExport={() => exportMpRapport('live')} busy={mpPdfBezig === 'live'} disabled={!minute || !minuteWindow.length}
          label="Rapport als PDF: momentopname van de laatste 30 minuten met kerncijfers, grafiek en tabel" />}
      >
        {minuteErr && <div className="mt-3 text-red-300 text-sm">{minuteErr}</div>}
        {!minute && !minuteErr && <div className="mt-3 text-slate-400 text-sm">Laden…</div>}

        {minute && (latestActual || upcomingForecasts.length > 0) && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {latestActual && <MpStat label={`Laatste onbalans · ${latestActual.time}`} value={`${latestActual.imbalanceEurMwh.toFixed(2)} €/MWh`} accentStyle={{ color: MP_COLORS.elia1 }} />}
            {upcomingForecasts.map(f => (
              <MpStat key={f.datetime || f.time} label={`Forecast · ${f.time}`} value={`${f.forecastPriceEurMwh.toFixed(2)} €/MWh`} accentStyle={{ color: MP_COLORS.eliaForecast }} />
            ))}
          </div>
        )}

        {minute && minuteWindow.length > 0 && (
          <div className="mt-3 flex flex-col lg:flex-row gap-4">
            <div className="flex-1 min-w-0 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={minuteWindow} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#1e293b" />
                  <XAxis tickLine={false} dataKey="time" stroke="#64748b" fontSize={11} tickMargin={6} interval={Math.max(0, Math.floor((minuteWindow.length || 1) / 8))} />
                  <YAxis axisLine={false} tickLine={false} stroke="#64748b" fontSize={11} unit=" €" />
                  <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, name) => v != null ? [`${v} €/MWh`, name] : ['—', name]} labelFormatter={(t) => `Minuut ${t}`} />
                  <Legend {...CHART_LEGENDA_LIJN} />
                  <ReferenceLine y={0} stroke="#475569" />
                  {minute.nowDatetime && <ReferenceLine x={minuteWindow.find(i => i.datetime === minute.nowDatetime)?.time} stroke="#475569" strokeDasharray="3 3" label={{ value: 'nu', position: 'insideTopRight', offset: 6, fill: '#94a3b8', fontSize: 10 }} />}
                  <Line type="monotone" name="Onbalansprijs (actual)" dataKey="imbalanceEurMwh" stroke={MP_COLORS.elia1} strokeWidth={2} dot={false} connectNulls={false} />
                  <Line type="monotone" name="Forecast (afgeleid)" dataKey="forecastPriceEurMwh" stroke={MP_COLORS.eliaForecast} strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3, fill: MP_COLORS.eliaForecast, stroke: MP_COLORS.eliaForecast }} activeDot={{ r: 5 }} connectNulls={true} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Kleine tabel met de laatste gegevens (nieuwste bovenaan). */}
            <div className="lg:w-60 shrink-0">
              <div className="text-xs font-semibold text-slate-300 mb-1.5">Laatste gegevens</div>
              <div className="border border-slate-700/50 rounded-lg overflow-hidden">
                {/* overflow-auto (i.p.v. enkel -y): op een smal scherm scrollt deze
                    detailtabel binnen haar eigen blok i.p.v. de pagina te verbreden. */}
                <div className="max-h-64 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-800"><tr className="text-slate-400">
                      <th className="text-left px-2 py-1.5 font-medium">Tijd</th>
                      <th className="text-right px-2 py-1.5 font-medium">Onbalans</th>
                      <th className="text-right px-2 py-1.5 font-medium">Forecast</th>
                    </tr></thead>
                    <tbody>
                      {[...minuteWindow].reverse().map((r, i) => (
                        <tr key={r.datetime || r.time || i} className="border-t border-slate-700/30">
                          <td className="px-2 py-1 text-slate-300 tabular-nums">{r.time}</td>
                          <td className="px-2 py-1 text-right tabular-nums" style={{ color: MP_COLORS.elia1 }}>{r.imbalanceEurMwh != null ? r.imbalanceEurMwh.toFixed(2) : '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums" style={{ color: MP_COLORS.eliaForecast }}>{r.forecastPriceEurMwh != null ? r.forecastPriceEurMwh.toFixed(2) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">€/MWh · laatste 30 min</div>
            </div>
          </div>
        )}
      </MpSection>
      </div>

      <div className={toonMpTab('profiel') ? '' : 'hidden'}>
      <MpSection
        title="Dagprofiel van de marktprijzen"
        subtitle={`Gemiddelde Belpex + Elia per kwartier-van-dag — periode ${mpFormatIsoBe(profileFrom)} tot ${mpFormatIsoBe(profileTo)} (${profileDays} dagen)`}
        right={
          <div className="flex flex-wrap items-end gap-2">
            <button onClick={() => { setProfileFrom(mpShiftIso(profileFrom, -30)); setProfileTo(mpShiftIso(profileTo, -30)); }} title="1 maand terug" aria-label="1 maand terug" className="inline-flex items-center justify-center h-[34px] w-9 mt-[18px] bg-slate-800 border border-slate-700 hover:border-lime-500 text-slate-200 rounded-lg"><ChevronLeft size={16} /></button>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Van</div>
              <BeDateInput value={profileFrom} onChange={setProfileFrom} max={laatsteDag(profileTo) || undefined} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Tot</div>
              <BeDateInput value={profileTo} onChange={setProfileTo} min={totNa(profileFrom) || undefined} max={mpTomorrowIso()} />
            </div>
            <button onClick={() => {
                // "Tot" mag hoogstens morgen zijn (= tot en met vandaag).
                const grens = mpTomorrowIso();
                const wantedTo = mpShiftIso(profileTo, 30);
                const cappedTo = wantedTo > grens ? grens : wantedTo;
                const delta = mpDaysBetweenIso(profileTo, cappedTo);
                setProfileFrom(mpShiftIso(profileFrom, delta));
                setProfileTo(cappedTo);
              }} disabled={profileTo >= mpTomorrowIso()} title="1 maand vooruit" aria-label="1 maand vooruit" className="inline-flex items-center justify-center h-[34px] w-9 mt-[18px] bg-slate-800 border border-slate-700 hover:border-lime-500 disabled:opacity-40 disabled:cursor-not-allowed text-slate-200 rounded-lg"><ChevronRight size={16} /></button>
            <button onClick={loadProfile} disabled={!profileValid || profileLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg text-sm">{profileLoading ? 'Berekenen…' : 'Vernieuwen'}</button>
            <ExportKnop soort="pdf" onExport={() => exportMpRapport('profiel')} busy={mpPdfBezig === 'profiel'} disabled={!profileRaw || profileLoading}
              label="Rapport als PDF: kerncijfers piek en dal, grafiek en de 96 kwartiergemiddelden van deze periode" />
          </div>
        }
      >
        {!profileValid && <div className="mt-3 text-amber-400 text-sm">Periode te lang (max 366 dagen) of ongeldig.</div>}
        {profileError && <div className="mt-3 text-red-300 text-sm">{profileError}</div>}
        {profileLoading && <div className="mt-3 text-slate-400 text-sm">Dagprofiel berekenen ({profileDays} dagen)…</div>}
        {profileRaw && !profileLoading && (
          <>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-amber-500/40" />Daluren (00–08 + 20–24)</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-rose-500/40" />Piekuren (08–20)</span>
              <span className="ml-auto">{profileRaw.rows.length} datapunten · {profileRaw.failedDays > 0 ? `${profileRaw.failedDays} dag(en) gefaald` : 'alle dagen ok'}</span>
            </div>
            <div className="h-80 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={profileChart} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#1e293b" />
                  <XAxis tickLine={false} dataKey="time" stroke="#64748b" fontSize={11} tickFormatter={t => t.endsWith(':00') ? t : ''} interval={0} />
                  <YAxis axisLine={false} tickLine={false} stroke="#64748b" fontSize={11} unit=" €/MWh" width={72} domain={['auto', (dataMax) => Math.ceil((dataMax || 0) * 1.15)]} padding={{ top: 8 }} />
                  <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, name) => v != null ? [`${v} €/MWh`, name] : ['—', name]} labelFormatter={t => `Kwartier ${t}`} />
                  <Legend {...CHART_LEGENDA_LIJN} />
                  <ReferenceArea x1="00:00" x2="08:00" fill="#f59e0b" fillOpacity={0.06} />
                  <ReferenceArea x1="20:00" x2="23:45" fill="#f59e0b" fillOpacity={0.06} />
                  <ReferenceArea x1="08:00" x2="20:00" fill="#f43f5e" fillOpacity={0.05} />
                  <ReferenceLine y={0} stroke="#475569" />
                  <Line type="monotone" name="Belpex (gem.)" dataKey="belpexAvg" stroke={MP_COLORS.belpex} strokeWidth={2.5} dot={false} connectNulls />
                  <Line type="monotone" name="Elia onbalans (gem.)" dataKey="eliaAvg" stroke={MP_COLORS.elia15} strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </MpSection>
      </div>
    </div>
  );
};

// ─── Learning module ─────────────────────────────────────────────────────────
// Drie views: categorieën-grid → topics-lijst → topic-detail.
// Admin krijgt edit-controls op elk niveau; partners zien alleen gepubliceerde
// content + audience-toegestane items (server filtert, UI dubbelt nooit).

const PROGRESS_BADGES = {
  not_started: { label: 'Nog niet bekeken', cls: 'bg-slate-700 text-slate-400' },
  viewed: { label: 'Bekeken', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  completed: { label: 'Voltooid', cls: 'bg-lime-500/20 text-lime-300 border-lime-500/30' },
};

const LearningPage = ({ token, currentUser }) => {
  const isAdmin = currentUser?.role === 'admin';
  const [view, setView] = useState('categories'); // categories | topics | detail
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicDetail, setTopicDetail] = useState(null);
  const [stats, setStats] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);

  const api = useMemo(() => ({
    get: async (path) => {
      const r = await fetch(`/api/learning${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      return r.json();
    },
    json: async (method, path, body) => {
      const r = await fetch(`/api/learning${path}`, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      return r.json();
    },
    upload: async (topicId, file) => {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`/api/learning/topics/${topicId}/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      return r.json();
    },
  }), [token]);

  const loadCategories = async () => {
    setLoading(true); setError('');
    try { setCategories(await api.get('/categories')); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  const loadTopics = async (catId) => {
    setLoading(true); setError('');
    try { setTopics(await api.get(`/topics?category_id=${catId}`)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  const loadTopicDetail = async (topicId) => {
    setLoading(true); setError('');
    try { setTopicDetail(await api.get(`/topics/${topicId}`)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  const loadStats = async () => {
    try { setStats(await api.get('/stats')); } catch (_) { /* niet kritisch */ }
  };

  useEffect(() => { loadCategories(); if (isAdmin) loadStats(); /* eslint-disable-next-line */ }, [token]);

  // Search-filter (alleen op huidige view's data)
  const filteredCats = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories.filter(c => c.title.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
  }, [categories, search]);
  const filteredTopics = useMemo(() => {
    if (!search.trim()) return topics;
    const q = search.toLowerCase();
    return topics.filter(t => t.title.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q));
  }, [topics, search]);

  // ─── Acties ──────────────────────────────────────────────────────────────
  const openCategory = async (cat) => { setSelectedCat(cat); await loadTopics(cat.id); setView('topics'); };
  const openTopic = async (topicId) => { setSelectedTopicId(topicId); await loadTopicDetail(topicId); setView('detail'); };
  const backToCategories = () => { setView('categories'); setSearch(''); loadCategories(); };
  const backToTopics = () => { setView('topics'); setSearch(''); if (selectedCat) loadTopics(selectedCat.id); };

  const markCompleted = async () => {
    if (!topicDetail) return;
    try { await api.json('POST', `/topics/${topicDetail.id}/complete`); setTopicDetail(t => ({ ...t, my_progress: 'completed' })); }
    catch (e) { setError(e.message); }
  };

  const saveCategory = async (data) => {
    try {
      if (data.id) await api.json('PATCH', `/categories/${data.id}`, data);
      else await api.json('POST', '/categories', data);
      setEditingCat(null); loadCategories();
    } catch (e) { setError(e.message); }
  };
  const deleteCategory = async (id) => {
    if (!window.confirm('Categorie + alle topics verwijderen?')) return;
    try { await api.json('DELETE', `/categories/${id}`); loadCategories(); }
    catch (e) { setError(e.message); }
  };
  const saveTopic = async (data) => {
    try {
      if (data.id) await api.json('PATCH', `/topics/${data.id}`, data);
      else await api.json('POST', '/topics', { ...data, category_id: selectedCat.id });
      setEditingTopic(null); if (selectedCat) loadTopics(selectedCat.id);
    } catch (e) { setError(e.message); }
  };
  const deleteTopic = async (id) => {
    if (!window.confirm('Topic verwijderen (incl. bijlagen)?')) return;
    try { await api.json('DELETE', `/topics/${id}`); if (selectedCat) loadTopics(selectedCat.id); }
    catch (e) { setError(e.message); }
  };
  const uploadFile = async (file) => {
    if (!topicDetail) return;
    try {
      await api.upload(topicDetail.id, file);
      await loadTopicDetail(topicDetail.id);
    } catch (e) { setError(e.message); }
  };
  const deleteFile = async (fileId) => {
    if (!window.confirm('Bestand verwijderen?')) return;
    try { await api.json('DELETE', `/files/${fileId}`); if (topicDetail) await loadTopicDetail(topicDetail.id); }
    catch (e) { setError(e.message); }
  };

  // ─── Sub-renders ─────────────────────────────────────────────────────────

  const Breadcrumb = () => (
    <div className="flex items-center gap-2 text-sm text-slate-400">
      <button onClick={backToCategories} className="hover:text-lime-400">Kennisbank</button>
      {view !== 'categories' && selectedCat && <>
        <ChevronDown size={14} className="-rotate-90" />
        <button onClick={backToTopics} className="hover:text-lime-400">{selectedCat.title}</button>
      </>}
      {view === 'detail' && topicDetail && <>
        <ChevronDown size={14} className="-rotate-90" />
        <span className="text-slate-200">{topicDetail.title}</span>
      </>}
    </div>
  );

  const SearchBar = ({ placeholder }) => (
    <div className="relative w-full md:w-80">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder} className="w-full pl-9 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-lime-500 focus:outline-none" />
    </div>
  );

  // ─── View: categorieën ──────────────────────────────────────────────────
  if (view === 'categories') {
    return <div className="space-y-6">
      <PageHeader title="Kennisbank" subtitle={seesEtBrand(currentUser?.role) ? 'Leer meer over Energy Together: producten, processen en markten' : 'Leer meer over producten, processen en markten'}>
        {isAdmin && <button onClick={() => setEditingCat({ title: '', description: '', icon: '', order_index: 0 })} className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-medium rounded-lg"><Plus size={18} />Nieuwe categorie</button>}
      </PageHeader>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Breadcrumb />
        <SearchBar placeholder="Zoek categorie…" />
      </div>
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}
      {loading ? <div className="text-slate-400 text-sm">Laden…</div> :
       filteredCats.length === 0 ? <div className="text-slate-500 text-sm text-center py-12">Nog geen categorieën{isAdmin ? '. Klik op "Nieuwe categorie".' : '.'}</div> :
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCats.map(cat => <div key={cat.id} className="group bg-slate-800 border border-slate-700/50 rounded-2xl p-6 hover:border-lime-500/40 transition-all cursor-pointer relative" onClick={() => openCategory(cat)}>
          <div className="flex items-start justify-between mb-3">
            <div className="p-3 rounded-xl bg-lime-500/10 text-lime-400"><BookOpen size={24} /></div>
            {isAdmin && <div className="flex gap-1 opacity-0 group-hover:opacity-100">
              <button onClick={e => { e.stopPropagation(); setEditingCat(cat); }} className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-200"><Edit2 size={14} /></button>
              <button onClick={e => { e.stopPropagation(); deleteCategory(cat.id); }} className="p-1.5 bg-red-900/40 hover:bg-red-900/60 rounded text-red-300"><Trash2 size={14} /></button>
            </div>}
          </div>
          <h3 className="text-slate-100 font-semibold text-lg mb-1">{cat.title}</h3>
          {cat.description && <p className="text-slate-400 text-sm mb-3 line-clamp-2">{cat.description}</p>}
          <div className="text-xs text-slate-500">{cat.topic_count} onderwerp{cat.topic_count === 1 ? '' : 'en'}</div>
        </div>)}
      </div>}

      {isAdmin && stats.length > 0 && <Card>
        <h4 className="text-lg font-semibold text-slate-100 mb-3">Meest bekeken onderwerpen</h4>
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700/50 text-xs text-slate-400"><th className="text-left py-2 px-2">Onderwerp</th><th className="text-left py-2 px-2">Categorie</th><th className="text-right py-2 px-2">Views</th><th className="text-right py-2 px-2">Voltooid</th></tr></thead>
          <tbody>{stats.slice(0, 10).map(s => <tr key={s.id} className="border-b border-slate-700/30"><td className="py-2 px-2 text-slate-200">{s.title}</td><td className="py-2 px-2 text-slate-400">{s.category_title}</td><td className="py-2 px-2 text-right text-slate-300">{s.view_count}</td><td className="py-2 px-2 text-right text-lime-400">{s.completions}</td></tr>)}</tbody>
        </table>
      </Card>}

      {editingCat && <CategoryEditModal initial={editingCat} onSave={saveCategory} onClose={() => setEditingCat(null)} />}
    </div>;
  }

  // ─── View: topics ───────────────────────────────────────────────────────
  if (view === 'topics') {
    return <div className="space-y-6">
      <PageHeader title={selectedCat?.title || 'Onderwerpen'} subtitle={selectedCat?.description || ''}>
        {isAdmin && <button onClick={() => setEditingTopic({ title: '', description: '', content: '', external_url: '', status: 'draft', audience: 'all', order_index: 0 })} className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-medium rounded-lg"><Plus size={18} />Nieuw onderwerp</button>}
      </PageHeader>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Breadcrumb />
        <SearchBar placeholder="Zoek onderwerp…" />
      </div>
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}
      {loading ? <div className="text-slate-400 text-sm">Laden…</div> :
       filteredTopics.length === 0 ? <div className="text-slate-500 text-sm text-center py-12">Geen onderwerpen{isAdmin ? '. Klik op "Nieuw onderwerp".' : '.'}</div> :
      <div className="space-y-3">{filteredTopics.map(t => {
        const badge = PROGRESS_BADGES[t.my_progress] || PROGRESS_BADGES.not_started;
        return <div key={t.id} className="group bg-slate-800 border border-slate-700/50 rounded-xl p-4 hover:border-lime-500/40 cursor-pointer flex items-start justify-between gap-4" onClick={() => openTopic(t.id)}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-slate-100 font-medium">{t.title}</h4>
              {t.status === 'draft' && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded text-xs">Concept</span>}
              {t.audience === 'admin_master' && <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">Admin + Master</span>}
            </div>
            {t.description && <p className="text-slate-400 text-sm line-clamp-2">{t.description}</p>}
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
              {t.files?.length > 0 && <span className="flex items-center gap-1"><File size={12} />{t.files.length} PDF</span>}
              {t.external_url && <span className="flex items-center gap-1"><ExternalLink size={12} />link</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`px-2 py-0.5 border rounded text-xs ${badge.cls}`}>{badge.label}</span>
            {isAdmin && <div className="flex gap-1 opacity-0 group-hover:opacity-100">
              <button onClick={e => { e.stopPropagation(); setEditingTopic(t); }} className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-200"><Edit2 size={14} /></button>
              <button onClick={e => { e.stopPropagation(); deleteTopic(t.id); }} className="p-1.5 bg-red-900/40 hover:bg-red-900/60 rounded text-red-300"><Trash2 size={14} /></button>
            </div>}
          </div>
        </div>;
      })}</div>}

      {editingTopic && <TopicEditModal initial={editingTopic} onSave={saveTopic} onClose={() => setEditingTopic(null)} />}
    </div>;
  }

  // ─── View: topic detail ─────────────────────────────────────────────────
  if (view === 'detail' && topicDetail) {
    const badge = PROGRESS_BADGES[topicDetail.my_progress] || PROGRESS_BADGES.not_started;
    return <div className="space-y-6">
      <PageHeader title={topicDetail.title} subtitle={topicDetail.description || ''} />
      <Breadcrumb />
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 border rounded text-sm ${badge.cls}`}>{badge.label}</span>
          {topicDetail.status === 'draft' && <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded text-sm">Concept</span>}
          {topicDetail.audience === 'admin_master' && <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded text-sm">Admin + Master only</span>}
        </div>
        {topicDetail.my_progress !== 'completed' && <button onClick={markCompleted} className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm"><Check size={16} />Markeer als voltooid</button>}
      </div>

      <Card>
        {topicDetail.content
          ? <div className="prose prose-invert max-w-none whitespace-pre-wrap text-slate-200">{topicDetail.content}</div>
          : <p className="text-slate-500 italic">Geen inhoudstekst.</p>}
        {topicDetail.external_url && <div className="mt-4 pt-4 border-t border-slate-700/50">
          <a href={topicDetail.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-lime-400 hover:text-lime-300 text-sm font-medium"><ExternalLink size={14} />Externe bron openen</a>
        </div>}
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold text-slate-100">Bijlagen (PDF)</h4>
          {isAdmin && <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm cursor-pointer text-slate-200">
            <Upload size={14} />Upload PDF
            <input type="file" accept="application/pdf" onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} className="hidden" />
          </label>}
        </div>
        {topicDetail.files?.length > 0 ? <div className="space-y-2">{topicDetail.files.map(f => <div key={f.id} className="flex items-center justify-between p-3 bg-slate-700/40 rounded-lg">
          <div className="flex items-center gap-3 min-w-0">
            <File size={18} className="text-red-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm text-slate-200 truncate">{f.original_name}</div>
              <div className="text-xs text-slate-500">{(f.size_bytes / 1024).toFixed(1)} KB</div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <a href={`/api/learning/files/${f.id}?token=${token}`} target="_blank" rel="noopener noreferrer" onClick={async e => {
              // We kunnen geen Authorization header zetten op <a> → fetch + blob URL
              e.preventDefault();
              try {
                const r = await fetch(`/api/learning/files/${f.id}`, { headers: { Authorization: `Bearer ${token}` } });
                if (!r.ok) throw new Error('Download mislukt');
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (err) { setError(err.message); }
            }} className="flex items-center gap-1 px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-200"><Download size={12} />Open</a>
            {isAdmin && <button onClick={() => deleteFile(f.id)} className="p-1.5 bg-red-900/40 hover:bg-red-900/60 rounded text-red-300"><Trash2 size={14} /></button>}
          </div>
        </div>)}</div> : <p className="text-slate-500 italic text-sm">Geen bijlagen.</p>}
      </Card>
    </div>;
  }

  return null;
};

const CategoryEditModal = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState({ title: '', description: '', icon: '', order_index: 0, ...initial });
  return <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-6 border-b border-slate-700">
        <h3 className="text-lg font-bold text-slate-100">{form.id ? 'Categorie bewerken' : 'Nieuwe categorie'}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
      </div>
      <div className="p-6 space-y-4">
        <div><label className="block text-sm text-slate-300 mb-2">Titel *</label><input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:border-lime-500 focus:outline-none" /></div>
        <div><label className="block text-sm text-slate-300 mb-2">Beschrijving</label><textarea rows={3} value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:border-lime-500 focus:outline-none" /></div>
        <div><label className="block text-sm text-slate-300 mb-2">Volgorde (laag = eerst)</label><input type="number" value={form.order_index || 0} onChange={e => setForm(p => ({ ...p, order_index: parseInt(e.target.value, 10) || 0 }))} className="w-32 px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100" /></div>
      </div>
      <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm">Annuleren</button>
        <button onClick={() => form.title.trim() && onSave(form)} disabled={!form.title.trim()} className="px-6 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm disabled:opacity-50">Opslaan</button>
      </div>
    </div>
  </div>;
};

const TopicEditModal = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState({ title: '', description: '', content: '', external_url: '', status: 'draft', audience: 'all', order_index: 0, ...initial });
  return <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
        <h3 className="text-lg font-bold text-slate-100">{form.id ? 'Onderwerp bewerken' : 'Nieuw onderwerp'}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
      </div>
      <div className="p-6 space-y-4">
        <div><label className="block text-sm text-slate-300 mb-2">Titel *</label><input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:border-lime-500 focus:outline-none" /></div>
        <div><label className="block text-sm text-slate-300 mb-2">Korte beschrijving</label><input type="text" value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:border-lime-500 focus:outline-none" /></div>
        <div><label className="block text-sm text-slate-300 mb-2">Inhoud</label><textarea rows={8} value={form.content || ''} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:border-lime-500 focus:outline-none font-mono text-sm" placeholder="Plain text — newlines worden behouden" /></div>
        <div><label className="block text-sm text-slate-300 mb-2">Externe link (optioneel)</label><input type="url" value={form.external_url || ''} onChange={e => setForm(p => ({ ...p, external_url: e.target.value }))} placeholder="https://..." className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:border-lime-500 focus:outline-none" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><label className="block text-sm text-slate-300 mb-2">Status</label><select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100"><option value="draft">Concept</option><option value="published">Gepubliceerd</option></select></div>
          <div><label className="block text-sm text-slate-300 mb-2">Zichtbaar voor</label><select value={form.audience} onChange={e => setForm(p => ({ ...p, audience: e.target.value }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100"><option value="all">Iedereen</option><option value="admin_master">Alleen admin + master</option></select></div>
          <div><label className="block text-sm text-slate-300 mb-2">Volgorde</label><input type="number" value={form.order_index || 0} onChange={e => setForm(p => ({ ...p, order_index: parseInt(e.target.value, 10) || 0 }))} className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100" /></div>
        </div>
        {form.id && <p className="text-xs text-slate-500">PDF-uploads doe je vanuit de detail-pagina van het onderwerp na opslaan.</p>}
      </div>
      <div className="p-6 border-t border-slate-700 sticky bottom-0 bg-slate-800 flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm">Annuleren</button>
        <button onClick={() => form.title.trim() && onSave(form)} disabled={!form.title.trim()} className="px-6 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm disabled:opacity-50">Opslaan</button>
      </div>
    </div>
  </div>;
};

// Het vroegere "Fee Menu rapport" (zelfstandig HTML/PDF) is geïntegreerd in de
// Menukaart-sectie van de Functionele specificatie (revenueDocs.js) — zie de
// FEE_*-constanten daar. De aparte generator is daardoor verwijderd.


// Welke rollen mag de actor instellen op een nieuwe/bestaande gebruiker?
// Strikt: alleen rollen onder die van de actor zelf — geen rol-escalatie.
function allowedTargetRoles(actorRole) {
  if (actorRole === 'admin') return ['admin', 'et_dossier', 'master', 'teamleader', 'reseller', 'prospect_label', 'prospect_reseller'];
  if (actorRole === 'master') return ['teamleader', 'reseller'];
  if (actorRole === 'teamleader') return ['reseller'];
  return [];
}

// Welke scope-velden moet de actor invullen voor de gekozen target-rol?
// admin en et_dossier hebben volledige data-toegang → geen scope-velden.
function scopeFieldsForRole(role) {
  if (role === 'admin' || role === 'et_dossier') return [];
  if (role === 'master') return ['energy_label'];
  if (role === 'teamleader') return ['energy_label', 'sales_team'];
  if (role === 'reseller') return ['energy_label', 'sales_team', 'seller_name'];
  // Prospect: het energielabel ligt vast op het bronlabel (server); alleen de
  // naam van het potentiële label wordt ingevuld.
  if (isProspectRol(role)) return ['prospect_label'];
  return [];
}

// Leesbare rol-labels voor de UI.
const ROLE_LABELS = { admin: 'Admin', et_dossier: 'ET Dossierbeheerder', master: 'Master', teamleader: 'Teamleader', reseller: 'Reseller', prospect_label: 'Prospect Energielabel', prospect_reseller: 'Prospect Reseller' };
const roleLabel = (r) => ROLE_LABELS[r] || r || '';

// Catalogus van toggelbare content-modules (verschijnen wel/niet in het hoofdmenu).
// LET OP: houd ids + volgorde gelijk aan NAV_ITEMS (verderop) én aan
// backend/src/config/modules.js. Labels zijn alleen voor weergave.
const MODULE_CATALOG = [
  { id: 'market', label: 'Market Prices' },
  { id: 'customer360', label: 'Customer 360°' },
  { id: 'metering', label: 'Metering' },
  { id: 'orders', label: 'Orders' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'sales', label: 'Sales' },
  { id: 'portfolio', label: 'Portfolio Insights' },
  { id: 'drop-loss', label: 'Retention' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'revenue-calculator', label: 'Revenue Calculator' },
  { id: 'helpdesk', label: 'Helpdesk' },
  { id: 'billing', label: 'Aging' },
  { id: 'debt-collection', label: 'Debt Collection' },
  { id: 'payment-plans', label: 'Payment Plans' },
  { id: 'billshock', label: 'Bill Shock' },
  { id: 'referrals', label: 'Referrals' },
  { id: 'learning', label: 'Learning' },
];
const CONTENT_MODULE_IDS = MODULE_CATALOG.map((m) => m.id);

// Helpdesk-capabilities (gespiegeld in backend/src/config/helpdeskPerms.js).
// `tip` = wat het recht precies toelaat (tooltip in het gebruikersformulier).
// Mailsjablonen, standaardteksten en het AI-voorstel staan hier bewust NIET: dat
// is interne kennis, enkel voor admin en ET-dossierbeheerder, en dus geen vinkje.
const HELPDESK_CAPS = [
  { id: 'read', label: 'Tickets lezen', tip: 'Tickets, berichten en bijlagen binnen de eigen rechten raadplegen. Voorwaarde voor alle andere rechten.' },
  { id: 'create', label: 'Ticket aanmaken', tip: 'Zelf een nieuw ticket aanmaken voor een klant binnen de eigen rechten.' },
  { id: 'message', label: 'Interne notitie', tip: 'Een interne notitie plaatsen: overleg tussen partner en support. De klant ziet ze niet.' },
  { id: 'mail', label: 'Mailen naar klant', tip: 'Een e-mail naar de klant versturen in de huisstijl van het energielabel. Zonder sjablonen: de gebruiker schrijft de tekst zelf.' },
  { id: 'attach', label: 'Bijlage toevoegen', tip: 'Een bijlage op een ticket zetten.' },
  { id: 'status', label: 'Status wijzigen', tip: 'De status tussen open fases wijzigen en instellen van wie de volgende actie verwacht wordt.' },
  { id: 'close', label: 'Afsluiten / heropenen', tip: 'Een ticket afsluiten of een gesloten ticket heropenen.' },
  { id: 'tags', label: 'Tags wijzigen', tip: 'De tags van een ticket wijzigen. Tags sturen de automatische toewijzing en de analyse.' },
  { id: 'customer', label: 'Klant wijzigen', tip: 'Een ticket aan een andere klant koppelen, binnen hetzelfde energielabel en de eigen rechten.' },
  { id: 'escalate', label: 'Escaleren', tip: 'Een ticket escaleren naar support.' },
  { id: 'assign', label: 'Behandelaar toewijzen', tip: 'Een ticket toewijzen aan een behandelaar van de partner (gebruikers met het vinkje "Ticket behandelaar") binnen de eigen rechten. Kan alleen als "Actie vereist van" op Partner of Klant staat.' },
];
// "Ticket behandelaar" (04-10-2026): wie kan een ticket toegewezen krijgen. Intern
// (admin, ET-dossierbeheerder) én extern (master, teamleader, verkoper). Spiegel
// van backend/src/util/helpdeskBehandelaars.js.
const HD_EXTERNE_BEHANDELAAR_ROLLEN = new Set(['master', 'teamleader', 'reseller']);
const HD_BEHANDELAAR_ROLLEN = new Set(['admin', 'et_dossier', 'master', 'teamleader', 'reseller']);
const HD_TICKET_BEHANDELAAR_TIP = 'Staat deze gebruiker in de keuzelijst "Behandelaar" van een ticket? Een behandelaar van de partner is alleen te kiezen als "Actie vereist van" op Partner of Klant staat, en alleen voor tickets binnen zijn eigen rechten (energielabel, verkoopteam). Partners zien enkel de behandelaars binnen hun eigen energielabel of team.';
const hdGelijk = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
// De keuzelijst "Behandelaar" van één ticket: de interne behandelaars altijd (een
// partnerrol krijgt die lijst leeg), de externe alleen als "Actie vereist van" op
// Partner of Klant staat, voor het energielabel van het ticket, en — voor een
// teamleader of verkoper — als het ticket bij zijn team of bij hem hoort. De
// server toetst hetzelfde opnieuw (PUT /tickets/:id/assignee).
const hdBehandelaarOpties = (t, intern = [], extern = [], labelNaam = '') => {
  const uit = [...intern];
  if (t && (t.action_required_by === 'partner' || t.action_required_by === 'customer')) {
    const r = (t.customer && t.customer.reseller) || {};
    for (const b of extern) {
      if (!hdGelijk(b.energy_label, labelNaam)) continue;
      if (b.role !== 'master' && !hdGelijk(b.sales_team, r.team_name)) continue;
      if (b.role === 'reseller' && !hdGelijk(b.seller_name, r.salesperson_name)) continue;
      uit.push(b);
    }
  }
  return uit;
};
// Tooltip bij het vinkje "AI 360°" in het gebruikersformulier.
const HD_AI_360_TIP = 'Mag deze gebruiker AI 360° gebruiken: een AI-voorstel dat de gegevens van de klant van het ticket mag raadplegen (contract, facturen, voorschot, meetgegevens, eerdere tickets). Uit = alleen het gewone AI-voorstel, zonder klantgegevens. De server dwingt dit af.';
// Wat een partnerrol standaard krijgt (spiegelt PARTNER_STANDAARD in de backend):
// melden en overleggen. De rest kent een beheerder bewust toe.
const HELPDESK_PARTNER_STANDAARD = ['read', 'create', 'message', 'attach', 'escalate'];
// Rolgebonden beheerpagina's: alleen vergrendeld getoond in de picker, niet per
// gebruiker toe te kennen — de rol bepaalt of ze zichtbaar zijn.
const ADMIN_MODULE_CATALOG = [
  { id: 'users', label: 'Gebruikers' },
  { id: 'audit-log', label: 'Audit Log' },
  { id: 'settings-label', label: 'Branding' },
  { id: 'settings-general', label: 'Algemene instellingen' },
];

// Standaard toegekende content-modules bij aanmaak, per rol (spiegelt backend).
function defaultModulesForRole(role) {
  if (role === 'admin' || role === 'et_dossier') return [...CONTENT_MODULE_IDS];
  // 'metering' altijd default-aan bij een nieuwe gebruiker, ongeacht rol.
  const base = ['orders', 'sales', 'invoices', 'metering'];
  // 'revenue' standaard AAN voor Master Accounts; teamleader/reseller standaard UIT
  // (wel per gebruiker activeerbaar). Spiegelt backend config/modules.js.
  if (role === 'master') return [...base, 'revenue', 'revenue-calculator'];
  // Prospect: de kennismakingsset (spiegelt backend config/modules.js).
  if (isProspectRol(role)) return ['market', 'customer360', 'metering', 'orders', 'invoices', 'sales', 'portfolio', 'drop-loss', 'revenue', 'revenue-calculator'];
  return base;
}

// Bepaalt voor één module-checkbox of die aangevinkt is en of die vergrendeld is.
//  - admin: alles aan + vergrendeld (ziet altijd alles)
//  - beheerpagina's (users/audit-log/settings): rolbepaald + vergrendeld
//  - overige content-modules: vrij toggelbaar
function moduleAccessState(role, moduleId, grantedSet) {
  if (role === 'admin') return { checked: true, locked: true };
  if (moduleId === 'users') return { checked: role === 'master' || role === 'teamleader', locked: true };
  if (moduleId === 'audit-log') return { checked: role === 'et_dossier', locked: true };
  if (moduleId === 'settings-label' || moduleId === 'settings-general') return { checked: false, locked: true };
  return { checked: grantedSet.has(moduleId), locked: false };
}

// Default helpdesk-capabilities per rol (gespiegeld in backend helpdeskPerms.js).
function defaultHelpdeskPermsForRole(role) {
  if (role === 'admin' || role === 'et_dossier') return HELPDESK_CAPS.map((c) => c.id);
  return [...HELPDESK_PARTNER_STANDAARD];
}

// Eén boomstructuur voor alles wat een gebruiker mag zien: per module een
// hoofdvinkje, en daaronder — enkel zolang de module aan staat — de subpagina's
// (tabs) van die module en, bij de Helpdesk, de rechten. Verving drie losse
// blokken (modules / subpagina's / helpdesk-rechten) die elk hun eigen "module
// niet toegekend"-uitleg nodig hadden. Module uit = kinderen verborgen, maar niet
// gewist: gaat de module later weer aan, dan staan de tabs er nog.
//
// Admin en ET-dossierbeheerder zien altijd alle subpagina's en rechten (ook
// nieuwe): hun boom toont geen kinderen, alleen de melding dat alles aan staat.
// Voor de andere rollen staat een nieuwe subpagina standaard UIT tot een
// beheerder ze aanvinkt. Sommige subpagina's horen bij ÉÉN energielabel (de
// commissietabs op Revenue); die staan alleen in de lijst bij dat label.
//
// `onChange` krijgt een PATCH ({ modules } / { subPages } / { helpdeskPerms }) —
// het formulier spreidt die over zijn eigen state. Geef een stabiele functie
// mee (useCallback): ze staat in de deps van het opruim-effect hieronder.
const ToegangPicker = ({ role, modules, subPages, helpdeskPerms, helpdeskAssignable = false, helpdeskAi360 = true, energyLabel, onChange, readOnly = false }) => {
  const granted = useMemo(() => new Set(modules || []), [modules]);
  const tabs = useMemo(() => new Set(subPages || []), [subPages]);
  const caps = useMemo(() => new Set(helpdeskPerms || []), [helpdeskPerms]);
  const full = SUBPAGE_FULL_ACCESS_ROLES.has(role);
  // Subpagina-catalogus zoals die voor DEZE gebruiker geldt, per module.
  const subBlokken = useMemo(() => {
    const m = new Map();
    for (const b of SUBPAGE_CATALOG) {
      const pages = b.pages.filter((p) => full || (subPageAllowedForLabel(subPageKey(b.module, p.id), energyLabel) && subPageAllowedForRole(subPageKey(b.module, p.id), role)));
      if (pages.length > 0) m.set(b.module, { ...b, pages });
    }
    return m;
  }, [full, energyLabel]);
  // Wisselt het energielabel, dan moeten al aangevinkte tabs van het OUDE label
  // meteen mee verdwijnen: anders bewaart het formulier een recht dat de server
  // toch wegfiltert en dat hier niet meer af te vinken is.
  useEffect(() => {
    if (full || readOnly) return;
    const huidig = subPages || [];
    const geldig = huidig.filter((id) => subPageAllowedForLabel(id, energyLabel));
    if (geldig.length !== huidig.length) onChange({ subPages: geldig });
  }, [full, readOnly, subPages, energyLabel, onChange]);
  if (!role) return null;

  const toggleModule = (id) => { const next = new Set(granted); if (next.has(id)) next.delete(id); else next.add(id); onChange({ modules: [...next] }); };
  const toggleTab = (id) => { const next = new Set(tabs); if (next.has(id)) next.delete(id); else next.add(id); onChange({ subPages: [...next] }); };
  const zetAlleTabs = (blok, aan) => {
    const next = new Set(tabs);
    for (const p of blok.pages) { const id = subPageKey(blok.module, p.id); if (aan) next.add(id); else next.delete(id); }
    onChange({ subPages: [...next] });
  };
  const toggleCap = (id) => { const next = new Set(caps); if (next.has(id)) next.delete(id); else next.add(id); onChange({ helpdeskPerms: [...next] }); };

  const vinkje = (checked, locked, onToggle) => <input type="checkbox" checked={checked} disabled={locked} onChange={() => { if (!locked) onToggle(); }} className={`w-4 h-4 rounded shrink-0 ${locked ? 'accent-slate-500' : 'accent-lime-500'}`} />;
  const kindKlas = (locked) => `flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm transition-colors ${locked ? 'border-slate-700/40 bg-slate-800/40 cursor-not-allowed' : 'border-slate-600/50 bg-slate-700/40 hover:border-lime-500/40 cursor-pointer'}`;

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm text-slate-400 mb-1.5 flex items-center gap-1.5"><SlidersHorizontal size={14} className="text-lime-400" />Toegang: modules, subpagina's en rechten</label>
        <div className="space-y-2">
          {MODULE_CATALOG.map((mod) => {
            const state = moduleAccessState(role, mod.id, granted);
            const locked = state.locked || readOnly;
            const blok = subBlokken.get(mod.id);
            const isHelpdesk = mod.id === 'helpdesk';
            // Admin/ET-dossier heeft alle tabs en rechten; onder de Helpdesk krijgen
            // zij wél één keuze: kunnen ze intern tickets toegewezen krijgen?
            const heeftKinderen = state.checked && (isHelpdesk || (!full && !!blok));
            const aantalTabs = blok ? blok.pages.filter((p) => tabs.has(subPageKey(mod.id, p.id))).length : 0;
            const alleAan = !!blok && aantalTabs === blok.pages.length;
            return (
              <div key={mod.id} className={`rounded-lg border ${state.checked ? 'border-slate-600/50 bg-slate-700/20' : 'border-slate-700/40 bg-slate-800/40'}`}>
                <label className={`flex items-center gap-2.5 px-3 py-2 text-sm ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  {vinkje(state.checked, locked, () => toggleModule(mod.id))}
                  <span className={`font-medium ${state.checked ? 'text-slate-200' : 'text-slate-500'}`}>{mod.label}</span>
                  {state.checked && full && (blok || isHelpdesk) && <span className="ml-auto text-xs text-slate-500">alle tabs{isHelpdesk ? ' en rechten' : ''}</span>}
                  {state.checked && !full && blok && <span className="ml-auto text-xs text-slate-500">{aantalTabs} van {blok.pages.length} tabs</span>}
                  {state.checked && !full && isHelpdesk && !blok && <span className="ml-auto text-xs text-slate-500">{caps.size} van {HELPDESK_CAPS.length} rechten</span>}
                </label>
                {heeftKinderen && <div className="border-t border-slate-700/40 px-3 py-2.5 sm:pl-9 space-y-2.5">
                  {isHelpdesk && full && (
                    <label className={kindKlas(readOnly)} title="Staat deze gebruiker in de keuzelijst 'Behandelaar' van een ticket (intern toewijzen binnen Energy Together)?">
                      {vinkje(helpdeskAssignable === true, readOnly, () => onChange({ helpdesk_assignable: !helpdeskAssignable }))}
                      <span className={readOnly ? 'text-slate-500' : 'text-slate-200'}>Kan tickets toegewezen krijgen (behandelaar)</span>
                    </label>
                  )}
                  {/* AI 360°: het AI-voorstel dat klantgegevens mag raadplegen. Een recht
                      per gebruiker, standaard aan voor admin en ET-dossierbeheerder; een
                      partnerrol kan het niet krijgen en ziet het vinkje dus niet. */}
                  {isHelpdesk && full && (
                    <label className={kindKlas(readOnly)} title={HD_AI_360_TIP}>
                      {vinkje(helpdeskAi360 !== false, readOnly, () => onChange({ helpdesk_ai360: helpdeskAi360 === false }))}
                      <span className={readOnly ? 'text-slate-500' : 'text-slate-200'}>AI 360° (voorstel met klantgegevens)</span>
                    </label>
                  )}
                  {blok && !full && <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <p className="text-xs text-slate-500">Subpagina's (tabs)</p>
                      {!readOnly && <button type="button" onClick={() => zetAlleTabs(blok, !alleAan)} className="text-xs text-lime-400 hover:text-lime-300">{alleAan ? 'Alles uit' : 'Alles aan'}</button>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                      {blok.pages.map((p) => {
                        const id = subPageKey(mod.id, p.id);
                        // Een tab kan bovenop deze instelling nog een eigen module
                        // vereisen (Customer 360 → Metering). Aanvinken mag altijd —
                        // zo staat het klaar zodra die module erbij komt — maar de
                        // beheerder moet wel zien dat er nu niets zal verschijnen.
                        const mistModule = p.vereistModule && !granted.has(p.vereistModule);
                        // Of een helpdesk-recht (Customer 360 → Helpdesk tickets): het
                        // blok haalt zijn tickets op via een route die dat recht eist,
                        // dus zonder het recht blijft het vinkje zonder gevolg.
                        const mistRecht = p.vereistHelpdeskRecht && !caps.has(p.vereistHelpdeskRecht);
                        const titel = mistModule ? `Verschijnt pas zodra de module ${p.label} ook is toegekend.`
                          : mistRecht ? 'Verschijnt pas zodra de gebruiker ook het leesrecht in de Helpdesk heeft.' : undefined;
                        return (
                          <label key={id} title={titel} className={kindKlas(readOnly)}>
                            {vinkje(tabs.has(id), readOnly, () => toggleTab(id))}
                            <span className={readOnly ? 'text-slate-500' : 'text-slate-200'}>{p.label}</span>
                            {mistModule && <span className="text-xs text-amber-400/80 whitespace-nowrap">module uit</span>}
                            {!mistModule && mistRecht && <span className="text-xs text-amber-400/80 whitespace-nowrap">leesrecht uit</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>}
                  {isHelpdesk && !full && <div>
                    <p className="text-xs text-slate-500 mb-1.5 flex items-center gap-1"><LifeBuoy size={12} />Rechten in de Helpdesk</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                      {HELPDESK_CAPS.map((c) => {
                        // Zonder leesrecht heeft geen enkel ander recht gevolg (de
                        // server weigert): toon het vinkje, maar zeg waarom het niets doet.
                        const zonderLezen = c.id !== 'read' && !caps.has('read');
                        return (
                          <label key={c.id} title={zonderLezen ? `${c.tip} Werkt pas met het recht "Tickets lezen".` : c.tip} className={kindKlas(readOnly)}>
                            {vinkje(caps.has(c.id), readOnly, () => toggleCap(c.id))}
                            <span className={readOnly || zonderLezen ? 'text-slate-500' : 'text-slate-200'}>{c.label}</span>
                            {zonderLezen && caps.has(c.id) && <span className="text-xs text-amber-400/80 whitespace-nowrap">leesrecht uit</span>}
                          </label>
                        );
                      })}
                    </div>
                    {HD_EXTERNE_BEHANDELAAR_ROLLEN.has(role) && (
                      <label className={`${kindKlas(readOnly)} mt-2`} title={HD_TICKET_BEHANDELAAR_TIP}>
                        {vinkje(helpdeskAssignable === true, readOnly, () => onChange({ helpdesk_assignable: !helpdeskAssignable }))}
                        <span className={readOnly ? 'text-slate-500' : 'text-slate-200'}>Ticket behandelaar</span>
                        <span className="text-xs text-slate-500">kan tickets toegewezen krijgen</span>
                      </label>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">Mailsjablonen, standaardteksten en het AI-voorstel zijn interne kennis: die krijgt een partner nooit te zien, welke rechten hier ook aan staan. De kennisbank is een aparte subpagina (hierboven) en staat standaard uit.</p>
                  </div>}
                </div>}
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-xs text-slate-500 mb-1.5">Beheer (rolgebonden — niet per gebruiker te wijzigen)</p>
        <div className="flex flex-wrap gap-1.5">
          {ADMIN_MODULE_CATALOG.map((m) => {
            const st = moduleAccessState(role, m.id, granted);
            return <span key={m.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${st.checked ? 'border-slate-600/50 bg-slate-700/40 text-slate-300' : 'border-slate-700/40 bg-slate-800/40 text-slate-500'}`}>{st.checked ? <Check size={12} className="text-lime-400" /> : <X size={12} />}{m.label}</span>;
          })}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        {role === 'admin'
          ? 'Een admin ziet altijd alle modules, subpagina\'s en helpdesk-rechten — hier valt niets in te stellen.'
          : full
            ? `${roleLabel(role)} ziet binnen elke toegekende module altijd alle subpagina's en helpdesk-rechten, ook nieuwe. Alleen de modules zelf zijn hier in te stellen.`
            : `Zonder vinkje verdwijnt de module uit het menu; de subpagina's en rechten eronder verschijnen pas zodra de module aan staat. Een later toegevoegde subpagina staat standaard uit en zet je hier expliciet aan. Subpagina's die bij één energielabel horen (de commissietabs op Revenue) verschijnen alleen${energyLabel ? ` bij ${energyLabel}` : ' zodra er een energielabel gekozen is'}.`}
      </p>
    </div>
  );
};

// Status van de "Toon alle orders"-vlag per rol. Admin/et_dossier: N.v.t. (zien
// alle labels). Master: standaard al label-breed → vergrendeld aan. Teamleader/
// reseller: vrij in/uit te schakelen.
// Energielabel waarvoor "Toon alle orders" voor wederverkopers (teamleader/
// reseller) NOOIT mag — zij mogen nooit álle orders van Power2You zien.
const ALL_ORDERS_BLOCKED_LABEL = 'Power2You';
function allOrdersFlagState(role, value, energyLabel) {
  if (role === 'admin' || role === 'et_dossier') return { checked: false, locked: true, note: 'Niet van toepassing — deze rol ziet álle energielabels.' };
  // Power2You: wederverkopers (teamleader/reseller) mogen NOOIT alle orders van
  // het label zien — de vlag is hier hard geblokkeerd en kan niet aan.
  if ((energyLabel || '').trim() === ALL_ORDERS_BLOCKED_LABEL && (role === 'teamleader' || role === 'reseller')) {
    return { checked: false, locked: true, blocked: true, note: 'Voor wederverkopers van Power2You nooit toegelaten.' };
  }
  if (role === 'master') return { checked: true, locked: true, note: 'Een master ziet standaard al alle orders van het eigen energielabel.' };
  return { checked: !!value, locked: false, note: 'Geeft toegang tot álle orders binnen het eigen energielabel (o.a. Orders en Customer 360°), ongeacht team of verkoper.' };
}

// Dwingt de Power2You-regel af op de op te slaan vlag-waarde (defense-in-depth,
// naast de UI-lock en de backend-validatie).
function enforceAllOrdersFlag(role, energyLabel, value) {
  return allOrdersFlagState(role, value, energyLabel).blocked ? false : !!value;
}

const AllOrdersToggle = ({ role, value, energyLabel, onChange, readOnly = false }) => {
  const base = allOrdersFlagState(role, value, energyLabel);
  // Geblokkeerd (bv. Power2You-wederverkoper) maar opgeslagen waarde nog true →
  // forceer terug naar false zodat de keuze niet stiekem bewaard blijft.
  useEffect(() => {
    if (base.blocked && value) onChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.blocked, value]);
  if (!role) return null;
  const { checked, note } = base;
  const locked = base.locked || readOnly;
  return (
    <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-sm transition-colors ${locked ? 'border-slate-700/40 bg-slate-800/40 cursor-not-allowed' : 'border-slate-600/50 bg-slate-700/40 hover:border-lime-500/40 cursor-pointer'}`}>
      <input type="checkbox" checked={checked} disabled={locked} onChange={e => { if (!locked) onChange(e.target.checked); }} className={`mt-0.5 w-4 h-4 rounded ${locked ? 'accent-slate-500' : 'accent-lime-500'}`} />
      <span>
        <span className={`font-medium ${locked ? 'text-slate-400' : 'text-slate-200'}`}>Toon alle orders</span>
        <span className="block text-xs text-slate-500 mt-0.5">{note}</span>
      </span>
    </label>
  );
};

// Demo-account. Toont echte cijfers, maar de server maakt élk klantgegeven
// onherkenbaar. Contactgegevens (telefoon, BTW-nummer, IBAN, contactpersoon)
// worden XXXX; klanten, adressen, verkopers en verkoopteams krijgen een
// pseudoniem ("Klant 1", "Adres 1", "Verkoper 1", "Verkoopteam A") zodat
// Customer 360, Metering en de verkoper-/teamcijfers bruikbaar blijven.
// Bedoeld voor demonstraties van het platform. Helpdesk en klantnotities blijven
// bewust dicht: dat is vrije tekst waar namen in staan. Admin/ET-dossierbeheerder
// kunnen dit niet krijgen — die rollen zien álle labels, daar is een demo niet
// voor bedoeld.
const DEMO_MODE_BLOCKED_ROLES = ['admin', 'et_dossier'];

const DemoModeToggle = ({ role, value, onChange, readOnly = false }) => {
  const blocked = DEMO_MODE_BLOCKED_ROLES.includes(role);
  // Prospect: altijd een demo-account — het vinkje staat vast aan.
  const verplicht = isProspectRol(role);
  useEffect(() => {
    if (blocked && value) onChange(false);
    if (verplicht && !value) onChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, verplicht, value]);
  if (!role) return null;
  const locked = blocked || verplicht || readOnly;
  const note = blocked
    ? 'Niet beschikbaar voor deze rol — een beheerder ziet alle energielabels.'
    : verplicht
      ? 'Een prospect kijkt altijd geanonimiseerd mee op de Power2You-data: klanten, verkopers en teams krijgen een pseudoniem, EAN\'s worden gedeeltelijk verborgen en Power2You heet overal de naam van het potentiële label.'
      : 'Klanten, verkopers en teams krijgen een pseudoniem (Klant 1, Verkoper 1, Verkoopteam A); contactgegevens worden XXXX. Helpdesk en klantnotities zijn dan niet beschikbaar.';
  return (
    <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-sm transition-colors ${locked ? 'border-slate-700/40 bg-slate-800/40 cursor-not-allowed' : 'border-slate-600/50 bg-slate-700/40 hover:border-amber-500/40 cursor-pointer'}`}>
      <input type="checkbox" checked={!!value && !blocked} disabled={locked} onChange={e => { if (!locked) onChange(e.target.checked); }} className={`mt-0.5 w-4 h-4 rounded ${locked ? 'accent-slate-500' : 'accent-amber-500'}`} />
      <span>
        <span className={`font-medium ${locked ? 'text-slate-400' : 'text-slate-200'}`}>Demo-account (geanonimiseerd)</span>
        <span className="block text-xs text-slate-500 mt-0.5">{note}</span>
      </span>
    </label>
  );
};

// Functionele beschrijving op de Revenue-pagina, per gebruiker. Het vinkje toont
// altijd de EFFECTIEVE waarde: staat er niets ingesteld (`value` null), dan de
// standaard voor deze rol + dit energielabel (aan voor admin,
// ET-dossierbeheerder en elke master behalve die van HOA Energy; uit voor
// teamleader en verkoper — zie revenueDocsStandaard in revenueDocs.js). Zo ziet
// een beheerder wat de gebruiker vandaag krijgt, en verschuift het vinkje mee
// zodra hij in dit formulier de rol of het energielabel wijzigt. Bij opslaan gaat
// de getoonde waarde expliciet mee.
const RevenueDocsToggle = ({ role, energyLabel, value, onChange, readOnly = false }) => {
  if (!role) return null;
  const standaard = revenueDocsStandaard(role, energyLabel);
  const effectief = value === true || value === false ? value : standaard;
  const note = standaard
    ? 'Standaard aan voor deze rol; vink uit om de beschrijving voor deze gebruiker te verbergen.'
    : 'Standaard uit voor deze rol; vink aan om de beschrijving voor deze gebruiker te tonen.';
  return (
    <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-sm transition-colors ${readOnly ? 'border-slate-700/40 bg-slate-800/40 cursor-not-allowed' : 'border-slate-600/50 bg-slate-700/40 hover:border-amber-500/40 cursor-pointer'}`}>
      <input type="checkbox" checked={effectief} disabled={readOnly} onChange={e => { if (!readOnly) onChange(e.target.checked); }} className={`mt-0.5 w-4 h-4 rounded ${readOnly ? 'accent-slate-500' : 'accent-amber-500'}`} />
      <span>
        <span className={`font-medium ${readOnly ? 'text-slate-400' : 'text-slate-200'}`}>Functionele beschrijving (Revenue)</span>
        <span className="block text-xs text-slate-500 mt-0.5">{note}</span>
      </span>
    </label>
  );
};



// MFA (authenticator-app). Verplicht voor de rollen met de meeste rechten
// (admin, ET-dossierbeheerder, master) — daar is het géén keuze. Voor verkoper en
// teamleader mag de drempel lager: e-mail + wachtwoord volstaat, met de optie om
// MFA tóch aan te zetten. Standaard staat het voor die twee rollen dus UIT.
// De backend beslist hetzelfde via mfaRequiredForRole() in middleware/scope.js;
// dit vinkje is de UI-kant van diezelfde regel, geen tweede waarheid.
const MFA_OPTIONEEL_ROLLEN = ['teamleader', 'reseller', 'prospect_label', 'prospect_reseller'];
const mfaVerplichtVoorRol = (role) => !MFA_OPTIONEEL_ROLLEN.includes(role);

const MfaToggle = ({ role, value, onChange, readOnly = false }) => {
  const verplicht = mfaVerplichtVoorRol(role);
  // Rol wisselt naar een verplichte rol → zet de opgeslagen waarde meteen op true,
  // zodat er nooit een gebruiker met MFA-uit onder een beheerdersrol belandt.
  useEffect(() => {
    if (verplicht && !value) onChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verplicht, value]);
  if (!role) return null;
  const locked = verplicht || readOnly;
  const note = verplicht
    ? 'Verplicht voor deze rol — deze gebruiker stelt bij de eerste aanmelding een authenticator-app in.'
    : 'Aan = de gebruiker heeft een authenticator-app nodig. Uit = aanmelden met e-mail en wachtwoord. De welkomstmail volgt deze keuze.';
  return (
    <label className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-sm transition-colors ${locked ? 'border-slate-700/40 bg-slate-800/40 cursor-not-allowed' : 'border-slate-600/50 bg-slate-700/40 hover:border-lime-500/40 cursor-pointer'}`}>
      <input type="checkbox" checked={verplicht || !!value} disabled={locked} onChange={e => { if (!locked) onChange(e.target.checked); }} className={`mt-0.5 w-4 h-4 rounded ${locked ? 'accent-slate-500' : 'accent-lime-500'}`} />
      <span>
        <span className={`font-medium ${locked ? 'text-slate-400' : 'text-slate-200'}`}>MFA via authenticator-app</span>
        <span className="block text-xs text-slate-500 mt-0.5">{note}</span>
      </span>
    </label>
  );
};

// Reseller Fee %-veld in het gebruikersformulier. Alleen voor teamleader/reseller
// (default 75%, aanpasbaar) — die wordt verrekend in de partnervergoeding als
// "Totale Fee R." = % × Totale Fee. Master/admin/ET hebben geen reseller-fee (100%).
// Prospect: het standaard-margeaandeel hangt van de rol af (Prospect Reseller
// 75 %, Prospect Energielabel 100 %) — spiegelt de server.
const ResellerFeeField = ({ role, value, onChange, readOnly = false }) => {
  if (!role) return null;
  if (role !== 'teamleader' && role !== 'reseller' && !isProspectRol(role)) {
    return (
      <div>
        <label className="block text-sm text-slate-400 mb-1.5">Reseller Fee %</label>
        <div className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-slate-400 text-sm">N.v.t. — deze rol krijgt altijd 100% (geen reseller-fee).</div>
      </div>
    );
  }
  const standaard = role === 'prospect_label' ? 100 : 75;
  const v = (value === null || value === undefined || value === '') ? standaard : value;
  return (
    <div>
      <label className="block text-sm text-slate-400 mb-1.5">Reseller Fee % <span className="text-slate-500">— verrekend in de partnervergoeding (Totale Fee R. = % × Totale Fee)</span></label>
      <div className="flex items-center gap-2">
        <input type="number" min={0} max={100} step="0.01" value={v} disabled={readOnly}
          onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className="w-32 px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm disabled:opacity-60" />
        <span className="text-slate-400 text-sm">% <span className="text-slate-500">(default 75%)</span></span>
      </div>
    </div>
  );
};

// Persoonlijke API-sleutel voor de koppeling met Odoo.
//
// Het bronsysteem leidt uit de API-sleutel af WIE een actie uitvoert. Met een
// eigen sleutel komen een nieuw ticket, een reactie of een statuswijziging in
// Odoo op naam van deze medewerker zelf; zonder sleutel gebeurt dat onder het
// gedeelde adres uit de instellingen. Alleen zinvol voor beheerders — bij een
// partnerrol bepaalt het login-adres de afscherming en blijft alles zoals het is.
//
// De sleutel komt NOOIT terug van de server: het veld staat altijd leeg en
// `meta` zegt enkel of er een sleutel is + de laatste 4 tekens. Leeg laten =
// ongewijzigd; wissen gaat met de aparte knop.
const EtApiKeyField = ({ role, meta, value, wissen, onChange, onWissen, readOnly = false }) => {
  if (!role) return null;
  if (role !== 'admin' && role !== 'et_dossier') return null;
  const gezet = !!(meta && meta.configured);
  return (
    <div>
      <label className="block text-sm text-slate-400 mb-1.5">
        Persoonlijke API-sleutel <span className="text-slate-500">— nodig voor de synchronisatie van helpdesktickets met Odoo</span>
      </label>
      <div className="flex items-center gap-2 mb-1.5 text-xs">
        {gezet && !wissen
          ? <span className="inline-flex items-center gap-1.5 text-lime-400"><Check size={13} /> Ingesteld · ••••{meta.last4}</span>
          : <span className="inline-flex items-center gap-1.5 text-amber-400"><AlertCircle size={13} /> {wissen ? 'Wordt gewist bij opslaan' : 'Niet ingesteld — acties gaan onder het gedeelde adres'}</span>}
        {gezet && meta.updated_at && !wissen && <span className="text-slate-500">· gewijzigd {new Date(meta.updated_at).toLocaleDateString('nl-BE')}</span>}
      </div>
      {readOnly
        ? <div className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-slate-400 text-sm">{gezet ? `Ingesteld · ••••${meta.last4}` : 'Niet ingesteld'}</div>
        : <div className="flex flex-wrap items-center gap-2">
            <input type="password" autoComplete="new-password" value={value || ''} onChange={e => onChange(e.target.value)}
              placeholder={gezet ? 'Nieuwe sleutel plakken… (leeg = ongewijzigd)' : 'Sleutel plakken…'}
              className="flex-1 min-w-48 px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-lime-500 text-sm font-mono" />
            {gezet && <button type="button" onClick={() => onWissen(!wissen)}
              className="px-3 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-all text-sm">{wissen ? 'Toch houden' : 'Wissen'}</button>}
          </div>}
      <p className="text-xs text-slate-500 mt-1.5">Genereer de sleutel in Odoo bij de gebruikersinstellingen van deze medewerker.</p>
    </div>
  );
};

// Read-only hiërarchisch overzicht van de volledige mapping
// energielabel → verkoopteam → verkoper. Bron: de centrale sales-structuur
// (/api/reference/sales-structure, rol-gescopet). Zoekterm filtert op label,
// team én verkoper; een label/team blijft zichtbaar zolang het na filtering nog
// verkopers bevat.
const SalesMappingOverview = ({ structure }) => {
  const [search, setSearch] = useState('');
  // Per-label uitklap-status (Set van uitgeklapte labels). Standaard alles
  // ingeklapt → compact overzicht; klik op een label toont pas de teams/verkopers.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (label) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  const rawLabels = structure?.energy_labels || [];
  const teamsByLabel = structure?.teams_by_label || {};
  const sellersByLT = structure?.sellers_by_label_team || {};

  // "Belvus Energie" wordt bewust uit de mapping gelaten; de Odoo-entiteit
  // "Energy Together (…)" hoort onderaan i.p.v. tussen de echte energielabels.
  const isBelvus = (l) => (l || '').trim().toLowerCase() === 'belvus energie';
  const isET = (l) => (l || '').trim().toLowerCase().startsWith('energy together');
  const labels = useMemo(() => {
    const kept = rawLabels.filter(l => !isBelvus(l));
    const rest = kept.filter(l => !isET(l));
    const et = kept.filter(isET);
    return [...rest, ...et];
  }, [rawLabels]);

  const term = search.trim().toLowerCase();
  const totalSellers = useMemo(() => {
    const set = new Set();
    for (const label of labels) {
      for (const team of Object.keys(sellersByLT[label] || {})) {
        for (const s of sellersByLT[label][team] || []) set.add(`${label}|${s}`);
      }
    }
    return set.size;
  }, [labels, sellersByLT]);

  // Bouw de gefilterde boom: { label, teams: [{ team, sellers: [] }] }
  const tree = useMemo(() => {
    const out = [];
    for (const label of labels) {
      const labelMatch = label.toLowerCase().includes(term);
      const teams = [];
      for (const team of (teamsByLabel[label] || [])) {
        const teamMatch = team.toLowerCase().includes(term);
        const allSellers = (sellersByLT[label] || {})[team] || [];
        const sellers = (!term || labelMatch || teamMatch)
          ? allSellers
          : allSellers.filter(s => s.toLowerCase().includes(term));
        if (sellers.length || (!term)) teams.push({ team, sellers });
      }
      const labelSellerCount = teams.reduce((n, t) => n + t.sellers.length, 0);
      if (!term || labelMatch || labelSellerCount > 0) out.push({ label, teams, count: labelSellerCount });
    }
    return out;
  }, [labels, teamsByLabel, sellersByLT, term]);

  return (
    <Card className="p-0 overflow-hidden">
      <div className="flex items-center justify-between p-6 border-b border-slate-700/50 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Mapping energielabels · verkoopteams · verkopers</h3>
          <p className="text-xs text-slate-400 mt-0.5">{labels.length} energielabels · {totalSellers} verkopers · afgeleid uit de ET-resellers (Odoo)</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(prev => prev.size >= tree.length ? new Set() : new Set(tree.map(t => t.label)))}
            className="px-2.5 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-xs text-slate-300 hover:text-slate-100 hover:border-lime-500/50 whitespace-nowrap"
          >{expanded.size >= tree.length && tree.length > 0 ? 'Alles inklappen' : 'Alles uitklappen'}</button>
          <div className="relative w-64">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek label, team of verkoper…" className="w-full pl-8 pr-3 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-lime-500" />
          </div>
        </div>
      </div>
      {labels.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm">Geen mapping-data beschikbaar</div>
      ) : tree.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">Geen resultaten voor “{search}”</div>
      ) : (
        <div className="divide-y divide-slate-700/40">
          {tree.map(({ label, teams, count }) => {
            // Tijdens het zoeken altijd uitgeklapt tonen zodat de treffers zichtbaar zijn.
            const open = expanded.has(label) || !!term;
            return (
            <div key={label} className="px-6 py-3">
              <button onClick={() => toggle(label)} className="w-full flex items-center gap-2 text-left group">
                <ChevronRight size={15} className={`text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} />
                <Tag size={15} className="text-lime-400" />
                <span className="text-sm font-semibold text-slate-100 group-hover:text-lime-300">{label}</span>
                <span className="text-xs text-slate-500">· {count} verkoper{count === 1 ? '' : 's'} · {teams.length} team{teams.length === 1 ? '' : 's'}</span>
              </button>
              {open && (
                <div className="space-y-2 pl-6 mt-3">
                  {teams.length === 0 ? <div className="text-xs text-slate-600 pl-5">Geen verkopers</div> :
                   teams.map(({ team, sellers }) => (
                    <div key={team} className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-1 md:gap-3">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Users size={14} className="text-slate-500" />
                        <span className="font-medium">{team}</span>
                        <span className="text-xs text-slate-600">({sellers.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 pl-6 md:pl-0">
                        {sellers.length === 0 ? <span className="text-xs text-slate-600">—</span> :
                         sellers.map(s => (
                          <span key={s} className="inline-flex items-center px-2 py-0.5 bg-slate-700/50 border border-slate-600/40 rounded-md text-xs text-slate-300">{s}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

// Aanmaakdatum van een gebruiker, in de platformnotatie DD/MM/YYYY (zie CLAUDE.md:
// altijd met de nl-BE-locale, anders volgt de browser en toont en-US MM/DD/YYYY).
// Enkel de datum: het tijdstip zegt niets over een account en zou de kolom breder
// maken dan ze waard is.
const gebruikerAangemaakt = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
// Laatste aanmelding: hier telt het tijdstip wél (vandaag 09:12 vs. gisteren),
// dus datum + uur in 24u-notatie. Nooit aangemeld = "—".
const gebruikerLaatstAangemeld = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  // Twee losse delen: toLocaleString zet er anders een komma tussen.
  return `${d.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

// Sterk tijdelijk wachtwoord voor het aanmaakformulier: zelfde leesbare alfabet
// als de server (geen 0/O, 1/l/I — dit wordt overgetypt uit een e-mail) en
// cryptografisch random. Het suffix "!2" garandeert cijfer + leesteken.
const WACHTWOORD_ALFABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
function genereerWachtwoord(lengte = 14) {
  const bytes = new Uint8Array(lengte);
  window.crypto.getRandomValues(bytes);
  let uit = '';
  for (const b of bytes) uit += WACHTWOORD_ALFABET[b % WACHTWOORD_ALFABET.length];
  return `${uit}!2`;
}

// Accountstatus voor de statusfilter, de badges en de export. Volgorde telt:
// gedeactiveerd wint van alles, daarna "nog niet uitgenodigd" (er kán nog geen
// aanmelding zijn), dan de aanmeldhistoriek.
const NIET_AANGEMELD_DAGEN = 90;
const dagenSinds = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / 86400000);
};
const gebruikerStatus = (u) => {
  if (u.active === false) return 'deactivated';
  if (u.welcome_pending) return 'pending';
  if (!u.last_login_at) return 'never';
  return dagenSinds(u.last_login_at) >= NIET_AANGEMELD_DAGEN ? 'inactive' : 'active';
};
const GEBRUIKER_STATUS_LABELS = {
  active: 'Actief',
  pending: 'Nog niet uitgenodigd',
  never: 'Nooit aangemeld',
  inactive: `${NIET_AANGEMELD_DAGEN}+ dagen niet aangemeld`,
  deactivated: 'Gedeactiveerd',
};

const UsersPage = ({ token, onImpersonate, currentUser, salesStructure }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const initialNew = { name: '', email: '', password: '', role: '', energy_label: '', prospect_label: '', sales_team: '', seller_name: '', modules: [], subPages: [], helpdeskPerms: [], helpdesk_assignable: false, helpdesk_ai360: true, view_all_label_orders: false, demo_mode: false, revenue_docs: null, reseller_fee_pct: 75, mfa_required: false };
  const [newUser, setNewUser] = useState(initialNew);
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmResend, setConfirmResend] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [confirmActive, setConfirmActive] = useState(null); // id waarvoor (de)activeren bevestigd wordt
  // Bulk-uitnodigingen: geselecteerde ids van gebruikers die nog niet uitgenodigd zijn.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editError, setEditError] = useState(''); // fout in de bewerk-modal (daar zichtbaar, niet erachter)
  const [viewOnly, setViewOnly] = useState(false); // detail-modal alleen-lezen (ET Dossierbeheerder)
  // Naam van de gebruiker die als sjabloon diende ("kopiëren als basis"). Enkel
  // ter informatie in het aanmaakformulier — de rechten staan al in newUser.
  const [copySource, setCopySource] = useState(null);
  const createFormRef = useRef(null);

  // Alleen admin mag schrijven (aanmaken/wijzigen/verwijderen/impersoneren).
  // ET Dossierbeheerder heeft alleen leesrechten op de gebruikers-module.
  const isAdmin = currentUser?.role === 'admin';
  const targetRoles = allowedTargetRoles(currentUser?.role);

  // Lock scope-velden op de scope van de actor (admin mag alles, anderen niet).
  // Voor reseller-aanmaak door teamleader: energy_label en sales_team komen
  // van de actor en zijn niet aanpasbaar in het formulier.
  const lockedFields = useMemo(() => {
    if (!currentUser) return {};
    if (currentUser.role === 'admin') return {};
    if (currentUser.role === 'master') return { energy_label: currentUser.energyLabel || '' };
    if (currentUser.role === 'teamleader') return { energy_label: currentUser.energyLabel || '', sales_team: currentUser.salesTeam || '' };
    return {};
  }, [currentUser]);

  const loadUsers = () => {
    setLoading(true);
    getUsers(token).then(d => { setUsers(d.users); setError(''); }).catch(e => setError(e.message)).finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, [token]);

  // Patch-handlers voor de toegangsboom; stabiel (useCallback) omdat ze in de
  // deps van het opruim-effect van de picker staan.
  const toegangNieuw = useCallback((patch) => setNewUser((p) => ({ ...p, ...patch })), []);
  const toegangBewerk = useCallback((patch) => setEditTarget((p) => (p ? { ...p, ...patch } : p)), []);

  // Na een herlaad van de lijst blijven enkel selecties over die nog zinvol zijn
  // (gebruiker bestaat nog én is nog niet uitgenodigd).
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => users.some((u) => u.id === id && u.welcome_pending)));
      return next.size === prev.size ? prev : next;
    });
  }, [users]);

  // Eén login per verkoper: dezelfde verkoper (energielabel + team + naam) mag
  // niet aan een tweede gebruiker hangen — die zouden exact dezelfde data delen.
  // De server weigert ook (409), maar zo ziet de beheerder het al in het formulier.
  const verkoperConflict = (spec, exceptId = null) => {
    if (!spec || !spec.seller_name) return null;
    return users.find((u) => u.id !== exceptId && u.seller_name === spec.seller_name
      && (u.energy_label || '') === (spec.energy_label || '') && (u.sales_team || '') === (spec.sales_team || '')) || null;
  };
  const verkoperConflictTekst = (spec, botsing) => `Er bestaat al een login voor verkoper "${spec.seller_name}" (${botsing.email}). Twee logins op dezelfde verkoper delen exact dezelfde data — kies een andere verkoper of bewerk de bestaande gebruiker.`;

  // Referentiedata voor dropdowns (labels + teams uit orders, gefilterd op scope)
  const [labelsTeams, setLabelsTeams] = useState({ energy_labels: [], teams_by_label: {} });
  useEffect(() => {
    if (!token) return;
    fetch('/api/reference/labels-teams', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setLabelsTeams(d))
      .catch(() => { /* fallback: lege dropdowns, server-validatie vangt fouten op */ });
  }, [token]);

  // De volledige verkoper-mapping (energielabel → verkoopteam → verkoper) wordt
  // niet meer hier opgehaald: ze komt uit de centrale sales-structuur (prop
  // salesStructure) en wordt read-only getoond via <SalesMappingOverview />.

  // Datalist-suggesties uit bestaande users (alleen wat actor al ziet — server filtert)

  const openCreate = () => {
    setShowCreate(true);
    setCreateSuccess(null);
    setCreateError('');
    setCopySource(null);
    setNewUser({ ...initialNew, ...lockedFields });
  };

  // Bestaande gebruiker als sjabloon nemen. Alles wat met RECHTEN te maken heeft
  // gaat mee (rol, scope, modules, subpagina's, helpdesk-rechten en alle
  // vinkjes); alles wat de PERSOON is blijft leeg: naam, e-mailadres en
  // wachtwoord. Ook de verkopernaam blijft leeg — dat is letterlijk de naam van
  // een persoon, en twee logins op dezelfde verkoper geven een verkeerde scope.
  // De MFA-sleutel wordt sowieso door de server nieuw aangemaakt.
  const openCopy = (bron) => {
    setShowCreate(true);
    setCreateSuccess(null);
    setCreateError('');
    setCopySource(bron.name || bron.email || '');
    setNewUser({
      ...initialNew,
      role: bron.role || '',
      energy_label: bron.energy_label || '',
      sales_team: bron.sales_team || '',
      seller_name: '',
      modules: [...(bron.modules || [])],
      subPages: [...(bron.subPages || [])],
      helpdeskPerms: [...(bron.helpdeskPerms || [])],
      helpdesk_assignable: bron.helpdesk_assignable === true,
      helpdesk_ai360: bron.helpdesk_ai360 !== false,
      view_all_label_orders: bron.view_all_label_orders === true,
      demo_mode: bron.demo_mode === true,
      prospect_label: bron.prospect_label || '',
      revenue_docs: bron.revenue_docs ?? null,
      reseller_fee_pct: bron.reseller_fee_pct ?? 75,
      mfa_required: bron.mfa_required === true,
      ...lockedFields,
    });
    // Het formulier staat bovenaan de pagina, de knop staat in de tabel — zonder
    // deze sprong lijkt er niets te gebeuren.
    setTimeout(() => createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  // Aanmaken mét (submit) of ZONDER de uitnodigingsmail. In het tweede geval
  // blijft de gebruiker "nog niet uitgenodigd" in de tabel staan en verstuurt
  // het vliegtuigje de uitnodiging op een later moment.
  const handleCreate = (e) => {
    e.preventDefault();
    maakGebruikerAan(true);
  };
  const handleCreateZonderMail = (e) => {
    // Een type="button" slaat de ingebouwde formuliervalidatie (required,
    // e-mailvorm) over — die roepen we dus zelf aan.
    const form = e.currentTarget.form;
    if (form && !form.reportValidity()) return;
    maakGebruikerAan(false);
  };
  const maakGebruikerAan = async (sendWelcome) => {
    setCreateError('');
    if (!newUser.role) { setCreateError('Kies een rol'); return; }
    if (newUser.password.length < 8) { setCreateError('Wachtwoord moet minimaal 8 tekens bevatten'); return; }
    for (const f of scopeFieldsForRole(newUser.role)) {
      if (!newUser[f]) { setCreateError(`Veld "${f}" is vereist voor rol ${newUser.role}`); return; }
    }
    const botsing = verkoperConflict(newUser);
    if (botsing) { setCreateError(verkoperConflictTekst(newUser, botsing)); return; }
    setCreating(true);
    try {
      const data = await register(token, {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        energy_label: newUser.energy_label || null,
        sales_team: newUser.sales_team || null,
        seller_name: newUser.seller_name || null,
        prospect_label: isProspectRol(newUser.role) ? (newUser.prospect_label || '').trim() : undefined,
        modules: newUser.modules || [],
        subPages: newUser.subPages || [],
        helpdeskPerms: newUser.helpdeskPerms || [],
        helpdesk_assignable: HD_BEHANDELAAR_ROLLEN.has(newUser.role) && newUser.helpdesk_assignable === true,
        helpdesk_ai360: SUBPAGE_FULL_ACCESS_ROLES.has(newUser.role) ? newUser.helpdesk_ai360 !== false : undefined,
        view_all_label_orders: enforceAllOrdersFlag(newUser.role, newUser.energy_label, newUser.view_all_label_orders),
        demo_mode: !DEMO_MODE_BLOCKED_ROLES.includes(newUser.role) && newUser.demo_mode === true,
        // Driewaardig veld: null = "volg de standaard". We sturen bewust de
        // EFFECTIEVE waarde mee — precies wat het vinkje toonde.
        revenue_docs: newUser.revenue_docs ?? revenueDocsStandaard(newUser.role, newUser.energy_label),
        mfa_required: mfaVerplichtVoorRol(newUser.role) || newUser.mfa_required === true,
        reseller_fee_pct: newUser.reseller_fee_pct,
        send_welcome: sendWelcome,
      });
      const email = newUser.email;
      setNewUser({ ...initialNew, ...lockedFields });
      loadUsers();
      if (data.emailSkipped) setCreateSuccess(`Gebruiker aangemaakt zonder uitnodiging. Verstuur die later via het vliegtuigje in de tabel; ${email} krijgt dan een nieuw tijdelijk wachtwoord.`);
      else if (data.emailSent) setCreateSuccess(`Gebruiker aangemaakt. Welkomstmail verstuurd naar ${email}.`);
      else setCreateSuccess(`Gebruiker aangemaakt, maar welkomstmail kon niet worden verstuurd${data.emailError ? ': ' + data.emailError : ''}.`);
    } catch (err) { setCreateError(err.message); }
    finally { setCreating(false); }
  };

  // Wat er met de persoonlijke API-sleutel moet gebeuren: een nieuwe waarde, een
  // lege string om te wissen, of niets (veld onaangeroerd).
  const sleutelWijziging = (t) => {
    const nieuw = String((t && t.et_api_key_nieuw) || '').trim();
    if (nieuw) return nieuw;
    return (t && t.et_api_key_wissen) ? '' : undefined;
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setEditError('');
    // Je eigen rij: rol en scope liggen vast (de server weigert dat bewust), dus
    // hier valt alleen de persoonlijke sleutel te zetten. Zonder deze aparte weg
    // zou een beheerder wél de sleutel van een collega kunnen zetten maar niet
    // zijn eigen.
    if (currentUser && editTarget.id === currentUser.id) {
      const sleutel = sleutelWijziging(editTarget);
      if (sleutel === undefined) { setEditTarget(null); return; }
      try {
        await updateOwnEtApiKey(token, sleutel);
        setEditTarget(null);
        loadUsers();
      } catch (err) { setEditError(err.message); }
      return;
    }
    // De naam is verplicht: hij draagt de gebruikerslijst, de exports en de
    // meldingen. Leeg opslaan zou de server toch weigeren (NOT NULL), dus vang het
    // hier al af met een leesbare melding.
    const naam = String(editTarget.name || '').trim();
    if (!naam) { setEditError('Naam mag niet leeg zijn'); return; }
    for (const f of scopeFieldsForRole(editTarget.role)) {
      if (!editTarget[f]) { setEditError(`Veld "${f}" is vereist voor rol ${editTarget.role}`); return; }
    }
    const botsing = verkoperConflict(editTarget, editTarget.id);
    if (botsing) { setEditError(verkoperConflictTekst(editTarget, botsing)); return; }
    try {
      await updateUserRole(token, editTarget.id, {
        name: naam,
        role: editTarget.role,
        energy_label: editTarget.energy_label || null,
        sales_team: editTarget.sales_team || null,
        seller_name: editTarget.seller_name || null,
        prospect_label: isProspectRol(editTarget.role) ? (editTarget.prospect_label || '').trim() : undefined,
        modules: editTarget.modules || [],
        subPages: editTarget.subPages || [],
        helpdeskPerms: editTarget.helpdeskPerms || [],
        helpdesk_assignable: HD_BEHANDELAAR_ROLLEN.has(editTarget.role) && editTarget.helpdesk_assignable === true,
        helpdesk_ai360: SUBPAGE_FULL_ACCESS_ROLES.has(editTarget.role) ? editTarget.helpdesk_ai360 !== false : undefined,
        view_all_label_orders: enforceAllOrdersFlag(editTarget.role, editTarget.energy_label, editTarget.view_all_label_orders),
        demo_mode: !DEMO_MODE_BLOCKED_ROLES.includes(editTarget.role) && editTarget.demo_mode === true,
        revenue_docs: editTarget.revenue_docs ?? revenueDocsStandaard(editTarget.role, editTarget.energy_label),
        mfa_required: mfaVerplichtVoorRol(editTarget.role) || editTarget.mfa_required === true,
        reseller_fee_pct: editTarget.reseller_fee_pct,
        // Persoonlijke API-sleutel: alleen meesturen als er iets moet veranderen.
        // Een leeg veld is "ongewijzigd", niet "wissen" — wissen gaat via de knop
        // en stuurt bewust de lege string.
        ...(sleutelWijziging(editTarget) !== undefined ? { et_api_key: sleutelWijziging(editTarget) } : {}),
      });
      setEditTarget(null);
      loadUsers();
    } catch (err) { setEditError(err.message); }
  };

  // Deactiveren i.p.v. verwijderen: de gebruiker raakt niet meer binnen, maar
  // audit-historiek, helpdesk-berichten en notities blijven aan hem hangen.
  const handleSetActive = async (u) => {
    setError('');
    try {
      await setUserActive(token, u.id, u.active === false);
      setConfirmActive(null);
      toast.success(u.active === false ? `${u.name} is geactiveerd.` : `${u.name} is gedeactiveerd.`);
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  // Uitnodigingen in bulk: één voor één via dezelfde route als het vliegtuigje,
  // zodat elke gebruiker zijn eigen tijdelijk wachtwoord en audit-regel krijgt.
  const verstuurBulk = async () => {
    const doelen = users.filter((u) => selectedIds.has(u.id) && u.welcome_pending);
    setConfirmBulk(false);
    setBulkBusy(true);
    setError('');
    let ok = 0;
    const fouten = [];
    for (const u of doelen) {
      try { await resendWelcome(token, u.id); ok++; }
      catch (err) { fouten.push(`${u.email}: ${err.message}`); }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    loadUsers();
    if (fouten.length === 0) toast.success(`${ok} uitnodiging${ok === 1 ? '' : 'en'} verstuurd.`);
    else { toast.error(`${ok} verstuurd, ${fouten.length} mislukt.`); setError(`Niet verstuurd — ${fouten.join(' · ')}`); }
  };

  const handleDelete = async (userId) => {
    try { await deleteUser(token, userId); setConfirmDelete(null); loadUsers(); }
    catch (err) { setError(err.message); }
  };

  // Uitnodiging opnieuw versturen. Bewust met een bevestiging: de server zet
  // hierbij een nieuw tijdelijk wachtwoord, dus het oude werkt daarna niet meer.
  const handleResend = async (userId) => {
    setError('');
    setResendBusy(true);
    try {
      const data = await resendWelcome(token, userId);
      setConfirmResend(null);
      toast.success(data.eersteUitnodiging ? `Uitnodiging verstuurd naar ${data.email}.` : `Uitnodiging opnieuw verstuurd naar ${data.email}.`);
      loadUsers();
    } catch (err) { setError(err.message); }
    finally { setResendBusy(false); }
  };

  const handleImpersonate = async (userId) => {
    try {
      const data = await impersonateUser(token, userId);
      // White-label: hoort de gebruiker op een ander merkdomein, spring daar dan
      // heen zodat de URL het merk volgt (partners.<domein>). De sessie reist mee
      // via een eenmalige overdrachtcode; het eigen token van de beheerder blijft
      // op dit domein staan, zodat "stop impersonatie" hier weer uitkomt.
      const host = brandHopTarget(data.user, 'partner');
      if (host) {
        // Mislukt de overdracht, blijf dan gewoon op dit domein impersoneren
        // (de merk-guard laat dat toe) i.p.v. de beheerder te laten stranden.
        try { await hopToBrandDomain(host, data.token, { page: 'orders' }); return; }
        catch { /* val terug op impersonatie binnen dit domein */ }
      }
      onImpersonate(data.token, data.user, data.impersonatedBy);
    } catch (err) { setError(err.message); }
  };

  const ScopeFields = ({ value, onChange, role, locked, readOnly = false }) => {
    const fields = scopeFieldsForRole(role);
    if (fields.length === 0) return null;
    // Beschikbare labels uit /api/reference (scope-gefilterd). Als locked, toon
    // alleen de gelockte waarde. Anders alle beschikbare labels.
    const labelOptions = locked?.energy_label !== undefined
      ? [locked.energy_label].filter(Boolean)
      : (labelsTeams.energy_labels || []);
    // Teams cascaden op gekozen label: pak teams_by_label[selected]; bij locked
    // beperken we tot de gelockte team.
    const currentLabel = value.energy_label || '';
    const teamOptions = locked?.sales_team !== undefined
      ? [locked.sales_team].filter(Boolean)
      : (labelsTeams.teams_by_label?.[currentLabel] || []);
    // Verkopers cascaden op gekozen energielabel + verkoopteam. Bron is de centrale
    // sales-structuur (sellers_by_label_team) — dat zijn exact de order.salesperson-
    // waarden binnen dat label/team, en de reseller-scope matcht juist op
    // seller === order.salesperson. Een dropdown i.p.v. vrij tekstveld sluit typo's/
    // mismatch dus volledig uit. Een reeds ingestelde verkoper (bij bewerken) blijft
    // kiesbaar, ook als die niet meer in de afgeleide lijst voorkomt.
    const currentTeam = value.sales_team || '';
    const sellerBase = (salesStructure?.sellers_by_label_team?.[currentLabel]?.[currentTeam]) || [];
    const sellerOptions = value.seller_name && !sellerBase.includes(value.seller_name)
      ? [value.seller_name, ...sellerBase]
      : sellerBase;
    return <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {fields.includes('prospect_label') && <div className="sm:col-span-3">
        <label className="block text-sm text-slate-400 mb-1.5">Naam van het potentiële energielabel</label>
        <input
          value={value.prospect_label || ''}
          disabled={readOnly}
          onChange={e => onChange({ ...value, prospect_label: e.target.value })}
          placeholder="Bijv. Zonnestroom Energie"
          maxLength={255}
          required
          className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm disabled:opacity-60"
        />
        <p className="text-[11px] text-slate-500 mt-1">Deze naam vervangt overal Power2You (filters, tabellen, exports, rapporten) en is de sleutel voor de eigen huisstijl op de pagina Branding. De gegevens zelf komen geanonimiseerd uit Power2You.</p>
        <p className="text-[11px] text-slate-500 mt-1">{role === 'prospect_label'
          ? 'Prospect Energielabel: huisstijl als energielabel (Branding → Energielabels), margeaandeel standaard 100 %.'
          : 'Prospect Reseller: huisstijl als verkoopteam onder Power2You (Branding → Verkoopteams), margeaandeel standaard 75 %.'}</p>
      </div>}
      {fields.includes('energy_label') && <div>
        <label className="block text-sm text-slate-400 mb-1.5">Energielabel</label>
        <select
          value={value.energy_label || ''}
          disabled={readOnly || locked?.energy_label !== undefined}
          onChange={e => {
            const next = e.target.value;
            // Reset team als die niet bij nieuw label hoort; reset verkoper mee
            // zodra het team (en dus de verkoper-cascade) wijzigt.
            const newTeams = labelsTeams.teams_by_label?.[next] || [];
            const keepTeam = value.sales_team && newTeams.includes(value.sales_team) ? value.sales_team : '';
            const keepSeller = keepTeam === value.sales_team ? value.seller_name : '';
            onChange({ ...value, energy_label: next, sales_team: keepTeam, seller_name: keepSeller });
          }}
          required
          className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm disabled:opacity-60"
        >
          <option value="">— kies energielabel —</option>
          {labelOptions.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>}
      {fields.includes('sales_team') && <div>
        <label className="block text-sm text-slate-400 mb-1.5">Verkoopteam</label>
        <select
          value={value.sales_team || ''}
          disabled={readOnly || locked?.sales_team !== undefined || (!currentLabel && locked?.energy_label === undefined)}
          onChange={e => onChange({ ...value, sales_team: e.target.value, seller_name: '' })}
          required
          className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm disabled:opacity-60"
        >
          <option value="">{currentLabel ? '— kies verkoopteam —' : 'eerst energielabel kiezen'}</option>
          {teamOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>}
      {fields.includes('seller_name') && <div>
        <label className="block text-sm text-slate-400 mb-1.5">Verkopernaam</label>
        <select
          value={value.seller_name || ''}
          disabled={readOnly || !currentTeam}
          onChange={e => onChange({ ...value, seller_name: e.target.value })}
          required
          className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm disabled:opacity-60"
        >
          <option value="">{currentTeam ? (sellerOptions.length ? '— kies verkoper —' : 'geen verkopers in dit team') : 'eerst verkoopteam kiezen'}</option>
          {sellerOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>}
    </div>;
  };

  // ── Filters op de gebruikerslijst ───────────────────────────────────────────
  // De keuzes komen uit de GELADEN gebruikers zelf, niet uit de referentiedata van
  // het aanmaakformulier: die laatste bevat labels/teams waar (nog) geen enkele
  // gebruiker aan hangt, en dan kies je iets dat 0 rijen oplevert.
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [labelFilter, setLabelFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  useFilterPersistence('users.filters', currentUser,
    { userSearch, roleFilter, labelFilter, teamFilter, statusFilter },
    (s) => { setUserSearch(s.userSearch ?? ''); setRoleFilter(s.roleFilter ?? 'all'); setLabelFilter(s.labelFilter ?? 'all'); setTeamFilter(s.teamFilter ?? 'all'); setStatusFilter(s.statusFilter ?? 'all'); });

  const roleOptions = useMemo(() => [...new Set(users.map(u => u.role).filter(Boolean))].sort((a, b) => roleLabel(a).localeCompare(roleLabel(b))), [users]);
  const labelOptions = useMemo(() => [...new Set(users.map(u => u.energy_label).filter(Boolean))].sort(), [users]);
  // Verkoopteams cascaderen op het gekozen energielabel — een teamnaam is enkel
  // binnen een label betekenisvol, dus zonder die cascade bied je teams aan die
  // bij een ander label horen.
  const teamOptions = useMemo(() => [...new Set(users
    .filter(u => labelFilter === 'all' || u.energy_label === labelFilter)
    .map(u => u.sales_team).filter(Boolean))].sort(), [users, labelFilter]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (labelFilter !== 'all' && (u.energy_label || '') !== labelFilter) return false;
      if (teamFilter !== 'all' && (u.sales_team || '') !== teamFilter) return false;
      if (statusFilter !== 'all' && gebruikerStatus(u) !== statusFilter) return false;
      if (q && !(u.name || '').toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, roleFilter, labelFilter, teamFilter, statusFilter, userSearch]);
  const userFilterActief = roleFilter !== 'all' || labelFilter !== 'all' || teamFilter !== 'all' || statusFilter !== 'all' || !!userSearch.trim();
  const wisUserFilters = () => { setRoleFilter('all'); setLabelFilter('all'); setTeamFilter('all'); setStatusFilter('all'); setUserSearch(''); };

  const usersSort = useTableSort('name');
  const userSortGetters = { role: u => roleLabel(u.role), mfa_enabled: u => (u.mfa_enabled ? 1 : 0), created_at: u => (u.created_at ? new Date(u.created_at).getTime() : 0), last_login_at: u => (u.last_login_at ? new Date(u.last_login_at).getTime() : 0) };
  const gesorteerdeUsers = sortRows(filteredUsers, usersSort.sortConfig, userSortGetters);

  // Bulk-selectie: enkel gebruikers die nog niet uitgenodigd zijn (en niet jijzelf).
  const teSelecteren = isAdmin ? filteredUsers.filter((u) => u.welcome_pending && u.id !== currentUser?.id) : [];
  const allesGeselecteerd = teSelecteren.length > 0 && teSelecteren.every((u) => selectedIds.has(u.id));
  const toggleAlles = () => setSelectedIds(allesGeselecteerd ? new Set() : new Set(teSelecteren.map((u) => u.id)));
  const toggleSelectie = (id) => setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  // Export = wat de tabel toont (gefilterd én gesorteerd), platformconventie.
  const exportGebruikers = () => exportRowsToCsv(exportBestandsnaam('gebruikers.csv'), [
    { header: 'Naam', get: u => u.name },
    { header: 'E-mail', get: u => u.email },
    { header: 'Rol', get: u => roleLabel(u.role) },
    { header: 'Energielabel', get: u => u.energy_label || '' },
    { header: 'Verkoopteam', get: u => u.sales_team || '' },
    { header: 'Verkoper', get: u => u.seller_name || '' },
    { header: 'MFA actief', get: u => (u.mfa_enabled ? 'Ja' : 'Nee') },
    { header: 'Status', get: u => GEBRUIKER_STATUS_LABELS[gebruikerStatus(u)] },
    { header: 'Aangemaakt op', get: u => gebruikerAangemaakt(u.created_at) },
    { header: 'Laatst aangemeld', get: u => gebruikerLaatstAangemeld(u.last_login_at) },
    { header: 'Modules', get: u => (u.modules || []).map((id) => MODULE_CATALOG.find((m) => m.id === id)?.label || id).join(', ') },
  ], gesorteerdeUsers);
  return <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-2 min-w-0"><h2 className="text-2xl font-bold text-slate-100">Gebruikers</h2><ExportKnop onExport={exportGebruikers} label="Exporteer de gebruikerslijst naar Excel" count={filteredUsers.length} disabled={filteredUsers.length === 0} /></div>
        <p className="text-slate-400 text-sm mt-1">{isAdmin ? 'Beheer gebruikerstoegang en -rechten' : 'Bekijk gebruikers en hun rechten (alleen-lezen)'}</p>
      </div>
      {isAdmin && <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-xl transition-all"><UserPlus size={18} />Nieuwe gebruiker</button>}
    </div>

    {showCreate && <div ref={createFormRef}><Card>
      {createSuccess ? <div className="space-y-4">
        <div className="flex items-center gap-2 text-lime-400"><Check size={20} /><span className="font-semibold">Gebruiker aangemaakt</span></div>
        <p className="text-slate-300 text-sm">{createSuccess}</p>
        <button onClick={() => { setShowCreate(false); setCreateSuccess(null); setCopySource(null); }} className="w-full py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium rounded-xl transition-all">Sluiten</button>
      </div> : <form onSubmit={handleCreate} className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-100">{copySource ? 'Nieuwe gebruiker op basis van een bestaande' : 'Nieuwe gebruiker aanmaken'}</h3>
        {copySource && <div className="flex items-start gap-2 bg-lime-500/10 border border-lime-500/30 text-lime-300 px-3 py-2.5 rounded-xl text-xs">
          <Copy size={14} className="shrink-0 w-4 mt-0.5" />
          <span>Rol, energielabel, verkoopteam, modules, subpagina's, helpdesk-rechten en de instellingen zijn overgenomen van <span className="font-semibold">{copySource}</span>. Vul naam, e-mailadres en wachtwoord aan{scopeFieldsForRole(newUser.role).includes('seller_name') ? ' en kies de verkopernaam' : ''}.</span>
        </div>}
        {createError && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">{createError}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><label className="block text-sm text-slate-400 mb-1.5">Naam</label><input type="text" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} required className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm" placeholder="Volledige naam" /></div>
          <div><label className="block text-sm text-slate-400 mb-1.5">Email</label><input type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} required className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm" placeholder="naam@bedrijf.nl" /></div>
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Wachtwoord</label>
            <div className="flex gap-2">
              <input type="text" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} required className="flex-1 min-w-0 px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm" placeholder="Min. 8 tekens" />
              {/* Genereren i.p.v. zelf tikken: het is toch een tijdelijk wachtwoord
                  (bij uitstel van de uitnodiging wordt het zelfs nog vervangen). */}
              <button type="button" onClick={() => setNewUser(p => ({ ...p, password: genereerWachtwoord() }))} title="Sterk tijdelijk wachtwoord genereren" className="shrink-0 px-3 py-2.5 bg-slate-700 hover:bg-slate-600 border border-slate-600/50 rounded-xl text-lime-300 transition-colors"><Wand2 size={16} /></button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Rol</label>
          <select value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value, ...lockedFields, modules: defaultModulesForRole(e.target.value), subPages: defaultSubPagesForRole(e.target.value), helpdeskPerms: defaultHelpdeskPermsForRole(e.target.value), mfa_required: mfaVerplichtVoorRol(e.target.value) }))} required className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm">
            <option value="">— kies rol —</option>
            {targetRoles.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
        </div>
        {newUser.role && <ScopeFields value={newUser} onChange={v => setNewUser(p => ({ ...p, ...v }))} role={newUser.role} locked={lockedFields} />}
        {newUser.role && verkoperConflict(newUser) && <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 px-3 py-2.5 rounded-xl text-xs"><AlertCircle size={14} className="shrink-0 w-4 mt-0.5" /><span>{verkoperConflictTekst(newUser, verkoperConflict(newUser))}</span></div>}
        {newUser.role && <AllOrdersToggle role={newUser.role} value={newUser.view_all_label_orders} energyLabel={newUser.energy_label} onChange={v => setNewUser(p => ({ ...p, view_all_label_orders: v }))} />}
        {newUser.role && <DemoModeToggle role={newUser.role} value={newUser.demo_mode} onChange={v => setNewUser(p => ({ ...p, demo_mode: v }))} />}
        {newUser.role && <RevenueDocsToggle role={newUser.role} energyLabel={newUser.energy_label} value={newUser.revenue_docs} onChange={v => setNewUser(p => ({ ...p, revenue_docs: v }))} />}
        {newUser.role && <MfaToggle role={newUser.role} value={newUser.mfa_required} onChange={v => setNewUser(p => ({ ...p, mfa_required: v }))} />}
        {newUser.role && <ResellerFeeField role={newUser.role} value={newUser.reseller_fee_pct} onChange={v => setNewUser(p => ({ ...p, reseller_fee_pct: v }))} />}
        {newUser.role && <ToegangPicker role={newUser.role} modules={newUser.modules} subPages={newUser.subPages} helpdeskPerms={newUser.helpdeskPerms} helpdeskAssignable={newUser.helpdesk_assignable} helpdeskAi360={newUser.helpdesk_ai360} energyLabel={newUser.energy_label} onChange={toegangNieuw} />}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => { setShowCreate(false); setCopySource(null); }} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-all text-sm">Annuleren</button>
          {/* Zonder mail: de gebruiker bestaat meteen, de uitnodiging volgt later
              via het vliegtuigje in de tabel (op een gepast moment). */}
          <button type="button" onClick={handleCreateZonderMail} disabled={creating} title="Gebruiker aanmaken zonder nu de uitnodigingsmail te versturen — later te versturen via het vliegtuigje in de tabel" className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 border border-lime-500/40 text-lime-300 font-medium rounded-xl transition-all disabled:opacity-50 text-sm">{creating ? 'Bezig...' : 'Aanmaken zonder verzenden'}</button>
          <button type="submit" disabled={creating} title="Gebruiker aanmaken en de uitnodigingsmail meteen versturen" className="px-6 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-xl transition-all disabled:opacity-50 text-sm">{creating ? 'Bezig...' : 'Aanmaken en uitnodigen'}</button>
        </div>
      </form>}
    </Card></div>}

    {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>}

    {/* Filterbalk in dezelfde vorm als op de datapagina's: cascade links, vrije
        zoektekst rechts, en de "Wissen"-knop pas zodra er iets aan staat. */}
    <Card className="!p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[106px] sm:w-[150px]">
          <label className="block text-xs text-slate-400 mb-1">Rol</label>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${roleFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <AllOption options={roleOptions}>Alle</AllOption>
            {roleOptions.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
        </div>
        <div className="w-[106px] sm:w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Energieleverancier</label>
          <select value={labelFilter} onChange={e => { setLabelFilter(e.target.value); setTeamFilter('all'); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${labelFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <AllOption options={labelOptions}>Alle</AllOption>
            {labelOptions.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="w-[106px] sm:w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Verkoopteam</label>
          <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${teamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <AllOption options={teamOptions}>Alle</AllOption>
            {teamOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="w-[106px] sm:w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${statusFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <option value="all">Alle</option>
            {Object.entries(GEBRUIKER_STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="w-full sm:w-[280px] shrink-0">
          <label className="block text-xs text-slate-400 mb-1">Zoeken</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2 text-slate-500" />
            <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Naam of e-mailadres…" className="w-full pl-7 pr-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200 placeholder-slate-500" />
          </div>
        </div>
        {userFilterActief && <button onClick={wisUserFilters} className="tag tag-fail px-2 py-1.5 text-xs"><X size={12} />Wissen</button>}
      </div>
      {userFilterActief && <div className="mt-2 text-xs text-lime-400">Gefilterd: {filteredUsers.length} van {users.length} gebruikers</div>}
      {/* Bulk-uitnodigingen: verschijnt zodra er nog-niet-uitgenodigde gebruikers
          aangevinkt zijn. Zelfde route als het vliegtuigje, één per gebruiker. */}
      {isAdmin && selectedIds.size > 0 && <div className="mt-3 pt-3 border-t border-slate-700/50 flex flex-wrap items-center gap-3">
        <span className="text-xs text-slate-300"><span className="font-semibold text-lime-400">{selectedIds.size}</span> nog niet uitgenodigd geselecteerd</span>
        {confirmBulk
          ? <div className="flex items-center gap-2"><span className="text-xs text-amber-400">Uitnodigingen versturen naar {selectedIds.size} gebruiker{selectedIds.size === 1 ? '' : 's'}?</span><button disabled={bulkBusy} onClick={verstuurBulk} className="px-3 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-lg hover:bg-amber-500/30 disabled:opacity-50">Ja</button><button onClick={() => setConfirmBulk(false)} className="px-3 py-1 bg-slate-700 text-slate-400 text-xs rounded-lg hover:bg-slate-600">Nee</button></div>
          : <button disabled={bulkBusy} onClick={() => setConfirmBulk(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lime-500 hover:bg-lime-400 text-slate-900 text-xs font-semibold rounded-lg disabled:opacity-50">{bulkBusy ? <Spinner size={12} /> : <Send size={12} />}Uitnodigingen versturen ({selectedIds.size})</button>}
        <button onClick={() => { setSelectedIds(new Set()); setConfirmBulk(false); }} className="text-xs text-slate-400 hover:text-slate-200">Selectie wissen</button>
      </div>}
    </Card>

    <Card className="p-0 overflow-hidden">
      {loading ? <div className="flex justify-center py-12"><Spinner size={32} /></div> :
      <div className="overflow-x-auto">
      <table className="w-full resp-card">
        <thead><tr className="border-b border-slate-700/50">
          {isAdmin && <th className="pl-4 pr-1 py-4 w-8">{teSelecteren.length > 0 && <input type="checkbox" checked={allesGeselecteerd} onChange={toggleAlles} title="Alle nog niet uitgenodigde gebruikers in deze lijst selecteren" className="w-4 h-4 rounded accent-lime-500" />}</th>}
          <SortHeader label="Naam" k="name" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="Email" k="email" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="Rol" k="role" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="Energielabel" k="energy_label" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="Verkoopteam" k="sales_team" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="Verkoper" k="seller_name" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="MFA" k="mfa_enabled" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="Aangemaakt op" k="created_at" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <SortHeader label="Laatst aangemeld" k="last_login_at" sortConfig={usersSort.sortConfig} onSort={usersSort.handleSort} thClass="px-6 py-4 text-xs font-semibold uppercase" />
          <th className="text-right px-6 py-4 text-xs font-semibold text-slate-400 uppercase"></th>
        </tr></thead>
        {/* created_at als tijdstempel sorteren, niet als tekst: dan staat de kolom
            op datum en niet op de toevallige schrijfwijze van de string. */}
        <tbody>{gesorteerdeUsers.map(u => <tr key={u.id} className={`border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors ${u.active === false ? 'opacity-60' : ''}`}>
          {isAdmin && <td className="pl-4 pr-1 py-4">{u.welcome_pending && u.id !== currentUser?.id && <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleSelectie(u.id)} title="Selecteren voor bulk-uitnodiging" className="w-4 h-4 rounded accent-lime-500" />}</td>}
          <td className="px-6 py-4 text-sm text-slate-200 font-medium">
            {u.name}
            {/* Gedeactiveerd wint van elke andere badge: er valt niets meer uit te nodigen. */}
            {u.active === false && <span title="Gedeactiveerd — kan niet meer aanmelden; historiek blijft bewaard" className="block mt-1 w-fit inline-flex items-center gap-1 text-[11px] font-medium text-red-300 bg-red-500/10 border border-red-500/30 px-2 py-0.5 rounded-full"><UserX size={10} />Gedeactiveerd</span>}
            {/* Aangemaakt met "Aanmaken zonder verzenden": de uitnodiging staat nog
                in de wacht tot iemand op het vliegtuigje klikt. */}
            {u.active !== false && u.welcome_pending && <span title="Aangemaakt zonder uitnodiging — verstuur die met het vliegtuigje rechts" className="block mt-1 w-fit inline-flex items-center gap-1 text-[11px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full"><Send size={10} />Nog niet uitgenodigd</span>}
          </td>
          <td className="px-6 py-4 text-sm text-slate-400">{u.email}</td>
          <td className="px-6 py-4 text-sm text-slate-300"><span className="px-2 py-0.5 rounded-full text-xs bg-slate-700/50 border border-slate-600/50">{roleLabel(u.role)}</span></td>
          <td className="px-6 py-4 text-sm text-slate-400">{u.energy_label || '—'}</td>
          <td className="px-6 py-4 text-sm text-slate-400">{u.sales_team || '—'}</td>
          <td className="px-6 py-4 text-sm text-slate-400">{u.seller_name || '—'}</td>
          <td className="px-6 py-4">{u.mfa_enabled ? <span className="inline-flex items-center gap-1.5 text-xs font-medium text-lime-400 bg-lime-500/10 border border-lime-500/30 px-2.5 py-1 rounded-full"><Shield size={12} />Actief</span> : <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-slate-700/50 px-2.5 py-1 rounded-full"><Eye size={12} />Niet</span>}</td>
          <td className="px-6 py-4 text-sm text-slate-400 whitespace-nowrap">{gebruikerAangemaakt(u.created_at)}</td>
          <td className="px-6 py-4 text-sm text-slate-400 whitespace-nowrap">
            {gebruikerLaatstAangemeld(u.last_login_at)}
            {/* Slapende accounts: nooit aangemeld (na een uitnodiging) of al lang niet meer. */}
            {gebruikerStatus(u) === 'never' && <span className="block mt-1 w-fit text-[11px] font-medium text-slate-400 bg-slate-700/50 border border-slate-600/50 px-2 py-0.5 rounded-full">Nooit aangemeld</span>}
            {gebruikerStatus(u) === 'inactive' && <span className="block mt-1 w-fit text-[11px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full">{dagenSinds(u.last_login_at)} dagen niet aangemeld</span>}
          </td>
          <td className="px-6 py-4 text-right whitespace-nowrap">{u.id === currentUser?.id ? <div className="flex items-center gap-2 justify-end">
            <span className="text-xs text-slate-500 italic">jij</span>
            {/* Je eigen account mag je niet bewerken of verwijderen, maar wél als
                sjabloon gebruiken voor een collega met dezelfde rechten. */}
            {isAdmin && <button onClick={() => openCopy(u)} title="Kopiëren als basis voor een nieuwe login" className="shrink-0 w-4 text-slate-600 hover:text-lime-400 transition-colors"><Copy size={16} /></button>}
          </div> : !isAdmin ? <div className="flex items-center gap-2 justify-end">
            <button onClick={() => { setViewOnly(true); setEditTarget({ ...u }); }} title="Details bekijken" className="shrink-0 w-4 text-slate-600 hover:text-lime-400 transition-colors"><Eye size={16} /></button>
          </div> : confirmDelete === u.id ? <div className="flex items-center gap-2 justify-end"><span className="text-xs text-red-400">Zeker?</span><button onClick={() => handleDelete(u.id)} className="px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded-lg hover:bg-red-500/30">Ja</button><button onClick={() => setConfirmDelete(null)} className="px-3 py-1 bg-slate-700 text-slate-400 text-xs rounded-lg hover:bg-slate-600">Nee</button></div>
          : confirmActive === u.id ? <div className="flex items-center gap-2 justify-end"><span className="text-xs text-amber-400">{u.active === false ? 'Account activeren?' : 'Account deactiveren?'}</span><button onClick={() => handleSetActive(u)} className="px-3 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-lg hover:bg-amber-500/30">Ja</button><button onClick={() => setConfirmActive(null)} className="px-3 py-1 bg-slate-700 text-slate-400 text-xs rounded-lg hover:bg-slate-600">Nee</button></div>
          : confirmResend === u.id ? <div className="flex items-center gap-2 justify-end"><span className="text-xs text-amber-400">{u.welcome_pending ? 'Uitnodiging versturen?' : 'Nieuw wachtwoord mailen?'}</span><button disabled={resendBusy} onClick={() => handleResend(u.id)} className="px-3 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-lg hover:bg-amber-500/30 disabled:opacity-50">Ja</button><button onClick={() => setConfirmResend(null)} className="px-3 py-1 bg-slate-700 text-slate-400 text-xs rounded-lg hover:bg-slate-600">Nee</button></div>
          : <div className="flex items-center gap-2 justify-end">
            <button onClick={() => { setViewOnly(false); setEditTarget({ ...u }); }} title="Bewerken" className="shrink-0 w-4 text-slate-600 hover:text-lime-400 transition-colors"><Edit2 size={16} /></button>
            <button onClick={() => openCopy(u)} title="Kopiëren als basis voor een nieuwe login — neemt rol, scope, modules, subpagina's en instellingen over; naam, e-mailadres en wachtwoord vul je zelf aan" className="shrink-0 w-4 text-slate-600 hover:text-lime-400 transition-colors"><Copy size={16} /></button>
            <button onClick={() => handleImpersonate(u.id)} disabled={u.active === false} title={u.active === false ? 'Gedeactiveerd — activeer het account eerst' : 'Inloggen als deze gebruiker — bekijk het portal met hun rechten'} className="shrink-0 w-4 text-slate-600 hover:text-lime-400 transition-colors disabled:opacity-40 disabled:hover:text-slate-600"><Eye size={16} /></button>
            <button onClick={() => { setConfirmDelete(null); setConfirmResend(null); setConfirmActive(u.id); }} title={u.active === false ? 'Account activeren' : 'Account deactiveren — kan niet meer aanmelden, historiek blijft bewaard'} className={`shrink-0 w-4 transition-colors ${u.active === false ? 'text-lime-500 hover:text-lime-300' : 'text-slate-600 hover:text-red-400'}`}>{u.active === false ? <UserCheck size={16} /> : <UserX size={16} />}</button>
            <button onClick={() => { setConfirmDelete(null); setConfirmActive(null); setConfirmResend(u.id); }} title={u.welcome_pending ? 'Uitnodiging versturen — stelt een nieuw tijdelijk wachtwoord in en mailt het' : 'Uitnodiging opnieuw versturen — stelt een nieuw tijdelijk wachtwoord in en mailt het'} className={`shrink-0 w-4 transition-colors ${u.welcome_pending ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-amber-400'}`}><Send size={16} /></button>
            <button onClick={() => setConfirmDelete(u.id)} title="Verwijderen" className="shrink-0 w-4 text-slate-600 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
          </div>}</td>
        </tr>)}
        {filteredUsers.length === 0 && <tr><td colSpan={isAdmin ? 11 : 10} className="px-6 py-10"><TableEmpty message={users.length === 0 ? 'Nog geen gebruikers.' : 'Geen gebruikers binnen deze filters.'} icon={users.length === 0 ? Folder : Search} /></td></tr>}
        </tbody>
      </table>
      </div>}
    </Card>

    <SalesMappingOverview structure={salesStructure} />

    {editTarget && <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setEditTarget(null); setEditError(''); }}>
      <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
          <div>
            <h3 className="text-lg font-bold text-slate-100">{editTarget.name}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{editTarget.email}{viewOnly ? ' · alleen-lezen' : ''}</p>
          </div>
          <button onClick={() => setEditTarget(null)} className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {editError && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">{editError}</div>}
          {!viewOnly && currentUser && editTarget.id === currentUser.id && <div className="flex items-start gap-2 bg-slate-700/40 border border-slate-600/50 text-slate-300 px-3 py-2.5 rounded-xl text-xs"><Info size={14} className="shrink-0 w-4 mt-0.5" /><span>Dit is je eigen account: je rol, scope en rechten kun je hier niet aanpassen — dat moet een andere beheerder doen. Je persoonlijke API-sleutel zet je wél zelf.</span></div>}
          {/* Naam — het e-mailadres blijft vast (dat is de inlognaam en de sleutel
              van de uitnodigingsmail); de naam mag wel bijgewerkt worden, bv. na een
              huwelijk, een typfout of een naamsverandering van een verkoper. */}
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Naam</label>
            {viewOnly
              ? <div className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-slate-300 text-sm">{editTarget.name}</div>
              : <input type="text" value={editTarget.name || ''} onChange={e => setEditTarget(p => ({ ...p, name: e.target.value }))} maxLength={255} required className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm" placeholder="Volledige naam" />}
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Rol</label>
            {viewOnly
              ? <div className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-slate-300 text-sm">{roleLabel(editTarget.role)}</div>
              : <select value={editTarget.role} onChange={e => setEditTarget(p => ({ ...p, role: e.target.value, mfa_required: mfaVerplichtVoorRol(e.target.value) }))} className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 text-sm">
                  {targetRoles.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </select>}
          </div>
          <ScopeFields value={editTarget} onChange={v => setEditTarget(p => ({ ...p, ...v }))} role={editTarget.role} locked={lockedFields} readOnly={viewOnly} />
          {!viewOnly && verkoperConflict(editTarget, editTarget.id) && <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 px-3 py-2.5 rounded-xl text-xs"><AlertCircle size={14} className="shrink-0 w-4 mt-0.5" /><span>{verkoperConflictTekst(editTarget, verkoperConflict(editTarget, editTarget.id))}</span></div>}
          <AllOrdersToggle role={editTarget.role} value={editTarget.view_all_label_orders} energyLabel={editTarget.energy_label} onChange={v => setEditTarget(p => ({ ...p, view_all_label_orders: v }))} readOnly={viewOnly} />
          <DemoModeToggle role={editTarget.role} value={editTarget.demo_mode} onChange={v => setEditTarget(p => ({ ...p, demo_mode: v }))} readOnly={viewOnly} />
          <RevenueDocsToggle role={editTarget.role} energyLabel={editTarget.energy_label} value={editTarget.revenue_docs} onChange={v => setEditTarget(p => ({ ...p, revenue_docs: v }))} readOnly={viewOnly} />
          <MfaToggle role={editTarget.role} value={editTarget.mfa_required} onChange={v => setEditTarget(p => ({ ...p, mfa_required: v }))} readOnly={viewOnly} />
          <ResellerFeeField role={editTarget.role} value={editTarget.reseller_fee_pct} onChange={v => setEditTarget(p => ({ ...p, reseller_fee_pct: v }))} readOnly={viewOnly} />
          <EtApiKeyField role={editTarget.role} meta={editTarget.etApiKey} value={editTarget.et_api_key_nieuw} wissen={editTarget.et_api_key_wissen}
            onChange={v => setEditTarget(p => ({ ...p, et_api_key_nieuw: v, et_api_key_wissen: false }))}
            onWissen={v => setEditTarget(p => ({ ...p, et_api_key_wissen: v, et_api_key_nieuw: '' }))} readOnly={viewOnly} />
          <ToegangPicker role={editTarget.role} modules={editTarget.modules} subPages={editTarget.subPages} helpdeskPerms={editTarget.helpdeskPerms} helpdeskAssignable={editTarget.helpdesk_assignable} helpdeskAi360={editTarget.helpdesk_ai360} energyLabel={editTarget.energy_label} onChange={toegangBewerk} readOnly={viewOnly} />
          <div className="flex gap-3 justify-end pt-2">
            {viewOnly
              ? <button onClick={() => setEditTarget(null)} className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium rounded-xl transition-all text-sm">Sluiten</button>
              : <>
                  <button onClick={() => setEditTarget(null)} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-all text-sm">Annuleren</button>
                  <button onClick={handleSaveEdit} className="px-6 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-xl transition-all text-sm">Opslaan</button>
                </>}
          </div>
        </div>
      </div>
    </div>}
  </div>;
};

const AccountPage = ({ token, onLogout }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ type: '', text: '' });
    if (newPassword !== confirmPassword) {
      setPwMsg({ type: 'error', text: 'Nieuwe wachtwoorden komen niet overeen' });
      return;
    }
    if (newPassword.length < 8) {
      setPwMsg({ type: 'error', text: 'Nieuw wachtwoord moet minimaal 8 tekens bevatten' });
      return;
    }
    setPwLoading(true);
    try {
      await changePassword(token, oldPassword, newPassword);
      setPwMsg({ type: 'success', text: 'Wachtwoord succesvol gewijzigd' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwMsg({ type: 'error', text: err.message });
    } finally {
      setPwLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const data = await exportData(token);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `mijn-gegevens-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error(err.message || 'Exporteren mislukt');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Voer je wachtwoord in ter bevestiging');
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteAccount(token, deletePassword);
      onLogout();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return <div className="space-y-6">
    <PageHeader title="Account" subtitle="Beheer je accountinstellingen en privacy" />

    <MeldingenVoorkeuren token={token} />

    <Card>
      <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2"><KeyRound size={20} className="text-lime-400" />Wachtwoord wijzigen</h3>
      <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Huidig wachtwoord</label>
          <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} required className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-200 focus:outline-none focus:border-lime-500" />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Nieuw wachtwoord</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-200 focus:outline-none focus:border-lime-500" />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Bevestig nieuw wachtwoord</label>
          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={8} className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-200 focus:outline-none focus:border-lime-500" />
        </div>
        {pwMsg.text && <div className={`p-3 rounded-lg text-sm ${pwMsg.type === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-lime-500/10 border border-lime-500/30 text-lime-400'}`}>{pwMsg.text}</div>}
        <button type="submit" disabled={pwLoading} className="px-6 py-2.5 bg-lime-500 hover:bg-lime-400 disabled:bg-lime-500/50 text-slate-900 font-semibold rounded-xl transition-all">
          {pwLoading ? 'Wijzigen...' : 'Wachtwoord wijzigen'}
        </button>
      </form>
    </Card>

    <Card>
      <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2"><Download size={20} className="text-lime-400" />Gegevens exporteren</h3>
      <p className="text-slate-400 text-sm mb-4">Download al je persoonsgegevens als JSON-bestand (AVG/GDPR recht op dataportabiliteit).</p>
      <button onClick={handleExport} disabled={exportLoading} className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-700/50 text-slate-200 font-medium rounded-xl transition-all flex items-center gap-2">
        <Download size={16} />{exportLoading ? 'Exporteren...' : 'Download mijn gegevens'}
      </button>
    </Card>

    <Card className="border-red-500/20">
      <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2"><Trash2 size={20} />Account verwijderen</h3>
      <p className="text-slate-400 text-sm mb-4">Dit verwijdert je account permanent. Je gegevens worden verwijderd en audit logs geanonimiseerd. Deze actie kan niet ongedaan worden gemaakt.</p>
      {!deleteConfirm ? (
        <button onClick={() => setDeleteConfirm(true)} className="px-6 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-medium rounded-xl transition-all border border-red-500/30">
          Account verwijderen...
        </button>
      ) : (
        <div className="space-y-3 max-w-md">
          <div>
            <label className="block text-sm text-red-400 mb-1">Voer je wachtwoord in ter bevestiging</label>
            <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} className="w-full px-4 py-2.5 bg-slate-700 border border-red-500/30 rounded-xl text-slate-200 focus:outline-none focus:border-red-500" />
          </div>
          {deleteError && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{deleteError}</div>}
          <div className="flex gap-3">
            <button onClick={handleDeleteAccount} disabled={deleteLoading} className="px-6 py-2.5 bg-red-500 hover:bg-red-600 disabled:bg-red-500/50 text-white font-semibold rounded-xl transition-all">
              {deleteLoading ? 'Verwijderen...' : 'Definitief verwijderen'}
            </button>
            <button onClick={() => { setDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }} className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-all">
              Annuleren
            </button>
          </div>
        </div>
      )}
    </Card>
  </div>;
};

const AuditLogPage = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const loadLogs = async (p) => {
    setLoading(true);
    try {
      const data = await getAuditLogs(token, p);
      setLogs(data.logs);
      setPage(data.page);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(1); }, []);

  const ACTION_LABELS = {
    login_success: 'Ingelogd',
    login_failed: 'Login mislukt',
    mfa_verify_success: 'MFA geverifieerd',
    mfa_verify_failed: 'MFA mislukt',
    mfa_setup_success: 'MFA ingesteld',
    mfa_setup_failed: 'MFA instelling mislukt',
    register_user: 'Gebruiker aangemaakt',
    send_welcome: 'Uitnodiging verstuurd',
    resend_welcome: 'Uitnodiging opnieuw verstuurd',
    deactivate_user: 'Gebruiker gedeactiveerd',
    activate_user: 'Gebruiker geactiveerd',
    change_role: 'Rol gewijzigd',
    delete_user: 'Gebruiker verwijderd',
    impersonate_start: 'Impersonatie gestart',
    impersonate_stop: 'Impersonatie gestopt',
    accept_privacy: 'Privacy geaccepteerd',
    change_password: 'Wachtwoord gewijzigd',
    change_password_failed: 'Wachtwoord wijziging mislukt',
    export_data: 'Data geexporteerd',
    account_deleted: 'Account verwijderd',
    change_order_reseller: 'Verkoper gewijzigd',
  };

  const formatDetails = (details) => {
    if (!details) return '-';
    // Verkoper/team-wijziging (change_order_reseller): klant · order + van → naar.
    if (details.from && details.to) {
      const f = details.from, t = details.to;
      const seg = [];
      if (details.customer) seg.push(details.customer);
      if (details.orderNumber) seg.push(`order ${details.orderNumber}`);
      seg.push(`${f.salesperson || '—'}${f.team ? ` (${f.team})` : ''} → ${t.salesperson || '—'}${t.team ? ` (${t.team})` : ''}`);
      return seg.join(' · ');
    }
    const parts = [];
    if (details.email) parts.push(details.email);
    if (details.targetEmail) parts.push(`doelwit: ${details.targetEmail}`);
    if (details.newRole) parts.push(`rol: ${details.newRole}`);
    if (details.reason) parts.push(details.reason);
    if (details.name) parts.push(details.name);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  return <div className="space-y-6">
    <PageHeader title="Audit Log" subtitle="Overzicht van alle gebruikersacties" />

    <Card>
      {loading ? (
        <div className="flex justify-center py-12"><Spinner size={32} /></div>
      ) : logs.length === 0 ? (
        <p className="text-slate-500 text-center py-12">Geen audit logs gevonden</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead><tr className="border-b border-slate-700">
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Datum</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Gebruiker</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Actie</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">Details</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase">IP</th>
              </tr></thead>
              <tbody>{logs.map(log => (
                <tr key={log.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">{new Date(log.created_at).toLocaleString('nl-NL')}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{log.user_email || <span className="text-slate-600">-</span>}</td>
                  <td className="px-4 py-3 text-sm"><span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${log.action.includes('failed') ? 'bg-red-500/10 text-red-400' : 'bg-slate-700 text-slate-300'}`}>{ACTION_LABELS[log.action] || log.action}</span></td>
                  <td className="px-4 py-3 text-sm text-slate-400">{formatDetails(log.details)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">{log.ip_address || '-'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
              <span className="text-sm text-slate-500">Pagina {page} van {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => loadLogs(page - 1)} disabled={page <= 1} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-300 text-sm rounded-lg transition-all">Vorige</button>
                <button onClick={() => loadLogs(page + 1)} disabled={page >= totalPages} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-300 text-sm rounded-lg transition-all">Volgende</button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  </div>;
};

// --- Mail Log (admin + ET Dossierbeheerder): in-/uitgaande e-mails -----------
// Toont het mail-logboek (mail_log) dat door config/email.logMail wordt gevuld
// bij elke verzending: status, afzender, ontvanger, onderwerp, type, label,
// triggerende gebruiker en (bij falen) de foutmelding. Read-only, gepagineerd.
const MailLogPage = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p) });
      if (status) params.set('status', status);
      if (q.trim()) params.set('q', q.trim());
      const r = await fetch(`/api/mail-log?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json(); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setLogs(d.logs || []); setPage(d.page || 1); setTotalPages(d.totalPages || 1); setTotal(d.total || 0);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  const statusBadge = (s) => s === 'sent'
    ? <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-lime-500/15 text-lime-300">Verstuurd</span>
    : <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-500/15 text-red-300">Mislukt</span>;

  return <div className="space-y-6">
    <PageHeader title="Mail Log" subtitle="Overzicht van alle in- en uitgaande e-mails: afzender, ontvanger, onderwerp, status, gebruiker en tijdstip." />
    <Card>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <label className="block"><span className="text-xs text-slate-400">Status</span>
          <select value={status} onChange={e => setStatus(e.target.value)} className="block mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100">
            <option value="">Alle</option><option value="sent">Verstuurd</option><option value="failed">Mislukt</option>
          </select></label>
        <label className="block flex-1 min-w-[200px]"><span className="text-xs text-slate-400">Zoek (afzender / ontvanger / onderwerp)</span>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(1)} placeholder="bv. klantenservice@ of welkom" className="w-full mt-1 px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100 font-mono" /></label>
        <button onClick={() => load(1)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg text-sm font-medium">Filter</button>
      </div>
      {loading ? <div className="flex justify-center py-12"><Spinner size={32} /></div>
        : logs.length === 0 ? <p className="text-slate-500 text-center py-12">Geen mails gevonden.</p>
        : <>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead><tr className="border-b border-slate-700">
                <th className="px-3 py-3 text-xs font-medium text-slate-400 uppercase">Datum</th>
                <th className="px-3 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-3 py-3 text-xs font-medium text-slate-400 uppercase">Afzender</th>
                <th className="px-3 py-3 text-xs font-medium text-slate-400 uppercase">Ontvanger</th>
                <th className="px-3 py-3 text-xs font-medium text-slate-400 uppercase">Onderwerp</th>
                <th className="px-3 py-3 text-xs font-medium text-slate-400 uppercase">Type</th>
                <th className="px-3 py-3 text-xs font-medium text-slate-400 uppercase">Gebruiker</th>
              </tr></thead>
              <tbody>{logs.map(l => <tr key={l.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 align-top">
                <td className="px-3 py-2.5 text-sm text-slate-300 whitespace-nowrap">{new Date(l.created_at).toLocaleString('nl-BE')}</td>
                <td className="px-3 py-2.5">{l.direction === 'in' ? <span className="text-slate-500 mr-1" title="Inkomend">↓</span> : <span className="text-slate-500 mr-1" title="Uitgaand">↑</span>}{statusBadge(l.status)}{l.error ? <div className="text-[11px] text-red-400 mt-0.5 max-w-[16rem] truncate" title={l.error}>{l.error}</div> : null}</td>
                <td className="px-3 py-2.5 text-sm text-slate-300 font-mono break-all">{l.from_address || '-'}</td>
                <td className="px-3 py-2.5 text-sm text-slate-300 font-mono break-all">{l.to_address || '-'}{l.details?.bcc?.length > 0 && <div className="text-[11px] text-slate-500">bcc: {l.details.bcc.join(', ')}</div>}{l.details?.attachment && <div className="text-[11px] text-slate-500 font-sans inline-flex items-center gap-1" title={`${l.details.attachment.name} · ${Math.round((l.details.attachment.size || 0) / 1024)} KB`}><Paperclip size={10} />{l.details.attachment.name}</div>}{l.details?.afwijkend && <div className="text-[11px] text-amber-400 font-sans" title={l.details.standaard ? `Ingesteld: ${(l.details.standaard.to || []).join(', ')}` : 'Geen standaardontvanger ingesteld'}>afwijkend van de standaard</div>}</td>
                <td className="px-3 py-2.5 text-sm text-slate-300 max-w-[18rem] truncate" title={l.subject || ''}>{l.subject || '-'}</td>
                <td className="px-3 py-2.5 text-xs text-slate-400 font-mono whitespace-nowrap">{l.type || '-'}{l.energy_label ? <div className="text-slate-500">{l.energy_label}</div> : null}{l.template_name ? <div className="text-slate-500 font-sans" title="Gebruikt sjabloon">sjabloon: {l.template_name}</div> : null}{l.target_id && l.type === 'helpdesk_reply' ? <a href={`#helpdesk/ticket/${l.target_id}`} className="text-lime-400 hover:underline font-sans" title="Open het ticket">ticket {l.target_id}</a> : null}</td>
                <td className="px-3 py-2.5 text-sm text-slate-400">{l.user_email || <span className="text-slate-600">systeem</span>}</td>
              </tr>)}</tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
            <span className="text-sm text-slate-500">{total} mails · pagina {page} van {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => load(page - 1)} disabled={page <= 1} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-300 text-sm rounded-lg">Vorige</button>
              <button onClick={() => load(page + 1)} disabled={page >= totalPages} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-300 text-sm rounded-lg">Volgende</button>
            </div>
          </div>
        </>}
    </Card>
  </div>;
};

// ─── Portfolio Insights ──────────────────────────────────────────────────────
// Werkt op de bestaande `orders` prop (scope-gefilterd door /api/orders).
// Geen aparte autorisatie: wie hier op klikt ziet alleen orders binnen z'n
// scope; computePortfolioKPIs ziet niet meer dan dat.

// Statussen volgens het vertrekmodel (src/portfolio.js, STAND_NAAR_STATUS). De
// labels komen uit PORTFOLIO_STATUS_LABEL zodat tabel, filter en detailvenster
// hetzelfde zeggen; hier alleen de kleur erbij.
const PORTFOLIO_STATUS_LABELS = {
  active: { label: PORTFOLIO_STATUS_LABEL.active, cls: 'bg-lime-500/20 text-lime-300 border-lime-500/30' },
  will_leave: { label: PORTFOLIO_STATUS_LABEL.will_leave, cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  leaving: { label: PORTFOLIO_STATUS_LABEL.leaving, cls: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  lost: { label: PORTFOLIO_STATUS_LABEL.lost, cls: 'bg-red-500/20 text-red-300 border-red-500/30' },
  internal_out: { label: PORTFOLIO_STATUS_LABEL.internal_out, cls: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  never_started: { label: PORTFOLIO_STATUS_LABEL.never_started, cls: 'bg-slate-600/30 text-slate-400 border-slate-500/30' },
  review: { label: PORTFOLIO_STATUS_LABEL.review, cls: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
  unknown: { label: PORTFOLIO_STATUS_LABEL.unknown, cls: 'bg-slate-600/30 text-slate-400 border-slate-500/30' },
};

// Alle tegels dezelfde, zeer lichte achtergrond + neutrale rand. Enkel het
// kengetal krijgt een rustige, grafiek-passende accentkleur (geen gekleurde
// achtergronden/randen). Gedeeld door de losse tegels en de kengetal-groepen.
const PF_KPI_COLOR = {
  lime: 'text-lime-500',
  blue: 'text-indigo-500',
  red: 'text-rose-500',
  amber: 'text-amber-500',
  slate: 'text-slate-100',
};

// Kengetallen staan ALTIJD op één regel per blok — nooit een tegel die naar een
// tweede regel valt en de andere blokken met witruimte achterlaat. Daarom:
// `flex-nowrap` met tegels die mogen krimpen (`flex-1 min-w-0`) en tekst die
// afkapt i.p.v. afbreekt. Op mobiel schaalt alles mee naar heel klein en valt de
// toelichting weg; de volledige tekst blijft als tooltip beschikbaar.
const PF_KPI_ROW = 'flex flex-nowrap items-start gap-1.5 sm:gap-2.5 md:gap-3 min-w-0';
const PF_KPI_LABEL = 'text-[9px] sm:text-[10px] md:text-[11px] text-slate-400 leading-tight truncate';
const PF_KPI_HINT = 'hidden sm:block text-[9px] md:text-[10px] text-slate-500 leading-tight truncate';
const pfKpiTitle = (label, hint) => (hint ? `${label} — ${hint}` : label);

// Percentage IN de ring van een donut, niet ernaast. Recharts tekent een label
// standaard búiten de buitenste straal; bij deze grafieken (hoogte 220, straal 85)
// viel het bovenste percentage daardoor buiten het tekenvlak en werd het afgekapt.
// De band tussen binnen- en buitenstraal is 40px breed — ruim genoeg — en zo
// vervalt meteen ook de labellijn. Flintertjes (<3%) blijven zonder label: daar
// past geen tekst en het zou over de buurslice heen lopen.
const donutPctLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (!percent || percent <= 0.03) return null;
  const straal = innerRadius + (outerRadius - innerRadius) / 2;
  const hoek = -midAngle * (Math.PI / 180);
  return (
    <text x={cx + straal * Math.cos(hoek)} y={cy + straal * Math.sin(hoek)}
      fill="#ffffff" fontSize={11} fontWeight={600}
      textAnchor="middle" dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Betalingsachterstand per klantreferentie uit de facturen: de oudste vervallen,
// niet-betaalde verkoopfactuur bepaalt hoeveel dagen de klant achterloopt.
// Gedeeld door Retention (Drop Risk) en Portfolio Insights (Drops-tab).
// Koppeling via relatieReferentie = customerReference (onbetaalde facturen
// dragen geen EAN). Een deelbetaling zonder restbedrag (invOutstanding null)
// telt mee als openstaand.
function bouwAchterstandPerReferentie(facturen) {
  const m = new Map();
  for (const f of facturen || []) {
    if (String(f.soort || '').toLowerCase() !== 'verkoopfactuur') continue;
    const ref = String(f.relatieReferentie || '').trim().toLowerCase();
    if (!ref) continue;
    if (invOutstanding(f) === 0) continue;      // betaald of omgekeerd
    if (!f.vervaldatum) continue;
    const dagen = getDaysOverdue(f.vervaldatum);
    if (dagen <= 0) continue;                   // nog niet vervallen
    const cur = m.get(ref) || { dagen: 0, aantal: 0, oudste: '' };
    cur.dagen = Math.max(cur.dagen, dagen);
    cur.aantal += 1;
    if (!cur.oudste || f.vervaldatum < cur.oudste) cur.oudste = f.vervaldatum;
    m.set(ref, cur);
  }
  return m;
}
// Drempels uit de herinnerings- en dropprocedure: het aantal dagen ná de
// vervaldag van een factuur waarop de ingebrekestelling verstuurd wordt. Vanaf
// dat punt loopt de klant richting drop. Residentieel (B2C) krijgt een langere
// aanloop dan niet-residentieel (B2B en Custom); zonder segment de kortere.
const DROP_RISK_DAGEN_B2C = 20;
const DROP_RISK_DAGEN_NIET_RESIDENTIEEL = 15;
const zitInDropRisk = (r, achterstandPerRef) => {
  if (r._status !== 'active') return false;
  const a = achterstandPerRef.get(String(r.customerReference || '').trim().toLowerCase());
  if (!a) return false;
  return a.dagen >= (orderSegmentClass(r) === 'B2C' ? DROP_RISK_DAGEN_B2C : DROP_RISK_DAGEN_NIET_RESIDENTIEEL);
};

const PortfolioKpiCard = ({ label, value, hint, accent = 'lime' }) => (
  <div className="flex-1 min-w-0 border border-slate-700/50 rounded-xl px-2 py-2 sm:px-3 sm:py-3 bg-slate-800/50" title={pfKpiTitle(label, hint)}>
    <div className={PF_KPI_LABEL}>{label}</div>
    <div className={`text-sm sm:text-xl md:text-2xl font-bold leading-tight ${PF_KPI_COLOR[accent] || 'text-slate-100'}`}><Kengetal>{value}</Kengetal></div>
    {hint && <div className={`${PF_KPI_HINT} mt-0.5`}>{hint}</div>}
  </div>
);

// Compacte kengetal-groep: één kaart per thema, met de kengetallen ernaast
// i.p.v. dertien losse tegels die samen een halve schermhoogte kostten. De
// groepstitel geeft de context, zodat de labels zelf kort kunnen blijven.
const PfKpiGroup = ({ titel, subtitel, children }) => (
  <div className="border border-slate-700/50 rounded-xl bg-slate-800/50 px-2 py-2 sm:px-3.5 sm:py-3 min-w-0">
    <div className="flex items-baseline gap-1.5 mb-1 sm:mb-2 min-w-0">
      <span className="text-[9px] sm:text-[10px] md:text-[11px] font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">{titel}</span>
      {subtitel && <span className="hidden sm:block text-[10px] text-slate-500 truncate">{subtitel}</span>}
    </div>
    <div className={PF_KPI_ROW}>{children}</div>
  </div>
);

const PfKpi = ({ label, value, hint, accent = 'slate' }) => (
  <div className="flex-1 min-w-0" title={pfKpiTitle(label, hint)}>
    <div className={PF_KPI_LABEL}>{label}</div>
    <div className={`text-[13px] sm:text-base md:text-lg font-bold leading-tight ${PF_KPI_COLOR[accent] || 'text-slate-100'}`}><Kengetal>{value}</Kengetal></div>
    {hint && <div className={`${PF_KPI_HINT} mt-0.5`}>{hint}</div>}
  </div>
);

// Datum uit de orderrecords (ISO YYYY-MM-DD) → Belgische weergave DD/MM/YYYY.
// De ISO-waarde blijft de sorteersleutel, zodat chronologisch sorteren klopt.
const PF_DATE_FMT = new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
function pfFormatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : PF_DATE_FMT.format(d);
}

// Eenheden voor de grafiek "Portfolio-evolutie". De reeksen blijven dezelfde;
// alleen WAT er per aansluiting geteld wordt verschilt: het aantal, of de
// geschatte jaarvolumes uit de order (kWh → MWh).
const EVOLUTIE_EENHEDEN = [
  {
    id: 'orders',
    knop: 'Aansluitingen',
    omschrijving: 'het aantal',
    lijn: 'Totaal actief',
    asLabel: 'totaal actief',
    waarde: null,                                  // null = tellen
    as: v => formatNumberRounded(v),
    tooltip: v => formatNumberRounded(v),
  },
  {
    id: 'offtake',
    knop: 'Afname (MWh)',
    omschrijving: 'de geschatte afname in MWh',
    lijn: 'Totaal actief (MWh)',
    asLabel: 'MWh afname',
    waarde: r => (Number(r.estOfftakeKwh) || 0) / 1000,
    as: v => formatNumberRounded(v),
    tooltip: v => `${formatNumberRounded(v)} MWh`,
  },
  {
    id: 'injection',
    knop: 'Injectie (MWh)',
    omschrijving: 'de geschatte injectie in MWh',
    lijn: 'Totaal actief (MWh)',
    asLabel: 'MWh injectie',
    waarde: r => (Number(r.estInjectionKwh) || 0) / 1000,
    as: v => formatNumberRounded(v),
    tooltip: v => `${formatNumberRounded(v)} MWh`,
  },
];

// Kleurschaal van de cohortmatrix. De KLEUR draagt de betekenis, niet de
// doorzichtigheid: groen bij een hoge retentie, via oranje rond de helft naar
// rood vanaf ongeveer een kwart. Een steeds lichter groen liet het verschil
// tussen 80% en 40% nauwelijks zien.
const COHORT_KLEURSTOPS = [
  { pct: 0, rgb: [185, 28, 28] },     // donkerrood
  { pct: 25, rgb: [239, 68, 68] },    // rood
  { pct: 50, rgb: [245, 158, 11] },   // oranje
  { pct: 75, rgb: [132, 204, 22] },   // limoen
  { pct: 100, rgb: [16, 185, 129] },  // groen
];

function cohortCellStyle(pct) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  let i = COHORT_KLEURSTOPS.length - 1;
  while (i > 0 && p < COHORT_KLEURSTOPS[i].pct) i--;
  const van = COHORT_KLEURSTOPS[i];
  const tot = COHORT_KLEURSTOPS[Math.min(i + 1, COHORT_KLEURSTOPS.length - 1)];
  const t = tot.pct === van.pct ? 0 : (p - van.pct) / (tot.pct - van.pct);
  const [r, g, b] = van.rgb.map((v, k) => Math.round(v + (tot.rgb[k] - v) * t));
  return { backgroundColor: `rgba(${r}, ${g}, ${b}, 0.88)` };
}

// De vullingen zijn verzadigd genoeg voor donkere tekst, in beide thema's.
const cohortCellText = () => 'text-slate-900 font-semibold';

// Kleuren per vertreksoort — gedeeld door de tegels en de grafieklegende, zodat
// "drop" overal dezelfde kleur heeft.
const DEPARTURE_TILE_CLASS = {
  drop: 'bg-red-500/10 border-red-500/30',
  eoc: 'bg-amber-500/10 border-amber-500/30',
  loss: 'bg-indigo-500/10 border-indigo-500/30',
  ilc: 'bg-teal-500/10 border-teal-500/30',
  mystery_switch: 'bg-slate-700/40 border-slate-600/40',
  internal: 'bg-purple-500/10 border-purple-500/30',
  unknown: 'bg-slate-700/40 border-slate-600/40',
};
const DEPARTURE_TEXT_CLASS = {
  drop: 'text-red-300',
  eoc: 'text-amber-300',
  loss: 'text-indigo-300',
  ilc: 'text-teal-300',
  mystery_switch: 'text-slate-300',
  internal: 'text-purple-300',
  unknown: 'text-slate-300',
};

// Extra kolommen die de drie vertrek-tabellen (Binnenkort vertrekken,
// Vroegtijdig vertrokken, Gedropte klanten) delen. De waarden worden vooraf op
// de rij gezet (velden met _), zodat sorteren op de standaard-accessor werkt.
// Aantal rijen per pagina in de vertrek-tabellen.
const PF_VERTREK_PAGE_SIZE = 20;

// Verrijkt vertrek-rijen met de gedeelde extra kolommen (segment, geschatte
// volumes, contractduur, openstaand bedrag, multiklant). Gedeeld door Portfolio
// Insights en Drop & Loss List, zodat beide pagina's dezelfde cijfers tonen.
function useVertrekVerrijking(orders, facturen) {
  // Openstaand bedrag per contract: som van de niet-betaalde verkoopfacturen.
  // Koppeling via `relatieReferentie` op de factuur = `customerReference` op het
  // order. Bewust NIET via de GSRN: van de 952 onbetaalde verkoopfacturen draagt
  // er geen enkele een GSRN, terwijl de referentie er 941 koppelt. Het restbedrag
  // komt uit de API (zie invOutstanding); is het voor een factuur nog niet
  // opgehaald, dan onthouden we dat apart zodat de cel een '?' kan tonen.
  const openstaandPerReferentie = useMemo(() => {
    const m = new Map();
    for (const f of facturen || []) {
      if (String(f.soort || '').toLowerCase() !== 'verkoopfactuur') continue;
      const ref = String(f.relatieReferentie || '').trim().toLowerCase();
      if (!ref) continue;
      const open = invOutstanding(f);
      if (open === 0) continue;
      const cur = m.get(ref) || { bedrag: 0, onbekend: false };
      if (open == null) cur.onbekend = true; else cur.bedrag += open;
      m.set(ref, cur);
    }
    return m;
  }, [facturen]);

  const blijvendeEansVan = useMemo(() => multiConnectionLookup(orders || []), [orders]);

  return useCallback((rijen) => {
    const nu = new Date();
    return (rijen || []).map(r => {
      const markt = String(r.market || '').toUpperCase();
      const start = parseDate(r.effectiveStartDate) || parseDate(r.deliveryStartDate);
      // Exit-datum: die van het vertrekmodel (_exit). Enkel bij een record dat
      // écht vertrekt of vertrok valt hij terug op de ruwe datums — bij een
      // actieve klant met een GEANNULEERDE drop staat de oude dropdatum nog in
      // `lossDate`, en die mag zijn contractduur niet afkappen. Zonder exit maar
      // mét eerdere situatie toont de datumkolom de datum van die situatie.
      const status = r._status;
      const vertrekt = status == null || LOST_STATUSES.has(status) || status === 'will_leave' || status === 'review';
      const exit = r._exit || (vertrekt ? (parseDate(r.effectiveEndDate) || parseDate(r.lossDate)) : null);
      const historiekDatum = !exit && r._historiek && r._historiek.datum ? parseDate(r._historiek.datum) : null;
      // Loopt de aansluiting nog, dan tot vandaag; is ze al weg, dan tot de
      // exit-datum — anders zou de duur van een vertrokken klant blijven groeien.
      const eind = exit && exit < nu ? exit : nu;
      const open = openstaandPerReferentie.get(String(r.customerReference || '').trim().toLowerCase());
      return {
        ...r,
        _datum: exit ? exit.toISOString().substring(0, 10) : (historiekDatum ? historiekDatum.toISOString().substring(0, 10) : ''),
        _estOfftakeEl: markt === 'EL' ? (Number(r.estOfftakeKwh) || 0) : 0,
        _estInjectieEl: markt === 'EL' ? (Number(r.estInjectionKwh) || 0) : 0,
        _estOfftakeGas: markt === 'NG' ? (Number(r.estOfftakeKwh) || 0) : 0,
        _duurMaanden: start ? monthsBetween(start, eind) : null,
        _duurToelichting: start
          ? `${start.toLocaleDateString('nl-BE')} → ${eind.toLocaleDateString('nl-BE')}${exit && exit < nu ? ' (exit)' : ' (vandaag)'}`
          : 'Geen startdatum bekend',
        _openstaand: open ? open.bedrag : 0,
        _openstaandOnbekend: open ? open.onbekend : false,
        _blijvendeEans: blijvendeEansVan(r),
      };
    });
  }, [openstaandPerReferentie, blijvendeEansVan]);
}

// Rolt verrijkte vertrek-rijen (één per aansluiting) op tot één rij per KLANT.
// Sleutel is het klantnummer uit het bronsysteem (customerId); ontbreekt dat —
// het komt pas mee sinds de volledige orders-sync — dan de klantreferentie en
// als laatste de klantnaam.
//
// Optelregels, bewust verschillend per kolom:
//   volumes            → som over alle aansluitingen
//   contractduur       → de KORTSTE (de aansluiting die het eerst wegvalt)
//   achterstand/dagen  → de LANGSTE
//   datum              → de eerstvolgende (vroegste)
//   openstaand bedrag  → som per unieke KLANTREFERENTIE, niet per aansluiting:
//                        dat bedrag komt uit de facturen en staat al op klant-
//                        niveau, dus per aansluiting optellen zou de schuld van
//                        een klant met drie EAN's verdrievoudigen.
const klantSleutel = (r) => String(r?.customerId || r?.customerReference || r?.customer || '').trim().toLowerCase();

function aggregeerPerKlant(rijen) {
  const groepen = new Map();
  for (const r of rijen || []) {
    const k = klantSleutel(r) || `rij:${r.id}`;
    if (!groepen.has(k)) groepen.set(k, []);
    groepen.get(k).push(r);
  }
  const som = (arr, f) => arr.reduce((s, r) => s + (Number(f(r)) || 0), 0);
  return [...groepen.values()].map(rows => {
    // Eén rij per unieke klantreferentie voor alles wat al op klantniveau is.
    const perRef = new Map();
    for (const r of rows) {
      const ref = String(r.customerReference || '').trim().toLowerCase() || `rij:${r.id}`;
      if (!perRef.has(ref)) perRef.set(ref, r);
    }
    const refRijen = [...perRef.values()];
    const datums = rows.map(r => r._datum).filter(Boolean).sort();
    const duurRijen = rows.filter(r => r._duurMaanden != null);
    const kortste = duurRijen.length
      ? duurRijen.reduce((a, b) => (a._duurMaanden <= b._duurMaanden ? a : b))
      : null;
    const gsrns = [...new Set(rows.map(r => r.connectionPointGSRN).filter(Boolean))];
    const tags = [...new Set(rows.flatMap(r => r._tags || []))];
    const redenen = [...new Set(rows.map(r => r._lossReason).filter(Boolean))];
    return {
      ...rows[0],
      id: `klant:${klantSleutel(rows[0]) || rows[0].id}`,
      _aantalAansluitingen: rows.length,
      _gsrnLijst: gsrns.join(' '),
      // Alle klantnummers (referenties) van de klant, voor de kolom Klantnummer.
      // `customerReference` = het eerste NIET-LEGE nummer van de klant: daar hangen
      // de tag-vergelijkingen (klantRef), het openstaand bedrag én het rechtsklik-
      // doel voor Customer 360 aan. Was het gewoon dat van rows[0], dan droeg de
      // rij een leeg nummer zodra de oudste order er geen had — en opende Customer
      // 360 op álle klantnummers in plaats van op het getoonde.
      customerReference: (rows.find(r => String(r.customerReference || '').trim()) || rows[0]).customerReference || '',
      _klantnummers: [...new Set(rows.map(r => r.customerReference).filter(Boolean))].join(' '),
      connectionPointGSRN: gsrns.length === 1 ? gsrns[0] : `${gsrns.length} EAN's`,
      _datum: datums[0] || '',
      _estOfftakeEl: som(rows, r => r._estOfftakeEl),
      _estInjectieEl: som(rows, r => r._estInjectieEl),
      _estOfftakeGas: som(rows, r => r._estOfftakeGas),
      _duurMaanden: kortste ? kortste._duurMaanden : null,
      _duurToelichting: kortste
        ? `Kortste van ${rows.length} aansluiting${rows.length === 1 ? '' : 'en'} · ${kortste._duurToelichting}`
        : 'Geen startdatum bekend',
      _openstaand: som(refRijen, r => r._openstaand),
      _openstaandOnbekend: refRijen.some(r => r._openstaandOnbekend),
      // Blijvende EAN's van de klant: elke rij kent dezelfde verzameling (min de
      // eigen EAN, die hier sowieso vertrekt), dus de hoogste waarde is de juiste.
      _blijvendeEans: Math.max(0, ...rows.map(r => Number(r._blijvendeEans) || 0)),
      _dagenVervallen: Math.max(0, ...rows.map(r => Number(r._dagenVervallen) || 0)),
      _aantalFacturen: som(refRijen, r => r._aantalFacturen),
      _tags: tags,
      _tagLabel: tags.map(t => RETENTION_TAGS[t].label).join(' · '),
      _lossReason: redenen.join(' · '),
    };
  });
}

// Kolommen voor de CSV-export van een vertrek-tabel. Zelfde volgorde en zelfde
// waarden als op het scherm, zodat de export herkenbaar blijft.
const pfVertrekExportKolommen = (datumLabel, redenLabel = 'Reden', metTags = false, metAantal = false) => [
  { header: 'Klantnummer', get: r => r._klantnummers || r.customerReference || '' },
  { header: 'Klant', get: r => customerPrimaryName(r) },
  { header: 'E-mail', get: r => r.customerEmail || '' },
  { header: 'Telefoon', get: r => r.customerPhone || '' },
  { header: 'EAN', get: r => r._gsrnLijst || r.connectionPointGSRN || '' },
  ...(metAantal ? [{ header: 'Aantal aansluitingen', get: r => r._aantalAansluitingen || 1 }] : []),
  { header: datumLabel, get: r => r._datum || '' },
  { header: 'Verkoper', get: r => r.salesperson || '' },
  { header: redenLabel, get: r => r._lossReason || '' },
  ...(metTags ? [{ header: 'Ook in', get: r => r._tagLabel || '' }] : []),
  { header: 'Segment', get: r => (r.segment || '').toUpperCase() },
  { header: 'Est. afname EL (kWh)', get: r => r._estOfftakeEl || 0 },
  { header: 'Est. injectie EL (kWh)', get: r => r._estInjectieEl || 0 },
  { header: 'Est. afname gas (kWh)', get: r => r._estOfftakeGas || 0 },
  { header: 'Contractduur (maanden)', get: r => (r._duurMaanden == null ? '' : r._duurMaanden) },
  { header: 'Openstaand incl. BTW (EUR)', get: r => r._openstaand || 0 },
  { header: "Multi EAN klant (blijvende EAN's)", get: r => Number(r._blijvendeEans) || 0 },
];

// Gedeelde tabel voor alle vertrek-lijsten: Binnenkort vertrekken, Vroegtijdig
// vertrokken, Gedropte klanten en Actieve drops. Toont 20 rijen per pagina met
// Vorige/Volgende, en rechtsboven de gebruikelijke export van de GESORTEERDE,
// volledige lijst (niet enkel de zichtbare pagina).
const PfVertrekTabel = ({ titel, rijen, sort, datumLabel, datumKlasse = 'text-slate-300', bestandsnaam, onRowClick, onRowContext, leeg = 'Geen aansluitingen', toelichting, redenLabel = 'Reden', toonTags = false, geaggregeerd = false }) => {
  const [page, setPage] = useState(1);
  const gesorteerd = useMemo(() => sortRows(rijen, sort.sortConfig), [rijen, sort.sortConfig]);
  // Nieuwe of anders gesorteerde data → terug naar de eerste pagina.
  useEffect(() => { setPage(1); }, [gesorteerd]);
  const th = (label, k, extra) => <SortHeader label={label} k={k} sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" {...extra} />;
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-lg font-semibold text-slate-100">{titel}</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">{formatNumberRounded(rijen.length)} {geaggregeerd ? 'klanten' : 'aansluitingen'}</span>
          <ExportButton onExport={() => exportRowsToCsv(`${bestandsnaam}.csv`, pfVertrekExportKolommen(datumLabel, redenLabel, toonTags, geaggregeerd), gesorteerd)} />
        </div>
      </div>
      {toelichting && <p className="text-xs text-slate-400 mb-3">{toelichting}</p>}
      {rijen.length === 0 ? <p className="text-sm text-slate-500 italic">{leeg}</p> :
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700 text-xs text-slate-400">
            {/* Klantnummer vóór de naam: de referentie waarop facturen, notities en
                Customer 360 koppelen. Bij een rij per klant alle referenties. */}
            {th('Klantnummer', 'customerReference')}
            {th('Klant', 'customer')}
            {th('E-mail', 'customerEmail')}
            {th('Telefoon', 'customerPhone')}
            {th(geaggregeerd ? 'Aansluitingen' : 'EAN', geaggregeerd ? '_aantalAansluitingen' : 'connectionPointGSRN')}
            {th(datumLabel, '_datum')}
            {th('Verkoper', 'salesperson')}
            {th(redenLabel, '_lossReason')}
            {toonTags && th('Ook in', '_tagLabel')}
            <PfVertrekHeaders sort={sort} />
          </tr></thead>
          <tbody>{renderPaged(page, setPage, gesorteerd, r => (
            <tr key={r.id} onClick={() => onRowClick && onRowClick(r)} onContextMenu={e => onRowContext && onRowContext(e, r)}
              className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer">
              <td className="py-2 px-2 text-slate-400 font-mono text-xs whitespace-nowrap" title={r._klantnummers || r.customerReference}>{r._klantnummers || r.customerReference || '—'}</td>
              <td className="py-2 px-2 text-slate-200 truncate max-w-[180px]" title={customerPrimaryName(r)}>{customerPrimaryName(r) || '—'}</td>
              <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[180px]" title={r.customerEmail}>{r.customerEmail || '—'}</td>
              <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{r.customerPhone || '—'}</td>
              <td className="py-2 px-2 text-slate-500 font-mono text-xs" title={r._gsrnLijst || r.connectionPointGSRN}>{r.connectionPointGSRN || '—'}</td>
              {/* `pf-datum` is het haakje voor de themaregel: de pale accentkleuren
                  (amber/red/rose-300) zijn gekozen voor het donkere thema en zijn op
                  wit niet te lezen. Het lichte thema zet ze via die class om naar de
                  donkere variant — zie het brand-color-overrides-blok. */}
              <td className={`py-2 px-2 text-xs whitespace-nowrap pf-datum ${datumKlasse}`} title={r._datum}>{pfFormatDate(r._datum)}</td>
              <td className="py-2 px-2 text-slate-300 text-xs">{r.salesperson || '—'}</td>
              <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[180px]" title={r._lossReason}>{r._lossReason}</td>
              {toonTags && <td className="py-2 px-2 whitespace-nowrap">
                {(r._tags || []).length === 0 ? <span className="text-slate-600 text-xs">—</span> : (r._tags || []).map(t => (
                  <span key={t} title={RETENTION_TAGS[t].title}
                    className={`inline-flex items-center px-1.5 py-0.5 mr-1 rounded text-[10px] font-semibold border ${RETENTION_TAGS[t].cls}`}>{RETENTION_TAGS[t].label}</span>
                ))}
              </td>}
              <PfVertrekCellen r={r} />
            </tr>
          ), PF_VERTREK_PAGE_SIZE)}</tbody>
        </table>
      </div>}
    </Card>
  );
};

const PfVertrekHeaders = ({ sort }) => {
  const th = (label, k, extra) => <SortHeader label={label} k={k} sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" {...extra} />;
  return <>
    {th('Segment', 'segment')}
    {th('Est. afname EL', '_estOfftakeEl', { align: 'right' })}
    {th('Est. injectie EL', '_estInjectieEl', { align: 'right' })}
    {th('Est. afname gas', '_estOfftakeGas', { align: 'right' })}
    {th('Contractduur', '_duurMaanden', { align: 'right' })}
    {/* Zelfde grootheid en dezelfde bron als de kolom in Invoices, Aging en
        Customer 360: het restbedrag uit de facturatie-API, incl. btw. De kop
        vermeldt de btw-basis daarom overal op dezelfde manier. */}
    {th('Openstaand (incl. BTW)', '_openstaand', { align: 'right' })}
    {/* Aantal in plaats van ja/nee: "Multiklant: nog andere aansluitingen" las
        als een eigenschap van de klant, terwijl de vraag is hoeveel EAN's van
        deze klant we WEL behouden. */}
    {th('Multi EAN klant', '_blijvendeEans', { align: 'right', title: "Aantal unieke EAN's van dezelfde klant die niet vertrekken (niet in drop, einde contract of loss)" })}
  </>;
};

const PfVertrekCellen = ({ r }) => {
  const kwh = v => (v ? formatNumberRounded(v) : '—');
  return <>
    <td className="py-2 px-2 text-slate-300 text-xs">{r.segment ? r.segment.toUpperCase() : '—'}</td>
    <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{kwh(r._estOfftakeEl)}</td>
    <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{kwh(r._estInjectieEl)}</td>
    <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap">{kwh(r._estOfftakeGas)}</td>
    <td className="py-2 px-2 text-slate-300 text-xs text-right whitespace-nowrap" title={r._duurToelichting}>{r._duurMaanden == null ? '—' : `${r._duurMaanden} mnd`}</td>
    <td className={`py-2 px-2 text-xs text-right whitespace-nowrap ${r._openstaand > 0 ? 'text-amber-300 font-medium' : 'text-slate-500'}`}
      title={r._openstaandOnbekend ? 'Van minstens één factuur is het restbedrag nog niet opgehaald bij de facturatie-API' : undefined}>
      {formatCurrency(r._openstaand)}{r._openstaandOnbekend ? ' ?' : ''}
    </td>
    <td className={`py-2 px-2 text-xs text-right whitespace-nowrap tabular-nums ${r._blijvendeEans > 0 ? 'text-lime-300 font-medium' : 'text-slate-500'}`}
      title={r._blijvendeEans > 0 ? (r._blijvendeEans === 1 ? '1 andere EAN van deze klant blijft' : `${r._blijvendeEans} andere EAN's van deze klant blijven`) : "Geen andere EAN's van deze klant die blijven"}>
      {Number(r._blijvendeEans) || 0}
    </td>
  </>;
};

// Grafiek per bewegingssoort (productwissels, klantenwissels, verlengingen): het
// aantal per maand als staaf, en daarnaast hoeveel dat voorstelt tegenover de
// actieve portefeuille op dát moment. Een absoluut aantal zegt weinig als de
// portefeuille intussen verdubbeld is.
const PfBewegingChart = ({ titel, toelichting, data, kleur }) => (
  <Card>
    <h3 className="text-lg font-semibold text-slate-100 mb-1">{titel}</h3>
    {toelichting && <p className="text-xs text-slate-400 mb-3">{toelichting}</p>}
    {data.length === 0 ? <p className="text-sm text-slate-500 italic">Geen data</p> :
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
        <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 10 }} angle={-45} textAnchor="end" height={56} interval={0} />
        <YAxis yAxisId="aantal" orientation="left" tick={{ fill: '#94A3B8', fontSize: 11 }} width={44} allowDecimals={false} />
        <YAxis yAxisId="pct" orientation="right" tick={{ fill: '#94A3B8', fontSize: 11 }} width={52}
          tickFormatter={v => `${v.toFixed(1)}%`} />
        <Tooltip contentStyle={CHART_TOOLTIP}
          formatter={(v, naam) => (naam === '% van actief portfolio' ? [`${Number(v).toFixed(2)}%`, naam] : [formatNumberRounded(v), naam])} />
        <Legend />
        <Bar yAxisId="aantal" dataKey="count" name="Aantal" fill={kleur} radius={[4, 4, 0, 0]} maxBarSize={26} />
        <Line yAxisId="pct" type="monotone" dataKey="pct" name="% van actief portfolio" stroke="#94A3B8" strokeWidth={2} strokeDasharray="4 4" dot={false} />
      </ComposedChart>
    </ResponsiveContainer>}
  </Card>
);

// Sorteersleutels voor de bewegingstabellen. De ISO-datums blijven de
// sorteerwaarde (de cel toont DD/MM/YYYY), zodat chronologisch sorteren klopt.
const PF_MOVEMENT_ACCESSORS = {
  gsrn: s => s.gsrn || '',
  klantnummer: s => s.new?.customerReference || '',
  oudKlantnummer: s => s.old?.customerReference || '',
  klant: s => s.new?.customer || '',
  oudeKlant: s => s.old?.customer || '',
  nieuweKlant: s => s.new?.customer || '',
  startOud: s => s.old?.deliveryStartDate || '',
  oldProduct: s => s.old?.productGroep || '',
  startNieuw: s => s.new?.deliveryStartDate || '',
  newProduct: s => s.new?.productGroep || '',
};

// Gedeelde tabel voor alle bewegingen op een aansluiting: productwissels,
// klantenwissels en de twee correctielijsten. Toont het oude order links en het
// nieuwe rechts, met per kant de startdatum vóór het bijhorende product.
// `toonKlanten` splitst de klantkolom in "Oude klant" / "Nieuwe klant" (voor de
// lijsten waar de klant wijzigt); anders staat er één klantkolom.
const PfMovementTable = ({ rows, sort, toonKlanten = false, onRowClick, onRowContext, limiet = 30 }) => {
  const th = (label, k) => <SortHeader label={label} k={k} sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm resp-card">
        <thead><tr className="border-b border-slate-700 text-xs text-slate-400">
          {th('EAN', 'gsrn')}
          {toonKlanten ? th('Oud klantnummer', 'oudKlantnummer') : th('Klantnummer', 'klantnummer')}
          {toonKlanten ? th('Oude klant', 'oudeKlant') : th('Klant', 'klant')}
          {th('Startdatum oud', 'startOud')}
          {th('Oud product', 'oldProduct')}
          {toonKlanten && th('Nieuw klantnummer', 'klantnummer')}
          {toonKlanten && th('Nieuwe klant', 'nieuweKlant')}
          {th('Startdatum nieuw', 'startNieuw')}
          {th('Nieuw product', 'newProduct')}
        </tr></thead>
        <tbody>{sortRows(rows, sort.sortConfig, PF_MOVEMENT_ACCESSORS).slice(0, limiet).map(s => (
          <tr key={s.gsrn + s.new.id} onClick={() => onRowClick(s.new)} onContextMenu={e => onRowContext(e, s.new)} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer">
            <td className="py-2 px-2 text-slate-500 font-mono text-xs">{s.gsrn}</td>
            <td className="py-2 px-2 text-slate-400 font-mono text-xs whitespace-nowrap">{(toonKlanten ? s.old.customerReference : s.new.customerReference) || '—'}</td>
            {toonKlanten
              ? <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[140px]" title={s.old.customer}>{s.old.customer || '—'}</td>
              : <td className="py-2 px-2 text-slate-200 truncate max-w-[140px]" title={s.new.customer}>{s.new.customer || '—'}</td>}
            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{pfFormatDate(s.old.deliveryStartDate)}</td>
            <td className="py-2 px-2 text-slate-400 text-xs">{s.old.productGroep || '—'}</td>
            {toonKlanten && <td className="py-2 px-2 text-amber-300 font-mono text-xs whitespace-nowrap">{s.new.customerReference || '—'}</td>}
            {toonKlanten && <td className="py-2 px-2 text-amber-300 text-xs font-semibold truncate max-w-[140px]" title={s.new.customer}>{s.new.customer || '—'}</td>}
            <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{pfFormatDate(s.new.deliveryStartDate)}</td>
            <td className="py-2 px-2 text-blue-300 text-xs font-semibold">{s.new.productGroep || '—'}</td>
          </tr>
        ))}</tbody>
      </table>
      {rows.length > limiet && <p className="text-xs text-slate-500 mt-2 italic">Eerste {limiet} weergegeven.</p>}
    </div>
  );
};

// Tabs van Portfolio Insights. Ids identiek aan de subpagina-catalogus (subPages.js).
const PORTFOLIO_TABS = [
  { id: 'evolutie', label: 'Samenvatting' },
  { id: 'drops', label: 'Drops' },
  { id: 'churn', label: 'Churn' },
  { id: 'productwissels', label: 'Productwissels' },
  { id: 'klantenwissels', label: 'Klantenwissels' },
  { id: 'verlengingen', label: 'Contractverlengingen' },
];

const PortfolioInsightsPage = ({ orders, facturen = [], salesStructure = EMPTY_STRUCTURE, currentUser, onOpenCustomer360 }) => {
  // Rechtsklikmenu voor de tabellen met een GSRN. De rijen daarin zijn (of dragen)
  // orderrecords, dus ze bevatten dezelfde Lynx/Odoo-deeplinkvelden als de
  // orderpagina's; die zijn server-side enkel voor admin/et_dossier gevuld.
  const { onContext: onPfContext, menu: pfMenu } = useCustomer360Menu(onOpenCustomer360);
  // Rechtsklik op een bewegingsrij werkt altijd op het NIEUWE order van het paar.
  // Mét klantTarget (naam + klantnummer): Customer 360 start dan op het
  // klantnummer van déze rij en niet op alle nummers van de klant.
  const pfRowContext = (e, order) => onPfContext(e, order?.customer, klantTarget(order?.customer, order?.customerReference), undefined, orderEtLinks(order || {}));
  const pfSwSort = useTableSort();
  const pfCsSort = useTableSort();
  const pfPcSort = useTableSort();
  const pfCpcSort = useTableSort();
  const pfRnSort = useTableSort();
  // De vertrek-tabellen tonen standaard de meest recente eerst; bij de geplande
  // vertrekken is dat de eerstvolgende exit-datum. Klik op "Klant" om alfabetisch
  // te sorteren.
  const pfUpSort = useTableSort('_datum', 'asc');
  const pfElSort = useTableSort('_datum', 'desc');
  const pfFpSort = useTableSort();
  const pfDrSort = useTableSort('_datum', 'desc');
  const pfDgSort = useTableSort('_datum', 'desc');   // geannuleerde drops
  const [dropsEenheid, setDropsEenheid] = useState('aantal');   // Drops per maand: 'aantal' | 'volume' (netto kWh)
  const [tijdTotDropWeergave, setTijdTotDropWeergave] = useState('pct');   // Tijd tot drop: 'pct' | 'aantal'
  const [portfolioTab, setPortfolioTab] = useState('evolutie'); // evolutie | churn | productwissels | klantenwissels | correcties | verlengingen | drops
  // Welke van die tabs zichtbaar zijn, is per gebruiker instelbaar (subpagina's).
  const { tabs: portfolioTabs, mag: magPfTab, leeg: geenPfTabs } = useSubPages('portfolio', currentUser, PORTFOLIO_TABS);
  useEffect(() => {
    if (portfolioTabs.length > 0 && !portfolioTabs.some(t => t.id === portfolioTab)) setPortfolioTab(portfolioTabs[0].id);
  }, [portfolioTabs, portfolioTab]);
  const toonPfTab = (id) => magPfTab(id) && portfolioTab === id;
  const [durationWeergave, setDurationWeergave] = useState('pct');  // contractduur-grafiek: 'pct' | 'aantal' — elke staaf is één maand
  const [cohortWeergave, setCohortWeergave] = useState('pct');  // cohortmatrix: 'pct' | 'aantal'
  const [evolutieEenheid, setEvolutieEenheid] = useState('orders'); // Portfolio-evolutie: orders | offtake | injection
  // Contractverlengingen als eigen staaf naast Nieuw en Vertrokken tonen. Ter
  // info: een verlenging is geen aanwas (de EAN was al actief), dus de staaf hoort
  // niet ín de aanwas maar ernaast, en is uit te zetten zoals in "Sales per Maand".
  const [toonVerlengingen, setToonVerlengingen] = useState(true);
  const [cohortSelectie, setCohortSelectie] = useState(null);   // aangeklikte startmaand in de cohortmatrix
  const [periodMode, setPeriodMode] = useState('all'); // 3m | 6m | 12m | all — default "Alles"
  const [companyFilter, setCompanyFilter] = useState('all');
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [verkoperFilter, setVerkoperFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  // Default cascade-selectie op de scope van de ingelogde gebruiker (master =
  // eigen energielabel, teamleader = +team, reseller = +verkoper; admin/ET =
  // alles). Herhaalt bij user-wissel (impersonate) zodra /api/auth/me de
  // scope-velden levert.
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setCompanyFilter(d.company);
    setVerkoopteamFilter(d.team);
    setVerkoperFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  useFilterPersistence('portfolio.filters', currentUser,
    { periodMode, companyFilter, verkoopteamFilter, verkoperFilter, statusFilter, reasonFilter },
    (s) => { setPeriodMode(s.periodMode ?? 'all'); setCompanyFilter(s.companyFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); setStatusFilter(s.statusFilter ?? 'all'); setReasonFilter(s.reasonFilter ?? 'all'); });
  const [selectedOrder, setSelectedOrder] = useState(null);

  const now = useMemo(() => new Date(), []);
  const period = useMemo(() => {
    if (periodMode === 'all') return {};
    const months = { '3m': 3, '6m': 6, '12m': 12 }[periodMode] || 12;
    const from = new Date(now.getFullYear(), now.getMonth() - months, 1);
    return { from, to: now };
  }, [periodMode, now]);

  // Cascade-filter dropdowns uit de centrale sales-structuur (identiek in alle
  // modules, rol-gescopet door de backend).
  const { companies, teams: verkoopteams, sellers: verkopers } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter),
    [salesStructure, companyFilter, verkoopteamFilter]
  );

  const handleCompanyChange = (v) => { setCompanyFilter(v); setVerkoopteamFilter('all'); setVerkoperFilter('all'); };
  const handleVerkoopteamChange = (v) => { setVerkoopteamFilter(v); setVerkoperFilter('all'); };
  const clearScopeFilters = () => { setCompanyFilter('all'); setVerkoopteamFilter('all'); setVerkoperFilter('all'); };
  const hasScopeFilters = companyFilter !== 'all' || verkoopteamFilter !== 'all' || verkoperFilter !== 'all';

  // Pre-filter orders volgens scope-keuze; vervolgens KPI's berekenen op die subset
  const scopedOrders = useMemo(() => {
    let data = orders || [];
    if (companyFilter !== 'all') data = data.filter(o => o.company === companyFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(o => o.salespersonTeam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(o => o.salesperson === verkoperFilter);
    return data;
  }, [orders, companyFilter, verkoopteamFilter, verkoperFilter]);

  const result = useMemo(() => computePortfolioKPIs(scopedOrders, period, now), [scopedOrders, period, now]);

  // Verdeling per segment en meter method — op deduped portfolio, met
  // breakdown van active/lost/will_leave per categorie.
  const breakdownBy = (field, normalize) => {
    const counts = new Map();
    for (const r of result.portfolio) {
      const raw = (r[field] || '').toString().trim();
      const key = normalize ? normalize(raw) : (raw || 'Onbekend');
      const cur = counts.get(key) || { name: key, total: 0, active: 0, lost: 0, will_leave: 0, early: 0 };
      cur.total++;
      // Vertrokken = lost + in afhandeling + interne overstap (LOST_STATUSES);
      // nooit gestart / te controleren / onbekend tellen in het totaal maar in
      // geen van de drie kolommen.
      const bucket = LOST_STATUSES.has(r._status) ? 'lost' : r._status;
      if (bucket === 'active' || bucket === 'lost' || bucket === 'will_leave') cur[bucket]++;
      if (r._isEarly) cur.early++;
      counts.set(key, cur);
    }
    return [...counts.values()].sort((a, b) => b.total - a.total);
  };
  const normalizeMeter = (m) => {
    const up = m.toUpperCase();
    if (up.includes('YMR')) return 'YMR';
    if (up.includes('SMR1') || up === 'SMR 1') return 'SMR1';
    if (up.includes('SMR3') || up === 'SMR 3') return 'SMR3';
    if (up.includes('MMR')) return 'MMR';
    if (up.includes('AMR')) return 'AMR';
    return m || 'Onbekend';
  };
  const normalizeSegment = (s) => {
    const up = s.toUpperCase();
    if (up.includes('B2C')) return 'B2C';
    if (up.includes('B2B')) return 'B2B';
    if (up.includes('CUSTOM')) return 'Custom';
    return s || 'Onbekend';
  };
  const segmentBreakdown = useMemo(() => breakdownBy('segment', normalizeSegment), [result.portfolio]); // eslint-disable-line react-hooks/exhaustive-deps
  const meterBreakdown = useMemo(() => breakdownBy('meterMethod', normalizeMeter), [result.portfolio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Productwissels en contractverlengingen — werkt op alle scoped orders
  // (niet de deduped portfolio, omdat we juist MEERDERE orders per GSRN nodig hebben)
  const { switches, customerSwitches, productCorrections, customerProductCorrections, renewals } = useMemo(() => detectProductSwitchesAndRenewals(scopedOrders), [scopedOrders]);
  const switchesByMonth = useMemo(() => aggregateByMonth(switches), [switches]);
  const customerSwitchesByMonth = useMemo(() => aggregateByMonth(customerSwitches), [customerSwitches]);

  // Bewegingen per maand, afgezet tegen de actieve portefeuille van diezelfde
  // maand (active_end uit de evolutiereeks). Zonder die noemer lijkt elke maand
  // met veel wissels erg, ook als de portefeuille intussen verdubbelde.
  const bewegingPerMaand = useCallback((events) => {
    const actiefPerMaand = new Map(result.monthlySeries.map(m => [m.month, m.active_end]));
    return aggregateByMonth(events).map(e => {
      const actief = actiefPerMaand.get(e.month) || 0;
      return { month: e.month, count: e.count, pct: actief > 0 ? (e.count / actief) * 100 : 0 };
    });
  }, [result.monthlySeries]);

  // Contractduur van vertrokken klanten. Werkt bewust op de RUWE orders (niet op
  // het gededupliceerde portfolio): een keten van verlengingen/wissels bestaat
  // juist uit meerdere orders op dezelfde GSRN.
  const durationRows = useMemo(() => closedContractDurations(scopedOrders), [scopedOrders]);
  const cohortMatrix = useMemo(() => cohortRetentionMatrix(scopedOrders, undefined, { minMaandPerLabel: COHORT_MIN_MAAND_PER_LABEL }), [scopedOrders]);
  // Welke afkappingen zijn in de huidige selectie zichtbaar? Alleen die labels
  // krijgen een voetnoot onder de matrix — anders zou er bij elk ander label een
  // regel staan over data die er sowieso niet is.
  const cohortAfkappingen = useMemo(
    () => Object.entries(COHORT_MIN_MAAND_PER_LABEL)
      .filter(([label]) => scopedOrders.some(o => o.company === label))
      .map(([label, vanaf]) => ({ label, vanaf })),
    [scopedOrders]
  );

  const productwisselsPerMaand = useMemo(() => bewegingPerMaand(switches), [switches, bewegingPerMaand]);
  const klantenwisselsPerMaand = useMemo(() => bewegingPerMaand(customerSwitches), [customerSwitches, bewegingPerMaand]);
  const verlengingenPerMaand = useMemo(() => bewegingPerMaand(renewals), [renewals, bewegingPerMaand]);

  // Maandreeks voor Portfolio-evolutie in de gekozen eenheid. Bij "Aansluitingen"
  // hergebruiken we de reeks die computePortfolioKPIs al berekende; voor de
  // MWh-varianten sommeren we dezelfde bewegingen over de geschatte volumes.
  const evolutieEenheidDef = EVOLUTIE_EENHEDEN.find(e => e.id === evolutieEenheid) || EVOLUTIE_EENHEDEN[0];
  const evolutieSeries = useMemo(
    // `result.verlengingIds` meegeven: ook de MWh-varianten splitsen de aanwas in
    // nieuw en verlenging, anders zou de grafiek per eenheid iets anders tonen.
    () => (evolutieEenheidDef.waarde
      ? buildMonthlySeries(result.portfolio, evolutieEenheidDef.waarde, result.verlengingIds)
      : result.monthlySeries),
    [evolutieEenheidDef, result.portfolio, result.monthlySeries, result.verlengingIds]
  );

  // Verrijking voor de vertrek-tabellen (gedeeld met Drop & Loss List).
  const verrijkVertrek = useVertrekVerrijking(scopedOrders, facturen);

  const upcomingVerrijkt = useMemo(() => verrijkVertrek(result.upcoming), [result.upcoming, verrijkVertrek]);
  const earlyLeaversVerrijkt = useMemo(() => verrijkVertrek(result.earlyLeavers), [result.earlyLeavers, verrijkVertrek]);
  // droppedVerrijkt staat verderop, ná de declaratie van droppedRecords — een
  // const vóór zijn declaratie gebruiken geeft een TDZ-crash bij het renderen.
  // Eén staaf per maand, met naast het aantal ook het aandeel in alle afgesloten contracten.
  const durationBins = useMemo(() => { const bins = binDurations(durationRows, 1); const totaal = durationRows.length || 1; return bins.map(b => ({ ...b, pct: (b.count / totaal) * 100 })); }, [durationRows]);
  const durationStats = useMemo(() => {
    if (durationRows.length === 0) return { mediaan: 0, gemiddeld: 0, max: 0 };
    const m = durationRows.map(d => d.months).sort((a, b) => a - b);
    return {
      mediaan: m[Math.floor(m.length / 2)],
      gemiddeld: (m.reduce((s, v) => s + v, 0) / m.length).toFixed(1),
      max: m[m.length - 1],
    };
  }, [durationRows]);
  const renewalsByMonth = useMemo(() => aggregateByMonth(renewals), [renewals]);
  // Gecombineerd voor stacked bar
  const churnMovementMonthly = useMemo(() => {
    const merged = {};
    const bucket = (m) => (merged[m] = merged[m] || { month: m, switches: 0, customerSwitches: 0, renewals: 0 });
    for (const s of switchesByMonth) bucket(s.month).switches = s.count;
    for (const c of customerSwitchesByMonth) bucket(c.month).customerSwitches = c.count;
    for (const r of renewalsByMonth) bucket(r.month).renewals = r.count;
    return Object.values(merged).sort((a, b) => a.month.localeCompare(b.month));
  }, [switchesByMonth, customerSwitchesByMonth, renewalsByMonth]);

  const k = result.kpis;
  const formatPct = v => `${v.toFixed(1)}%`;
  const formatMonths = m => m >= 12 ? `${(m / 12).toFixed(1)} jaar` : `${Math.round(m)} mnd`;
  // Aandeel van een vertreksoort in álle vertrokken aansluitingen.
  const aandeelVertrokken = n => (k.lostCount > 0 ? formatPct((n / k.lostCount) * 100) : '0%');

  // Filtered portfolio voor de detail-tabellen
  const filteredPortfolio = useMemo(() => {
    let p = result.portfolio;
    if (statusFilter !== 'all') p = p.filter(r => r._status === statusFilter);
    if (reasonFilter !== 'all') p = p.filter(r => r._lossReason === reasonFilter);
    return p;
  }, [result.portfolio, statusFilter, reasonFilter]);

  if (!orders || orders.length === 0) {
    return <div className="space-y-6">
      <PageHeader title="Portfolio Insights" subtitle="Evolutie en gezondheid van het klantenportfolio" />
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-12 text-center">
        <Activity className="mx-auto text-slate-600 mb-3" size={48} />
        <p className="text-slate-400">Geen orderdata beschikbaar voor je scope.</p>
      </div>
    </div>;
  }

  // Genoeg kleuren voor alle vertrekscenario's (er zijn er 9), zodat geen twee
  // segmenten in de taartgrafiek dezelfde kleur krijgen.
  const reasonColors = ['#ef4444', '#f59e0b', '#3b82f6', '#a855f7', '#22c55e', '#6b7280', '#ec4899', '#14b8a6', '#6366f1', '#f97316'];

  // Gedropte klanten = reeds vertrokken aansluitingen met een Stop Contract van het
  // type ResidentialDrop of NonResidentialDrop. Uitsluitend dát is een drop; een
  // Stop Contract met EOC, een Loss of een verhuis (ILC) hoort hier NIET.
  // "Gedropt" = de CP-status is ext; een drop waarvan de datum voorbij is maar
  // de markt nog niet bevestigde staat apart als "in afhandeling", en een drop
  // die werd afgeblazen (klant betaalde alsnog) als "geannuleerd" — die klant is
  // gebleven en hoort in geen enkele drop-telling.
  const droppedRecords = result.portfolio.filter(r => r._status === 'lost' && r._departureKind === 'drop');
  const dropsInAfhandeling = result.leavingRecords.filter(r => r._departureKind === 'drop');
  const dropsGeannuleerd = result.cancelledRecords.filter(r => r._historiek?.soort === 'drop');
  const dropsResidentieel = droppedRecords.filter(r => r._departureSegment === 'residential').length;
  const dropsNietResidentieel = droppedRecords.filter(r => r._departureSegment === 'non_residential').length;
  const droppedVerrijkt = verrijkVertrek(droppedRecords);
  const dropsGeannuleerdVerrijkt = verrijkVertrek(dropsGeannuleerd);
  // Netto jaarvolume van een aansluiting: geschatte afname min geschatte injectie
  // (kWh). Negatief kan (netto-injecteerder); dat telt dan ook negatief mee.
  const nettoKwh = (r) => (Number(r.estOfftakeKwh) || 0) - (Number(r.estInjectionKwh) || 0);
  const isResidentieleDrop = (r) => r._departureSegment === 'residential';
  // Drops per maand, gesplitst residentieel / niet-residentieel, in aantal én in
  // netto kWh — de knop bij de grafiek kiest de eenheid.
  const dropsByMonth = (() => {
    const byMonth = new Map();
    for (const r of droppedRecords) {
      const mk = r._exit ? r._exit.toISOString().substring(0, 7) : null;
      if (!mk) continue;
      const rij = byMonth.get(mk) || { month: mk, res: 0, nonres: 0, resKwh: 0, nonresKwh: 0 };
      if (isResidentieleDrop(r)) { rij.res += 1; rij.resKwh += nettoKwh(r); } else { rij.nonres += 1; rij.nonresKwh += nettoKwh(r); }
      byMonth.set(mk, rij);
    }
    return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  })();
  // Drops per verkoopteam: welk kanaal brengt de wanbetalers binnen? Aantal,
  // netto volume en het openstaande bedrag (per unieke klantreferentie, zoals
  // overal — het bedrag staat op klantniveau, niet per aansluiting).
  const dropsPerTeam = (() => {
    const m = new Map();
    for (const r of droppedVerrijkt) {
      const team = r.salespersonTeam || '—';
      const t = m.get(team) || { team, aantal: 0, kwh: 0, refs: new Map() };
      t.aantal += 1; t.kwh += nettoKwh(r);
      const ref = String(r.customerReference || '').trim().toLowerCase() || `rij:${r.id}`;
      if (!t.refs.has(ref)) t.refs.set(ref, r._openstaand || 0);
      m.set(team, t);
    }
    const totaal = droppedVerrijkt.length || 1;
    return [...m.values()]
      .map(t => ({ team: t.team, aantal: t.aantal, pct: (t.aantal / totaal) * 100, kwh: t.kwh, openstaand: [...t.refs.values()].reduce((s, v) => s + v, 0) }))
      .sort((a, b) => b.aantal - a.aantal);
  })();
  // Kengetallen over de gedropte klanten: openstaand (per unieke klant) en de
  // gemiddelde tijd dat ze klant waren vóór de drop.
  const dropsOpenstaand = (() => { const m = new Map(); for (const r of droppedVerrijkt) { const ref = String(r.customerReference || '').trim().toLowerCase() || `rij:${r.id}`; if (!m.has(ref)) m.set(ref, r._openstaand || 0); } return [...m.values()].reduce((s, v) => s + v, 0); })();
  const dropsGemDuur = (() => { const d = droppedVerrijkt.map(r => r._duurMaanden).filter(v => v != null); return d.length ? d.reduce((s, v) => s + v, 0) / d.length : null; })();
  // Drops per verkoper: het aandeel gedropte klanten tegenover de actieve
  // klanten van dezelfde verkoper. Wie te makkelijk verkoopt aan slechte
  // betalers, valt hier op. Drop-% = drops / (drops + actief).
  const dropsPerVerkoper = (() => {
    const m = new Map();
    const sleutel = (r) => `${r.salesperson || '—'}|${r.salespersonTeam || '—'}`;
    for (const r of result.portfolio) {
      if (r._status !== 'active') continue;
      const k = sleutel(r); const v = m.get(k) || { verkoper: r.salesperson || '—', team: r.salespersonTeam || '—', drops: 0, actief: 0, refs: new Map() };
      v.actief += 1; m.set(k, v);
    }
    for (const r of droppedVerrijkt) {
      const k = sleutel(r); const v = m.get(k) || { verkoper: r.salesperson || '—', team: r.salespersonTeam || '—', drops: 0, actief: 0, refs: new Map() };
      v.drops += 1;
      const ref = String(r.customerReference || '').trim().toLowerCase() || `rij:${r.id}`;
      if (!v.refs.has(ref)) v.refs.set(ref, r._openstaand || 0);
      m.set(k, v);
    }
    return [...m.values()].filter(v => v.drops > 0)
      .map(v => ({ ...v, pct: (v.drops / (v.drops + v.actief)) * 100, openstaand: [...v.refs.values()].reduce((s, x) => s + x, 0) }))
      .sort((a, b) => b.drops - a.drops || b.pct - a.pct);
  })();
  // Tijd tot drop: hoeveel maanden was de klant actief vóór de drop? Een drop in
  // de eerste maanden wijst op de acceptatie bij de verkoop, een late drop op een
  // klant die onderweg in de problemen kwam.
  const TIJD_TOT_DROP_BUCKETS = [
    { naam: '≤ 3 mnd', max: 3 }, { naam: '4–6 mnd', max: 6 }, { naam: '7–12 mnd', max: 12 }, { naam: '13–24 mnd', max: 24 }, { naam: '> 24 mnd', max: Infinity },
  ];
  const tijdTotDrop = (() => {
    const rows = TIJD_TOT_DROP_BUCKETS.map(b => ({ naam: b.naam, res: 0, nonres: 0, count: 0 }));
    let onbekend = 0;
    for (const r of droppedVerrijkt) {
      const m = r._duurMaanden;
      if (m == null) { onbekend += 1; continue; }
      const i = TIJD_TOT_DROP_BUCKETS.findIndex(b => m <= b.max);
      const rij = rows[i < 0 ? rows.length - 1 : i];
      rij.count += 1; if (isResidentieleDrop(r)) rij.res += 1; else rij.nonres += 1;
    }
    const totaal = rows.reduce((s, x) => s + x.count, 0) || 1;
    return { rijen: rows.map(x => ({ ...x, pct: (x.count / totaal) * 100, resPct: (x.res / totaal) * 100, nonresPct: (x.nonres / totaal) * 100 })), onbekend };
  })();
  // Van Drop Risk naar drop: de trechter van vandaag. Drop Risk telt de klanten
  // die NU voorbij de ingebrekestelling zitten (zelfde regel als Retention),
  // Drop List de drops die nu lopen, en daarnaast de uitkomst van alle
  // aangekondigde drops tot nu toe: uitgevoerd of afgeblazen. Het aandeel
  // geannuleerd is de succesratio van de herinneringsprocedure en het nabellen.
  const achterstandPerRef = bouwAchterstandPerReferentie(facturen);
  const dropRiskNu = (() => { const s = new Set(); for (const r of result.portfolio) if (zitInDropRisk(r, achterstandPerRef)) s.add(klantSleutel(r) || r.id); return s.size; })();
  const dropListNu = (() => { const s = new Set(); for (const r of result.portfolio) if (r._departureKind === 'drop' && (r._status === 'will_leave' || r._status === 'leaving' || r._status === 'active')) s.add(klantSleutel(r) || r.id); return s.size; })();
  const dropAfgeblazenPct = (dropsGeannuleerd.length + droppedRecords.length) > 0 ? (dropsGeannuleerd.length / (dropsGeannuleerd.length + droppedRecords.length)) * 100 : null;

  // Gedeelde grafiek "Portfolio-evolutie": per maand de gestarte tegenover de
  // vertrokken aansluitingen (staven, uitgesplitst naar vertreksoort) én de
  // lopende portefeuille als lijn. Enkel op Samenvatting (sinds 11-09-2026 niet meer op Churn), over
  // de volle breedte zodat de staven per maand leesbaar blijven.
  //
  // Twee y-assen: links de maandbewegingen (tientallen), rechts het totaal
  // actieve aansluitingen (duizenden). Op één as zou de lijn de staven volledig
  // platdrukken.
  const nieuwVsVertrokkenChart = (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-lg font-semibold text-slate-100">Portfolio-evolutie</h3>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
          {/* Vinkje vóór de toggle: het bepaalt WAT er in de grafiek staat, de
              toggle enkel in welke eenheid. Zelfde opzet als "Sales per Maand". */}
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none whitespace-nowrap"
            title="Toont contractverlengingen als aparte staaf naast Nieuw en Vertrokken. Ter info: een verlenging is geen aanwas, de EAN was al actief en telt in de lijn al mee sinds haar eerste contract.">
            <input type="checkbox" checked={toonVerlengingen} onChange={e => setToonVerlengingen(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-700 accent-lime-500 cursor-pointer" />
            incl. contractverlengingen
          </label>
          <Toggle options={EVOLUTIE_EENHEDEN.map(e => ({ value: e.id, label: e.knop }))}
            value={evolutieEenheid} onChange={setEvolutieEenheid} />
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-3">
        Staven: {evolutieEenheidDef.omschrijving} van de aansluitingen die in die maand startten of vertrokken (as links),
        vertrek per vertreksoort.{toonVerlengingen ? ' Contractverlengingen staan er als aparte staaf naast, ter info: een verlenging start een contract maar brengt geen aansluiting bij — die EAN telt al mee sinds haar eerste contract.' : ''}
        {' '}Lijn: de lopende stand van geleverde aansluitingen op het einde van de maand (as rechts).
      </p>
      {evolutieSeries.length === 0 ? <p className="text-sm text-slate-500 italic">Geen data</p> :
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={evolutieSeries} margin={{ top: 8, right: 16, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 11 }} angle={-45} textAnchor="end" height={60} interval={0} />
          <YAxis yAxisId="beweging" orientation="left" tick={{ fill: '#94A3B8', fontSize: 11 }} width={56}
            allowDecimals={evolutieEenheid !== 'orders'} tickFormatter={evolutieEenheidDef.as} />
          <YAxis yAxisId="actief" orientation="right" tick={{ fill: 'var(--chart-neutral, #0f172a)', fontSize: 11 }} width={70}
            tickFormatter={evolutieEenheidDef.as}
            label={{ value: evolutieEenheidDef.asLabel, angle: 90, position: 'insideRight', fill: 'var(--chart-neutral, #0f172a)', fontSize: 11 }} />
          <Tooltip contentStyle={CHART_TOOLTIP} formatter={v => evolutieEenheidDef.tooltip(v)} />
          <Legend />
          {/* Kleuren ver uit elkaar in tint — groen 160° (nieuw), blauw 217°
              (loss), rood 0° (drop), amber 38° (EOC), violet 271° (verhuis) en
              een neutraal grijs (rest). De vorige teal voor Verhuis lag te dicht
              bij het groen van Nieuw. Elke tint haalt minstens 3:1 contrast tegen
              zowel wit als de donkere kaartachtergrond, dus de staven blijven in
              beide thema's van elkaar te onderscheiden. */}
          {/* Verlengingen staan als EIGEN staaf naast Nieuw en Vertrokken (niet
              erbovenop): het is informatie, geen aanwas. Het vinkje in de kop haalt
              de staaf weg.
              ARCERING in plaats van een zevende kleur. Het palet van deze grafiek
              is vol: geen enkele extra tint haalde de leesbaarheidsdrempel (ΔE 15)
              tegen álle zes bestaande reeksen én bleef in beide thema's binnen de
              helderheidsband — gemeten met de dataviz-validator. Fuchsia leek te
              werken tot het op het scherm stond: naast het violet van "Verhuis
              (ILC)" was het niet te onderscheiden (ΔE 9,1). Verlenging is bovendien
              geen eigen categorie maar een ONDERDEEL van de aanwas, dus dezelfde
              groentint met een schuine arcering klopt ook inhoudelijk — en dat
              onderscheid overleeft grijstinten, druk en elke vorm van
              kleurenblindheid. */}
          <defs>
            <pattern id="pf-verlenging" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#A7F3D0" />
              <rect width="3" height="6" fill="#059669" />
            </pattern>
            {/* Interne overstap: zelfde arcering-truc als de verlengingen, in het
                neutrale grijs van "overig" — het is voor het label een vertrek,
                maar geen klant die de groep verlaat. Alleen getekend als de
                selectie er een heeft (zeldzaam), anders vervuilt hij de legende. */}
            <pattern id="pf-intern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#E4E4E7" />
              <rect width="3" height="6" fill="#71717A" />
            </pattern>
          </defs>
          <Bar yAxisId="beweging" dataKey="new_nieuw" name="Nieuw" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={26} />
          {toonVerlengingen && <Bar yAxisId="beweging" dataKey="new_verlenging" name="Contractverlenging" fill="url(#pf-verlenging)" stroke="#059669" strokeWidth={1} radius={[4, 4, 0, 0]} maxBarSize={26} />}
          <Bar yAxisId="beweging" dataKey="lost_loss" name="Loss — andere leverancier" fill="#3B82F6" stackId="lost" maxBarSize={26} />
          <Bar yAxisId="beweging" dataKey="lost_drop" name="Drop" fill="#DC2626" stackId="lost" maxBarSize={26} />
          <Bar yAxisId="beweging" dataKey="lost_eoc" name="Einde contract (EOC)" fill="#D97706" stackId="lost" maxBarSize={26} />
          <Bar yAxisId="beweging" dataKey="lost_ilc" name="Verhuis (ILC)" fill="#A855F7" stackId="lost" maxBarSize={26} />
          {evolutieSeries.some(m => m.lost_internal > 0) && <Bar yAxisId="beweging" dataKey="lost_internal" name="Interne overstap" fill="url(#pf-intern)" stroke="#71717A" strokeWidth={1} stackId="lost" maxBarSize={26} />}
          <Bar yAxisId="beweging" dataKey="lost_other" name="Mystery switch / onbekend" fill="#71717A" stackId="lost" radius={[4, 4, 0, 0]} maxBarSize={26} />
          {/* De cumulatieve lijn loopt over alle staven heen en krijgt daarom een
              neutrale, thema-afhankelijke kleur in plaats van een zesde tint. */}
          <Line yAxisId="actief" type="monotone" dataKey="active_end" name={evolutieEenheidDef.lijn} stroke="var(--chart-neutral, #0f172a)" strokeWidth={2.5} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>}
    </Card>
  );

  // Bewegingen op de aansluitingen (kengetallen + maandgrafiek). Staat op de tab
  // Portfolio-evolutie, in de rechterkolom naast "Contractduur van vertrokken
  // klanten". Die twee kaarten horen even hoog te zijn, dus alles boven de
  // grafiek blijft hier zo laag mogelijk: drie kengetallen op één regel, elk met
  // het getal en het percentage naast elkaar i.p.v. onder elkaar.
  //
  // Bewust GEEN "Correcties" en "Totaal bewegingen" meer: correcties zijn
  // rechtzettingen en geen portfoliobeweging (ze staan al in hun eigen tabs), en
  // een totaal van drie zichtbare getallen voegt niets toe. De drie reeksen in de
  // grafiek eronder zijn precies deze drie kengetallen.
  // Zelfde kleuren als de reeksen in de grafiek eronder — de tegel is het
  // kengetal van die balk, dus hij hoort dezelfde kleur te dragen.
  const wisselKpis = [
    { label: 'Productwissels', value: switches.length, kleur: COLORS_BEWEGING.productwissel },
    { label: 'Klantenwissels', value: customerSwitches.length, kleur: COLORS_BEWEGING.klantenwissel },
    { label: 'Contractverlengingen', value: renewals.length, kleur: COLORS_BEWEGING.verlenging },
  ];
  const bewegingenOverview = (
    <Card>
      <h3 className="text-lg font-semibold text-slate-100">Wissels &amp; verlengingen</h3>
      <p className="text-xs text-slate-400 mt-0.5 mb-3">Productwissels, klantenwissels en contractverlengingen — gedetecteerd op actieve EAN's met meerdere verkooporders</p>
      {/* Drie naast elkaar vanaf sm; daaronder onder elkaar met het label links en
          het getal rechts. Gemeten op 385px: naast elkaar is elke tegel 92px breed
          en heeft "Contractverlengingen" er 95 nodig — dan kapt het label af tot
          onleesbaar. Op één regel per tegel past het volledig én blijft het kort. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {wisselKpis.map(k => {
          const pct = result.kpis.activeCount > 0 ? `${((k.value / result.kpis.activeCount) * 100).toFixed(1)}%` : '—';
          return (
            <div key={k.label} className="border border-slate-700/50 rounded-lg bg-slate-800/50 px-2.5 py-1.5 min-w-0 flex items-baseline justify-between gap-2 sm:block"
              title={`${k.label} — ${pct} van het actieve portfolio`}>
              <div className="text-[10px] text-slate-400 leading-tight truncate">{k.label}</div>
              <div className="flex items-baseline gap-1.5 min-w-0 shrink-0">
                <span className="min-w-0 text-lg font-bold leading-tight" style={{ color: k.kleur }}><Kengetal>{k.value}</Kengetal></span>
                <span className="text-[10px] text-slate-500">{pct}</span>
              </div>
            </div>
          );
        })}
      </div>
      {churnMovementMonthly.length === 0 ? <p className="text-sm text-slate-500 italic">Geen wissels of verlengingen gedetecteerd in de huidige scope.</p> :
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={churnMovementMonthly}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 10 }} angle={-45} textAnchor="end" height={50} />
          <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={false} />
          <Tooltip contentStyle={CHART_TOOLTIP} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {/* Klantenwissels vóór productwissels: magenta naast groen haalt de
              kleurenblindheid-drempel, geel naast groen niet. Zie het palet. */}
          <Bar dataKey="customerSwitches" name="Klantenwissels" fill={COLORS_BEWEGING.klantenwissel} stackId="a" />
          <Bar dataKey="switches" name="Productwissels" fill={COLORS_BEWEGING.productwissel} stackId="a" />
          <Bar dataKey="renewals" name="Contractverlengingen" fill={COLORS_BEWEGING.verlenging} stackId="a" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>}
    </Card>
  );

  // Samenstelling van het portfolio: waaruit het bestaat (segment) en waarmee
  // het gemeten wordt (meter method). Als variabele, zodat de tab-JSX ze bovenaan
  // kan renderen terwijl de rest van die tab verderop in het bestand staat.
  // Taart + tabel staan op mobiel ONDER elkaar: een tabel van 5 kolommen naast
  // een taart van 50% breedte hield op een telefoon ~140px over en rekte de
  // pagina op. Vanaf sm weer naast elkaar (desktop ongewijzigd).
  // GEEN items-start: de twee kaarten moeten even hoog zijn (samen beginnen én
  // samen eindigen), ook als de ene tabel meer rijen heeft dan de andere. Grid
  // rekt ze dan vanzelf tot de hoogte van de rij.
  const verdelingCards = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <h3 className="text-lg font-semibold text-slate-100 mb-3">Verdeling per Segment</h3>
        {segmentBreakdown.length === 0 ? <p className="text-sm text-slate-500 italic">Geen data</p> :
        <div className="flex flex-col sm:flex-row items-center gap-4 min-w-0">
          <div className="w-full sm:w-1/2 min-w-0"><ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={segmentBreakdown} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85}
                labelLine={false} label={donutPctLabel}>
                {segmentBreakdown.map((d, i) => <Cell key={i} fill={{ B2C: '#3B82F6', B2B: '#22C55E', Custom: '#A855F7' }[d.name] || '#6B7280'} />)}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP} />
            </PieChart>
          </ResponsiveContainer></div>
          <div className="w-full sm:flex-1 min-w-0 overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="border-b border-slate-700 text-slate-400">
              <th className="text-left py-1">Segment</th>
              <th className="text-right py-1">Totaal</th>
              <th className="text-right py-1">Actief</th>
              <th className="text-right py-1">Verloren</th>
              <th className="text-right py-1">Churn %</th>
            </tr></thead>
            <tbody>{segmentBreakdown.map(d => {
              const denom = d.active + d.lost;
              const churnPct = denom > 0 ? (d.lost / denom) * 100 : 0;
              return <tr key={d.name} className="border-b border-slate-700/30">
                <td className="py-1.5 text-slate-200">{d.name}</td>
                <td className="py-1.5 text-right text-slate-200 font-semibold">{d.total}</td>
                <td className="py-1.5 text-right text-lime-300">{d.active}</td>
                <td className="py-1.5 text-right text-red-300">{d.lost}</td>
                <td className="py-1.5 text-right text-amber-300">{churnPct.toFixed(1)}%</td>
              </tr>;
            })}</tbody>
          </table></div>
        </div>}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-slate-100 mb-3">Verdeling per Meter Method</h3>
        {meterBreakdown.length === 0 ? <p className="text-sm text-slate-500 italic">Geen data</p> :
        <div className="flex flex-col sm:flex-row items-center gap-4 min-w-0">
          <div className="w-full sm:w-1/2 min-w-0"><ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={meterBreakdown} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85}
                labelLine={false} label={donutPctLabel}>
                {meterBreakdown.map((d, i) => <Cell key={i} fill={COLORS_METER[d.name] || '#6B7280'} />)}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP} />
            </PieChart>
          </ResponsiveContainer></div>
          <div className="w-full sm:flex-1 min-w-0 overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="border-b border-slate-700 text-slate-400">
              <th className="text-left py-1">Meter</th>
              <th className="text-right py-1">Totaal</th>
              <th className="text-right py-1">Actief</th>
              <th className="text-right py-1">Verloren</th>
              <th className="text-right py-1">Churn %</th>
            </tr></thead>
            <tbody>{meterBreakdown.map(d => {
              const denom = d.active + d.lost;
              const churnPct = denom > 0 ? (d.lost / denom) * 100 : 0;
              return <tr key={d.name} className="border-b border-slate-700/30">
                <td className="py-1.5 text-slate-200">{d.name}</td>
                <td className="py-1.5 text-right text-slate-200 font-semibold">{d.total}</td>
                <td className="py-1.5 text-right text-lime-300">{d.active}</td>
                <td className="py-1.5 text-right text-red-300">{d.lost}</td>
                <td className="py-1.5 text-right text-amber-300">{churnPct.toFixed(1)}%</td>
              </tr>;
            })}</tbody>
          </table></div>
        </div>}
      </Card>
    </div>
  );

  return <div className="space-y-6">
    <PageHeader title="Portfolio Insights" subtitle="Evolutie en gezondheid van het klantenportfolio">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
        <Toggle options={[{ value: '3m', label: '3m' }, { value: '6m', label: '6m' }, { value: '12m', label: '12m' }, { value: 'all', label: 'Alles' }]} value={periodMode} onChange={setPeriodMode} />
      </div>
    </PageHeader>

    {/* ─── Scope-filters (Energielabel / Verkoopteam / Verkoper) ─────────── */}
    <Card className="!p-3 !overflow-visible">
      <div className="flex flex-wrap items-end gap-3 relative z-40">
        <SearchableSelect label="Energieleverancier" value={companyFilter} onChange={handleCompanyChange} options={companies} placeholder="Alle" />
        <SearchableSelect label="Verkoopteam" value={verkoopteamFilter} onChange={handleVerkoopteamChange} options={verkoopteams} placeholder="Alle" />
        <SearchableSelect label="Verkoper" value={verkoperFilter} onChange={setVerkoperFilter} options={verkopers} placeholder="Alle" />
        {hasScopeFilters && <button onClick={clearScopeFilters} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}
        <div className="text-xs text-slate-500 ml-auto">{scopedOrders.length} orders · {result.portfolio.length} unieke aansluitingen</div>
      </div>
    </Card>


    {/* ─── Tab-menu binnen Portfolio Insights (zoals Market Prices/Sales) ──
        Welke tabs er staan, hangt af van de toegekende subpagina's. */}
    {geenPfTabs && <GeenSubpaginas module="Portfolio Insights" />}
    <div className="-mx-1 overflow-x-auto no-scrollbar">
      <div className="flex gap-2 px-1 w-max">
        {portfolioTabs.map(pt => (
          <button key={pt.id} onClick={() => setPortfolioTab(pt.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${portfolioTab === pt.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-600'}`}>
            {pt.label}
          </button>
        ))}
      </div>
    </div>

    {/* ─── Tab: Samenvatting ────────────────────────────────────────────── */}
    <div className={toonPfTab('evolutie') ? 'space-y-6' : 'hidden'}>
    {/* ─── Kengetallen, gebundeld per thema — enkel op Samenvatting ──────────
        Drie groepen op één regel (4/5/4 kengetallen → kolombreedtes 4fr/5fr/4fr),
        zodat elk blok zijn kengetallen kwijt kan zonder dat de buren witruimte
        overhouden. De groep "Behoud" (geannuleerd, herwonnen, intern, nooit
        gestart, te controleren) staat hier bewust niet: die cijfers en lijsten
        leven in Retention → Behouden; de tellingen blijven in result.kpis. */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[4fr_5fr_4fr] gap-2 sm:gap-3">
      <PfKpiGroup titel="Portefeuille" subtitel="stand en groei">
        <PfKpi label="Actief" value={formatNumberRounded(k.activeCount)} accent="lime" hint={k.plannedCount > 0 ? `${formatNumberRounded(k.plannedCount)} vertrek gepland > 90 d` : "actieve EAN's"} />
        <PfKpi label="Nieuw" value={formatNumberRounded(k.newInPeriod)} accent="blue" hint="in periode" />
        <PfKpi label="Netto" value={`${k.netto >= 0 ? '+' : ''}${formatNumberRounded(k.netto)}`} accent={k.netto >= 0 ? 'lime' : 'red'} hint="nieuw − vertrokken" />
        <PfKpi label="Gem. duur" value={formatMonths(k.avgDurationMonths)} accent="slate" hint="actieve aansluiting" />
      </PfKpiGroup>

      {/* "Vertrokken" telt ook het vertrek in afhandeling mee (datum voorbij,
          marktbevestiging ontbreekt nog) — de hint zegt hoeveel dat er zijn. */}
      <PfKpiGroup titel="Vertrek" subtitel="verloop en churn">
        <PfKpi label="Vertrokken" value={formatNumberRounded(k.lostCount)} accent="red" hint={k.leavingCount > 0 ? `${formatNumberRounded(k.leavingCount)} in afhandeling` : 'totaal'} />
        <PfKpi label="In periode" value={formatNumberRounded(k.lostInPeriodCount)} accent="red" hint="vertrokken" />
        <PfKpi label="Zal weg" value={formatNumberRounded(k.willLeaveCount)} accent="amber" hint="≤ 90 dagen" />
        <PfKpi label="Churn" value={formatPct(k.churnRate)} accent="amber" hint="periode" />
        <PfKpi label="Vroegtijdig" value={formatNumberRounded(k.earlyLeaversCount)} accent="red" hint={`${formatPct(k.earlyLeaversPct)} van vertrokken`} />
      </PfKpiGroup>

      {/* Vertreksoorten volgens de MIG6-scenario's; zie de functionele beschrijving.
          De percentages staan tegenover álle vertrokken aansluitingen, dus die
          noemer staat één keer in de groepstitel i.p.v. bij elk kengetal. */}
      <PfKpiGroup titel="Vertreksoort" subtitel="aandeel van alle vertrokken">
        <PfKpi label="Loss" value={formatNumberRounded(k.lossLostCount)} accent="blue" hint={`${aandeelVertrokken(k.lossLostCount)} · andere lev.`} />
        <PfKpi label="Drop" value={formatNumberRounded(k.dropLostCount)} accent="red" hint={aandeelVertrokken(k.dropLostCount)} />
        <PfKpi label="EOC" value={formatNumberRounded(k.eocLostCount)} accent="amber" hint={`${aandeelVertrokken(k.eocLostCount)} · einde contract`} />
        <PfKpi label="ILC" value={formatNumberRounded(k.ilcLostCount)} accent="lime" hint={`${aandeelVertrokken(k.ilcLostCount)} · verhuis`} />
      </PfKpiGroup>
    </div>
    {/* De evolutiegrafiek staat enkel hier (de Churn-tab toonde hem vroeger ook;
        weg sinds 11-09-2026, dubbel). Volle breedte.
        De rest van deze tab staat verderop ("Portfolio-evolutie (vervolg)"),
        na de JSX van de andere tabs — die zijn dan verborgen, dus op het scherm
        sluit het naadloos aan. Leesvolgorde van de tab: evolutie → samenstelling
        → contractduur naast de bewegingen. */}
    {nieuwVsVertrokkenChart}
    </div>

    {/* ─── Tab: Churn ────────────────────────────────────────────────────── */}
    <div className={toonPfTab('churn') ? 'space-y-6' : 'hidden'}>
    <div>
      {/* Scenario's op het niveau van de bron: het stop-contract-type waar dat er
          is (de vier MIG6-scenario's), anders de workflow zelf (Loss, ILC,
          Mystery switch). Vervangt het vroegere blok "Top vertrekredenen", dat
          dezelfde vertrekken toonde maar op de samengevatte reden. */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-100 mb-3">Vertrekscenario's</h3>
        {result.stopContractBreakdown.length === 0 ? <p className="text-sm text-slate-500 italic">Geen vertrekscenario's</p> : (
          <>
            {/* Samenvatting per vertreksoort boven de verdeling: loss, drop, EOC,
                verhuis (ILC), mystery switch. */}
            {k.departureTotal > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {result.departureBreakdown.map(d => (
                  <div key={d.kind} className={`flex-1 min-w-[110px] rounded-lg p-2.5 border ${DEPARTURE_TILE_CLASS[d.kind] || 'bg-slate-700/40 border-slate-600/40'}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 truncate" title={d.name}>{d.name}</div>
                    <div className={`text-lg font-bold ${DEPARTURE_TEXT_CLASS[d.kind] || 'text-slate-300'}`}><Kengetal>{d.count}</Kengetal></div>
                    <div className="text-[10px] text-slate-500">{formatPct(d.pct)}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-center gap-4 min-w-0">
              <div className="w-full sm:w-[60%] min-w-0"><ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={result.stopContractBreakdown} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90}>
                    {result.stopContractBreakdown.map((_, i) => <Cell key={i} fill={reasonColors[i % reasonColors.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={CHART_TOOLTIP}
                    formatter={(v, _n, item) => [`${v} (${item?.payload?.pct?.toFixed(0) || 0}%)`, 'aansluitingen']} />
                </PieChart>
              </ResponsiveContainer></div>
              <div className="w-full sm:flex-1 min-w-0 space-y-1.5">
                {result.stopContractBreakdown.map((r, i) => (
                  <div key={r.name} className="flex items-center gap-2 text-sm">
                    <div className="w-2.5 h-2.5 rounded shrink-0" style={{ backgroundColor: reasonColors[i % reasonColors.length] }} />
                    <span className="text-slate-300 truncate flex-1" title={r.name}>{r.name}</span>
                    <span className="text-slate-100 font-semibold">{r.count}</span>
                    <span className="text-slate-500 text-xs">({r.pct.toFixed(0)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </Card>
    </div>

    {/* Cohortmatrix: rij = startmaand, kolom = aantal maanden ná die start. Zo
        zie je in één blik of recentere lichtingen langer blijven dan oudere.
        Cellen voorbij de leeftijd van een lichting blijven leeg — een cohort van
        drie maanden oud heeft nog geen maand 6. */}
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-lg font-semibold text-slate-100">Cohortmatrix — retentie per startmaand</h3>
        <Toggle
          options={[{ value: 'pct', label: '%' }, { value: 'aantal', label: 'Aantal' }]}
          value={cohortWeergave} onChange={setCohortWeergave} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs text-slate-400 max-w-3xl">
          Elke rij is een lichting: alle contracten die in die maand in levering gingen. De kolommen tonen hoeveel er
          na 1, 2, 3 … maanden nog liepen. Klik op een rij voor de details van die lichting.
          {cohortAfkappingen.map(a => (
            <span key={a.label} className="block mt-1 text-slate-500">
              {a.label}: enkel lichtingen vanaf {formatMaand(a.vanaf)}; oudere startmaanden blijven hier buiten beschouwing.
            </span>
          ))}
        </p>
        {/* Legende bij de kleurschaal, zodat een cel ook los te lezen is. */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-slate-500">laag</span>
          {[10, 25, 50, 75, 100].map(p => (
            <span key={p} style={cohortCellStyle(p)} className="w-6 h-3.5 rounded-sm" title={`${p}% retentie`} />
          ))}
          <span className="text-[10px] text-slate-500">hoog</span>
        </div>
      </div>
      {cohortMatrix.rows.length === 0 ? <p className="text-sm text-slate-500 italic">Geen contracten in de huidige scope</p> : <>
        <div className="overflow-x-auto">
          <table className="text-xs border-separate" style={{ borderSpacing: '2px' }}>
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-slate-800 px-2 py-1.5 text-left text-slate-400 font-medium whitespace-nowrap">Startmaand</th>
                <th className="px-2 py-1.5 text-right text-slate-400 font-medium whitespace-nowrap">Contracten</th>
                {cohortMatrix.offsets.map(o => (
                  <th key={o} className="px-2 py-1.5 text-center text-slate-400 font-medium whitespace-nowrap" title={`${o} maanden na de start`}>M{o}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohortMatrix.rows.map(r => (
                <tr key={r.month} onClick={() => setCohortSelectie(cohortSelectie === r.month ? null : r.month)}
                  className={`cursor-pointer ${cohortSelectie === r.month ? 'ring-1 ring-lime-500' : ''}`}>
                  <td className={`sticky left-0 z-10 px-2 py-1 whitespace-nowrap font-mono ${cohortSelectie === r.month ? 'bg-lime-500/20 text-lime-300' : 'bg-slate-800 text-slate-300'}`}>{r.month}</td>
                  <td className="px-2 py-1 text-right text-slate-400 whitespace-nowrap">{formatNumberRounded(r.size)}</td>
                  {r.cells.map((c, i) => c === null
                    ? <td key={i} className="px-2 py-1 text-center text-slate-700">·</td>
                    : (
                      <td key={i} style={cohortCellStyle(c.pct)}
                        className={`px-2 py-1 text-center whitespace-nowrap rounded ${cohortCellText(c.pct)}`}
                        title={`Lichting ${r.month} · na ${c.offset} ${c.offset === 1 ? 'maand' : 'maanden'}: ${c.retained} van ${r.size} nog actief (${c.pct.toFixed(1)}%)`}>
                        {cohortWeergave === 'pct' ? `${Math.round(c.pct)}%` : formatNumberRounded(c.retained)}
                      </td>
                    ))}
                </tr>
              ))}
              {/* Gewogen gemiddelde over alle lichtingen die die maand al bereikt hebben. */}
              <tr>
                <td className="sticky left-0 z-10 bg-slate-800 px-2 py-1 whitespace-nowrap font-semibold text-slate-200">Gemiddeld</td>
                <td className="px-2 py-1 text-right text-slate-400">{formatNumberRounded(cohortMatrix.rows.reduce((s, r) => s + r.size, 0))}</td>
                {cohortMatrix.gemiddeld.map((c, i) => c === null
                  ? <td key={i} className="px-2 py-1 text-center text-slate-700">·</td>
                  : (
                    <td key={i} className="px-2 py-1 text-center whitespace-nowrap font-semibold text-slate-200 border-t border-slate-600"
                      title={`Alle lichtingen die maand ${c.offset} bereikt hebben: ${c.retained} van ${c.size} nog actief`}>
                      {cohortWeergave === 'pct' ? `${Math.round(c.pct)}%` : formatNumberRounded(c.retained)}
                    </td>
                  ))}
              </tr>
            </tbody>
          </table>
        </div>
        {(() => {
          const r = cohortMatrix.rows.find(x => x.month === cohortSelectie);
          if (!r) return <p className="text-xs text-slate-500 mt-3 italic">Klik op een startmaand voor de details van die lichting.</p>;
          const laatste = r.cells.filter(Boolean).slice(-1)[0];
          return (
            <div className="mt-3 flex flex-wrap gap-2">
              <StatTile label={`Lichting ${r.month}`} value={formatNumberRounded(r.size)} sub="contracten gestart" minW={140} />
              <StatTile label="Nog actief" value={formatNumberRounded(r.actief)} accent="#10B981" dot="#10B981" sub={`${Math.round((r.actief / r.size) * 100)}% van de lichting`} minW={140} />
              <StatTile label="Vertrokken" value={formatNumberRounded(r.vertrokken)} accent="#F43F5E" dot="#F43F5E" sub={`${Math.round((r.vertrokken / r.size) * 100)}% van de lichting`} minW={140} />
              <StatTile label="Mediane looptijd" value={r.medianeDuur == null ? '—' : `${r.medianeDuur} mnd`} sub="van de vertrokken contracten" minW={140} />
              <StatTile label={`Retentie na ${laatste?.offset ?? 0} mnd`} value={`${Math.round(laatste?.pct ?? 0)}%`} sub="laatst meetbare maand" minW={140} />
            </div>
          );
        })()}
      </>}
    </Card>
    </div>

    {/* ─── Tab: Portfolio-evolutie (vervolg) ─────────────────────────────── */}
    <div className={toonPfTab('evolutie') ? 'space-y-6' : 'hidden'}>

    {/* Samenstelling direct onder de evolutiegrafiek: eerst WAARUIT het
        portfolio bestaat, daarna pas hoe lang klanten blijven en wat er beweegt. */}
    {verdelingCards}

    {/* Contractduur naast de bewegingen: twee kanten van dezelfde vraag — hoe
        lang klanten blijven, en wat er ondertussen op hun aansluiting gebeurt.
        GEEN items-start: beide kaarten beginnen én eindigen op dezelfde hoogte. */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Contractduur van klanten die de portefeuille verlaten hebben. De duur
          loopt over verlengingen/productwissels heen: van de start van het eerste
          order tot de effectieve einddatum van het laatste. Nog lopende
          aansluitingen tellen niet mee — dit is een analyse van verloren klanten. */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h3 className="text-lg font-semibold text-slate-100">Contractduur van vertrokken klanten</h3>
          {/* Aandeel (%) of aantal per maand; standaard %, want het gaat om de
              verdeling ("hoeveel procent vertrekt in maand 1?"), niet om de
              volumes. De vroegere staafbreedte-keuze (1/3/6 mnd) is weg: die was
              niet te doorgronden. Elke staaf is één maand. */}
          <Toggle
            options={[{ value: 'pct', label: '%' }, { value: 'aantal', label: 'Aantal' }]}
            value={durationWeergave} onChange={setDurationWeergave} />
        </div>
        <p className="text-xs text-slate-400 mb-3">
          {durationRows.length === 0 ? 'Volledige, afgesloten contractperiodes op aansluitingen die niet meer van ons zijn.'
            : <>{formatNumberRounded(durationRows.length)} afgesloten contracten · mediaan {durationStats.mediaan} mnd · gemiddeld {durationStats.gemiddeld} mnd · langste {durationStats.max} mnd</>}
        </p>
        {durationBins.length === 0 ? <p className="text-sm text-slate-500 italic">Geen afgesloten contracten in de huidige scope</p> :
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={durationBins} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="name" tick={{ fill: '#94A3B8', fontSize: 11 }} interval={1}
              label={{ value: 'Aantal actieve maanden vóór vertrek', position: 'insideBottom', offset: -2, fill: '#64748B', fontSize: 11 }} height={44} />
            <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={durationWeergave === 'pct'} width={44}
              tickFormatter={(v) => (durationWeergave === 'pct' ? `${v}%` : formatNumberRounded(v))} />
            <Tooltip contentStyle={CHART_TOOLTIP}
              formatter={(v, _n, item) => (durationWeergave === 'pct'
                ? [`${Number(v).toFixed(1)}% (${formatNumberRounded(item?.payload?.count || 0)} aansluitingen)`, 'aandeel']
                : [`${formatNumberRounded(v)} aansluitingen (${(item?.payload?.pct || 0).toFixed(1)}%)`, 'aantal'])}
              labelFormatter={(l) => `${l} ${Number(l) === 1 ? 'maand' : 'maanden'} actief vóór vertrek`} />
            <Bar dataKey={durationWeergave === 'pct' ? 'pct' : 'count'} name={durationWeergave === 'pct' ? 'Aandeel' : 'Aantal'} fill={CHART_HUES.blauw} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>}
      </Card>
      {bewegingenOverview}
    </div>

    </div>

    {/* ─── Tab: Productwissels ───────────────────────────────────────────── */}
    <div className={toonPfTab('productwissels') ? 'space-y-6' : 'hidden'}>
      <PfBewegingChart titel="Productwissels per maand" kleur={COLORS_BEWEGING.productwissel} data={productwisselsPerMaand}
        toelichting="Aantal productwissels per maand, met daarnaast hoeveel dat voorstelt tegenover de actieve portefeuille van die maand." />
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-100">Productwissels</h3>
          <span className="text-xs text-slate-500">{switches.length} aansluitingen</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Dezelfde klant wisselt van product op de aansluiting. Wisselde ook de klant, dan staat de beweging onder <strong>Klantenwissels</strong>.
        </p>
        {switches.length === 0 ? <p className="text-sm text-slate-500 italic">Geen wissels gedetecteerd</p> :
        <PfMovementTable rows={switches} sort={pfSwSort} onRowClick={setSelectedOrder} onRowContext={pfRowContext} />}
      </Card>

      {/* Correcties op het product horen inhoudelijk bij de productwissels: het is
          hetzelfde patroon, maar het oude order heeft nooit gelopen. */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-100">Correcties product</h3>
          <span className="text-xs text-slate-500">{productCorrections.length} aansluitingen</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Het product werd rechtgezet: dezelfde klant, en het nieuwe order start op dezelfde dag als het oude. Dit is
          geen wissel tijdens de looptijd en telt dus niet mee in de grafiek hierboven.
        </p>
        {productCorrections.length === 0 ? <p className="text-sm text-slate-500 italic">Geen correcties gedetecteerd</p> :
        <PfMovementTable rows={productCorrections} sort={pfPcSort} onRowClick={setSelectedOrder} onRowContext={pfRowContext} />}
      </Card>
    </div>

    {/* ─── Tab: Klantenwissels ─────────────────────────────────────────────── */}
    {/* Zelfde detectie als een productwissel (overlappende levering + ander
        product), maar de klant op de aansluiting verschilt: een nieuwe bewoner of
        eigenaar, geen bestaande klant die van product wisselt. Daarom een aparte
        lijst; ze tellen niet mee in de productwissels. */}
    <div className={toonPfTab('klantenwissels') ? 'space-y-6' : 'hidden'}>
      <PfBewegingChart titel="Klantenwissels per maand" kleur={COLORS_BEWEGING.klantenwissel} data={klantenwisselsPerMaand}
        toelichting="Aantal klantenwissels per maand, met daarnaast hoeveel dat voorstelt tegenover de actieve portefeuille van die maand." />
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-100">Klantenwissels</h3>
          <span className="text-xs text-slate-500">{customerSwitches.length} aansluitingen</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Aansluitingen waar het opvolgende order op een <strong>andere klant</strong> staat. Een afwijkende klantnaam telt als wissel,
          tenzij het klant-id of het e-mailadres gelijk is — dan gaat het om een dubbele klantfiche of een naamscorrectie.
        </p>
        {customerSwitches.length === 0 ? <p className="text-sm text-slate-500 italic">Geen klantenwissels gedetecteerd</p> :
        <PfMovementTable rows={customerSwitches} sort={pfCsSort} toonKlanten onRowClick={setSelectedOrder} onRowContext={pfRowContext} />}
      </Card>

      {/* Correcties waarbij óók de klant wijzigde: hetzelfde patroon als een
          klantenwissel, maar het oude order heeft nooit gelopen. */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-100">Correcties klant en product</h3>
          <span className="text-xs text-slate-500">{customerProductCorrections.length} aansluitingen</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Zowel de klant als het product werden rechtgezet, met dezelfde startdatum op het oude en het nieuwe order. Dit
          is geen wissel tijdens de looptijd en telt dus niet mee in de grafiek hierboven.
        </p>
        {customerProductCorrections.length === 0 ? <p className="text-sm text-slate-500 italic">Geen correcties gedetecteerd</p> :
        <PfMovementTable rows={customerProductCorrections} sort={pfCpcSort} toonKlanten onRowClick={setSelectedOrder} onRowContext={pfRowContext} />}
      </Card>
    </div>

    {/* ─── Tab: Contractverlengingen ─────────────────────────────────────── */}
    <div className={toonPfTab('verlengingen') ? 'space-y-6' : 'hidden'}>
      <PfBewegingChart titel="Contractverlengingen per maand" kleur={COLORS_BEWEGING.verlenging} data={verlengingenPerMaand}
        toelichting="Aantal contractverlengingen per maand, met daarnaast hoeveel dat voorstelt tegenover de actieve portefeuille van die maand." />
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-100">Contractverlengingen</h3>
          <span className="text-xs text-slate-500">{renewals.length} aansluitingen</span>
        </div>
        {renewals.length === 0 ? <p className="text-sm text-slate-500 italic">Geen verlengingen gedetecteerd</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead><tr className="border-b border-slate-700 text-xs text-slate-400">
              <SortHeader label="EAN" k="gsrn" sortConfig={pfRnSort.sortConfig} onSort={pfRnSort.handleSort} thClass="py-2 px-2" />
              <SortHeader label="Klantnummer" k="klantnummer" sortConfig={pfRnSort.sortConfig} onSort={pfRnSort.handleSort} thClass="py-2 px-2" />
              <SortHeader label="Klant" k="klant" sortConfig={pfRnSort.sortConfig} onSort={pfRnSort.handleSort} thClass="py-2 px-2" />
              <SortHeader label="Product" k="product" sortConfig={pfRnSort.sortConfig} onSort={pfRnSort.handleSort} thClass="py-2 px-2" />
              <SortHeader label="Verlengd op" k="verlengdOp" sortConfig={pfRnSort.sortConfig} onSort={pfRnSort.handleSort} thClass="py-2 px-2" />
              <SortHeader label="Nieuwe einddatum" k="einddatum" sortConfig={pfRnSort.sortConfig} onSort={pfRnSort.handleSort} thClass="py-2 px-2" />
            </tr></thead>
            <tbody>{sortRows(renewals, pfRnSort.sortConfig, { klantnummer: r => r.new?.customerReference || '', klant: r => r.new?.customer || '', product: r => r.new?.productGroep || '', verlengdOp: r => r.new?.effectiveStartDate || '', einddatum: r => r.new?.deliveryEndDate || '' }).slice(0, 30).map(r =><tr key={r.gsrn + r.new.id} onClick={() => setSelectedOrder(r.new)} onContextMenu={e => onPfContext(e, r.new?.customer, undefined, undefined, orderEtLinks(r.new || {}))} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer">
              <td className="py-2 px-2 text-slate-500 font-mono text-xs">{r.gsrn}</td>
              <td className="py-2 px-2 text-slate-400 font-mono text-xs whitespace-nowrap">{r.new.customerReference || '—'}</td>
              <td className="py-2 px-2 text-slate-200 truncate max-w-[140px]" title={r.new.customer}>{r.new.customer || '—'}</td>
              <td className="py-2 px-2 text-slate-300 text-xs">{r.new.productGroep || '—'}</td>
              <td className="py-2 px-2 text-lime-300 text-xs font-semibold">{r.new.effectiveStartDate || '—'}</td>
              <td className="py-2 px-2 text-slate-400 text-xs">{r.new.deliveryEndDate || '—'}</td>
            </tr>)}</tbody>
          </table>
          {renewals.length > 30 && <p className="text-xs text-slate-500 mt-2 italic">Eerste 30 weergegeven.</p>}
        </div>}
      </Card>
    </div>

    {/* ─── Tab: Churn (vervolg) ──────────────────────────────────────────── */}
    <div className={toonPfTab('churn') ? 'space-y-6' : 'hidden'}>
    {/* ─── Filterbar voor tabellen ───────────────────────────────────────── */}
    <Card className="!p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[132px]">
          <label className="block text-xs text-slate-400 mb-1">Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${statusFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <option value="all">Alle</option>
            {Object.entries(PORTFOLIO_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="min-w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Vertrekreden</label>
          <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${reasonFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <option value="all">Alle</option>
            {result.topReasons.map(r => <option key={r.name} value={r.name}>{r.name} ({r.count})</option>)}
          </select>
        </div>
        <div className="text-xs text-slate-500 ml-2">{filteredPortfolio.length} van {result.portfolio.length} aansluitingen</div>
      </div>
    </Card>

    {/* ─── Tabellen ──────────────────────────────────────────────────────── */}
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <PfVertrekTabel titel="Binnenkort vertrekken" rijen={upcomingVerrijkt} sort={pfUpSort}
        datumLabel="Exit-datum" datumKlasse="text-amber-300" bestandsnaam="binnenkort-vertrekken"
        leeg="Geen geplande vertrekken" onRowClick={setSelectedOrder} onRowContext={pfRowContext} />

      <PfVertrekTabel titel="Vroegtijdig vertrokken" rijen={earlyLeaversVerrijkt} sort={pfElSort}
        datumLabel="Eff. eind" datumKlasse="text-red-300" bestandsnaam="vroegtijdig-vertrokken"
        leeg="Geen vroegtijdig vertrek" onRowClick={setSelectedOrder} onRowContext={pfRowContext} />
    </div>

    {/* ─── Gefilterde portfolio-tabel (drill-down naar detail) ────────────── */}
    {(statusFilter !== 'all' || reasonFilter !== 'all') && <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-slate-100">Gefilterde aansluitingen</h3>
        <span className="text-xs text-slate-500">{filteredPortfolio.length} resultaten</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700 text-xs text-slate-400">
            <SortHeader label="Status" k="_status" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Klantnummer" k="customerReference" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Klant" k="customer" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="EAN" k="connectionPointGSRN" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Start" k="deliveryStartDate" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Eff. eind" k="effectiveEndDate" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Reden" k="_lossReason" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Verkoper" k="salesperson" sortConfig={pfFpSort.sortConfig} onSort={pfFpSort.handleSort} thClass="py-2 px-2" />
          </tr></thead>
          <tbody>{sortRows(filteredPortfolio, pfFpSort.sortConfig).slice(0, 100).map(r => {
            const sl = PORTFOLIO_STATUS_LABELS[r._status] || PORTFOLIO_STATUS_LABELS.unknown;
            return <tr key={r.id} onClick={() => setSelectedOrder(r)} onContextMenu={e => onPfContext(e, r.customer, klantTarget(r.customer, r.customerReference), undefined, orderEtLinks(r))} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer">
              <td className="py-2 px-2"><span className={`px-2 py-0.5 border rounded text-xs ${sl.cls}`}>{sl.label}</span>{r._isEarly && <span className="ml-1 px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">vroegtijdig</span>}</td>
              <td className="py-2 px-2 text-slate-400 font-mono text-xs whitespace-nowrap">{r.customerReference || '—'}</td>
              <td className="py-2 px-2 text-slate-200 truncate max-w-[140px]" title={r.customer}>{r.customer || '—'}</td>
              <td className="py-2 px-2 text-slate-500 font-mono text-xs">{r.connectionPointGSRN}</td>
              <td className="py-2 px-2 text-slate-400 text-xs">{r.deliveryStartDate || '—'}</td>
              <td className="py-2 px-2 text-slate-400 text-xs">{r.effectiveEndDate || '—'}</td>
              <td className="py-2 px-2 text-slate-300 text-xs truncate max-w-[160px]" title={r._lossReason}>{r._lossReason}</td>
              <td className="py-2 px-2 text-slate-400 text-xs">{r.salesperson || '—'}</td>
            </tr>;
          })}</tbody>
        </table>
        {filteredPortfolio.length > 100 && <p className="text-xs text-slate-500 mt-2 italic">Eerste 100 weergegeven; verfijn de filters voor meer detail.</p>}
      </div>
    </Card>}

    </div>

    {/* ─── Tab: Drops ────────────────────────────────────────────────────── */}
    <div className={toonPfTab('drops') ? 'space-y-6' : 'hidden'}>
      <div className="flex flex-wrap gap-2 sm:gap-3">
        <PortfolioKpiCard label="Gedropte aansluitingen" value={droppedRecords.length} accent="red" hint={k.lostCount > 0 ? `${formatPct((droppedRecords.length / k.lostCount) * 100)} van vertrokken` : '—'} />
        <PortfolioKpiCard label="Residential Drop" value={dropsResidentieel} accent="red" hint="residentiële klant" />
        <PortfolioKpiCard label="Non-residential Drop" value={dropsNietResidentieel} accent="red" hint="professionele klant" />
        <PortfolioKpiCard label="In afhandeling" value={dropsInAfhandeling.length} accent="amber" hint="dropdatum voorbij, nog niet bevestigd" />
        <PortfolioKpiCard label="Geannuleerd" value={dropsGeannuleerd.length} accent="lime" hint="drop geannuleerd, klant gebleven" />
        <PortfolioKpiCard label="Openstaand" value={formatEuroRounded(dropsOpenstaand)} accent="amber" hint="restbedrag facturen, per klant" />
        <PortfolioKpiCard label="Gem. duur" value={dropsGemDuur == null ? '—' : `${Math.round(dropsGemDuur)} mnd`} accent="slate" hint="klant vóór de drop" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Drops per verkoopteam: welk kanaal brengt de wanbetalers binnen? Het
            openstaande bedrag telt per unieke klant, niet per aansluiting. */}
        <Card>
          <h3 className="text-lg font-semibold text-slate-100 mb-1">Drops per verkoopteam</h3>
          <p className="text-xs text-slate-400 mb-3">Welk kanaal brengt de meeste gedropte klanten binnen? Aantal, aandeel, netto jaarvolume (afname − injectie) en wat er nog openstaat.</p>
          {dropsPerTeam.length === 0 ? <p className="text-sm text-slate-500 italic">Geen drops in de huidige scope</p> :
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="border-b border-slate-700 text-slate-400">
              <th className="text-left py-1">Verkoopteam</th>
              <th className="text-right py-1">Drops</th>
              <th className="text-right py-1">Aandeel</th>
              <th className="text-right py-1">Volume (kWh)</th>
              <th className="text-right py-1">Openstaand</th>
            </tr></thead>
            <tbody>{dropsPerTeam.slice(0, 12).map(t => (
              <tr key={t.team} className="border-b border-slate-700/30">
                <td className="py-1.5 text-slate-200 truncate max-w-[200px]" title={t.team}>{t.team}</td>
                <td className="py-1.5 text-right text-red-300 font-semibold">{t.aantal}</td>
                <td className="py-1.5 text-right text-slate-400">{t.pct.toFixed(1)}%</td>
                <td className="py-1.5 text-right text-slate-300 whitespace-nowrap">{formatNumberRounded(t.kwh)}</td>
                <td className="py-1.5 text-right text-amber-300 whitespace-nowrap">{formatCurrency(t.openstaand)}</td>
              </tr>
            ))}</tbody>
          </table>
          {dropsPerTeam.length > 12 && <p className="text-xs text-slate-500 mt-2 italic">Eerste 12 teams weergegeven.</p>}
          </div>}
        </Card>
        {/* Drops per maand, residentieel tegenover niet-residentieel, in aantal of
            in netto kWh. Dezelfde segmentkleuren als "Verdeling per Segment". */}
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h3 className="text-lg font-semibold text-slate-100">Drops per maand</h3>
            <Toggle options={[{ value: 'aantal', label: 'Aantal' }, { value: 'volume', label: 'Volume (kWh)' }]} value={dropsEenheid} onChange={setDropsEenheid} />
          </div>
          <p className="text-xs text-slate-400 mb-3">{dropsEenheid === 'volume' ? 'Netto jaarvolume (geschatte afname − geschatte injectie) van de gedropte aansluitingen, per dropmaand.' : 'Aantal gedropte aansluitingen per dropmaand.'} Residentieel en niet-residentieel gestapeld.</p>
          {dropsByMonth.length === 0 ? <p className="text-sm text-slate-500 italic">Geen drops in de huidige scope</p> :
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={dropsByMonth} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 10 }} angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={false} width={dropsEenheid === 'volume' ? 64 : 40} tickFormatter={(v) => formatNumberRounded(v)} />
              <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, naam) => [dropsEenheid === 'volume' ? `${formatNumberRounded(v)} kWh` : `${formatNumberRounded(v)} ${v === 1 ? 'aansluiting' : 'aansluitingen'}`, naam]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey={dropsEenheid === 'volume' ? 'nonresKwh' : 'nonres'} name="Non-residential Drop" fill={COLORS_SEGMENT.B2B} stackId="drops" stroke="var(--chart-surface, #ffffff)" strokeWidth={1} maxBarSize={34} />
              <Bar dataKey={dropsEenheid === 'volume' ? 'resKwh' : 'res'} name="Residential Drop" fill={COLORS_SEGMENT.B2C} stackId="drops" stroke="var(--chart-surface, #ffffff)" strokeWidth={1} radius={[4, 4, 0, 0]} maxBarSize={34} />
            </BarChart>
          </ResponsiveContainer>}
        </Card>
      </div>
      {/* Van Drop Risk naar drop: de trechter van vandaag en de uitkomst van alle
          aangekondigde drops. Eén kaart met vijf stappen op een rij. */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-100 mb-1">Van Drop Risk naar drop</h3>
        <p className="text-xs text-slate-400 mb-3">Links de stand van vandaag: wie zit voorbij de ingebrekestelling en bij wie loopt de drop al. Rechts de uitkomst van alle aangekondigde drops tot nu toe: uitgevoerd, of geannuleerd omdat de klant alsnog betaalde. Het aandeel geannuleerde drops is de succesratio van de herinneringsprocedure en het nabellen.</p>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <PortfolioKpiCard label="In Drop Risk (nu)" value={dropRiskNu} accent="amber" hint={(facturen || []).length ? 'klanten voorbij de ingebrekestelling' : 'facturen nog niet geladen'} />
          <PortfolioKpiCard label="Drop loopt (nu)" value={dropListNu} accent="red" hint="klanten in de Drop List" />
          <PortfolioKpiCard label="Uitgevoerd" value={droppedRecords.length} accent="red" hint="aansluitingen gedropt" />
          <PortfolioKpiCard label="Geannuleerd" value={dropsGeannuleerd.length} accent="lime" hint="klant betaalde alsnog" />
          <PortfolioKpiCard label="Succesratio" value={dropAfgeblazenPct == null ? '—' : formatPct(dropAfgeblazenPct)} accent="lime" hint="geannuleerde drops als aandeel van alle aangekondigde drops" />
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Drops per verkoper: drop-% tegenover de actieve klanten van de verkoper. */}
        <Card>
          <h3 className="text-lg font-semibold text-slate-100 mb-1">Drops per verkoper</h3>
          <p className="text-xs text-slate-400 mb-3">Drop-% = gedropte aansluitingen tegenover alle aansluitingen (gedropt + actief) van die verkoper. Een hoog percentage bij een klein aantal zegt weinig; bij een groot aantal is het een signaal over de acceptatie bij de verkoop.</p>
          {dropsPerVerkoper.length === 0 ? <p className="text-sm text-slate-500 italic">Geen drops in de huidige scope</p> :
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="border-b border-slate-700 text-slate-400">
              <th className="text-left py-1">Verkoper</th>
              <th className="text-left py-1">Team</th>
              <th className="text-right py-1">Drops</th>
              <th className="text-right py-1">Actief</th>
              <th className="text-right py-1">Drop-%</th>
              <th className="text-right py-1">Openstaand</th>
            </tr></thead>
            <tbody>{dropsPerVerkoper.slice(0, 12).map(v => (
              <tr key={`${v.verkoper}|${v.team}`} className="border-b border-slate-700/30">
                <td className="py-1.5 text-slate-200 truncate max-w-[160px]" title={v.verkoper}>{v.verkoper}</td>
                <td className="py-1.5 text-slate-400 truncate max-w-[140px]" title={v.team}>{v.team}</td>
                <td className="py-1.5 text-right text-red-300 font-semibold">{v.drops}</td>
                <td className="py-1.5 text-right text-slate-300">{formatNumberRounded(v.actief)}</td>
                <td className={`py-1.5 text-right font-semibold ${v.pct >= 10 ? 'text-red-300' : v.pct >= 5 ? 'text-amber-300' : 'text-slate-300'}`}>{v.pct.toFixed(1)}%</td>
                <td className="py-1.5 text-right text-amber-300 whitespace-nowrap">{formatCurrency(v.openstaand)}</td>
              </tr>
            ))}</tbody>
          </table>
          {dropsPerVerkoper.length > 12 && <p className="text-xs text-slate-500 mt-2 italic">Eerste 12 verkopers weergegeven (op aantal drops).</p>}
          </div>}
        </Card>
        {/* Tijd tot drop: hoeveel maanden was de klant actief vóór de drop. */}
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h3 className="text-lg font-semibold text-slate-100">Tijd tot drop</h3>
            <Toggle options={[{ value: 'pct', label: '%' }, { value: 'aantal', label: 'Aantal' }]} value={tijdTotDropWeergave} onChange={setTijdTotDropWeergave} />
          </div>
          <p className="text-xs text-slate-400 mb-3">Hoeveel maanden was de klant actief vóór de drop? Een drop in de eerste maanden wijst op de acceptatie bij de verkoop; een late drop op een klant die onderweg in de problemen kwam.{tijdTotDrop.onbekend > 0 && ` ${tijdTotDrop.onbekend} zonder bekende startdatum.`}</p>
          {droppedVerrijkt.length === 0 ? <p className="text-sm text-slate-500 italic">Geen drops in de huidige scope</p> :
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={tijdTotDrop.rijen} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="naam" tick={{ fill: '#94A3B8', fontSize: 11 }} label={{ value: 'Aantal actieve maanden vóór de drop', position: 'insideBottom', offset: -2, fill: '#64748B', fontSize: 11 }} height={44} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={tijdTotDropWeergave === 'pct'} width={44} tickFormatter={(v) => (tijdTotDropWeergave === 'pct' ? `${v}%` : formatNumberRounded(v))} />
              <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, naam) => [tijdTotDropWeergave === 'pct' ? `${Number(v).toFixed(1)}%` : `${formatNumberRounded(v)} ${v === 1 ? 'aansluiting' : 'aansluitingen'}`, naam]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey={tijdTotDropWeergave === 'pct' ? 'nonresPct' : 'nonres'} name="Non-residential Drop" fill={COLORS_SEGMENT.B2B} stackId="ttd" stroke="var(--chart-surface, #ffffff)" strokeWidth={1} maxBarSize={48} />
              <Bar dataKey={tijdTotDropWeergave === 'pct' ? 'resPct' : 'res'} name="Residential Drop" fill={COLORS_SEGMENT.B2C} stackId="ttd" stroke="var(--chart-surface, #ffffff)" strokeWidth={1} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>}
        </Card>
      </div>
      <PfVertrekTabel titel="Gedropte klanten" rijen={droppedVerrijkt} sort={pfDrSort}
        datumLabel="Dropdatum" datumKlasse="text-red-300" bestandsnaam="gedropte-klanten"
        toelichting="Aansluitingen waarvan de drop is uitgevoerd: de dropdatum is voorbij én de aansluiting is niet meer van ons. Drops die nog lopen staan in Retention → Drop List."
        leeg="Geen gedropte klanten in de huidige scope." onRowClick={setSelectedOrder} onRowContext={pfRowContext} />
      {/* De drops die NIET doorgingen: de klant betaalde alsnog of de procedure
          werd gestopt. Eerdere situatie, geen vertrek — maar wél recidive-
          informatie voor Drop Risk. */}
      <PfVertrekTabel titel="Geannuleerde drops" rijen={dropsGeannuleerdVerrijkt} sort={pfDgSort}
        datumLabel="Was gepland op" datumKlasse="text-lime-300" bestandsnaam="geannuleerde-drops" redenLabel="Eerdere situatie"
        toelichting="Drops die werden geannuleerd, waardoor de klant nog in portfolio is. Deze aansluitingen staan hier informatief om een beeld te geven welke aansluitingen risico kunnen lopen op betalingsachterstand."
        leeg="Geen geannuleerde drops in de huidige scope." onRowClick={setSelectedOrder} onRowContext={pfRowContext} />
    </div>

    {pfMenu}
    {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
  </div>;
};

// ─── Retention ─────────────────────────────────────────────────────────────
// Werklijst rond vertrek, met een horizontaal tab-menu zoals Portfolio Insights:
//   Drop Risk — lopende aansluitingen met een betalingsachterstand die volgens de
//               herinneringsprocedure richting drop gaat (nog te redden)
//   Drop List — de LOPENDE drops (dropdatum nog niet gepasseerd)
//   Losses    — aansluitingen met een exit-datum binnen 90 dagen
// Gebruikt dezelfde tabellen, verrijking en export als Portfolio Insights, met de
// cascade-scope die het hele platform deelt.
//
// Verschil met de tabel "Gedropte klanten" in Portfolio Insights: daar staan de
// AFGERONDE drops (de dropdatum ligt in het verleden).
const RETENTION_TABS = [
  { id: 'risk', label: 'Drop Risk' },
  { id: 'drops', label: 'Drop List' },
  { id: 'losses', label: 'Losses' },
  { id: 'behouden', label: 'Behouden' },
];

// De drempels DROP_RISK_DAGEN_* en bouwAchterstandPerReferentie staan bij de
// Portfolio-helpers (gedeeld met de Drops-tab van Portfolio Insights).

// Tags op een Drop Risk-rij: staat dezelfde KLANT (mogelijk met een andere
// aansluiting) ook al in de Drop List of in Losses? Kleuren volgen die van de
// bijbehorende lijst: rose voor drops, amber voor geplande vertrekken.
// Kengetal op deze pagina = aantal UNIEKE klanten, niet aansluitingen; de
// eenheid staat er klein achter zodat dat meteen duidelijk is.
const KlantAantal = ({ n }) => <>{formatNumberRounded(n)}<span className="ml-1 text-xs sm:text-sm font-normal opacity-60">klanten</span></>;

// Tekst in de kolom "Achterstand": langste achterstand + hoeveel facturen.
const achterstandTekst = (dagen, aantal) =>
  `${dagen} dagen vervallen · ${aantal || 0} ${aantal === 1 ? 'factuur' : 'facturen'}`;

const RETENTION_TAGS = {
  drop: { label: 'In Drop List', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30', title: 'Deze klant heeft ook een lopende drop' },
  loss: { label: 'In Losses', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30', title: 'Deze klant heeft ook een aansluiting die binnen 90 dagen vertrekt' },
  // Standen uit het vertrekmodel, als tag op de rij zelf.
  afhandeling: { label: 'In afhandeling', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30', title: 'De datum is voorbij, maar de markt heeft het vertrek nog niet bevestigd' },
  gepland: { label: 'Gepland > 90 d', cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30', title: 'Vertrek aangekondigd, maar de datum ligt meer dan 90 dagen vooruit' },
  recidive: { label: 'Eerder drop geannuleerd', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30', title: 'Voor deze klant werd al eens een drop opgestart en weer geannuleerd' },
  teruggewonnen: { label: 'Teruggewonnen', cls: 'bg-lime-500/15 text-lime-300 border-lime-500/30', title: 'Deze klant kwam terug na een eerder vertrek' },
};

const RetentionPage = ({ orders = [], facturen = [], salesStructure = EMPTY_STRUCTURE, currentUser, onOpenCustomer360 }) => {
  const [retentionTab, setRetentionTab] = useState('risk');
  const { onContext: onDlContext, menu: dlMenu } = useCustomer360Menu(onOpenCustomer360);
  const dlRowContext = (e, order) => onDlContext(e, order?.customer, klantTarget(order?.customer, order?.customerReference), undefined, orderEtLinks(order || {}));
  const dlUpSort = useTableSort('_datum', 'asc');
  const dlDrSort = useTableSort('_datum', 'asc');
  const dlRiskB2cSort = useTableSort('_datum', 'asc');
  const dlRiskNrSort = useTableSort('_datum', 'asc');
  const dlLvSort = useTableSort('_datum', 'desc');    // vertrek in afhandeling
  const dlCaSort = useTableSort('_datum', 'desc');    // geannuleerde vertrekken
  const dlWbSort = useTableSort('_datum', 'desc');    // teruggewonnen klanten
  // Elke lijst staat in twee tabellen: niet-residentieel (B2B en Custom) eerst,
  // dan residentieel (B2C) — dezelfde tweedeling als Drop Risk. De sort-states
  // hierboven dienen de niet-residentiële tabel, deze de residentiële.
  const dlDrB2cSort = useTableSort('_datum', 'asc');
  const dlUpB2cSort = useTableSort('_datum', 'asc');
  const dlLvB2cSort = useTableSort('_datum', 'desc');
  const dlCaB2cSort = useTableSort('_datum', 'desc');
  const dlWbB2cSort = useTableSort('_datum', 'desc');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [companyFilter, setCompanyFilter] = useState('all');
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [verkoperFilter, setVerkoperFilter] = useState('all');
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setCompanyFilter(d.company);
    setVerkoopteamFilter(d.team);
    setVerkoperFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  useFilterPersistence('droploss.filters', currentUser,
    { companyFilter, verkoopteamFilter, verkoperFilter },
    (s) => { setCompanyFilter(s.companyFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); });

  const { companies, teams: verkoopteams, sellers: verkopers } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter),
    [salesStructure, companyFilter, verkoopteamFilter]
  );
  const handleCompanyChange = (v) => { setCompanyFilter(v); setVerkoopteamFilter('all'); setVerkoperFilter('all'); };
  const handleVerkoopteamChange = (v) => { setVerkoopteamFilter(v); setVerkoperFilter('all'); };

  const scopedOrders = useMemo(() => {
    let data = orders || [];
    if (companyFilter !== 'all') data = data.filter(o => o.company === companyFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(o => o.salespersonTeam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(o => o.salesperson === verkoperFilter);
    return data;
  }, [orders, companyFilter, verkoopteamFilter, verkoperFilter]);

  const now = useMemo(() => new Date(), []);
  const result = useMemo(() => computePortfolioKPIs(scopedOrders, {}, now), [scopedOrders, now]);
  const verrijkVertrek = useVertrekVerrijking(scopedOrders, facturen);

  // Tags uit het vertrekmodel op een rij: in afhandeling (datum voorbij, markt
  // bevestigde nog niet), gepland (> 90 dagen), teruggewonnen.
  const standTags = (r) => {
    const tags = [];
    if (r._status === 'leaving') tags.push('afhandeling');
    if (r._planned) tags.push('gepland');
    if (r._teruggewonnen) tags.push('teruggewonnen');
    return tags;
  };
  const metStandTags = (rijen) => rijen.map(r => {
    const tags = [...(r._tags || []), ...standTags(r)];
    return { ...r, _tags: tags, _tagLabel: tags.map(t => RETENTION_TAGS[t].label).join(' · ') };
  });

  // Actieve drops: het scenario is een drop en de aansluiting is nog van ons —
  // de stopzetting loopt dus nog. Dat omvat drops zonder datum (aangekondigd,
  // nog niet gedateerd), drops met een datum in de toekomst én drops waarvan de
  // datum voorbij is maar de markt het nog niet bevestigde ("in afhandeling").
  // Uitgevoerde drops (CP ext) staan in Portfolio Insights; geannuleerde drops
  // onder Behouden.
  const actieveDrops = useMemo(() => metStandTags(verrijkVertrek(
    result.portfolio.filter(r => r._departureKind === 'drop' && (r._status === 'will_leave' || r._status === 'leaving' || r._status === 'active'))
  )), [result.portfolio, verrijkVertrek]);   // eslint-disable-line react-hooks/exhaustive-deps
  const binnenkort = useMemo(() => verrijkVertrek(result.upcoming), [result.upcoming, verrijkVertrek]);
  // Vertrek in afhandeling (alle soorten): de datum is voorbij, de aansluiting is
  // nog niet ext. Normaal een kwestie van weken; blijft het hangen, dan is het
  // een dossier om na te kijken.
  const inAfhandeling = useMemo(() => verrijkVertrek(result.leavingRecords), [result.leavingRecords, verrijkVertrek]);
  // Behouden: geannuleerde vertrekken (klant gebleven) en teruggewonnen klanten.
  const geannuleerd = useMemo(() => verrijkVertrek(result.cancelledRecords), [result.cancelledRecords, verrijkVertrek]);
  const teruggewonnen = useMemo(() => verrijkVertrek(result.winbackRecords), [result.winbackRecords, verrijkVertrek]);

  // ─── Losses per maand ────────────────────────────────────────────────────
  // Hoeveel aansluitingen verlaten ons in de huidige en de komende maanden? Per
  // maand het aantal exit-datums, gestapeld per vertreksoort. De huidige maand
  // telt volledig mee (ook de exits die deze maand al gepasseerd zijn): dat is
  // het verlies van déze maand. Anders dan de tabel eronder (90 dagen) kijkt de
  // grafiek zover vooruit als de horizon toelaat, zodat een exit in maart al in
  // november te zien is.
  const [lossHorizon, setLossHorizon] = useState(12); // maanden; 0 = alles
  const lossesPerMaand = useMemo(() => {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const sleutel = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const perMaand = new Map();
    let laatste = start;
    for (const r of result.portfolio) {
      if (!r._exit || r._exit < start) continue;
      const k = sleutel(r._exit);
      let rij = perMaand.get(k);
      if (!rij) { rij = { month: k, drop: 0, loss: 0, eoc: 0, ilc: 0, overig: 0, totaal: 0 }; perMaand.set(k, rij); }
      const soort = r._departureKind === DEPARTURE_DROP ? 'drop'
        : r._departureKind === DEPARTURE_LOSS ? 'loss'
        : r._departureKind === DEPARTURE_EOC ? 'eoc'
        : r._departureKind === DEPARTURE_ILC ? 'ilc' : 'overig';
      rij[soort] += 1;
      rij.totaal += 1;
      if (r._exit > laatste) laatste = r._exit;
    }
    // Elke maand tussen nu en de horizon staat op de as, ook zonder exits: een gat
    // in de reeks is informatie (een rustige maand), geen ontbrekende staaf.
    const eind = lossHorizon > 0
      ? new Date(start.getFullYear(), start.getMonth() + lossHorizon - 1, 1)
      : new Date(laatste.getFullYear(), laatste.getMonth(), 1);
    const rijen = [];
    let voorbij = 0;
    for (const [k, rij] of perMaand) {
      const d = new Date(Number(k.slice(0, 4)), Number(k.slice(5, 7)) - 1, 1);
      if (d > eind) voorbij += rij.totaal;
    }
    for (let d = new Date(start); d <= eind; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
      const k = sleutel(d);
      rijen.push(perMaand.get(k) || { month: k, drop: 0, loss: 0, eoc: 0, ilc: 0, overig: 0, totaal: 0 });
    }
    return { rijen, voorbij, totaal: rijen.reduce((s, r) => s + r.totaal, 0) };
  }, [result.portfolio, now, lossHorizon]);
  const LOSS_SOORTEN = [
    // Volgorde van onder naar boven; de aangrenzende tinten zijn met de
    // dataviz-validator gecontroleerd (ΔE ≥ 16 in beide thema's). Grijs is het
    // neutrale "overig" en bewust geen zesde tint. Zelfde kleuren als de
    // vertreksoorten in Portfolio-evolutie: dezelfde soort, dezelfde kleur.
    { key: 'drop', naam: 'Drop', kleur: '#DC2626' },
    { key: 'loss', naam: 'Loss — andere leverancier', kleur: '#3B82F6' },
    { key: 'eoc', naam: 'Einde contract (EOC)', kleur: '#D97706' },
    { key: 'ilc', naam: 'Verhuis (ILC)', kleur: '#A855F7' },
    { key: 'overig', naam: 'Mystery switch / onbekend', kleur: '#71717A' },
  ];
  const exportLossesPerMaand = () => exportRowsToCsv(
    'retention-losses-per-maand.csv',
    [
      { header: 'Maand', get: (r) => formatMaand(r.month), text: true },
      { header: 'Sleutel (ISO)', get: (r) => r.month, text: true },
      ...LOSS_SOORTEN.map((s) => ({ header: s.naam, get: (r) => r[s.key] })),
      { header: 'Totaal', get: (r) => r.totaal },
    ],
    lossesPerMaand.rijen,
  );

  // ─── Drop Risk ───────────────────────────────────────────────────────────
  // Betalingsachterstand per klant: de oudste vervallen, niet-betaalde
  // verkoopfactuur bepaalt hoeveel dagen de klant achterloopt. Koppeling via
  // `relatieReferentie` op de factuur = `customerReference` op het order, exact
  // zoals het openstaande bedrag in useVertrekVerrijking (de GSRN staat niet op
  // onbetaalde facturen). Een deelbetaling zonder restbedrag (invOutstanding
  // null) telt mee als openstaand — er is dan nog steeds niet volledig betaald.
  const achterstandPerReferentie = useMemo(() => bouwAchterstandPerReferentie(facturen), [facturen]);

  // Klanten die elders op deze pagina staan, op klantniveau: heeft de klant nog
  // ergens een LOPENDE drop (Drop List) of een aangekondigd vertrek (Losses)?
  // Dat kan op een ANDERE aansluiting van dezelfde klant zijn, vandaar de
  // vergelijking op klantreferentie i.p.v. op GSRN.
  const klantRef = (r) => String(r?.customerReference || '').trim().toLowerCase();
  const dropListKlanten = useMemo(() => new Set(actieveDrops.map(klantRef).filter(Boolean)), [actieveDrops]);
  const lossKlanten = useMemo(() => new Set(binnenkort.map(klantRef).filter(Boolean)), [binnenkort]);
  // Recidive: klanten voor wie al eens een drop werd opgestart en afgeblazen.
  const recidiveKlanten = useMemo(() => new Set(geannuleerd.filter(r => r._historiek?.soort === 'drop').map(klantRef).filter(Boolean)), [geannuleerd]);

  // Kandidaten: enkel aansluitingen die nog LOPEN (status active) — een klant
  // zonder actieve aansluiting is al vertrokken en valt niet meer te redden —
  // en waarvan de klant voorbij de drempel van zijn segment zit. Loopt er elders
  // al een drop of een vertrek voor dezelfde klant, dan blijft de rij staan maar
  // krijgt ze een tag; zo zie je meteen dat er al iets in gang is gezet.
  const dropRisico = useMemo(() => {
    const achterstand = (r) => achterstandPerReferentie.get(klantRef(r));
    const kandidaten = result.portfolio.filter(r => {
      if (r._status !== 'active') return false;
      const a = achterstand(r);
      if (!a) return false;
      return a.dagen >= (orderSegmentClass(r) === 'B2C' ? DROP_RISK_DAGEN_B2C : DROP_RISK_DAGEN_NIET_RESIDENTIEEL);
    });
    return verrijkVertrek(kandidaten).map(r => {
      const a = achterstand(r) || { dagen: 0, aantal: 0, oudste: '' };
      const ref = klantRef(r);
      const tags = [];
      if (ref && dropListKlanten.has(ref)) tags.push('drop');
      if (ref && lossKlanten.has(ref)) tags.push('loss');
      if (ref && recidiveKlanten.has(ref)) tags.push('recidive');
      return {
        ...r,
        _datum: a.oudste,                         // oudste vervallen factuur
        _dagenVervallen: a.dagen,
        _aantalFacturen: a.aantal,
        _lossReason: achterstandTekst(a.dagen, a.aantal),
        _tags: tags,
        _tagLabel: tags.map(t => RETENTION_TAGS[t].label).join(' · '),
      };
    });
  }, [result.portfolio, achterstandPerReferentie, verrijkVertrek, dropListKlanten, lossKlanten, recidiveKlanten]);

  // ─── Aggregatie per klant ────────────────────────────────────────────────
  // Drop Risk en Drop List tonen één rij per klant i.p.v. per aansluiting. Het
  // segment bepaalt de drempel, dus voor Drop Risk splitsen we eerst op segment
  // en aggregeren we daarna per lijst; een klant met zowel een B2C- als een
  // B2B-aansluiting staat dan in beide tabellen, elk met zijn eigen termijn.
  const klantRijen = (rijen) => aggregeerPerKlant(rijen).map(r => (
    r._dagenVervallen ? { ...r, _lossReason: achterstandTekst(r._dagenVervallen, r._aantalFacturen) } : r
  ));
  const dropRisicoB2c = useMemo(() => klantRijen(dropRisico.filter(r => orderSegmentClass(r) === 'B2C')), [dropRisico]);   // eslint-disable-line react-hooks/exhaustive-deps
  const dropRisicoNietRes = useMemo(() => klantRijen(dropRisico.filter(r => orderSegmentClass(r) !== 'B2C')), [dropRisico]); // eslint-disable-line react-hooks/exhaustive-deps
  // Voor de kengetal-tegel: unieke klanten over BEIDE risicolijsten samen (een
  // klant met een B2C- én een B2B-aansluiting telt dus één keer).
  const dropRisicoKlanten = useMemo(() => aggregeerPerKlant(dropRisico), [dropRisico]);
  const actieveDropsKlanten = useMemo(() => aggregeerPerKlant(actieveDrops), [actieveDrops]);
  // Segmentsplitsing van de lijsten: eerst op aansluiting splitsen, dan pas per
  // klant aggregeren (Drop List), zodat een klant met een B2C- én een
  // B2B-aansluiting in beide tabellen staat — net als bij Drop Risk.
  // Aansluitingen zonder segment horen bij niet-residentieel.
  const isB2c = (r) => orderSegmentClass(r) === 'B2C';
  const actieveDropsNietRes = useMemo(() => aggregeerPerKlant(actieveDrops.filter(r => !isB2c(r))), [actieveDrops]);
  const actieveDropsB2c = useMemo(() => aggregeerPerKlant(actieveDrops.filter(isB2c)), [actieveDrops]);
  // Twee tabellen voor één lijst (niet-residentieel boven, residentieel onder);
  // de gedeelde props gaan naar allebei, de titel en het bestand krijgen een suffix.
  // Gewone functie, geen component: een component die hier gedefinieerd wordt,
  // remount bij elke render en verliest dan zijn paginastand.
  const segmentTabellen = ({ titel, rijen, sortNr, sortB2c, bestandsnaam, leeg, ...rest }) => <>
    <PfVertrekTabel titel={`${titel} — niet-residentieel (B2B en Custom)`} rijen={rijen.filter(r => !isB2c(r))} sort={sortNr}
      bestandsnaam={`${bestandsnaam}-niet-residentieel`} leeg={`${leeg} (niet-residentieel)`} {...rest} />
    <PfVertrekTabel titel={`${titel} — residentieel (B2C)`} rijen={rijen.filter(isB2c)} sort={sortB2c}
      bestandsnaam={`${bestandsnaam}-b2c`} leeg={`${leeg} (residentieel)`} {...rest} />
  </>;
  const binnenkortKlanten = useMemo(() => aggregeerPerKlant(binnenkort), [binnenkort]);
  const inAfhandelingKlanten = useMemo(() => aggregeerPerKlant(inAfhandeling), [inAfhandeling]);
  const behoudenKlanten = useMemo(() => aggregeerPerKlant([...geannuleerd, ...teruggewonnen]), [geannuleerd, teruggewonnen]);

  // Openstaand over de geaggregeerde rijen: die tellen het bedrag al per unieke
  // klantreferentie, dus geen dubbeltelling bij klanten met meerdere EAN's.
  const openstaandTotaal = actieveDropsKlanten.reduce((s, r) => s + (r._openstaand || 0), 0);

  return <div className="space-y-6">
    <PageHeader title="Retention" subtitle="Werklijst van aansluitingen die vertrekken of waarvan de drop nog loopt" />

    <Card className="!p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[184px]"><SearchableSelect label="Energielabel" value={companyFilter} onChange={handleCompanyChange} options={companies} /></div>
        <div className="min-w-[184px]"><SearchableSelect label="Verkoopteam" value={verkoopteamFilter} onChange={handleVerkoopteamChange} options={verkoopteams} /></div>
        <div className="min-w-[184px]"><SearchableSelect label="Verkoper" value={verkoperFilter} onChange={setVerkoperFilter} options={verkopers} /></div>
      </div>
    </Card>

    <div className="flex flex-wrap gap-3">
      <StatTile label="Drop-risico" value={<KlantAantal n={dropRisicoKlanten.length} />} accent="#F59E0B" dot="#F59E0B" sub="achterstand voorbij ingebrekestelling" minW={220} />
      <StatTile label="Actieve drops" value={<KlantAantal n={actieveDropsKlanten.length} />} accent="#F43F5E" dot="#F43F5E" sub="stopzetting loopt nog" />
      <StatTile label="Binnenkort vertrekken" value={<KlantAantal n={binnenkortKlanten.length} />} accent="#F59E0B" dot="#F59E0B" sub="exit binnen 90 dagen" />
      <StatTile label="Vertrek in afhandeling" value={<KlantAantal n={inAfhandelingKlanten.length} />} accent="#F97316" dot="#F97316" sub="datum voorbij, nog niet bevestigd" minW={220} />
      <StatTile label="Behouden" value={<KlantAantal n={behoudenKlanten.length} />} accent="#84CC16" dot="#84CC16" sub="vertrek geannuleerd of teruggewonnen" minW={220} />
      <StatTile label="Openstaand bij actieve drops" value={formatCurrency(openstaandTotaal)} accent="#F59E0B" sub="restbedrag verkoopfacturen, incl. btw" minW={220} />
    </div>

    {/* Horizontaal tab-menu (zelfde pillen als Portfolio Insights/Sales/Market
        Prices); scrollt zijwaarts op een smal scherm. */}
    <div className="-mx-1 overflow-x-auto no-scrollbar">
      <div className="flex gap-2 px-1 w-max">
        {RETENTION_TABS.map(t => (
          <button key={t.id} onClick={() => setRetentionTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${retentionTab === t.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-600'}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>

    <div className="space-y-6">
      {retentionTab === 'risk' && <>
        {(facturen || []).length === 0 && (
          <Card className="!p-3">
            <p className="text-xs text-amber-300 flex items-center gap-2"><AlertCircle size={14} className="shrink-0" />
              Er zijn nog geen facturen geladen. De betalingsachterstand kan pas berekend worden zodra die binnen zijn — even geduld, ze laden automatisch.</p>
          </Card>
        )}
        <PfVertrekTabel titel="Drop Risk — niet-residentieel (B2B en Custom)" rijen={dropRisicoNietRes} sort={dlRiskNrSort} geaggregeerd
          datumLabel="Oudste vervaldag" datumKlasse="text-amber-300" redenLabel="Achterstand" toonTags bestandsnaam="retention-drop-risk-niet-residentieel"
          toelichting={`Eén rij per klant met lopende niet-residentiële aansluitingen waarvan minstens één verkoopfactuur ${DROP_RISK_DAGEN_NIET_RESIDENTIEEL} dagen of langer vervallen is — het punt van de ingebrekestelling. Aansluitingen zonder segment volgen deze kortere termijn. Volumes en openstaand bedrag zijn opgeteld, de contractduur is de kortste en de achterstand de langste van de klant.`}
          leeg="Geen niet-residentiële klanten met een achterstand voorbij de drempel" onRowClick={setSelectedOrder} onRowContext={dlRowContext} />
        <PfVertrekTabel titel="Drop Risk — residentieel (B2C)" rijen={dropRisicoB2c} sort={dlRiskB2cSort} geaggregeerd
          datumLabel="Oudste vervaldag" datumKlasse="text-amber-300" redenLabel="Achterstand" toonTags bestandsnaam="retention-drop-risk-b2c"
          toelichting={`Eén rij per klant met lopende residentiële aansluitingen waarvan minstens één verkoopfactuur ${DROP_RISK_DAGEN_B2C} dagen of langer vervallen is — het punt van de ingebrekestelling in de herinneringsprocedure. Zonder reactie volgt de drop. Volumes en openstaand bedrag zijn opgeteld, de contractduur is de kortste en de achterstand de langste van de klant.`}
          leeg="Geen residentiële klanten met een achterstand voorbij de drempel" onRowClick={setSelectedOrder} onRowContext={dlRowContext} />
      </>}
      {retentionTab === 'drops' && <>
        <PfVertrekTabel titel="Actieve drops — niet-residentieel (B2B en Custom)" rijen={actieveDropsNietRes} sort={dlDrSort} geaggregeerd toonTags
          datumLabel="Dropdatum" datumKlasse="text-rose-300" bestandsnaam="retention-actieve-drops-niet-residentieel"
          toelichting="Eén rij per klant met een drop die nog loopt: de aansluiting is nog van ons. Getoond wordt de eerstvolgende dropdatum van die klant. Is die datum al voorbij maar bevestigde de markt het vertrek nog niet, dan staat er de tag 'In afhandeling'. Zodra de aansluiting effectief weg is, verhuist ze naar 'Gedropte klanten' in Portfolio Insights; een geannuleerde drop staat onder Behouden."
          leeg="Geen lopende drops bij niet-residentiële klanten" onRowClick={setSelectedOrder} onRowContext={dlRowContext} />
        <PfVertrekTabel titel="Actieve drops — residentieel (B2C)" rijen={actieveDropsB2c} sort={dlDrB2cSort} geaggregeerd toonTags
          datumLabel="Dropdatum" datumKlasse="text-rose-300" bestandsnaam="retention-actieve-drops-b2c"
          toelichting="Zelfde lijst als hierboven, voor residentiële klanten."
          leeg="Geen lopende drops bij residentiële klanten" onRowClick={setSelectedOrder} onRowContext={dlRowContext} />
      </>}
      {retentionTab === 'losses' && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-lg font-semibold text-slate-100">Losses per maand</h3>
              <ExportKnop onExport={exportLossesPerMaand} disabled={!lossesPerMaand.rijen.length} label="Exporteer de losses per maand naar Excel" count={lossesPerMaand.totaal} />
            </div>
            <Toggle options={[{ value: 12, label: '12 maanden' }, { value: 24, label: '24 maanden' }, { value: 0, label: 'Alles' }]}
              value={lossHorizon} onChange={setLossHorizon} />
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Aantal aansluitingen met een exit-datum in de huidige en de komende maanden, per vertreksoort. De huidige maand telt
            volledig mee. De tabel eronder toont enkel de eerstkomende 90 dagen; de grafiek kijkt verder vooruit.
            {lossesPerMaand.voorbij > 0 && <> Buiten deze horizon liggen nog <span className="text-slate-200 font-medium">{formatNumberRounded(lossesPerMaand.voorbij)}</span> exits.</>}
          </p>
          {lossesPerMaand.totaal === 0 ? <TableEmpty message="Geen geplande exits in deze periode." icon={Folder} /> :
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={lossesPerMaand.rijen} margin={{ top: 18, right: 16, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="month" tickFormatter={(m) => formatMaand(m).replace(/^(\w+) (\d{4})$/, (_, mnd, jr) => `${mnd.slice(0, 3)} ${jr.slice(2)}`)}
                tick={{ fill: '#94A3B8', fontSize: 11 }} interval={0} angle={lossesPerMaand.rijen.length > 14 ? -45 : 0}
                textAnchor={lossesPerMaand.rijen.length > 14 ? 'end' : 'middle'} height={lossesPerMaand.rijen.length > 14 ? 56 : 30} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} width={40} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TOOLTIP} labelFormatter={(m) => formatMaand(m)}
                formatter={(v, naam) => [`${formatNumberRounded(v)} ${v === 1 ? 'aansluiting' : 'aansluitingen'}`, naam]} />
              <Legend />
              {/* Witte scheidingslijn tussen de segmenten (2 px), zodat de stapel ook
                  in grijstinten of bij kleurenblindheid uit elkaar te houden blijft.
                  Het totaal staat boven de stapel, op het bovenste segment. */}
              {LOSS_SOORTEN.map((s, i) => (
                <Bar key={s.key} dataKey={s.key} name={s.naam} fill={s.kleur} stackId="losses" stroke="var(--chart-surface, #ffffff)" strokeWidth={1} maxBarSize={34}
                  radius={i === LOSS_SOORTEN.length - 1 ? [4, 4, 0, 0] : 0}>
                  {i === LOSS_SOORTEN.length - 1 && (
                    <LabelList dataKey="totaal" position="top" fill="#94A3B8" fontSize={11} formatter={(v) => (v > 0 ? formatNumberRounded(v) : '')} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>}
        </Card>
      )}
      {retentionTab === 'losses' && segmentTabellen({ titel: 'Binnenkort vertrekken', rijen: binnenkort, sortNr: dlUpSort, sortB2c: dlUpB2cSort,
        datumLabel: 'Exit-datum', datumKlasse: 'text-amber-300', bestandsnaam: 'retention-binnenkort-vertrekken',
        toelichting: 'Aansluitingen met een exit-datum binnen 90 dagen die nog van ons zijn. De eerstvolgende staat bovenaan.',
        leeg: 'Geen geplande vertrekken', onRowClick: setSelectedOrder, onRowContext: dlRowContext })}
      {retentionTab === 'losses' && segmentTabellen({ titel: 'Vertrek in afhandeling', rijen: inAfhandeling, sortNr: dlLvSort, sortB2c: dlLvB2cSort,
        datumLabel: 'Exit-datum', datumKlasse: 'text-orange-300', bestandsnaam: 'retention-vertrek-in-afhandeling',
        toelichting: 'De exit-datum is voorbij, maar de markt heeft het vertrek nog niet bevestigd: de aansluiting staat nog op onze naam. Normaal duurt dat hooguit enkele weken. Staat een aansluiting hier maanden, dan is het dossier het nakijken waard. Deze aansluitingen tellen in Portfolio Insights al als vertrokken.',
        leeg: 'Geen vertrekken in afhandeling', onRowClick: setSelectedOrder, onRowContext: dlRowContext })}
      {retentionTab === 'behouden' && <>
        {segmentTabellen({ titel: 'Geannuleerde vertrekken', rijen: geannuleerd, sortNr: dlCaSort, sortB2c: dlCaB2cSort, redenLabel: 'Eerdere situatie',
          datumLabel: 'Was gepland op', datumKlasse: 'text-lime-300', bestandsnaam: 'retention-geannuleerde-vertrekken',
          toelichting: 'Aansluitingen waarvoor een drop, loss of ander vertrek werd opgestart en weer geannuleerd terwijl ze nog van ons zijn: de klant is gebleven. De kolom "Eerdere situatie" zegt wat er was aangekondigd en wanneer. Een eerder geannuleerde drop is een sterk signaal bij een nieuwe betalingsachterstand; in Drop Risk krijgen die klanten de tag "Eerder drop geannuleerd".',
          leeg: 'Geen geannuleerde vertrekken in de huidige scope', onRowClick: setSelectedOrder, onRowContext: dlRowContext })}
        {segmentTabellen({ titel: 'Teruggewonnen klanten', rijen: teruggewonnen, sortNr: dlWbSort, sortB2c: dlWbB2cSort, redenLabel: 'Eerdere situatie',
          datumLabel: 'Vorig vertrek', datumKlasse: 'text-lime-300', bestandsnaam: 'retention-teruggewonnen-klanten',
          toelichting: 'Aansluitingen met een nieuw contract na een eerder vertrek: de klant is teruggekomen. De datum is die van het vorige vertrek; de contractduur loopt vanaf de nieuwe start.',
          leeg: 'Geen teruggewonnen klanten in de huidige scope', onRowClick: setSelectedOrder, onRowContext: dlRowContext })}
      </>}
    </div>

    {dlMenu}
    {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
  </div>;
};

// Referrals: orders met ingevulde referral-velden plus inzicht of de referrer
// zelf via een verkoper binnenkwam. Matching op customer-naam is heuristisch
// (vrije tekst in `referrer`), genormaliseerd op lowercase+trim.
const ReferralsPage = ({ orders, onOpenCustomer360 }) => {
  const { onContext: onRefContext, menu: refMenu } = useCustomer360Menu(onOpenCustomer360);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'orderDate', dir: 'desc' });
  useFilterPersistence('referrals.filters', undefined,
    { companyFilter, searchTerm, sortConfig },
    (s) => { setCompanyFilter(s.companyFilter ?? 'all'); setSearchTerm(s.searchTerm ?? ''); if (s.sortConfig && s.sortConfig.key) setSortConfig(s.sortConfig); });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const pageSize = TABLE_PAGE_SIZE;

  const norm = (s) => (s || '').trim().toLowerCase();
  const hasReferralData = (o) => Boolean(o.ambassadorCode || o.referralLinkName || o.referrer || o.referralAmbassadorCode);

  const companies = useMemo(() => [...new Set((orders || []).map(o => o.company).filter(Boolean))].sort(), [orders]);

  // Lookup: customer-naam → had die klant ooit een order met een salesperson?
  // Wordt gebruikt om te bepalen of de referrer "via verkoper" is binnengekomen.
  const customerHasSellerMap = useMemo(() => {
    const m = new Map();
    for (const o of orders || []) {
      const k = norm(o.customer);
      if (!k) continue;
      const cur = m.get(k) || false;
      m.set(k, cur || Boolean(o.salesperson));
    }
    return m;
  }, [orders]);

  const referralOrders = useMemo(() => (orders || []).filter(hasReferralData), [orders]);

  const filteredReferrals = useMemo(() => {
    let data = referralOrders;
    if (companyFilter !== 'all') data = data.filter(o => o.company === companyFilter);
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      data = data.filter(o =>
        o.customer?.toLowerCase().includes(t) ||
        o.customerCompanyName?.toLowerCase().includes(t) ||
        o.customerReference?.toLowerCase().includes(t) ||
        o.customerVat?.toLowerCase().includes(t) ||
        o.referrer?.toLowerCase().includes(t) ||
        o.referralLinkName?.toLowerCase().includes(t) ||
        o.referralAmbassadorCode?.toLowerCase().includes(t) ||
        o.ambassadorCode?.toLowerCase().includes(t) ||
        o.salesperson?.toLowerCase().includes(t) ||
        o.company?.toLowerCase().includes(t)
      );
    }
    data = [...data].sort((a, b) => {
      const av = a[sortConfig.key] || '';
      const bv = b[sortConfig.key] || '';
      return sortConfig.dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return data;
  }, [referralOrders, companyFilter, searchTerm, sortConfig]);

  // KPI's berekend op de gefilterde set (scope-respecterend)
  const scopeBase = useMemo(() => companyFilter === 'all' ? (orders || []) : (orders || []).filter(o => o.company === companyFilter), [orders, companyFilter]);
  const totalReferrals = filteredReferrals.length;
  const totalOrdersInScope = scopeBase.length;
  const pctOfOrders = totalOrdersInScope > 0 ? (totalReferrals / totalOrdersInScope) * 100 : 0;
  const referralsViaSellerReferrer = useMemo(
    () => filteredReferrals.filter(o => customerHasSellerMap.get(norm(o.referrer)) === true).length,
    [filteredReferrals, customerHasSellerMap]
  );
  const referralsViaSellerOnOrder = useMemo(
    () => filteredReferrals.filter(o => Boolean(o.salesperson)).length,
    [filteredReferrals]
  );

  // Top 10 referrers (op naam) — meest aanbrengende klanten
  const topReferrers = useMemo(() => {
    const counts = new Map();
    for (const o of filteredReferrals) {
      const key = (o.referrer || '').trim();
      if (!key) continue;
      const cur = counts.get(key) || { name: key, count: 0, viaSeller: false };
      cur.count += 1;
      if (customerHasSellerMap.get(norm(key)) === true) cur.viaSeller = true;
      counts.set(key, cur);
    }
    return [...counts.values()].sort((a, b) => b.count - a.count).slice(0, 10);
  }, [filteredReferrals, customerHasSellerMap]);

  // Paginatie
  const totalPages = Math.max(1, Math.ceil(filteredReferrals.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filteredReferrals.slice(pageStart, pageStart + pageSize);

  const toggleSort = (key) => setSortConfig(p => p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  return <div className="space-y-6">
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
      <Share2 className="text-lime-400" size={28} />
      <div>
        <h2 className="text-2xl font-bold text-slate-100">Referrals</h2>
        <p className="text-sm text-slate-400">Verkooporders waar referral-data is ingevuld, plus inzicht hoe deze referrals binnenkomen.</p>
      </div>
    </div>

    {/* KPI strip */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-4">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Totaal referrals</p>
        <p className="text-3xl font-bold text-slate-100 mt-1"><Kengetal>{totalReferrals}</Kengetal></p>
        <p className="text-xs text-slate-500 mt-1">{pctOfOrders.toFixed(1)}% van {totalOrdersInScope} orders</p>
      </div>
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-4">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Referrer via verkoper</p>
        <p className="text-3xl font-bold text-lime-400 mt-1"><Kengetal>{referralsViaSellerReferrer}</Kengetal></p>
        <p className="text-xs text-slate-500 mt-1">Aanbrengende klant kwam zelf via verkoper binnen</p>
      </div>
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-4">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Order zelf via verkoper</p>
        <p className="text-3xl font-bold text-blue-400 mt-1"><Kengetal>{referralsViaSellerOnOrder}</Kengetal></p>
        <p className="text-xs text-slate-500 mt-1">Nieuwe order heeft zelf een salesperson</p>
      </div>
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-4">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Unieke referrers</p>
        <p className="text-3xl font-bold text-slate-100 mt-1"><Kengetal>{new Set(filteredReferrals.map(o => norm(o.referrer)).filter(Boolean)).size}</Kengetal></p>
        <p className="text-xs text-slate-500 mt-1">Klanten die minstens 1× hebben doorverwezen</p>
      </div>
    </div>

    {/* Top 10 referrers */}
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6">
      <h3 className="text-sm font-semibold text-lime-400 uppercase mb-4">Top 10 klanten op aantal referrals</h3>
      {topReferrers.length === 0 ? (
        <p className="text-sm text-slate-500">Geen referrals binnen de huidige filter.</p>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(200, topReferrers.length * 36)}>
          <BarChart data={topReferrers} layout="vertical" margin={{ top: 8, right: 24, left: 12, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis type="number" allowDecimals={false} stroke="#94a3b8" />
            <YAxis type="category" dataKey="name" stroke="#94a3b8" width={180} tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={CHART_TOOLTIP}
              formatter={(v, _n, item) => [`${v} referral${v === 1 ? '' : 's'}${item?.payload?.viaSeller ? ' • via verkoper' : ''}`, item?.payload?.name]}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {topReferrers.map((r, i) => <Cell key={i} fill={r.viaSeller ? '#84cc16' : '#6366F1'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      <div className="flex gap-4 mt-3 text-xs text-slate-400">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-lime-500 rounded-sm" /> Referrer kwam zelf via verkoper</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-500 rounded-sm" /> Andere referrer</span>
      </div>
    </div>

    {/* Filters + tabel */}
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6">
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="min-w-[184px]">
          <label className="block text-xs text-slate-400 mb-1">Energieleverancier</label>
          <select value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setCurrentPage(1); }} className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200">
            <AllOption options={companies}>Alle</AllOption>
            {companies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="w-full sm:w-[280px] shrink-0">
          <label className="block text-xs text-slate-400 mb-1">Zoeken</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2 text-slate-500" />
            <input type="text" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} placeholder="Klant, referrer, code, verkoper…" className="w-full pl-7 pr-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" />
          </div>
        </div>
        <div className="text-xs text-slate-400">{filteredReferrals.length} resultaten</div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="py-3 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('orderDate')}>Orderdatum</th>
              <th className="py-3 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('company')}>Energielabel</th>
              <th className="py-3 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('customer')}>Klant (nieuw)</th>
              <th className="py-3 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('referrer')}>Referrer</th>
              <th className="py-3 px-2 text-left text-slate-400 text-xs">Referrer via verkoper</th>
              <th className="py-3 px-2 text-left text-slate-400 text-xs">Referral Link</th>
              <th className="py-3 px-2 text-left text-slate-400 text-xs">Amb. Code</th>
              <th className="py-3 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('salesperson')}>Verkoper (deze order)</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(o => {
              const referrerViaSeller = customerHasSellerMap.get(norm(o.referrer)) === true;
              return (
                <tr key={o.id} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer" onClick={() => setSelectedOrder(o)} onContextMenu={e => onRefContext(e, o.customer, klantTarget(o.customer, o.customerReference, undefined, [o.connectionPointGSRN]),undefined, orderEtLinks(o))}>
                  <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{o.orderDate || '—'}</td>
                  <td className="py-2 px-2 text-slate-300 text-xs">{o.company || '—'}</td>
                  <td className="py-2 px-2 text-slate-100 text-xs">{o.customer || '—'}</td>
                  <td className="py-2 px-2 text-slate-300 text-xs">{o.referrer || '—'}</td>
                  <td className="py-2 px-2 text-xs">
                    {o.referrer
                      ? (referrerViaSeller
                          ? <span className="px-2 py-0.5 rounded-full bg-lime-500/20 text-lime-300 border border-lime-500/30">Ja</span>
                          : <span className="px-2 py-0.5 rounded-full bg-slate-600/40 text-slate-400 border border-slate-500/30">Nee</span>)
                      : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-32" title={o.referralLinkName}>{o.referralLinkName || '—'}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.referralAmbassadorCode || o.ambassadorCode || '—'}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs">{o.salesperson || <span className="text-slate-500">—</span>}</td>
                </tr>
              );
            })}
            {pageRows.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-slate-500 text-sm">Geen referral-orders gevonden.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-slate-400">
          <span>Pagina {currentPage} van {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-40">Vorige</button>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-40">Volgende</button>
          </div>
        </div>
      )}
    </div>

    {refMenu}
    {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
  </div>;
};

// ─── BillShock module ─────────────────────────────────────────────────────────
// Toont Lynx /api/BillShock/Get records. Scope wordt server-side toegepast:
// admin krijgt {items, orphans}; non-admin alleen {items} binnen hun scope.
// Cascade-filter (energielabel → verkoopteam → verkoper) net als Orders.

// Drempel voor top-10 lijsten op de Bill Shock pagina: records met absoluut
// bill-shock-bedrag onder dit bedrag tellen niet mee, ongeacht of er op
// deviation % of op € geranked wordt. Voorkomt dat "ruis" (kleine bedragen
// met toevallig hoog %) de top opslokt.
// Standaardstand van de CP-statusfilter op Bill Shock. Een bill shock van een
// aansluiting die we niet meer leveren (ext, draft, int_out) is geen actie meer;
// die maakten het grootste deel van de ruis uit. Lynx levert de status lowercase.
const BS_CP_STATUS_DEFAULT = 'owned';
// Hoeveel KLANTEN een Top-selectie per keer toont. Eén plek, want het getal staat
// ook in de knoptekst en in de bladerknoppen eronder. Sinds de top per klant
// groepeert is dit een aantal groepen, geen aantal tabelrijen: onder elke groep
// staan ook de overige berekeningen van die klant.
const BS_TOP_SIZE = 20;
const BS_MIN_TOP_AMOUNT_EUR = 50;

const BS_EUR_FMT = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });
const BS_NUM_FMT = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 });
// GEMETEN kWh (calc offtake/injection) tonen we op hele kWh. Een tiende of
// honderdste kWh zegt niets over een verbruik van duizenden kWh en maakt de kolom
// alleen moeilijker te scannen. Apart van bsFormatNum, want die formatteert óók
// bedragen en percentages in deze module — daar horen de decimalen wél.
const BS_KWH_FMT = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 0 });
function bsFormatEuro(v) { return v == null || Number.isNaN(Number(v)) ? '—' : BS_EUR_FMT.format(Number(v)); }
function bsFormatNum(v) { return v == null || Number.isNaN(Number(v)) ? '—' : BS_NUM_FMT.format(Number(v)); }
function bsFormatKwh(v) { return v == null || Number.isNaN(Number(v)) ? '—' : BS_KWH_FMT.format(Number(v)); }
// LET OP: `billshockDeviationPercentage` is AL een percentage, geen fractie —
// Lynx rekent bill shock ÷ settlement × 100. Nog eens ×100 doen gaf een factor 100
// te hoge afwijking (11,9% werd 1.186%). Nagerekend op de volledige set: alle 2.667
// records voldoen aan dev = billShockAmount / settlementAmount × 100.
function bsFormatPct(v) { return v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(1)}%`; }
// Kengetallen bovenaan: hele euro's en hele procenten. Op portefeuilleniveau zeggen
// centen en tienden van procenten niets meer — de tabel eronder houdt het detail.
const BS_EUR_FMT0 = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
function bsFormatEuro0(v) { return v == null || Number.isNaN(Number(v)) ? '—' : BS_EUR_FMT0.format(Number(v)); }
// As-labels van de maandgrafiek: kort, met k vanaf tienduizend ("€ 12k"), het
// teken blijft staan omdat de staven onder de nullijn negatief zijn.
const bsAsBedrag = (v) => (Math.abs(v) >= 10000 ? `€ ${Math.round(v / 1000)}k` : `€ ${Math.round(v)}`);
// Reeksnamen van de maandgrafiek: legenda, tooltip en tabel gebruiken dezelfde.
const BS_MAAND_REEKS = { betalen: 'Te betalen door klanten', ontvangen: 'Terug naar klanten', netto: 'Netto' };

// Eén kaart voor de twee maandgrafieken op Bill Shock ("Afrekeningen per maand" en
// "Cumulatief per afrekeningsmaand"): kop + ondertitel, Grafiek/Tabel-schakelaar,
// lege staat, de tabel (met een totaalregel als `totaal` meegegeven is) of de
// staafgrafiek met het netto als lijn. Boven de nullijn wat klanten bijbetalen,
// eronder wat er terug moet. Rood/groen = dezelfde codering als de bedragen en de
// Top-knoppen; het paar is gecontroleerd met de dataviz-validator (licht én donker).
// `reeks` heeft de vorm van perAfrekeningsmaand (ook na cumulatief()).
const BsAfrekeningKaart = ({ titel, ondertitel, reeks, totaal, eenheid, weergave, onWeergave, loading }) => {
  const tooltip = (value, name, props) => {
    const p = props?.payload || {};
    const n = name === BS_MAAND_REEKS.betalen ? p.aantalBetalen : name === BS_MAAND_REEKS.ontvangen ? p.aantalOntvangen : null;
    return [n == null ? bsFormatEuro(value) : `${bsFormatEuro(value)} · ${formatNumberRounded(n)} ${eenheid}`, name];
  };
  const streep = <span className="text-slate-600">—</span>;
  const nettoKleur = (v) => (v > 0 ? 'amt-pos' : v < 0 ? 'amt-neg' : 'text-slate-300');
  const schuin = reeks.length > 8;
  return (
    <Card className="!p-4 grafiek-modern">
      {/* Geen flex-wrap: de schakelaar blijft op de kopregel en de ondertitel
          loopt eronder door (min-w-0 + flex-1), ook op een halve kaartbreedte. */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-100">{titel}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{ondertitel}</p>
        </div>
        <div className="shrink-0"><Toggle options={[{ value: 'grafiek', label: 'Grafiek' }, { value: 'tabel', label: 'Tabel' }]} value={weergave} onChange={onWeergave} /></div>
      </div>
      {reeks.length === 0 ? (
        <TableEmpty message={loading ? 'Laden…' : 'Geen afrekeningen binnen je filters.'} icon={Folder} />
      ) : weergave === 'tabel' ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead>
              <tr className="border-b border-slate-700/50 bg-slate-800/40">
                <th className="py-2 px-2 text-left text-slate-400 text-xs">Maand</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Te betalen</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs capitalize">{eenheid}</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Te ontvangen</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs capitalize">{eenheid}</th>
                <th className="py-2 px-2 text-right text-slate-400 text-xs">Netto</th>
              </tr>
            </thead>
            <tbody>
              {reeks.map(m => (
                <tr key={m.maand} className="border-b border-slate-700/30">
                  <td className="py-1.5 px-2 text-slate-300 text-xs whitespace-nowrap">{m.label}</td>
                  <td className="py-1.5 px-2 text-right text-xs whitespace-nowrap amt-pos">{m.betalen ? bsFormatEuro(m.betalen) : streep}</td>
                  <td className="py-1.5 px-2 text-right text-xs text-slate-400">{m.aantalBetalen || streep}</td>
                  <td className="py-1.5 px-2 text-right text-xs whitespace-nowrap amt-neg">{m.ontvangen ? bsFormatEuro(m.ontvangen) : streep}</td>
                  <td className="py-1.5 px-2 text-right text-xs text-slate-400">{m.aantalOntvangen || streep}</td>
                  <td className={`py-1.5 px-2 text-right text-xs font-semibold whitespace-nowrap ${nettoKleur(m.netto)}`}>{bsFormatEuro(m.netto)}</td>
                </tr>
              ))}
              {totaal && (
                <tr className="border-t-2 border-slate-600/60 font-semibold">
                  <td className="py-1.5 px-2 text-slate-200 text-xs">Totaal</td>
                  <td className="py-1.5 px-2 text-right text-xs whitespace-nowrap amt-pos">{bsFormatEuro(totaal.betalen)}</td>
                  <td className="py-1.5 px-2 text-right text-xs text-slate-400">{formatNumberRounded(totaal.aantalBetalen)}</td>
                  <td className="py-1.5 px-2 text-right text-xs whitespace-nowrap amt-neg">{bsFormatEuro(totaal.ontvangen)}</td>
                  <td className="py-1.5 px-2 text-right text-xs text-slate-400">{formatNumberRounded(totaal.aantalOntvangen)}</td>
                  <td className={`py-1.5 px-2 text-right text-xs whitespace-nowrap ${nettoKleur(totaal.netto)}`}>{bsFormatEuro(totaal.netto)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          {/* stackOffset="sign": positief boven en negatief onder de nullijn in
              dezelfde kolom. Elke reeks heeft maar één teken, dus de afronding
              zit per reeks aan de buitenkant van de staaf. */}
          <ComposedChart data={reeks} stackOffset="sign" barCategoryGap="25%">
            <CartesianGrid vertical={false} stroke="#334155" />
            <XAxis dataKey="label" tick={{ fill: '#94A3B8', fontSize: 11 }} angle={schuin ? -45 : 0} textAnchor={schuin ? 'end' : 'middle'} height={schuin ? 60 : 30} {...CHART_AS_X} />
            <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} tickFormatter={bsAsBedrag} width={64} {...CHART_AS_Y} />
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={tooltip} />
            <Legend {...CHART_LEGENDA} />
            <ReferenceLine y={0} stroke="#64748b" />
            <Bar dataKey="betalen" name={BS_MAAND_REEKS.betalen} stackId="afrekening" fill={CHART_HUES.rood} maxBarSize={34} radius={[4, 4, 0, 0]} />
            <Bar dataKey="ontvangen" name={BS_MAAND_REEKS.ontvangen} stackId="afrekening" fill={CHART_HUES.groen} maxBarSize={34} radius={[0, 0, 4, 4]} />
            {/* Het netto is een referentie, geen categorie: neutrale inkt, zoals
                de totaallijn op Sales. */}
            <Line type="monotone" dataKey="netto" name={BS_MAAND_REEKS.netto} stroke="var(--chart-neutral)" strokeWidth={2} dot={CHART_LIJN_PUNT} activeDot={CHART_LIJN_PUNT_ACTIEF} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};
function bsFormatPct0(v) { return v == null || Number.isNaN(Number(v)) ? '—' : `${Math.round(Number(v))}%`; }

// Markt van een bill-shock-record. De backend zet 'm plat op `market` (Lynx wint,
// order als vangnet); de fallback op projectConnectionPoint houdt oudere,
// nog niet ververste snapshots leesbaar.
const bsMarket = (r) => (r.market || r.projectConnectionPoint?.market || '');
const bsMarketLabel = (m) => (String(m).toUpperCase() === 'NG' ? 'Gas' : String(m).toUpperCase() === 'EL' ? 'Elektriciteit' : m);

// Klantnaam volgens de platformregel (zie customerPrimaryName): bij een zakelijke
// klant (B2B/Custom mét ondernemingsnaam) de BEDRIJFSNAAM, bij een particulier
// (B2C) de persoonsnaam. Wijkt de contactpersoon af, dan staat die er klein onder,
// zodat je nog steeds ziet met wie je te maken hebt.
// De contactpersoon zoals de tabel hem TOONT: leeg wanneer er geen tweede regel
// onder de klantnaam staat. Apart van de component zodat de export exact dezelfde
// waarde kan schrijven als het scherm — dat is de afspraak voor exports, en met
// twee losse condities zou dat vroeg of laat uit elkaar lopen.
const bsContactpersoon = (row) => (
  isBusiness(row) && String(row.customer || '').trim() && row.customer !== row.customerCompanyName
    ? row.customer : ''
);

const BsCustomerName = ({ row, className = 'max-w-[200px]' }) => {
  const primair = customerPrimaryName(row);
  const persoon = bsContactpersoon(row);
  if (!primair) return <div className="text-slate-500">—</div>;
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="text-slate-100 truncate" title={primair}>{primair}</div>
      {persoon && <div className="text-slate-500 text-[10px] truncate" title={persoon}>{persoon}</div>}
    </div>
  );
};

// Kenmerken van het aansluitpunt, klein onder de EAN: klantensegment, energietype
// en metermethode. Bewust badges en geen extra kolommen — het is context bij de
// GSRN, en de tabel is al breed. Kleurcodering van de markt volgt de rest van het
// platform: groen = elektriciteit, oranje = gas.
const BsMeta = ({ row }) => {
  const segment = (row.segment || '').toUpperCase();
  const markt = bsMarket(row).toUpperCase();
  const meter = row.meterMethod || '';
  if (!segment && !markt && !meter) return null;
  // `tag`-klassen uit het centrale themablok: die zetten vulling én tekst per
  // thema, dus de badges blijven leesbaar op wit én op zwart.
  const pill = 'tag px-1.5 py-px text-[9px]';
  return (
    <div className="flex flex-wrap items-center gap-1 mt-0.5">
      {segment && <span className={`${pill} tag-neutral`} title="Klantensegment">{segment}</span>}
      {markt && <span className={`${pill} ${markt === 'NG' ? 'tag-ng' : 'tag-el'}`} title="Energietype">{markt === 'NG' ? 'GAS' : 'ELEK'}</span>}
      {meter && <span className={`${pill} tag-neutral`} title="Metermethode">{meter}</span>}
    </div>
  );
};
// Verkoper, CP-status en lopende workflow komen alle drie uit de order achter de
// GSRN (Lynx kent ze niet). Ze staan in élke tabel van deze module in dezelfde
// volgorde en met dezelfde opmaak, dus één cel-component i.p.v. drie kopieën.
// De workflow toont de stap met de flow als terugval — zelfde regel als Orders,
// zie workflowLabel.
// `pad` volgt de tabel waarin de cel staat: de hoofdtabel is ruimer dan de
// compacte Top 10-blokken, en cellen in één rij horen dezelfde hoogte te hebben.
const BsSellerCell = ({ row, className = 'max-w-[140px]', pad = 'py-2 px-2' }) => (
  <td className={`${pad} text-slate-400 text-xs truncate ${className}`} title={row.salesperson || ''}>
    {row.salesperson || <span className="text-slate-500">—</span>}
  </td>
);
const BsCpStatusCell = ({ row, pad = 'py-2 px-2' }) => (
  <td className={`${pad} text-slate-400 text-xs whitespace-nowrap`} title="Status van het aansluitpunt">
    {row.connectionPointStatus || <span className="text-slate-500">—</span>}
  </td>
);
// Productgroep = het verkochte product ("Flexi", "Dynamisch Pro"), uit de order.
// Niet te verwarren met het energietype (grey/green) — dat is `energyProductGroup`
// en staat al als badge onder de EAN.
// Schatting uit de order tegenover de gemeten hoeveelheid uit de bill shock, per
// richting naast elkaar. Vier cellen in één component, want ze horen bij elkaar en
// staan in elke tabel in dezelfde volgorde. De schatting staat doffer dan de
// meting: de meting is het feit, de schatting de referentie.
// De GEMETEN hoeveelheid staat vóór de schatting: de berekening zelf is het
// harde cijfer, de jaarschatting uit de order is de referentie waartegen je ze
// leest. Per richting gegroepeerd (eerst offtake, dan injectie). Alles in kWh.
const BsKwhCells = ({ row, pad = 'py-2 px-2' }) => (
  <>
    <td className={`${pad} text-right text-slate-300 text-xs whitespace-nowrap`}>{bsFormatKwh(row.offtakeQuantityKWh)}</td>
    <td className={`${pad} text-right text-slate-400 text-xs whitespace-nowrap`}>{row.estOfftakeKwh ? bsFormatNum(row.estOfftakeKwh) : '—'}</td>
    <td className={`${pad} text-right text-slate-300 text-xs whitespace-nowrap`}>{bsFormatKwh(row.injectionQuantityKWh)}</td>
    <td className={`${pad} text-right text-slate-400 text-xs whitespace-nowrap`}>{row.estInjectionKwh ? bsFormatNum(row.estInjectionKwh) : '—'}</td>
  </>
);
const BsProductGroupCell = ({ row, className = 'max-w-[130px]', pad = 'py-2 px-2' }) => (
  <td className={`${pad} text-slate-400 text-xs truncate ${className}`} title={row.productGroep || ''}>
    {row.productGroep || <span className="text-slate-500">—</span>}
  </td>
);
function bsFormatDate(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('nl-BE', { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch { return iso; } }

const BillShockDetailModal = ({ row, token, onClose }) => {
  // De lijst (/api/billshock) draagt de settlement- en prepaymentregels NIET meer
  // mee: die twee collecties waren samen ~70% van een antwoord van tientallen MB's,
  // en ze worden alleen hier getoond. Bij het openen halen we daarom het volledige
  // record op (GET /api/billshock/:id — uit dezelfde servercache, dus geen extra
  // Lynx-call). Alle overige velden zitten al in `row`, zodat de modal meteen
  // gevuld is en enkel de twee detailtabellen naladen.
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const rowId = row?.id;
  // Komt het record toch mét detail binnen (oudere response, andere bron), dan
  // laden we niets bij.
  const heeftDetail = Array.isArray(row?.billShockSettlementCollection);
  useEffect(() => {
    setDetail(null); setDetailError(null);
    if (rowId == null || heeftDetail || !token) return;
    let geannuleerd = false;
    setDetailLoading(true);
    fetch(`/api/billshock/${rowId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error(((await r.json().catch(() => ({}))).error) || `HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!geannuleerd) setDetail(j); })
      .catch(e => { if (!geannuleerd) setDetailError(e.message); })
      .finally(() => { if (!geannuleerd) setDetailLoading(false); });
    return () => { geannuleerd = true; };
  }, [rowId, heeftDetail, token]);
  if (!row) return null;
  const settle = detail?.billShockSettlementCollection || row.billShockSettlementCollection || [];
  const prepay = detail?.billshockPrepaymentCollection || row.billshockPrepaymentCollection || [];
  // Aantallen komen uit de lijst (settlementLineCount/prepaymentLineCount) zodat de
  // titel al klopt terwijl de regels nog onderweg zijn.
  const settleCount = settle.length || row.settlementLineCount || 0;
  const prepayCount = prepay.length || row.prepaymentLineCount || 0;
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
          <div>
            <h3 className="text-lg font-bold text-slate-100">Bill Shock #{row.id}</h3>
            <p className="text-xs text-slate-400 mt-0.5">EAN {row.connectionPointGSRN || '—'} · {customerPrimaryName(row) || row.customerReference || '—'}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">Scope (uit orders-cache)</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Energielabel</div><div className="text-slate-200">{row.company || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Verkoopteam</div><div className="text-slate-200">{row.salespersonTeam || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Verkoper</div><div className="text-slate-200">{row.salesperson || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Klant</div><div className="text-slate-200">{customerPrimaryName(row) || '—'}</div>{isBusiness(row) && row.customer && row.customer !== row.customerCompanyName && <div className="text-[10px] text-slate-500">Contact: {row.customer}</div>}</div>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">Connection Point</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">EAN</div><div className="text-slate-200 font-mono text-xs">{row.connectionPointGSRN || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Market</div><div className="text-slate-200">{row.projectConnectionPoint?.market || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Project ref</div><div className="text-slate-200 text-xs">{row.projectConnectionPoint?.projectReference || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Agreement ID</div><div className="text-slate-200">{row.projectConnectionPoint?.currentAgreementId || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Project ID</div><div className="text-slate-200">{row.projectConnectionPoint?.projectId || '—'}</div></div>
              <div><div className="text-xs text-slate-500">CP ID</div><div className="text-slate-200">{row.projectConnectionPoint?.connectionPointId || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Delivery start</div><div className="text-slate-200">{bsFormatDate(row.projectConnectionPoint?.deliveryStartDateUTC)}</div></div>
              <div><div className="text-xs text-slate-500">Delivery einde</div><div className="text-slate-200">{bsFormatDate(row.projectConnectionPoint?.deliveryEndDateUTC)}</div></div>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">Berekening</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Periode</div><div className="text-slate-200">{bsFormatDate(row.fromUtc)} → {bsFormatDate(row.toUtc)}</div></div>
              <div>
                <div className="text-xs text-slate-500">Berekend op</div>
                <div className="text-slate-200">{bsFormatDate(row.calculationUtc)}</div>
                {/* Lynx bewaart ELKE berekening van een aansluiting; de module toont
                    er één — de laatste. Dit regeltje maakt zichtbaar dat er een
                    voorgeschiedenis is, zodat een afwijkend bedrag navraagbaar blijft. */}
                {row.earlierCount > 0 && (
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {row.earlierCount} eerdere berekening{row.earlierCount === 1 ? '' : 'en'}
                    {row.earlierLatestUtc ? ' · laatste ' + bsFormatDate(row.earlierLatestUtc) : ''}
                  </div>
                )}
              </div>
              <div><div className="text-xs text-slate-500">Status</div><div>{row.isSuccess ? <span className="px-2 py-0.5 rounded-full text-xs bg-lime-500/20 text-lime-300 border border-lime-500/30">OK</span> : <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-300 border border-red-500/30">Gefaald</span>}</div></div>
              <div><div className="text-xs text-slate-500">Resterende periodes</div><div className="text-slate-200">{row.remainingPeriods ?? '—'}</div></div>
              <div><div className="text-xs text-slate-500">Offtake (kWh)</div><div className="text-slate-200">{bsFormatNum(row.offtakeQuantityKWh)}</div></div>
              <div><div className="text-xs text-slate-500">Injection (kWh)</div><div className="text-slate-200">{bsFormatNum(row.injectionQuantityKWh)}</div></div>
              <div><div className="text-xs text-slate-500">Settlement</div><div className="text-slate-200">{bsFormatEuro(row.settlementAmount)}{row.settlementVAT != null ? ` (BTW ${bsFormatEuro(row.settlementVAT)})` : ''}</div></div>
              <div><div className="text-xs text-slate-500">Unreversed prepayment</div><div className="text-slate-200">{bsFormatEuro(row.unreversedPrepaymentAmount)}</div></div>
              <div><div className="text-xs text-slate-500">Future prepayment</div><div className="text-slate-200">{bsFormatEuro(row.futurePrepaymentAmount)}</div></div>
              <div><div className="text-xs text-slate-500">Target</div><div className="text-slate-200">{bsFormatEuro(row.targetAmount)}{row.targetVatIncluded ? ' (incl. BTW)' : ''}</div></div>
              <div><div className="text-xs text-slate-500">Proposed prepayment</div><div className="text-slate-200">{bsFormatEuro(row.proposedPrepaymentAmount)}{row.proposedPrepaymentVatIncluded ? ' (incl. BTW)' : ''}</div></div>
              <div><div className="text-xs text-slate-500">Bill shock</div><div className="text-slate-200 font-semibold">{bsFormatEuro(row.billShockAmount)}{row.billShockVatIncluded ? ' (incl. BTW)' : ''}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-500">Deviation</div><div className="text-slate-200">{bsFormatPct(row.billshockDeviationPercentage)}</div></div>
            </div>
            {row.calculationError && <div className="mt-3 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-xs">{row.calculationError}</div>}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">Settlement details ({settleCount})</h4>
            {detailLoading ? <p className="text-sm text-slate-500">Detailregels laden…</p> : detailError ? <p className="text-sm text-red-400">Detailregels konden niet geladen worden: {detailError}</p> : settle.length === 0 ? <p className="text-sm text-slate-500">Geen settlement-regels.</p> : (
              <div className="overflow-x-auto border border-slate-700/50 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-700/40">
                    <tr>
                      <th className="text-left px-2 py-1.5 text-slate-300">Periode</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Artikel</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Omschrijving</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">Aantal</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Unit</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">Prijs</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">Bedrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settle.map(s => (
                      <tr key={s.id} className="border-t border-slate-700/40">
                        <td className="px-2 py-1.5 text-slate-300 whitespace-nowrap">{bsFormatDate(s.fromUtc)} → {bsFormatDate(s.toUtc)}</td>
                        <td className="px-2 py-1.5 text-slate-400">{s.invoiceArticleId ?? '—'}</td>
                        <td className="px-2 py-1.5 text-slate-300 truncate max-w-[200px]" title={s.description}>{s.description || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-300">{bsFormatNum(s.quantity)}</td>
                        <td className="px-2 py-1.5 text-slate-400">{s.unit || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-300">{s.price != null ? Number(s.price).toFixed(6) : '—'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-200 font-medium">{bsFormatEuro(s.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">Prepayment details ({prepayCount})</h4>
            {detailLoading ? <p className="text-sm text-slate-500">Detailregels laden…</p> : detailError ? <p className="text-sm text-red-400">Detailregels konden niet geladen worden: {detailError}</p> : prepay.length === 0 ? <p className="text-sm text-slate-500">Geen prepayment-regels.</p> : (
              <div className="overflow-x-auto border border-slate-700/50 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-700/40">
                    <tr>
                      <th className="text-left px-2 py-1.5 text-slate-300">Periode</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Invoice ID</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">Bedrag</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">BTW</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prepay.map(p => (
                      <tr key={p.id} className="border-t border-slate-700/40">
                        <td className="px-2 py-1.5 text-slate-300 whitespace-nowrap">{bsFormatDate(p.fromUTC)} → {bsFormatDate(p.toUTC)}</td>
                        <td className="px-2 py-1.5 text-slate-400">{p.prepaymentInvoiceId ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-200">{bsFormatEuro(p.amount)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-400">{bsFormatEuro(p.vat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const BillShockPage = ({ token, currentUser, salesStructure = EMPTY_STRUCTURE, onOpenCustomer360 }) => {
  const orphSort = useTableSort();
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [data, setData] = useState({ items: [], orphans: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [verkoperFilter, setVerkoperFilter] = useState('all');
  const [successFilter, setSuccessFilter] = useState('all');
  // Kenmerken van het aansluitpunt (staan ook als badges onder elke EAN).
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [marketFilter, setMarketFilter] = useState('all');
  const [meterMethodFilter, setMeterMethodFilter] = useState('all');
  // Default cascade-selectie op de scope van de ingelogde gebruiker (master =
  // eigen energielabel, teamleader = +team, reseller = +verkoper; admin/ET =
  // alles). Herhaalt bij user-wissel (impersonate) zodra /api/auth/me de
  // scope-velden levert.
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setCompanyFilter(d.company);
    setVerkoopteamFilter(d.team);
    setVerkoperFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  const [searchTerm, setSearchTerm] = useState('');
  // Standaard op klantnaam (zie BS_SORT_DEFAULT): een klant met meerdere
  // aansluitingen staat zo in één blok in plaats van verspreid over de hele lijst.
  const [sortConfig, setSortConfig] = useState(BS_SORT_DEFAULT);
  // Top 10-selectie: 'none' | 'pay' (grootste positieve afwijking — klant moet
  // bijbetalen) | 'receive' (grootste negatieve — klant krijgt geld terug). Dit is
  // een filter OP de tabel, niet langer een apart blok erboven.
  const [topFilter, setTopFilter] = useState('none');
  // Welke tien van de ranglijst je ziet: 0 = 1–10, 1 = 11–20, … De vroegere Top 10
  // stopte hard bij tien, terwijl de elfde vaak net zo goed een telefoontje waard is.
  const [topPagina, setTopPagina] = useState(0);
  const [rankBy, setRankBy] = useState('deviation'); // 'deviation' | 'amount' — waarop de Top rangschikt
  // Waarop de top gerangschikt wordt: op de losse berekening, of op het NETTO van
  // de klant (al zijn berekeningen samen). Dat tweede beantwoordt de vraag waar het
  // om gaat — heeft deze klant te veel of te weinig betaald — terwijl 'record' de
  // grootste losse uitschieters toont, ook als die intern gecompenseerd worden.
  // Weergave van de tabel: één regel per AANSLUITING (de berekeningen zoals Lynx ze
  // levert) of één regel per KLANT (alles binnen de filters opgeteld). Die tweede
  // beantwoordt "moet deze klant per saldo bijbetalen" zonder dat je zelf moet
  // optellen, en is even goed exporteerbaar. Alle filters gelden in beide standen —
  // de aggregatie draait op exact dezelfde gefilterde set.
  const [weergave, setWeergave] = useState('klant'); // 'klant' | 'ean' — standaard per klant (de klant is de eenheid van een bill shock)
  // Productgroep is een VINKJESFILTER (meerdere tegelijk): een bill shock hangt aan
  // het verkochte product, en je wilt vaak twee of drie producten samen bekijken.
  // Eén keuze per keer zou dat onmogelijk maken. asMultiFilter vangt bewaarde
  // filters en favorieten van vóór deze filter op.
  const [productGroepFilters, setProductGroepFilters] = useState([]);
  // Months: één keuze uit de looptijden die in de data voorkomen. Zo licht je in
  // één klik alle berekeningen over bv. 12 maanden uit de lijst — precies de
  // vergelijking die zonder deze filter met het oog gemaakt moest worden.
  const [monthsFilter, setMonthsFilter] = useState('all');
  // Afrekening vanaf / tot: een bereik van afrekeningsmaanden ('JJJJ-MM' of 'all').
  // De afrekening valt in de maand waarin de periode afloopt. Zo zie je in twee
  // klikken alleen de klanten die pas vanaf januari een afrekening krijgen (en
  // dus nog bij te sturen zijn), of juist wie deze en volgende maand aan de beurt
  // is. Een berekening zonder einddatum valt af zodra een van beide grenzen staat.
  const [afrekeningVanaf, setAfrekeningVanaf] = useState('all');
  const [afrekeningTot, setAfrekeningTot] = useState('all');
  // CP Status staat standaard op "owned" en niet op "alle": een bill shock van een
  // aansluiting die we niet meer leveren (ext, draft, int_out) is geen actie meer,
  // en die maakten hier het grootste deel van de ruis uit. Wie ze tóch wil zien,
  // zet de filter op Alle — hij staat zichtbaar in de balk, dus de beperking is
  // nooit stil. "Wissen" brengt hem terug op owned, want dat ís de standaardstand.
  const [cpStatusFilter, setCpStatusFilter] = useState(BS_CP_STATUS_DEFAULT);
  // Min. Customer Bill Shock (gevraagd 28-10-2026): alleen klanten die per saldo
  // minstens dit bedrag moeten bijbetalen. Bewaard als TEKST zoals getypt
  // ("150,50"), zodat het veld niet onder je vingers herschrijft; de rekenregel
  // leest er een getal uit (bsLeesBedrag) en leeg/onzin = geen filter.
  const [minKlantBedrag, setMinKlantBedrag] = useState('');
  const minKlantBedragGetal = bsLeesBedrag(minKlantBedrag);
  // Min. Customer Deviation (%): hetzelfde, maar op de afwijking per klant (netto
  // gedeeld door de som van de afrekeningen) — de "Deviation %" van de klantweergave.
  const [minKlantDeviation, setMinKlantDeviation] = useState('');
  const minKlantDeviationGetal = bsLeesBedrag(minKlantDeviation);
  useFilterPersistence('billshock.filters', currentUser,
    { companyFilter, verkoopteamFilter, verkoperFilter, successFilter, segmentFilter, marketFilter, meterMethodFilter, monthsFilter, afrekeningVanaf, afrekeningTot, cpStatusFilter, minKlantBedrag, minKlantDeviation, productGroepFilters, searchTerm, topFilter, rankBy, weergave, sortConfig },
    (s) => { setCompanyFilter(s.companyFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); setSuccessFilter(s.successFilter ?? 'all'); setSegmentFilter(s.segmentFilter ?? 'all'); setMarketFilter(s.marketFilter ?? 'all'); setMeterMethodFilter(s.meterMethodFilter ?? 'all'); setProductGroepFilters(asMultiFilter(s.productGroepFilters, s.productGroepFilter)); setMonthsFilter(s.monthsFilter ?? 'all'); setAfrekeningVanaf(s.afrekeningVanaf ?? 'all'); setAfrekeningTot(s.afrekeningTot ?? 'all'); setCpStatusFilter(s.cpStatusFilter ?? BS_CP_STATUS_DEFAULT); setMinKlantBedrag(s.minKlantBedrag ?? ''); setMinKlantDeviation(s.minKlantDeviation ?? ''); setSearchTerm(s.searchTerm ?? ''); setTopFilter(s.topFilter ?? 'none'); setRankBy(s.rankBy ?? 'deviation'); setWeergave(s.weergave ?? 'klant'); if (s.sortConfig && s.sortConfig.key) setSortConfig(s.sortConfig); });
  const [selected, setSelected] = useState(null);
  const [showOrphans, setShowOrphans] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = TABLE_PAGE_SIZE;
  const isAdmin = currentUser?.role === 'admin';

  const load = useMemo(() => async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      // Cursor-gepagineerd: de volledige set komt binnen, maar in brokken van
      // PAGE_LIMIT records. Eén antwoord van tientallen MB's kwam op een trage of
      // haperende lijn afgekapt aan en liet de pagina met een JSON-parsefout leeg.
      // De server scopet vóór het pagineren, dus doorvragen kan nooit records
      // buiten je rechten opleveren. `isOrphan` splitst de twee secties.
      const alle = await fetchPaginated('/api/billshock', token);
      // Periodelengte in maanden meteen op het record zetten i.p.v. bij elke render
      // opnieuw: de kolom, de sortering, de filter én de export gebruiken hetzelfde
      // getal, en zo kan het nooit uiteenlopen.
      // Idem voor de afrekeningsmaand (de maand waarin de periode afloopt, Belgische
      // dag): de filter "Afrekening vanaf/tot" en de grafieken lezen hetzelfde veld.
      const metMaanden = alle.map(r => ({ ...r, periodeMaanden: maandenInPeriode(r.fromUtc, r.toUtc), afrekeningMaand: maandVanDatum(r.toUtc) }));
      setData({
        items: metMaanden.filter(r => !r.isOrphan),
        orphans: metMaanden.filter(r => r.isOrphan),
        total: metMaanden.length,
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // De jongste berekeningsdatum in de geladen set: het bronsysteem rekent
  // wekelijks en de job ververst in het weekend, dus aan deze datum zie je meteen
  // of de pagina bij is (25-09-2026: de snapshot bleek maanden oud te kunnen zijn
  // zonder dat iets dat verried).
  const nieuwsteBerekening = useMemo(() => {
    let max = '';
    for (const r of [...data.items, ...data.orphans]) { const c = String(r.calculationUtc || ''); if (c > max) max = c; }
    return max ? new Date(max).toLocaleDateString('nl-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
  }, [data]);

  // Reset paginatie als filters of sortering wijzigen — anders blijf je op pagina 5 hangen
  // terwijl er na een filter nog maar 2 pagina's data is.
  useEffect(() => { setCurrentPage(1); }, [companyFilter, verkoopteamFilter, verkoperFilter, successFilter, segmentFilter, marketFilter, meterMethodFilter, monthsFilter, afrekeningVanaf, afrekeningTot, cpStatusFilter, minKlantBedragGetal, minKlantDeviationGetal, productGroepFilters, searchTerm, topFilter, rankBy, sortConfig.key, sortConfig.dir]);

  // Cascade-filter dropdowns uit de centrale sales-structuur (identiek in alle
  // modules, rol-gescopet door de backend).
  const { companies, teams: verkoopteams, sellers: verkopers } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter),
    [salesStructure, companyFilter, verkoopteamFilter]
  );

  const handleCompanyChange = (v) => { setCompanyFilter(v); setVerkoopteamFilter('all'); setVerkoperFilter('all'); };
  const handleVerkoopteamChange = (v) => { setVerkoopteamFilter(v); setVerkoperFilter('all'); };
  const clearFilters = () => { setCompanyFilter('all'); setVerkoopteamFilter('all'); setVerkoperFilter('all'); setSuccessFilter('all'); setSegmentFilter('all'); setMarketFilter('all'); setMeterMethodFilter('all'); setProductGroepFilters([]); setMonthsFilter('all'); setAfrekeningVanaf('all'); setAfrekeningTot('all'); setCpStatusFilter(BS_CP_STATUS_DEFAULT); setMinKlantBedrag(''); setMinKlantDeviation(''); setSearchTerm(''); setTopFilter('none'); };
  const hasActiveFilters = companyFilter !== 'all' || verkoopteamFilter !== 'all' || verkoperFilter !== 'all' || successFilter !== 'all' || segmentFilter !== 'all' || marketFilter !== 'all' || meterMethodFilter !== 'all' || productGroepFilters.length > 0 || monthsFilter !== 'all' || afrekeningVanaf !== 'all' || afrekeningTot !== 'all' || cpStatusFilter !== BS_CP_STATUS_DEFAULT || minKlantBedrag !== '' || minKlantDeviation !== '' || searchTerm || topFilter !== 'none';

  // Favoriete filtercombinaties — zelfde mechaniek als op Orders, Invoices en
  // Helpdesk, inclusief het energielabel (zie favorietScope).
  const filterSnapshot = {
    companyFilter,
    verkoopteamFilter, verkoperFilter, successFilter, segmentFilter, marketFilter,
    meterMethodFilter, productGroepFilters, monthsFilter, afrekeningVanaf, afrekeningTot, cpStatusFilter, minKlantBedrag, minKlantDeviation, searchTerm, topFilter, rankBy, sortConfig,
  };
  const applyFavorite = (f) => {
    const scope = favorietScope(salesStructure, { company: f.companyFilter, team: f.verkoopteamFilter, seller: f.verkoperFilter });
    setCompanyFilter(scope.company);
    setVerkoopteamFilter(scope.team);
    setVerkoperFilter(scope.seller);
    setSuccessFilter(f.successFilter ?? 'all');
    setSegmentFilter(f.segmentFilter ?? 'all');
    setMarketFilter(f.marketFilter ?? 'all');
    setMeterMethodFilter(f.meterMethodFilter ?? 'all');
    setProductGroepFilters(asMultiFilter(f.productGroepFilters, f.productGroepFilter));
    setMonthsFilter(f.monthsFilter ?? 'all');
    setAfrekeningVanaf(f.afrekeningVanaf ?? 'all');
    setAfrekeningTot(f.afrekeningTot ?? 'all');
    setCpStatusFilter(f.cpStatusFilter ?? BS_CP_STATUS_DEFAULT);
    setMinKlantBedrag(f.minKlantBedrag ?? '');
    setMinKlantDeviation(f.minKlantDeviation ?? '');
    setSearchTerm(f.searchTerm ?? '');
    setTopFilter(f.topFilter ?? 'none');
    setRankBy(f.rankBy ?? 'deviation'); setWeergave(f.weergave ?? 'klant');
    if (f.sortConfig && f.sortConfig.key) setSortConfig(f.sortConfig);
    setCurrentPage(1);
  };

  // De SCOPE-selectie (energielabel → verkoopteam → verkoper) bakent af over welke
  // klanten je kijkt. Alles wat daarna komt — de keuzelijsten hieronder én de
  // gefilterde tabel — vertrekt daarom van deze verzameling, zodat een productgroep
  // van een ánder energielabel nergens meer opduikt.
  const scopedItems = useMemo(() => {
    let rows = data.items;
    if (companyFilter !== 'all') rows = rows.filter(r => r.company === companyFilter);
    if (verkoopteamFilter !== 'all') rows = rows.filter(r => r.salespersonTeam === verkoopteamFilter);
    if (verkoperFilter !== 'all') rows = rows.filter(r => r.salesperson === verkoperFilter);
    return rows;
  }, [data.items, companyFilter, verkoopteamFilter, verkoperFilter]);

  // Keuzelijsten uit de records binnen die scope, zodat er nooit een optie in staat
  // die niets oplevert. De overige filters (segment, markt, meter, maanden,
  // CP-status, productgroep) tellen hier bewust NIET mee: die zijn nevengeschikt en
  // zouden elkaars opties uit de lijst laten verdwijnen zodra je er één aanzet.
  const { segmentOpties, marktOpties, meterOpties, productGroepOpties, maandOpties, afrekeningOpties, cpStatusOpties } = useMemo(() => {
    const s = new Set(), m = new Set(), mm = new Set(), pg = new Set(), mnd = new Set(), afr = new Set(), cps = new Set();
    // CP-status "owned" is de standaardstand van de filter en moet dus altijd te
    // kiezen zijn, ook in een scope waar (nog) geen enkele owned-aansluiting zit —
    // anders staat er een lege keuzelijst tegenover een actieve beperking.
    cps.add(BS_CP_STATUS_DEFAULT);
    for (const r of scopedItems) {
      if (r.segment) s.add(r.segment);
      const mk = bsMarket(r); if (mk) m.add(mk);
      if (r.meterMethod) mm.add(r.meterMethod);
      // productGroep = het VERKOCHTE product ("Flexi", "Dynamisch Pro", …). Niet
      // energyProductGroup: dat is het energietype (grijs/groen) en staat al als
      // badge onder de EAN.
      if (r.productGroep) pg.add(r.productGroep);
      if (r.periodeMaanden != null) mnd.add(r.periodeMaanden);
      if (r.afrekeningMaand) afr.add(r.afrekeningMaand);
      if (r.connectionPointStatus) cps.add(r.connectionPointStatus);
    }
    const sorteer = (set) => [...set].sort((a, b) => String(a).localeCompare(String(b)));
    // Maanden numeriek sorteren: als tekst zou 10 vóór 2 komen te staan.
    // Afrekeningsmaanden zijn 'JJJJ-MM' en sorteren als tekst dus wél chronologisch.
    return { segmentOpties: sorteer(s), marktOpties: sorteer(m), meterOpties: sorteer(mm), productGroepOpties: sorteer(pg), maandOpties: [...mnd].sort((a, b) => a - b), afrekeningOpties: sorteer(afr), cpStatusOpties: sorteer(cps) };
  }, [scopedItems]);

  // Alleen de toekomst is te filteren (keuze gebruiker 26-10-2026): "vanaf" begint
  // bij DEZE maand, "tot" bij VOLGENDE maand. Een afrekening die al voorbij is,
  // vraagt geen actie meer, dus eerdere maanden staan niet in de lijsten. Een
  // gekozen maand die (in deze scope) geen afrekening heeft, blijft wél staan: de
  // grens blijft gelden, en een select zonder passende optie zou stil de eerste
  // optie tonen terwijl er gefilterd wordt. "Alle" staat als 'all' bovenaan.
  const bsHuidigeMaand = maandVanDatum(new Date().toISOString());
  const metGekozen = (lijst, gekozen) => ((gekozen !== 'all' && !lijst.includes(gekozen)) ? [...lijst, gekozen].sort() : lijst);
  const afrekeningVanafOpties = metGekozen(afrekeningOpties.filter(m => m >= bsHuidigeMaand), afrekeningVanaf);
  // "Tot" telt niet mee (platformregel, src/datumFilter.js): "tot maart" = tot en
  // met februari. De lijst bevat daarom de maand NA elke afrekeningsmaand, zodat
  // ook de laatste afrekeningsmaand nog in te sluiten is.
  const afrekeningTotOpties = metGekozen(afrekeningOpties.map(maandNa).filter(m => m > bsHuidigeMaand), afrekeningTot);
  // Een bewaarde of gedeelde grens in het verleden (vorige sessie, favoriet van
  // vorige maand) valt onder dezelfde regel: terug naar "Alle".
  useEffect(() => {
    if (afrekeningVanaf !== 'all' && afrekeningVanaf < bsHuidigeMaand) setAfrekeningVanaf('all');
    if (afrekeningTot !== 'all' && afrekeningTot <= bsHuidigeMaand) setAfrekeningTot('all');
  }, [afrekeningVanaf, afrekeningTot, bsHuidigeMaand]);

  // Wissel je van energielabel, team of verkoper, dan kan een gekozen waarde uit de
  // keuzelijst verdwijnen. Blijft die staan, dan filtert ze door op iets wat niet
  // meer te zien is: een lege tabel zonder zichtbare oorzaak. Zet zo'n keuze daarom
  // terug op "Alle" (CP-status op zijn eigen standaard).
  //
  // Enkel opruimen zolang er records IN deze scope zitten. Zonder die grens zou
  // dit toeslaan op het moment dat de pagina nog aan het laden is (lege lijst →
  // lege keuzelijsten) en je zojuist herstelde filters meteen wissen; en bij een
  // energielabel zonder bill shocks laat het je selectie liever staan, want de
  // tabel is daar toch al leeg om een zichtbare reden.
  useEffect(() => {
    if (scopedItems.length === 0) return;
    if (segmentFilter !== 'all' && !segmentOpties.includes(segmentFilter)) setSegmentFilter('all');
    if (marketFilter !== 'all' && !marktOpties.includes(marketFilter)) setMarketFilter('all');
    if (meterMethodFilter !== 'all' && !meterOpties.includes(meterMethodFilter)) setMeterMethodFilter('all');
    if (monthsFilter !== 'all' && !maandOpties.some(m => String(m) === monthsFilter)) setMonthsFilter('all');
    if (cpStatusFilter !== 'all' && !cpStatusOpties.some(c => c.toLowerCase() === cpStatusFilter.toLowerCase())) setCpStatusFilter(BS_CP_STATUS_DEFAULT);
    const geldigeProducten = productGroepFilters.filter(p => productGroepOpties.includes(p));
    if (geldigeProducten.length !== productGroepFilters.length) setProductGroepFilters(geldigeProducten);
  }, [scopedItems, segmentOpties, marktOpties, meterOpties, maandOpties, cpStatusOpties, productGroepOpties,
    segmentFilter, marketFilter, meterMethodFilter, monthsFilter, cpStatusFilter, productGroepFilters]);

  const filtered = useMemo(() => {
    let rows = scopedItems;
    if (successFilter === 'ja') rows = rows.filter(r => r.isSuccess === true);
    if (successFilter === 'nee') rows = rows.filter(r => r.isSuccess === false);
    if (segmentFilter !== 'all') rows = rows.filter(r => r.segment === segmentFilter);
    if (marketFilter !== 'all') rows = rows.filter(r => bsMarket(r) === marketFilter);
    if (meterMethodFilter !== 'all') rows = rows.filter(r => r.meterMethod === meterMethodFilter);
    // Leeg = geen filter. Staat er wél iets aangevinkt, dan vallen records ZONDER
    // productgroep af: die horen bij geen enkel aangevinkt product.
    if (productGroepFilters.length) rows = rows.filter(r => productGroepFilters.includes(r.productGroep));
    if (monthsFilter !== 'all') rows = rows.filter(r => String(r.periodeMaanden) === monthsFilter);
    // Bereik van afrekeningsmaanden: "vanaf" inbegrepen, "tot" niet. 'JJJJ-MM' vergelijkt als
    // tekst chronologisch. Zonder einddatum is er geen maand → valt af.
    if (afrekeningVanaf !== 'all') rows = rows.filter(r => r.afrekeningMaand && r.afrekeningMaand >= afrekeningVanaf);
    if (afrekeningTot !== 'all') rows = rows.filter(r => r.afrekeningMaand && r.afrekeningMaand < afrekeningTot);
    // Ongevoelig voor hoofdletters: Lynx levert 'owned', maar een bewaarde filter of
    // een gedeelde favoriet kan 'Owned' bevatten.
    if (cpStatusFilter !== 'all') rows = rows.filter(r => (r.connectionPointStatus || '').toLowerCase() === cpStatusFilter.toLowerCase());
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      rows = rows.filter(r =>
        r.connectionPointGSRN?.toLowerCase().includes(t) ||
        r.customer?.toLowerCase().includes(t) ||
        r.customerCompanyName?.toLowerCase().includes(t) ||
        r.customerVat?.toLowerCase().includes(t) ||
        r.customerReference?.toLowerCase().includes(t) ||
        r.projectConnectionPoint?.projectReference?.toLowerCase().includes(t) ||
        r.salesperson?.toLowerCase().includes(t)
      );
    }
    // Min. Customer Bill Shock komt NA alle andere filters: het netto per klant
    // wordt berekend over precies de rijen die hierboven overbleven, net als de
    // kolom "Customer Bill Shock" zelf. Een klant blijft of valt met al zijn rijen
    // (zie filterMinimumKlantBedrag), dus de totalen eronder blijven kloppen.
    rows = bsFilterMinimumKlantBedrag(rows, minKlantBedragGetal, customerPrimaryName);
    // Volgorde maakt niet uit: beide filters laten hele klanten staan of vallen,
    // dus het netto en de afwijking van wie blijft veranderen er niet door.
    rows = bsFilterMinimumKlantDeviation(rows, minKlantDeviationGetal, customerPrimaryName);
    // Sorteren zit in src/billshockSort.js: de kolom "Klant" sorteert op de naam
    // die er ook echt staat, en onder elke sortering blijven de rijen van dezelfde
    // klant bij elkaar.
    // Sorteren op "Customer Bill Shock" gaat via een accessor: die waarde staat niet
    // op de rij maar is het totaal van de klant over deze gefilterde set. De
    // opzoektabel wordt hier ter plaatse gebouwd, want ze hoort bij precies déze
    // selectie — een totaal uit een andere filterstand zou naast de tabel staan.
    const totalen = bsKlantTotalen(rows, customerPrimaryName);
    return sorteerBillShock(rows, sortConfig, customerPrimaryName, {
      totKlant: (r) => totalen.get(bsKlantSleutel(r, customerPrimaryName)) ?? null,
    });
  }, [scopedItems, successFilter, segmentFilter, marketFilter, meterMethodFilter, productGroepFilters, monthsFilter, afrekeningVanaf, afrekeningTot, cpStatusFilter, minKlantBedragGetal, minKlantDeviationGetal, searchTerm, sortConfig]);

  // Top 10 grootste afwijkingen — nu een SELECTIE op dezelfde tabel in plaats van
  // twee aparte blokken erboven. Werkt op de gefilterde set, zodat scope en filters
  // doorwerken. `rankBy` kiest het veld (deviation % of bill shock €). Records met
  // null/NaN doen niet mee, en records met |billShockAmount| < BS_MIN_TOP_AMOUNT_EUR
  // vallen altijd af — anders domineert een bedrag van een paar euro met een
  // percentage van duizenden procenten de hele lijst.
  //
  // De rangschikking OVERSCHRIJFT de kolomsortering: wie "Top 10" kiest wil de
  // grootste bovenaan, niet de eerste klant uit het alfabet. Zonder die regel zou
  // de standaardsortering (klantnaam) de volgorde van de tien meteen door elkaar
  // gooien.
  const rankField = rankBy === 'amount' ? 'billShockAmount' : 'billshockDeviationPercentage';

  // ── Weergave per klant ──────────────────────────────────────────────────────
  // Aggregeert dezelfde GEFILTERDE set tot één regel per klant (logica + tests in
  // src/billshockSort.js). Staat los van de Top: is er ook een Top actief, dan
  // rangschikt die de klantregels op netto of op de herberekende deviation, en
  // blader je er per twintig doorheen.
  const klantAggregaat = useMemo(
    () => (weergave === 'klant' ? bsAggregeerPerKlant(filtered, customerPrimaryName) : []),
    [weergave, filtered]
  );
  const klantRijen = useMemo(() => {
    if (weergave !== 'klant') return [];
    // Zonder Top: gewoon de kolomsortering, met de klantnaam als tweede sleutel.
    if (topFilter === 'none') return sorteerBillShock(klantAggregaat, sortConfig, (r) => r.klant || '');
    const positief = topFilter === 'pay';
    return klantAggregaat
      .filter(k => {
        const v = Number(k[rankField]);
        if (!Number.isFinite(v)) return false;
        if (positief ? v <= 0 : v >= 0) return false;
        return Math.abs(Number(k.billShockAmount) || 0) >= BS_MIN_TOP_AMOUNT_EUR;
      })
      .sort((a, b) => positief ? Number(b[rankField]) - Number(a[rankField]) : Number(a[rankField]) - Number(b[rankField]));
  }, [weergave, klantAggregaat, topFilter, rankField, sortConfig]);
  const klantRijenLengte = klantRijen.length;
  // De VOLLEDIGE ranglijst achter een Top-selectie, als GROEPEN per klant: de
  // berekening die de klant in de top bracht, plus al zijn andere berekeningen.
  // Logica + tests in src/billshockSort.js.
  const topRanglijst = useMemo(() => bouwTopGroepen(filtered, {
    richting: topFilter, veld: rankField, niveau: weergave === 'klant' ? 'klant' : 'record',
    minBedrag: BS_MIN_TOP_AMOUNT_EUR, klantNaam: customerPrimaryName,
  }), [filtered, topFilter, rankField, weergave]);
  // Aantal top-bladzijden: in de klant-weergave telt de lijst met klantregels, in
  // de EAN-weergave de groepen. Beide bladeren per BS_TOP_SIZE.
  const topLengte = weergave === 'klant' ? klantRijenLengte : topRanglijst.length;
  const topMaxPagina = Math.max(0, Math.ceil(topLengte / BS_TOP_SIZE) - 1);
  // Verandert er iets aan de selectie (andere filters, andere rangschikking), dan
  // slaat "de volgende twintig" nergens meer op: terug naar het begin.
  useEffect(() => { setTopPagina(0); }, [topFilter, rankBy, weergave, filtered]);
  // De groepen van deze bladzijde, plat geslagen tot tabelrijen met een `lead`-vlag.
  const topRijen = useMemo(() => {
    if (topFilter === 'none') return [];
    const start = Math.min(topPagina, topMaxPagina) * BS_TOP_SIZE;
    return groepenNaarRijen(topRanglijst.slice(start, start + BS_TOP_SIZE), customerPrimaryName);
  }, [topFilter, topRanglijst, topPagina, topMaxPagina]);
  // Totaal per klant over de GEFILTERDE set, voor de kolom "Customer Bill Shock" in
  // de weergave per aansluiting: naast het bedrag van deze ene berekening staat wat
  // de klant per saldo moet betalen of terugkrijgt. Verandert een filter, dan
  // verandert het totaal mee — het beschrijft altijd dezelfde selectie als de tabel.
  const klantTotaalMap = useMemo(() => bsKlantTotalen(filtered, customerPrimaryName), [filtered]);
  const totaalVoorKlant = (r) => klantTotaalMap.get(bsKlantSleutel(r, customerPrimaryName));
  // De klant-weergave heeft maar één zulke kolom: het bedrag IS daar al het
  // klanttotaal, dus draagt die kolom zelf de naam "Customer Bill Shock". Vandaar
  // één kolom minder, en een colSpan die meebeweegt.
  const kolomAantal = weergave === 'klant' ? 19 : 20;

  // Bij een Top blijft de bladering per BS_TOP_SIZE lopen, net als in de EAN-weergave.
  const klantZichtbaar = useMemo(() => {
    if (weergave !== 'klant') return [];
    if (topFilter === 'none') return klantRijen;
    const maxPag = Math.max(0, Math.ceil(klantRijen.length / BS_TOP_SIZE) - 1);
    const start = Math.min(topPagina, maxPag) * BS_TOP_SIZE;
    return klantRijen.slice(start, start + BS_TOP_SIZE);
  }, [weergave, klantRijen, topFilter, topPagina]);

  const zichtbaar = useMemo(
    () => (weergave === 'klant' ? klantZichtbaar : (topFilter === 'none' ? filtered : topRijen.map(x => x.r))),
    [weergave, klantZichtbaar, filtered, topFilter, topRijen]
  );

  // KPI's berekend op de ZICHTBARE set (zelfde conventie als Orders/Sales: de
  // kengetallen beschrijven wat de tabel toont, dus ook een actieve Top 10).
  const kpis = useMemo(() => {
    const total = zichtbaar.length;
    // Een klantregel (weergave per klant) draagt de tellingen van haar
    // berekeningen; een gewone rij telt zichzelf.
    const ok = zichtbaar.reduce((s, r) => s + (r.aantalSuccesvol ?? (r.isSuccess === true ? 1 : 0)), 0);
    const failed = zichtbaar.reduce((s, r) => s + (r.aantalGefaald ?? (r.isSuccess === false ? 1 : 0)), 0);
    const sumShock = zichtbaar.reduce((s, r) => s + (Number(r.billShockAmount) || 0), 0);
    const devs = zichtbaar.map(r => Number(r.billshockDeviationPercentage)).filter(v => !Number.isNaN(v));
    const avgDev = devs.length ? devs.reduce((a, b) => a + b, 0) / devs.length : null;
    const bedragen = zichtbaar.map(r => Number(r.billShockAmount)).filter(v => !Number.isNaN(v));
    const avgShock = bedragen.length ? bedragen.reduce((a, b) => a + b, 0) / bedragen.length : null;
    return { total, ok, failed, sumShock, avgDev, avgShock };
  }, [zichtbaar]);

  // ── Afrekeningen per maand ──────────────────────────────────────────────────
  // Het einde van de periode is de afrekening: dan wordt het verschil écht
  // betaald of terugbetaald. Per eindmaand: wat klanten samen nog bijbetalen en
  // wat er terug moet (rekenregels + tests in src/billshockMaanden.js). Draait
  // op de GEFILTERDE set en volgt de weergave: per klant wordt eerst genet, per
  // aansluiting telt elke berekening apart. Een Top-selectie rangschikt de tabel
  // maar is geen filter, dus de grafiek blijft de hele selectie tonen.
  const [bsMaandWeergave, setBsMaandWeergave] = useState('grafiek'); // 'grafiek' | 'tabel'
  const afrekeningen = useMemo(
    () => perAfrekeningsmaand(filtered, { niveau: weergave === 'klant' ? 'klant' : 'record', klantNaam: customerPrimaryName }),
    [filtered, weergave]
  );
  // Cumulatief: dezelfde maanden, maar elke maand telt de maanden ervoor mee —
  // hoeveel bill shock er in totaal verrekend is zodra die maand voorbij is. Eigen
  // Grafiek/Tabel-stand, want je wilt de ene vaak als tabel naast de andere als grafiek.
  const [bsCumWeergave, setBsCumWeergave] = useState('grafiek'); // 'grafiek' | 'tabel'
  const afrekeningCumulatief = useMemo(() => cumulatief(afrekeningen.reeks), [afrekeningen]);
  const bsMaandEenheid = weergave === 'klant' ? 'klanten' : 'berekeningen';
  const afrekeningTotaal = useMemo(() => afrekeningen.reeks.reduce((t, m) => ({
    betalen: t.betalen + m.betalen, ontvangen: t.ontvangen + m.ontvangen, netto: t.netto + m.netto,
    aantalBetalen: t.aantalBetalen + m.aantalBetalen, aantalOntvangen: t.aantalOntvangen + m.aantalOntvangen,
  }), { betalen: 0, ontvangen: 0, netto: 0, aantalBetalen: 0, aantalOntvangen: 0 }), [afrekeningen]);

  // Paginatie op de zichtbare set
  // Bij een Top-selectie doet de tabel zelf NIET aan paginatie: de bladzijde is dan
  // al "twintig klanten", en de tabelpaginatie zou een klant middenin doorknippen —
  // dan mis je net de berekening die zijn netto verklaart. Bladeren gaat via de
  // Vorige/Volgende 20-knoppen in de kop.
  const topActief = topFilter !== 'none';
  const totalPages = topActief ? 1 : Math.max(1, Math.ceil(zichtbaar.length / pageSize));
  const pageStart = topActief ? 0 : (currentPage - 1) * pageSize;
  const pageRows = topActief ? zichtbaar : zichtbaar.slice(pageStart, pageStart + pageSize);
  // Wat de tabel rendert: bij een top de groepen (met een gekleurde kopregel en een
  // netto-afsluiter per klant), anders gewoon de rijen van deze pagina.
  const tabelRijen = useMemo(() => {
    if (!topActief) return pageRows.map(r => ({ r, lead: false, groep: null, laatsteVanGroep: false }));
    return topRijen.map((x, i) => ({
      ...x,
      laatsteVanGroep: i === topRijen.length - 1 || topRijen[i + 1].groep !== x.groep,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topActief, topRijen, pageRows]);

  // Excel-compatibele CSV-export van de gefilterde set (niet alleen de huidige
  // pagina). BOM + puntkomma + komma-decimaal zodat Excel-NL het correct opent.
  //
  // TWEE HARDE AFSPRAKEN, allebei zichtbaar in de kolomlijsten hieronder:
  //  1. De export bevat EXACT de kolommen die de tabel toont, in DEZELFDE volgorde.
  //     Velden die enkel in de detailmodal staan (settlement, target, un-/future
  //     prepayment, status, calculation error, InvoiceID, workflow) horen hier
  //     niet: wie de export opent verwacht de lijst die hij zag. Eén bewuste
  //     uitzondering (gevraagd 28-10-2026): "Project ref" als ALLERLAATSTE kolom,
  //     want daarmee wordt een rij in het bronsysteem teruggevonden. Achteraan,
  //     zodat geen enkele bestaande kolom opschuift.
  //  2. Staat er informatie in een cel zonder eigen kop — de badges onder de EAN,
  //     de contactpersoon onder de klantnaam, het klantnummer, de tweede regel van
  //     "Aansluitingen" — dan krijgt die een eigen kolom op de plaats van die cel.
  //     Ze staat op het scherm, dus ze hoort in de export; en één datumbereik of
  //     twee waarden in één cel is in Excel niet te filteren.
  //
  // Bestandsnaam volgt de platformvorm uit src/exportNaam.js:
  //   <tijdstempel>_<energielabel>[-team][-verkoper]_<basis>.csv
  // 'all' in een filter betekent voor een teamleader/reseller niet "alles" — die
  // ziet sowieso enkel zijn eigen scope. Zonder deze terugval zou hun bestand naar
  // het hele energielabel heten terwijl er één team in zit (zie Revenue).
  const [bsEffTeam, bsEffVerkoper] = useMemo(() => {
    const eigen = defaultScopeSelection(currentUser);
    return [
      verkoopteamFilter !== 'all' ? verkoopteamFilter : (eigen.team !== 'all' ? eigen.team : ''),
      verkoperFilter !== 'all' ? verkoperFilter : (eigen.seller !== 'all' ? eigen.seller : ''),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verkoopteamFilter, verkoperFilter, currentUser?.role, currentUser?.salesTeam, currentUser?.sellerName]);

  const bsExportNaam = (basis) => exportBestandsnaam({
    stamp: exportTimestamp(),
    label: companyFilter === 'all' ? '' : companyFilter,
    scope: exportScopeStaart([bsEffTeam, bsEffVerkoper]),
    basis,
  });

  const csvTxt = (v) => (v == null ? '' : String(v).replace(/[\r\n;]/g, ' '));
  const csvNum = (v) => (v == null || Number.isNaN(Number(v)) ? '' : String(Number(v)).replace('.', ','));
  // Gemeten kWh gaat op hele kWh de export in, gelijk aan wat de kolom toont.
  // Zonder dit zou je in Excel alsnog 1.234,5678 zien terwijl het scherm 1.235
  // toont — dan lijkt de export nauwkeuriger dan hij bedoeld is.
  const csvKwh = (v) => (v == null || Number.isNaN(Number(v)) ? '' : String(Math.round(Number(v))));
  // Zie bsFormatPct: de waarde is al een percentage, niet nog eens ×100.
  const csvPct = (v) => (v == null || Number.isNaN(Number(v)) ? '' : Number(v).toFixed(2).replace('.', ','));

  // Kop én cel komen uit DEZELFDE lijst. Met een aparte headers-array naast een
  // aparte waarden-array schuift vroeg of laat één kolom op en staan alle cellen
  // erna onder de verkeerde kop — in Excel zie je dat niet, want elke cel bevat
  // nog steeds iets plausibels. Eén kolom toevoegen of verplaatsen is nu één regel.
  const leverBillShockCsv = (basis, kolommen, rijen) => {
    const csv = '﻿' + [
      kolommen.map(k => k.kop).join(';'),
      ...rijen.map(r => kolommen.map(k => k.lees(r)).join(';')),
    ].join('\r\n');
    downloadBlob(bsExportNaam(basis), new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  };

  // ── Kolommen van de klant-weergave, in schermvolgorde ────────────────────────
  // "Periode" en "Voorschotperiode" staan er niet in: die verschillen per
  // berekening en tonen op klantniveau altijd een streepje. Een altijd lege kolom
  // is in een export enkel ruis.
  const BS_KOLOMMEN_KLANT = [
    { kop: 'Laatste berekening', lees: k => bsFormatDate(k.laatsteBerekening) },
    { kop: 'Aansluitingen', lees: k => csvNum(k.aantalAansluitingen) },
    { kop: 'Berekeningen', lees: k => csvNum(k.aantalBerekeningen) },
    { kop: 'Klant', lees: k => csvTxt(k.klant) },
    { kop: 'Klantnr.', lees: k => csvTextCell(k.customerReference) },
    { kop: 'Customer Bill Shock', lees: k => csvNum(k.billShockAmount) },
    { kop: 'Proposed prepayment', lees: k => csvNum(k.proposedPrepaymentAmount) },
    { kop: 'Deviation %', lees: k => csvPct(k.billshockDeviationPercentage) },
    { kop: 'Months', lees: k => csvNum(k.periodeMaanden) },
    { kop: 'Calc Offtake in period (kWh)', lees: k => csvKwh(k.offtakeQuantityKWh) },
    { kop: 'Est. Offtake (kWh per Year)', lees: k => csvNum(k.estOfftakeKwh) },
    { kop: 'Calc Injection in period (kWh)', lees: k => csvKwh(k.injectionQuantityKWh) },
    { kop: 'Est. Injection (kWh per Year)', lees: k => csvNum(k.estInjectionKwh) },
    { kop: 'Productgroep', lees: k => csvTxt(k.productGroep) },
    { kop: 'CP Status', lees: k => csvTxt(k.connectionPointStatus) },
    { kop: 'Voorschotbedrag (Excl. BTW)', lees: k => csvNum(k.lastPrepaymentAmount) },
    { kop: 'Energielabel', lees: k => csvTxt(k.company) },
    { kop: 'Verkoopteam', lees: k => csvTxt(k.salespersonTeam) },
    { kop: 'Verkoper', lees: k => csvTxt(k.salesperson) },
    // Laatste kolom (zie afspraak 1 hierboven). Per klant alle unieke referenties
    // van zijn aansluitingen, gescheiden door " | " — de puntkomma is het
    // veldscheidingsteken en zou de kolommen erna laten opschuiven.
    { kop: 'Project ref', lees: k => csvTxt((k.projectRefs || []).join(' | ')) },
  ];

  // ── Kolommen van de weergave per aansluiting, in schermvolgorde ──────────────
  const BS_KOLOMMEN_EAN = [
    { kop: 'Berekend', lees: r => bsFormatDate(r.calculationUtc) },
    { kop: 'EAN', lees: r => csvTextCell(r.connectionPointGSRN) },
    // De drie badges onder de EAN, in de volgorde waarin ze op het scherm staan.
    // Energietype als ELEK/GAS: dezelfde tekst als de badge, niet de ruwe code.
    { kop: 'Segment', lees: r => csvTxt((r.segment || '').toUpperCase()) },
    { kop: 'Energietype', lees: r => csvTxt(bsMarket(r) ? (String(bsMarket(r)).toUpperCase() === 'NG' ? 'GAS' : 'ELEK') : '') },
    { kop: 'Metermethode', lees: r => csvTxt(r.meterMethod) },
    // 'Klant' = wat de tabel toont (bedrijfsnaam bij zakelijk, persoon bij B2C),
    // met daarnaast de contactpersoon die eronder staat — enkel wanneer hij ook
    // echt getoond wordt, zodat de export de tabel niet overtreft.
    { kop: 'Klant', lees: r => csvTxt(customerPrimaryName(r)) },
    { kop: 'Contactpersoon', lees: r => csvTxt(bsContactpersoon(r)) },
    { kop: 'Bill shock', lees: r => csvNum(r.billShockAmount) },
    { kop: 'Customer Bill Shock', lees: r => csvNum(totaalVoorKlant(r)) },
    { kop: 'Proposed prepayment', lees: r => csvNum(r.proposedPrepaymentAmount) },
    { kop: 'Deviation %', lees: r => csvPct(r.billshockDeviationPercentage) },
    // Eén datumbereik in één cel is in Excel niet te filteren of te sorteren,
    // vandaar twee kolommen op de plaats van die ene schermkolom.
    { kop: 'Periode van', lees: r => bsFormatDate(r.fromUtc) },
    { kop: 'Periode tot', lees: r => bsFormatDate(r.toUtc) },
    { kop: 'Months', lees: r => csvNum(r.periodeMaanden) },
    { kop: 'Calc Offtake in period (kWh)', lees: r => csvKwh(r.offtakeQuantityKWh) },
    { kop: 'Est. Offtake (kWh per Year)', lees: r => csvNum(r.estOfftakeKwh) },
    { kop: 'Calc Injection in period (kWh)', lees: r => csvKwh(r.injectionQuantityKWh) },
    { kop: 'Est. Injection (kWh per Year)', lees: r => csvNum(r.estInjectionKwh) },
    { kop: 'Productgroep', lees: r => csvTxt(r.productGroep) },
    { kop: 'CP Status', lees: r => csvTxt(r.connectionPointStatus) },
    { kop: 'Voorschotperiode van', lees: r => bsFormatDate(r.lastPrepaymentFromUtc) },
    { kop: 'Voorschotperiode tot', lees: r => bsFormatDate(r.lastPrepaymentToUtc) },
    { kop: 'Voorschotbedrag (Excl. BTW)', lees: r => csvNum(r.lastPrepaymentAmount) },
    { kop: 'Energielabel', lees: r => csvTxt(r.company) },
    { kop: 'Verkoopteam', lees: r => csvTxt(r.salespersonTeam) },
    { kop: 'Verkoper', lees: r => csvTxt(r.salesperson) },
    // Laatste kolom (zie afspraak 1 hierboven): de projectreferentie van deze
    // aansluiting, bv. PROJP2Y/2026/07/560-13289.
    { kop: 'Project ref', lees: r => csvTxt(r.projectConnectionPoint?.projectReference) },
  ];

  // Export = wat de tabel toont (platformconventie), dus inclusief een actieve Top.
  const exportToCsv = () => (weergave === 'klant'
    ? leverBillShockCsv('billshock-per-klant', BS_KOLOMMEN_KLANT, zichtbaar)
    : leverBillShockCsv('billshock-per-aansluiting', BS_KOLOMMEN_EAN, zichtbaar));

  const toggleSort = (key) => setSortConfig(p => p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  return (
    <div className="space-y-4">
      {/* Het kengetal-blok "Berekeningen" is vervallen: succesvol/gefaald is één
          regel informatie en verdiende geen tegel van 100 px hoog. Het staat nu als
          kleine tekst onder de titel, waar het meteen bij het openen van het scherm
          staat — en het telt de ZICHTBARE set, dus het volgt de filters. */}
      <PageHeader title="Bill Shock" subtitle={`${data.total} aansluitingen, laatste berekening${isAdmin && data.orphans.length ? ` (${data.orphans.length} buiten orders-cache)` : ''} · ${loading && !zichtbaar.length ? 'berekeningen worden geladen…' : `${formatNumberRounded(kpis.ok)} berekeningen succesvol, ${formatNumberRounded(kpis.failed)} gefaald`}${nieuwsteBerekening ? ` · gegevens tot ${nieuwsteBerekening}` : ''}`}>
        <ExportKnop onExport={exportToCsv} disabled={zichtbaar.length === 0} count={zichtbaar.length} label="Exporteer alle zichtbare records naar Excel" />
        <button onClick={load} disabled={loading} className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg ${loading ? 'bg-slate-700 text-slate-400 cursor-wait' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}>
          {loading ? <><div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />Laden…</> : <><Activity size={14} />Vernieuwen</>}
        </button>
      </PageHeader>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}

      <Card className="!p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-[106px] sm:w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Energieleverancier</label>
            <select value={companyFilter} onChange={e => handleCompanyChange(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${companyFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={companies}>Alle</AllOption>
              {companies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {/* Favoriete filtercombinaties: enkel het sterretje, op dezelfde plek als
              op Orders/Invoices/Helpdesk — naast de energieleverancier, uitgelijnd
              op de onderkant van de velden ernaast. */}
          <div className="self-end"><FilterFavorites pageKey="billshock" filters={filterSnapshot} onApply={applyFavorite} /></div>
          <div className="w-[106px] sm:w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Verkoopteam</label>
            <select value={verkoopteamFilter} onChange={e => handleVerkoopteamChange(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={verkoopteams}>Alle</AllOption>
              {verkoopteams.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="w-[106px] sm:w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Verkoper</label>
            <select value={verkoperFilter} onChange={e => { setVerkoperFilter(e.target.value); }} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoperFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={verkopers}>Alle</AllOption>
              {verkopers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="w-full sm:w-[280px] shrink-0">
            <label className="block text-xs text-slate-400 mb-1">Zoeken</label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2 text-slate-500" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="EAN, klant, project, verkoper…" className="w-full pl-7 pr-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" />
            </div>
          </div>
        </div>

        {/* TWEEDE REGEL. De bovenste regel is "wie": energielabel, team, verkoper,
            en de zoekbalk die daar het snelst bij helpt. Hier staat "wat": de
            kenmerken van de aansluiting en van de berekening zelf. Twee regels in
            plaats van één lange rij, zodat de zoekbalk niet halverwege een reeks
            dropdowns verdwijnt. */}
        <div className="mt-3 flex flex-wrap items-end gap-3">
          {/* Market: samen met energielabel/team/verkoper de grove indeling waarop
              je de lijst opdeelt. Het heette "Energietype"; dezelfde filter, nu met
              de naam die de rest van het platform gebruikt. */}
          <div className="w-[106px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">Market</label>
            <select value={marketFilter} onChange={e => setMarketFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${marketFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={marktOpties}>Alle</AllOption>
              {marktOpties.map(m => <option key={m} value={m}>{bsMarketLabel(m)}</option>)}
            </select>
          </div>
          <div className="w-[100px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">Segment</label>
            <select value={segmentFilter} onChange={e => setSegmentFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${segmentFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={segmentOpties}>Alle</AllOption>
              {segmentOpties.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
          <div className="w-[106px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">Metermethode</label>
            <select value={meterMethodFilter} onChange={e => setMeterMethodFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${meterMethodFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={meterOpties}>Alle</AllOption>
              {meterOpties.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {/* Months hoort bij de metermethode-kant van de balk: allebei kenmerken
              van wat er gemeten is, niet van wie de klant is. De lijst bevat enkel
              looptijden die echt voorkomen, numeriek gesorteerd. */}
          <div className="w-[106px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">Months</label>
            <select value={monthsFilter} onChange={e => setMonthsFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${monthsFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              {maandOpties.map(m => <option key={m} value={String(m)}>{m}</option>)}
            </select>
          </div>
          {/* Afrekening vanaf / tot: de maand waarin de periode afloopt, dus wanneer
              de klant zijn afrekening krijgt. Twee grenzen — "vanaf" inbegrepen,
              "tot" niet (platformregel, src/datumFilter.js) — zodat "pas vanaf
              januari" én "deze en volgende maand" allebei in twee klikken kunnen.
              Alleen toekomstige maanden (zie afrekeningVanafOpties).
              Dezelfde maandnaam als op de as van de grafieken. */}
          <div className="w-[106px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">Afrekening vanaf</label>
            <select value={afrekeningVanaf} onChange={e => setAfrekeningVanaf(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${afrekeningVanaf !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              {afrekeningVanafOpties.map(m => <option key={m} value={m}>{bsMaandLabel(m)}</option>)}
            </select>
          </div>
          <div className="w-[106px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">Afrekening tot</label>
            <select value={afrekeningTot} onChange={e => setAfrekeningTot(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${afrekeningTot !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              {afrekeningTotOpties.map(m => <option key={m} value={m}>{bsMaandLabel(m)}</option>)}
            </select>
          </div>
          {/* Min. Customer Bill Shock: een vrij bedrag, geen keuzelijst — welk
              minimum zinvol is hangt van de actie af (bellen vanaf € 100, een
              betaalplan vanaf € 500). Tekstveld met decimaal toetsenbord i.p.v.
              type="number": dat weigert de Belgische komma. Het (i)-knopje legt
              uit dat het om het NETTO per klant gaat, want dat is niet het bedrag
              dat in de rij "Bill shock" staat. */}
          <div className="w-[106px] sm:w-[184px]">
            <label className="flex items-center gap-1 text-xs text-slate-400 mb-1">
              Min. Customer Bill Shock
              <InfoTip title="Min. Customer Bill Shock (€)" text={'Toont alleen klanten die per saldo minstens dit bedrag moeten bijbetalen.\n\nHet gaat om de "Customer Bill Shock": het netto van al hun berekeningen binnen de huidige filters, dus na aftrek van wat ze op andere aansluitingen terugkrijgen. Een klant blijft met al zijn aansluitingen staan of valt in zijn geheel weg, in beide weergaven. Klanten die geld terugkrijgen vallen altijd weg.\n\nLeeg = geen filter. Decimale komma mag: 150,50.'} />
            </label>
            <input type="text" inputMode="decimal" value={minKlantBedrag} onChange={e => setMinKlantBedrag(e.target.value)} placeholder="€ bv. 100"
              className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs placeholder:text-slate-500 ${minKlantBedragGetal != null ? 'border-lime-500 text-lime-300' : (minKlantBedrag !== '' ? 'border-red-500/60 text-slate-200' : 'border-slate-600 text-slate-200')}`} />
          </div>
          <div className="w-[106px] sm:w-[184px]">
            <label className="flex items-center gap-1 text-xs text-slate-400 mb-1">
              Min. Customer Deviation
              <InfoTip title="Min. Customer Deviation (%)" text={'Toont alleen klanten van wie de afwijking per saldo minstens dit percentage is.\n\nHet gaat om de "Deviation %" van de klant: zijn netto bill shock gedeeld door de som van zijn afrekeningen, over alle berekeningen binnen de huidige filters. Een klant blijft met al zijn aansluitingen staan of valt in zijn geheel weg, in beide weergaven. Klanten die geld terugkrijgen (negatieve afwijking) of zonder afrekeningsbedrag vallen altijd weg.\n\nLeeg = geen filter. Decimale komma mag: 12,5.'} />
            </label>
            <input type="text" inputMode="decimal" value={minKlantDeviation} onChange={e => setMinKlantDeviation(e.target.value)} placeholder="% bv. 20"
              className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs placeholder:text-slate-500 ${minKlantDeviationGetal != null ? 'border-lime-500 text-lime-300' : (minKlantDeviation !== '' ? 'border-red-500/60 text-slate-200' : 'border-slate-600 text-slate-200')}`} />
          </div>
          {/* CP Status staat standaard op "owned": alleen aansluitingen die we nu
              leveren. Een bill shock op een aansluiting die weg is (ext, draft,
              int_out) vraagt geen actie meer. De filter staat zichtbaar in de balk,
              dus die beperking is nooit stil — en "Alle" zet ze in één klik uit. */}
          <div className="w-[106px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">CP Status</label>
            <select value={cpStatusFilter} onChange={e => setCpStatusFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${cpStatusFilter !== BS_CP_STATUS_DEFAULT ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              {cpStatusOpties.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {/* Vinkjes i.p.v. één keuze: je wilt vaak meerdere productgroepen samen
              zien. Zelfde component als de vinkjesfilters op Orders, dus ook hier
              zoeken, "alles selecteren" en "alles wissen". */}
          <div className="flex"><SearchableMultiSelect label="Productgroep" values={productGroepFilters} onChange={setProductGroepFilters} options={productGroepOpties} placeholder="Alle" /></div>
          {/* Status staat achteraan: het is een kenmerk van de BEREKENING, niet van
              de klant of de aansluiting waarop je de lijst opdeelt. */}
          <div className="w-[106px] sm:w-[132px]">
            <label className="block text-xs text-slate-400 mb-1">Status</label>
            <select value={successFilter} onChange={e => setSuccessFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${successFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              <option value="ja">Succesvol</option>
              <option value="nee">Gefaald</option>
            </select>
          </div>
          {/* `tag-fail` i.p.v. bg-red-900/30 + text-red-300: die combinatie werd op
              een witte achtergrond lichtrode tekst op roze — onleesbaar. */}
          {hasActiveFilters && <button onClick={clearFilters} className="tag tag-fail px-2 py-1.5 text-xs"><X size={12} />Wissen</button>}
        </div>

        {/* Onderste rij, bewust ANDERS vormgegeven dan de dropdowns erboven: dit zijn
            geen gewone waardefilters maar twee snelkeuzes die de tabel terugbrengen
            tot de tien grootste afwijkingen — de vroegere Top 10-blokken, nu in de
            tabel zelf. Rood = de klant moet bijbetalen, groen = de klant krijgt geld
            terug; dezelfde kleurcodering als in de bedragen. */}
        <div className="mt-3 pt-3 border-t border-slate-700/50 flex flex-wrap items-center gap-2">
          {/* Weergave staat VOORAAN: ze bepaalt wat een regel betekent (een
              berekening of een klant), terwijl de knoppen ernaast bepalen welke
              regels je ziet. Beide standen draaien op dezelfde gefilterde set. */}
          <div className="flex bg-slate-700/40 border border-slate-700 rounded-lg p-0.5 text-xs mr-1" title="Toon elke berekening apart, of alles per klant opgeteld binnen de huidige filters.">
            <button onClick={() => setWeergave('klant')} className={`px-3 py-1 rounded-md font-medium ${weergave === 'klant' ? 'bg-lime-500 text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}>Per klant</button>
            <button onClick={() => setWeergave('ean')} className={`px-3 py-1 rounded-md font-medium ${weergave === 'ean' ? 'bg-lime-500 text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}>Per aansluiting</button>
          </div>
          <button onClick={() => setTopFilter(t => t === 'pay' ? 'none' : 'pay')}
            title="Toon de klanten met de grootste POSITIEVE afwijking — die moeten bijbetalen. Onder elke klant staan al zijn andere berekeningen. Per twintig klanten te doorbladeren."
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${topFilter === 'pay' ? 'bg-red-500 border-red-500 text-white' : 'bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/20'}`}>
            <TrendingUp size={13} />Top {BS_TOP_SIZE} BETALEN
          </button>
          <button onClick={() => setTopFilter(t => t === 'receive' ? 'none' : 'receive')}
            title="Toon de klanten met de grootste NEGATIEVE afwijking — die krijgen geld terug. Onder elke klant staan al zijn andere berekeningen. Per twintig klanten te doorbladeren."
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${topFilter === 'receive' ? 'bg-lime-500 border-lime-500 text-slate-900' : 'bg-lime-500/10 border-lime-500/40 text-lime-300 hover:bg-lime-500/20'}`}>
            <TrendingUp size={13} className="rotate-180" />Top {BS_TOP_SIZE} ONTVANGEN
          </button>
          {/* Waarop de twintig gerangschikt worden. Alleen zinvol zolang er een Top
              actief is, dus dan pas tonen — anders staat er een knop die niets doet. */}
          {topFilter !== 'none' && <>
            <div className="flex bg-slate-700/40 border border-slate-700 rounded-lg p-0.5 text-xs ml-1">
              <button onClick={() => setRankBy('deviation')} className={`px-3 py-1 rounded-md font-medium ${rankBy === 'deviation' ? 'bg-lime-500 text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}>Deviation %</button>
              <button onClick={() => setRankBy('amount')} className={`px-3 py-1 rounded-md font-medium ${rankBy === 'amount' ? 'bg-lime-500 text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}>Bill shock €</button>
            </div>
            {/* Verder dan de eerste twintig. De eenentwintigste grootste afwijking is
                vaak net zo goed een telefoontje waard, en die was onbereikbaar
                zonder de Top helemaal uit te zetten. */}
            <div className="flex items-center gap-1">
              <button onClick={() => setTopPagina(n => Math.max(0, n - 1))} disabled={topPagina === 0}
                title="Vorige twintig klanten"
                className="px-2 py-1 rounded-lg text-xs bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed text-slate-200 inline-flex items-center gap-1"><ChevronLeft size={13} />Vorige {BS_TOP_SIZE}</button>
              <button onClick={() => setTopPagina(n => Math.min(topMaxPagina, n + 1))} disabled={topPagina >= topMaxPagina}
                title="Volgende twintig klanten uit dezelfde ranglijst"
                className="px-2 py-1 rounded-lg text-xs bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed text-slate-200 inline-flex items-center gap-1">Volgende {BS_TOP_SIZE}<ChevronRight size={13} /></button>
            </div>
            <span className="text-xs text-slate-500">
              {/* Bewust de POSITIE in de ranglijst, niet alleen een aantal: "21–40 van
                  128" zegt meteen dat je niet meer naar de allergrootste kijkt. */}
              {topLengte === 0 ? '0 klanten' : `${topPagina * BS_TOP_SIZE + 1}–${Math.min((topPagina + 1) * BS_TOP_SIZE, topLengte)} van ${topLengte} klanten`} · gerangschikt op {weergave === 'klant' ? 'het netto per klant' : 'de losse berekening'} ({rankBy === 'amount' ? 'bill shock €' : 'deviation %'}){weergave === 'klant' ? '' : ' · gekleurde regel = de aanleiding, grijze regels = de overige berekeningen van dezelfde klant'} · onder {bsFormatEuro(BS_MIN_TOP_AMOUNT_EUR)} telt niet mee
            </span>
          </>}
        </div>
      </Card>

      {/* Kengetallen ONDER de filters: ze slaan op de zichtbare set, dus de filters
          horen erboven — anders lees je de cijfers voor je weet waarover ze gaan.
          "Totaal records" staat al in de tabelvoet; "Berekeningen" (succesvol/
          gefaald) is verhuisd naar de regel onder de paginatitel. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Rood bij een positieve som (klanten moeten samen bijbetalen), groen bij
            een negatieve (er moet geld terug) — dezelfde codering als de Top 10. */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
          <div className="text-xs text-slate-500 mb-1">Som bill shock</div>
          <div className={`text-2xl font-bold ${kpis.sumShock > 0 ? 'amt-pos' : kpis.sumShock < 0 ? 'amt-neg' : 'text-slate-100'}`}><Kengetal>{bsFormatEuro0(kpis.sumShock)}</Kengetal></div>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
          <div className="text-xs text-slate-500 mb-1">Gem. afwijking</div>
          {/* Percentage en bedrag naast elkaar: het percentage zegt hoe scheef de
              schatting zat, het bedrag hoe zwaar dat weegt. Het bedrag krijgt
              dezelfde kleurcodering als elders — rood = de klant betaalt bij. */}
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="min-w-0 text-2xl font-bold text-slate-100"><Kengetal>{bsFormatPct0(kpis.avgDev)}</Kengetal></span>
            <span className={`min-w-0 text-2xl font-bold ${kpis.avgShock > 0 ? 'amt-pos' : kpis.avgShock < 0 ? 'amt-neg' : 'text-slate-100'}`}><Kengetal>{bsFormatEuro0(kpis.avgShock)}</Kengetal></span>
          </div>
        </div>
      </div>

      {/* Twee maandgrafieken naast elkaar (onder elkaar op een smal scherm).
          Links per maand waarin de periode afloopt, rechts hetzelfde maar
          opgeteld tot en met die maand. Allebei op de GEFILTERDE set en in de
          weergave van de tabel; een Top-selectie rangschikt de tabel maar is geen
          filter, dus de grafieken blijven de hele selectie tonen. Kaart:
          BsAfrekeningKaart, rekenregels: src/billshockMaanden.js. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <BsAfrekeningKaart
          titel="Afrekeningen per maand"
          ondertitel={<>
            Per maand waarin de periode afloopt: wat klanten samen nog bijbetalen en wat er terug moet, binnen de huidige filters · {weergave === 'klant' ? 'netto per klant' : 'per berekening'}
            {afrekeningen.zonderDatum > 0 && <> · {formatNumberRounded(afrekeningen.zonderDatum)} berekening{afrekeningen.zonderDatum === 1 ? '' : 'en'} zonder einddatum niet meegeteld</>}
          </>}
          reeks={afrekeningen.reeks} totaal={afrekeningTotaal} eenheid={bsMaandEenheid}
          weergave={bsMaandWeergave} onWeergave={setBsMaandWeergave} loading={loading}
        />
        <BsAfrekeningKaart
          titel="Cumulatief per afrekeningsmaand"
          ondertitel={<>Opgeteld tot en met elke maand: wie dan al een afrekening kreeg, telt volledig mee · de laatste maand is het totaal van je selectie</>}
          reeks={afrekeningCumulatief} totaal={null} eenheid={bsMaandEenheid}
          weergave={bsCumWeergave} onWeergave={setBsCumWeergave} loading={loading}
        />
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead>
              <tr className="border-b border-slate-700/50 bg-slate-800/40">
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort(weergave === 'klant' ? 'laatsteBerekening' : 'calculationUtc')}>{weergave === 'klant' ? 'Laatste berekening' : 'Berekend'}</th>
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort(weergave === 'klant' ? 'aantalAansluitingen' : 'connectionPointGSRN')}>{weergave === 'klant' ? 'Aansluitingen' : 'EAN'}</th>
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('customer')}>Klant</th>
                {/* De uitkomst staat vooraan: bedrag en afwijking beantwoorden samen
                    met de klantnaam de enige vraag waarvoor je deze lijst opent —
                    bij wie loopt het mis, en hoe erg. */}
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('billShockAmount')} title={weergave === 'klant' ? 'Totaal van alle bill shocks van deze klant binnen de huidige filters' : undefined}>{weergave === 'klant' ? 'Customer Bill Shock' : 'Bill shock'}</th>
                {/* Customer Bill Shock: het netto van deze KLANT over de hele gefilterde
                    set, herhaald op elke regel. Zo lees je een bijbetaling van
                    € 800 meteen samen met wat de klant per saldo moet betalen.
                    Alleen in de weergave per aansluiting — per klant is de kolom
                    ernaast al het totaal. */}
                {weergave === 'ean' && <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('totKlant')} title="Totaal van alle bill shocks van deze klant binnen de huidige filters">Customer Bill Shock</th>}
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('proposedPrepaymentAmount')}>
                  <span className="inline-flex items-center gap-1 justify-end">
                    Proposed prepayment
                    <span onClick={(e) => e.stopPropagation()}>
                      <InfoTip title="Proposed prepayment (monthly)" text="Dit is het geoptimaliseerde, voorgestelde voorschotbedrag over de nog resterende maanden tot aan de volgende afrekening, om tot het target amount te komen." />
                    </span>
                  </span>
                </th>
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('billshockDeviationPercentage')}>Deviation</th>
                {/* Markt heeft geen eigen kolom: die staat als badge onder de EAN,
                    samen met segment en metermethode. */}
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs">Periode</th>
                {/* Lengte van diezelfde periode in maanden: een bill shock van
                    € 300 betekent iets anders over 12 maanden dan over 2. Sorteerbaar,
                    want "toon mij eerst de lange periodes" is een echte vraag. */}
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('periodeMaanden')}>Months</th>
                {/* Per richting gegroepeerd: eerst wat de berekening over díe periode
                    GEMETEN heeft, dan de jaarschatting uit de order ernaast. Zo lees je
                    het harde cijfer eerst en de referentie erbij, en zie je in één
                    oogopslag of de afwijking uit een verkeerde inschatting komt.
                    Alles in kWh. */}
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('offtakeQuantityKWh')}>Calc Offtake in period (kWh)</th>
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('estOfftakeKwh')}>Est. Offtake (kWh per Year)</th>
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('injectionQuantityKWh')}>Calc Injection in period (kWh)</th>
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('estInjectionKwh')}>Est. Injection (kWh per Year)</th>
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('productGroep')}>Productgroep</th>
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('connectionPointStatus')}>CP Status</th>
                {/* Het laatst AANGEREKENDE voorschot: periode, factuur en bedrag.
                    Bedragen in deze collectie zijn exclusief btw — de btw staat er
                    in de bron als apart veld naast, en die tonen we hier niet. */}
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('lastPrepaymentFromUtc')}>
                  <span className="inline-flex items-center gap-1">
                    Voorschotperiode
                    <span onClick={(e) => e.stopPropagation()}>
                      <InfoTip title="Voorschotperiode" text="De periode van het laatste voorschot dat effectief aangerekend is. De voorschotten die volgens het huidige bedrag nog moeten volgen, dragen nog geen factuur en staan hier dus niet." />
                    </span>
                  </span>
                </th>
                <th className="py-2.5 px-2 text-right text-slate-400 text-xs cursor-pointer whitespace-nowrap" onClick={() => toggleSort('lastPrepaymentAmount')}>Voorschotbedrag (Excl. BTW)</th>
                {/* Scope HELEMAAL achteraan: wie het verkocht heeft pak je er pas bij
                    zodra een bedrag je is opgevallen, dus die drie kolommen mogen de
                    cijfers niet uit elkaar duwen. */}
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('company')}>Energielabel</th>
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs">Verkoopteam</th>
                {/* Verkoper staat apart (niet meer samengeplakt met het team): op deze
                    pagina wordt per verkoper opgevolgd wie zijn klanten moet bellen. */}
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs cursor-pointer" onClick={() => toggleSort('salesperson')}>Verkoper</th>
              </tr>
            </thead>
            <tbody>
              {loading && zichtbaar.length === 0 && (
                <tr><td colSpan={kolomAantal} className="py-8 text-center text-slate-500 text-sm">Laden…</td></tr>
              )}
              {!loading && zichtbaar.length === 0 && (
                <tr><td colSpan={kolomAantal} className="py-8 text-center text-slate-500 text-sm">{topFilter === 'none' ? 'Geen records binnen je filters.' : `Geen records met een ${topFilter === 'pay' ? 'positieve' : 'negatieve'} afwijking van minstens ${bsFormatEuro(BS_MIN_TOP_AMOUNT_EUR)} binnen je filters.`}</td></tr>
              )}
              {/* Weergave "Per klant": dezelfde kolommen, maar opgeteld. De
                  EAN-kolom toont het AANTAL aansluitingen in plaats van een EAN, en
                  kenmerken die per aansluiting verschillen (periode, productgroep,
                  CP-status, voorschotperiode) blijven leeg — een willekeurige van de
                  onderliggende waarden zou een verkeerd beeld geven. */}
              {weergave === 'klant' && pageRows.map(k => (
                <tr key={k.id} onContextMenu={(e) => onC360Context(e, k.klant, klantTarget(k.klant, k.customerReference), { lookup: { ref: k.customerReference }, label: k.klant }, {})}
                  className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-context-menu">
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(k.laatsteBerekening)}</td>
                  <td className="py-2 px-2 text-xs">
                    <div className="text-slate-300">{bsFormatNum(k.aantalAansluitingen)} EAN{k.aantalAansluitingen === 1 ? '' : "'s"}</div>
                    <div className="text-[10px] text-slate-500">{bsFormatNum(k.aantalBerekeningen)} berekening{k.aantalBerekeningen === 1 ? '' : 'en'}</div>
                  </td>
                  <td className="py-2 px-2 text-xs">
                    <div className="text-slate-200 truncate max-w-[180px]" title={k.klant}>{k.klant || '—'}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{k.customerReference}</div>
                  </td>
                  <td className={`py-2 px-2 text-right text-xs font-semibold whitespace-nowrap ${k.billShockAmount > 0 ? 'text-red-300' : k.billShockAmount < 0 ? 'text-lime-300' : 'text-slate-100'}`}>{bsFormatEuro(k.billShockAmount)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(k.proposedPrepaymentAmount)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs">{bsFormatPct(k.billshockDeviationPercentage)}</td>
                  {/* Periode en looptijd verschillen per berekening → leeg. */}
                  <td className="py-2 px-2 text-slate-600 text-xs">—</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs">{k.periodeMaanden ?? <span className="text-slate-600">—</span>}</td>
                  {/* Dezelfde component als de EAN-weergave, zodat de vier kWh-kolommen
                      hier nooit in een andere volgorde kunnen komen te staan. */}
                  <BsKwhCells row={k} />
                  <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[130px]" title={k.productGroep || ''}>{k.productGroep || <span className="text-slate-600">—</span>}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs">{k.connectionPointStatus || <span className="text-slate-600">—</span>}</td>
                  <td className="py-2 px-2 text-slate-600 text-xs">—</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(k.lastPrepaymentAmount)}</td>
                  <td className="py-2 px-2 text-slate-300 text-xs">{k.company || <span className="text-slate-600">—</span>}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[140px]" title={k.salespersonTeam || ''}>{k.salespersonTeam || <span className="text-slate-600">—</span>}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[140px]" title={k.salesperson || ''}>{k.salesperson || <span className="text-slate-600">—</span>}</td>
                </tr>
              ))}
              {weergave === 'ean' && tabelRijen.map(({ r, lead, groep, laatsteVanGroep }) => (
                <Fragment key={`${groep ? groep.sleutel + ':' : ''}${r.id}`}>
                <tr onClick={() => setSelected(r)} onContextMenu={(e) => onC360Context(e, r.connectionPointGSRN, { type: 'gsrn', value: r.connectionPointGSRN, label: r.connectionPointGSRN }, { lookup: { gsrn: r.connectionPointGSRN, ref: r.customerReference }, label: customerPrimaryName(r) || r.connectionPointGSRN }, orderEtLinks(r))}
                  className={`border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer ${lead ? (topFilter === 'pay' ? 'bs-top-betalen' : 'bs-top-ontvangen') : ''}`}>
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(r.calculationUtc)}</td>
                  <td className="py-2 px-2 text-xs">
                    <div className="text-slate-300 font-mono">{r.connectionPointGSRN || '—'}</div>
                    <BsMeta row={r} />
                  </td>
                  <td className="py-2 px-2 text-xs"><BsCustomerName row={r} className="max-w-[180px]" /></td>
                  <td className="py-2 px-2 text-right text-slate-100 text-xs font-medium whitespace-nowrap">{bsFormatEuro(r.billShockAmount)}</td>
                  {/* Het netto van de klant, herhaald op elke regel van die klant.
                      Rood = per saldo bijbetalen, groen = terugkrijgen. Bewust
                      lichter gezet dan het bedrag ernaast: het beschrijft de klant,
                      niet deze berekening. */}
                  <td className={`py-2 px-2 text-right text-xs whitespace-nowrap ${totaalVoorKlant(r) > 0 ? 'text-red-400/80' : totaalVoorKlant(r) < 0 ? 'text-lime-400/80' : 'text-slate-400'}`}>{bsFormatEuro(totaalVoorKlant(r))}</td>
                  {/* Neutraal gekleurd: dit is geen afwijking maar een voorstel. */}
                  <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(r.proposedPrepaymentAmount)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs">{bsFormatPct(r.billshockDeviationPercentage)}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(r.fromUtc)} → {bsFormatDate(r.toUtc)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs">{r.periodeMaanden ?? <span className="text-slate-500">—</span>}</td>
                  <BsKwhCells row={r} />
                  <BsProductGroupCell row={r} />
                  <BsCpStatusCell row={r} />
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">
                    {r.lastPrepaymentFromUtc ? <>{bsFormatDate(r.lastPrepaymentFromUtc)} → {bsFormatDate(r.lastPrepaymentToUtc)}</> : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{r.lastPrepaymentAmount == null ? <span className="text-slate-500">—</span> : bsFormatEuro(r.lastPrepaymentAmount)}</td>
                  {/* Scope achteraan — zelfde volgorde als de koppen hierboven. */}
                  <td className="py-2 px-2 text-slate-300 text-xs">{r.company || <span className="text-slate-500">—</span>}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[140px]" title={r.salespersonTeam || ''}>{r.salespersonTeam || <span className="text-slate-500">—</span>}</td>
                  <BsSellerCell row={r} />
                </tr>
                {/* Afsluiter per klant: het NETTO over al zijn berekeningen. Dat is
                    de vraag achter deze lijst — moet de klant per saldo bijbetalen
                    of krijgt hij geld terug? Eén groot record zegt dat niet. */}
                {laatsteVanGroep && (
                  <tr className="bs-groep-totaal border-b-2 border-slate-600/60">
                    <td colSpan={kolomAantal} className="py-1.5 px-2 text-xs">
                      <span className="text-slate-400">Netto voor {customerPrimaryName(groep.lead) || '—'}:</span>{' '}
                      <span className={`font-semibold ${groep.netto > 0 ? 'text-red-300' : groep.netto < 0 ? 'text-lime-300' : 'text-slate-300'}`}>{bsFormatEuro(groep.netto)}</span>
                      <span className="text-slate-500">
                        {' '}· {groep.overige.length + 1} berekening{groep.overige.length === 0 ? '' : 'en'}
                        {groep.deviation != null && <> · {bsFormatPct(groep.deviation)} van {bsFormatEuro(groep.settlement)}</>}
                      </span>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {zichtbaar.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 border-t border-slate-700/50 text-xs text-slate-400">
            <span>Toon <span className="text-slate-200">{pageStart + 1}–{Math.min(pageStart + pageSize, zichtbaar.length)}</span> van <span className="text-slate-200">{zichtbaar.length}</span> {weergave === 'klant' ? 'klanten' : 'records'}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)} title="Eerste pagina" className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">«</button>
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">‹ Vorige</button>
                <span className="px-2">Pagina <span className="text-slate-200">{currentPage}</span> / {totalPages}</span>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">Volgende ›</button>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} title="Laatste pagina" className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">»</button>
              </div>
            )}
          </div>
        )}
      </Card>

      {isAdmin && data.orphans.length > 0 && (
        <Card className="border-amber-500/30">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-amber-300 flex items-center gap-2"><AlertCircle size={16} />Onbekende scope ({data.orphans.length})</h3>
              <p className="text-xs text-slate-400 mt-0.5">BillShock-records waarvan de EAN niet in de orders-cache zit — geen company/team/verkoper afleidbaar. Alleen zichtbaar voor admin.</p>
            </div>
            <button onClick={() => setShowOrphans(s => !s)} className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded">{showOrphans ? 'Verbergen' : 'Tonen'}</button>
          </div>
          {showOrphans && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm resp-card">
                <thead>
                  <tr className="border-b border-slate-700/50">
                    <SortHeader label="Berekend" k="calculationUtc" sortConfig={orphSort.sortConfig} onSort={orphSort.handleSort} thClass="py-2 px-2 text-xs" />
                    <SortHeader label="EAN" k="connectionPointGSRN" sortConfig={orphSort.sortConfig} onSort={orphSort.handleSort} thClass="py-2 px-2 text-xs" />
                    <SortHeader label="Project ref" k="projectRef" sortConfig={orphSort.sortConfig} onSort={orphSort.handleSort} thClass="py-2 px-2 text-xs" />
                    <SortHeader label="Markt" k="market" sortConfig={orphSort.sortConfig} onSort={orphSort.handleSort} thClass="py-2 px-2 text-xs" />
                    <SortHeader label="Periode" k="fromUtc" sortConfig={orphSort.sortConfig} onSort={orphSort.handleSort} thClass="py-2 px-2 text-xs" />
                    <SortHeader label="Bill shock" k="billShockAmount" align="right" sortConfig={orphSort.sortConfig} onSort={orphSort.handleSort} thClass="py-2 px-2 text-xs" />
                    <SortHeader label="Status" k="orphStatus" sortConfig={orphSort.sortConfig} onSort={orphSort.handleSort} thClass="py-2 px-2 text-xs" />
                  </tr>
                </thead>
                <tbody>
                  {sortRows(data.orphans, orphSort.sortConfig, { projectRef: r => r.projectConnectionPoint?.projectReference || '', market: r => r.projectConnectionPoint?.market || '', orphStatus: r => (r.isSuccess ? 1 : 0) }).map(r => (
                    <tr key={r.id} onClick={() => setSelected(r)} onContextMenu={(e) => onC360Context(e, r.connectionPointGSRN, { type: 'gsrn', value: r.connectionPointGSRN, label: r.connectionPointGSRN }, undefined, orderEtLinks(r))} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer">
                      <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(r.calculationUtc)}</td>
                      <td className="py-2 px-2 text-slate-300 text-xs font-mono">{r.connectionPointGSRN || '—'}</td>
                      <td className="py-2 px-2 text-slate-400 text-xs">{r.projectConnectionPoint?.projectReference || '—'}</td>
                      <td className="py-2 px-2 text-slate-400 text-xs">{r.projectConnectionPoint?.market || '—'}</td>
                      <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(r.fromUtc)} → {bsFormatDate(r.toUtc)}</td>
                      <td className="py-2 px-2 text-right text-slate-100 text-xs">{bsFormatEuro(r.billShockAmount)}</td>
                      <td className="py-2 px-2 text-xs">{r.isSuccess ? <span className="tag tag-ok px-2 py-0.5">OK</span> : <span className="tag tag-fail px-2 py-0.5">Gefaald</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {c360Menu}
      {selected && <BillShockDetailModal row={selected} token={token} onClose={() => setSelected(null)} />}
    </div>
  );
};

// ─── Klant 360° module ────────────────────────────────────────────────────────
// 360°-view per klant of GSRN. Alle data komt uit reeds-gescopete props:
// `orders` en `facturen` zijn server-side gefilterd op de user's energielabel/
// verkoopteam/verkoper (via /api/orders en /api/invoices), en /api/billshock
// past dezelfde scope toe via GSRN→order lookup. Records buiten scope zitten
// dus niet in deze datasets en zijn voor de huidige gebruiker onzichtbaar.

function c360Norm(s) { return (s || '').trim().toLowerCase(); }

// Notities-editor voor de Debt Collection sectie in Customer 360.
// Lokale state houdt de editbare waarde bij; saven (Enter is geen save — Ctrl+S
// is gangbaar in browsers maar conflict met "Save Page As", dus expliciete knop).
const Customer360DebtNotes = ({ initial, onSave, disabled }) => {
  const [val, setVal] = useState(initial || '');
  // Sync wanneer initial van buiten verandert (bv. na refresh van debtStatus)
  useEffect(() => { setVal(initial || ''); }, [initial]);
  const dirty = val !== (initial || '');
  return (
    <div>
      <textarea
        value={val}
        onChange={e => setVal(e.target.value)}
        maxLength={4000}
        placeholder="Vrije tekst voor deze klant…"
        disabled={disabled}
        className="w-full min-h-[80px] p-2 bg-slate-700 border border-slate-600 rounded text-xs text-slate-100 focus:border-lime-500 focus:outline-none resize-y disabled:opacity-60"
      />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-slate-500">{val.length}/4000</span>
        <button
          onClick={() => onSave(val)}
          disabled={!dirty || disabled}
          className="px-2.5 py-1 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 text-xs font-semibold rounded"
        >Notities opslaan</button>
      </div>
    </div>
  );
};

// Gedeelde notities/logs voor Customer 360 — op klant- (customer_reference) en
// EAN-niveau (GSRN). Iedereen die de klant/EAN binnen zijn rechten opzoekt ziet
// de notities; toevoegen/verwijderen via /api/customer-notes (server scoopt mee).
const CustomerNotes = ({ token, currentUser, gsrns = [], customerName, defaultGsrn }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [custDraft, setCustDraft] = useState('');
  const [eanDraft, setEanDraft] = useState('');
  const [eanTarget, setEanTarget] = useState(defaultGsrn || gsrns[0] || '');
  const [saving, setSaving] = useState(false);
  // Een notitie is een gedeeld log; afgehandelde notities blijven bewaard maar staan
  // standaard niet in de weg. Zelfde drie standen als bij de helpdesk-tickets.
  const [notitieFilter, setNotitieFilter] = useState("open");
  // Toevoegen en beheren gebeurt in een popup, zodat de Samenvatting alleen de
  // openstaande notities toont en verder rustig blijft.
  const [beheerOpen, setBeheerOpen] = useState(false);

  const gsrnsKey = gsrns.join(',');

  // Houd het gekozen EAN geldig wanneer de selectie wisselt.
  useEffect(() => { setEanTarget(defaultGsrn || gsrns[0] || ''); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [defaultGsrn, gsrnsKey]);

  // Notities laden voor de huidige klant (op naam) + GSRN's. Klant-notities
  // sleutelen op klantnaam — die is altijd aanwezig (i.t.t. customer_reference),
  // zodat een klantlog altijd mogelijk is, net als bij EAN.
  useEffect(() => {
    if (!token) return;
    if (!customerName && !gsrnsKey) { setNotes([]); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    const params = new URLSearchParams();
    if (customerName) params.set('customer', customerName);
    if (gsrnsKey) params.set('gsrns', gsrnsKey);
    fetch(`/api/customer-notes?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancelled) setNotes(j.notes || []); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, customerName, gsrnsKey, reload]);

  const addNote = async (scope, refKey, text, clear) => {
    if (!text.trim() || !refKey) return;
    setSaving(true); setError(null);
    try {
      const r = await fetch('/api/customer-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scope, ref_key: refKey, note: text.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      clear();
      setReload(x => x + 1);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const removeNote = async (id) => {
    setError(null);
    try {
      const r = await fetch(`/api/customer-notes/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      setNotes(ns => ns.filter(n => n.id !== id));
    } catch (e) { setError(e.message); }
  };


  // Afsluiten of weer openzetten. Optimistisch: de lijst springt meteen, en bij een
  // fout draaien we terug. Mag door iedereen die de klant mag zien — een notitie
  // wordt meestal door een collega afgehandeld, niet door wie ze schreef.
  const toggleResolved = async (n) => {
    const nieuw = !n.resolved_at;
    setError(null);
    setNotes(ns => ns.map(x => (x.id === n.id
      ? { ...x, resolved_at: nieuw ? new Date().toISOString() : null, resolved_by: nieuw ? { id: currentUser?.id, name: currentUser?.name } : null }
      : x)));
    try {
      const r = await fetch(`/api/customer-notes/${n.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ resolved: nieuw }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const j = await r.json();
      setNotes(ns => ns.map(x => (x.id === n.id ? j.note : x)));
    } catch (e) {
      setError(e.message);
      setNotes(ns => ns.map(x => (x.id === n.id ? n : x)));
    }
  };
  const fmt = (s) => { try { return new Date(s).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };
  const canDelete = (n) => (n.created_by && currentUser && n.created_by.id === currentUser.id) || currentUser?.role === 'admin';
  // Tellingen over ALLE notities (klant + EAN samen), zodat de knoppen in de kop
  // laten zien wat er achter een filter verstopt zit.
  const openCount = notes.filter(n => !n.resolved_at).length;
  const closedCount = notes.length - openCount;
  const pastFilter = (n) => (notitieFilter === 'all' ? true : notitieFilter === 'open' ? !n.resolved_at : !!n.resolved_at);
  const customerNotes = notes.filter(n => n.scope === 'customer' && pastFilter(n));
  const eanNotes = notes.filter(n => n.scope === 'ean' && pastFilter(n));

  const NoteList = ({ items, showGsrn }) => (
    items.length === 0
      ? <p className="text-xs text-slate-500 italic">{notitieFilter === 'open' ? 'Geen openstaande notities.' : notitieFilter === 'closed' ? 'Geen afgesloten notities.' : 'Nog geen notities.'}</p>
      : <ul className="space-y-2">
          {items.map(n => (
            <li key={n.id} className={`border rounded-lg p-2.5 ${n.resolved_at ? 'bg-slate-950/20 border-slate-700/30' : 'bg-slate-950/40 border-slate-700/50'}`}>
              <div className="flex items-start justify-between gap-2">
                <p className={`text-sm whitespace-pre-wrap break-words flex-1 ${n.resolved_at ? 'text-slate-400' : 'text-slate-100'}`}>{n.note}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => toggleResolved(n)} title={n.resolved_at ? 'Weer openzetten' : 'Afsluiten — de notitie blijft bewaard'}
                    className={`shrink-0 w-4 ${n.resolved_at ? 'text-lime-500 hover:text-slate-400' : 'text-slate-600 hover:text-lime-400'}`}>
                    {n.resolved_at ? <RotateCcw size={14} /> : <Check size={14} />}
                  </button>
                  {canDelete(n) && <button onClick={() => removeNote(n.id)} title="Verwijderen" className="shrink-0 w-4 text-slate-600 hover:text-red-400"><Trash2 size={14} /></button>}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500 flex-wrap">
                <span>{n.created_by?.name || 'Onbekend'}</span><span>·</span><span>{fmt(n.created_at)}</span>
                {showGsrn && <><span>·</span><span className="font-mono">{n.ref_key}</span></>}
                {n.resolved_at && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300"><Check size={10} />Afgesloten door {n.resolved_by?.name || 'onbekend'} · {fmt(n.resolved_at)}</span>}
              </div>
            </li>
          ))}
        </ul>
  );

  // Het volledige beheer (filteren, toevoegen op klant- én EAN-niveau) zit in een
  // popup. Op de Samenvatting zelf staat alleen wat er nog OPEN staat, zodat die
  // pagina niet volloopt met invoervelden die je zelden nodig hebt.
  const beheerPaneel = (
    <>
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded text-xs mb-3">{error}</div>}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex bg-slate-800 rounded-lg p-0.5 text-xs">
          {[{ v: 'open', l: `Openstaand (${openCount})` }, { v: 'closed', l: `Afgesloten (${closedCount})` }, { v: 'all', l: `Alle (${notes.length})` }].map(o => (
            <button key={o.v} onClick={() => setNotitieFilter(o.v)} className={`px-2.5 py-1 rounded-md transition-all ${notitieFilter === o.v ? 'bg-lime-500 text-slate-900 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}>{o.l}</button>
          ))}
        </div>
        {loading && <span className="text-[10px] text-slate-500">laden…</span>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Klant-notities */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider"><User size={12} />Klant{customerName ? ` · ${customerName}` : ''}</div>
          <NoteList items={customerNotes} showGsrn={false} />
          <div className="pt-1">
            <textarea value={custDraft} onChange={e => setCustDraft(e.target.value)} maxLength={4000} disabled={!customerName || saving}
              placeholder={customerName ? 'Notitie voor deze klant… bv. "Belangrijke strategische klant"' : 'Geen klant geselecteerd'}
              className="w-full min-h-[60px] p-2 bg-slate-700 border border-slate-600 rounded text-xs text-slate-100 focus:border-lime-500 focus:outline-none resize-y disabled:opacity-50" />
            <div className="flex justify-end mt-1.5">
              <button onClick={() => addNote('customer', customerName, custDraft, () => setCustDraft(''))} disabled={!customerName || !custDraft.trim() || saving}
                className="px-2.5 py-1 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 text-xs font-semibold rounded">Notitie toevoegen</button>
            </div>
          </div>
        </div>
        {/* EAN-notities */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider"><Activity size={12} />EAN / aansluiting</div>
          <NoteList items={eanNotes} showGsrn={true} />
          <div className="pt-1 space-y-1.5">
            {gsrns.length > 1 && (
              <select value={eanTarget} onChange={e => setEanTarget(e.target.value)} disabled={saving}
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded text-xs text-slate-100 focus:border-lime-500 focus:outline-none font-mono disabled:opacity-50">
                {gsrns.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            )}
            <textarea value={eanDraft} onChange={e => setEanDraft(e.target.value)} maxLength={4000} disabled={!eanTarget || saving}
              placeholder={eanTarget ? `Notitie voor EAN ${eanTarget}… bv. "Aansluiting is 100% onbalans"` : 'Geen EAN beschikbaar'}
              className="w-full min-h-[60px] p-2 bg-slate-700 border border-slate-600 rounded text-xs text-slate-100 focus:border-lime-500 focus:outline-none resize-y disabled:opacity-50" />
            <div className="flex items-center justify-between">
              {gsrns.length <= 1 ? <span className="text-[10px] text-slate-500 font-mono">{eanTarget || '—'}</span> : <span />}
              <button onClick={() => addNote('ean', eanTarget, eanDraft, () => setEanDraft(''))} disabled={!eanTarget || !eanDraft.trim() || saving}
                className="px-2.5 py-1 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 text-xs font-semibold rounded">Notitie toevoegen</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Compacte weergave: enkel de openstaande notities, klant en EAN door elkaar in
  // de volgorde waarin ze binnenkwamen (nieuwste eerst). Zonder openstaande
  // notities blijft er één regel over — zoals bij de helpdesk-tickets.
  const openNotes = notes.filter(n => !n.resolved_at);
  return (
    <Card className={openNotes.length === 0 ? '!p-3' : undefined}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-lime-400 uppercase flex items-center gap-2">
          <FileText size={16} />Notities{openNotes.length > 0 ? ` (${openNotes.length})` : ''}
        </h3>
        <div className="flex items-center gap-2">
          {loading && <span className="text-[10px] text-slate-500">laden…</span>}
          {closedCount > 0 && openNotes.length === 0 && <span className="text-[10px] text-slate-500">{closedCount} afgesloten</span>}
          <button onClick={() => { setNotitieFilter('open'); setBeheerOpen(true); }}
            title="Notities toevoegen en beheren"
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 hover:text-lime-300 transition-colors">
            <Plus size={15} />
          </button>
        </div>
      </div>
      {openNotes.length === 0
        ? <p className="text-xs text-slate-500 mt-1.5">Geen openstaande notities.</p>
        : <div className="mt-3"><NoteList items={openNotes} showGsrn /></div>}

      <DocsModal open={beheerOpen} onClose={() => setBeheerOpen(false)}
        title="Notities &amp; logs"
        subtitle={customerName ? `Klant · ${customerName}` : undefined}>
        {beheerPaneel}
      </DocsModal>
    </Card>
  );
};

// ─── Metering-pagina ──────────────────────────────────────────────────────────
// Toont per klant/GSRN de Lynx-meetgegevens. Zoek op e-mail/klant/GSRN (uit de
// gescopete orders); bij keuze van een e-mail verschijnen de klanten (uniek
// klantnummer+naam) en een GSRN-dropdown per gekozen klant. De meet-viewer toont
// het verbruiksprofiel per kwartier (Dag), per dag (Maand) of per maand (Jaar).
const mtrMonthLong = new Intl.DateTimeFormat('nl-BE', { month: 'long' });
const mtrMonthShort = new Intl.DateTimeFormat('nl-BE', { month: 'short' });
const mtrToday = () => new Date().toISOString().substring(0, 10);
// Het ophaalvenster loopt van Brusselse middernacht tot Brusselse middernacht — zie
// localDayWindowUtc in src/metering.js voor waarom een UTC-dag hier fout is.
const mtrLastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const mtrMarket = (market) => /gas|^ng$|aardgas/i.test(market || '') ? 'NG' : 'EL';
// De eenheid komt altijd uit de meting zelf (unitInfo in src/metering.js), nooit uit
// de markt: dezelfde GSRN levert kWh, kW en kVAr door elkaar.

// `jaar` (enkel bij viewMode 'year'): een KALENDERJAAR i.p.v. het rollende
// venster — van 1 januari tot de laatste dag van de anchor-maand (december voor
// een afgesloten jaar, de lopende maand voor het huidige), nooit na vandaag.
function mtrRangeFor(viewMode, anchor, jaar = null) {
  const [y, m] = anchor.split('-').map(Number);
  const today = mtrToday();
  if (viewMode === 'day') return { from: anchor, to: anchor };
  if (viewMode === 'month') {
    const last = `${y}-${String(m).padStart(2, '0')}-${String(mtrLastDayOfMonth(y, m)).padStart(2, '0')}`;
    return { from: `${y}-${String(m).padStart(2, '0')}-01`, to: last > today ? today : last };
  }
  if (jaar) {
    const last = `${y}-${String(m).padStart(2, '0')}-${String(mtrLastDayOfMonth(y, m)).padStart(2, '0')}`;
    return { from: `${y}-01-01`, to: last > today ? today : last };
  }
  // "Jaar" is standaard een ROLLEND venster van twaalf maanden dat eindigt op de
  // maand van de anchor — bewust niet het kalenderjaar. Wie deze pagina opent wil
  // de laatste twaalf maanden zien; in januari zou een kalenderjaar één magere
  // maand tonen en in december elf. Het venster loopt dus bv. van september 2025
  // t.e.m. augustus 2026. Wie wél een kalenderjaar wil, kiest dat in de filterbalk
  // (jaarKeuze) en komt in de tak hierboven terecht.
  const eindMaand = new Date(Date.UTC(y, m, 0));          // laatste dag van de anchor-maand
  const startMaand = new Date(Date.UTC(y, m - 12, 1));    // elf maanden eerder, dag 1
  const last = eindMaand.toISOString().substring(0, 10);
  return { from: startMaand.toISOString().substring(0, 10), to: last > today ? today : last };
}
function mtrShiftAnchor(viewMode, anchor, dir) {
  const [y, m, d] = anchor.split('-').map(Number);
  if (viewMode === 'day') { const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + dir); return dt.toISOString().substring(0, 10); }
  if (viewMode === 'month') { const dt = new Date(Date.UTC(y, m - 1 + dir, 1)); return dt.toISOString().substring(0, 10); }
  return `${y + dir}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
// De periode groot boven de grafiek: dag = "01/06/2026 – Maandag", maand =
// "06/2026", jaar = "2026" (of "2025 – 2026" als het rollende jaarvenster twee
// kalenderjaren beslaat — anders leest "2026" als een kalenderjaar terwijl er
// maanden van 2025 in staan).
function mtrPeriodeKop(viewMode, anchor, range) {
  const [y, m, d] = String(anchor || '').split('-').map(Number);
  if (!y || !m) return '';
  if (viewMode === 'day') {
    const dt = new Date(Date.UTC(y, m - 1, d || 1, 12));
    const dag = new Intl.DateTimeFormat('nl-BE', { weekday: 'long' }).format(dt);
    return `${String(d || 1).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y} – ${dag.charAt(0).toUpperCase()}${dag.slice(1)}`;
  }
  if (viewMode === 'month') return `${String(m).padStart(2, '0')}/${y}`;
  const vanJaar = range && range.from ? Number(String(range.from).substring(0, 4)) : y;
  const totJaar = range && range.to ? Number(String(range.to).substring(0, 4)) : y;
  return vanJaar && totJaar && vanJaar !== totJaar ? `${vanJaar} – ${totJaar}` : String(totJaar || y);
}
function mtrAnchorLabel(viewMode, anchor, jaar = null) {
  const [y, m] = anchor.split('-').map(Number);
  if (viewMode === 'day') { const dt = new Date(Date.UTC(y, m - 1, anchor.split('-')[2], 12)); return new Intl.DateTimeFormat('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(dt); }
  if (viewMode === 'month') { const dt = new Date(Date.UTC(y, m - 1, 1, 12)); const l = mtrMonthLong.format(dt); return `${l.charAt(0).toUpperCase()}${l.slice(1)} ${y}`; }
  // Kalenderjaar: "Kalenderjaar 2025" als het tot december loopt, anders de
  // beschikbare maanden — "jan – sep 2026".
  if (jaar) return m === 12 ? `Kalenderjaar ${y}` : `jan – ${mtrMaandJaar(new Date(Date.UTC(y, m - 1, 1)))}`;
  // Rollend jaarvenster: benoem begin- én eindmaand, anders leest "2026" als een
  // kalenderjaar terwijl er maanden van 2025 in de grafiek staan.
  return `${mtrMaandJaar(new Date(Date.UTC(y, m - 12, 1)))} – ${mtrMaandJaar(new Date(Date.UTC(y, m - 1, 1)))}`;
}
// "sep 2025" — korte maand zonder punt, met het jaar erbij.
function mtrMaandJaar(dt) {
  return `${mtrMonthShort.format(dt).replace('.', '')} ${dt.getUTCFullYear()}`;
}
// Het jaar van een bucket-sleutel ("2025-09" / "2025-09-14" / "2025-09-14 12:00").
const mtrKeyYear = (k) => Number(String(k).substring(0, 4));
// Soort van een bucket-sleutel, afgeleid uit haar VORM: "HH:MM" = kwartier,
// "YYYY-MM-DD" = dag, "YYYY-MM" = maand, anders null. De grafiekformatters lezen
// dit in plaats van de gekozen resolutie, want de tooltip van Recharts kan een
// sleutel van de vorige resolutie aanreiken (zie xLabel/tipLabel in MeteringPage).
const mtrSleutelSoort = (k) => {
  const s = String(k ?? '');
  if (/^\d{2}:\d{2}$/.test(s)) return 'quarter';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'day';
  if (/^\d{4}-\d{2}$/.test(s)) return 'month';
  return null;
};
// ISO-datum → DD/MM/YYYY (platformconventie, zie CLAUDE.md).
const mtrBeDatum = (iso) => { const p = String(iso).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso); };
// De aggregatie voor grafiek en tabellen leeft in src/metering.js (energySeries /
// aggregateBuckets): daar staat ook waarom kW en kWh nooit samen opgeteld worden en
// hoe rectificaties worden ontdubbeld.

// Analyse-parameters die 1-op-1 naar de Lynx-API gaan (zie backend routes/lynx.js
// voor de gemeten semantiek). Leeg/default = ongefilterd, zoals voorheen.
// `rectified: true` is de standaard: zonder de gecorrigeerde rijen zie je niet dat
// een meting herzien is. De pagina houdt vervolgens per periode de rij met de
// NIEUWSTE validFromUTC over (zie dedupeNewest in src/metering.js), dus meelezen
// leidt niet tot dubbeltellen.
const MTR_DEFAULT_ANALYSIS = { categories: [], methods: [], mode: 'default', rectified: true };
// Wat de GRAFIEK en de kengetallen nodig hebben: alleen actieve energie. Reactief
// (Capacitive/Inductive) en capaciteit horen bij "Ruwe meetgegevens" en worden pas
// opgehaald als dat blok opengeklapt wordt — ze zijn goed voor tweederde van alle
// readings. Zie ENERGY_METHODS in src/metering.js: dat is exact dezelfde lijst, en
// dat MOET zo blijven — anders vraagt de pagina data op die ze daarna wegfiltert,
// of erger: filtert ze weg wat ze net niet opgehaald heeft.
const MTR_CHART_ANALYSIS = { categories: [], methods: ENERGY_METHODS, mode: 'default', rectified: true };
// Stabiele sleutel voor cache + effect-deps: dezelfde selectie ⇒ dezelfde string.
const mtrAnalysisKey = (a) => `${[...a.categories].sort().join('+')}|${[...a.methods].sort().join('+')}|${a.mode}|${a.rectified ? 1 : 0}`;

// Client-side 1u-cache: meetgegevens veranderen zelden → niet continu Lynx aanroepen.
// De analyse-selectie zit in de sleutel, want ze verandert wat de API teruggeeft.
const mtrCache = new Map();
const MTR_HOUR_MS = 60 * 60 * 1000;
// `granularity`: op welke resolutie de server de readings mag samenvatten vóór hij
// ze verstuurt. De grafiek tekent per maand of per dag; dan zijn 96 kwartierwaarden
// per dag over de lijn slepen pure verspilling (gemeten: 14 MB → 0,75 MB voor een
// jaar, met exact dezelfde staven). Enkel de dagweergave heeft de kwartieren écht
// nodig, en die vraagt dus 'quarter'.
// `methods`: welke stroomsoorten. De grafiek gebruikt alleen actieve energie;
// reactief is tweederde van de readings en staat enkel in "Ruwe meetgegevens".
async function mtrFetch(token, gsrn, fromUtc, toUtc, analysis = MTR_DEFAULT_ANALYSIS, opts = {}) {
  const { granularity = 'quarter' } = opts;
  const key = `${gsrn}|${fromUtc}|${toUtc}|${mtrAnalysisKey(analysis)}|${granularity}`;
  const hit = mtrCache.get(key);
  if (hit && Date.now() - hit.at < MTR_HOUR_MS) return hit.data;
  const qs = new URLSearchParams({ gsrn, fromUtc, toUtc, reportingMode: analysis.mode });
  if (granularity !== 'quarter') qs.set('granularity', granularity);
  // Herhaalde parameters (niet komma-gescheiden): de API verwacht een unie.
  for (const c of analysis.categories) qs.append('categories', c);
  for (const m of analysis.methods) qs.append('methods', m);
  if (analysis.rectified) qs.set('includeRectified', '1');
  const r = await fetch(`/api/lynx/meterdata?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  const data = await r.json();
  mtrCache.set(key, { at: Date.now(), data });
  return data;
}

// Meettijdstippen: de API levert UTC, de gebruiker leest Belgische lokale tijd
// (platformregel: DD/MM/YYYY, 24u). De ruwe UTC-waarde blijft in de export staan.
const MTR_BE_DATETIME = new Intl.DateTimeFormat('nl-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
const mtrDateTime = (iso) => { if (!iso) return '—'; const d = new Date(iso); return isNaN(d) ? '—' : MTR_BE_DATETIME.format(d).replace(', ', ' '); };

// De kleuren van de overzichtsweergave staan bij MTR_TONE (groen = elektriciteit,
// oranje = gas). De losse tint-reeksen per EAN zijn vervallen: de overzichtsgrafiek
// toont niet meer één reeks per aansluiting maar verbruik/productie/netto samen, en
// het onderscheid tussen aansluitingen zit nu in de blokken Top locaties en Detail
// EAN — daar met een naam en een adres in plaats van met een kleurcode.

const MTR_PAGE_SIZE = 20;
const MTR_READING_PAGE_SIZE = 50;

// Gedeelde paginatiebalk voor de metering-tabellen (zelfde vorm als elders).
const MtrPager = ({ page, totalPages, total, size, onPage }) => (total <= size ? null : (
  <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-700">
    <div className="text-xs text-slate-400">Toont {((page - 1) * size) + 1} – {Math.min(page * size, total)} van {mtrFmtNum(total, 0)}</div>
    <div className="flex items-center gap-2">
      <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-xs rounded-lg">Vorige</button>
      <span className="px-3 py-1 bg-lime-500 text-slate-900 font-semibold text-xs rounded-lg">{page} / {totalPages}</span>
      <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-xs rounded-lg">Volgende</button>
    </div>
  </div>
));

const MtrExportButton = ({ onClick, count, label }) => (
  <ExportKnop onExport={onClick} disabled={!count} count={count} label={`${label} — exporteer naar Excel`} />
);

// Omgerekende kWh-cel: uitsluitend voor vermogenseenheden (kW/kWt ÷ 4). Voor kWh
// staat de waarde al in de vorige kolom, reactief is niet omrekenbaar.
const MtrKwhCell = ({ quantity, unit }) => {
  const { value, converted } = toKwh(quantity, unit);
  if (!converted || value == null) return <td className="py-1.5 px-2 text-right text-slate-600 text-xs">—</td>;
  return <td className="py-1.5 px-2 text-right text-lime-300/80 font-mono text-xs" title="Vermogen ÷ 4: 1 kW gedurende een kwartier = 0,25 kWh">{mtrFmtNum(value)} kWh</td>;
};

// Filter op Methode en Timeframe voor de ruwe meettabellen (categorie-, kwartier-,
// dag- en maandtabel). De opties komen uit de rijen zelf, dus een filter staat er
// alleen als die dimensie in de data voorkomt. Een keuze die na een periodewissel
// niet meer bestaat telt stil niet mee: anders bleef de tabel leeg zonder dat de
// gebruiker zag waarom. De export gebruikt DEZELFDE gefilterde rijen
// (platformregel: export = gefilterde data).
const mtrDimOpties = (rows, veld) => [...new Set(rows.map((r) => r[veld]).filter(Boolean))].sort();
function useMtrDimFilter(rows) {
  const [methods, setMethods] = useState([]);
  const [timeframes, setTimeframes] = useState([]);
  const methodOpties = useMemo(() => mtrDimOpties(rows, 'meteringMethod'), [rows]);
  const timeframeOpties = useMemo(() => mtrDimOpties(rows, 'timeframe'), [rows]);
  const m = methods.filter((v) => methodOpties.includes(v));
  const t = timeframes.filter((v) => timeframeOpties.includes(v));
  const mKey = m.join('|'), tKey = t.join('|');
  const gefilterd = useMemo(() => {
    if (!mKey && !tKey) return rows;
    const mSet = new Set(mKey ? mKey.split('|') : []), tSet = new Set(tKey ? tKey.split('|') : []);
    return rows.filter((r) => (!mSet.size || mSet.has(r.meteringMethod)) && (!tSet.size || tSet.has(r.timeframe)));
  }, [rows, mKey, tKey]);
  return {
    rows: gefilterd, actief: m.length > 0 || t.length > 0,
    balk: { methods: m, setMethods, methodOpties, timeframes: t, setTimeframes, timeframeOpties },
  };
}
// De twee keuzelijsten in de kop van de tabel, naast de exportknop. Verbergt een
// dimensie die in deze tabel niet voorkomt; toont niets als beide ontbreken.
const MtrDimFilterBalk = ({ balk }) => {
  const { methods, setMethods, methodOpties, timeframes, setTimeframes, timeframeOpties } = balk;
  if (!methodOpties.length && !timeframeOpties.length) return null;
  return (
    <div className="flex items-end gap-2 flex-wrap">
      {methodOpties.length > 0 && <SearchableMultiSelect label="Methode" size="sm" values={methods} onChange={setMethods} options={methodOpties} placeholder="Alle" />}
      {timeframeOpties.length > 0 && <SearchableMultiSelect label="Timeframe" size="sm" values={timeframes} onChange={setTimeframes} options={timeframeOpties} placeholder="Alle" />}
    </div>
  );
};
// "12 van 40 metingen" zodra een filter actief is, anders gewoon het aantal.
const mtrAantalTekst = (getoond, totaal, actief, enkelvoud, meervoud) =>
  `${mtrFmtNum(getoond, 0)}${actief ? ` van ${mtrFmtNum(totaal, 0)}` : ''} ${(actief ? totaal : getoond) === 1 ? enkelvoud : meervoud}`;

// Tabel met de ruwe metingen van ÉÉN categorie: elk veld dat de API teruggeeft,
// met uitklapbare kwartierwaarden per meting.
const MtrCategoryTable = ({ category, rows: alleRijen, gsrn, fileTag }) => {
  const sort = useTableSort('fromUTC');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(() => new Set());
  const dim = useMtrDimFilter(alleRijen);
  const rows = dim.rows;
  useEffect(() => { setPage(1); setOpen(new Set()); }, [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / MTR_PAGE_SIZE));
  const cur = Math.min(page, totalPages);
  const sorted = sortRows(rows, sort.sortConfig, { readings: (e) => (e.readings?.length ?? 0) });
  const visible = sorted.slice((cur - 1) * MTR_PAGE_SIZE, cur * MTR_PAGE_SIZE);
  const toggle = (k) => setOpen((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const superseded = rows.filter((e) => e.status === 'Rectified').length;
  const units = [...new Set(rows.map((e) => unitInfo(e.unit).label))];

  const exportCsv = () => {
    const utc = (s) => (s || '').replace('T', ' ').replace(/\.\d+Z?$/, '');
    exportRowsToCsv(`metingen-${category}-${fileTag}.csv`, [
      { header: 'EAN', get: (e) => e.connectionPointGsrn || gsrn, text: true },
      { header: 'Categorie', get: (e) => e.category },
      { header: 'Van (Brussel)', get: (e) => mtrDateTime(e.fromUTC) }, { header: 'Tot (Brussel)', get: (e) => mtrDateTime(e.toUTC) },
      { header: 'Van (UTC)', get: (e) => utc(e.fromUTC) }, { header: 'Tot (UTC)', get: (e) => utc(e.toUTC) },
      { header: 'Status', get: (e) => e.status }, { header: 'Methode', get: (e) => e.meteringMethod },
      { header: 'Timeframe', get: (e) => e.timeframe }, { header: 'Metering context', get: (e) => e.meteringContext },
      { header: 'Interval (min)', get: (e) => e.readingInterval },
      { header: 'Hoeveelheid', get: (e) => e.quantity }, { header: 'Eenheid', get: (e) => unitInfo(e.unit).label },
      { header: 'Omgerekend (kWh)', get: (e) => toKwh(e.quantity, e.unit).converted ? toKwh(e.quantity, e.unit).value : '' },
      { header: 'Kwartierwaarden', get: (e) => e.readings?.length ?? 0 },
      { header: 'Gas online-records', get: (e) => e.gasOnlineData?.length ?? 0 },
      { header: 'Geldig vanaf (UTC)', get: (e) => utc(e.validFromUTC) },
      { header: 'Meting-id', get: (e) => e.id, text: true }, { header: 'Aansluitpunt-id', get: (e) => e.connectionPointId, text: true },
    ], sorted);
  };

  return (
    <Card className="!p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-100">Metingen — {category}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {mtrAantalTekst(rows.length, alleRijen.length, dim.actief, 'meting', 'metingen')} · eenheden: {units.join(', ') || '—'}
            {superseded > 0 && <> · <span className="text-amber-300">{mtrFmtNum(superseded, 0)} verouderd (Rectified)</span></>}
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <MtrDimFilterBalk balk={dim.balk} />
          <MtrExportButton onClick={exportCsv} count={rows.length} label="Export" />
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-500">
            <th className="py-2 px-2 w-6" aria-label="Uitklappen" />
            <SortHeader label="Van" k="fromUTC" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Tot" k="toUTC" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Status" k="status" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Methode" k="meteringMethod" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Timeframe" k="timeframe" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Context" k="meteringContext" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Interval" k="readingInterval" align="right" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Hoeveelheid" k="quantity" align="right" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <th className="py-2 px-2 text-right">Omgerekend</th>
            <SortHeader label="Kwartier" k="readings" align="right" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Geldig vanaf" k="validFromUTC" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
          </tr></thead>
          <tbody>
            {visible.map((e, i) => {
              // Meting-id is 0 bij door de API samengevoegde rijen → val terug op de
              // periode + methode voor een stabiele sleutel.
              const rowKey = e.id ? `e${e.id}` : `${cur}-${i}-${e.fromUTC}-${e.meteringMethod}-${e.timeframe}`;
              const readings = e.readings || [];
              const gas = e.gasOnlineData || [];
              const isOpen = open.has(rowKey);
              const canOpen = readings.length > 0 || gas.length > 0;
              return <React.Fragment key={rowKey}>
                <tr onClick={() => canOpen && toggle(rowKey)} className={`border-b border-slate-800/50 ${canOpen ? 'cursor-pointer hover:bg-slate-700/20' : ''} ${e.status === 'Rectified' ? 'opacity-60' : ''}`}>
                  <td className="py-1.5 px-2 text-slate-500">{canOpen ? (isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />) : null}</td>
                  <td className="py-1.5 px-2 text-slate-300 text-xs whitespace-nowrap">{mtrDateTime(e.fromUTC)}</td>
                  <td className="py-1.5 px-2 text-slate-300 text-xs whitespace-nowrap">{mtrDateTime(e.toUTC)}</td>
                  <td className="py-1.5 px-2 text-xs"><span className={`px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${e.status === 'Rectified' ? 'bg-amber-900/50 text-amber-300' : 'bg-slate-700/60 text-slate-300'}`} title={e.status === 'Rectified' ? 'Verouderde meting: er bestaat een recentere waarde voor dezelfde periode' : 'Geldige meting'}>{e.status || '—'}</span></td>
                  <td className="py-1.5 px-2 text-slate-200">{e.meteringMethod}</td>
                  <td className="py-1.5 px-2 text-slate-400 text-xs">{e.timeframe}</td>
                  <td className="py-1.5 px-2 text-slate-400 text-xs">{e.meteringContext || '—'}</td>
                  <td className="py-1.5 px-2 text-right text-slate-400 text-xs">{e.readingInterval ? `${e.readingInterval} min` : '—'}</td>
                  <td className="py-1.5 px-2 text-right text-lime-300 font-mono whitespace-nowrap">{fmtQty(e.quantity, e.unit)}</td>
                  <MtrKwhCell quantity={e.quantity} unit={e.unit} />
                  <td className="py-1.5 px-2 text-right text-slate-500 text-xs">{readings.length || '—'}{gas.length ? ` +${gas.length} gas` : ''}</td>
                  <td className="py-1.5 px-2 text-slate-500 text-xs whitespace-nowrap">{mtrDateTime(e.validFromUTC)}</td>
                </tr>
                {isOpen && <tr className="bg-slate-900/40"><td colSpan={12} className="px-2 py-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                    Kwartierwaarden — meting {e.id || '(samengevoegd)'} · aansluitpunt {e.connectionPointId ?? '—'} · {unitInfo(e.unit).label}
                  </div>
                  <div className="max-h-72 overflow-y-auto rounded border border-slate-700/60">
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500 border-b border-slate-700/60"><th className="text-left py-1 px-2">Van</th><th className="text-left py-1 px-2">Tot</th><th className="text-right py-1 px-2">Waarde</th><th className="text-right py-1 px-2">Omgerekend</th><th className="text-left py-1 px-2">Kwaliteit</th></tr></thead>
                      <tbody>
                        {readings.map((r, ri) => <tr key={ri} className="border-b border-slate-800/40">
                          <td className="py-1 px-2 text-slate-300 whitespace-nowrap">{mtrDateTime(r.fromUTC)}</td>
                          <td className="py-1 px-2 text-slate-300 whitespace-nowrap">{mtrDateTime(r.toUTC)}</td>
                          <td className="py-1 px-2 text-right font-mono text-lime-300 whitespace-nowrap">{fmtQty(r.quantity, e.unit)}</td>
                          <MtrKwhCell quantity={r.quantity} unit={e.unit} />
                          <td className="py-1 px-2 text-slate-400">{r.quality || '—'}</td>
                        </tr>)}
                        {gas.map((g, gi) => <tr key={`g${gi}`} className="border-b border-slate-800/40">
                          <td className="py-1 px-2 text-orange-300" colSpan={5}>gasOnlineData: {JSON.stringify(g)}</td>
                        </tr>)}
                      </tbody>
                    </table>
                  </div>
                </td></tr>}
              </React.Fragment>;
            })}
            {rows.length === 0 && <tr><td colSpan={12} className="py-5"><TableEmpty message={dim.actief ? 'Geen metingen voor deze methode/timeframe.' : 'Geen metingen in deze categorie.'} icon={Gauge} /></td></tr>}
          </tbody>
        </table>
      </div>
      <MtrPager page={cur} totalPages={totalPages} total={rows.length} size={MTR_PAGE_SIZE} onPage={setPage} />
    </Card>
  );
};

// Tabel met de kwartierwaarden zelf: de fijnste korrel die de API levert.
const MtrQuarterTable = ({ rows: alleRijen, gsrn, fileTag }) => {
  const sort = useTableSort('fromUTC');
  const [page, setPage] = useState(1);
  const dim = useMtrDimFilter(alleRijen);
  const rows = dim.rows;
  useEffect(() => { setPage(1); }, [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / MTR_READING_PAGE_SIZE));
  const cur = Math.min(page, totalPages);
  const sorted = sortRows(rows, sort.sortConfig);
  const visible = sorted.slice((cur - 1) * MTR_READING_PAGE_SIZE, cur * MTR_READING_PAGE_SIZE);
  const units = [...new Set(rows.map((r) => unitInfo(r.unit).label))];

  const exportCsv = () => {
    const utc = (s) => (s || '').replace('T', ' ').replace(/\.\d+Z?$/, '');
    exportRowsToCsv(`kwartierwaarden-${fileTag}.csv`, [
      { header: 'EAN', get: () => gsrn, text: true },
      { header: 'Van (Brussel)', get: (r) => mtrDateTime(r.fromUTC) }, { header: 'Tot (Brussel)', get: (r) => mtrDateTime(r.toUTC) },
      { header: 'Van (UTC)', get: (r) => utc(r.fromUTC) }, { header: 'Tot (UTC)', get: (r) => utc(r.toUTC) },
      { header: 'Waarde', get: (r) => r.quantity }, { header: 'Eenheid', get: (r) => unitInfo(r.unit).label },
      { header: 'Omgerekend (kWh)', get: (r) => toKwh(r.quantity, r.unit).converted ? toKwh(r.quantity, r.unit).value : '' },
      { header: 'Kwaliteit', get: (r) => r.quality }, { header: 'Methode', get: (r) => r.meteringMethod },
      { header: 'Timeframe', get: (r) => r.timeframe }, { header: 'Categorie', get: (r) => r.category },
      { header: 'Meting-id', get: (r) => r.entityId, text: true },
    ], sorted);
  };

  return (
    <Card className="!p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-100">Kwartierwaarden</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {mtrAantalTekst(rows.length, alleRijen.length, dim.actief, 'waarde', 'waarden')} · eenheden: {units.join(', ') || '—'} · uit de geldige metingen (rectificaties ontdubbeld)
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <MtrDimFilterBalk balk={dim.balk} />
          <MtrExportButton onClick={exportCsv} count={rows.length} label="Export" />
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-500">
            <SortHeader label="Van" k="fromUTC" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Tot" k="toUTC" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Waarde" k="quantity" align="right" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <th className="py-2 px-2 text-right">Omgerekend</th>
            <SortHeader label="Kwaliteit" k="quality" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Methode" k="meteringMethod" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Timeframe" k="timeframe" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Categorie" k="category" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
          </tr></thead>
          <tbody>
            {visible.map((r, i) => <tr key={`${r.entityId}-${r.fromUTC}-${i}`} className="border-b border-slate-800/50">
              <td className="py-1.5 px-2 text-slate-300 text-xs whitespace-nowrap">{mtrDateTime(r.fromUTC)}</td>
              <td className="py-1.5 px-2 text-slate-300 text-xs whitespace-nowrap">{mtrDateTime(r.toUTC)}</td>
              <td className="py-1.5 px-2 text-right text-lime-300 font-mono whitespace-nowrap">{fmtQty(r.quantity, r.unit)}</td>
              <MtrKwhCell quantity={r.quantity} unit={r.unit} />
              <td className="py-1.5 px-2 text-xs"><span className={`px-1.5 py-0.5 rounded font-medium ${r.quality === 'Definitive' ? 'bg-lime-900/40 text-lime-300' : r.quality === 'NonExistent' ? 'bg-red-900/40 text-red-300' : 'bg-amber-900/40 text-amber-300'}`}>{r.quality}</span></td>
              <td className="py-1.5 px-2 text-slate-200">{r.meteringMethod}</td>
              <td className="py-1.5 px-2 text-slate-400 text-xs">{r.timeframe}</td>
              <td className="py-1.5 px-2 text-slate-400 text-xs">{r.category}</td>
            </tr>)}
            {rows.length === 0 && <tr><td colSpan={8} className="py-5"><TableEmpty message={dim.actief ? 'Geen kwartierwaarden voor deze methode/timeframe.' : 'Deze meter levert geen kwartierwaarden in deze periode.'} icon={Gauge} /></td></tr>}
          </tbody>
        </table>
      </div>
      <MtrPager page={cur} totalPages={totalPages} total={rows.length} size={MTR_READING_PAGE_SIZE} onPage={setPage} />
    </Card>
  );
};

// Dag- of maandwaarden: per tijdvak × methode × EENHEID (nooit over eenheden heen
// opgeteld) met de bron erbij, zodat een kwartier-som en een periodestand niet als
// hetzelfde soort getal gelezen worden.
// Tijdvak-sleutel blijft ISO voor de sortering, maar wordt Belgisch weergegeven
// (DD/MM/YYYY voor een dag, MM/YYYY voor een maand) — platformregel.
const mtrKeyLabel = (key) => {
  const p = String(key).split('-');
  if (p.length === 3) return `${p[2]}/${p[1]}/${p[0]}`;
  if (p.length === 2) return `${p[1]}/${p[0]}`;
  return String(key);
};

const MtrBucketTable = ({ title, subtitle, keyLabel, agg, gsrn, fileTag, filePrefix }) => {
  const sort = useTableSort('key');
  const [page, setPage] = useState(1);
  const dim = useMtrDimFilter(agg.rows);
  const rows = dim.rows;
  useEffect(() => { setPage(1); }, [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / MTR_PAGE_SIZE));
  const cur = Math.min(page, totalPages);
  const sorted = sortRows(rows, sort.sortConfig);
  const visible = sorted.slice((cur - 1) * MTR_PAGE_SIZE, cur * MTR_PAGE_SIZE);

  const exportCsv = () => exportRowsToCsv(`${filePrefix}-${fileTag}.csv`, [
    { header: 'EAN', get: () => gsrn, text: true },
    { header: keyLabel, get: (r) => mtrKeyLabel(r.key), text: true },
    { header: `${keyLabel} (ISO)`, get: (r) => r.key, text: true },
    { header: 'Methode', get: (r) => r.meteringMethod }, { header: 'Timeframe', get: (r) => r.timeframe },
    { header: 'Waarde', get: (r) => r.quantity }, { header: 'Eenheid', get: (r) => unitInfo(r.unit).label },
    { header: 'Omgerekend (kWh)', get: (r) => toKwh(r.quantity, r.unit).converted ? toKwh(r.quantity, r.unit).value : '' },
    { header: 'Bron', get: (r) => r.source }, { header: 'Aantal metingen', get: (r) => r.count },
  ], sorted);

  return (
    <Card className="!p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-100">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{mtrAantalTekst(rows.length, agg.rows.length, dim.actief, 'rij', 'rijen')} · {subtitle}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <MtrDimFilterBalk balk={dim.balk} />
          <MtrExportButton onClick={exportCsv} count={rows.length} label="Export" />
        </div>
      </div>
      {rows.some((r) => r.source === 'verdeeld') && (
        <p className="mt-2 text-[11px] text-indigo-300/90 flex items-start gap-1.5">
          <Info size={13} className="mt-0.5 shrink-0" />
          <span>Rijen met bron <span className="font-medium">verdeeld</span> komen uit één meterstand die meerdere {keyLabel.toLowerCase()}en beslaat en pro rata naar tijdsduur is toegewezen — een schatting, geen afzonderlijk gemeten waarde. De onbewerkte stand staat in de categorietabel.</span>
        </p>
      )}
      {agg.skipped.length > 0 && (
        <p className="mt-2 text-[11px] text-amber-300/90 flex items-start gap-1.5">
          <AlertCircle size={13} className="mt-0.5 shrink-0" />
          <span>{mtrFmtNum(agg.skipped.length, 0)} meting(en) konden niet aan een {keyLabel.toLowerCase()} toegewezen worden; ze staan wél volledig in de categorietabellen.</span>
        </p>
      )}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-500">
            <SortHeader label={keyLabel} k="key" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Methode" k="meteringMethod" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Timeframe" k="timeframe" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Waarde" k="quantity" align="right" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <th className="py-2 px-2 text-right">Omgerekend</th>
            <SortHeader label="Bron" k="source" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
            <SortHeader label="Metingen" k="count" align="right" sortConfig={sort.sortConfig} onSort={sort.handleSort} thClass="py-2 px-2" />
          </tr></thead>
          <tbody>
            {visible.map((r, i) => <tr key={`${r.key}-${r.meteringMethod}-${r.unit}-${r.source}-${i}`} className="border-b border-slate-800/50">
              <td className="py-1.5 px-2 text-slate-200 text-xs whitespace-nowrap font-mono">{mtrKeyLabel(r.key)}</td>
              <td className="py-1.5 px-2 text-slate-200">{r.meteringMethod}</td>
              <td className="py-1.5 px-2 text-slate-400 text-xs">{r.timeframe}</td>
              <td className="py-1.5 px-2 text-right text-lime-300 font-mono whitespace-nowrap">{fmtQty(r.quantity, r.unit)}</td>
              <MtrKwhCell quantity={r.quantity} unit={r.unit} />
              <td className="py-1.5 px-2 text-xs"><span
                className={`px-1.5 py-0.5 rounded ${r.source === 'kwartier' ? 'bg-slate-700/60 text-slate-300' : r.source === 'verdeeld' ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-700/60 text-slate-200'}`}
                title={r.source === 'kwartier' ? 'Opgeteld uit de kwartierwaarden'
                  : r.source === 'verdeeld' ? 'Schatting: één meterstand over meerdere tijdvakken, pro rata naar tijdsduur verdeeld'
                    : 'De meting zelf: één stand die precies in dit tijdvak valt'}
              >{r.source}</span></td>
              <td className="py-1.5 px-2 text-right text-slate-500 text-xs">{mtrFmtNum(r.count, 0)}</td>
            </tr>)}
            {rows.length === 0 && <tr><td colSpan={7} className="py-5"><TableEmpty message={dim.actief ? 'Geen waarden voor deze methode/timeframe.' : 'Geen waarden in deze periode.'} icon={Gauge} /></td></tr>}
          </tbody>
        </table>
      </div>
      <MtrPager page={cur} totalPages={totalPages} total={rows.length} size={MTR_PAGE_SIZE} onPage={setPage} />
    </Card>
  );
};

// ─── Overzichtsweergave ───────────────────────────────────────────────────────
// De pagina vertelt per energietype één verhaal: filterbalk → kengetallen →
// grafiek → verdiepende blokken. De ruwe meetgegevens blijven volledig
// beschikbaar, maar ingeklapt: die zijn het controle-instrument, niet het
// startpunt.
//
// Elektriciteit en gas staan in aparte tabs BINNEN de module (niet in het
// hoofdmenu): het zijn andere aansluitingen met een ander verbruikspatroon, en ze
// in één grafiek mengen zou een optelsom maken die niemand nodig heeft. Beide
// worden in kWh getoond — de omrekening van een kW-kwartierprofiel gebeurt in
// src/metering.js, waar ook staat waarom eenheden nooit zomaar samengeteld worden.

// Groen = elektriciteit, oranje = gas (dezelfde semantiek als elders in de
// module). Injectie krijgt een lichtere tint van dezelfde kleur — het is dezelfde
// energie in de andere richting; netto is indigo uit het platformpalet.
// `prod` is de vulkleur in de grafiek (mag licht zijn, staat naast een label);
// `prodInk` is dezelfde kleur als TEKST — die moet ook op een witte achtergrond
// leesbaar blijven, dus donkerder.
const MTR_TONE = {
  // Verbruik in blauw, productie in groen (vraag van de partners, 09-2026).
  EL: { label: 'Elektriciteit', icon: Zap, bar: '#2563eb', prod: '#16a34a', prodInk: '#15803d', soft: 'rgba(37,99,235,.14)' },
  NG: { label: 'Gas', icon: Flame, bar: '#ea580c', prod: '#fb923c', prodInk: '#c2410c', soft: 'rgba(234,88,12,.14)' },
};
// Netto verbruik (lijn) — cyaan, zodat hij naast het blauw van de staven leesbaar
// blijft (ΔE ≥ 15 tegenover elke andere reeks).
const MTR_NET = '#0891b2';
// De twee prijslijnen op de rechteras: spot (gestreept, amber) en contract
// (doorlopend, roze). Het hele meetpalet is in één keer gevalideerd met de
// validator uit de dataviz-skill, licht én donker:
//   validate_palette.js "#2563eb,#16a34a,#0891b2,#b45309,#db2777" --mode light --surface "#ffffff" --pairs all
//   (idem --mode dark --surface "#1e293b") → beide ALL CHECKS PASS.
// Twee WARNs blijven bewust staan en zijn gedekt door een tweede codering:
// roze↔groen (deutan ΔE 6,1) is lijn tegenover staaf, en het blauw haalt op de
// donkere kaart 2,8:1 — staven met een legende, geen dunne lijn. Wijzig een van
// deze vijf kleuren niet zonder die twee runs opnieuw te doen.
const MTR_SPOT = '#b45309';
const MTR_CONTRACT = '#db2777';
// Hoeveel GSRN's er onder de grafiektitel passen voor we gaan tellen. Zes is
// ongeveer twee regels op een telefoon; daarboven leest niemand de lijst nog en
// duwt ze de grafiek weg.
const MTR_GSRN_TOON = 6;
const MTR_GOOD = '#059669';
const MTR_BAD = '#e11d48';
const mtrKwh = (v, d = 0) => `${mtrFmtNum(v, d)} kWh`;

// Verschil met de vorige, even lange periode. Voor verbruik is dalen gunstig, voor
// productie stijgen — de kleur volgt dus de betekenis, niet het teken.
const MtrDelta = ({ value, label, goodWhen = 'down' }) => {
  if (value == null || !isFinite(value)) return <div className="mt-1 text-[11px] text-slate-500 truncate">geen vergelijking</div>;
  const up = value >= 0;
  const good = goodWhen === 'up' ? up : !up;
  const color = good ? MTR_GOOD : MTR_BAD;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div className="mt-1 flex items-start gap-1 text-[11px] min-w-0">
      <Icon size={12} style={{ color }} className="shrink-0" />
      <span style={{ color }} className="font-semibold shrink-0">{up ? '+' : '−'}{mtrFmtNum(Math.abs(value), 1)}%</span>
      <span className="text-slate-500 truncate xl:whitespace-normal xl:leading-snug">{label}</span>
    </div>
  );
};

// Eén kengetal als REGEL, niet als kaart: icoon in een zachte cirkel links, label
// en getal rechts. Vier losse kaarten naast elkaar kostten een volle schermbreedte
// voor vier getallen; als kolom passen ze naast de grafiek en blijft de hele
// module op één scherm. De scheidingslijnen komen van de kolom eromheen.
// Compacte KPI-kaart boven de grafiek: label, kengetal, duiding, en rechts een
// zacht gekleurde icoonschijf. Horizontaal naast elkaar in plaats van de vroegere
// verticale kolom naast de grafiek — die kolom kostte 280 px breedte die de
// grafiek beter kan gebruiken, en op een breed scherm bleef er onderaan lege
// ruimte over. `primary` zet het kengetal een maat groter voor de twee cijfers
// waar de pagina om draait (verbruik en, waar die bestaat, netto).
const MtrKpiCard = ({ icon: Icon, label, value, tint, color, primary = false, children }) => (
  <div className="min-w-0 rounded-xl border border-slate-700/60 bg-slate-800/40 px-3.5 py-2.5 flex items-start xl:items-center justify-between gap-3">
    <div className="min-w-0">
      <div className="text-[11px] text-slate-400 truncate">{label}</div>
      <div className={`${primary ? 'text-2xl' : 'text-xl'} font-bold text-slate-100 leading-tight mt-0.5`}><Kengetal>{value}</Kengetal></div>
      {children}
    </div>
    <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: tint }}>
      <Icon size={16} style={{ color }} />
    </span>
  </div>
);

// De verzameling eromheen. Sinds de herindeling van 11-09-2026 staan de kaarten
// op een breed scherm (xl) als KOLOM rechts naast de grafiek, onder elkaar, zodat
// de grafiek de rest van de breedte krijgt. Daaronder (laptop, tablet, telefoon)
// vallen ze onder de grafiek terug op auto-fit: het aantal kaarten hangt af van
// de klant (productie en netto verschijnen alleen bij injectie), en met vaste
// kolommen zou er een gat vallen zodra er vier in plaats van zes staan.
const MtrKpiGrid = ({ children }) => (
  <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(180px,1fr))] xl:grid-cols-1 xl:auto-rows-fr xl:h-full">
    {children}
  </div>
);

// Veldlabel in de filterbalk. Eén plek, zodat de labels niet per veld uit elkaar
// lopen in grootte of kleur.
const MtrVeld = ({ label, children, className = '' }) => (
  <div className={`min-w-0 ${className}`}>
    <div className="text-[11px] text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);

// Segmentkeuze (resolutie, welke reeks getoond wordt). Zelfde vorm als de gedeelde
// Toggle, maar compacter: hij staat in de filterbalk en in een kaart-kop.
// `grow` laat de knoppen de breedte van het veld gelijk verdelen (voor de
// filterbalk, waar de controle even breed moet zijn als de velden ernaast).
// `size="lg"` voor een keuze die zwaarder weegt dan de rest van een rij knoppen —
// gebruikt voor het energietype in de grafiekkop, dat bepaalt WELKE reeks je ziet
// terwijl de knoppen ernaast alleen over de weergave gaan.
const MtrSegment = ({ options, value, onChange, grow = false, className = '', size = 'md' }) => (
  <div className={`${grow ? 'flex w-full' : 'inline-flex'} bg-slate-800 border border-slate-700 rounded-lg p-0.5 ${className}`}>
    {options.map((o) => (
      <button key={o.value} onClick={() => onChange(o.value)}
        className={`${grow ? 'flex-1' : ''} ${size === 'lg' ? 'px-3.5 sm:px-4 py-2 text-sm gap-1.5' : 'px-2.5 sm:px-3 py-1.5 text-xs'} inline-flex items-center justify-center rounded-md font-semibold whitespace-nowrap ${value === o.value ? 'bg-lime-500 text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}>
        {o.icon && <o.icon size={size === 'lg' ? 15 : 13} />}
        {o.label}
      </button>
    ))}
  </div>
);

// Markt-badge (EL/NG) — één plek, zodat de kleuren overal gelijk zijn.
const MtrMarketBadge = ({ market }) => (
  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${market === 'NG' ? 'bg-orange-900/50 text-orange-300' : 'bg-lime-900/50 text-lime-300'}`}>{market}</span>
);

// Keuze van de aansluitingen die in de cijfers meetellen. Aanvinken = meetellen,
// klikken op de GSRN = focus voor het detailblok. Staat in de filterbalk, dus als
// popover: de lijst kan lang zijn en mag de pagina niet uit elkaar duwen.
const MtrLocationPicker = ({ groups, selected, focus, onToggle, onFocus, onAll, onNone }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const knopRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    // Portal-paneel: het zit buiten `ref`, dus ook op het paneel zelf testen.
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-dropdown-panel]")) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const all = groups.flatMap((g) => g.items);
  const chosen = all.filter((g) => selected.includes(g.gsrn));
  const first = chosen[0];
  return (
    <div className="relative" ref={ref}>
      <button ref={knopRef} onClick={() => setOpen((o) => !o)} disabled={!all.length}
        className="w-full flex items-center gap-2 px-2.5 py-2 h-[42px] bg-slate-800 border border-slate-700 hover:border-lime-500 disabled:opacity-50 disabled:hover:border-slate-700 rounded-lg text-sm">
        <span className="px-2 py-0.5 rounded-md bg-slate-700 text-slate-200 text-xs font-semibold shrink-0">{chosen.length} EAN{chosen.length === 1 ? '' : "'s"}</span>
        <span className="truncate text-xs text-slate-300">{first ? (first.address || first.gsrn) : 'geen locatie gekozen'}</span>
        {chosen.length > 1 && <span className="text-xs text-slate-500 shrink-0">+{chosen.length - 1}</span>}
        <ChevronDown size={15} className="ml-auto text-slate-500 shrink-0" />
      </button>
      {open && (
        <DropdownPanel anchorRef={knopRef} minWidth={300} className="max-h-[26rem] overflow-y-auto bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-2">
          <div className="flex items-center gap-2 px-1 pb-2 text-xs">
            <button onClick={onAll} className="text-slate-400 hover:text-lime-400">Alles</button>
            <span className="text-slate-600">·</span>
            <button onClick={onNone} className="text-slate-400 hover:text-lime-400">Niets</button>
            <span className="ml-auto text-slate-500">{chosen.length}/{all.length}</span>
          </div>
          {groups.filter((g) => g.items.length > 0).map((g) => (
            <div key={g.title}>
              <div className="px-1 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-slate-500">{g.title}</div>
              {g.items.map((it) => (
                <div key={it.gsrn} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${focus === it.gsrn ? 'bg-slate-700/50' : 'hover:bg-slate-700/30'}`}>
                  <input type="checkbox" checked={selected.includes(it.gsrn)} onChange={() => onToggle(it.gsrn)}
                    className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-3.5 h-3.5 shrink-0" />
                  <MtrMarketBadge market={it.market} />
                  <button onClick={() => onFocus(it.gsrn)} className="min-w-0 text-left" title="Toon dit aansluitpunt in het detailblok">
                    <span className="block font-mono text-xs text-slate-300 truncate">{it.gsrn}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{it.address || it.name || '—'}</span>
                  </button>
                  {focus === it.gsrn && <span className="ml-auto text-[10px] text-lime-400 shrink-0">focus</span>}
                </div>
              ))}
            </div>
          ))}
        </DropdownPanel>
      )}
    </div>
  );
};


// Toplocaties: rangschikking op verbruik met een staaf als verhouding. Klikken zet
// de focus, zodat het detailblok ernaast meteen die aansluiting toont.
const MtrTopLocations = ({ rows, tone, focus, onFocus, onShowAll }) => {
  const max = rows.reduce((m, r) => Math.max(m, r.verbruik), 0);
  return (
    <Card className="!p-4 flex flex-col">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-100">Top locaties</h3>
        <span className="text-[11px] text-slate-500 shrink-0">Verbruik (kWh)</span>
      </div>
      <div className="mt-3 flex-1 space-y-2.5">
        {rows.length === 0 && <TableEmpty message="Geen locatie met verbruik in deze periode." icon={MapPin} />}
        {rows.map((r, i) => (
          <button key={r.gsrn} onClick={() => onFocus(r.gsrn)} className="w-full text-left group" title={r.gsrn}>
            <div className="flex items-center gap-2 text-xs">
              <span className="w-4 shrink-0 text-slate-500 tabular-nums">{i + 1}</span>
              <span className={`truncate ${focus === r.gsrn ? 'text-lime-400 font-semibold' : 'text-slate-300 group-hover:text-slate-100'}`}>{r.address || r.gsrn}</span>
              <span className="ml-auto shrink-0 font-semibold text-slate-100 tabular-nums">{mtrFmtNum(r.verbruik, 0)}</span>
            </div>
            <div className="mt-1 ml-6 h-1.5 rounded-full bg-slate-700/60 overflow-hidden">
              <span className="block h-full rounded-full" style={{ width: `${max ? (r.verbruik / max) * 100 : 0}%`, background: tone.bar }} />
            </div>
          </button>
        ))}
      </div>
      {onShowAll && <button onClick={onShowAll} className="mt-3 pt-3 border-t border-slate-700/60 text-left text-xs text-lime-400 hover:underline">Bekijk alle locaties</button>}
    </Card>
  );
};

// Verdeling als ring. Twee vormen: afname/injectie waar er productie is, anders de
// verdeling van het verbruik over de locaties — zodat het blok altijd iets zegt.
const MtrDonut = ({ title, tip, slices, footerLabel, footerValue }) => {
  const total = slices.reduce((s, x) => s + x.value, 0);
  return (
    <Card className="!p-4 flex flex-col">
      <h3 className="text-base font-semibold text-slate-100 flex items-center gap-1.5">{title}{tip && <InfoTip text={tip} />}</h3>
      {total <= 0 ? (
        <div className="flex-1 flex items-center"><TableEmpty message="Geen energie gemeten in deze periode." icon={Gauge} /></div>
      ) : (
        <div className="mt-2 flex flex-1 flex-col sm:flex-row items-center gap-4">
          <div className="h-40 w-40 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="none">
                  {slices.map((s) => <Cell key={s.name} fill={s.color} />)}
                </Pie>
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => [mtrKwh(v, 1), n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="min-w-0 flex-1 space-y-2.5 w-full">
            {slices.map((s) => (
              <div key={s.name} className="flex items-start gap-2 text-xs">
                <span className="mt-1 w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                <div className="min-w-0">
                  <div className="text-slate-300 truncate">{s.name}</div>
                  <div className="text-slate-100 font-semibold">{mtrFmtNum((s.value / total) * 100, 1)}% <span className="font-normal text-slate-500">({mtrKwh(s.value)})</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {footerLabel && (
        <div className="mt-3 pt-3 border-t border-slate-700/60 flex items-baseline justify-between gap-2 text-xs">
          <span className="text-slate-500 truncate">{footerLabel}</span>
          <span className="font-semibold text-slate-100 shrink-0">{footerValue}</span>
        </div>
      )}
    </Card>
  );
};

// ÉÉN Metering-pagina, twee ingangen: de module Metering in het hoofdmenu en de
// tab Metering in Customer 360 renderen allebei DEZE component (07-09-2026 zo
// bevestigd: elke aanpassing hier geldt voor beide). `embedded` = ingebed in
// Customer 360. Dan vervallen enkel de eigen paginakop en het klant-zoekveld: de
// klant staat daar al vast, en een tweede zoekbalk binnen een klantdossier nodigt
// uit tot per ongeluk een andere klant openen. Al de rest (locatiekiezer, periode,
// energietype, tabs, grafieken, exports, lege staten) is letterlijk hetzelfde —
// voeg dus geen nieuwe `embedded`-vertakkingen toe zonder goede reden.
// ── Dagprofiel: het gemiddelde 24-uursprofiel per weekdag ───────────────────
// (Metering, 11-09-2026.) Onder de hoofdgrafiek: hoe ziet een gemiddelde maandag
// eruit over de laatste maanden, en wijkt één gekozen dag daarvan af? Het
// rekenwerk staat op de server (/api/lynx/meterdata/dagprofiel, util/dagProfiel.js)
// met exact de regels van de grafiek; hier komt ~25 KB binnen i.p.v. de ruwe
// kwartierdata van maanden. De kaart haalt NIETS op vóór de klik "Dagprofiel
// berekenen": wie de pagina alleen voor de gewone grafiek opent, betaalt er
// niets voor. Vormgeving = die van de grafiekkaart: titel + badge links, keuzes
// rechts, de periode groot en gecentreerd, vette as-eenheden.
const MTR_PROFIEL_MAANDEN = [1, 2, 3, 6, 12];
const MTR_WEEKDAGEN = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];
const MTR_WEEKDAG_KORT = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
// Zeven lijnen voor de stand "Dagen apart": het gedeelde grafiekpalet,
// zodat maandag hier dezelfde kleur heeft als een eerste reeks elders.
const MTR_WEEKDAG_KLEUR = ['#6366F1', '#14B8A6', '#8B5CF6', '#F59E0B', '#F43F5E', '#0EA5E9', '#84CC16'];
const mtrProfielCache = new Map();
// De keuze "welke dagen": alle, werkdagen, weekend, één weekdag, of elke weekdag
// als eigen lijn. Waarden zijn de sleutels van `groepen` in het API-antwoord.
const MTR_PROFIEL_GROEPEN = [
  { value: 'alle', label: 'Alle dagen' }, { value: 'apart', label: 'Dagen apart' },
  { value: 'werkdagen', label: 'Werkdagen' }, { value: 'weekend', label: 'Weekend' },
  ...MTR_WEEKDAG_KORT.map((k, i) => ({ value: String(i + 1), label: k })),
];
const mtrProfielGroepNaam = (groep, dagen) => {
  if (groep === 'alle') return `Gemiddelde dag · ${dagen} dagen`;
  if (groep === 'werkdagen') return `Gemiddelde werkdag · ${dagen} dagen`;
  if (groep === 'weekend') return `Gemiddelde weekenddag · ${dagen} dagen`;
  if (groep === 'apart') return 'Gemiddelde per weekdag';
  const naam = MTR_WEEKDAGEN[Number(groep) - 1] || '';
  return `Gemiddelde ${naam} · ${dagen} ${naam}${dagen === 1 ? '' : 'en'}`;
};

const MtrDagprofielKaart = ({ token, gsrns, totDag, standaardDag, tone, kwartierMogelijk, spotMogelijk = false, embedded = false, onRapport = null }) => {
  const [actief, setActief] = useState(false);
  const [maanden, setMaanden] = useState(3);
  const [groep, setGroep] = useState('alle');
  const [vergelijk, setVergelijk] = useState(false);
  const [vergelijkDag, setVergelijkDag] = useState(standaardDag);
  const [toonBand, setToonBand] = useState(true);
  // De gemiddelde spotprijs (EPEX, per kwartier) over dezelfde dagen, op een eigen
  // as rechts — zelfde knop en kleur als in de hoofdgrafiek. Standaard uit.
  const [toonSpot, setToonSpot] = useState(false);
  const [data, setData] = useState(null);
  const [laadt, setLaadt] = useState(false);
  const [fout, setFout] = useState(null);

  // Volgt de dag van de hoofdgrafiek zolang de gebruiker hier nog geen eigen dag
  // koos: wie in de hoofdgrafiek naar maandag 8 september bladert, wil díe dag
  // vergelijken.
  const eigenDag = useRef(false);
  useEffect(() => { if (!eigenDag.current) setVergelijkDag(standaardDag); }, [standaardDag]);

  const gsrnSleutel = gsrns.join('+');
  const spotAan = toonSpot && spotMogelijk;
  const sleutel = `${gsrnSleutel}|${maanden}|${totDag}|${vergelijkDag || ''}|${spotAan ? 'spot' : ''}`;
  useEffect(() => {
    if (!actief || !gsrns.length || !kwartierMogelijk) return undefined;
    const hit = mtrProfielCache.get(sleutel);
    if (hit && Date.now() - hit.at < MTR_HOUR_MS) { setData(hit.data); setFout(null); return undefined; }
    let weg = false;
    setLaadt(true); setFout(null);
    const qs = new URLSearchParams({ maanden: String(maanden), tot: totDag });
    for (const g of gsrns) qs.append('gsrn', g);
    if (vergelijkDag) qs.set('dag', vergelijkDag);
    if (spotAan) qs.set('spot', '1');
    fetch(`/api/lynx/meterdata/dagprofiel?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        if (weg) return;
        mtrProfielCache.set(sleutel, { at: Date.now(), data: d });
        setData(d);
        // Standaarddag zonder metingen (vandaag is meestal nog niet binnen): val
        // terug op de laatste dag mét data, zolang de gebruiker zelf geen dag koos.
        const laatste = d.kalender && d.kalender.length ? d.kalender[d.kalender.length - 1].dag : null;
        if (!eigenDag.current && !d.dag && laatste && laatste !== vergelijkDag) setVergelijkDag(laatste);
      })
      .catch((e) => { if (!weg) { setData(null); setFout(e.message || 'Dagprofiel ophalen mislukt'); } })
      .finally(() => { if (!weg) setLaadt(false); });
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actief, sleutel, kwartierMogelijk]);

  // Wie "Ma" kiest zonder zelf een dag te kiezen, vergelijkt met de laatste
  // maandag in de periode — een woensdag naast een gemiddelde maandag zegt niets.
  useEffect(() => {
    if (eigenDag.current || !data || !/^[1-7]$/.test(groep)) return;
    const laatste = [...(data.kalender || [])].reverse().find((d) => String(d.wd) === groep);
    if (laatste && laatste.dag !== vergelijkDag) setVergelijkDag(laatste.dag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groep, data]);

  const heeftProductie = !!(data && data.heeftProductie);
  const groepData = data && groep !== 'apart' ? data.groepen[groep] : null;
  const dag = data && data.dag ? data.dag : null;

  // De 96 rijen van de grafiek: gemiddelde, band (laagste–hoogste), en de
  // vergelijkdag. Bij "apart" zeven gemiddelden naast elkaar.
  const rijen = useMemo(() => {
    if (!data) return [];
    return data.slots.map((slot, i) => {
      const r = { key: slot };
      if (groep === 'apart') {
        for (let wd = 1; wd <= 7; wd++) r[`wd${wd}`] = data.groepen[String(wd)].verbruik.gem[i];
      } else if (groepData) {
        r.gem = groepData.verbruik.gem[i];
        r.band = [groepData.verbruik.min[i], groepData.verbruik.max[i]];
        if (heeftProductie && groepData.productie) r.gemProd = groepData.productie.gem[i];
      }
      if (vergelijk && dag) {
        r.dag = dag.verbruik ? dag.verbruik[i] : null;
        if (heeftProductie && dag.productie) r.dagProd = dag.productie[i];
      }
      // Spot: bij "Dagen apart" het gemiddelde over alle dagen (zeven prijslijnen
      // zouden de grafiek onleesbaar maken).
      if (spotAan && data.spot) {
        const sg = data.spot.groepen[groep === 'apart' ? 'alle' : groep];
        r.spot = sg ? sg.gem[i] : null;
        if (vergelijk && dag && data.spot.dag) r.spotDag = data.spot.dag[i];
      }
      return r;
    });
  }, [data, groep, groepData, vergelijk, dag, heeftProductie, spotAan]);
  const spotGroep = spotAan && data && data.spot ? data.spot.groepen[groep === 'apart' ? 'alle' : groep] : null;
  const spotGem = spotGroep ? (() => { const v = spotGroep.gem.filter((x) => x != null); return v.length ? v.reduce((a, x) => a + x, 0) / v.length : null; })() : null;

  const leeg = data && data.aantalDagen === 0;
  const dagNaam = (d) => (d ? `${MTR_WEEKDAGEN[d.wd - 1]} ${mtrBeDatum(d.datum)}` : '');
  const verschil = groepData && dag && groepData.verbruik.dagTotaal ? ((dag.verbruikTotaal - groepData.verbruik.dagTotaal) / groepData.verbruik.dagTotaal) * 100 : null;

  // Momentopname voor het PDF-rapport van de pagina (buildMeteringReportHtml):
  // precies wat hier getekend staat, of null zolang er geen profiel berekend is.
  // De ouder bewaart ze in een ref, zodat dit geen render van de pagina kost.
  useEffect(() => {
    if (!onRapport) return undefined;
    onRapport(actief && data && !leeg ? {
      data, rijen, groep, groepData, maanden, heeftProductie, spotAan, spotGem, verschil,
      dag: vergelijk ? dag : null, toonBand: toonBand && groep !== 'apart',
    } : null);
    return () => onRapport(null);
  }, [onRapport, actief, data, leeg, rijen, groep, groepData, maanden, heeftProductie, spotAan, spotGem, verschil, vergelijk, dag, toonBand]);

  const tipWaarde = (v, naam) => {
    if (Array.isArray(v)) return [`${mtrFmtNum(v[0], 2)} – ${mtrFmtNum(v[1], 2)} kWh`, naam];
    if (/spotprijs/i.test(String(naam))) return [v == null ? '—' : `${mtrFmtNum(v, 1)} EUR/MWh`, naam];
    return [v == null ? '—' : `${mtrFmtNum(v, 2)} kWh`, naam];
  };

  const exporteer = () => {
    if (!rijen.length) return;
    const kolommen = [{ header: 'Kwartier', get: (r) => r.key }];
    if (groep === 'apart') {
      for (let wd = 1; wd <= 7; wd++) kolommen.push({ header: `${MTR_WEEKDAGEN[wd - 1]} (kWh)`, get: (r) => r[`wd${wd}`] });
    } else {
      kolommen.push({ header: 'Gemiddeld verbruik (kWh)', get: (r) => r.gem });
      kolommen.push({ header: 'Laagste (kWh)', get: (r) => (r.band ? r.band[0] : null) });
      kolommen.push({ header: 'Hoogste (kWh)', get: (r) => (r.band ? r.band[1] : null) });
      if (heeftProductie) kolommen.push({ header: 'Gemiddelde productie (kWh)', get: (r) => r.gemProd });
    }
    if (vergelijk && dag) {
      kolommen.push({ header: `Verbruik ${dagNaam(dag)} (kWh)`, get: (r) => r.dag });
      if (heeftProductie) kolommen.push({ header: `Productie ${dagNaam(dag)} (kWh)`, get: (r) => r.dagProd });
    }
    if (spotAan && data.spot) {
      kolommen.push({ header: 'Gemiddelde spotprijs (EUR/MWh)', get: (r) => r.spot });
      if (vergelijk && dag && data.spot.dag) kolommen.push({ header: `Spotprijs ${dagNaam(dag)} (EUR/MWh)`, get: (r) => r.spotDag });
    }
    exportRowsToCsv(`dagprofiel_${gsrns.length === 1 ? gsrns[0] : `${gsrns.length}_eans`}_${data.vanDag}_${data.totDag}`, kolommen, rijen);
  };

  const knopKlasse = (aan) => `inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border font-medium ${aan ? 'bg-lime-500 border-lime-500 text-slate-900' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`;

  return (
    <Card className="!p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-slate-100 flex flex-wrap items-center gap-2">
            <span>Dagprofiel</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-semibold" style={{ background: tone.soft, color: tone.bar }}>
              <tone.icon size={13} className="shrink-0" />{tone.label}
            </span>
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Het gemiddelde verloop over 24 uur, per kwartier, over een langere periode — per weekdag te bekijken en te vergelijken met één dag.
          </p>
        </div>
        {actief && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-slate-500 shrink-0">Periode</span>
              <MtrSegment value={maanden} onChange={setMaanden} options={MTR_PROFIEL_MAANDEN.map((m) => ({ value: m, label: m === 1 ? '1 mnd' : `${m} mnd` }))} />
            </div>
            <button onClick={() => setToonBand((v) => !v)} disabled={groep === 'apart'} className={`${knopKlasse(toonBand && groep !== 'apart')} disabled:opacity-50 disabled:cursor-not-allowed`}
              title="Toon per kwartier de laagste en hoogste waarde van de gekozen dagen als band rond het gemiddelde">
              <span className="w-3 h-2 rounded-sm" style={{ background: tone.bar, opacity: 0.35 }} />Laagste–hoogste
            </button>
            <button onClick={() => setVergelijk((v) => !v)} className={knopKlasse(vergelijk)} title="Leg de kwartierwaarden van één dag over het gemiddelde">
              <span className="w-3 h-0.5 rounded" style={{ background: vergelijk ? '#0f172a' : MTR_NET }} />Vergelijk met één dag
            </button>
            {vergelijk && (
              <BeDateInput value={vergelijkDag} min={data ? data.vanDag : undefined} max={totDag}
                onChange={(v) => { if (v) { eigenDag.current = true; setVergelijkDag(v); } }}
                className="min-w-0 w-[150px] h-8 px-2.5 bg-slate-800 border border-slate-700 hover:border-lime-500 rounded-lg text-xs text-slate-200 focus:border-lime-500 focus:outline-none text-left" />
            )}
            {spotMogelijk && (
              <button onClick={() => setToonSpot((v) => !v)} title="Toon de gemiddelde spotprijs (EPEX, per kwartier) over dezelfde dagen — eigen as rechts"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border font-medium ${toonSpot ? 'text-slate-900' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}
                style={toonSpot ? { background: MTR_SPOT, borderColor: MTR_SPOT } : undefined}>
                <span className="w-3 h-0.5 rounded" style={{ background: toonSpot ? '#fff' : MTR_SPOT }} />Spotprijs
              </button>
            )}
            <ExportKnop onExport={exporteer} disabled={!rijen.length} label="Dagprofiel — de 96 kwartierwaarden zoals ze nu getekend staan, naar Excel" />
          </div>
        )}
      </div>

      {!actief ? (
        // Nog niets opgehaald: één knop, met de reden erbij als het niet kan.
        <div className="mt-3 flex flex-col items-center justify-center gap-2 py-8 text-center">
          <button onClick={() => setActief(true)} disabled={!kwartierMogelijk || !gsrns.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-lime-500 hover:bg-lime-400 text-slate-900 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
            <Activity size={15} />Dagprofiel berekenen
          </button>
          <p className="text-[11px] text-slate-500 max-w-md">
            {!gsrns.length ? 'Vink eerst minstens één aansluiting aan.'
              : !kwartierMogelijk ? 'Een dagprofiel vraagt kwartierwaarden (meters van het type SMR3 of AMR). De aangevinkte aansluitingen leveren die niet.'
                : `De laatste ${maanden} maanden t.e.m. ${mtrBeDatum(totDag)} worden op de server samengevat tot 96 kwartieren per weekdag; er komt maar een klein antwoord naar dit scherm.`}
          </p>
        </div>
      ) : (
        <>
          {/* Welke dagen: dezelfde segmentknoppen als elders; op een telefoon
              scrollt de rij horizontaal in plaats van te breken. */}
          <div className="mt-3 overflow-x-auto">
            <MtrSegment value={groep} onChange={setGroep} options={MTR_PROFIEL_GROEPEN} />
          </div>

          {data && !leeg && (
            <div className="mt-2 text-center">
              <div className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100 leading-tight">{mtrProfielGroepNaam(groep, groep === 'apart' ? data.aantalDagen : groepData.dagen)}</div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                {mtrBeDatum(data.vanDag)} – {mtrBeDatum(data.totDag)}
                {gsrns.length > 0 && <> · EAN {gsrns.slice(0, MTR_GSRN_TOON).map((g) => <span key={g} className="font-mono text-slate-400 ml-1">{g}</span>)}{gsrns.length > MTR_GSRN_TOON && <span className="ml-1">+{gsrns.length - MTR_GSRN_TOON} andere</span>}</>}
              </div>
            </div>
          )}

          {data && !leeg && (
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
              {groep === 'apart' ? MTR_WEEKDAG_KORT.map((k, i) => (
                <span key={k} className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: MTR_WEEKDAG_KLEUR[i] }} />{MTR_WEEKDAGEN[i]}</span>
              )) : (
                <>
                  <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: tone.bar }} />Gemiddeld verbruik (kWh per kwartier)</span>
                  {toonBand && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-3 h-2 rounded-sm" style={{ background: tone.bar, opacity: 0.25 }} />Laagste – hoogste per kwartier</span>}
                  {heeftProductie && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: tone.prod }} />Gemiddelde productie (kWh)</span>}
                </>
              )}
              {vergelijk && dag && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: MTR_NET }} />{dagNaam(dag)}</span>}
              {vergelijk && !dag && data && <span className="flex items-center gap-1.5 text-amber-300/90"><AlertCircle size={12} />Geen meetgegevens voor {mtrBeDatum(vergelijkDag)} binnen deze periode.</span>}
              {spotAan && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: MTR_SPOT }} />Gemiddelde spotprijs (EUR/MWh, as rechts)</span>}
              {spotAan && vergelijk && dag && data && data.spot && data.spot.dag && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded opacity-60" style={{ background: MTR_SPOT }} />Spotprijs {dagNaam(dag)}</span>}
            </div>
          )}

          {laadt ? (
            <div className="h-72 flex items-center justify-center"><Spinner /></div>
          ) : fout ? (
            <div className="h-72 flex items-center justify-center"><TableEmpty message={fout} icon={AlertCircle} /></div>
          ) : !data || leeg ? (
            <div className="h-72 flex items-center justify-center"><TableEmpty message="Geen kwartierwaarden in deze periode." icon={Gauge} /></div>
          ) : (
            <div className={`mt-1 h-64 sm:h-80 ${embedded ? '' : 'lg:h-[24rem]'}`}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={rijen} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="key" stroke="#64748b" fontSize={10} tickLine={false} interval={3} height={38} label={{ value: 'Kwartieren', position: 'insideBottom', offset: 0, fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                  <YAxis yAxisId="kwh" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => mtrFmtNum(v, 2)} label={{ value: 'kWh', angle: -90, position: 'insideLeft', offset: 8, fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                  {spotAan && <YAxis yAxisId="prijs" orientation="right" stroke={MTR_SPOT} fontSize={11} tickLine={false} axisLine={false} width={62} tickFormatter={(v) => mtrFmtNum(v, 0)} label={{ value: 'EUR/MWh', angle: 90, position: 'insideRight', offset: 8, fill: MTR_SPOT, fontSize: 11, fontWeight: 700 }} />}
                  <Tooltip contentStyle={CHART_TOOLTIP} formatter={tipWaarde} labelFormatter={(k) => `Kwartier ${k}`} />
                  {groep !== 'apart' && toonBand && <Area yAxisId="kwh" type="monotone" dataKey="band" name="Laagste – hoogste" stroke="none" fill={tone.bar} fillOpacity={0.16} isAnimationActive={false} connectNulls />}
                  {groep !== 'apart' && <Line yAxisId="kwh" type="monotone" dataKey="gem" name="Gemiddeld verbruik" stroke={tone.bar} strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls />}
                  {groep !== 'apart' && heeftProductie && <Line yAxisId="kwh" type="monotone" dataKey="gemProd" name="Gemiddelde productie" stroke={tone.prod} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />}
                  {groep === 'apart' && MTR_WEEKDAG_KORT.map((k, i) => (
                    <Line key={k} yAxisId="kwh" type="monotone" dataKey={`wd${i + 1}`} name={MTR_WEEKDAGEN[i]} stroke={MTR_WEEKDAG_KLEUR[i]} strokeWidth={1.75} dot={false} isAnimationActive={false} connectNulls />
                  ))}
                  {vergelijk && dag && <Line yAxisId="kwh" type="monotone" dataKey="dag" name={`Verbruik ${dagNaam(dag)}`} stroke={MTR_NET} strokeWidth={2} strokeDasharray="5 3" dot={false} isAnimationActive={false} connectNulls />}
                  {vergelijk && dag && heeftProductie && <Line yAxisId="kwh" type="monotone" dataKey="dagProd" name={`Productie ${dagNaam(dag)}`} stroke={tone.prodInk} strokeWidth={1.5} strokeDasharray="5 3" dot={false} isAnimationActive={false} connectNulls />}
                  {/* Spot met een halo in de kaartkleur, zoals in de hoofdgrafiek: leesbaar waar hij over de band loopt. */}
                  {spotAan && <Line yAxisId="prijs" type="monotone" dataKey="spot" stroke="var(--chart-surface, #1e293b)" strokeWidth={5} dot={false} connectNulls isAnimationActive={false} legendType="none" tooltipType="none" />}
                  {spotAan && <Line yAxisId="prijs" type="monotone" dataKey="spot" name="Gemiddelde spotprijs" stroke={MTR_SPOT} strokeWidth={2.5} strokeDasharray="5 3" dot={false} connectNulls isAnimationActive={false} />}
                  {spotAan && vergelijk && dag && <Line yAxisId="prijs" type="monotone" dataKey="spotDag" name={`Spotprijs ${dagNaam(dag)}`} stroke={MTR_SPOT} strokeOpacity={0.55} strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls isAnimationActive={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Kengetallen onder de grafiek: wat het gemiddelde per dag is, en hoe
              de vergelijkdag zich daartoe verhoudt. */}
          {data && !leeg && (
            <div className="mt-2 rounded-lg bg-slate-800/50 border border-slate-700/50 px-3 py-1.5 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              {groep !== 'apart' && groepData && (
                <span>Gemiddeld <span className="text-slate-300 font-semibold">{mtrKwh(groepData.verbruik.dagTotaal, 1)}</span> verbruik per dag{heeftProductie && groepData.productie ? <> · productie <span className="text-slate-300 font-semibold">{mtrKwh(groepData.productie.dagTotaal, 1)}</span></> : null}</span>
              )}
              {groep === 'apart' && (
                <span>{MTR_WEEKDAGEN.map((n, i) => <span key={n} className="mr-3">{MTR_WEEKDAG_KORT[i]} <span className="text-slate-300 font-semibold">{mtrKwh(data.groepen[String(i + 1)].verbruik.dagTotaal, 1)}</span></span>)}</span>
              )}
              {vergelijk && dag && (
                <span>{dagNaam(dag)}: <span className="text-slate-300 font-semibold">{mtrKwh(dag.verbruikTotaal, 1)}</span>
                  {verschil != null && groep !== 'apart' && <span className="ml-1 font-semibold" style={{ color: verschil > 0 ? MTR_BAD : MTR_GOOD }}>({verschil > 0 ? '+' : ''}{mtrFmtNum(verschil, 1)} %)</span>}
                </span>
              )}
              {spotAan && spotGem != null && (
                <span>Gemiddelde spotprijs <span className="text-slate-300 font-semibold">{mtrFmtNum(spotGem, 1)} EUR/MWh</span></span>
              )}
              {(data.opzij.verbruik.length > 0 || data.opzij.productie.length > 0) && (
                <span className="flex items-center gap-1"><Info size={12} className="shrink-0" />{data.opzij.verbruik.length + data.opzij.productie.length} bijkomende dataset(s) beschrijven dezelfde energie uit een andere bron en zitten niet in dit profiel.</span>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

const MeteringPage = ({ orders = [], ordersLoading = false, token, currentUser, prefill, prefillKey, embedded = false, branding = null, allBranding = [], allTeamBranding = [] }) => {
  const [query, setQuery] = useState('');
  // Het zoekveld (KlantZoekVeld) houdt zijn eigen paneel-status bij; deze pagina
  // hoeft alleen nog te weten wat er GEKOZEN is.
  const [selEmail, setSelEmail] = useState(null);
  const [selRef, setSelRef] = useState(null);
  const [selGsrn, setSelGsrn] = useState(null);
  // De resolutie hangt aan het ENERGIETYPE, niet aan de pagina; de keuze wordt per
  // type onthouden zodat heen en weer schakelen tussen de tabs een handmatige
  // aanpassing niet telkens overschrijft. BEIDE starten op Jaar: dat is het rollende
  // venster van de laatste twaalf maanden, en dat is voor elektriciteit én gas het
  // bruikbaarste startbeeld. Elektriciteit stond op Maand, maar wie de pagina opent
  // wil eerst het verloop over een jaar zien en pas daarna inzoomen.
  const [viewModeByMarket, setViewModeByMarket] = useState({ EL: 'year', NG: 'year' });
  const [anchor, setAnchor] = useState(mtrToday());
  // Jaarweergave: 'rollend' (laatste twaalf maanden, standaard) of een kalenderjaar
  // als string ('2025'). Bij een kalenderjaar staat de anchor op de laatste maand
  // ervan (december, of de lopende maand voor het huidige jaar).
  const [jaarKeuze, setJaarKeuze] = useState('rollend');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartGsrns, setChartGsrns] = useState([]);      // EAN's die meetellen (multi-select)
  const [chartType, setChartType] = useState('bar');     // 'bar' | 'line'
  const [showFlow, setShowFlow] = useState('alles');     // welke reeks(en) in de grafiek
  const [seriesRaw, setSeriesRaw] = useState([]);        // [{gsrn, market, entities}] huidige periode
  const [prevRaw, setPrevRaw] = useState([]);            // idem vorige periode, enkel voor het verschil
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [tab, setTab] = useState('EL');                  // 'EL' | 'NG' | 'locaties'
  const [market, setMarket] = useState('EL');            // energietype waarop de cijfers slaan
  const viewMode = viewModeByMarket[market] || 'month';
  const setViewMode = (v) => setViewModeByMarket((prev) => ({ ...prev, [market]: v }));
  const [rawOpen, setRawOpen] = useState(false);         // ruwe meetgegevens uitgeklapt
  const rawRef = useRef(null);
  const focusPick = useRef(null);                        // energietype waarvoor de gebruiker zelf een focus koos
  // Vroeg de gebruiker om één specifieke GSRN? Dan mag de klantcascade niet alsnog
  // álle aansluitingen aanvinken. Deze wens geldt voor precies één doorloop van het
  // effect op refGsrns en wordt daarna gewist.
  const gewensteGsrns = useRef(null);
  // Meerdere klantnummers tegelijk (Customer 360 met meer dan één nummer
  // aangevinkt): de EAN's van ál die nummers horen dan bij "deze klant". Geldt
  // zolang `selRef` een van die nummers is; kiest de gebruiker hier zelf een
  // andere klant, dan valt de pagina terug op één nummer.
  const [prefillRefs, setPrefillRefs] = useState(null);
  // De meetdata wordt ONGEFILTERD opgehaald: geen categorie- of methodefilter, zodat
  // álle beschikbare categorieën binnenkomen en elk in haar eigen tabel terechtkomt.
  // Gecorrigeerde (Rectified) metingen komen altijd mee; de pagina houdt per periode
  // de nieuwste waarde over. Zie MTR_DEFAULT_ANALYSIS, dat mtrFetch als standaard
  // gebruikt — er is dus geen keuze meer die de pagina moet doorgeven.
  //
  // We halen wél de lijst met categorieën op die de API ondersteunt, zodat de pagina
  // kan zeggen welke categorie in deze periode géén metingen heeft. Zonder die lijst
  // zie je een ontbrekende categorie niet: er is dan simpelweg geen tabel.
  const [knownCategories, setKnownCategories] = useState([]);
  useEffect(() => {
    if (!token) return;
    fetch('/api/lynx/meterdata/options', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.categories) setKnownCategories(d.categories); })
      .catch(() => { /* niet kritisch: de tabellen komen uit de data zelf */ });
  }, [token]);

  // Unieke (email, ref, naam, gsrn, market) uit de gescopete orders.
  const conns = useMemo(() => {
    const seen = new Set(); const list = [];
    for (const o of orders) {
      const gsrn = (o.connectionPointGSRN || '').trim(); if (!gsrn) continue;
      const email = (o.customerEmail || '').trim(); const ref = (o.customerReference || '').trim();
      const name = (o.customer || '').trim(); const market = mtrMarket(o.market);
      const address = (o.connectionPointAddress || '').trim();
      // Metermethode hoort erbij: spotprijzen tonen we enkel bij SMR3/AMR, want
      // alleen daar is er een kwartierwaarde om een prijs aan te hangen.
      const meterMethod = (o.meterMethod || '').trim().toUpperCase();
      const k = `${email}|${ref}|${gsrn}`; if (seen.has(k)) continue; seen.add(k);
      list.push({ email, ref, name, gsrn, market, address, meterMethod, company: (o.company || '').trim(), team: (o.salespersonTeam || '').trim(), bedrijf: (o.customerCompanyName || '').trim(), segment: (o.segment || '').trim() });
    }
    return list;
  }, [orders]);

  // Zoeksuggesties — gedeeld met Orders, Invoices en Customer 360 (zie
  // bouwZoekSuggesties): dezelfde lijst met segment, klantnaam en klantnummer.
  // Metering groepeert per KLANTNUMMER: de selectie hangt aan een ref, en twee
  // klanten kunnen dezelfde naam dragen. Het getal achter een klant is hier het
  // aantal UNIEKE GSRN's, niet het aantal orders: op deze pagina kies je een klant
  // om zijn aansluitingen te bekijken.
  const zoekKenmerken = useMemo(() => klantKenmerken(orders), [orders]);
  const suggesties = useMemo(() => bouwZoekSuggesties({
    query, orders, kenmerken: zoekKenmerken, groepeerOp: 'ref', telGsrns: true,
  }), [query, orders, zoekKenmerken]);

  // Klanten (uniek ref+naam) onder de gekozen e-mail; GSRN's onder de gekozen klant.
  const emailCustomers = useMemo(() => {
    if (!selEmail) return [];
    const m = new Map();
    for (const c of conns) if (c.email === selEmail && c.ref) { if (!m.has(c.ref)) m.set(c.ref, c.name); }
    return [...m.entries()].map(([ref, name]) => ({ ref, name }));
  }, [conns, selEmail]);
  const refGsrns = useMemo(() => {
    if (!selRef) return [];
    const refs = new Set(prefillRefs && prefillRefs.includes(selRef) ? prefillRefs : [selRef]);
    const m = new Map();
    for (const c of conns) if (refs.has(c.ref) && c.gsrn && !m.has(c.gsrn)) m.set(c.gsrn, { market: c.market, address: c.address, meterMethod: c.meterMethod, name: c.name });
    return [...m.entries()].map(([gsrn, v]) => ({ gsrn, ...v }));
  }, [conns, selRef, prefillRefs]);
  // Alle GSRN's onder het gekozen e-mailadres (om EAN's te kunnen vergelijken).
  const emailGsrns = useMemo(() => {
    if (!selEmail) return [];
    const m = new Map();
    for (const c of conns) if (c.email === selEmail && c.gsrn && !m.has(c.gsrn)) m.set(c.gsrn, { market: c.market, address: c.address, meterMethod: c.meterMethod, name: c.name });
    return [...m.entries()].map(([gsrn, v]) => ({ gsrn, ...v }));
  }, [conns, selEmail]);
  // Lookup gsrn → {market,address,name} (binnen het e-mailadres) + GSRN's van het
  // e-mailadres die NIET bij de gekozen klant horen (voor de "andere EAN's"-sectie).
  const gsrnMeta = useMemo(() => {
    const m = new Map();
    for (const g of emailGsrns) m.set(g.gsrn, g);
    for (const g of refGsrns) if (!m.has(g.gsrn)) m.set(g.gsrn, g);
    return m;
  }, [emailGsrns, refGsrns]);
  const otherEmailGsrns = useMemo(() => {
    const inCust = new Set(refGsrns.map((g) => g.gsrn));
    return emailGsrns.filter((g) => !inCust.has(g.gsrn));
  }, [emailGsrns, refGsrns]);

  // ── Contractvoorwaarden per aansluiting (voor de verkoopprijs) ──────────────
  // De coëfficiënt, de majoratie én de prijsformule staan op de FACTUUR, per
  // EL-/NG-blok — niet op de order. We nemen die van de MEEST RECENTE factuur
  // voor die GSRN.
  //
  // Ze komen van /api/tariffs en NIET uit de facturen-prop. Die prop is hier
  // onbruikbaar: de facturen komen uit de snelle sync-feed, die geen EL/NG-detail
  // draagt (gemeten: 0 van de 51.698 facturen heeft een GSRN of een coëfficiënt,
  // alleen de creditnota's wel). De verkoopprijs bleef daardoor leeg voor bijna
  // iedereen — en een lege cel ziet er hetzelfde uit als "geen contract". Zie
  // backend/src/routes/tariffs.js, dat dezelfde bron leest als Revenue.
  //
  // LET OP, dit blijft een aanname: een factuur draagt geen leveringsperiode
  // (alleen een delivery_start), dus per kwartier de juiste factuur kiezen kan
  // niet sluitend. Wijzigt de coëfficiënt middenin een geëxporteerde periode, dan
  // is het oudere deel berekend met de nieuwste voorwaarden. Daarom staan de
  // gebruikte waarden ook op het scherm: dan is de aanname zichtbaar en
  // controleerbaar in plaats van verstopt in een exportkolom.
  const [tariefPerGsrn, setTariefPerGsrn] = useState(new Map());
  const tariefSleutel = chartGsrns.join(',');
  useEffect(() => {
    const lijst = chartGsrns.filter((g) => /^\d{18}$/.test(g)).slice(0, 60);
    if (!token || !lijst.length) { setTariefPerGsrn(new Map()); return undefined; }
    let leeft = true;
    fetch(`/api/tariffs?gsrn=${lijst.join(',')}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { tariffs: {} }))
      .then((d) => { if (leeft) setTariefPerGsrn(new Map(Object.entries(d.tariffs || {}))); })
      .catch(() => { if (leeft) setTariefPerGsrn(new Map()); });
    return () => { leeft = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tariefSleutel]);

  // De indexparameters (Beheer → Indexparameters): de maandprijs waarmee een
  // niet-dynamisch contract afrekent. Eén keer per sessie ophalen — het zijn een
  // paar tientallen getallen die niet aan de selectie hangen.
  const [indexParams, setIndexParams] = useState(null);
  useEffect(() => {
    if (!token) return undefined;
    let leeft = true;
    fetch('/api/index-parameters', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (leeft && d?.parameters) setIndexParams(d.parameters); })
      .catch(() => { /* geen index → geen prijs, de pagina zegt dat zelf */ });
    return () => { leeft = false; };
  }, [token]);

  const allGsrns = useMemo(() => [...refGsrns, ...otherEmailGsrns], [refGsrns, otherEmailGsrns]);
  const pickerGroups = useMemo(() => [
    { title: 'Aansluitingen van deze klant', items: refGsrns },
    { title: "Andere EAN's van dit e-mailadres", items: otherEmailGsrns },
  ], [refGsrns, otherEmailGsrns]);

  // Auto-selecteer wanneer er maar één keuze is.
  useEffect(() => { if (emailCustomers.length && (!selRef || !emailCustomers.some((c) => c.ref === selRef))) setSelRef(emailCustomers[0].ref); }, [emailCustomers]); // eslint-disable-line react-hooks/exhaustive-deps
  // Bij keuze van een klant: diens GSRN's standaard geselecteerd; focus = de eerste
  // (of de huidige focus als die nog in de selectie zit — belangrijk voor prefill).
  // Koos de gebruiker één specifieke EAN (zoekveld, deep-link, order- of
  // factuurrij), dan wint die: `gewensteGsrns` BLIJFT staan tot de gebruiker zelf
  // een andere klant kiest (pickCustomer/clearAll) of een klant-prefill zonder EAN
  // binnenkomt. Vroeger overleefde de wens precies één doorloop; kwam daarna de
  // volledige orderlijst binnen (nieuw tabblad: eerst de snelle doelophaal, dan
  // alles) of veranderde de klantcontext nog eens, dan vinkte dit effect alsnog
  // álle EAN's van de klant aan — precies wat een zoekopdracht op één EAN niet
  // mag doen. Valt geen enkele gewenste EAN meer onder de klant (andere klant
  // gekozen via het e-mailadres), dan vervalt de wens en geldt weer "alles".
  //
  // Dit effect zet de standaard alleen wanneer de SET aansluitingen verandert;
  // dezelfde set opnieuw (nieuw array-object) laat de vinkjes van de gebruiker met
  // rust.
  const laatsteRefSet = useRef('');
  useEffect(() => {
    const alle = refGsrns.map((g) => g.gsrn);
    const sleutel = alle.join(',');
    if (sleutel === laatsteRefSet.current) return;
    laatsteRefSet.current = sleutel;
    const gewenst = gewensteGsrns.current;
    const gewenstGeldig = gewenst && gewenst.length && (!alle.length || gewenst.some((g) => alle.includes(g)));
    if (!gewenstGeldig) gewensteGsrns.current = null;
    const def = gewenstGeldig ? gewenst : alle;
    setChartGsrns(def);
    setSelGsrn((cur) => (cur && def.includes(cur)) ? cur : (def[0] || null));
  }, [refGsrns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Welke energietypes heeft deze klant? Het tabblad springt mee: een klant met
  // enkel gas hoeft niet eerst op een lege elektriciteitstab te landen.
  // De aangevinkte GSRN's tellen mee, want een GSRN die via het rechtsklikmenu
  // binnenkomt zonder klantcontext staat niet in `allGsrns` — zonder deze regel zou
  // de pagina dan zeggen dat er geen aansluiting is terwijl ze net geladen wordt.
  const marketsAvailable = useMemo(() => {
    const s = new Set(allGsrns.map((g) => g.market));
    for (const g of chartGsrns) s.add(gsrnMeta.get(g)?.market || 'EL');
    return s;
  }, [allGsrns, chartGsrns, gsrnMeta]);
  useEffect(() => {
    if (!marketsAvailable.size || marketsAvailable.has(market)) return;
    const next = marketsAvailable.has('EL') ? 'EL' : 'NG';
    setMarket(next);
    setTab((t) => (t === 'locaties' ? t : next));
  }, [marketsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickCustomer = (ref, email) => { gewensteGsrns.current = null; setPrefillRefs(null); setSelEmail(email || selEmail); setSelRef(ref); setSelGsrn(null); setQuery(''); };
  // Zoeken op één GSRN: de klantcontext wordt WÉL volledig opgehaald (je ziet al
  // zijn aansluitingen in "Locatie / EAN van deze klant"), maar aangevinkt staat enkel de
  // gevraagde EAN. Anders krijg je meteen de cijfers van de hele klant te zien
  // terwijl je naar één aansluiting vroeg. `gewensteGsrns` zorgt dat het effect op
  // refGsrns hieronder er niet alsnog alles bij aanvinkt; setChartGsrns hier dekt
  // het geval waarin de klant al geselecteerd was (dan verandert refGsrns niet).
  const pickGsrn = (g) => {
    gewensteGsrns.current = [g.gsrn];
    setPrefillRefs(null);
    setSelEmail(g.email || null); setSelRef(g.ref || null); setSelGsrn(g.gsrn);
    setChartGsrns([g.gsrn]);
    setQuery('');
    const c = conns.find((x) => x.gsrn === g.gsrn);
    if (c?.market) { focusPick.current = c.market; setMarket(c.market); setTab((t) => (t === 'locaties' ? t : c.market)); }
  };
  const clearAll = () => { gewensteGsrns.current = null; setPrefillRefs(null); setSelEmail(null); setSelRef(null); setSelGsrn(null); setChartGsrns([]); setQuery(''); setData(null); };
  // GSRN aan/uit in de cijfers; focus zet de GSRN voor het detailblok en de ruwe
  // tabellen (en voegt 'm toe aan de selectie als die er nog niet in zat).
  const toggleChartGsrn = (g) => setChartGsrns((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]);
  const focusGsrn = (g) => {
    setSelGsrn(g);
    setChartGsrns((prev) => prev.includes(g) ? prev : [...prev, g]);
    const m = gsrnMeta.get(g)?.market || market;
    // Onthoud dat de focus voor dít energietype een bewuste keuze is, zodat het
    // effect hieronder hem niet terugzet naar de zwaarste aansluiting.
    focusPick.current = m;
    if (m !== market) { setMarket(m); setTab((t) => (t === 'locaties' ? t : m)); }
  };

  // Prefill vanuit een rechtsklik-menu ("Metering") in een tabel op een andere
  // pagina. target = { type:'customer'|'gsrn', value, label }. Voor 'customer' is
  // value de klantnaam (zoals Customer 360 doet); voor 'gsrn' de GSRN. We zoeken de
  // bijhorende connectie uit de gescopete orders en zetten de cascade.
  //
  // Wacht op `conns`: bij een deeplink in een NIEUW tabblad (rechtsklik → Metering)
  // mount deze pagina vóór de orders binnen zijn en is die lijst nog leeg — de
  // prefill zou dan stil mislukken. Vandaar conns.length in de dependencies, met
  // een ref zodat elke prefill maar één keer wordt toegepast en een latere
  // handmatige keuze van de gebruiker niet overschreven wordt.
  const toegepastePrefill = useRef(0);
  useEffect(() => {
    if (!prefill || !prefillKey || toegepastePrefill.current === prefillKey) return;
    // Orders nog onderweg → later opnieuw proberen. Zijn ze binnen en is de lijst
    // toch leeg (gebruiker zonder orders, of een mislukte ophaal), dan gaan we WEL
    // door: anders blijft de pagina eeuwig "aansluitingen zoeken" tonen i.p.v. de
    // gewone lege staat.
    if (!conns.length && ordersLoading) return;
    toegepastePrefill.current = prefillKey;
    setQuery('');
    if (prefill.type === 'gsrn') {
      // Ook hier geldt: enkel de gevraagde EAN aangevinkt (zelfde regel als een
      // GSRN uit het zoekveld), de rest van de klant staat er wel bij.
      gewensteGsrns.current = [prefill.value];
      setPrefillRefs(null);
      const c = conns.find((x) => x.gsrn === prefill.value);
      if (c) { focusPick.current = c.market; setSelEmail(c.email || null); setSelRef(c.ref || null); setSelGsrn(c.gsrn); setChartGsrns([c.gsrn]); setMarket(c.market); setTab(c.market); }
      // Onbekende GSRN (staat niet in de gescopete orders): toch tonen, en meteen
      // aanvinken — anders blijft de pagina op "vink een aansluiting aan" staan.
      else { setSelEmail(null); setSelRef(null); setSelGsrn(prefill.value); setChartGsrns([prefill.value]); }
    } else {
      // Komt de prefill van een order- of factuurrij, dan draagt hij de EAN(s) van
      // díe rij (klantTarget → gsrns): enkel die aangevinkt, de rest van de klant
      // staat er uitgevinkt bij. Zonder EAN's (Customer 360, klantnaam) alles aan.
      const eigen = Array.isArray(prefill.gsrns) ? prefill.gsrns.filter(Boolean) : [];
      gewensteGsrns.current = eigen.length ? eigen : null;
      // Zoek de aansluiting eerst op KLANTNUMMER en pas dan op naam. Twee redenen:
      //  - de rij waar de rechtsklik vandaan kwam kent de klant niet altijd onder
      //    dezelfde naam als de order (factuur = onderneming, order = contactpersoon),
      //    dus op naam alleen bleef deze pagina bij zakelijke klanten leeg terwijl
      //    het zoekveld hiernaast dezelfde klant wél vond;
      //  - deze pagina selecteert sowieso per klantnummer (twee klanten kunnen
      //    dezelfde naam dragen), dus de ref is hier de scherpste sleutel.
      // De naam-vergelijking loopt nu ook via c360Norm i.p.v. een exacte ===, zodat
      // een afwijkende hoofdletter of dubbele spatie geen misser meer oplevert.
      const ref = String(prefill.ref || '').trim();
      const naam = c360Norm(prefill.value);
      // Meerdere klantnummers (Customer 360 met vinkjes): ze tellen allemaal mee
      // in refGsrns; `ref` is er de eerste van en blijft de gekozen klant.
      const refs = Array.isArray(prefill.refs) ? prefill.refs.map((r) => String(r || '').trim()).filter(Boolean) : [];
      setPrefillRefs(refs.length > 1 ? refs : null);
      const c = (ref && conns.find((x) => String(x.ref || '').trim() === ref))
        || (refs.length && conns.find((x) => refs.includes(String(x.ref || '').trim())))
        || (naam && conns.find((x) => c360Norm(x.name) === naam))
        || null;
      if (c) {
        setSelEmail(c.email || null); setSelRef(c.ref || null); setSelGsrn(null);
        // Eigen EAN's van de rij meteen aanvinken. Stond dezelfde klant al
        // geselecteerd, dan verandert de set aansluitingen niet en zou het effect
        // op refGsrns (dat de wens anders toepast) niet lopen.
        if (eigen.length) {
          setChartGsrns(eigen);
          setSelGsrn(eigen[0]);
          const eg = conns.find((x) => x.gsrn === eigen[0]);
          if (eg?.market) { focusPick.current = eg.market; setMarket(eg.market); setTab((t) => (t === 'locaties' ? t : eg.market)); }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey, conns.length, ordersLoading]);

  // De resolutie van de grafiek. Staat hier BEWUST bovenaan: ze zit in de
  // dependency-arrays van de ophaal-effects hieronder, en een dependency-array wordt
  // tijdens het renderen geëvalueerd — een const die later in de component staat zou
  // dus een TDZ-fout geven.
  const chartGranularity = viewMode === 'day' ? 'quarter' : viewMode === 'month' ? 'day' : 'month';
  // Het gekozen kalenderjaar, enkel van kracht in de jaarweergave.
  const kalenderJaar = viewMode === 'year' && jaarKeuze !== 'rollend' ? Number(jaarKeuze) : null;
  const range = useMemo(() => mtrRangeFor(viewMode, anchor, kalenderJaar), [viewMode, anchor, kalenderJaar]);
  // Dezelfde periode één stap terug, uitsluitend om het verschil op de kengetallen
  // te kunnen tonen. Beide vensters zitten in dezelfde cache van een uur, dus heen
  // en weer bladeren kost geen extra API-aanroepen.
  // Bij een kalenderjaar: dezelfde maanden van het jaar ervoor (jan–sep 2026 naast
  // jan–sep 2025), anders vergelijk je een half jaar met een heel jaar.
  const prevRange = useMemo(() => mtrRangeFor(viewMode, mtrShiftAnchor(viewMode, anchor, -1), kalenderJaar ? kalenderJaar - 1 : null), [viewMode, anchor, kalenderJaar]);

  // Bij GSRN-keuze: zet de periode op de meest recente maand/dag MET metingen.
  // Begin je op vandaag terwijl de laatste meting van maanden geleden is, dan kijk
  // je naar een leeg overzicht terwijl er wél data is.
  //
  // Dit deed de pagina vroeger zelf, met één of twee VOLLEDIGE ophalen (30 dagen
  // ≈ 1,6 MB, en als die leeg was 24 maanden ≈ 14 MB) om één datum te weten —
  // blokkerend, vóór er iets op het scherm stond. De server beantwoordt dezelfde
  // vraag nu in ~100 bytes, met dezelfde twee vensters en dezelfde regel, en warmt
  // meteen zijn cache op voor de grafiek die hierna volgt.
  useEffect(() => {
    if (!selGsrn || !token) return;
    let afgebroken = false;
    fetch(`/api/lynx/meterdata/last?gsrn=${encodeURIComponent(selGsrn)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!afgebroken) { setAnchor(d?.lastDay || mtrToday()); setJaarKeuze((k) => (k === "rollend" ? k : String((d?.lastDay || mtrToday()).substring(0, 4)))); } })
      .catch(() => { if (!afgebroken) { setAnchor(mtrToday()); setJaarKeuze((k) => (k === "rollend" ? k : mtrToday().substring(0, 4))); } });
    return () => { afgebroken = true; };
  }, [token, selGsrn]);

  // Meetgegevens van de FOCUS-aansluiting: voedt UITSLUITEND het blok "Ruwe
  // meetgegevens". Dat blok begint dichtgeklapt, en dit is de zwaarste ophaal van de
  // pagina (alle categorieën, alle kwartieren — tot 14 MB voor een jaar). Daarom pas
  // ophalen zodra iemand het openklapt. De grafiek en de kengetallen hebben deze set
  // niet nodig: die draaien op de ingedikte reeks hieronder.
  useEffect(() => {
    if (!selGsrn || !token || !rawOpen) { setData(null); return; }
    setLoading(true); setError(null); setData(null);
    const venster = localDayWindowUtc(range.from, range.to);
    mtrFetch(token, selGsrn, venster.fromUtc, venster.toUtc)
      .then(setData).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [token, selGsrn, range.from, range.to, rawOpen]);

  // ── Alle geselecteerde aansluitingen samen ────────────────────────────────────
  const activeGsrns = useMemo(() => chartGsrns.map((g) => ({ gsrn: g, market: gsrnMeta.get(g)?.market || 'EL' })), [chartGsrns, gsrnMeta]);
  const activeKey = activeGsrns.map((g) => g.gsrn).join(',');

  // De aansluitingen die de grafiek NU tekent: de geselecteerde locaties van het
  // gekozen energietype. Alles wat over de grafiek gaat — de spotprijs, de
  // tarieven, de detailexport — hangt hieraan en niet aan de rauwe selectie.
  // Anders bepaalt een aangevinkte gasaansluiting mee wat er bij elektriciteit
  // mag, terwijl ze niet eens in beeld is.
  const activeMarketGsrns = useMemo(() => activeGsrns.filter((g) => g.market === market), [activeGsrns, market]);

  // ── Marktprijs bij de meetdata: spot óf maandindex ──────────────────────────
  // TWEE vragen bepalen of er een prijs naast de meting mag staan, en ze staan los
  // van elkaar:
  //
  // 1. MAG DEZE METER DEZE RESOLUTIE DRAGEN? Een SMR3/AMR meet per kwartier en mag
  //    dus overal een prijs krijgen. Een SMR1/MMR wordt maandelijks gelezen: daar
  //    heeft een kwartier- of dagprijs niets om aan te hangen, maar de maand- en
  //    jaarweergave wél. Een YMR (jaarlijks) enkel de jaarweergave. Die regel staat
  //    in prijsMogelijk() in src/indexPrijs.js, met tests.
  // 2. WELKE PRIJS IS HET? Een dynamisch contract (…_Hourly, Belpex, Hourly Belpex)
  //    rekent met de ECHTE spotprijs; een niet-dynamisch contract (Belpex_RLP,
  //    Belpex_SPP, TTF RLP) met een afgesproken MAANDindex uit de instellingen.
  //    Bij zo'n contract de spotlijn tekenen zou een prijs tonen waarmee de klant
  //    niet afrekent — vandaar dat de bron de lijn bepaalt, niet omgekeerd.
  //
  // Eén lijn dus, en alleen wanneer de aangevinkte aansluitingen het EENS zijn over
  // de bron. Verschillen ze (de ene dynamisch, de andere vast), dan tonen we niets
  // en zeggen we dat: één lijn die voor de helft van de selectie klopt is erger dan
  // geen lijn. Zonder tarieven valt elektriciteit terug op de spot — dat is een
  // marktprijs die ook zonder contract waar is; gas heeft zonder index niets.
  const activeMeters = useMemo(
    () => activeMarketGsrns.map((g) => gsrnMeta.get(g.gsrn)?.meterMethod || ''),
    [activeMarketGsrns, gsrnMeta],
  );
  const prijsResolutieOk = prijsMogelijk(activeMeters, viewMode);
  const prijsResolutieUitleg = prijsUitleg(activeMeters, viewMode);
  const actieveTarieven = useMemo(
    () => activeMarketGsrns
      .map(({ gsrn }) => ({ gsrn, t: tariefPerGsrn.get(gsrn) }))
      .filter((x) => x.t),
    [activeMarketGsrns, tariefPerGsrn],
  );
  // De prijsbron van de AFNAME — dat is de reeks die de grafiek tekent. De
  // injectiebron (Belpex_SPP) kan een andere zijn; die telt mee in de verkoopprijs
  // van de export, niet in de lijn.
  const prijsBronnen = useMemo(() => {
    const m = new Map();
    for (const { t } of actieveTarieven) {
      const b = prijsBron(t, 'afname');
      if (b) m.set(b.soort === 'index' ? `index:${b.code}` : 'spot', b);
    }
    return [...m.values()];
  }, [actieveTarieven]);
  const prijsBronActief = useMemo(() => {
    if (prijsBronnen.length === 1) return prijsBronnen[0];
    if (prijsBronnen.length === 0) return market === 'EL' ? { soort: 'spot' } : null;
    return null;   // gemengde selectie
  }, [prijsBronnen, market]);
  const prijsGemengd = prijsBronnen.length > 1;
  const prijsMeta = prijsBronActief?.soort === 'index' ? indexMeta(prijsBronActief.code) : null;
  const prijsNaam = prijsMeta ? prijsMeta.label : 'Spotprijs';
  const prijsSerieNaam = `${prijsNaam} (EUR/MWh)`;
  const prijsBeschikbaar = prijsResolutieOk && activeMarketGsrns.length > 0 && Boolean(prijsBronActief);

  const [toonPrijs, setToonPrijs] = useState(false);   // standaard uit, zoals gevraagd
  // De knop onthoudt haar stand, maar de reeks verschijnt alleen waar ze MAG. Zet
  // je hem aan op de jaarweergave en schakel je naar maand met een jaarmeter, dan
  // verdwijnt de knop — zonder deze tweede voorwaarde bleven de rechteras, de
  // legende en een stukje lijn staan voor een prijs die er niet meer is.
  const prijsAan = toonPrijs && prijsBeschikbaar;
  // Contractprijs = de verkoopprijs van de afname: coeff × prijs + majoratie, met
  // de voorwaarden uit de laatste factuur. Eén lijn kan alleen wanneer alle
  // aangevinkte aansluitingen DEZELFDE afnamevoorwaarden dragen; anders zou de
  // lijn voor een deel van de selectie een ander contract tekenen. Zonder
  // coëfficiënt is er geen prijs (zie verkoopprijs() in src/metering.js).
  const [toonContract, setToonContract] = useState(false);
  const contractTermen = useMemo(() => {
    if (!actieveTarieven.length) return null;
    const eerste = actieveTarieven[0].t;
    const zelfde = actieveTarieven.every(({ t }) => t.coeffOfftake === eerste.coeffOfftake && t.majOfftake === eerste.majOfftake);
    return zelfde && eerste.coeffOfftake ? eerste : null;
  }, [actieveTarieven]);
  const contractUitleg = !prijsBeschikbaar ? null
    : !actieveTarieven.length ? 'Geen contractvoorwaarden gevonden voor deze selectie (geen factuur met tarieven).'
      : !contractTermen && actieveTarieven.length > 1 && !actieveTarieven.every(({ t }) => t.coeffOfftake === actieveTarieven[0].t.coeffOfftake && t.majOfftake === actieveTarieven[0].t.majOfftake)
        ? 'Verschillende contractvoorwaarden in deze selectie — vink aansluitingen met dezelfde voorwaarden aan voor een contractprijs.'
        : !contractTermen ? 'De contractvoorwaarden dragen geen coëfficiënt — geen contractprijs.' : null;
  const contractBeschikbaar = prijsBeschikbaar && Boolean(contractTermen);
  const contractAan = toonContract && contractBeschikbaar;
  const prijsAsAan = prijsAan || contractAan;
  // Kleur van de rechteras: die van de enige lijn, of neutraal als er twee staan.
  const prijsAsKleur = prijsAan && contractAan ? '#94a3b8' : contractAan ? MTR_CONTRACT : MTR_SPOT;
  const [spotItems, setSpotItems] = useState([]);
  const [spotLaadt, setSpotLaadt] = useState(false);
  // De spotreeks halen we alleen op wanneer ze ook echt de bron is: bij een
  // indexcontract zou het een ophaal van 35.000 kwartierprijzen zijn voor een lijn
  // die er niet komt.
  const spotNodig = prijsBeschikbaar && prijsBronActief?.soort === 'spot';
  useEffect(() => {
    if (!(toonPrijs || toonContract) || !spotNodig || !token) { setSpotItems([]); return undefined; }
    let leeft = true;
    setSpotLaadt(true);
    fetch(`/api/lynx/spot/range?from=${range.from}&to=${range.to}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => { if (leeft) setSpotItems(d.items || []); })
      .catch(() => { if (leeft) setSpotItems([]); })
      .finally(() => { if (leeft) setSpotLaadt(false); });
    return () => { leeft = false; };
  }, [toonPrijs, toonContract, spotNodig, token, range.from, range.to]);

  // Spot naar dezelfde buckets als de grafiek. Een bucket krijgt het GEMIDDELDE
  // van zijn kwartierprijzen — optellen zou een prijs × aantal kwartieren geven
  // en dat is geen prijs meer.
  const spotPerBucket = useMemo(() => {
    const som = new Map();
    for (const it of spotItems) {
      const k = bucketKey(it.from, chartGranularity);
      if (!k) continue;
      const cur = som.get(k) || { totaal: 0, n: 0 };
      cur.totaal += Number(it.priceEurMwh) || 0;
      cur.n += 1;
      som.set(k, cur);
    }
    const uit = new Map();
    for (const [k, v] of som) uit.set(k, v.n ? v.totaal / v.n : null);
    return uit;
  }, [spotItems, chartGranularity]);
  // Alleen wat de grafiek tekent: actieve energie (MTR_CHART_ANALYSIS), samengevat
  // tot de resolutie die op het scherm komt. De uitkomst is bucket voor bucket
  // identiek aan die van de volledige set — nagerekend op echte data — maar het
  // antwoord is ~95% kleiner.
  const fetchWindow = useCallback((from, to) => Promise.all(activeGsrns.map((g) =>
    mtrFetch(token, g.gsrn, localDayWindowUtc(from, to).fromUtc, localDayWindowUtc(from, to).toUtc, MTR_CHART_ANALYSIS, { granularity: chartGranularity })
      .then((d) => ({ gsrn: g.gsrn, market: g.market, entities: d.entities || [] }))
      .catch(() => ({ gsrn: g.gsrn, market: g.market, entities: [] })))), [token, activeGsrns, chartGranularity]);
  useEffect(() => {
    if (!token || !activeGsrns.length) { setSeriesRaw([]); return undefined; }
    let cancelled = false; setSeriesLoading(true);
    fetchWindow(range.from, range.to)
      .then((rows) => { if (!cancelled) { setSeriesRaw(rows); setLoadedAt(new Date()); } })
      .finally(() => { if (!cancelled) setSeriesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeKey, range.from, range.to, chartGranularity]);
  // De vorige periode laadt apart en blokkeert niets: de kengetallen staan er al,
  // het verschil verschijnt zodra het binnen is.
  useEffect(() => {
    if (!token || !activeGsrns.length) { setPrevRaw([]); return undefined; }
    let cancelled = false;
    fetchWindow(prevRange.from, prevRange.to).then((rows) => { if (!cancelled) setPrevRaw(rows); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeKey, prevRange.from, prevRange.to, chartGranularity]);

  // Het gekozen venster. Nodig om de verdeling van een meterstand die dóórloopt tot
  // ná de periode af te kappen: in de weergave "2025" horen geen maanden van 2026.
  const clipWindow = useMemo(() => ({ fromDay: range.from, toDay: range.to }), [range.from, range.to]);
  const prevClip = useMemo(() => ({ fromDay: prevRange.from, toDay: prevRange.to }), [prevRange.from, prevRange.to]);
  // Per aansluiting één reeks per stroomrichting (zie siteFlows/pickPrimaryDataset in
  // src/metering.js: datasets van dezelfde EAN worden GEKOZEN, niet opgeteld), daarna
  // over de aansluitingen heen samengevoegd — dát mag wel, want dat is andere energie.
  const combineRaw = useCallback((raws, clip) => combineSites(raws.map((s) => ({
    gsrn: s.gsrn, market: s.market,
    address: gsrnMeta.get(s.gsrn)?.address || '', name: gsrnMeta.get(s.gsrn)?.name || '',
    flows: siteFlows(dedupeNewest(s.entities).rows, chartGranularity, { clip }),
  })), { quarters: chartGranularity === 'quarter' }), [gsrnMeta, chartGranularity]);

  const marketRaw = useMemo(() => seriesRaw.filter((s) => s.market === market), [seriesRaw, market]);
  const combined = useMemo(() => combineRaw(marketRaw, clipWindow), [combineRaw, marketRaw, clipWindow]);
  const prevCombined = useMemo(() => combineRaw(prevRaw.filter((s) => s.market === market), prevClip), [combineRaw, prevRaw, market, prevClip]);
  const allCombined = useMemo(() => combineRaw(seriesRaw, clipWindow), [combineRaw, seriesRaw, clipWindow]);
  const siteTotals = useMemo(() => new Map(allCombined.perSite.map((s) => [s.gsrn, s])), [allCombined]);

  // Houd de focus-GSRN geldig binnen het actieve energietype, en laat hem standaard
  // op de aansluiting met het MEESTE verbruik staan: een detailblok dat 0 kWh toont
  // terwijl er in dezelfde selectie wél data zit, leest als een fout. Koos de
  // gebruiker zélf een aansluiting voor dit energietype (`focusPick`), dan blijft die
  // staan — ook als ze leeg is, want dat kan precies zijn wat hij wil zien. Per
  // energietype apart, zodat wisselen tussen de tabs de keuze aan de andere kant niet
  // wist.
  // Staat hier BEWUST ná siteTotals: die zit in de dependency-array, en een
  // dependency-array wordt tijdens het renderen geëvalueerd — een effect hierboven
  // zou dus een TDZ-fout geven.
  useEffect(() => {
    const inMarket = chartGsrns.filter((g) => (gsrnMeta.get(g)?.market || 'EL') === market);
    setSelGsrn((cur) => {
      if (focusPick.current === market && inMarket.includes(cur)) return cur;
      const best = [...inMarket].sort((a, b) => (siteTotals.get(b)?.verbruik || 0) - (siteTotals.get(a)?.verbruik || 0))[0];
      if (!best) return inMarket.includes(cur) ? cur : (chartGsrns[0] || null);
      // Nog geen enkele meting binnen? Dan is er niets te kiezen — laat staan wat er
      // staat in plaats van te wisselen zodra de eerste bytes binnenkomen.
      if (inMarket.includes(cur) && !(siteTotals.get(best)?.verbruik > 0)) return cur;
      return best;
    });
  }, [chartGsrns, market, gsrnMeta, siteTotals]);

  const tone = MTR_TONE[market] || MTR_TONE.EL;
  const hasProduction = combined.totals.productie > 0;
  // Tijdvakken MET een meting, een gemeten 0 kWh inbegrepen (zie combineSites).
  const filledBuckets = combined.rows.filter((r) => r.data).length;
  const focusStats = useMemo(() => combined.perSite.find((s) => s.gsrn === selGsrn) || siteTotals.get(selGsrn) || null, [combined.perSite, siteTotals, selGsrn]);
  const focusMeta = gsrnMeta.get(selGsrn);

  // ── Labels die met de resolutie meebewegen ────────────────────────────────────
  const bucketWord = viewMode === 'day' ? 'kwartier' : viewMode === 'month' ? 'dag' : 'maand';
  // Aslabel onder de X-as, vet: zegt in één woord waar elke staaf voor staat.
  const asWoordX = viewMode === 'day' ? 'Kwartieren' : viewMode === 'month' ? 'Dagen' : 'Maanden';
  const peakLabel = viewMode === 'day' ? 'Piekkwartier' : viewMode === 'month' ? 'Piekdag' : 'Piekmaand';
  // Eén dag is een klein getal (4,62 kWh): afgerond op hele kWh zegt dat niets meer,
  // dus in weergave Dag altijd twee cijfers na de komma. Maand en jaar blijven op
  // hele kWh.
  const totaalKwh = (v) => viewMode === 'day'
    ? `${Number(v || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh`
    : mtrKwh(v);
  const topTitle = viewMode === 'day' ? 'Topkwartieren' : viewMode === 'month' ? 'Topdagen' : 'Topmaanden';
  // In het rollende jaarvenster staan maanden uit TWEE jaren naast elkaar; daarom
  // draagt elke maandtick zijn jaar ("sep '25"). Zonder dat verschil lees je een
  // reeks van twaalf maanden als één kalenderjaar.
  //
  // LET OP: beide formatters lezen de VORM van de sleutel, niet `viewMode`. Recharts
  // houdt zijn tooltip actief met het label van de VORIGE reeks (na een tik op de
  // grafiek op een telefoon blijft die zelfs staan) en tekent die opnieuw zodra de
  // resolutie wisselt. Dan kwam hier "12:30" binnen terwijl viewMode al 'year'
  // was: Date.UTC(2000, NaN) → Intl.format gooit "Invalid time value" → React haalt
  // de hele pagina weg (wit scherm, gemeld door gebruikers bij snel wisselen).
  // Zie mtrSleutelSoort/mtrMaandUitSleutel: onbekende vorm = de tekst zelf tonen.
  const xLabel = (k) => {
    const soort = mtrSleutelSoort(k);
    if (soort === 'quarter') return k.endsWith(':00') ? k : '';
    if (soort === 'day') return k.substring(8);
    if (soort === 'month') { const [yy, mm] = k.split('-').map(Number); return `${mtrMonthShort.format(new Date(Date.UTC(2000, mm - 1, 1))).replace('.', '')} '${String(yy).slice(-2)}`; }
    return String(k ?? '');
  };
  const tipLabel = (k) => {
    const soort = mtrSleutelSoort(k);
    if (soort === 'quarter') return `Kwartier ${k}`;
    if (soort === 'day') { const [y, m, d] = k.split('-'); return `Dag ${d}/${m}/${y}`; }
    if (soort === 'month') { const [yy, mm] = k.split('-').map(Number); const mo = mtrMonthLong.format(new Date(Date.UTC(2000, mm - 1, 1))); return `${mo.charAt(0).toUpperCase()}${mo.slice(1)} ${yy}`; }
    return String(k ?? '');
  };
  // De spotprijs deelt de tooltip met de kWh-reeksen maar heeft een andere eenheid.
  // Zonder eigen opmaak zou "142,5 kWh" er staan waar € 142,50/MWh bedoeld is.
  const tipWaarde = (v, n) => [String(n).includes('EUR/MWh') ? `${mtrFmtNum(v, 2)} EUR/MWh` : mtrKwh(v, 1), n];
  // Kort en Belgisch: DD/MM/YYYY voor een dag, "Maand JJJJ" voor een maand.
  const bucketDisplay = (k) => {
    if (viewMode === 'day') return k;
    const p = String(k).split('-');
    if (p.length === 3) return `${p[2]}/${p[1]}/${p[0]}`;
    const mo = mtrMonthLong.format(new Date(Date.UTC(2000, Number(p[1]) - 1, 1)));
    return `${mo.charAt(0).toUpperCase()}${mo.slice(1)} ${p[0]}`;
  };
  // Het jaarvenster eindigt op de anchor-MAAND (rollend), dus "verder kan niet" gaat
  // over de maand, niet over het jaar.
  const atMax = viewMode === 'day' ? anchor >= mtrToday() : anchor.substring(0, 7) >= mtrToday().substring(0, 7);
  const prevPeriodLabel = mtrAnchorLabel(viewMode, mtrShiftAnchor(viewMode, anchor, -1), kalenderJaar ? kalenderJaar - 1 : null);
  // "vs sep 2024 – aug 2025" wordt te lang voor het kengetal; bij een rollend jaar
  // volstaat de vergelijking zelf.
  const vsLabel = viewMode === 'year' ? (kalenderJaar ? `vs ${prevPeriodLabel.replace('Kalenderjaar ', '')}` : 'vs vorige 12 maanden') : `vs ${prevPeriodLabel}`;

  // ── Tabelgegevens (ruwe meetgegevens van de focus-aansluiting) ────────────────
  // De ruwe metingen (mét de verouderde Rectified-rijen, want dat detail is het punt
  // van deze tabellen) gaan per CATEGORIE in een eigen tabel. De afgeleide
  // kwartier-/dag-/maandtabellen werken op de ONTDUBBELDE set: per periode de rij
  // met de nieuwste validFromUTC, zodat een herziene meting niet dubbel telt.
  const tableEntities = useMemo(() => data?.entities || [], [data]);
  const deduped = useMemo(() => dedupeNewest(tableEntities), [tableEntities]);
  const categoryTables = useMemo(() => splitByCategory(tableEntities), [tableEntities]);
  const quarterRows = useMemo(() => mtrReadingRows(deduped.rows, { clip: clipWindow }), [deduped.rows, clipWindow]);
  const focusDatasets = useMemo(() => energyDatasets(deduped.rows, chartGranularity, ENERGY_METHODS, { clip: clipWindow }), [deduped.rows, chartGranularity, clipWindow]);
  const dayAgg = useMemo(() => aggregateBuckets(deduped.rows, 'day', { clip: clipWindow }), [deduped.rows, clipWindow]);
  const monthAgg = useMemo(() => aggregateBuckets(deduped.rows, 'month', { clip: clipWindow }), [deduped.rows, clipWindow]);
  const fileTag = `${selGsrn}-${range.from}_${range.to}`;

  const openRaw = () => { setRawOpen(true); setTimeout(() => rawRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); };

  const showVerbruik = showFlow === 'alles' || showFlow === 'verbruik';
  const showProductie = hasProduction && (showFlow === 'alles' || showFlow === 'productie');
  const showNetto = hasProduction && (showFlow === 'alles' || showFlow === 'netto');
  const chartEmpty = !combined.rows.some((r) => r.verbruik || r.productie);

  // De aansluitingen zoals ze in de kop van de grafiek komen te staan: die waar de
  // grafiek écht cijfers voor heeft (combined.perSite), met terugval op de selectie
  // zolang de meetgegevens nog binnenkomen. Staat hier BEWUST ná `combined` — er
  // eerder naar verwijzen is een TDZ-fout en dus een leeg scherm.
  const chartGsrnLijst = useMemo(
    () => (combined.perSite.length ? combined.perSite.map((s) => s.gsrn) : activeMarketGsrns.map((g) => g.gsrn)),
    [combined.perSite, activeMarketGsrns],
  );

  // ── De prijs per bucket ──────────────────────────────────────────────────────
  // Twee bronnen, dezelfde vorm: sleutel van de bucket → prijs in EUR/MWh.
  //   spot  → het gemiddelde van de kwartierprijzen in die bucket (spotPerBucket).
  //   index → de MAANDwaarde uit de instellingen. Op de jaarweergave is dat één
  //           waarde per staaf; op de maand- of dagweergave herhaalt dezelfde
  //           maandwaarde zich, want fijner IS de index niet. Een maand zonder
  //           ingestelde waarde blijft leeg (geen 0, geen buurmaand).
  const prijsPerBucket = useMemo(() => {
    if (!prijsBronActief) return new Map();
    if (prijsBronActief.soort === 'spot') return spotPerBucket;
    const uit = new Map();
    for (const r of combined.rows) {
      const v = indexEurMwh(indexParams, prijsBronActief.code, maandVanBucket(r.key, chartGranularity, anchor));
      if (v != null) uit.set(r.key, v);
    }
    return uit;
  }, [prijsBronActief, spotPerBucket, combined.rows, indexParams, chartGranularity, anchor]);

  // Maanden in beeld waarvoor er géén indexwaarde is ingesteld. Die buckets krijgen
  // geen prijs, en dat hoort de pagina te zeggen in plaats van een gat te tonen.
  const indexGaten = useMemo(() => {
    // Nog niet geladen (of niet op te halen) is iets anders dan "niet ingesteld";
    // dan zou deze lijst élke maand van het venster opsommen terwijl er alleen nog
    // een fetch onderweg is.
    if (prijsBronActief?.soort !== 'index' || !indexParams) return [];
    const missend = new Set();
    for (const r of combined.rows) {
      const maand = maandVanBucket(r.key, chartGranularity, anchor);
      if (maand && indexEurMwh(indexParams, prijsBronActief.code, maand) == null) missend.add(maand);
    }
    return [...missend].sort();
  }, [prijsBronActief, combined.rows, indexParams, chartGranularity, anchor]);

  // De rijen die de grafiek tekent, met de prijs erbij wanneer die aan staat.
  // Een aparte memo i.p.v. de prijs in `combined` mengen: `combined` gaat ook naar
  // de kengetallen, de toplocaties en de export, en die hebben niets met een prijs
  // te maken.
  const chartRows = useMemo(() => {
    if (!prijsAsAan || !prijsPerBucket.size) return combined.rows;
    return combined.rows.map((r) => {
      const basis = prijsPerBucket.has(r.key) ? prijsPerBucket.get(r.key) : null;
      return {
        ...r,
        ...(prijsAan ? { prijs: basis } : {}),
        ...(contractAan ? { contract: verkoopprijs(contractTermen, basis, 'afname') } : {}),
      };
    });
  }, [combined.rows, prijsAsAan, prijsAan, contractAan, prijsPerBucket, contractTermen]);

  // ── Jaargrens in het rollende jaarvenster ────────────────────────────────────
  // Maanden van het VORIGE jaar krijgen een doffere staaf en er komt een stippellijn
  // op de overgang te staan. Zo zie je in één oogopslag dat "sep" links en "jan"
  // rechts niet uit hetzelfde jaar komen — anders lijkt het één doorlopende reeks.
  const eindJaar = mtrKeyYear(anchor);
  const isVorigJaar = (k) => viewMode === 'year' && mtrKeyYear(k) < eindJaar;
  const jaarGrensKey = useMemo(() => {
    if (viewMode !== 'year') return null;
    for (let i = 1; i < combined.rows.length; i++) {
      if (mtrKeyYear(combined.rows[i].key) !== mtrKeyYear(combined.rows[i - 1].key)) return combined.rows[i].key;
    }
    return null;
  }, [combined.rows, viewMode]);

  // ── PDF-rapport: wat NU op het scherm staat ──────────────────────────────────
  // Zelfde selectie, zelfde periode, zelfde reeksen als de grafiek van dat
  // moment: de klant/aansluitingen die aangevinkt zijn, het energietype, de
  // periode en resolutie, de aan- en uitgezette reeksen (verbruik, productie,
  // netto, prijs) en het rollende jaarvenster met zijn doffe staven. De grafiek
  // wordt zelf getekend (reportCharts.js) — de Recharts-SVG van het scherm is
  // thema-gekleurd en meet 0×0 in een verborgen tab. De server maakt er een
  // echte PDF van (POST /api/report/pdf, headless Chromium).
  const [pdfBezig, setPdfBezig] = useState(false);
  // Het dagprofiel (MtrDagprofielKaart) meldt hier wat het getekend heeft; het
  // rapport neemt dat over zodra er een profiel berekend is. Een ref, geen state:
  // het rapport leest het pas bij de klik, de pagina hoeft er niet voor te renderen.
  const dagprofielRapport = useRef(null);
  const bewaarDagprofiel = useRef((s) => { dagprofielRapport.current = s; }).current;
  // Klantnaam zoals in de tabellen: de onderneming bij een zakelijke klant, anders
  // de persoon. Het energielabel van de selectie komt in de kop van het rapport.
  const rapportConn = conns.find((c) => (selRef && c.ref === selRef) || (selGsrn && c.gsrn === selGsrn)) || null;
  const klantNaamRapport = (rapportConn && klantWeergave({ naam: rapportConn.name, ref: rapportConn.ref, bedrijf: rapportConn.bedrijf, segment: rapportConn.segment }).display)
    || (emailCustomers.find((c) => c.ref === selRef) || {}).name
    || (combined.perSite.find((s) => s.name) || {}).name
    || (selRef ? `Klant ${selRef}` : selGsrn || '');
  const rapportLabel = (rapportConn && rapportConn.company) || currentUser?.energyLabel || '';
  const buildMeteringReportHtml = () => {
    const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const rt = (v) => esc(hideEtBrand(String(v == null ? '' : v)));
    const isEt = (v) => /energy\s*together/i.test(String(v || ''));
    // Huisstijl van het rapport. Een partnerrol krijgt zijn eigen huisstijl (de
    // server heeft label en verkoopteam al samengevoegd). Admin en ET-dossier
    // dragen zelf de ET-huisstijl en die hoort niet op een klantrapport: dan
    // geldt de huisstijl van het energielabel waar de klant bij hoort, met de
    // teamhuisstijl van zijn verkoopteam eroverheen — zoals het Revenue-rapport.
    const eigen = branding && !isEt(branding.energy_label) && !isEt(branding.display_name) ? branding : null;
    let merk = eigen;
    if (!merk && rapportLabel && !isEt(rapportLabel)) {
      const basis = (allBranding || []).find((b) => b.energy_label === rapportLabel) || null;
      const team = rapportConn && rapportConn.team
        ? (allTeamBranding || []).find((b) => b.energy_label === rapportLabel && b.sales_team === rapportConn.team) || null
        : null;
      merk = mergeBranding(basis, team);
      if (merk && (isEt(merk.energy_label) || isEt(merk.display_name))) merk = null;
    }
    const primair = merk?.primary_color || '#4f46e5';
    const labelNaam = merk?.display_name || merk?.energy_label || hideEtBrand(rapportLabel) || '';
    const logo = merk?.logo_data_url
      ? `<img class="logo" src="${esc(merk.logo_data_url)}" alt="${rt(labelNaam)}" />`
      : `<div class="logo-fallback">${rt(labelNaam)}</div>`;
    const nu = new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
    const periodeTekst = mtrAnchorLabel(viewMode, anchor, kalenderJaar);
    const resolutieTekst = viewMode === 'day' ? 'Dag (kwartieren)' : viewMode === 'month' ? 'Maand (dagen)' : kalenderJaar ? 'Kalenderjaar (maanden)' : 'Rollend jaar (maanden)';

    // Staven of lijnen: zoals de schakelaar op het scherm staat.
    const stromen = [
      ...(showVerbruik ? [{ key: 'verbruik', naam: 'Verbruik', kleur: tone.bar }] : []),
      ...(showProductie ? [{ key: 'productie', naam: 'Productie', kleur: tone.prod }] : []),
    ];
    const staven = chartType === 'line' ? [] : stromen;
    const lijnen = [
      ...(chartType === 'line' ? stromen.map((s) => ({ ...s, as: 'links' })) : []),
      ...(showNetto ? [{ key: 'netto', naam: 'Netto verbruik', kleur: MTR_NET, as: 'links' }] : []),
      ...(prijsAan ? [{ key: 'prijs', naam: prijsSerieNaam, kleur: MTR_SPOT, as: 'rechts', streep: true }] : []),
      ...(contractAan ? [{ key: 'contract', naam: 'Contractprijs (EUR/MWh)', kleur: MTR_CONTRACT, as: 'rechts' }] : []),
    ];
    const rijen = chartRows.map((r) => ({ key: r.key, label: xLabel(r.key), dof: isVorigJaar(r.key), waarden: r }));
    const grafiek = chartEmpty
      ? meetGrafiekSvg({ rijen: [] })
      : meetGrafiekSvg({ rijen, staven, lijnen, breedte: 703, hoogte: 300, eenheid: 'kWh', grens: jaarGrensKey, grensLabel: jaarGrensKey ? String(eindJaar) : '' });
    const legende = [
      ...staven.map((s) => `<span class="lg"><span class="stip" style="background:${esc(s.kleur)}"></span>${rt(s.naam)} (kWh)</span>`),
      ...lijnen.map((l) => `<span class="lg"><span class="lijn" style="background:${esc(l.kleur)}"></span>${rt(l.naam)}${l.as === 'rechts' ? ' — as rechts' : ' (kWh)'}</span>`),
      ...(jaarGrensKey ? [`<span class="lg"><span class="stip" style="background:${esc(tone.bar)};opacity:.45"></span>doffe staven = ${eindJaar - 1}</span>`] : []),
    ].join('');

    // Over hoeveel maanden het totaal gaat: in de jaarweergave de maanden mét een
    // meting, in de maand- en dagweergave de ene maand of dag met zijn tijdvakken.
    const maandenData = new Set(combined.rows.filter((r) => r.data).map((r) => String(r.key).slice(0, 7))).size;
    const dekkingTekst = viewMode === 'year' ? `${maandenData} maand${maandenData === 1 ? '' : 'en'} met data`
      : viewMode === 'month' ? `1 maand · ${filledBuckets} dag${filledBuckets === 1 ? '' : 'en'} met data`
        : `1 dag · ${filledBuckets} kwartier${filledBuckets === 1 ? '' : 'en'} met data`;
    const deltaVerbruik = pctDelta(combined.totals.verbruik, prevCombined.totals.verbruik);
    // Het aantal aansluitingen staat al in de kop van het rapport, niet nog eens hier.
    const kpis = [
      ['Verbruik totaal', totaalKwh(combined.totals.verbruik), `${dekkingTekst} · ${vsLabel}: ${deltaVerbruik == null ? '—' : `${deltaVerbruik > 0 ? '+' : ''}${mtrFmtNum(deltaVerbruik, 1)} %`}`],
      ...(hasProduction ? [
        ['Productie totaal', totaalKwh(combined.totals.productie), ''],
        ['Netto verbruik', totaalKwh(combined.totals.netto), ''],
      ] : []),
      [`Gemiddeld per ${bucketWord}`, mtrKwh(filledBuckets ? combined.totals.verbruik / filledBuckets : 0, 1), `${filledBuckets} ${bucketWord}${filledBuckets === 1 ? '' : 'en'} met data`],
      [`${peakLabel} (verbruik)`, combined.peak ? mtrKwh(combined.peak.value, 1) : '—', combined.peak ? bucketDisplay(combined.peak.key) : 'geen verbruik in deze periode'],
    ];
    const kpiHtml = kpis.map(([l, v, s]) => `<div class="kpi"><div class="kpi-label">${rt(l)}</div><div class="kpi-value">${rt(v)}</div>${s ? `<div class="kpi-sub">${rt(s)}</div>` : ''}</div>`).join('');

    // Per aansluiting: de cijfers achter de grafiek, in dezelfde selectie.
    const siteRijen = combined.perSite.map((s) => `<tr>
      <td class="ean">${esc(s.gsrn)}</td><td>${rt(s.address || '—')}</td>
      <td class="r">${esc(mtrFmtNum(s.verbruik, 0))}</td>
      ${hasProduction ? `<td class="r">${esc(mtrFmtNum(s.productie, 0))}</td><td class="r">${esc(mtrFmtNum(s.netto, 0))}</td>` : ''}
      <td class="r">${s.peak ? `${esc(mtrFmtNum(s.peak.value, 1))} <span class="muted">${rt(bucketDisplay(s.peak.key))}</span>` : '—'}</td>
    </tr>`).join('');

    // De reeks zelf, als tabel — zodat het rapport ook zonder de grafiek te lezen is.
    const kolommen = ['Tijdvak', ...(showVerbruik ? ['Verbruik (kWh)'] : []), ...(showProductie ? ['Productie (kWh)'] : []), ...(showNetto ? ['Netto (kWh)'] : []), ...(prijsAan ? [prijsSerieNaam] : []), ...(contractAan ? ['Contractprijs (EUR/MWh)'] : [])];
    const reeksRijen = chartRows.map((r) => `<tr>
      <td>${rt(bucketDisplay(r.key))}</td>
      ${showVerbruik ? `<td class="r">${esc(mtrFmtNum(r.verbruik, viewMode === 'day' ? 3 : 0))}</td>` : ''}
      ${showProductie ? `<td class="r">${esc(mtrFmtNum(r.productie, viewMode === 'day' ? 3 : 0))}</td>` : ''}
      ${showNetto ? `<td class="r">${esc(mtrFmtNum(r.netto, viewMode === 'day' ? 3 : 0))}</td>` : ''}
      ${prijsAan ? `<td class="r">${r.prijs == null ? '—' : esc(mtrFmtNum(r.prijs, 2))}</td>` : ''}
      ${contractAan ? `<td class="r">${r.contract == null ? '—' : esc(mtrFmtNum(r.contract, 2))}</td>` : ''}
    </tr>`).join('');

    const tarievenHtml = prijsAsAan && actieveTarieven.length
      ? `<p class="noot">Prijsreeks: ${rt(prijsNaam)}. De contractprijs (verkoopprijs) volgt de contractvoorwaarden van de meest recente factuur per aansluiting${actieveTarieven.length === 1 ? '' : ` (${actieveTarieven.length} aansluitingen)`}.</p>`
      : '';

    // Dagprofiel: alleen wanneer het op het scherm berekend is, en dan precies zoals
    // het daar staat (periode, dagkeuze, band, vergelijkdag, spotprijs).
    const dp = dagprofielRapport.current;
    let dagprofielHtml = '';
    if (dp && dp.rijen.length) {
      const dagNaam = (d) => (d ? `${MTR_WEEKDAGEN[d.wd - 1]} ${mtrBeDatum(d.datum)}` : '');
      const apart = dp.groep === 'apart';
      const dpLijnen = [
        ...(apart
          ? MTR_WEEKDAG_KORT.map((k, i) => ({ key: `wd${i + 1}`, naam: MTR_WEEKDAGEN[i], kleur: MTR_WEEKDAG_KLEUR[i], as: 'links' }))
          : [{ key: 'gem', naam: 'Gemiddeld verbruik', kleur: tone.bar, as: 'links' }]),
        ...(!apart && dp.heeftProductie ? [{ key: 'gemProd', naam: 'Gemiddelde productie', kleur: tone.prod, as: 'links' }] : []),
        ...(dp.dag ? [{ key: 'dag', naam: `Verbruik ${dagNaam(dp.dag)}`, kleur: MTR_NET, as: 'links', streep: true }] : []),
        ...(dp.dag && dp.heeftProductie ? [{ key: 'dagProd', naam: `Productie ${dagNaam(dp.dag)}`, kleur: tone.prodInk, as: 'links', streep: true }] : []),
        ...(dp.spotAan ? [{ key: 'spot', naam: 'Gemiddelde spotprijs', kleur: MTR_SPOT, as: 'rechts', streep: true }] : []),
        ...(dp.spotAan && dp.dag && dp.data.spot && dp.data.spot.dag ? [{ key: 'spotDag', naam: `Spotprijs ${dagNaam(dp.dag)}`, kleur: MTR_SPOT, as: 'rechts', streep: true }] : []),
      ];
      const dpBanden = dp.toonBand ? [{ laag: 'bandLaag', hoog: 'bandHoog', kleur: tone.bar }] : [];
      // 96 kwartieren; een tijdlabel om de twee uur houdt de as leesbaar.
      const dpRijen = dp.rijen.map((r, i) => ({ key: r.key, label: i % 8 === 0 ? r.key : '', waarden: { ...r, bandLaag: r.band ? r.band[0] : null, bandHoog: r.band ? r.band[1] : null } }));
      const dpGrafiek = meetGrafiekSvg({ rijen: dpRijen, lijnen: dpLijnen, banden: dpBanden, breedte: 703, hoogte: 250, eenheid: 'kWh per kwartier' });
      const dpLegende = [
        ...dpLijnen.map((l) => `<span class="lg"><span class="lijn" style="background:${esc(l.kleur)}"></span>${rt(l.naam)}${l.as === 'rechts' ? ' (EUR/MWh, as rechts)' : ''}</span>`),
        ...(dp.toonBand ? [`<span class="lg"><span class="stip" style="background:${esc(tone.bar)};opacity:.3"></span>laagste – hoogste per kwartier</span>`] : []),
      ].join('');
      const dpTitel = mtrProfielGroepNaam(dp.groep, apart ? dp.data.aantalDagen : dp.groepData.dagen);
      const dpCijfers = [
        ...(!apart && dp.groepData ? [`gemiddeld ${mtrKwh(dp.groepData.verbruik.dagTotaal, 1)} verbruik per dag${dp.heeftProductie && dp.groepData.productie ? ` · productie ${mtrKwh(dp.groepData.productie.dagTotaal, 1)}` : ''}`] : []),
        ...(apart ? [MTR_WEEKDAGEN.map((n, i) => `${MTR_WEEKDAG_KORT[i]} ${mtrKwh(dp.data.groepen[String(i + 1)].verbruik.dagTotaal, 1)}`).join(' · ')] : []),
        ...(dp.dag ? [`${dagNaam(dp.dag)}: ${mtrKwh(dp.dag.verbruikTotaal, 1)}${dp.verschil != null && !apart ? ` (${dp.verschil > 0 ? '+' : ''}${mtrFmtNum(dp.verschil, 1)} % t.o.v. het gemiddelde)` : ''}`] : []),
        ...(dp.spotAan && dp.spotGem != null ? [`gemiddelde spotprijs ${mtrFmtNum(dp.spotGem, 1)} EUR/MWh`] : []),
      ];
      dagprofielHtml = `<section class="blok"><h2>Dagprofiel</h2>
    <div class="dp-kop"><span class="dp-titel">${rt(dpTitel)}</span><span class="dp-sub">${esc(mtrBeDatum(dp.data.vanDag))} – ${esc(mtrBeDatum(dp.data.totDag))} · laatste ${esc(String(dp.maanden))} maand${dp.maanden === 1 ? '' : 'en'} · het gemiddelde verloop over 24 uur, per kwartier</span></div>
    <div class="grafiek"><div class="legende">${dpLegende}</div>${dpGrafiek}${dpCijfers.length ? `<p class="noot">${rt(dpCijfers.join(' · '))}</p>` : ''}</div>
  </section>`;
    }

    return `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>${exportTimestamp()}${rt(`Meetrapport — ${klantNaamRapport || 'selectie'}`)}</title>
<style>
  :root { --primair: ${esc(primair)}; }
  * { box-sizing: border-box; }
  body { font-family: ${merk?.font_family || "'Segoe UI', system-ui, -apple-system, Arial, sans-serif"}; color: #1e293b; margin: 0; font-size: 11px; line-height: 1.45; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .vel { max-width: 190mm; margin: 0 auto; padding: 6mm 0 12mm; }
  /* Kop en kenmerkenbalk compact: de grafiek moet zo hoog mogelijk op de pagina komen. */
  header { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding-bottom: 6px; border-bottom: 3px solid var(--primair); }
  .logo { max-height: 40px; max-width: 200px; object-fit: contain; display: block; }
  .logo-fallback { font-size: 18px; font-weight: 700; color: var(--primair); }
  .kop-rechts { text-align: right; }
  .kop-rechts h1 { margin: 0; font-size: 17px; color: #0f172a; letter-spacing: -0.01em; line-height: 1.2; }
  .kop-rechts .sub { color: #64748b; font-size: 10px; margin-top: 1px; }
  .meta { display: flex; flex-wrap: wrap; margin: 6px 0 8px; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; }
  .meta div { flex: 1 1 25%; min-width: 110px; padding: 4px 10px; border-right: 1px solid #e2e8f0; }
  .meta div:last-child { border-right: 0; }
  .meta .k { color: #64748b; font-size: 8.5px; text-transform: uppercase; letter-spacing: .04em; }
  .meta .v { color: #0f172a; font-weight: 600; font-size: 11px; margin-top: 0; overflow-wrap: anywhere; }
  .meta .v .muted { font-weight: 400; font-size: 9px; }
  h2 { font-size: 12px; color: #0f172a; margin: 10px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: .05em; }
  .blok:first-of-type h2 { margin-top: 0; }
  .dp-kop { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 12px; margin: 0 0 6px; }
  .dp-titel { font-size: 13px; font-weight: 700; color: #0f172a; }
  .dp-sub { color: #64748b; font-size: 9.5px; }
  /* Kerncijfers even compact als de kop: één rij tegels, klein label, kengetal, een regel uitleg. */
  .kpis { display: grid; grid-template-columns: repeat(${hasProduction ? 5 : 3}, 1fr); gap: 6px; }
  .kpi { border: 1px solid #e2e8f0; border-left: 3px solid var(--primair); border-radius: 5px; padding: 4px 9px; }
  .kpi-label { color: #64748b; font-size: 8.5px; text-transform: uppercase; letter-spacing: .04em; }
  .kpi-value { color: #0f172a; font-size: 12.5px; font-weight: 700; margin-top: 0; line-height: 1.25; }
  .kpi-sub { color: #64748b; font-size: 8px; margin-top: 1px; }
  .grafiek { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px 4px; }
  .legende { display: flex; flex-wrap: wrap; gap: 4px 14px; margin: 0 0 6px; font-size: 9.5px; color: #475569; }
  .lg { display: inline-flex; align-items: center; gap: 5px; }
  .stip { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  .lijn { width: 14px; height: 2px; display: inline-block; border-radius: 1px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th { background: #f1f5f9; text-align: left; padding: 5px 8px; font-weight: 600; color: #334155; border-bottom: 1.5px solid #cbd5e1; white-space: nowrap; }
  td { padding: 4px 8px; border-bottom: 1px solid #eef2f7; }
  th.r, td.r { text-align: right; white-space: nowrap; }
  td.ean { font-family: ui-monospace, Menlo, Consolas, monospace; color: var(--primair); white-space: nowrap; }
  .muted { color: #94a3b8; }
  .noot { color: #64748b; font-size: 9.5px; margin: 8px 0 0; }
  .blok { margin-bottom: 10px; }
  footer { margin-top: 12px; padding-top: 6px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 9px; display: flex; justify-content: space-between; }
  @page { size: A4 portrait; margin: 12mm; }
  @media print { .vel { max-width: none; padding: 0; } thead { display: table-header-group; } tr, .kpi, .blok, .grafiek { break-inside: avoid; page-break-inside: avoid; } h2 { break-after: avoid; } }
</style>
</head>
<body>
<div class="vel">
  <header>
    <div>${logo}</div>
    <div class="kop-rechts"><h1>Meetrapport</h1><div class="sub">${rt(tone.label)} · ${rt(periodeTekst)}</div><div class="sub">Opgemaakt op ${esc(nu)}</div></div>
  </header>
  <div class="meta">
    <div><div class="k">Klant</div><div class="v">${rt(klantNaamRapport || '—')}</div></div>
    <div><div class="k">Aansluitingen in grafiek</div><div class="v">${esc(String(chartGsrnLijst.length))} <span class="muted">· ${esc(String(combined.perSite.filter((s) => s.verbruik > 0).length))} met verbruik</span></div></div>
    <div><div class="k">Periode</div><div class="v">${rt(periodeTekst)}</div></div>
    <div><div class="k">Resolutie</div><div class="v">${rt(resolutieTekst)}</div></div>
  </div>
  <section class="blok"><h2>Kerncijfers</h2><div class="kpis">${kpiHtml}</div></section>
  <section class="blok"><h2>Per aansluiting</h2>
    <table><thead><tr><th>EAN</th><th>Adres</th><th class="r">Verbruik (kWh)</th>${hasProduction ? '<th class="r">Productie (kWh)</th><th class="r">Netto (kWh)</th>' : ''}<th class="r">${rt(peakLabel)}</th></tr></thead>
    <tbody>${siteRijen || '<tr><td colspan="6" class="muted">Geen aansluitingen in de selectie.</td></tr>'}</tbody></table>
  </section>
  <section class="blok grafiek">
    <div class="legende">${legende}</div>
    ${grafiek}
    ${tarievenHtml}
  </section>
  ${dagprofielHtml}
  <section class="blok"><h2>Reeks per ${rt(bucketWord)}</h2>
    <table><thead><tr>${kolommen.map((k, i) => `<th${i ? ' class="r"' : ''}>${rt(k)}</th>`).join('')}</tr></thead>
    <tbody>${reeksRijen || `<tr><td colspan="${kolommen.length}" class="muted">Geen meetgegevens voor deze periode.</td></tr>`}</tbody></table>
  </section>
  <footer><span>${rt(labelNaam)} — meetgegevens per aansluiting, altijd in kWh</span><span>${esc(nu)}</span></footer>
</div>
</body>
</html>`;
  };
  const exportMeteringPdf = async () => {
    if (pdfBezig) return;
    setPdfBezig(true);
    try {
      const r = await fetch('/api/report/pdf', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/html; charset=utf-8' },
        body: buildMeteringReportHtml(),
      });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || `HTTP ${r.status}`); }
      const blob = await r.blob();
      downloadBlob(`${exportTimestamp()}meetrapport_${String(klantNaamRapport || selGsrn || 'selectie').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').toLowerCase()}.pdf`, blob);
      toast.success('Meetrapport als PDF gedownload');
    } catch (e) {
      toast.error(`PDF maken mislukt: ${e.message}`);
    } finally { setPdfBezig(false); }
  };

  // ── Detailexport: meetwaarden met prijzen, eigen tijdvenster ────────────────
  // De ENIGE export van de grafiek. Er stond er een tweede naast ("de getoonde
  // reeks"), die de som over alle aangevinkte aansluitingen op de resolutie van de
  // grafiek gaf. Twee knoppen naast elkaar met elk een ander bestand vroeg telkens
  // uitleg; deze levert hetzelfde en meer, per aansluiting.
  //
  // Ze gaat naar de KLEINST beschikbare korrel over een zelf gekozen periode, met
  // de marktprijs en de daaruit berekende verkoopprijs erbij — per aansluiting,
  // want de coëfficiënt en de majoratie verschillen per contract. De prijskolommen
  // zijn een KEUZE in het dialoogvenster: wie enkel het verbruiksprofiel nodig
  // heeft, houdt een bestand van vier kolommen over.
  //
  // "Kleinst beschikbaar" hangt af van de meter: bij SMR3/AMR is dat het kwartier,
  // bij SMR1/MMR/YMR de maand. Een maandstand pro rata over 96 kwartieren
  // uitsmeren zou een profiel verzinnen dat de meter nooit gemeten heeft, met een
  // prijs eraan — dus dan levert deze export maandregels.
  const detailKwartier = activeMeters.length > 0 && activeMeters.every((m) => meterKlasse(m) === 'kwartier');
  // Einddag van het dagprofiel: de dag van de hoofdgrafiek, of de laatste dag van
  // haar periode — en nooit later dan vandaag (de jaarweergave eindigt op het
  // einde van de lopende maand).
  const dagprofielTot = range.to > mtrToday() ? mtrToday() : range.to;
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailVan, setDetailVan] = useState(range.from);
  // "Tot" is de eerste dag die NIET meer meetelt (platformregel, src/datumFilter.js);
  // de getoonde periode eindigt inclusief op range.to, dus de dag erna.
  const [detailTot, setDetailTot] = useState(totNa(range.to));
  const [detailBezig, setDetailBezig] = useState(false);
  const [detailFout, setDetailFout] = useState(null);
  // Prijskolommen aan/uit. Standaard AAN, zoals de export ze altijd al gaf.
  // "Marktprijs" is de spot- of indexprijs; "verkoopprijs" is die prijs mét de
  // coëfficiënt en de majoratie van het contract. Beide uit = enkel het profiel.
  const [detailMetMarkt, setDetailMetMarkt] = useState(true);
  const [detailMetVerkoop, setDetailMetVerkoop] = useState(true);
  // Of er überhaupt een prijs te zetten valt, hangt hier NIET aan de resolutie van
  // de grafiek (prijsBeschikbaar): deze export kiest haar eigen korrel, die per
  // definitie bij de meters past. Wel aan een eenduidige prijsbron — bij een
  // gemengde selectie is er geen kolomkop die voor elke rij klopt.
  const detailPrijsMogelijk = activeMarketGsrns.length > 0 && Boolean(prijsBronActief);
  const detailPrijzen = detailPrijsMogelijk && (detailMetMarkt || detailMetVerkoop);
  // Aansluitingen zonder contractvoorwaarden: die krijgen wél een marktprijs (die
  // hangt niet aan een contract) maar geen verkoopprijs.
  const zonderTarief = useMemo(
    () => activeMarketGsrns.filter((g) => !tariefPerGsrn.get(g.gsrn)),
    [activeMarketGsrns, tariefPerGsrn],
  );
  // Het venster volgt de periodekeuze zolang het dialoogvenster dicht is; zodra
  // iemand het opent en zelf datums kiest, blijft zijn keuze staan.
  useEffect(() => {
    if (detailOpen) return;
    setDetailVan(range.from); setDetailTot(totNa(range.to));
  }, [detailOpen, range.from, range.to]);

  // Een kwartierexport is zwaar: 96 rijen per dag per aansluiting, en de ophaal
  // gebeurt per aansluiting. Een jaar is te verantwoorden, meer niet — en de
  // spotreeks van de backend stopt sowieso na 400 dagen. Een MAANDexport is dat
  // niet: twaalf regels per jaar per aansluiting, dus daar mag het venster ruimer.
  const DETAIL_MAX_DAGEN = detailKwartier ? 366 : 1830;
  const detailDagen = useMemo(() => dagenInVenster(detailVan, detailTot), [detailVan, detailTot]);
  const detailGeldig = detailDagen != null && detailDagen <= DETAIL_MAX_DAGEN;

  const doeDetailExport = async () => {
    if (!detailGeldig) return;
    setDetailBezig(true); setDetailFout(null);
    try {
      const korrel = detailKwartier ? 'quarter' : 'month';
      // De meetdata-API, de spotreeks en de clip rekenen met de LAATSTE dag die
      // meetelt; het scherm vraagt "tot" (niet inbegrepen).
      const detailLaatsteDag = laatsteDag(detailTot);
      // 1. Meetgegevens per aansluiting, op de korrel die de meters aankunnen.
      //    `granularity: 'quarter'` laat de server de readings ONGEMOEID (zie
      //    meterCollapse); 'month' laat haar de standen per maand samenvatten.
      const perGsrn = await Promise.all(activeMarketGsrns.map(async (g) => {
        const venster = localDayWindowUtc(detailVan, detailLaatsteDag);
        const d = await mtrFetch(token, g.gsrn, venster.fromUtc, venster.toUtc,
          MTR_CHART_ANALYSIS, { granularity: korrel }).catch(() => ({ entities: [] }));
        return { gsrn: g.gsrn, rows: dedupeNewest(d.entities || []).rows };
      }));

      // 2. Spot over HETZELFDE venster: geen prijsregel buiten de meetperiode.
      //    Alleen ophalen wanneer een contract er ook echt mee rekent — een
      //    indexcontract (Belpex_RLP/SPP, TTF RLP) heeft de spot niet nodig, en
      //    gas kent hier geen spotreeks.
      const spotNodigVoorExport = detailPrijzen && market === 'EL' && activeMarketGsrns.some(({ gsrn }) => {
        const t = tariefPerGsrn.get(gsrn);
        if (!t) return true;   // onbekend contract → toon de marktprijs als referentie
        return ['afname', 'injectie'].some((r) => prijsBron(t, r)?.soort === 'spot');
      });
      const spotOpTijd = new Map();
      const spotPerMaand = new Map();
      if (spotNodigVoorExport) {
        const spotResp = await fetch(`/api/lynx/spot/range?from=${detailVan}&to=${detailLaatsteDag}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!spotResp.ok) throw new Error('Kon de spotprijzen voor deze periode niet ophalen.');
        const spotLijst = (await spotResp.json()).items || [];
        // Sleutel op tijdstip in milliseconden, niet op de tekst: Lynx en de
        // meetdata schrijven hetzelfde kwartier niet noodzakelijk identiek
        // ('…T22:00:00Z' tegenover '…T00:00:00+02:00').
        const somPerMaand = new Map();
        for (const s of spotLijst) {
          const ms = Date.parse(s.from);
          const p = Number(s.priceEurMwh);
          if (!Number.isFinite(ms) || !Number.isFinite(p)) continue;
          spotOpTijd.set(ms, p);
          // Op maandregels is de spotprijs het GEMIDDELDE van de kwartieren van die
          // maand — een ongewogen gemiddelde, dus niet hetzelfde als de prijs die
          // een klant met dat verbruiksprofiel betaalt. Vandaar dat de export de
          // bron per kolom benoemt.
          const maand = mtrBeDagKey(s.from).substring(0, 7);
          const cur = somPerMaand.get(maand) || { totaal: 0, n: 0 };
          cur.totaal += p; cur.n += 1;
          somPerMaand.set(maand, cur);
        }
        for (const [maand, v] of somPerMaand) spotPerMaand.set(maand, v.n ? v.totaal / v.n : null);
      }

      // 3. Per aansluiting verbruik en productie samenbrengen op de korrel, met de
      //    prijs erbij. De basisprijs komt PER RICHTING uit de eigen bron: afname
      //    kan op Belpex_RLP staan terwijl injectie op Belpex_SPP staat, en een
      //    dynamisch contract gebruikt de spot van dat kwartier.
      const clip = { fromDay: detailVan, toDay: detailLaatsteDag };
      const rijen = [];
      for (const { gsrn, rows } of perGsrn) {
        const tarief = tariefPerGsrn.get(gsrn) || null;
        const merge = new Map();
        if (korrel === 'quarter') {
          const vul = (lijst, veld) => {
            for (const p of lijst) {
              const cur = merge.get(p.fromUTC) || { sleutel: p.fromUTC, fromUTC: p.fromUTC, verbruik: 0, productie: 0 };
              cur[veld] = p.kwh;
              merge.set(p.fromUTC, cur);
            }
          };
          vul(quarterDetail(rows, CONSUMPTION_METHODS, { clip }), 'verbruik');
          vul(quarterDetail(rows, PRODUCTION_METHODS, { clip }), 'productie');
        } else {
          // Dezelfde weg als de grafiek: per stroomrichting wint ÉÉN dataset
          // (siteFlows/pickPrimaryDataset), zodat een tweede bron voor dezelfde
          // energie niet dubbel telt.
          const flows = siteFlows(rows, 'month', { clip });
          const vul = (punten, veld) => {
            for (const p of punten) {
              const cur = merge.get(p.key) || { sleutel: p.key, maand: p.key, verbruik: 0, productie: 0 };
              cur[veld] = p.value;
              merge.set(p.key, cur);
            }
          };
          vul(flows.verbruik.points, 'verbruik');
          vul(flows.productie.points, 'productie');
        }
        for (const r of merge.values()) {
          const maand = korrel === 'quarter' ? mtrBeDagKey(r.fromUTC).substring(0, 7) : r.maand;
          const spot = korrel === 'quarter'
            ? (spotOpTijd.has(Date.parse(r.fromUTC)) ? spotOpTijd.get(Date.parse(r.fromUTC)) : null)
            : (spotPerMaand.has(maand) ? spotPerMaand.get(maand) : null);
          const opties = { spot, parameters: indexParams, maand };
          // Zonder prijskolommen niets berekenen: dan is dit bestand puur het
          // gemeten profiel en hoeft er geen contract bij gezocht te worden.
          const basisAfname = detailPrijzen ? basisprijs(tarief, 'afname', opties) : null;
          const basisInjectie = detailPrijzen ? basisprijs(tarief, 'injectie', opties) : null;
          // Afronden op vier decimalen. Zonder dat levert 53,1 − 22 in binaire
          // drijvende komma "31,099999999999998" op, en dat staat dan zo in een
          // prijskolom die een mens naleest.
          const p4 = (v) => (v == null ? null : Math.round(v * 1e4) / 1e4);
          rijen.push({
            ...r, gsrn, spot: p4(spot),
            basisAfname: p4(basisAfname),
            basisInjectie: p4(basisInjectie),
            prijsAfname: p4(verkoopprijs(tarief, basisAfname, 'afname')),
            prijsInjectie: p4(verkoopprijs(tarief, basisInjectie, 'injectie')),
          });
        }
      }
      rijen.sort((a, b) => String(a.sleutel).localeCompare(String(b.sleutel)) || String(a.gsrn).localeCompare(String(b.gsrn)));
      if (!rijen.length) {
        setDetailFout(korrel === 'quarter'
          ? 'Geen kwartierwaarden in deze periode. Een maand- of jaarstand levert geen kwartierprofiel.'
          : 'Geen meetwaarden in deze periode.');
        return;
      }

      // Wat NIET in het bestand komt (afgesproken):
      //  - de coëfficiënt en de majoratie: die staan op het scherm als
      //    verantwoording bij de verkoopprijs;
      //  - de BRON van de marktprijs (spot of maandindex): nooit, in geen enkel
      //    geval. Ze staat wel onder de grafiek, bij de contractvoorwaarden.
      const tijdKolommen = korrel === 'quarter'
        ? [
          { header: 'Datum', get: (r) => mtrBeDatum(mtrBeDagKey(r.fromUTC)), text: true },
          { header: 'Kwartier', get: (r) => mtrBeTijdKey(r.fromUTC), text: true },
          { header: 'Start (UTC)', get: (r) => r.fromUTC, text: true },
        ]
        : [
          { header: 'Maand', get: (r) => `${r.maand.substring(5)}/${r.maand.substring(0, 4)}`, text: true },
          { header: 'Sleutel (ISO)', get: (r) => r.maand, text: true },
        ];
      // De injectiekolommen staan er ALLEEN wanneer er een injectieprijs te tonen
      // valt (zie heeftInjectiePrijs in src/metering.js). Zonder injectiecontract
      // beschrijven ze een contract dat niet bestaat, en dat leverde bij een
      // gewone afnameklant lege kolommen op. "Productie (kWh)" blijft wél altijd
      // staan: dat is een gemeten hoeveelheid — nul is daar een echt antwoord.
      const toonInjectie = detailPrijzen && heeftInjectiePrijs(rijen);
      exportRowsToCsv(`metering-detail-${market}-${detailVan}_${detailTot}.csv`, [
        ...tijdKolommen,
        { header: 'EAN', get: (r) => r.gsrn, text: true },
        { header: 'Verbruik (kWh)', get: (r) => r.verbruik },
        { header: 'Productie (kWh)', get: (r) => r.productie },
        // Marktprijs en verkoopprijs zijn elk apart aan te vinken: de eerste is de
        // beurs-/indexprijs, de tweede diezelfde prijs mét coëfficiënt en majoratie.
        ...(detailPrijsMogelijk && detailMetMarkt ? [{ header: 'Marktprijs afname (EUR/MWh)', get: (r) => r.basisAfname }] : []),
        ...(detailPrijsMogelijk && detailMetVerkoop ? [{ header: 'Contractprijs afname (EUR/MWh)', get: (r) => r.prijsAfname }] : []),
        ...(toonInjectie && detailMetMarkt ? [{ header: 'Marktprijs injectie (EUR/MWh)', get: (r) => r.basisInjectie }] : []),
        ...(toonInjectie && detailMetVerkoop ? [{ header: 'Contractprijs injectie (EUR/MWh)', get: (r) => r.prijsInjectie }] : []),
      ], rijen);
      setDetailOpen(false);
    } catch (e) {
      setDetailFout(e.message || 'De detailexport is niet gelukt.');
    } finally {
      setDetailBezig(false);
    }
  };

  // Het dialoogvenster hoort BUITEN de grafiekkaart gerenderd te worden: Card draagt
  // backdrop-blur (eigen stapelcontext) én overflow-hidden, dus een overlay erbinnen
  // zou afgekapt worden hoe hoog de z-index ook staat.
  const detailExportDialog = (
    <DocsModal open={detailOpen} onClose={() => setDetailOpen(false)}
      title={`Detailexport — ${detailKwartier ? 'kwartierwaarden' : 'maandwaarden'}`}
      subtitle={`${detailKwartier ? 'Kwartierwaarden' : 'Maandwaarden'} van de geselecteerde aansluitingen, met de prijskolommen die je hieronder aanvinkt · de einddag zelf telt niet mee`}>
      <div className="space-y-4 max-w-xl">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1">Van</label>
            <BeDateInput value={detailVan} max={laatsteDag(detailTot) || mtrToday()} onChange={(v) => { if (v) setDetailVan(v); }}
              className="w-[150px] h-9 px-2.5 bg-slate-800 border border-slate-700 hover:border-lime-500 rounded-lg text-sm text-slate-200 focus:border-lime-500 focus:outline-none text-left" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1">Tot</label>
            <BeDateInput value={detailTot} min={totNa(detailVan) || undefined} max={totNa(mtrToday())} onChange={(v) => { if (v) setDetailTot(v); }}
              className="w-[150px] h-9 px-2.5 bg-slate-800 border border-slate-700 hover:border-lime-500 rounded-lg text-sm text-slate-200 focus:border-lime-500 focus:outline-none text-left" />
          </div>
          <button onClick={doeDetailExport} disabled={!detailGeldig || detailBezig}
            className="h-9 inline-flex items-center gap-1.5 px-3.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-slate-900 text-sm font-semibold rounded-lg">
            {detailBezig ? <Spinner size={14} /> : <Download size={14} />}
            {detailBezig ? 'Bezig…' : 'Exporteren'}
          </button>
        </div>

        {/* Prijskolommen zijn een keuze: wie het verbruiksprofiel naar een eigen
            rekenblad brengt, wil de prijzen er soms juist NIET bij. Bestaat er voor
            deze selectie geen eenduidige prijs, dan staan de vinkjes uit en zegt de
            regel eronder waarom — dat is duidelijker dan ze weglaten. */}
        <div>
          <span className="block text-[11px] font-semibold text-slate-500 mb-1">Prijskolommen</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className={`inline-flex items-center gap-2 text-sm ${detailPrijsMogelijk ? 'text-slate-200 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}>
              <input type="checkbox" checked={detailPrijsMogelijk && detailMetMarkt} disabled={!detailPrijsMogelijk}
                onChange={(e) => setDetailMetMarkt(e.target.checked)}
                className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-4 h-4" />
              {prijsMeta ? `Marktprijs (${prijsMeta.label})` : 'Spotprijs (marktprijs)'}
            </label>
            <label className={`inline-flex items-center gap-2 text-sm ${detailPrijsMogelijk ? 'text-slate-200 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}>
              <input type="checkbox" checked={detailPrijsMogelijk && detailMetVerkoop} disabled={!detailPrijsMogelijk}
                onChange={(e) => setDetailMetVerkoop(e.target.checked)}
                className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-4 h-4" />
              Contractprijs (verkoopprijs)
            </label>
          </div>
          {!detailPrijsMogelijk && (
            <p className="mt-1 text-xs text-slate-500">
              {activeMarketGsrns.length === 0
                ? 'Vink eerst een aansluiting aan.'
                : prijsGemengd
                  ? 'De aangevinkte aansluitingen volgen verschillende prijsformules — vink aansluitingen met dezelfde formule aan voor prijskolommen.'
                  : 'Voor deze selectie is er geen marktprijs beschikbaar; het bestand bevat enkel de meetwaarden.'}
            </p>
          )}
          {detailPrijsMogelijk && !detailMetMarkt && !detailMetVerkoop && (
            <p className="mt-1 text-xs text-slate-500">Geen prijskolommen: het bestand bevat enkel verbruik en productie.</p>
          )}
          {/* Zonder contractvoorwaarden blijft de verkoopprijskolom leeg. Dat hoort
              hier te staan: anders lever je een bestand af waarin die kolom stilzwijgend
              leeg is, en dat leest als "geen contract" terwijl het "geen coëfficiënt op
              de factuur" is. De marktprijs staat er wél, want die hangt niet aan een
              contract. */}
          {detailPrijsMogelijk && detailMetVerkoop && zonderTarief.length > 0 && (
            <p className="mt-1 text-xs text-amber-300/90">
              Geen verkoopprijs voor {zonderTarief.length} van de {activeMarketGsrns.length} aansluiting{activeMarketGsrns.length === 1 ? '' : 'en'}:
              op hun facturen staat geen coëfficiënt, en zonder die factor is er geen prijs te berekenen. Die kolom blijft daar leeg.
            </p>
          )}
        </div>

        {detailDagen == null ? (
          <p className="text-xs text-amber-300/90">Kies een geldige periode — de einddatum mag niet vóór de begindatum liggen.</p>
        ) : !detailGeldig ? (
          <p className="text-xs text-amber-300/90">Deze periode beslaat {detailDagen} dagen; maximaal {DETAIL_MAX_DAGEN}. {detailKwartier ? 'Een kwartierexport telt 96 regels per dag per aansluiting.' : 'Een maandexport telt één regel per maand per aansluiting.'}</p>
        ) : (
          <p className="text-xs text-slate-400">
            {detailDagen} dag{detailDagen === 1 ? '' : 'en'} × {activeMarketGsrns.length} aansluiting{activeMarketGsrns.length === 1 ? '' : 'en'} — tot {mtrFmtNum(detailKwartier ? detailDagen * 96 * activeMarketGsrns.length : Math.ceil(detailDagen / 28) * activeMarketGsrns.length, 0)} regels.
          </p>
        )}
        {detailFout && <p className="text-xs text-rose-300">{detailFout}</p>}

        <div className="rounded-lg bg-slate-800/50 border border-slate-700/50 px-3 py-2 text-[11px] text-slate-500 space-y-1">
          {/* De korrel is geen keuze maar een gevolg: hij volgt de meter. Dat hier
              zeggen scheelt de vraag waarom er geen kwartieren in het bestand staan. */}
          <p>
            Per {detailKwartier ? 'kwartier' : 'maand'}: verbruik en productie in kWh, plus de aangevinkte prijskolommen voor afname en injectie.
            {detailKwartier
              ? ' De aangevinkte aansluitingen meten per kwartier (SMR3/AMR).'
              : ' De aangevinkte aansluitingen meten niet per kwartier (SMR1/MMR/YMR), dus dit bestand gaat per maand — een maandstand over 96 kwartieren uitsmeren zou een profiel verzinnen dat de meter nooit gemeten heeft.'}
          </p>
          <p>De marktprijs is per aansluiting en per richting de bron die op het contract staat: de spotprijs (EPEX) bij een dynamische formule, de maandindex uit Beheer → Indexparameters bij Belpex_RLP, Belpex_SPP of TTF RLP. Welke bron dat is, staat onder de grafiek bij de contractvoorwaarden.</p>
          <p>De contractprijs (verkoopprijs) volgt de contractvoorwaarden van de laatste factuur per aansluiting — die staan onder de grafiek. De coëfficiënt, de majoratie en de bron zelf zitten niet in het bestand.</p>
          <p>De injectiekolommen staan er alleen bij een aansluiting die ook echt injecteert; bij een gewone afnameklant blijven ze weg.</p>
          <p>Prijzen komen enkel voor de periode waarvoor er meetgegevens zijn; een tijdvak zonder meting levert geen regel.</p>
        </div>
      </div>
    </DocsModal>
  );

  // ── Filterbalk ────────────────────────────────────────────────────────────────
  // Vier velden op één regel op desktop, gestapeld op mobiel. `relative z-40` zodat de
  // suggestie-dropdown en de locatie-popover boven de kaarten eronder blijven (Card
  // heeft backdrop-blur → eigen stacking-context, anders schilderen latere kaarten
  // eroverheen). Bewust z-40 en NIET z-50: de accountbalk rechtsboven is
  // `fixed … z-50` en staat eerder in de DOM, dus bij een gelijke z-index zou deze
  // kaart daar overheen komen en het admin-menu half verbergen. Zelfde conventie als
  // de filterbalken op de andere pagina's.
  // De klant achter de grafiektitel ("Verbruik – Bakkerij Janssens"): bij een
  // zakelijke klant de ONDERNEMINGSNAAM, anders de persoonsnaam — dezelfde regel
  // als in Orders en Customer 360 (customerPrimaryName). De naam komt van de
  // geselecteerde klant (klantnummer), en anders van de gekozen EAN of het
  // e-mailadres; een schermafdruk van de grafiek zegt zo altijd over wie ze gaat.
  const grafiekKlant = useMemo(() => {
    const c = conns.find((x) => (selRef ? x.ref === selRef : selGsrn ? x.gsrn === selGsrn : selEmail ? x.email === selEmail : false));
    if (!c) return '';
    return String(customerPrimaryName({ customerCompanyName: c.bedrijf, segment: c.segment, customer: c.name }) || '').trim();
  }, [conns, selRef, selGsrn, selEmail]);

  // ── Filterbalk ────────────────────────────────────────────────────────────────
  // Eén rij, links naar rechts in de volgorde waarin je de vraag stelt: WIE
  // (klant/GSRN) → WELKE aansluitingen → WELKE energie → WELKE periode en op welke
  // resolutie. Periode en resolutie staan in één omkaderde groep: ze beschrijven
  // samen de x-as van de grafiek, en stonden vroeger op twee plaatsen (periode
  // hier, resolutie in de grafiekkop), waardoor je heen en weer moest.
  // Het energietype stond zowel in een aparte tab-balk als in de grafiekkop; het
  // staat nu één keer, hier, want die keuze bepaalt wat de héle pagina toont.
  // Jaarweergave: "Laatste 12 mnd" (rollend) of een kalenderjaar. Een kalenderjaar
  // kiezen zet de anchor op de laatste maand ervan — december, of de lopende maand
  // voor het huidige jaar — zodat de pijltjes en het label daarop verder werken.
  const huidigJaar = Number(mtrToday().substring(0, 4));
  const kiesJaar = (keuze) => {
    setJaarKeuze(keuze);
    if (keuze === 'rollend') { setAnchor(mtrToday()); return; }
    const j = Number(keuze);
    setAnchor(j < huidigJaar ? `${j}-12-01` : mtrToday().substring(0, 7) + '-01');
  };
  const jaarOpties = [{ value: 'rollend', label: 'Laatste 12 mnd' }, ...[huidigJaar - 2, huidigJaar - 1, huidigJaar].map((j) => ({ value: String(j), label: String(j) }))];
  // Bladeren: in een kalenderjaar per jaar, anders per dag/maand (rollend jaar =
  // per maand, dat is de anchor).
  const blader = (dir) => {
    if (kalenderJaar) { kiesJaar(String(kalenderJaar + dir)); return; }
    setAnchor(mtrShiftAnchor(viewMode, anchor, dir));
  };
  const bladerMax = kalenderJaar ? kalenderJaar >= huidigJaar : atMax;
  const periodeResolutie = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border-2 border-lime-500/50 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[11px] font-semibold text-slate-500 shrink-0">Periode</span>
        <div className="flex items-center gap-1 min-w-0">
          <button onClick={() => blader(-1)} title="Vorige periode"
            className="inline-flex items-center justify-center h-9 w-8 shrink-0 bg-slate-800 border border-slate-700 hover:border-lime-500 text-slate-200 rounded-lg"><ChevronLeft size={15} /></button>
          {viewMode === 'day' ? (
            <BeDateInput value={anchor} max={mtrToday()} onChange={(v) => { if (v) setAnchor(v); }}
              className="min-w-0 w-[150px] h-9 px-2.5 bg-slate-800 border border-slate-700 hover:border-lime-500 rounded-lg text-sm text-slate-200 focus:border-lime-500 focus:outline-none text-left" />
          ) : (
            <div className="min-w-0 h-9 flex items-center justify-center gap-2 px-3 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200">
              <CalendarDays size={14} className="text-slate-500 shrink-0" />
              <span className="truncate">{mtrAnchorLabel(viewMode, anchor, kalenderJaar)}</span>
            </div>
          )}
          <button onClick={() => blader(1)} disabled={bladerMax} title="Volgende periode"
            className="inline-flex items-center justify-center h-9 w-8 shrink-0 bg-slate-800 border border-slate-700 hover:border-lime-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-700 text-slate-200 rounded-lg"><ChevronRight size={15} /></button>
        </div>
      </div>
      <div className="hidden sm:block w-px h-6 bg-slate-700/70" />
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-slate-500 shrink-0">Resolutie</span>
        <MtrSegment value={viewMode} onChange={setViewMode}
          options={[{ value: 'day', label: 'Dag' }, { value: 'month', label: 'Maand' }, { value: 'year', label: 'Jaar' }]} />
      </div>
      {/* Enkel in de jaarweergave: rollend of een kalenderjaar (dit jaar en de
          twee ervoor). Het huidige jaar toont de maanden die er al zijn. */}
      {viewMode === 'year' && (
        <>
          <div className="hidden sm:block w-px h-6 bg-slate-700/70" />
          <MtrSegment value={jaarKeuze} onChange={kiesJaar} options={jaarOpties} />
        </>
      )}
    </div>
  );

  // Twee rijen (herindeling 11-09-2026): bovenaan WIE en WELKE aansluitingen over
  // de volle breedte, daaronder een compacte rij met energietype, periode +
  // resolutie en de Locaties-knop. Die tweede rij vult de breedte bewust niet: de
  // knoppen hebben een vaste maat en uitrekken zou ze alleen maar uit elkaar
  // trekken. Zelfde velden, zelfde state, alleen anders geplaatst.
  const filterBar = (
    <Card className="!p-3 !overflow-visible relative z-40 space-y-2.5">
      <div className="flex flex-wrap items-end gap-3">
        {/* Ingebed in Customer 360 staat de klant al vast, dus geen zoekveld: een
            tweede zoekbalk binnen een klantdossier nodigt uit tot per ongeluk een
            andere klant openen. De locatiekiezer hiernaast is daar de manier om
            tussen de EAN's van díe klant te wisselen. */}
        {!embedded && (
          <MtrVeld label="Klant / EAN" className="flex-1 basis-[280px]">
            <KlantZoekVeld
              value={query}
              onChange={setQuery}
              onClear={clearAll}
              clearActief={!!(query || selEmail || selRef || selGsrn)}
              suggesties={suggesties}
              onKlant={(k) => pickCustomer(k.ref, k.email)}
              onGsrn={pickGsrn}
              telWoord={{ een: 'aansluiting', meer: 'aansluitingen' }}
              placeholder="Zoek op klantnaam, klantnummer of EAN…"
            />
          </MtrVeld>
        )}

        {/* Zelfde label en breedte als in Customer 360: het is dezelfde pagina. */}
        <MtrVeld label="Locatie / EAN van deze klant" className="flex-1 basis-[280px]">
          <MtrLocationPicker groups={pickerGroups} selected={chartGsrns} focus={selGsrn}
            onToggle={toggleChartGsrn} onFocus={focusGsrn}
            onAll={() => setChartGsrns(allGsrns.map((g) => g.gsrn))} onNone={() => setChartGsrns([])} />
        </MtrVeld>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <MtrVeld label="Energietype">
          <MtrSegment size="lg" value={market} onChange={(m) => { setMarket(m); setTab(m); }}
            options={[
              { value: 'EL', label: 'Elektriciteit', icon: Zap },
              { value: 'NG', label: 'Gas', icon: Flame },
            ]} />
        </MtrVeld>

        {periodeResolutie}

        {/* Het locatie-overzicht is een andere LEZING van dezelfde selectie, geen
            filter. Vandaar één knop achteraan en geen derde tab naast EL/Gas. */}
        <button onClick={() => setTab(tab === 'locaties' ? market : 'locaties')}
          className={`inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium whitespace-nowrap ${tab === 'locaties' ? 'border-lime-500 bg-lime-500 text-slate-900' : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'}`}>
          <MapPin size={15} />
          Locaties
          {allGsrns.length > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${tab === 'locaties' ? 'bg-slate-900/15' : 'bg-slate-700 text-slate-400'}`}>{allGsrns.length}</span>}
        </button>
      </div>
    </Card>
  );

  // ── Kengetallen ───────────────────────────────────────────────────────────────
  // Sinds 11-09-2026 een kolom RECHTS van de grafiek (op xl), daaronder een rij
  // onder de grafiek — zie MtrKpiGrid. Volgorde: eerst waar de cijfers over gáán
  // (aansluitingen), dan het totaal, dan de duiding (productie/netto waar die
  // bestaan) en tot slot gemiddelde en piek. Zelfde waarden en zelfde berekening
  // als voorheen — dit is enkel een andere plaatsing.
  const kpiCards = (
    <MtrKpiGrid>
      {/* Enkel bij MEERDERE aansluitingen: bij één zegt "1" niets, en de EAN
          staat al onder de grafiektitel. Bij meerdere staan de adressen er klein
          bij, zodat je ziet wat er opgeteld is zonder naar "Locaties" te gaan. */}
      {combined.perSite.length > 1 && (
        <MtrKpiCard icon={MapPin} label="Aansluitingen" value={`${combined.perSite.length}`} tint="rgba(99,102,241,.14)" color={MTR_NET}>
          <div className="mt-0.5 text-[11px] text-slate-500 truncate">{combined.perSite.filter((s) => s.verbruik > 0).length} met verbruik</div>
          <ul className="mt-1 space-y-0.5 text-[10px] leading-snug text-slate-500">
            {combined.perSite.slice(0, 6).map((s) => (
              <li key={s.gsrn} className="truncate" title={`${s.address || ''} · ${s.gsrn}`}>{String(s.address || s.gsrn).replace(/\s+Belgi(um|ë)$/i, '')}</li>
            ))}
            {combined.perSite.length > 6 && <li>+{combined.perSite.length - 6} andere</li>}
          </ul>
        </MtrKpiCard>
      )}
      {/* Verbruik is het cijfer waarvoor je deze pagina opent; vandaar `primary`. */}
      <MtrKpiCard icon={tone.icon} label="Verbruik totaal" value={totaalKwh(combined.totals.verbruik)} tint={tone.soft} color={tone.bar} primary>
        <MtrDelta value={pctDelta(combined.totals.verbruik, prevCombined.totals.verbruik)} label={vsLabel} goodWhen="down" />
      </MtrKpiCard>
      {hasProduction && (
        <MtrKpiCard icon={Sun} label="Productie totaal" value={totaalKwh(combined.totals.productie)} tint="rgba(74,222,128,.22)" color={tone.prodInk}>
          <MtrDelta value={pctDelta(combined.totals.productie, prevCombined.totals.productie)} label={vsLabel} goodWhen="up" />
        </MtrKpiCard>
      )}
      {hasProduction && (
        <MtrKpiCard icon={Activity} label="Netto verbruik" value={totaalKwh(combined.totals.netto)} tint="rgba(99,102,241,.14)" color={MTR_NET} primary>
          <MtrDelta value={pctDelta(combined.totals.netto, prevCombined.totals.netto)} label={vsLabel} goodWhen="down" />
        </MtrKpiCard>
      )}
      <MtrKpiCard icon={Activity} label={`Gemiddeld per ${bucketWord}`} value={mtrKwh(filledBuckets ? combined.totals.verbruik / filledBuckets : 0, 1)} tint="rgba(99,102,241,.14)" color={MTR_NET}>
        <div className="mt-0.5 text-[11px] text-slate-500 truncate">{filledBuckets} {bucketWord}{filledBuckets === 1 ? '' : 'en'} met data</div>
      </MtrKpiCard>
      <MtrKpiCard icon={CalendarDays} label={`${peakLabel} (verbruik)`} value={combined.peak ? mtrKwh(combined.peak.value, 1) : '—'} tint="rgba(99,102,241,.14)" color={MTR_NET}>
        <div className="mt-0.5 text-[11px] text-slate-500 truncate">{combined.peak ? bucketDisplay(combined.peak.key) : 'geen verbruik in deze periode'}</div>
      </MtrKpiCard>
    </MtrKpiGrid>
  );

  // ── Grafiek ───────────────────────────────────────────────────────────────────
  // Verbruik en productie staan NAAST elkaar, niet gestapeld: het zijn tegengestelde
  // stromen, dus een gestapelde balk zou een som suggereren die niets betekent. De
  // netto-lijn (verbruik − productie) legt de verhouding wél correct vast.
  //
  // De grafiek krijgt de volle breedte van de pagina en is daarmee het grootste
  // onderdeel. In de kop staan alleen nog SECUNDAIRE keuzes — welke reeks, staven
  // of lijn, en de export. Wat de hele pagina bepaalt (energietype, periode,
  // resolutie) staat in de filterbalk bovenaan, zodat je niet op twee plaatsen
  // dezelfde soort knop tegenkomt.
  const chartCard = (
    <Card className="!p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Titel met daarachter het energietype als badge in de kleur van de
              reeks: wie naar de grafiek kijkt (of er een schermafdruk van maakt)
              moet zonder omhoog te scrollen weten of dit stroom of gas is. */}
          <h3 className="text-lg font-semibold text-slate-100 flex flex-wrap items-center gap-2">
            <span>{hasProduction ? 'Verbruik & productie' : 'Verbruik'}</span>
            {grafiekKlant && <span className="text-slate-300 font-medium min-w-0 truncate max-w-[28rem]" title={grafiekKlant}>– {grafiekKlant}</span>}
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-semibold"
              style={{ background: tone.soft, color: tone.bar }}>
              <tone.icon size={13} className="shrink-0" />{tone.label}
            </span>
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasProduction && (
            <MtrSegment value={showFlow} onChange={setShowFlow}
              options={[{ value: 'alles', label: 'Alles' }, { value: 'verbruik', label: 'Verbruik' }, { value: 'productie', label: 'Productie' }, { value: 'netto', label: 'Netto' }]} />
          )}
          <MtrSegment value={chartType} onChange={setChartType} options={[{ value: 'bar', label: 'Staven' }, { value: 'line', label: 'Lijn' }]} />
          {/* De prijsreeks: standaard UIT, en enkel aanwezig waar ze betekenis
              heeft. De knop draagt de NAAM van de bron — "Spotprijs" bij een
              dynamisch contract, "Belpex_RLP"/"TTF_RLP" bij een maandindex — want
              dat zijn verschillende getallen en de klant rekent maar met één ervan
              af. Weg is de knop wanneer de meter deze resolutie niet kan dragen
              (een YMR heeft geen dagprofiel) of wanneer de aangevinkte
              aansluitingen verschillende prijsformules volgen. */}
          {prijsBeschikbaar && (
            <button
              onClick={() => setToonPrijs((v) => !v)}
              title={prijsMeta
                ? `Toon ${prijsMeta.label} (maandindex uit de instellingen) bij deze meetgegevens — eigen as rechts`
                : 'Toon de spotprijs (EPEX, per kwartier) bij deze meetgegevens — eigen as rechts'}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border font-medium ${toonPrijs ? 'text-slate-900' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}
              style={toonPrijs ? { background: MTR_SPOT, borderColor: MTR_SPOT } : undefined}
            >
              {spotLaadt ? <Spinner size={12} /> : <span className="w-3 h-0.5 rounded" style={{ background: toonPrijs ? '#fff' : MTR_SPOT }} />}
              {prijsNaam}
            </button>
          )}
          {/* De contractprijs: dezelfde regels als de prijsreeks (meter, resolutie,
              bron), plus: de aangevinkte aansluitingen moeten dezelfde
              afnamevoorwaarden hebben. Kan het niet, dan blijft de knop staan
              maar uit, met de reden in de tooltip. */}
          {prijsBeschikbaar && (
            <button
              onClick={() => contractBeschikbaar && setToonContract((v) => !v)}
              disabled={!contractBeschikbaar}
              title={contractUitleg || 'Toon de contractprijs (verkoopprijs afname: coeff × prijs + majoratie, uit de laatste factuur) bij deze meetgegevens — eigen as rechts'}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border font-medium ${toonContract && contractBeschikbaar ? 'text-slate-900' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'} disabled:opacity-50 disabled:cursor-not-allowed`}
              style={toonContract && contractBeschikbaar ? { background: MTR_CONTRACT, borderColor: MTR_CONTRACT } : undefined}
            >
              <span className="w-3 h-0.5 rounded" style={{ background: toonContract && contractBeschikbaar ? '#fff' : MTR_CONTRACT }} />
              Contractprijs
            </button>
          )}
          {/* Staat de knop er niet, zeg dan waarom. Zonder dit zinnetje is het
              verschil tussen "deze meter kan dat niet" en "er is iets stuk" op het
              scherm niet te zien. */}
          {!prijsBeschikbaar && activeMarketGsrns.length > 0 && (prijsResolutieUitleg || prijsGemengd) && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-slate-500">
              <Info size={12} className="shrink-0" />
              {prijsGemengd
                ? 'Verschillende prijsformules in deze selectie — vink aansluitingen met dezelfde formule aan voor een prijsreeks.'
                : prijsResolutieUitleg}
            </span>
          )}
          {/* Eén exportknop. De korrel volgt de meter — kwartier bij SMR3/AMR, maand
              bij SMR1/MMR/YMR — en de prijskolommen zijn een keuze in het
              dialoogvenster. Ze staat er dus ook zonder prijs: zonder prijzen is dit
              nog altijd het gemeten profiel per aansluiting. */}
          <ExportKnop onExport={() => { setDetailFout(null); setDetailOpen(true); }} disabled={!activeMarketGsrns.length}
            label={`Detailexport — ${detailKwartier ? 'kwartierwaarden' : 'maandwaarden'} per aansluiting over een zelf gekozen periode, met de prijzen naar keuze`} />
          {/* Het meetrapport als PDF: precies de grafiek en cijfers die nu op het
              scherm staan (selectie, periode, resolutie, aangezette reeksen). */}
          <ExportKnop onExport={exportMeteringPdf} soort="pdf" busy={pdfBezig} disabled={!activeMarketGsrns.length || seriesLoading}
            label="Meetrapport als PDF — de grafiek Verbruik en productie en de cijfers zoals ze nu op het scherm staan" />
        </div>
      </div>
      {/* De periode groot en gecentreerd boven de grafiek — "01/06/2026 – Maandag",
          "06/2026" of "2026" — met de EAN's er klein onder. Vervangt de regel
          "Per kwartier · … · 2 aansluitingen": de resolutie staat nu vet op de
          X-as en de rest hier. Boven een handvol EAN's tonen we er zes en tellen
          we de rest; de volledige lijst staat in "Locaties". */}
      <div className="mt-1 text-center">
        <div className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100 leading-tight">{mtrPeriodeKop(viewMode, anchor, range)}</div>
        {chartGsrnLijst.length > 0 && (
          <div className="mt-0.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
            <span className="shrink-0">EAN</span>
            {chartGsrnLijst.slice(0, MTR_GSRN_TOON).map((g) => <span key={g} className="font-mono text-slate-400">{g}</span>)}
            {chartGsrnLijst.length > MTR_GSRN_TOON && <span>+{chartGsrnLijst.length - MTR_GSRN_TOON} andere</span>}
          </div>
        )}
      </div>

      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
        {showVerbruik && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-2.5 h-2.5 rounded-full" style={{ background: tone.bar }} />Verbruik (kWh)</span>}
        {showProductie && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-2.5 h-2.5 rounded-full" style={{ background: tone.prod }} />Productie (kWh)</span>}
        {showNetto && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: MTR_NET }} />Netto verbruik (kWh)</span>}
        {prijsAan && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: MTR_SPOT }} />{prijsNaam} (EUR/MWh, as rechts)</span>}
        {contractAan && <span className="flex items-center gap-1.5 text-slate-400"><span className="w-4 h-0.5 rounded" style={{ background: MTR_CONTRACT }} />Contractprijs (EUR/MWh, as rechts)</span>}
        {jaarGrensKey && chartType === 'bar' && (
          <span className="flex items-center gap-1.5 text-slate-500">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: tone.bar, opacity: 0.45 }} />
            doffe staven = {eindJaar - 1}
          </span>
        )}
      </div>

      {seriesLoading ? (
        <div className="h-72 flex items-center justify-center"><Spinner /></div>
      ) : chartEmpty ? (
        <div className="h-72 flex items-center justify-center"><TableEmpty message="Geen meetgegevens voor deze periode." icon={Gauge} /></div>
      ) : (
        // Hoger dan voorheen (was 224-288 px): de grafiek is nu het middelpunt van
        // de pagina en heeft de breedte die de KPI-kolom vrijgaf.
        <div className="mt-1 h-64 sm:h-80 lg:h-[26rem] xl:h-[clamp(28rem,calc(100vh-27rem),38rem)]">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <LineChart data={chartRows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="key" stroke="#64748b" fontSize={10} tickLine={false} tickFormatter={xLabel} interval={viewMode === 'day' ? 3 : 'preserveStartEnd'} height={38} label={{ value: asWoordX, position: 'insideBottom', offset: 0, fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                <YAxis yAxisId="kwh" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => mtrFmtNum(v, 0)} label={{ value: 'kWh', angle: -90, position: 'insideLeft', offset: 8, fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                {/* Tweede as ALLEEN voor de spotprijs. Volume (kWh) en prijs (EUR/MWh)
                    delen geen schaal, dus samen op een as zou een van beide platdrukken.
                    De as verschijnt pas wanneer de reeks aanstaat. */}
                {prijsAsAan && <YAxis yAxisId="prijs" orientation="right" stroke={prijsAsKleur} fontSize={11} tickLine={false} axisLine={false} width={62} tickFormatter={(v) => `${mtrFmtNum(v, 0)}`} />}
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={tipWaarde} labelFormatter={tipLabel} />
                {jaarGrensKey && <ReferenceLine yAxisId="kwh" x={jaarGrensKey} stroke="#94a3b8" strokeDasharray="4 3" label={{ value: String(eindJaar), position: 'insideTopLeft', fill: '#94a3b8', fontSize: 10 }} />}
                {showVerbruik && <Line yAxisId="kwh" type="monotone" dataKey="verbruik" name="Verbruik (kWh)" stroke={tone.bar} strokeWidth={2} dot={false} />}
                {showProductie && <Line yAxisId="kwh" type="monotone" dataKey="productie" name="Productie (kWh)" stroke={tone.prod} strokeWidth={2} dot={false} />}
                {showNetto && <Line yAxisId="kwh" type="monotone" dataKey="netto" name="Netto verbruik (kWh)" stroke={MTR_NET} strokeWidth={2} dot={false} />}
                {/* Twee lijnen over elkaar: eerst een bredere streep in de kleur van de
                    kaart, dan de prijslijn zelf. Die halo houdt de lijn leesbaar op de
                    plek waar ze OVER de staven loopt — daar botsten anders twee
                    verzadigde kleuren zonder scheiding. De halo doet niet mee in
                    tooltip of legende (tooltipType/legendType "none"). */}
                {prijsAan && <Line yAxisId="prijs" type="monotone" dataKey="prijs" stroke="var(--chart-surface, #1e293b)" strokeWidth={5} dot={false} connectNulls isAnimationActive={false} legendType="none" tooltipType="none" />}
                {prijsAan && <Line yAxisId="prijs" type="monotone" dataKey="prijs" name={prijsSerieNaam} stroke={MTR_SPOT} strokeWidth={2.5} strokeDasharray="5 3" dot={false} connectNulls isAnimationActive={false} />}
                {contractAan && <Line yAxisId="prijs" type="monotone" dataKey="contract" stroke="var(--chart-surface, #1e293b)" strokeWidth={5} dot={false} connectNulls isAnimationActive={false} legendType="none" tooltipType="none" />}
                {contractAan && <Line yAxisId="prijs" type="monotone" dataKey="contract" name="Contractprijs (EUR/MWh)" stroke={MTR_CONTRACT} strokeWidth={2.5} dot={false} connectNulls isAnimationActive={false} />}
              </LineChart>
            ) : (
              <ComposedChart data={chartRows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="key" stroke="#64748b" fontSize={10} tickLine={false} tickFormatter={xLabel} interval={viewMode === 'day' ? 3 : 'preserveStartEnd'} height={38} label={{ value: asWoordX, position: 'insideBottom', offset: 0, fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                <YAxis yAxisId="kwh" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => mtrFmtNum(v, 0)} label={{ value: 'kWh', angle: -90, position: 'insideLeft', offset: 8, fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                {/* Tweede as ALLEEN voor de spotprijs. Volume (kWh) en prijs (EUR/MWh)
                    delen geen schaal, dus samen op een as zou een van beide platdrukken.
                    De as verschijnt pas wanneer de reeks aanstaat. */}
                {prijsAsAan && <YAxis yAxisId="prijs" orientation="right" stroke={prijsAsKleur} fontSize={11} tickLine={false} axisLine={false} width={62} tickFormatter={(v) => `${mtrFmtNum(v, 0)}`} />}
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={tipWaarde} labelFormatter={tipLabel} cursor={{ fill: 'rgba(100,116,139,.12)' }} />
                {jaarGrensKey && <ReferenceLine yAxisId="kwh" x={jaarGrensKey} stroke="#94a3b8" strokeDasharray="4 3" label={{ value: String(eindJaar), position: 'insideTopLeft', fill: '#94a3b8', fontSize: 10 }} />}
                {/* Zelfde kleur, doffer voor het vorige jaar: het blijft dezelfde
                    reeks, dus een tweede kleur zou een tweede grootheid suggereren. */}
                {showVerbruik && <Bar yAxisId="kwh" dataKey="verbruik" name="Verbruik (kWh)" fill={tone.bar} radius={[3, 3, 0, 0]} maxBarSize={34}>
                  {combined.rows.map((r) => <Cell key={`v${r.key}`} fillOpacity={isVorigJaar(r.key) ? 0.45 : 1} />)}
                </Bar>}
                {showProductie && <Bar yAxisId="kwh" dataKey="productie" name="Productie (kWh)" fill={tone.prod} radius={[3, 3, 0, 0]} maxBarSize={34}>
                  {combined.rows.map((r) => <Cell key={`p${r.key}`} fillOpacity={isVorigJaar(r.key) ? 0.45 : 1} />)}
                </Bar>}
                {showNetto && <Line yAxisId="kwh" type="monotone" dataKey="netto" name="Netto verbruik (kWh)" stroke={MTR_NET} strokeWidth={2} dot={false} />}
                {/* Twee lijnen over elkaar: eerst een bredere streep in de kleur van de
                    kaart, dan de prijslijn zelf. Die halo houdt de lijn leesbaar op de
                    plek waar ze OVER de staven loopt — daar botsten anders twee
                    verzadigde kleuren zonder scheiding. De halo doet niet mee in
                    tooltip of legende (tooltipType/legendType "none"). */}
                {prijsAan && <Line yAxisId="prijs" type="monotone" dataKey="prijs" stroke="var(--chart-surface, #1e293b)" strokeWidth={5} dot={false} connectNulls isAnimationActive={false} legendType="none" tooltipType="none" />}
                {prijsAan && <Line yAxisId="prijs" type="monotone" dataKey="prijs" name={prijsSerieNaam} stroke={MTR_SPOT} strokeWidth={2.5} strokeDasharray="5 3" dot={false} connectNulls isAnimationActive={false} />}
                {contractAan && <Line yAxisId="prijs" type="monotone" dataKey="contract" stroke="var(--chart-surface, #1e293b)" strokeWidth={5} dot={false} connectNulls isAnimationActive={false} legendType="none" tooltipType="none" />}
                {contractAan && <Line yAxisId="prijs" type="monotone" dataKey="contract" name="Contractprijs (EUR/MWh)" stroke={MTR_CONTRACT} strokeWidth={2.5} dot={false} connectNulls isAnimationActive={false} />}
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* De contractvoorwaarden waarmee de verkoopprijs gerekend wordt. Klein en
          onder de grafiek, want het is verantwoording en geen kengetal — maar ze
          MOETEN zichtbaar zijn: ze komen van de meest recente factuur per
          aansluiting, en zonder ze te tonen zou die aanname onzichtbaar in een
          exportkolom verdwijnen. Injectie staat er alleen bij als de aansluiting
          ook injecteert. Deze regels gaan bewust NIET mee in de export. */}
      {prijsAsAan && actieveTarieven.length > 0 && (
        <div className="mt-2 rounded-lg bg-slate-800/50 border border-slate-700/50 px-3 py-1.5 text-[11px] text-slate-500">
          <div className="flex items-start gap-1.5">
            <Info size={12} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              {/* De formule staat één keer, bovenaan; per aansluiting volgen enkel
                  de getallen. Een majoratie mag NEGATIEF zijn (komt echt voor bij
                  injectie: −20), en dan zou "× prijs − 20" met een weggemoffeld
                  minteken het omgekeerde beweren van wat er gerekend wordt.
                  "prijs" is hier de bron die de knop noemt: de spotprijs van het
                  kwartier, of de maandindex uit de instellingen. */}
              <span className="text-slate-400">
                Contractprijs (verkoopprijs) — afname = coeff × prijs + maj · injectie = coeff × prijs − maj.
                {prijsMeta
                  ? ` Prijs = ${prijsMeta.label}, de maandindex uit Beheer → Indexparameters (c€/kWh, × 10 naar EUR/MWh).`
                  : ' Prijs = de spotprijs (EPEX) van dat kwartier.'}
                {' '}Voorwaarden uit de laatste factuur per aansluiting:
              </span>
              <ul className="mt-1 space-y-0.5">
                {/* Op een telefoon past deze regel nooit op één lijn; `truncate`
                    kapte daar precies de prijsformule en de factuurdatum af — net
                    de twee dingen die verklaren waarmee er gerekend wordt. Onder
                    `sm` breekt de regel dus af; vanaf `sm` blijft het één lijn
                    per aansluiting, zoals bedoeld. */}
                {actieveTarieven.map(({ gsrn, t }) => (
                  <li key={gsrn} className="break-words sm:truncate">
                    <span className="font-mono">{gsrn}</span><EanKopieerKnop ean={gsrn} />
                    {` — ${t.market} coeff Offtake `}<span className="text-slate-300">{mtrFmtNum(t.coeffOfftake, 4)}</span>
                    {` · ${t.market} Maj Offtake `}<span className="text-slate-300">{mtrFmtNum(t.majOfftake, 2)}</span>
                    {t.typeOfftake ? <span className="text-slate-600"> ({t.typeOfftake})</span> : null}
                    {t.coeffInjection || t.majInjection ? (
                      <>{` · ${t.market} coeff Injection `}<span className="text-slate-300">{mtrFmtNum(t.coeffInjection, 4)}</span>
                        {/* De grootte, niet het teken uit de factuur: dat veld staat
                            negatief opgeslagen (−20) terwijl het bedrag afgetrokken
                            wordt. Zie verkoopprijs() in src/metering.js. */}
                        {` · ${t.market} Maj Injection `}<span className="text-slate-300">{mtrFmtNum(Math.abs(t.majInjection), 2)}</span>
                        {t.typeInjection ? <span className="text-slate-600"> ({t.typeInjection})</span> : null}</>
                    ) : null}
                    {t.datum ? <span className="text-slate-600"> · factuur {mtrBeDatum(String(t.datum).substring(0, 10))}</span> : null}
                  </li>
                ))}
              </ul>
              {/* Een maand zonder ingestelde index levert geen prijs. Dat gat hoort
                  benoemd te worden — anders lijkt de lijn gewoon te stoppen. */}
              {indexGaten.length > 0 && (
                <p className="mt-1 text-amber-300/90">
                  Geen indexwaarde ingesteld voor {indexGaten.map((m) => `${m.substring(5)}/${m.substring(0, 4)}`).join(', ')} — die {indexGaten.length === 1 ? 'maand blijft' : 'maanden blijven'} zonder prijs.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Verantwoording, compact onder de grafiek: wat zit er NIET in deze cijfers?
          Een tweede bron voor dezelfde stroom wordt bewust niet meegeteld (dat zou
          dubbeltellen), en een pro rata verdeelde meterstand is een schatting.
          Beide worden benoemd in plaats van stil verwerkt. Eén rustige strook in
          plaats van losse regels, zodat het als voetnoot leest en niet als alarm. */}
      {(combined.totals.setAside > 0 || combined.totals.distributedKwh > 0 || combined.totals.excludedUnit > 0) && (
        <div className="mt-2 rounded-lg bg-slate-800/50 border border-slate-700/50 px-3 py-1.5 space-y-1 text-[11px] text-slate-500">
          {combined.totals.setAside > 0 && (
            <p className="flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />
              <span>{combined.totals.setAside} bijkomende dataset(s) beschrijven dezelfde energie uit een andere bron en zitten niet in deze cijfers — optellen zou dubbeltellen. Ze staan bij de ruwe meetgegevens.</span></p>
          )}
          {combined.totals.distributedKwh > 0 && (
            <p className="flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />
              <span>{mtrKwh(combined.totals.distributedKwh, 1)} komt uit meterstanden die meerdere tijdvakken beslaan en pro rata verdeeld zijn — een schatting, geen afzonderlijk gemeten waarde.</span></p>
          )}
          {combined.totals.excludedUnit > 0 && (
            <p className="flex items-start gap-1.5 text-amber-300/90"><AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>{combined.totals.excludedUnit} meting(en) staan in een eenheid die niet naar kWh om te rekenen is en tellen niet mee.</span></p>
          )}
        </div>
      )}
    </Card>
  );

  // ── Verdiepende blokken ───────────────────────────────────────────────────────
  const donutSlices = hasProduction
    ? [
      { name: 'Verbruik (afname net)', value: combined.totals.verbruik, color: tone.bar },
      { name: 'Productie (injectie)', value: combined.totals.productie, color: tone.prod },
    ]
    : combined.perSite.filter((s) => s.verbruik > 0).slice(0, 4)
      .map((s, i) => ({ name: s.address || s.gsrn, value: s.verbruik, color: [tone.bar, tone.prod, MTR_NET, '#94a3b8'][i] }))
      .concat(combined.perSite.filter((s) => s.verbruik > 0).length > 4
        ? [{ name: 'Overige locaties', value: combined.perSite.filter((s) => s.verbruik > 0).slice(4).reduce((t, s) => t + s.verbruik, 0), color: '#cbd5e1' }]
        : []);

  const topRows = topBuckets(combined.rows, 8);

  const detailCard = (
    <Card className="!p-4 flex flex-col">
      <div className="flex items-center gap-2 min-w-0">
        <h3 className="text-base font-semibold text-slate-100 shrink-0">Detail EAN</h3>
        {selGsrn && <><MtrMarketBadge market={focusMeta?.market || market} /><span className="font-mono text-xs text-slate-300 truncate">{selGsrn}</span><EanKopieerKnop ean={selGsrn} /></>}
      </div>
      {!selGsrn ? (
        <div className="flex-1 flex items-center"><TableEmpty message="Kies een aansluiting bij de locaties." icon={MapPin} /></div>
      ) : (
        <>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{focusMeta?.address || '—'}</p>
          <dl className="mt-3 flex-1 space-y-0">
            {[
              ['Verbruik totaal', focusStats ? totaalKwh(focusStats.verbruik) : '—'],
              ['Productie totaal', focusStats ? totaalKwh(focusStats.productie) : '—'],
              ['Netto verbruik', focusStats ? totaalKwh(focusStats.netto) : '—'],
              [`${peakLabel} (verbruik)`, focusStats?.peak ? `${mtrKwh(focusStats.peak.value, 1)} (${bucketDisplay(focusStats.peak.key)})` : '—'],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-3 py-2 border-b border-slate-700/50 last:border-0">
                <dt className="text-xs text-slate-400 truncate">{k}</dt>
                <dd className="text-xs font-semibold text-slate-100 shrink-0 tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>
          <button onClick={openRaw} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium rounded-lg">
            Bekijk volledig profiel <ExternalLink size={13} />
          </button>
        </>
      )}
    </Card>
  );

  const topCard = (
    <Card className="!p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-100">{topTitle}</h3>
        <span className="text-[11px] text-slate-500">Zwaarste tijdvakken van alle geselecteerde locaties samen</span>
      </div>
      {topRows.length === 0 ? (
        <div className="py-6"><TableEmpty message="Geen verbruik in deze periode." icon={Gauge} /></div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead><tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-500">
              <th className="py-2 px-2 text-left font-medium w-8">#</th>
              <th className="py-2 px-2 text-left font-medium">{viewMode === 'day' ? 'Kwartier' : viewMode === 'month' ? 'Dag' : 'Maand'}</th>
              <th className="py-2 px-2 text-right font-medium">Verbruik</th>
              {hasProduction && <th className="py-2 px-2 text-right font-medium">Productie</th>}
              {hasProduction && <th className="py-2 px-2 text-right font-medium">Netto</th>}
              <th className="py-2 px-2 text-left font-medium w-1/3">Aandeel</th>
            </tr></thead>
            <tbody>
              {topRows.map((r, i) => (
                <tr key={r.key} className="border-b border-slate-800/50">
                  <td className="py-1.5 px-2 text-slate-500 tabular-nums">{i + 1}</td>
                  <td className="py-1.5 px-2 text-slate-200 whitespace-nowrap">{bucketDisplay(r.key)}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-slate-100 whitespace-nowrap">{mtrKwh(r.verbruik, 1)}</td>
                  {hasProduction && <td className="py-1.5 px-2 text-right font-mono text-slate-400 whitespace-nowrap">{mtrKwh(r.productie, 1)}</td>}
                  {hasProduction && <td className="py-1.5 px-2 text-right font-mono text-slate-300 whitespace-nowrap">{mtrKwh(r.netto, 1)}</td>}
                  <td className="py-1.5 px-2">
                    <span className="block h-1.5 rounded-full bg-slate-700/60 overflow-hidden">
                      <span className="block h-full rounded-full" style={{ width: `${topRows[0].verbruik ? (r.verbruik / topRows[0].verbruik) * 100 : 0}%`, background: tone.bar }} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  // ── Tab "Locaties": alle aansluitingen van de klant naast elkaar ──────────────
  const locatiesTab = (
    <Card className="!p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-100">Alle locaties</h3>
          <p className="text-xs text-slate-500 mt-0.5">Vink aan welke aansluitingen in de cijfers meetellen; klik op een EAN om ze in het detailblok te tonen.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={() => setChartGsrns(allGsrns.map((g) => g.gsrn))} className="text-slate-400 hover:text-lime-400">Alles aanvinken</button>
          <span className="text-slate-600">·</span>
          <button onClick={() => setChartGsrns(selGsrn ? [selGsrn] : [])} className="text-slate-400 hover:text-lime-400">Enkel focus</button>
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm resp-card">
          <thead><tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-500">
            <th className="py-2 px-2 text-left font-medium w-8">In cijfers</th>
            <th className="py-2 px-2 text-left font-medium">Type</th>
            <th className="py-2 px-2 text-left font-medium">EAN</th>
            <th className="py-2 px-2 text-left font-medium">Leveringsadres</th>
            <th className="py-2 px-2 text-left font-medium">Klant</th>
            <th className="py-2 px-2 text-right font-medium">Verbruik</th>
            <th className="py-2 px-2 text-right font-medium">Productie</th>
          </tr></thead>
          <tbody>
            {allGsrns.map((g) => {
              const s = siteTotals.get(g.gsrn);
              const on = chartGsrns.includes(g.gsrn);
              return (
                <tr key={g.gsrn} className={`border-b border-slate-800/50 ${selGsrn === g.gsrn ? 'bg-slate-700/20' : ''}`}>
                  <td className="py-1.5 px-2"><input type="checkbox" checked={on} onChange={() => toggleChartGsrn(g.gsrn)} className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-3.5 h-3.5" /></td>
                  <td className="py-1.5 px-2"><MtrMarketBadge market={g.market} /></td>
                  <td className="py-1.5 px-2">
                    <button onClick={() => focusGsrn(g.gsrn)} className={`font-mono text-xs hover:underline ${selGsrn === g.gsrn ? 'text-lime-400 font-semibold' : 'text-slate-300'}`}>{g.gsrn}</button><EanKopieerKnop ean={g.gsrn} />
                  </td>
                  <td className="py-1.5 px-2 text-xs text-slate-400">{g.address || '—'}</td>
                  <td className="py-1.5 px-2 text-xs text-slate-400 truncate max-w-[220px]">{g.name || '—'}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-xs whitespace-nowrap text-slate-100">{on ? (s ? mtrKwh(s.verbruik) : '…') : <span className="text-slate-600">niet geladen</span>}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-xs whitespace-nowrap text-slate-400">{on ? (s ? mtrKwh(s.productie) : '…') : '—'}</td>
                </tr>
              );
            })}
            {allGsrns.length === 0 && <tr><td colSpan={7} className="py-6"><TableEmpty message="Zoek eerst een klant of EAN." icon={Search} /></td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );

  // ── Ruwe meetgegevens (ingeklapt) ────────────────────────────────────────────
  // Alles wat de API per meting teruggeeft, ongewijzigd: per categorie, per
  // kwartier, per dag en per maand. Dit is het controle-instrument achter de
  // cijfers hierboven; het staat dicht zodat de pagina met het overzicht begint.
  const rawSection = (
    <div ref={rawRef} className="space-y-4">
      <button onClick={() => setRawOpen((o) => !o)}
        className="w-full app-card flex items-center gap-3 px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-2xl text-left">
        <Gauge size={16} className="text-slate-400 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-100">Ruwe meetgegevens</div>
          <div className="text-xs text-slate-500 truncate">Alle metingen van {selGsrn || 'de focus-aansluiting'} per categorie, kwartier, dag en maand</div>
        </div>
        {rawOpen ? <ChevronUp size={16} className="ml-auto text-slate-400 shrink-0" /> : <ChevronDown size={16} className="ml-auto text-slate-400 shrink-0" />}
      </button>

      {rawOpen && error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-3 py-2">{error}</div>}
      {rawOpen && loading && <div className="flex items-center gap-2 text-slate-400 text-sm"><Spinner size={16} />Meetgegevens laden…</div>}
      {rawOpen && data && !loading && (
        <>
          {/* Kengetallen per DATASET (categorie × methode × eenheid). Twee datasets
              van dezelfde EAN kunnen hetzelfde verbruik beschrijven vanuit een andere
              bron — die optellen tot één totaal zou een verzonnen getal opleveren,
              dus staan ze naast elkaar. */}
          <div className="flex flex-wrap gap-3">
            {focusDatasets.map((d) => (
              <StatTile key={d.key}
                label={`${PRODUCTION_METHODS.includes(d.method) ? 'Productie' : 'Verbruik'} · ${d.category}`}
                value={mtrKwh(d.series.totalKwh, 1)}
                accent={PRODUCTION_METHODS.includes(d.method) ? tone.prodInk : tone.bar}
                dot={PRODUCTION_METHODS.includes(d.method) ? tone.prod : tone.bar}
                sub={<>
                  <div>{d.method} · {unitInfo(d.unit).isPower ? 'kW ÷4 naar kWh' : unitInfo(d.unit).label}</div>
                  {d.series.distributedKwh > 0 && <div className="text-indigo-300">{mtrKwh(d.series.distributedKwh, 1)} pro rata verdeeld</div>}
                </>} />
            ))}
            {deduped.superseded > 0 && <StatTile label="Herzien" value={`${mtrFmtNum(deduped.superseded, 0)} rijen`} sub="verouderd door een recentere meting" />}
            {data.summary.gasOnlineCount > 0 && <StatTile label="Gas online-records" value={`${mtrFmtNum(data.summary.gasOnlineCount, 0)} stuks`} sub="gasOnlineData" />}
          </div>
          {(data.fromCache || !data.api?.valid || data.api?.errorMessage) && (
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
              {data.fromCache && <span className="flex items-center gap-1"><Info size={12} />Uit de cache (max. 1 uur oud)</span>}
              {!data.api?.valid && <span className="text-amber-300">API meldt valid=false</span>}
              {data.api?.errorMessage && <span className="text-amber-300">API-melding: {data.api.errorMessage}</span>}
            </div>
          )}

          {/* Eén compact overzicht van alle dimensies die de API per meting meegeeft.
              De hoeveelheid staat PER EENHEID: kWh (energie), kW (vermogen) en
              kVAr/K3 (reactief) zijn verschillende grootheden, dus één totaal
              eroverheen zou betekenisloos zijn. */}
          <Card className="!p-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h3 className="text-base font-semibold text-slate-100">Overzicht</h3>
              <p className="text-xs text-slate-500">Aantal metingen en hoeveelheid per eenheid, over de gekozen periode</p>
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs resp-card">
                <thead><tr className="border-b border-slate-700 uppercase tracking-wider text-slate-500">
                  <th className="py-1.5 px-2 text-left font-medium">Dimensie</th>
                  <th className="py-1.5 px-2 text-left font-medium">Waarde</th>
                  <th className="py-1.5 px-2 text-right font-medium">Metingen</th>
                  <th className="py-1.5 px-2 text-right font-medium">Hoeveelheid</th>
                </tr></thead>
                <tbody>
                  {[
                    ['byCategory', 'Categories'], ['byMethod', 'Methods'], ['byTimeframe', 'Timeframe'],
                    ['byStatus', 'Status'], ['byContext', 'Metering context'],
                    ['byInterval', 'Interval'], ['byQuality', 'Kwaliteit kwartierwaarden'],
                  ].flatMap(([key, title]) => {
                    const rows = data.summary[key] || [];
                    return rows.map((r, i) => (
                      // De dimensienaam staat enkel op de eerste regel van haar groep;
                      // de scheidingslijn erboven houdt de groepen leesbaar.
                      <tr key={`${key}-${r.name}`} className={i === 0 ? 'border-t border-slate-700/60' : ''}>
                        <td className="py-1 px-2 text-slate-500 whitespace-nowrap">{i === 0 ? title : ''}</td>
                        <td className="py-1 px-2 text-slate-200">
                          {key === 'byInterval' ? (r.name === '0' ? 'geen interval' : `${r.name} min`) : r.name}
                        </td>
                        <td className="py-1 px-2 text-right text-slate-400 whitespace-nowrap">{mtrFmtNum(r.count, 0)}</td>
                        <td className="py-1 px-2 text-right font-mono text-slate-200 whitespace-nowrap">
                          {(r.units || []).map((u) => fmtQty(u.quantity, u.unit)).join(' · ') || '—'}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Eén tabel per CATEGORIE (Ediel / EdielHistorical / CMSAggregation …).
              Deze tabellen tonen de ruwe rijen inclusief de verouderde Rectified-
              metingen — dat detail is juist waarvoor ze bedoeld zijn. */}
          {deduped.superseded > 0 && (
            <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>
                {mtrFmtNum(deduped.superseded, 0)} van de {mtrFmtNum(tableEntities.length, 0)} metingen zijn herzien: voor dezelfde periode bestaat een recentere waarde.
                De categorietabellen tonen ze allemaal (verouderde rijen gedimd, status <span className="text-amber-300">Rectified</span>); de kwartier-, dag- en maandtabellen en de grafiek gebruiken uitsluitend de nieuwste waarde per periode.
              </span>
            </p>
          )}
          {categoryTables.map((g) => (
            <MtrCategoryTable key={g.category} category={g.category} rows={g.rows} gsrn={selGsrn} fileTag={fileTag} />
          ))}
          {/* Categorieën die de API ondersteunt maar die hier geen enkele meting
              hebben: expliciet benoemen, anders is hun afwezigheid onzichtbaar. */}
          {(() => {
            const aanwezig = new Set(categoryTables.map((g) => g.category));
            const ontbreekt = knownCategories.filter((c) => !aanwezig.has(c));
            if (!ontbreekt.length) return null;
            return (
              <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
                <Info size={13} className="mt-0.5 shrink-0" />
                <span>Geen metingen in deze periode voor: {ontbreekt.join(', ')}. De data wordt ongefilterd opgehaald, dus alle categorieën die de API levert staan hierboven.</span>
              </p>
            );
          })()}

          {/* Afgeleide tabellen, alle op de ontdubbelde set en zonder eenheden te
              mengen: elke rij draagt haar eigen eenheid. */}
          <MtrQuarterTable rows={quarterRows} gsrn={selGsrn} fileTag={fileTag} />
          <MtrBucketTable
            title="Dagwaarden" keyLabel="Dag" filePrefix="dagwaarden" agg={dayAgg} gsrn={selGsrn} fileTag={fileTag}
            subtitle='per dag (Brusselse tijd) × methode × eenheid; bron "kwartier" = opgeteld uit de kwartierwaarden, "periode" = de meting zelf'
          />
          <MtrBucketTable
            title="Maandwaarden" keyLabel="Maand" filePrefix="maandwaarden" agg={monthAgg} gsrn={selGsrn} fileTag={fileTag}
            subtitle='per maand × methode × eenheid; een maandstand van de netbeheerder verschijnt hier als bron "periode"'
          />
        </>
      )}
    </div>
  );

  const nothingSelected = !chartGsrns.length;

  // ── Waar wacht de pagina op? ──────────────────────────────────────────────
  // Kom je hier via het rechtsklikmenu (Orders, Bill Shock, Invoices…), dan gebeurt
  // er eerst een hoop dat je niet ziet: de aansluitingenlijst moet uit de orders
  // komen, daarna moet de laatste periode mét metingen gezocht worden, en pas dan
  // komen de meetgegevens zelf binnen. Zonder signaal staart de gebruiker naar een
  // lege pagina die zegt "zoek op klantnaam" — terwijl de app hard aan het werk is.
  // Dus: één duidelijke melding midden op het scherm, met de fase erbij.
  const prefillBezig = !!prefillKey && toegepastePrefill.current !== prefillKey;
  const wachtOpAansluitingen = prefillBezig && (ordersLoading || !conns.length);
  // "Eerste keer": bij het wisselen van periode staan de cijfers er al en hoeft het
  // scherm niet opnieuw dichtgelegd te worden — dan volstaat de spinner in de grafiek.
  const eersteMeting = (seriesLoading && !seriesRaw.length) || (loading && !data && !seriesRaw.length);
  const paginaLaadt = wachtOpAansluitingen || prefillBezig || eersteMeting;
  const laadTitel = wachtOpAansluitingen ? 'Aansluitingen zoeken…'
    : prefillBezig ? 'Klant openen…'
    : 'Meetgegevens ophalen…';
  const laadUitleg = wachtOpAansluitingen ? 'De aansluitingen van deze klant worden uit de orders gehaald.'
    : prefillBezig ? 'De juiste aansluiting wordt geselecteerd.'
    : 'De metingen per aansluiting worden opgehaald; even geduld.';

  return (
    <div className={`space-y-4${embedded ? ' relative' : ''}`}>
      {paginaLaadt && (
        // pointer-events-none: puur een melding, ze mag geen klik tegenhouden.
        // Ingebed in Customer 360 dekt ze enkel de tab, niet het hele scherm.
        <div className={`${embedded ? 'absolute' : 'fixed'} inset-0 z-[8000] flex items-center justify-center bg-black/20 backdrop-blur-[1px] pointer-events-none`}>
          <div className="flex flex-col items-center gap-3 px-7 py-6 rounded-2xl bg-slate-800 border border-slate-600 shadow-2xl max-w-[19rem] text-center">
            <Spinner size={34} />
            <div className="text-sm font-semibold text-slate-100">{laadTitel}</div>
            <div className="text-[11px] text-slate-400 leading-relaxed">{laadUitleg}</div>
          </div>
        </div>
      )}
      {!embedded && (
        <PageHeader title="Metering" subtitle="Meetgegevens per aansluiting — elektriciteit en gas, altijd in kWh">
          {loadedAt && (
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <span className="w-2 h-2 rounded-full" style={{ background: MTR_GOOD }} />
              Laatst bijgewerkt: {new Intl.DateTimeFormat('nl-BE', { hour: '2-digit', minute: '2-digit', hour12: false }).format(loadedAt)}
            </span>
          )}
        </PageHeader>
      )}
      {filterBar}
      {detailExportDialog}

      {!selEmail && !selRef && !selGsrn ? (
        // Tijdens een prefill NIET "zoek op klantnaam" tonen: er is al gekozen, de
        // pagina is die keuze alleen nog aan het opzoeken.
        prefillBezig ? (
          <Card className="!p-8"><div className="flex flex-col items-center gap-3 text-slate-400"><Spinner size={28} /><span className="text-sm">Aansluitingen laden…</span></div></Card>
        ) : (
          <Card className="!p-8"><TableEmpty message={embedded ? "Deze klant heeft geen aansluiting met meetgegevens." : "Zoek op klantnaam, klantnummer of EAN om te beginnen."} icon={embedded ? Gauge : Search} /></Card>
        )
      ) : tab === 'locaties' ? (
        locatiesTab
      ) : nothingSelected ? (
        <Card className="!p-8"><TableEmpty message="Vink bij “Locatie / EAN van deze klant” minstens één aansluiting aan." icon={MapPin} /></Card>
      ) : !marketsAvailable.has(market) ? (
        <Card className="!p-8"><TableEmpty message={`Deze klant heeft geen ${tone.label.toLowerCase()}-aansluiting.`} icon={tone.icon} /></Card>
      ) : (
        <>
          {/* Hoofdgrid: de grafiek links neemt alle resterende breedte, de
              kengetallen staan rechts in een VASTE kolom van 240 px — smal genoeg
              om de grafiek breed te laten, breed genoeg voor "1.216,59 kWh". De
              kolom rekt mee tot de hoogte van de grafiekkaart en de kaarten
              verdelen die hoogte gelijk (auto-rows-fr), zodat beide onderaan
              gelijk eindigen. Onder xl (laptop, tablet, telefoon) staat de kolom
              onder de grafiek; de grafiek komt in de DOM eerst, want die is waar
              de pagina om draait. */}
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_240px] gap-4 xl:items-stretch">
            {chartCard}
            {kpiCards}
          </div>
          {/* Het dagprofiel rekent pas na een klik; zie MtrDagprofielKaart. De
              periode eindigt op de dag van de hoofdgrafiek (dagweergave) of op
              de laatste dag van haar periode, nooit in de toekomst. */}
          <MtrDagprofielKaart
            token={token}
            gsrns={activeMarketGsrns.map((g) => g.gsrn)}
            totDag={dagprofielTot}
            standaardDag={viewMode === 'day' ? anchor : dagprofielTot}
            tone={tone}
            kwartierMogelijk={detailKwartier}
            spotMogelijk={market === 'EL'}
            embedded={embedded}
            onRapport={bewaarDagprofiel}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <MtrTopLocations rows={combined.perSite.filter((s) => s.verbruik > 0).slice(0, 5)} tone={tone} focus={selGsrn} onFocus={focusGsrn} onShowAll={() => setTab('locaties')} />
            <MtrDonut
              title={hasProduction ? 'Verdeling verbruik / injectie' : 'Verdeling per locatie'}
              tip={hasProduction ? 'De meter registreert wat er van het net wordt afgenomen (verbruik) en wat er op het net wordt gezet (injectie). Eigen verbruik achter de meter wordt niet gemeten en zit dus in geen van beide.' : null}
              slices={donutSlices}
              footerLabel={hasProduction ? 'Netto verbruik' : 'Totaal verbruik'}
              footerValue={mtrKwh(hasProduction ? combined.totals.netto : combined.totals.verbruik)}
            />
            {detailCard}
          </div>
          {topCard}
          {rawSection}
        </>
      )}
    </div>
  );
};

// Initialen (max 2) uit een naam, voor de profiel-avatar in Customer 360.
const nameInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

// Aansluitadressen in twee kolommen met een subtiele scheidingslijn per adres;
// inklapbaar tot de eerste 10 met een "Toon alle …"-knop.
// Aansluitingen van de klant, compact: één regel per EAN met het adres en
// daaronder de kenmerken die je bij een aansluiting wil zien — GSRN, markt,
// metertype, connectietype en CP-status. Eerder stond hier alleen het adres,
// waardoor je voor de rest naar de orderstabel moest.
const C360Aansluitingen = ({ items = [] }) => {
  // Vijf per pagina i.p.v. "eerst acht, daarna alles". Zo houdt dit blok een
  // VASTE, korte hoogte: het loopt niet meer weg onder de kaart ernaast bij een
  // klant met veel EAN's, en bij weinig aansluitingen blijft er links geen
  // grote witruimte staan. Alles blijft bereikbaar — via de pagina's, niet door
  // de kaart te laten groeien. Helpdesk en notities schuiven daardoor omhoog.
  const PER_PAGINA = 5;
  const [pagina, setPagina] = useState(1);
  const totaalPaginas = Math.max(1, Math.ceil(items.length / PER_PAGINA));
  // Wisselt de klant (minder aansluitingen), dan mag de teller niet blijven staan.
  const huidige = Math.min(pagina, totaalPaginas);
  const getoond = items.slice((huidige - 1) * PER_PAGINA, huidige * PER_PAGINA);
  // CP-status kleurt zoals in de Orders-tabel: groen = owned, rood = vertrokken,
  // amber = in beweging, grijs = draft/onbekend.
  const statusKleur = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'owned') return 'bg-lime-900/50 text-lime-300';
    if (v === 'ext' || v === 'external') return 'bg-red-900/50 text-red-300';
    if (v.startsWith('in') || v.startsWith('int')) return 'bg-amber-900/50 text-amber-300';
    return 'bg-slate-700 text-slate-300';
  };
  if (!items.length) return <p className="text-sm text-slate-500">Geen aansluitingen.</p>;
  const navKnop = 'px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed';
  return (
    <>
      <div className="divide-y divide-slate-700/30">
        {getoond.map((a) => (
          <div key={a.gsrn || a.adres} className="py-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
            <div className="flex items-start gap-2 text-[13px] text-slate-300 min-w-0">
              <MapPin size={13} className="text-slate-500 mt-0.5 shrink-0" />
              <span className="min-w-0 leading-tight">
                <span className="block break-words">{a.adres || '—'}</span>
                {a.gsrn && <span className="flex items-center font-mono text-[10px] text-slate-500">{a.gsrn}<EanKopieerKnop ean={a.gsrn} className="w-4 h-4" /></span>}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[10px] shrink-0">
              {a.markt && <span className={`px-1.5 py-0.5 rounded font-semibold ${a.markt === 'NG' ? 'bg-orange-500/15 text-orange-300' : 'bg-sky-500/15 text-sky-300'}`}>{a.markt}</span>}
              {a.meterMethod && <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300">{a.meterMethod}</span>}
              {a.connectionType && <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300">{a.connectionType}</span>}
              {a.cpStatus && <span className={`px-1.5 py-0.5 rounded font-medium ${statusKleur(a.cpStatus)}`}>{a.cpStatus}</span>}
            </div>
          </div>
        ))}
      </div>
      {items.length > PER_PAGINA && (
        <div className="mt-2 pt-2 border-t border-slate-700/30 flex items-center justify-between gap-2 text-[11px] text-slate-400">
          <span>{(huidige - 1) * PER_PAGINA + 1}–{Math.min(huidige * PER_PAGINA, items.length)} van {items.length}</span>
          <span className="flex items-center gap-1">
            <button onClick={() => setPagina(Math.max(1, huidige - 1))} disabled={huidige === 1} className={navKnop} aria-label="Vorige aansluitingen">‹</button>
            <span className="px-1 tabular-nums">{huidige} / {totaalPaginas}</span>
            <button onClick={() => setPagina(Math.min(totaalPaginas, huidige + 1))} disabled={huidige === totaalPaginas} className={navKnop} aria-label="Volgende aansluitingen">›</button>
          </span>
        </div>
      )}
    </>
  );
};

// Gepagineerde tabelrijen voor Customer 360: toont TABLE_PAGE_SIZE (15) rijen +
// een navigatie-rij onderaan de tabel wanneer er meer zijn. `page`/`setPage` per
// tabel; colSpan={99} laat de navigatie-rij over alle kolommen lopen. Retourneert
// een array van <tr>'s (geldig als <tbody>-kinderen), zodat enkel de openingsregel
// van elke tabel hoeft te wijzigen — de sluit-tags blijven identiek.
const renderPaged = (page, setPage, rows, render, pageSize = TABLE_PAGE_SIZE) => {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const clamped = Math.min(page, totalPages);
  const out = rows.slice((clamped - 1) * pageSize, clamped * pageSize).map(render);
  if (total > pageSize) out.push(
    <tr key="__c360pager" className="border-t border-slate-700/50 bg-slate-800/30">
      <td colSpan={99} className="py-2 px-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">Toont {((clamped - 1) * pageSize) + 1}–{Math.min(clamped * pageSize, total)} van {total}</span>
          <span className="flex items-center gap-2">
            <button onClick={() => setPage(Math.max(1, clamped - 1))} disabled={clamped === 1} className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-xs rounded">Vorige</button>
            <span className="px-2.5 py-1 bg-lime-500 text-slate-900 font-semibold text-xs rounded">{clamped} / {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, clamped + 1))} disabled={clamped === totalPages} className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 text-xs rounded">Volgende</button>
          </span>
        </div>
      </td>
    </tr>
  );
  return out;
};

const Customer360Page = ({ orders = [], ordersLoading = false, facturen = [], selfbills = [], facturenLoading = false, token, currentUser, prefill, prefillKey, visiblePartnerTags, salesStructure = EMPTY_STRUCTURE, onReassignOrder, labelConfig = {}, userModules = null, branding = null, allBranding = [], allTeamBranding = [] }) => {
  // ── Tab-menu ───────────────────────────────────────────────────────────────
  // Het klantdossier is opgedeeld in tabs; welke actief is, blijft bewaard zodat
  // een volgende klant in hetzelfde tabblad opent.
  //
  // Welke tabs er staan, is per gebruiker in te stellen (subpagina's). De
  // Metering-tab kent BOVENDIEN een modulegrens: hij verschijnt alleen als de
  // gebruiker ook de Metering-module heeft, ook al staat de subpagina aan —
  // Customer 360 mag geen achterdeur zijn naar een module die een beheerder
  // bewust heeft weggehaald. userModules = null betekent "geen beperking bekend"
  // (admin), dus wél tonen.
  const kanMetering = !userModules || userModules.has('metering');
  const alleC360Tabs = useMemo(() => [
    { id: 'samenvatting', label: 'Samenvatting' },
    // `kort` = het label op een telefoon; de volle namen duwden de strip te breed.
    { id: 'orders', label: 'Orders' },
    { id: 'facturen', label: 'Facturen', kort: 'Facturen' },
    ...(kanMetering ? [{ id: 'metering', label: 'Metering' }] : []),
    { id: 'billshock', label: 'Bill Shock' },
    { id: 'debtcollection', label: 'Debt Collection', kort: 'Debt' },
    { id: 'paymentplans', label: 'Payment Plans', kort: 'Plans' },
  ], [kanMetering]);
  const { tabs: c360Tabs, mag: magC360Tab, leeg: geenC360Tabs } = useSubPages('customer360', currentUser, alleC360Tabs);
  const [c360Tab, setC360Tab] = useState(() => {
    return leesSessieWaarde('c360Tab') || 'samenvatting';
  });
  useEffect(() => { schrijfSessieWaarde('c360Tab', c360Tab); }, [c360Tab]);
  // Verdween de actieve tab (bv. Metering ingetrokken of de subpagina niet
  // toegekend), val dan terug op de eerste die deze gebruiker wél mag zien.
  useEffect(() => {
    if (c360Tabs.length > 0 && !c360Tabs.some(t => t.id === c360Tab)) setC360Tab(c360Tabs[0].id);
  }, [c360Tabs, c360Tab]);
  const toonC360Tab = (id) => magC360Tab(id) && c360Tab === id;

  const c360HdSort = useTableSort();
  const c360OrdSort = useTableSort();
  const c360FacSort = useTableSort();
  const c360AgeSort = useTableSort();
  const c360RefRSort = useTableSort();
  const c360RefSSort = useTableSort();
  const c360BsSort = useTableSort();
  const [c360HdPage, setC360HdPage] = useState(1);
  const [c360OrdPage, setC360OrdPage] = useState(1);
  const [c360FacPage, setC360FacPage] = useState(1);
  const [c360AgePage, setC360AgePage] = useState(1);
  const [c360RefRPage, setC360RefRPage] = useState(1);
  const [c360RefSPage, setC360RefSPage] = useState(1);
  const [c360BsPage, setC360BsPage] = useState(1);
  const showBrand = seesEtBrand(currentUser?.role);
  // Rechtsklik op een factuurrij → "Invoice PDF" (we zitten al in Customer 360, dus
  // geen C360/Metering-navigatie → onOpen = null → enkel de PDF-actie).
  const { onContext: onInvoicePdfCtx, menu: invoicePdfMenu } = useCustomer360Menu(null);
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState(null); // { type: 'customer'|'gsrn', value, label }
  // Het zoekveld (KlantZoekVeld) beheert zijn eigen suggestiepaneel.

  // Prefill vanuit een ander menu-item (bv. klik op klant/GSRN in Orders).
  // We luisteren op `prefillKey` zodat opnieuw klikken op dezelfde klant ook
  // weer triggert; een lege prefillKey (0) tijdens initial render doet niets.
  useEffect(() => {
    if (!prefill || !prefillKey) return;
    setSelection({ type: prefill.type, value: prefill.value, ref: prefill.ref || '', label: prefill.label || prefill.value });
    setQuery(prefill.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);
  const [billshocks, setBillshocks] = useState([]);
  const [billshockLoading, setBillshockLoading] = useState(false);
  const [billshockError, setBillshockError] = useState(null);
  // Detail-modals — hergebruiken de bestaande full-detail viewers uit Orders en Bill Shock.
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedBillshock, setSelectedBillshock] = useState(null);
  // Helpdesk-tickets van deze klant (op uniek klantnummer). Alleen als de
  // gebruiker leesrechten op de Helpdesk heeft ÉN de subpagina "Helpdesk
  // tickets" van Customer 360 toegekend kreeg (per gebruiker instelbaar; admin/ET
  // altijd, master/teamleader standaard aan, verkoper standaard uit). Het is geen
  // tab maar een blok op de Samenvatting, dus het loopt niet via c360Tabs.
  const hdCaps = useMemo(() => new Set(currentUser?.helpdeskPerms || []), [currentUser]);
  const magTicketsBlok = useMemo(() => {
    const set = subPageSetForUser(currentUser);
    return !set || set.has(subPageKey('customer360', 'tickets'));
  }, [currentUser]);
  const hdCanRead = hdCaps.has('read') && magTicketsBlok;
  const [hdTickets, setHdTickets] = useState({ items: [], loading: false, error: null });
  const [hdSelectedId, setHdSelectedId] = useState(null);
  const [hdCompanies, setHdCompanies] = useState({});
  const [hdStages, setHdStages] = useState([]);
  const [hdRefresh, setHdRefresh] = useState(0);
  const [hdStatusFilter, setHdStatusFilter] = useState('open'); // 'open' | 'closed' | 'all' — default enkel openstaand
  // Nieuw ticket vanuit het klantdossier: de klant staat al vast (naam +
  // klantnummer gaan als prefill mee), dus geen aparte zoektocht in de Helpdesk.
  const [hdShowCreate, setHdShowCreate] = useState(false);
  // Payment plans (V2) per customer reference. Wordt geladen wanneer de
  // selectie wisselt naar een klant of GSRN met een bekende customerReference.
  const [paymentPlans, setPaymentPlans] = useState({ entities: [], loading: false, error: null, errorMessage: null });
  // Debt collection: status-map (alle gescopete klant-refs) + per-ref audit-logs.
  // Editing van statussen + notes gebeurt inline (zelfde flow als DebtCollectionPage).
  const [debtStatus, setDebtStatus] = useState({});       // { [customerReference]: { contact_status, customer_response, complaint_status, notes, updated_at, updated_by } }
  const [debtLogs, setDebtLogs] = useState({});          // { [customerReference]: [logEntry, ...] }
  const [debtError, setDebtError] = useState(null);
  const [debtSaving, setDebtSaving] = useState(null);    // ref dat momenteel een PUT in flight heeft (voor knop-disable)

  // BillShocks one-time on mount; backend cache is 5 min.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setBillshockLoading(true); setBillshockError(null);
    // Gepagineerd (zie BillShockPage): nooit één groot antwoord, wel de volledige
    // set. De records dragen zelf `isOrphan`, dus splitsen is hier niet nodig —
    // Customer 360 matcht op GSRN.
    fetchPaginated('/api/billshock', token)
      .then(alle => { if (!cancelled) setBillshocks(alle); })
      .catch(e => { if (!cancelled) setBillshockError(e.message); })
      .finally(() => { if (!cancelled) setBillshockLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Helpdesk referentiedata (companies → labelnaam, partner-selecteerbare stages)
  // éénmalig, enkel met leesrechten.
  useEffect(() => {
    if (!token || !hdCanRead) return;
    const api = hdApi(token);
    api.get('/companies').then((d) => setHdCompanies(d.byId || {})).catch(() => {});
    api.get('/stages').then((d) => setHdStages(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token, hdCanRead]);

  // Zoeksuggesties — gedeeld met Orders, Invoices en Metering (zie
  // bouwZoekSuggesties): klanten met segment, klantnummer en het aantal orders,
  // plus de GSRN's. Een factuurnummer leidt hier naar de klant van die factuur.
  const zoekKenmerken = useMemo(() => klantKenmerken(orders), [orders]);
  const suggesties = useMemo(() => bouwZoekSuggesties({
    query, orders, facturen, kenmerken: zoekKenmerken,
  }), [query, orders, facturen, zoekKenmerken]);

  // `ref` gaat enkel mee als de gebruiker op een KLANTNUMMER zocht: dan start het
  // dossier met alleen dat nummer aangevinkt (zie klantnummerKeuze). Wie op naam
  // zoekt, krijgt de hele klant met al zijn nummers.
  const selectCustomer = (name, label, ref = '') => { setSelection({ type: 'customer', value: name, ref: String(ref || '').trim(), label: label || name }); setQuery(label || name); };
  const selectGsrn = (gsrn, customerName) => { setSelection({ type: 'gsrn', value: gsrn, label: customerName ? `${customerName} · ${gsrn}` : gsrn }); setQuery(gsrn); };
  const clearSelection = () => { setSelection(null); setQuery(''); };
  // Zocht de gebruiker op het klantnummer zelf? Dan is de suggestie via dat nummer
  // gevonden en hoort het dossier op dat nummer te starten. Een naam-, e-mail- of
  // BTW-zoekopdracht levert géén ref: de suggestie draagt dan het toevallig eerste
  // nummer van die klant, en dat mag het dossier niet beperken.
  const refUitZoekopdracht = (k) => {
    const ref = String(k?.ref || '').trim();
    const q = String(query || '').trim().toLowerCase();
    return ref && q.length >= 3 && ref.toLowerCase().includes(q) ? ref : '';
  };

  // Alles van deze klant, over al zijn klantnummers heen. De tabs kijken niet
  // hiernaar maar naar `matched` (hieronder), dat op de aangevinkte nummers filtert.

  // ── Snel pad voor de facturen: alleen die van deze klant ─────────────────
  // De volledige factuurlijst (51.698 records, 206 pagina's, 59 MB) laadt op de
  // achtergrond voor Invoices en Aging. Dit dossier hoeft daar niet op te wachten:
  // zolang die lijst onderweg is, vraagt het de facturen, creditnota's en self
  // bills van zíjn klant apart op (GET /api/invoices/klant, één request, enkele
  // kB) en koppelt die al. Zodra de volledige lijst binnen is, neemt die het
  // over — dezelfde records (ontdubbeld op id), dus niets verspringt.
  // De vraag (klantnummers + namen + ondernemingsnamen) komt uit de ORDERS en de
  // selectie, nooit uit de facturen zelf — anders zou elke binnenkomende factuur
  // een nieuwe vraag uitlokken.
  const [facturenDoel, setFacturenDoel] = useState({ sleutel: '', facturen: [], selfbills: [] });
  const facturenDossier = useMemo(() => {
    if (!facturenLoading || !facturenDoel.facturen.length) return facturen;
    const gezien = new Set(facturen.map((x) => x.id));
    const extra = facturenDoel.facturen.filter((x) => !gezien.has(x.id));
    return extra.length ? [...facturen, ...extra] : facturen;
  }, [facturen, facturenDoel.facturen, facturenLoading]);
  const selfbillsDossier = useMemo(() => {
    if (!facturenLoading || !facturenDoel.selfbills.length) return selfbills || [];
    const gezien = new Set((selfbills || []).map((x) => x.id));
    const extra = facturenDoel.selfbills.filter((x) => !gezien.has(x.id));
    return extra.length ? [...(selfbills || []), ...extra] : (selfbills || []);
  }, [selfbills, facturenDoel.selfbills, facturenLoading]);

  const matchedAlles = useMemo(() => {
    if (!selection) return null;
    let mOrders;
    const vats = new Set();        // BTW-nummers van deze klant (genormaliseerd)
    const bedrijven = new Set();   // ondernemingsnamen van deze klant (genormaliseerd)
    if (selection.type === 'gsrn') {
      mOrders = orders.filter(o => (o.connectionPointGSRN || '').trim() === selection.value);
    } else {
      // Klant-selectie: matchen op klantnaam ÉN op klantnummer, want de rij waar de
      // selectie vandaan komt kent de klant niet altijd onder dezelfde naam. Een
      // ORDERrij draagt de contactpersoon, een FACTUURrij de onderneming — op naam
      // alleen bleef dit dossier bij elke zakelijke klant leeg, terwijl het zoekveld
      // hiernaast dezelfde klant wél vond (zie klantTarget).
      //
      // UNIE, geen vervanging: één klantnaam kan meer dan één klantnummer dragen en
      // niet elke order heeft er een, dus enkel op ref filteren zou minder orders
      // tonen dan voorheen. Zonder ref (oude deep-link, eigen zoekveld) is het
      // gedrag exact als vroeger.
      const q = c360Norm(selection.value);
      const ref = String(selection.ref || '').trim();
      mOrders = orders.filter(o => (q && c360Norm(o.customer) === q)
        || (ref && String(o.customerReference || '').trim() === ref));
      // Dezelfde klant onder een ANDER klantnummer. Een zakelijke klant krijgt in
      // het bronsysteem per contract of vestiging een eigen nummer, vaak met een
      // andere contactpersoon op de order (Beanscene EBVBA: twee nummers, twee
      // contacten). Op naam of nummer alleen bleef zo'n tweede nummer buiten het
      // dossier en kon het dus ook niet aangevinkt worden. Zelfde BTW-nummer of
      // zelfde ondernemingsnaam (enkel bij zakelijke orders) = zelfde klant.
      for (const o of mOrders) {
        const vat = c360NormVat(o.customerVat);
        if (vat) vats.add(vat);
        if (isBusiness(o)) { const b = c360Norm(o.customerCompanyName); if (b) bedrijven.add(b); }
      }
      if (vats.size || bedrijven.size) {
        const gezien = new Set(mOrders.map(o => o.id));
        for (const o of orders) {
          if (gezien.has(o.id)) continue;
          if ((vats.size && vats.has(c360NormVat(o.customerVat)))
            || (bedrijven.size && isBusiness(o) && bedrijven.has(c360Norm(o.customerCompanyName)))) { mOrders.push(o); gezien.add(o.id); }
        }
      }
    }
    // Customer references uit gematchte orders (link naar facturen)
    const custRefs = new Set(mOrders.map(o => (o.customerReference || '').trim()).filter(Boolean));
    const customerNames = new Set(mOrders.map(o => c360Norm(o.customer)).filter(Boolean));
    const gsrns = new Set(mOrders.map(o => (o.connectionPointGSRN || '').trim()).filter(Boolean));
    // De selectie zelf hoort er ook bij. Zonder dit blijft het hele dossier leeg
    // zodra er GEEN order matcht — bv. een klant wiens orders buiten de scope of uit
    // de lijst gevallen zijn: de facturen zijn er dan wél, maar custRefs/customerNames
    // worden uit de orders opgebouwd en zijn dus leeg. De klant die je aanklikte is
    // per definitie deze klant, dus zijn naam en klantnummer mogen mee.
    if (selection.type === 'customer') {
      const q = c360Norm(selection.value);
      const ref = String(selection.ref || '').trim();
      if (q) customerNames.add(q);
      if (ref) custRefs.add(ref);
    }

    // De vraag voor het snelle factuurpad (zie facturenDoel hierboven): de stand
    // VÓÓR de uitbreiding via de facturen, zodat ze enkel van orders + selectie
    // afhangt en niet verandert wanneer de facturen binnenkomen. De server past
    // dezelfde uitbreiding toe (util/factuurSelectie.js).
    const doelVraag = { refs: [...custRefs].sort(), namen: [...customerNames].sort(), bedrijven: [...bedrijven].sort() };
    doelVraag.sleutel = `${selection.type}:${selection.value}|${doelVraag.refs.join(',')}|${doelVraag.namen.join('')}|${doelVraag.bedrijven.join('')}`;

    // Facturen: koppel via relatieReferentie ÓF via klantnaam (relatieNaam). De
    // naam-match is nodig omdat order.customerReference vaak leeg is / anders
    // geformatteerd dan factuur.relatieReferentie → anders bleef de tabel leeg.
    // Als selectie=GSRN: extra filter op gsrn-velden indien aanwezig.
    // Ook op ONDERNEMINGSnaam: een factuur van een zakelijke klant draagt de
    // onderneming, niet de contactpersoon van de order.
    const factuurNaamPast = (f) => { const n = c360Norm(f.relatieNaam); return !!n && (customerNames.has(n) || bedrijven.has(n)); };
    let mFacturen = facturenDossier.filter(f =>
      (f.relatieReferentie && custRefs.has(f.relatieReferentie)) || factuurNaamPast(f)
    );
    // Facturen op de ONDERNEMINGSnaam kunnen een klantnummer dragen dat op geen
    // enkele gematchte order staat: dan hoort dat nummer, mét zijn orders, óók bij
    // deze klant. Eén uitbreidingsronde volstaat (de nieuwe orders brengen geen
    // nieuwe namen mee die we nog niet kenden). Bewust enkel via de
    // ondernemingsnaam en niet via de contactpersoon: twee verschillende "Jan
    // Janssens" mogen niet samensmelten.
    if (selection.type === 'customer' && bedrijven.size) {
      const extraRefs = new Set(mFacturen
        .map(f => String(f.relatieReferentie || '').trim())
        .filter((r, i) => r && !custRefs.has(r) && bedrijven.has(c360Norm(mFacturen[i].relatieNaam))));
      if (extraRefs.size) {
        const gezien = new Set(mOrders.map(o => o.id));
        for (const o of orders) {
          const r = String(o.customerReference || '').trim();
          if (r && extraRefs.has(r) && !gezien.has(o.id)) {
            mOrders.push(o); gezien.add(o.id);
            const g = String(o.connectionPointGSRN || '').trim(); if (g) gsrns.add(g);
            const n = c360Norm(o.customer); if (n) customerNames.add(n);
          }
        }
        for (const r of extraRefs) custRefs.add(r);
        mFacturen = facturenDossier.filter(f => (f.relatieReferentie && custRefs.has(f.relatieReferentie)) || factuurNaamPast(f));
      }
    }
    if (selection.type === 'gsrn') {
      // Sommige factuur-records hebben EL/GSRN of NG/GSRN velden; filter erop als die bestaan
      mFacturen = mFacturen.filter(f => {
        const fg = [f.elGSRN, f.ngGSRN].filter(Boolean);
        return fg.length === 0 || fg.includes(selection.value);
      });
    }

    // Aging: openstaande Verkoopfacturen voorbij vervaldatum
    const today = new Date();
    const agingItems = mFacturen.filter(f => {
      const status = (f.statusBetaling || '').toLowerCase();
      const isUnpaid = status !== 'betaald' && status !== 'omgekeerd';
      const isVerkoop = (f.soort || '').toLowerCase() === 'verkoopfactuur';
      const due = f.vervaldatum ? new Date(f.vervaldatum) : null;
      return isUnpaid && isVerkoop && due && due < today;
    }).map(f => ({
      ...f,
      daysOverdue: Math.floor((today - new Date(f.vervaldatum)) / 86400000),
    }));

    // Referrals: orders waarin deze klant referrer is (aangebracht door iemand anders),
    // én orders die door deze klant zijn aangebracht (deze klant als referrer in andere orders).
    const referralsReceived = mOrders.filter(o => o.ambassadorCode || o.referralLinkName || o.referrer || o.referralAmbassadorCode);
    const referralsSent = orders.filter(o => o.referrer && customerNames.has(c360Norm(o.referrer)));

    // BillShock: matchen op GSRN
    const mBillshocks = billshocks.filter(b => gsrns.has((b.connectionPointGSRN || '').trim()));

    // Self bills van deze klant: op klantnummer, anders op naam (zelfde regel als de facturen).
    const mSelfbills = selfbillsDossier.filter(f => (f.relatieReferentie && custRefs.has(f.relatieReferentie)) || (f.relatieNaam && customerNames.has(c360Norm(f.relatieNaam))));
    return { orders: mOrders, facturen: mFacturen, selfbills: mSelfbills, agingItems, referralsReceived, referralsSent, billshocks: mBillshocks, custRefs, gsrns, customerNames, doelVraag };
  }, [selection, orders, facturenDossier, selfbillsDossier, billshocks]);

  // De gerichte factuurophaal zelf. Eén request per vraag-sleutel; het antwoord
  // telt alleen als de vraag intussen niet veranderd is (andere klant gekozen).
  // Geen cleanup die het lopende request weggooit: `matchedAlles` verandert bij
  // elke binnenkomende orderpagina, en dan zou het antwoord nooit landen.
  // Mislukt de ophaal, dan blijft het dossier gewoon wachten op de volledige
  // lijst — zoals vroeger. Is die lijst al binnen, dan gebeurt hier niets.
  const doelGevraagd = useRef('');
  const doelGewenst = useRef('');
  useEffect(() => {
    const vraag = matchedAlles && matchedAlles.doelVraag;
    doelGewenst.current = vraag ? vraag.sleutel : '';
    if (!token || !facturenLoading || !vraag || (!vraag.refs.length && !vraag.namen.length && !vraag.bedrijven.length)) return;
    if (doelGevraagd.current === vraag.sleutel) return;
    doelGevraagd.current = vraag.sleutel;
    const qs = new URLSearchParams();
    if (vraag.refs.length) qs.set('refs', vraag.refs.join(','));
    for (const n of vraag.namen) qs.append('name', n);
    for (const b of vraag.bedrijven) qs.append('bedrijf', b);
    // Zelfde afvlakking als in fetchInvoicesAndCreditNotesFromApi: el/ng-contract
    // komt uit de cache soms als object en wordt rechtstreeks gerenderd.
    const plat = (recs) => (Array.isArray(recs) ? recs : []).map(r => {
      const elObj = r.elContract && typeof r.elContract === 'object';
      const ngObj = r.ngContract && typeof r.ngContract === 'object';
      if (!elObj && !ngObj) return r;
      return { ...r, elContract: elObj ? (r.elContract.name || '') : r.elContract, ngContract: ngObj ? (r.ngContract.name || '') : r.ngContract };
    });
    fetch(`/api/invoices/klant?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d || doelGewenst.current !== vraag.sleutel) return;
        // scopeFilter: hetzelfde client-side vangnet als op de volledige lijst.
        setFacturenDoel({ sleutel: vraag.sleutel, facturen: scopeFilter(plat(d.facturen), currentUser), selfbills: scopeFilter(plat(d.selfbills), currentUser) });
      })
      .catch(() => { /* mislukt = wachten op de volledige lijst */ });
  }, [token, facturenLoading, matchedAlles, currentUser]);

  // ── Klantnummers van deze klant, aan- en uitvinkbaar ──────────────────────
  // Eén klant kan meer dan één klantnummer dragen (Beanscene EBVBA heeft er twee).
  // Elk nummer is een eigen dossier in het bronsysteem: eigen orders, eigen EAN's,
  // eigen facturen. Het hele scherm — Samenvatting, Orders, Facturen, Aging,
  // Payment Plans, Debt Collection, Bill Shock, Metering, tickets — volgt de
  // aangevinkte nummers. Minstens één blijft altijd aan.
  //
  // Standaard: kwam de selectie binnen MET een klantnummer (zoeken op het nummer,
  // of rechtsklik op een order-, factuur- of vertrekrij), dan staat enkel dát
  // nummer aan en de andere uit — de gebruiker bekijkt dan het dossier van die rij,
  // niet de hele klant. Zonder nummer (zoeken op naam, EAN, e-mail) staan ze
  // allemaal aan. De keuze hoort bij één selectie: een nieuwe selectie begint
  // opnieuw met de standaard.
  const klantnummers = useMemo(() => {
    if (!matchedAlles) return [];
    const telling = new Map();   // ref → { orders, eans:Set, facturen, naam, naamDatum }
    const tel = (ref) => {
      const r = String(ref || '').trim();
      if (!r) return null;
      if (!telling.has(r)) telling.set(r, { ref: r, orders: 0, eans: new Set(), facturen: 0, naam: '', naamDatum: '' });
      return telling.get(r);
    };
    // De naam achter het nummer: de ondernemingsnaam bij een zakelijke klant
    // (B2B/Custom), de klantnaam bij B2C — uit de meest recente order van dat
    // nummer. Zonder orders (nummer enkel op facturen): de relatienaam daar.
    for (const o of matchedAlles.orders) {
      const t = tel(o.customerReference);
      if (!t) continue;
      t.orders += 1;
      const g = String(o.connectionPointGSRN || '').trim(); if (g) t.eans.add(g);
      const datum = String(o.orderDateTime || o.orderDate || '');
      if (datum >= t.naamDatum) { const n = customerPrimaryName(o); if (n) { t.naam = n; t.naamDatum = datum; } if (o.segment) t.segment = String(o.segment).trim(); }
    }
    for (const f of matchedAlles.facturen) { const t = tel(f.relatieReferentie); if (t) { t.facturen += 1; if (!t.naam && f.relatieNaam) t.naam = String(f.relatieNaam).trim(); } }
    for (const r of matchedAlles.custRefs) tel(r);
    return [...telling.values()].map(({ naamDatum, ...t }) => ({ ...t, eans: t.eans.size })).sort((a, b) => a.ref.localeCompare(b.ref, 'nl', { numeric: true }));
  }, [matchedAlles]);
  const selectieSleutel = selection ? `${selection.type}:${selection.value}:${selection.ref || ''}` : '';
  const [refKeuze, setRefKeuze] = useState({ sleutel: '', refs: null });
  const actieveRefs = useMemo(() => {
    if (klantnummers.length <= 1) return null;                       // niets te kiezen
    const bekend = new Set(klantnummers.map(k => k.ref));
    if (refKeuze.sleutel === selectieSleutel && refKeuze.refs) {
      const gekozen = new Set([...refKeuze.refs].filter(r => bekend.has(r)));
      if (gekozen.size) return gekozen;
    }
    const start = String(selection?.ref || '').trim();
    return start && bekend.has(start) ? new Set([start]) : null;      // null = alle nummers
  }, [klantnummers, refKeuze, selectieSleutel, selection]);
  const isRefActief = (ref) => !actieveRefs || actieveRefs.has(ref);
  const toggleRef = (ref) => {
    const huidig = new Set(actieveRefs || klantnummers.map(k => k.ref));
    if (huidig.has(ref)) { if (huidig.size === 1) return; huidig.delete(ref); } else huidig.add(ref);
    setRefKeuze({ sleutel: selectieSleutel, refs: huidig });
  };

  // Wat de tabs zien: de klant beperkt tot de aangevinkte klantnummers. Records
  // ZONDER klantnummer (oude orders, facturen enkel op naam gekoppeld) blijven
  // staan — die horen bij de klant maar bij geen enkel nummer in het bijzonder.
  const matched = useMemo(() => {
    if (!matchedAlles || !actieveRefs) return matchedAlles;
    const past = (ref) => { const r = String(ref || '').trim(); return !r || actieveRefs.has(r); };
    const mOrders = matchedAlles.orders.filter(o => past(o.customerReference));
    const mFacturen = matchedAlles.facturen.filter(f => past(f.relatieReferentie));
    const gsrns = new Set(mOrders.map(o => (o.connectionPointGSRN || '').trim()).filter(Boolean));
    const customerNames = new Set(mOrders.map(o => c360Norm(o.customer)).filter(Boolean));
    if (selection?.type === 'customer' && c360Norm(selection.value)) customerNames.add(c360Norm(selection.value));
    return {
      ...matchedAlles,
      orders: mOrders,
      facturen: mFacturen,
      selfbills: matchedAlles.selfbills.filter(f => past(f.relatieReferentie)),
      agingItems: matchedAlles.agingItems.filter(f => past(f.relatieReferentie)),
      referralsReceived: matchedAlles.referralsReceived.filter(o => past(o.customerReference)),
      billshocks: matchedAlles.billshocks.filter(b => gsrns.has((b.connectionPointGSRN || '').trim())),
      custRefs: new Set([...matchedAlles.custRefs].filter(r => actieveRefs.has(r))),
      gsrns,
      customerNames,
    };
  }, [matchedAlles, actieveRefs, selection]);

  // Helpdesk-tickets van deze klant: de server leest ze uit dezelfde cache als
  // de Helpdesk, op klantnummer (uniek) + rol-scope. De namen gaan mee voor
  // tickets ZONDER klantnummer (die hangen in Odoo aan een contactpersoon of de
  // onderneming): de geselecteerde naam, de klantnaam op de orders én de
  // bedrijfsnaam — een zakelijke klant heet op het ticket vaak anders dan op de
  // order.
  useEffect(() => {
    if (!token || !hdCanRead || !matched || matched.custRefs.size === 0) { setHdTickets({ items: [], loading: false, error: null }); return; }
    let cancelled = false;
    setHdTickets((p) => ({ ...p, loading: true, error: null }));
    const qs = new URLSearchParams();
    const namen = new Set([
      selection?.type === 'customer' ? selection.value : (matched.orders[0]?.customer || ''),
      ...matched.orders.map((o) => o.customer),
      ...matched.orders.map((o) => o.customerCompanyName),
    ].map((s) => String(s || '').trim()).filter(Boolean));
    for (const n of namen) qs.append('name', n);
    qs.set('refs', [...matched.custRefs].join(','));
    hdApi(token).get(`/customer-tickets?${qs.toString()}`)
      .then((d) => { if (!cancelled) setHdTickets({ items: d.items || [], loading: false, error: null }); })
      .catch((e) => { if (!cancelled) setHdTickets({ items: [], loading: false, error: e.message }); });
    return () => { cancelled = true; };
  }, [token, hdCanRead, matched, selection, hdRefresh]);

  // Open/gesloten afgeleid uit het autoritatieve ET-veld `stage_is_closed`.
  // Customer 360 toont standaard enkel openstaande tickets; de filter laat
  // gesloten of alle tickets toe.
  const hdOpenCount = useMemo(() => hdTickets.items.filter((t) => !t.stage_is_closed).length, [hdTickets.items]);
  const hdClosedCount = hdTickets.items.length - hdOpenCount;
  const hdVisibleTickets = useMemo(() => {
    if (hdStatusFilter === 'open') return hdTickets.items.filter((t) => !t.stage_is_closed);
    if (hdStatusFilter === 'closed') return hdTickets.items.filter((t) => t.stage_is_closed);
    return hdTickets.items;
  }, [hdTickets.items, hdStatusFilter]);

  // Payment plans (V2): één call per unieke customerReference die uit matched.orders
  // komt. Meestal 1 ref per klant, dus 1 call. Resultaten worden gemerged in
  // `paymentPlans.entities`. Scope wordt server-side opnieuw afgedwongen.
  useEffect(() => {
    if (!token || !matched || matched.custRefs.size === 0) {
      setPaymentPlans({ entities: [], loading: false, error: null, errorMessage: null });
      return;
    }
    let cancelled = false;
    setPaymentPlans(p => ({ ...p, loading: true, error: null, errorMessage: null }));
    const refs = [...matched.custRefs];
    Promise.all(refs.map(ref =>
      fetch(`/api/payment-plans/by-reference/${encodeURIComponent(ref)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
        .then(j => ({ ref, entities: j.entities || [], errorMessage: j.errorMessage || null }))
        .catch(e => ({ ref, entities: [], error: e.message }))
    )).then(results => {
      if (cancelled) return;
      const allEntities = [];
      const errs = [];
      const notFound = [];
      for (const r of results) {
        if (r.error) errs.push(`${r.ref}: ${r.error}`);
        else if (r.entities.length === 0 && r.errorMessage) notFound.push(r.ref);
        allEntities.push(...r.entities);
      }
      setPaymentPlans({
        entities: allEntities,
        loading: false,
        error: errs.length ? errs.join('; ') : null,
        errorMessage: notFound.length === refs.length ? 'Geen payment plans gevonden voor deze klant.' : null,
      });
    });
    return () => { cancelled = true; };
  }, [token, matched]);

  // Debt collection: één call voor alle gescopete statussen (backend filtert
  // server-side op user-scope; alleen klanten binnen rechten komen terug).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch('/api/debt-collection/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancelled) setDebtStatus(j.customers || {}); })
      .catch(e => { if (!cancelled) setDebtError(e.message); });
    return () => { cancelled = true; };
  }, [token]);

  // Per geselecteerde klant: log ophalen voor elk customer_reference (meestal 1).
  // Bij wissel van selectie wordt opnieuw gefetcht — log groeit mee na PUT's.
  useEffect(() => {
    if (!token || !matched || matched.custRefs.size === 0) {
      setDebtLogs({});
      return;
    }
    let cancelled = false;
    const refs = [...matched.custRefs];
    Promise.all(refs.map(ref =>
      fetch(`/api/debt-collection/status/${encodeURIComponent(ref)}/log`, { headers: { Authorization: `Bearer ${token}` } })
        .then(async r => { if (!r.ok) return { ref, entries: [] }; return r.json().then(j => ({ ref, entries: j.entries || [] })); })
        .catch(() => ({ ref, entries: [] }))
    )).then(results => {
      if (cancelled) return;
      const map = {};
      for (const { ref, entries } of results) map[ref] = entries;
      setDebtLogs(map);
    });
    return () => { cancelled = true; };
  }, [token, matched]);

  // Optimistic update + PUT voor één veld of de notes. Bij fout revert + foutmelding.
  const updateDebtField = async (customerReference, field, value) => {
    const prev = debtStatus[customerReference] || null;
    const next = { ...(prev || { contact_status: null, customer_response: null, complaint_status: null, notes: '' }), [field]: value || null, updated_at: new Date().toISOString(), updated_by: currentUser ? { id: currentUser.id, name: currentUser.name, email: currentUser.email } : null };
    setDebtStatus(m => ({ ...m, [customerReference]: next }));
    setDebtSaving(customerReference); setDebtError(null);
    try {
      const r = await fetch(`/api/debt-collection/status/${encodeURIComponent(customerReference)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value || null }),
      });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      const updated = await r.json();
      setDebtStatus(m => ({ ...m, [customerReference]: { contact_status: updated.contact_status, customer_response: updated.customer_response, complaint_status: updated.complaint_status, notes: updated.notes || '', updated_at: updated.updated_at, updated_by: updated.updated_by } }));
      // Vernieuw log
      const r2 = await fetch(`/api/debt-collection/status/${encodeURIComponent(customerReference)}/log`, { headers: { Authorization: `Bearer ${token}` } });
      if (r2.ok) { const j = await r2.json(); setDebtLogs(m => ({ ...m, [customerReference]: j.entries || [] })); }
    } catch (e) {
      setDebtStatus(m => ({ ...m, [customerReference]: prev || undefined }));
      setDebtError(e.message);
      setTimeout(() => setDebtError(null), 4000);
    } finally {
      setDebtSaving(null);
    }
  };

  // KPI's voor de header
  const summary = useMemo(() => {
    if (!matched) return null;
    const isSale = f => (f.soort || '').toLowerCase() === 'verkoopfactuur';
    // Totaal BETAALDE verkoopfacturen (vervangt 'omzet').
    const betaaldFacturen = matched.facturen
      .filter(f => isSale(f) && (f.statusBetaling || '').toLowerCase() === 'betaald')
      .reduce((s, f) => s + (Number(f.totaal) || 0), 0);
    // Openstaand = wat er per factuur écht nog rest (API-restbedrag, incl. btw),
    // niet het volledige factuurbedrag — anders telt een deels betaalde factuur
    // voor haar hele bedrag mee en spreekt de tegel de kolom "Openstaand (incl.
    // BTW)" in de factuurtabel eronder tegen. Is het restbedrag nog niet
    // opgehaald (invOutstanding null), dan blijft het volledige bedrag staan.
    const openstaand = matched.facturen
      .filter(f => {
        const status = (f.statusBetaling || '').toLowerCase();
        return status !== 'betaald' && status !== 'omgekeerd' && isSale(f);
      })
      .reduce((s, f) => s + (invOutstanding(f) ?? Number(f.totaal) ?? 0), 0);

    // Contractduur: oudste (effective start, anders delivery start) van alle orders
    // → vandaag, in maanden, afgerond op 1 maand.
    const startTimes = matched.orders
      .map(o => o.effectiveStartDate || o.deliveryStartDate)
      .filter(Boolean).map(d => new Date(d).getTime()).filter(t => !isNaN(t));
    // De oudste startdatum zelf tonen we ook: die zegt in één oogopslag hoe lang
    // deze klant al loopt, waar "23 maanden" nog omgerekend moet worden.
    const startDate = startTimes.length ? new Date(Math.min(...startTimes)) : null;
    let contractMonths = null;
    if (startTimes.length) {
      const oldest = new Date(Math.min(...startTimes));
      const today = new Date();
      const months = (today.getFullYear() - oldest.getFullYear()) * 12
        + (today.getMonth() - oldest.getMonth())
        + (today.getDate() - oldest.getDate()) / 30;
      contractMonths = Math.max(0, Math.round(months));
    }

    // Eind Datum: oudste effective end date als die bestaan, anders oudste delivery
    // end date van alle orders van de klant.
    const effEnds = matched.orders.map(o => o.effectiveEndDate).filter(Boolean);
    const delEnds = matched.orders.map(o => o.deliveryEndDate).filter(Boolean);
    const endTimes = (effEnds.length ? effEnds : delEnds)
      .map(d => new Date(d).getTime()).filter(t => !isNaN(t));
    const endDate = endTimes.length ? new Date(Math.min(...endTimes)) : null;

    // Gecontracteerde (geschatte) volumes uit de est_kwh van ALLE orders van de klant.
    // Afname (offtake) apart per markt (EL vs Gas/NG); injectie is enkel elektriciteit
    // maar we sommeren over alle orders (gas-injectie is sowieso 0).
    const contractedOfftakeEl = matched.orders
      .filter(o => (o.market || '').toUpperCase() === 'EL')
      .reduce((s, o) => s + (Number(o.estOfftakeKwh) || 0), 0);
    const contractedOfftakeGas = matched.orders
      .filter(o => (o.market || '').toUpperCase() === 'NG')
      .reduce((s, o) => s + (Number(o.estOfftakeKwh) || 0), 0);
    const contractedInjection = matched.orders
      .reduce((s, o) => s + (Number(o.estInjectionKwh) || 0), 0);

    // Verkoper(s): unieke verkopers uit de orders (OdooBot → Online).
    const sellers = [...new Set(matched.orders
      .map(o => (o.salesperson === 'OdooBot' ? 'Online' : (o.salesperson || '').trim()))
      .filter(Boolean))];

    // Actief: heeft minstens één order met CP-status owned/in/int_in (zelfde lijst als
    // "Actieve aansluitingen"); anders External.
    const ACTIVE_CP = new Set(['owned', 'in', 'int_in']);
    const activeOrders = matched.orders.filter(o => ACTIVE_CP.has((o.connectionPointStatus || '').toLowerCase()));
    const isActive = activeOrders.length > 0;

    // Meest recente actieve ratecard(s) per markt (EL/NG) + bijhorende tariefkaart
    // (prijsformule) en price addition.
    const activeSorted = [...activeOrders].sort((a, b) =>
      String(b.orderDateTime || b.orderDate || '').localeCompare(String(a.orderDateTime || a.orderDate || '')));
    const seenMarket = new Set();
    const activeRatecards = [];
    for (const o of activeSorted) {
      const mkt = (o.market || '').toUpperCase();
      if (!mkt || seenMarket.has(mkt)) continue;
      seenMarket.add(mkt);
      const add = mkt === 'NG' ? o.priceAdditionsNG : o.priceAdditionsEL;
      activeRatecards.push({
        market: mkt,
        ratecard: o.ratecardName || '—',
        tariffCard: o.tariffCard || '',
        priceAddition: (add === '' || add == null) ? null : add,
        unit: o.priceAdditionsUnit || '',
        date: o.orderDate || '',
      });
    }

    return {
      orders: matched.orders.length,
      sellers,
      isActive,
      activeRatecards,
      gsrns: matched.gsrns.size,
      facturen: matched.facturen.length,
      contractedOfftakeEl,
      contractedOfftakeGas,
      contractedInjection,
      betaaldFacturen,
      openstaand,
      aging: matched.agingItems.length,
      referralsTotal: matched.referralsReceived.length + matched.referralsSent.length,
      billshocks: matched.billshocks.length,
      startDate,
      contractMonths,
      endDate,
    };
  }, [matched]);

  // Alle LOSSE customer tags van deze klant die de gebruiker mag zien, ontdubbeld
  // over zijn orders heen.
  const partnerTags = useMemo(() => {
    if (!matched) return [];
    return [...new Set(matched.orders.flatMap(o => zichtbareTags(o.partnerTags, visiblePartnerTags)))];
  }, [matched, visiblePartnerTags]);

  // Klant-masterdata voor het blok bovenaan: samengesteld uit de order-velden van
  // de geselecteerde klant (naam, bedrijf, BTW, e-mail, telefoon, bankrekening,
  // segment, aansluitingsadres(sen)) + de Odoo/Lynx-deeplinks. Die deep-link-velden
  // worden server-side ENKEL aan admin/et_dossier meegegeven (stripOrderUrls); bij
  // andere rollen zijn ze afwezig → hier automatisch verborgen. We nemen per veld de
  // meest recente niet-lege waarde (orders nieuw → oud).
  const customerInfo = useMemo(() => {
    if (!matched || matched.orders.length === 0) return null;
    const sorted = [...matched.orders].sort((a, b) =>
      String(b.orderDateTime || '').localeCompare(String(a.orderDateTime || '')));
    const pick = (field) => {
      for (const o of sorted) { const v = String(o[field] ?? '').trim(); if (v) return v; }
      return '';
    };
    return {
      name: pick('customer'),
      companyName: pick('customerCompanyName'),
      vat: pick('customerVat'),
      email: pick('customerEmail'),
      phone: pick('customerPhone'),
      bankAccount: pick('customerBankAccount'),
      segment: pick('segment'),
      lynxRelationUrl: pick('lynxRelationUrl'),
      odooCustomerUrl: pick('odooCustomerUrl'),
    };
  }, [matched]);
  // Koppelingen naar de backend-systemen PER KLANTNUMMER (08-10-2026): een klant
  // met twee nummers heeft in Odoo én in Lynx twee records, en een link die
  // "de recentste order" volgde, opende bij het andere nummer de verkeerde.
  // Odoo: uit de recentste order van dat nummer. Lynx: via de eigen opzoeking op
  // klantnummer (/api/lynx/relatie) — de link uit de API wijst soms naar de
  // contactpersoon in plaats van de relatie — met de order-URL als terugval
  // zolang het antwoord er niet is of Lynx het nummer niet kent.
  const [lynxRelaties, setLynxRelaties] = useState({});   // ref → url, of null = niet in Lynx
  const koppelingen = useMemo(() => {
    if (!matched || !showBrand) return [];
    const refs = actieveRefs ? [...actieveRefs] : klantnummers.map((k) => k.ref);
    const perRef = refs.map((ref) => {
      const o = matched.orders.filter((x) => String(x.customerReference || '').trim() === ref)
        .sort((a, b) => String(b.orderDateTime || '').localeCompare(String(a.orderDateTime || '')))[0];
      const lynx = lynxRelaties[ref] || (o && o.lynxRelationUrl) || '';
      return { ref, lynx, odoo: (o && o.odooCustomerUrl) || '' };
    }).filter((k) => k.lynx || k.odoo);
    // Zonder klantnummer (oude orders enkel op naam): de recentste order.
    if (!perRef.length && customerInfo && (customerInfo.lynxRelationUrl || customerInfo.odooCustomerUrl)) return [{ ref: '', lynx: customerInfo.lynxRelationUrl, odoo: customerInfo.odooCustomerUrl }];
    return perRef;
  }, [matched, showBrand, actieveRefs, klantnummers, lynxRelaties, customerInfo]);
  useEffect(() => {
    if (!token || !showBrand) return undefined;
    const refs = (actieveRefs ? [...actieveRefs] : klantnummers.map((k) => k.ref)).filter((r) => r && lynxRelaties[r] === undefined);
    if (!refs.length) return undefined;
    let actief = true;
    Promise.all(refs.map((ref) => fetch(`/api/lynx/relatie?ref=${encodeURIComponent(ref)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null)).then((j) => [ref, j ? (j.url || null) : undefined]).catch(() => [ref, undefined])))
      .then((paren) => { if (!actief) return; setLynxRelaties((cur) => { const n = { ...cur }; for (const [ref, url] of paren) if (url !== undefined) n[ref] = url; return n; }); });
    return () => { actief = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showBrand, actieveRefs, klantnummers]);
  // Segmenten van de aangevinkte klantnummers (B2C, B2B, Custom) — één badge per
  // segment, want een klant kan uitzonderlijk een B2C- én een B2B-nummer hebben.
  // Zonder nummers: het segment van de recentste order.
  const segmentenActief = useMemo(() => {
    const uit = [...new Set(klantnummers.filter((k) => !actieveRefs || actieveRefs.has(k.ref)).map((k) => (k.segment || '').toLowerCase()).filter(Boolean))];
    if (uit.length) return uit;
    return customerInfo?.segment ? [String(customerInfo.segment).toLowerCase()] : [];
  }, [klantnummers, actieveRefs, customerInfo]);

  // Weergavenaam voor het profiel: bij zakelijke klanten (B2B/Custom) de
  // ondernemingsnaam (gekoppeld aan het klantnummer) i.p.v. de contactpersoon.
  // Gemeten volumes uit de bill shock-resultaten van deze klant: de som over al
  // haar aansluitingen. Bill shock rekent per EAN met de werkelijk verbruikte en
  // geïnjecteerde kWh, dus dit is het beste "echt verbruik" dat we hier hebben.
  // Naast het gecontracteerde (geschatte) volume laat dat zien of de inschatting
  // klopt — daarom ook het afwijkingspercentage.
  const c360Volumes = useMemo(() => {
    const bs = (matched?.billshocks) || [];
    // Splits de gemeten afname op markt, zodat elektriciteit en gas naast hun eigen
    // gecontracteerde volume staan — die twee optellen zegt niets, het zijn andere
    // energiedragers met andere ordegroottes. bsMarket() is dezelfde helper als de
    // Bill Shock-pagina gebruikt (market, anders projectConnectionPoint.market).
    // Geen meting voor een markt → 0, niet leeg: nul is hier een antwoord.
    const somPerMarkt = (veld, markt) => bs
      .filter(b => bsMarket(b).toUpperCase() === markt)
      .reduce((s, b) => s + (Number(b[veld]) || 0), 0);
    const gemetenOfftakeEl = somPerMarkt('offtakeQuantityKWh', 'EL');
    const gemetenOfftakeGas = somPerMarkt('offtakeQuantityKWh', 'NG');
    // Injectie is elektriciteit; we sommeren over alles, want gas-injectie bestaat
    // niet en telt dus sowieso als nul mee.
    const gemetenInjectie = bs.reduce((s, b) => s + (Number(b.injectionQuantityKWh) || 0), 0);
    // Afwijking t.o.v. het gecontracteerde volume, per markt. Zonder contractvolume
    // is er niets om tegen af te zetten; dan tonen we geen percentage in plaats van
    // een deling door nul te verbergen achter een 0%.
    const afwijking = (gemeten, contract) => (contract > 0 ? (gemeten - contract) / contract : null);
    const contractOfftakeEl = summary?.contractedOfftakeEl || 0;
    const contractOfftakeGas = summary?.contractedOfftakeGas || 0;
    const contractInjectie = summary?.contractedInjection || 0;
    return {
      aantalEan: new Set(bs.map(b => b.connectionPointGSRN).filter(Boolean)).size,
      gemetenOfftakeEl,
      gemetenOfftakeGas,
      gemetenInjectie,
      contractOfftakeEl,
      contractOfftakeGas,
      contractInjectie,
      afwOfftakeEl: afwijking(gemetenOfftakeEl, contractOfftakeEl),
      afwOfftakeGas: afwijking(gemetenOfftakeGas, contractOfftakeGas),
      afwInjectie: afwijking(gemetenInjectie, contractInjectie),
      heeftBillshock: bs.length > 0,
    };
  }, [matched, summary]);

  // Aansluitingen met hun kenmerken, voor het compacte overzicht in de
  // Samenvatting. Eén regel per EAN (niet per order): een verlenging op dezelfde
  // aansluiting is geen tweede aansluiting. De meest recente order wint, zodat de
  // CP-status en het metertype de huidige situatie tonen.
  const c360Aansluitingen = useMemo(() => {
    if (!matched) return [];
    const perGsrn = new Map();
    const gesorteerd = [...matched.orders].sort((a, b) =>
      String(b.orderDateTime || b.orderDate || '').localeCompare(String(a.orderDateTime || a.orderDate || '')));
    for (const o of gesorteerd) {
      const gsrn = (o.connectionPointGSRN || '').trim();
      const adres = (o.connectionPointAddress || '').trim();
      const sleutel = gsrn || adres;
      if (!sleutel || perGsrn.has(sleutel)) continue;
      perGsrn.set(sleutel, {
        gsrn,
        adres,
        markt: (o.market || '').toUpperCase(),
        meterMethod: o.meterMethod || '',
        connectionType: o.connectionType || '',
        cpStatus: o.connectionPointStatus || '',
      });
    }
    return [...perGsrn.values()].sort((a, b) => a.adres.localeCompare(b.adres));
  }, [matched]);

  const infoAsOrder = customerInfo ? { segment: customerInfo.segment, customerCompanyName: customerInfo.companyName, customer: customerInfo.name } : {};
  const isZakelijkeKlant = isBusiness(infoAsOrder);
  // Voorvulling voor de ingebedde Metering-pagina. Diezelfde vorm gebruikt de
  // rechtsklik "Openen in Metering": type 'customer' met de klantnaam, of 'gsrn'
  // wanneer er op één aansluiting gezocht is. De sleutel verandert bij elke nieuwe
  // selectie, want MeteringPage past een prefill maar één keer per sleutel toe.
  // Ouderdomsanalyse voor deze klant — exact dezelfde emmers en berekening als de
  // pagina Aging (berekenAgingBuckets), maar over de facturen van deze klant.
  // Basis is dus ook identiek: alles wat niet betaald of omgekeerd is en waarop nog
  // een bedrag openstaat, inclusief het nog niet vervallen deel ("< 0 dagen"). Dat
  // is ruimer dan de detailtabel eronder, die enkel de vervállen facturen toont.
  const c360AgingBuckets = useMemo(() => {
    if (!matched) return [];
    const open = matched.facturen
      .filter(f => {
        const s = (f.statusBetaling || '').toLowerCase();
        return s !== 'betaald' && s !== 'omgekeerd';
      })
      .map(f => {
        const daysOverdue = getDaysOverdue(f.vervaldatum || '');
        return { ...f, daysOverdue, agingCategory: getAgingCategory(daysOverdue), totaal: f.totaal };
      })
      .filter(f => invOutstandingOrTotal(f) !== 0);
    return berekenAgingBuckets(open);
  }, [matched]);

  const meteringPrefill = useMemo(() => {
    if (!selection) return null;
    // Het klantnummer gaat mee naar de Metering-tab: die selecteert per ref (twee
    // klanten kunnen dezelfde naam dragen), en zonder ref viel een zakelijke klant
    // daar op dezelfde manier uit de boot als hier.
    // Meerdere klantnummers aangevinkt → `refs`: dan toont Metering de EAN's van
    // ál die nummers (anders enkel die van `ref`).
    const refs = actieveRefs ? [...actieveRefs] : klantnummers.map(k => k.ref);
    return selection.type === 'gsrn'
      ? { type: 'gsrn', value: selection.value }
      : {
          type: 'customer',
          value: matched?.orders?.[0]?.customer || selection.value,
          ref: refs[0] || matched?.orders?.[0]?.customerReference || selection.ref || '',
          refs: refs.length > 1 ? refs : undefined,
        };
  }, [selection, matched, actieveRefs, klantnummers]);
  // De sleutel draagt de aangevinkte klantnummers mee: elke wijziging van de
  // vinkjes is voor Metering een nieuwe voorvulling.
  const meteringPrefillKey = useMemo(
    () => (selection ? `c360:${selection.type}:${selection.value}:${actieveRefs ? [...actieveRefs].join(',') : '*'}` : 0),
    [selection, actieveRefs]
  );

  const profileName = customerPrimaryName(infoAsOrder) || (selection ? (selection.type === 'gsrn' ? selection.value : selection.label) : '');

  // ── Waar wacht dit dossier op? ────────────────────────────────────────────
  // Belangrijk in een deep-link-tab (rechtsklik → "Toon in Customer 360"): daar is
  // GEEN welkomstscherm meer, dus moet dit scherm zelf zeggen dat het bezig is.
  // Zonder dat zie je een zoekveld met een lege pagina eronder terwijl de app de
  // klant aan het ophalen is.
  // De orders zijn de ruggengraat: zonder match valt er niets te tonen, dus zolang
  // ze onderweg zijn én er nog niets gematcht is, één melding midden op het scherm —
  // dezelfde vorm als op Metering.
  const wachtOpKlant = !!selection && ordersLoading && !(matched && matched.orders.length);
  // Facturen laden dáárna door en vullen maar een deel van het dossier (Aging &
  // Facturen, de factuur-KPI's). Dat blokkeert niets, dus daar volstaat een klein
  // laadteken bij de tabs — en enkel zolang ook het snelle pad (de facturen van
  // déze klant, zie facturenDoel) nog niets opgeleverd heeft.
  const doelFacturenKlaar = !!matchedAlles && !!facturenDoel.sleutel && facturenDoel.sleutel === matchedAlles.doelVraag.sleutel;
  const wachtOpFacturen = !!selection && !!matched && facturenLoading && !doelFacturenKlaar;

  return (
    <div className="space-y-4">
      {wachtOpKlant && (
        // pointer-events-none: puur een melding, ze mag geen klik tegenhouden —
        // zoeken op een andere klant blijft dus mogelijk terwijl dit staat.
        <div className="fixed inset-0 z-[8000] flex items-center justify-center bg-black/20 backdrop-blur-[1px] pointer-events-none">
          <div className="flex flex-col items-center gap-3 px-7 py-6 rounded-2xl bg-slate-800 border border-slate-600 shadow-2xl max-w-[19rem] text-center">
            <Spinner size={34} />
            <div className="text-sm font-semibold text-slate-100">Klantgegevens ophalen…</div>
            <div className="text-xs text-slate-400">De orders van deze klant worden opgehaald.</div>
          </div>
        </div>
      )}
      <PageHeader title="Customer 360°" subtitle={<span className="hidden sm:inline">Zoek een klant of EAN en bekijk alle gekoppelde data binnen je rechten</span>}>
        {selection && <button onClick={clearSelection} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded"><X size={12} />Wissen</button>}
      </PageHeader>

      {/* Search — hetzelfde veld als op Orders, Invoices en Metering. */}
      <Card className="!p-4 relative">
        {/* Op een telefoon een kort label: de volledige opsomming vulde daar drie regels
            en zegt niets meer dan de placeholder eronder al doet. */}
        <KlantZoekVeld
          label={<><span className="sm:hidden">Zoek een klant of EAN</span><span className="hidden sm:inline">Zoek op klantnaam, klantnummer, EAN, factuurnummer, BTW-nummer, e-mail of telefoon</span></>}
          placeholder="Bijv. Jan Janssens, klantnummer, EAN, factuurnummer, BTW BE0123…, e-mail of telefoon"
          value={query}
          onChange={(v) => { setQuery(v); if (selection) setSelection(null); }}
          suggesties={suggesties}
          onKlant={(k) => selectCustomer(k.naam, k.display, refUitZoekopdracht(k))}
          onGsrn={(g) => selectGsrn(g.gsrn, g.klant)}
        />
      </Card>

      {/* Horizontaal tab-menu binnen Customer 360 (zelfde patroon als Sales/Revenue).
          Alleen zichtbaar zodra er een klant gekozen is; zonder selectie is er niets
          te verdelen. De niet-actieve tabs blijven met `hidden` in de DOM staan —
          zo houden Payment Plans, Bill Shock en Metering hun opgehaalde data en
          kost heen-en-weer schakelen geen nieuwe API-aanroepen. Welke tabs er
          staan, hangt af van de toegekende subpagina's. */}
      {/* Facturen zijn er nog niet: zeg dat, in plaats van lege factuurtabellen te
          tonen alsof deze klant er geen heeft. */}
      {wachtOpFacturen && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Spinner size={13} />Facturen laden… de factuurtabellen en -kengetallen vullen zich zodra ze binnen zijn.
        </div>
      )}
      {selection && matched && geenC360Tabs && <GeenSubpaginas module="Customer 360°" />}
      {selection && matched && (
        <div className="-mx-1 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 px-1 w-max">
            {c360Tabs.map(t => (
              <button key={t.id} onClick={() => setC360Tab(t.id)}
                className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${c360Tab === t.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}>
                <span className="sm:hidden">{t.kort || t.label}</span><span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {selection && matched && (
        <>
          <div className={toonC360Tab('samenvatting') ? "space-y-4" : "hidden"}>
          {/* ── Klantkop ───────────────────────────────────────────────────
              Eén kaart met drie vakken naast elkaar: wie het is, hoe lang hij
              klant is, en de tellingen. Het vroegere vaste zijpaneel is hier in
              opgegaan: dat maakte de pagina links smal en liet de rechterkolom
              met de hoeveelheid gegevens meebewegen, waardoor de blokken nooit
              netjes uitlijnden. Nu ligt alles op volle breedte in vaste rijen,
              en blijft de indeling dus dezelfde bij één order of bij honderd. */}
          {/* Niet tonen zolang de orders nog onderweg zijn (`wachtOpKlant`): "geen
              orders" is dan geen uitkomst maar een tussenstand. */}
          {matched.orders.length === 0 && !wachtOpKlant && (
            <div className="text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded px-3 py-1.5">Geen orders binnen je rechten voor deze selectie</div>
          )}
          <Card className="!p-0 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1.1fr)] divide-y lg:divide-y-0 lg:divide-x divide-slate-700/40">
              {/* Identiteit */}
              <div className="p-4 min-w-0">
                {/* Naam links, verkoper rechtsboven: zo heeft de rij met de
                    klantnummers eronder de volle breedte voor nummer, naam en
                    telling — die stonden vroeger in twee kolommen naast de
                    verkoper en werden afgekapt. */}
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="flex items-start gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-lime-500/15 border border-lime-500/40 text-lime-300 flex items-center justify-center font-bold shrink-0">{nameInitials(profileName)}</div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-slate-100 break-words leading-tight">{profileName}</h2>
                    {isBusiness(infoAsOrder) && customerInfo?.name && customerInfo.name !== customerInfo.companyName && (
                      <div className="text-xs text-slate-500 truncate">{customerInfo.name}</div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {/* Eén badge per segment van de AANGEVINKTE klantnummers: een klant
                          kan uitzonderlijk een B2C- én een B2B-nummer hebben. */}
                      {segmentenActief.map((s) => <SegmentBadge key={s} o={{ segment: s }} />)}
                      {summary && (summary.isActive
                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-500/15 border border-green-500/40 text-green-300 text-xs font-medium">Actief</span>
                        : <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-600/30 border border-slate-500/50 text-slate-300 text-xs font-medium">External</span>)}
                      {partnerTags.map(t => <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">{t}</span>)}
                    </div>
                  </div>
                  </div>
                  <div className="shrink-0 text-right max-w-[45%]">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Verkoper</div>
                    <div className="text-sm text-slate-200 break-words">{summary && summary.sellers.length ? summary.sellers.join(', ') : '—'}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{klantnummers.length > 1 ? `Klantnummers (${klantnummers.length})` : 'Klantnummer'}</div>
                    {klantnummers.length > 1 ? (
                      // Meer dan één klantnummer: vinkjes. Het dossier volgt de aangevinkte
                      // nummers (zie klantnummers/actieveRefs); de telling erachter zegt wat
                      // elk nummer inbrengt, zodat je ziet wat je uitzet.
                      <div className="flex flex-col gap-0.5">
                        {klantnummers.map(k => {
                          const aan = isRefActief(k.ref);
                          const laatste = aan && (actieveRefs ? actieveRefs.size : klantnummers.length) === 1;
                          return (
                            <label key={k.ref} className={`inline-flex items-center gap-2 text-sm font-mono font-semibold break-all cursor-pointer ${aan ? 'text-slate-200' : 'text-slate-500'}`}
                              title={laatste ? 'Minstens één klantnummer blijft aangevinkt' : (aan ? 'Uitvinken: dit klantnummer uit het dossier laten' : 'Aanvinken: dit klantnummer erbij nemen')}>
                              <input type="checkbox" checked={aan} disabled={laatste} onChange={() => toggleRef(k.ref)} className="accent-lime-500 shrink-0" />
                              <span className="inline-flex items-center">{k.ref}<KlantnrKopieerKnop nr={k.ref} /></span>
                              {k.naam && <span className="text-[11px] font-sans font-normal text-slate-400 truncate max-w-[420px]" title={k.naam}>{k.naam}</span>}
                              <span className="text-[11px] font-sans font-normal text-slate-500 whitespace-nowrap">{k.eans} EAN{k.eans === 1 ? '' : "'s"} · {k.facturen} factu{k.facturen === 1 ? 'ur' : 'ren'}</span>
                            </label>
                          );
                        })}
                        <div className="text-[11px] text-slate-500 mt-0.5">Het dossier toont enkel de aangevinkte klantnummers.</div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                        {[...matched.custRefs].length
                          ? [...matched.custRefs].map(ref => <span key={ref} className="text-slate-200 text-sm font-mono font-semibold break-all inline-flex items-center">{ref}<KlantnrKopieerKnop nr={ref} /></span>)
                          : <span className="text-slate-500 text-sm">—</span>}
                      </div>
                    )}
                    {selection.type === 'gsrn' && <div className="text-[11px] text-slate-500 font-mono break-all">{selection.value}<EanKopieerKnop ean={selection.value} /></div>}
                  </div>
                </div>
                {koppelingen.length > 0 && (
                  // Eén regel per aangevinkt klantnummer: elk nummer is in Odoo en Lynx
                  // een eigen record. Bij één nummer blijft het nummer weg.
                  <div className="mt-3 flex flex-col gap-y-1 text-xs">
                    {koppelingen.map((k) => (
                      <div key={k.ref || '*'} className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        {koppelingen.length > 1 && k.ref && <span className="font-mono text-slate-400">{k.ref}</span>}
                        {k.lynx && <a href={k.lynx} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300" title={k.ref ? `Relatie ${k.ref} in Lynx` : 'Relatie in Lynx'}>Lynx relatie<ExternalLink size={11} /></a>}
                        {k.odoo && <a href={k.odoo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300" title={k.ref ? `Klant ${k.ref} in Odoo` : 'Klant in Odoo'}>Odoo klant<ExternalLink size={11} /></a>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Looptijd */}
              <div className="p-4 flex flex-col justify-center min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Klant sinds</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <CalendarDays size={18} className="text-slate-500 shrink-0" />
                  <span className="min-w-0 text-xl lg:text-2xl font-bold text-slate-100"><Kengetal>{summary && summary.startDate ? summary.startDate.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</Kengetal></span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {summary && summary.contractMonths != null ? `${summary.contractMonths} maanden` : 'geen startdatum bekend'}
                  {summary && summary.endDate ? ` · eindigt ${summary.endDate.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : ''}
                </div>
              </div>

              {/* Tellingen — vast raster van 2×2, dus even hoog bij elke klant. */}
              <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3 content-center">
                {[
                  { label: 'Orders', waarde: summary?.orders, icon: ClipboardList },
                  { label: 'Facturen', waarde: summary?.facturen, icon: Receipt },
                  { label: 'GSRNs', waarde: summary?.gsrns, icon: Gauge },
                  { label: 'Referrals', waarde: summary?.referralsTotal, icon: Share2 },
                ].map(({ label, waarde, icon: Icon }) => (
                  <div key={label} className="flex items-center justify-between gap-2 min-w-0">
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500 min-w-0"><Icon size={13} className="text-slate-500 shrink-0" /><span className="truncate">{label}</span></span>
                    <span className="text-base font-bold text-slate-100 tabular-nums shrink-0">{formatNumberRounded(waarde || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* ── Kerncijfers ────────────────────────────────────────────────
              Eén strook over de volle breedte: gemeten volume, wat er
              gecontracteerd is en wat er betaald/open staat. Vaste kolommen,
              dus de strook houdt zijn hoogte ook zonder bill shock-resultaten. */}
          {summary && (
            <Card className="!p-0 overflow-hidden">
              <div className="px-4 pt-3 text-[10px] uppercase tracking-wider text-slate-500">
                Gemeten volume (kWh){c360Volumes.heeftBillshock ? ` · ${c360Volumes.aantalEan} EAN uit bill shock` : ''}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-y-3 divide-slate-700/40 sm:divide-x px-1 py-3">
                {[
                  { label: 'Offtake EL', waarde: c360Volumes.gemetenOfftakeEl, afw: c360Volumes.afwOfftakeEl, icon: Zap, kleur: 'text-lime-400' },
                  { label: 'Offtake gas', waarde: c360Volumes.gemetenOfftakeGas, afw: c360Volumes.afwOfftakeGas, icon: Flame, kleur: 'text-orange-400' },
                  { label: 'Injectie', waarde: c360Volumes.gemetenInjectie, afw: c360Volumes.afwInjectie, icon: Sun, kleur: 'text-sky-400' },
                ].map(({ label, waarde, afw, icon: Icon, kleur }) => (
                  <div key={label} className="px-3 min-w-0">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500"><Icon size={13} className={`${kleur} shrink-0`} /><span className="truncate">{label}</span></div>
                    <div className="text-lg font-bold text-slate-100"><Kengetal>{c360Volumes.heeftBillshock ? formatNumberRounded(waarde) : '—'}</Kengetal></div>
                    {c360Volumes.heeftBillshock && afw != null && (
                      <div className={`text-[10px] ${afw >= 0 ? 'text-amber-300' : 'text-sky-300'}`}>{afw >= 0 ? '+' : ''}{(afw * 100).toFixed(1)}% t.o.v. gecontracteerd</div>
                    )}
                  </div>
                ))}
                {[
                  { label: 'Contract EL', waarde: c360Volumes.contractOfftakeEl },
                  { label: 'Contract gas', waarde: c360Volumes.contractOfftakeGas },
                  { label: 'Contract inj.', waarde: c360Volumes.contractInjectie },
                ].map(({ label, waarde }) => (
                  <div key={label} className="px-3 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{label}</div>
                    <div className="text-lg font-semibold text-slate-300"><Kengetal>{formatNumberRounded(waarde)}</Kengetal></div>
                  </div>
                ))}
                <div className="px-3 min-w-0">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500"><Coins size={13} className="text-lime-400 shrink-0" /><span className="truncate">Totaal betaald</span></div>
                  <div className="text-lg font-bold text-lime-400"><Kengetal>{formatEuroRounded(summary.betaaldFacturen)}</Kengetal></div>
                </div>
                <div className="px-3 min-w-0">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500"><Wallet size={13} className="text-slate-400 shrink-0" /><span className="truncate">Openstaand</span></div>
                  <div className={`text-lg font-bold ${summary.openstaand > 0 ? 'text-amber-300' : 'text-slate-400'}`}><Kengetal>{formatEuroRounded(summary.openstaand)}</Kengetal></div>
                </div>
              </div>
            </Card>
          )}

          {/* ── Gegevens + aansluitingen ───────────────────────────────────
              Twee kaarten naast elkaar die ALTIJD even hoog zijn (`items-stretch`
              + `h-full`): links de vaste klantgegevens, rechts een lijst die met
              het aantal aansluitingen meegroeit. Zonder dat sprong de linkerkaart
              bij één aansluiting tot halverwege de rechterkaart. */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
            <Card className="lg:col-span-1 h-full flex flex-col !py-4">
              {/* Bij een particulier (B2C) heeft "Bedrijfsgegevens" geen betekenis:
                  bedrijfsnaam en BTW-nummer zijn dan leeg. De kaart heet daar
                  Klantgegevens en toont enkel wat wél bestaat. */}
              <h3 className="text-sm font-semibold text-slate-300 mb-3">{isZakelijkeKlant ? 'Bedrijfs- en contactgegevens' : 'Klant- en contactgegevens'}</h3>
              <div className="space-y-2 text-sm">
                {isZakelijkeKlant && <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">Bedrijf</span><span className="text-slate-200 text-right break-words">{customerInfo?.companyName || '—'}</span></div>}
                {isZakelijkeKlant && <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">BTW-nummer</span><span className="text-slate-200 text-right font-mono">{customerInfo?.vat || '—'}</span></div>}
                <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">Telefoon</span><span className="text-slate-200 text-right">{customerInfo?.phone ? <a href={`tel:${customerInfo.phone}`} className="hover:text-lime-400">{customerInfo.phone}</a> : '—'}</span></div>
                <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">E-mail</span><span className="text-slate-200 text-right break-all">{customerInfo?.email ? <a href={`mailto:${customerInfo.email}`} className="hover:text-lime-400">{customerInfo.email}</a> : '—'}</span></div>
                <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">Bankrekening</span><span className="text-slate-200 text-right font-mono break-all">{customerInfo?.bankAccount || '—'}</span></div>
              </div>
              {summary && summary.activeRatecards.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700/50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Actieve ratecards</div>
                  <div className="space-y-1.5">
                    {summary.activeRatecards.map(rc => (
                      <div key={rc.market} className="flex items-center gap-2 text-xs min-w-0">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border shrink-0 ${rc.market === 'NG' ? 'bg-orange-500/15 text-orange-300 border-orange-500/40' : 'bg-lime-500/15 text-lime-300 border-lime-500/40'}`}>{rc.market}</span>
                        <span className="text-slate-200 font-medium truncate" title={[rc.ratecard, rc.tariffCard].filter(Boolean).join(' · ')}>{rc.ratecard}</span>
                        {rc.priceAddition != null && <span className="ml-auto text-slate-400 shrink-0" title={rc.unit ? priceAdditionUnitTitle(rc.unit) : undefined}>+{rc.priceAddition}{rc.unit ? ` (${rc.unit})` : ''}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card className="lg:col-span-2 h-full flex flex-col !py-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-1.5">Aansluitingen ({c360Aansluitingen.length})</h3>
              <C360Aansluitingen items={c360Aansluitingen} />
            </Card>
          </div>

          {/* Helpdesk-tickets van deze klant (op uniek klantnummer) — meteen onder
              het klant/EAN-blok. Toont standaard enkel openstaande tickets. */}
          {hdCanRead && (
            <Card>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-lime-400 uppercase flex items-center gap-2"><LifeBuoy size={16} />Helpdesk tickets ({hdVisibleTickets.length})</h3>
                <div className="flex items-center gap-3 text-xs flex-wrap">
                  {hdTickets.loading && <span className="text-slate-500">Laden…</span>}
                  {hdTickets.error && <span className="text-red-400">{hdTickets.error}</span>}
                  <div className="flex bg-slate-800 rounded-lg p-0.5">
                    {[{ v: 'open', l: `Openstaand (${hdOpenCount})` }, { v: 'closed', l: `Gesloten (${hdClosedCount})` }, { v: 'all', l: `Alle (${hdTickets.items.length})` }].map((o) => (
                      <button key={o.v} onClick={() => setHdStatusFilter(o.v)} className={`px-2.5 py-1 rounded-md transition-all ${hdStatusFilter === o.v ? 'bg-lime-500 text-slate-900 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}>{o.l}</button>
                    ))}
                  </div>
                  {!hdTickets.loading && hdVisibleTickets.length > 0 && <span className="text-slate-500 hidden lg:inline">Klik op een rij voor het volledige ticket</span>}
                  {hdCaps.has('create') && <button onClick={() => setHdShowCreate(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-xs"><Plus size={14} />Nieuw ticket</button>}
                </div>
              </div>
              {!hdTickets.loading && hdVisibleTickets.length === 0 ? (
                <p className="text-sm text-slate-500">{hdTickets.items.length === 0 ? 'Geen helpdesk-tickets voor deze klant.' : hdStatusFilter === 'open' ? 'Geen openstaande tickets.' : hdStatusFilter === 'closed' ? 'Geen gesloten tickets.' : 'Geen tickets.'}</p>
              ) : hdVisibleTickets.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm resp-card">
                    <thead><tr className="border-b border-slate-700/50 bg-slate-800/40">
                      <SortHeader label="Ref" k="ref" sortConfig={c360HdSort.sortConfig} onSort={c360HdSort.handleSort} thClass="py-2 px-2 text-xs" />
                      <SortHeader label="Onderwerp" k="subject" sortConfig={c360HdSort.sortConfig} onSort={c360HdSort.handleSort} thClass="py-2 px-2 text-xs" />
                      <SortHeader label="Status" k="stage_name" sortConfig={c360HdSort.sortConfig} onSort={c360HdSort.handleSort} thClass="py-2 px-2 text-xs" />
                      <SortHeader label="Actie vereist" k="action_required_by" sortConfig={c360HdSort.sortConfig} onSort={c360HdSort.handleSort} thClass="py-2 px-2 text-xs" />
                      <SortHeader label="Aangemaakt" k="create_date" sortConfig={c360HdSort.sortConfig} onSort={c360HdSort.handleSort} thClass="py-2 px-2 text-xs" />
                    </tr></thead>
                    <tbody>
                      {renderPaged(c360HdPage, setC360HdPage, sortRows(hdVisibleTickets, c360HdSort.sortConfig), (t) => (
                        <tr key={t.id} onClick={() => setHdSelectedId(t.id)} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer">
                          <td className="py-2 px-2 font-mono text-xs text-slate-400 whitespace-nowrap">{t.ref}</td>
                          <td className="py-2 px-2 text-slate-200 text-xs">{t.subject}{t.escalated_to_et && <ArrowUpCircle size={12} className="inline ml-1 text-purple-400" />}</td>
                          <td className="py-2 px-2 text-xs">{hdStageBadge(t)}</td>
                          <td className="py-2 px-2">{hdArbBadge(t.action_required_by, showBrand)}</td>
                          <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{hdFmt(t.create_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Gedeelde notities/logs op klant- en EAN-niveau */}
          <CustomerNotes
            token={token}
            currentUser={currentUser}
            gsrns={[...matched.gsrns]}
            customerName={selection.type === 'customer' ? selection.value : (matched.orders[0]?.customer || '')}
            defaultGsrn={selection.type === 'gsrn' ? selection.value : ([...matched.gsrns][0] || '')}
          />
          </div>

          <div className={toonC360Tab('orders') ? "space-y-4" : "hidden"}>
          {/* Orders — volledig kolommenpakket zoals OrdersPage. Klik = OrderDetailModal voor alle velden. */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-lime-400 uppercase flex items-center gap-2"><ClipboardList size={16} />Orders ({matched.orders.length})</h3>
              <span className="text-xs text-slate-500">Klik op een rij voor alle velden</span>
            </div>
            {matched.orders.length === 0 ? <p className="text-sm text-slate-500">Geen orders.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm resp-card">
                  <thead><tr className="border-b border-slate-700/50 bg-slate-800/40">
                    <SortHeader label="Energielabel" k="company" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Segment" k="segment" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Datum" k="orderDate" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Ref" k="customerReference" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Bedrijfsnaam" k="customerCompanyName" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="BTW-nr" k="customerVat" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Klant" k="customer" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Email" k="customerEmail" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <th className="py-2 px-2 text-left text-slate-400 text-xs whitespace-nowrap">Telefoon</th>
                    <SortHeader label="Markt" k="market" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="EAN" k="connectionPointGSRN" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Adres" k="connectionPointAddress" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Meter" k="meterMethod" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="CP type" k="connectionType" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="CP status" k="connectionPointStatus" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Stage" k="opportunityStage" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Status" k="status" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Totaal" k="totaal" align="right" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Delivery start" k="deliveryStartDate" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Eff. start" k="effectiveStartDate" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Delivery end" k="deliveryEndDate" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Eff. end" k="effectiveEndDate" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Product groep" k="productGroep" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Energy product" k="energyProductGroup" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Ratecard" k="ratecardName" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Team" k="salespersonTeam" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Verkoper" k="salesperson" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Contract" k="contract" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    {/* "Flow ID" en "Stop type" dragen de ruwe workflow-benaming en
                        zijn dezelfde informatie als de WL-kolommen op Orders — dus
                        enkel admin/ET. Andere rollen krijgen de afgeleide reden, die
                        de server neutraal invult (zie backend/src/util/vertrek.js).
                        Zonder deze splitsing verborg Orders de gegevens terwijl
                        Customer 360 ze gewoon toonde. */}
                    {magWorkflowLaatste(currentUser?.role)
                      ? <SortHeader label="Flow ID" k="flowIdentifier" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                      : <SortHeader label="Vertrekreden" k="vertrekReden" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />}
                    <SortHeader label="Loss-datum" k="lossDate" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    {magWorkflowLaatste(currentUser?.role) && <SortHeader label="Stop type" k="stopContractType" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />}
                    <SortHeader label="Ambassador" k="ambassadorCode" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Ref. link" k="referralLinkName" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Referrer" k="referrer" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Ref. amb. code" k="referralAmbassadorCode" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Customer Tags" k="partnerTags" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Est. Offtake kWh" k="estOfftakeKwh" align="right" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Est. Injection kWh" k="estInjectionKwh" align="right" sortConfig={c360OrdSort.sortConfig} onSort={c360OrdSort.handleSort} thClass="py-2 px-2" />
                  </tr></thead>
                  <tbody>
                    {renderPaged(c360OrdPage, setC360OrdPage, sortRows(matched.orders, c360OrdSort.sortConfig), o => (
                      <tr key={o.id} onClick={() => setSelectedOrder(o)} onContextMenu={e => onInvoicePdfCtx(e, o.customer, undefined, undefined, orderEtLinks(o))} className={`border-b border-slate-700/30 cursor-pointer ${cpStatusRowClass(o.connectionPointStatus)}`}>
                        <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{o.company || '—'}</td>
                        <td className="py-2 px-2"><SegmentBadge o={o} /></td>
                        <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{o.orderDate || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.customerReference || '—'}<KlantnrKopieerKnop nr={o.customerReference} /></td>
                        <td className="py-2 px-2 text-slate-300 text-xs truncate max-w-[160px]" title={o.customerCompanyName}>{o.customerCompanyName || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap font-mono">{o.customerVat || '—'}</td>
                        <td className="py-2 px-2 text-xs truncate max-w-[180px]"><CustomerNameCell order={o} /></td>
                        <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[160px]" title={o.customerEmail}>{o.customerEmail || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.customerPhone || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{o.market || '—'}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs font-mono whitespace-nowrap">{o.connectionPointGSRN || '—'}<EanKopieerKnop ean={o.connectionPointGSRN} /></td>
                        <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[180px]" title={o.connectionPointAddress}>{o.connectionPointAddress || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{o.meterMethod || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{o.connectionType || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{o.connectionPointStatus || '—'}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs"><OppStageText value={o.opportunityStage} /></td>
                        <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[120px]">{o.status || '—'}</td>
                        <td className="py-2 px-2 text-right text-lime-400 text-xs whitespace-nowrap">{o.totaal > 0 ? bsFormatEuro(o.totaal) : '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.deliveryStartDate || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.effectiveStartDate || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.deliveryEndDate || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.effectiveEndDate || '—'}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs">{o.productGroep || '—'}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs">{o.energyProductGroup || '—'}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs truncate max-w-[140px]" title={o.ratecardName}>{o.ratecardName || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[100px]">{o.salespersonTeam || '—'}</td>
                        <td className="py-2 px-2 text-xs whitespace-nowrap"><SellerCell order={o} currentUser={currentUser} salesStructure={salesStructure} onReassignOrder={onReassignOrder} /></td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[100px]">{o.contract || '—'}</td>
                        {magWorkflowLaatste(currentUser?.role)
                          ? <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[100px]" title={o.flowIdentifier}>{o.flowIdentifier || '—'}</td>
                          : <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[140px]" title={o.vertrekReden}>{o.vertrekReden || '—'}</td>}
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.lossDate || '—'}</td>
                        {magWorkflowLaatste(currentUser?.role) && <td className="py-2 px-2 text-slate-400 text-xs">{o.stopContractType || '—'}</td>}
                        <td className="py-2 px-2 text-slate-500 text-xs">{o.ambassadorCode || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[120px]" title={o.referralLinkName}>{o.referralLinkName || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[120px]" title={o.referrer}>{o.referrer || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs">{o.referralAmbassadorCode || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[120px]" title={zichtbareTagsTekst(o.partnerTags, visiblePartnerTags)}>{zichtbareTagsTekst(o.partnerTags, visiblePartnerTags) || '—'}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{o.estOfftakeKwh ? Math.round(o.estOfftakeKwh).toLocaleString('nl-BE') : '—'}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{o.estInjectionKwh ? Math.round(o.estInjectionKwh).toLocaleString('nl-BE') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          </div>


          {/* Metering — dezelfde pagina als de module Metering, maar ingebed en
              voorgevuld op deze klant. Eén implementatie, dus wat daar verandert
              verandert hier mee. `embedded` verbergt de paginakop en het
              klant-zoekveld. Alleen zichtbaar als de gebruiker de Metering-module
              heeft: anders zou dit dossier die module langs de achterdeur openen.
              Daarbovenop geldt de subpagina-instelling (toonC360Tab). */}
          {kanMetering && (
            <div className={toonC360Tab('metering') ? 'space-y-4' : 'hidden'}>
              <MeteringPage orders={orders} ordersLoading={ordersLoading} token={token} currentUser={currentUser} embedded branding={branding} allBranding={allBranding} allTeamBranding={allTeamBranding}
                prefill={meteringPrefill} prefillKey={meteringPrefillKey} />
            </div>
          )}
          <div className={toonC360Tab('facturen') ? "space-y-4" : "hidden"}>
          {/* Facturen — alle kolommen uit InvoicesPage (samenvattende EL+NG volumes/bedragen). */}
          <Card>
            <h3 className="text-sm font-semibold text-lime-400 uppercase mb-3 flex items-center gap-2"><Receipt size={16} />Facturen ({matched.facturen.length})</h3>
            {matched.facturen.length === 0 ? <p className="text-sm text-slate-500">Geen facturen.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm resp-card">
                  <thead><tr className="border-b border-slate-700/50 bg-slate-800/40">
                    <SortHeader label="Relatie/Bedrijf" k="relatieBedrijf" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Verkoper" k="verkoper" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Verkoopteam" k="verkoopteam" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Factuurnummer" k="nummer" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Referentie" k="relatieReferentie" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Klant" k="relatieNaam" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Factuurdatum" k="factuurdatum" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    {/* Kolom "Customer Payment Status" bewust weggelaten (10-09-2026): de status staat al op klantniveau in de kop van Customer 360. */}
                    <SortHeader label="Soort" k="soort" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Vervaldatum" k="vervaldatum" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Status" k="statusBetaling" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Totaal (Excl. BTW)" k="totaalExclBTW" align="right" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Totaal" k="totaal" align="right" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                    {/* Afgeleid uit de betaalstatus (zie invOutstanding); incl. btw. */}
                    <SortHeader label="Openstaand (incl. BTW)" k="openstaand" align="right" sortConfig={c360FacSort.sortConfig} onSort={c360FacSort.handleSort} thClass="py-2 px-2" />
                  </tr></thead>
                  <tbody>
                    {renderPaged(c360FacPage, setC360FacPage, sortRows(matched.facturen, c360FacSort.sortConfig, { isACompany: f => (f.isACompany ? 1 : 0), openstaand: f => (invOutstanding(f) ?? -1) }), f => (
                      <tr key={f.id} onContextMenu={e => onInvoicePdfCtx(e, f.relatieNaam, undefined, { id: f.id, label: f.nummer })} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-context-menu">
                        <td className="py-2 px-2 text-slate-300 text-xs">{f.relatieBedrijf || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.verkoper || '—'}{f.verkoperFactuur ? <span className="ml-1 text-slate-500" title={`Volgt de huidige verkoper van het order; op de factuur zelf staat ${f.verkoperFactuur}`} aria-hidden="true">*</span> : null}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[100px]">{f.verkoopteam || '—'}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{f.nummer || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.relatieReferentie || '—'}<KlantnrKopieerKnop nr={f.relatieReferentie} /></td>
                        <td className="py-2 px-2 text-slate-300 text-xs truncate max-w-[160px]" title={f.relatieNaam}>{f.relatieNaam || '—'}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{f.factuurdatum || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{f.soort || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.vervaldatum || '—'}</td>
                        <td className="py-2 px-2 text-xs"><span className={`px-2 py-0.5 rounded-full whitespace-nowrap ${((f.statusBetaling || '').toLowerCase() === 'betaald') ? 'bg-lime-500/20 text-lime-300 border border-lime-500/30' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>{f.statusBetaling || '—'}</span></td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{f.totaalExclBTW != null ? bsFormatEuro(f.totaalExclBTW) : '—'}</td>
                        <td className="py-2 px-2 text-right text-lime-400 text-xs font-medium whitespace-nowrap">{bsFormatEuro(f.totaal)}</td>
                        <td className="py-2 px-2 text-right text-xs whitespace-nowrap">{(() => {
                          const open = invOutstanding(f);
                          if (open == null) return <span className="text-slate-500 cursor-help" title="Het restbedrag van deze factuur is nog niet opgehaald bij de facturatie-API — hoeveel er nog openstaat is dus niet bekend.">?</span>;
                          return <span className={open > 0 ? 'text-amber-300 font-medium' : 'text-slate-500'}>{bsFormatEuro(open)}</span>;
                        })()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          {invoicePdfMenu}
          {/* Self bills van deze klant, onder de facturen (eigen feed). */}
          {(matched.selfbills || []).length > 0 && (
            <SelfbillsTabel compact rijen={matched.selfbills} bestandsnaam={`self-bills-${(selection.value || 'klant').replace(/[^a-z0-9]+/gi, '-')}`}
              onRowContext={(e, r) => onInvoicePdfCtx(e, r.relatieNaam, undefined, { id: r.id, label: r.nummer, soort: 'selfbill' })}
              toelichting="Facturen die deze klant aan ons stuurt (injectie) en hun creditnota's. Tellen niet mee in de ouderdomsanalyse of het openstaande bedrag." />
          )}

          {/* Ouderdomsanalyse — dezelfde tabel als op de pagina Aging, maar over de
              facturen van deze klant. Staat bewust tussen de facturen en de
              detailtabel met vervallen facturen: eerst het beeld per periode, dan
              de regels erachter. Niet klikbaar; filteren doe je op de Aging-pagina. */}
          {c360AgingBuckets.length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold text-lime-400 uppercase mb-3 flex items-center gap-2">
                <BarChart3 size={16} />Ouderdomsanalyse
              </h3>
              <AgingBucketTable categories={c360AgingBuckets} />
            </Card>
          )}

          {/* Aging — gedetailleerd overzicht openstaande Verkoopfacturen voorbij vervaldatum */}
          {matched.agingItems.length > 0 && (
            <Card className="border-amber-500/30">
              <h3 className="text-sm font-semibold text-amber-300 uppercase mb-3 flex items-center gap-2"><FileText size={16} />Aging — openstaand voorbij vervaldatum ({matched.agingItems.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm resp-card">
                  <thead><tr className="border-b border-slate-700/50 bg-slate-800/40">
                    <SortHeader label="Nummer" k="nummer" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Factuurdatum" k="factuurdatum" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Vervaldatum" k="vervaldatum" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Dagen te laat" k="daysOverdue" align="right" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Bucket" k="daysOverdue" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Soort" k="soort" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Ref" k="relatieReferentie" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="EL EAN" k="elGSRN" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="NG EAN" k="ngGSRN" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Verkoper" k="verkoper" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Status" k="statusBetaling" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Excl. BTW" k="totaalExclBTW" align="right" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Totaal" k="totaal" align="right" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                    {/* Waar deze tabel echt over gaat: wat er van die factuur nog
                        openstaat. Zelfde bron en opmaak als in Invoices en Aging. */}
                    <SortHeader label="Openstaand (incl. BTW)" k="openstaand" align="right" sortConfig={c360AgeSort.sortConfig} onSort={c360AgeSort.handleSort} thClass="py-2 px-2" />
                  </tr></thead>
                  <tbody>
                    {renderPaged(c360AgePage, setC360AgePage, sortRows(matched.agingItems, c360AgeSort.sortConfig, { openstaand: f => (invOutstanding(f) ?? -1) }), f => {
                      const bucket = f.daysOverdue <= 30 ? '0–30' : f.daysOverdue <= 60 ? '31–60' : f.daysOverdue <= 90 ? '61–90' : '90+';
                      const bucketCls = f.daysOverdue <= 30 ? 'bg-amber-500/15 text-amber-300' : f.daysOverdue <= 60 ? 'bg-orange-500/15 text-orange-300' : 'bg-red-500/15 text-red-300';
                      return (
                        <tr key={f.id} className="border-b border-slate-700/30 hover:bg-slate-700/30">
                          <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{f.nummer || '—'}</td>
                          <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.factuurdatum || '—'}</td>
                          <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.vervaldatum || '—'}</td>
                          <td className="py-2 px-2 text-right text-amber-300 text-xs font-medium">{f.daysOverdue}</td>
                          <td className="py-2 px-2 text-xs"><span className={`px-2 py-0.5 rounded-full whitespace-nowrap ${bucketCls}`}>{bucket}</span></td>
                          <td className="py-2 px-2 text-slate-400 text-xs">{f.soort || '—'}</td>
                          <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.relatieReferentie || '—'}<KlantnrKopieerKnop nr={f.relatieReferentie} /></td>
                          <td className="py-2 px-2 text-slate-500 text-xs font-mono">{f.elGSRN || '—'}</td>
                          <td className="py-2 px-2 text-slate-500 text-xs font-mono">{f.ngGSRN || '—'}</td>
                          <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{f.verkoper || '—'}{f.verkoperFactuur ? <span className="ml-1 text-slate-500" title={`Volgt de huidige verkoper van het order; op de factuur zelf staat ${f.verkoperFactuur}`} aria-hidden="true">*</span> : null}</td>
                          <td className="py-2 px-2 text-slate-400 text-xs">{f.statusBetaling || '—'}</td>
                          <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{f.totaalExclBTW != null ? bsFormatEuro(f.totaalExclBTW) : '—'}</td>
                          <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(f.totaal)}</td>
                          <td className="py-2 px-2 text-right text-xs whitespace-nowrap">{(() => {
                            const open = invOutstanding(f);
                            if (open == null) return <span className="text-slate-500 cursor-help" title="Het restbedrag van deze factuur is nog niet opgehaald bij de facturatie-API — hoeveel er nog openstaat is dus niet bekend.">?</span>;
                            return <span className={open > 0 ? 'text-amber-200 font-medium' : 'text-slate-500'}>{bsFormatEuro(open)}</span>;
                          })()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          </div>

          <div className={toonC360Tab('debtcollection') ? "space-y-4" : "hidden"}>
          {/* Debt Collection — per customer_reference een editable status-blok + audit log.
              Wordt altijd getoond bij klant-selectie, ook als er nog geen openstaand
              bedrag is (preemptief markeren mogelijk). Editable, optimistic + PUT. */}
          {matched.custRefs.size > 0 && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-lime-400 uppercase flex items-center gap-2"><Coins size={16} />Debt Collection</h3>
                {debtError && <span className="text-xs text-red-400">{debtError}</span>}
              </div>
              <div className="space-y-5">
                {[...matched.custRefs].map(ref => {
                  const s = debtStatus[ref] || null;
                  const log = debtLogs[ref] || [];
                  const isSaving = debtSaving === ref;
                  const hasAging = matched.agingItems.some(f => f.relatieReferentie === ref);
                  return (
                    <div key={ref} className="bg-slate-900/40 border border-slate-700/50 rounded-lg p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <div className="text-xs text-slate-400">Klantref <span className="font-mono text-slate-200">{ref}</span></div>
                        <div className="flex items-center gap-2 text-[11px]">
                          {hasAging ? <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">Heeft overdue facturen</span>
                                    : <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">Geen overdue facturen</span>}
                          {isSaving && <span className="text-slate-500">opslaan…</span>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Contact status</label>
                          <select value={s?.contact_status || ''} onChange={e => updateDebtField(ref, 'contact_status', e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${s?.contact_status ? 'border-lime-500/50 text-lime-200' : 'border-slate-600 text-slate-300'}`}>
                            <option value="">— niet ingesteld —</option>
                            {DC_CONTACT_STATUS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Klant respons</label>
                          <select value={s?.customer_response || ''} onChange={e => updateDebtField(ref, 'customer_response', e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${s?.customer_response ? 'border-lime-500/50 text-lime-200' : 'border-slate-600 text-slate-300'}`}>
                            <option value="">— niet ingesteld —</option>
                            {DC_CUSTOMER_RESPONSE.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Complaint</label>
                          <select value={s?.complaint_status || ''} onChange={e => updateDebtField(ref, 'complaint_status', e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${s?.complaint_status ? 'border-lime-500/50 text-lime-200' : 'border-slate-600 text-slate-300'}`}>
                            <option value="">— niet ingesteld —</option>
                            {DC_COMPLAINT_STATUS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="block text-xs text-slate-400 mb-1">Notities</label>
                        <Customer360DebtNotes
                          initial={s?.notes || ''}
                          onSave={val => updateDebtField(ref, 'notes', val)}
                          disabled={isSaving}
                        />
                      </div>

                      {s?.updated_at && (
                        <div className="text-[11px] text-slate-500 mb-3">
                          Laatste wijziging {dcFormatDateTime(s.updated_at)} door {s.updated_by?.name || '—'}{s.updated_by?.email ? ` (${s.updated_by.email})` : ''}
                        </div>
                      )}

                      {log.length > 0 && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-slate-400 hover:text-slate-200">Audit log ({log.length})</summary>
                          <ol className="mt-2 space-y-1.5">
                            {log.map(l => (
                              <li key={l.id} className="bg-slate-900/60 border border-slate-700/50 rounded px-3 py-2">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-slate-300 font-medium">{l.field.replace(/_/g, ' ')}</span>
                                  <span className="text-slate-500">{dcFormatDateTime(l.created_at)}</span>
                                </div>
                                <div className="mt-0.5 text-slate-400">
                                  <span className="line-through text-slate-600">{l.old_value || '—'}</span>
                                  <span className="mx-2 text-slate-500">→</span>
                                  <span className="text-slate-200">{l.new_value || '—'}</span>
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1">door {l.user_name || '—'} {l.user_email ? `(${l.user_email})` : ''}</div>
                              </li>
                            ))}
                          </ol>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
          </div>

          <div className={toonC360Tab('samenvatting') ? "space-y-4" : "hidden"}>
          {/* Referrals — twee tabellen: ontvangen (orders met referral-data) en verzonden (deze klant is referrer) */}
          {(matched.referralsReceived.length > 0 || matched.referralsSent.length > 0) && (
            <Card>
              <h3 className="text-sm font-semibold text-lime-400 uppercase mb-4 flex items-center gap-2"><Share2 size={16} />Referrals</h3>

              {matched.referralsReceived.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xs text-lime-300 uppercase mb-2">Klant is aangebracht ({matched.referralsReceived.length}) — klik voor alle order-details</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm resp-card">
                      <thead><tr className="border-b border-slate-700/50 bg-slate-800/40">
                        <SortHeader label="Orderdatum" k="orderDate" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="EAN" k="connectionPointGSRN" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Markt" k="market" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Referrer" k="referrer" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Ref. link" k="referralLinkName" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Ref. amb. code" k="referralAmbassadorCode" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Ambassador" k="ambassadorCode" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Verkoper" k="salesperson" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Stage" k="opportunityStage" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Delivery start" k="deliveryStartDate" sortConfig={c360RefRSort.sortConfig} onSort={c360RefRSort.handleSort} thClass="py-2 px-2" />
                      </tr></thead>
                      <tbody>
                        {renderPaged(c360RefRPage, setC360RefRPage, sortRows(matched.referralsReceived, c360RefRSort.sortConfig), o => (
                          <tr key={o.id} onClick={() => setSelectedOrder(o)} onContextMenu={e => onInvoicePdfCtx(e, o.customer, undefined, undefined, orderEtLinks(o))} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer">
                            <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{o.orderDate || '—'}</td>
                            <td className="py-2 px-2 text-slate-300 text-xs font-mono whitespace-nowrap">{o.connectionPointGSRN || '—'}<EanKopieerKnop ean={o.connectionPointGSRN} /></td>
                            <td className="py-2 px-2 text-slate-400 text-xs">{o.market || '—'}</td>
                            <td className="py-2 px-2 text-lime-300 text-xs truncate max-w-[140px]" title={o.referrer}>{o.referrer || '—'}</td>
                            <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[140px]" title={o.referralLinkName}>{o.referralLinkName || '—'}</td>
                            <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.referralAmbassadorCode || '—'}</td>
                            <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.ambassadorCode || '—'}</td>
                            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.salesperson || '—'}</td>
                            <td className="py-2 px-2 text-slate-400 text-xs"><OppStageText value={o.opportunityStage} /></td>
                            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.deliveryStartDate || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {matched.referralsSent.length > 0 && (
                <div>
                  <h4 className="text-xs text-blue-300 uppercase mb-2">Klant bracht zelf anderen aan ({matched.referralsSent.length}) — klik voor alle order-details</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm resp-card">
                      <thead><tr className="border-b border-slate-700/50 bg-slate-800/40">
                        <SortHeader label="Orderdatum" k="orderDate" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Aangebracht: klant" k="customer" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="EAN" k="connectionPointGSRN" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Markt" k="market" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Energielabel" k="company" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Verkoper" k="salesperson" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Ref. amb. code" k="referralAmbassadorCode" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Stage" k="opportunityStage" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                        <SortHeader label="Delivery start" k="deliveryStartDate" sortConfig={c360RefSSort.sortConfig} onSort={c360RefSSort.handleSort} thClass="py-2 px-2" />
                      </tr></thead>
                      <tbody>
                        {renderPaged(c360RefSPage, setC360RefSPage, sortRows(matched.referralsSent, c360RefSSort.sortConfig), o => (
                          <tr key={o.id} onClick={() => setSelectedOrder(o)} onContextMenu={e => onInvoicePdfCtx(e, o.customer, undefined, undefined, orderEtLinks(o))} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer">
                            <td className="py-2 px-2 text-slate-300 text-xs whitespace-nowrap">{o.orderDate || '—'}</td>
                            <td className="py-2 px-2 text-blue-300 text-xs truncate max-w-[160px]" title={o.customer}>{o.customer || '—'}</td>
                            <td className="py-2 px-2 text-slate-300 text-xs font-mono whitespace-nowrap">{o.connectionPointGSRN || '—'}<EanKopieerKnop ean={o.connectionPointGSRN} /></td>
                            <td className="py-2 px-2 text-slate-400 text-xs">{o.market || '—'}</td>
                            <td className="py-2 px-2 text-slate-400 text-xs">{o.company || '—'}</td>
                            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.salesperson || '—'}</td>
                            <td className="py-2 px-2 text-slate-500 text-xs whitespace-nowrap">{o.referralAmbassadorCode || '—'}</td>
                            <td className="py-2 px-2 text-slate-400 text-xs"><OppStageText value={o.opportunityStage} /></td>
                            <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{o.deliveryStartDate || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card>
          )}
          </div>

          <div className={toonC360Tab('paymentplans') ? "space-y-4" : "hidden"}>
          {/* Payment Plans (V2) — via Lynx /PaymentPlan2/GetByRelationReference per customerReference.
              Toont status, openstaand/vervallen bedrag, items en gekoppelde facturen. */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-lime-400 uppercase flex items-center gap-2"><Wallet size={16} />Payment Plans ({paymentPlans.entities.length})</h3>
              {paymentPlans.loading && <span className="text-xs text-slate-500">Laden…</span>}
              {paymentPlans.error && <span className="text-xs text-red-400">{paymentPlans.error}</span>}
            </div>
            {!paymentPlans.loading && paymentPlans.entities.length === 0 ? (
              <p className="text-sm text-slate-500">{paymentPlans.errorMessage || 'Geen payment plans voor deze klant.'}</p>
            ) : paymentPlans.entities.length > 0 && (
              <div className="space-y-4">
                {paymentPlans.entities.map(plan => (
                  <div key={plan.id} className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-100">{plan.reference || `Plan #${plan.id}`}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Relation ref: {plan.relationReference || '—'}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {plan.status === 'Warning' ? <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30">Warning</span>
                          : plan.status === 'OnTrack' ? <span className="px-2 py-0.5 rounded-full text-xs bg-lime-500/20 text-lime-300 border border-lime-500/30">On Track</span>
                          : plan.status ? <span className="text-xs text-slate-400">{plan.status}</span> : null}
                        {plan.isActive ? <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700 text-slate-300">Actief</span>
                                      : <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700 text-slate-500">Inactief</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><div className="text-xs text-slate-500">Totaal</div><div className="text-slate-200">{bsFormatEuro(plan.amount)}</div></div>
                      <div><div className="text-xs text-slate-500">Openstaand</div><div className="text-amber-300 font-semibold">{bsFormatEuro(plan.outstandingAmount)}</div></div>
                      <div><div className="text-xs text-slate-500">Verwacht openstaand</div><div className="text-slate-200">{bsFormatEuro(plan.expectedOutstandingAmount)}</div></div>
                      <div><div className="text-xs text-slate-500">Vervallen</div><div className={`font-semibold ${plan.dueAmount > 0 ? 'text-red-300' : 'text-slate-200'}`}>{bsFormatEuro(plan.dueAmount)}</div></div>
                    </div>

                    {(plan.paymentPlanItems || []).length > 0 && (
                      <div className="mt-4">
                        <div className="text-xs text-slate-400 mb-1.5">Plan items ({plan.paymentPlanItems.length})</div>
                        <div className="overflow-x-auto border border-slate-700/50 rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-700/40">
                              <tr>
                                <th className="text-left px-2 py-1.5 text-slate-300">Verwacht</th>
                                <th className="text-left px-2 py-1.5 text-slate-300">Effectief</th>
                                <th className="text-right px-2 py-1.5 text-slate-300">Bedrag</th>
                              </tr>
                            </thead>
                            <tbody>
                              {plan.paymentPlanItems.map((it, i) => (
                                <tr key={i} className="border-t border-slate-700/40">
                                  <td className="px-2 py-1.5 text-slate-300 whitespace-nowrap">{bsFormatDate(it.expectedPaymentDateUtc)}</td>
                                  <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{bsFormatDate(it.effectivePaymentPlanDateUtc)}</td>
                                  <td className="px-2 py-1.5 text-right text-slate-200">{bsFormatEuro(it.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {(plan.invoices || []).length > 0 && (
                      <div className="mt-4">
                        <div className="text-xs text-slate-400 mb-1.5">Gekoppelde facturen ({plan.invoices.length})</div>
                        <div className="overflow-x-auto border border-slate-700/50 rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-700/40">
                              <tr>
                                <th className="text-left px-2 py-1.5 text-slate-300">Reference</th>
                                <th className="text-left px-2 py-1.5 text-slate-300">Datum</th>
                                <th className="text-left px-2 py-1.5 text-slate-300">Vervaldatum</th>
                                <th className="text-right px-2 py-1.5 text-slate-300">Totaal (incl.)</th>
                                <th className="text-right px-2 py-1.5 text-slate-300">Restant</th>
                              </tr>
                            </thead>
                            <tbody>
                              {plan.invoices.map(inv => (
                                <tr key={inv.id} className="border-t border-slate-700/40">
                                  <td className="px-2 py-1.5 text-slate-300">{inv.reference || `#${inv.id}`}</td>
                                  <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{bsFormatDate(inv.dateUtc)}</td>
                                  <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{bsFormatDate(inv.dueUtc)}</td>
                                  <td className="px-2 py-1.5 text-right text-slate-200">{bsFormatEuro(inv.totalIncl)}</td>
                                  <td className={`px-2 py-1.5 text-right font-semibold ${inv.remainder > 0 ? 'text-amber-300' : 'text-slate-400'}`}>{bsFormatEuro(inv.remainder)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
          </div>

          <div className={toonC360Tab('billshock') ? "space-y-4" : "hidden"}>
          {/* Bill Shock — volledig kolommenpakket; klik = BillShockDetailModal voor settlement + prepayment details */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-lime-400 uppercase flex items-center gap-2"><AlertCircle size={16} />Bill Shock ({matched.billshocks.length})</h3>
              <div className="flex items-center gap-3 text-xs">
                {billshockLoading && <span className="text-slate-500">Laden…</span>}
                {billshockError && <span className="text-red-400">{billshockError}</span>}
                {!billshockLoading && matched.billshocks.length > 0 && <span className="text-slate-500">Klik op een rij voor settlement + prepayment details</span>}
              </div>
            </div>
            {!billshockLoading && matched.billshocks.length === 0 ? (
              <p className="text-sm text-slate-500">Geen bill shock records voor deze klant's EAN{matched.gsrns.size === 1 ? '' : 's'}.</p>
            ) : matched.billshocks.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm resp-card">
                  <thead><tr className="border-b border-slate-700/50 bg-slate-800/40">
                    <SortHeader label="Berekend" k="calculationUtc" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="EAN" k="connectionPointGSRN" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Project ref" k="projectRef" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Markt" k="bsMarket" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Periode" k="fromUtc" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Offtake kWh" k="offtakeQuantityKWh" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Injection kWh" k="injectionQuantityKWh" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Settlement" k="settlementAmount" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Target" k="targetAmount" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Unrev. prepay" k="unreversedPrepaymentAmount" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Future prepay" k="futurePrepaymentAmount" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Proposed prepay" k="proposedPrepaymentAmount" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Bill shock" k="billShockAmount" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Deviation" k="billshockDeviationPercentage" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Resterend" k="remainingPeriods" align="right" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Status" k="bsStatus" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                    <SortHeader label="Foutmelding" k="calculationError" sortConfig={c360BsSort.sortConfig} onSort={c360BsSort.handleSort} thClass="py-2 px-2" />
                  </tr></thead>
                  <tbody>
                    {renderPaged(c360BsPage, setC360BsPage, sortRows(matched.billshocks, c360BsSort.sortConfig, { projectRef: b => b.projectConnectionPoint?.projectReference || '', bsMarket: b => b.projectConnectionPoint?.market || '', bsStatus: b => (b.isSuccess ? 1 : 0) }), b => (
                      <tr key={b.id} onClick={() => setSelectedBillshock(b)} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer">
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(b.calculationUtc)}</td>
                        <td className="py-2 px-2 text-slate-300 text-xs font-mono">{b.connectionPointGSRN || '—'}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[140px]" title={b.projectConnectionPoint?.projectReference}>{b.projectConnectionPoint?.projectReference || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs">{b.projectConnectionPoint?.market || '—'}</td>
                        <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(b.fromUtc)} → {bsFormatDate(b.toUtc)}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatKwh(b.offtakeQuantityKWh)}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatKwh(b.injectionQuantityKWh)}</td>
                        <td className="py-2 px-2 text-right text-slate-200 text-xs whitespace-nowrap">{bsFormatEuro(b.settlementAmount)}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(b.targetAmount)}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(b.unreversedPrepaymentAmount)}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(b.futurePrepaymentAmount)}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatEuro(b.proposedPrepaymentAmount)}</td>
                        <td className="py-2 px-2 text-right text-slate-100 text-xs font-medium whitespace-nowrap">{bsFormatEuro(b.billShockAmount)}</td>
                        <td className="py-2 px-2 text-right text-slate-300 text-xs whitespace-nowrap">{bsFormatPct(b.billshockDeviationPercentage)}</td>
                        <td className="py-2 px-2 text-right text-slate-400 text-xs">{b.remainingPeriods ?? '—'}</td>
                        <td className="py-2 px-2 text-xs">{b.isSuccess ? <span className="px-2 py-0.5 rounded-full bg-lime-500/20 text-lime-300 border border-lime-500/30 whitespace-nowrap">OK</span> : <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 whitespace-nowrap">Gefaald</span>}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[200px]" title={b.calculationError}>{b.calculationError || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          </div>
        </>
      )}

      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
      {selectedBillshock && <BillShockDetailModal row={selectedBillshock} token={token} onClose={() => setSelectedBillshock(null)} />}
      {hdSelectedId && <HelpdeskTicketModal id={hdSelectedId} token={token} caps={hdCaps} stages={hdStages} companies={hdCompanies} showBrand={showBrand} magKennisbank={hdMagKennisbank(currentUser)} magAi360={currentUser?.helpdeskAi360 === true} orders={orders} onClose={() => setHdSelectedId(null)} onChanged={() => setHdRefresh((r) => r + 1)} />}
      {hdShowCreate && matched && (
        <HelpdeskNieuwTicket token={token} orders={orders} currentUser={currentUser} showBrand={showBrand}
          prefill={{ name: selection?.type === 'customer' ? selection.value : (matched.orders[0]?.customer || ''), reference: [...matched.custRefs][0] || '' }}
          onClose={() => setHdShowCreate(false)}
          onCreated={(c) => { setHdShowCreate(false); setHdRefresh((r) => r + 1); if (c && c.id) setHdSelectedId(c.id); }} />
      )}
    </div>
  );
};

// ─── Debt Collection module ──────────────────────────────────────────────────
// Aggregeer per klant (customer_reference) de openstaande Verkoopfacturen die
// voorbij hun vervaldatum staan. Statussen + notes worden server-side persistent
// opgeslagen met audit-log; backend past dezelfde RBAC-scope toe (zie route).

const DC_CONTACT_STATUS = [
  'Not contacted yet',
  'First call attempted – no answer',
  'Second call attempted – no answer',
  'Third call attempted – no answer',
  'Customer reached',
  'Left voicemail',
  'Email sent',
  'SMS sent',
  'Reminder letter sent',
];
const DC_CUSTOMER_RESPONSE = [
  'Customer promised to pay',
  'Payment expected',
  'Payment received',
  'Customer requested payment plan',
  'Payment plan proposed',
  'Payment plan accepted',
  'Payment plan rejected',
  'Customer disputes invoice',
  'Customer refuses to pay',
  'Customer requests more information',
  'Customer unreachable',
];
const DC_COMPLAINT_STATUS = [
  'Invoice disputed',
  'Complaint open',
  'Complaint under review',
  'Awaiting internal feedback',
  'Awaiting customer feedback',
  'Dispute resolved',
  'Payment blocked due to complaint',
];

function dcFormatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('nl-BE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

const DebtCollectionDetailModal = ({ row, token, onClose, onSaved }) => {
  const [notes, setNotes] = useState(row.status?.notes || '');
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [logError, setLogError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingLog(true); setLogError(null);
    fetch(`/api/debt-collection/status/${encodeURIComponent(row.customerReference)}/log`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancelled) setLog(j.entries || []); })
      .catch(e => { if (!cancelled) setLogError(e.message); })
      .finally(() => { if (!cancelled) setLoadingLog(false); });
    return () => { cancelled = true; };
  }, [row.customerReference, token]);

  const saveNotes = async () => {
    setSaving(true); setSaveError(null);
    try {
      const r = await fetch(`/api/debt-collection/status/${encodeURIComponent(row.customerReference)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes }),
      });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      const updated = await r.json();
      onSaved?.(updated);
      // refresh log
      const r2 = await fetch(`/api/debt-collection/status/${encodeURIComponent(row.customerReference)}/log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r2.ok) { const j = await r2.json(); setLog(j.entries || []); }
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
          <div>
            <h3 className="text-lg font-bold text-slate-100">{row.customer || '—'}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{row.customerReference} · {row.company || '—'} · Openstaand <span className="text-amber-300 font-medium">{bsFormatEuro(row.outstanding)}</span></p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-2">Notities</h4>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              maxLength={4000}
              placeholder="Vrije tekst voor deze klant…"
              className="w-full min-h-[100px] p-3 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-100 focus:border-lime-500 focus:outline-none resize-y"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-slate-500">{notes.length}/4000</span>
              <div className="flex items-center gap-2">
                {saveError && <span className="text-xs text-red-400">{saveError}</span>}
                <button onClick={saveNotes} disabled={saving || notes === (row.status?.notes || '')} className="px-3 py-1.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-600 disabled:text-slate-400 text-slate-900 text-sm font-semibold rounded-lg">{saving ? 'Opslaan…' : 'Notities opslaan'}</button>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-2">Audit log ({log.length})</h4>
            {loadingLog ? <p className="text-sm text-slate-500">Laden…</p>
              : logError ? <p className="text-sm text-red-400">{logError}</p>
              : log.length === 0 ? <p className="text-sm text-slate-500">Nog geen wijzigingen.</p>
              : (
                <ol className="space-y-1.5">
                  {log.map(l => (
                    <li key={l.id} className="bg-slate-900/60 border border-slate-700/50 rounded px-3 py-2 text-xs">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-300 font-medium">{l.field.replace(/_/g, ' ')}</span>
                        <span className="text-slate-500">{dcFormatDateTime(l.created_at)}</span>
                      </div>
                      <div className="mt-0.5 text-slate-400">
                        <span className="line-through text-slate-600">{l.old_value || '—'}</span>
                        <span className="mx-2 text-slate-500">→</span>
                        <span className="text-slate-200">{l.new_value || '—'}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">door {l.user_name || '—'} {l.user_email ? `(${l.user_email})` : ''}</div>
                    </li>
                  ))}
                </ol>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DebtCollectionPage = ({ orders = [], facturen = [], token, currentUser, onOpenCustomer360, salesStructure = EMPTY_STRUCTURE, visiblePartnerTags = null }) => {
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // row voor detail-modal
  const [companyFilter, setCompanyFilter] = useState('all');
  const [verkoopteamFilter, setVerkoopteamFilter] = useState('all');
  const [verkoperFilter, setVerkoperFilter] = useState('all');
  const [contactFilter, setContactFilter] = useState('all');
  // Default cascade-selectie op de scope van de ingelogde gebruiker (master =
  // eigen energielabel, teamleader = +team, reseller = +verkoper; admin/ET =
  // alles). Herhaalt bij user-wissel (impersonate) zodra /api/auth/me de
  // scope-velden levert.
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setCompanyFilter(d.company);
    setVerkoopteamFilter(d.team);
    setVerkoperFilter(d.seller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]);
  const [responseFilter, setResponseFilter] = useState('all');
  const [complaintFilter, setComplaintFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  useFilterPersistence('debt.filters', currentUser,
    { companyFilter, verkoopteamFilter, verkoperFilter, contactFilter, responseFilter, complaintFilter, searchTerm },
    (s) => { setCompanyFilter(s.companyFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); setContactFilter(s.contactFilter ?? 'all'); setResponseFilter(s.responseFilter ?? 'all'); setComplaintFilter(s.complaintFilter ?? 'all'); setSearchTerm(s.searchTerm ?? ''); });
  // Pagination — 15 wanbetalers per pagina (TABLE_PAGE_SIZE), grootste bedrag bovenaan.
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = TABLE_PAGE_SIZE;

  // Status ophalen
  const loadStatus = useMemo(() => async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch('/api/debt-collection/status', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      const j = await r.json();
      setStatusMap(j.customers || {});
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Bouw per customer_reference een aggregatie uit de gescopete orders + facturen.
  // Alleen klanten met een openstaand bedrag > 0 in Verkoopfacturen voorbij vervaldatum.
  const rows = useMemo(() => {
    const today = new Date();
    // Map customerRef → laatste order-info (voor klantgegevens, customer tag, scope)
    const orderInfo = new Map();
    for (const o of orders) {
      const ref = (o.customerReference || '').trim();
      if (!ref) continue;
      const cur = orderInfo.get(ref);
      const ts = o.orderDateTime || o.orderDate || '';
      if (!cur || ts > cur._ts) {
        orderInfo.set(ref, {
          _ts: ts,
          customer: o.customer || '',
          customerCompanyName: o.customerCompanyName || '',
          customerVat: o.customerVat || '',
          customerEmail: o.customerEmail || '',
          customerPhone: o.customerPhone || '',
          company: o.company || '',
          salespersonTeam: o.salespersonTeam || '',
          salesperson: o.salesperson || '',
          partnerTags: new Set(),
        });
      }
    }
    // Verzamel alle customer tags die we tegenkomen per klant
    for (const o of orders) {
      const ref = (o.customerReference || '').trim();
      if (!ref) continue;
      const info = orderInfo.get(ref);
      // Losse tags verzamelen, en enkel de zichtbare — deze lijst gaat ook naar export.
      if (info) for (const t of zichtbareTags(o.partnerTags, visiblePartnerTags)) info.partnerTags.add(t);
    }

    // Aggregatie van openstaande Verkoopfacturen voorbij vervaldatum
    const agg = new Map();
    for (const f of facturen) {
      const ref = (f.relatieReferentie || '').trim();
      if (!ref) continue;
      const status = (f.statusBetaling || '').toLowerCase();
      const isUnpaid = status !== 'betaald' && status !== 'omgekeerd';
      const isVerkoop = (f.soort || '').toLowerCase() === 'verkoopfactuur';
      const due = f.vervaldatum ? new Date(f.vervaldatum) : null;
      if (!isUnpaid || !isVerkoop || !due || due >= today) continue;
      let a = agg.get(ref);
      if (!a) { a = { outstanding: 0, count: 0, oldestDays: 0 }; agg.set(ref, a); }
      a.outstanding += Number(f.totaal) || 0;
      a.count += 1;
      const daysOverdue = Math.floor((today - due) / 86400000);
      if (daysOverdue > a.oldestDays) a.oldestDays = daysOverdue;
    }

    const list = [];
    for (const [ref, a] of agg.entries()) {
      if (a.outstanding <= 0) continue;
      const info = orderInfo.get(ref) || { customer: '', customerEmail: '', customerPhone: '', company: '', salespersonTeam: '', salesperson: '', partnerTags: new Set() };
      list.push({
        customerReference: ref,
        customer: info.customer,
        customerCompanyName: info.customerCompanyName || '',
        customerVat: info.customerVat || '',
        customerEmail: info.customerEmail,
        customerPhone: info.customerPhone,
        company: info.company,
        salespersonTeam: info.salespersonTeam,
        salesperson: info.salesperson,
        partnerTags: [...info.partnerTags].join(', '),
        outstanding: a.outstanding,
        overdueCount: a.count,
        oldestDays: a.oldestDays,
        status: statusMap[ref] || null,
      });
    }
    // Grootste wanbetalers eerst
    list.sort((x, y) => y.outstanding - x.outstanding);
    return list;
  }, [orders, facturen, statusMap, visiblePartnerTags]);

  // Cascade-filter dropdowns uit de centrale sales-structuur (identiek in alle
  // modules, rol-gescopet door de backend).
  const { companies, teams: verkoopteams, sellers: verkopers } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter),
    [salesStructure, companyFilter, verkoopteamFilter]
  );

  const filtered = useMemo(() => {
    let data = rows;
    if (companyFilter !== 'all') data = data.filter(r => r.company === companyFilter);
    if (verkoopteamFilter !== 'all') data = data.filter(r => r.salespersonTeam === verkoopteamFilter);
    if (verkoperFilter !== 'all') data = data.filter(r => r.salesperson === verkoperFilter);
    if (contactFilter !== 'all') data = data.filter(r => (r.status?.contact_status || '') === contactFilter);
    if (responseFilter !== 'all') data = data.filter(r => (r.status?.customer_response || '') === responseFilter);
    if (complaintFilter !== 'all') data = data.filter(r => (r.status?.complaint_status || '') === complaintFilter);
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      data = data.filter(r =>
        r.customer?.toLowerCase().includes(t) ||
        r.customerCompanyName?.toLowerCase().includes(t) ||
        r.customerVat?.toLowerCase().includes(t) ||
        r.customerReference?.toLowerCase().includes(t) ||
        r.customerEmail?.toLowerCase().includes(t) ||
        r.partnerTags?.toLowerCase().includes(t) ||
        r.salesperson?.toLowerCase().includes(t)
      );
    }
    return data;
  }, [rows, companyFilter, verkoopteamFilter, verkoperFilter, contactFilter, responseFilter, complaintFilter, searchTerm]);

  const totals = useMemo(() => ({
    customers: filtered.length,
    outstanding: filtered.reduce((s, r) => s + r.outstanding, 0),
    invoices: filtered.reduce((s, r) => s + r.overdueCount, 0),
  }), [filtered]);

  // Reset paginatie als filters wijzigen — anders zit je op pagina 5 terwijl
  // er na een filter nog maar 1 pagina data is.
  useEffect(() => { setCurrentPage(1); }, [companyFilter, verkoopteamFilter, verkoperFilter, contactFilter, responseFilter, complaintFilter, searchTerm]);

  const dcSort = useTableSort();
  const dcSortAcc = {
    contactStatus: r => r.status?.contact_status || '', customerResponse: r => r.status?.customer_response || '',
    complaintStatus: r => r.status?.complaint_status || '', lastUpdated: r => r.status?.updated_at || '',
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = sortRows(filtered, dcSort.sortConfig, dcSortAcc).slice(pageStart, pageStart + pageSize);

  const handleCompanyChange = (v) => { setCompanyFilter(v); setVerkoopteamFilter('all'); setVerkoperFilter('all'); };
  const handleVerkoopteamChange = (v) => { setVerkoopteamFilter(v); setVerkoperFilter('all'); };
  const clearFilters = () => { setCompanyFilter('all'); setVerkoopteamFilter('all'); setVerkoperFilter('all'); setContactFilter('all'); setResponseFilter('all'); setComplaintFilter('all'); setSearchTerm(''); };

  // Update één status-veld voor een klant. Optimistic UI: zet de waarde direct
  // in statusMap; bij fout revert + toon error.
  const updateField = async (customerReference, field, value) => {
    const prev = statusMap[customerReference] || null;
    const optimistic = { ...(prev || { contact_status: null, customer_response: null, complaint_status: null, notes: '' }), [field]: value || null, updated_at: new Date().toISOString(), updated_by: currentUser ? { id: currentUser.id, name: currentUser.name, email: currentUser.email } : null };
    setStatusMap(m => ({ ...m, [customerReference]: optimistic }));
    try {
      const r = await fetch(`/api/debt-collection/status/${encodeURIComponent(customerReference)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value || null }),
      });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      const updated = await r.json();
      setStatusMap(m => ({ ...m, [customerReference]: { contact_status: updated.contact_status, customer_response: updated.customer_response, complaint_status: updated.complaint_status, notes: updated.notes || '', updated_at: updated.updated_at, updated_by: updated.updated_by } }));
    } catch (e) {
      // Revert
      setStatusMap(m => ({ ...m, [customerReference]: prev || undefined }));
      setError(e.message);
      setTimeout(() => setError(null), 4000);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Debt Collection" subtitle={`Klanten met openstaande Verkoopfacturen voorbij vervaldatum — gesorteerd op grootste wanbetalers`}>
        <ExportKnop
          onExport={() => exportRowsToCsv(`debt-collection-${new Date().toISOString().slice(0, 10)}.csv`, [
            { header: 'Klant', get: r => r.customer },
            { header: 'Klantref', get: r => r.customerReference },
            { header: 'Email', get: r => r.customerEmail },
            { header: 'Telefoon', get: r => r.customerPhone },
            { header: 'Energielabel', get: r => r.company },
            { header: 'Verkoopteam', get: r => r.salespersonTeam },
            { header: 'Verkoper', get: r => r.salesperson },
            { header: 'Customer Tags', get: r => r.partnerTags },
            { header: 'Overdue facturen', get: r => r.overdueCount },
            { header: 'Dagen oudste overdue', get: r => r.oldestDays },
            { header: 'Openstaand (€)', get: r => r.outstanding },
            { header: 'Contact status', get: r => r.status?.contact_status || '' },
            { header: 'Klant respons', get: r => r.status?.customer_response || '' },
            { header: 'Complaint status', get: r => r.status?.complaint_status || '' },
            { header: 'Notes', get: r => r.status?.notes || '' },
            { header: 'Laatste wijziging', get: r => r.status?.updated_at || '' },
            { header: 'Door', get: r => r.status?.updated_by?.name || '' },
          ], filtered)}
          disabled={filtered.length === 0}
          count={filtered.length}
          label="Exporteer alle gefilterde wanbetalers naar Excel"
        />
        <button onClick={loadStatus} disabled={loading} className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg ${loading ? 'bg-slate-700 text-slate-400 cursor-wait' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}>
          {loading ? <><div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />Laden…</> : <><Activity size={14} />Vernieuwen</>}
        </button>
      </PageHeader>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Wanbetalers (filtered)</div><div className="text-2xl font-bold text-amber-300"><Kengetal>{totals.customers}</Kengetal></div></div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Openstaand totaal</div><div className="text-2xl font-bold text-amber-300"><Kengetal>{bsFormatEuro(totals.outstanding)}</Kengetal></div></div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Openstaande facturen</div><div className="text-2xl font-bold text-slate-100"><Kengetal>{totals.invoices}</Kengetal></div></div>
      </div>

      <Card className="!p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Energieleverancier</label>
            <select value={companyFilter} onChange={e => handleCompanyChange(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${companyFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={companies}>Alle</AllOption>
              {companies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Verkoopteam</label>
            <select value={verkoopteamFilter} onChange={e => handleVerkoopteamChange(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={verkoopteams}>Alle</AllOption>
              {verkoopteams.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Verkoper</label>
            <select value={verkoperFilter} onChange={e => setVerkoperFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoperFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <AllOption options={verkopers}>Alle</AllOption>
              {verkopers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Contact status</label>
            <select value={contactFilter} onChange={e => setContactFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${contactFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              {DC_CONTACT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Klant respons</label>
            <select value={responseFilter} onChange={e => setResponseFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${responseFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              {DC_CUSTOMER_RESPONSE.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="w-[184px]">
            <label className="block text-xs text-slate-400 mb-1">Complaint</label>
            <select value={complaintFilter} onChange={e => setComplaintFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${complaintFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
              <option value="all">Alle</option>
              {DC_COMPLAINT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="w-full sm:w-[280px] shrink-0">
            <label className="block text-xs text-slate-400 mb-1">Zoeken</label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2 text-slate-500" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Klant, ref, email, customer tag…" className="w-full pl-7 pr-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" />
            </div>
          </div>
          <button onClick={clearFilters} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded">Wissen</button>
        </div>
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead>
              <tr className="border-b border-slate-700/50 bg-slate-800/40">
                <SortHeader label="Klant" k="customer" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Ref" k="customerReference" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Email / Tel" k="customerEmail" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Energielabel" k="company" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Team / Verkoper" k="salespersonTeam" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Customer Tag" k="partnerTags" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Facturen" k="overdueCount" align="right" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Oudste" k="oldestDays" align="right" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Openstaand" k="outstanding" align="right" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Contact status" k="contactStatus" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Klant respons" k="customerResponse" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Complaint" k="complaintStatus" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Laatst aangepast" k="lastUpdated" sortConfig={dcSort.sortConfig} onSort={dcSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <th className="py-2.5 px-2 text-left text-slate-400 text-xs whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={14} className="py-8 text-center text-slate-500 text-sm">Geen wanbetalers binnen je rechten en filters.</td></tr>
              )}
              {pageRows.map(r => {
                const s = r.status;
                return (
                  <tr key={r.customerReference} onContextMenu={e => onC360Context(e, r.customer, klantTarget(r.customer, r.customerReference))} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-context-menu">
                    <td className="py-2 px-2 text-slate-100 text-xs truncate max-w-[180px]" title={r.customer}>{r.customer || '—'}</td>
                    <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{r.customerReference}</td>
                    <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[160px]" title={`${r.customerEmail} / ${r.customerPhone}`}>
                      <div className="truncate">{r.customerEmail || '—'}</div>
                      <div className="truncate text-slate-500">{r.customerPhone || '—'}</div>
                    </td>
                    <td className="py-2 px-2 text-slate-300 text-xs">{r.company || '—'}</td>
                    <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[140px]" title={`${r.salespersonTeam} / ${r.salesperson}`}>
                      <div className="truncate">{r.salespersonTeam || '—'}</div>
                      <div className="truncate text-slate-500">{r.salesperson || '—'}</div>
                    </td>
                    <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[140px]" title={r.partnerTags}>{r.partnerTags || '—'}</td>
                    <td className="py-2 px-2 text-right text-slate-300 text-xs">{r.overdueCount}</td>
                    <td className="py-2 px-2 text-right text-amber-300 text-xs whitespace-nowrap">{r.oldestDays} d</td>
                    <td className="py-2 px-2 text-right text-amber-200 text-xs font-semibold whitespace-nowrap">{bsFormatEuro(r.outstanding)}</td>
                    <td className="py-2 px-2">
                      <select value={s?.contact_status || ''} onChange={e => updateField(r.customerReference, 'contact_status', e.target.value)} className={`px-1.5 py-1 bg-slate-700 border rounded text-[11px] min-w-[160px] ${s?.contact_status ? 'border-lime-500/50 text-lime-200' : 'border-slate-600 text-slate-400'}`}>
                        <option value="">— niet ingesteld —</option>
                        {DC_CONTACT_STATUS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <select value={s?.customer_response || ''} onChange={e => updateField(r.customerReference, 'customer_response', e.target.value)} className={`px-1.5 py-1 bg-slate-700 border rounded text-[11px] min-w-[180px] ${s?.customer_response ? 'border-lime-500/50 text-lime-200' : 'border-slate-600 text-slate-400'}`}>
                        <option value="">— niet ingesteld —</option>
                        {DC_CUSTOMER_RESPONSE.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <select value={s?.complaint_status || ''} onChange={e => updateField(r.customerReference, 'complaint_status', e.target.value)} className={`px-1.5 py-1 bg-slate-700 border rounded text-[11px] min-w-[180px] ${s?.complaint_status ? 'border-lime-500/50 text-lime-200' : 'border-slate-600 text-slate-400'}`}>
                        <option value="">— niet ingesteld —</option>
                        {DC_COMPLAINT_STATUS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2 text-slate-400 text-[11px] whitespace-nowrap">
                      {s?.updated_at ? (
                        <>
                          <div>{dcFormatDateTime(s.updated_at)}</div>
                          <div className="text-slate-500 truncate max-w-[140px]" title={s.updated_by?.email}>door {s.updated_by?.name || '—'}</div>
                        </>
                      ) : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="py-2 px-2">
                      <button onClick={() => setSelected(r)} className="text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded whitespace-nowrap">Notities &amp; log</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 border-t border-slate-700/50 text-xs text-slate-400">
            <span>Toon <span className="text-slate-200">{pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)}</span> van <span className="text-slate-200">{filtered.length}</span> wanbetalers</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)} title="Eerste pagina" className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">«</button>
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">‹ Vorige</button>
                <span className="px-2">Pagina <span className="text-slate-200">{currentPage}</span> / {totalPages}</span>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">Volgende ›</button>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} title="Laatste pagina" className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed">»</button>
              </div>
            )}
          </div>
        )}
      </Card>

      {selected && (
        <DebtCollectionDetailModal
          row={{ ...selected, status: statusMap[selected.customerReference] || null }}
          token={token}
          onClose={() => setSelected(null)}
          onSaved={updated => setStatusMap(m => ({ ...m, [updated.customer_reference]: { contact_status: updated.contact_status, customer_response: updated.customer_response, complaint_status: updated.complaint_status, notes: updated.notes || '', updated_at: updated.updated_at, updated_by: updated.updated_by } }))}
        />
      )}
      {c360Menu}
    </div>
  );
};

// ─── Payment Plans module ────────────────────────────────────────────────────
// V1 overzicht via /api/payment-plans (Lynx /PaymentPlan/ListAll, gescopet via
// relationReferences). Klik op rij → V2 detail-modal met rijkere data uit
// /api/payment-plans/by-reference/:ref. Customer360Page heeft een eigen sectie
// die V2 rechtstreeks fetcht per customerReference (zie verderop).

const PaymentPlanDetailModal = ({ plan, customerRef, token, onClose }) => {
  // plan = V1 ReadPaymentPlan (uit lijst). We fetchen V2 voor extra status/items/invoices.
  const [v2, setV2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!customerRef) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(`/api/payment-plans/by-reference/${encodeURIComponent(customerRef)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancelled) setV2(j); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerRef, token]);

  // Match het juiste V2 plan op id of reference (er kunnen er meerdere zijn per klant)
  const v2Entry = useMemo(() => {
    const list = v2?.entities || [];
    return list.find(e => e.id === plan.id || e.reference === plan.reference) || list[0] || null;
  }, [v2, plan]);

  const items = v2Entry?.paymentPlanItems || plan.paymentPlanItemCollection || [];
  const invoices = v2Entry?.invoices || [];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
          <div>
            <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2"><Wallet size={18} className="text-lime-400" />Payment plan #{plan.id} · {plan.reference || '—'}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{plan.memo || 'Geen memo'}{customerRef ? ` · klant ${customerRef}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">V1 (PaymentPlan/ListAll)</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Plan ID</div><div className="text-slate-200">{plan.id}</div></div>
              <div><div className="text-xs text-slate-500">Reference</div><div className="text-slate-200">{plan.reference || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Relation ID</div><div className="text-slate-200">{plan.relationId ?? '—'}</div></div>
              <div><div className="text-xs text-slate-500">EAN</div><div className="text-slate-200 font-mono text-xs">{plan.connectionPointGsrn || '—'}</div></div>
              <div><div className="text-xs text-slate-500">Totaal</div><div className="text-slate-200">{bsFormatEuro(plan.totalAmount)}</div></div>
              <div><div className="text-xs text-slate-500">Van</div><div className="text-slate-200">{bsFormatDate(plan.from)}</div></div>
              <div><div className="text-xs text-slate-500">Tot</div><div className="text-slate-200">{bsFormatDate(plan.to)}</div></div>
              <div><div className="text-xs text-slate-500">Aantal items</div><div className="text-slate-200">{plan.paymentPlanItemCollection?.length ?? 0}</div></div>
            </div>
          </div>

          {loading && <p className="text-sm text-slate-400">V2-detail laden…</p>}
          {error && <p className="text-sm text-red-400">V2-fout: {error}</p>}
          {!loading && !error && v2 && v2.valid === false && (
            <p className="text-sm text-slate-500">V2 (GetByRelationReference): {v2.errorMessage || 'geen match'}</p>
          )}
          {v2Entry && (
            <div>
              <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">V2 (PaymentPlan2/GetByRelationReference)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-slate-500">Status</div><div>{v2Entry.status === 'Warning' ? <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30">Warning</span> : v2Entry.status === 'OnTrack' ? <span className="px-2 py-0.5 rounded-full text-xs bg-lime-500/20 text-lime-300 border border-lime-500/30">On Track</span> : <span className="text-slate-400">{v2Entry.status || '—'}</span>}</div></div>
                <div><div className="text-xs text-slate-500">Actief</div><div className="text-slate-200">{v2Entry.isActive ? 'Ja' : 'Nee'}</div></div>
                <div><div className="text-xs text-slate-500">Totaal bedrag</div><div className="text-slate-200">{bsFormatEuro(v2Entry.amount)}</div></div>
                <div><div className="text-xs text-slate-500">Openstaand</div><div className="text-amber-300 font-semibold">{bsFormatEuro(v2Entry.outstandingAmount)}</div></div>
                <div><div className="text-xs text-slate-500">Verwacht openstaand</div><div className="text-slate-200">{bsFormatEuro(v2Entry.expectedOutstandingAmount)}</div></div>
                <div><div className="text-xs text-slate-500">Vervallen (due)</div><div className={`font-semibold ${v2Entry.dueAmount > 0 ? 'text-red-300' : 'text-slate-200'}`}>{bsFormatEuro(v2Entry.dueAmount)}</div></div>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">Items ({items.length})</h4>
            {items.length === 0 ? <p className="text-sm text-slate-500">Geen plan-items.</p> : (
              <div className="overflow-x-auto border border-slate-700/50 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-700/40">
                    <tr>
                      <th className="text-left px-2 py-1.5 text-slate-300">Verwacht</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Effectief</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">Bedrag</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Status</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Invoice ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={it.id || i} className="border-t border-slate-700/40">
                        <td className="px-2 py-1.5 text-slate-300 whitespace-nowrap">{bsFormatDate(it.expectedPaymentDateUtc || it.fromUtc)}</td>
                        <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{bsFormatDate(it.effectivePaymentPlanDateUtc || it.toUtc)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-200 whitespace-nowrap">{bsFormatEuro(it.amount ?? it.paymentAmount)}</td>
                        <td className="px-2 py-1.5 text-slate-400">{it.paymentStatus || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-400">{it.invoiceId ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {invoices.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-lime-400 uppercase mb-3">Facturen onder dit plan ({invoices.length})</h4>
              <div className="overflow-x-auto border border-slate-700/50 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-700/40">
                    <tr>
                      <th className="text-left px-2 py-1.5 text-slate-300">Reference</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Datum</th>
                      <th className="text-left px-2 py-1.5 text-slate-300">Vervaldatum</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">Totaal (incl.)</th>
                      <th className="text-right px-2 py-1.5 text-slate-300">Restant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="border-t border-slate-700/40">
                        <td className="px-2 py-1.5 text-slate-300">{inv.reference || `#${inv.id}`}</td>
                        <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{bsFormatDate(inv.dateUtc)}</td>
                        <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{bsFormatDate(inv.dueUtc)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-200">{bsFormatEuro(inv.totalIncl)}</td>
                        <td className={`px-2 py-1.5 text-right font-semibold ${inv.remainder > 0 ? 'text-amber-300' : 'text-slate-400'}`}>{bsFormatEuro(inv.remainder)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaymentPlansPage = ({ orders = [], token }) => {
  const [data, setData] = useState({ entities: [], totalCount: 0, scoped: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onlyActive, setOnlyActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  useFilterPersistence('paymentplans.filters', undefined,
    { onlyActive, searchTerm },
    (s) => { setOnlyActive(!!s.onlyActive); setSearchTerm(s.searchTerm ?? ''); });
  const [selected, setSelected] = useState(null);

  const load = useMemo(() => async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/api/payment-plans?onlyActive=${onlyActive ? 'true' : 'false'}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      const j = await r.json();
      setData({ entities: j.entities || [], totalCount: j.totalCount || 0, scoped: !!j.scoped });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, onlyActive]);
  useEffect(() => { load(); }, [load]);

  // V1 retourneert connectionPointGsrn maar geen customer naam/ref. We koppelen
  // GSRN → customer (+ ref) via de orders-prop zodat de listing de klant toont.
  const gsrnLookup = useMemo(() => {
    const m = new Map();
    for (const o of orders) {
      const g = (o.connectionPointGsrn || o.connectionPointGSRN || '').trim();
      if (g && !m.has(g)) {
        m.set(g, { customer: o.customer || '', customerReference: o.customerReference || '', company: o.company || '', salesperson: o.salesperson || '' });
      }
    }
    return m;
  }, [orders]);

  const rows = useMemo(() => {
    return (data.entities || []).map(p => {
      const info = p.connectionPointGsrn ? gsrnLookup.get(p.connectionPointGsrn) : null;
      return {
        ...p,
        _customer: info?.customer || '',
        _customerReference: info?.customerReference || '',
        _company: info?.company || '',
        _salesperson: info?.salesperson || '',
        _itemCount: p.paymentPlanItemCollection?.length || 0,
      };
    });
  }, [data.entities, gsrnLookup]);

  const filtered = useMemo(() => {
    if (!searchTerm) return rows;
    const t = searchTerm.toLowerCase();
    return rows.filter(r =>
      String(r.reference || '').toLowerCase().includes(t) ||
      String(r.memo || '').toLowerCase().includes(t) ||
      String(r.connectionPointGsrn || '').toLowerCase().includes(t) ||
      String(r._customer || '').toLowerCase().includes(t) ||
      String(r._customerReference || '').toLowerCase().includes(t) ||
      String(r._company || '').toLowerCase().includes(t)
    );
  }, [rows, searchTerm]);

  const totals = useMemo(() => ({
    count: filtered.length,
    totalAmount: filtered.reduce((s, r) => s + (Number(r.totalAmount) || 0), 0),
    uniqueCustomers: new Set(filtered.map(r => r.relationId).filter(Boolean)).size,
  }), [filtered]);

  // Uniforme pagination — 15 rijen per pagina (zelfde als alle andere tabellen).
  const ppSort = useTableSort();
  const pagination = useTablePagination(sortRows(filtered, ppSort.sortConfig, { periode: p => p.from || '' }));

  return (
    <div className="space-y-4">
      <PageHeader title="Payment Plans" subtitle={`${data.totalCount} plans${data.scoped ? ' (gescopet op je rechten)' : ''}`}>
        <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm rounded-lg cursor-pointer">
          <input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} className="w-4 h-4 accent-lime-500" />
          Alleen actieve
        </label>
        <button onClick={load} disabled={loading} className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg ${loading ? 'bg-slate-700 text-slate-400 cursor-wait' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}>
          {loading ? <><div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />Laden…</> : <><Activity size={14} />Vernieuwen</>}
        </button>
      </PageHeader>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Plans</div><div className="text-2xl font-bold text-slate-100"><Kengetal>{totals.count}</Kengetal></div></div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Totaal bedrag</div><div className="text-2xl font-bold text-lime-400"><Kengetal>{bsFormatEuro(totals.totalAmount)}</Kengetal></div></div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4"><div className="text-xs text-slate-500 mb-1">Unieke klanten</div><div className="text-2xl font-bold text-slate-100"><Kengetal>{totals.uniqueCustomers}</Kengetal></div></div>
      </div>

      <Card className="!p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-[280px] shrink-0">
            <label className="block text-xs text-slate-400 mb-1">Zoeken</label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2 text-slate-500" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Reference, memo, EAN, klant…" className="w-full pl-7 pr-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" />
            </div>
          </div>
        </div>
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm resp-card">
            <thead>
              <tr className="border-b border-slate-700/50 bg-slate-800/40">
                <SortHeader label="Plan ref" k="reference" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Klant" k="_customer" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Klant ref" k="_customerReference" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Energielabel" k="_company" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="EAN" k="connectionPointGsrn" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Totaal" k="totalAmount" align="right" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Periode" k="periode" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Items" k="_itemCount" align="right" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
                <SortHeader label="Memo" k="memo" sortConfig={ppSort.sortConfig} onSort={ppSort.handleSort} thClass="py-2.5 px-2 text-xs" />
              </tr>
            </thead>
            <tbody>
              {loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-slate-500 text-sm">Laden…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-slate-500 text-sm">Geen payment plans binnen je filters / rechten.</td></tr>
              )}
              {pagination.pageItems.map(p => (
                <tr key={p.id} onClick={() => setSelected(p)} className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer">
                  <td className="py-2 px-2 text-slate-100 text-xs">{p.reference || `#${p.id}`}</td>
                  <td className="py-2 px-2 text-slate-200 text-xs truncate max-w-[180px]" title={p._customer}>{p._customer || <span className="text-slate-500">—</span>}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{p._customerReference || '—'}</td>
                  <td className="py-2 px-2 text-slate-300 text-xs">{p._company || '—'}</td>
                  <td className="py-2 px-2 text-slate-500 text-xs font-mono">{p.connectionPointGsrn || '—'}</td>
                  <td className="py-2 px-2 text-right text-lime-400 text-xs font-medium whitespace-nowrap">{bsFormatEuro(p.totalAmount)}</td>
                  <td className="py-2 px-2 text-slate-400 text-xs whitespace-nowrap">{bsFormatDate(p.from)} → {bsFormatDate(p.to)}</td>
                  <td className="py-2 px-2 text-right text-slate-300 text-xs">{p._itemCount}</td>
                  <td className="py-2 px-2 text-slate-500 text-xs truncate max-w-[200px]" title={p.memo}>{p.memo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TableFooter
          pagination={pagination}
          onExport={() => exportRowsToCsv(`payment-plans-${new Date().toISOString().slice(0, 10)}.csv`, [
            { header: 'Plan ref', get: r => r.reference || `#${r.id}` },
            { header: 'Plan ID', get: r => r.id },
            { header: 'Klant', get: r => r._customer },
            { header: 'Klantref', get: r => r._customerReference },
            { header: 'Energielabel', get: r => r._company },
            { header: 'Verkoper', get: r => r._salesperson },
            { header: 'EAN', get: r => r.connectionPointGsrn, text: true },
            { header: 'Totaalbedrag (€)', get: r => r.totalAmount },
            { header: 'Van', get: r => r.from },
            { header: 'Tot', get: r => r.to },
            { header: 'Aantal items', get: r => r._itemCount },
            { header: 'Memo', get: r => r.memo },
            { header: 'Relation ID', get: r => r.relationId },
          ], filtered)}
          exportLabel={`Excel (${filtered.length})`}
        />
      </Card>

      {selected && (
        <PaymentPlanDetailModal
          plan={selected}
          customerRef={selected._customerReference}
          token={token}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

const NAV_ITEMS = [{ id: 'market', label: 'Market Prices', icon: Activity }, { id: 'customer360', label: 'Customer 360°', icon: Compass }, { id: 'metering', label: 'Metering', icon: Gauge }, { id: 'orders', label: 'Orders', icon: ClipboardList }, { id: 'invoices', label: 'Invoices', icon: Receipt }, { id: 'sales', label: 'Sales', icon: TrendingUp }, { id: 'portfolio', label: 'Portfolio Insights', icon: Activity }, { id: 'drop-loss', label: 'Retention', icon: UserMinus }, { id: 'revenue', label: 'Revenue', icon: DollarSign }, { id: 'revenue-calculator', label: 'Revenue Calculator', icon: Hash }, { id: 'helpdesk', label: 'Helpdesk', icon: LifeBuoy }, { id: 'billing', label: 'Aging', icon: FileText }, { id: 'debt-collection', label: 'Debt Collection', icon: Coins }, { id: 'payment-plans', label: 'Payment Plans', icon: Wallet }, { id: 'billshock', label: 'Bill Shock', icon: AlertCircle }, { id: 'referrals', label: 'Referrals', icon: Share2 }, { id: 'learning', label: 'Learning', icon: BookOpen }, { id: 'optimise', label: 'Optimise', icon: Lightbulb, always: true }];

// Deep-link vanuit een PWA-shortcut (of gedeelde link): /?page=<moduleId> opent
// meteen die module. De id wordt gevalideerd tegen NAV_ITEMS; onbekende waarden
// worden genegeerd (→ standaard startpagina). RBAC/module-zichtbaarheid wordt
// dáárna afgedwongen door de landing-guard: een pagina die de gebruiker niet mag
// zien valt terug op zijn eerste zichtbare module (geen lek, geen fout).
// Beheerpagina's buiten NAV_ITEMS die tóch als startpagina mogen (gebruikt door
// de terugsprong na impersonatie). Hun eigen render-gating bepaalt de toegang.
const SPECIAL_START_PAGES = new Set(['users']);
const startPageId = () => {
  try {
    const p = new URLSearchParams(window.location.search).get('page');
    if (!p) return null;
    // In de device-preview tonen we EXACT de pagina waar de gebruiker op staat —
    // ook beheerpagina's buiten NAV_ITEMS. Het frame draait op dezelfde origin met
    // dezelfde sessie, dus de render-gating van die pagina's blijft gelden.
    if (IS_PREVIEW_FRAME) return p;
    return NAV_ITEMS.some((i) => i.id === p) || SPECIAL_START_PAGES.has(p) ? p : null;
  } catch { return null; }
};

// ── Doorklikken naar Customer 360 / Metering in een NIEUW tabblad ────────────
// Het rechtsklik-menu opent die twee pagina's in een eigen tabblad, zodat de lijst
// waar je op klikte onaangeroerd blijft staan (filters, pagina, scrollpositie).
// Een nieuw tabblad is een verse app-load, dus kan de selectie niet via React-state
// mee — ze reist in de URL: /?page=<pagina>&sel=<type>:<waarde>[&ref=<klantnummer>][&lbl=<label>].
// Het label is enkel weergave; ontbreekt het, dan valt de pagina terug op de waarde.
//
// `ref` = het klantnummer, en dat is geen luxe. Komt de selectie van een FACTUURrij,
// dan is de klantnaam bij een zakelijke klant de ONDERNEMING ("VIKI'S CHOICE")
// terwijl de order de contactPERSOON draagt ("Viki Reybrouck") — op naam alleen
// vindt Customer 360 dan niets, terwijl dezelfde klant via het zoekveld wél data
// toont. Naam en klantnummer worden GEUNIEERD, nooit vervangen: één naam kan meer
// dan één klantnummer dragen, en niet elke order heeft er een.
const SELECTION_PARAM = 'sel';
const SELECTION_LABEL_PARAM = 'lbl';
const SELECTION_REF_PARAM = 'ref';
const SELECTION_GSRNS_PARAM = 'gsrns';   // EAN(s) van de aangeklikte order/factuur (Metering)
const targetPageUrl = (target, dest) => {
  const p = new URLSearchParams();
  p.set('page', dest === 'metering' ? 'metering' : 'customer360');
  p.set(SELECTION_PARAM, `${target.type || 'customer'}:${target.value ?? ''}`);
  // Enkel bij een klant-selectie: bij een GSRN is de sleutel al uniek.
  if (target.ref && (target.type || 'customer') === 'customer') p.set(SELECTION_REF_PARAM, target.ref);
  if (target.label && target.label !== target.value) p.set(SELECTION_LABEL_PARAM, target.label);
  // De EAN(s) van de rij (order/factuur): Metering vinkt in het nieuwe tabblad
  // enkel díe aan. Zie klantTarget.
  if (Array.isArray(target.gsrns) && target.gsrns.length) p.set(SELECTION_GSRNS_PARAM, target.gsrns.join(','));
  return `${window.location.pathname}?${p.toString()}`;
};
// De selectie uit de URL van een verse load. Levert hetzelfde object als de
// prefill die via React-state binnenkomt: { type, value, ref, label }.
const startSelection = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    const raw = q.get(SELECTION_PARAM);
    if (!raw) return null;
    const i = raw.indexOf(':');
    if (i < 1) return null;
    const type = raw.slice(0, i);
    const value = raw.slice(i + 1);
    if (!value || (type !== 'customer' && type !== 'gsrn')) return null;
    const ref = (q.get(SELECTION_REF_PARAM) || '').trim();
    const gsrns = (q.get(SELECTION_GSRNS_PARAM) || '').split(',').map((s) => s.trim()).filter(Boolean);
    return { type, value, ref: type === 'customer' ? ref : '', label: q.get(SELECTION_LABEL_PARAM) || value, ...(gsrns.length ? { gsrns } : {}) };
  } catch { return null; }
};
// Onthoud de laatst geopende pagina voor de duur van de SESSIE (sessieOpslag.js)
// zodat een refresh op DEZELFDE pagina blijft; een nieuwe sessie start weer op
// Market Prices. RBAC/zichtbaarheid wordt daarna alsnog door de landing-guard
// afgedwongen.
const savedActivePage = () => leesSessieWaarde(ACTIVE_PAGE_KEY);

// Op een telefoon start de app ALTIJD op Market Prices: dat is het scherm dat
// onderweg telt. De onthouden pagina wordt daar dus bewust genegeerd — op desktop
// blijft die wél werken, want daar is doorwerken op dezelfde pagina de norm.
// Een EXPLICIETE deep-link (?page=… of een ticket-hash) wint hier nog steeds van:
// dat is een bewuste keuze van de gebruiker en geen automatisch herstel.
// Grens = 768px, dezelfde `md`-breekpunt als de rest van het platform.
const isMobileViewport = () => {
  try { return window.matchMedia('(max-width: 767px)').matches; } catch { return false; }
};

// ─── Helpdesk ────────────────────────────────────────────────────────────────
// Kleuren via de .hd-pill-*-klassen (thema-blok C in App.jsx): per thema een
// eigen palet, want de generieke -300-tinten zijn op wit onleesbaar.
const HD_ARB = {
  partner: { label: 'Partner', cls: 'hd-pill hd-pill-amber' },
  customer: { label: 'Klant', cls: 'hd-pill hd-pill-sky' },
  energy_together: { label: 'Energy Together', cls: 'hd-pill hd-pill-violet' },
};
// Statuspil: de toon volgt de naam van de status (die komt uit ET en is vrij),
// met de sluitende toets op "gesloten" als de naam niets zegt.
const hdStageToon = (stage, closed) => {
  const s = String(stage || '').toLowerCase();
  if (closed || /afgehandeld|gesloten|closed|done/.test(s)) return 'emerald';
  if (/nieuw|new/.test(s)) return 'indigo';
  if (/heropend|reopen/.test(s)) return 'amber';
  if (/wacht|pending|on hold/.test(s)) return 'violet';
  if (/behandeling|progress|bezig/.test(s)) return 'sky';
  return 'slate';
};
// Tags (onderwerpcategorieën uit het bronsysteem): null = nog niet bekend.
const hdTagPillen = (tags, { klein = true } = {}) => (Array.isArray(tags) && tags.length
  ? tags.map((naam) => <span key={naam} className={`hd-pill hd-pill-slate ${klein ? '' : 'text-xs'}`} title="Tag (onderwerpcategorie)">{naam}</span>)
  : null);
const hdStageBadge = (t, extra = '') => <span className={`hd-pill hd-pill-${hdStageToon(t && t.stage_name, t && t.stage_is_closed)} ${extra}`}>{(t && t.stage_name) || '—'}</span>;
// Stipkleur bij elke toon (kengetallen naast de pillen) — het vaste grafiekpalet.
const HD_TOON_KLEUR = { indigo: '#6366F1', sky: '#0EA5E9', amber: '#F59E0B', violet: '#8B5CF6', emerald: '#10B981', rose: '#F43F5E', slate: '#94A3B8' };
// Tijdstippen ALTIJD in Belgische tijd (Europe/Brussels, zomer-/wintertijd via
// Intl). Het bronsysteem levert ze zonder tijdzone en bedoelt UTC; `new Date()`
// las ze als lokale tijd, waardoor elk ticket 1 of 2 uur te vroeg stond. Alle
// helpdeskdatums lopen daarom via src/helpdeskDatum.js: hdFmt om te tonen,
// hdTijdMs om te rekenen en te sorteren.
const hdFmt = (s) => hdFmtBrussel(s);
// ET geeft veel relatievelden als Odoo many2one terug ({id,name} of [id,name]).
// Veilig naar tekst omzetten zodat React nooit een object als child krijgt.
const hdText = (v) => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.length > 1 ? String(v[1]) : String(v[0] ?? '');
  if (typeof v === 'object') return String(v.name ?? v.display_name ?? v.label ?? v.id ?? '');
  return String(v);
};
const hdArbBadge = (v, showBrand = true) => { const a = HD_ARB[v]; if (!a) return <span className="text-slate-500 text-xs">—</span>; const label = (v === 'energy_together' && !showBrand) ? 'Support' : a.label; return <span className={a.cls}>{label}</span>; };
const hdApi = (token) => ({
  get: async (p) => { const r = await fetch(`/api/helpdesk${p}`, { headers: { Authorization: `Bearer ${token}` } }); const b = await r.json().catch(() => null); if (!r.ok) throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`); return b; },
  // Fout: de melding van de server, mét het detail van het bronsysteem erachter
  // (bv. "No matching partner user") — anders is "Kon ticket niet bijwerken" niet
  // te onderscheiden van een echte storing.
  send: async (p, method, body) => { const r = await fetch(`/api/helpdesk${p}`, { method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) }); const b = await r.json().catch(() => null); if (!r.ok) { const d = b && b.detail ? String(typeof b.detail === 'string' ? b.detail : JSON.stringify(b.detail)).replace(/^\{"detail":\s*"(.*)"\}$/, '$1') : ''; throw new Error(((b && b.error) || `HTTP ${r.status}`) + (d && d !== (b && b.error) ? ` — ${d}` : '')); } return b; },
});
// ── Live-verversing ──────────────────────────────────────────────────────────
// De Helpdesk staat een hele werkdag open: niemand mag F5 nodig hebben. Lijst en
// ticket vragen daarom op een vast ritme of er iets wijzigde (GET /wijzigingen,
// GET /tickets/:id/stand — goedkoop, meestal een leeg antwoord) en halen enkel
// dán data op. Alleen in een ZICHTBAAR tabblad: een Helpdesk op de achtergrond
// vraagt niets, en haalt bij het terugkeren meteen in wat hij miste.
const HD_LIJST_POLL_MS = 30000;
const HD_TICKET_POLL_MS = 20000;
const useHdVerversing = (fn, ms, actief = true) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!actief) return undefined;
    let laatste = Date.now();
    const zichtbaar = () => { try { return document.visibilityState === 'visible'; } catch { return true; } };
    const tik = () => { if (!zichtbaar()) return; laatste = Date.now(); fnRef.current(); };
    const timer = setInterval(tik, ms);
    // Terug in beeld (ander tabblad, ander venster, laptop uit slaap): niet op de
    // volgende tik wachten.
    const terug = () => { if (zichtbaar() && Date.now() - laatste > 5000) tik(); };
    document.addEventListener('visibilitychange', terug);
    window.addEventListener('focus', terug);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', terug); window.removeEventListener('focus', terug); };
  }, [ms, actief]);
};
// Compacte verversknop (zoals in Odoo): ververst enkel de ticketdata, nooit de
// pagina. Draait het icoon, dan loopt er een ophaal; het uur ernaast is het
// laatste moment waarop de data gecontroleerd is.
const HdVerversKnop = ({ onClick, bezig, om, titel = 'Tickets vernieuwen', klein = false }) => (
  <span className="inline-flex items-center gap-1.5 shrink-0">
    {om && <span className={`hidden sm:inline tabular-nums text-slate-500 ${klein ? 'text-[11px]' : 'text-xs'}`} title="Laatste controle op nieuwe of gewijzigde tickets (automatisch)">bijgewerkt {hdUurBrussel(om)}</span>}
    <button type="button" onClick={onClick} disabled={bezig} title={titel} aria-label={titel}
      className={`inline-flex items-center justify-center rounded-lg border border-slate-600/70 bg-slate-700/60 hover:bg-slate-600 text-slate-200 disabled:opacity-70 ${klein ? 'w-6 h-6' : 'w-9 h-9'}`}>
      <RefreshCw size={klein ? 12 : 15} className={bezig ? 'animate-spin' : ''} />
    </button>
  </span>
);
const hdFileToB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });

// Deep-link voor ticketdetail: aanspreekbaar via de URL-hash (#helpdesk/ticket/<id>)
// zodat CTRL/⌘+klik of middenklik op een ticketrij het ticket in een nieuwe
// browser-tab kan openen — de app leest de hash bij laden. Het JWT-token staat in
// localStorage, dus de nieuwe tab is automatisch ingelogd.
const ticketHashId = () => { const m = /^#?helpdesk\/ticket\/(\d+)/.exec(window.location.hash || ''); return m ? Number(m[1]) : null; };
const ticketTabUrl = (id) => `${window.location.pathname}${window.location.search}#helpdesk/ticket/${id}`;
// Openen in een nieuw venster: GEEN feature-string meegeven. `window.open(url,
// '_blank', 'noopener')` maakt er in Chrome een POPUP van — een kaal venster zonder
// tabbladen of adresbalk, dat er precies uitziet als de geïnstalleerde app. Vandaar
// de klacht "in de browser opent hij de app". Zonder feature-string is het gewoon
// een nieuw tabblad in de browser, en binnen de geïnstalleerde app een nieuw
// app-venster — dus in beide gevallen wat je verwacht. `opener` zetten we zelf op
// null; dat is wat 'noopener' moest doen.
// Én dit is de reden dat een gewone <a target="_blank" rel="noopener"> NIET volstaat:
// Chrome (139+, "navigation capturing") vangt elke navigatie die een nieuw frame
// maakt ZONDER opener op met de geïnstalleerde app — vanuit de browser opent zo'n
// link dus in de app. Met een opener (window.open zonder noopener) niet.
// Startte DEZE tab op een ticket-deeplink? Eén keer bij het laden bepaald: het gaat
// over hoe de tab geopend is, niet over waar de gebruiker daarna heen navigeert.
// Zie `ticketTabStil` in PartnerDashboard — zo'n tab laadt niet het hele platform.
const IS_TICKET_DEEPLINK_START = typeof window !== 'undefined' && ticketHashId() != null;
const openTicketInNewTab = (id) => { const w = window.open(ticketTabUrl(id), '_blank'); if (w) w.opener = null; };
// true wanneer een muisklik in een nieuwe tab geopend moet worden (Ctrl/⌘+klik,
// middenklik of shift+klik) — net als de native nieuwe-tab-gebaren op een link.
const isNewTabClick = (e) => e.button === 1 || e.ctrlKey || e.metaKey || e.shiftKey;

// Logboek van een ticket: wie deed wat, wanneer. Elke soort krijgt zijn eigen
// icoon en kleur, zodat je in een lange draad meteen ziet waar de statuswissels
// en de verstuurde mails zitten.
const HD_LOG_SOORT = {
  aangemaakt: { icon: Plus, cls: 'text-slate-400 bg-slate-700/50' },
  status: { icon: RefreshCw, cls: 'text-blue-300 bg-blue-500/15' },
  bericht: { icon: MessageSquare, cls: 'text-slate-300 bg-slate-700/50' },
  bijlage: { icon: Paperclip, cls: 'text-slate-300 bg-slate-700/50' },
  escalatie: { icon: ArrowUpCircle, cls: 'text-purple-300 bg-purple-500/15' },
  mail: { icon: Mail, cls: 'text-lime-300 bg-lime-500/15' },
  overig: { icon: Activity, cls: 'text-slate-400 bg-slate-700/50' },
};

const HelpdeskTicketLog = ({ entries, compact = false }) => {
  if (!entries) return <div className="text-slate-500 text-sm">Logboek laden…</div>;
  if (!entries.length) return <div className="text-slate-500 text-sm">Nog geen geregistreerde acties.</div>;
  // Compact (in een popup, bv. Customer 360) scrolt het logboek in een vast vak;
  // op de volledige ticketpagina loopt het gewoon mee met de kolom.
  return (
    <div className={`space-y-2 pr-1 ${compact ? 'max-h-64 overflow-y-auto' : ''}`}>
      {entries.map((e, i) => {
        const soort = HD_LOG_SOORT[e.kind] || HD_LOG_SOORT.overig;
        const Icoon = soort.icon;
        return (
          <div key={i} className="flex items-start gap-2.5 text-sm">
            <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${soort.cls}`}><Icoon size={13} className="shrink-0 w-3.5" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-slate-200 break-words">{e.tekst}</div>
              <div className="text-xs text-slate-500">{e.actor}{e.at ? ` · ${hdFmt(e.at)}` : ''}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// "2 u geleden" voor de tijdlijn; het exacte tijdstip staat in de tooltip. Ouder
// dan een week is een datum duidelijker dan "23 dagen geleden".
const hdGeleden = (at) => {
  const ms = hdTijdMs(at);
  if (!Number.isFinite(ms)) return '';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return 'zonet';
  if (s < 3600) return `${Math.floor(s / 60)} min geleden`;
  if (s < 86400) return `${Math.floor(s / 3600)} u geleden`;
  const d = Math.floor(s / 86400);
  if (d < 7) return d === 1 ? 'gisteren' : `${d} dagen geleden`;
  return hdFmt(at).slice(0, 10);
};
// Het logboek als TIJDLIJN in de rechterkolom van een ticket (21-09-2026; stond
// vroeger verstopt in het derde tabblad "Activiteit"). Nieuwste bovenaan, één
// doorlopende lijn met per soort een eigen icoon en kleur (`icoon` verfijnt `kind`:
// tags, toewijzing, automatische regel, interne notitie, bericht van de klant —
// zie backend util/helpdeskLog.js). Eerst de laatste zes; de rest achter een knop,
// zodat de kolom kort blijft op een ticket met een lange geschiedenis.
const HD_TIJDLIJN_SOORT = {
  aangemaakt: { icon: Plus, kleur: '#64748b' },
  status: { icon: RefreshCw, kleur: '#3b82f6' },
  bericht: { icon: MessageSquare, kleur: '#64748b' },
  klant: { icon: User, kleur: '#2563eb' },
  inkomend: { icon: Inbox, kleur: '#0891b2' },   // mail van buiten, niet van het klantadres
  notitie: { icon: StickyNote, kleur: '#d97706' },
  mail: { icon: Mail, kleur: '#65a30d' },
  bijlage: { icon: Paperclip, kleur: '#64748b' },
  escalatie: { icon: ArrowUpCircle, kleur: '#9333ea' },
  tags: { icon: Tag, kleur: '#0891b2' },
  toewijzing: { icon: UserCog, kleur: '#0891b2' },
  auto: { icon: Zap, kleur: '#7c3aed' },
  klantwissel: { icon: Pencil, kleur: '#db2777' },
  ai: { icon: Wand2, kleur: '#7c3aed' },
  overig: { icon: Activity, kleur: '#64748b' },
};
const HD_TIJDLIJN_KORT = 6;
// Eerdere tickets van dezelfde klant (vraag 08-10-2026), in de zijkolom onder het
// logboek: de vijf recentste met ref, onderwerp en status; "Alle tonen" klapt de
// rest open. Dezelfde bron als Customer 360 → Tickets (/customer-tickets: op
// klantnummer, met de naam voor tickets zonder nummer, binnen de rol-scope).
// Een klik opent het ticket in dit scherm (onOpen), het huidige ticket staat er niet in.
const HdKlantTickets = ({ token, ticket, onOpen }) => {
  const ref = String((ticket && ticket.customer && ticket.customer.reference) || '').trim();
  const naam = String((ticket && ticket.customer && ticket.customer.name) || '').trim();
  const [lijst, setLijst] = useState(null);     // null = laden, [] = geen
  const [alle, setAlle] = useState(false);
  useEffect(() => {
    if (!token || (!ref && !naam)) { setLijst([]); return undefined; }
    let actief = true;
    const qs = new URLSearchParams();
    if (ref) qs.set('refs', ref);
    if (naam) qs.append('name', naam);
    hdApi(token).get(`/customer-tickets?${qs.toString()}`)
      .then((d) => { if (!actief) return; const items = ((d && d.items) || []).filter((x) => String(x.id) !== String(ticket.id)); items.sort((a, b) => String(b.create_date || '').localeCompare(String(a.create_date || ''))); setLijst(items); })
      .catch(() => { if (actief) setLijst([]); });
    return () => { actief = false; };
  }, [token, ref, naam, ticket && ticket.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const getoond = lijst ? (alle ? lijst : lijst.slice(0, 5)) : [];
  return (
    <div className="min-w-0 bg-slate-800/40 border border-slate-700/50 rounded-xl px-3.5 py-3 text-sm">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><LifeBuoy size={12} className="text-slate-500" />Eerdere tickets van de klant{lijst ? ` (${lijst.length})` : ''}</span>
      </div>
      {lijst === null && <div className="text-xs text-slate-500">Laden…</div>}
      {lijst && lijst.length === 0 && <div className="text-xs text-slate-500">Geen andere tickets van deze klant.</div>}
      {getoond.length > 0 && (
        <div className="flex flex-col divide-y divide-slate-700/40">
          {getoond.map((x) => (
            <button key={x.id} type="button" onClick={() => onOpen(x.id)} title={`Ticket #${x.id} openen`}
              className="w-full text-left py-1.5 flex items-start gap-2 hover:bg-slate-700/30 rounded px-1 -mx-1">
              <span className="font-mono text-[11px] text-slate-400 shrink-0 pt-0.5">#{x.id}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-slate-200 truncate" title={x.subject}>{x.subject || '—'}</span>
                <span className="block text-[10px] text-slate-500">{hdFmt(x.create_date)}</span>
              </span>
              {hdStageBadge(x, 'shrink-0 text-[10px]')}
            </button>
          ))}
        </div>
      )}
      {lijst && lijst.length > 5 && (
        <button type="button" onClick={() => setAlle((v) => !v)} className="mt-2 text-[11px] text-lime-400 hover:text-lime-300">
          {alle ? 'Enkel de 5 recentste tonen' : `Alle ${lijst.length} tickets tonen`}
        </button>
      )}
    </div>
  );
};

const HdLogTijdlijn = ({ entries }) => {
  const [alles, setAlles] = useState(false);
  if (!entries) return <div className="text-slate-500 text-xs">Logboek laden…</div>;
  if (!entries.length) return <div className="text-slate-500 text-xs">Nog geen geregistreerde acties.</div>;
  const nieuwsteEerst = [...entries].reverse();
  const lijst = alles ? nieuwsteEerst : nieuwsteEerst.slice(0, HD_TIJDLIJN_KORT);
  return (
    <div>
      <ol className="relative">
        {lijst.map((e, i) => {
          const soort = HD_TIJDLIJN_SOORT[e.icoon] || HD_TIJDLIJN_SOORT[e.kind] || HD_TIJDLIJN_SOORT.overig;
          const Icoon = soort.icon;
          const laatste = i === lijst.length - 1;
          return (
            <li key={`${e.at || 'x'}-${i}`} className="relative flex gap-2.5 pb-3 last:pb-0">
              {!laatste && <span aria-hidden className="absolute left-[11px] top-6 bottom-0 w-px" style={{ background: 'rgba(148,163,184,.45)' }} />}
              <span className="relative z-[1] shrink-0 w-[23px] h-[23px] rounded-full flex items-center justify-center" style={{ color: soort.kleur, background: `${soort.kleur}22`, boxShadow: `inset 0 0 0 1px ${soort.kleur}44` }}><Icoon size={12} className="shrink-0" /></span>
              <div className="min-w-0 flex-1 -mt-0.5">
                <div className="text-xs text-slate-200 break-words leading-snug">{e.tekst}</div>
                <div className="text-[11px] text-slate-500 leading-snug" title={e.at ? hdFmt(e.at) : undefined}>{e.actor}{e.at ? ` · ${hdGeleden(e.at)}` : ''}</div>
              </div>
            </li>
          );
        })}
      </ol>
      {entries.length > HD_TIJDLIJN_KORT && (
        <button type="button" onClick={() => setAlles((v) => !v)} className="mt-2 text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
          {alles ? <><ChevronUp size={12} />Minder tonen</> : <><ChevronDown size={12} />Toon alles ({entries.length})</>}
        </button>
      )}
    </div>
  );
};

// ── Mailen naar de klant vanaf een ticket ───────────────────────────────────
// De agent schrijft vrije tekst of start van een sjabloon uit Mailsjablonen (enkel
// de sjablonen die voor de Helpdesk zijn vrijgegeven én bij het energielabel/
// verkoopteam van dit ticket horen). De historiek van het ticket gaat er standaard
// onder mee; interne berichten (partner↔support) blijven eruit, dat filtert de server.
//
// De tekst mag gewone tekst zijn: zonder HTML-tags maken we er alsnog alinea's van,
// anders zou elke regelovergang in de mail verdwijnen. Kiest iemand een sjabloon,
// dan staat er HTML in het veld en laten we die ongemoeid.
const bevatHtml = (s) => /<[a-z][\s\S]*>/i.test(String(s || ''));
const hdEscape = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function hdTekstNaarHtml(tekst) {
  const s = String(tekst || '');
  if (bevatHtml(s)) return s;
  return s.split(/\n{2,}/).map((alinea) => `<p style="margin:0 0 12px;">${hdEscape(alinea).replace(/\n/g, '<br/>')}</p>`).join('');
}
// HTML → platte tekst voor het interne tekstveld (standaardtekst, kennisbank of
// AI-voorstel in het interne kanaal). Via de DOM, zodat entiteiten en <br> kloppen.
function hdHtmlNaarTekst(html) {
  const el = document.createElement('div');
  el.innerHTML = String(html || '').replace(/<\/p>\s*<p/gi, '</p>\n\n<p').replace(/<br\s*\/?>/gi, '\n').replace(/<\/li>/gi, '\n');
  return (el.innerText || el.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}
// De interne markering van klantgegevens in een AI-voorstel (<mark class="ai-klant">)
// gaat er vóór het versturen af; de server doet hetzelfde als vangnet.
const hdStripAiMarks = (html) => String(html || '').replace(/<mark\b[^>]*\bai-klant\b[^>]*>([\s\S]*?)<\/mark>/gi, '$1');

// Bewerkbare weergave van het mailbericht: de HTML zoals de klant ze ongeveer
// ziet (wit, mail-lettertype), rechtstreeks aanpasbaar. Ongecontroleerd: de
// inhoud wordt alleen vanuit `html` gezet wanneer `versie` verandert (sjabloon
// geladen, gewisseld vanuit de HTML-weergave) — anders zou elke toetsaanslag de
// cursor naar het begin gooien. `onSync` geeft de door de browser genormaliseerde
// HTML terug, zodat "is de tekst nog het sjabloon?" blijft kloppen.
const HdVisueleEditor = ({ html, versie, onChange, onSync, editorRef, laag = false }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = html || '';
    if (onSync) onSync(ref.current.innerHTML);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versie]);
  // Handvat voor de composer: HTML invoegen op de cursor (standaardtekst,
  // kennisbankartikel). Staat de cursor niet in de editor, dan komt het onderaan.
  if (editorRef) {
    editorRef.current = {
      voegHtml: (fragment) => {
        const el = ref.current; if (!el || !fragment) return;
        const sel = window.getSelection();
        const binnen = sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer);
        // Het blok waar de vaste groet begint (src/mailGroet.js): staat de cursor
        // dáárin of erna, dan komt de tekst toch vóór de groet.
        const kinderen = Array.from(el.children);
        const groetBlok = kinderen.find((k, i) => slotPositie(k.outerHTML) === 0 && kinderen.slice(i).map((x) => x.textContent).join(' ').trim().length <= 260) || null;
        let cursorBlok = binnen ? sel.getRangeAt(0).commonAncestorContainer : null;
        while (cursorBlok && cursorBlok.parentNode !== el) cursorBlok = cursorBlok.parentNode;
        const naGroet = !!(groetBlok && cursorBlok && (cursorBlok === groetBlok || (groetBlok.compareDocumentPosition(cursorBlok) & Node.DOCUMENT_POSITION_FOLLOWING)));
        if (binnen && naGroet) { groetBlok.insertAdjacentHTML('beforebegin', fragment); }
        else if (binnen) { el.focus(); document.execCommand('insertHTML', false, fragment); }
        // Niet op de cursor: vóór de vaste groet (src/mailGroet.js), zodat een
        // standaardtekst of artikel nooit ónder "Met energieke groeten" belandt.
        else { el.innerHTML = voegVoorGroet(el.innerHTML, fragment, '<p><br></p>'); }
        onChange(el.innerHTML);
      },
    };
  }
  return (
    <div className="border border-slate-600/50 rounded-lg overflow-hidden bg-slate-700/50">
      <OpmaakBalk editorRef={ref} onChange={onChange} />
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={() => onChange(ref.current.innerHTML)} onBlur={() => onChange(ref.current.innerHTML)}
        onPaste={(e) => hdPlakAfbeeldingen(e, ref, onChange)} onDrop={(e) => hdPlakAfbeeldingen(e, ref, onChange)} onDragOver={(e) => { if (afbeeldingenUitEvent(e).length || (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files'))) e.preventDefault(); }}
        className={`hd-html hd-mail-canvas ${laag ? 'min-h-[150px]' : 'min-h-[220px]'} max-h-[60vh] overflow-y-auto px-5 py-4 text-sm focus:outline-none`} />
    </div>
  );
};
// Geplakte of gesleepte afbeeldingen in de mail-editor: verkleind (max 1600 px,
// zwaar → JPEG) en dan op de cursor ingevoegd. Zonder dit zet de browser een
// schermafdruk van enkele MB als data-URL in de HTML en liep het bericht tegen
// "Het bericht is te lang" (30-10-2026). De server haalt ze er bij het versturen
// weer uit en stuurt ze als inline bijlage mee (src/plakAfbeelding.js).
const HD_PLAK_MAX_TOTAAL = 15 * 1024 * 1024;
async function hdPlakAfbeeldingen(e, ref, onChange) {
  const bestanden = afbeeldingenUitEvent(e);
  if (!bestanden.length) return;            // gewone tekst: de browser doet zijn werk
  e.preventDefault();
  const el = ref.current; if (!el) return;
  el.focus();
  let totaal = inlineBytesIn(el.innerHTML);
  for (const f of bestanden) {
    try {
      const beeld = await verkleinAfbeelding(f);
      if (beeld.bytes > PLAK_MAX_PER_BEELD) { toast.error(`Afbeelding ${f.name || ''} is te groot (max ${Math.round(PLAK_MAX_PER_BEELD / 1024 / 1024)} MB)`.replace('  ', ' ')); continue; }
      if (totaal + beeld.bytes > HD_PLAK_MAX_TOTAAL) { toast.error(`De afbeeldingen in het bericht zijn samen te groot (max ${Math.round(HD_PLAK_MAX_TOTAAL / 1024 / 1024)} MB)`); break; }
      totaal += beeld.bytes;
      const html = afbeeldingHtml(beeld);
      const sel = window.getSelection();
      const binnen = sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer);
      if (binnen) document.execCommand('insertHTML', false, html);
      else el.insertAdjacentHTML('beforeend', html);
    } catch (err) { toast.error(`Afbeelding niet ingevoegd: ${err.message}`); }
  }
  onChange(el.innerHTML);
}

// Eén antwoordblok voor de drie kanalen van een ticket (vraag van de partners,
// 09-2026 — vervangt de losse knop "Mailen naar klant" én het oude tekstvak):
//   mail             → een echte e-mail in de huisstijl (sjabloon, weergave/HTML,
//                      historiek, preview), daarna als publiek bericht op het ticket;
//   public           → een bericht op het ticket dat de klant te zien krijgt;
//   partner_internal → een intern bericht tussen partner en support.
// De sjabloonkiezer staat op dezelfde plek in elk kanaal, maar toont per kanaal
// een andere lijst: mailsjablonen (module helpdesk), snelle antwoorden naar de
// klant (helpdesk-bericht) of interne snelle antwoorden (helpdesk-intern).
// Twee kanalen (beleid 09-2026): de klant krijgt ALTIJD een mail in de huisstijl
// van zijn energielabel, vanaf het geverifieerde verzendadres — nooit een kaal
// ticketbericht vanuit het bronsysteem. Zonder huisstijl of verzenddomein kan
// er dus niet gemaild worden; de server weigert dat ook (mailBlokkade).
// Een kanaal bestaat alleen voor wie het recht heeft: "mail" voor Mail naar klant,
// "message" voor de interne notitie (rechten per gebruiker, zie HELPDESK_CAPS).
const HD_KANALEN = (showBrand, caps = null) => [
  { id: 'mail', recht: 'mail', label: 'Mail naar klant', Icoon: Mail, tip: showBrand ? 'Een e-mail in de huisstijl van het merk, met sjabloon en preview; wordt ook op het ticket vastgelegd' : 'Een e-mail in de huisstijl van het merk, met preview; wordt ook op het ticket vastgelegd' },
  { id: 'partner_internal', recht: 'message', label: showBrand ? 'Intern (ET)' : 'Intern (support)', Icoon: Users, tip: 'Alleen partner en support zien dit — de klant niet' },
].filter((k) => !caps || caps.has(k.recht));
// Mailsjablonen, standaardteksten en het AI-voorstel zijn interne kennis: enkel
// admin en ET-dossierbeheerder (de server weigert ze voor elke andere rol, zie
// backend util/helpdeskPartnerZicht.js). Het scherm toont de kiezers dan niet.
const hdInterneKennis = (role) => role === 'admin' || role === 'et_dossier';
// De kennisbank in het antwoordblok: admin/ET altijd, een partnerrol alleen met
// de subpagina "Kennisbank" (standaard uit).
const hdMagKennisbank = (user) => { const set = subPageSetForUser(user); return !set || set.has(subPageKey('helpdesk', 'kennisbank')); };

// Bijlagen kiezen vóór het versturen (antwoordblok, nieuw ticket): knop +
// chips, meerdere bestanden, max HD_MAX_BIJLAGE_BYTES per stuk. De bestanden
// zelf reizen als base64 mee met het bericht/de mail (hdBijlagenNaarPayload).
const HdBijlageKiezer = ({ files, onChange, disabled = false, compact = false }) => {
  const ref = useRef(null);
  const voegToe = (lijst) => {
    const gekozen = Array.from(lijst || []);
    const teGroot = gekozen.filter((f) => f.size > HD_MAX_BIJLAGE_BYTES);
    if (teGroot.length) toast.error(`Te groot (max ${hdBytes(HD_MAX_BIJLAGE_BYTES)}): ${teGroot.map((f) => f.name).join(', ')}`);
    const rest = gekozen.filter((f) => f.size <= HD_MAX_BIJLAGE_BYTES && !files.some((x) => x.name === f.name && x.size === f.size));
    if (rest.length) onChange([...files, ...rest]);
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button type="button" disabled={disabled} onClick={() => ref.current?.click()} title={`Bijlage toevoegen (max ${hdBytes(HD_MAX_BIJLAGE_BYTES)} per bestand)`}
        className={`inline-flex items-center gap-1 ${compact ? 'h-7 px-2 text-[11px]' : 'h-8 px-2.5 text-xs'} bg-slate-700/70 hover:bg-slate-600/70 border border-slate-600/70 rounded-md text-slate-200 disabled:opacity-50`}>
        <Paperclip size={13} />Bijlage
      </button>
      <input ref={ref} type="file" multiple onChange={(e) => { voegToe(e.target.files); e.target.value = ''; }} className="hidden" />
      {files.map((f, i) => (
        <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-slate-700/60 border border-slate-600/50 rounded text-xs text-slate-200 max-w-[240px]">
          <span className="truncate" title={f.name}>{f.name}</span>
          <span className="text-slate-500 shrink-0">{hdBytes(f.size)}</span>
          <button type="button" disabled={disabled} onClick={() => onChange(files.filter((_, j) => j !== i))} title="Verwijderen" className="text-slate-500 hover:text-red-400 shrink-0"><X size={12} /></button>
        </span>
      ))}
    </div>
  );
};
// File[] → [{ name, mimetype, data }] (base64) voor de API.
const hdBijlagenNaarPayload = async (files) => Promise.all((files || []).map(async (f) => ({ name: f.name, mimetype: f.type || undefined, data: await hdFileToB64(f) })));

// ── Gedeelde panelen van de composer ─────────────────────────────────────────
// Het antwoordblok op een ticket én het scherm "Nieuw ticket" bieden dezelfde
// hulpmiddelen: standaardteksten, kennisbank en AI-voorstel. De panelen staan
// daarom één keer hier; de ouder houdt de tekst en beslist wat "invoegen" doet.

// Kennisbank: zoeken, lezen en invoegen zonder de pagina te verlaten.
const HdKennisbankPaneel = ({ token, onInvoegen, onSluiten, onFout }) => {
  const [q, setQ] = useState('');
  const [lijst, setLijst] = useState(null);
  const [artikel, setArtikel] = useState(null);
  const kbFetch = useCallback(async (p) => {
    const r = await fetch(`/api/kennisbank${p}`, { headers: { Authorization: `Bearer ${token}` } });
    const b = await r.json().catch(() => null);
    if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
    return b;
  }, [token]);
  useEffect(() => {
    let leeft = true;
    const h = setTimeout(() => {
      kbFetch(`?q=${encodeURIComponent(q.trim())}`).then((d) => leeft && setLijst(d.artikels || [])).catch(() => leeft && setLijst([]));
    }, q ? 300 : 0);
    return () => { leeft = false; clearTimeout(h); };
  }, [q, kbFetch]);
  const open = async (a) => {
    try { const d = await kbFetch(`/${a.id}`); setArtikel(d.artikel); } catch (e) { onFout && onFout(e.message); }
  };
  return (
    <div className="mb-2 p-3 rounded-lg border border-sky-500/30 bg-sky-500/[.05] space-y-2">
      <div className="flex items-center gap-2">
        <Search size={14} className="text-sky-300 shrink-0" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek een werkinstructie…" className="flex-1 h-8 px-3 bg-slate-800/70 border border-slate-600/60 rounded-md text-xs text-slate-100" />
        <button type="button" onClick={onSluiten} className="h-8 px-2 text-xs text-slate-400 hover:text-slate-200">Sluiten</button>
      </div>
      {artikel ? (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-3">
          <div className="flex items-center gap-2 mb-1">
            <button type="button" onClick={() => setArtikel(null)} className="text-xs text-sky-300 hover:underline">← Terug</button>
            <span className="text-sm font-semibold text-slate-100 truncate">{artikel.title}</span>
            <span className="flex-1" />
            <button type="button" onClick={() => onInvoegen(artikel.body_html)} className="inline-flex items-center gap-1 h-7 px-2.5 bg-sky-500 hover:bg-sky-400 text-slate-900 rounded-md text-xs font-semibold"><Plus size={12} />Invoegen</button>
          </div>
          <div className="hd-html text-xs text-slate-300 max-h-64 overflow-y-auto" dangerouslySetInnerHTML={{ __html: artikel.body_html }} />
        </div>
      ) : (
        <div className="max-h-56 overflow-y-auto divide-y divide-slate-700/40">
          {lijst === null && <div className="py-3 text-xs text-slate-500">Zoeken…</div>}
          {lijst && lijst.length === 0 && <div className="py-3 text-xs text-slate-500">Geen werkinstructies gevonden{q ? ` voor “${q}”` : ''}.</div>}
          {(lijst || []).map((a) => (
            <div key={a.id} className="flex items-start gap-2 py-1.5">
              <button type="button" onClick={() => open(a)} className="flex-1 min-w-0 text-left">
                <div className="text-xs font-semibold text-slate-200 truncate">{a.title}{a.energy_label ? <span className="ml-1.5 px-1 py-px rounded bg-slate-700/70 text-[10px] font-normal text-slate-300">{a.energy_label}</span> : null}{a.category ? <span className="ml-1 text-[10px] font-normal text-slate-500">{a.category}</span> : null}</div>
                <div className="text-[11px] text-slate-400 truncate">{a.summary || a.fragment}</div>
              </button>
              <button type="button" onClick={() => open(a)} className="text-[11px] text-sky-300 hover:underline shrink-0 mt-0.5">Lezen</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// AI-voorstel: aanwijzing + knop. Het voorstel vervangt de tekst; de vorige
// tekst blijft bewaard achter "Verwerp" (in de ouder).
// `variant`: 'voorstel' (zonder klantgegevens) of '360' (AI 360°: raadpleegt de
// gegevens van déze klant die de vraag nodig heeft). De keuze is bewust: twee
// knoppen, geen stille standaard.
const HD_AI_360_INFO = 'AI 360° kan relevante klant-, contract-, factuur- en meetgegevens raadplegen om een vollediger antwoordvoorstel te maken. Gebruikte klantdata wordt intern gemarkeerd en deze markering is niet zichtbaar voor de klant.';
const HdAiPaneel = ({ instructie, setInstructie, busy, onMaak, onSluiten, brandName, nieuw = false, variant = 'voorstel' }) => {
  // De uitleg zit achter de info-knop naast de titel: uitgeschreven nam ze drie
  // regels in en duwde ze het invulveld naar beneden.
  const basis = `Op basis van ${nieuw ? 'het onderwerp en je aanwijzing' : 'dit ticket'}, de kennisbank, de standaardteksten en de algemene voorwaarden van ${brandName || 'dit energielabel'}, en geanonimiseerde eerdere tickets.`;
  const uitleg = variant === '360'
    ? <>{basis} AI 360° raadpleegt daarbij de gegevens van déze klant die de vraag nodig heeft: contract, facturen, voorschot, meetgegevens of eerdere tickets. Klantgegevens in het voorstel staan <mark className="ai-klant">gemarkeerd</mark>; controleer ze voor je verstuurt. De markering verdwijnt bij het versturen.</>
    : <>{basis} Zonder klantgegevens: waar een bedrag, een datum of een verbruik nodig is, laat het voorstel een plaatshouder tussen vierkante haken die je zelf invult.</>;
  return (
    <div className="mb-3 p-4 rounded-lg border border-violet-500/30 bg-violet-500/[.06] space-y-3" data-ai-paneel={variant}>
      <div className="flex items-center gap-2 text-xs">
        {variant === '360' ? <Compass size={14} className="text-violet-300 shrink-0" /> : <Wand2 size={14} className="text-violet-300 shrink-0" />}
        <span className="font-semibold text-slate-200">{variant === '360' ? 'AI 360°: ' : ''}{nieuw ? 'eerste mail laten opstellen' : 'conceptantwoord laten opstellen'}</span>
        <InfoTip title={variant === '360' ? 'AI 360°' : 'AI voorstel'} text={uitleg} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={instructie} onChange={(e) => setInstructie(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onMaak(); } }}
          placeholder={nieuw ? 'Waarover gaat de mail? bv. \'vraag de meterstand op\' of \'meld dat het contract stopt op 1 mei\'' : 'Extra aanwijzing (optioneel), bv. \'kort en formeel\' of \'vraag de meterstand op\''} className="flex-1 min-w-[220px] h-10 px-3.5 bg-slate-800/70 border border-slate-600/60 rounded-md text-sm text-slate-100" />
        <button type="button" onClick={onMaak} disabled={busy} className="inline-flex items-center gap-1.5 h-10 px-4 bg-violet-500 hover:bg-violet-400 text-white rounded-md text-xs font-semibold disabled:opacity-50">{busy ? <Spinner size={12} /> : <Wand2 size={13} />}{busy ? 'Voorstel maken…' : 'Voorstel maken'}</button>
        <button type="button" onClick={onSluiten} className="h-10 px-2 text-xs text-slate-400 hover:text-slate-200">Sluiten</button>
      </div>
    </div>
  );
};

// Het ingevulde voorstel: bronnen, waarschuwingen, verwerpen en beoordelen.
// Leren uit gebruik: duim omhoog maakt dit voorstel een voorbeeld voor latere
// voorstellen van dit label; duim omlaag met een opmerking wordt een
// aandachtspunt. Wat je uiteindelijk verstuurt, telt als correctie.
const HdAiResultaat = ({ resultaat, isMail, onderwerpHuidig, onOnderwerp, onVerwerp, onSluit, onFeedback, opmerking, setOpmerking }) => (
  <div className="mb-2 p-3 rounded-lg border border-violet-500/30 bg-violet-500/[.06] text-xs space-y-1.5">
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 font-semibold text-violet-200">{resultaat.variant === '360' ? <Compass size={12} /> : <Wand2 size={12} />}{resultaat.variant === '360' ? 'AI 360°-voorstel ingevuld' : 'AI-voorstel ingevuld'}</span>
      <span className="text-slate-500">{resultaat.gebruikt ? `${resultaat.gebruikt.kennisbank} werkinstructie(s) · ${resultaat.gebruikt.standaardteksten} standaardtekst(en)${resultaat.gebruikt.voorwaarden ? ` · ${resultaat.gebruikt.voorwaarden} artikel(s) uit de algemene voorwaarden` : ''} · ${resultaat.gebruikt.historiek} eerder(e) ticket(s)` : ''}</span>
      <span className="flex-1" />
      {isMail && resultaat.onderwerp && resultaat.onderwerp !== onderwerpHuidig && onOnderwerp && <button type="button" onClick={() => onOnderwerp(resultaat.onderwerp)} className="text-violet-300 hover:underline">Onderwerp overnemen: “{resultaat.onderwerp}”</button>}
      <button type="button" onClick={onVerwerp} className="inline-flex items-center gap-1 text-slate-400 hover:text-red-300"><RotateCcw size={12} />Verwerp</button>
      <button type="button" onClick={onSluit} className="text-slate-500 hover:text-slate-300" title="Melding sluiten (tekst blijft)"><X size={12} /></button>
    </div>
    {/* Welke klantgegevens geraadpleegd zijn: de medewerker ziet wat het model
        opvroeg, niet meer en niet minder. */}
    {resultaat.variant === '360' && (
      <div className="flex flex-wrap items-center gap-1" data-ai360-geraadpleegd>
        <span className="text-slate-400">Geraadpleegde klantgegevens:</span>
        {(resultaat.geraadpleegd || []).length === 0
          ? <span className="text-slate-500">geen — het antwoord kwam uit het ticket en de kennis van het label</span>
          : resultaat.geraadpleegd.map((g) => <span key={g.naam} className="hd-pill hd-pill-violet">{g.label}</span>)}
      </div>
    )}
    {resultaat.toelichting && <div className="text-slate-300">{resultaat.toelichting}</div>}
    {resultaat.ontbrekend && resultaat.ontbrekend.length > 0 && <div className="text-amber-300">Nog na te kijken: {resultaat.ontbrekend.join(' · ')}</div>}
    {resultaat.waarschuwingen && resultaat.waarschuwingen.length > 0 && <div className="text-red-300">{resultaat.waarschuwingen.join(' · ')}</div>}
    {resultaat.bronnen && resultaat.bronnen.length > 0 && (
      <div className="flex flex-wrap gap-1">{resultaat.bronnen.map((b, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-slate-800/70 border border-slate-700/60 text-[10px] text-slate-300" title={b.type}>{b.type === 'kennisbank' ? '📘 ' : b.type === 'standaardtekst' ? '📝 ' : b.type === 'voorwaarden' ? '⚖️ ' : b.type === 'historiek' ? '🕘 ' : ''}{b.titel || `${b.type} ${b.id ?? ''}`}</span>)}</div>
    )}
    {resultaat.id && (
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-violet-500/20">
        <span className="text-slate-400">Was dit voorstel bruikbaar?</span>
        <button type="button" onClick={() => onFeedback(1)} title="Goed voorstel — wordt een voorbeeld voor latere voorstellen" className={`h-6 px-2 rounded border text-[11px] ${resultaat.beoordeling === 1 ? 'bg-lime-500/20 border-lime-500/50 text-lime-200' : 'border-slate-600/60 text-slate-300 hover:border-slate-400'}`}>👍 Goed</button>
        <button type="button" onClick={() => onFeedback(-1)} title="Niet bruikbaar — zeg waarom, dat wordt een aandachtspunt" className={`h-6 px-2 rounded border text-[11px] ${resultaat.beoordeling === -1 ? 'bg-red-500/20 border-red-500/50 text-red-200' : 'border-slate-600/60 text-slate-300 hover:border-slate-400'}`}>👎 Niet goed</button>
        {resultaat.beoordeling != null && (
          <>
            <input value={opmerking} onChange={(e) => setOpmerking(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onFeedback(resultaat.beoordeling); } }}
              placeholder={resultaat.beoordeling === -1 ? 'Wat klopte niet? (bv. te lang, verkeerde toon, procedure X vergeten)' : 'Opmerking (optioneel)'} className="flex-1 min-w-[200px] h-6 px-2 bg-slate-800/70 border border-slate-600/60 rounded text-[11px] text-slate-100" />
            <button type="button" onClick={() => onFeedback(resultaat.beoordeling)} className="h-6 px-2 rounded bg-slate-700 hover:bg-slate-600 text-[11px] text-slate-200">Opslaan</button>
            {resultaat.feedbackOk && <span className="text-lime-300">Bedankt, opgeslagen.</span>}
          </>
        )}
      </div>
    )}
  </div>
);

// De knoppen "Kennisbank" en "AI voorstel" naast de sjabloonkiezer. `magKb` en
// `magAi` beslissen of de knop bestaat: het AI-voorstel alleen voor de interne
// rollen, de kennisbank voor wie de subpagina heeft.
// "AI voorstel" en "AI 360°" zijn twee aparte knoppen: wie klantgegevens laat
// raadplegen, kiest daar zelf voor. `aiOpen` en `aiBusy` = false, 'voorstel' of
// '360'. `magAi360` = het recht van deze gebruiker (de server toetst het opnieuw).
const HdHulpKnoppen = ({ kbOpen, onKb, aiOpen, aiBusy, onAi, nieuw = false, magKb = true, magAi = true, magAi360 = false }) => (
  <>
    {magKb && <button type="button" onClick={onKb} title="Werkinstructies zoeken en invoegen"
      className={`inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs border ${kbOpen ? 'bg-sky-500/20 border-sky-500/40 text-sky-200' : 'bg-slate-700/70 border-slate-600/70 text-slate-200 hover:border-slate-500'}`}><BookOpen size={12} />Kennisbank</button>}
    {magAi && <button type="button" onClick={() => onAi('voorstel')} disabled={!!aiBusy} title={nieuw ? 'Laat de eerste mail opstellen op basis van het onderwerp, de kennisbank, de standaardteksten en eerdere tickets, zonder klantgegevens' : 'Laat een conceptantwoord opstellen op basis van het ticket, de kennisbank, de standaardteksten en eerdere tickets, zonder klantgegevens'}
      className={`inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs border ${aiOpen === 'voorstel' || aiBusy === 'voorstel' ? 'hd-pill-violet' : 'bg-slate-700/70 border-slate-600/70 text-slate-200 hover:border-slate-500'}`}>
      {aiBusy === 'voorstel' ? <Spinner size={12} /> : <Wand2 size={12} />}AI voorstel</button>}
    {magAi && magAi360 && <span className="inline-flex items-center gap-0.5">
      <button type="button" onClick={() => onAi('360')} disabled={!!aiBusy} title="Laat een voorstel opstellen dat ook de gegevens van deze klant raadpleegt"
        className={`inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs border ${aiOpen === '360' || aiBusy === '360' ? 'hd-pill-violet' : 'bg-slate-700/70 border-slate-600/70 text-slate-200 hover:border-slate-500'}`}>
        {aiBusy === '360' ? <Spinner size={12} /> : <Compass size={12} />}AI 360°</button>
      <InfoTip title="AI 360°" text={HD_AI_360_INFO} />
    </span>}
  </>
);

// Verplicht veld dat nog leeg is: dik rood kader rond het veld. Een outline, dus
// de layout verspringt niet; inline stijl, zodat het in elk thema en per
// energielabel hetzelfde rood blijft. `fout` komt van ontbrekend*() uit
// helpdeskVerplicht.js en gaat pas aan NA een druk op de verzendknop; zodra het
// veld ingevuld is, valt het kader vanzelf weg.
const HdVerplicht = ({ fout, children, className = '' }) => (
  <div data-hd-fout={fout ? '' : undefined} className={className} style={fout ? { outline: '3px solid #ef4444', outlineOffset: 2, borderRadius: 8 } : undefined}>{children}</div>
);
// De korte melding bij die kaders: noemt de velden bij naam en brengt het eerste
// in beeld (op een laptop staat de verzendknop vaak een scherm lager dan het veld).
const meldOntbrekend = (velden) => {
  toast.error(`Nog in te vullen: ${somOp(velden)}.`, { duration: 6000 });
  setTimeout(() => { const el = document.querySelector('[data-hd-fout]'); if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 60);
};
// De knop "Toon preview" staat in de kop van het blok, de preview zelf eronder
// (of ernaast op een breed scherm). Bij het openen komt ze daarom in beeld,
// anders lijkt de knop niets te doen.
const useInBeeldBijOpenen = (open) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, 80);
    return () => clearTimeout(t);
  }, [open]);
  return ref;
};

// Standaardteksten kiezen met een zoekveld (vraag 08-10-2026): de lijst is lang
// en een gewone <select> laat je scrollen. Typen filtert op categorie en naam;
// Enter kiest de eerste treffer. Paneel in de portal (zie CLAUDE.md, dropdowns).
const HdStandaardtekstKiezer = ({ snippets, onKies, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState('');
  const wrapRef = useRef(null);
  const knopRef = useRef(null);
  const label = (s) => `${s.category ? `${s.category} · ` : ''}${s.name}${(s.language || 'nl') !== 'nl' ? ` (${String(s.language).toUpperCase()})` : ''}`;
  const norm = (x) => String(x || '').toLowerCase();
  const woorden = norm(zoek).split(/\s+/).filter(Boolean);
  const lijst = (snippets || []).filter((s) => woorden.every((w) => norm(label(s)).includes(w) || norm(s.text).includes(w)));
  useEffect(() => {
    if (!open) return undefined;
    const klik = (e) => { if (e.target.closest && e.target.closest('[data-dropdown-panel]')) return; if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const toets = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', klik); document.addEventListener('keydown', toets);
    return () => { document.removeEventListener('mousedown', klik); document.removeEventListener('keydown', toets); };
  }, [open]);
  const kies = (s) => { setOpen(false); setZoek(''); onKies(s); };
  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button ref={knopRef} type="button" onClick={() => { setOpen((v) => !v); setZoek(''); }} title="Standaardtekst invoegen op de cursor — typ om te zoeken; meerdere na elkaar kan"
        className={`${className} inline-flex items-center justify-between gap-1 text-left`}>
        <span className="truncate">Standaardtekst…</span><ChevronDown size={12} className="shrink-0 text-slate-400" />
      </button>
      {open && (
        <DropdownPanel anchorRef={knopRef} minWidth={300} maxWidth={460} className={`${DROPDOWN_SHELL} !max-h-80`}>
          <div className="p-1.5 border-b border-slate-600 bg-slate-800">
            <input type="text" value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek op naam, categorie of tekst…" autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && lijst.length) { e.preventDefault(); kies(lijst[0]); } }}
              className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-100 focus:outline-none focus:border-lime-500/60" />
          </div>
          <div className="max-h-64 overflow-y-auto bg-slate-800">
            {lijst.map((s) => (
              <button key={s.id} type="button" onClick={() => kies(s)} title={hdPlatteTekst(s.html || s.text).slice(0, 300)}
                className="w-full text-left px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100">
                {s.category && <span className="text-slate-500">{s.category} · </span>}{s.name}{(s.language || 'nl') !== 'nl' ? <span className="text-slate-500"> ({String(s.language).toUpperCase()})</span> : null}
              </button>
            ))}
            {lijst.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Geen standaardtekst gevonden</div>}
          </div>
        </DropdownPanel>
      )}
    </div>
  );
};

// `onNaMail(modus)`: na een verstuurde mail zet het ticket zijn status —
// "behandeling" (Verstuur mail) of "afsluiten" (Mail en afsluiten); de modal
// kent de fasen en de rechten. `magAfsluiten` toont de tweede knop.
const HdAntwoordBlok = ({ id, token, kanaal, onKanaal, snippets, showBrand, fullPage, composerRef, onGeplaatst, caps = null, interneKennis = false, magKennisbank = false, magAi360 = false, onNaMail = null, magAfsluiten = false }) => {
  const api = useMemo(() => hdApi(token), [token]);
  // De kanalen waar deze gebruiker recht op heeft (mail / interne notitie) en of
  // hij een bijlage mag meesturen.
  const kanalen = HD_KANALEN(showBrand, caps);
  const magBijlage = !caps || caps.has('attach');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Verplichte velden: de kaders gaan aan na een mislukte verzendpoging.
  const [toonVerplicht, setToonVerplicht] = useState(false);
  // Bijlagen bij dit bericht/deze mail (voor beide kanalen).
  const [bijlagen, setBijlagen] = useState([]);
  // ── Mailkanaal ──────────────────────────────────────────────────────────────
  const [ctx, setCtx] = useState(null);           // GET /tickets/:id/mail — pas bij het eerste gebruik
  const [ctxFout, setCtxFout] = useState(null);
  const [form, setForm] = useState({ to: '', subject: '', body: '', includeHistory: true, templateId: '' });
  const [toonHistoriek, setToonHistoriek] = useState(false);
  // Historiek meesturen en de afzender: zelden aangepast, dus ingeklapt tot één
  // regel die zegt wat er nu staat. Een niet-geverifieerd verzenddomein blijft
  // wél altijd zichtbaar (zie verder): dat is een waarschuwing, geen optie.
  const [optiesOpen, setOptiesOpen] = useState(false);
  const [toonPreview, setToonPreview] = useState(true);   // standaard open (vraag 08-10-2026): één klik minder per ticket
  const previewRef = useInBeeldBijOpenen(toonPreview);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewBreed, setPreviewBreed] = useState('desktop');
  const [weergave, setWeergave] = useState('visueel');   // 'visueel' | 'html'
  const [editorVersie, setEditorVersie] = useState(0);
  const laatsteSjabloon = useRef('');
  // ── Standaardteksten, kennisbank en AI-voorstel ─────────────────────────────
  // Standaardteksten (module helpdesk-bericht) worden in het mailkanaal als HTML
  // op de cursor ingevoegd en kunnen dus gecombineerd worden; het interne kanaal
  // krijgt de platte tekst. De kennisbank is een zoekpaneel met "Invoegen". Het
  // AI-voorstel vervangt de tekst (de vorige wordt bewaard voor "Verwerp") en
  // markeert klantgegevens met <mark class="ai-klant">; die markering gaat er bij
  // het versturen af — hier én op de server.
  const editorRef = useRef(null);
  const [kbOpen, setKbOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInstructie, setAiInstructie] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResultaat, setAiResultaat] = useState(null);
  const aiVorige = useRef(null);
  const voegHtmlIn = (html) => {
    if (!html) return;
    if (kanaal !== 'mail') { const t = hdHtmlNaarTekst(html); setMsg((m) => (m.trim() ? `${m.replace(/\s+$/, '')}\n\n${t}` : t)); return; }
    if (weergave === 'visueel' && editorRef.current && editorRef.current.voegHtml) { editorRef.current.voegHtml(html); return; }
    setForm((f) => ({ ...f, body: voegVoorGroet(f.body, html, '\n') }));
    setEditorVersie((v) => v + 1);
  };
  // `aiOpen` = welke variant open staat ('voorstel' of '360'); elk heeft zijn eigen
  // route, en de server toetst het recht op AI 360° bij elk verzoek.
  const maakAiVoorstel = async () => {
    const variant = aiOpen === '360' ? '360' : 'voorstel';
    setAiBusy(variant); setErr(null);
    try {
      const d = await api.send(`/tickets/${id}/${variant === '360' ? 'ai-360' : 'ai-voorstel'}`, 'POST', { kanaal, instructie: aiInstructie.trim() });
      if (kanaal === 'mail') {
        aiVorige.current = { body: form.body };
        laatsteSjabloon.current = d.antwoord_html;
        setForm((f) => ({ ...f, body: d.antwoord_html }));
        setEditorVersie((v) => v + 1);
        if (weergave !== 'visueel') setWeergave('visueel');
      } else {
        aiVorige.current = { msg };
        setMsg(hdHtmlNaarTekst(d.antwoord_html));
      }
      setAiResultaat(d); setAiOpen(false);
    } catch (e) { setErr(e.message); } finally { setAiBusy(false); }
  };
  const [aiOpmerking, setAiOpmerking] = useState('');
  const geefAiFeedback = async (beoordeling) => {
    if (!aiResultaat || !aiResultaat.id) return;
    setAiResultaat((r) => ({ ...r, beoordeling, feedbackOk: false }));
    try {
      await api.send(`/tickets/${id}/ai-voorstel/${aiResultaat.id}/feedback`, 'POST', { beoordeling, opmerking: aiOpmerking.trim() });
      setAiResultaat((r) => (r ? { ...r, feedbackOk: true } : r));
    } catch (e) { setErr(e.message); }
  };
  const verwerpAi = () => {
    const v = aiVorige.current;
    if (v) {
      if (kanaal === 'mail') { setForm((f) => ({ ...f, body: v.body || '' })); laatsteSjabloon.current = v.body || ''; setEditorVersie((x) => x + 1); }
      else setMsg(v.msg || '');
    }
    aiVorige.current = null; setAiResultaat(null);
  };

  useEffect(() => {
    if (kanaal !== 'mail' || ctx) return undefined;
    let leeft = true;
    setCtxFout(null);
    api.get(`/tickets/${id}/mail`)
      .then((d) => {
        if (!leeft) return;
        setCtx(d);
        // Leeg bericht → de vaste groet staat er al (server: groetHtml); sjabloon
        // en standaardteksten komen erboven. Ze telt als "sjabloontekst", zodat
        // een sjabloon kiezen niet om bevestiging vraagt.
        setForm((f) => {
          const body = f.body.trim() ? f.body : (d.groetHtml || '');
          if (!f.body.trim() && d.groetHtml) laatsteSjabloon.current = d.groetHtml;
          return { ...f, body, to: f.to || d.to || '', subject: f.subject || d.subject || '' };
        });
        setEditorVersie((v) => v + 1);
      })
      .catch((e) => leeft && setCtxFout(e.message));
    return () => { leeft = false; };
  }, [kanaal, ctx, api, id]);

  // Sjabloon: server-side ingevuld met de gegevens van dit ticket.
  const kiesSjabloon = async (tid) => {
    setForm((f) => ({ ...f, templateId: tid }));
    if (!tid) return;
    setErr(null);
    try {
      const d = await api.get(`/tickets/${id}/mail/template/${encodeURIComponent(tid)}`);
      setForm((f) => {
        // Werkwijze: eerst het sjabloon kiezen, dan aanpassen. Het sjabloon
        // vervangt dus de tekst; eigen tekst wordt enkel met bevestiging overschreven.
        const eigenTekst = f.body.trim() && f.body !== laatsteSjabloon.current;
        if (eigenTekst && !window.confirm('Je hebt al tekst geschreven. Vervangen door het sjabloon? Kies eerst het sjabloon en pas het daarna aan.')) return { ...f, templateId: '' };
        laatsteSjabloon.current = d.html || '';
        return { ...f, subject: d.subject || f.subject, body: d.html || '' };
      });
      setEditorVersie((v) => v + 1);
    } catch (e) { setErr(e.message); }
  };

  // Preview = de mail zoals de klant ze krijgt (merk-gewrapt door de server).
  useEffect(() => {
    if (kanaal !== 'mail' || !toonPreview || !ctx) return undefined;
    const h = setTimeout(() => {
      setPreviewBusy(true);
      api.send(`/tickets/${id}/mail/preview`, 'POST', { to: form.to, subject: form.subject, body_html: hdTekstNaarHtml(form.body), include_history: form.includeHistory })
        .then(setPreview).catch(() => setPreview(null)).finally(() => setPreviewBusy(false));
    }, 500);
    return () => clearTimeout(h);
  }, [kanaal, toonPreview, form.subject, form.body, form.includeHistory, api, id, ctx]);

  const kiesWeergave = (w) => {
    if (w === weergave) return;
    if (w === 'visueel') { setForm((f) => (f.body && !bevatHtml(f.body) ? { ...f, body: hdTekstNaarHtml(f.body) } : f)); setEditorVersie((v) => v + 1); }
    setWeergave(w);
  };
  const editorSync = (html) => setForm((f) => { if (f.body === laatsteSjabloon.current) laatsteSjabloon.current = html; return f.body === html ? f : { ...f, body: html }; });

  // Snel antwoord in het tekstvak (bericht- en interne kanaal).
  const lijstSnippets = kanaal === 'partner_internal' ? (snippets.intern || []) : (snippets.klant || []);

  // Wat ontbreekt er nog, voor het kanaal dat nu open staat.
  const ontbreekt = kanaal === 'mail' ? ontbrekendMail({ aan: form.to, onderwerp: form.subject, bericht: form.body }) : ontbrekendBericht({ bericht: msg });
  const mist = (veldId) => toonVerplicht && ontbreekt.some((v) => v.id === veldId);
  useEffect(() => { setToonVerplicht(false); }, [kanaal]);

  const plaatsBericht = async () => {
    if (ontbreekt.length) { setToonVerplicht(true); meldOntbrekend(ontbreekt); return; }
    setToonVerplicht(false);
    setBusy(true); setErr(null);
    try {
      // Bijlagen eerst op het ticket (elk apart), dan het bericht met hun namen.
      const namen = [];
      for (const f of bijlagen) {
        await api.send(`/tickets/${id}/attachments`, 'POST', { name: f.name, mimetype: f.type, data: await hdFileToB64(f) });
        namen.push(f.name);
      }
      await api.send(`/tickets/${id}/messages`, 'POST', { body: namen.length ? `${msg}\n\nBijlagen: ${namen.join(', ')}` : msg, visibility: kanaal, ai_voorstel_id: aiResultaat && aiResultaat.id ? aiResultaat.id : undefined });
      setMsg(''); setBijlagen([]); setAiResultaat(null); aiVorige.current = null;
      onGeplaatst(kanaal === 'partner_internal' ? 'Intern bericht geplaatst' : 'Bericht naar de klant geplaatst');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  // `modus` = 'afsluiten' bij "Mail en afsluiten"; een klik-event telt als gewoon versturen.
  const verstuurMail = async (modus) => {
    const afsluiten = modus === 'afsluiten';
    if (ontbreekt.length) { setToonVerplicht(true); meldOntbrekend(ontbreekt); return; }
    setToonVerplicht(false);
    setBusy(true); setErr(null);
    try {
      const d = await api.send(`/tickets/${id}/mail`, 'POST', {
        to: form.to.trim(), subject: form.subject.trim(),
        // De interne markering van klantgegevens (AI-voorstel) gaat er hier af;
        // de server doet het nog eens.
        body_html: hdStripAiMarks(hdTekstNaarHtml(form.body)), include_history: form.includeHistory,
        template_id: form.templateId ? Number(form.templateId) : undefined,
        // Na een AI-voorstel: de server bewaart wat er echt verstuurd werd als
        // correctie, zodat latere voorstellen ervan leren.
        ai_voorstel_id: aiResultaat && aiResultaat.id ? aiResultaat.id : undefined,
        // Bijlagen gaan mee in de mail én komen op het ticket (server).
        attachments: bijlagen.length ? await hdBijlagenNaarPayload(bijlagen) : undefined,
      });
      if (d && d.opTicket === false) toast.error(`Mail verstuurd naar ${d.to}, maar niet op het ticket vastgelegd: ${d.ticketFout || 'onbekende fout'}`);
      const nietOpTicket = ((d && d.attachments) || []).filter((a) => !a.ok);
      if (nietOpTicket.length) toast.error(`Mail verstuurd, maar ${nietOpTicket.length} bijlage(n) niet op het ticket gezet: ${nietOpTicket.map((a) => a.name).join(', ')}`);
      setBijlagen([]);
      // Terug naar de beginstand: enkel de vaste groet.
      setForm((f) => ({ ...f, body: (ctx && ctx.groetHtml) || '', templateId: '' }));
      laatsteSjabloon.current = (ctx && ctx.groetHtml) || '';
      setEditorVersie((v) => v + 1);
      setAiResultaat(null); aiVorige.current = null;
      setPreview(null);   // de preview blijft open (standaard aan) en toont de nieuwe, lege tekst
      // Status mee laten bewegen (vraag 08-10-2026): "Verstuur mail" → In
      // behandeling, "Mail en afsluiten" → Afgehandeld. Een mislukte statuswissel
      // maakt de verzonden mail niet ongedaan; dat wordt een aparte melding.
      let statusFout = '';
      if (onNaMail) { try { await onNaMail(afsluiten ? 'afsluiten' : 'behandeling'); } catch (e) { statusFout = e.message; } }
      onGeplaatst(d && d.opTicket === false ? null : `Mail verstuurd naar ${d.to} en vastgelegd op het ticket.${afsluiten && !statusFout ? ' Ticket afgehandeld.' : ''}`);
      if (statusFout) toast.error(`Mail verstuurd, maar de status kon niet gewijzigd worden: ${statusFout}`);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const groepen = useMemo(() => {
    const m = new Map();
    for (const t of (ctx && ctx.templates) || []) { const c = t.category || 'Overige'; if (!m.has(c)) m.set(c, []); m.get(c).push(t); }
    return [...m.entries()];
  }, [ctx]);
  const gekozen = ctx && form.templateId ? (ctx.templates || []).find((t) => String(t.id) === String(form.templateId)) : null;
  const sender = ctx && ctx.sender;
  const andereAdressen = ((ctx && ctx.adressen) || []).filter((a) => a.email !== form.to.trim().toLowerCase());

  const veld = 'w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100';
  const selKlein = 'h-7 px-2 bg-slate-700/70 border border-slate-600/70 rounded-md text-xs text-slate-200';
  const wgKnop = (w, Icoon, label) => <button type="button" onClick={() => kiesWeergave(w)} className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${weergave === w ? 'bg-lime-500/20 text-lime-300 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}><Icoon size={12} />{label}</button>;
  const isMail = kanaal === 'mail';
  const isIntern = kanaal === 'partner_internal';
  // Mailen kan pas als de gegevens er zijn en niets het verhindert (huisstijl,
  // verzenddomein): dan pas horen de verzendknoppen in de kop.
  const magMailen = isMail && !!ctx && !ctxFout && !ctx.blokkade;

  return (
    <div ref={composerRef} className="border-b border-slate-700/60 bg-slate-900/95 px-4 py-3">
      {/* Kop van het blok (27-09-2026): links het kanaal, RECHTS de acties —
          preview en de groene verzendknop. Zelfde plaats als op "Nieuw ticket" en in
          de kennisbank: de hoofdactie staat rechtsboven en is er meteen, niet pas
          onder de editor. Op een telefoon (< sm) staat ze onderaan het blok, over de
          volle breedte. */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="inline-flex items-center gap-0.5 bg-slate-800/60 border border-slate-700/60 rounded-md p-0.5">
          {kanalen.map(({ id: k, label, Icoon, tip }) => (
            <button key={k} type="button" onClick={() => onKanaal(k)} title={tip}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${kanaal === k ? (k === 'partner_internal' ? 'hd-pill-amber' : 'bg-lime-500/20 text-lime-300') : 'text-slate-400 hover:text-slate-200'}`}>
              <Icoon size={12} />{label}
            </button>
          ))}
        </div>
        {isIntern && <span className="text-[11px] text-amber-300/90">De klant ziet dit niet.</span>}
        {/* Eén groep met ml-auto: past ze niet meer naast het kanaal, dan springt ze
            in haar geheel naar de volgende regel en blijft ze rechts staan. */}
        <div className="hidden sm:flex items-center gap-2 ml-auto">
          {!isMail && <span className="text-[11px] text-slate-500 hidden xl:inline">Ctrl+Enter verstuurt</span>}
          {magMailen && <button type="button" onClick={() => setToonPreview((s) => !s)} className="inline-flex items-center gap-1.5 h-8 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-xs whitespace-nowrap"><Eye size={14} />{toonPreview ? 'Verberg preview' : 'Toon preview'}</button>}
          {/* "Mail en afsluiten" (08-10-2026): dezelfde mail, daarna meteen Afgehandeld.
              Secundair naast de groene hoofdknop, enkel voor wie mag afsluiten. */}
          {magMailen && magAfsluiten && <button type="button" onClick={() => verstuurMail('afsluiten')} disabled={busy} title="De mail versturen en het ticket meteen op Afgehandeld zetten" className="inline-flex items-center gap-1.5 h-8 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-xs font-semibold disabled:opacity-50 whitespace-nowrap"><Check size={14} />Mail en afsluiten</button>}
          {magMailen && <button type="button" onClick={() => verstuurMail()} disabled={busy} title="De mail versturen; het ticket gaat naar In behandeling" className="inline-flex items-center gap-1.5 h-8 px-4 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-md text-xs font-semibold disabled:opacity-50 whitespace-nowrap"><Send size={14} />{busy ? 'Versturen…' : 'Verstuur mail'}</button>}
          {!isMail && <button type="button" onClick={plaatsBericht} disabled={busy} className="inline-flex items-center gap-1.5 h-8 px-4 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-md text-xs font-semibold disabled:opacity-50 whitespace-nowrap"><Send size={14} />{isIntern ? 'Notitie plaatsen' : 'Versturen'}</button>}
        </div>
      </div>
      {/* Hulpmiddelen: de kiezer verandert van lijst met het kanaal. */}
      {(interneKennis || magKennisbank) && <div className="flex items-center gap-2 mb-2 flex-wrap">
        {interneKennis && isMail && ctx && (
          <select value={form.templateId} onChange={(e) => kiesSjabloon(e.target.value)} title="Mailsjabloon (volledige brief; vervangt de tekst)" className={`${selKlein} w-[150px]`}>
            <option value="">Sjabloon…</option>
            {groepen.map(([cat, lijst]) => <optgroup key={cat} label={cat}>{lijst.map((t) => <option key={t.id} value={t.id}>{t.name}{(t.language || 'nl') !== 'nl' ? ` (${String(t.language).toUpperCase()})` : ''}</option>)}</optgroup>)}
          </select>
        )}
        {/* Standaardteksten: in het mailkanaal als HTML op de cursor (combineerbaar),
            in het interne kanaal als platte tekst achteraan. */}
        {interneKennis && lijstSnippets.length > 0 && (!isMail || ctx) && (
          <HdStandaardtekstKiezer snippets={lijstSnippets} className={`${selKlein} w-[150px]`}
            onKies={(s) => voegHtmlIn(isMail ? (s.html || hdTekstNaarHtml(s.text)) : hdTekstNaarHtml(s.text))} />
        )}
        {(!isMail || ctx) && <HdHulpKnoppen magKb={magKennisbank} magAi={interneKennis} magAi360={magAi360} kbOpen={kbOpen} onKb={() => { setKbOpen((s) => !s); setAiOpen(false); }} aiOpen={aiOpen} aiBusy={aiBusy} onAi={(v) => { setAiOpen((s) => (s === v ? false : v)); setKbOpen(false); }} />}
      </div>}

      {interneKennis && aiOpen && <HdAiPaneel variant={aiOpen === '360' ? '360' : 'voorstel'} instructie={aiInstructie} setInstructie={setAiInstructie} busy={!!aiBusy} onMaak={maakAiVoorstel} onSluiten={() => setAiOpen(false)} brandName={ctx && ctx.brandName} />}
      {interneKennis && aiResultaat && <HdAiResultaat resultaat={aiResultaat} isMail={isMail} onderwerpHuidig={form.subject} onOnderwerp={(s) => setForm((f) => ({ ...f, subject: s }))} onVerwerp={verwerpAi} onSluit={() => setAiResultaat(null)} onFeedback={geefAiFeedback} opmerking={aiOpmerking} setOpmerking={setAiOpmerking} />}
      {magKennisbank && kbOpen && <HdKennisbankPaneel token={token} onInvoegen={voegHtmlIn} onSluiten={() => setKbOpen(false)} onFout={setErr} />}

      {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm mb-2">{err}</div>}

      {!isMail && (
        <>
          <HdVerplicht fout={mist('bericht')}>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); plaatsBericht(); } }}
              rows={3} placeholder={isIntern ? 'Interne notitie (de klant ziet dit niet)…' : 'Antwoord aan de klant…'}
              className={`block w-full px-3 py-2 border rounded-lg text-sm text-slate-200 ${isIntern ? 'bg-amber-500/[.06] border-amber-500/30' : 'bg-slate-700/60 border-slate-600/70'}`} />
          </HdVerplicht>
          <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
            {magBijlage ? <HdBijlageKiezer files={bijlagen} onChange={setBijlagen} disabled={busy} compact /> : <span />}
            {/* Telefoon: de verzendknop onderaan, over de volle breedte. */}
            <button onClick={plaatsBericht} disabled={busy} className="sm:hidden w-full inline-flex items-center justify-center gap-1.5 h-10 px-3.5 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-md text-sm font-semibold disabled:opacity-50"><Send size={14} />{isIntern ? 'Notitie plaatsen' : 'Versturen'}</button>
          </div>
        </>
      )}

      {isMail && (ctxFout ? <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{ctxFout}</div>
        : !ctx ? <div className="flex justify-center py-6"><Spinner size={22} /></div>
        : ctx.blokkade ? (
          <div className="flex items-start gap-2 text-sm rounded-lg px-3 py-3 border border-amber-500/40 bg-amber-500/10 text-amber-200">
            <AlertCircle size={16} className="shrink-0 mt-0.5 text-amber-400" />
            <div><div className="font-semibold">Mailen naar de klant is niet mogelijk</div><div className="text-xs mt-0.5 text-amber-200/90">{ctx.blokkade}{kanalen.some((k) => k.id === 'partner_internal') ? ' Intern overleggen kan wel, via het kanaal Intern.' : ''}</div></div>
          </div>
        ) : (
        <div className={`grid gap-4 ${toonPreview ? 'xl:grid-cols-2' : ''}`}>
          <div className="space-y-3 min-w-0">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Aan *</label>
                <HdVerplicht fout={mist('aan')}><input value={form.to} onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))} placeholder="klant@adres.be" className={`${veld} font-mono`} /></HdVerplicht>
                {!ctx.to && !form.to && <p className="text-[11px] text-amber-400/80 mt-1">Geen adres gevonden in het ticket of bij de orders van deze klant — vul het zelf in.</p>}
                {/* De mail kwam van een ander adres dan in de orders van de klant
                    staat: zeg het, met beide adressen klikbaar. Het afzenderadres
                    is de standaard (de klant schreef ervan), de orders het
                    alternatief. */}
                {ctx.afwijkend && (
                  <div className="mt-1 text-[11px] text-orange-300 flex items-start gap-1.5">
                    <AlertCircle size={12} className="shrink-0 mt-0.5" />
                    <span>Het afzenderadres van de mail (<button type="button" onClick={() => setForm((f) => ({ ...f, to: ctx.afwijkend.afzender }))} title="Dit adres gebruiken" className="font-mono underline decoration-dotted">{ctx.afwijkend.afzender}</button>) wijkt af van het adres in de orders van de klant (<button type="button" onClick={() => setForm((f) => ({ ...f, to: ctx.afwijkend.orders }))} title="Dit adres gebruiken" className="font-mono underline decoration-dotted">{ctx.afwijkend.orders}</button>). Controleer aan wie je antwoordt.</span>
                  </div>
                )}
                {andereAdressen.length > 0 && (
                  <div className="mt-1 text-[11px] text-amber-300/90 flex items-start gap-1.5">
                    <AlertCircle size={12} className="shrink-0 mt-0.5" />
                    <span>Ook gevonden: {andereAdressen.map((a, i) => (
                      <span key={a.email}>{i > 0 && ' · '}<button type="button" onClick={() => setForm((f) => ({ ...f, to: a.email }))} title="Dit adres gebruiken" className="font-mono underline decoration-dotted hover:text-amber-100">{a.email}</button> <span className="text-slate-500">({a.bron === 'orders' ? 'uit de orders' : a.bron === 'bericht' ? 'uit een bericht' : 'uit het ticket'})</span></span>
                    ))}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Onderwerp *</label>
                <HdVerplicht fout={mist('onderwerp')}><input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} className={veld} /></HdVerplicht>
                {interneKennis && (ctx.templates || []).length === 0 && <p className="text-[11px] text-slate-500 mt-1">Nog geen mailsjabloon vrijgegeven voor de Helpdesk{ctx.brandName ? ` van ${ctx.brandName}` : ''}.</p>}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="block text-xs text-slate-400">Bericht *</label>
                <div className="flex items-center gap-0.5 bg-slate-800/60 border border-slate-700/60 rounded-md p-0.5">
                  {wgKnop('visueel', Pencil, 'Weergave')}
                  {wgKnop('html', Code2, 'HTML')}
                </div>
              </div>
              <HdVerplicht fout={mist('bericht')}>
                {weergave === 'visueel'
                  ? <HdVisueleEditor laag html={form.body} versie={editorVersie} onChange={(html) => setForm((f) => ({ ...f, body: html }))} onSync={editorSync} editorRef={editorRef} />
                  : <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={8}
                      placeholder="Schrijf je antwoord aan de klant…" className={`${veld} block min-h-[150px] max-h-[60vh] ${bevatHtml(form.body) ? 'font-mono text-xs leading-relaxed' : ''}`} />}
              </HdVerplicht>
              <p className="text-[11px] text-slate-500 mt-1">{weergave === 'visueel' ? 'Bewerk de tekst zoals ze in de mail verschijnt; het logo, de kleuren en het contactblok komen er bij het versturen omheen.' : (bevatHtml(form.body) ? 'De technische HTML-bron — wijzigingen hier zie je ook in de weergave.' : 'Gewone tekst; lege regels worden alinea’s.')}{gekozen && gekozen.attachments && gekozen.attachments.length > 0 && <> · Bijlagen van het sjabloon gaan mee: <span className="text-slate-300">{gekozen.attachments.join(', ')}</span></>}</p>
            </div>

            <button type="button" onClick={() => setOptiesOpen((v) => !v)} aria-expanded={optiesOpen} className="w-full flex items-center gap-2 text-left text-[11px] text-slate-400 hover:text-slate-200">
              <SlidersHorizontal size={12} className="shrink-0" />
              <span className="font-medium uppercase tracking-wide">Opties</span>
              <span className="truncate normal-case text-slate-500">{form.includeHistory ? `eerdere berichten gaan mee (${ctx.history.length})` : 'zonder eerdere berichten'}{sender && sender.verified ? ` · vanaf ${sender.from}` : ''}{ctx.brandName ? ` · huisstijl ${ctx.brandName}` : ''}</span>
              {optiesOpen ? <ChevronUp size={13} className="ml-auto shrink-0" /> : <ChevronDown size={13} className="ml-auto shrink-0" />}
            </button>
            {optiesOpen && <div className="p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <input type="checkbox" checked={form.includeHistory} onChange={(e) => setForm((f) => ({ ...f, includeHistory: e.target.checked }))} className="w-4 h-4 rounded accent-lime-500" />
                Voorgaande berichten meesturen ({ctx.history.length})
              </label>
              <p className="text-[11px] text-slate-500">Alleen wat de klant al gezien heeft; interne berichten blijven eruit. De mail vertrekt met het logo, de kleuren en het contactblok van {ctx.brandName || ctx.energyLabel || 'het portaal'}.</p>
              {ctx.history.length > 0 && <button type="button" onClick={() => setToonHistoriek((s) => !s)} className="text-xs text-lime-400 hover:underline">{toonHistoriek ? 'Verberg historiek' : 'Toon wat er meegaat'}</button>}
              {toonHistoriek && <div className="max-h-52 overflow-y-auto space-y-2 border-t border-slate-700/50 pt-2">
                {ctx.history.map((h, i) => (
                  <div key={i} className="text-xs">
                    <div className="text-slate-500">{h.author}{h.date ? ` · ${hdFmt(h.date)}` : ''}</div>
                    <div className="text-slate-300 bg-slate-800/50 rounded p-2 mt-0.5 max-h-24 overflow-hidden hd-html" dangerouslySetInnerHTML={{ __html: h.html }} />
                  </div>
                ))}
              </div>}
            </div>}

            {sender && (optiesOpen || !sender.verified) && (
              <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 border ${sender.verified ? 'border-lime-500/30 bg-lime-500/5 text-slate-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'}`}>
                {sender.verified ? <Check size={14} className="shrink-0 mt-0.5 text-lime-400" /> : <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-400" />}
                <span>{sender.verified
                  ? <>Verzonden vanaf <strong className="font-mono">{sender.fromName} &lt;{sender.from}&gt;</strong>{sender.replyTo ? <> · antwoorden komen aan op <span className="font-mono">{sender.replyTo}</span></> : null}</>
                  : <>{ctx.brandName || 'Dit merk'} heeft nog geen geverifieerd verzenddomein — de mail vertrekt vanaf het algemene systeemadres. Vraag een beheerder het domein te koppelen (Mail Domeinen).</>}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {magBijlage && <HdBijlageKiezer files={bijlagen} onChange={setBijlagen} disabled={busy} />}
              <span className="flex-1" />
              {/* Telefoon: preview en verzendknop onderaan, de knop over de volle breedte. */}
              <button onClick={() => setToonPreview((s) => !s)} className="sm:hidden flex items-center gap-1.5 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm"><Eye size={14} />{toonPreview ? 'Verberg preview' : 'Toon preview'}</button>
              <button onClick={verstuurMail} disabled={busy} className="sm:hidden order-last w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm disabled:opacity-50"><Send size={15} />{busy ? 'Versturen…' : 'Verstuur mail'}</button>
            </div>
          </div>

          {toonPreview && (
            <div ref={previewRef} className="space-y-2 min-w-0 scroll-mt-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-slate-400 flex items-center gap-2"><Eye size={14} className="text-lime-400" />Zo ziet de klant het{previewBusy && <Spinner size={12} />}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPreviewBreed('desktop')} title="Desktop" className={`p-1.5 rounded ${previewBreed === 'desktop' ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}><Tablet size={14} /></button>
                  <button onClick={() => setPreviewBreed('mobiel')} title="Telefoon" className={`p-1.5 rounded ${previewBreed === 'mobiel' ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}><Smartphone size={14} /></button>
                </div>
              </div>
              {preview && <div className="text-xs text-slate-300 px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg truncate"><span className="text-slate-500">Onderwerp: </span>{preview.subject}</div>}
              <div className="border border-slate-700 rounded-lg overflow-hidden bg-[#f1f5f9] flex justify-center">
                {preview
                  ? <iframe title="Mailpreview" sandbox="" srcDoc={preview.html} style={{ width: previewBreed === 'mobiel' ? 360 : '100%', maxWidth: '100%', height: 560, border: 0, background: '#f1f5f9' }} />
                  : <div className="py-16 text-slate-500 text-sm">{previewBusy ? 'Preview maken…' : 'Nog geen preview.'}</div>}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Bijlagen: de ET-API kent alleen het OPLIJSTEN en TOEVOEGEN van bijlagen (naam,
// type, grootte — geen inhoud), dus downloaden kan hier niet. Wat wél kan: vooraf
// weten of een bestand past. De server neemt 15 MB JSON aan en base64 blaast een
// bestand ~33% op → 10 MB is de grens die zeker past, en die melden we vóór het
// coderen en uploaden, niet erna.
const HD_MAX_BIJLAGE_BYTES = 10 * 1024 * 1024;
const hdBytes = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toLocaleString('nl-BE', { maximumFractionDigits: 0 })} kB`;
  return `${(v / (1024 * 1024)).toLocaleString('nl-BE', { maximumFractionDigits: 1 })} MB`;
};

// Wie schreef een bericht? De ET-API geeft author_id mee; is dat de klant van het
// ticket, dan kleurt het bericht als "van de klant". Interne berichten
// (partner↔support) zijn amber. De rest — partner of support — blijft neutraal.
// `per_mail` zet de server (util/helpdeskBericht.js): een mail die van buiten
// binnenkwam. Komt die niet van het klantadres (de klant vanaf een ander adres,
// een derde), dan is het "per e-mail" — en zeker geen medewerker.
const hdBerichtSoort = (m, t) => {
  if (m.visibility === 'partner_internal') return 'intern';
  // Een mail die vanuit het portaal naar de klant vertrok (server: util/helpdeskDraad.js).
  if (m.soort === 'uitgaande_mail') return 'mail';
  const kid = t && t.customer && t.customer.id;
  if (kid != null && m.author_id != null && String(m.author_id) === String(kid)) return 'klant';
  if (m.per_mail) return 'extern';
  return 'overig';
};
// Eén regel platte tekst uit ticket-HTML (voor de ingeklapte berichten).
const hdPlatteTekst = (html) => String(html || '').replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&#160;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\s+/g, ' ').trim();
// Kleuren van label en avatar uit het hd-pill-palet (thema-blok): per thema een
// eigen tint met donkere of lichte inkt. De generieke -300-tinten van vroeger
// waren in het lichte thema licht op licht (gemeten 27-09-2026). De klant draagt
// dezelfde kleur als "actie van: Klant" in de kop.
const HD_BERICHT_STIJL = {
  intern: { cls: 'bg-amber-500/10 border border-amber-500/20', chip: 'intern', chipCls: 'border hd-pill-amber' },
  klant: { cls: 'bg-sky-500/10 border border-sky-500/20', chip: 'klant', chipCls: 'border hd-pill-sky' },
  mail: { cls: 'bg-emerald-500/10 border border-emerald-500/20', chip: 'uitgaande mail', chipCls: 'border hd-pill-emerald', Icoon: Mail },
  extern: { cls: 'bg-indigo-500/10 border border-indigo-500/20', chip: 'per e-mail', chipCls: 'border hd-pill-indigo', Icoon: Inbox, titel: 'Per e-mail binnengekomen van een ander adres dan dat van de klant' },
  overig: { cls: 'bg-slate-800/50 border border-transparent', chip: 'medewerker', chipCls: 'border hd-pill-slate' },
};

// Escaleren is één klik en vanuit het portaal onomkeerbaar, dus eerst bevestigen —
// met een optionele reden. De ET-API neemt bij /escalate geen tekst aan; de reden
// gaat daarom vooraf als INTERN bericht op het ticket (partner↔support, nooit
// naar de klant).
const HelpdeskEscalateDialog = ({ onConfirm, onClose, busy, showBrand }) => {
  const [reden, setReden] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const doel = showBrand ? 'Energy Together' : 'Support';
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9700] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-700/70">
          <h3 className="text-base font-bold text-slate-100 flex items-center gap-2"><ArrowUpCircle size={16} className="text-purple-400" />Escaleren naar {doel}</h3>
          <p className="text-xs text-slate-400 mt-1">Het ticket krijgt een escalatie-markering en de actie komt bij {doel} te liggen. Dit kan vanuit het portaal niet ongedaan gemaakt worden.</p>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Reden (optioneel, intern — de klant ziet dit niet)</label>
            <textarea autoFocus value={reden} onChange={(e) => setReden(e.target.value)} rows={3} placeholder="Waarom kan je hier als partner niet verder mee?" className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm">Annuleren</button>
            <button onClick={() => onConfirm(reden.trim())} disabled={busy} className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-lg text-sm disabled:opacity-50"><ArrowUpCircle size={15} />{busy ? 'Bezig…' : 'Escaleren'}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Ticketdetail. Als volledige pagina (Helpdesk) staat het gesprek links en de
// context rechts — klant, contract, aansluitingen, bijlagen, logboek — zodat je
// bij een lange draad niet meer in twee kleine scrollvakken zit te turen. Als
// popup (Customer 360) stapelen dezelfde blokken onder elkaar.
// Icoon per bijlagetype; de naam beslist als het mimetype ontbreekt.
const hdBijlageIcoon = (mime, naam) => {
  const m = String(mime || '').toLowerCase(); const n = String(naam || '').toLowerCase();
  if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(n)) return Camera;
  if (m === 'application/pdf' || /\.pdf$/.test(n)) return FileText;
  if (/spreadsheet|excel|csv/.test(m) || /\.(xlsx?|csv)$/.test(n)) return FileSpreadsheet;
  if (/zip|compressed/.test(m) || /\.(zip|rar|7z)$/.test(n)) return FileArchive;
  return File;
};
// Hoe een bijlage heet op het scherm. Elke inkomende mail brengt naast de echte
// bestanden ook "original_email.eml" mee: de ruwe mail zelf. Die naam zegt een
// medewerker niets, dus tonen we waar het om gaat. De rest houdt zijn eigen naam.
const hdBijlageWeergave = (a) => {
  const naam = hdText(a && (a.name || a.filename)) || 'bijlage';
  return /^original_email\.eml$/i.test(naam) ? 'Oorspronkelijke e-mail (.eml)' : naam;
};
const hdIsBeeld = (a) => String((a && a.mimetype) || '').toLowerCase().startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(hdText(a && (a.name || a.filename)) || '');

// De HTML van een bericht, mét de foto's die in de mail zelf stonden (04-10-2026).
// Het bronsysteem bewaart zo'n foto als bijlage; de server zet op de <img> het
// merkteken data-hd-bijlage="<id>" (util/helpdeskHtml.js). Een gewone src kan de
// Authorization-header niet meesturen, dus haalt `laadBeeld(id)` het beeld op en
// krijgt de <img> een object-URL. Klik = groot bekijken (`onBeeld`).
const HdHtml = ({ html, className, laadBeeld, onBeeld }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !laadBeeld) return undefined;
    let weg = false;
    el.querySelectorAll('img[data-hd-bijlage]').forEach((img) => {
      const attId = Number(img.getAttribute('data-hd-bijlage'));
      if (!Number.isInteger(attId)) return;
      laadBeeld(attId).then((url) => {
        if (weg) return;
        img.src = url;
        img.classList.add('hd-beeld');
        img.title = 'Klik om groot te bekijken';
        img.onclick = () => onBeeld && onBeeld(attId);
      }).catch(() => { if (!weg) img.classList.add('hd-beeld-fout'); });
    });
    return () => { weg = true; };
  }, [html]);   // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: html }} />;
};

// Miniatuur van een beeldbijlage die NIET in de tekst van de mail staat: een foto
// wil je zien, niet eerst downloaden. Klik = groot bekijken.
const HdBeeldMiniatuur = ({ a, laad, onOpen }) => {
  const [url, setUrl] = useState(null);
  const [mislukt, setMislukt] = useState(false);
  useEffect(() => {
    let weg = false;
    laad(a).then((u) => { if (!weg) setUrl(u); }).catch(() => { if (!weg) setMislukt(true); });
    return () => { weg = true; };
  }, [a.id, a.local_id]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (mislukt) return null;
  const naam = hdBijlageWeergave(a);
  return (
    <button type="button" onClick={onOpen} title={`${naam} — klik om groot te bekijken`}
      className="block rounded-lg overflow-hidden border border-slate-600/50 bg-slate-900/40 hover:border-lime-400/60 transition-colors">
      {url ? <img src={url} alt={naam} className="max-h-40 max-w-full object-contain" /> : <div className="h-24 w-36 animate-pulse bg-slate-700/40" />}
    </button>
  );
};

// Een beeld groot bekijken, over het scherm, met de downloadknop erbij. Sluit met
// Escape, het kruisje of een klik naast het beeld.
const HdBeeldKijker = ({ kijker, onSluit, onDownload }) => {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onSluit(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onSluit]);
  const naam = hdBijlageWeergave(kijker.a);
  const knop = 'w-9 h-9 inline-flex items-center justify-center rounded-full bg-slate-900/80 text-white hover:bg-slate-700 border border-white/20';
  return ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9600, background: 'rgba(2,6,23,.85)' }} onClick={onSluit} role="dialog" aria-label={naam}>
      <div className="absolute top-3 right-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => onDownload(kijker.a)} className={knop} title={`${naam} downloaden`} aria-label={`${naam} downloaden`}><Download size={16} /></button>
        <button type="button" onClick={onSluit} className={knop} title="Sluiten" aria-label="Sluiten"><X size={16} /></button>
      </div>
      <img src={kijker.url} alt={naam} onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" style={{ background: '#fff' }} />
    </div>,
    document.body,
  );
};

// De paperclip in de kop van een bericht: klik = de bijlagen van dat bericht, elk
// meteen te downloaden (een foto ook te bekijken), zonder het bericht open te
// klappen of naar de lijst onderaan te scrollen.
const HdBerichtBijlagen = ({ bijlagen, onDownload, onBekijk }) => {
  const [open, setOpen] = useState(false);
  const knopRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const klik = (e) => { if (knopRef.current && !knopRef.current.contains(e.target) && !e.target.closest?.('[data-dropdown-panel]')) setOpen(false); };
    const toets = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', klik); document.addEventListener('keydown', toets);
    return () => { document.removeEventListener('mousedown', klik); document.removeEventListener('keydown', toets); };
  }, [open]);
  const n = bijlagen.length;
  return (
    <>
      <button ref={knopRef} type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} aria-expanded={open}
        title={`${n} bijlage${n === 1 ? '' : 'n'} bij dit bericht — klik om te downloaden`}
        className="inline-flex items-center gap-0.5 h-5 px-1.5 rounded border border-slate-600/60 text-[10px] text-slate-300 hover:text-lime-300 hover:border-lime-500/50 shrink-0">
        <Paperclip size={10} />{n}<Download size={10} />
      </button>
      {open && <DropdownPanel anchorRef={knopRef} minWidth={260} maxWidth={380} className="bg-slate-800 border border-slate-600 rounded-lg shadow-2xl p-1 max-h-72 overflow-y-auto">
        {bijlagen.map((a) => {
          const Icoon = hdBijlageIcoon(a.mimetype, hdText(a.name) || '');
          const naam = hdBijlageWeergave(a);
          return (
            <div key={a.id || `l${a.local_id}`} className="flex items-center gap-1 px-1 py-0.5 rounded hover:bg-slate-700/60" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={() => { setOpen(false); onDownload(a); }} title={`${naam} downloaden`} className="flex-1 min-w-0 flex items-center gap-2 px-1 py-1 text-xs text-slate-200 text-left">
                <Icoon size={12} className="shrink-0 w-3 text-slate-400" />
                <span className="truncate">{naam}</span>
                {hdBytes(a.file_size) && <span className="text-[11px] text-slate-500 shrink-0">{hdBytes(a.file_size)}</span>}
                <Download size={12} className="shrink-0 w-3 ml-auto text-slate-400" />
              </button>
              {hdIsBeeld(a) && <button type="button" onClick={() => { setOpen(false); onBekijk(a); }} title={`${naam} bekijken`} aria-label={`${naam} bekijken`} className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-400 hover:text-lime-300 shrink-0"><Eye size={12} /></button>}
            </div>
          );
        })}
      </DropdownPanel>}
    </>
  );
};

// Rechtstreekse downloadlink in Odoo voor een bijlage — enkel als het ticket zijn
// Odoo-URL draagt (admin/ET; voor partnerrollen strippen we die velden). Het
// standaard Odoo-pad /web/content/<id>?download=true werkt voor wie daar
// aangemeld is.
const odooBijlageUrl = (ticket, a) => {
  try {
    if (!ticket || !ticket.url || !a || !a.id) return null;
    const u = new URL(ticket.url);
    return `${u.origin}/web/content/${encodeURIComponent(a.id)}?download=true`;
  } catch { return null; }
};
const HelpdeskTicketModal = ({ id, token, caps, stages, companies, onClose, onChanged, nav = null, onNav = null, fullPage = false, showBrand = true, onOpenCustomer360 = null, orders = [], magNieuweTag = false, magKennisbank = false, magAi360 = false }) => {
  const api = useMemo(() => hdApi(token), [token]);
  const can = (c) => caps.has(c);
  // Sjablonen, standaardteksten en AI-voorstel: enkel de interne rollen — dezelfde
  // twee rollen die het merk mogen zien (showBrand), hier bij naam genoemd.
  const interneKennis = showBrand;
  const [t, setT] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  // Vanuit het portaal verstuurde mails (Mail naar klant) met hun status op het ticket.
  const [portalMails, setPortalMails] = useState([]);
  // Bijlagen met enkel een lokale kopie (upload via het portaal, geen ET-id).
  const [localAttachments, setLocalAttachments] = useState([]);
  // Tags bewerken in de kop (potloodje): de kiesbare tags + de wijziging naar ET.
  const [tagLijst, setTagLijst] = useState([]);
  useEffect(() => { if (caps.has('tags')) api.get('/tags').then((d) => setTagLijst(Array.isArray(d) ? d : (d && d.items) || [])).catch(() => {}); }, [api]);   // eslint-disable-line react-hooks/exhaustive-deps
  const wijzigTags = async (ids, namen) => {
    const d = await api.send(`/tickets/${id}`, 'PATCH', { tag_ids: ids });
    setT((x) => (x ? { ...x, tags: namen, tag_ids: (d && d.tag_ids) || ids } : x));
    toast.success('Tags bijgewerkt');
    onChanged && onChanged();
  };
  const nieuweTag = async (naam) => { const nt = await api.send('/tags', 'POST', { name: naam }); setTagLijst((l) => [...l, nt].sort((a, b) => String(a.name).localeCompare(String(b.name)))); return nt; };
  // Klant wijzigen (portaal-koppeling, zie PUT /tickets/:id/customer).
  const [klantWijzigen, setKlantWijzigen] = useState(false);
  const [klantQ, setKlantQ] = useState('');
  const klantKenmerkenHd = useMemo(() => klantKenmerken(orders), [orders]);
  const klantSuggesties = useMemo(() => bouwZoekSuggesties({ query: klantQ, orders, kenmerken: klantKenmerkenHd, groepeerOp: 'ref' }), [klantQ, orders, klantKenmerkenHd]);
  const wijzigKlant = (k) => {
    const ref = String((k && k.ref) || '').trim(); const naam = String((k && (k.naam || k.klant)) || '').trim();
    if (!ref) { toast.error('Kies een klant met klantnummer.'); return; }
    if (!window.confirm(`Dit ticket koppelen aan ${naam || ref} (${ref})? De klant in het bronsysteem verandert niet; er komt een interne notitie op het ticket.`)) return;
    act(async () => { await api.send(`/tickets/${id}/customer`, 'PUT', { reference: ref, name: naam }); setKlantWijzigen(false); setKlantQ(''); }, `Ticket gekoppeld aan ${naam || ref}.`);
  };
  const klantTerug = () => { if (!window.confirm('Terug naar de klant zoals hij in het bronsysteem staat?')) return; act(async () => { await api.send(`/tickets/${id}/customer`, 'DELETE'); }, 'Klantwijziging teruggedraaid.'); };
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // Kanaal van het antwoordblok: 'mail' | 'partner_internal'. Wie de klant niet
  // mag mailen, start (en blijft) op de interne notitie.
  const [vis, setVis] = useState(() => (caps.has('mail') ? 'mail' : 'partner_internal'));
  const [escOpen, setEscOpen] = useState(false);
  // Behandelaars: de interne (Energy Together — een partnerrol krijgt die lijst
  // leeg en ziet hun namen nooit) en de externe (mensen van de partner met het
  // vinkje "Ticket behandelaar", binnen de rechten van de lezer).
  const [assignees, setAssignees] = useState([]);
  const [behExtern, setBehExtern] = useState([]);
  useEffect(() => {
    api.get('/assignees').then((d) => { setAssignees(d.items || []); setBehExtern(d.extern || []); }).catch(() => { setAssignees([]); setBehExtern([]); });
  }, [api]);
  const [log, setLog] = useState(null);
  // Snelle antwoorden: sjablonen met de module 'helpdesk-bericht', door de server
  // ingevuld met de gegevens van dit ticket en als platte tekst (een ticketbericht
  // is geen opgemaakte mail). Eén keer per ticket opgehaald.
  const [snippets, setSnippets] = useState({ klant: [], intern: [] });
  // Live-verversing van het open ticket. `stand` = de versiestempel van het
  // ticket in de cache (GET /tickets/:id/stand, één tekst, geen inhoud). Hij
  // vertrekt SAMEN met het detail en is dus hoogstens ouder dan wat op het scherm
  // staat: verandert hij daarna (automatische regel, collega, bronsysteem), dan
  // komt het detail stil opnieuw binnen — state wordt vervangen, niet gewist, dus
  // het antwoord in opmaak, de open tab en de uitgeklapte berichten blijven staan.
  const stand = useRef(null);
  const standBezig = useRef(false);
  const vorigeT = useRef(null);
  const [stilBezig, setStilBezig] = useState(false);
  const [bijgewerktOm, setBijgewerktOm] = useState(null);
  const reload = useMemo(() => async ({ stil = false } = {}) => {
    if (!stil) setErr(null);
    try {
      const [s, d] = await Promise.all([
        api.get(`/tickets/${id}/stand`).catch(() => null),
        api.get(`/tickets/${id}/detail`), // ticket + berichten + bijlagen in één call
      ]);
      stand.current = (s && s.versie) || null;
      // Stil bijgewerkt: zeg in één regel WAT er wijzigde, zodat een veld dat
      // verandert terwijl je ernaar kijkt geen raadsel is.
      if (stil && vorigeT.current && d.ticket) {
        const oud = vorigeT.current; const nw = d.ticket; const wat = [];
        if (String(oud.stage_id) !== String(nw.stage_id)) wat.push(`status → ${nw.stage_name || '—'}`);
        if (JSON.stringify(oud.tags || []) !== JSON.stringify(nw.tags || [])) wat.push(`tags → ${(nw.tags || []).join(', ') || 'geen'}`);
        if (showBrand && ((oud.assignee && oud.assignee.id) || null) !== ((nw.assignee && nw.assignee.id) || null)) wat.push(`behandelaar → ${nw.assignee ? nw.assignee.name : 'niet toegewezen'}`);
        if ((oud.action_required_by || '') !== (nw.action_required_by || '')) wat.push('actie vereist van');
        if (wat.length) toast.info(`Ticket bijgewerkt: ${wat.join(' · ')}`);
      }
      vorigeT.current = d.ticket || null;
      setBijgewerktOm(Date.now());
      setT(d.ticket || null);
      setMessages(Array.isArray(d.messages) ? d.messages : []);
      setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
      setPortalMails(Array.isArray(d.portal_mails) ? d.portal_mails : []);
      setLocalAttachments(Array.isArray(d.local_attachments) ? d.local_attachments : []);
      // Het logboek hangt aan dezelfde reload: na een statuswijziging, een bericht
      // of een verstuurde mail moet het meteen kloppen. Aparte call, want het
      // combineert audit_logs met de ticketberichten.
      api.get(`/tickets/${id}/log`).then((l) => setLog(l.entries || [])).catch(() => { if (!stil) setLog([]); });
    } catch (e) { if (!stil) setErr(e.message); }
  }, [api, id, showBrand]);
  useEffect(() => { reload(); }, [reload]);
  // Om de 20 s (enkel in een zichtbaar tabblad): is de versiestempel veranderd?
  // Niet tijdens een eigen actie — die herlaadt zelf en zet de stempel opnieuw.
  const ververs = async ({ altijd = false } = {}) => {
    if (busy || standBezig.current) return;
    standBezig.current = true;
    try {
      let anders = altijd;
      if (!altijd) {
        const s = await api.get(`/tickets/${id}/stand`);
        if (!s || !s.versie) return;
        if (!stand.current) { stand.current = s.versie; setBijgewerktOm(Date.now()); return; }
        anders = s.versie !== stand.current;
        if (!anders) { setBijgewerktOm(Date.now()); return; }
      }
      setStilBezig(true);
      await reload({ stil: true });
      if (!altijd && onChanged) onChanged();
    } catch (e) { /* een gemiste controle is geen fout: de volgende haalt ze in */ }
    finally { standBezig.current = false; setStilBezig(false); }
  };
  useHdVerversing(() => ververs(), HD_TICKET_POLL_MS, true);
  useEffect(() => {
    // Standaardteksten zijn interne kennis: een partnerrol vraagt ze niet eens op
    // (de server zou weigeren).
    if (!interneKennis || !caps.has('message')) return;
    api.get(`/tickets/${id}/snippets`).then((d) => setSnippets({ klant: d.snippets || [], intern: d.intern || [] })).catch(() => setSnippets({ klant: [], intern: [] }));
  }, [api, id, caps, interneKennis]);
  // Elke actie: uitvoeren, herladen, de lijst verwittigen — en bij succes een
  // toast. Zonder melding is het gissen of er iets gebeurd is: de keuzelijst
  // springt terug en de pagina flikkert hooguit even.
  const act = async (fn, melding) => {
    setBusy(true); setErr(null);
    try { await fn(); await reload(); onChanged && onChanged(); if (melding) toast.success(melding); return true; }
    catch (e) { setErr(e.message); return false; }
    finally { setBusy(false); }
  };
  // Behandelaar: wie hem ziet, wie hem mag wijzigen en wie er te kiezen valt.
  const arbExtern = !!t && (t.action_required_by === 'partner' || t.action_required_by === 'customer');
  const toonBehandelaar = showBrand || behExtern.length > 0 || !!(t && t.assignee);
  const magToewijzen = showBrand ? can('status') : (can('assign') && arbExtern && behExtern.length > 0);
  const behOpties = hdBehandelaarOpties(t, assignees, behExtern, t ? (companies[t.company_id] || '') : '');
  const arbLabel = (v) => (v === 'partner' ? 'Partner' : v === 'customer' ? 'Klant' : (showBrand ? 'Energy Together' : 'Support'));
  // Alle bijlagen in één lijst: die uit het bronsysteem (met local_id als er een
  // kopie is) plus kopieën zonder ET-id.
  const alleBijlagen = [...attachments, ...localAttachments];
  // Welke bijlage hoort bij welk bericht. Het bronsysteem geeft sinds de
  // API-uitbreiding van 09-2026 een message_id mee, en levert nu ook de bijlagen
  // van INKOMENDE mails (die ontbraken daarvóór volledig). Daarmee kunnen we ze
  // tonen wáár ze horen: onder de mail waarmee de klant ze stuurde, in plaats van
  // alleen in één lijst onderaan waar je moet raden waar ze vandaan komen.
  const bijlagenPerBericht = useMemo(() => {
    const m = new Map();
    for (const a of attachments) {
      if (a.message_id == null) continue;
      const k = Number(a.message_id);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    }
    return m;
  }, [attachments]);
  // Downloaden: een lokale kopie (portaal-upload) rechtstreeks, elke andere
  // bijlage via de ET-API (sinds 11-09-2026 levert die de inhoud). Met de
  // Authorization-header, dus via fetch + blob.
  const bijlagePad = (a) => (a.local_id ? `/api/helpdesk/tickets/${id}/attachments/${a.local_id}/download` : `/api/helpdesk/tickets/${id}/attachments/et/${a.id}/download`);
  // Beelden (foto in de mail, miniatuur, groot bekijken): één keer ophalen per
  // bijlage en bewaren zolang dit ticket openstaat; daarna de object-URL's vrijgeven.
  const beeldCache = useRef(new Map());
  useEffect(() => {
    const cache = beeldCache.current;
    return () => { for (const p of cache.values()) p.then((u) => URL.revokeObjectURL(u)).catch(() => {}); cache.clear(); };
  }, [id]);
  const laadBeeld = (a) => {
    const sleutel = a.local_id ? `l${a.local_id}` : `e${a.id}`;
    const cache = beeldCache.current;
    if (!cache.has(sleutel)) {
      const p = fetch(bijlagePad(a), { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
        .then((b) => URL.createObjectURL(b));
      p.catch(() => cache.delete(sleutel));
      cache.set(sleutel, p);
    }
    return cache.get(sleutel);
  };
  const bijlageMetId = (attId) => attachments.find((a) => Number(a.id) === Number(attId)) || { id: attId, name: 'afbeelding' };
  const laadBeeldId = (attId) => laadBeeld(bijlageMetId(attId));
  const [kijker, setKijker] = useState(null);   // { a, url } — beeld groot bekijken
  const bekijkBeeld = (a) => laadBeeld(a).then((url) => setKijker({ a, url })).catch(() => toast.error('De afbeelding kon niet geladen worden.'));
  const bekijkBeeldId = (attId) => bekijkBeeld(bijlageMetId(attId));
  const downloadBijlage = async (a) => {
    try {
      const pad = bijlagePad(a);
      const r = await fetch(pad, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || `HTTP ${r.status}`); }
      downloadBlob(hdText(a.name) || hdText(a.filename) || 'bijlage', await r.blob());
    } catch (e) { toast.error(e.message); }
  };
  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > HD_MAX_BIJLAGE_BYTES) { toast.error(`"${file.name}" is ${hdBytes(file.size)}; een bijlage mag hoogstens ${hdBytes(HD_MAX_BIJLAGE_BYTES)} zijn.`); return; }
    act(async () => { const data = await hdFileToB64(file); await api.send(`/tickets/${id}/attachments`, 'POST', { name: file.name, mimetype: file.type, data }); }, `Bijlage "${file.name}" toegevoegd`);
  };
  const setStage = (stage_id) => {
    const s = stages.find((x) => String(x.id) === String(stage_id));
    act(async () => { await api.send(`/tickets/${id}`, 'PATCH', { stage_id: Number(stage_id) }); }, s ? `Status gewijzigd naar ${s.name}` : 'Status gewijzigd');
  };
  const setArb = (v) => act(async () => { await api.send(`/tickets/${id}`, 'PATCH', { action_required_by: v }); }, `Actie vereist van: ${arbLabel(v)}`);
  const escalate = async (reden) => {
    const ok = await act(async () => {
      if (reden) await api.send(`/tickets/${id}/messages`, 'POST', { body: `Reden van escalatie: ${reden}`, visibility: 'partner_internal' });
      await api.send(`/tickets/${id}/escalate`, 'POST', {});
    }, `Geëscaleerd naar ${arbLabel('energy_together')}`);
    if (ok) setEscOpen(false);
  };
  const conns = (t && t.contract && t.contract.connections) || [];
  // Huidige status voorgeselecteerd. Staat hij niet in de partner-selecteerbare
  // lijst, dan toont de keuzelijst hem als eerste, niet-kiesbare regel.
  // Fases zijn per team geconfigureerd (API-update 25-09-2026: elke fase draagt
  // team_ids). Toon voor dit ticket enkel de fases van zijn team; een fase zonder
  // team_ids (terugval uit de cache) geldt overal.
  const stagesVoorTeam = useMemo(() => {
    const teamId = t && t.team_id != null ? Number(t.team_id) : null;
    if (!teamId) return stages;
    const perTeam = stages.filter((s) => !Array.isArray(s.team_ids) || !s.team_ids.length || s.team_ids.map(Number).includes(teamId));
    // "Nieuw" (Odoo id 1, de fase mét ontvangstbevestiging) is nooit een keuze:
    // een ticket verplaatsen naar die fase kan het sjabloon van het bronsysteem
    // alsnog laten mailen. "Nieuw (zonder auto bevestiging)" blijft wel kiesbaar.
    const zonderNieuw = (perTeam.length ? perTeam : stages).filter((s) => !(Number(s.id) === 1 || /^s*nieuws*$/i.test(String(s.name || ''))));
    return zonderNieuw;
  }, [stages, t]);
  const huidigeStageInLijst = !!t && stagesVoorTeam.some((s) => String(s.id) === String(t.stage_id));
  const stageWaarde = huidigeStageInLijst ? String(t.stage_id) : '';
  const onStageChange = (e) => { const v = e.target.value; if (!v || v === stageWaarde) return; setStage(v); };
  const arbWaarde = (t && t.action_required_by) || '';
  const klantNaarC360 = onOpenCustomer360 && t && t.customer && t.customer.name
    // In een NIEUW tabblad (browser) of een nieuw app-venster (app): het ticket
    // blijft staan. window.open mét opener, zodat Chrome de navigatie niet met de
    // geïnstalleerde app opvangt (zie openTicketInNewTab). Popup geblokkeerd →
    // in deze tab.
    ? () => { const target = klantTarget(t.customer.name, t.customer.reference); const w = window.open(targetPageUrl(target, 'customer360'), '_blank'); if (w) w.opener = null; else onOpenCustomer360(target); }
    : null;
  const teamNaam = t ? (showBrand ? hdText(t.team_name) : hideEtBrand(hdText(t.team_name))) : '';
  const laatsteActiviteit = t ? (t.write_date || t.updated_at) : null;

  // ── Layout (07-09-2026): één helpdeskoppervlak i.p.v. losse kaarten ──────────
  // Boven: een compacte, plakkende actiebalk (nummer · status · klant · onderwerp
  // links, de acties rechts, "Antwoorden" als primaire knop). Daaronder één
  // metadata-regel. Links (¾) de conversatie als hoofdinhoud met tabs
  // Conversatie | Interne notities | Activiteit en onderaan een composer die
  // meeplakt; rechts (¼) een compacte context-kolom (klant, contract en
  // aansluitingen, bijlagen, details) met separators i.p.v. kaarten. Alle acties,
  // velden en API-calls van vóór de herindeling zijn behouden.
  const [gespreksTab, setGespreksTab] = useState('alles'); // filter van de draad: alles | conversatie (klant) | intern
  // De oorspronkelijke e-mail (de klantvraag) staat standaard OPEN (vraag van
  // 08-10-2026): wie een ticket opent, leest de vraag meteen zonder te klikken.
  // Inklappen kan nog altijd met de kop.
  const [mailUitgeklapt, setMailUitgeklapt] = useState(true);
  // Welke berichten opengeklapt staan. De draad toont het NIEUWSTE bericht
  // bovenaan en open; al het oudere staat ingeklapt tot één regel (naam, datum,
  // eerste woorden) — wie meer wil, klikt. Zo is de laatste stand meteen te zien
  // zonder eerst door de hele historiek te scrollen.
  const [openBerichten, setOpenBerichten] = useState(() => new Set());
  const wisselBericht = (sleutel) => setOpenBerichten((cur) => { const n = new Set(cur); if (n.has(sleutel)) n.delete(sleutel); else n.add(sleutel); return n; });
  const [alleAansluitingen, setAlleAansluitingen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const composerRef = useRef(null);
  const berichtenPubliek = useMemo(() => messages.filter((m) => m.visibility !== 'partner_internal'), [messages]);
  const berichtenIntern = useMemo(() => messages.filter((m) => m.visibility === 'partner_internal'), [messages]);
  // De filter boven de draad. Bewust ZONDER invloed op het kanaal van het antwoordblok.
  const kiesTab = (tab) => setGespreksTab(tab);
  const initialen = (naam) => {
    const delen = String(naam || '').trim().split(/\s+/).filter(Boolean);
    return delen.length ? (delen[0][0] + (delen.length > 1 ? delen[delen.length - 1][0] : '')).toUpperCase() : '?';
  };
  const AVATAR_KLASSE = { klant: 'border hd-pill-sky', intern: 'border hd-pill-amber', mail: 'border hd-pill-emerald', extern: 'border hd-pill-indigo', overig: 'border hd-pill-slate' };
  const RIJ_KLASSE = { klant: 'bg-sky-500/[.06]', intern: 'bg-amber-500/[.07]', mail: 'bg-emerald-500/[.05]', extern: 'bg-indigo-500/[.06]', overig: '' };
  const klantNaam = t ? (t.customer?.name || '—') : '';
  const stageLijst = stagesVoorTeam;
  // Afsluiten/heropenen met één knop: de gesloten status (bij voorkeur
  // "Afgehandeld") en de open status om naar terug te keren (bij voorkeur
  // "Heropend", anders "In behandeling").
  const dichteStage = stageLijst.find((s) => s.is_closed && /afgehandeld|opgelost|solved|done|closed/i.test(s.name || '')) || stageLijst.find((s) => s.is_closed) || null;
  const heropenStage = stageLijst.find((s) => !s.is_closed && /heropend|reopen/i.test(s.name || '')) || stageLijst.find((s) => !s.is_closed && /behandeling|progress/i.test(s.name || '')) || stageLijst.find((s) => !s.is_closed) || null;
  // Na "Verstuur mail" gaat het ticket naar de open fase "In behandeling".
  const behandelingStage = stageLijst.find((s) => !s.is_closed && /behandeling|progress/i.test(s.name || '')) || null;
  const isDicht = !!(t && (t.stage_is_closed === true || (dichteStage && String(t.stage_id) === String(dichteStage.id))));
  // Rechten in de kop (27-09-2026): status wijzigen, afsluiten/heropenen en tags
  // zijn drie aparte rechten. De statuskiezer toont een gesloten fase alleen aan
  // wie mag afsluiten, en bestaat op een gesloten ticket alleen voor wie mag
  // heropenen — de server toetst hetzelfde.
  const magStatusKiezen = can('status') && (!isDicht || can('close'));
  const statusKeuzes = can('close') ? stageLijst : stageLijst.filter((s) => !s.is_closed);
  const magKopActies = can('status') || can('escalate') || can('close') || can('tags');
  const knopSecundair ='inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium bg-slate-700/70 hover:bg-slate-600 text-slate-200 disabled:opacity-50 whitespace-nowrap';
  const selectKlasse = 'h-8 px-2 bg-slate-700/70 border border-slate-600/70 rounded-md text-xs text-slate-200 max-w-[200px]';

  // ── Kop (21-09-2026) ────────────────────────────────────────────────────────
  // Drie regels, van belangrijk naar bijkomstig:
  //   1. nummer + status, en het ONDERWERP voluit (twee regels als het moet) — dat
  //      werd vroeger weggeduwd tot "R…" door drie keuzelijsten en vier knoppen;
  //   2. wie de klant is;
  //   3. de eigenschappen als chips die je aanklikt om ze te wijzigen (status,
  //      actie vereist van, behandelaar, tags) en rechts de twee ticketacties.
  // Er staat hier bewust GEEN groene knop: de enige primaire actie op dit scherm
  // is versturen, en die staat in het antwoordblok. Wat je zelden nodig hebt
  // (nieuw tabblad, klant wijzigen) zit achter het ⋯-menu.
  const [meerOpen, setMeerOpen] = useState(false);
  const meerRef = useRef(null);
  const meerKnopRef = useRef(null);
  useEffect(() => {
    if (!meerOpen) return undefined;
    const weg = (e) => { if (meerRef.current && !meerRef.current.contains(e.target) && !e.target.closest?.('[data-dropdown-panel]')) setMeerOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMeerOpen(false); };
    document.addEventListener('mousedown', weg); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', weg); document.removeEventListener('keydown', esc); };
  }, [meerOpen]);
  const chipKlasse = 'flex sm:inline-flex items-center h-9 sm:h-8 min-w-0 max-w-full rounded-full border border-slate-600/70 bg-slate-700/70 pl-3 text-[10px] uppercase tracking-wide text-slate-400';
  const chipSelect = 'h-[30px] min-w-0 flex-1 sm:flex-none sm:max-w-[190px] pl-1.5 pr-1 bg-transparent border-0 rounded-full text-xs font-semibold normal-case tracking-normal text-slate-100 cursor-pointer focus:outline-none disabled:opacity-60';
  const menuItem = 'w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-slate-200 hover:bg-slate-700/70 rounded-md';
  const meerItems = [
    fullPage && { sleutel: 'tab', Icoon: ExternalLink, label: 'Openen in een nieuw tabblad', doe: () => openTicketInNewTab(id) },
    can('customer') && { sleutel: 'klant', Icoon: Pencil, label: 'Klant wijzigen', doe: () => setKlantWijzigen(true) },
  ].filter(Boolean);

  const actiebalk = (
    <div className={fullPage ? 'lg:sticky lg:top-0 z-30 -mx-1 px-1' : ''}>
      <div className="bg-slate-900/95 backdrop-blur border-b border-slate-700/60 px-4 pt-3 pb-3 space-y-2.5">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-slate-400 shrink-0">#{t ? t.ref : '…'}</span>
              {t && hdStageBadge(t, 'shrink-0')}
              {t && t.escalated_to_et && <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 text-[11px] flex items-center gap-1 shrink-0"><ArrowUpCircle size={11} />Geëscaleerd</span>}
              {t && !can('status') && <span className="flex items-center gap-1 text-[11px] text-slate-500">actie vereist van {hdArbBadge(t.action_required_by, showBrand)}</span>}
            </div>
            <h3 className="mt-1.5 text-base sm:text-lg font-semibold text-slate-100 leading-snug break-words line-clamp-3 sm:line-clamp-2" title={t ? t.subject : ''}>{t ? t.subject : 'Laden…'}</h3>
            {t && (
              <div className="mt-1 text-xs text-slate-400 flex items-center gap-x-2 gap-y-0.5 flex-wrap">
                <span className="inline-flex items-center gap-1 text-slate-200 font-medium"><User size={12} className="shrink-0 text-slate-500" />{klantNaam}</span>
                {t.customer?.reference && <span className="font-mono text-[11px]">{t.customer.reference}</span>}
                <span className="inline-flex items-center gap-1"><Building2 size={11} className="shrink-0 text-slate-500" />{companies[t.company_id] || t.company_id}{teamNaam ? ` · ${teamNaam}` : ''}</span>
                <span className="inline-flex items-center gap-1 text-slate-500"><CalendarDays size={11} className="shrink-0" />aangemaakt {hdFmt(t.create_date)}</span>
              </div>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-1" ref={meerRef}>
            {/* Het ticket volgt wijzigingen vanzelf (versiestempel om de 20 s); de
                knop haalt het detail meteen opnieuw op, zonder de pagina te herladen. */}
            {t && <HdVerversKnop klein onClick={() => ververs({ altijd: true })} bezig={stilBezig} om={bijgewerktOm} titel="Ticket vernieuwen (gebeurt ook automatisch)" />}
            {t && meerItems.length > 0 && (
              <>
                <button ref={meerKnopRef} type="button" onClick={() => setMeerOpen((o) => !o)} aria-haspopup="menu" aria-expanded={meerOpen} title="Meer acties" className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-slate-100 hover:bg-slate-700/70"><MoreHorizontal size={16} /></button>
                {meerOpen && (
                  <DropdownPanel anchorRef={meerKnopRef} width={230} className="bg-slate-800 border border-slate-600 rounded-lg shadow-2xl p-1">
                    {meerItems.map((it) => <button key={it.sleutel} type="button" role="menuitem" onClick={() => { setMeerOpen(false); it.doe(); }} className={menuItem}><it.Icoon size={13} className="shrink-0 text-slate-400" />{it.label}</button>)}
                  </DropdownPanel>
                )}
              </>
            )}
            {!fullPage && <button onClick={onClose} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-700/70" title="Sluiten"><X size={18} /></button>}
          </div>
        </div>
        {t && magKopActies && (
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:flex-wrap">
            {magStatusKiezen && statusKeuzes.length > 0 && (
              <label className={chipKlasse} title="Status wijzigen">
                <span className="shrink-0">Status</span>
                <select value={stageWaarde} onChange={onStageChange} disabled={busy} className={chipSelect}>
                  {!huidigeStageInLijst && <option value="">{t.stage_name || '—'}</option>}
                  {statusKeuzes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
            )}
            {can('status') && (
              <label className={chipKlasse} title="Wie moet nu iets doen?">
                <span className="shrink-0">Actie van</span>
                <select value={arbWaarde} onChange={(e) => { const v = e.target.value; if (v && v !== arbWaarde) setArb(v); }} disabled={busy} className={chipSelect}>
                  <option value="" disabled>kies…</option>
                  <option value="partner">Partner</option>
                  <option value="customer">Klant</option>
                  <option value="energy_together">{showBrand ? 'Energy Together' : 'Support'}</option>
                </select>
              </label>
            )}
            {/* Behandelaar. Intern (admin/ET-dossier): de interne behandelaars, en bij
                "Actie vereist van" Partner of Klant ook de externe van het label van
                het ticket. Partnerrol: alleen met het recht "Behandelaar toewijzen",
                alleen bij Partner of Klant, en alleen de externe behandelaars binnen
                de eigen rechten; een interne behandelaar heet er "Support". Amber
                zolang het ticket van niemand is. */}
            {magToewijzen && (
              <label className={`${chipKlasse} ${t.assignee ? '' : '!border-amber-500/50 !bg-amber-500/10'}`} title={showBrand ? 'Toewijzen: intern, of aan een behandelaar van de partner als de actie bij Partner of Klant ligt' : 'Toewijzen aan een behandelaar binnen je eigen rechten'}>
                <span className="shrink-0">Behandelaar</span>
                <select value={t.assignee ? String(t.assignee.id ?? 'x') : ''} disabled={busy} className={chipSelect}
                  onChange={(e) => { const v = e.target.value; act(async () => { await api.send(`/tickets/${id}/assignee`, 'PUT', { user_id: v ? Number(v) : null }); }, v ? 'Ticket toegewezen' : 'Toewijzing weggehaald'); }}>
                  <option value="">Niet toegewezen</option>
                  {behOpties.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  {t.assignee && !behOpties.some((u) => u.id === t.assignee.id) && <option value={String(t.assignee.id ?? 'x')} disabled={t.assignee.id == null}>{t.assignee.name}</option>}
                </select>
              </label>
            )}
            {toonBehandelaar && !magToewijzen && (t.assignee
              ? <span className="hd-pill hd-pill-sky shrink-0 inline-flex items-center gap-1" title="Behandelaar van dit ticket"><User size={11} className="shrink-0" />{t.assignee.name}</span>
              : <span className="hd-pill hd-pill-amber shrink-0 inline-flex items-center gap-1" title="Dit ticket heeft nog geen behandelaar"><User size={11} className="shrink-0" />Niet toegewezen</span>)}
            <span className="col-span-2 inline-flex items-center gap-1.5 min-w-0 flex-wrap">
              {!(Array.isArray(t.tags) && t.tags.length > 0) && <span className="text-[11px] text-slate-500 shrink-0 inline-flex items-center gap-1" title="Dit ticket heeft geen tags"><Tag size={11} className="shrink-0" />Geen tags</span>}
              {can('tags')
                ? <HdTagEditor huidige={Array.isArray(t.tags) ? t.tags : []} tags={tagLijst} klein={false} onSave={wijzigTags} magNieuw={magNieuweTag} onNieuweTag={nieuweTag} />
                : (Array.isArray(t.tags) && t.tags.length > 0 && <span className="flex items-center gap-1 flex-wrap">{hdTagPillen(t.tags, { klein: false })}</span>)}
            </span>
            {/* De ticketacties als ÉÉN groep, rechts (27-09-2026). Als losse knoppen
                brak de rij op 1280 px tussen de twee: Escaleren bleef rechts staan en
                Afsluiten sprong naar links op de volgende regel. Nu springt de groep
                in haar geheel en blijft ze rechts. Op een telefoon: twee kolommen. */}
            <span className="col-span-2 grid grid-cols-2 gap-2 sm:flex sm:items-center sm:ml-auto">
              {can('escalate') && !t.escalated_to_et && <button onClick={() => setEscOpen(true)} disabled={busy} className={`${knopSecundair} justify-center !h-9 sm:!h-8 !text-purple-200 !bg-purple-700/30 hover:!bg-purple-700/50`}><ArrowUpCircle size={14} />Escaleren</button>}
              {/* Eén klik om af te sluiten (of te heropenen): de gesloten status uit de
                  stagelijst, bij voorkeur "Afgehandeld". */}
              {can('close') && !isDicht && dichteStage && (
                <button type="button" disabled={busy} onClick={() => setStage(dichteStage.id)} className={`${knopSecundair} justify-center !h-9 sm:!h-8 !bg-emerald-500/15 hover:!bg-emerald-500/25 !text-emerald-300 border border-emerald-500/40`} title={`Status op "${dichteStage.name}" zetten`}><Check size={13} />Afsluiten</button>
              )}
              {can('close') && isDicht && heropenStage && (
                <button type="button" disabled={busy} onClick={() => setStage(heropenStage.id)} className={`${knopSecundair} justify-center !h-9 sm:!h-8 border border-amber-500/40 !text-amber-300`} title={`Status op "${heropenStage.name}" zetten`}><RotateCcw size={13} />Heropenen</button>
              )}
            </span>
          </div>
        )}
        {t && !magKopActies && Array.isArray(t.tags) && t.tags.length > 0 && <div className="flex items-center gap-1 flex-wrap">{hdTagPillen(t.tags, { klein: false })}</div>}
      </div>
    </div>
  );

  // De vroegere metadata-regel is opgegaan in de kop (klant, label, aangemaakt) en in
  // de tijdlijn (laatste activiteit). Op zijn plaats: de sprongknoppen voor de telefoon.
  const spring = (doel) => { const el = document.getElementById(doel); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const sprongKnop = 'flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-md text-xs font-medium text-slate-200 bg-slate-700/70 hover:bg-slate-600';
  const metaRegel = t && fullPage && (
    <div className="lg:hidden flex items-center gap-2 px-4 py-2 border-b border-slate-700/40">
      <button type="button" onClick={() => spring('hd-gesprek')} className={sprongKnop}><MessageSquare size={13} />Gesprek ({messages.length})<ChevronDown size={13} /></button>
      <button type="button" onClick={() => spring('hd-logboek')} className={sprongKnop}><Activity size={13} />Logboek{log && log.length ? ` (${log.length})` : ''}<ChevronDown size={13} /></button>
    </div>
  );

  // Oorspronkelijke e-mail (de ticketomschrijving): standaard open, met een kop
  // met afzender, datum en onderwerp; met de kop klap je ze in tot één regel
  // (onderwerp plus de eerste woorden).
  // De bijlagen van een bericht als downloadknoppen, BOVEN de tekst (04-10-2026):
  // bij een lange mail hoef je niet meer naar beneden te scrollen.
  const bijlageKnoppen = (lijst, klasse) => (
    <div className={`flex flex-wrap gap-1.5 ${klasse}`}>
      {lijst.map((a) => {
        const bnaam = hdBijlageWeergave(a);
        const Icoon = hdBijlageIcoon(a.mimetype, hdText(a.name) || '');
        return (
          <button key={a.id} type="button" onClick={() => downloadBijlage(a)} title={`${hdText(a.name) || bnaam} downloaden`}
            className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded border border-slate-600/60 bg-slate-800/40 text-[11px] text-slate-300 hover:text-lime-300 hover:border-lime-500/50">
            <Icoon size={11} className="shrink-0 w-3" />
            <span className="truncate">{bnaam}</span>
            {hdBytes(a.file_size) && <span className="text-slate-500 shrink-0">{hdBytes(a.file_size)}</span>}
            <Download size={11} className="shrink-0 w-3" />
          </button>
        );
      })}
    </div>
  );
  // Foto's die al in de tekst staan, niet nog eens als miniatuur eronder.
  const losseBeeldenVan = (lijst, html) => {
    const inTekst = new Set([...String(html || '').matchAll(/data-hd-bijlage="(\d+)"/g)].map((x) => Number(x[1])));
    return lijst.filter((a) => hdIsBeeld(a) && !inTekst.has(Number(a.id)));
  };
  const miniaturen = (lijst, klasse) => lijst.length > 0 && (
    <div className={`flex flex-wrap gap-2 ${klasse}`}>
      {lijst.map((a) => <HdBeeldMiniatuur key={a.id} a={a} laad={laadBeeld} onOpen={() => bekijkBeeld(a)} />)}
    </div>
  );
  // De oorspronkelijke e-mail staat niet als bericht in de draad; haar bijlagen
  // hangen aan een bericht-id dat de draad niet toont. Die horen dus hier.
  const mailBijlagen = useMemo(() => {
    const getoond = new Set(messages.map((m) => Number(m.id)));
    return attachments.filter((a) => a.message_id != null && !getoond.has(Number(a.message_id)) && a.visibility !== 'partner_internal');
  }, [attachments, messages]);
  const mailHtml = t && (typeof t.description === 'string' ? t.description : hdText(t.description));
  const oorspronkelijkeMail = t && t.description && (
    <div className="px-4 py-3 bg-slate-800/30">
      <div role="button" tabIndex={0} onClick={() => setMailUitgeklapt((v) => !v)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setMailUitgeklapt((v) => !v); } }} className="w-full flex items-center gap-2.5 text-xs text-left cursor-pointer" title={mailUitgeklapt ? 'E-mail inklappen' : 'Volledige e-mail tonen'}>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${AVATAR_KLASSE.klant}`}>{initialen(klantNaam)}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-slate-200 font-medium truncate">{klantNaam}</span>
            <span className="px-1.5 rounded-full text-[10px] font-medium bg-slate-700 text-slate-300 shrink-0">oorspronkelijke e-mail</span>
            {mailBijlagen.length > 0 && <HdBerichtBijlagen bijlagen={mailBijlagen} onDownload={downloadBijlage} onBekijk={bekijkBeeld} />}
            <span className="text-slate-500 ml-auto shrink-0">{hdFmt(t.create_date)}</span>
          </div>
          <div className="text-slate-400 truncate" title={t.subject}>{mailUitgeklapt ? t.subject : `${t.subject} — ${hdPlatteTekst(t.description).slice(0, 120)}`}</div>
        </div>
        {mailUitgeklapt ? <ChevronUp size={14} className="text-slate-500 shrink-0" /> : <ChevronDown size={14} className="text-slate-500 shrink-0" />}
      </div>
      {mailUitgeklapt && mailBijlagen.length > 0 && bijlageKnoppen(mailBijlagen, 'mt-2 pl-9')}
      {mailUitgeklapt && <HdHtml className="mt-2 pl-9 text-sm text-slate-300 hd-html" html={mailHtml} laadBeeld={laadBeeldId} onBeeld={bekijkBeeldId} />}
      {mailUitgeklapt && miniaturen(losseBeeldenVan(mailBijlagen, mailHtml), 'mt-2 pl-9')}
    </div>
  );

  // Eén bericht in de draad: avatar met initialen, naam, soort-chip, tijdstip,
  // inhoud. De soort (klant / medewerker / intern) zit in een zachte achtergrond.
  const bericht = (m, i, open = false) => {
    const soort = hdBerichtSoort(m, t);
    const stijl = HD_BERICHT_STIJL[soort];
    const naam = hdText(m.author_name) || hdText(m.author) || 'Onbekend';
    const sleutel = m.id || `i${i}`;
    const body = typeof m.body === 'string' ? m.body : hdText(m.body);
    const uit = open || openBerichten.has(sleutel);
    const bijlagen = bijlagenPerBericht.get(Number(m.id)) || [];
    return (
      <div key={sleutel} className={`px-4 py-3 flex gap-3 ${RIJ_KLASSE[soort]}`}>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 mt-0.5 ${AVATAR_KLASSE[soort]}`} title={soort === 'mail' ? `Uitgaande mail, verstuurd door ${naam}` : naam}>{soort === 'mail' ? <Mail size={13} /> : initialen(naam)}</span>
        <div className="min-w-0 flex-1">
          <div role="button" tabIndex={0} onClick={() => wisselBericht(sleutel)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); wisselBericht(sleutel); } }} className="w-full flex items-center gap-2 text-xs mb-1 text-left cursor-pointer" title={uit ? 'Inklappen' : 'Uitklappen'}>
            <span className="text-slate-200 font-medium truncate">{naam}</span>
            {stijl.chip && <span title={stijl.titel} className={`inline-flex items-center gap-1 px-1.5 rounded-full text-[10px] font-medium shrink-0 ${stijl.chipCls}`}>{stijl.Icoon && <stijl.Icoon size={10} />}{stijl.chip}</span>}
            {soort === 'mail' && m.mail && m.mail.to && <span className="hidden sm:inline text-[11px] text-slate-400 truncate min-w-0" title={`Aan ${m.mail.to}${m.mail.subject ? ` — ${m.mail.subject}` : ''}`}>→ {m.mail.to}</span>}
            {soort === 'mail' && m.mail && m.mail.posted === false && <span className="text-[10px] text-amber-300 shrink-0" title="De mail is verstuurd, maar de notitie staat nog niet op het ticket in het bronsysteem. Dat wordt automatisch opnieuw geprobeerd.">nog niet in het bronsysteem</span>}
            {bijlagen.length > 0 && <HdBerichtBijlagen bijlagen={bijlagen} onDownload={downloadBijlage} onBekijk={bekijkBeeld} />}
            <span className="hidden sm:inline text-slate-500 ml-auto shrink-0 tabular-nums">{hdFmt(m.date || m.create_date)}</span>
            <span className="sm:hidden ml-auto" />
            {uit ? <ChevronUp size={13} className="text-slate-500 shrink-0" /> : <ChevronDown size={13} className="text-slate-500 shrink-0" />}
          </div>
          <div className="sm:hidden text-[11px] text-slate-500 tabular-nums -mt-0.5 mb-1">{hdFmt(m.date || m.create_date)}</div>
          {uit && soort === 'mail' && m.mail && m.mail.to && <div className="sm:hidden text-xs text-slate-400 break-all"><span className="text-slate-500">Aan:</span> {m.mail.to}</div>}
          {uit && soort === 'mail' && m.mail && m.mail.subject && <div className="text-xs text-slate-400 mb-1.5"><span className="text-slate-500">Onderwerp:</span> {m.mail.subject}</div>}
          {uit && bijlagen.length > 0 && bijlageKnoppen(bijlagen, 'mb-2')}
          {uit
            ? <HdHtml className="text-sm text-slate-200 hd-html" html={body} laadBeeld={laadBeeldId} onBeeld={bekijkBeeldId} />
            : <div className="text-xs text-slate-400 truncate cursor-pointer" onClick={() => wisselBericht(sleutel)}>{hdPlatteTekst(body).slice(0, 160) || '—'}</div>}
          {uit && miniaturen(losseBeeldenVan(bijlagen, body), 'mt-2')}
        </div>
      </div>
    );
  };
  // Nieuwste bovenaan; het eerste (= nieuwste) staat open, de rest ingeklapt.
  const draad = (lijst) => [...lijst].reverse().map((m, i) => bericht(m, i, i === 0));


  // Eén gesprekslijn met een filter in plaats van drie tabbladen (21-09-2026):
  // "Alles" toont klantcontact én interne notities door elkaar, in de tijd — zo
  // lees je het dossier in één beweging. Het logboek staat rechts als tijdlijn.
  const filterKnop = (fid, label, aantal) => (
    <button key={fid} type="button" onClick={() => kiesTab(fid)} aria-pressed={gespreksTab === fid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium ${gespreksTab === fid ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}>
      {label}<span className={`px-1.5 rounded-full text-[10px] ${gespreksTab === fid ? 'bg-lime-500/20' : 'bg-slate-700 text-slate-400'}`}>{aantal}</span>
    </button>
  );
  const draadLijst = gespreksTab === 'intern' ? berichtenIntern : gespreksTab === 'conversatie' ? berichtenPubliek : messages;

  // Het antwoordblok staat BOVEN de draad (vraag van de partners): eerst
  // antwoorden, daaronder de nieuwste communicatie, de oudste onderaan. Wie van
  // kanaal wisselt terwijl de filter het andere kanaal verbergt, gaat naar "Alles":
  // anders verdwijnt het bericht dat je net plaatste meteen uit beeld.
  const antwoordBlok = (
    <HdAntwoordBlok id={id} token={token} kanaal={vis} onKanaal={(k) => { setVis(k); if ((k === 'partner_internal' && gespreksTab === 'conversatie') || (k !== 'partner_internal' && gespreksTab === 'intern')) setGespreksTab('alles'); }} snippets={snippets} showBrand={showBrand} fullPage={fullPage} composerRef={composerRef} caps={caps} interneKennis={interneKennis} magKennisbank={magKennisbank} magAi360={interneKennis && magAi360}
      magAfsluiten={can('close') && !!dichteStage}
      onNaMail={async (modus) => {
        // Statuswissel na een mail: enkel met het recht, en niet als het ticket
        // al in die fase staat. Zonder passende fase in de lijst gebeurt er niets.
        const doel = modus === 'afsluiten' ? dichteStage : behandelingStage;
        if (!doel || !t || String(t.stage_id) === String(doel.id)) return;
        if (!can('status') && !(modus === 'afsluiten' && can('close'))) return;
        await api.send(`/tickets/${id}`, 'PATCH', { stage_id: Number(doel.id) });
      }}
      onGeplaatst={async (melding) => { await reload(); onChanged && onChanged(); if (melding) toast.success(melding); }} />
  );
  // Hoofdoppervlak links: antwoordblok, filter, draad.
  const hoofd = t && (
    <div className="min-w-0 bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden flex flex-col">
      {(can('message') || can('mail')) && antwoordBlok}
      <div id="hd-gesprek" className="scroll-mt-4 flex items-center gap-2 flex-wrap border-b border-slate-700/60 px-4 py-2">
        <span className="text-[11px] text-slate-500 uppercase tracking-wide flex items-center gap-1"><MessageSquare size={12} />Gesprek</span>
        <div className="ml-auto inline-flex items-center gap-0.5 bg-slate-800/60 border border-slate-700/60 rounded-md p-0.5">
          {filterKnop('alles', 'Alles', messages.length)}
          {filterKnop('conversatie', 'Klant', berichtenPubliek.length)}
          {filterKnop('intern', 'Intern', berichtenIntern.length)}
        </div>
      </div>
      <div className={`divide-y divide-slate-700/40 ${fullPage ? '' : 'max-h-[60vh] overflow-y-auto'}`}>
        {draadLijst.length === 0 && !(gespreksTab !== 'intern' && t.description) && (
          <div className="px-4 py-6 text-slate-500 text-sm">{gespreksTab === 'intern' ? `Nog geen interne notities. Wat je hier schrijft, blijft tussen partner en ${showBrand ? 'Energy Together' : 'support'}.` : 'Nog geen berichten.'}</div>
        )}
        {draad(draadLijst)}
        {gespreksTab !== 'intern' && oorspronkelijkeMail}
      </div>
    </div>
  );

  // Context-kolom rechts: compacte secties met separators, geen losse kaarten.
  const sectieKop = (titel, Icoon, extra = null) => (
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <div className="text-[11px] text-slate-500 uppercase tracking-wide flex items-center gap-1"><Icoon size={12} />{titel}</div>
      {extra}
    </div>
  );
  const toonConns = alleAansluitingen ? conns : conns.slice(0, 3);
  // De verstuurde mails staan in het gesprek (rij "uitgaande mail"); in de kolom
  // blijft alleen de mail waarvan de notitie nog niet in het bronsysteem staat.
  const nietGeplaatst = portalMails.filter((m) => !m.posted);
  const zijkolom = t && (
    <div className="min-w-0 space-y-3">
    <div className="min-w-0 bg-slate-800/40 border border-slate-700/50 rounded-xl divide-y divide-slate-700/50 text-sm">
      <div className="px-3.5 py-3">
        {sectieKop('Klant', User)}
        <div className="text-slate-100 font-semibold break-words leading-snug">{t.customer?.name || '—'}</div>
        <div className="text-slate-400 text-xs break-words">{[t.customer?.reference, t.customer?.city].filter(Boolean).join(' · ') || '—'}</div>
        {t.customer_override && (
          <div className="mt-1 text-[11px] text-amber-300/90 flex flex-wrap items-center gap-x-2">
            <span title={t.customer_override.at ? `Gewijzigd op ${hdFmt(t.customer_override.at)}` : undefined}>Gewijzigd in het portaal{t.customer_override.from_ref || t.customer_override.from_name ? ` (was ${[t.customer_override.from_name, t.customer_override.from_ref].filter(Boolean).join(' · ')})` : ''}</span>
            {can('customer') && !t.customer_override.buiten_scope && <button type="button" onClick={klantTerug} disabled={busy} className="underline hover:text-amber-200">Terugzetten</button>}
          </div>
        )}
        {klantWijzigen && (
          <div className="mt-2 relative z-40 space-y-1.5">
            {orders.length ? (
              <KlantZoekVeld label="Nieuwe klant (zelfde energielabel)" placeholder="Zoek op klantnaam, klantnummer, bedrijfsnaam, EAN…"
                value={klantQ} onChange={setKlantQ} onClear={() => setKlantQ('')} clearActief={!!klantQ}
                suggesties={klantSuggesties} onKlant={(k) => wijzigKlant(k)} onGsrn={(g) => wijzigKlant({ naam: g.klant, ref: g.ref })}
                telWoord={{ een: 'aansluiting', meer: 'aansluitingen' }} />
            ) : (
              <div className="flex items-center gap-1.5">
                <input value={klantQ} onChange={(e) => setKlantQ(e.target.value)} placeholder="Klantnummer (bv. EVI/2026/01/0001)" className="flex-1 h-8 px-2 bg-slate-700/60 border border-slate-600/60 rounded-md text-xs text-slate-100 font-mono" />
                <button type="button" onClick={() => wijzigKlant({ ref: klantQ })} disabled={busy || !klantQ.trim()} className="h-8 px-2.5 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-slate-900 rounded-md text-xs font-semibold">Koppelen</button>
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-slate-500 leading-snug">Alleen het portaal volgt de nieuwe klant; in het bronsysteem komt een interne notitie.</span>
              <button type="button" onClick={() => { setKlantWijzigen(false); setKlantQ(''); }} className="text-[11px] text-slate-400 hover:text-slate-200 shrink-0">Annuleren</button>
            </div>
          </div>
        )}
        <div className="text-slate-400 text-xs mt-1 break-words">Verkoper: <span className="text-slate-300">{t.customer?.reseller?.salesperson_name || '—'}</span> · Team: <span className="text-slate-300">{t.customer?.reseller?.team_name || '—'}</span>
          {/* Klant in Lynx en Odoo (vraag van 08-10-2026): dezelfde koppelingen als in de
              klantkop van Customer 360. De server geeft ze alleen aan admin/ET-dossier
              (ticketNaarBuiten); de Lynx-id komt uit de eigen opzoeking op klantnummer. */}
          {showBrand && t.lynx_relation_url && <> · <a href={t.lynx_relation_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300" title="Relatie in Lynx">Lynx relatie<ExternalLink size={11} /></a></>}
          {showBrand && (t.customer_url || t.customer?.url) && <> · <a href={t.customer_url || t.customer.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300" title="Klant in Odoo">Odoo klant<ExternalLink size={11} /></a></>}
        </div>
        {klantNaarC360 && <button onClick={klantNaarC360} title="Open het klantdossier" className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-lime-500/15 hover:bg-lime-500/25 text-lime-300 border border-lime-500/30"><Compass size={14} />Customer 360</button>}
      </div>
      <div className="px-3.5 py-3">
        {sectieKop('Contract & aansluitingen', Plug)}
        <div className="text-slate-200 break-words leading-snug">{hdText(t.contract?.name) || '—'} {hdText(t.contract?.state) && <span className="text-slate-500 text-xs">{hdText(t.contract?.state)}</span>}</div>
        <div className="text-slate-400 text-xs">{hdText(t.contract?.product_group) || '—'} · einde {hdText(t.contract?.date_end) || '—'}</div>
        {conns.length === 0
          ? <div className="text-slate-500 text-xs mt-2">Geen aansluitingen gekoppeld.</div>
          : <div className="mt-2 space-y-1.5">
            {toonConns.map((c, i) => (
              <div key={c.gsrn || i} className="text-xs leading-snug">
                <span className="font-mono text-slate-200">{hdText(c.gsrn)}</span>
                <span className="text-slate-400"> · {hdText(c.market) || '—'}{hdText(c.status) ? ` · ${hdText(c.status)}` : ''}</span>
                {hdText(c.address) && <div className="text-slate-500 break-words">{hdText(c.address)}</div>}
              </div>
            ))}
            {conns.length > 3 && <button onClick={() => setAlleAansluitingen((v) => !v)} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">{alleAansluitingen ? <><ChevronUp size={12} />Minder tonen</> : <><ChevronDown size={12} />Alle {conns.length} aansluitingen</>}</button>}
          </div>}
      </div>
      {/* Elke mail die vanuit dit ticket verstuurd is, ook als het bronsysteem de
          plaatsing als bericht weigerde — dan staat hij hier met de fout en een
          knop om het opnieuw te proberen (de mail zelf wordt nooit opnieuw verzonden). */}
      {nietGeplaatst.length > 0 && <div className="px-3.5 py-3">
        {sectieKop(`Mail nog niet in het bronsysteem (${nietGeplaatst.length})`, Mail)}
        <div className="space-y-1.5">
          {nietGeplaatst.map((m) => (
            <div key={m.id} className={`text-xs rounded-lg border px-2.5 py-1.5 ${m.posted ? 'border-slate-700/50 bg-slate-800/30' : 'border-amber-500/40 bg-amber-500/10'}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-slate-300">
                <span className="font-medium truncate" title={m.subject || ''}>{m.subject || '(zonder onderwerp)'}</span>
                <span className="text-slate-500">→ {m.to}</span>
                <span className="text-slate-500">{hdFmt(m.sent_at)}{m.by ? ` · ${m.by}` : ''}</span>
              </div>
              {m.posted
                ? <div className="text-[11px] text-lime-400/90 mt-0.5">Op het ticket geplaatst {m.posted_at ? hdFmt(m.posted_at) : ''}</div>
                : <div className="flex flex-wrap items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-amber-300" title={m.error || ''}>Nog niet op het ticket{m.error ? `: ${m.error.slice(0, 90)}` : ''}{m.attempts > 1 ? ` (${m.attempts} pogingen)` : ''}</span>
                    {can('mail') && <button type="button" disabled={busy} onClick={() => act(async () => { await api.send(`/tickets/${id}/mail-posts/${m.id}/retry`, 'POST', {}); }, 'Mail alsnog op het ticket geplaatst.')} className="text-[11px] px-2 py-0.5 rounded border border-amber-500/50 text-amber-200 hover:bg-amber-500/20">Opnieuw plaatsen</button>}
                  </div>}
            </div>
          ))}
        </div>
      </div>}
      <div className="px-3.5 py-3">
        {sectieKop(`Bijlagen (${alleBijlagen.length})`, Paperclip, can('attach') && (
          <label className={`inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-slate-100 cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`} title={`Bijlage uploaden (max. ${hdBytes(HD_MAX_BIJLAGE_BYTES)} per bestand)`}><Upload size={12} />{busy ? 'Bezig…' : 'Uploaden'}<input type="file" className="hidden" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} /></label>
        ))}
        <div className="space-y-1">
          {alleBijlagen.length === 0 && <div className="text-slate-500 text-xs">Geen bijlagen.</div>}
          {alleBijlagen.map((a, i) => {
            const naam = hdBijlageWeergave(a) || `bijlage ${i + 1}`;
            const Icoon = hdBijlageIcoon(a.mimetype, hdText(a.name) || hdText(a.filename) || '');
            // Lokale kopie (via het portaal geüpload) = hier te downloaden. Anders
            // enkel in het bronsysteem; admin/ET krijgen daar een rechtstreekse link.
            const odoo = odooBijlageUrl(t, a);
            return (
              <div key={a.local_id ? `l${a.local_id}` : (a.id || i)} className="text-xs text-slate-300 flex items-center gap-2 min-w-0">
                <Icoon size={14} className="text-slate-500 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-slate-200" title={naam}>{naam}</span>
                  {/* Datum erbij: een ticket met meerdere inkomende mails heeft ook meerdere
                      "Oorspronkelijke e-mail"-bijlagen; zonder datum zijn die niet uit elkaar te houden. */}
                  <span className="block text-[11px] text-slate-500 tabular-nums">{[hdBytes(a.file_size), (a.create_date || a.uploaded_at) ? hdFmt(a.create_date || a.uploaded_at) : ''].filter(Boolean).join(' · ')}</span>
                </span>
                {/* Zichtbaarheid (API-update 25-09-2026: AttachmentOut.visibility): een
                    bijlage bij een interne notitie ziet de klant nooit; wat bij een
                    publiek bericht of een klantmail hoort, heeft de klant (gehad). */}
                {a.visibility === 'partner_internal' && <span className="hd-pill hd-pill-amber shrink-0" title="Bijlage bij een interne notitie — niet zichtbaar voor de klant">intern</span>}
                <span className="ml-auto shrink-0 flex items-center gap-1.5">
                  {(a.local_id || a.id) && hdIsBeeld(a) && <button type="button" onClick={() => bekijkBeeld(a)} title="Bekijken" aria-label={`${naam} bekijken`} className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-600/60 text-slate-200 hover:text-lime-300 hover:border-lime-500/50"><Eye size={13} /></button>}
                  {(a.local_id || a.id) && <button type="button" onClick={() => downloadBijlage(a)} title="Downloaden" aria-label={`${naam} downloaden`} className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-600/60 text-slate-200 hover:text-lime-300 hover:border-lime-500/50"><Download size={13} /></button>}
                  {odoo && <a href={odoo} target="_blank" rel="noopener noreferrer" title="Openen in het bronsysteem (vereist een aanmelding daar)" aria-label={`${naam} openen in het bronsysteem`} className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-slate-600/60 text-slate-200 hover:text-lime-300 hover:border-lime-500/50"><ExternalLink size={13} /></a>}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-600 mt-2 leading-snug">Max. {hdBytes(HD_MAX_BIJLAGE_BYTES)} per bestand. Elke bijlage is hier te downloaden, ook bijlagen uit mails.</p>
      </div>
      <div className="px-3.5 py-2.5">
        <button onClick={() => setDetailsOpen((v) => !v)} className="w-full flex items-center justify-between text-[11px] text-slate-500 uppercase tracking-wide hover:text-slate-300">
          <span className="flex items-center gap-1"><Info size={12} />Details</span>{detailsOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        {detailsOpen && (
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-500">Ticket</dt><dd className="text-slate-300 font-mono break-all">{t.ref}</dd>
            <dt className="text-slate-500">Energielabel</dt><dd className="text-slate-300 break-words">{companies[t.company_id] || t.company_id}</dd>
            <dt className="text-slate-500">Team</dt><dd className="text-slate-300 break-words">{teamNaam || '—'}</dd>
            <dt className="text-slate-500">Status</dt><dd className="text-slate-300">{t.stage_name}{t.stage_is_closed ? ' (gesloten)' : ''}</dd>
            <dt className="text-slate-500">Aangemaakt</dt><dd className="text-slate-300 tabular-nums">{hdFmt(t.create_date)}</dd>
            <dt className="text-slate-500">Laatste activiteit</dt><dd className="text-slate-300 tabular-nums">{hdFmt(laatsteActiviteit)}</dd>
            {/* Hoelang open, in Belgische werkdagen en per periode: een heropend
                ticket telt vanaf de heropening; de eerdere periodes en het totaal
                staan eronder. */}
            {hdDagenOpen(t) != null && <><dt className="text-slate-500">{isHdOpen(t) ? 'Open' : 'Open geweest'}</dt><dd className="text-slate-300 tabular-nums" title="Werkdagen: weekends en Belgische feestdagen tellen niet mee.">{hdDagenOpen(t)} werkdag{hdDagenOpen(t) === 1 ? '' : 'en'}{isHdOpen(t) && t.open_sinds ? ` sinds de heropening op ${hdDag(t.open_sinds)}` : ''}</dd></>}
            {(Array.isArray(t.open_periodes) ? t.open_periodes : []).filter(() => isHdOpen(t) || t.open_periodes.length > 1).map((p, i) => (
              <React.Fragment key={p.van}><dt className="text-slate-500">Periode {i + 1}</dt><dd className="text-slate-300 tabular-nums">{hdDag(p.van)} tot {hdDag(p.tot)} · {p.werkdagen} werkdag{p.werkdagen === 1 ? '' : 'en'}</dd></React.Fragment>
            ))}
            {isHdOpen(t) && t.open_sinds && <><dt className="text-slate-500">Totaal open</dt><dd className="text-slate-300 tabular-nums">{t.open_werkdagen_totaal != null ? `${t.open_werkdagen_totaal} werkdag${t.open_werkdagen_totaal === 1 ? '' : 'en'}` : 'niet gekend (de eerdere periode is niet bewaard)'}</dd></>}
            {t.escalated_to_et && <><dt className="text-slate-500">Geëscaleerd</dt><dd className="text-slate-300 tabular-nums">{hdFmt(t.escalated_date)}</dd></>}
          </dl>
        )}
      </div>
    </div>
    {/* Logboek: wie deed wat en wanneer, als tijdlijn (nieuwste bovenaan). */}
    <div id="hd-logboek" className="scroll-mt-4 min-w-0 bg-slate-800/40 border border-slate-700/50 rounded-xl px-3.5 py-3 text-sm">
      {sectieKop(`Logboek${log && log.length ? ` (${log.length})` : ''}`, Activity)}
      <HdLogTijdlijn entries={log} />
      <p className="text-[10px] text-slate-600 mt-2.5 leading-snug">Portaalacties met de gebruiker die ze deed, en de berichten van het ticket. Wijzigingen rechtstreeks in het bronsysteem zonder bericht staan er niet in.</p>
    </div>
    {/* Eerdere tickets van dezelfde klant (08-10-2026): openen in dit scherm; zonder
        navigatie (bv. vanuit Customer 360) in een nieuw tabblad. */}
    {t && <HdKlantTickets token={token} ticket={t} onOpen={(tid) => { if (onNav) onNav(tid); else window.open(`/?page=helpdesk#helpdesk/ticket/${tid}`, '_blank', 'noopener'); }} />}
    </div>
  );

  const inhoud = t && (
    <div className={fullPage ? 'grid gap-3 items-start lg:grid-cols-[minmax(0,3fr)_minmax(280px,1fr)]' : 'space-y-3'}>
      {hoofd}
      {zijkolom}
    </div>
  );

  const dialogen = (
    <>
      {escOpen && <HelpdeskEscalateDialog busy={busy} showBrand={showBrand} onClose={() => setEscOpen(false)} onConfirm={escalate} />}
      {kijker && <HdBeeldKijker kijker={kijker} onSluit={() => setKijker(null)} onDownload={downloadBijlage} />}
    </>
  );
  const fout = err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{err}</div>;

  if (fullPage) return (
    <div className="space-y-3">
      {/* "Overzicht" + bladeren door de tickets van het overzicht (zelfde filters en
          volgorde), zonder telkens terug te moeten. */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onClose} title="Terug naar het overzicht" className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-slate-600/60 text-sm text-slate-300 hover:text-lime-300 hover:border-lime-500/50"><List size={14} />Overzicht</button>
        {nav && onNav && (
          <span className="inline-flex items-center gap-1">
            <button type="button" onClick={() => onNav(nav.vorige)} disabled={!nav.vorige} title="Vorig ticket uit het overzicht" aria-label="Vorig ticket" className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-600/60 text-sm text-slate-300 hover:text-lime-300 hover:border-lime-500/50 disabled:opacity-40 disabled:pointer-events-none"><ChevronLeft size={15} /><span className="hidden sm:inline">Vorige</span></button>
            <button type="button" onClick={() => onNav(nav.volgende)} disabled={!nav.volgende} title="Volgend ticket uit het overzicht" aria-label="Volgend ticket" className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-600/60 text-sm text-slate-300 hover:text-lime-300 hover:border-lime-500/50 disabled:opacity-40 disabled:pointer-events-none"><span className="hidden sm:inline">Volgende</span><ChevronRight size={15} /></button>
            {nav.positie && <span className="ml-1 text-xs text-slate-500 tabular-nums" title="Plaats van dit ticket in het overzicht (met de filters en de sortering van toen je het opende)">{nav.positie} van {nav.totaal}</span>}
          </span>
        )}
      </div>
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
        {actiebalk}
        {metaRegel}
        {fout && <div className="p-3">{fout}</div>}
      </div>
      {!t && !err && <Card className="!p-5"><ListSkeleton rows={4} /></Card>}
      {inhoud}
      {dialogen}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl my-8 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {actiebalk}
        {metaRegel}
        {fout && <div className="mx-4 mt-3">{fout}</div>}
        {t && <div className="p-4">{inhoud}</div>}
        {dialogen}
      </div>
    </div>
  );
};

// Nieuw ticket — een volledig scherm in dezelfde vormgeving als het ticketscherm
// (actiebalk bovenaan, links het opstelblok, rechts de klantkaart), geen popup.
// Vanuit de Helpdesk zoek je zelf een klant; vanuit Customer 360 komt de klant
// mee als `prefill` ({ name, reference }). `teams` mag ontbreken.
//
// Het eerste bericht werkt zoals het antwoordblok op een bestaand ticket: kanaal
// "Mail naar klant" (helpdesk-mailsjabloon, bewerkbaar, in de huisstijl, met
// bijlagen — via POST /tickets/:id/mail na de aanmaak) of "Interne notitie"
// (partner_internal bericht + bijlagen apart, na de aanmaak). Een publiek
// ticketbericht bestaat hier niet: de klant wordt alleen per mail aangeschreven.
// De opstelgegevens komen van POST /compose,
// op basis van de gekozen klant; er is dan nog geen ticket.
const HelpdeskNieuwTicket = ({ token, teams: teamsProp = null, onClose, onCreated, prefill = null, orders = [], currentUser = null, showBrand = false, fullPage = false }) => {
  const api = useMemo(() => hdApi(token), [token]);
  const [customers, setCustomers] = useState([]);   // terugval: de klantenlijst van het bronsysteem
  const [teams, setTeams] = useState(teamsProp || []);
  const [tags, setTags] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState({ customer_id: '', subject: '', team_id: '', tagNames: [], assignee_id: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // Verplichte velden: de kaders gaan aan na een mislukte poging om aan te maken.
  const [toonVerplicht, setToonVerplicht] = useState(false);
  const prefillNaam = (prefill && prefill.name) || '';
  const prefillRef = String((prefill && prefill.reference) || '').trim();
  const [custQ, setCustQ] = useState(prefillNaam);
  const [gekozen, setGekozen] = useState(null);
  const [zoekBezig, setZoekBezig] = useState(false);
  const autoGekozen = useRef(false);
  // ── Eerste bericht ─────────────────────────────────────────────────────
  // Rechten van deze gebruiker: het kanaal "Mail naar klant" vraagt het recht mail,
  // de interne notitie het recht message, een bijlage het recht attach. Sjablonen,
  // standaardteksten en AI-voorstel bestaan alleen voor de interne rollen.
  const caps = useMemo(() => new Set(currentUser?.helpdeskPerms || []), [currentUser]);
  const interneKennis = hdInterneKennis(currentUser?.role);
  const magKennisbank = hdMagKennisbank(currentUser);
  // AI 360°: het recht van deze gebruiker (profiel); de server toetst het opnieuw.
  const magAi360 = interneKennis && currentUser?.helpdeskAi360 === true;
  const magBijlage = caps.has('attach');
  const [kanaal, setKanaal] = useState(() => (caps.has('mail') ? 'mail' : caps.has('message') ? 'intern' : 'geen'));          // 'mail' | 'intern' | 'geen'
  // Interne notitie als eerste bericht (bv. wat de klant aan de telefoon vroeg):
  // gewone tekst, de klant ziet ze nooit. "Bericht op ticket" (publiek) is weg
  // sinds 21-09-2026: de klant wordt alleen per mail in huisstijl aangeschreven.
  const [notitie, setNotitie] = useState('');
  const [ctx, setCtx] = useState(null);                  // POST /compose
  const [ctxFout, setCtxFout] = useState(null);
  const [mail, setMail] = useState({ to: '', body: '', templateId: '' });
  const [weergave, setWeergave] = useState('visueel');
  const [editorVersie, setEditorVersie] = useState(0);
  const [bijlagen, setBijlagen] = useState([]);
  const [toonPreview, setToonPreview] = useState(false);
  const previewRef = useInBeeldBijOpenen(toonPreview);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewBreed, setPreviewBreed] = useState('desktop');
  // De laatst ingevulde sjabloontekst: zolang de tekst daaraan gelijk is, wordt het
  // sjabloon opnieuw ingevuld als het onderwerp verandert (het staat in de tekst).
  const laatsteSjabloon = useRef('');
  // ── Standaardteksten, kennisbank en AI-voorstel — zoals in het antwoordblok ──
  // Dezelfde hulpmiddelen als bij een antwoord op een bestaand ticket (vraag
  // 16-09-2026): de standaardteksten komen van POST /compose/snippets (ingevuld
  // voor de gekozen klant), de kennisbank is klantonafhankelijk, en het
  // AI-voorstel loopt via POST /compose/ai-voorstel — er is nog geen ticket, dus
  // het model schrijft de EERSTE mail over het onderwerp en de aanwijzing.
  const editorRef = useRef(null);
  const [snippets, setSnippets] = useState([]);
  const [kbOpen, setKbOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInstructie, setAiInstructie] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResultaat, setAiResultaat] = useState(null);
  const [aiOpmerking, setAiOpmerking] = useState('');
  const aiVorige = useRef(null);
  const voegHtmlIn = (html) => {
    if (!html) return;
    if (weergave === 'visueel' && editorRef.current && editorRef.current.voegHtml) { editorRef.current.voegHtml(html); return; }
    setMail((m) => ({ ...m, body: voegVoorGroet(m.body, html, '\n') }));
    setEditorVersie((v) => v + 1);
  };
  const maakAiVoorstel = async () => {
    if (!gekozen || !gekozen.customer_id) return;
    if (!form.subject.trim() && !aiInstructie.trim()) { setErr('Geef een onderwerp of een aanwijzing mee, anders weet het model niet waarover de mail gaat.'); return; }
    const variant = aiOpen === '360' ? '360' : 'voorstel';
    setAiBusy(variant); setErr(null);
    try {
      const d = await api.send(variant === '360' ? '/compose/ai-360' : '/compose/ai-voorstel', 'POST', { ...klantBody(), kanaal: 'mail', instructie: aiInstructie.trim() });
      aiVorige.current = { body: mail.body };
      laatsteSjabloon.current = d.antwoord_html;
      setMail((m) => ({ ...m, body: d.antwoord_html }));
      setEditorVersie((v) => v + 1);
      if (weergave !== 'visueel') setWeergave('visueel');
      if (d.onderwerp && !form.subject.trim()) setForm((p) => ({ ...p, subject: d.onderwerp }));
      setAiResultaat(d); setAiOpen(false);
    } catch (e) { setErr(e.message); } finally { setAiBusy(false); }
  };
  const geefAiFeedback = async (beoordeling) => {
    if (!aiResultaat || !aiResultaat.id) return;
    setAiResultaat((r) => ({ ...r, beoordeling, feedbackOk: false }));
    try {
      await api.send(`/compose/ai-voorstel/${aiResultaat.id}/feedback`, 'POST', { beoordeling, opmerking: aiOpmerking.trim() });
      setAiResultaat((r) => (r ? { ...r, feedbackOk: true } : r));
    } catch (e) { setErr(e.message); }
  };
  const verwerpAi = () => {
    const v = aiVorige.current;
    if (v) { setMail((m) => ({ ...m, body: v.body || '' })); laatsteSjabloon.current = v.body || ''; setEditorVersie((x) => x + 1); }
    aiVorige.current = null; setAiResultaat(null);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);
  useEffect(() => {
    const lijst = (d) => (Array.isArray(d) ? d : (d && d.items) || []);
    if (!teamsProp) api.get('/teams').then((d) => setTeams(lijst(d))).catch(() => {});
    api.get('/tags').then((d) => setTags(lijst(d))).catch(() => {});
    if (showBrand) api.get('/assignees').then((d) => {
      const l = d.items || [];
      setAssignees(l);
      if (currentUser && l.some((u) => u.id === currentUser.id)) setForm((p) => ({ ...p, assignee_id: p.assignee_id || String(currentUser.id) }));
    }).catch(() => setAssignees([]));
  }, [api, teamsProp, showBrand, currentUser]);

  // ── Klant zoeken: dezelfde suggesties als Customer 360 / Orders.
  const zoekKenmerken = useMemo(() => klantKenmerken(orders), [orders]);
  const suggesties = useMemo(() => bouwZoekSuggesties({ query: custQ, orders, kenmerken: zoekKenmerken, groepeerOp: 'ref' }), [custQ, orders, zoekKenmerken]);
  const metOrders = orders.length > 0;
  const kiesKlant = async ({ naam, ref, email }) => {
    const n = String(naam || '').trim(); const r = String(ref || '').trim();
    const order = orders.find((o) => (r && String(o.customerReference || '').trim() === r) || (!r && String(o.customer || '').trim().toLowerCase() === n.toLowerCase()));
    const label = order ? String(order.company || '').trim() : '';
    setGekozen({ naam: n, ref: r, email: email || (order && order.customerEmail) || '', label, customer_id: '' });
    setCustQ(n || r);
    setErr(null); setZoekBezig(true); setCtx(null); setCtxFout(null);
    try {
      const erp = order && /^erp:(\d+)$/.exec(String(order.customerId || ''));
      const zoek = async (q) => { const d = await api.get(`/customers?limit=50&q=${encodeURIComponent(q)}`); return Array.isArray(d) ? d : (d && d.items) || []; };
      let lijst = [];
      let match = erp ? { id: Number(erp[1]), name: n, reference: r } : null;
      if (!match && r) { lijst = await zoek(r); match = lijst.find((c) => String(c.reference || '').trim() === r) || null; }
      if (!match && n) { lijst = await zoek(n); match = lijst.find((c) => String(c.name || '').trim().toLowerCase() === n.toLowerCase()) || (lijst.length === 1 ? lijst[0] : null); }
      if (!match) { setErr(`Klant "${n || r}" is niet gevonden in het bronsysteem — kies een andere klant of zoek hieronder in de klantenlijst.`); setGekozen(null); return; }
      const klant = { naam: n || match.name, ref: r || match.reference || '', label, customer_id: String(match.id) };
      setGekozen((g) => ({ ...(g || {}), ...klant }));
      setForm((p) => ({ ...p, customer_id: String(match.id) }));
      if (label) {
        const team = teams.find((t) => t.label && t.label.toLowerCase() === label.toLowerCase() && /klantenservice/i.test(t.name)) || teams.find((t) => t.label && t.label.toLowerCase() === label.toLowerCase());
        if (team) setForm((p) => ({ ...p, team_id: p.team_id || String(team.id) }));
      }
      // Opstelgegevens voor het eerste bericht (adres, sjablonen, afzender).
      try {
        const c = await api.send('/compose', 'POST', { reference: klant.ref, name: klant.naam, subject: '' });
        setCtx(c);
        // Leeg bericht → de vaste groet staat er al; sjabloon en standaardteksten
        // komen erboven (zelfde regel als het antwoordblok op een ticket).
        setMail((m) => {
          const body = m.body.trim() ? m.body : (c.groetHtml || '');
          if (!m.body.trim() && c.groetHtml) laatsteSjabloon.current = c.groetHtml;
          return { ...m, body, to: m.to || c.to || '' };
        });
        setEditorVersie((v) => v + 1);
        // Standaardteksten voor deze klant (module helpdesk-bericht), zoals in het
        // antwoordblok. Mislukt = gewoon geen kiezer.
        if (interneKennis) api.send('/compose/snippets', 'POST', { reference: klant.ref, name: klant.naam, subject: '' })
          .then((d) => setSnippets((d && d.snippets) || [])).catch(() => setSnippets([]));
      } catch (e) { setCtxFout(e.message); }
    } catch (e) { setErr(e.message); setGekozen(null); }
    finally { setZoekBezig(false); }
  };
  useEffect(() => {
    if (autoGekozen.current || (!prefillNaam && !prefillRef)) return;
    autoGekozen.current = true;
    kiesKlant({ naam: prefillNaam, ref: prefillRef });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillNaam, prefillRef, teams.length]);
  useEffect(() => {
    if (metOrders) return undefined;
    const h = setTimeout(() => {
      api.get(`/customers?limit=50${custQ ? `&q=${encodeURIComponent(custQ)}` : ''}`)
        .then((d) => setCustomers(Array.isArray(d) ? d : (d && d.items) || []))
        .catch(() => setCustomers([]));
    }, 300);
    return () => clearTimeout(h);
  }, [api, custQ, metOrders]);

  const klantBody = () => ({ reference: gekozen?.ref || '', name: gekozen?.naam || '', subject: form.subject.trim() });
  // Sjabloon: server-side ingevuld met de gegevens van deze klant.
  const kiesSjabloon = async (tid) => {
    setMail((m) => ({ ...m, templateId: tid }));
    if (!tid) return;
    setErr(null);
    try {
      const d = await api.send(`/compose/template/${encodeURIComponent(tid)}`, 'POST', klantBody());
      // Het sjabloon vervangt de tekst: eerst het sjabloon kiezen, dan aanpassen
      // (zelfde werkwijze als het antwoordblok op een ticket).
      setMail((m) => {
        laatsteSjabloon.current = d.html || '';
        return { ...m, body: d.html || '' };
      });
      if (d.subject && !form.subject.trim()) setForm((p) => ({ ...p, subject: d.subject }));
      setEditorVersie((v) => v + 1);
      setPreview(null);
    } catch (e) { setErr(e.message); }
  };
  // Onderwerp gewijzigd na het kiezen van een sjabloon? Zolang de tekst nog het
  // sjabloon is (niet bewerkt), opnieuw invullen — het onderwerp staat in de tekst.
  const onderwerpKlaar = () => { if (mail.templateId && mail.body === laatsteSjabloon.current) kiesSjabloon(mail.templateId); };
  const kiesWeergave = (w) => { if (w === weergave) return; setWeergave(w); if (w === 'visueel') setEditorVersie((v) => v + 1); };
  const maakPreview = async () => {
    setPreviewBusy(true);
    try { setPreview(await api.send('/compose/preview', 'POST', { ...klantBody(), body_html: hdTekstNaarHtml(mail.body) })); }
    catch (e) { setErr(e.message); }
    finally { setPreviewBusy(false); }
  };
  useEffect(() => { if (toonPreview && kanaal === 'mail') maakPreview(); }, [toonPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  // Klantenserviceteam en minstens één tag zijn verplicht: zonder team komt het
  // ticket bij niemand terecht, zonder tag valt het buiten elke analyse.
  const ontbreekt = ontbrekendNieuwTicket({
    klantGekozen: !!form.customer_id, onderwerp: form.subject, teamId: form.team_id, tagNamen: form.tagNames,
    kanaal, aan: mail.to, bericht: kanaal === 'intern' ? notitie : mail.body,
  });
  const mist = (veldId) => toonVerplicht && ontbreekt.some((v) => v.id === veldId);

  const submit = async () => {
    if (ontbreekt.length) {
      setToonVerplicht(true); meldOntbrekend(ontbreekt);
      return;
    }
    setToonVerplicht(false);
    if (kanaal === 'mail' && ctx && ctx.blokkade) { setErr(ctx.blokkade); return; }
    setBusy(true); setErr(null);
    try {
      const tag_ids = tags.filter((x) => form.tagNames.includes(x.name)).map((x) => x.id);
      const created = await api.send('/tickets', 'POST', {
        customer_id: Number(form.customer_id), subject: form.subject.trim(),
        // Het klantnummer reist mee voor de scope-toets op de server (die het zelf
        // naleest: een klantnummer uit de browser wordt nooit op zijn woord geloofd).
        customer_reference: (gekozen && gekozen.ref) || undefined,
        team_id: form.team_id ? Number(form.team_id) : undefined, tag_ids,
        // Geen `initial_message`: dat is in het bronsysteem een PUBLIEK bericht en
        // zou de klant bereiken vanaf het adres van het bronsysteem. De klant krijgt
        // alleen een mail in huisstijl (hieronder); wat je voor jezelf wil noteren,
        // gaat als interne notitie.
      });
      if (created && created.id && form.assignee_id) {
        await api.send(`/tickets/${created.id}/assignee`, 'PUT', { user_id: Number(form.assignee_id) }).catch((e) => toast.error(`Ticket aangemaakt, maar niet toegewezen: ${e.message}`));
      }
      toast.success(`Ticket ${created && created.ref ? created.ref : ''} aangemaakt`.replace('  ', ' '));
      if (created && created.arbFout) toast.error(`Ticket aangemaakt, maar "actie vereist van" kon niet op ${showBrand ? 'Energy Together' : 'support'} gezet worden: ${created.arbFout}`);
      if (created && created.stageFout) toast.error(`Ticket aangemaakt, maar niet in de fase "Nieuw (zonder auto bevestiging)": ${created.stageFout}`, { duration: 8000 });
      if (created && created.id && kanaal === 'mail') {
        try {
          const d = await api.send(`/tickets/${created.id}/mail`, 'POST', {
            // De interne markering van klantgegevens (AI-voorstel) gaat er hier af;
            // de server doet het nog eens. ai_voorstel_id koppelt het voorstel aan
            // het nieuwe ticket en bewaart wat er echt verstuurd werd als correctie.
            to: mail.to.trim(), subject: form.subject.trim(), body_html: hdStripAiMarks(hdTekstNaarHtml(mail.body)), include_history: false,
            template_id: mail.templateId ? Number(mail.templateId) : undefined,
            ai_voorstel_id: aiResultaat && aiResultaat.id ? aiResultaat.id : undefined,
            attachments: bijlagen.length ? await hdBijlagenNaarPayload(bijlagen) : undefined,
          });
          if (d && d.opTicket === false) toast.error(`Mail verstuurd naar ${d.to}, maar niet op het ticket vastgelegd: ${d.ticketFout || 'onbekende fout'}`);
          else toast.success(`Mail verstuurd naar ${d.to}.`);
        } catch (e) { toast.error(`Ticket aangemaakt, maar de mail is niet verstuurd: ${e.message}`); }
      } else if (created && created.id) {
        // Bijlagen eerst (elk apart), dan de interne notitie met hun namen — zelfde
        // volgorde als het antwoordblok op een ticket.
        const namen = [];
        for (const f of bijlagen) {
          try { await api.send(`/tickets/${created.id}/attachments`, 'POST', { name: f.name, mimetype: f.type, data: await hdFileToB64(f) }); namen.push(f.name); }
          catch (e) { toast.error(`Bijlage ${f.name} niet op het ticket gezet: ${e.message}`); }
        }
        if (kanaal === 'intern') {
          const tekst = notitie.trim();
          try { await api.send(`/tickets/${created.id}/messages`, 'POST', { body: namen.length ? `${tekst}\n\nBijlagen: ${namen.join(', ')}` : tekst, visibility: 'partner_internal' }); }
          catch (e) { toast.error(`Ticket aangemaakt, maar de interne notitie is niet geplaatst: ${e.message}`); }
        }
      }
      onCreated && onCreated(created);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const veld = 'w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100';
  const selKlein = 'h-7 px-2 bg-slate-700/70 border border-slate-600/70 rounded-md text-xs text-slate-200';
  const teamLabel = (t) => (t.label ? `${t.label} — ${t.name}` : t.name);
  const sectieKop = (titel, Icoon, extra = null) => (
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <div className="text-[11px] text-slate-500 uppercase tracking-wide flex items-center gap-1"><Icoon size={12} />{titel}</div>
      {extra}
    </div>
  );
  const groepen = useMemo(() => {
    const m = new Map();
    for (const t of (ctx && ctx.templates) || []) { const c = t.category || 'Overige'; if (!m.has(c)) m.set(c, []); m.get(c).push(t); }
    return [...m.entries()];
  }, [ctx]);
  const sender = ctx && ctx.sender;
  const wgKnop = (w, Icoon, label) => <button type="button" onClick={() => kiesWeergave(w)} className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${weergave === w ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}><Icoon size={12} />{label}</button>;
  const klantKlaar = !!(gekozen && gekozen.customer_id);
  const kanalen = [
    { id: 'mail', recht: 'mail', label: 'Mail naar klant', Icoon: Mail, tip: interneKennis ? 'Een e-mail in de huisstijl van het merk, met sjabloon en preview; wordt ook op het ticket vastgelegd' : 'Een e-mail in de huisstijl van het merk, met preview; wordt ook op het ticket vastgelegd' },
    { id: 'intern', recht: 'message', label: 'Interne notitie', Icoon: Users, tip: 'Een notitie op het ticket die de klant niet ziet, bijvoorbeeld wat hij aan de telefoon vroeg — er vertrekt geen mail' },
    { id: 'geen', label: 'Geen bericht', Icoon: X, tip: 'Enkel het ticket aanmaken' },
  ].filter((k) => !k.recht || caps.has(k.recht));

  // ── Opbouw (21-09-2026; acties naar de kop 27-09-2026) ──────────────────────
  // Eén kop met links waar je bent (terug, "Nieuw ticket", onderwerp, klant) en
  // RECHTS de acties: Annuleren en de groene hoofdknop. De kop blijft in beeld
  // terwijl je door de drie genummerde blokken (① Klant, ② Ticket, ③ Eerste
  // bericht) scrolt, dus de hoofdknop is er altijd. Vroeger stond ze in een regel
  // helemaal onderaan, over de volle breedte: rechts onder de zijkolom, los van
  // het formulier. Rechts zegt de controlelijst "Klaar om aan te maken" wat er
  // nog ontbreekt; de rode kaders blijven het vangnet na een klik.
  // Onder xl (telefoon, tablet, smal venster) past dat niet naast elkaar — gemeten
  // op 1024 px schoof de knop onder de accountbalk. Daar blijft de hoofdknop
  // onderaan staan, over de volle breedte, binnen duimbereik.
  // Rechts van de kop staat de vaste accountbalk; de kop houdt die ruimte vrij
  // (--accountbar-w, zoals PageHeader), anders valt de balk over de knoppen.
  const knopLabel = kanaal === 'mail' ? 'Ticket aanmaken en mail versturen' : 'Ticket aanmaken';
  const hoofdKnop = (extra = '') => (
    <button onClick={submit} disabled={busy || zoekBezig} className={`inline-flex items-center justify-center gap-1.5 h-9 px-4 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-md text-sm font-semibold disabled:opacity-50 whitespace-nowrap ${extra}`}>
      {busy ? <Spinner size={13} /> : <Plus size={15} />}{busy ? 'Bezig…' : knopLabel}
    </button>
  );
  const annuleerKnop = <button onClick={onClose} disabled={busy} className="h-9 px-3.5 bg-slate-700/70 hover:bg-slate-600/70 text-slate-200 rounded-md text-xs shrink-0">Annuleren</button>;
  const actiebalk = (
    <div className="xl:sticky xl:top-3 z-30" style={fullPage ? { marginRight: 'var(--accountbar-w, 0px)' } : undefined}>
      <div className="bg-slate-900/95 backdrop-blur border border-slate-700/60 rounded-xl shadow-sm pl-2 pr-3 py-2 flex items-center gap-2.5">
        <button onClick={onClose} disabled={busy} title="Terug naar tickets" aria-label="Terug naar tickets" className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-lime-300 hover:bg-slate-700/70 shrink-0"><ChevronLeft size={18} /></button>
        <span className="hd-pill hd-pill-sky shrink-0">Nieuw ticket</span>
        <div className="min-w-0 flex-1">
          <h3 className={`text-base font-semibold truncate leading-tight ${form.subject.trim() ? 'text-slate-100' : 'text-slate-500'}`} title={form.subject}>{form.subject.trim() || 'Nog geen onderwerp'}</h3>
          {klantKlaar && <div className="text-xs text-slate-400 truncate flex items-center gap-1" title={gekozen.naam}><User size={11} className="shrink-0 text-slate-500" />{gekozen.naam}</div>}
        </div>
        <div className="hidden xl:flex items-center gap-2 shrink-0">
          <span className={`text-xs ${ontbreekt.length ? 'text-slate-400' : 'text-lime-300'} hidden 2xl:inline-flex items-center gap-1.5`} title={ontbreekt.length ? `Nog in te vullen: ${somOp(ontbreekt)}` : undefined}>
            {ontbreekt.length ? <><AlertCircle size={13} className="shrink-0 text-amber-400" />Nog {ontbreekt.length} in te vullen</> : <><Check size={13} className="shrink-0" />Alles ingevuld</>}
          </span>
          {annuleerKnop}
          {hoofdKnop()}
        </div>
      </div>
    </div>
  );
  const blokKop = (nr, titel, extra = null) => (
    <div className="flex items-center gap-2.5">
      <span className="w-6 h-6 rounded-full bg-lime-500/20 text-lime-300 text-xs font-bold inline-flex items-center justify-center shrink-0">{nr}</span>
      <h4 className="text-sm font-semibold text-slate-100">{titel}</h4>
      {extra}
    </div>
  );
  // Alle verplichte velden voor het gekozen kanaal, met of ze al ingevuld zijn.
  const verplichtLijst = [
    { id: 'klant', label: 'Klant' }, { id: 'onderwerp', label: 'Onderwerp' }, { id: 'team', label: 'Klantenserviceteam' }, { id: 'tags', label: 'Minstens één tag' },
    ...(kanaal === 'mail' ? [{ id: 'aan', label: 'E-mailadres van de klant' }, { id: 'bericht', label: 'Bericht' }] : []),
    ...(kanaal === 'intern' ? [{ id: 'bericht', label: 'Interne notitie' }] : []),
  ].map((v) => ({ ...v, ok: !ontbreekt.some((o) => o.id === v.id) }));
  // Alleen onder xl: daar is de kop te smal voor de acties.
  const actieRegel = (
    <div className="xl:hidden sticky z-20 -mx-1 px-1" style={{ bottom: 'calc(var(--tabbar-h, 0px) + var(--installbanner-h, 0px) + 8px)' }}>
      <div className="bg-slate-900/95 backdrop-blur border border-slate-700/60 rounded-xl shadow-lg px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <span className={`text-xs ${ontbreekt.length ? 'text-slate-400' : 'text-lime-300'} inline-flex items-center gap-1.5 min-w-0`}>
          {ontbreekt.length ? <><AlertCircle size={13} className="shrink-0 text-amber-400" /><span className="truncate">Nog in te vullen: {somOp(ontbreekt)}</span></> : <><Check size={13} className="shrink-0" />Alles ingevuld</>}
        </span>
        <span className="flex-1" />
        {annuleerKnop}
        {hoofdKnop('flex-1 sm:flex-none')}
      </div>
    </div>
  );

  const hoofd = (
    <div className="min-w-0 space-y-3">
      {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
      <div className="relative z-40 bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 space-y-3">
        {blokKop(1, 'Klant')}
        <div className="relative z-40">
          {metOrders ? (
            <>
              <HdVerplicht fout={mist('klant')}>
                <KlantZoekVeld label="Klant *" placeholder="Zoek op klantnaam, klantnummer, bedrijfsnaam, btw-nummer, EAN, adres…"
                  value={custQ} onChange={(v) => { setCustQ(v); if (gekozen) { setGekozen(null); setCtx(null); setForm((p) => ({ ...p, customer_id: '' })); } }}
                  onClear={() => { setCustQ(''); setGekozen(null); setCtx(null); setForm((p) => ({ ...p, customer_id: '' })); }} clearActief={!!custQ}
                  suggesties={suggesties} onKlant={(k) => kiesKlant(k)} onGsrn={(g) => kiesKlant({ naam: g.klant, ref: g.ref, email: g.email })}
                  telWoord={{ een: 'aansluiting', meer: 'aansluitingen' }} />
              </HdVerplicht>
              {zoekBezig && <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><Spinner size={12} />Klant opzoeken in het bronsysteem…</div>}
            </>
          ) : (
            <>
              <label className="block text-xs text-slate-400 mb-1">Klant *</label>
              <input value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder="Zoek klant…" className={`${veld} mb-1 py-1.5`} />
              <HdVerplicht fout={mist('klant')}>
                <select value={form.customer_id} onChange={(e) => setForm((p) => ({ ...p, customer_id: e.target.value }))} className={`${veld} block`}>
                  <option value="">— kies klant —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.reference ? ` (${c.reference})` : ''}</option>)}
                </select>
              </HdVerplicht>
              {customers.length === 0 && <div className="text-xs text-slate-500 mt-1">Geen klanten gevonden (mogelijk is je account niet aan een reseller gekoppeld).</div>}
            </>
          )}
        </div>
      </div>
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 space-y-3">
        {blokKop(2, 'Ticket')}
        <div><label className="block text-xs text-slate-400 mb-1">Onderwerp *</label>
          <HdVerplicht fout={mist('onderwerp')}>
            <input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} onBlur={onderwerpKlaar} placeholder="Waarover gaat het ticket?" className={`${veld} block font-semibold`} />
          </HdVerplicht>
          {kanaal === 'mail' && <p className="text-[11px] text-slate-500 mt-1">Dit is ook het onderwerp van de mail naar de klant.</p>}
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="block text-xs text-slate-400 mb-1">Klantenserviceteam *</label>
            <HdVerplicht fout={mist('team')}>
              <select value={form.team_id} onChange={(e) => setForm((p) => ({ ...p, team_id: e.target.value }))} className={`${veld} block`}>
                <option value="">— kies een team —</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{teamLabel(t)}</option>)}
              </select>
            </HdVerplicht>
            {gekozen && gekozen.label && form.team_id && (teams.find((t) => String(t.id) === String(form.team_id)) || {}).label && (teams.find((t) => String(t.id) === String(form.team_id)) || {}).label.toLowerCase() !== gekozen.label.toLowerCase() && (
              <p className="text-[11px] text-amber-400/90 mt-1">Dit team hoort bij een ander energielabel dan de klant ({gekozen.label}).</p>
            )}
          </div>
          {showBrand && (
            <div><label className="block text-xs text-slate-400 mb-1">Behandelaar</label>
              <select value={form.assignee_id} onChange={(e) => setForm((p) => ({ ...p, assignee_id: e.target.value }))} className={veld}>
                <option value="">Niet toegewezen</option>
                {assignees.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
              </select>
            </div>
          )}
          <div><label className="block text-xs text-slate-400 mb-1">Tags *</label>
            <HdVerplicht fout={mist('tags')} className="w-fit max-w-full"><HdMultiSelect options={tags.map((x) => x.name)} selected={form.tagNames} onChange={(v) => setForm((p) => ({ ...p, tagNames: v }))} placeholder="Geen" /></HdVerplicht>
          </div>
        </div>
        <p className="text-[11px] text-slate-500">Velden met * zijn verplicht. Een nieuw ticket start met actie vereist van {showBrand ? 'Energy Together' : 'support'}.</p>
      </div>

      {/* Eerste bericht — zelfde opbouw als het antwoordblok op een ticket. */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 space-y-3">
        {blokKop(3, 'Eerste bericht', kanaal === 'mail'
          ? <button type="button" onClick={() => setToonPreview((s) => !s)} className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-xs shrink-0"><Eye size={14} />{toonPreview ? 'Verberg preview' : 'Toon preview'}</button>
          : null)}
        <div className="flex flex-wrap items-center gap-1.5">
          {kanalen.map((k) => (
            <button key={k.id} type="button" onClick={() => setKanaal(k.id)} title={k.tip}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${kanaal === k.id ? 'bg-lime-500/20 text-lime-300 ring-1 ring-lime-500/40' : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600/60'}`}>
              <k.Icoon size={13} />{k.label}
            </button>
          ))}
          {kanaal === 'mail' && ctx && ctx.brandName && <span className="text-[11px] text-slate-500 ml-1">Huisstijl {ctx.brandName}</span>}
          {kanaal === 'intern' && <span className="text-[11px] text-amber-300/90 ml-1">De klant ziet dit niet.</span>}
        </div>
        {kanaal === 'geen' ? <p className="text-xs text-slate-500">Het ticket wordt aangemaakt zonder eerste bericht.{magBijlage ? ' Bijlagen komen wel op het ticket.' : ''}</p> : kanaal === 'intern' ? (
          <>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Interne notitie *</label>
              <HdVerplicht fout={mist('bericht')}>
                <textarea value={notitie} onChange={(e) => setNotitie(e.target.value)} rows={5} placeholder="Interne notitie (de klant ziet dit niet)…"
                  className="block w-full px-3 py-2 border rounded-lg text-sm text-slate-200 bg-amber-500/[.06] border-amber-500/30" />
              </HdVerplicht>
              <p className="text-[11px] text-slate-500 mt-1">Komt als intern bericht op het ticket; er vertrekt geen mail naar de klant.</p>
            </div>
            {magBijlage && <HdBijlageKiezer files={bijlagen} onChange={setBijlagen} disabled={busy} />}
          </>
        ) : (
          <>
            {ctxFout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{ctxFout}</div>}
            {kanaal === 'mail' && ctx && ctx.blokkade && <div className="bg-amber-500/10 border border-amber-500/40 text-amber-200 px-3 py-2 rounded text-sm">{ctx.blokkade}</div>}
            <div className="flex flex-wrap items-center gap-2">
              {interneKennis && <select value={mail.templateId} onChange={(e) => kiesSjabloon(e.target.value)} disabled={!klantKlaar || !ctx} className={`${selKlein} max-w-[320px] disabled:opacity-60`} title={klantKlaar ? 'Mailsjabloon van de Helpdesk, ingevuld voor deze klant' : 'Kies eerst een klant — de sjablonen hangen af van het energielabel'}>
                <option value="">{klantKlaar ? 'Sjabloon…' : 'Sjabloon… (kies eerst een klant)'}</option>
                {groepen.map(([cat, lijst]) => <optgroup key={cat} label={cat}>{lijst.map((t) => <option key={t.id} value={String(t.id)}>{t.name}{t.language && t.language !== 'nl' ? ` (${t.language})` : ''}</option>)}</optgroup>)}
              </select>}
              {/* Standaardteksten: als HTML op de cursor, combineerbaar — zelfde
                  kiezer als in het antwoordblok op een ticket. */}
              {interneKennis && klantKlaar && ctx && snippets.length > 0 && (
                <HdStandaardtekstKiezer snippets={snippets} className={`${selKlein} w-[150px]`} onKies={(s) => voegHtmlIn(s.html || hdTekstNaarHtml(s.text))} />
              )}
              {klantKlaar && ctx && <HdHulpKnoppen nieuw magKb={magKennisbank} magAi={interneKennis} magAi360={magAi360} kbOpen={kbOpen} onKb={() => { setKbOpen((s) => !s); setAiOpen(false); }} aiOpen={aiOpen} aiBusy={aiBusy} onAi={(v) => { setAiOpen((s) => (s === v ? false : v)); setKbOpen(false); }} />}
              <span className="flex items-center gap-0.5 ml-auto">{wgKnop('visueel', Eye, 'Weergave')}{wgKnop('html', Code2, 'HTML')}</span>
            </div>
            {interneKennis && aiOpen && <HdAiPaneel nieuw variant={aiOpen === '360' ? '360' : 'voorstel'} instructie={aiInstructie} setInstructie={setAiInstructie} busy={!!aiBusy} onMaak={maakAiVoorstel} onSluiten={() => setAiOpen(false)} brandName={ctx && ctx.brandName} />}
            {interneKennis && aiResultaat && <HdAiResultaat resultaat={aiResultaat} isMail={kanaal === 'mail'} onderwerpHuidig={form.subject} onOnderwerp={(s) => setForm((p) => ({ ...p, subject: s }))} onVerwerp={verwerpAi} onSluit={() => setAiResultaat(null)} onFeedback={geefAiFeedback} opmerking={aiOpmerking} setOpmerking={setAiOpmerking} />}
            {magKennisbank && kbOpen && <HdKennisbankPaneel token={token} onInvoegen={voegHtmlIn} onSluiten={() => setKbOpen(false)} onFout={setErr} />}
            {kanaal === 'mail' && (
              <div className="grid gap-3">
                <div><label className="block text-xs text-slate-400 mb-1">Aan *</label>
                  <HdVerplicht fout={mist('aan')}><input value={mail.to} onChange={(e) => setMail((m) => ({ ...m, to: e.target.value }))} list="hd-nieuw-adressen" className={`${veld} block`} placeholder="klant@voorbeeld.be" /></HdVerplicht>
                  <datalist id="hd-nieuw-adressen">{((ctx && ctx.adressen) || []).map((a) => <option key={a.email} value={a.email}>{a.bron || ''}</option>)}</datalist>
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Bericht *</label>
              <HdVerplicht fout={mist('bericht')}>
                {weergave === 'visueel'
                  ? <HdVisueleEditor editorRef={editorRef} html={mail.body} versie={editorVersie} onChange={(html) => setMail((m) => ({ ...m, body: html }))} onSync={(html) => setMail((m) => { if (m.body === laatsteSjabloon.current) laatsteSjabloon.current = html; return m.body === html ? m : { ...m, body: html }; })} />
                  : <textarea value={mail.body} onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))} rows={10} spellCheck={false} className={`${veld} block font-mono text-xs`} placeholder="HTML of gewone tekst…" />}
              </HdVerplicht>
            </div>
            {kanaal === 'mail' && sender && (
              <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 border ${sender.verified ? 'border-lime-500/30 bg-lime-500/5 text-slate-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'}`}>
                {sender.verified ? <Check size={14} className="shrink-0 mt-0.5 text-lime-400" /> : <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-400" />}
                <span>{sender.verified ? <>Verzonden vanaf <strong className="font-mono">{sender.fromName} &lt;{sender.from}&gt;</strong></> : <>{ctx.brandName || 'Dit merk'} heeft nog geen geverifieerd verzenddomein — de mail vertrekt vanaf het algemene systeemadres.</>}</span>
              </div>
            )}
            {magBijlage && <div className="flex flex-wrap items-center gap-2 pt-1">
              <HdBijlageKiezer files={bijlagen} onChange={setBijlagen} disabled={busy} />
            </div>}
            {kanaal === 'mail' && toonPreview && (
              <div ref={previewRef} className="space-y-2 min-w-0 scroll-mt-20">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-slate-400 flex items-center gap-2"><Eye size={14} className="text-lime-400" />Zo ziet de klant het{previewBusy && <Spinner size={12} />}</div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setPreviewBreed('desktop')} title="Desktop" className={`p-1.5 rounded ${previewBreed === 'desktop' ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}><Tablet size={14} /></button>
                    <button type="button" onClick={() => setPreviewBreed('mobiel')} title="Telefoon" className={`p-1.5 rounded ${previewBreed === 'mobiel' ? 'bg-lime-500/20 text-lime-300' : 'text-slate-400 hover:text-slate-200'}`}><Smartphone size={14} /></button>
                    <button type="button" onClick={maakPreview} title="Preview vernieuwen" className="p-1.5 rounded text-slate-400 hover:text-slate-200"><RefreshCw size={14} /></button>
                  </div>
                </div>
                <div className="border border-slate-700 rounded-lg overflow-hidden bg-[#f1f5f9] flex justify-center">
                  {preview
                    ? <iframe title="Mailpreview" sandbox="" srcDoc={preview.html} style={{ width: previewBreed === 'mobiel' ? 360 : '100%', maxWidth: '100%', height: 480, border: 0, background: '#f1f5f9' }} />
                    : <div className="py-16 text-slate-500 text-sm">{previewBusy ? 'Preview maken…' : 'Nog geen preview.'}</div>}
                </div>
              </div>
            )}
          </>
        )}
        {kanaal === 'geen' && magBijlage && <HdBijlageKiezer files={bijlagen} onChange={setBijlagen} disabled={busy} />}
      </div>
    </div>
  );

  const zijkolom = (
    <div className="min-w-0 bg-slate-800/40 border border-slate-700/50 rounded-xl divide-y divide-slate-700/50 text-sm">
      <div className="px-3.5 py-3">
        {sectieKop(`Klaar om aan te maken (${verplichtLijst.filter((v) => v.ok).length}/${verplichtLijst.length})`, Check)}
        <ul className="space-y-1.5">
          {verplichtLijst.map((v) => (
            <li key={v.id} className={`flex items-center gap-2 text-xs ${v.ok ? 'text-slate-400' : 'text-slate-200'}`}>
              <span className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0" style={v.ok ? { background: '#65a30d', color: '#fff' } : { boxShadow: 'inset 0 0 0 1.5px rgba(148,163,184,.7)' }}>{v.ok && <Check size={11} />}</span>
              <span className={v.ok ? 'line-through decoration-slate-500/60' : 'font-medium'}>{v.label}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="px-3.5 py-3">
        {sectieKop('Klant', User)}
        {klantKlaar ? (
          <>
            <div className="text-slate-100 font-semibold break-words leading-snug">{gekozen.naam || '—'}</div>
            <div className="text-slate-400 text-xs break-words">{[gekozen.ref, gekozen.label].filter(Boolean).join(' · ') || '—'}</div>
            <div className="text-slate-400 text-xs mt-1 break-words">Verkoper: <span className="text-slate-300">{(ctx && ctx.salesperson) || '—'}</span> · Team: <span className="text-slate-300">{(ctx && ctx.salesTeam) || '—'}</span></div>
            {(ctx && ctx.customer && ctx.customer.email) && <div className="text-slate-400 text-xs mt-1 break-words">E-mail: <span className="text-slate-300 font-mono">{ctx.customer.email}</span></div>}
          </>
        ) : <div className="text-slate-500 text-xs">Nog geen klant gekozen.</div>}
      </div>
      <div className="px-3.5 py-3">
        {sectieKop('Contract & aansluitingen', Plug)}
        {klantKlaar && ctx && ctx.gsrns && ctx.gsrns.length
          ? <div className="space-y-1">{ctx.gsrns.slice(0, 6).map((g) => <div key={g} className="text-xs font-mono text-slate-200">{g}</div>)}{ctx.gsrns.length > 6 && <div className="text-xs text-slate-500">+{ctx.gsrns.length - 6} meer</div>}</div>
          : <div className="text-slate-500 text-xs">{klantKlaar ? 'Geen aansluitingen bekend.' : '—'}</div>}
      </div>
      <div className="px-3.5 py-3">
        {sectieKop(`Bijlagen (${bijlagen.length})`, Paperclip)}
        {bijlagen.length === 0 ? <div className="text-slate-500 text-xs">Nog geen bijlagen — voeg ze toe bij het bericht.</div>
          : <div className="space-y-1">{bijlagen.map((f, i) => <div key={`${f.name}-${i}`} className="text-xs text-slate-300 flex items-center gap-2 min-w-0"><File size={12} className="text-slate-500 shrink-0 w-3" /><span className="truncate" title={f.name}>{f.name}</span><span className="text-[11px] text-slate-500 shrink-0 ml-auto">{hdBytes(f.size)}</span></div>)}</div>}
        <p className="text-[10px] text-slate-600 mt-2 leading-snug">{kanaal === 'mail' ? 'Gaan mee in de mail én komen op het ticket.' : 'Komen op het ticket na de aanmaak.'}</p>
      </div>
    </div>
  );

  const scherm = (
    <div className="space-y-3">
      {actiebalk}
      <div className="grid gap-3 items-start lg:grid-cols-[minmax(0,3fr)_minmax(280px,1fr)]">
        {hoofd}
        {zijkolom}
      </div>
      {actieRegel}
    </div>
  );
  if (fullPage) return scherm;
  // Buiten de Helpdesk (Customer 360): hetzelfde scherm, schermvullend over de pagina.
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/95 overflow-y-auto">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">{scherm}</div>
    </div>
  );
};

// Compacte multi-select dropdown (checkboxes) — voor de status-filter.
// Afgehandelde tickets: de eindstatus uit ET. Vallen standaard buiten de lijst —
// wie de helpdesk opent, wil zien wat nog werk vraagt.
const isHdAfgehandeld = (stage) => String(stage || '').trim().toLowerCase() === 'afgehandeld';
// Nieuwe tickets staan altijd bovenaan, ongeacht de aanmaakdatum.
const isHdNieuw = (stage) => String(stage || '').trim().toLowerCase() === 'nieuw';

const HdMultiSelect = ({ options, selected, onChange, placeholder = 'Alle', size = 'md', searchable = null }) => {
  const [open, setOpen] = useState(false);
  // Zoekveld boven de lijst: automatisch zodra er veel opties zijn (tags), of
  // expliciet via `searchable`. Zoeken filtert de lijst, niet de selectie.
  const [zoek, setZoek] = useState('');
  const zoekbaar = searchable != null ? searchable : options.length > 8;
  const ref = useRef(null);
  const knopRef = useRef(null);
  const zoekRef = useRef(null);
  // Portal-paneel: het zit buiten `ref`, dus ook op het paneel zelf testen.
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-dropdown-panel]")) setOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);
  useEffect(() => { if (open && zoekbaar) setTimeout(() => zoekRef.current?.focus(), 30); if (!open) setZoek(''); }, [open, zoekbaar]);
  const sel = new Set(selected);
  const toggle = (v) => { const s = new Set(sel); if (s.has(v)) s.delete(v); else s.add(v); onChange([...s]); };
  const term = zoek.trim().toLowerCase();
  const zichtbaar = term ? options.filter((o) => String(o).toLowerCase().includes(term)) : options;
  return (
    <div className={`relative shrink-0 ${size === 'sm' ? FILTER_SM : FILTER_MD}`} ref={ref}>
      <button ref={knopRef} type="button" onClick={() => setOpen((o) => !o)} className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-700 border rounded text-xs ${selected.length ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
        <span className="truncate">{selected.length ? `${selected.length} geselecteerd` : placeholder}</span>
        <ChevronDown size={13} />
      </button>
      {open && (
        <DropdownPanel anchorRef={knopRef} minWidth={zoekbaar ? 300 : 160} className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-64 flex flex-col p-1">
          {zoekbaar && (
            <div className="relative shrink-0 mb-1">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
              <input ref={zoekRef} value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder={`Zoeken in ${options.length}…`}
                onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } if (e.key === 'Enter' && zichtbaar.length === 1) { e.preventDefault(); toggle(zichtbaar[0]); } }}
                className="w-full pl-6 pr-2 py-1 bg-slate-900/60 border border-slate-600/60 rounded text-xs text-slate-100" />
            </div>
          )}
          <div className="overflow-y-auto min-h-0">
            {options.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Geen opties</div>}
            {options.length > 0 && zichtbaar.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Niets gevonden voor "{zoek}"</div>}
            {zichtbaar.map((o) => (
              <label key={o} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-700/60 cursor-pointer text-xs text-slate-200">
                <input type="checkbox" checked={sel.has(o)} onChange={() => toggle(o)} className="w-3.5 h-3.5 rounded accent-lime-500" />
                <span className="truncate">{o}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && <button type="button" onClick={() => onChange([])} className="shrink-0 mt-1 px-2 py-1 text-[11px] text-slate-400 hover:text-red-300 text-left">Selectie wissen</button>}
        </DropdownPanel>
      )}
    </div>
  );
};

// Tags van een ticket bewerken (sinds de API-update van 11-09-2026 via
// PATCH tag_ids). Toont de pillen met een potloodje; het paneel (portal) laat
// zoeken, aan- en afvinken, en voor een beheerder een nieuwe tag aanmaken.
// `huidige` = de tagNAMEN op het ticket (de lijst en de cache dragen namen),
// `tags` = de kiesbare tags {id, name} (Beheer → Helpdesk tags). Een tag die op
// het ticket staat maar niet meer kiesbaar is, blijft aangevinkt te zien.
// Opslaan stuurt de volledige set id's; de aanroeper werkt zijn rij/ticket bij.
// Meerkeuzefilters Tag en Behandelaar in de ticketlijst: de bijzondere keuzes
// staan als vaste tekst tussen de gewone namen.
const HD_TAG_GEEN = 'Zonder tag';
const HD_BEH_MIJ = 'Aan mij';
const HD_BEH_GEEN = 'Niet toegewezen';
// Een bewaard filter van vóór 11-09-2026 was één string ('all', 'me', 'none',
// '__geen', een id of een tagnaam); daarna een lijst. Beide vormen → lijst.
const hdAlsLijst = (v, soort, assignees = []) => {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x);
  const s = v == null ? '' : String(v);
  if (!s || s === 'all') return [];
  if (soort === 'tag') return [s === '__geen' ? HD_TAG_GEEN : s];
  if (s === 'me') return [HD_BEH_MIJ];
  if (s === 'none') return [HD_BEH_GEEN];
  const u = assignees.find((a) => String(a.id) === s);
  return u && u.name ? [String(u.name)] : [];
};

const HdTagEditor = ({ huidige = [], tags = [], onSave, magNieuw = false, onNieuweTag = null, klein = true, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState('');
  const [sel, setSel] = useState(() => new Set(huidige));
  const [nieuw, setNieuw] = useState('');
  const [bezig, setBezig] = useState(false);
  const ref = useRef(null);
  const knopRef = useRef(null);
  const zoekRef = useRef(null);
  useEffect(() => { if (!open) { setSel(new Set(huidige)); setZoek(''); setNieuw(''); } }, [open, huidige]);
  useEffect(() => { if (open) setTimeout(() => zoekRef.current?.focus(), 30); }, [open]);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.('[data-dropdown-panel]')) setOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);
  const stil = (e) => { e.stopPropagation(); };
  const byName = new Map(tags.map((t) => [t.name, t]));
  const alleNamen = [...new Set([...tags.map((t) => t.name), ...huidige])].sort((a, b) => a.localeCompare(b));
  const term = zoek.trim().toLowerCase();
  const zichtbaar = term ? alleNamen.filter((n) => n.toLowerCase().includes(term)) : alleNamen;
  const toggle = (n) => setSel((s) => { const x = new Set(s); if (x.has(n)) x.delete(n); else x.add(n); return x; });
  const gewijzigd = sel.size !== huidige.length || [...sel].some((n) => !huidige.includes(n));
  const opslaan = async () => {
    const namen = [...sel];
    // Alleen tags met een bekend id gaan mee; een verdwenen tag zonder id kan
    // niet opnieuw gezet worden en valt dus weg (dat zegt het paneel erbij).
    const ids = namen.map((n) => byName.get(n)?.id).filter((id) => id != null);
    setBezig(true);
    try { await onSave(ids, namen.filter((n) => byName.has(n))); setOpen(false); }
    catch (e) { toast.error(e.message); }
    finally { setBezig(false); }
  };
  const maakNieuw = async () => {
    const naam = nieuw.trim();
    if (!naam || !onNieuweTag) return;
    setBezig(true);
    try { const t = await onNieuweTag(naam); if (t && t.name) { setSel((s) => new Set([...s, t.name])); setNieuw(''); } }
    catch (e) { toast.error(e.message); }
    finally { setBezig(false); }
  };
  return (
    <span ref={ref} className="inline-flex items-center gap-1 flex-wrap" onClick={stil} onAuxClick={stil} onMouseDown={stil}>
      {hdTagPillen(huidige, { klein })}
      {!disabled && (
        <button ref={knopRef} type="button" onClick={() => setOpen((o) => !o)} title="Tags bewerken"
          className={`inline-flex items-center justify-center shrink-0 rounded text-slate-500 hover:text-lime-400 hover:bg-slate-700/40 ${klein ? 'w-5 h-5' : 'w-6 h-6'}`}>
          <Pencil size={klein ? 11 : 13} className="shrink-0" />
        </button>
      )}
      {open && (
        <DropdownPanel anchorRef={knopRef} minWidth={300} className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-80 flex flex-col p-1.5 text-left">
          <div className="relative shrink-0 mb-1">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input ref={zoekRef} value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder={`Zoeken in ${alleNamen.length} tags…`}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } if (e.key === 'Enter' && zichtbaar.length === 1) { e.preventDefault(); toggle(zichtbaar[0]); } }}
              className="w-full pl-6 pr-2 py-1 bg-slate-900/60 border border-slate-600/60 rounded text-xs text-slate-100" />
          </div>
          <div className="overflow-y-auto min-h-0">
            {zichtbaar.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Niets gevonden voor "{zoek}"</div>}
            {zichtbaar.map((n) => (
              <label key={n} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-700/60 cursor-pointer text-xs text-slate-200">
                <input type="checkbox" checked={sel.has(n)} onChange={() => toggle(n)} className="w-3.5 h-3.5 rounded accent-lime-500" />
                <span className="truncate">{n}</span>
                {!byName.has(n) && <span className="ml-auto text-[10px] text-slate-500" title="Deze tag is niet meer kiesbaar; uitvinken kan, opnieuw aanvinken niet.">niet kiesbaar</span>}
              </label>
            ))}
          </div>
          {magNieuw && (
            <div className="shrink-0 mt-1 pt-1 border-t border-slate-700/60 flex items-center gap-1">
              <input value={nieuw} onChange={(e) => setNieuw(e.target.value)} placeholder="Nieuwe tag…" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); maakNieuw(); } }}
                className="flex-1 min-w-0 px-2 py-1 bg-slate-900/60 border border-slate-600/60 rounded text-xs text-slate-100" />
              <button type="button" onClick={maakNieuw} disabled={bezig || !nieuw.trim()} className="px-2 py-1 text-[11px] rounded bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50">Toevoegen</button>
            </div>
          )}
          <div className="shrink-0 mt-1 pt-1 border-t border-slate-700/60 flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-500">{sel.size} gekozen</span>
            <span className="flex items-center gap-1">
              <button type="button" onClick={() => setOpen(false)} className="px-2 py-1 text-[11px] rounded text-slate-400 hover:text-slate-200">Annuleren</button>
              <button type="button" onClick={opslaan} disabled={bezig || !gewijzigd} className="px-2.5 py-1 text-[11px] font-semibold rounded bg-lime-500 text-slate-900 disabled:opacity-50">Opslaan</button>
            </span>
          </div>
        </DropdownPanel>
      )}
    </span>
  );
};

// Open of gesloten? ET geeft het autoritatieve `stage_is_closed` mee (cache én
// live). Ontbreekt het (oudere respons), dan valt de toets terug op de naam.
const isHdOpen = (t) => (t && t.stage_is_closed != null ? !t.stage_is_closed : !isHdAfgehandeld(t && t.stage_name));
// Hoeveel dagen staat een ticket open? Enkel zinvol voor open tickets.
// Doorlooptijd in dagen. De cache geeft open_seconds mee: bij een open ticket tot
// nu, bij een afgehandeld ticket tot het bewaarde sluitmoment (closed_at) — zo
// blijft te zien hoelang een ticket open GESTAAN heeft, ook na afhandeling. Komt
// het ticket zonder dat veld binnen (live pad), dan rekenen we zelf tot nu, en
// enkel voor open tickets: van een gesloten ticket kennen we het sluitmoment dan niet.
//
// Sinds 04-10-2026 in BELGISCHE WERKDAGEN (weekends en feestdagen tellen niet) en
// per open PERIODE: een heropend ticket telt vanaf de heropening, niet vanaf de
// aanmaak. De server rekent het uit (open_werkdagen = de lopende periode,
// open_werkdagen_totaal = alle periodes samen; util/ticketPeriodes.js). Ontbreken
// die velden (live pad), dan rekenen we hier zelf vanaf de aanmaak.
const hdDagenOpen = (t) => {
  if (!t) return null;
  if (isHdOpen(t)) {
    if (t.open_werkdagen != null) return Number(t.open_werkdagen);
    const ms = hdTijdMs(t.create_date);
    return Number.isNaN(ms) ? null : werkdagenTussen(ms);
  }
  if (t.open_werkdagen_totaal != null) return Number(t.open_werkdagen_totaal);
  const van = hdTijdMs(t.create_date); const tot = hdTijdMs(t.closed_at);
  return Number.isNaN(van) || Number.isNaN(tot) ? null : werkdagenTussen(van, tot);
};
const hdDag = (s) => String(hdFmt(s) || '').slice(0, 10);
// De uitleg achter het cijfer: heropening, eerdere periodes en het totaal.
const hdOpenUitleg = (t) => {
  if (!t) return undefined;
  const regels = [];
  const per = Array.isArray(t.open_periodes) ? t.open_periodes : [];
  const wd = (n) => `${n} werkdag${Number(n) === 1 ? '' : 'en'}`;
  if (isHdOpen(t)) {
    if (t.open_sinds) regels.push(`Heropend op ${hdDag(t.open_sinds)}: ${wd(hdDagenOpen(t) ?? 0)} open sinds de heropening`);
    else regels.push(`Open sinds ${hdDag(t.create_date)}: ${wd(hdDagenOpen(t) ?? 0)}`);
  } else if (t.closed_at) regels.push(`Afgehandeld op ${hdAfgehandeldOp(t)}`);
  per.forEach((p, i) => regels.push(`${per.length > 1 || isHdOpen(t) ? `Periode ${i + 1}` : 'Open'}: ${hdDag(p.van)} tot ${hdDag(p.tot)} (${wd(p.werkdagen)})`));
  if (t.open_werkdagen_totaal != null && (per.length > 1 || (isHdOpen(t) && per.length > 0))) regels.push(`Totaal open: ${wd(t.open_werkdagen_totaal)}`);
  if (isHdOpen(t) && t.open_sinds && t.open_werkdagen_totaal == null) regels.push('De eerdere periode is niet bewaard: het totaal is niet gekend.');
  regels.push('Werkdagen: weekends en Belgische feestdagen tellen niet mee.');
  return regels.join('\n');
};
// Kleur van de doorlooptijd: bij een open ticket volgens de leeftijd (het is een
// wachttijd), bij een afgehandeld ticket rustig grijs (het is geschiedenis).
const hdDoorlooptijdCls = (t, d) => (isHdOpen(t) ? hdLeeftijdCls(d) : 'text-slate-500');
const hdAfgehandeldOp = (t) => (t && t.closed_at ? hdFmt(t.closed_at) : '');
// Afgehandeld in de afgelopen 7 kalenderdagen — op het bewaarde sluitmoment
// (closed_at), niet op de laatste wijziging: een oud ticket dat gisteren nog een
// notitie kreeg, is daarmee niet gisteren afgehandeld.
const HD_WEEK_MS = 7 * 86400000;
const isHdAfgehandeldInWeek = (t, nu = Date.now()) => { if (!t || isHdOpen(t) || !t.closed_at) return false; const ms = hdTijdMs(t.closed_at); return Number.isFinite(ms) && nu - ms <= HD_WEEK_MS; };
// Grenzen in werkdagen: meer dan 10 (twee weken) = let op, meer dan 20 (vier weken) = te lang.
const hdLeeftijdCls = (d) => (d == null ? 'text-slate-500' : d > 20 ? 'hd-age-late' : d > 10 ? 'hd-age-warn' : 'hd-age-ok');
// Wachttijd in werkdagen van de lopende open periode — voor de kengetallen.
const hdWerkdagenOpen = (t) => (isHdOpen(t) ? hdDagenOpen(t) : null);
// Kleuren van de kengetallen — het vaste grafiekpalet (zie CLAUDE.md).
const HD_KPI_KLEUR = { open: '#14B8A6', actie: '#F59E0B', escalatie: '#8B5CF6', oud: '#F43F5E' };
// Volgorde van "actie vereist van" in het kengetallenblok.
const HD_ACTIE_VOLGORDE = ['customer', 'partner', 'energy_together'];

// Compact kengetallenblok: een titel en rijen "label … aantal". Een rij die een
// filter zet, is een knop; de actieve rij krijgt een merkrand.
// Compact (11-09-2026): kleinere letter en rijhoogte, zodat de drie blokken
// samen zo weinig mogelijk van de ticketlijst wegduwen; de rijen blijven
// klikbare filters.
const HdKpiBlok = ({ titel, rijen, loading, voet }) => (
  <Card className="!p-2.5 h-full">
    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{titel}</div>
    <div className="space-y-0">
      {rijen.length === 0 && <div className="text-[11px] text-slate-500 py-0.5">—</div>}
      {rijen.map((r) => (
        <button key={r.key} type="button" onClick={r.klik} title={r.klik ? `Filter op "${r.label}"` : undefined}
          className={`w-full flex items-center justify-between gap-2 px-1.5 py-[3px] rounded text-[11px] leading-tight text-left ${r.klik ? 'hover:bg-slate-700/50 cursor-pointer' : 'cursor-default'} ${r.actief ? 'ring-1 ring-lime-500/70 bg-slate-700/40' : ''}`}>
          <span className="flex items-center gap-1.5 min-w-0">
            {r.dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.dot }} />}
            <span className="truncate text-slate-300">{r.label}</span>
          </span>
          <span className="font-semibold tabular-nums text-slate-100" style={r.value > 0 && r.dot ? { color: r.dot } : undefined}>{loading ? '…' : formatNumberRounded(r.value)}</span>
        </button>
      ))}
    </div>
    {voet}
  </Card>
);

// ── Helpdesk → Analyse (alleen admin / ET-dossierbeheerder) ──────────────────
// Kwaliteit van de helpdesk en verschillen per energieleverancier. De cijfers
// komen kant-en-klaar van GET /api/helpdesk/analyse (util/helpdeskAnalyse.js,
// getest); dit scherm tekent ze alleen. Definities staan onderaan de pagina en
// in de technische beschrijving van de module.
// ─── Helpdesk → Admin: automatische regels (trefwoord → tag → behandelaar) ───
// Eén mappingtabel (helpdesk_auto_regels). Een rij met trefwoorden zet de tag
// (en/of behandelaar) zodra een trefwoord in het onderwerp van een NIEUW ticket
// staat. De TAG alleen is genoeg voor de behandelaar: draagt een ticket de tag
// van een regel, dan volgt die behandelaar — trefwoord of niet, en ook wanneer
// iemand de tag met de hand in het ticket zet. De
// keuzelijsten komen van de server: kiesbare tags (Helpdesk tags) en de
// behandelaars (users.helpdesk_assignable). Uitvoering: na elke delta-sync,
// of meteen met "Nu uitvoeren". Server: /api/helpdesk/auto-regels[...].
const hdRegelLeeg = () => ({ keywords: '', tag_id: '', assignee_user_id: '', active: true });
const hdRegelNaarForm = (r) => ({ keywords: (r.keywords || []).join(', '), tag_id: r.tag_id != null ? String(r.tag_id) : '', assignee_user_id: r.assignee_user_id != null ? String(r.assignee_user_id) : '', active: r.active !== false });

const HdRegelVelden = ({ form, setForm, tags, assignees, disabled }) => {
  const inp = 'w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200 focus:outline-none focus:border-lime-500';
  return (<>
    <td className="px-3 py-2 align-top"><input value={form.keywords} disabled={disabled} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="bv. factuur, betaling (komma = meerdere)" className={inp} /></td>
    <td className="px-3 py-2 align-top"><select value={form.tag_id} disabled={disabled} onChange={(e) => setForm({ ...form, tag_id: e.target.value })} className={inp}><option value="">— geen tag —</option>{tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></td>
    <td className="px-3 py-2 align-top"><select value={form.assignee_user_id} disabled={disabled} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })} className={inp}><option value="">— geen behandelaar —</option>{assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></td>
    <td className="px-3 py-2 align-top text-center"><input type="checkbox" checked={form.active} disabled={disabled} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-lime-500" /></td>
  </>);
};

const HelpdeskAdminTab = ({ token }) => {
  const api = useMemo(() => hdApi(token), [token]);
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [nieuw, setNieuw] = useState(hdRegelLeeg());
  const [bewerkId, setBewerkId] = useState(null);
  const [bewerk, setBewerk] = useState(null);
  const [verwijderId, setVerwijderId] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [testOnderwerp, setTestOnderwerp] = useState('');
  const [testUit, setTestUit] = useState(null);

  const load = useCallback(async () => {
    setFout(null);
    try { setData(await api.get('/auto-regels')); } catch (e) { setFout(e.message); } finally { setLaden(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, melding) => {
    setBezig(true);
    try { const r = await fn(); if (melding) toast.success(typeof melding === 'function' ? melding(r) : melding); await load(); return r; }
    catch (e) { toast.error(e.message); return null; }
    finally { setBezig(false); }
  };
  // Een bewaarde regel wordt op de server meteen op de open tickets toegepast die
  // ze raakt (achtergrondrun, zie `alleRun`); de melding zegt hoeveel.
  const metToegepast = (basis) => (r) => {
    const t = r && r.toegepast;
    if (t && t.gestart) return `${basis} — ${t.kandidaten} open ticket${t.kandidaten === 1 ? '' : 's'} worden nagekeken`;
    return basis;
  };
  const bewaarNieuw = () => act(async () => { const r = await api.send('/auto-regels', 'POST', nieuw); setNieuw(hdRegelLeeg()); return r; }, metToegepast('Regel toegevoegd'));
  const bewaarBewerk = () => act(async () => { const r = await api.send(`/auto-regels/${bewerkId}`, 'PUT', bewerk); setBewerkId(null); setBewerk(null); return r; }, metToegepast('Regel bewaard'));
  const wisselActief = (r) => act(() => api.send(`/auto-regels/${r.id}`, 'PUT', { ...hdRegelNaarForm(r), active: !r.active }), r.active ? 'Regel uitgezet' : metToegepast('Regel aangezet'));
  const verwijder = (id) => act(async () => { await api.send(`/auto-regels/${id}`, 'DELETE'); setVerwijderId(null); }, 'Regel verwijderd');
  const runNu = () => act(() => api.send('/auto-regels/run', 'POST'), (r) => r && r.skipped ? `Niets gedaan: ${r.reden}` : `${r.verwerkt} nieuwe tickets bekeken, ${r.geraakt} aangepast${r.fouten ? `, ${r.fouten} mislukt` : ''}`);
  // "Alle open tickets bijwerken": start op de server een achtergrondrun (kan
  // minuten duren) en kijk mee via `alleRun` in GET /auto-regels. Vraagt eerst
  // een bevestiging: het schrijft tags naar het bronsysteem.
  const [alleBevestig, setAlleBevestig] = useState(false);
  const alleRun = (data && data.alleRun) || null;
  const alleBezig = !!(alleRun && alleRun.bezig);
  const runAlle = () => { setAlleBevestig(false); act(() => api.send('/auto-regels/run-alle', 'POST'), (r) => r && r.gestart ? `Gestart: ${r.kandidaten} open tickets worden nagekeken` : `Niet gestart: ${(r && r.reden) || 'onbekend'}`); };
  useEffect(() => {
    if (!alleBezig) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [alleBezig, load]);
  const test = async () => {
    if (!testOnderwerp.trim()) { setTestUit(null); return; }
    try { setTestUit(await api.send('/auto-regels/test', 'POST', { subject: testOnderwerp })); } catch (e) { toast.error(e.message); }
  };

  const regels = (data && data.regels) || [];
  const tags = (data && data.tags) || [];
  const assignees = (data && data.assignees) || [];
  const recent = (data && data.recent) || [];
  const recentAantal = data && Number.isFinite(data.recentAantal) ? data.recentAantal : recent.length;
  const bewaarMeer = !!(data && data.bewaarMaanden > 1);
  const bewaarPeriode = bewaarMeer ? `de laatste ${data.bewaarMaanden} maanden` : 'de laatste maand';
  const bewaarDuur = bewaarMeer ? `${data.bewaarMaanden} maanden` : 'één maand';
  const [recentOpen, setRecentOpen] = useState(false);
  const wanneer = (s) => (s ? hdFmt(s) : '');   // Belgische tijd, zie helpdeskDatum.js
  const th = 'px-3 py-2 font-medium text-left';
  const knop = 'px-3 py-1.5 text-xs rounded-lg disabled:opacity-50';

  return (
    <div className="space-y-6">
      {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{fout}</div>}
      <Card className="!p-4">
        <div className="flex flex-wrap items-start gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2"><Wand2 size={16} className="text-lime-400" />Automatische tags en behandelaars</h3>
            <p className="text-xs text-slate-400 mt-1">Voor elk nieuw inkomend ticket dat nog niet is toegewezen: staat een <b>trefwoord</b> in het onderwerp, dan krijgt het ticket de gekozen <b>tag</b> en/of <b>behandelaar</b>. <b>De tag alleen is genoeg voor de behandelaar:</b> draagt een ticket de tag van een regel, dan volgt de behandelaar van die regel — ook als het trefwoord niet in het onderwerp staat, en ook wanneer je de tag zelf in het ticket zet. <b>Wat er al staat, blijft staan:</b> een ticket dat al een tag draagt krijgt geen tags van de regels, en een bestaande behandelaar wordt nooit overschreven. Loopt na elke synchronisatie van nieuwe tickets{data && data.kijkTerugDagen ? ` (tickets van de laatste ${data.kijkTerugDagen} dagen, elk één keer)` : ''}; voor tickets van daarvóór gebruik je "Alle open tickets bijwerken".</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <button type="button" disabled={bezig || alleBezig || !regels.some((r) => r.active)} onClick={runNu} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200 flex items-center gap-1.5`} title="De nieuwe tickets nu door de regels halen"><Play size={12} />Nu uitvoeren</button>
            {alleBevestig ? (
              <span className="flex items-center gap-1.5 text-xs text-slate-300">Alle open tickets nakijken?
                <button type="button" disabled={bezig} onClick={runAlle} className={`${knop} bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold`}>Ja</button>
                <button type="button" disabled={bezig} onClick={() => setAlleBevestig(false)} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`}>Nee</button>
              </span>
            ) : (
              <button type="button" disabled={bezig || alleBezig || !regels.some((r) => r.active)} onClick={() => setAlleBevestig(true)} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200 flex items-center gap-1.5`} title="Alle open tickets door de regels halen — enkel waar nog geen tag en/of behandelaar staat"><Wand2 size={12} />Alle open tickets bijwerken</button>
            )}
          </div>
        </div>
        {alleRun && (alleRun.bezig || alleRun.klaar) && (
          <div className={`mb-3 px-3 py-2 rounded-lg border text-xs ${alleRun.bezig ? 'bg-slate-800/60 border-slate-700 text-slate-300' : 'bg-lime-500/5 border-lime-500/20 text-slate-300'}`}>
            {alleRun.bezig ? <span className="inline-flex items-center gap-2"><Spinner size={12} />Alle open tickets bijwerken: {alleRun.verwerkt} van {alleRun.kandidaten} nagekeken, {alleRun.geraakt} aangepast{alleRun.fouten ? `, ${alleRun.fouten} mislukt` : ''}…</span>
              : <>Laatste volledige run ({wanneer(alleRun.klaar)}): {alleRun.verwerkt} van {alleRun.kandidaten} open tickets nagekeken, <b>{alleRun.geraakt} aangepast</b>{alleRun.fouten ? <>, <span className="text-rose-300">{alleRun.fouten} mislukt</span></> : ''}{alleRun.reden ? <span className="text-rose-300"> — afgebroken: {alleRun.reden}</span> : ''}. Tickets die al een tag of behandelaar hadden zijn ongemoeid gelaten.</>}
          </div>
        )}
        {laden ? <ListSkeleton rows={4} /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
            <thead className="bg-slate-800 text-slate-400 text-xs"><tr>
              <th className={`${th} min-w-[260px]`}>Trefwoorden in het onderwerp</th>
              <th className={`${th} min-w-[200px]`}>Tag</th>
              <th className={`${th} min-w-[200px]`}>Behandelaar</th>
              <th className={`${th} text-center w-16`}>Actief</th>
              <th className={`${th} w-44`}></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-700/60">
              <tr className="bg-lime-500/5">
                <HdRegelVelden form={nieuw} setForm={setNieuw} tags={tags} assignees={assignees} disabled={bezig} />
                <td className="px-3 py-2 align-top"><button type="button" disabled={bezig} onClick={bewaarNieuw} className={`${knop} bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold flex items-center gap-1.5`}><Plus size={12} />Regel toevoegen</button></td>
              </tr>
              {regels.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-slate-500">Nog geen regels. Vul hierboven trefwoorden, een tag en/of een behandelaar in.</td></tr>}
              {regels.map((r) => bewerkId === r.id ? (
                <tr key={r.id} className="bg-slate-800/40">
                  <HdRegelVelden form={bewerk} setForm={setBewerk} tags={tags} assignees={assignees} disabled={bezig} />
                  <td className="px-3 py-2 align-top"><div className="flex gap-1.5">
                    <button type="button" disabled={bezig} onClick={bewaarBewerk} className={`${knop} bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold flex items-center gap-1`}><Check size={12} />Bewaren</button>
                    <button type="button" disabled={bezig} onClick={() => { setBewerkId(null); setBewerk(null); }} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`}>Annuleren</button>
                  </div></td>
                </tr>
              ) : (
                <tr key={r.id} className={r.active ? '' : 'opacity-50'}>
                  <td className="px-3 py-2 align-top">{(r.keywords || []).length ? <div className="flex flex-wrap gap-1">{r.keywords.map((k) => <span key={k} className="px-1.5 py-0.5 rounded bg-slate-700/70 border border-slate-600/60 text-[11px] text-slate-200">{k}</span>)}</div> : <span className="text-xs text-slate-500 italic">geen — geldt zodra het ticket de tag draagt</span>}</td>
                  <td className="px-3 py-2 align-top text-xs text-slate-200">{r.tag_id != null ? <span className="inline-flex items-center gap-1"><Tag size={11} className="text-slate-400" />{r.tag_name || `Tag ${r.tag_id}`}</span> : <span className="text-slate-500">—</span>}</td>
                  <td className="px-3 py-2 align-top text-xs text-slate-200">{r.assignee_user_id != null ? <span className="inline-flex items-center gap-1"><UserCheck size={11} className="text-slate-400" />{r.assignee_name || `Gebruiker ${r.assignee_user_id}`}</span> : <span className="text-slate-500">—</span>}</td>
                  <td className="px-3 py-2 align-top text-center"><input type="checkbox" checked={r.active !== false} disabled={bezig} onChange={() => wisselActief(r)} className="accent-lime-500" title={r.active ? 'Uitzetten' : 'Aanzetten'} /></td>
                  <td className="px-3 py-2 align-top">{verwijderId === r.id ? (
                    <div className="flex items-center gap-1.5 text-xs text-slate-300">Verwijderen?
                      <button type="button" disabled={bezig} onClick={() => verwijder(r.id)} className={`${knop} bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30`}>Ja</button>
                      <button type="button" disabled={bezig} onClick={() => setVerwijderId(null)} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`}>Nee</button>
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      <button type="button" disabled={bezig} onClick={() => { setBewerkId(r.id); setBewerk(hdRegelNaarForm(r)); setVerwijderId(null); }} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200 flex items-center gap-1`}><Pencil size={12} />Bewerken</button>
                      <button type="button" disabled={bezig} onClick={() => setVerwijderId(r.id)} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-rose-300`} title="Verwijderen"><Trash2 size={12} /></button>
                    </div>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        <div className="mt-4 pt-3 border-t border-slate-700/60">
          <label className="block text-xs text-slate-400 mb-1">Test een onderwerp tegen de actieve regels</label>
          <div className="flex flex-wrap gap-2 items-center">
            <input value={testOnderwerp} onChange={(e) => setTestOnderwerp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') test(); }} placeholder="bv. Re: Servolt Energie factuur VOSER/202609/06639" className="flex-1 min-w-[280px] px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200 focus:outline-none focus:border-lime-500" />
            <button type="button" onClick={test} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`}>Testen</button>
            {testUit && <span className="text-xs text-slate-300">{testUit.tags.length || testUit.naar ? <>{testUit.tags.length > 0 && <>Tag{testUit.tags.length > 1 ? 's' : ''} <b>{testUit.tags.join(', ')}</b></>}{testUit.tags.length > 0 && testUit.naar && ' · '}{testUit.naar && <>behandelaar <b>{testUit.naar}</b></>}{testUit.trefwoorden.length > 0 && <span className="text-slate-500"> (trefwoord "{testUit.trefwoorden.join('", "')}")</span>}</> : <span className="text-slate-500">Geen regel raakt dit onderwerp.</span>}</span>}
          </div>
        </div>
      </Card>

      {/* Inklapbaar en standaard DICHT (27-09-2026): de historiek is naslag, geen
          werk. De kop zegt hoeveel er in de bewaarde periode aangepast is; de tabel
          toont de recentste. Ouder dan de bewaartermijn bestaat niet meer (de server
          ruimt op, services/helpdeskAutomatisering.js). */}
      <Card className="!p-0 overflow-hidden">
        <button type="button" onClick={() => setRecentOpen((o) => !o)} aria-expanded={recentOpen} className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-slate-800/40">
          <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${recentOpen ? '' : '-rotate-90'}`} />
          <h3 className="text-sm font-semibold text-slate-100">Laatst automatisch aangepast</h3>
          <span className="text-xs text-slate-400">{laden ? '' : recentAantal === 0 ? `geen in ${bewaarPeriode}` : `${formatNumberRounded(recentAantal)} in ${bewaarPeriode}`}</span>
          <span className="ml-auto text-[11px] text-slate-500">{recentOpen ? 'Inklappen' : 'Tonen'}</span>
        </button>
        {recentOpen && <div className="px-4 pb-4">
        <p className="text-xs text-slate-500 mb-2">{recent.length < recentAantal ? `De ${recent.length} recentste. ` : ''}Historiek ouder dan {bewaarDuur} wordt automatisch verwijderd; het logboek van elk ticket blijft volledig.</p>
        {recent.length === 0 ? <p className="text-xs text-slate-500">Nog geen tickets automatisch aangepast.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
            <thead className="bg-slate-800 text-slate-400 text-xs"><tr><th className={th}>Wanneer</th><th className={th}>Ticket</th><th className={th}>Onderwerp</th><th className={th}>Tags</th><th className={th}>Behandelaar</th><th className={th}>Status</th></tr></thead>
            <tbody className="divide-y divide-slate-700/60 text-xs text-slate-200">
              {recent.map((r) => <tr key={r.ticket_id}>
                <td className="px-3 py-1.5 whitespace-nowrap">{wanneer(r.applied_at)}</td>
                <td className="px-3 py-1.5"><a href={ticketTabUrl(r.ticket_id)} target="_blank" rel="noreferrer" className="text-lime-300 hover:underline">{r.ref || r.ticket_id}</a></td>
                <td className="px-3 py-1.5 max-w-[360px] truncate" title={r.details && r.details.subject}>{r.details && r.details.subject}</td>
                <td className="px-3 py-1.5">{((r.details && r.details.tags) || []).join(', ') || '—'}</td>
                <td className="px-3 py-1.5">{(r.details && r.details.naar) || '—'}</td>
                <td className="px-3 py-1.5">{r.status === 'fout' ? <span className="text-rose-300" title={r.details && r.details.fout}>mislukt</span> : 'toegepast'}</td>
              </tr>)}
            </tbody>
          </table></div>
        )}
        </div>}
      </Card>

      <HdDiagnoseDraad api={api} th={th} knop={knop} wanneer={wanneer} />

      {/* Het bestaande tagbeheer (welke tags kiesbaar zijn) hoort hier ook thuis. */}
      <HelpdeskTagsPage token={token} />
    </div>
  );
};

// Diagnose van de draadkoppeling op de echte data (GET /diagnose-draad, admin):
// dubbele tickets aan de bron, de Message-ID-status van elke portaalmail en
// klantantwoorden die naast hun draad vielen. Laadt pas op de knop: drie zware
// queries, en je kijkt er alleen naar als je twijfelt.
const HdDiagnoseDraad = ({ api, th, knop, wanneer }) => {
  const [uit, setUit] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [dagen, setDagen] = useState(60);
  const laad = async () => {
    setBezig(true);
    try { setUit(await api.get(`/diagnose-draad?dagen=${dagen}`)); } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };
  const s = uit && uit.samenvatting;
  const tegel = (label, waarde, goed) => (
    <div className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/60 min-w-[150px]">
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className={`text-lg font-semibold ${goed === undefined ? 'text-slate-100' : goed ? 'text-emerald-300' : 'text-rose-300'}`}>{waarde}</div>
    </div>
  );
  const duur = (sec) => (sec < 60 ? `${sec} s` : sec < 3600 ? `${Math.round(sec / 60)} min` : sec < 86400 ? `${Math.round(sec / 3600)} u` : `${Math.round(sec / 86400)} d`);
  return (
    <Card className="!p-4">
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2"><Activity size={16} className="text-lime-400" />Diagnose draadkoppeling</h3>
          <p className="text-xs text-slate-400 mt-1">Controleert op de echte tickets of alles in één ticket blijft: <b>dubbels</b> (zelfde klant, zelfde onderwerp, kort na elkaar — seconden of minuten uit elkaar is een mail die aan de bron twee keer verwerkt is), de <b>Message-ID</b> van elke mail vanuit het portaal (kreeg ze er een, staat ze op het ticket, nam het bronsysteem de id over) en <b>antwoorden naast de draad</b> (een "Re:" op een portaalmail dat een nieuw ticket werd).</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select value={dagen} onChange={(e) => setDagen(Number(e.target.value))} className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-slate-200">
            {[14, 30, 60, 120].map((d) => <option key={d} value={d}>{d} dagen</option>)}
          </select>
          <button onClick={laad} disabled={bezig} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`}>{bezig ? 'Bezig…' : 'Nakijken'}</button>
        </div>
      </div>
      {s && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {tegel('Dubbels (<15 min)', s.dubbelsBinnen15Min, s.dubbelsBinnen15Min === 0)}
            {tegel('Dubbels (tot 30 d)', s.dubbels)}
            {tegel('Portaalmails', s.portaalmails)}
            {tegel('… met Message-ID', `${s.metMessageId}/${s.portaalmails}`, s.portaalmails === 0 || s.metMessageId === s.portaalmails)}
            {tegel('… op het ticket', `${s.opTicket}/${s.portaalmails}`, s.portaalmails === 0 || s.opTicket === s.portaalmails)}
            {tegel('… id door bron overgenomen', `${s.messageIdOpTicket}/${s.portaalmails}`, s.portaalmails === 0 || s.messageIdOpTicket === s.portaalmails)}
            {tegel('Antwoord naast de draad', s.naastDraad, s.naastDraad === 0)}
          </div>
          {uit.naastDraad.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-rose-300 mb-1">Antwoorden die een nieuw ticket werden</h4>
              <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
                <thead className="bg-slate-800 text-slate-400 text-xs"><tr><th className={th}>Gemaild ticket</th><th className={th}>Mail verstuurd</th><th className={th}>Klant</th><th className={th}>Nieuw ticket</th><th className={th}>Aangemaakt</th><th className={th}>Onderwerp</th></tr></thead>
                <tbody className="divide-y divide-slate-700/60 text-xs text-slate-200">
                  {uit.naastDraad.map((r) => <tr key={`${r.post_id}-${r.nieuw_ticket}`}>
                    <td className="px-3 py-1.5"><a href={ticketTabUrl(r.gemaild_ticket)} target="_blank" rel="noreferrer" className="text-lime-300 hover:underline">{r.gemaild_ref || r.gemaild_ticket}</a></td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{wanneer(r.sent_at)}</td>
                    <td className="px-3 py-1.5">{r.klant}</td>
                    <td className="px-3 py-1.5"><a href={ticketTabUrl(r.nieuw_ticket)} target="_blank" rel="noreferrer" className="text-rose-300 hover:underline">{r.nieuw_ref || r.nieuw_ticket}</a></td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{wanneer(r.nieuw_aangemaakt)}</td>
                    <td className="px-3 py-1.5 max-w-[360px] truncate" title={r.nieuw_onderwerp}>{r.nieuw_onderwerp}</td>
                  </tr>)}
                </tbody>
              </table></div>
            </div>
          )}
          {uit.dubbels.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-300 mb-1">Dubbels: zelfde klant, zelfde onderwerp</h4>
              <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
                <thead className="bg-slate-800 text-slate-400 text-xs"><tr><th className={th}>Eerste</th><th className={th}>Later</th><th className={th}>Ertussen</th><th className={th}>Klant</th><th className={th}>Onderwerp</th><th className={th}>Fase (later)</th></tr></thead>
                <tbody className="divide-y divide-slate-700/60 text-xs text-slate-200">
                  {uit.dubbels.map((r) => <tr key={`${r.eerste}-${r.later}`}>
                    <td className="px-3 py-1.5"><a href={ticketTabUrl(r.eerste)} target="_blank" rel="noreferrer" className="text-lime-300 hover:underline">{r.eerste_ref || r.eerste}</a></td>
                    <td className="px-3 py-1.5"><a href={ticketTabUrl(r.later)} target="_blank" rel="noreferrer" className="text-lime-300 hover:underline">{r.later_ref || r.later}</a></td>
                    <td className={`px-3 py-1.5 whitespace-nowrap ${r.seconden < 900 ? 'text-rose-300' : ''}`}>{duur(r.seconden)}</td>
                    <td className="px-3 py-1.5">{r.klant}</td>
                    <td className="px-3 py-1.5 max-w-[360px] truncate" title={r.onderwerp_later}>{r.onderwerp_later}</td>
                    <td className="px-3 py-1.5">{r.fase_later}</td>
                  </tr>)}
                </tbody>
              </table></div>
            </div>
          )}
          {uit.portaalmails.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-300 mb-1">Portaalmails sinds 25-09-2026</h4>
              <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
                <thead className="bg-slate-800 text-slate-400 text-xs"><tr><th className={th}>Verstuurd</th><th className={th}>Ticket</th><th className={th}>Aan</th><th className={th}>Onderwerp</th><th className={th}>Message-ID</th><th className={th}>Op ticket</th><th className={th}>Id overgenomen</th></tr></thead>
                <tbody className="divide-y divide-slate-700/60 text-xs text-slate-200">
                  {uit.portaalmails.map((p) => <tr key={p.id}>
                    <td className="px-3 py-1.5 whitespace-nowrap">{wanneer(p.sent_at)}</td>
                    <td className="px-3 py-1.5"><a href={ticketTabUrl(p.ticket_id)} target="_blank" rel="noreferrer" className="text-lime-300 hover:underline">{p.ref || p.ticket_id}</a></td>
                    <td className="px-3 py-1.5">{p.to_address}</td>
                    <td className="px-3 py-1.5 max-w-[300px] truncate" title={p.subject}>{p.subject}</td>
                    <td className="px-3 py-1.5 max-w-[220px] truncate font-mono" title={p.message_id || ''}>{p.message_id ? p.message_id : <span className="text-rose-300">geen</span>}</td>
                    <td className="px-3 py-1.5">{p.posted_at ? 'ja' : <span className="text-rose-300" title={p.post_error || ''}>nee</span>}</td>
                    <td className="px-3 py-1.5">{p.message_id_op_ticket === true ? 'ja' : p.message_id_op_ticket === false ? <span className="text-rose-300">nee</span> : '—'}</td>
                  </tr>)}
                </tbody>
              </table></div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const HELPDESK_TABS = [{ id: 'samenvatting', label: 'Overzicht' }, { id: 'analyse', label: 'Analyse' }, { id: 'kennisbank', label: 'Kennisbank' }, { id: 'ai', label: 'AI-voorstellen' }, { id: 'admin', label: 'Admin' }];

// ─── Kennisbank ──────────────────────────────────────────────────────────────
// Werkinstructies voor de helpdesk: lezen en zoeken voor iedereen met leesrecht
// (binnen het eigen energielabel), beheren door admin en ET-dossierbeheerder.
// Dezelfde artikels voeden het AI-voorstel (server-side, op het label van het
// ticket) en het invoegpaneel in de composer.
const KB_HTML_LEEG = '<p></p>';
const KennisbankPagina = ({ token, currentUser, salesStructure = EMPTY_STRUCTURE }) => {
  const magBeheren = currentUser?.role === 'admin' || currentUser?.role === 'et_dossier';
  const labels = salesStructure.energy_labels || [];
  const [q, setQ] = useState('');
  const [fLabel, setFLabel] = useState('');
  const [lijst, setLijst] = useState(null);
  const [fout, setFout] = useState('');
  const [gekozenId, setGekozenId] = useState(null);
  const [artikel, setArtikel] = useState(null);
  const [draft, setDraft] = useState(null);       // { ...velden } bij bewerken/nieuw
  const [busy, setBusy] = useState(false);
  const [editorVersie, setEditorVersie] = useState(0);
  const [editorHtml, setEditorHtml] = useState(false);
  const kb = useCallback(async (p, method = 'GET', body) => {
    const r = await fetch(`/api/kennisbank${p}`, { method, headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
    const b = await r.json().catch(() => null);
    if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
    return b;
  }, [token]);
  const laad = useCallback(() => {
    const qs = new URLSearchParams();
    if (q.trim()) qs.set('q', q.trim());
    if (fLabel) qs.set('label', fLabel);
    return kb(`?${qs.toString()}`).then((d) => { setLijst(d.artikels || []); setFout(''); }).catch((e) => { setFout(e.message); setLijst([]); });
  }, [kb, q, fLabel]);
  useEffect(() => { const h = setTimeout(laad, q ? 300 : 0); return () => clearTimeout(h); }, [laad, q]);
  useEffect(() => {
    if (gekozenId == null) { setArtikel(null); return undefined; }
    let leeft = true;
    kb(`/${gekozenId}`).then((d) => leeft && setArtikel(d.artikel)).catch((e) => leeft && setFout(e.message));
    return () => { leeft = false; };
  }, [gekozenId, kb]);

  const nieuw = () => { setDraft({ id: null, title: '', summary: '', category: '', energy_label: '', tags: '', status: 'published', ai_bruikbaar: true, niet_voor_labels: [], body_html: KB_HTML_LEEG }); setEditorHtml(false); setEditorVersie((v) => v + 1); };
  const bewerk = () => { if (!artikel) return; setDraft({ ...artikel, tags: (artikel.tags || []).join(', '), energy_label: artikel.energy_label || '', niet_voor_labels: artikel.niet_voor_labels || [] }); setEditorHtml(false); setEditorVersie((v) => v + 1); };
  const bewaar = async () => {
    if (!draft || !draft.title.trim()) { toast.error('Geef het artikel een titel.'); return; }
    setBusy(true);
    try {
      const body = { ...draft, tags: String(draft.tags || '').split(',').map((t) => t.trim()).filter(Boolean), energy_label: draft.energy_label || '' };
      const d = draft.id ? await kb(`/${draft.id}`, 'PUT', body) : await kb('', 'POST', body);
      toast.success(draft.id ? 'Artikel bijgewerkt' : 'Artikel aangemaakt');
      setDraft(null); await laad(); setGekozenId(d.artikel.id); setArtikel(d.artikel);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const verwijder = async () => {
    if (!artikel || !window.confirm(`Artikel "${artikel.title}" verwijderen?`)) return;
    setBusy(true);
    try { await kb(`/${artikel.id}`, 'DELETE'); toast.success('Artikel verwijderd'); setGekozenId(null); await laad(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const groepen = useMemo(() => {
    const m = new Map();
    for (const a of lijst || []) { const c = a.category || 'Algemeen'; if (!m.has(c)) m.set(c, []); m.get(c).push(a); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'nl-BE'));
  }, [lijst]);
  const veld = 'w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100';
  const labelBadge = (a) => (a.energy_label
    ? <span className="px-1.5 py-0.5 rounded bg-slate-700/60 border border-slate-600/50 text-[10px] text-slate-300">{a.energy_label}</span>
    : <span className="px-1.5 py-0.5 rounded bg-lime-500/10 border border-lime-500/30 text-[10px] text-lime-300">alle labels</span>);

  return (
    <>
    <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
      <Card className="!p-3 space-y-2 min-w-0">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek een werkinstructie…" className={`${veld} pl-8`} />
          </div>
          {magBeheren && <button onClick={nieuw} title="Nieuw artikel" className="inline-flex items-center gap-1 h-9 px-3 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-lg text-xs font-semibold shrink-0"><Plus size={14} />Nieuw</button>}
        </div>
        {magBeheren && labels.length > 0 && (
          <select value={fLabel} onChange={(e) => setFLabel(e.target.value)} className="w-full h-8 px-2 bg-slate-700/70 border border-slate-600/70 rounded-md text-xs text-slate-200">
            <option value="">Alle energielabels</option>
            {labels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
        {fout && <div className="text-xs text-red-300">{fout}</div>}
        <div className="max-h-[70vh] overflow-y-auto -mx-1 px-1">
          {lijst === null && <div className="flex justify-center py-6"><Spinner size={20} /></div>}
          {lijst && lijst.length === 0 && <TableEmpty message={q ? `Niets gevonden voor “${q}”` : 'Nog geen werkinstructies'} icon={Folder} />}
          {groepen.map(([cat, items]) => (
            <div key={cat} className="mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">{cat}</div>
              {items.map((a) => (
                <button key={a.id} onClick={() => { setGekozenId(a.id); setDraft(null); }} className={`w-full text-left px-2.5 py-2 rounded-lg border ${gekozenId === a.id ? 'bg-lime-500/10 text-lime-300 border-lime-500/30' : 'hover:bg-slate-800 text-slate-300 border-transparent'}`}>
                  <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium truncate">{a.title}</span>{a.status === 'draft' && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px] shrink-0">concept</span>}</div>
                  <div className="text-[11px] text-slate-500 truncate">{a.summary || a.fragment}</div>
                  <div className="mt-1 flex flex-wrap gap-1">{labelBadge(a)}{(a.tags || []).slice(0, 4).map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 text-[10px]">{t}</span>)}</div>
                </button>
              ))}
            </div>
          ))}
        </div>
      </Card>

      <Card className="min-w-0">
        {draft ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-bold text-slate-100">{draft.id ? 'Artikel bewerken' : 'Nieuw artikel'}</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setDraft(null)} className="h-8 px-3 text-xs text-slate-400 hover:text-slate-200">Annuleren</button>
                <button onClick={bewaar} disabled={busy} className="inline-flex items-center gap-1 h-8 px-3.5 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-md text-xs font-semibold disabled:opacity-50"><Check size={14} />Opslaan</button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><label className="block text-xs text-slate-400 mb-1">Titel</label><input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} className={veld} /></div>
              <div className="sm:col-span-2"><label className="block text-xs text-slate-400 mb-1">Samenvatting (één zin, ook wat het zoekpaneel toont)</label><input value={draft.summary} onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))} className={veld} /></div>
              <div><label className="block text-xs text-slate-400 mb-1">Categorie</label><input value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} placeholder="bv. Facturatie, Verhuis, Meterstanden" className={veld} /></div>
              <div><label className="block text-xs text-slate-400 mb-1">Energielabel</label>
                <select value={draft.energy_label} onChange={(e) => setDraft((d) => ({ ...d, energy_label: e.target.value }))} className={veld}>
                  <option value="">Alle energielabels</option>
                  {labels.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">Met een label komt het artikel nooit in het dossier of het AI-voorstel van een ander label.</p>
              </div>
              {/* De uitzondering: een artikel voor alle labels dat voor één of meer
                  labels niet geldt (dat label heeft er een eigen artikel over). */}
              {!draft.energy_label && labels.length > 0 && (
                <div className="sm:col-span-2" data-kb-niet-voor>
                  <label className="block text-xs text-slate-400 mb-1">Geldt niet voor</label>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {labels.map((l) => {
                      const aan = (draft.niet_voor_labels || []).some((x) => x.toLowerCase() === l.toLowerCase());
                      return <label key={l} className="inline-flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer select-none"><input type="checkbox" checked={aan} onChange={(e) => setDraft((d) => ({ ...d, niet_voor_labels: e.target.checked ? [...(d.niet_voor_labels || []), l] : (d.niet_voor_labels || []).filter((x) => x.toLowerCase() !== l.toLowerCase()) }))} className="w-3.5 h-3.5 rounded accent-lime-500" />{l}</label>;
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Vink een label aan als het hier een eigen artikel over heeft of als dit voor dat label niet klopt. Het artikel komt dan niet in de voorstellen van dat label.</p>
                </div>
              )}
              <div><label className="block text-xs text-slate-400 mb-1">Tags (komma-gescheiden)</label><input value={draft.tags} onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))} className={veld} /></div>
              <div className="flex flex-wrap items-center gap-4 pt-5">
                <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer"><input type="checkbox" checked={draft.status === 'published'} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.checked ? 'published' : 'draft' }))} className="w-3.5 h-3.5 rounded accent-lime-500" />Gepubliceerd</label>
                <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer" title="Mag het AI-voorstel dit artikel als bron gebruiken?"><input type="checkbox" checked={!!draft.ai_bruikbaar} onChange={(e) => setDraft((d) => ({ ...d, ai_bruikbaar: e.target.checked }))} className="w-3.5 h-3.5 rounded accent-violet-500" />Bron voor AI-voorstel</label>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="block text-xs text-slate-400">Inhoud</label>
                <button type="button" onClick={() => { setEditorHtml((s) => !s); setEditorVersie((v) => v + 1); }} className="text-[11px] text-slate-400 hover:text-slate-200">{editorHtml ? 'Visueel' : 'HTML'}</button>
              </div>
              {editorHtml
                ? <textarea value={draft.body_html} onChange={(e) => setDraft((d) => ({ ...d, body_html: e.target.value }))} rows={16} className={`${veld} font-mono text-xs`} />
                : <HdVisueleEditor html={draft.body_html} versie={editorVersie} onChange={(html) => setDraft((d) => ({ ...d, body_html: html }))} />}
            </div>
          </div>
        ) : artikel ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-100">{artikel.title}</h3>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                  {labelBadge(artikel)}
                  {artikel.category && <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-[10px] text-slate-300">{artikel.category}</span>}
                  {(artikel.tags || []).map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 text-[10px]">{t}</span>)}
                  {artikel.status === 'draft' && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px]">concept</span>}
                  {artikel.soort === 'voorwaarden' && <span className="hd-pill hd-pill-indigo">algemene voorwaarden</span>}
                  {(artikel.niet_voor_labels || []).length > 0 && <span className="hd-pill hd-pill-amber" title="Dit artikel komt niet in de voorstellen van deze labels">geldt niet voor {artikel.niet_voor_labels.join(', ')}</span>}
                  {!artikel.ai_bruikbaar && <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-[10px] text-slate-400">niet voor AI</span>}
                  <span>· bijgewerkt {artikel.updated_at ? new Date(artikel.updated_at).toLocaleDateString('nl-BE') : '—'}</span>
                </div>
              </div>
              {artikel.soort === 'voorwaarden' && artikel.bron_url && (
                <a href={artikel.bron_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-8 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-xs shrink-0"><ExternalLink size={13} />Open het document</a>
              )}
              {magBeheren && artikel.soort !== 'voorwaarden' && (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={bewerk} className="inline-flex items-center gap-1 h-8 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-xs"><Edit2 size={13} />Bewerken</button>
                  <button onClick={verwijder} disabled={busy} className="inline-flex items-center gap-1 h-8 px-3 bg-slate-700 hover:bg-red-500/30 text-slate-300 hover:text-red-200 rounded-md text-xs"><Trash2 size={13} />Verwijderen</button>
                </div>
              )}
            </div>
            {artikel.soort === 'voorwaarden'
              ? <p className="text-xs text-slate-400">Dit is de letterlijke tekst van het document. Ze is hier niet te bewerken: laad een nieuwe versie van het document op bij "Algemene voorwaarden" onderaan.</p>
              : artikel.summary && <p className="text-sm text-slate-300">{artikel.summary}</p>}
            <div className="hd-html text-sm text-slate-200 leading-relaxed" dangerouslySetInnerHTML={{ __html: artikel.body_html }} />
          </div>
        ) : (
          <TableEmpty icon={BookOpen} className="py-16" message={magBeheren ? 'Kies links een werkinstructie, of maak een nieuw artikel. Artikels zonder energielabel gelden voor alle labels.' : 'Kies links een werkinstructie om ze te lezen.'} />
        )}
      </Card>
    </div>
    {magBeheren && <KennisbankBeheer token={token} labels={labels} onArtikelsGewijzigd={laad} />}
    </>
  );
};

// Algemene voorwaarden per energielabel: één document voor kleinverbruik en één
// voor grootverbruik (prijs op maat). Een document komt van een adres (de pagina
// van de website waar het te downloaden is, of het document zelf; de wekelijkse
// job haalt het opnieuw op) of van een opgeladen pdf. Elk artikel wordt een
// kennisbankartikel waarin het AI-voorstel zoekt, voor het segment van de klant.
const AV_SEGMENTEN = [{ id: 'kleinverbruik', label: 'Kleinverbruik', uitleg: 'particuliere en kleine professionele klanten' }, { id: 'grootverbruik', label: 'Grootverbruik', uitleg: 'prijs op maat (Custom)' }];
const AV_DOELGROEP = { B2C: 'particulier', B2B: 'professioneel' };
const KennisbankVoorwaarden = ({ token, labels, onArtikelsGewijzigd }) => {
  const [lijst, setLijst] = useState(null);
  const [vorm, setVorm] = useState({ energy_label: '', segment: 'kleinverbruik', via: 'adres', url: '' });
  const [bestand, setBestand] = useState(null);
  const [bezig, setBezig] = useState(null);
  const bestandRef = useRef(null);
  const kop = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const roep = useCallback(async (pad, opties = {}) => {
    const r = await fetch(`/api/kennisbank${pad}`, { ...opties, headers: { ...kop, ...(opties.headers || {}) } });
    const b = await r.json().catch(() => null);
    if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
    return b;
  }, [kop]);
  const laad = useCallback(() => roep('/voorwaarden').then((d) => setLijst(d.documenten || [])).catch((e) => { toast.error(e.message); setLijst([]); }), [roep]);
  useEffect(() => { laad(); }, [laad]);
  const meld = (d, actie) => {
    const r = d.resultaat || {};
    if (!d.ok) { toast.error(`${actie} mislukt: ${r.error || 'onbekende fout'}`); return; }
    if (r.gewijzigd === false) { toast.success('Het document is ongewijzigd; de artikels blijven zoals ze zijn.'); return; }
    const delen = (r.doelgroepen || []).filter(Boolean).map((g) => AV_DOELGROEP[g] || g);
    toast.success(`${r.artikels} artikels ingelezen${r.versie ? `, versie ${r.versie}` : ''}${delen.length > 1 ? ` (${delen.join(' en ')})` : ''}`);
  };
  const bewaar = async () => {
    const bestaat = (lijst || []).some((d) => String(d.energy_label || '').toLowerCase() === vorm.energy_label.toLowerCase() && d.segment === vorm.segment && d.aantal_artikels > 0);
    if (bestaat && !window.confirm(`Er staat al een document voor ${vorm.energy_label || 'alle energielabels'}, ${vorm.segment}. Vervangen door het nieuwe?`)) return;
    setBezig('nieuw');
    try {
      let d;
      if (vorm.via === 'upload') {
        if (!bestand) { toast.error('Kies een pdf-bestand.'); return; }
        const fd = new FormData();
        fd.append('energy_label', vorm.energy_label); fd.append('segment', vorm.segment); fd.append('bestand', bestand);
        d = await roep('/voorwaarden/upload', { method: 'POST', body: fd });
      } else {
        if (!/^https?:\/\//i.test(vorm.url.trim())) { toast.error('Geef een volledige URL (https://…)'); return; }
        d = await roep('/voorwaarden', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ energy_label: vorm.energy_label, segment: vorm.segment, url: vorm.url.trim() }) });
      }
      meld(d, 'Inlezen');
      if (d.ok) { setVorm((v) => ({ ...v, url: '' })); setBestand(null); if (bestandRef.current) bestandRef.current.value = ''; }
      await laad(); onArtikelsGewijzigd && onArtikelsGewijzigd();
    } catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const haalOp = async (d) => {
    setBezig(d.id);
    try { meld(await roep(`/voorwaarden/${d.id}/ophalen`, { method: 'POST' }), 'Ophalen'); await laad(); onArtikelsGewijzigd && onArtikelsGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const zetActief = async (d, actief) => {
    setBezig(d.id);
    try { await roep(`/voorwaarden/${d.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actief }) }); toast.success(actief ? 'Het AI-voorstel gebruikt dit document weer' : 'Het AI-voorstel gebruikt dit document niet meer'); await laad(); onArtikelsGewijzigd && onArtikelsGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const verwijder = async (d) => {
    if (!window.confirm(`De algemene voorwaarden van ${d.energy_label || 'alle energielabels'} (${d.segment}) en hun ${d.aantal_artikels || 0} artikels verwijderen?`)) return;
    setBezig(d.id);
    try { await roep(`/voorwaarden/${d.id}`, { method: 'DELETE' }); toast.success('Document verwijderd'); await laad(); onArtikelsGewijzigd && onArtikelsGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const download = async (d) => {
    try {
      const r = await fetch(`/api/kennisbank/voorwaarden/${d.id}/bestand`, { headers: kop });
      if (!r.ok) throw new Error('Kon het bestand niet ophalen');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a'); a.href = url; a.download = d.bestandsnaam || 'algemene-voorwaarden.pdf'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
  };
  // Per segment: welk document geldt voor welk label. Het gedeelde document geldt
  // voor elk label zonder eigen document; een eigen document is de uitzondering.
  const dekking = useMemo(() => {
    if (!lijst) return [];
    const bruikbaar = lijst.filter((d) => d.actief && d.aantal_artikels > 0);
    return AV_SEGMENTEN.map((s) => {
      const gedeeld = bruikbaar.some((d) => !d.energy_label && d.segment === s.id);
      const eigen = labels.filter((l) => bruikbaar.some((d) => d.energy_label && d.energy_label.toLowerCase() === l.toLowerCase() && d.segment === s.id));
      const zonder = labels.filter((l) => !eigen.includes(l));
      return { segment: s, gedeeld, eigen, zonder };
    });
  }, [lijst, labels]);
  const veld = 'px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100';
  return (
    <Card className="space-y-3 min-w-0" id="kb-voorwaarden">
      <div>
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2"><ScrollText size={15} className="text-lime-400" />Algemene voorwaarden (gedeeld of per energielabel)</h3>
          <InfoTip title="Algemene voorwaarden" text={'Eén document voor kleinverbruik (particuliere en kleine professionele klanten) en één voor grootverbruik (prijs op maat).\n\nZijn de voorwaarden van je labels dezelfde, laad ze dan één keer op voor "Alle energielabels". Dat document geldt voor elk label. Wijken de voorwaarden van één label af, laad dan voor dat label een eigen document op: dat gaat voor, in zijn geheel.\n\nDe naam, het e-mailadres en de website van het label waaruit het gedeelde document komt, worden in elk voorstel vervangen door die van het label van het ticket. Een klant leest nooit de naam van een ander energielabel.\n\nGeef bij voorkeur de pagina van de website op waar het document te downloaden is: het adres van het document zelf verandert bij elke nieuwe versie, de pagina niet. De job "Helpdesk — kennisbank" haalt het document elke week opnieuw op en leest het alleen opnieuw in als het gewijzigd is. Staat het document niet op de website, laad dan de pdf op.\n\nElk artikel van het document wordt een artikel in de kennisbank. Het AI-voorstel zoekt erin voor het segment van de klant van het ticket, legt uit in gewone taal en verwijst naar het artikelnummer. Deze artikels zijn niet zichtbaar voor partners.'} />
        </div>
        <p className="text-xs text-slate-400 mt-1">Het AI-voorstel gebruikt de inhoud om juist en concreet te antwoorden, ook bij een klacht. Het schrijft zelf in gewone, vriendelijke taal en verwijst naar het artikel.</p>
      </div>
      <div className="grid sm:grid-cols-[minmax(0,170px)_minmax(0,150px)_minmax(0,130px)_minmax(0,1fr)_auto] gap-2 items-end">
        <div><label className="block text-[11px] text-slate-400 mb-1">Energielabel</label>
          <select value={vorm.energy_label} onChange={(e) => setVorm((v) => ({ ...v, energy_label: e.target.value }))} className={`${veld} w-full`} aria-label="Energielabel van de voorwaarden"><option value="">Alle energielabels (gedeeld)</option>{labels.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
        <div><label className="block text-[11px] text-slate-400 mb-1">Segment</label>
          <select value={vorm.segment} onChange={(e) => setVorm((v) => ({ ...v, segment: e.target.value }))} className={`${veld} w-full`} aria-label="Segment">{AV_SEGMENTEN.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
        <div><label className="block text-[11px] text-slate-400 mb-1">Bron</label>
          <select value={vorm.via} onChange={(e) => setVorm((v) => ({ ...v, via: e.target.value }))} className={`${veld} w-full`} aria-label="Bron"><option value="adres">Adres (URL)</option><option value="upload">Pdf opladen</option></select></div>
        {vorm.via === 'adres'
          ? <div><label className="block text-[11px] text-slate-400 mb-1">Pagina of document (bv. https://www.power2you.be/algemene-voorwaarden)</label><input value={vorm.url} onChange={(e) => setVorm((v) => ({ ...v, url: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') bewaar(); }} className={`${veld} w-full font-mono text-xs`} /></div>
          : <div><label className="block text-[11px] text-slate-400 mb-1">Pdf-bestand (max. 15 MB)</label>
            <input ref={bestandRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setBestand((e.target.files && e.target.files[0]) || null)} />
            <button type="button" onClick={() => bestandRef.current && bestandRef.current.click()} className={`${veld} w-full text-left truncate hover:border-slate-500`}><Upload size={13} className="inline mr-1.5 -mt-0.5 text-slate-400" />{bestand ? bestand.name : <span className="text-slate-400">Kies een bestand…</span>}</button></div>}
        <button onClick={bewaar} disabled={bezig === 'nieuw'} className="inline-flex items-center gap-1 h-9 px-3 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-lg text-xs font-semibold disabled:opacity-50 whitespace-nowrap">{bezig === 'nieuw' ? <Spinner size={12} /> : <Plus size={14} />}{bezig === 'nieuw' ? 'Bezig…' : 'Inlezen'}</button>
      </div>
      <p className="text-[11px] text-slate-500">{(AV_SEGMENTEN.find((s) => s.id === vorm.segment) || {}).label}: {(AV_SEGMENTEN.find((s) => s.id === vorm.segment) || {}).uitleg}.</p>
      {lijst === null ? <div className="flex justify-center py-4"><Spinner size={18} /></div> : lijst.length === 0 ? <div className="text-xs text-slate-500">Nog geen algemene voorwaarden ingelezen.</div> : (
        <div className="overflow-x-auto"><table className="w-full text-xs">
          <thead><tr className="text-slate-400 text-left whitespace-nowrap"><th className="py-1.5 pr-2 font-medium">Label</th><th className="py-1.5 px-2 font-medium">Segment</th><th className="py-1.5 px-2 font-medium">Document</th><th className="py-1.5 px-2 font-medium">Versie</th><th className="py-1.5 px-2 font-medium text-right">Artikels</th><th className="py-1.5 px-2 font-medium">Laatst nagekeken</th><th className="py-1.5 px-2 font-medium">Status</th><th /></tr></thead>
          <tbody>{lijst.map((d) => (
            <tr key={d.id} className={`border-t border-slate-700/40 align-top ${d.actief ? '' : 'opacity-60'}`} data-kb-voorwaarde={d.id}>
              <td className="py-1.5 pr-2 whitespace-nowrap text-slate-200">{d.energy_label || <span className="hd-pill hd-pill-emerald">alle labels</span>}</td>
              <td className="py-1.5 px-2 whitespace-nowrap text-slate-300">{(AV_SEGMENTEN.find((s) => s.id === d.segment) || {}).label || d.segment}</td>
              <td className="py-1.5 px-2 min-w-0">
                <div className="text-slate-200 truncate max-w-[230px]" title={d.bestandsnaam || ''}>{d.bestandsnaam || <span className="text-slate-500">nog niet ingelezen</span>}</div>
                {d.url
                  ? <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-lime-400 font-mono truncate max-w-[230px] block" title={d.url}>{d.url.replace(/^https?:\/\//, '')}</a>
                  : d.heeft_bestand && <button type="button" onClick={() => download(d)} className="text-slate-500 hover:text-lime-400">opgeladen bestand · downloaden</button>}
              </td>
              <td className="py-1.5 px-2 whitespace-nowrap text-slate-300 tabular-nums">{d.versie || '—'}</td>
              <td className="py-1.5 px-2 text-right tabular-nums text-slate-300 whitespace-nowrap" title={(d.doelgroepen || []).map((g) => `${g.aantal} ${AV_DOELGROEP[g.doelgroep] || 'algemeen'}`).join(' · ')}>{d.aantal_artikels == null ? '—' : d.aantal_artikels}{(d.doelgroepen || []).filter((g) => g.doelgroep).length > 1 && <div className="text-[10px] text-slate-500">{d.doelgroepen.filter((g) => g.doelgroep).map((g) => `${g.aantal} ${AV_DOELGROEP[g.doelgroep] || g.doelgroep}`).join(' · ')}</div>}</td>
              <td className="py-1.5 px-2 whitespace-nowrap text-slate-400 tabular-nums">{d.last_fetched_at ? aiDatumTijd(d.last_fetched_at) : '—'}</td>
              <td className="py-1.5 px-2">{!d.actief ? <span className="hd-pill hd-pill-slate">uit</span> : d.last_status === 'ok' ? <span className="hd-pill hd-pill-emerald">ok</span> : d.last_status === 'fout' ? <span className="hd-pill hd-pill-rose">fout</span> : <span className="text-slate-500">—</span>}{d.last_error && <div className="text-[10px] ai-slecht max-w-[220px] mt-1" title={d.last_error}>{d.last_error}</div>}</td>
              <td className="py-1.5 pl-1 whitespace-nowrap text-right w-[84px]">
                <button onClick={() => haalOp(d)} disabled={bezig === d.id} title={d.url ? 'Nu opnieuw ophalen en inlezen' : 'Het opgeladen bestand opnieuw inlezen'} className="p-1 text-slate-400 hover:text-lime-300 disabled:opacity-50">{bezig === d.id ? <Spinner size={12} /> : <RefreshCw size={13} />}</button>
                <button onClick={() => zetActief(d, !d.actief)} disabled={bezig === d.id} title={d.actief ? 'Niet meer gebruiken voor het AI-voorstel (blijft bewaard)' : 'Weer gebruiken voor het AI-voorstel'} className="p-1 text-slate-400 hover:text-lime-300 disabled:opacity-50">{d.actief ? <Eye size={13} /> : <Check size={13} />}</button>
                <button onClick={() => verwijder(d)} disabled={bezig === d.id} title="Verwijderen" className="p-1 text-slate-400 hover:text-red-300 disabled:opacity-50"><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {lijst && lijst.length > 0 && (
        <div className="text-[11px] text-slate-500 space-y-0.5" data-av-dekking>
          {dekking.map((d) => (
            <div key={d.segment.id}><span className="text-slate-400 font-medium">{d.segment.label}:</span>{' '}
              {d.gedeeld
                ? (d.eigen.length ? `het gedeelde document geldt voor alle labels, behalve ${d.eigen.join(', ')} (eigen document).` : 'het gedeelde document geldt voor alle labels.')
                : d.eigen.length ? `eigen document voor ${d.eigen.join(', ')}. Nog geen document voor ${d.zonder.join(', ') || 'geen enkel ander label'}.` : 'nog geen document.'}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Beheerblok onder de kennisbank (admin/ET): de websitebronnen (FAQ-pagina's die
// de job helpdesk.kennisbronnen ververst). Alles over de AI-voorstellen zelf —
// leren uit gebruik, ticketkennis, standaardteksten afleiden — staat sinds
// 27-09-2026 op de subpagina AI-voorstellen (HelpdeskAiPagina).
const KennisbankBeheer = ({ token, labels, onArtikelsGewijzigd }) => {
  const [bronnen, setBronnen] = useState(null);
  const [nieuw, setNieuw] = useState({ energy_label: '', url: '', titel: '' });
  const [busyId, setBusyId] = useState(null);
  const kb = useCallback(async (p, method = 'GET', body) => {
    const r = await fetch(`/api/kennisbank${p}`, { method, headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
    const b = await r.json().catch(() => null);
    if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
    return b;
  }, [token]);
  const laadBronnen = useCallback(() => kb('/bronnen').then((d) => setBronnen(d.bronnen || [])).catch((e) => toast.error(e.message)), [kb]);
  useEffect(() => { laadBronnen(); }, [laadBronnen]);
  const voegToe = async () => {
    if (!/^https?:\/\//i.test(nieuw.url.trim())) { toast.error('Geef een volledige URL (https://…)'); return; }
    setBusyId('nieuw');
    try {
      const d = await kb('/bronnen', 'POST', nieuw);
      if (d.resultaat && d.resultaat.ok) toast.success(`Bron toegevoegd: ${d.resultaat.artikels} artikel(s) uit ${d.resultaat.paginas} pagina(s)`);
      else toast.error(`Bron toegevoegd, maar ophalen mislukte: ${(d.resultaat && d.resultaat.error) || 'onbekend'}`);
      setNieuw({ energy_label: '', url: '', titel: '' }); await laadBronnen(); onArtikelsGewijzigd && onArtikelsGewijzigd();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };
  const haalOp = async (b) => {
    setBusyId(b.id);
    try {
      const d = await kb(`/bronnen/${b.id}/ophalen`, 'POST');
      if (d.ok) toast.success(`${d.resultaat.artikels} artikel(s) bijgewerkt uit ${d.resultaat.paginas} pagina(s)`); else toast.error(d.resultaat.error || 'Ophalen mislukt');
      await laadBronnen(); onArtikelsGewijzigd && onArtikelsGewijzigd();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };
  // Naam en URL van een bron aanpassen (inline in de rij). Een gewijzigde URL
  // wordt door de server meteen opnieuw opgehaald.
  const [bewerk, setBewerk] = useState(null);   // { id, titel, url }
  const bewaarBewerk = async () => {
    if (!/^https?:\/\//i.test(bewerk.url.trim())) { toast.error('Geef een volledige URL (https://…)'); return; }
    setBusyId(bewerk.id);
    try {
      const d = await kb(`/bronnen/${bewerk.id}`, 'PUT', { titel: bewerk.titel, url: bewerk.url.trim(), energy_label: bewerk.energy_label || '' });
      if (d.urlGewijzigd) {
        if (d.resultaat && d.resultaat.ok) toast.success(`Bron bijgewerkt: ${d.resultaat.artikels} artikel(s) uit ${d.resultaat.paginas} pagina(s)`);
        else toast.error(`Bron bijgewerkt, maar ophalen mislukte: ${(d.resultaat && d.resultaat.error) || 'onbekend'}`);
      } else toast.success('Bron bijgewerkt');
      setBewerk(null); await laadBronnen(); onArtikelsGewijzigd && onArtikelsGewijzigd();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };
  const verwijder = async (b) => {
    if (!window.confirm(`Bron "${b.titel || b.url}" en de automatisch aangemaakte artikels verwijderen?`)) return;
    setBusyId(b.id);
    try { await kb(`/bronnen/${b.id}`, 'DELETE'); toast.success('Bron verwijderd'); await laadBronnen(); onArtikelsGewijzigd && onArtikelsGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };
  const veld = 'px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100';
  return (
    <div className="mt-4 space-y-4">
      <KennisbankVoorwaarden token={token} labels={labels} onArtikelsGewijzigd={onArtikelsGewijzigd} />
      <Card className="space-y-3 min-w-0">
        <div>
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2"><ExternalLink size={15} className="text-lime-400" />Websitebronnen (FAQ per energielabel)</h3>
          <p className="text-xs text-slate-400 mt-1">Geef per label de startpagina van de FAQ. De job "Helpdesk — kennisbank: FAQ-pagina's verversen" volgt wekelijks de FAQ-links twee niveaus diep en maakt per vraag een artikel (categorie "FAQ website"). Overzichtspagina's worden overgeslagen; verdwenen vragen ook. Het AI-voorstel gebruikt die artikels alleen voor het label van de bron. Een bron voor "Alle labels" geldt voor elk label: de naam, het e-mailadres en de website van de bron worden in elk voorstel vervangen door die van het label van het ticket.</p>
        </div>
        <div className="grid sm:grid-cols-[minmax(0,160px)_minmax(0,1fr)_minmax(0,160px)_auto] gap-2 items-end">
          <div><label className="block text-[11px] text-slate-400 mb-1">Energielabel</label>
            <select value={nieuw.energy_label} onChange={(e) => setNieuw((n) => ({ ...n, energy_label: e.target.value }))} className={`${veld} w-full`}><option value="">Alle labels</option>{labels.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
          <div><label className="block text-[11px] text-slate-400 mb-1">Startpagina (bv. https://www.power2you.be/hulp-contact)</label><input value={nieuw.url} onChange={(e) => setNieuw((n) => ({ ...n, url: e.target.value }))} className={`${veld} w-full font-mono text-xs`} /></div>
          <div><label className="block text-[11px] text-slate-400 mb-1">Naam (optioneel)</label><input value={nieuw.titel} onChange={(e) => setNieuw((n) => ({ ...n, titel: e.target.value }))} className={`${veld} w-full`} /></div>
          <button onClick={voegToe} disabled={busyId === 'nieuw'} className="inline-flex items-center gap-1 h-9 px-3 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-lg text-xs font-semibold disabled:opacity-50">{busyId === 'nieuw' ? <Spinner size={12} /> : <Plus size={14} />}Toevoegen en ophalen</button>
        </div>
        {bronnen === null ? <div className="flex justify-center py-4"><Spinner size={18} /></div> : bronnen.length === 0 ? <div className="text-xs text-slate-500">Nog geen websitebronnen.</div> : (
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="text-slate-400 text-left"><th className="py-1.5 pr-2">Label</th><th className="py-1.5 pr-2">Bron</th><th className="py-1.5 pr-2 text-right">Artikels</th><th className="py-1.5 pr-2">Laatst</th><th className="py-1.5 pr-2">Status</th><th /></tr></thead>
            <tbody>{bronnen.map((b) => (
              <tr key={b.id} className="border-t border-slate-700/40 align-top">
                <td className="py-1.5 pr-2 whitespace-nowrap text-slate-300">{bewerk && bewerk.id === b.id
                  ? <select value={bewerk.energy_label} onChange={(e) => setBewerk((x) => ({ ...x, energy_label: e.target.value }))} className={`${veld} !py-1 text-xs`} aria-label="Energielabel van de bron"><option value="">Alle labels</option>{labels.map((l) => <option key={l} value={l}>{l}</option>)}</select>
                  : (b.energy_label || <span className="hd-pill hd-pill-emerald">alle labels</span>)}</td>
                <td className="py-1.5 pr-2 min-w-0">{bewerk && bewerk.id === b.id ? (
                  <div className="space-y-1 min-w-[260px]">
                    <input value={bewerk.titel} onChange={(e) => setBewerk((x) => ({ ...x, titel: e.target.value }))} placeholder="Naam (optioneel)" className={`${veld} w-full !py-1 text-xs`} autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') bewaarBewerk(); if (e.key === 'Escape') setBewerk(null); }} />
                    <input value={bewerk.url} onChange={(e) => setBewerk((x) => ({ ...x, url: e.target.value }))} placeholder="https://…" className={`${veld} w-full !py-1 font-mono text-xs`}
                      onKeyDown={(e) => { if (e.key === 'Enter') bewaarBewerk(); if (e.key === 'Escape') setBewerk(null); }} />
                  </div>
                ) : (<><div className="text-slate-200 truncate max-w-[260px]" title={b.url}>{b.titel || b.url}</div>{b.titel && <div className="text-slate-500 font-mono truncate max-w-[260px]" title={b.url}>{b.url}</div>}</>)}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums text-slate-300">{b.aantal_artikels ?? '—'}</td>
                <td className="py-1.5 pr-2 whitespace-nowrap text-slate-400">{b.last_fetched_at ? new Date(b.last_fetched_at).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                <td className="py-1.5 pr-2">{b.last_status === 'ok' ? <span className="text-lime-300">ok</span> : b.last_status === 'fout' ? <span className="text-red-300" title={b.last_error || ''}>fout</span> : <span className="text-slate-500">—</span>}{b.last_error && <div className="text-[10px] text-red-300/80 max-w-[220px] truncate" title={b.last_error}>{b.last_error}</div>}</td>
                <td className="py-1.5 whitespace-nowrap text-right">{bewerk && bewerk.id === b.id ? (<>
                  <button onClick={bewaarBewerk} disabled={busyId === b.id} title="Bewaren" className="p-1 text-lime-300 hover:text-lime-200 disabled:opacity-50">{busyId === b.id ? <Spinner size={12} /> : <Check size={14} />}</button>
                  <button onClick={() => setBewerk(null)} disabled={busyId === b.id} title="Annuleren" className="p-1 text-slate-400 hover:text-slate-200 disabled:opacity-50"><X size={14} /></button>
                </>) : (<>
                  <button onClick={() => setBewerk({ id: b.id, titel: b.titel || '', url: b.url || '', energy_label: b.energy_label || '' })} disabled={busyId === b.id} title="Naam, URL of energielabel aanpassen" className="p-1 text-slate-400 hover:text-lime-300 disabled:opacity-50"><Pencil size={13} /></button>
                  <button onClick={() => haalOp(b)} disabled={busyId === b.id} title="Nu ophalen" className="p-1 text-slate-400 hover:text-lime-300 disabled:opacity-50">{busyId === b.id ? <Spinner size={12} /> : <RefreshCw size={13} />}</button>
                  <button onClick={() => verwijder(b)} disabled={busyId === b.id} title="Verwijderen" className="p-1 text-slate-400 hover:text-red-300 disabled:opacity-50"><Trash2 size={13} /></button>
                </>)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
};
// ─── AI-voorstellen (Helpdesk → AI-voorstellen) ──────────────────────────────
// Wat de AI-voorstellen opleveren en de hefbomen om ze bij te sturen. Alleen voor
// admin en ET-dossierbeheerder (subpagina met alleenRollen; de routes onder
// /api/helpdesk-ai eisen die rol zelf ook). Vier delen:
//   Inzicht       kengetallen, verloop, uitsplitsingen, kennishiaten
//   Voorstellen   elk voorstel, met wat er uiteindelijk verstuurd werd
//   Bijsturen     vaste instructies en wat het model nu per label meekrijgt
//   Kennisbronnen ticketkennis en standaardteksten afleiden (stond onder Kennisbank)
// De rekenregels staan op de server (util/aiInzicht.js), de weergave in
// src/helpdeskAi.js.
// Reekskleuren van de uitkomst. Zelfde drie tinten en dezelfde buren als de
// bewegingsstapel (COLORS_BEWEGING): groen naast magenta en magenta naast geel
// zijn gevalideerd, groen naast geel niet — vandaar aangepast in het midden.
const HD_AI_KLEUR = { ongewijzigd: CHART_HUES.groen, aangepast: CHART_HUES.magenta, niet_gebruikt: CHART_HUES.geel };
const HD_AI_STAPEL = [
  { key: 'ongewijzigd', naam: AI_UITKOMST.ongewijzigd.label, kleur: HD_AI_KLEUR.ongewijzigd },
  { key: 'aangepast', naam: AI_UITKOMST.aangepast.label, kleur: HD_AI_KLEUR.aangepast },
  { key: 'niet_gebruikt', naam: AI_UITKOMST.niet_gebruikt.label, kleur: HD_AI_KLEUR.niet_gebruikt },
];
const hdAiApi = (token) => async (p, method = 'GET', body) => {
  const r = await fetch(`/api/helpdesk-ai${p}`, { method, headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
  const b = await r.json().catch(() => null);
  if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
  return b;
};
const HD_AI_VELD = 'px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100';
const HdAiBeoordeling = ({ waarde, size = 13 }) => (Number(waarde) === 1
  ? <ThumbsUp size={size} className="ai-goed shrink-0" aria-label="Goed" />
  : Number(waarde) === -1 ? <ThumbsDown size={size} className="ai-slecht shrink-0" aria-label="Niet goed" /> : null);

// Tabel met dezelfde kolommen voor elke uitsplitsing (label, variant, medewerker…).
const HdAiGroepTabel = ({ titel, kolom, rijen, bestand, uitleg }) => (
  <Card className="space-y-3 min-w-0">
    <div className="flex items-center gap-2 min-w-0">
      <h3 className="text-sm font-semibold text-slate-100 truncate">{titel}</h3>
      {uitleg && <InfoTip title={titel} text={uitleg} />}
      <ExportKnop onExport={() => exportRowsToCsv(bestand, aiGroepKolommen(kolom), rijen)} label={`Exporteer "${titel}" naar Excel`} count={rijen.length} disabled={!rijen.length} />
    </div>
    {rijen.length === 0 ? <div className="text-xs text-slate-500">Geen voorstellen in deze selectie.</div> : (
      <div className="overflow-x-auto"><table className="w-full text-xs tabular-nums">
        <thead><tr className="text-slate-400 text-left whitespace-nowrap">
          <th className="py-1.5 pr-2 font-medium">{kolom}</th><th className="py-1.5 px-1.5 text-right font-medium">Aantal</th><th className="py-1.5 px-1.5 text-right font-medium">Verstuurd</th>
          <th className="py-1.5 px-1.5 text-right font-medium">Ongewijzigd</th><th className="py-1.5 px-1.5 text-right font-medium">Goed</th><th className="py-1.5 px-1.5 text-right font-medium">Niet goed</th><th className="py-1.5 pl-1.5 text-right font-medium">Duur</th>
        </tr></thead>
        <tbody>{rijen.map((r) => (
          <tr key={r.sleutel} className="border-t border-slate-700/40">
            <td className="py-1.5 pr-2 text-slate-200 whitespace-nowrap">{r.naam}</td>
            <td className="py-1.5 px-1.5 text-right text-slate-100 font-semibold">{aiGetal(r.voorstellen)}</td>
            <td className="py-1.5 px-1.5 text-right text-slate-300 whitespace-nowrap" title={`${aiGetal(r.verstuurd)} van ${aiGetal(r.voorstellen)} voorstellen`}>{aiPct(r.pct_verstuurd)}</td>
            <td className="py-1.5 px-1.5 text-right text-slate-300 whitespace-nowrap" title={`${aiGetal(r.ongewijzigd)} van ${aiGetal(r.verstuurd)} verstuurde voorstellen`}>{aiPct(r.pct_ongewijzigd)}</td>
            <td className="py-1.5 px-1.5 text-right ai-goed">{aiGetal(r.goed)}</td>
            <td className="py-1.5 px-1.5 text-right ai-slecht">{aiGetal(r.slecht)}</td>
            <td className="py-1.5 pl-1.5 text-right text-slate-400 whitespace-nowrap">{aiDuur(r.gem_duur_ms)}</td>
          </tr>
        ))}</tbody>
      </table></div>
    )}
  </Card>
);

// Balk met een aandeel: naam links, aantal rechts, streep eronder.
const HdAiAandeel = ({ naam, aantal, pct, kleur, sub }) => (
  <div className="text-xs">
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-slate-200 truncate" title={naam}>{naam}{sub && <span className="text-slate-500"> · {sub}</span>}</span>
      <span className="tabular-nums text-slate-300 shrink-0">{aiGetal(aantal)}{pct != null && <span className="text-slate-500"> · {aiPct(pct)}</span>}</span>
    </div>
    <div className="mt-1 h-1.5 rounded-full bg-slate-700/60 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(2, Math.min(100, pct == null ? 0 : pct))}%`, backgroundColor: kleur }} /></div>
  </div>
);

const HdAiInzicht = ({ data, filters }) => {
  const t = data.totaal;
  const v = data.vorige;
  const kleurVan = (verschil, goedIsOp = true) => (!verschil || verschil.richting === 'gelijk' ? 'text-slate-500' : (verschil.richting === 'op') === goedIsOp ? 'ai-goed' : 'ai-let-op');
  const onder = (verschil, goedIsOp) => (verschil ? <div className={kleurVan(verschil, goedIsOp)}>{verschil.tekst}</div> : null);
  const punten = data.reeks.punten.map((p) => ({ ...p, label: aiPeriodeLabel(p.periode, data.reeks.eenheid) }));
  const stapel = HD_AI_STAPEL.map((s) => s.key);
  const maxBron = Math.max(1, ...data.bronnen.map((b) => b.aantal));
  const exportReeks = () => exportRowsToCsv('ai-voorstellen-verloop.csv', [
    { header: data.reeks.eenheid === 'maand' ? 'Maand' : 'Week van', get: (r) => (data.reeks.eenheid === 'maand' ? aiPeriodeTitel(r.periode, 'maand') : aiDatum(`${r.periode}T12:00:00Z`)) },
    { header: 'Voorstellen', get: (r) => r.voorstellen }, { header: 'Ongewijzigd verstuurd', get: (r) => r.ongewijzigd }, { header: 'Aangepast verstuurd', get: (r) => r.aangepast },
    { header: 'Niet gebruikt', get: (r) => r.niet_gebruikt }, { header: 'Goed beoordeeld', get: (r) => r.goed }, { header: 'Niet goed beoordeeld', get: (r) => r.slecht },
  ], data.reeks.punten);
  if (t.voorstellen === 0) {
    return <Card><TableEmpty icon={Wand2} className="py-12" message={data.bewaard.totaal ? 'Geen AI-voorstellen in deze selectie. Kies een langere periode of een ander filter.' : 'Er is nog geen enkel AI-voorstel gemaakt. Zodra een medewerker in een ticket op "AI voorstel" of "AI 360°" klikt, verschijnen hier de cijfers.'} /></Card>;
  }
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4">
        <StatTile label="Voorstellen" value={aiGetal(t.voorstellen)} sub={<><div>{aiGetal(t.gebruikers)} {t.gebruikers === 1 ? 'medewerker' : 'medewerkers'}</div>{onder(v && aiVerschil(t.voorstellen, v.voorstellen), true)}</>} />
        <StatTile label="Verstuurd" value={aiPct(t.pct_verstuurd)} sub={<><div>{aiGetal(t.verstuurd)} van {aiGetal(t.voorstellen)} voorstellen</div>{onder(v && aiVerschilPunten(t.pct_verstuurd, v.pct_verstuurd), true)}</>} />
        <StatTile label="Ongewijzigd" value={aiPct(t.pct_ongewijzigd)} sub={<><div>{aiGetal(t.ongewijzigd)} van {aiGetal(t.verstuurd)} verstuurd zonder wijziging</div>{onder(v && aiVerschilPunten(t.pct_ongewijzigd, v.pct_ongewijzigd), true)}</>} />
        <StatTile label="Goed beoordeeld" value={aiPct(t.pct_goed)} sub={<><div>{aiGetal(t.goed)} goed · {aiGetal(t.slecht)} niet goed</div><div>{aiGetal(t.voorstellen - t.beoordeeld)} niet beoordeeld</div>{onder(v && aiVerschilPunten(t.pct_goed, v.pct_goed), true)}</>} />
        <StatTile label="Gemiddelde duur" minW={140} value={aiDuur(t.gem_duur_ms)} sub={<><div>9 op 10 binnen {aiDuur(t.p90_duur_ms)}</div>{onder(v && aiVerschil(t.gem_duur_ms, v.gem_duur_ms), false)}</>} />
        <StatTile label="Tokens per voorstel" minW={140} value={aiTokens(t.gem_tokens)} sub={<div>totaal {aiTokens(t.tokens_in)} in · {aiTokens(t.tokens_uit)} uit</div>} />
      </div>

      <Card className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-lg font-semibold text-slate-100">Gebruik per {data.reeks.eenheid}</h3>
          <InfoTip title="Gebruik" text={'Elke staaf is het aantal voorstellen dat in die periode gemaakt werd, verdeeld naar wat ermee gebeurde.\n\nOngewijzigd verstuurd: de medewerker verstuurde het voorstel zonder één woord te veranderen.\nAangepast verstuurd: er werd verstuurd, maar de tekst week af.\nNiet gebruikt: er is niets verstuurd met dit voorstel.'} />
          <ExportKnop onExport={exportReeks} label="Exporteer het verloop naar Excel" count={data.reeks.punten.length} />
        </div>
        <p className="text-xs text-slate-400 mt-0.5 mb-3">Hoeveel voorstellen er gemaakt werden en wat de medewerker ermee deed.</p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={punten} barCategoryGap="20%">
            <CartesianGrid vertical={false} stroke="#334155" />
            <XAxis dataKey="label" tick={{ fill: '#94A3B8', fontSize: 11 }} {...CHART_AS_X} minTickGap={8} />
            <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} allowDecimals={false} {...CHART_AS_Y} />
            <Tooltip contentStyle={CHART_TOOLTIP} labelFormatter={(l, p) => (p && p[0] ? aiPeriodeTitel(p[0].payload.periode, data.reeks.eenheid) : l)} />
            <Legend {...CHART_LEGENDA} />
            {HD_AI_STAPEL.map((s, i) => <Bar key={s.key} dataKey={s.key} name={s.naam} stackId="a" fill={s.kleur} maxBarSize={34} shape={stapelVorm(stapel, i)} />)}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <HdAiGroepTabel titel="Per energielabel" kolom="Energielabel" rijen={data.perLabel} bestand="ai-voorstellen-per-energielabel.csv" />
        <HdAiGroepTabel titel="Per medewerker" kolom="Medewerker" rijen={data.perGebruiker} bestand="ai-voorstellen-per-medewerker.csv" />
        <HdAiGroepTabel titel="AI voorstel tegenover AI 360°" kolom="Variant" rijen={data.perVariant} bestand="ai-voorstellen-per-variant.csv" uitleg={'AI voorstel raadpleegt geen klantgegevens. AI 360° vraagt op wat het voor de vraag nodig heeft: contract, facturen, voorschot, meetgegevens of eerdere tickets.'} />
        <HdAiGroepTabel titel="Per kanaal en model" kolom="Kanaal of model" rijen={[...data.perKanaal, ...data.perModel.map((m) => ({ ...m, sleutel: `model:${m.sleutel}`, naam: `Model ${m.naam}` }))]} bestand="ai-voorstellen-per-kanaal-en-model.csv" />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="space-y-3 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-slate-100">Klantgegevens die AI 360° raadpleegde</h3>
            <InfoTip title="Geraadpleegde klantgegevens" text={'Per soort: in hoeveel AI 360°-voorstellen het model die gegevens opvroeg. Het model vraagt alleen op wat het voor de vraag nodig heeft; een soort die zelden opgevraagd wordt, is dus niet fout.\n\nHier staan alleen de soorten, nooit de gegevens zelf.'} />
          </div>
          {data.geraadpleegd.voorstellen === 0 ? <div className="text-xs text-slate-500">Geen AI 360°-voorstellen in deze selectie.</div> : (
            <div className="space-y-2.5">
              {data.geraadpleegd.bronnen.map((b) => <HdAiAandeel key={b.naam} naam={aiHoofdletter(b.label)} aantal={b.aantal} pct={b.pct} kleur={CHART_HUES.violet} />)}
              <div className="text-xs text-slate-400 pt-1">{aiGetal(data.geraadpleegd.zonder)} van de {aiGetal(data.geraadpleegd.voorstellen)} AI 360°-voorstellen kwamen tot stand zonder klantgegevens op te vragen.</div>
            </div>
          )}
        </Card>
        <Card className="space-y-3 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-slate-100">Waar kennis ontbreekt</h3>
            <InfoTip title="Waar kennis ontbreekt" text={'Voorstellen waarbij het model geen enkele werkinstructie uit de kennisbank als bron gebruikte: er was er geen die over de vraag ging. Een nieuw artikel in de kennisbank voor het label met de meeste hiaten levert het meeste op.\n\nDaaronder: hoe vaak het model meldde dat het een gegeven nodig had en niet vond, en hoe vaak de bewaking een gegeven van een andere klant uit het voorstel haalde.'} />
          </div>
          <div className="text-xs text-slate-300"><span className="text-lg font-bold text-slate-100 tabular-nums mr-1.5">{aiPct(data.kennishiaten.pct)}</span>van de voorstellen gebruikte geen werkinstructie ({aiGetal(data.kennishiaten.aantal)} van {aiGetal(t.voorstellen)}).</div>
          {data.kennishiaten.perLabel.length > 0 && <div className="space-y-2.5">{data.kennishiaten.perLabel.map((l) => <HdAiAandeel key={l.label} naam={l.label} aantal={l.aantal} pct={pctVan(l.aantal, data.kennishiaten.aantal)} kleur={CHART_HUES.oranje} />)}</div>}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="rounded-md border border-slate-700/50 bg-slate-800/40 px-3 py-2"><div className="text-[11px] text-slate-400">Model miste een gegeven</div><div className="text-base font-semibold text-slate-100 tabular-nums">{aiGetal(t.met_ontbrekend)}</div></div>
            <div className="rounded-md border border-slate-700/50 bg-slate-800/40 px-3 py-2"><div className="text-[11px] text-slate-400">Bewaking greep in</div><div className="text-base font-semibold text-slate-100 tabular-nums">{aiGetal(t.met_waarschuwing)}</div></div>
          </div>
          {data.waarschuwingen.length > 0 && <div className="flex flex-wrap gap-1.5">{data.waarschuwingen.map((w) => <span key={w.soort} className="hd-pill hd-pill-amber">{w.soort}: {aiGetal(w.aantal)}</span>)}</div>}
        </Card>
      </div>

      <Card className="space-y-3 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-slate-100">Meest gebruikte bronnen</h3>
          <InfoTip title="Meest gebruikte bronnen" text={'Werkinstructies, standaardteksten en artikels uit de algemene voorwaarden die het model zelf als bron opgaf, met het aantal voorstellen waarin ze dienden en hoe die voorstellen beoordeeld werden. Een bron met veel duimen omlaag is het nalezen waard.'} />
          <ExportKnop onExport={() => exportRowsToCsv('ai-voorstellen-bronnen.csv', [{ header: 'Soort', get: (r) => aiHoofdletter(aiBronSoort(r.type)) }, { header: 'Titel', get: (r) => r.titel }, { header: 'Voorstellen', get: (r) => r.aantal }, { header: 'Goed beoordeeld', get: (r) => r.goed }, { header: 'Niet goed beoordeeld', get: (r) => r.slecht }], data.bronnen)} label="Exporteer de bronnen naar Excel" count={data.bronnen.length} disabled={!data.bronnen.length} />
        </div>
        {data.bronnen.length === 0 ? <div className="text-xs text-slate-500">Het model gaf in deze selectie geen werkinstructie, standaardtekst of artikel uit de algemene voorwaarden als bron op.</div> : (
          <div className="grid gap-x-8 gap-y-2.5 md:grid-cols-2">
            {data.bronnen.map((b) => <HdAiAandeel key={`${b.type}:${b.id}:${b.titel}`} naam={b.titel || `${b.type} ${b.id ?? ''}`} sub={aiBronSoort(b.type)} aantal={b.aantal} pct={pctVan(b.aantal, maxBron)} kleur={b.type === 'kennisbank' ? CHART_HUES.blauw : b.type === 'voorwaarden' ? CHART_HUES.violet : CHART_HUES.aqua} />)}
          </div>
        )}
      </Card>
      <p className="text-[11px] text-slate-500">Bewaard: {aiGetal(data.bewaard.totaal)} {data.bewaard.totaal === 1 ? 'voorstel' : 'voorstellen'}{data.bewaard.eerste ? ` sinds ${aiDatum(data.bewaard.eerste)}` : ''}. De historiek wordt niet opgeruimd; met de periode "Alles" zie je ze volledig.{filters}</p>
    </div>
  );
};

// Eén voorstel: wat het model schreef, wat er verstuurd werd, en de knoppen om
// eruit te leren.
const HdAiVoorstelModal = ({ id, api, onSluit, onGewijzigd, onInstructie }) => {
  const [v, setV] = useState(null);
  const [fout, setFout] = useState('');
  const [bezig, setBezig] = useState(false);
  useEffect(() => { let leeft = true; setV(null); setFout(''); api(`/voorstellen/${id}`).then((d) => leeft && setV(d)).catch((e) => leeft && setFout(e.message)); return () => { leeft = false; }; }, [api, id]);
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onSluit(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onSluit]);
  const zetLeren = async (uit) => {
    setBezig(true);
    try { const d = await api(`/voorstellen/${id}`, 'PATCH', { leren_uit: uit }); setV((x) => ({ ...x, leren_uit: d.leren_uit })); toast.success(uit ? 'Dit voorstel telt niet meer mee voor het leren' : 'Dit voorstel telt weer mee voor het leren'); onGewijzigd && onGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };
  const uitkomst = v && AI_UITKOMST[v.uitkomst];
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" onClick={onSluit}>
      <div className="w-full max-w-5xl bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl my-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="AI-voorstel" data-ai-voorstel-modal>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-700">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-100 truncate">{v ? (v.onderwerp || 'Voorstel zonder ticket') : 'AI-voorstel'}</h3>
            {v && <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
              <span>{aiDatumTijd(v.created_at)}</span><span>·</span><span>{v.user_name || 'Onbekend'}</span><span>·</span><span>{v.energy_label || '—'}</span>
              <span className="hd-pill hd-pill-violet">{aiVariantNaam(v.variant)}</span>
              <span className="hd-pill hd-pill-slate">{aiKanaalNaam(v.kanaal)}</span>
              {uitkomst && <span className={`hd-pill ${uitkomst.pill}`}>{uitkomst.label}</span>}
              {v.leren_uit && <span className="hd-pill hd-pill-rose">telt niet mee voor het leren</span>}
            </div>}
          </div>
          <button onClick={onSluit} className="p-1.5 text-slate-400 hover:text-slate-200 shrink-0" aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {fout && <div className="text-sm text-red-300">{fout}</div>}
          {!v && !fout && <div className="flex justify-center py-10"><Spinner size={22} /></div>}
          {v && <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><div className="text-slate-500">Ticket</div><div className="text-slate-200">{v.ticket_id ? <button type="button" onClick={() => openTicketInNewTab(v.ticket_id)} className="text-lime-400 hover:underline">#{v.ticket_ref || v.ticket_id}</button> : '—'}</div></div>
              <div><div className="text-slate-500">Duur</div><div className="text-slate-200 tabular-nums">{aiDuur(v.duur_ms)}</div></div>
              <div><div className="text-slate-500">Tokens</div><div className="text-slate-200 tabular-nums">{aiTokens(v.input_tokens)} in · {aiTokens(v.output_tokens)} uit</div></div>
              <div><div className="text-slate-500">Model</div><div className="text-slate-200 truncate" title={v.model || ''}>{v.model || '—'}</div></div>
            </div>
            {(v.beoordeling === 1 || v.beoordeling === -1 || v.opmerking) && (
              <div className="rounded-md border border-slate-700/50 bg-slate-900/40 px-3 py-2 text-xs flex items-start gap-2">
                <HdAiBeoordeling waarde={v.beoordeling} size={14} />
                <div className="min-w-0"><span className="text-slate-200">{v.opmerking || (v.beoordeling === 1 ? 'Goed bevonden, zonder opmerking.' : 'Niet goed bevonden, zonder opmerking.')}</span>
                  {v.opmerking && <button type="button" onClick={() => onInstructie({ tekst: v.opmerking, energy_label: v.energy_label || '', bron_voorstel_id: v.id })} className="ml-2 text-violet-300 hover:underline whitespace-nowrap">Maak er een vaste instructie van</button>}</div>
              </div>
            )}
            <div className={`grid gap-4 ${v.correctie_html != null ? 'lg:grid-cols-2' : ''}`}>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Voorstel van het model</div>
                <div className="hd-html text-sm text-slate-200 leading-relaxed rounded-md border border-slate-700/50 bg-slate-900/40 px-3 py-2.5 max-h-[46vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: v.antwoord_html || '<p>(de tekst van dit voorstel is niet bewaard)</p>' }} />
              </div>
              {v.correctie_html != null && (
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Verstuurd door de medewerker{v.overeenkomst != null && <span className="normal-case tracking-normal text-slate-400"> · {v.overeenkomst} % van de woorden bleef</span>}</div>
                  <div className="hd-html text-sm text-slate-200 leading-relaxed rounded-md border border-amber-500/30 bg-amber-500/[.05] px-3 py-2.5 max-h-[46vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: v.correctie_html }} />
                </div>
              )}
            </div>
            {v.correctie_html == null && <div className="text-xs text-slate-400">{v.verzonden ? 'Dit voorstel is ongewijzigd verstuurd.' : 'Met dit voorstel is niets verstuurd.'}</div>}
            <div className="grid gap-3 sm:grid-cols-2 text-xs">
              <div><div className="text-slate-500 mb-1">Bronnen die het model opgaf</div>{v.bronnen.length === 0 ? <span className="text-slate-500">geen</span> : <div className="flex flex-wrap gap-1">{v.bronnen.map((b, i) => <span key={i} className="hd-pill hd-pill-slate" title={b.type}>{b.titel || `${b.type} ${b.id ?? ''}`}</span>)}</div>}</div>
              {v.variant === '360' && <div><div className="text-slate-500 mb-1">Geraadpleegde klantgegevens</div>{v.geraadpleegd.length === 0 ? <span className="text-slate-500">geen</span> : <div className="flex flex-wrap gap-1">{v.geraadpleegd.map((g) => <span key={g.naam} className="hd-pill hd-pill-violet">{aiHoofdletter(g.label)}</span>)}</div>}</div>}
              {v.ontbrekend.length > 0 && <div><div className="text-slate-500 mb-1">Wat het model miste</div><ul className="list-disc pl-4 ai-let-op space-y-0.5">{v.ontbrekend.map((o, i) => <li key={i}>{o}</li>)}</ul></div>}
              {v.waarschuwingen.length > 0 && <div><div className="text-slate-500 mb-1">De bewaking haalde weg</div><div className="flex flex-wrap gap-1">{v.waarschuwingen.map((w) => <span key={w} className="hd-pill hd-pill-amber">{w}</span>)}</div></div>}
            </div>
          </>}
        </div>
        {v && <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-slate-700">
          <div className="text-[11px] text-slate-500 max-w-xl">{v.leren_uit ? `Uitgesloten van het leren${v.leren_uit_naam ? ` door ${v.leren_uit_naam}` : ''}${v.leren_uit_at ? ` op ${aiDatum(v.leren_uit_at)}` : ''}: dient niet als voorbeeld en de opmerking gaat niet mee als aandachtspunt.` : 'Een goed bevonden of gecorrigeerd voorstel dient als voorbeeld voor latere voorstellen van dit label; een opmerking bij een duim omlaag gaat mee als aandachtspunt.'}</div>
          <button type="button" onClick={() => zetLeren(!v.leren_uit)} disabled={bezig} className="inline-flex items-center gap-1.5 h-8 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-xs disabled:opacity-50">{bezig ? <Spinner size={12} /> : v.leren_uit ? <Check size={13} /> : <X size={13} />}{v.leren_uit ? 'Weer laten meetellen' : 'Niet van dit voorstel leren'}</button>
        </div>}
      </div>
    </div>,
    document.body
  );
};

const HD_AI_BEOORDELING_KEUZES = [{ value: '', label: 'Elke beoordeling' }, { value: 'goed', label: 'Goed beoordeeld' }, { value: 'slecht', label: 'Niet goed beoordeeld' }, { value: 'geen', label: 'Niet beoordeeld' }];
const HD_AI_UITKOMST_KEUZES = [{ value: '', label: 'Elke uitkomst' }, ...Object.entries(AI_UITKOMST).map(([value, u]) => ({ value, label: u.label }))];
const HdAiVoorstellen = ({ token, api, dagen, label, variant, versie, onGewijzigd, onInstructie }) => {
  const [beoordeling, setBeoordeling] = useState('');
  const [uitkomst, setUitkomst] = useState('');
  const [opmerking, setOpmerking] = useState(false);
  const [leren, setLeren] = useState(false);
  const [rijen, setRijen] = useState(null);
  const [fout, setFout] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const ctrl = new AbortController();
    setRijen(null); setFout(''); setPage(1);
    fetchPaginated(`/api/helpdesk-ai/voorstellen?${aiQuery({ dagen, label, variant, beoordeling, uitkomst, opmerking, leren: leren ? 'uit' : '' })}`, token, { signal: ctrl.signal })
      .then(setRijen).catch((e) => { if (e?.name !== 'AbortError') { setFout(e.message); setRijen([]); } });
    return () => ctrl.abort();
  }, [token, dagen, label, variant, beoordeling, uitkomst, opmerking, leren, versie]);
  const kies = 'h-9 px-2 bg-slate-700/70 border border-slate-600/70 rounded-lg text-xs text-slate-200';
  return (
    <Card className="space-y-3 min-w-0">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <h3 className="text-lg font-semibold text-slate-100">Voorstellen</h3>
        <ExportKnop onExport={() => exportRowsToCsv('ai-voorstellen.csv', AI_VOORSTEL_KOLOMMEN, rijen || [])} label="Exporteer de voorstellen naar Excel" count={rijen ? rijen.length : null} disabled={!rijen || !rijen.length} />
        <span className="flex-1" />
        <select value={uitkomst} onChange={(e) => setUitkomst(e.target.value)} className={`${kies} ${FILTER_MD}`} aria-label="Uitkomst">{HD_AI_UITKOMST_KEUZES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
        <select value={beoordeling} onChange={(e) => setBeoordeling(e.target.value)} className={`${kies} ${FILTER_MD}`} aria-label="Beoordeling">{HD_AI_BEOORDELING_KEUZES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer select-none whitespace-nowrap"><input type="checkbox" checked={opmerking} onChange={(e) => setOpmerking(e.target.checked)} className="w-3.5 h-3.5 rounded accent-lime-500" />met opmerking</label>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer select-none whitespace-nowrap"><input type="checkbox" checked={leren} onChange={(e) => setLeren(e.target.checked)} className="w-3.5 h-3.5 rounded accent-lime-500" />uitgesloten van het leren</label>
      </div>
      <p className="text-xs text-slate-400">Klik op een voorstel om te zien wat het model schreef en wat de medewerker uiteindelijk verstuurde.</p>
      {fout && <div className="text-xs text-red-300">{fout}</div>}
      {rijen === null ? <div className="flex justify-center py-8"><Spinner size={20} /></div> : rijen.length === 0 ? <TableEmpty icon={Wand2} message="Geen voorstellen in deze selectie" /> : (
        <div className="overflow-x-auto"><table className="w-full text-xs resp-card">
          <thead><tr className="border-b border-slate-700 text-slate-400 text-left whitespace-nowrap">
            <th className="py-2 pr-2 font-medium">Datum</th><th className="py-2 px-2 font-medium">Ticket</th><th className="py-2 px-2 font-medium">Energielabel</th><th className="py-2 px-2 font-medium">Variant</th>
            <th className="py-2 px-2 font-medium">Medewerker</th><th className="py-2 px-2 font-medium">Uitkomst</th><th className="py-2 px-2 font-medium">Beoordeling</th><th className="py-2 pl-2 font-medium text-right">Duur</th>
          </tr></thead>
          <tbody>{renderPaged(page, setPage, rijen, (r) => (
            <tr key={r.id} onClick={() => setOpen(r.id)} className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer align-top" data-ai-voorstel={r.id}>
              <td data-label="Datum" className="py-2 pr-2 whitespace-nowrap text-slate-300 tabular-nums">{aiDatumTijd(r.created_at)}</td>
              <td data-label="Ticket" className="py-2 px-2 min-w-0"><div className="text-slate-200 truncate max-w-[280px]" title={r.onderwerp || ''}>{r.onderwerp || <span className="text-slate-500">zonder ticket</span>}</div>{r.ticket_id && <div className="text-slate-500 tabular-nums">#{r.ticket_ref || r.ticket_id}</div>}</td>
              <td data-label="Energielabel" className="py-2 px-2 whitespace-nowrap text-slate-300">{r.energy_label || '—'}</td>
              <td data-label="Variant" className="py-2 px-2 whitespace-nowrap"><span className={`hd-pill ${r.variant === '360' ? 'hd-pill-violet' : 'hd-pill-slate'}`}>{aiVariantNaam(r.variant)}</span></td>
              <td data-label="Medewerker" className="py-2 px-2 whitespace-nowrap text-slate-300">{r.user_name || 'Onbekend'}</td>
              <td data-label="Uitkomst" className="py-2 px-2 whitespace-nowrap"><span className={`hd-pill ${(AI_UITKOMST[r.uitkomst] || AI_UITKOMST.niet_gebruikt).pill}`}>{(AI_UITKOMST[r.uitkomst] || AI_UITKOMST.niet_gebruikt).kort}</span>{r.leren_uit && <span className="hd-pill hd-pill-rose ml-1" title="Telt niet mee voor het leren">uitgesloten</span>}</td>
              <td data-label="Beoordeling" className="py-2 px-2 min-w-0"><div className="flex items-start gap-1.5"><HdAiBeoordeling waarde={r.beoordeling} />{r.opmerking ? <span className="text-slate-300 line-clamp-2 max-w-[260px]" title={r.opmerking}>{r.opmerking}</span> : (!r.beoordeling && <span className="text-slate-500">—</span>)}</div></td>
              <td data-label="Duur" className="py-2 pl-2 text-right whitespace-nowrap text-slate-400 tabular-nums">{aiDuur(r.duur_ms)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {open != null && <HdAiVoorstelModal id={open} api={api} onSluit={() => setOpen(null)} onGewijzigd={onGewijzigd} onInstructie={(i) => { setOpen(null); onInstructie(i); }} />}
    </Card>
  );
};

// Vaste instructies en wat het model nu per label meekrijgt.
const HdAiBijsturen = ({ api, labels, concept, onConceptVerwerkt, versie, onGewijzigd }) => {
  const [lijst, setLijst] = useState(null);
  const [vorm, setVorm] = useState(null);   // { id|null, tekst, energy_label, actief, bron_voorstel_id }
  const [bezig, setBezig] = useState(null);
  const [leerLabel, setLeerLabel] = useState('');
  const [leerstof, setLeerstof] = useState(null);
  const vormRef = useRef(null);
  const laad = useCallback(() => api('/instructies').then((d) => setLijst(d.instructies || [])).catch((e) => { toast.error(e.message); setLijst([]); }), [api]);
  useEffect(() => { laad(); }, [laad]);
  // Een opmerking die elders "een vaste instructie" werd: het formulier staat klaar.
  useEffect(() => {
    if (!concept) return;
    setVorm({ id: null, tekst: String(concept.tekst || '').slice(0, AI_MAX_INSTRUCTIE), energy_label: concept.energy_label || '', actief: true, bron_voorstel_id: concept.bron_voorstel_id || null });
    onConceptVerwerkt();
    setTimeout(() => { try { vormRef.current && vormRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* geen scroll */ } }, 50);
  }, [concept, onConceptVerwerkt]);
  useEffect(() => { if (!leerLabel && labels.length) setLeerLabel(labels[0]); }, [labels, leerLabel]);
  const laadLeerstof = useCallback(() => {
    if (!leerLabel) { setLeerstof(null); return; }
    api(`/leerstof?label=${encodeURIComponent(leerLabel)}`).then(setLeerstof).catch((e) => { toast.error(e.message); setLeerstof(null); });
  }, [api, leerLabel]);
  useEffect(() => { laadLeerstof(); }, [laadLeerstof, versie]);
  const bewaar = async () => {
    const tekst = String(vorm.tekst || '').trim();
    if (!tekst) { toast.error('Geef de instructie een tekst.'); return; }
    setBezig('vorm');
    try {
      const body = { tekst, energy_label: vorm.energy_label || '', actief: !!vorm.actief, ...(vorm.id ? {} : { bron_voorstel_id: vorm.bron_voorstel_id || null }) };
      await (vorm.id ? api(`/instructies/${vorm.id}`, 'PUT', body) : api('/instructies', 'POST', body));
      toast.success(vorm.id ? 'Instructie bijgewerkt' : 'Instructie toegevoegd: ze gaat mee vanaf het volgende voorstel');
      setVorm(null); await laad(); laadLeerstof(); onGewijzigd && onGewijzigd();
    } catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const zetActief = async (i, actief) => {
    setBezig(i.id);
    try { await api(`/instructies/${i.id}`, 'PUT', { actief }); await laad(); laadLeerstof(); onGewijzigd && onGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const verwijder = async (i) => {
    if (!window.confirm('Deze instructie verwijderen? Ze gaat dan niet meer mee in de voorstellen.')) return;
    setBezig(i.id);
    try { await api(`/instructies/${i.id}`, 'DELETE'); toast.success('Instructie verwijderd'); await laad(); laadLeerstof(); onGewijzigd && onGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const nietMeerMeegeven = async (a) => {
    setBezig(`a${a.id}`);
    try { await api(`/voorstellen/${a.id}`, 'PATCH', { leren_uit: true }); toast.success('Dit aandachtspunt gaat niet meer mee'); laadLeerstof(); onGewijzigd && onGewijzigd(); }
    catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const tekens = vorm ? String(vorm.tekst || '').length : 0;
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card className="space-y-3 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-lg font-semibold text-slate-100">Vaste instructies</h3>
          <InfoTip title="Vaste instructies" text={'Werkafspraken die bij elk voorstel meegaan: over toon, opbouw en aanpak. Eén afspraak per instructie, kort en concreet, bijvoorbeeld "Verwijs bij een vraag over het voorschot altijd naar de klantenzone".\n\nEen instructie geldt voor één energielabel of voor alle labels. De vaste regels van het portaal gaan altijd voor: een instructie kan het model geen ander merk laten noemen en geen gegevens van een andere klant laten gebruiken.'} />
          <span className="flex-1" />
          {!vorm && <button type="button" onClick={() => setVorm({ id: null, tekst: '', energy_label: '', actief: true, bron_voorstel_id: null })} className="inline-flex items-center gap-1 h-8 px-3 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-lg text-xs font-semibold"><Plus size={14} />Nieuwe instructie</button>}
        </div>
        <p className="text-xs text-slate-400">Het model wordt niet getraind: het leest bij elk voorstel wat het meekrijgt. Een vaste instructie is de snelste manier om het blijvend bij te sturen.</p>
        {vorm && (
          <div ref={vormRef} className="rounded-lg border border-violet-500/30 bg-violet-500/[.06] p-4 space-y-3" data-ai-instructie-vorm>
            <div className="grid sm:grid-cols-[minmax(0,220px)_auto] gap-3 items-end">
              <div><label className="block text-[11px] text-slate-400 mb-1">Geldt voor</label>
                <select value={vorm.energy_label} onChange={(e) => setVorm((x) => ({ ...x, energy_label: e.target.value }))} className={`${HD_AI_VELD} w-full`}><option value="">Alle energielabels</option>{labels.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer select-none pb-2.5"><input type="checkbox" checked={!!vorm.actief} onChange={(e) => setVorm((x) => ({ ...x, actief: e.target.checked }))} className="w-3.5 h-3.5 rounded accent-lime-500" />Actief</label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><label className="block text-[11px] text-slate-400">Instructie</label><span className={`text-[11px] tabular-nums ${tekens > AI_MAX_INSTRUCTIE ? 'text-red-300' : 'text-slate-500'}`}>{tekens} / {AI_MAX_INSTRUCTIE}</span></div>
              <textarea value={vorm.tekst} onChange={(e) => setVorm((x) => ({ ...x, tekst: e.target.value }))} rows={4} autoFocus placeholder={'bv. "Houd het antwoord kort: hoogstens vijf zinnen, zonder opsommingen."'} className={`${HD_AI_VELD} w-full leading-relaxed`} />
              {vorm.bron_voorstel_id && <p className="text-[11px] text-slate-500 mt-1">Vertrekt van de opmerking van een medewerker. Herschrijf ze tot een afspraak die voor elk ticket geldt, zonder gegevens van een klant.</p>}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setVorm(null)} className="h-9 px-3 text-xs text-slate-400 hover:text-slate-200">Annuleren</button>
              <button type="button" onClick={bewaar} disabled={bezig === 'vorm' || tekens > AI_MAX_INSTRUCTIE} className="inline-flex items-center gap-1 h-9 px-4 bg-lime-500 hover:bg-lime-400 text-slate-900 rounded-lg text-xs font-semibold disabled:opacity-50">{bezig === 'vorm' ? <Spinner size={12} /> : <Check size={14} />}Opslaan</button>
            </div>
          </div>
        )}
        {lijst === null ? <div className="flex justify-center py-6"><Spinner size={18} /></div> : lijst.length === 0 ? (!vorm && <div className="text-xs text-slate-500">Nog geen vaste instructies.</div>) : (
          <div className="space-y-2">{lijst.map((i) => (
            <div key={i.id} className={`rounded-lg border px-3 py-2.5 text-xs ${i.actief ? 'border-slate-700/60 bg-slate-800/40' : 'border-slate-700/40 bg-slate-800/20 opacity-70'}`} data-ai-instructie={i.id}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-slate-200 leading-relaxed">{i.tekst}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                    {i.energy_label ? <span className="hd-pill hd-pill-slate">{i.energy_label}</span> : <span className="hd-pill hd-pill-emerald">alle labels</span>}
                    {!i.actief && <span className="hd-pill hd-pill-amber">niet actief</span>}
                    {i.bron_voorstel_id && <span>uit een opmerking</span>}
                    <span>· {i.gewijzigd_door || i.aangemaakt_door || 'onbekend'} · {aiDatum(i.updated_at || i.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center shrink-0">
                  <button type="button" onClick={() => zetActief(i, !i.actief)} disabled={bezig === i.id} title={i.actief ? 'Uitzetten (blijft bewaard)' : 'Aanzetten'} className="p-1.5 text-slate-400 hover:text-lime-300 disabled:opacity-50">{bezig === i.id ? <Spinner size={12} /> : i.actief ? <Eye size={14} /> : <Check size={14} />}</button>
                  <button type="button" onClick={() => setVorm({ id: i.id, tekst: i.tekst, energy_label: i.energy_label || '', actief: i.actief, bron_voorstel_id: i.bron_voorstel_id })} disabled={bezig === i.id} title="Bewerken" className="p-1.5 text-slate-400 hover:text-lime-300 disabled:opacity-50"><Pencil size={13} /></button>
                  <button type="button" onClick={() => verwijder(i)} disabled={bezig === i.id} title="Verwijderen" className="p-1.5 text-slate-400 hover:text-red-300 disabled:opacity-50"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}</div>
        )}
      </Card>

      <Card className="space-y-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <h3 className="text-lg font-semibold text-slate-100">Wat het model nu meekrijgt</h3>
          <InfoTip title="Wat het model meekrijgt" text={'Per energielabel: waaruit het volgende voorstel put.\n\nVoorbeelden: eerdere voorstellen van dit label die goed bevonden of gecorrigeerd werden. De drie die het best bij het ticket passen gaan mee, geanonimiseerd.\n\nAandachtspunten: de laatste vijf opmerkingen bij een duim omlaag. Is een opmerking achterhaald of te specifiek voor één ticket, haal ze er dan uit; maak er een vaste instructie van als ze voor elk ticket geldt.'} />
          <span className="flex-1" />
          <select value={leerLabel} onChange={(e) => setLeerLabel(e.target.value)} className={`h-9 px-2 bg-slate-700/70 border border-slate-600/70 rounded-lg text-xs text-slate-200 ${FILTER_MD}`} aria-label="Energielabel">{labels.length === 0 && <option value="">Geen energielabels</option>}{labels.map((l) => <option key={l} value={l}>{l}</option>)}</select>
        </div>
        {!leerstof ? <div className="text-xs text-slate-500">{leerLabel ? 'Laden…' : 'Kies een energielabel.'}</div> : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-slate-700/50 bg-slate-800/40 px-3 py-2"><div className="text-[11px] text-slate-400">Voorbeelden</div><div className="text-base font-semibold text-slate-100 tabular-nums">{aiGetal(leerstof.voorbeelden)}</div><div className="text-[11px] text-slate-500">{aiGetal(leerstof.voorbeelden_gecorrigeerd)} gecorrigeerd</div></div>
              <div className="rounded-md border border-slate-700/50 bg-slate-800/40 px-3 py-2"><div className="text-[11px] text-slate-400">Aandachtspunten</div><div className="text-base font-semibold text-slate-100 tabular-nums">{aiGetal(leerstof.aandachtspunten.length)}</div><div className="text-[11px] text-slate-500">hoogstens 5</div></div>
              <div className="rounded-md border border-slate-700/50 bg-slate-800/40 px-3 py-2"><div className="text-[11px] text-slate-400">Vaste instructies</div><div className="text-base font-semibold text-slate-100 tabular-nums">{aiGetal(leerstof.instructies)}</div><div className="text-[11px] text-slate-500">dit label + alle labels</div></div>
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 pt-1">Aandachtspunten uit afgekeurde voorstellen</div>
            {leerstof.aandachtspunten.length === 0 ? <div className="text-xs text-slate-500">Geen aandachtspunten voor {leerstof.label}.</div> : (
              <div className="space-y-2">{leerstof.aandachtspunten.map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-700/60 bg-slate-800/40 px-3 py-2.5 text-xs" data-ai-aandachtspunt={a.id}>
                  <div className="flex items-start gap-2"><ThumbsDown size={13} className="ai-slecht shrink-0 mt-0.5" /><div className="text-slate-200 leading-relaxed min-w-0">{a.opmerking}</div></div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                    <span className="text-slate-500">{a.door || 'onbekend'} · {aiDatum(a.created_at)}</span>
                    <span className="flex-1" />
                    <button type="button" onClick={() => setVorm({ id: null, tekst: String(a.opmerking || '').slice(0, AI_MAX_INSTRUCTIE), energy_label: leerstof.label, actief: true, bron_voorstel_id: a.id })} className="text-violet-300 hover:underline">Maak er een vaste instructie van</button>
                    <button type="button" onClick={() => nietMeerMeegeven(a)} disabled={bezig === `a${a.id}`} className="text-slate-400 hover:text-red-300 disabled:opacity-50">{bezig === `a${a.id}` ? 'Bezig…' : 'Niet meer meegeven'}</button>
                  </div>
                </div>
              ))}</div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

// Ticketkennis en standaardteksten afleiden: de kennis waaruit het voorstel put.
// Stond tot 27-09-2026 onder de kennisbank.
const HdAiKennisbronnen = ({ token, labels, isAdmin }) => {
  const [kennis, setKennis] = useState(null);
  const [bezig, setBezig] = useState(null);
  const [afleid, setAfleid] = useState({ label: '', maxTickets: 120, resultaat: null });
  const kb = useCallback(async (p, method = 'GET', body) => {
    const r = await fetch(`/api/kennisbank${p}`, { method, headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
    const b = await r.json().catch(() => null);
    if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
    return b;
  }, [token]);
  const laadKennis = useCallback(() => kb('/ticketkennis').then(setKennis).catch(() => setKennis(null)), [kb]);
  useEffect(() => { laadKennis(); }, [laadKennis]);
  const oogstNu = async () => {
    setBezig('oogst');
    try { const r = await kb('/ticketkennis/oogst', 'POST', { max: 100 }); toast.success(`${r.records} ticket(s) geoogst${r.fouten ? `, ${r.fouten} mislukt` : ''}`); await laadKennis(); }
    catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const leidAf = async () => {
    if (!afleid.label) return;
    setBezig('afleid'); setAfleid((a) => ({ ...a, resultaat: null }));
    try {
      const r = await fetch('/api/helpdesk/admin/standaardteksten/afleiden', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ label: afleid.label, maxTickets: Number(afleid.maxTickets) || 120 }) });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
      setAfleid((a) => ({ ...a, resultaat: b }));
      toast.success(`${b.aangemaakt.length} concept-standaardtekst(en) klaargezet op de pagina Mailsjablonen`);
    } catch (e) { toast.error(e.message); } finally { setBezig(null); }
  };
  const geoogst = kennis ? kennis.perLabel.reduce((s, r) => s + r.geoogst, 0) : 0;
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card className="space-y-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <h3 className="text-lg font-semibold text-slate-100">Ticketkennis</h3>
          <InfoTip title="Ticketkennis" text={'De job "Helpdesk — ticketkennis oogsten" haalt elk uur 300 afgehandelde tickets op, anonimiseert vraag en antwoord en slaat ze op. Daarin zoekt het AI-voorstel op inhoud, per energielabel. Belvus Energie blijft eruit.'} />
          <span className="flex-1" />
          <button onClick={oogstNu} disabled={bezig === 'oogst'} className="inline-flex items-center gap-1 h-8 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs disabled:opacity-50">{bezig === 'oogst' ? <Spinner size={12} /> : <RefreshCw size={13} />}Nu 100 oogsten</button>
        </div>
        <p className="text-xs text-slate-400">Geanonimiseerde vraag en antwoord van afgehandelde tickets: de voorbeelden waaruit het model de aanpak haalt.</p>
        {!kennis ? <div className="text-xs text-slate-500">Nog geen gegevens.</div> : (
          <>
            <div className="text-xs text-slate-300"><span className="text-lg font-bold text-slate-100 tabular-nums mr-1.5">{aiGetal(geoogst)}</span>van {aiGetal(kennis.afgehandeldTotaal)} afgehandelde tickets geoogst ({aiPct(pctVan(geoogst, kennis.afgehandeldTotaal))}).</div>
            {kennis.perLabel.length > 0 && <div className="space-y-2.5">{kennis.perLabel.map((r) => <HdAiAandeel key={r.energy_label || 'x'} naam={r.energy_label || '—'} sub={`${aiGetal(r.met_antwoord)} met antwoord`} aantal={r.geoogst} pct={pctVan(r.met_antwoord, r.geoogst)} kleur={CHART_HUES.blauw} />)}</div>}
          </>
        )}
      </Card>
      {isAdmin && (
        <Card className="space-y-3 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-lg font-semibold text-slate-100">Standaardteksten afleiden uit tickets</h3>
            <InfoTip title="Standaardteksten afleiden" text={'Het model leest geanonimiseerde vraag/antwoord-paren uit afgehandelde tickets van één energielabel en leidt er 8 tot 15 herbruikbare tekstblokken uit af.\n\nZe komen als concept op de pagina Mailsjablonen (module "Helpdesk — snelle antwoorden", categorie "AI-concept · …"): lees, corrigeer en activeer ze daar. Belvus Energie doet niet mee. Dezelfde stap bestaat als job "Helpdesk — standaardteksten afleiden" (standaard uit).'} />
          </div>
          <p className="text-xs text-slate-400">Nieuwe standaardteksten als concept, afgeleid uit wat medewerkers al antwoordden.</p>
          <div className="grid sm:grid-cols-[minmax(0,1fr)_120px_auto] gap-2 items-end">
            <div><label className="block text-[11px] text-slate-400 mb-1">Energielabel</label>
              <select value={afleid.label} onChange={(e) => setAfleid((a) => ({ ...a, label: e.target.value }))} className={`${HD_AI_VELD} w-full`}><option value="">Kies een label…</option>{labels.filter((l) => !/belvus/i.test(l)).map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
            <div><label className="block text-[11px] text-slate-400 mb-1">Max. tickets</label><input type="number" min={10} max={400} value={afleid.maxTickets} onChange={(e) => setAfleid((a) => ({ ...a, maxTickets: e.target.value }))} className={`${HD_AI_VELD} w-full`} /></div>
            <button onClick={leidAf} disabled={bezig === 'afleid' || !afleid.label} className="inline-flex items-center gap-1 h-9 px-3 bg-sky-500 hover:bg-sky-400 text-slate-900 rounded-lg text-xs font-semibold disabled:opacity-50">{bezig === 'afleid' ? <Spinner size={12} /> : <Wand2 size={14} />}{bezig === 'afleid' ? 'Bezig… (kan een minuut duren)' : 'Afleiden'}</button>
          </div>
          {afleid.resultaat && (
            <div className="text-xs space-y-1">
              <div className="text-slate-300">{afleid.resultaat.aangemaakt.length} concept(en) aangemaakt uit {afleid.resultaat.voorbeelden} voorbeelden ({afleid.resultaat.bekekenTickets} tickets bekeken{afleid.resultaat.live ? `, ${afleid.resultaat.live} live opgehaald` : ''}).{afleid.resultaat.overgeslagen.length ? ` Overgeslagen (bestond al): ${afleid.resultaat.overgeslagen.join(', ')}.` : ''}</div>
              <ul className="list-disc pl-4 text-slate-400">{afleid.resultaat.aangemaakt.map((a) => <li key={a.id}><span className="text-slate-200">{a.name}</span> <span className="text-slate-500">· {a.category} · {a.language}</span>{a.gebruik ? <span className="text-slate-500"> — {a.gebruik}</span> : null}</li>)}</ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

const HelpdeskAiPagina = ({ token, currentUser, salesStructure = EMPTY_STRUCTURE }) => {
  const api = useMemo(() => hdAiApi(token), [token]);
  const [deel, setDeel] = useState(() => { const d = leesSessieWaarde('helpdeskAiDeel'); return AI_DELEN.some((x) => x.value === d) ? d : 'inzicht'; });
  useEffect(() => { schrijfSessieWaarde('helpdeskAiDeel', deel); }, [deel]);
  const [dagen, setDagen] = useState(30);
  const [label, setLabel] = useState('');
  const [variant, setVariant] = useState('');
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  // Telt op na elke wijziging in Bijsturen of in een voorstel, zodat de andere
  // delen hun cijfers opnieuw ophalen.
  const [versie, setVersie] = useState(0);
  const gewijzigd = useCallback(() => setVersie((n) => n + 1), []);
  const [concept, setConcept] = useState(null);
  const conceptVerwerkt = useCallback(() => setConcept(null), []);
  useEffect(() => {
    let leeft = true;
    setFout('');
    api(`/overzicht?${aiQuery({ dagen, label, variant })}`).then((d) => { if (leeft) setData(d); }).catch((e) => { if (leeft) { setFout(e.message); setData(null); } });
    return () => { leeft = false; };
  }, [api, dagen, label, variant, versie]);
  // Labels: alle labels van het platform, aangevuld met labels die alleen nog in
  // de historiek voorkomen.
  const labels = useMemo(() => [...new Set([...(salesStructure.energy_labels || []), ...((data && data.labels) || [])])].sort((a, b) => a.localeCompare(b, 'nl-BE')), [salesStructure, data]);
  const metFilters = deel === 'inzicht' || deel === 'voorstellen';
  const naarInstructie = (i) => { setConcept(i); setDeel('bijsturen'); };
  return (
    <div className="space-y-6 grafiek-modern" data-helpdesk-ai>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
        <Toggle options={AI_DELEN} value={deel} onChange={setDeel} />
        {metFilters && <>
          <Toggle options={AI_PERIODES} value={dagen} onChange={setDagen} />
          <Toggle options={AI_VARIANTEN} value={variant} onChange={setVariant} />
          <select value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Energielabel" className={`h-9 px-2 bg-slate-700 border rounded-lg text-xs sm:text-sm ${FILTER_MD} ${label ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <option value="">Alle energielabels</option>{labels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </>}
      </div>
      {fout && <Card><div className="text-sm text-red-300">{fout}</div></Card>}
      {deel === 'inzicht' && !fout && (data ? <HdAiInzicht data={data} filters={data.leren ? ` Het model krijgt nu ${aiGetal(data.leren.instructies_actief)} vaste ${data.leren.instructies_actief === 1 ? 'instructie' : 'instructies'} mee; ${aiGetal(data.leren.uitgesloten)} ${data.leren.uitgesloten === 1 ? 'voorstel is' : 'voorstellen zijn'} uitgesloten van het leren.` : ''} /> : <div className="flex justify-center py-16"><Spinner size={26} /></div>)}
      {deel === 'voorstellen' && <HdAiVoorstellen token={token} api={api} dagen={dagen} label={label} variant={variant} versie={versie} onGewijzigd={gewijzigd} onInstructie={naarInstructie} />}
      {deel === 'bijsturen' && <HdAiBijsturen api={api} labels={labels} concept={concept} onConceptVerwerkt={conceptVerwerkt} versie={versie} onGewijzigd={gewijzigd} />}
      {deel === 'kennis' && <HdAiKennisbronnen token={token} labels={labels} isAdmin={currentUser?.role === 'admin'} />}
    </div>
  );
};

const HD_ANALYSE_KLEUREN = [CHART_HUES.blauw, CHART_HUES.oranje, CHART_HUES.aqua, CHART_HUES.geel, CHART_HUES.magenta, CHART_HUES.groen, CHART_HUES.violet, CHART_HUES.rood, CHART_HUES.goud];
const HD_ANALYSE_DOORLOOPTIJD_VANAF = '2026-01';
const hdAnalyseMaandLabel = (k) => { const [y, m] = String(k).split('-').map(Number); return `${mtrMonthShort.format(new Date(Date.UTC(2000, m - 1, 1))).replace('.', '')} '${String(y).slice(-2)}`; };
const hdPct = (v) => (v == null ? '—' : `${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 1 }).format(v)} %`);
const hdDec = (v, d = 1) => (v == null ? '—' : new Intl.NumberFormat('nl-BE', { maximumFractionDigits: d }).format(v));
const pctVan = (deel, geheel) => (geheel ? Math.round((deel / geheel) * 1000) / 10 : null);

const HelpdeskAnalyse = ({ token, salesStructure = EMPTY_STRUCTURE, onOpenCustomer360 }) => {
  const api = useMemo(() => hdApi(token), [token]);
  const [maanden, setMaanden] = useState(12);
  // Filters bovenaan: energieleverancier → verkoopteam (zelfde cascade als de
  // andere modules). Ze beperken de tickets én de actieve klanten.
  const [fLabel, setFLabel] = useState('all');
  const [fTeam, setFTeam] = useState('all');
  const [fTag, setFTag] = useState('all');   // tag-id, 'all' = uit
  const [fArb, setFArb] = useState('all');   // wie moest de actie nemen, 'all' = uit
  const { companies: labelOpts, teams: teamOpts } = useMemo(() => scopeOptions(salesStructure, fLabel, fTeam), [salesStructure, fLabel, fTeam]);
  const kiesLabel = (v) => { setFLabel(v); setFTeam('all'); };
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [laadt, setLaadt] = useState(true);
  const [reeks, setReeks] = useState('nieuw');           // nieuw | afgehandeld | backlog
  const [sorteer, setSorteer] = useState({ key: 'ratioPer100', dir: 'desc' });
  useEffect(() => {
    let leeft = true;
    setLaadt(true); setFout(null);
    const qs = new URLSearchParams({ maanden: String(maanden), ...(fLabel !== 'all' ? { label: fLabel } : {}), ...(fTeam !== 'all' ? { team: fTeam } : {}), ...(fTag !== 'all' ? { tag: fTag } : {}), ...(fArb !== 'all' ? { arb: fArb } : {}) });
    api.get(`/analyse?${qs}`).then((d) => { if (leeft) setData(d); }).catch((e) => leeft && setFout(e.message)).finally(() => leeft && setLaadt(false));
    return () => { leeft = false; };
  }, [api, maanden, fLabel, fTeam, fTag, fArb]);

  // Labels die in de periode iets betekenen: minstens één ticket of klant.
  const labels = useMemo(() => (data ? data.perLabel.filter((l) => l.tickets || l.openNu || l.actieveKlanten).map((l) => l.label) : []), [data]);
  const kleur = (label) => HD_ANALYSE_KLEUREN[labels.indexOf(label) % HD_ANALYSE_KLEUREN.length];
  const grafiek = useMemo(() => (data ? data.perMaand.map((m) => ({ maand: m.maand, ...Object.fromEntries(labels.map((l) => [l, (m[reeks] || {})[l] || 0])) })) : []), [data, labels, reeks]);
  // Enkel maanden vanaf januari 2026: eerder is het bewaarde sluitmoment een
  // benadering (backfill op de laatste ET-schrijfdatum) en zegt de curve niets.
  const doorlooptijd = useMemo(() => (data ? data.perMaand.filter((m) => m.maand >= HD_ANALYSE_DOORLOOPTIJD_VANAF).map((m) => ({ maand: m.maand, wd: m.gemDoorlooptijdWd })) : []), [data]);
  const rijen = useMemo(() => {
    if (!data) return [];
    const lijst = data.perLabel.filter((l) => labels.includes(l.label));
    const v = (r) => r[sorteer.key];
    return [...lijst].sort((a, b) => {
      const x = v(a), y = v(b);
      if (x == null && y == null) return 0; if (x == null) return 1; if (y == null) return -1;
      return (typeof x === 'string' ? x.localeCompare(y) : x - y) * (sorteer.dir === 'asc' ? 1 : -1);
    });
  }, [data, labels, sorteer]);
  const kop = (key, label, titel) => (
    <th className="px-3 py-2 font-medium text-right cursor-pointer select-none whitespace-nowrap" title={titel} onClick={() => setSorteer((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}>
      {label}{sorteer.key === key ? (sorteer.dir === 'desc' ? ' ▾' : ' ▴') : ''}
    </th>
  );
  const exporteer = () => {
    const kolommen = [
      { header: 'Energielabel', get: (r) => r.label },
      { header: 'Tickets (periode)', get: (r) => r.tickets },
      { header: 'Afgehandeld (periode)', get: (r) => r.afgehandeld },
      { header: 'Open nu', get: (r) => r.openNu },
      { header: 'Actieve klanten', get: (r) => r.actieveKlanten },
      { header: 'Tickets per 100 klanten', get: (r) => r.ratioPer100 },
      { header: `Gem. doorlooptijd (wd, max ${data.capWd})`, get: (r) => r.gemDoorlooptijdWd },
      { header: 'Mediaan doorlooptijd (wd)', get: (r) => r.mediaanDoorlooptijdWd },
      { header: `Binnen ${data.snelWd} wd (%)`, get: (r) => r.binnen2WdPct },
      { header: `Langer dan ${data.capWd} wd (%)`, get: (r) => r.boven10WdPct },
      { header: 'Escalatie (%)', get: (r) => r.escalatiePct },
      { header: 'Heropend (%)', get: (r) => r.heropendPct },
      { header: `Eerste reactie (wd, max ${data.capWd})`, get: (r) => r.eersteReactieWd },
      { header: 'Eerste reactie bekend (%)', get: (r) => r.eersteReactieDekkingPct },
      { header: 'Via portaal (%)', get: (r) => r.viaPortaalPct },
      { header: 'Open: wacht op klant', get: (r) => r.wachtOp.customer },
      { header: 'Open: wacht op partner', get: (r) => r.wachtOp.partner },
      { header: 'Open: wacht op Energy Together', get: (r) => r.wachtOp.energy_together },
    ];
    exportRowsToCsv(`helpdesk-analyse-${data.periode.van}_${data.periode.tot}.csv`, kolommen, rijen);
  };
  const t = data && data.totaal;
  const tip = { contentStyle: CHART_TOOLTIP, labelFormatter: (k) => hdAnalyseMaandLabel(k) };

  return (
    <div className="space-y-6">
      <Card className="!p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Energieleverancier</label><select value={fLabel} onChange={(e) => kiesLabel(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${fLabel !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={labelOpts}>Alle</AllOption>{labelOpts.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Verkoopteam</label><select value={fTeam} onChange={(e) => setFTeam(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${fTeam !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={teamOpts}>Alle</AllOption>{teamOpts.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Tag</label><select value={fTag} onChange={(e) => setFTag(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${fTag !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <option value="all">Alle</option>
            {((data && data.tagLijst) || []).map((tg) => <option key={tg.id} value={String(tg.id)}>{tg.name}</option>)}
          </select></div>
          <div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Actie vereist van</label><select value={fArb} onChange={(e) => setFArb(e.target.value)} title="Enkel tickets waar deze partij (laatst) aan zet was" className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${fArb !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}>
            <option value="all">Alle</option><option value="energy_together">Energy Together</option><option value="partner">Partner</option><option value="customer">Klant</option>
          </select></div>
          <div className="w-[132px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Periode</label>
            <select value={maanden} onChange={(e) => setMaanden(Number(e.target.value))} className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200">
              <option value={6}>6 maanden</option><option value={12}>12 maanden</option><option value={24}>24 maanden</option>
            </select></div>
          {data && <div className="text-xs text-slate-500 self-end pb-1.5">{hdAnalyseMaandLabel(data.periode.van)} t/m {hdAnalyseMaandLabel(data.periode.tot)}{data.ordersWarm === false && <span className="text-amber-400 ml-2">· de orders-cache is nog koud: actieve klanten en ratio ontbreken</span>}</div>}
        </div>
      </Card>

      {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{fout}</div>}
      {laadt && !data ? <Card className="!p-3"><ListSkeleton rows={6} /></Card> : data && (<>
        {/* Kengetallen over de hele periode, alle labels samen. */}
        <div className="flex flex-wrap gap-3">
          <StatTile label="Tickets in periode" value={formatNumberRounded(t.tickets)} dot={CHART_HUES.blauw} sub={`${formatNumberRounded(t.afgehandeld)} afgehandeld · ${formatNumberRounded(t.openNu)} nu open`} />
          <StatTile label="Tickets per 100 klanten" value={hdDec(t.ratioPer100)} dot={CHART_HUES.oranje} sub={`${formatNumberRounded(t.actieveKlanten)} actieve klanten`} />
          <StatTile label={`Gem. doorlooptijd (max ${data.capWd} wd)`} value={t.gemDoorlooptijdWd == null ? '—' : `${hdDec(t.gemDoorlooptijdWd)} wd`} dot={CHART_HUES.aqua} sub={`mediaan ${t.mediaanDoorlooptijdWd == null ? '—' : `${hdDec(t.mediaanDoorlooptijdWd)} wd`}`} />
          <StatTile label={`Afgehandeld binnen ${data.snelWd} wd`} value={hdPct(t.binnen2WdPct)} dot={CHART_HUES.groen} sub={`${hdPct(t.boven10WdPct)} langer dan ${data.capWd} wd`} />
          <StatTile label="Escalatiegraad" value={hdPct(t.escalatiePct)} dot={CHART_HUES.violet} sub="geëscaleerd naar Energy Together" />
          <StatTile label="Heropend" value={hdPct(t.heropendPct)} dot={CHART_HUES.magenta} sub="minstens één keer heropend" />
          <StatTile label={`Eerste reactie (max ${data.capWd} wd)`} value={t.eersteReactieWd == null ? '—' : `${hdDec(t.eersteReactieWd)} wd`} dot={CHART_HUES.geel} sub={`bekend voor ${hdPct(t.eersteReactieDekkingPct)} van de tickets`} />
        </div>

        {/* Grafiek 1: per maand per energielabel (gestapeld). */}
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="font-semibold text-slate-100">Tickets per maand per energielabel</h3>
            <MtrSegment value={reeks} onChange={setReeks} options={[{ value: 'nieuw', label: 'Nieuw' }, { value: 'afgehandeld', label: 'Afgehandeld' }, { value: 'backlog', label: 'Open op maandeinde' }]} />
          </div>
          <p className="text-xs text-slate-500 mb-3">{reeks === 'nieuw' ? 'Aangemaakt in die maand.' : reeks === 'afgehandeld' ? 'Gesloten in die maand (op het bewaarde sluitmoment).' : 'Nog open op de laatste dag van de maand: aangemaakt vóór het maandeinde en toen nog niet gesloten.'}</p>
          <div className="h-72 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={grafiek} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="maand" stroke="#64748b" fontSize={10} tickLine={false} tickFormatter={hdAnalyseMaandLabel} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => formatNumberRounded(v)} />
                <Tooltip {...tip} formatter={(v, n) => [formatNumberRounded(v), n]} cursor={{ fill: 'rgba(100,116,139,.12)' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {labels.map((l) => <Bar key={l} dataKey={l} stackId="a" fill={kleur(l)} maxBarSize={38} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Waarover gaan de tickets: taart + cijfers per tag (alle tags). */}
        <Card>
          <h3 className="font-semibold text-slate-100 mb-1">Waarover gaan de tickets (tags)</h3>
          <p className="text-xs text-slate-500 mb-3">Onderwerpcategorieën uit het bronsysteem. Bekend voor {hdPct(data.tagDekkingPct)} van de tickets in de periode{data.tagDekkingPct != null && data.tagDekkingPct < 100 ? ' — de verrijkingsjob haalt de rest elk uur in porties op' : ''}. Een ticket met meerdere tags telt bij elke tag mee.</p>
          {data.perTag.length === 0 ? <div className="py-8 text-center text-xs text-slate-500">Nog geen tags opgehaald — de verrijkingsjob loopt elk uur.</div> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <div className="h-72 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.perTag} dataKey="tickets" nameKey="tag" cx="50%" cy="50%" outerRadius="85%" innerRadius="45%" paddingAngle={1} isAnimationActive={false}>
                      {data.perTag.map((r, i) => <Cell key={r.tag} fill={HD_ANALYSE_KLEUREN[i % HD_ANALYSE_KLEUREN.length]} fillOpacity={1 - Math.floor(i / HD_ANALYSE_KLEUREN.length) * 0.3} />)}
                    </Pie>
                    <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n, ctx) => [`${formatNumberRounded(v)} tickets (${hdPct(ctx && ctx.payload && ctx.payload.aandeelPct)})`, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-auto max-h-80"><table className="w-full text-sm">
                <thead className="text-slate-400 text-xs"><tr><th className="px-2 py-1.5 font-medium text-left">Tag</th><th className="px-2 py-1.5 font-medium text-right">Tickets</th><th className="px-2 py-1.5 font-medium text-right">Aandeel</th><th className="px-2 py-1.5 font-medium text-right">Open</th><th className="px-2 py-1.5 font-medium text-right" title="Gemiddelde doorlooptijd in werkdagen (max 10)">Gem. wd</th></tr></thead>
                <tbody>{data.perTag.map((r, i) => (
                  <tr key={r.tag} className="border-t border-slate-700/40">
                    <td className="px-2 py-1.5 text-slate-200"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: HD_ANALYSE_KLEUREN[i % HD_ANALYSE_KLEUREN.length], opacity: 1 - Math.floor(i / HD_ANALYSE_KLEUREN.length) * 0.3 }} />{r.tag}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-100">{formatNumberRounded(r.tickets)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{hdPct(r.aandeelPct)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{formatNumberRounded(r.openNu)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{hdDec(r.gemDoorlooptijdWd)}</td>
                  </tr>))}
                </tbody></table></div>
            </div>
          )}
        </Card>

        {/* Grafiek 2: gemiddelde doorlooptijd per maand. */}
        <Card className="!p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
            <h3 className="font-semibold text-slate-100 text-sm">Gemiddelde doorlooptijd per maand</h3>
            <span className="text-[11px] text-slate-500">werkdagen, max {data.capWd} per ticket · vanaf {hdAnalyseMaandLabel(HD_ANALYSE_DOORLOOPTIJD_VANAF)}</span>
          </div>
          <div className="h-36 sm:h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={doorlooptijd} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="maand" stroke="#64748b" fontSize={10} tickLine={false} tickFormatter={hdAnalyseMaandLabel} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={40} domain={[0, data.capWd]} />
                <Tooltip {...tip} formatter={(v) => [v == null ? '—' : `${hdDec(v)} wd`, 'Gem. doorlooptijd']} />
                <ReferenceLine y={data.snelWd} stroke={CHART_HUES.groen} strokeDasharray="4 3" label={{ value: `${data.snelWd} wd`, position: 'insideTopLeft', fill: CHART_HUES.groen, fontSize: 10 }} />
                <Line type="monotone" dataKey="wd" stroke={CHART_HUES.aqua} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Tabel per energielabel. */}
        <Card className="!p-0 overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
            <div>
              <h3 className="font-semibold text-slate-100">Per energielabel</h3>
              <p className="text-xs text-slate-500">Welke leverancier veroorzaakt de meeste tickets en hoe snel raken ze afgehandeld. Klik op een kolomkop om te sorteren.</p>
            </div>
            <ExportKnop onExport={exporteer} label="Exporteer deze tabel naar Excel" count={rijen.length} disabled={!rijen.length} />
          </div>
          <div className="overflow-auto"><table className="w-full text-sm resp-card">
            <thead className="bg-slate-800 text-slate-400 text-xs"><tr>
              <th className="px-3 py-2 font-medium text-left cursor-pointer select-none" onClick={() => setSorteer((s) => ({ key: 'label', dir: s.key === 'label' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Energielabel{sorteer.key === 'label' ? (sorteer.dir === 'desc' ? ' ▾' : ' ▴') : ''}</th>
              {kop('tickets', 'Tickets', 'Aangemaakt in de periode')}
              {kop('afgehandeld', 'Afgehandeld', 'Gesloten in de periode')}
              {kop('openNu', 'Open nu', 'Vandaag nog open')}
              {kop('actieveKlanten', 'Actieve klanten', 'Unieke klantnummers met een actieve aansluiting (owned / in / int_in)')}
              {kop('ratioPer100', 'Per 100 klanten', 'Tickets in de periode per 100 actieve klanten')}
              {kop('gemDoorlooptijdWd', `Gem. wd (max ${data.capWd})`, `Gemiddelde doorlooptijd in werkdagen, per ticket afgekapt op ${data.capWd}`)}
              {kop('mediaanDoorlooptijdWd', 'Mediaan wd', 'Mediaan van de doorlooptijd (werkdagen)')}
              {kop('binnen2WdPct', `≤ ${data.snelWd} wd`, `Aandeel afgehandeld binnen ${data.snelWd} werkdagen`)}
              {kop('boven10WdPct', `> ${data.capWd} wd`, `Aandeel dat langer dan ${data.capWd} werkdagen open bleef (zonder cap)`)}
              {kop('escalatiePct', 'Escalatie', 'Aandeel van de tickets uit de periode dat geëscaleerd werd')}
              {kop('heropendPct', 'Heropend', 'Aandeel dat minstens één keer heropend werd (gesloten → weer open, of status Heropend)')}
              {kop('eersteReactieWd', 'Eerste reactie', `Gemiddelde werkdagen tot het eerste antwoord richting klant (max ${data.capWd}); alleen tickets waarvan de berichten al opgehaald zijn`)}
              {kop('viaPortaalPct', 'Via portaal', 'Aandeel dat vanuit dit portaal aangemaakt werd (de rest kwam via mail, telefoon of het bronsysteem)')}
              <th className="px-3 py-2 font-medium text-right whitespace-nowrap" title="Open tickets: wie is aan zet (klant / partner / Energy Together)">Wacht op K / P / ET</th>
            </tr></thead>
            <tbody>
              {rijen.map((r) => (
                <tr key={r.label} className="border-t border-slate-700/40">
                  <td className="px-3 py-2 text-slate-200 whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: kleur(r.label) }} />{r.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-200">{formatNumberRounded(r.tickets)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{formatNumberRounded(r.afgehandeld)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{formatNumberRounded(r.openNu)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{formatNumberRounded(r.actieveKlanten)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-100">{hdDec(r.ratioPer100)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-200">{hdDec(r.gemDoorlooptijdWd)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{hdDec(r.mediaanDoorlooptijdWd)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{hdPct(r.binnen2WdPct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{hdPct(r.boven10WdPct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{hdPct(r.escalatiePct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{hdPct(r.heropendPct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300" title={`bekend voor ${hdPct(r.eersteReactieDekkingPct)}`}>{r.eersteReactieWd == null ? '—' : hdDec(r.eersteReactieWd)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{hdPct(r.viaPortaalPct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-400 whitespace-nowrap">{r.wachtOp.customer} / {r.wachtOp.partner} / {r.wachtOp.energy_together}</td>
                </tr>
              ))}
              {t && <tr className="border-t-2 border-slate-600/60 font-semibold">
                <td className="px-3 py-2 text-slate-100">Totaal</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-100">{formatNumberRounded(t.tickets)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{formatNumberRounded(t.afgehandeld)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{formatNumberRounded(t.openNu)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{formatNumberRounded(t.actieveKlanten)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-100">{hdDec(t.ratioPer100)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-100">{hdDec(t.gemDoorlooptijdWd)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{hdDec(t.mediaanDoorlooptijdWd)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{hdPct(t.binnen2WdPct)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{hdPct(t.boven10WdPct)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{hdPct(t.escalatiePct)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{hdPct(t.heropendPct)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{t.eersteReactieWd == null ? '—' : hdDec(t.eersteReactieWd)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-200">{hdPct(t.viaPortaalPct)}</td>
                <td className="px-3 py-2" />
              </tr>}
            </tbody>
          </table></div>
        </Card>

        {/* Per verkoopteam, top-klanten, tags, kanaal en behandelaars. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card className="!p-0 overflow-hidden">
            <div className="px-4 pt-4 pb-2"><h3 className="font-semibold text-slate-100">Top 10 klanten</h3><p className="text-xs text-slate-500">Klanten met de meeste tickets in de periode. Klik op de naam voor Customer 360.</p></div>
            <div className="overflow-auto max-h-80"><table className="w-full text-sm resp-card">
              <thead className="bg-slate-800 text-slate-400 text-xs"><tr><th className="px-3 py-2 font-medium text-left">Klant</th><th className="px-3 py-2 font-medium text-left">Energielabel</th><th className="px-3 py-2 font-medium text-right">Tickets</th><th className="px-3 py-2 font-medium text-right">Open</th><th className="px-3 py-2 font-medium text-right">Heropend</th></tr></thead>
              <tbody>{data.topKlanten.map((k) => (
                <tr key={k.ref || k.naam} className="border-t border-slate-700/40">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {onOpenCustomer360 && (k.naam || k.ref)
                      ? <button type="button" onClick={() => onOpenCustomer360(klantTarget(k.naam, k.ref))} className="text-slate-200 hover:text-lime-300 text-left" title="Open in Customer 360">{k.naam || k.ref}</button>
                      : <span className="text-slate-200">{k.naam || k.ref || '—'}</span>}
                    {k.ref && <span className="ml-2 text-[11px] text-slate-500 font-mono">{k.ref}</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs whitespace-nowrap">{k.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-100">{formatNumberRounded(k.tickets)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{formatNumberRounded(k.openNu)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-300">{formatNumberRounded(k.heropend)}</td>
                </tr>))}
                {data.topKlanten.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-slate-500 text-xs">Geen tickets in de periode.</td></tr>}
              </tbody></table></div>
          </Card>
          <Card>
            <h3 className="font-semibold text-slate-100 mb-1">Gebruikte mailsjablonen</h3>
            <p className="text-xs text-slate-500 mb-3">Aandeel per mailsjabloon in de {formatNumberRounded(data.mailsTotaal || 0)} mails die in de periode vanaf deze tickets verstuurd zijn. "Vrije tekst" = zonder sjabloon.</p>
            {(data.perSjabloon || []).length === 0 ? <div className="py-8 text-center text-xs text-slate-500">Nog geen mails verstuurd vanaf deze tickets.</div> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.perSjabloon} dataKey="mails" nameKey="sjabloon" cx="50%" cy="50%" outerRadius="85%" innerRadius="45%" paddingAngle={1} isAnimationActive={false}>
                        {data.perSjabloon.map((r, i) => <Cell key={r.sjabloon} fill={HD_ANALYSE_KLEUREN[i % HD_ANALYSE_KLEUREN.length]} fillOpacity={1 - Math.floor(i / HD_ANALYSE_KLEUREN.length) * 0.3} />)}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n, ctx) => [`${formatNumberRounded(v)} mails (${hdPct(ctx && ctx.payload && ctx.payload.aandeelPct)})`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-auto max-h-56"><table className="w-full text-sm">
                  <thead className="text-slate-400 text-xs"><tr><th className="px-2 py-1.5 font-medium text-left">Sjabloon</th><th className="px-2 py-1.5 font-medium text-right">Mails</th><th className="px-2 py-1.5 font-medium text-right">Aandeel</th></tr></thead>
                  <tbody>{data.perSjabloon.map((r, i) => (
                    <tr key={r.sjabloon} className="border-t border-slate-700/40">
                      <td className="px-2 py-1.5 text-slate-200"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: HD_ANALYSE_KLEUREN[i % HD_ANALYSE_KLEUREN.length], opacity: 1 - Math.floor(i / HD_ANALYSE_KLEUREN.length) * 0.3 }} />{r.sjabloon}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-100">{formatNumberRounded(r.mails)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{hdPct(r.aandeelPct)}</td>
                    </tr>))}
                  </tbody></table></div>
              </div>
            )}
          </Card>
          <div className="space-y-6">
            <Card>
              <h3 className="font-semibold text-slate-100 mb-1">Kanaal</h3>
              <p className="text-xs text-slate-500 mb-3">Hoe de tickets uit de periode binnenkwamen: aangemaakt vanuit dit portaal, of via mail, telefoon of het bronsysteem.</p>
              <div className="flex flex-wrap gap-3">
                <StatTile label="Via het portaal" value={formatNumberRounded(data.kanaal.portaal)} dot={CHART_HUES.blauw} sub={hdPct(pctVan(data.kanaal.portaal, data.kanaal.portaal + data.kanaal.overig))} />
                <StatTile label="Mail / telefoon / bronsysteem" value={formatNumberRounded(data.kanaal.overig)} dot={CHART_HUES.oranje} sub={hdPct(pctVan(data.kanaal.overig, data.kanaal.portaal + data.kanaal.overig))} />
              </div>
            </Card>
          </div>
        </div>

      </>)}
    </div>
  );
};

// Paperclip met aantal bijlagen in de ticketlijst (uit de verrijking); de namen
// staan in de tooltip. Geen bijlagen of nog niet bekend = niets.
const HdBijlageClip = ({ t }) => {
  const lijst = Array.isArray(t.attachments) ? t.attachments : [];
  if (!lijst.length) return null;
  return <span className="inline-flex items-center gap-0.5 ml-1.5 text-[11px] text-slate-400" title={lijst.map((a) => `${a.name}${a.size ? ` (${hdBytes(a.size)})` : ''}`).join('\n')}><Paperclip size={11} className="shrink-0 w-3" />{lijst.length}</span>;
};
// Waarschuwing voor beheerders: mails die vanuit een ticket verstuurd zijn maar
// (nog) niet als bericht op het ticket staan. Eén knop probeert ze meteen
// opnieuw; de job helpdesk.mail-posts doet dat anders elk kwartier.
const HdOpenMailPosts = ({ token, onOpenTicket }) => {
  const [posts, setPosts] = useState([]);
  const [bezig, setBezig] = useState(false);
  const laad = async () => {
    try { const r = await fetch('/api/helpdesk/mail-posts/open', { headers: { Authorization: `Bearer ${token}` } }); const b = await r.json().catch(() => null); if (r.ok) setPosts(b.posts || []); }
    catch (_) { /* stil */ }
  };
  useEffect(() => { laad(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!posts.length) return null;
  const opnieuw = async () => {
    setBezig(true);
    try {
      const r = await fetch('/api/helpdesk/mail-posts/retry', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && b.error) || `HTTP ${r.status}`);
      toast[b.records ? 'success' : 'info'](b.message);
      await laad();
    } catch (e) { toast.error(e.message); }
    finally { setBezig(false); }
  };
  const tickets = [...new Set(posts.map((p) => p.ticket_id))];
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 px-4 py-2 rounded-lg text-sm flex flex-wrap items-center gap-2">
      <AlertCircle size={15} className="shrink-0" />
      <span>{posts.length} verstuurde mail{posts.length === 1 ? '' : 's'} sta{posts.length === 1 ? 'at' : 'an'} nog niet als bericht op het ticket
        {posts[0]?.error ? <span className="text-amber-300/80"> ({posts[0].error.slice(0, 80)})</span> : null}.</span>
      <span className="flex flex-wrap items-center gap-1">
        {tickets.slice(0, 6).map((t) => <button key={t} type="button" onClick={() => onOpenTicket?.(t)} className="px-1.5 py-0.5 rounded border border-amber-500/40 text-xs hover:bg-amber-500/20">#{t}</button>)}
        {tickets.length > 6 && <span className="text-xs">+{tickets.length - 6}</span>}
      </span>
      <button type="button" onClick={opnieuw} disabled={bezig} className="ml-auto px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 text-xs font-semibold">{bezig ? 'Bezig…' : 'Nu opnieuw plaatsen'}</button>
    </div>
  );
};

const HelpdeskPage = ({ token, currentUser, salesStructure = EMPTY_STRUCTURE, onOpenCustomer360, onTicketGesloten, orders = [] }) => {
  const hdSort = useTableSort();
  // Segment (B2C / B2B / Custom) per klantnummer, uit de orders: een ticket draagt
  // zelf geen segment. Meerdere segmenten op één klant → allemaal, gescheiden door "/".
  const segmentPerKlantnr = useMemo(() => {
    const m = new Map();
    for (const o of orders || []) {
      const ref = String(o.customerReference || '').trim().toLowerCase();
      const seg = orderSegmentClass(o);
      if (!ref || !seg) continue;
      const s = m.get(ref) || new Set();
      s.add(seg === 'CUSTOM' ? 'Custom' : seg);
      m.set(ref, s);
    }
    return m;
  }, [orders]);
  const ticketSegment = (t) => { const s = segmentPerKlantnr.get(String(t.customer?.reference || '').trim().toLowerCase()); return s ? [...s].sort().join('/') : ''; };
  const api = useMemo(() => hdApi(token), [token]);
  const caps = useMemo(() => new Set(currentUser?.helpdeskPerms || []), [currentUser]);
  const can = (c) => caps.has(c);
  const showBrand = seesEtBrand(currentUser?.role);
  // Horizontaal tab-menu (Samenvatting / Analyse). Analyse bestaat alleen voor
  // admin/ET-dossier (subpagina met alleenRollen); voor de rest is het één tab.
  // "Admin" (automatische regels, tagbeheer) is er alleen voor de rol admin:
  // subPageSetForUser laat full-access rollen álles zien, dus hier zelf filteren.
  const helpdeskTabDefs = useMemo(() => HELPDESK_TABS.filter((t) => t.id !== 'admin' || currentUser?.role === 'admin'), [currentUser]);
  const { tabs: helpdeskTabs, mag: magHelpdeskTab, leeg: geenHelpdeskTabs } = useSubPages('helpdesk', currentUser, helpdeskTabDefs);
  const [helpdeskTab, setHelpdeskTab] = useState(() => leesSessieWaarde('helpdeskTab') || 'samenvatting');
  useEffect(() => { schrijfSessieWaarde('helpdeskTab', helpdeskTab); }, [helpdeskTab]);
  useEffect(() => {
    if (helpdeskTabs.length > 0 && !helpdeskTabs.some((t) => t.id === helpdeskTab)) setHelpdeskTab(helpdeskTabs[0].id);
  }, [helpdeskTabs, helpdeskTab]);
  const toonKennisbank = magHelpdeskTab('kennisbank') && helpdeskTab === 'kennisbank';
  const toonAdmin = magHelpdeskTab('admin') && helpdeskTab === 'admin';
  const toonAi = magHelpdeskTab('ai') && helpdeskTab === 'ai';
  // "Analyse", "Kennisbank", "AI-voorstellen" en "Admin" vervangen de lijst; alles wat bij de lijst
  // hoort (export, nieuw ticket, filters) hangt aan `toonAnalyse` = een andere tab open.
  const toonAnalyse = (magHelpdeskTab('analyse') && helpdeskTab === 'analyse') || toonKennisbank || toonAdmin || toonAi;
  const { onContext: onC360Context, menu: c360Menu } = useCustomer360Menu(onOpenCustomer360);
  // Cascade-filters: zelfde RBAC-cascade (energielabel → verkoopteam → verkoper)
  // als alle andere modules, met default-selectie op de scope van de gebruiker.
  const scopeDefaults = useMemo(() => defaultScopeSelection(currentUser), [currentUser]);
  const [companyFilter, setCompanyFilter] = useState(scopeDefaults.company);
  const [verkoopteamFilter, setVerkoopteamFilter] = useState(scopeDefaults.team);
  const [verkoperFilter, setVerkoperFilter] = useState(scopeDefaults.seller);
  // Secundaire filters (client-side op de geladen set, zoals andere modules).
  // null = nog geen eigen keuze → de standaardweergave: alles BEHALVE afgehandelde
  // tickets. Een array is een expliciete keuze van de gebruiker (leeg = alle
  // statussen). Dat onderscheid is nodig omdat de filters bewaard worden: zonder
  // sentinel zou een lege lijst na een refresh niet te onderscheiden zijn van
  // "nog niets gekozen" en zou de standaard nooit meer gelden.
  const [statusFilter, setStatusFilter] = useState(null); // null | string[]
  const [actionFilter, setActionFilter] = useState('all');
  const [escalatedFilter, setEscalatedFilter] = useState('all');
  // Tweede filterlaag (verkoopteam, verkoper, escalatie, tag, open langer dan) open of dicht.
  const [meerFilters, setMeerFilters] = useState(false);
  // "Ouder dan": enkel open tickets die langer dan N dagen openstaan ('all' = uit).
  const [ageFilter, setAgeFilter] = useState('all');
  // Behandelaar (intern, enkel admin/ET-dossier): 'all' | 'none' | 'me' | <user id>.
  // Behandelaar en Tag zijn MEERKEUZE (vraag 11-09-2026): lijsten van namen.
  // Behandelaar kent twee bijzondere keuzes ('Aan mij', 'Niet toegewezen'), Tag
  // één ('Zonder tag'). Een oud bewaard filter (één string) wordt via hdAlsLijst
  // omgezet, zodat favorieten en sessie-opslag van vóór deze wijziging blijven werken.
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  // Kengetalblokken ingeklapt? Voorkeur per gebruiker, over sessies heen.
  const [kpiIngeklapt, setKpiIngeklapt] = useUiPref(token, 'helpdesk.kpi_ingeklapt', false);
  const [assignees, setAssignees] = useState([]);
  // Externe behandelaars (mensen van de partner) binnen de rechten van de lezer.
  const [behExtern, setBehExtern] = useState([]);
  useEffect(() => {
    api.get('/assignees').then((d) => { setAssignees(d.items || []); setBehExtern(d.extern || []); }).catch(() => { setAssignees([]); setBehExtern([]); });
  }, [api]);
  // Een partnerrol ziet de behandelaar pas als er binnen zijn rechten behandelaars
  // zijn; wijzigen vraagt het recht "Behandelaar toewijzen".
  const toonBehandelaar = showBrand || behExtern.length > 0;
  const magToewijzen = showBrand ? can('status') : can('assign');
  const alleBehandelaars = [...assignees, ...behExtern];
  // Wachttijd-categorie in werkdagen (klik in het kengetallenblok), 'all' = uit.
  const [wdFilter, setWdFilter] = useState('all');
  // Enkel afgehandeld in de laatste 7 kalenderdagen (klik op "Afgehandeld in week").
  const [weekFilter, setWeekFilter] = useState(false);
  // Tag (onderwerpcategorie), 'all' = uit; de opties komen uit de geladen set.
  const [tagFilter, setTagFilter] = useState([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const PAGE = 25;
  const [raw, setRaw] = useState({ items: [], complete: true, truncated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState({});
  const [teams, setTeams] = useState([]);
  const [stages, setStages] = useState([]); // partner-selecteerbare stages (status-wijziging)
  // Het geopende ticket wordt afgeleid uit de URL-hash (#helpdesk/ticket/<id>),
  // zodat een diep gelinkte tab (Ctrl/⌘+klik) meteen het juiste ticket toont en
  // de browser-terug-knop werkt.
  const [selectedId, setSelectedId] = useState(() => ticketHashId());
  useEffect(() => {
    const sync = () => setSelectedId(ticketHashId());
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);
  // Bladeren tussen tickets vanuit het ticketscherm (04-10-2026): de volgorde is
  // die van het overzicht — met de filters en de sortering van dat moment — en
  // wordt vastgelegd wanneer je een ticket opent. Zo verspringt de reeks niet
  // terwijl je erdoor loopt (een ticket dat je afsluit, valt uit de lijst).
  const lijstVolgorde = useRef([]);   // id's van het overzicht, bij elke render bijgewerkt
  const navVolgorde = useRef([]);     // de vastgelegde reeks waar je doorheen bladert
  const openTicket = (id) => { navVolgorde.current = lijstVolgorde.current; setSelectedId(id); try { window.location.hash = `helpdesk/ticket/${id}`; } catch (e) { /* hash niet beschikbaar */ } };
  // LET OP: replaceState vuurt GEEN hashchange. De app zou dus niet merken dat je
  // het ticket verlaat — vandaar de expliciete melding naar boven, die in een
  // ticket-deeplink-tab het achtergrondladen vrijgeeft.
  const closeTicket = () => { setSelectedId(null); try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) { window.location.hash = ''; } if (onTicketGesloten) onTicketGesloten(); };
  const onRowOpen = (e, id) => { if (isNewTabClick(e)) { e.preventDefault(); openTicketInNewTab(id); } else { openTicket(id); } };
  // Inline bewerken in de tabel: "actie vereist van" en de behandelaar, zonder
  // het ticket te openen. De rij zelf opent op klik, dus de keuzelijst slikt haar
  // klikken (stopPropagation). Na een geslaagde wijziging past de lokale set mee
  // aan; de kengetallen en filters volgen daaruit.
  const [inlineBezig, setInlineBezig] = useState(null); // ticket-id dat net bewerkt wordt
  const werkRijBij = (ticketId, patch) => setRaw((r) => ({ ...r, items: r.items.map((x) => (x.id === ticketId ? { ...x, ...patch } : x)) }));
  const wijzigArb = async (t, v) => {
    if (!v || v === t.action_required_by) return;
    setInlineBezig(t.id);
    try {
      const d = await api.send(`/tickets/${t.id}`, 'PATCH', { action_required_by: v });
      werkRijBij(t.id, { action_required_by: (d && d.action_required_by) || v });
      toast.success(`Actie vereist van: ${v === 'partner' ? 'Partner' : v === 'customer' ? 'Klant' : (showBrand ? 'Energy Together' : 'Support')}`);
    } catch (e) { toast.error(e.message); } finally { setInlineBezig(null); }
  };
  // Tags inline bewerken (potloodje in de kolom Tags). De kiesbare tags komen
  // één keer binnen; de rij krijgt meteen de nieuwe namen.
  const [tagLijst, setTagLijst] = useState([]);
  useEffect(() => { if (can('read')) api.get('/tags').then((d) => setTagLijst(Array.isArray(d) ? d : (d && d.items) || [])).catch(() => {}); }, [api]);   // eslint-disable-line react-hooks/exhaustive-deps
  const wijzigTags = async (t, ids, namen) => {
    setInlineBezig(t.id);
    try {
      await api.send(`/tickets/${t.id}`, 'PATCH', { tag_ids: ids });
      werkRijBij(t.id, { tags: namen });
      toast.success('Tags bijgewerkt');
    } finally { setInlineBezig(null); }
  };
  const nieuweTag = async (naam) => { const t = await api.send('/tags', 'POST', { name: naam }); setTagLijst((l) => [...l, t].sort((a, b) => String(a.name).localeCompare(String(b.name)))); return t; };
  const wijzigBehandelaar = async (t, userId) => {
    setInlineBezig(t.id);
    try {
      const d = await api.send(`/tickets/${t.id}/assignee`, 'PUT', { user_id: userId ? Number(userId) : null });
      werkRijBij(t.id, { assignee: (d && d.assignee) || null });
      toast.success(userId ? 'Ticket toegewezen' : 'Toewijzing weggehaald');
    } catch (e) { toast.error(e.message); } finally { setInlineBezig(null); }
  };
  const stil = (e) => { e.stopPropagation(); };
  const inlineSelect = 'h-7 max-w-full px-1.5 bg-slate-700/70 border border-slate-600/70 rounded-md text-xs text-slate-200 disabled:opacity-50';
  const [showCreate, setShowCreate] = useState(false);

  const { companies: companyOpts, teams: teamOpts, sellers: sellerOpts } = useMemo(
    () => scopeOptions(salesStructure, companyFilter, verkoopteamFilter),
    [salesStructure, companyFilter, verkoopteamFilter]
  );

  useEffect(() => {
    if (!can('read')) return;
    api.get('/companies').then((d) => setCompanies(d.byId || {})).catch(() => {});
    api.get('/teams').then((d) => setTeams(Array.isArray(d) ? d : [])).catch(() => {});
    api.get('/stages').then((d) => setStages(Array.isArray(d) ? d : [])).catch(() => {});
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  // Herhaal de scope-default wanneer de gebruiker wisselt (impersonate).
  useEffect(() => {
    const d = defaultScopeSelection(currentUser);
    setCompanyFilter(d.company); setVerkoopteamFilter(d.team); setVerkoperFilter(d.seller);
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.energyLabel, currentUser?.salesTeam, currentUser?.sellerName]); // eslint-disable-line react-hooks/exhaustive-deps
  useFilterPersistence('helpdesk.filters', currentUser,
    { companyFilter, verkoopteamFilter, verkoperFilter, statusFilter, actionFilter, escalatedFilter, ageFilter, wdFilter, assigneeFilter, weekFilter, tagFilter, q },
    (s) => { setWeekFilter(s.weekFilter === true); setTagFilter(hdAlsLijst(s.tagFilter, 'tag')); setCompanyFilter(s.companyFilter ?? 'all'); setVerkoopteamFilter(s.verkoopteamFilter ?? 'all'); setVerkoperFilter(s.verkoperFilter ?? 'all'); setStatusFilter(Array.isArray(s.statusFilter) ? s.statusFilter : null); setActionFilter(s.actionFilter ?? 'all'); setEscalatedFilter(s.escalatedFilter ?? 'all'); setAgeFilter(s.ageFilter ?? 'all'); setWdFilter(s.wdFilter ?? 'all'); setAssigneeFilter(hdAlsLijst(s.assigneeFilter, 'behandelaar', assignees)); setQ(s.q ?? ''); });

  // Laad de gescopete ticketset (per energielabel volledig) bij wijziging van de
  // cascade. Status/actie/escalatie/zoeken filteren client-side (zoals elders).
  const ticketLaadBeurt = useRef(0);
  // Live-verversing: `watermerk` = de klok van de databank op het moment van de
  // laatste (geslaagde) ophaal; GET /wijzigingen?sinds=<watermerk> geeft enkel
  // wat daarna wijzigde. `lijstLaadt` houdt de controle tegen zolang een volledige
  // ophaal loopt: die eindigt met een setRaw van de hele set en zou een
  // tussentijds ingevoegde wijziging weer overschrijven. Na de ophaal vertrekt de
  // eerste controle van het watermerk van vóór de ophaal, dus er gaat niets verloren.
  const watermerk = useRef(null);
  const lijstLaadt = useRef(false);
  const controleBezig = useRef(false);
  const [verversen, setVerversen] = useState(false);   // subtiel: draaiend icoon, geen skelet
  const [bijgewerktOm, setBijgewerktOm] = useState(null);
  const scopeQs = useMemo(() => {
    const qs = new URLSearchParams();
    if (companyFilter && companyFilter !== 'all') qs.append('company', companyFilter);
    if (verkoopteamFilter && verkoopteamFilter !== 'all') qs.append('team', verkoopteamFilter);
    if (verkoperFilter && verkoperFilter !== 'all') qs.append('seller', verkoperFilter);
    return qs;
  }, [companyFilter, verkoopteamFilter, verkoperFilter]);
  // `stil`: de lijst blijft staan terwijl de nieuwe set binnenkomt (verversknop,
  // of de server vraagt om te herladen); zonder `stil` het gewone laadscherm.
  const loadTickets = useMemo(() => async ({ stil = false } = {}) => {
    if (stil) setVerversen(true); else { setLoading(true); setError(null); }
    lijstLaadt.current = true;
    const beurt = ++ticketLaadBeurt.current;
    try {
      const qs = new URLSearchParams(scopeQs);
      // Het watermerk vertrekt SAMEN met de ophaal (geen extra wachttijd) en is
      // dus van vóór de lijst gelezen werd; de server rekent er zelf nog een
      // overlap bij. `cached: false` = geen cache (live pad) → geen automatische
      // controle, enkel de knop.
      const basisVraag = api.get(`/wijzigingen${qs.toString() ? `?${qs.toString()}` : ''}`).catch(() => null);
      // Gepagineerd ophalen. De gescopete ticketset is voor een admin ~10 MB in
      // één respons; dat overleeft niet elke verbinding en kwam dan afgekapt aan
      // (JSON-parsefout, leeg scherm). In brokken blijft élk antwoord klein en
      // krijgt de gebruiker nog steeds ALLE tickets binnen zijn scope. De meta
      // (complete/truncated) staat op elke pagina en pikken we via onMeta op.
      let meta = { complete: true, truncated: false };
      const onMeta = (j) => { meta = { complete: j.complete !== false, truncated: !!j.truncated }; };
      const qsStr = qs.toString();
      // De server stuurt de OPEN tickets eerst (dan pas de afgehandelde), dus de
      // eerste pagina bevat alles waar men doorgaans mee werkt en waar de lijst
      // standaard op filtert. Die tonen we meteen; de overige pagina's — vooral
      // afgehandelde tickets — vullen de set op de achtergrond aan (`aanvullen`
      // laat het scherm zeggen dat er nog aankomt). Een nieuwe ophaal (andere
      // cascade) maakt de vorige ongeldig via `beurt`, anders zouden late
      // pagina's van de oude scope in de nieuwe lijst belanden.
      let eerste = true;
      const onPage = (rows) => {
        if (stil || beurt !== ticketLaadBeurt.current) return;   // stil: de oude lijst blijft staan tot alles binnen is
        if (eerste) { eerste = false; setRaw({ items: rows, complete: true, truncated: false, aanvullen: true }); setLoading(false); return; }
        setRaw((prev) => ({ ...prev, items: prev.items.concat(rows) }));
      };
      const items = await fetchPaginated(`/api/helpdesk/tickets${qsStr ? `?${qsStr}` : ''}`, token, { onMeta, onPage });
      const basis = await basisVraag;
      if (beurt !== ticketLaadBeurt.current) return;
      watermerk.current = basis && basis.cached !== false && basis.tot ? basis.tot : null;
      setRaw({ items, complete: meta.complete, truncated: meta.truncated, aanvullen: false });
      setBijgewerktOm(Date.now());
    } catch (e) {
      if (beurt !== ticketLaadBeurt.current) return;
      // Stil verversen mislukt → de lijst die er staat is nog altijd bruikbaar.
      if (stil) toast.error(`Vernieuwen mislukt: ${e.message}`);
      else { setError(e.message); setRaw({ items: [], complete: true, truncated: false, aanvullen: false }); }
    } finally {
      if (beurt === ticketLaadBeurt.current) { lijstLaadt.current = false; setLoading(false); setVerversen(false); }
    }
  }, [token, api, scopeQs]);
  // Wat wijzigde er sinds de laatste ophaal? Enkel die tickets komen terug en
  // worden op id ingevoegd: geen nieuw laadscherm, geen sprong in de lijst, en
  // filters, sortering, pagina en scrollpositie blijven staan. `toon` laat het
  // icoon van de verversknop draaien (tabwissel, terug uit een ticket).
  const controleerWijzigingen = async ({ toon = false } = {}) => {
    if (!can('read') || !lijstNodig) return;
    if (lijstLaadt.current || controleBezig.current) return;
    if (!watermerk.current) return;
    controleBezig.current = true;
    const beurt = ticketLaadBeurt.current;
    if (toon) setVerversen(true);
    try {
      const qs = new URLSearchParams(scopeQs);
      qs.append('sinds', watermerk.current);
      const d = await api.get(`/wijzigingen?${qs.toString()}`);
      if (beurt !== ticketLaadBeurt.current) return;
      if (d && d.herladen) { controleBezig.current = false; await loadTickets({ stil: true }); return; }
      if (d && d.tot) watermerk.current = d.tot;
      if (d && Array.isArray(d.items) && d.items.length) setRaw((prev) => { const items = hdVoegWijzigingenIn(prev.items, d.items); return items === prev.items ? prev : { ...prev, items }; });
      setBijgewerktOm(Date.now());
    } catch (e) { /* een gemiste controle is geen fout: de volgende haalt ze in */ }
    finally { controleBezig.current = false; if (toon) setVerversen(false); }
  };
  useHdVerversing(() => controleerWijzigingen(), HD_LIJST_POLL_MS, can('read'));
  // De ticketLIJST is voor een admin een ophaal van meerdere megabytes. Opent deze
  // tab rechtstreeks op één ticket, dan is die lijst niet in beeld en dus niet
  // nodig — we laden ze pas zodra je het ticket sluit en de lijst echt te zien
  // krijgt. Daarna blijft `lijstNodig` gewoon true, zodat het openen en sluiten van
  // een volgend ticket geen nieuwe ophaal veroorzaakt (de lijst staat al in state).
  const [lijstNodig, setLijstNodig] = useState(!IS_TICKET_DEEPLINK_START);
  useEffect(() => { if (!selectedId) setLijstNodig(true); }, [selectedId]);
  useEffect(() => { if (can('read') && lijstNodig) loadTickets(); }, [loadTickets, lijstNodig]); // eslint-disable-line react-hooks/exhaustive-deps
  // Bij het openen van de ticketlijst — terug van Analyse/Kennisbank/Admin, of
  // terug uit een ticket — meteen nagaan of er iets wijzigde, zonder op de
  // volgende tik te wachten. De andere tabs halen hun data op bij het openen
  // zelf (ze worden dan pas opgebouwd) en zijn dus altijd actueel. De eerste
  // weergave slaat dit over: dan loopt de gewone ophaal al.
  const eersteWeergave = useRef(true);
  useEffect(() => {
    if (eersteWeergave.current) { eersteWeergave.current = false; return; }
    if (!selectedId && !showCreate && helpdeskTab === 'samenvatting') controleerWijzigingen({ toon: true });
  }, [helpdeskTab, selectedId, showCreate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [statusFilter, actionFilter, escalatedFilter, ageFilter, wdFilter, assigneeFilter, weekFilter, tagFilter, q, companyFilter, verkoopteamFilter, verkoperFilter]);

  // Statusopties afgeleid uit de geladen tickets (ET /stages is leeg = geen
  // partner-selecteerbare stages geconfigureerd; de filter werkt op wat bestaat).
  const statusOptions = useMemo(() => [...new Set(raw.items.map((t) => t.stage_name).filter(Boolean))].sort(), [raw.items]);
  // Wat de statuskeuze toont zolang de gebruiker niets koos: alle statussen die
  // in de geladen set voorkomen, behalve de afgehandelde.
  const standaardStatussen = useMemo(() => statusOptions.filter((s) => !isHdAfgehandeld(s)), [statusOptions]);
  const zichtbareStatussen = statusFilter ?? standaardStatussen;

  // Kengetallen over de cascade-scope (energielabel/team/verkoper), vóór de
  // secundaire filters: zo blijven ze staan terwijl je erop klikt om te filteren.
  // "Actie bij mij" volgt de rol: een partner wacht op 'partner', admin/ET op
  // 'energy_together' — dezelfde regel als het telletje in het menu.
  const mijnBeurt = showBrand ? 'energy_together' : 'partner';
  const kpi = useMemo(() => {
    const open = raw.items.filter(isHdOpen);
    const perStatus = {};
    // Open statussen: alle tickets. Een afgehandelde status: enkel wat in de
    // laatste 7 kalenderdagen afgehandeld is — het totaal (duizenden) zegt niets
    // over de werkdruk van vandaag.
    const nu = Date.now();
    raw.items.forEach((t) => { const s = t.stage_name || '—'; if (isHdAfgehandeld(s) && !isHdAfgehandeldInWeek(t, nu)) return; perStatus[s] = (perStatus[s] || 0) + 1; });
    const perActie = {};
    open.forEach((t) => { const a = t.action_required_by || 'onbekend'; perActie[a] = (perActie[a] || 0) + 1; });
    const perWd = {};
    open.forEach((t) => { const b = werkdagBucket(hdWerkdagenOpen(t)); if (b) perWd[b] = (perWd[b] || 0) + 1; });
    return {
      open: open.length,
      actie: open.filter((t) => t.action_required_by === mijnBeurt).length,
      escalatie: open.filter((t) => !!t.escalated_to_et).length,
      perStatus, perActie, perWd,
    };
  }, [raw.items, mijnBeurt]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const statusSet = new Set(statusFilter || []);
    const minDagen = ageFilter === 'all' ? null : Number(ageFilter);
    return raw.items.filter((t) => {
      // Geen eigen keuze → afgehandelde tickets vallen weg. Bewust op de status
      // zelf getoetst en niet op `standaardStatussen`: staan er in deze set enkel
      // afgehandelde tickets, dan is die lijst leeg en zou een set-toets alles
      // weer doorlaten.
      if (statusFilter === null) { if (isHdAfgehandeld(t.stage_name)) return false; }
      else if (statusSet.size && !statusSet.has(t.stage_name)) return false;
      if (actionFilter !== 'all' && t.action_required_by !== actionFilter) return false;
      if (escalatedFilter === 'true' && !t.escalated_to_et) return false;
      if (escalatedFilter === 'false' && t.escalated_to_et) return false;
      if (minDagen != null) { if (!isHdOpen(t)) return false; if ((hdDagenOpen(t) ?? 0) <= minDagen) return false; }
      if (wdFilter !== 'all') { if (!isHdOpen(t)) return false; if (werkdagBucket(hdWerkdagenOpen(t)) !== wdFilter) return false; }
      if (weekFilter && !isHdOpen(t) && !isHdAfgehandeldInWeek(t)) return false;
      // Meerkeuze = OF: een ticket past zodra één van de aangevinkte tags erop staat
      // (of het geen tag heeft, bij 'Zonder tag').
      if (tagFilter.length) {
        const tags = Array.isArray(t.tags) ? t.tags : [];
        const past = (tagFilter.includes(HD_TAG_GEEN) && tags.length === 0) || tags.some((x) => tagFilter.includes(x));
        if (!past) return false;
      }
      if (assigneeFilter.length) {
        const aid = t.assignee ? String(t.assignee.id) : '';
        const naam = t.assignee ? String(t.assignee.name || '') : '';
        const past = (assigneeFilter.includes(HD_BEH_MIJ) && aid !== '' && aid === String(currentUser?.id))
          || (assigneeFilter.includes(HD_BEH_GEEN) && aid === '')
          || (naam && assigneeFilter.includes(naam));
        if (!past) return false;
      }
      if (term) {
        const hay = `${t.ref || ''} ${t.subject || ''} ${t.customer?.name || ''} ${t.customer?.reference || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [raw.items, statusFilter, actionFilter, escalatedFilter, ageFilter, wdFilter, assigneeFilter, weekFilter, tagFilter, currentUser?.id, q]);
  // Tagopties uit de geladen set (alfabetisch), met "Zonder tag" als aparte keuze.
  // De tagfilter toont enkel de tags die in Beheer → Helpdesk tags AAN staan
  // (GET /tags geeft die lijst); zolang die lijst niet binnen is, de tags uit de
  // tickets zelf. Een uitgeschakelde tag blijft op het ticket staan, maar is geen
  // filterkeuze meer.
  const tagOptions = useMemo(() => {
    const uitConfig = tagLijst.map((t) => String(t.name || '')).filter(Boolean);
    const bron = uitConfig.length ? uitConfig : raw.items.flatMap((t) => (Array.isArray(t.tags) ? t.tags : []));
    return [...new Set(bron)].sort((a, b) => a.localeCompare(b));
  }, [raw.items, tagLijst]);

  if (!can('read')) return <div><PageHeader title="Helpdesk" /><EmptyState message="Je hebt geen leesrechten voor de Helpdesk. Neem contact op met een beheerder." /></div>;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  // Standaardvolgorde: nieuwe tickets eerst, daarbinnen het recentst aangemaakte
  // bovenaan (en dezelfde regel voor de rest). Klikt de gebruiker een kolomkop
  // aan, dan neemt sortRows het over — deze volgorde is dus enkel het vertrekpunt.
  const hdAangemaakt = (t) => { const ms = hdTijdMs(t.create_date); return Number.isNaN(ms) ? 0 : ms; };
  const hdBijgewerkt = (t) => { const ms = hdTijdMs(t.updated_at || t.write_date); return Number.isNaN(ms) ? 0 : ms; };
  const standaardVolgorde = [...filtered].sort((a, b) =>
    (isHdNieuw(b.stage_name) - isHdNieuw(a.stage_name)) || (hdAangemaakt(b) - hdAangemaakt(a)));
  const gesorteerd = sortRows(standaardVolgorde, hdSort.sortConfig, {
    klant: (t) => t.customer?.name || '',
    klantnr: (t) => t.customer?.reference || '',
    segment: (t) => ticketSegment(t),
    label: (t) => companies[t.company_id] || t.company_id || '',
    verkoper: (t) => t.customer?.reseller?.salesperson_name || '',
    behandelaar: (t) => (t.assignee && t.assignee.name) || '',
    tags: (t) => (Array.isArray(t.tags) ? t.tags.join(', ') : ''),
    mailsjabloon: (t) => (Array.isArray(t.mail_templates) && t.mail_templates.length ? t.mail_templates[0] : ''),
    create_date: hdAangemaakt,
    updated_at: hdBijgewerkt,
    leeftijd: (t) => (hdDagenOpen(t) ?? -1),
  });
  const pageItems = gesorteerd.slice(page * PAGE, page * PAGE + PAGE);
  lijstVolgorde.current = gesorteerd.map((t) => Number(t.id));
  // Vorige/volgende ticket: de buur in de vastgelegde reeks die NU nog in het
  // overzicht staat. Staat het open ticket niet in de reeks (deeplink, of de
  // filters zijn intussen gewijzigd), dan geldt het overzicht van nu.
  const ticketNav = (() => {
    if (!selectedId) return null;
    let reeks = navVolgorde.current;
    if (!reeks.includes(Number(selectedId))) reeks = navVolgorde.current = lijstVolgorde.current;
    const i = reeks.indexOf(Number(selectedId));
    if (i < 0) return { vorige: null, volgende: null, positie: null, totaal: reeks.length };
    const nu = new Set(lijstVolgorde.current);
    let vorige = null; let volgende = null;
    for (let k = i - 1; k >= 0; k--) if (nu.has(reeks[k])) { vorige = reeks[k]; break; }
    for (let k = i + 1; k < reeks.length; k++) if (nu.has(reeks[k])) { volgende = reeks[k]; break; }
    return { vorige, volgende, positie: i + 1, totaal: reeks.length };
  })();
  // Naar een buur: de reeks blijft staan (openTicket zou ze opnieuw vastleggen).
  const gaNaarTicket = (id) => { setSelectedId(id); try { window.location.hash = `helpdesk/ticket/${id}`; } catch (e) { /* hash niet beschikbaar */ } };

  // Ticketdetail = volledige pagina (vervangt de lijst), niet langer een popup.
  // `onChanged` ververst de lijst alleen als die er al is: in een tab die op EEN
  // ticket opende zou een statuswijziging anders alsnog de hele lijst ophalen.

  // Nieuw ticket = een volledig scherm in plaats van de lijst (zoals het ticketdetail).
  if (showCreate) return <HelpdeskNieuwTicket token={token} teams={teams} orders={orders} currentUser={currentUser} showBrand={showBrand} fullPage onClose={() => setShowCreate(false)} onCreated={(c) => { setShowCreate(false); loadTickets(); if (c && c.id) openTicket(c.id); }} />;
  if (selectedId) return <HelpdeskTicketModal key={selectedId} id={selectedId} nav={ticketNav} onNav={gaNaarTicket} token={token} caps={caps} stages={stages} companies={companies} fullPage showBrand={showBrand} magKennisbank={hdMagKennisbank(currentUser)} magAi360={currentUser?.helpdeskAi360 === true} magNieuweTag={currentUser?.role === "admin"} orders={orders} onOpenCustomer360={onOpenCustomer360} onClose={closeTicket}
    // Een actie in het ticket zet de wijziging server-side meteen in de cache
    // (changed_at): enkel dát ticket ophalen volstaat. Zonder watermerk (geen
    // cache, live pad) blijft het de volledige ophaal van vroeger.
    onChanged={() => { if (!lijstNodig) return; if (watermerk.current) controleerWijzigingen(); else loadTickets(); }} />;

  // Een lege selectie is óók een keuze (= alle statussen), dus telt als filter;
  // `null` is de standaardweergave en telt niet mee.
  const hasActiveFilters = statusFilter !== null || actionFilter !== 'all' || escalatedFilter !== 'all' || ageFilter !== 'all' || wdFilter !== 'all' || assigneeFilter.length > 0 || weekFilter || tagFilter.length > 0 || q;
  const aantalMeerActief = [verkoopteamFilter !== 'all', verkoperFilter !== 'all', escalatedFilter !== 'all', tagFilter.length > 0, ageFilter !== 'all'].filter(Boolean).length;
  const meerFiltersOpen = meerFilters || aantalMeerActief > 0;
  const clearFilters = () => { setStatusFilter(null); setActionFilter('all'); setEscalatedFilter('all'); setAgeFilter('all'); setWdFilter('all'); setAssigneeFilter([]); setWeekFilter(false); setTagFilter([]); setQ(''); };
  const onCompany = (v) => { setCompanyFilter(v); setVerkoopteamFilter('all'); setVerkoperFilter('all'); };
  const onTeam = (v) => { setVerkoopteamFilter(v); setVerkoperFilter('all'); };

  // Favoriete filters: het energielabel hoort MEE in de snapshot — zie favorietScope.
  const filterSnapshot = {
    companyFilter,
    verkoopteamFilter, verkoperFilter, statusFilter, actionFilter, escalatedFilter, ageFilter, wdFilter, assigneeFilter, weekFilter, tagFilter, q,
  };
  const applyFavorite = (f) => {
    const scope = favorietScope(salesStructure, { company: f.companyFilter, team: f.verkoopteamFilter, seller: f.verkoperFilter });
    setCompanyFilter(scope.company);
    setVerkoopteamFilter(scope.team);
    setVerkoperFilter(scope.seller);
    setStatusFilter(Array.isArray(f.statusFilter) ? f.statusFilter : null);
    setActionFilter(f.actionFilter ?? 'all');
    setEscalatedFilter(f.escalatedFilter ?? 'all');
    setAgeFilter(f.ageFilter ?? 'all');
    setWdFilter(f.wdFilter ?? 'all');
    setAssigneeFilter(hdAlsLijst(f.assigneeFilter, 'behandelaar', alleBehandelaars));
    setWeekFilter(f.weekFilter === true);
    setTagFilter(hdAlsLijst(f.tagFilter, 'tag'));
    setQ(f.q ?? '');
    setPage(0);
  };

  // Export = de gefilterde, gesorteerde lijst (alle pagina's), zoals overal.
  const exportTickets = () => {
    const kolommen = [
      { header: 'Ref', get: (t) => t.ref },
      { header: 'Onderwerp', get: (t) => t.subject },
      { header: 'Klantnummer', get: (t) => t.customer?.reference || '' },
      { header: 'Segment', get: (t) => ticketSegment(t) },
      { header: 'Klant', get: (t) => t.customer?.name || '' },
      { header: 'Energielabel', get: (t) => companies[t.company_id] || t.company_id || '' },
      { header: 'Verkoper', get: (t) => t.customer?.reseller?.salesperson_name || '' },
      { header: 'Verkoopteam', get: (t) => t.customer?.reseller?.team_name || '' },
      { header: 'Status', get: (t) => t.stage_name || '' },
      { header: 'Tags', get: (t) => (Array.isArray(t.tags) ? t.tags.join(', ') : '') },
      // Welk sjabloon gebruikt werd is interne kennis: de kolom bestaat alleen voor admin/ET.
      ...(showBrand ? [{ header: 'Mailsjabloon', get: (t) => (Array.isArray(t.mail_templates) ? t.mail_templates.join(', ') : '') }] : []),
      { header: 'Actie vereist van', get: (t) => { const a = HD_ARB[t.action_required_by]; return a ? ((t.action_required_by === 'energy_together' && !showBrand) ? 'Support' : a.label) : ''; } },
      { header: 'Geëscaleerd', get: (t) => (t.escalated_to_et ? 'Ja' : 'Nee') },
      ...(toonBehandelaar ? [{ header: 'Behandelaar', get: (t) => (t.assignee && t.assignee.name) || '' }] : []),
      { header: 'Aangemaakt', get: (t) => hdFmt(t.create_date) },
      { header: 'Laatste activiteit', get: (t) => hdFmt(t.updated_at || t.write_date) },
      { header: 'Open (werkdagen)', get: (t) => (hdDagenOpen(t) ?? '') },
      { header: 'Heropend op', get: (t) => (isHdOpen(t) && t.open_sinds ? hdFmt(t.open_sinds) : '') },
      { header: 'Totaal open (werkdagen)', get: (t) => (t.open_werkdagen_totaal ?? '') },
      { header: 'Afgehandeld op', get: (t) => hdAfgehandeldOp(t) },
    ];
    exportRowsToCsv('helpdesk-tickets.csv', kolommen, gesorteerd);
  };

  // Kengetallen in drie compacte blokken; een rij aanklikken zet de bijbehorende
  // filter (nog eens klikken laat hem los).
  const actieLabel = (a) => (a === 'energy_together' ? (showBrand ? 'Energy Together' : 'Support') : a === 'partner' ? 'Partner' : a === 'customer' ? 'Klant' : 'Niet ingevuld');
  const enkeleStatus = (st) => Array.isArray(statusFilter) && statusFilter.length === 1 && statusFilter[0] === st;
  // Open statussen eerst (alfabetisch), de afgehandelde onderaan.
  const statusVolgorde = [...statusOptions.filter((s) => !isHdAfgehandeld(s)), ...statusOptions.filter(isHdAfgehandeld)];
  const statusRijen = statusVolgorde.map((st) => ({
    key: st, label: isHdAfgehandeld(st) ? `${st} in week` : st, value: kpi.perStatus[st] || 0,
    dot: HD_TOON_KLEUR[hdStageToon(st, isHdAfgehandeld(st))],
    actief: enkeleStatus(st) && (!isHdAfgehandeld(st) || weekFilter),
    klik: () => {
      const aan = Array.isArray(statusFilter) && statusFilter.length === 1 && statusFilter[0] === st && (!isHdAfgehandeld(st) || weekFilter);
      setStatusFilter(aan ? null : [st]);
      // De wachttijdfilters gelden enkel voor open tickets: een afgehandelde
      // status ernaast zou altijd een lege lijst geven. En "Afgehandeld in week"
      // toont precies wat het kengetal telt: de laatste 7 kalenderdagen.
      if (isHdAfgehandeld(st)) { setWdFilter('all'); setAgeFilter('all'); setWeekFilter(!aan); } else setWeekFilter(false);
    },
  }));
  const actieRijen = [...HD_ACTIE_VOLGORDE, ...Object.keys(kpi.perActie).filter((a) => !HD_ACTIE_VOLGORDE.includes(a))].map((a) => ({
    key: a, label: actieLabel(a), value: kpi.perActie[a] || 0,
    dot: HD_TOON_KLEUR[a === 'customer' ? 'sky' : a === 'partner' ? 'amber' : a === 'energy_together' ? 'violet' : 'slate'],
    actief: actionFilter === a,
    // Een leeg veld is geen filterwaarde in de dropdown → die rij is enkel een telling.
    klik: HD_ACTIE_VOLGORDE.includes(a) ? () => setActionFilter((v) => (v === a ? 'all' : a)) : undefined,
  }));
  const wdRijen = WERKDAG_BUCKETS.map((b, i) => ({
    key: b.key, label: b.label, value: kpi.perWd[b.key] || 0,
    dot: i >= 2 ? HD_KPI_KLEUR.oud : undefined,
    actief: wdFilter === b.key,
    klik: () => setWdFilter((v) => (v === b.key ? 'all' : b.key)),
  }));
  const escalatieVoet = (
    <button type="button" onClick={() => setEscalatedFilter((v) => (v === 'true' ? 'all' : 'true'))} title='Filter op "Geëscaleerd"'
      className={`mt-1.5 pt-1.5 border-t border-slate-700/60 w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-xs hover:bg-slate-700/50 ${escalatedFilter === 'true' ? 'ring-1 ring-lime-500/70 bg-slate-700/40' : ''}`}>
      <span className="flex items-center gap-1.5 text-slate-400"><span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: HD_KPI_KLEUR.escalatie }} />Waarvan geëscaleerd (open)</span>
      <span className="font-semibold tabular-nums" style={kpi.escalatie > 0 ? { color: HD_KPI_KLEUR.escalatie } : undefined}>{loading ? '…' : formatNumberRounded(kpi.escalatie)}</span>
    </button>
  );

  return <div className="space-y-6">
    <PageHeader title="Helpdesk" subtitle={showBrand ? 'Klantenservice-tickets — samenwerken met Energy Together' : 'Klantenservice-tickets'}>
      {/* Verversknop: enkel de ticketdata, nooit de pagina. De lijst controleert
          zichzelf ook om de halve minuut; de knop haalt de volledige set opnieuw. */}
      {!toonAnalyse && <HdVerversKnop onClick={() => loadTickets({ stil: raw.items.length > 0 })} bezig={verversen || loading} om={bijgewerktOm} titel="Tickets vernieuwen (gebeurt ook automatisch)" />}
      {!toonAnalyse && <ExportButton onExport={exportTickets} label="Exporteer tickets naar Excel" count={filtered.length} disabled={loading || filtered.length === 0} />}
      {!toonAnalyse && can('create') && <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm"><Plus size={16} />Nieuw ticket</button>}
    </PageHeader>
    {currentUser?.role === 'admin' && <HdOpenMailPosts token={token} onOpenTicket={openTicket} />}

    {/* Tab-menu (zoals Invoices/Orders): welke tabs er staan, hangt af van de
        subpagina's van deze gebruiker. Eén tab = de balk blijft staan als
        oriëntatie, net als elders. */}
    {geenHelpdeskTabs && <GeenSubpaginas module="Helpdesk" />}
    {helpdeskTabs.length > 1 && (
      <div className="-mx-1 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 px-1 w-max">
          {helpdeskTabs.map((t) => (
            <button key={t.id} onClick={() => setHelpdeskTab(t.id)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${helpdeskTab === t.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
    )}

    {toonAdmin && <HelpdeskAdminTab token={token} />}
    {toonKennisbank && <KennisbankPagina token={token} currentUser={currentUser} salesStructure={salesStructure} />}
    {toonAi && <HelpdeskAiPagina token={token} currentUser={currentUser} salesStructure={salesStructure} />}
    {toonAnalyse && !toonKennisbank && !toonAdmin && !toonAi && <HelpdeskAnalyse token={token} salesStructure={salesStructure} onOpenCustomer360={onOpenCustomer360} />}
    {!toonAnalyse && magHelpdeskTab('samenvatting') && (<>

    {/* Filters meteen onder de titel; de kengetallen eronder tellen over de
        cascade-scope (energielabel/team/verkoper), vóór de secundaire filters. */}
    <Card className="!p-4 relative z-30">
      <CollapsibleFilters active={hasActiveFilters}>
      <div className="flex flex-wrap items-end gap-3">
        {/* Cascade (zelfde als andere modules): energielabel → verkoopteam → verkoper */}
        <div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Energieleverancier</label><select value={companyFilter} onChange={(e) => onCompany(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${companyFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={companyOpts}>Alle</AllOption>{companyOpts.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        <div className="self-end"><FilterFavorites pageKey="helpdesk" filters={filterSnapshot} onApply={applyFavorite} /></div>
        <div><label className="block text-xs text-slate-400 mb-1">Status</label><HdMultiSelect options={statusOptions} selected={zichtbareStatussen} onChange={setStatusFilter} /></div>
        <div className="w-[132px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Actie vereist van</label><select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${actionFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="partner">Partner</option><option value="customer">Klant</option><option value="energy_together">{showBrand ? 'Energy Together' : 'Support'}</option></select></div>
        {toonBehandelaar && (
          <SearchableMultiSelect label="Behandelaar" values={assigneeFilter} onChange={setAssigneeFilter} options={[HD_BEH_MIJ, HD_BEH_GEEN, ...alleBehandelaars.map((u) => String(u.name || '')).filter(Boolean)]} />
        )}
        <div className="w-full sm:w-[280px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Zoeken</label><div className="relative"><Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Onderwerp, klant, ref…" className="w-full pl-7 pr-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" /></div></div>
        {/* Twee lagen (21-09-2026): er stonden altijd tien filters open. Wat je dagelijks
            gebruikt blijft staan; verkoopteam, verkoper, escalatie, tag en "open langer
            dan" zitten achter deze knop, die zegt hoeveel ervan actief zijn. Staat er
            één actief, dan klapt de laag vanzelf open: een filter die je niet ziet maar
            die wel werkt, is een valkuil. */}
        <button type="button" onClick={() => setMeerFilters((v) => !v)} aria-expanded={meerFiltersOpen}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs border ${aantalMeerActief ? 'border-lime-500 text-lime-300 bg-lime-500/10' : 'border-slate-600 text-slate-200 bg-slate-700 hover:bg-slate-600'}`}>
          <SlidersHorizontal size={13} />Meer filters{aantalMeerActief ? ` (${aantalMeerActief})` : ''}{meerFiltersOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs rounded"><X size={12} />Wissen</button>}
      </div>
      {meerFiltersOpen && (
        <div className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t border-slate-700/50">
        <div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Verkoopteam</label><select value={verkoopteamFilter} onChange={(e) => onTeam(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoopteamFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={teamOpts}>Alle</AllOption>{teamOpts.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
        <div className="w-[184px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Verkoper</label><select value={verkoperFilter} onChange={(e) => setVerkoperFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${verkoperFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><AllOption options={sellerOpts}>Alle</AllOption>{sellerOpts.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
        <div className="w-[132px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Escalatie</label><select value={escalatedFilter} onChange={(e) => setEscalatedFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${escalatedFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="true">Geëscaleerd</option><option value="false">Niet</option></select></div>
        <SearchableMultiSelect label="Tag" values={tagFilter} onChange={setTagFilter} options={[HD_TAG_GEEN, ...tagOptions]} />
        <div className="w-[132px] shrink-0"><label className="block text-xs text-slate-400 mb-1">Open langer dan</label><select value={ageFilter} onChange={(e) => setAgeFilter(e.target.value)} className={`w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${ageFilter !== 'all' ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`}><option value="all">Alle</option><option value="5">5 werkdagen</option><option value="10">10 werkdagen</option><option value="20">20 werkdagen</option><option value="40">40 werkdagen</option>{!['all', '5', '10', '20', '40'].includes(String(ageFilter)) && <option value={ageFilter}>{ageFilter} werkdagen</option>}</select></div>
        </div>
      )}
      </CollapsibleFilters>
      {raw.truncated && <div className="mt-2 text-xs text-amber-400">Toont de eerste 1000 tickets — verfijn met een energielabel voor een volledig overzicht.</div>}
      {raw.aanvullen && <div className="mt-2 text-xs text-slate-400 inline-flex items-center gap-1.5"><Spinner size={11} />De open tickets staan er; de afgehandelde tickets worden nog aangevuld…</div>}
    </Card>

    {/* Kengetallen binnen de cascade-scope (vóór de secundaire filters); klik =
        filter aan/uit. Samen inklapbaar; de stand is een voorkeur per gebruiker
        (useUiPref) en blijft dus over sessies en toestellen heen bewaard. */}
    <div>
      <button type="button" onClick={() => setKpiIngeklapt(!kpiIngeklapt)} aria-expanded={!kpiIngeklapt}
        className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-200 py-0.5"
        title={kpiIngeklapt ? 'Kengetallen tonen' : 'Kengetallen verbergen — je keuze wordt onthouden'}>
        {kpiIngeklapt ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
        <span className="font-semibold uppercase tracking-wide">Kengetallen</span>
        {kpiIngeklapt && <span className="text-slate-500">· {loading ? '…' : formatNumberRounded(raw.items.length)} tickets · {loading ? '…' : formatNumberRounded(kpi.open)} open</span>}
      </button>
      {/* Op een telefoon naast elkaar in een veegbare rij: onder elkaar namen de drie
          blokken een vol scherm in voor het eerste ticket in beeld kwam. */}
      {!kpiIngeklapt && (
        <div className="flex md:grid md:grid-cols-3 gap-2 mt-1 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none -mx-1 px-1 pb-1 md:mx-0 md:px-0 md:pb-0">
          <div className="min-w-[82%] sm:min-w-[300px] md:min-w-0 snap-start flex"><div className="flex-1 min-w-0"><HdKpiBlok titel={`Per status (${loading ? '…' : formatNumberRounded(raw.items.length)})`} rijen={statusRijen} loading={loading} voet={escalatieVoet} /></div></div>
          <div className="min-w-[82%] sm:min-w-[300px] md:min-w-0 snap-start flex"><div className="flex-1 min-w-0"><HdKpiBlok titel={`Actie vereist van (${loading ? '…' : formatNumberRounded(kpi.open)} open)`} rijen={actieRijen} loading={loading} /></div></div>
          <div className="min-w-[82%] sm:min-w-[300px] md:min-w-0 snap-start flex"><div className="flex-1 min-w-0"><HdKpiBlok titel="Open, wachttijd in werkdagen" rijen={wdRijen} loading={loading} /></div></div>
        </div>
      )}
    </div>


    {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}
    {loading ? <Card className="!p-3"><ListSkeleton rows={8} /></Card> : filtered.length === 0 ? (
      <EmptyState message={raw.items.length === 0 ? 'Geen tickets gevonden binnen je rechten.' : 'Geen tickets voor deze filters.'} />
    ) : (
      <Card className="!p-0 overflow-hidden">
        {/* Mobiel: compacte ticketkaarten i.p.v. de brede tabel. Zelfde data
            (pageItems) en dezelfde open-actie (openTicket); puur visueel. */}
        <div className="md:hidden p-3 space-y-2">
          {pageItems.map((t) => {
            const dagen = hdDagenOpen(t);
            return (
            <button key={t.id} onClick={() => openTicket(t.id)} className="w-full text-left rounded-xl border border-slate-700/50 bg-slate-800 p-3 transition-colors hover:bg-slate-700">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-100 truncate">{t.subject}{t.escalated_to_et && <ArrowUpCircle size={12} className="inline ml-1 text-purple-400" />}<HdBijlageClip t={t} /></div>
                  <div className="text-xs text-slate-400 truncate">{[t.customer?.name, companies[t.company_id] || t.company_id].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                <ChevronRight size={16} className="text-slate-400 shrink-0 mt-0.5" />
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {hdStageBadge(t)}
                  {hdTagPillen(t.tags)}
                  {hdArbBadge(t.action_required_by, showBrand)}
                  {dagen != null && <span className={`text-[11px] ${hdDoorlooptijdCls(t, dagen)}`} title={hdOpenUitleg(t)}>{dagen} wd {isHdOpen(t) ? (t.open_sinds ? 'open sinds heropening' : 'open') : 'open geweest'}</span>}
                  {toonBehandelaar && t.assignee && <span className="text-[11px] text-slate-400">· {t.assignee.name}</span>}
                </div>
                <span className="text-[11px] text-slate-400">{hdFmt(t.updated_at || t.write_date || t.create_date)}</span>
              </div>
            </button>
            );
          })}
        </div>
        <div className="overflow-auto hidden md:block md:max-h-[70vh]"><table className="w-full text-sm resp-card">
          <thead className="bg-slate-800 text-slate-400 text-xs sticky top-0 z-10"><tr>
            <SortHeader label="Ref" k="ref" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Onderwerp" k="subject" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Klantnr · segment" k="klantnr" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Klant" k="klant" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Label · verkoper" k="label" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Status" k="stage_name" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Tags" k="tags" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Actie vereist" k="action_required_by" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" />{toonBehandelaar && <SortHeader label="Behandelaar" k="behandelaar" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" />}<SortHeader label="Open · activiteit" k="leeftijd" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" /><SortHeader label="Aangemaakt" k="create_date" sortConfig={hdSort.sortConfig} onSort={hdSort.handleSort} thClass="px-3 py-2 font-medium" />
          </tr></thead>
          <tbody>
            {pageItems.map((t) => {
              const dagen = hdDagenOpen(t);
              return (
              <tr key={t.id} onClick={(e) => onRowOpen(e, t.id)} onAuxClick={(e) => onRowOpen(e, t.id)} onContextMenu={(e) => onC360Context(e, t.customer?.name, klantTarget(t.customer?.name, t.customer?.reference), null, showBrand ? ticketEtLinks(t) : null)} title="Klik om te openen · Ctrl/⌘+klik of middenklik = nieuwe tab · rechtsklik = Customer 360, Metering en (beheer) Lynx/Odoo" className="border-t border-slate-700/40 hover:bg-slate-800/50 cursor-pointer">
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{t.ref}</td>
                <td className="px-3 py-2 text-slate-200">{t.subject}{t.escalated_to_et && <ArrowUpCircle size={12} className="inline ml-1 text-purple-400" />}<HdBijlageClip t={t} /></td>
                <td className="px-3 py-2 text-xs whitespace-nowrap" title={t.customer_override ? `Klant gewijzigd in het portaal${t.customer_override.from_name || t.customer_override.from_ref ? ` (was ${t.customer_override.from_name || ''} ${t.customer_override.from_ref || ''})` : ''}` : undefined}>
                  <div className="font-mono text-slate-300">{t.customer?.reference || '—'}{t.customer_override && <span className="ml-1 text-amber-400" title="Gewijzigd in het portaal">*</span>}</div>
                  <div className="text-[11px] text-slate-500">{ticketSegment(t) || '—'}</div>
                </td>
                <td className="px-3 py-2 text-slate-300">{t.customer?.name || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-slate-300 whitespace-nowrap">{companies[t.company_id] || t.company_id}</div>
                  <div className="text-[11px] text-slate-500 truncate max-w-[150px]">{t.customer?.reseller?.salesperson_name || '—'}</div>
                </td>
                <td className="px-3 py-2">{hdStageBadge(t)}</td>
                <td className="px-3 py-2"><div className="flex flex-wrap items-center gap-1 max-w-[240px]">
                  {can('tags')
                    ? <HdTagEditor huidige={Array.isArray(t.tags) ? t.tags : []} tags={tagLijst} disabled={inlineBezig === t.id} onSave={(ids, namen) => wijzigTags(t, ids, namen)} magNieuw={currentUser?.role === 'admin'} onNieuweTag={nieuweTag} />
                    : (hdTagPillen(t.tags) || <span className="text-slate-500 text-xs">{t.tags == null ? '…' : '—'}</span>)}
                </div></td>
                {/* Kolom Mailsjabloon weggelaten (11-09-2026); het sjabloon blijft in de export en het ticket. */}
                <td className="px-3 py-2" onClick={stil} onAuxClick={stil}>
                  {can('status')
                    ? <select value={t.action_required_by || ''} disabled={inlineBezig === t.id} onChange={(e) => wijzigArb(t, e.target.value)} onMouseDown={stil} title="Actie vereist van wijzigen" className={inlineSelect}>
                        {!t.action_required_by && <option value="">—</option>}
                        <option value="partner">Partner</option>
                        <option value="customer">Klant</option>
                        <option value="energy_together">{showBrand ? 'Energy Together' : 'Support'}</option>
                      </select>
                    : hdArbBadge(t.action_required_by, showBrand)}
                </td>
                {/* Behandelaar: keuzelijst per ticket (hdBehandelaarOpties). Een partnerrol
                    wijzigt alleen bij "Actie vereist van" Partner of Klant; een interne
                    behandelaar heet voor hem "Support" en is geen keuze. */}
                {toonBehandelaar && <td className="px-3 py-2 text-xs whitespace-nowrap" onClick={stil} onAuxClick={stil}>
                  {magToewijzen && (showBrand || t.action_required_by === 'partner' || t.action_required_by === 'customer')
                    ? <select value={t.assignee ? String(t.assignee.id ?? 'x') : ''} disabled={inlineBezig === t.id} onChange={(e) => wijzigBehandelaar(t, e.target.value)} onMouseDown={stil} title="Behandelaar wijzigen" className={inlineSelect}>
                        <option value="">—</option>
                        {hdBehandelaarOpties(t, assignees, behExtern, companies[t.company_id] || '').map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                        {t.assignee && !hdBehandelaarOpties(t, assignees, behExtern, companies[t.company_id] || '').some((u) => u.id === t.assignee.id) && <option value={String(t.assignee.id ?? 'x')} disabled={t.assignee.id == null}>{t.assignee.name}</option>}
                      </select>
                    : (t.assignee ? <span className="text-slate-300">{t.assignee.name}</span> : <span className="text-slate-500">—</span>)}
                </td>}
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  <div className={`${hdDoorlooptijdCls(t, dagen)} inline-flex items-center gap-1`} title={hdOpenUitleg(t)}>{dagen == null ? '—' : `${dagen} wd${isHdOpen(t) ? '' : ' (dicht)'}`}{isHdOpen(t) && t.open_sinds && <RotateCcw size={11} className="shrink-0 w-3" aria-label="heropend" />}</div>
                  <div className="text-[11px] text-slate-500" title={hdFmt(t.updated_at || t.write_date)}>{hdFmt(t.updated_at || t.write_date)}</div>
                </td>
                <td className="px-3 py-2 text-slate-400 text-xs whitespace-nowrap">{hdFmt(t.create_date)}</td>
              </tr>
              );
            })}
          </tbody>
        </table></div>
      </Card>
    )}

    {!loading && filtered.length > PAGE && (
      <div className="flex items-center justify-between text-sm text-slate-400">
        <span>{filtered.length} tickets · pagina {page + 1}/{totalPages}</span>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-40"><ChevronLeft size={14} /></button>
          <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-40"><ChevronRight size={14} /></button>
        </div>
      </div>
    )}

    </>)}
    {c360Menu}
  </div>;
};

// ─── Optimise (platform-feedback) ──────────────────────────────────────────────
// Feedback OVER het partnerportaal zelf. Iedere gebruiker dient in en ziet enkel
// zijn eigen ingaves; alleen de admin ziet alles (met wie/wanneer/type/pagina).
// Waarden gespiegeld in backend/src/routes/feedback.js.
const FEEDBACK_TYPES = [
  { id: 'question', label: 'Vraag' },
  { id: 'bug', label: 'Bug' },
  { id: 'feature', label: 'Nieuwe functie' },
  { id: 'ux', label: 'Gebruikerservaring' },
];
const FEEDBACK_PRIORITIES = [
  { id: 'low', label: 'Laag' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'Hoog' },
  { id: 'urgent', label: 'Dringend' },
];
const FEEDBACK_STATUSES = [
  { id: 'open', label: 'Open' },
  { id: 'planned', label: 'Gepland' },
  { id: 'in_progress', label: 'In behandeling' },
  { id: 'done', label: 'Afgehandeld' },
  { id: 'declined', label: 'Afgewezen' },
];
const fbLabel = (list, id) => (list.find((x) => x.id === id) || {}).label || id || '—';
const fbFmt = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' }); } catch { return s; } };
const FB_TYPE_CLS = { question: 'bg-blue-500/20 text-blue-300 border-blue-500/40', bug: 'bg-red-500/20 text-red-300 border-red-500/40', feature: 'bg-lime-500/20 text-lime-300 border-lime-500/40', ux: 'bg-purple-500/20 text-purple-300 border-purple-500/40' };
const FB_PRIO_CLS = { low: 'bg-slate-600/40 text-slate-300 border-slate-500/40', medium: 'bg-blue-500/20 text-blue-300 border-blue-500/40', high: 'bg-amber-500/20 text-amber-300 border-amber-500/40', urgent: 'bg-red-500/20 text-red-300 border-red-500/40' };
const FB_STATUS_CLS = { open: 'bg-slate-600/40 text-slate-300 border-slate-500/40', planned: 'bg-blue-500/20 text-blue-300 border-blue-500/40', in_progress: 'bg-amber-500/20 text-amber-300 border-amber-500/40', done: 'bg-lime-500/20 text-lime-300 border-lime-500/40', declined: 'bg-red-500/20 text-red-300 border-red-500/40' };
const FbBadge = ({ cls, children }) => <span className={`px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${cls || ''}`}>{children}</span>;

// ── Gesprek op een feedback-item ────────────────────────────────────────────
// De beheerder reageert op een melding — meestal met een bijkomende vraag — en
// de indiener antwoordt in dezelfde draad. Eén component voor beide kanten: wie
// "jij" bent volgt uit `from_admin` van elk bericht, dus de bubbels staan bij de
// beheerder én bij de indiener aan de juiste kant.
//
// De naam van een beheerder wordt door de API al vervangen door "Platformteam"
// voor partnerrollen (merk-afscherming) — hier dus gewoon `author` tonen.

// Knop met het aantal berichten; een amberen telletje = nieuw voor jou.
const FbDraadKnop = ({ item, open, onClick }) => (
  <button type="button" onClick={onClick} title={open ? 'Gesprek sluiten' : 'Gesprek openen'}
    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs transition-colors ${open ? 'border-lime-500/40 bg-lime-500/10 text-lime-300' : 'border-slate-600/60 text-slate-300 hover:bg-slate-700/50'}`}>
    <MessageSquare size={13} className="shrink-0" />
    <span>{item.replies?.length || 0}</span>
    {item.unread > 0 && <span className="px-1.5 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold leading-4">{item.unread}</span>}
  </button>
);

// Bestanden uit een plak-actie (Ctrl+V met een schermafbeelding op het klembord)
// of een drop. Alleen echte bestanden; geplakte tekst valt gewoon in het veld.
const fbBestandenUitEvent = (e) => {
  const dt = e.clipboardData || e.dataTransfer;
  if (!dt) return [];
  const uit = [];
  for (const it of Array.from(dt.items || [])) {
    if (it.kind === 'file') { const f = it.getAsFile(); if (f) uit.push(f); }
  }
  if (!uit.length && dt.files) uit.push(...Array.from(dt.files));
  // Een geplakte afbeelding heet in elke browser "image.png" — geef hem een
  // tijdstempel, anders staan er straks vijf gelijknamige bijlagen.
  return uit.map((f) => (f.name && f.name !== 'image.png') ? f
    : new window.File([f], `schermafbeelding-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type }));
};

// Tekst met verwijzingen naar andere meldingen: "#12" wordt een link die naar die
// melding springt (als ze in de lijst staat). Handig als het platformteam twee
// meldingen samenvoegt: "zie #12". Buiten een lijst (geen onNaar) blijft het tekst.
const FB_VERWIJZING_RE = /(^|[^\w&])#(\d{1,7})\b/g;
const FbTekst = ({ tekst, onNaar, bestaat, className = '' }) => {
  const delen = [];
  const s = String(tekst || '');
  let last = 0; let k = 0;
  for (const m of s.matchAll(FB_VERWIJZING_RE)) {
    const start = m.index + m[1].length;
    delen.push(s.slice(last, start));
    const id = Number(m[2]);
    const kan = onNaar && (!bestaat || bestaat(id));
    delen.push(kan
      ? <button key={`v${k++}`} type="button" onClick={(e) => { e.stopPropagation(); onNaar(id); }} title={`Naar melding #${id}`} className="text-lime-300 hover:underline font-medium">#{id}</button>
      : <span key={`v${k++}`} title={onNaar ? 'Deze melding staat niet in je lijst' : undefined}>#{id}</span>);
    last = start + m[0].length - m[1].length;
  }
  delen.push(s.slice(last));
  return <span className={className}>{delen}</span>;
};

// Mag de lezer dit bericht nog bewerken/intrekken? Spiegel van magBewerken in
// backend/src/util/feedbackDraad.js — de server beslist, dit bepaalt enkel of de
// knoppen verschijnen.
const fbMagBewerken = (r, { isAdmin, editWindowMs, intrekken = false, nu = Date.now() }) => {
  if (!r) return false;
  if (r.kind === 'status') return isAdmin && intrekken;
  if (intrekken && isAdmin) return true;
  if (!r.mine) return false;
  const leeftijd = nu - new Date(r.created_at).getTime();
  return leeftijd >= 0 && leeftijd <= (editWindowMs || 15 * 60 * 1000);
};

// Gespreksdraad met een schrijfvak dat óók bijlagen aanneemt. Een vraag als
// "kun je tonen wat je ziet?" beantwoord je met een schermafbeelding, niet met
// een omschrijving: knop voor een opname van het platform, bestandskiezer,
// plakken (Ctrl+V) en slepen op het schrijfvak. De bijlagen hangen aan het
// bericht zelf en staan als voorbeeld in de bubbel. Verzenden mag zonder tekst
// als er een bijlage is.
//
// Drie soorten regels in de draad:
//   - bericht (bubbel links/rechts, wie "jij" bent volgt uit from_admin);
//   - statusregel ("Status gewijzigd naar Gepland") gecentreerd als systeemregel;
//   - interne notitie (amber, alleen beheerders — de API geeft ze een partnerrol
//     niet eens mee).
// Een eigen bericht kan kort na het versturen nog bewerkt of ingetrokken worden.
const FbDraad = ({ item, isAdmin, busy, draft, onDraft, files = [], onFiles, onSend, token, onDeleteFile, onError, verstuurd,
  kind = 'message', onKind, editWindowMs, onEdit, onDeleteReply, onNaar, bestaat }) => {
  const berichten = item.replies || [];
  const bestandRef = useRef(null);
  const [schermBezig, setSchermBezig] = useState(false);
  const [markeer, setMarkeer] = useState(null);   // afbeelding die gemarkeerd wordt { file, index }
  const [sleep, setSleep] = useState(false);
  const onderRef = useRef(null);
  // Welk eigen bericht staat open om te bewerken, en de tekst ervan.
  const [bewerkId, setBewerkId] = useState(null);
  const [bewerkTekst, setBewerkTekst] = useState('');
  // Elke minuut herberekenen: de knoppen Bewerken/Intrekken verdwijnen vanzelf
  // zodra het venster dicht is.
  const [, setTik] = useState(0);
  useEffect(() => { const t = setInterval(() => setTik((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  const nu = Date.now();
  // Nieuwe berichten in beeld brengen, anders leest niemand het antwoord onderaan.
  useEffect(() => { onderRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [berichten.length]);

  const voegToe = (lijst) => {
    const gekozen = Array.from(lijst || []);
    if (!gekozen.length) return;
    const ruimte = FB_MAX_FILES - files.length;
    const teGroot = gekozen.filter((f) => f.size > FB_MAX_FILE_MB * 1024 * 1024);
    const rest = gekozen.filter((f) => f.size <= FB_MAX_FILE_MB * 1024 * 1024);
    if (teGroot.length) onError?.(`Te groot (max ${FB_MAX_FILE_MB} MB): ${teGroot.map((f) => f.name).join(', ')}`);
    else if (rest.length > ruimte) onError?.(`Maximaal ${FB_MAX_FILES} bijlagen per bericht.`);
    if (ruimte > 0) onFiles([...files, ...rest.slice(0, ruimte)]);
  };
  // Markeringen op een afbeelding: meteen na een opname (index null = nieuw
  // toevoegen) of via "Markeren" op een bijlage in de lijst (index = vervangen).
  // De state `markeer` staat bovenaan de component (hooks vóór elke return).
  const markeerKlaar = (nieuw) => {
    const { file, index } = markeer;
    const f = nieuw || file;
    if (index == null) voegToe([f]); else onFiles(files.map((x, j) => (j === index ? f : x)));
    setMarkeer(null);
  };
  const schermafbeelding = async () => {
    setSchermBezig(true);
    try {
      await new Promise((r) => setTimeout(r, 250));
      const f = await maakSchermafbeelding();
      setMarkeer({ file: f, index: null });
    } catch (e) {
      if (!(e && (e.name === 'NotAllowedError' || e.name === 'AbortError'))) onError?.(e.message || 'Schermafbeelding mislukt.');
    } finally { setSchermBezig(false); }
  };
  const kanVersturen = !busy && ((draft || '').trim() || files.length > 0);
  const intern = isAdmin && kind === 'internal';
  const knop = 'flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg';
  const kleinKnop = 'text-[11px] text-slate-400 hover:text-lime-300';

  const startBewerken = (r) => { setBewerkId(r.id); setBewerkTekst(r.message || ''); };
  const bewaarBewerking = async () => {
    const ok = await onEdit?.(bewerkId, bewerkTekst);
    if (ok !== false) setBewerkId(null);
  };

  return (
    <div className="space-y-2">
      {berichten.length === 0 ? (
        <p className="text-xs text-slate-500">{isAdmin ? 'Nog geen reacties. Stel hier een bijkomende vraag aan de indiener — die ziet ze op zijn Optimise-pagina en krijgt er een mail over.' : 'Nog geen reacties. Reageert het platformteam op je melding, dan lees je het hier en krijg je een mail.'}</p>
      ) : berichten.map((r) => {
        // Statusregel: een feit, geen bubbel. Gecentreerd, zonder kant.
        if (r.kind === 'status') {
          return (
            <div key={r.id} className="flex items-center gap-2 text-[11px] text-slate-400 justify-center py-0.5">
              <span className="h-px flex-1 bg-slate-700/60" />
              <span className="inline-flex flex-wrap items-center justify-center gap-1.5 text-center">
                <FbBadge cls={FB_STATUS_CLS[r.message]}>{fbLabel(FEEDBACK_STATUSES, r.message)}</FbBadge>
                <span>door {r.author} · {fbFmt(r.created_at)}</span>
                {fbMagBewerken(r, { isAdmin, editWindowMs, intrekken: true, nu }) && onDeleteReply
                  && <button type="button" onClick={() => onDeleteReply(r)} title="Regel verwijderen" className="text-slate-500 hover:text-red-400"><X size={11} /></button>}
              </span>
              <span className="h-px flex-1 bg-slate-700/60" />
            </div>
          );
        }
        const vanMij = r.from_admin === isAdmin;
        const isIntern = r.kind === 'internal';
        const kader = isIntern ? 'ml-auto bg-amber-500/10 border border-amber-500/40'
          : vanMij ? 'ml-auto bg-lime-500/10 border border-lime-500/30' : 'bg-slate-700/40 border border-slate-600/40';
        const magBewerk = fbMagBewerken(r, { isAdmin, editWindowMs, nu }) && onEdit;
        const magIntrek = fbMagBewerken(r, { isAdmin, editWindowMs, intrekken: true, nu }) && onDeleteReply;
        return (
          <div key={r.id} className={`max-w-[85%] rounded-lg px-3 py-2 ${kader}`}>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400 mb-0.5">
              {isIntern && <span className="px-1.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-semibold uppercase tracking-wide">Intern</span>}
              <span className="font-medium text-slate-300">{r.author}</span><span>{fbFmt(r.created_at)}</span>
              {r.edited_at && <span className="italic" title={`Bewerkt op ${fbFmt(r.edited_at)}`}>(bewerkt)</span>}
              {bewerkId !== r.id && (magBewerk || magIntrek) && <span className="ml-auto flex items-center gap-2">
                {magBewerk && <button type="button" onClick={() => startBewerken(r)} className={kleinKnop}>Bewerken</button>}
                {magIntrek && <button type="button" onClick={() => onDeleteReply(r)} className="text-[11px] text-slate-400 hover:text-red-400">Intrekken</button>}
              </span>}
            </div>
            {bewerkId === r.id ? (
              <div className="space-y-1.5">
                <textarea value={bewerkTekst} onChange={(e) => setBewerkTekst(e.target.value)} rows={3} maxLength={4000} autoFocus
                  onKeyDown={(e) => { if (e.key === 'Escape') setBewerkId(null); if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); bewaarBewerking(); } }}
                  className="w-full px-2 py-1.5 bg-slate-800/60 border border-slate-600/50 rounded text-sm text-slate-100" />
                <div className="flex items-center gap-2">
                  <button type="button" onClick={bewaarBewerking} disabled={busy || (!bewerkTekst.trim() && !(r.files?.length))} className="px-2.5 py-1 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-slate-900 text-xs font-semibold rounded">Opslaan</button>
                  <button type="button" onClick={() => setBewerkId(null)} className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs rounded">Annuleren</button>
                </div>
              </div>
            ) : (r.message ? <p className="text-sm text-slate-200 whitespace-pre-wrap"><FbTekst tekst={r.message} onNaar={onNaar} bestaat={bestaat} /></p> : null)}
            {r.files?.length > 0 && <div className={r.message ? 'mt-1.5' : ''}>
              <FbAttachments files={r.files} token={token} onError={onError} onDelete={vanMij && onDeleteFile ? (f) => onDeleteFile(r, f) : undefined} />
            </div>}
          </div>
        );
      })}
      <div ref={onderRef} />
      {verstuurd && <div className="text-xs text-lime-300 flex items-center gap-1.5"><Check size={13} />{verstuurd}</div>}
      <div className={`rounded-lg border ${sleep ? 'border-lime-400 bg-lime-500/5' : intern ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-600/50 bg-slate-700/30'} p-2 space-y-2`}
        onDragOver={(e) => { e.preventDefault(); setSleep(true); }} onDragLeave={() => setSleep(false)}
        onDrop={(e) => { e.preventDefault(); setSleep(false); voegToe(fbBestandenUitEvent(e)); }}>
        {/* Beheerder kiest: bericht aan de indiener, of een interne notitie die
            alleen beheerders zien (zoals in de Helpdesk). */}
        {isAdmin && onKind && <div className="flex items-center gap-1 text-xs">
          {[['message', 'Bericht aan de indiener'], ['internal', 'Interne notitie']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => onKind(k)}
              className={`px-2.5 py-1 rounded-full border transition-colors ${kind === k ? (k === 'internal' ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'bg-lime-500/20 border-lime-500/50 text-lime-200') : 'border-slate-600/60 text-slate-400 hover:bg-slate-700/50'}`}>
              {l}
            </button>
          ))}
          {intern && <span className="text-[11px] text-amber-300/80 ml-1">Alleen zichtbaar voor beheerders — de indiener krijgt hier geen mail over.</span>}
        </div>}
        <textarea value={draft || ''} onChange={(e) => onDraft(e.target.value)} rows={2} maxLength={4000}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (kanVersturen) onSend(); } }}
          onPaste={(e) => { const b = fbBestandenUitEvent(e); if (b.length) { e.preventDefault(); voegToe(b); } }}
          placeholder={intern ? 'Interne notitie voor beheerders… (Ctrl+Enter verstuurt)' : isAdmin ? 'Stel een bijkomende vraag of geef een stand van zaken… (Ctrl+Enter verstuurt, Ctrl+V plakt een schermafbeelding, #12 verwijst naar een melding)' : 'Antwoord het platformteam… (Ctrl+Enter verstuurt, Ctrl+V plakt een schermafbeelding)'}
          className="w-full px-3 py-2 bg-slate-800/60 border border-slate-600/50 rounded-lg text-sm text-slate-100" />
        {files.length > 0 && <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 bg-slate-700/60 border border-slate-600/50 rounded text-xs text-slate-200 max-w-[240px]">
              {f.type?.startsWith('image/') ? <FbMiniVoorbeeld file={f} /> : <File size={11} className="text-slate-400 shrink-0" />}
              <span className="truncate" title={f.name}>{f.name}</span>
              <span className="text-slate-500 shrink-0">{fbFileSize(f.size)}</span>
              {f.type?.startsWith('image/') && <button type="button" onClick={() => setMarkeer({ file: f, index: i })} title="Markeringen aanbrengen" className="text-slate-500 hover:text-lime-300 shrink-0"><Pencil size={12} /></button>}
              <button type="button" onClick={() => onFiles(files.filter((_, j) => j !== i))} title="Verwijderen" className="text-slate-500 hover:text-red-400 shrink-0"><X size={12} /></button>
            </span>
          ))}
        </div>}
        {markeer && <SchermMarkeerVenster file={markeer.file} onKlaar={markeerKlaar} onSluiten={() => setMarkeer(null)} />}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={schermafbeelding} disabled={files.length >= FB_MAX_FILES || schermBezig || busy} className={knop} title="Maak een opname van het platform en voeg die als bijlage toe">
            <Camera size={13} />{schermBezig ? 'Bezig…' : 'Schermafbeelding'}
          </button>
          <button type="button" onClick={() => bestandRef.current?.click()} disabled={files.length >= FB_MAX_FILES || busy} className={knop} title={`Afbeelding of PDF toevoegen (max ${FB_MAX_FILES} × ${FB_MAX_FILE_MB} MB)`}>
            <Paperclip size={13} />Bestand
          </button>
          <input ref={bestandRef} type="file" multiple accept={FB_ACCEPT} onChange={(e) => { voegToe(e.target.files); e.target.value = ''; }} className="hidden" />
          <span className="text-[11px] text-slate-500 hidden sm:inline">of sleep/plak een schermafbeelding in het vak</span>
          <button type="button" onClick={onSend} disabled={!kanVersturen}
            className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-50 font-semibold rounded-lg text-sm shrink-0 ${intern ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' : 'bg-lime-500 hover:bg-lime-400 text-slate-900'}`}>
            {intern ? <StickyNote size={14} /> : <Send size={14} />}{busy ? 'Bezig…' : intern ? 'Notitie bewaren' : 'Versturen'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Miniatuur van een nog niet verstuurde afbeelding (object-URL, opgeruimd bij unmount).
const FbMiniVoorbeeld = ({ file }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => { const u = URL.createObjectURL(file); setUrl(u); return () => URL.revokeObjectURL(u); }, [file]);
  return url ? <img src={url} alt="" className="h-5 w-5 object-cover rounded shrink-0" /> : <File size={11} className="text-slate-400 shrink-0" />;
};

// Bijlagen bij een feedback-item — grenzen gespiegeld in backend/src/routes/feedback.js.
const FB_MAX_FILES = 5;
const FB_MAX_FILE_MB = 10;
const FB_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';
const fbFileSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Lijst met de reeds opgeslagen bijlagen van één feedback-item. Openen kan niet
// met een gewone <a href>: de API wil een Authorization-header, dus halen we het
// bestand op en openen we een blob-URL (zelfde aanpak als bij Learning).
const fbOpenBijlage = async (f, token, onError) => {
  try {
    const r = await fetch(`/api/feedback/files/${f.id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || 'Openen mislukt'); }
    const url = URL.createObjectURL(await r.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { onError?.(e.message); }
};

// Voorbeeld van een opgeslagen afbeelding, in de bubbel zelf: een schermafbeelding
// wil je zíen, niet eerst openen. Opgehaald met de Authorization-header (een
// gewone <img src> kan die niet meesturen), object-URL opgeruimd bij unmount.
const FbBijlageBeeld = ({ file, token, onOpen }) => {
  const [url, setUrl] = useState(null);
  const [mislukt, setMislukt] = useState(false);
  useEffect(() => {
    let u = null; let weg = false;
    fetch(`/api/feedback/files/${file.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error('x'); return r.blob(); })
      .then((b) => { if (weg) return; u = URL.createObjectURL(b); setUrl(u); })
      .catch(() => { if (!weg) setMislukt(true); });
    return () => { weg = true; if (u) URL.revokeObjectURL(u); };
  }, [file.id, token]);
  if (mislukt) return null;
  return (
    <button type="button" onClick={onOpen} title={`${file.original_name} · ${fbFileSize(file.size_bytes)} — klik om te openen`}
      className="block rounded-lg overflow-hidden border border-slate-600/50 bg-slate-900/40 hover:border-lime-400/60 transition-colors">
      {url ? <img src={url} alt={file.original_name} className="max-h-40 max-w-full object-contain" /> : <div className="h-20 w-32 animate-pulse bg-slate-700/40" />}
    </button>
  );
};

const FbAttachments = ({ files = [], token, onDelete, onError, compact = false }) => {
  if (!files.length) return compact ? <span className="text-xs text-slate-600">—</span> : null;
  const open = (f) => fbOpenBijlage(f, token, onError);
  const isBeeld = (f) => !compact && /^image\//.test(f.mime_type || '');
  const beelden = files.filter(isBeeld);
  const overige = files.filter((f) => !isBeeld(f));
  return (
    <div className="space-y-1.5">
      {beelden.length > 0 && <div className="flex flex-wrap gap-2">
        {beelden.map((f) => (
          <div key={f.id} className="relative group">
            <FbBijlageBeeld file={f} token={token} onOpen={() => open(f)} />
            <div className="flex items-center gap-1 mt-0.5 text-[11px] text-slate-400 max-w-[220px]">
              <span className="truncate" title={f.original_name}>{f.original_name}</span>
              <span className="shrink-0 text-slate-500">{fbFileSize(f.size_bytes)}</span>
              {onDelete && <button type="button" onClick={() => onDelete(f)} title="Bijlage verwijderen" className="text-slate-500 hover:text-red-400 shrink-0"><X size={12} /></button>}
            </div>
          </div>
        ))}
      </div>}
      {overige.length > 0 && <div className="flex flex-wrap gap-1.5">
        {overige.map((f) => (
          <span key={f.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-slate-700/50 border border-slate-600/50 rounded text-xs text-slate-200 max-w-[220px]">
            <Paperclip size={11} className="text-slate-400 shrink-0" />
            <button type="button" onClick={() => open(f)} title={`${f.original_name} · ${fbFileSize(f.size_bytes)}`} className="truncate hover:text-lime-300 hover:underline">{f.original_name}</button>
            {onDelete && <button type="button" onClick={() => onDelete(f)} title="Bijlage verwijderen" className="text-slate-500 hover:text-red-400 shrink-0"><X size={12} /></button>}
          </span>
        ))}
      </div>}
    </div>
  );
};

// ── Feedback melden vanaf ELKE pagina ───────────────────────────────────────
// Naast elke paginatitel staat een Optimise-knopje. Daarachter zit hetzelfde
// formulier als op de Optimise-pagina (pagina/module, type, belangrijkheid,
// bericht, bijlagen) met twee toevoegingen:
//   • de module staat al ingevuld én het EXACTE pad, de openstaande subtabs, de
//     rol, het scherm, de browser en de laadtijden gaan automatisch mee als
//     technische context — dat is wat een bugmelding bruikbaar maakt;
//   • één knop maakt een schermafbeelding van het platform en hangt die als
//     bijlage aan de melding.
// De context is zichtbaar vóór het versturen: wat meegaat, mag geen verrassing zijn.

// Pagina-keuzelijst, gedeeld met de Optimise-pagina. Een gebruiker meldt alleen
// iets over pagina's die hij zelf ziet: een module die niet in zijn menu staat
// kan hij niet beoordelen, en de lijst zou anders verklappen welke modules er
// nog meer bestaan. De gating spiegelt daarom die van het menu —
// visibleNavItems voor de content-modules, canViewUsers/canSeeAuditLog/isAdmin
// voor de beheerpagina's. 'Algemeen' en 'Optimise' staan er altijd bij: de
// Optimise-module zelf is platform-breed (NAV_ITEMS `always: true`).
function feedbackPaginaOpties(user) {
  const rol = user?.role || null;
  const isAdmin = rol === 'admin';
  const modules = new Set(user?.modules || []);
  const magContent = (id) => isAdmin || modules.has(id);
  const magBeheer = (id) => {
    if (isAdmin) return true;
    // ET Dossierbeheerder is de enige andere rol met Gebruikers + Audit Log;
    // Branding en Algemene instellingen blijven admin-only.
    if (id === 'users' || id === 'audit-log') return rol === 'et_dossier';
    return false;
  };
  return [
    'Algemeen',
    ...MODULE_CATALOG.filter((m) => magContent(m.id)).map((m) => m.label),
    'Optimise',
    ...ADMIN_MODULE_CATALOG.filter((m) => magBeheer(m.id)).map((m) => m.label),
  ];
}
const feedbackModuleLabel = (id) =>
  (MODULE_CATALOG.find((m) => m.id === id) || ADMIN_MODULE_CATALOG.find((m) => m.id === id) || {}).label
  || (id === 'optimise' ? 'Optimise' : 'Algemeen');

// Alle *Tab-sleutels uit het sessiegeheugen: precies de subpagina's waarop de
// gebruiker stond (ordersTab, c360Tab…), zie actieveSubpaginas in sessieOpslag.js.
const fbGetal = (v) => (typeof v === 'number' && isFinite(v) && v > 0 ? Math.round(v) : null);

function verzamelFeedbackContext({ moduleId, moduleLabel, user }) {
  let nav = null;
  try { nav = performance.getEntriesByType('navigation')[0] || null; } catch { /* oude browser */ }
  let thema = 'light';
  try { thema = localStorage.getItem('uiTheme') === 'dark' ? 'dark' : 'light'; } catch { /* private mode */ }
  return {
    pad: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    module: { id: moduleId || null, label: moduleLabel || null },
    subpaginas: actieveSubpaginas(),
    gebruiker: {
      rol: user?.role || null,
      energielabel: user?.energyLabel || null,
      verkoopteam: user?.salesTeam || null,
      verkoper: user?.sellerName || null,
    },
    scherm: {
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      scherm: `${window.screen?.width || '?'}×${window.screen?.height || '?'}`,
      pixelratio: window.devicePixelRatio || 1,
      thema,
    },
    browser: {
      userAgent: String(navigator.userAgent || '').slice(0, 300),
      taal: navigator.language || null,
      online: navigator.onLine !== false,
    },
    tijd: {
      lokaal: new Date().toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'medium' }),
      iso: new Date().toISOString(),
      tijdzone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } })(),
    },
    timings: {
      domGereedMs: fbGetal(nav?.domContentLoadedEventEnd),
      paginaLaadMs: fbGetal(nav?.loadEventEnd),
      appOpenMs: fbGetal(typeof performance !== 'undefined' ? performance.now() : null),
    },
  };
}

// Schermafbeelding van het platform. De browser vraagt zélf welk venster of
// tabblad gedeeld wordt — dat kan niet buiten de gebruiker om, en dat hoort ook
// zo. `preferCurrentTab` zet in Chrome het huidige tabblad bovenaan die keuze.
// Het feedbackvenster staat tijdens de opname verborgen, anders levert dit een
// foto van het formulier op i.p.v. van het probleem erachter.
async function maakSchermafbeelding() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    throw new Error('Deze browser kan geen schermafbeelding maken — voeg er handmatig een toe.');
  }
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: 'browser' }, audio: false, preferCurrentTab: true });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();
    // Twee frames wachten: het eerste is soms nog zwart.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const b = video.videoWidth;
    const h = video.videoHeight;
    if (!b || !h) throw new Error('Geen beeld ontvangen.');
    // Terugschalen naar maximaal 1920 px breed: een 4K-opname haalt anders zo de
    // bijlagelimiet van 10 MB, en scherper dan dit hoeft een bugmelding niet.
    const schaal = Math.min(1, 1920 / b);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(b * schaal);
    canvas.height = Math.round(h * schaal);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    video.pause();
    video.srcObject = null;
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
    if (!blob) throw new Error('Kon de afbeelding niet opslaan.');
    const stempel = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    // window.File, niet File: die naam is hier het lucide-icoon (import bovenaan) en
    // "File is not a constructor" liet de schermafbeelding stil mislukken.
    return new window.File([blob], `schermafbeelding-${stempel}.png`, { type: 'image/png' });
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

// Bijlagen horen aan een BESTAAND feedback-item (ze hangen aan een id), dus dit
// draait pas na het aanmaken. Een mislukte upload laat de melding zelf staan.
async function uploadFeedbackBijlagen(feedbackId, files, token) {
  const mislukt = [];
  for (const file of files || []) {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`/api/feedback/${feedbackId}/files`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || `HTTP ${r.status}`); }
    } catch (e) { mislukt.push(`${file.name}: ${e.message}`); }
  }
  return mislukt;
}

const FeedbackDialog = ({ open, onClose, moduleId, moduleLabel }) => {
  const { token, user } = useAuth();
  // Alleen de pagina's die deze gebruiker mag zien (zie feedbackPaginaOpties).
  const paginaOpties = useMemo(() => feedbackPaginaOpties(user), [user]);
  const [form, setForm] = useState({ module: 'Algemeen', type: 'question', priority: 'medium', message: '' });
  const [bijlagen, setBijlagen] = useState([]);
  const [context, setContext] = useState(null);
  const [toonContext, setToonContext] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [schermBezig, setSchermBezig] = useState(false);
  const [markeer, setMarkeer] = useState(null);   // afbeelding die gemarkeerd wordt { file, index }
  const [fout, setFout] = useState(null);
  const bestandRef = useRef(null);

  // Bij het openen: verse context en een leeg formulier met de huidige module.
  useEffect(() => {
    if (!open) return;
    setForm({ module: paginaOpties.includes(moduleLabel) ? moduleLabel : 'Algemeen', type: 'question', priority: 'medium', message: '' });
    setBijlagen([]); setFout(null); setToonContext(false);
    setContext(verzamelFeedbackContext({ moduleId, moduleLabel, user }));
  }, [open, moduleId, moduleLabel, user, paginaOpties]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const kiesBestanden = (lijst) => {
    const gekozen = Array.from(lijst || []);
    const ruimte = FB_MAX_FILES - bijlagen.length;
    const teGroot = gekozen.filter((f) => f.size > FB_MAX_FILE_MB * 1024 * 1024);
    const rest = gekozen.filter((f) => f.size <= FB_MAX_FILE_MB * 1024 * 1024);
    if (teGroot.length) setFout(`Te groot (max ${FB_MAX_FILE_MB} MB): ${teGroot.map((f) => f.name).join(', ')}`);
    else if (rest.length > ruimte) setFout(`Maximaal ${FB_MAX_FILES} bijlagen per melding.`);
    setBijlagen((cur) => [...cur, ...rest.slice(0, ruimte)]);
  };

  // Markeringen op een afbeelding: meteen na een opname (index null = nieuw
  // toevoegen) of via "Markeren" op een bijlage in de lijst (index = vervangen).
  // De state `markeer` staat bovenaan de component (hooks vóór elke return).
  const markeerKlaar = (nieuw) => {
    const { file, index } = markeer;
    const f = nieuw || file;
    setBijlagen((cur) => (index == null ? (cur.length >= FB_MAX_FILES ? cur : [...cur, f]) : cur.map((x, j) => (j === index ? f : x))));
    setMarkeer(null);
  };
  const schermafbeelding = async () => {
    setFout(null); setSchermBezig(true);
    try {
      // Even wachten zodat het (nu verborgen) venster echt weg is uit beeld.
      await new Promise((r) => setTimeout(r, 250));
      const f = await maakSchermafbeelding();
      setMarkeer({ file: f, index: null });
    } catch (e) {
      // Afgebroken door de gebruiker is geen fout om over te klagen.
      if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) setFout(null);
      else setFout(e.message || 'Schermafbeelding mislukt.');
    } finally { setSchermBezig(false); }
  };

  const versturen = async () => {
    if (!form.message.trim()) { setFout('Schrijf eerst je feedback.'); return; }
    setBezig(true); setFout(null);
    try {
      const r = await fetch('/api/feedback', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, message: form.message.trim(), context }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || `HTTP ${r.status}`);
      const mislukt = bijlagen.length ? await uploadFeedbackBijlagen(b.feedback?.id, bijlagen, token) : [];
      if (mislukt.length) toast.error(`Melding opgeslagen, maar ${mislukt.length} bijlage(n) niet: ${mislukt.join(' · ')}`);
      else toast.success('Bedankt! Je melding staat genoteerd bij het platformteam.');
      onClose();
    } catch (e) {
      setFout(e.message || 'Versturen mislukt.');
    } finally { setBezig(false); }
  };

  const veld = 'w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100';
  return ReactDOM.createPortal(
    // Tijdens het maken van een schermafbeelding verbergen we het venster (niet
    // unmounten — dan zou het formulier leeglopen).
    <div className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[9600] flex items-start justify-center p-3 sm:p-6 overflow-y-auto ${schermBezig ? 'invisible' : ''}`} onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-700/70">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-slate-100 flex items-center gap-2"><Lightbulb size={16} className="text-lime-400" />Feedback over deze pagina</h3>
            <p className="text-xs text-slate-400 mt-0.5">Pagina en technische gegevens gaan automatisch mee.</p>
          </div>
          <button onClick={onClose} aria-label="Sluiten" className="shrink-0 p-1 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-slate-700/50"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {fout && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{fout}</div>}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Pagina / module</label>
              <select value={form.module} onChange={(e) => set('module', e.target.value)} className={veld}>
                {paginaOpties.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Type</label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)} className={veld}>
                {FEEDBACK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Belangrijkheid</label>
              <select value={form.priority} onChange={(e) => set('priority', e.target.value)} className={veld}>
                {FEEDBACK_PRIORITIES.map((pr) => <option key={pr.id} value={pr.id}>{pr.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Je feedback</label>
            <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4} maxLength={4000} autoFocus
              placeholder="Beschrijf je opmerking, idee of het probleem zo concreet mogelijk…" className={veld} />
            <div className="text-xs text-slate-500 mt-1">{form.message.length}/4000</div>
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Bijlagen <span className="text-slate-500">(max {FB_MAX_FILES} × {FB_MAX_FILE_MB} MB)</span></label>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={schermafbeelding} disabled={bijlagen.length >= FB_MAX_FILES || schermBezig}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg">
                <Camera size={13} />{schermBezig ? 'Bezig…' : 'Schermafbeelding toevoegen'}
              </button>
              <button type="button" onClick={() => bestandRef.current?.click()} disabled={bijlagen.length >= FB_MAX_FILES}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg">
                <Paperclip size={13} />Bestand kiezen
              </button>
              <input ref={bestandRef} type="file" multiple accept={FB_ACCEPT} onChange={(e) => { kiesBestanden(e.target.files); e.target.value = ''; }} className="hidden" />
              {bijlagen.map((f, i) => (
                <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 bg-slate-700/50 border border-slate-600/50 rounded text-xs text-slate-200 max-w-[240px]">
                  {f.type?.startsWith('image/') ? <FbMiniVoorbeeld file={f} /> : <File size={11} className="text-slate-400 shrink-0" />}
                  <span className="truncate" title={f.name}>{f.name}</span>
                  <span className="text-slate-500 shrink-0">{fbFileSize(f.size)}</span>
                  {f.type?.startsWith('image/') && <button type="button" onClick={() => setMarkeer({ file: f, index: i })} title="Markeringen aanbrengen" className="text-slate-500 hover:text-lime-300 shrink-0"><Pencil size={12} /></button>}
                  <button type="button" onClick={() => setBijlagen((cur) => cur.filter((_, j) => j !== i))} title="Verwijderen" className="text-slate-500 hover:text-red-400 shrink-0"><X size={12} /></button>
                </span>
              ))}
              {markeer && <SchermMarkeerVenster file={markeer.file} onKlaar={markeerKlaar} onSluiten={() => setMarkeer(null)} />}
            </div>
          </div>

          {/* Wat er automatisch meegaat — inzichtelijk vóór het versturen. */}
          <div className="border border-slate-700/50 rounded-lg">
            <button type="button" onClick={() => setToonContext((v) => !v)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800/60 rounded-lg">
              <span className="flex items-center gap-1.5"><Wrench size={12} className="text-slate-500" />Technische context — {context?.module?.label || 'pagina'} · {context?.pad}</span>
              <ChevronDown size={14} className={`shrink-0 transition-transform ${toonContext ? 'rotate-180' : ''}`} />
            </button>
            {toonContext && (
              <pre className="px-3 pb-3 text-[11px] leading-snug text-slate-400 overflow-x-auto max-h-56 overflow-y-auto whitespace-pre-wrap break-all">{JSON.stringify(context, null, 2)}</pre>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-700/70">
          <button onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm">Annuleren</button>
          <button onClick={versturen} disabled={bezig || !form.message.trim()} className="flex items-center gap-2 px-5 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm disabled:opacity-50">
            <Send size={15} />{bezig ? 'Versturen…' : 'Versturen'}
          </button>
        </div>
      </div>
    </div>, document.body);
};

// Het knopje zelf — PageHeader rendert dit naast élke paginatitel, dus geen enkele
// pagina hoeft er iets voor te doen. De module komt uit PageDocsContext (die de
// app-root al vult met de actieve pagina).
const FeedbackKnop = () => {
  const ctx = React.useContext(PageDocsContext);
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const moduleId = ctx?.moduleId || null;
  if (!token) return null;
  return <>
    <DocsIconButton icon={Lightbulb} tone="feedback" label="Feedback of probleem melden over deze pagina" onClick={() => setOpen(true)} />
    <FeedbackDialog open={open} onClose={() => setOpen(false)} moduleId={moduleId} moduleLabel={feedbackModuleLabel(moduleId)} />
  </>;
};

// Melding uit de URL (/?page=optimise&fb=12): de link in de mail "het platformteam
// reageerde op je melding" opent meteen dát gesprek. Eén keer gelezen, daarna uit
// de adresbalk gehaald zodat een refresh niet opnieuw springt. Op MODULENIVEAU gelezen:
// de pagina wordt na het welkomstscherm opnieuw gemount, en dan is de parameter
// al weg — de waarde staat daarom klaar tot de eerste OptimisePage ze opneemt.
const fbUitUrl = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    const v = parseInt(p.get('fb') || '', 10);
    if (!Number.isInteger(v)) return null;
    p.delete('fb');
    const rest = p.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    return v;
  } catch { return null; }
};
let fbDeepLink = fbUitUrl();
// Pas verbruikt op het moment van springen: een OptimisePage die vóór het einde van
// het laden weer verdwijnt (welkomstscherm), mag de sprong niet opsouperen.

// ─── Revenue Calculator (11-09-2026; scenario's + 5 jaar 24-09-2026) ─────────
// De reseller-menukaart "vanaf 01/01/2026" als interactieve rekentool én demo
// voor kandidaat-partners. Drie scenario's (worst / base / best case) met elk hun
// eigen globale parameters (marktprijzen, margeaandeel, vaste kost per maand,
// groei en churn) en hun eigen gele velden per productlijn; de opbrengst wordt
// vijf jaar vooruit berekend uit een EAN-bestand dat groeit (instroom) en
// krimpt (churn). De vaste kostopbouw komt uit src/menukaart.js — dezelfde bron
// als de Revenue-berekening en het Menukaart-paneel. Rekenlogica (getest):
// src/revenueCalculator.js. De drie cases worden per gebruiker bewaard (ui-prefs).
const RC_FMT_EUR = (n) => `€ ${Number(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('nl-BE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const RC_FMT_EUR0 = (n) => `€ ${Math.round(Number(n) || 0).toLocaleString('nl-BE')}`;
const RC_FMT_NUM = (n) => Number(n || 0).toLocaleString('nl-BE', { maximumFractionDigits: 2 });
const RC_FMT_PCT = (n) => `${Number(n || 0).toLocaleString('nl-BE', { maximumFractionDigits: 1 })} %`;
// Vaste reekskleur per scenario en per segment. De segmenten dragen het
// platformpalet (COLORS_SEGMENT: particulier blauw, professioneel goud,
// grootverbruik violet — zelfde als Sales en Revenue). De scenario's mogen daar
// NIET mee botsen: de base case was blauw en stond naast "Particulier" in blauw
// (verwarrend, 25-09-2026). Nu worst = rood, base = fuchsia, best = groen; de
// enige tint die in beide thema's ≥ 15 ΔE van alle vijf andere reeksen blijft
// (rood, groen, blauw, goud, violet) én de kleurenblindheidstoets haalt
// (dataviz-validator). Fuchsia haalt op donker net geen 3:1 → de vergelijkings-
// tabel en de legenda dragen de cijfers ook in tekst.
const RC_CASE_KLEUR = { worst: CHART_HUES.rood, base: '#a21caf', best: CHART_HUES.groen };
const RC_SEG_KLEUR = { GV: COLORS_SEGMENT.Custom, B2B: COLORS_SEGMENT.B2B, B2C: COLORS_SEGMENT.B2C };
// Kleur van de staffelkorting op het vastrecht in de segmentgrafiek en -tabel (portaalpalet teal).
const RC_STAFFEL_KLEUR = '#14B8A6';
const RC_SEG_KORT = RC_SEGMENT_NAAM;
const RC_JAAR = Array.from({ length: RC_JAREN }, (_, i) => `Jaar ${i + 1}`);
// De producttabel per segment toont enkel de eerste drie jaren (25-09-2026): de
// volledige vijfjaarsreeks staat in de EAN/volume-tabel, de grafieken en de vergelijking.
const RC_JAREN_TABEL = 3;
const num0RC = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? 0 : n; };
const RC_JAAR_TABEL = RC_JAAR.slice(0, RC_JAREN_TABEL);
// Naam, eenheid en toelichting komen uit RC_PARAM_INFO (revenueCalculator.js):
// dezelfde teksten als in de configuratietabel op de Admin-pagina.
const RC_KOLOMMEN = [
  { f: 'I', letter: 'I', ...RC_PARAM_INFO.I },
  { f: 'J', letter: 'J', ...RC_PARAM_INFO.J },
  { f: 'K', letter: 'K', ...RC_PARAM_INFO.K },
  { f: 'L', letter: 'L', groep: 'portfolio', ...RC_PARAM_INFO.L },
  { f: 'N', letter: 'N', groep: 'portfolio', ...RC_PARAM_INFO.N },
];
const RC_GLOBALE_VELDEN = ['elMkt', 'gasMkt', 'groei', 'churn'].map((f) => ({ f, ...RC_PARAM_INFO[f] }));
// Menukaart per segment: de vaste kostopbouw van de lijnen (ter inzage) en het
// veld voor de vaste kost (€/maand per EAN; GV heeft zijn eigen, B2B en B2C delen
// de vaste kost klein verbruik). De kostopbouw is onafhankelijk van de scenario's.
const RC_MENUKAART_SEGMENT = {
  GV: { vast: 'vastGV', rijen: [{ key: 'gv_el', naam: 'Elektriciteit (AMR)' }, { key: 'gv_gas', naam: 'Gas' }] },
  B2B: { vast: 'vastKV', rijen: [{ key: 'b2b_el_off', naam: 'Elektriciteit RLP' }, { key: 'b2b_el_smr', naam: 'Elektriciteit SMR3' }, { key: 'b2b_gas', naam: 'Gas' }] },
  B2C: { vast: 'vastKV', rijen: [{ key: 'b2c_el_off', naam: 'Elektriciteit RLP' }, { key: 'b2c_el_smr', naam: 'Elektriciteit SMR3' }, { key: 'b2c_gas', naam: 'Gas' }] },
};
// De prijsformule van de klant en hoe de marge daaruit volgt — beknopt, onder de
// (i) bij de groepskop "Verkoopprijs" van de producttabel (het aparte blok
// "Prijsformule van de klant" is op 25-09-2026 weggehaald).
const RC_VERKOOPPRIJS_TIP = 'Prijs klant = A (vaste jaarvergoeding) + Index (marktprijs) × B (indexfactor) + C (opslag), aangerekend op de werkelijke kWh. ' +
  'Jouw labelmarge per EAN per jaar = (volume × energiemarge + (vaste vergoeding − vaste kost × 12)) × margeaandeel; ' +
  'energiemarge = (opslag − leveranciersmarge − sourcing − onbalans) + (indexfactor − 1) × marktprijs (bij injectie telt de grootte van de opslag en levert een indexfactor onder 1 marge op). ' +
  'Opbrengst per jaar = labelmarge × actieve EAN\'s (instroom groeit met het groeipercentage, het bestand krimpt met de churn). ' +
  'Kostopbouw en vaste kost volgen de menukaart; sociaal tarief, afbetalingsplannen, faillissement of invordering leveren geen partnervergoeding op. Cijfers zijn indicatief; de ondertekende menukaart geldt.';
const RC_VASTE_KOST_TIP = 'Vaste kost per EAN per maand uit de menukaart (2,5 €/maand = 30 €/jaar). De berekening is per jaar en rekent dus met × 12. Wat je daarboven als vaste vergoeding aanrekent, is marge. Klein zakelijk en particulier delen dezelfde vaste kost.';
const RC_INPUT = 'w-full px-2.5 py-1.5 rounded-lg border text-sm text-right tabular-nums text-slate-900 focus:outline-none focus:ring-2 focus:ring-lime-500/60';
// Twee kleuren voor bewerkbare velden: amber = verkoopprijs en globale parameters,
// sky = portfolio (volume en nieuwe EAN's). Zo is in de lijnentabel meteen
// zichtbaar welke velden de prijs van de klant bepalen en welke het volume.
const RC_TINT = {
  amber: { veld: 'bg-amber-100/90', rand: 'border-amber-300', kop: 'text-amber-600', lijn: 'border-amber-300' },
  sky: { veld: 'bg-sky-100/90', rand: 'border-sky-300', kop: 'text-sky-600', lijn: 'border-sky-300' },
};
// Eén bewerkbaar veld: eenheid rechts in het veld, foutmelding eronder.
const RcVeld = ({ value, onChange, eenheid, fout, step = 'any', minW = 84, tint = 'amber' }) => (
  <div className="min-w-0">
    <div className="relative">
      {/* Decimalen staan met een KOMMA in het veld (Belgische notatie). Bewaard en
          gerekend wordt met een punt; wie toch een punt typt, ziet hem als komma. */}
      <input value={String(value ?? '').replace(/\./g, ',')} onChange={(e) => onChange(e.target.value.replace(/,/g, '.'))} inputMode="decimal" step={step}
        style={{ minWidth: minW, paddingRight: eenheid ? `${Math.max(2.2, eenheid.length * 0.55 + 1)}rem` : undefined }}
        className={`${RC_INPUT} ${RC_TINT[tint].veld} ${fout ? 'border-red-500' : RC_TINT[tint].rand}`} />
      {eenheid && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-500 pointer-events-none">{eenheid}</span>}
    </div>
    {fout && <div className="text-[11px] text-red-400 mt-0.5 leading-tight">{fout}</div>}
  </div>
);

// ── Herontwerp 25-09-2026: premium demo-uitstraling ─────────────────────────
// Decoratief energielandschap (SVG, merkloos, aria-hidden) achter de paginatitel
// en de scenario's (de variant in het menukaartblok is op 25-09-2026 weer weggehaald). Zachte groenen en één zonnetje;
// turbines en pyloon in de tekstkleur, zodat ze in beide thema's leesbaar blijven.
const RcLandschap = ({ variant = 'kop', className = '', style }) => {
  const Turbine = ({ x, y, h, s = 1 }) => (
    <g transform={`translate(${x} ${y}) scale(${s})`} className="text-slate-400">
      <line x1="0" y1="0" x2="0" y2={-h} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <g transform={`translate(0 ${-h})`} stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="0" y1="0" x2="0" y2="-22" /><line x1="0" y1="0" x2="19" y2="11" /><line x1="0" y1="0" x2="-19" y2="11" />
        <circle r="2.4" fill="currentColor" />
      </g>
    </g>
  );
  return (
    <svg viewBox="0 0 620 150" preserveAspectRatio="xMaxYMax meet" className={className} style={style} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="rcHeuvelA" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#86efac" stopOpacity=".85" /><stop offset="1" stopColor="#4ade80" stopOpacity=".5" /></linearGradient>
        <linearGradient id="rcLucht" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#7dd3fc" stopOpacity=".28" /><stop offset="1" stopColor="#7dd3fc" stopOpacity="0" /></linearGradient>
      </defs>
      <circle cx="250" cy="46" r="18" fill="#fde68a" fillOpacity=".9" />
      <path d="M0 118 C 90 84, 170 104, 260 92 S 420 62, 520 84 S 590 96, 620 90 L620 150 L0 150 Z" fill="url(#rcHeuvelA)" />
      <path d="M0 136 C 110 118, 200 130, 300 122 S 470 104, 620 118 L620 150 L0 150 Z" fill="#22c55e" fillOpacity=".38" />
      <Turbine x={330} y={100} h={60} s={1.05} />
      <Turbine x={410} y={92} h={48} s={0.85} />
      <Turbine x={470} y={98} h={40} s={0.7} />
      {[[560, 122, 7], [590, 128, 6], [220, 128, 6]].map(([x, y, r]) => (
        <g key={x}><line x1={x} y1={y} x2={x} y2={y + 10} stroke="#16a34a" strokeWidth="2" /><circle cx={x} cy={y - r * 0.6} r={r} fill="#16a34a" fillOpacity=".75" /></g>
      ))}
    </svg>
  );
};

// Kengetalkaart met icoonrondje in een zachte tint. Zelfde lichte kaart als de
// StatTile van het platform (geen gekleurde achtergrond), enkel het icoon en —
// voor het hoofdcijfer — het kengetal krijgen kleur. <Kengetal> voorkomt afkappen.
const RC_KPI_TINT = {
  emerald: 'bg-emerald-500/15 text-emerald-500', sky: 'bg-sky-500/15 text-sky-500', amber: 'bg-amber-500/15 text-amber-500',
  violet: 'bg-violet-500/15 text-violet-500', cyan: 'bg-cyan-500/15 text-cyan-500',
};
const RcKpi = ({ icoon: Icoon, tint = 'sky', label, value, sub, accent, className = '' }) => (
  <div className={`app-card bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 flex items-start gap-3 min-w-0 ${className}`}>
    <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${RC_KPI_TINT[tint] || RC_KPI_TINT.sky}`}><Icoon size={18} /></span>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-slate-400 truncate">{label}</div>
      <div className="text-2xl font-bold text-slate-100 min-w-0 mt-0.5" style={accent ? { color: accent } : undefined}><Kengetal>{value}</Kengetal></div>
      {sub ? <div className="text-[11px] text-slate-500 mt-1 leading-tight">{sub}</div> : null}
    </div>
  </div>
);

// Sectiekop met icoonrondje: titel links, bediening rechts.
const RcKop = ({ icoon: Icoon, tint = 'sky', titel, sub, children }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
    <div className="flex items-start gap-3 min-w-0">
      {Icoon && <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${RC_KPI_TINT[tint] || RC_KPI_TINT.sky}`}><Icoon size={17} /></span>}
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-slate-100 leading-tight">{titel}</h3>
        {sub ? <p className="text-xs text-slate-500 mt-0.5">{sub}</p> : null}
      </div>
    </div>
    {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
  </div>
);
const RC_SEG_ICOON = { GV: Factory, B2B: Briefcase, B2C: Home };
const RC_KNOP_SEC = 'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 shadow-sm';
const RC_SCHAKEL = (aan) => `px-3 py-1.5 text-xs font-medium transition-colors ${aan ? 'bg-lime-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`;
// Invoer kan als tekst bewaard zijn ("2.5"); in een export hoort het een getal te
// zijn, dan schrijft exportRowsToCsv het met een decimale komma.
const rcGetal = (v) => (v === '' || v == null || typeof v === 'boolean' || Number.isNaN(Number(v)) ? v : Number(v));
const rcGetallen = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, rcGetal(v)]));
const RC_FAVORIET_TEKSTEN ={ titel: 'Favoriete configuraties', opslaan: 'Huidige configuratie opslaan…', gedeeld: 'Het margeaandeel blijft bij elke gebruiker dat van zijn rol.' };

// ── Admin-tab: standaardwaarden per type (27-09-2026) ────────────────────────
// Eén tabel met ALLE parameters van de calculator (globaal, menukaart per
// segment, en de velden per product), telkens voor de drie scenario's. Er zijn
// twee sets: Energielabel (master, prospect-label, admin, dossierbeheerder) en
// Wederverkoper (teamleader, verkoper, prospect-reseller). Bewaard wordt alleen
// wat van de menukaart afwijkt (rcConfigVoorOpslag); een gebruiker die een veld
// niet zelf wijzigde, rekent meteen met de nieuwe standaard. Alleen admin: de
// tab bestaat voor geen enkele andere rol en de route eist de rol ook.
const RC_CONFIG_RIJEN = rcConfigRijen();
const RC_CONFIG_PARAMETERS = [...new Set(RC_CONFIG_RIJEN.map((r) => r.kop))];
const RC_CONFIG_SELECT = (actief) => `w-full px-2 py-1.5 bg-slate-700 border rounded text-xs ${actief ? 'border-lime-500 text-lime-300' : 'border-slate-600 text-slate-200'}`;
const rcConfigGetal = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? String(v ?? '') : Number(v).toLocaleString('nl-BE', { maximumFractionDigits: 4 }));
const RcConfigAdmin = ({ token, config, laden, onBewaard }) => {
  const [type, setType] = useState('label');
  const [bewerk, setBewerk] = useState({});   // type → waardenset in bewerking (nog niet bewaard)
  const [bezig, setBezig] = useState(false);
  const [zoek, setZoek] = useState('');
  const [fSegment, setFSegment] = useState('all');
  const [fEnergie, setFEnergie] = useState('all');
  const [fParameter, setFParameter] = useState('all');
  const [alleenAfwijkend, setAlleenAfwijkend] = useState(false);
  const filtersActief = !!zoek.trim() || fSegment !== 'all' || fEnergie !== 'all' || fParameter !== 'all' || alleenAfwijkend;
  const wisFilters = () => { setZoek(''); setFSegment('all'); setFEnergie('all'); setFParameter('all'); setAlleenAfwijkend(false); };

  const fabriek = useMemo(() => rcFabriek(type), [type]);
  const bewaard = useMemo(() => rcStandaard(type, config?.[type]?.waarden), [type, config]);
  const set = bewerk[type] || bewaard;
  const zetSet = (s) => setBewerk((b) => ({ ...b, [type]: s }));
  const anderType = RC_TYPES.find((t) => t.id !== type);
  const nietBewaard = useMemo(() => rcAantalVelden(rcSetVerschil(set, bewaard)), [set, bewaard]);
  const afwijkend = useMemo(() => rcAantalVelden(rcSetVerschil(set, fabriek, { getal: true })), [set, fabriek]);
  const foutVan = (rij, id) => validateField(rij.veld, rcRijWaarde(set, id, rij), rij.line);
  const aantalFouten = useMemo(() => RC_CONFIG_RIJEN.reduce((n, r) => n + RC_CASE_IDS.filter((id) => validateField(r.veld, rcRijWaarde(set, id, r), r.line)).length, 0), [set]);
  const isAnders = (rij, id) => Number(rcRijWaarde(set, id, rij)) !== Number(rcRijWaarde(fabriek, id, rij));

  const rijen = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return RC_CONFIG_RIJEN.filter((r) => (fSegment === 'all' || (fSegment === 'globaal' ? r.groep === 'globaal' : r.segmenten.includes(fSegment)))
      && (fEnergie === 'all' || r.energie === fEnergie)
      && (fParameter === 'all' || r.kop === fParameter)
      && (!alleenAfwijkend || RC_CASE_IDS.some((id) => Number(rcRijWaarde(set, id, r)) !== Number(rcRijWaarde(fabriek, id, r))))
      && (!q || `${r.type} ${r.kop} ${r.eenheid}`.toLowerCase().includes(q)));
  }, [zoek, fSegment, fEnergie, fParameter, alleenAfwijkend, set, fabriek]);

  const opslaan = async () => {
    if (bezig) return;
    if (aantalFouten) {
      wisFilters();   // een ongeldige waarde mag niet achter een filter verborgen zitten
      toast.error(aantalFouten === 1 ? 'Er staat nog 1 ongeldige waarde in de tabel (rood).' : `Er staan nog ${aantalFouten} ongeldige waarden in de tabel (rood).`);
      return;
    }
    setBezig(true);
    try {
      const r = await fetch(`/api/revenue-calculator/config/${type}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ waarden: rcConfigVoorOpslag(set, type) }) });
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error((d && d.error) || `HTTP ${r.status}`);
      onBewaard(d.types || {});
      setBewerk((b) => ({ ...b, [type]: null }));
      toast.success(`Standaardwaarden ${rcType(type).label} bewaard.`);
    } catch (e) { toast.error(`Bewaren mislukt: ${e.message}`); }
    finally { setBezig(false); }
  };
  // Alles behalve het margeaandeel: dat is precies waarin de twee types verschillen.
  const kopieerVanAnder = () => {
    const bron = JSON.parse(JSON.stringify(rcStandaard(anderType.id, config?.[anderType.id]?.waarden)));
    for (const id of RC_CASE_IDS) bron.cases[id].g.share = set.cases[id].g.share;
    zetSet(bron);
    toast.info(`Waarden van ${anderType.label} overgenomen (zonder het margeaandeel). Klik op "Opslaan" om ze te bewaren.`);
  };
  const naarMenukaart = () => { zetSet(rcFabriek(type)); toast.info('Alle waarden staan op de menukaart. Klik op "Opslaan" om dat te bewaren.'); };

  const exporteer = () => exportRowsToCsv(`revenue_calculator_standaardwaarden_${type}`, [
    { header: 'Standaardwaarden', get: () => rcType(type).label }, { header: 'Type', get: (r) => r.type }, { header: 'Parameter', get: (r) => r.kop }, { header: 'Eenheid', get: (r) => r.eenheid },
    ...RC_CASES.map((c) => ({ header: c.label, get: (r) => rcGetal(rcRijWaarde(set, c.id, r)) })),
    ...RC_CASES.map((c) => ({ header: `Menukaart ${c.label.toLowerCase()}`, get: (r) => rcRijWaarde(fabriek, c.id, r) })),
  ], rijen);

  // Eén invoercel (tabel op desktop, blok op telefoon): groene rand = nog niet
  // bewaard; eronder de menukaartwaarde zodra de cel ervan afwijkt.
  const cel = (r, id, smal) => {
    const waarde = rcRijWaarde(set, id, r);
    const gewijzigd = String(waarde) !== String(rcRijWaarde(bewaard, id, r));
    return (
      <>
        <div className={`rounded-lg ${gewijzigd ? 'ring-2 ring-lime-500/60' : ''}`} title={gewijzigd ? 'Nog niet bewaard' : undefined}>
          <RcVeld value={waarde} onChange={(val) => zetSet(rcZetRijWaarde(set, id, r, val))} fout={foutVan(r, id)} step={r.veld === 'N' ? '1' : 'any'} minW={smal ? 0 : 96} tint={r.veld === 'L' || r.veld === 'N' ? 'sky' : 'amber'}
            eenheid={!smal && r.veld === 'J' ? `${String(jGrens(r.line).min).replace('.', ',')}–${String(jGrens(r.line).max).replace('.', ',')}` : undefined} />
        </div>
        {isAnders(r, id) && <div className="text-[10px] text-slate-500 mt-0.5 text-right tabular-nums whitespace-nowrap">menukaart {rcConfigGetal(rcRijWaarde(fabriek, id, r))}</div>}
      </>
    );
  };

  const meta = config?.[type];
  const bewaardOp =meta?.updated_at ? new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(meta.updated_at)) : null;

  if (laden) return <ListSkeleton rows={6} />;
  return (
    <Card className="!p-5 min-w-0">
      <RcKop icoon={Settings} tint="violet"
        titel={<span className="inline-flex items-center gap-2">Standaardwaarden · {rcType(type).label}<ExportKnop onExport={exporteer} count={rijen.length} label="Exporteer de getoonde parameters naar Excel" /></span>}
        sub={type === 'label' ? 'Geldt voor master en prospect-label, en als vertrekpunt voor admin en dossierbeheerder.' : 'Geldt voor teamleader, verkoper en prospect-reseller.'}>
        <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden text-xs shadow-sm" role="group" aria-label="Type standaardwaarden">
          {RC_TYPES.map((t, i) => (
            <button key={t.id} type="button" onClick={() => setType(t.id)} aria-pressed={type === t.id} className={`inline-flex items-center gap-1.5 ${RC_SCHAKEL(type === t.id)} ${i > 0 ? 'border-l border-slate-700' : ''}`}>
              {t.label}{bewerk[t.id] && rcAantalVelden(rcSetVerschil(bewerk[t.id], rcStandaard(t.id, config?.[t.id]?.waarden))) > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Niet-bewaarde wijzigingen" />}
            </button>
          ))}
        </div>
      </RcKop>

      <p className="text-xs text-slate-400 mb-4 max-w-[880px]">Deze waarden zijn het vertrekpunt van elke gebruiker van dit type. Wie op zijn rekenscherm zelf een veld wijzigt, houdt die eigen waarde; voor alle andere velden volgt hij wat hier staat. Een leeg of ongewijzigd veld volgt de menukaart.</p>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="w-full sm:w-[280px] shrink-0">
          <label className="block text-xs text-slate-400 mb-1">Zoeken</label>
          <div className="relative"><Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" /><input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Type of parameter…" className="w-full pl-7 pr-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" /></div>
        </div>
        <div className={FILTER_MD}><label className="block text-xs text-slate-400 mb-1">Segment</label>
          <select value={fSegment} onChange={(e) => setFSegment(e.target.value)} className={RC_CONFIG_SELECT(fSegment !== 'all')}>
            <option value="all">Alle</option><option value="globaal">Globaal</option>{RC_ORDER.map((k) => <option key={k} value={k}>{RC_SEG_KORT[k]}</option>)}
          </select></div>
        <div className={FILTER_SM}><label className="block text-xs text-slate-400 mb-1">Energie</label>
          <select value={fEnergie} onChange={(e) => setFEnergie(e.target.value)} className={RC_CONFIG_SELECT(fEnergie !== 'all')}>
            <option value="all">Alle</option><option value="el">Elektriciteit</option><option value="gas">Gas</option>
          </select></div>
        <div className={FILTER_MD}><label className="block text-xs text-slate-400 mb-1">Parameter</label>
          <select value={fParameter} onChange={(e) => setFParameter(e.target.value)} className={RC_CONFIG_SELECT(fParameter !== 'all')}>
            <option value="all">Alle</option>{RC_CONFIG_PARAMETERS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select></div>
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none pb-1.5">
          <input type="checkbox" checked={alleenAfwijkend} onChange={(e) => setAlleenAfwijkend(e.target.checked)} className="rounded border-slate-500 text-lime-500 focus:ring-lime-500 bg-slate-600 w-3.5 h-3.5" />
          Alleen wat afwijkt van de menukaart
        </label>
        {filtersActief && <button type="button" onClick={wisFilters} className="text-xs text-slate-400 hover:text-slate-200 underline pb-1.5">Filters wissen</button>}
      </div>

      {/* Stand + acties */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="text-xs text-slate-400 min-w-0">
          {rijen.length} van {RC_CONFIG_RIJEN.length} parameters · {afwijkend} {afwijkend === 1 ? 'waarde wijkt' : 'waarden wijken'} af van de menukaart
          {nietBewaard > 0 && <span className="text-amber-500 font-medium"> · {nietBewaard} niet bewaard</span>}
          <span className="block text-[11px] text-slate-500">{bewaardOp ? `Laatst bewaard op ${bewaardOp}${meta.updated_by ? ` door ${meta.updated_by}` : ''}` : 'Nog niets ingesteld: dit type rekent met de menukaart.'}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={kopieerVanAnder} className={RC_KNOP_SEC} title={`Alle waarden van ${anderType.label} overnemen, behalve het margeaandeel`}><Copy size={13} />Kopieer van {anderType.label}</button>
          <button type="button" onClick={naarMenukaart} disabled={afwijkend === 0} className={`${RC_KNOP_SEC} disabled:opacity-50 disabled:cursor-not-allowed`} title="Alle waarden van dit type terug op de menukaart"><RotateCcw size={13} />Terug naar de menukaart</button>
          <button type="button" onClick={() => setBewerk((b) => ({ ...b, [type]: null }))} disabled={!bewerk[type]} className={`${RC_KNOP_SEC} disabled:opacity-50 disabled:cursor-not-allowed`} title="Niet-bewaarde wijzigingen ongedaan maken"><X size={13} />Verwerpen</button>
          <button type="button" onClick={opslaan} disabled={bezig || nietBewaard === 0} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-lime-500 hover:bg-lime-400 text-slate-900 shadow-sm disabled:bg-slate-600 disabled:text-slate-400 disabled:cursor-not-allowed">
            {bezig ? <Spinner size={13} /> : <Check size={13} />}{bezig ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>

      {/* Telefoon: één blok per parameter met de drie scenario's naast elkaar — in
          de tabel zouden de waarden rechts buiten beeld vallen. */}
      {rijen.length > 0 && (
        <div className="md:hidden">
          {rijen.map((r, i) => {
            const eerste = i === 0 || rijen[i - 1].type !== r.type;
            return (
              <div key={r.key}>
                {eerste && <div className={`text-xs font-semibold text-slate-100 mb-1.5 ${i > 0 ? 'mt-4' : ''}`}>{r.type}</div>}
                <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-3 mb-2">
                  <div className="text-sm text-slate-200 flex flex-wrap items-center gap-x-1.5">{r.kop} <span className="text-slate-500">({r.veld === 'J' ? `${String(jGrens(r.line).min).replace('.', ',')}–${String(jGrens(r.line).max).replace('.', ',')}` : r.eenheid})</span> <InfoTip text={r.tip} /></div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {RC_CASES.map((c) => (
                      <div key={c.id} className="min-w-0">
                        <div className="text-[10px] text-slate-500 mb-0.5 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: RC_CASE_KLEUR[c.id] }} />{c.kort}</div>
                        {cel(r, c.id, true)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rijen.length === 0 ? <TableEmpty message="Geen parameters voor deze filters" /> : (
        <div className="hidden md:block overflow-x-auto -mx-1 px-1">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-700/60">
                <th className="text-left py-2 pr-3 font-medium">Type</th>
                <th className="text-left py-2 pr-3 font-medium">Parameter</th>
                {RC_CASES.map((c) => (
                  <th key={c.id} className="text-right py-2 px-1.5 font-medium whitespace-nowrap w-[132px]"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: RC_CASE_KLEUR[c.id] }} />{c.label}</span></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rijen.map((r, i) => {
                const eerste = i === 0 || rijen[i - 1].type !== r.type;
                return (
                  <tr key={r.key} className={`align-top hover:bg-slate-800/30 ${eerste && i > 0 ? 'border-t-2 border-slate-700/70' : 'border-t border-slate-700/30'}`}>
                    <td className={`py-2.5 pr-3 whitespace-nowrap ${eerste ? 'text-slate-100 font-medium' : 'text-slate-500'}`}>{r.type}</td>
                    <td className="py-2.5 pr-3 text-slate-200"><span className="inline-flex items-center gap-1.5 whitespace-nowrap">{r.kop} <span className="text-slate-500">({r.eenheid})</span> <InfoTip text={r.tip} /></span></td>
                    {RC_CASE_IDS.map((id) => <td key={id} className="py-1.5 px-1.5">{cel(r, id, false)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const RevenueCalculatorPage = ({ token, currentUser, branding = null }) => {
  const showBrand = seesEtBrand(currentUser?.role);
  const rol = currentUser?.role;
  const isAdmin = rol === 'admin';
  // Tabs: alleen een admin heeft er twee (Calculator en Admin); elke andere rol
  // ziet het rekenscherm zonder tabbalk.
  const [rcTab, setRcTab] = useState(() => (leesSessieWaarde('rcTab') === 'admin' ? 'admin' : 'calculator'));
  useEffect(() => { schrijfSessieWaarde('rcTab', rcTab); }, [rcTab]);
  const tab = isAdmin ? rcTab : 'calculator';

  // Type standaardwaarden: volgt de rol (master → Energielabel, teamleader en
  // verkoper → Wederverkoper). Admin en dossierbeheerder openen op Energielabel
  // en mogen wisselen; hun eigen wijzigingen worden per type bewaard.
  const eigenType = rcTypeVoorRol(rol);
  const magWisselen = rcMagTypeWisselen(rol);
  const [gekozenType, setGekozenType] = useState(null);
  const type = magWisselen && gekozenType ? gekozenType : eigenType;

  // De standaardwaarden die de beheerder per type instelde (Admin-tab).
  const [config, setConfig] = useState(null);
  const [configStatus, setConfigStatus] = useState('laden');   // laden | klaar | fout
  useEffect(() => {
    if (!token) return undefined;
    let weg = false;
    fetch('/api/revenue-calculator/config', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!weg) { setConfig(d.types || {}); setConfigStatus('klaar'); } })
      .catch(() => { if (!weg) setConfigStatus('fout'); });
    return () => { weg = true; };
  }, [token]);
  const standaard = useMemo(() => rcStandaard(type, config?.[type]?.waarden), [type, config]);

  // Wat de gebruiker zelf wijzigde: per type bewaard, alleen de velden die van de
  // standaard afwijken. De sleutel zonder type is de vorm van vóór 27-09-2026 en
  // telt enkel nog mee zolang er voor het eigen type niets nieuws bewaard is.
  const [oudScenario] = useUiPref(token, 'revenue-calculator.scenario', null);
  const [eigenLabel, zetEigenLabel] = useUiPref(token, 'revenue-calculator.scenario.label', null);
  const [eigenReseller, zetEigenReseller] = useUiPref(token, 'revenue-calculator.scenario.reseller', null);
  const zetEigen = type === 'reseller' ? zetEigenReseller : zetEigenLabel;
  const persoonlijk = (type === 'reseller' ? eigenReseller : eigenLabel) || (type === eigenType ? oudScenario : null);
  // Margeaandeel: komt uit de standaardwaarden; alleen een admin mag ervan afwijken.
  const magShare = isAdmin;
  const staat = useMemo(() => rcStaat(standaard, persoonlijk, { magShare }), [standaard, persoonlijk, magShare]);
  const actief = staat.actief;
  const kase = staat.cases[actief];
  const bewaar = (s) => zetEigen(rcPersoonlijkVan(s, standaard, { magShare }));
  const aantalEigen = useMemo(() => rcAantalVelden(rcPersoonlijkVan(staat, standaard, { magShare })), [staat, standaard, magShare]);
  const kloon = () => JSON.parse(JSON.stringify(staat));
  const zetVeld = (segKey, id, f, value) => {
    const s = kloon();
    const line = s.cases[actief].segments[segKey].lines.find((l) => l.id === id);
    line[f] = value;
    bewaar(s);
  };
  const zetGlobaal = (f, value) => { const s = kloon(); s.cases[actief].g[f] = value; bewaar(s); };
  const kiesCase = (id) => bewaar({ ...staat, actief: id });
  // Een lege set i.p.v. null: null zou de oude sleutel weer laten meetellen.
  const reset = () => { zetEigen({ v: 2, actief, cases: {} }); toast.success("Alle drie de scenario's staan terug op de standaardwaarden."); };
  // Favoriet: alle waarden onder een eigen naam. Toepassen legt ze over de
  // standaard van het huidige type; het margeaandeel volgt alleen bij een admin.
  const favoriet = useMemo(() => rcFavorietVan(staat, type), [staat, type]);
  const pasFavorietToe = (f) => { bewaar(rcStaat(standaard, f, { magShare })); toast.success("Favoriet toegepast op alle drie de scenario's."); };
  const [segment, setSegment] = useState('GV');
  // Welke scenario's de grafieken tonen: vrij aan/uit te zetten, standaard alle
  // drie; het laatste overgebleven scenario kan niet uitgezet worden.
  const [grafiekCases, setGrafiekCases] = useState(RC_CASE_IDS);
  const wisselGrafiekCase = (id) => setGrafiekCases((cur) => (cur.includes(id) ? (cur.length > 1 ? cur.filter((c) => c !== id) : cur) : RC_CASE_IDS.filter((c) => c === id || cur.includes(c))));
  const [grafiekMaat, setGrafiekMaat] = useState('eur');   // 'eur' = opbrengst in €, 'mwh' = volume in MWh
  const [showMenukaart, setShowMenukaart] = useState(false);
  const [menukaartOpen, setMenukaartOpen] = useState(false);   // kostopbouw-blok onder de producttabel, standaard dicht

  // Kopiëren van de actieve case naar een andere (of beide) — paneel in een portal.
  const [kopieerOpen, setKopieerOpen] = useState(false);
  const kopieerRef = useRef(null);
  const kopieerWrap = useRef(null);
  useEffect(() => {
    if (!kopieerOpen) return undefined;
    const buiten = (e) => { if (kopieerWrap.current && !kopieerWrap.current.contains(e.target) && !e.target.closest?.('[data-dropdown-panel]')) setKopieerOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setKopieerOpen(false); };
    document.addEventListener('mousedown', buiten); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', buiten); document.removeEventListener('keydown', esc); };
  }, [kopieerOpen]);
  const kopieerNaar = (doelen) => {
    let s = staat;
    for (const d of doelen) s = rcKopieerCase(s, actief, d);
    bewaar(s);
    setKopieerOpen(false);
    toast.success(`${rcCase(actief).label} gekopieerd naar ${doelen.map((d) => rcCase(d).label).join(' en ')}.`);
  };

  // Kostopbouw: de BRP-kost voor particulieren hangt af van het energielabel van
  // de gebruiker; admin/dossierbeheerder zonder label rekent als een nieuw label.
  const labelVoorBrp = currentUser?.energyLabel ? bronLabel(currentUser.energyLabel) : null;
  const lock = useMemo(() => rcLock(labelVoorBrp), [labelVoorBrp]);
  // Margeaandeel: per scenario, uit de standaardwaarden van het type.
  const share = kase.g.share;
  const uitPerCase = useMemo(() => Object.fromEntries(RC_CASE_IDS.map((id) => [id, calcAll(staat.cases[id].segments, staat.cases[id].g, lock)])), [staat, lock]);
  const uit = uitPerCase[actief];
  const seg = kase.segments[segment];
  const uitSeg = uit.perSegment[segment];
  const cum = (id) => uitPerCase[id].cumulative;

  // Grafiekdata. Cumulatief = lopende som per case.
  // Eén maat voor de hele prognoseweergave: opbrengst (€), volume (MWh) of
  // actieve EAN's. `reeks` = de sleutel in calcAll/calcLine.
  const reeks = grafiekMaat === 'mwh' ? 'mwh' : grafiekMaat === 'ean' ? 'actief' : 'jaren';
  const dataJaren = useMemo(() => RC_JAAR.map((jaar, i) => ({ jaar, ...Object.fromEntries(RC_CASE_IDS.map((id) => [id, Math.round(uitPerCase[id][reeks][i])])) })), [uitPerCase, reeks]);
  const dataCum = useMemo(() => {
    const som = Object.fromEntries(RC_CASE_IDS.map((id) => [id, 0]));
    return RC_JAAR.map((jaar, i) => { for (const id of RC_CASE_IDS) som[id] += uitPerCase[id][reeks][i]; return { jaar, ...Object.fromEntries(RC_CASE_IDS.map((id) => [id, Math.round(som[id])])) }; });
  }, [uitPerCase, reeks]);
  // De staffelkorting op het vastrecht is geen segment maar een voordeel op het
  // hele bestand; in euro's komt ze als eigen reeks bovenop de stapel, zodat de
  // stapel weer gelijk is aan het totaal.
  const toonStaffel = reeks === 'jaren' && uit.staffel.actief;
  const dataSeg = useMemo(() => RC_JAAR.map((jaar, i) => ({ jaar, ...Object.fromEntries(RC_ORDER.map((k) => [k, Math.round(uit.perSegment[k].total[reeks][i])])), ...(toonStaffel ? { staffel: Math.round(uit.staffel.voordeel[i]) } : {}) })), [uit, reeks, toonStaffel]);
  const fmtMaat = (v) => (grafiekMaat === 'mwh' ? `${formatNumberRounded(v)} MWh` : grafiekMaat === 'ean' ? RC_FMT_NUM(Math.round(v * 10) / 10) : RC_FMT_EUR0(v));
  const fmtAs = (v) => (grafiekMaat === 'eur' ? formatEuroRounded(v) : formatNumberRounded(v));
  const maatWoord = grafiekMaat === 'mwh' ? 'Volume' : grafiekMaat === 'ean' ? "Actieve EAN's" : 'Opbrengst';
  const maatEenheid = grafiekMaat === 'mwh' ? 'MWh' : grafiekMaat === 'ean' ? "EAN's" : '€';
  // Bij EAN's is een lopende som betekenisloos (het bestand ís al een stand);
  // de tweede grafiek toont dan de nieuwe instroom per jaar per scenario.
  const dataTweede = useMemo(() => (grafiekMaat !== 'ean' ? dataCum : RC_JAAR.map((jaar, i) => ({ jaar, ...Object.fromEntries(RC_CASE_IDS.map((id) => { const a = uitPerCase[id].actief; const churn = num0RC(staat.cases[id].g.churn) / 100; return [id, Math.round((i === 0 ? a[0] : a[i] - a[i - 1] * (1 - churn)) * 10) / 10]; })) }))), [dataCum, grafiekMaat, uitPerCase, staat]);
  const getoondeCases = grafiekCases;
  const allesNul = RC_CASE_IDS.every((id) => uitPerCase[id].cumulative === 0);

  const exporteer = () => {
    const rijen = [];
    for (const id of RC_CASE_IDS) for (const key of RC_ORDER) for (const r of uitPerCase[id].perSegment[key].lines) rijen.push({ kase: rcCase(id).label, g: rcGetallen(staat.cases[id].g), segment: staat.cases[id].segments[key].title, ...rcGetallen(r.line), margin: r.margin, M: r.M, jaren: r.jaren, actief: r.actief, mwh: r.mwh });
    const r2 = (v) => Math.round(v * 100) / 100;
    exportRowsToCsv('revenue_calculator', [
      { header: 'Scenario', get: (r) => r.kase }, { header: 'Segment', get: (r) => r.segment }, { header: 'Product', get: (r) => r.name }, { header: 'Code', get: (r) => r.tag },
      { header: 'Vaste vergoeding (€/EAN/jaar)', get: (r) => r.I }, { header: 'Indexfactor', get: (r) => r.J }, { header: 'Opslag (€/MWh)', get: (r) => r.K },
      { header: 'Volume (MWh/jaar)', get: (r) => r.L }, { header: "Nieuwe EAN's jaar 1", get: (r) => r.N },
      { header: 'Marktprijs EL (€/MWh)', get: (r) => r.g.elMkt }, { header: 'Marktprijs gas (€/MWh)', get: (r) => r.g.gasMkt }, { header: 'Margeaandeel (%)', get: (r) => r.g.share },
      { header: 'Vaste kost GV (€/maand per EAN)', get: (r) => r.g.vastGV }, { header: 'Vaste kost KV (€/maand per EAN)', get: (r) => r.g.vastKV }, { header: 'Jaarlijkse groei (%)', get: (r) => r.g.groei }, { header: 'Jaarlijkse churn (%)', get: (r) => r.g.churn }, { header: 'Leveranciersmarge GV elektriciteit (€/MWh)', get: (r) => r.g.margeGvEl }, { header: 'Leveranciersmarge GV gas (€/MWh)', get: (r) => r.g.margeGvGas },
      ...RC_JAAR.map((jaar, i) => ({ header: `Volume ${jaar.toLowerCase()} (MWh)`, get: (r) => r2(r.mwh[i]) })),
      { header: 'Partner-energiemarge (€/MWh)', get: (r) => r2(r.margin) }, { header: 'Labelmarge (€/EAN/jaar)', get: (r) => r2(r.M) },
      ...RC_JAAR.map((jaar, i) => ({ header: `${jaar} (€)`, get: (r) => r2(r.jaren[i]) })),
      ...RC_JAAR.map((jaar, i) => ({ header: `Actieve EAN's ${jaar.toLowerCase()}`, get: (r) => r2(r.actief[i]) })),
      { header: 'Cumulatief 5 jaar (€)', get: (r) => r2(r.jaren.reduce((a, v) => a + v, 0)) },
    ], rijen);
  };

  const labelKlasse = 'text-xs text-slate-400 mb-1 flex items-center gap-1';
  const tipKnop = (tekst) => <InfoTip text={tekst} />;
  const kleurKase = RC_CASE_KLEUR[actief];
  const tooltipEur = { contentStyle: CHART_TOOLTIP, formatter: (v, name) => [fmtMaat(v), name] };

  // ── PDF-rapport ──────────────────────────────────────────────────────────────
  // Zelfde weg als het Metering-meetrapport en het Revenue-rapport: de pagina
  // bouwt HTML in de huisstijl van de gebruiker, de server maakt er met headless
  // Chromium een echte PDF van (POST /api/report/pdf). Grafieken worden zelf
  // getekend (reportCharts.js); de Recharts-SVG van het scherm is thema-gekleurd
  // en meet 0×0 in een verborgen tab. Inhoud = wat op het scherm staat: het
  // actieve scenario met parameters, kerncijfers op 3 jaar, de producttabel per
  // segment (jaar 1–3) met de kostopbouw uit de menukaart, de prognose over vijf
  // jaar (alle scenario's), de vergelijking en de beknopte prijsformule.
  const [pdfBezig, setPdfBezig] = useState(false);
  const buildRcRapportHtml = () => {
    const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const rt = (v) => esc(showBrand ? String(v == null ? '' : v) : hideEtBrand(String(v == null ? '' : v)));
    // Huisstijl: de partnerrol draagt zijn eigen label/team (activeBranding is al
    // samengevoegd); admin en dossierbeheerder mogen het eigen merk tonen, want
    // zij presenteren de calculator zelf aan een kandidaat-partner.
    const merk = branding || null;
    const primair = merk?.primary_color || '#4f46e5';
    const labelNaam = merk?.display_name || merk?.energy_label || (showBrand ? (currentUser?.energyLabel || '') : hideEtBrand(currentUser?.energyLabel || '')) || 'Partnerportaal';
    const logo = merk?.logo_data_url
      ? `<img class="logo" src="${esc(merk.logo_data_url)}" alt="${rt(labelNaam)}" />`
      : `<div class="logo-fallback">${rt(labelNaam)}</div>`;
    const nu = new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
    const c = rcCase(actief);
    const eur = (v) => RC_FMT_EUR0(v);
    const eur2 = (v) => RC_FMT_EUR(v);
    const num = (v) => RC_FMT_NUM(v);
    const pct = (v) => RC_FMT_PCT(v);

    const meta = [
      ['Scenario', c.label], ['Margeaandeel partner', `${num(share)} %`],
      ['Marktprijs elektriciteit', `${num(kase.g.elMkt)} €/MWh`], ['Marktprijs gas', `${num(kase.g.gasMkt)} €/MWh`],
      ['Jaarlijkse groei nieuwe EAN\'s', pct(kase.g.groei)], ['Jaarlijkse churn', pct(kase.g.churn)],
      ['Vaste kost grootverbruik', `${num(kase.g.vastGV)} €/maand per EAN`], ['Vaste kost klein verbruik', `${num(kase.g.vastKV)} €/maand per EAN`],
    ].map(([k, v]) => `<div><div class="k">${rt(k)}</div><div class="v">${rt(v)}</div></div>`).join('');

    const kpis = [
      ['Nieuwe EAN\'s · jaar 1', num(uit.totalEan), 'over alle segmenten'],
      ['Actieve EAN\'s na 3 jaar', num(Math.round(uit.actief[2])), `${formatNumberRounded(uit.mwh[2])} MWh in jaar 3`],
      ['Opbrengst · jaar 1', eur(uit.jaren[0]), 'jaarlijkse partneropbrengst'],
      ['Opbrengst · jaar 3', eur(uit.jaren[2]), `${num(Math.round(uit.actief[2]))} actieve EAN's`],
      ['Cumulatief · 3 jaar', eur(uit.cumulative3), `5 jaar: ${eur(uit.cumulative)}`],
    ].map(([l, v, s]) => `<div class="kpi"><div class="kpi-label">${rt(l)}</div><div class="kpi-value">${rt(v)}</div><div class="kpi-sub">${rt(s)}</div></div>`).join('');

    // Per segment: de producttabel (jaar 1–3) en de kostopbouw uit de menukaart.
    const segmenten = RC_ORDER.map((key) => {
      const s = kase.segments[key]; const u = uit.perSegment[key]; const mk = RC_MENUKAART_SEGMENT[key];
      const rijen = u.lines.map(({ line, margin, M, jaren }) => `<tr>
        <td>${rt(line.name)}<div class="muted mono">${rt(line.tag)}</div></td>
        <td class="r">${num(line.I)}</td><td class="r">${num(line.J)}</td><td class="r">${num(line.K)}</td><td class="r">${num(line.L)}</td><td class="r">${num(line.N)}</td>
        <td class="r">${num(margin)}</td><td class="r"><b>${eur2(M)}</b></td>
        ${jaren.slice(0, RC_JAREN_TABEL).map((y) => `<td class="r">${eur2(y)}</td>`).join('')}
      </tr>`).join('');
      const totaal = u.total.jaren.slice(0, RC_JAREN_TABEL);
      const kost = mk.rijen.map((r) => { const k = lock[r.key]; const margeVeld = r.key === 'gv_el' ? 'margeGvEl' : r.key === 'gv_gas' ? 'margeGvGas' : null; const marge = margeVeld && kase.g[margeVeld] !== '' && kase.g[margeVeld] != null ? Number(kase.g[margeVeld]) : k.etMargin; return `${r.naam}: leveranciersmarge ${num(marge)} · sourcing/BRP ${num(k.sourcing)}${k.imbalance != null ? ` · onbalans ${num(k.imbalance)}` : ''}`; }).join(' — ');
      return `<section class="blok"><h2>${rt(s.title)} <span class="sub-h2">${rt(s.sub)}</span></h2>
        <table><thead><tr><th>Product</th><th class="r">Vaste verg.<br><span class="eenh">€/EAN/jaar</span></th><th class="r">Index<br><span class="eenh">factor</span></th><th class="r">Opslag<br><span class="eenh">€/MWh</span></th><th class="r">Volume<br><span class="eenh">MWh/jaar</span></th><th class="r">Nieuwe<br><span class="eenh">EAN's/jaar</span></th><th class="r">Energie-<br>marge <span class="eenh">€/MWh</span></th><th class="r">Labelmarge<br><span class="eenh">€/EAN/jaar</span></th>${RC_JAAR_TABEL.map((j) => `<th class="r">${rt(j)}<br><span class="eenh">€</span></th>`).join('')}</tr></thead>
        <tbody>${rijen}<tr class="tot"><td colspan="8">Segmenttotaal</td>${totaal.map((y) => `<td class="r">${eur2(y)}</td>`).join('')}</tr></tbody></table>
        <p class="noot">Menukaart (€/MWh): ${rt(kost)}. Vaste kost ${num(kase.g[mk.vast])} €/maand per EAN (× 12 in de berekening).</p>
      </section>`;
    }).join('');

    // Prognose over vijf jaar: alle scenario's in €, het actieve scenario ook in EAN's en MWh.
    const prognoseRijen = RC_JAAR.map((jaar, i) => `<tr><td>${rt(jaar)}</td>
      ${RC_CASE_IDS.map((id) => `<td class="r${id === actief ? ' act' : ''}">${eur(uitPerCase[id].jaren[i])}</td>`).join('')}
      <td class="r">${num(Math.round(uit.actief[i] * 10) / 10)}</td><td class="r">${formatNumberRounded(uit.mwh[i])}</td></tr>`).join('');
    const stavenScenario = meetGrafiekSvg({
      rijen: RC_JAAR.map((jaar, i) => ({ key: jaar, label: jaar, waarden: Object.fromEntries(RC_CASE_IDS.map((id) => [id, Math.round(uitPerCase[id].jaren[i])])) })),
      staven: RC_CASES.map((x) => ({ key: x.id, naam: x.label, kleur: RC_CASE_KLEUR[x.id] })), lijnen: [], breedte: 703, hoogte: 250, eenheid: '€',
    });
    const cumSom = Object.fromEntries(RC_CASE_IDS.map((id) => [id, 0]));
    const lijnenCum = meetGrafiekSvg({
      rijen: RC_JAAR.map((jaar, i) => { for (const id of RC_CASE_IDS) cumSom[id] += uitPerCase[id].jaren[i]; return { key: jaar, label: jaar, waarden: Object.fromEntries(RC_CASE_IDS.map((id) => [id, Math.round(cumSom[id])])) }; }),
      staven: [], lijnen: RC_CASES.map((x) => ({ key: x.id, naam: x.label, kleur: RC_CASE_KLEUR[x.id], as: 'links' })), breedte: 703, hoogte: 230, eenheid: '€',
    });
    const stapelSegment = staafGrafiekSvg({
      rijen: RC_JAAR.map((jaar, i) => ({ key: jaar, label: jaar, waarden: Object.fromEntries(RC_ORDER.map((k) => [k, Math.round(uit.perSegment[k].total.jaren[i])])) })),
      reeksen: RC_ORDER.map((k) => ({ key: k, naam: RC_SEG_KORT[k], kleur: RC_SEG_KLEUR[k] })), breedte: 703, hoogte: 230, eenheid: '€',
    });
    const legendeCases = RC_CASES.map((x) => `<span class="lg"><span class="stip" style="background:${esc(RC_CASE_KLEUR[x.id])}"></span>${rt(x.label)}</span>`).join('');
    const legendeSeg = RC_ORDER.map((k) => `<span class="lg"><span class="stip" style="background:${esc(RC_SEG_KLEUR[k])}"></span>${rt(RC_SEG_KORT[k])}</span>`).join('');

    const vergelijking = `<table><thead><tr><th>Periode</th>${RC_CASES.map((x) => `<th class="r">${rt(x.label)}<br><span class="eenh">groei ${pct(staat.cases[x.id].g.groei)} · churn ${pct(staat.cases[x.id].g.churn)}</span></th>`).join('')}</tr></thead><tbody>
      ${RC_JAAR.map((jaar, i) => `<tr><td>${rt(jaar)}</td>${RC_CASE_IDS.map((id) => `<td class="r${id === actief ? ' act' : ''}">${eur(uitPerCase[id].jaren[i])}</td>`).join('')}</tr>`).join('')}
      <tr class="tot"><td>Cumulatief 5 jaar</td>${RC_CASE_IDS.map((id) => `<td class="r" style="color:${esc(RC_CASE_KLEUR[id])}">${eur(cum(id))}</td>`).join('')}</tr>
      ${RC_CASE_IDS.some((id) => uitPerCase[id].staffel.actief) ? `<tr><td>Waarvan staffelkorting vastrecht · 5 jaar</td>${RC_CASE_IDS.map((id) => `<td class="r">${eur(uitPerCase[id].staffel.cumulatief)}</td>`).join('')}</tr>` : ''}
      <tr><td>Actieve EAN's na 5 jaar</td>${RC_CASE_IDS.map((id) => `<td class="r">${num(Math.round(uitPerCase[id].actiefEind))}</td>`).join('')}</tr>
      <tr><td>Volume jaar 5 · cumulatief (MWh)</td>${RC_CASE_IDS.map((id) => `<td class="r">${formatNumberRounded(uitPerCase[id].mwh[RC_JAREN - 1])} · ${formatNumberRounded(uitPerCase[id].mwh.reduce((a, v) => a + v, 0))}</td>`).join('')}</tr>
    </tbody></table>
    ${RC_CASE_IDS.some((id) => uitPerCase[id].staffel.actief) ? `<p class="noot">Staffelkorting vastrecht: boven ${num(MENUKAART.vastrecht.staffel[0].tot)} actieve EAN's (alle segmenten samen) daalt het vastrecht per schijf van ${num(MENUKAART.vastrecht.staffel[0].perMaand)} naar ${num(MENUKAART.vastrecht.staffel[1].perMaand)} € per EAN per maand, en vanaf ${num(MENUKAART.vastrecht.staffel[1].tot + 1)} naar ${num(MENUKAART.vastrecht.staffel[2].perMaand)} €. Het voordeel is in de opbrengst meegeteld. De korting geldt alleen zolang het percentage wanbetalers in "Statusverdeling vergoeding" niet boven ${num(MENUKAART.vastrecht.staffelWanbetalersMaxPct)} % uitkomt op het moment van de maandelijkse goedkeuringsmail.</p>` : ''}`;

    return `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>${exportTimestamp()}${rt(`Revenue Calculator — ${c.label}`)}</title>
<style>
  :root { --primair: ${esc(primair)}; }
  * { box-sizing: border-box; }
  body { font-family: ${merk?.font_family || "'Segoe UI', system-ui, -apple-system, Arial, sans-serif"}; color: #1e293b; margin: 0; font-size: 11px; line-height: 1.45; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .vel { max-width: 190mm; margin: 0 auto; padding: 10mm 0 14mm; }
  header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; padding-bottom: 12px; border-bottom: 3px solid var(--primair); }
  .logo { max-height: 54px; max-width: 240px; object-fit: contain; }
  .logo-fallback { font-size: 20px; font-weight: 700; color: var(--primair); }
  .kop-rechts { text-align: right; }
  .kop-rechts h1 { margin: 0; font-size: 19px; color: #0f172a; letter-spacing: -0.01em; }
  .kop-rechts .sub { color: #64748b; font-size: 11px; margin-top: 3px; }
  .meta { display: flex; flex-wrap: wrap; margin: 14px 0 16px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
  .meta div { flex: 1 1 25%; min-width: 110px; padding: 8px 12px; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
  .meta div:nth-child(4n) { border-right: 0; }
  .meta .k { color: #64748b; font-size: 9.5px; text-transform: uppercase; letter-spacing: .04em; }
  .meta .v { color: #0f172a; font-weight: 600; font-size: 11.5px; margin-top: 2px; overflow-wrap: anywhere; }
  h2 { font-size: 13px; color: #0f172a; margin: 18px 0 8px; padding-bottom: 5px; border-bottom: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: .05em; }
  h2 .sub-h2 { text-transform: none; letter-spacing: 0; color: #64748b; font-weight: 400; font-size: 10.5px; margin-left: 6px; }
  .kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
  .kpi { border: 1px solid #e2e8f0; border-left: 3px solid var(--primair); border-radius: 6px; padding: 8px 10px; }
  .kpi-label { color: #64748b; font-size: 9.5px; }
  .kpi-value { color: #0f172a; font-size: 15px; font-weight: 700; margin-top: 1px; white-space: nowrap; }
  .kpi-sub { color: #64748b; font-size: 8.5px; margin-top: 2px; }
  .grafiek { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 10px 4px; margin-bottom: 10px; }
  .grafiek .titel { font-size: 10.5px; font-weight: 600; color: #334155; margin: 0 0 4px; }
  .legende { display: flex; flex-wrap: wrap; gap: 6px 14px; margin: 0 0 8px; font-size: 9.5px; color: #475569; }
  .lg { display: inline-flex; align-items: center; gap: 5px; }
  .stip { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  table { width: 100%; border-collapse: collapse; font-size: 9.5px; }
  th { background: #f1f5f9; text-align: left; padding: 5px 7px; font-weight: 600; color: #334155; border-bottom: 1.5px solid #cbd5e1; white-space: nowrap; vertical-align: bottom; }
  th .eenh { font-weight: 400; color: #64748b; font-size: 8.5px; }
  td { padding: 4px 7px; border-bottom: 1px solid #eef2f7; vertical-align: top; }
  th.r, td.r { text-align: right; white-space: nowrap; }
  td.act { font-weight: 700; color: #0f172a; }
  tr.tot td { background: #f8fafc; font-weight: 700; border-top: 1.5px solid #cbd5e1; }
  .mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 8px; }
  .muted { color: #94a3b8; }
  .noot { color: #64748b; font-size: 9.5px; margin: 8px 0 0; }
  .blok { margin-bottom: 14px; }
  .uitleg p { margin: 0 0 6px; color: #475569; font-size: 9.5px; }
  footer { margin-top: 14px; padding-top: 8px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 9px; display: flex; justify-content: space-between; }
  @page { size: A4 portrait; margin: 12mm; }
  @media print { .vel { max-width: none; padding: 0; } thead { display: table-header-group; } tr, .kpi, .grafiek, .kpis { break-inside: avoid; page-break-inside: avoid; } h2 { break-after: avoid; } }
</style>
</head>
<body>
<div class="vel">
  <header>
    <div>${logo}</div>
    <div class="kop-rechts"><h1>Revenue Calculator</h1><div class="sub">Prognose partneropbrengst · ${rt(c.label)}</div><div class="sub">Opgemaakt op ${esc(nu)}</div></div>
  </header>
  <div class="meta">${meta}</div>
  <section class="blok"><h2>Kerncijfers · ${rt(c.label)}</h2><div class="kpis">${kpis}</div></section>
  ${segmenten}
  <section class="blok"><h2>Prognose over vijf jaar</h2>
    <div class="grafiek"><div class="titel">Opbrengst per jaar · per scenario</div><div class="legende">${legendeCases}</div>${stavenScenario}</div>
    <div class="grafiek"><div class="titel">Cumulatieve opbrengst · lopende som</div><div class="legende">${legendeCases}</div>${lijnenCum}</div>
    <div class="grafiek"><div class="titel">Opbouw per segment · ${rt(c.label)}</div><div class="legende">${legendeSeg}</div>${stapelSegment}</div>
    <table><thead><tr><th>Jaar</th>${RC_CASES.map((x) => `<th class="r">${rt(x.label)} (€)</th>`).join('')}<th class="r">Actieve EAN's<br><span class="eenh">${rt(c.label)}</span></th><th class="r">Volume (MWh)<br><span class="eenh">${rt(c.label)}</span></th></tr></thead><tbody>${prognoseRijen}</tbody></table>
  </section>
  <section class="blok"><h2>Vergelijking van de scenario's</h2>${vergelijking}</section>
  <section class="blok uitleg"><h2>Hoe de cijfers tot stand komen</h2>
    <p><b>Prijs van de klant</b> = A (vaste jaarvergoeding) + Index (marktprijs) × B (indexfactor) + C (opslag), aangerekend op de werkelijke kWh.</p>
    <p><b>Labelmarge per EAN per jaar</b> = (volume × energiemarge + (vaste vergoeding − vaste kost × 12)) × margeaandeel. Energiemarge = (opslag − leveranciersmarge − sourcing − onbalans) + (indexfactor − 1) × marktprijs; bij injectie telt de grootte van de opslag en levert een indexfactor onder 1 marge op.</p>
    <p><b>Vijf jaar</b>: de instroom van nieuwe EAN's groeit elk jaar met het groeipercentage, het bestand krimpt met de churn; opbrengst per jaar = labelmarge × actieve EAN's.</p>
    <p>De kostopbouw en de vaste kost volgen de menukaart. Sociaal tarief, afbetalingsplannen, faillissement of invordering leveren geen partnervergoeding op. Cijfers zijn indicatief; de ondertekende menukaart geldt.</p>
  </section>
  <footer><span>${rt(labelNaam)} — Revenue Calculator, indicatieve prognose</span><span>${esc(nu)}</span></footer>
</div>
</body>
</html>`;
  };
  const exportRcPdf = async () => {
    if (pdfBezig) return;
    setPdfBezig(true);
    try {
      const r = await fetch('/api/report/pdf', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/html; charset=utf-8' }, body: buildRcRapportHtml() });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || `HTTP ${r.status}`); }
      downloadBlob(`${exportTimestamp()}revenue_calculator_${rcCase(actief).id}.pdf`, await r.blob());
      toast.success('Rapport als PDF gedownload');
    } catch (e) { toast.error(`PDF maken mislukt: ${e.message}`); }
    finally { setPdfBezig(false); }
  };

  return (
    <div className="space-y-5">
      {/* Kop + scenario-schakelaar met een energielandschap erachter (decoratief,
          enkel op brede schermen; loopt links uit in het niets). */}
      <div className="relative space-y-5">
        <RcLandschap variant="kop" className="pointer-events-none absolute bottom-0 right-0 h-[60px] w-[300px] max-w-[30%] hidden lg:block opacity-60" style={{ WebkitMaskImage: "linear-gradient(to right, transparent, #000 40%)", maskImage: "linear-gradient(to right, transparent, #000 40%)" }} />
        <div className="relative">
          <PageHeader title="Revenue Calculator">
            <div className="flex flex-wrap items-center gap-2">
              <RevenueMenukaartButton onToggle={() => setShowMenukaart(true)} />
              {tab === 'calculator' && <>
                <ExportKnop onExport={exporteer} label="Alle scenario's en producten met invoer en uitkomst naar Excel" />
                <FilterFavorites pageKey="revenue-calculator" filters={favoriet} onApply={pasFavorietToe} teksten={RC_FAVORIET_TEKSTEN} />
                <button type="button" onClick={reset} disabled={aantalEigen === 0} className={`${RC_KNOP_SEC} disabled:opacity-50 disabled:cursor-not-allowed`} title={aantalEigen === 0 ? 'Je rekent met de standaardwaarden' : `Alle drie de scenario's terug op de standaardwaarden (${aantalEigen} eigen ${aantalEigen === 1 ? 'wijziging' : 'wijzigingen'})`}>
                  <RotateCcw size={13} />Standaardwaarden{aantalEigen > 0 && <span className="ml-0.5 px-1.5 rounded-full bg-amber-500/20 text-amber-500 text-[10px] font-semibold tabular-nums">{aantalEigen}</span>}
                </button>
                <button type="button" onClick={exportRcPdf} disabled={pdfBezig} className={`${RC_KNOP_SEC} disabled:opacity-60`} title="Rapport in de huisstijl als PDF: scenario, kerncijfers, producten per segment, prognose en vergelijking">
                  {pdfBezig ? <Spinner size={13} /> : <FileText size={13} />}{pdfBezig ? 'PDF maken…' : 'PDF-rapport'}
                </button>
              </>}
            </div>
          </PageHeader>
        </div>
      <DocsModal open={showMenukaart} onClose={() => setShowMenukaart(false)} title="Menukaart" subtitle="Alle fees, excl. btw" padded={false}>
        <RevenueMenukaartDocs open currentUser={currentUser} energyLabel={currentUser?.energyLabel || ''} />
      </DocsModal>

      {/* Eén rij (04-10-2026): tabs (alleen admin), type standaardwaarden (admin en
          dossierbeheerder), de scenario's en de kopieerknop naast elkaar in plaats
          van drie rijen onder elkaar. Op een smal scherm breekt de rij per groep. */}
      <div className="relative flex flex-wrap items-center gap-x-5 gap-y-2">
      {/* Tabs — alleen voor een admin: het rekenscherm en de standaardwaarden. */}
      {isAdmin && (
          <div className="flex gap-2">
            {[{ id: 'calculator', label: 'Calculator' }, { id: 'admin', label: 'Admin' }].map((t) => (
              <button key={t.id} type="button" onClick={() => setRcTab(t.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.id ? 'bg-lime-500 text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-600'}`}>
                {t.label}
              </button>
            ))}
          </div>
      )}

      {/* Type standaardwaarden — alleen admin en dossierbeheerder mogen wisselen. */}
      {tab === 'calculator' && configStatus !== 'laden' && magWisselen && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 inline-flex items-center gap-1">Standaardwaarden <InfoTip text="Met welke standaardwaarden je rekent. Energielabel geldt voor een master, Wederverkoper voor een teamleader en een verkoper. Je eigen wijzigingen worden per type bewaard." /></span>
          <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden text-xs shadow-sm" role="group" aria-label="Type standaardwaarden">
            {RC_TYPES.map((t, i) => <button key={t.id} type="button" onClick={() => setGekozenType(t.id)} aria-pressed={type === t.id} className={`${RC_SCHAKEL(type === t.id)} ${i > 0 ? 'border-l border-slate-700' : ''}`}>{t.label}</button>)}
          </div>
        </div>
      )}

      {/* Scenario-schakelaar */}
      {tab === 'calculator' && configStatus !== 'laden' && (
      <div className="flex flex-wrap items-center gap-2">
        {RC_CASES.map((c) => {
          const aan = actief === c.id;
          return (
            <button key={c.id} type="button" onClick={() => kiesCase(c.id)} aria-pressed={aan}
              className={`inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all shadow-sm ${aan ? 'bg-lime-500 text-slate-900 ring-2 ring-lime-500/30' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: RC_CASE_KLEUR[c.id], boxShadow: `0 0 0 3px ${RC_CASE_KLEUR[c.id]}33` }} />
              {c.label}
              <span className={`text-xs font-medium tabular-nums ${aan ? 'text-slate-800' : 'text-slate-500'}`}>{RC_FMT_EUR0(cum(c.id))}</span>
            </button>
          );
        })}
        <div ref={kopieerWrap} className="relative">
          <button ref={kopieerRef} type="button" onClick={() => setKopieerOpen((o) => !o)} className={`${RC_KNOP_SEC} !py-2`} title="De invoer van dit scenario overnemen in een ander scenario">
            <Copy size={13} />Kopieer {rcCase(actief).kort} naar<ChevronDown size={12} />
          </button>
          {kopieerOpen && (
            <DropdownPanel anchorRef={kopieerRef} minWidth={200}>
              <div className="py-1">
                {RC_CASES.filter((c) => c.id !== actief).map((c) => (
                  <button key={c.id} type="button" onClick={() => kopieerNaar([c.id])} className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-700 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: RC_CASE_KLEUR[c.id] }} />{c.label}
                  </button>
                ))}
                <button type="button" onClick={() => kopieerNaar(RC_CASE_IDS.filter((id) => id !== actief))} className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-700 border-t border-slate-700">Beide andere scenario's</button>
              </div>
            </DropdownPanel>
          )}
        </div>
      </div>
      )}
      </div>

      {tab === 'calculator' && configStatus === 'fout' && (
        <div className="relative bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-2 rounded text-sm flex items-center gap-2"><AlertCircle size={15} className="shrink-0" />De standaardwaarden konden niet geladen worden. De pagina rekent voorlopig met de menukaart.</div>
      )}
      </div>

      {/* Admin-tab: blijft gemount (hidden), zodat niet-bewaarde invoer een
          tabwissel overleeft. */}
      {isAdmin && (
        <div className={tab === 'admin' ? '' : 'hidden'}>
          <RcConfigAdmin token={token} config={config} laden={configStatus === 'laden'} onBewaard={(types) => { setConfig(types); setConfigStatus('klaar'); }} />
        </div>
      )}

      {tab === 'calculator' && (configStatus === 'laden' ? <ListSkeleton rows={4} /> : <>
      {/* Globale parameters van het actieve scenario */}
      <Card className="!p-5">
        <RcKop icoon={SlidersHorizontal} tint="sky" titel={<span className="inline-flex items-center gap-2">Globale parameters<span className="text-slate-500 font-normal">·</span><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: kleurKase }} />{rcCase(actief).label}</span></span>} />
        <div className="flex flex-wrap gap-3">
          {/* Margeaandeel: onafhankelijk van het scenario; volgt de rol, alleen admin past aan. */}
          <div className="w-[calc(50%-6px)] sm:w-[184px]">
            <div className={labelKlasse}><span className="truncate">Margeaandeel partner</span> {tipKnop(isAdmin ? 'Het deel van de labelmarge dat naar de partner gaat. De standaard staat per type en per scenario op de Admin-pagina; als beheerder mag je er hier van afwijken.' : 'Het deel van de labelmarge dat naar de partner gaat. Dit volgt je rol en is niet aanpasbaar.')}</div>
            {isAdmin
              ? <RcVeld value={share} onChange={(val) => zetGlobaal('share', val)} eenheid="%" fout={validateField('share', share)} />
              : <div className="px-2.5 py-1.5 rounded-lg border border-slate-700/60 bg-slate-800/40 text-sm text-slate-200 text-right tabular-nums" title="Volgens je rol — niet aanpasbaar">{RC_FMT_NUM(share)}<span className="text-[11px] text-slate-500 ml-1">%</span></div>}
          </div>
          {RC_GLOBALE_VELDEN.map((v) => (
            <div key={v.f} className="w-[calc(50%-6px)] sm:w-[184px]">
              <div className={labelKlasse}><span className="truncate">{v.kop}</span> {tipKnop(v.tip)}</div>
              <RcVeld value={kase.g[v.f]} onChange={(val) => zetGlobaal(v.f, val)} eenheid={v.eenheid} fout={validateField(v.f, kase.g[v.f])} />
            </div>
          ))}
        </div>
      </Card>

      {/* Segmenten */}
      <div className="flex flex-wrap gap-2">
        {RC_ORDER.map((key) => { const Icoon = RC_SEG_ICOON[key]; const aan = segment === key; return (
          <button key={key} type="button" onClick={() => setSegment(key)} aria-pressed={aan}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all shadow-sm ${aan ? 'bg-lime-500 text-slate-900 ring-2 ring-lime-500/30' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}>
            <Icoon size={15} className={aan ? 'text-slate-800' : 'text-slate-400'} />{kase.segments[key].title}
          </button>
        ); })}
      </div>

      <Card className="!p-5">
        <RcKop icoon={RC_SEG_ICOON[segment]} tint="emerald" titel={seg.title} sub={`${seg.sub} · ${rcCase(actief).label}`}>
          <div className="text-xs text-slate-500">Segmenttotaal 3 jaar <span className="ml-1 inline-block px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-slate-100 font-semibold tabular-nums">{RC_FMT_EUR0(uitSeg.total.jaren.slice(0, RC_JAREN_TABEL).reduce((a, v) => a + v, 0))}</span></div>
        </RcKop>
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-sm min-w-[920px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.12em] font-semibold">
                <th />
                <th colSpan={3} className={`pb-1 px-1.5 text-center ${RC_TINT.amber.kop}`}><span className={`flex items-center justify-center gap-1 border-b-2 ${RC_TINT.amber.lijn} pb-1 mx-1`}>Verkoopprijs {tipKnop(RC_VERKOOPPRIJS_TIP)}</span></th>
                <th colSpan={2} className={`pb-1 px-1.5 text-center ${RC_TINT.sky.kop}`}><span className={`block border-b-2 ${RC_TINT.sky.lijn} pb-1 mx-1`}>Portfolio</span></th>
                <th colSpan={2 + RC_JAREN_TABEL} />
              </tr>
              <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-700/60">
                <th className="text-left py-2 pr-3 font-medium">Product</th>
                {RC_KOLOMMEN.map((k) => (
                  <th key={k.f} className="text-right py-2 px-1.5 font-medium whitespace-nowrap"><span className="flex items-center justify-end gap-1">{k.kop} {tipKnop(k.tip)}</span><span className="block text-[9px] normal-case text-slate-600">{k.eenheid} · {k.letter}</span></th>
                ))}
                <th className="text-right py-2 px-1.5 font-medium whitespace-nowrap">Energiemarge<span className="block text-[9px] normal-case text-slate-600">€/MWh</span></th>
                <th className="text-right py-2 px-1.5 font-medium whitespace-nowrap">Labelmarge<span className="block text-[9px] normal-case text-slate-600">€/EAN/jaar · M</span></th>
                {RC_JAAR_TABEL.map((jaar) => <th key={jaar} className="text-right py-2 px-1.5 font-medium whitespace-nowrap">{jaar}<span className="block text-[9px] normal-case text-slate-600">€</span></th>)}
              </tr>
            </thead>
            <tbody>
              {uitSeg.lines.map(({ line, margin, M, jaren }) => (
                <tr key={line.id} className="border-b border-slate-700/40 align-top hover:bg-slate-800/30">
                  <td className="py-2.5 pr-3">
                    <div className="text-slate-100 font-medium whitespace-nowrap">{line.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{line.tag}</div>
                  </td>
                  {RC_KOLOMMEN.map((k) => (
                    <td key={k.f} className="py-2 px-1.5">
                      <RcVeld value={line[k.f]} onChange={(val) => zetVeld(segment, line.id, k.f, val)} fout={validateField(k.f, line[k.f], line)} step={k.f === 'N' ? '1' : 'any'} minW={k.f === 'J' ? 96 : 84} tint={k.groep === 'portfolio' ? 'sky' : 'amber'}
                        eenheid={k.f === 'J' ? `${String(jGrens(line).min).replace('.', ',')}–${String(jGrens(line).max).replace('.', ',')}` : undefined} />
                    </td>
                  ))}
                  <td className="py-2.5 px-1.5 text-right tabular-nums text-slate-300">{RC_FMT_NUM(margin)}</td>
                  <td className="py-2.5 px-1.5 text-right tabular-nums"><span className="inline-block px-2 py-0.5 rounded-md border font-semibold whitespace-nowrap" style={{ background: 'rgba(99,102,241,.14)', borderColor: 'rgba(99,102,241,.45)', color: '#6366F1' }}>{RC_FMT_EUR(M)}</span></td>
                  {jaren.slice(0, RC_JAREN_TABEL).map((y, i) => <td key={i} className={`py-2.5 px-1.5 text-right tabular-nums ${i === 0 ? 'font-semibold' : ''} ${y < 0 ? 'text-red-400' : i === 0 ? 'text-slate-100' : 'text-slate-300'}`}>{RC_FMT_EUR(y)}</td>)}
                </tr>
              ))}
              <tr className="bg-slate-800/60">
                <td className="py-2.5 pr-3 text-xs font-semibold text-slate-300 rounded-l-lg" colSpan={8}>Segmenttotaal</td>
                {uitSeg.total.jaren.slice(0, RC_JAREN_TABEL).map((y, i) => <td key={i} className={`py-2.5 px-1.5 text-right tabular-nums font-semibold text-slate-100 ${i === RC_JAREN_TABEL - 1 ? 'rounded-r-lg' : ''}`}>{RC_FMT_EUR(y)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Menukaart van dit segment: vaste kostopbouw (ter inzage) + vaste kost.
            De kostopbouw is onafhankelijk van de scenario's; de vaste kost hoort bij
            de menukaart maar is per scenario bewerkbaar. Rechts een decoratief
            energielandschap (enkel op brede schermen). */}
        {(() => { const mk = RC_MENUKAART_SEGMENT[segment]; const kop = seg.title.replace(/\s*\(.*\)$/, '').toLowerCase(); return (
          <div className="mt-5 rounded-xl border border-slate-700/50 bg-slate-800/30">
            {/* Inklapbaar (standaard dicht): de kostopbouw ligt vast en de vaste kost
                verandert zelden; ingeklapt blijven de kerncijfers in de kopregel staan. */}
            <button type="button" onClick={() => setMenukaartOpen((o) => !o)} aria-expanded={menukaartOpen} className="w-full flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-left rounded-xl hover:bg-slate-800/40">
              <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                <ChevronDown size={14} className={`text-slate-500 transition-transform ${menukaartOpen ? '' : '-rotate-90'}`} />
                Menukaart · kostopbouw {kop}<span className="normal-case font-normal text-slate-500 tracking-normal"> · €/MWh</span>
              </span>
              <span className="text-xs text-slate-400">Vaste kost <span className="text-slate-100 font-semibold tabular-nums">{RC_FMT_NUM(kase.g[mk.vast])}</span> €/maand per EAN{!menukaartOpen && <span className="text-slate-500"> · marge {mk.rijen.map((r) => `${r.naam.replace(/\s*\(.*\)$/, '').toLowerCase()} ${RC_FMT_NUM(lock[r.key].etMargin)}`).join(' · ')}</span>}</span>
            </button>
            {menukaartOpen && (
            <div className="px-4 pb-4 flex flex-col lg:flex-row lg:items-start gap-5 lg:gap-10">
              <div className="min-w-0 w-full lg:w-[520px]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                      <th className="text-left pb-1 pr-3 font-medium">Product</th>
                      <th className="text-right pb-1 px-2 font-medium whitespace-nowrap"><span className="lg:hidden">Marge</span><span className="hidden lg:inline">Leveranciersmarge</span>{segment === 'GV' && <span className="block normal-case text-slate-600">bewerkbaar</span>}</th>
                      <th className="text-right pb-1 px-2 font-medium whitespace-nowrap">Sourcing / BRP</th>
                      <th className="text-right pb-1 pl-2 font-medium whitespace-nowrap">Onbalans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mk.rijen.map((r) => { const c = lock[r.key]; const margeVeld = r.key === 'gv_el' ? 'margeGvEl' : r.key === 'gv_gas' ? 'margeGvGas' : null; return (
                      <tr key={r.key} className="border-t border-slate-700/30">
                        <td className="py-1.5 pr-3 text-slate-200">{r.naam}</td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-slate-300">
                          {margeVeld
                            ? <div className="inline-block w-[84px]"><RcVeld value={kase.g[margeVeld]} onChange={(val) => zetGlobaal(margeVeld, val)} fout={validateField(margeVeld, kase.g[margeVeld])} minW={72} /></div>
                            : RC_FMT_NUM(c.etMargin)}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-slate-300">{RC_FMT_NUM(c.sourcing)}</td>
                        <td className="py-1.5 pl-2 text-right tabular-nums text-slate-300">{c.imbalance != null ? RC_FMT_NUM(c.imbalance) : <span className="text-slate-600">—</span>}</td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
              <div className="shrink-0 lg:border-l lg:border-slate-700/50 lg:pl-8">
                <div className={labelKlasse}>Vaste kost {tipKnop(RC_VASTE_KOST_TIP)}</div>
                <div className="flex items-center gap-2">
                  <div className="w-24"><RcVeld value={kase.g[mk.vast]} onChange={(val) => zetGlobaal(mk.vast, val)} fout={validateField(mk.vast, kase.g[mk.vast])} minW={72} /></div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">€/maand per EAN</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{mk.vast === 'vastKV' ? 'Klein zakelijk en particulier delen deze vaste kost.' : 'In de berekening × 12 per jaar.'}</div>
              </div>
            </div>
            )}
          </div>
        ); })()}
      </Card>

      {/* Kengetallen van het actieve scenario */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <RcKpi icoon={Users} tint="emerald" label="Nieuwe EAN's · jaar 1" value={RC_FMT_NUM(uit.totalEan)} sub="over alle segmenten" />
        <RcKpi icoon={BarChart3} tint="sky" label="Actieve EAN's na 3 jaar" value={RC_FMT_NUM(Math.round(uit.actief[2]))} sub={`${formatNumberRounded(uit.mwh[2])} MWh in jaar 3 · groei ${RC_FMT_PCT(kase.g.groei)} · churn ${RC_FMT_PCT(kase.g.churn)}`} />
        <RcKpi icoon={Coins} tint="amber" label="Opbrengst · jaar 1" value={RC_FMT_EUR0(uit.jaren[0])} sub="jaarlijkse partneropbrengst" />
        <RcKpi icoon={TrendingUp} tint="violet" label="Opbrengst · jaar 3" value={RC_FMT_EUR0(uit.jaren[2])} sub={`${RC_FMT_NUM(Math.round(uit.actief[2]))} actieve EAN's`} />
        <RcKpi icoon={Trophy} tint="cyan" label="Cumulatief · 3 jaar" value={RC_FMT_EUR0(uit.cumulative3)} accent={kleurKase} sub={`${rcCase(actief).label} · 5 jaar: ${RC_FMT_EUR0(uit.cumulative)}`} className="col-span-2 md:col-span-1" />
      </div>

      {/* Staffelkorting op het vastrecht (contract 6.6.1 / 6.6.2, src/menukaart.js):
          boven 2.000 actieve EAN's daalt het vastrecht per schijf. Het voordeel zit
          al in de opbrengst hierboven; dit blok maakt het zichtbaar zodra een jaar
          van het actieve scenario de drempel overschrijdt. Daaronder één regel,
          zodat de partner weet dat de korting bestaat. */}
      {uit.staffel.actief ? (
        <Card className="!p-0 overflow-hidden">
          <div className="px-4 pt-4 pb-3 flex flex-wrap items-start gap-3">
            <RcKop icoon={Award} tint="emerald" titel="Staffelkorting vastrecht" sub={`Boven ${RC_FMT_NUM(uit.staffel.drempel)} actieve EAN's betaal je per schijf minder vastrecht aan de leverancier. Het voordeel zit in de opbrengst hierboven; hier zie je hoeveel het is.`} />
            <div className="ml-auto text-right">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Voordeel · 5 jaar · {rcCase(actief).label}</div>
              <div className="text-2xl font-bold text-emerald-400 tabular-nums">{RC_FMT_EUR0(uit.staffel.cumulatief)}</div>
            </div>
          </div>
          <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-5">
            <div className="flex flex-col gap-2">
              <div className="text-xs text-slate-400">Schijven · vastrecht per EAN per maand</div>
              {MENUKAART.vastrecht.staffel.map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-slate-700/60 bg-slate-800/40 px-3 py-2 text-sm">
                  <span className="text-slate-300">{i === 0 ? `Tot en met ${RC_FMT_NUM(s.tot)}` : s.tot === Infinity ? `Vanaf ${RC_FMT_NUM(MENUKAART.vastrecht.staffel[i - 1].tot + 1)}` : `${RC_FMT_NUM(MENUKAART.vastrecht.staffel[i - 1].tot + 1)} tot en met ${RC_FMT_NUM(s.tot)}`}</span>
                  <span className={`font-semibold tabular-nums ${i === 0 ? 'text-slate-200' : 'text-emerald-300'}`}>{s.perMaand.toLocaleString('nl-BE', { minimumFractionDigits: 2 })} €{i === 0 ? ' (standaard)' : ''}</span>
                </div>
              ))}
              <p className="text-[11px] text-slate-500 leading-snug mt-1">Voorwaarde: de korting geldt alleen zolang het percentage wanbetalers in "Statusverdeling vergoeding" niet boven {MENUKAART.vastrecht.staffelWanbetalersMaxPct} % uitkomt op het moment van de maandelijkse goedkeuringsmail. Erboven geldt het standaardtarief tot het percentage weer onder de drempel zakt. De berekening gaat ervan uit dat je aan de voorwaarde voldoet.{Number(kase.g.share) < 100 ? ` Het voordeel telt mee voor jouw margeaandeel (${RC_FMT_PCT(kase.g.share)}).` : ''}</p>
            </div>
            <div className="min-w-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[440px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-700/60">
                    <th className="text-left py-2 pr-3 font-medium">Per jaar</th>
                    {RC_JAAR.map((jaar) => <th key={jaar} className="text-right py-2 px-2 font-medium whitespace-nowrap">{jaar}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-700/30"><td className="py-2 pr-3 text-slate-300">Actieve EAN's</td>{uit.actief.map((v, i) => <td key={i} className="py-2 px-2 text-right tabular-nums text-slate-300">{RC_FMT_NUM(Math.round(v))}</td>)}</tr>
                  <tr className="border-b border-slate-700/30"><td className="py-2 pr-3 text-slate-300">Vastrecht zonder staffel</td>{uit.staffel.detail.map((d, i) => <td key={i} className="py-2 px-2 text-right tabular-nums text-slate-400">{RC_FMT_EUR0(d.standaard)}</td>)}</tr>
                  <tr className="border-b border-slate-700/30"><td className="py-2 pr-3 text-slate-300">Vastrecht met staffel</td>{uit.staffel.detail.map((d, i) => <td key={i} className="py-2 px-2 text-right tabular-nums text-slate-400">{RC_FMT_EUR0(d.kost)}</td>)}</tr>
                  <tr className="bg-slate-800/60"><td className="py-2 pr-3 text-xs font-semibold text-slate-300 rounded-l-lg">Voordeel voor jou</td>{uit.staffel.voordeel.map((v, i) => <td key={i} className={`py-2 px-2 text-right tabular-nums font-semibold text-emerald-300 ${i === RC_JAREN - 1 ? 'rounded-r-lg' : ''}`}>{RC_FMT_EUR0(v)}</td>)}</tr>
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      ) : (
        <p className="text-[11px] text-slate-500 px-1">Staffelkorting: vanaf {RC_FMT_NUM(uit.staffel.drempel + 1)} actieve EAN's (alle segmenten samen) daalt het vastrecht van 2,50 naar 2,00 € per EAN per maand, en vanaf {RC_FMT_NUM(MENUKAART.vastrecht.staffel[1].tot + 1)} naar 1,75 €, per schijf en zolang het percentage wanbetalers onder {MENUKAART.vastrecht.staffelWanbetalersMaxPct} % blijft. De calculator rekent het voordeel automatisch mee zodra een scenario die grens haalt.</p>
      )}

      {/* Prognose over vijf jaar: één dynamische weergave. De maat (€ / MWh / EAN's)
          stuurt de grafieken én de detailtabel; de scenario-chips bepalen welke
          scenario's in de grafieken staan. Bevat alles wat de vroegere blokken
          "Actieve EAN's en volume per jaar" en "Opbrengst per jaar en cumulatief" toonden. */}
      <Card className="!p-5 min-w-0">
        <RcKop icoon={TrendingUp} tint="violet" titel={`Prognose per jaar · ${rcCase(actief).label}`} sub={grafiekMaat === 'mwh' ? "Geleverd volume = volume per EAN × actieve EAN's in dat jaar; de lijn is de lopende som." : grafiekMaat === 'ean' ? "Actieve EAN's per jaar = vorig jaar × (1 − churn) + nieuwe instroom; de tweede grafiek toont die instroom." : 'Partneropbrengst per scenario over vijf jaar; de lijn is de lopende som.'}>
          <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden text-xs shadow-sm" role="group" aria-label="Maat">
            <button type="button" onClick={() => setGrafiekMaat('eur')} className={RC_SCHAKEL(grafiekMaat === 'eur')} title="Partneropbrengst in euro">Opbrengst €</button>
            <button type="button" onClick={() => setGrafiekMaat('mwh')} className={`${RC_SCHAKEL(grafiekMaat === 'mwh')} border-l border-slate-700`} title="Geleverd volume in MWh">Volume MWh</button>
            <button type="button" onClick={() => setGrafiekMaat('ean')} className={`${RC_SCHAKEL(grafiekMaat === 'ean')} border-l border-slate-700`} title="Actieve aansluitingen">EAN's</button>
          </div>
          <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden text-xs shadow-sm" role="group" aria-label="Scenario's in de grafieken">
            {RC_CASES.map((c, i) => { const aan = grafiekCases.includes(c.id); return (
              <button key={c.id} type="button" onClick={() => wisselGrafiekCase(c.id)} aria-pressed={aan} title={aan ? `${c.label} verbergen` : `${c.label} tonen`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors ${i > 0 ? 'border-l border-slate-700' : ''} ${aan ? 'bg-slate-800 text-slate-100 font-semibold' : 'bg-slate-800/60 text-slate-500 hover:bg-slate-700 hover:text-slate-300'}`}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: RC_CASE_KLEUR[c.id], opacity: aan ? 1 : 0.35 }} />{c.kort}
              </button>
            ); })}
          </div>
        </RcKop>
        {allesNul ? <TableEmpty message="Vul nieuwe EAN's in om een prognose te zien" icon={Folder} /> : (
          <div className="space-y-5">
            {/* Boven: per jaar per scenario (staven) en de lopende som (lijn). */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="min-w-0">
                <div className="text-xs text-slate-400 mb-1">{maatWoord} per jaar · per scenario</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={dataJaren} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="20%" barGap={3}>
                    <defs>
                      {RC_CASE_IDS.map((id) => (
                        <linearGradient key={id} id={`rcStaaf-${id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0" stopColor={RC_CASE_KLEUR[id]} stopOpacity={1} />
                          <stop offset="1" stopColor={RC_CASE_KLEUR[id]} stopOpacity={0.55} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,.16)" vertical={false} />
                    <XAxis dataKey="jaar" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAs} width={78} axisLine={false} tickLine={false} />
                    <Tooltip {...tooltipEur} cursor={{ fill: 'rgba(148,163,184,.08)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {getoondeCases.map((id) => <Bar key={id} dataKey={id} name={rcCase(id).label} fill={`url(#rcStaaf-${id})`} stroke={RC_CASE_KLEUR[id]} strokeWidth={1} radius={[6, 6, 0, 0]} maxBarSize={40} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="min-w-0">
                <div className="text-xs text-slate-400 mb-1">{grafiekMaat === 'ean' ? "Nieuwe EAN's per jaar · instroom per scenario" : `Cumulatief · ${maatWoord.toLowerCase()} · lopende som`}</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dataTweede} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,.16)" vertical={false} />
                    <XAxis dataKey="jaar" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAs} width={78} axisLine={false} tickLine={false} />
                    <Tooltip {...tooltipEur} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {getoondeCases.map((id) => <Line key={id} type="monotone" dataKey={id} name={rcCase(id).label} stroke={RC_CASE_KLEUR[id]} strokeWidth={2.5} dot={{ r: 4, strokeWidth: 2, fill: 'var(--chart-surface, #fff)' }} activeDot={{ r: 6 }} />)}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* Onder: het actieve scenario per segment — grafiek links, detailtabel rechts. */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-5 pt-4 border-t border-slate-700/50">
              <div className="min-w-0">
                <div className="text-xs text-slate-400 mb-1">Opbouw per segment · {maatWoord.toLowerCase()} · {rcCase(actief).label}</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={dataSeg} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="32%">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,.16)" vertical={false} />
                    <XAxis dataKey="jaar" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAs} width={78} axisLine={false} tickLine={false} />
                    <Tooltip {...tooltipEur} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {toonStaffel && <Bar dataKey="staffel" name="Staffelkorting vastrecht" stackId="seg" fill={RC_STAFFEL_KLEUR} stroke="var(--chart-surface, #fff)" strokeWidth={1} radius={[5, 5, 0, 0]} maxBarSize={34} />}
                    {RC_ORDER.map((k, i) => <Bar key={k} dataKey={k} name={RC_SEG_KORT[k]} stackId="seg" fill={RC_SEG_KLEUR[k]} stroke="var(--chart-surface, #fff)" strokeWidth={1} radius={!toonStaffel && i === RC_ORDER.length - 1 ? [5, 5, 0, 0] : 0} maxBarSize={44} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="min-w-0 overflow-x-auto">
                <div className="text-xs text-slate-400 mb-1">Detail per segment · {maatEenheid} · {rcCase(actief).label}</div>
                <table className="w-full text-sm min-w-[440px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-700/60">
                      <th className="text-left py-2 pr-3 font-medium">Segment</th>
                      {RC_JAAR.map((jaar) => <th key={jaar} className="text-right py-2 px-2 font-medium whitespace-nowrap">{jaar}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {RC_ORDER.map((k) => (
                      <tr key={k} className="border-b border-slate-700/30">
                        <td className="py-2 pr-3 text-slate-200"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: RC_SEG_KLEUR[k] }} />{RC_SEG_KORT[k]}</span></td>
                        {uit.perSegment[k].total[reeks].map((v, i) => <td key={i} className="py-2 px-2 text-right tabular-nums text-slate-300">{fmtMaat(v)}</td>)}
                      </tr>
                    ))}
                    {grafiekMaat === 'eur' && uit.staffel.actief && (
                      <tr className="border-b border-slate-700/30">
                        <td className="py-2 pr-3 text-slate-200"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: RC_STAFFEL_KLEUR }} />Staffelkorting vastrecht</span></td>
                        {uit.staffel.voordeel.map((v, i) => <td key={i} className="py-2 px-2 text-right tabular-nums text-slate-300">{fmtMaat(v)}</td>)}
                      </tr>
                    )}
                    <tr className="bg-slate-800/60">
                      <td className="py-2 pr-3 text-xs font-semibold text-slate-300 rounded-l-lg">Totaal</td>
                      {uit[reeks].map((v, i) => <td key={i} className={`py-2 px-2 text-right tabular-nums font-semibold text-slate-100 ${i === RC_JAREN - 1 ? 'rounded-r-lg' : ''}`}>{fmtMaat(v)}</td>)}
                    </tr>
                    {grafiekMaat !== 'ean' && (
                      <tr>
                        <td className="py-2 pr-3 text-xs text-slate-400">Cumulatief</td>
                        {uit[reeks].reduce((acc, v) => { acc.push((acc.length ? acc[acc.length - 1] : 0) + v); return acc; }, []).map((v, i) => <td key={i} className="py-2 px-2 text-right tabular-nums text-slate-400">{fmtMaat(v)}</td>)}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Vergelijking van de drie scenario's. De prijsformule staat sinds 25-09-2026
          beknopt onder de (i) bij "Verkoopprijs" in de producttabel. */}
      <div>
        <Card className="!p-5 min-w-0">
          <RcKop icoon={Target} tint="amber" titel="Vergelijking van de scenario's" sub="Opbrengst per jaar, cumulatief, het EAN-bestand en het volume per scenario." />
          <div className="overflow-x-auto">
            <table className="w-full max-w-[880px] text-sm min-w-[560px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-700/60">
                  <th className="text-left py-2 pr-3 font-medium">Periode</th>
                  {RC_CASES.map((c) => (
                    <th key={c.id} className={`text-right py-2 px-2 font-medium whitespace-nowrap ${c.id === actief ? 'text-slate-200' : ''}`}>
                      <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: RC_CASE_KLEUR[c.id] }} />{c.label}</span>
                      <span className="block text-[9px] normal-case text-slate-600">groei {RC_FMT_PCT(staat.cases[c.id].g.groei)} · churn {RC_FMT_PCT(staat.cases[c.id].g.churn)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RC_JAAR.map((jaar, i) => (
                  <tr key={jaar} className="border-b border-slate-700/40 align-top hover:bg-slate-800/30">
                    <td className="py-2.5 pr-3 text-slate-200">{jaar}</td>
                    {RC_CASE_IDS.map((id) => <td key={id} className={`py-2.5 px-2 text-right tabular-nums ${id === actief ? 'text-slate-100 font-semibold' : 'text-slate-300'}`}>{RC_FMT_EUR0(uitPerCase[id].jaren[i])}</td>)}
                  </tr>
                ))}
                <tr className="bg-slate-800/60 align-top">
                  <td className="py-2.5 pr-3 text-xs font-semibold text-slate-300">Cumulatief 5 jaar</td>
                  {RC_CASE_IDS.map((id) => <td key={id} className="py-2.5 px-2 text-right tabular-nums font-semibold text-base" style={{ color: RC_CASE_KLEUR[id] }}>{RC_FMT_EUR0(cum(id))}</td>)}
                </tr>
                <tr className="align-top">
                  <td className="py-2 pr-3 text-xs text-slate-400">Actieve EAN's na 5 jaar</td>
                  {RC_CASE_IDS.map((id) => <td key={id} className="py-2 px-2 text-right tabular-nums text-slate-300">{RC_FMT_NUM(Math.round(uitPerCase[id].actiefEind))}</td>)}
                </tr>
                <tr className="align-top border-t border-slate-700/30">
                  <td className="py-2 pr-3 text-xs text-slate-400">Volume jaar 5 (MWh)<span className="block text-[11px] text-slate-500">cumulatief 5 jaar</span></td>
                  {RC_CASE_IDS.map((id) => <td key={id} className="py-2 px-2 text-right tabular-nums text-slate-300">{formatNumberRounded(uitPerCase[id].mwh[RC_JAREN - 1])} MWh<span className="block text-[11px] text-slate-500">{formatNumberRounded(uitPerCase[id].mwh.reduce((a, v) => a + v, 0))} MWh</span></td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

      </div>
      </>)}
    </div>
  );
};

const OptimisePage = ({ token, currentUser, onUnread }) => {
  const fbSort = useTableSort();
  const isAdmin = currentUser?.role === 'admin';
  // Pagina-opties = "Algemeen" + de modules en beheerpagina's die DEZE gebruiker
  // in zijn menu heeft (zie feedbackPaginaOpties).
  const pageOptions = useMemo(() => feedbackPaginaOpties(currentUser), [currentUser]);
  const [form, setForm] = useState({ module: 'Algemeen', type: 'question', priority: 'medium', message: '' });
  // Nog niet verzonden bijlagen (File-objecten). Ze gaan pas naar de server nadat
  // het feedback-item is aangemaakt; de ref laat het native input-veld leegmaken.
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [editWindowMs, setEditWindowMs] = useState(15 * 60 * 1000);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // Admin-filters op het overzicht.
  const [fType, setFType] = useState('all');
  const [fPriority, setFPriority] = useState('all');
  // Admin: standaard alles behalve Afgehandeld. De indiener ziet ál zijn meldingen,
  // anders verdwijnt een kaart onder zijn neus zodra hij op "Opgelost, bedankt" klikt.
  const [fStatus, setFStatus] = useState(isAdmin ? 'niet_afgehandeld' : 'all');
  const [q, setQ] = useState('');
  // Groeperen per pagina (admin): terugkerende problemen op één scherm zien.
  const [groeperen, setGroeperen] = useState(false);
  const [dichteGroepen, setDichteGroepen] = useState(() => new Set());
  // Gesprek op een item: welke draad staat open, wat is er per item getypt en of
  // er op dit moment iets verstuurd wordt.
  const [openDraadId, setOpenDraadId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [replyBusy, setReplyBusy] = useState(false);
  // Nog niet verstuurde bijlagen per draad + de bevestiging na het versturen.
  const [replyFiles, setReplyFiles] = useState({});
  const [verstuurd, setVerstuurd] = useState(null);
  // Per draad: gewoon bericht of interne notitie (alleen admin).
  const [replyKind, setReplyKind] = useState({});
  // Melding waar we naartoe sprongen (uit de URL of via een #12-verwijzing):
  // kort oplichten zodat het oog ze vindt.
  const [lichtOp, setLichtOp] = useState(null);
  const [springNaar, setSpringNaar] = useState(() => fbDeepLink);
  // Volgnummer per ophaalactie: een oudere respons die later binnenkomt (dubbele
  // mount in dev) mag een nieuwere lijst niet overschrijven.
  const loadSeq = useRef(0);

  const load = useMemo(() => async () => {
    const seq = ++loadSeq.current;
    setLoading(true); setError(null);
    try {
      const r = await fetch('/api/feedback', { headers: { Authorization: `Bearer ${token}` } });
      const b = await r.json().catch(() => null);
      if (seq !== loadSeq.current) return; // ingehaald door een nieuwere ophaalactie
      if (!r.ok) throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`);
      setItems(Array.isArray(b.feedback) ? b.feedback : []);
      if (b.editWindowMs) setEditWindowMs(b.editWindowMs);
    } catch (e) { if (seq !== loadSeq.current) return; setError(e.message); setItems([]); }
    finally { if (seq === loadSeq.current) setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const bestaat = (id) => items.some((x) => x.id === id);

  // Naar een melding springen: filters opzij als ze de melding verbergen, draad
  // openen, in beeld scrollen en even laten oplichten.
  const naarMelding = (id) => {
    const it = items.find((x) => x.id === id);
    if (!it) { setError(`Melding #${id} staat niet in je lijst.`); return; }
    if (fStatus !== 'all' && (fStatus === 'niet_afgehandeld' ? it.status === 'done' : it.status !== fStatus)) setFStatus('all');
    if (fType !== 'all' && it.type !== fType) setFType('all');
    if (fPriority !== 'all' && it.priority !== fPriority) setFPriority('all');
    setQ('');
    setDichteGroepen((s) => { if (!s.has(it.module)) return s; const n = new Set(s); n.delete(it.module); return n; });
    if (openDraadId !== id) toggleDraad(id);
    setLichtOp(id);
    setTimeout(() => {
      document.getElementById(`fb-${id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => setLichtOp((v) => (v === id ? null : v)), 2500);
    }, 50);
  };
  // De sprong uit de URL kan pas als de lijst er is.
  useEffect(() => {
    if (loading || springNaar == null) return;
    setSpringNaar(null);
    if (fbDeepLink !== springNaar) return; // al door een eerdere mount afgehandeld
    fbDeepLink = null;
    if (bestaat(springNaar)) naarMelding(springNaar);
    else setError(`Melding #${springNaar} staat niet (meer) in je lijst.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, springNaar]);

  // Bijlagen worden pas geüpload NADAT het feedback-item bestaat: ze hangen aan
  // een id. Slaagt een upload niet, dan blijft de feedback zelf wél staan — dat
  // melden we dan apart in plaats van alles terug te draaien.
  const uploadFiles = (feedbackId, files) => uploadFeedbackBijlagen(feedbackId, files, token);

  const pickFiles = (fileList) => {
    const gekozen = Array.from(fileList || []);
    if (!gekozen.length) return;
    const teGroot = gekozen.filter((f) => f.size > FB_MAX_FILE_MB * 1024 * 1024).map((f) => f.name);
    const rest = gekozen.filter((f) => f.size <= FB_MAX_FILE_MB * 1024 * 1024);
    setAttachments((cur) => {
      const ruimte = Math.max(0, FB_MAX_FILES - cur.length);
      if (rest.length > ruimte) setError(`Maximaal ${FB_MAX_FILES} bijlagen per feedback.`);
      return [...cur, ...rest.slice(0, ruimte)];
    });
    if (teGroot.length) setError(`Te groot (max ${FB_MAX_FILE_MB} MB): ${teGroot.join(', ')}`);
    else if (!teGroot.length && rest.length) setError(null);
  };

  const submit = async () => {
    if (!form.message.trim()) { setError('Schrijf eerst je feedback.'); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await fetch('/api/feedback', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        // Ook hier gaat de technische context mee — zelfde gegevens als het
        // venster achter een paginatitel, zodat elke melding even bruikbaar is.
        body: JSON.stringify({ ...form, message: form.message.trim(), context: verzamelFeedbackContext({ moduleId: 'optimise', moduleLabel: form.module, user: currentUser }) }) });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`);
      const mislukt = attachments.length ? await uploadFiles(b.feedback?.id, attachments) : [];
      setForm((f) => ({ ...f, message: '' }));
      setAttachments([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (mislukt.length) setError(`Feedback opgeslagen, maar ${mislukt.length} bijlage(n) niet: ${mislukt.join(' · ')}`);
      else setNotice('Bedankt! Je feedback is opgeslagen en zichtbaar voor het platformteam.');
      load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const removeFile = async (f) => {
    try {
      const r = await fetch(`/api/feedback/files/${f.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || `HTTP ${r.status}`); }
      setItems((list) => list.map((it) => ({ ...it, files: (it.files || []).filter((x) => x.id !== f.id) })));
    } catch (e) { setError(e.message); }
  };

  // Een draad openklappen betekent: gelezen. Het telletje op het item én de stip
  // in het menu zakken meteen mee — het serverantwoord hoeft daar niet op gewacht.
  const toggleDraad = async (id) => {
    const openen = openDraadId !== id;
    setOpenDraadId(openen ? id : null);
    if (!openen) return;
    const it = items.find((x) => x.id === id);
    if (!it || !it.unread) return;
    setItems((list) => list.map((x) => (x.id === id ? { ...x, unread: 0 } : x)));
    try {
      await fetch(`/api/feedback/${id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      // Nogmaals: een lijst-respons die nog onderweg was (dubbele load in dev,
      // de sprong uit de mail) mag het telletje niet terugzetten.
      setItems((list) => list.map((x) => (x.id === id ? { ...x, unread: 0 } : x)));
      if (onUnread) onUnread();
    } catch (_) { /* stil — de teller klopt weer bij de volgende keer laden */ }
  };

  // Reageren in de draad. Voor de admin is dit de bijkomende vraag aan de
  // indiener (of een interne notitie), voor de indiener het antwoord daarop; de
  // API bepaalt zelf wie wie is. Bijlagen bij het bericht (schermafbeelding,
  // bestand) gaan NA het bericht: ze hangen aan het id ervan. Een bericht mag
  // leeg zijn als er een bijlage bij zit.
  const sendReply = async (id) => {
    const message = (drafts[id] || '').trim();
    const bijlagen = replyFiles[id] || [];
    const kind = isAdmin ? (replyKind[id] || 'message') : 'message';
    if (!message && !bijlagen.length) return;
    setReplyBusy(true); setError(null); setVerstuurd(null);
    try {
      const r = await fetch(`/api/feedback/${id}/replies`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, metBijlagen: bijlagen.length > 0, kind }),
      });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`);
      const reply = { ...b.reply, files: [] };
      const mislukt = [];
      for (const file of bijlagen) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const u = await fetch(`/api/feedback/${id}/replies/${reply.id}/files`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
          const ub = await u.json().catch(() => null);
          if (!u.ok) throw new Error((ub && ub.error) || `HTTP ${u.status}`);
          reply.files.push(ub.file);
        } catch (e) { mislukt.push(`${file.name}: ${e.message}`); }
      }
      setItems((list) => list.map((x) => (x.id === id ? { ...x, replies: [...(x.replies || []), reply], unread: 0 } : x)));
      setDrafts((d) => ({ ...d, [id]: '' }));
      setReplyFiles((d) => ({ ...d, [id]: [] }));
      if (mislukt.length) setError(`Bericht verstuurd, maar niet elke bijlage: ${mislukt.join('; ')}`);
      else setVerstuurd({ id, tekst: kind === 'internal' ? 'Interne notitie bewaard — alleen zichtbaar voor beheerders.' : isAdmin ? 'Verstuurd — de indiener ziet je bericht op zijn Optimise-pagina en krijgt een mail.' : 'Verstuurd naar het platformteam.' });
      if (onUnread) onUnread();
    } catch (e) { setError(e.message); }
    finally { setReplyBusy(false); }
  };
  // Bijlage bij een eigen bericht weer weghalen (zelfde route als bij de melding).
  const removeReplyFile = async (reply, f) => {
    try {
      const r = await fetch(`/api/feedback/files/${f.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && b.error) || `HTTP ${r.status}`); }
      setItems((list) => list.map((it) => ({ ...it, replies: (it.replies || []).map((c) => (c.id === reply.id ? { ...c, files: (c.files || []).filter((x) => x.id !== f.id) } : c)) })));
    } catch (e) { setError(e.message); }
  };
  // Eigen bericht bewerken (typefout) — kort na het versturen; de server bewaakt het venster.
  const editReply = async (id, replyId, message) => {
    setReplyBusy(true); setError(null);
    try {
      const r = await fetch(`/api/feedback/${id}/replies/${replyId}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ message }) });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`);
      setItems((list) => list.map((it) => (it.id === id ? { ...it, replies: (it.replies || []).map((c) => (c.id === replyId ? { ...c, ...b.reply, files: c.files } : c)) } : it)));
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setReplyBusy(false); }
  };
  // Bericht intrekken — eigen bericht binnen het venster, of als beheerder.
  const deleteReply = async (id, reply) => {
    if (!window.confirm(reply.kind === 'status' ? 'Deze statusregel uit het gesprek verwijderen?' : 'Dit bericht intrekken?')) return;
    try {
      const r = await fetch(`/api/feedback/${id}/replies/${reply.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`); }
      setItems((list) => list.map((it) => (it.id === id ? { ...it, replies: (it.replies || []).filter((c) => c.id !== reply.id) } : it)));
    } catch (e) { setError(e.message); }
  };

  // Status zetten. De server laat een systeemregel achter in de draad en geeft
  // die terug; hij komt meteen onderaan het gesprek te staan.
  const updateStatus = async (id, status) => {
    try {
      const r = await fetch(`/api/feedback/${id}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      const b = await r.json().catch(() => null);
      if (!r.ok) throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`);
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status, replies: b && b.reply ? [...(it.replies || []), { ...b.reply, files: [] }] : it.replies } : it)));
      if (!isAdmin) setNotice(status === 'done' ? `Melding #${id} staat op Afgehandeld. Bedankt voor de terugkoppeling!` : `Melding #${id} is heropend — het platformteam bekijkt ze opnieuw.`);
    } catch (e) { setError(e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm('Deze feedback verwijderen?')) return;
    try {
      const r = await fetch(`/api/feedback/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b && (b.error || b.detail)) || `HTTP ${r.status}`); }
      setItems((list) => list.filter((it) => it.id !== id));
    } catch (e) { setError(e.message); }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((it) => {
      if (fType !== 'all' && it.type !== fType) return false;
      if (fPriority !== 'all' && it.priority !== fPriority) return false;
      if (fStatus === 'niet_afgehandeld') { if (it.status === 'done') return false; } else if (fStatus !== 'all' && it.status !== fStatus) return false;
      if (term) {
        const hay = `#${it.id} ${it.message || ''} ${it.module || ''} ${it.user?.name || ''} ${it.user?.email || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [items, fType, fPriority, fStatus, q]);

  const gesorteerd = useMemo(() => sortRows(filtered, fbSort.sortConfig, { wie: (it) => it.user?.name || '' }), [filtered, fbSort.sortConfig]);
  // Groepen per pagina: grootste groep bovenaan, met een telling per status.
  const groepen = useMemo(() => {
    if (!groeperen) return null;
    const m = new Map();
    for (const it of gesorteerd) { const k = it.module || 'Algemeen'; if (!m.has(k)) m.set(k, []); m.get(k).push(it); }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [gesorteerd, groeperen]);
  const toggleGroep = (k) => setDichteGroepen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const rijKlasse = (id) => (lichtOp === id ? 'ring-2 ring-lime-400/70 ring-inset' : '');

  const draadProps = (it) => ({
    item: it, busy: replyBusy, draft: drafts[it.id], token, editWindowMs, onNaar: naarMelding, bestaat,
    onDraft: (v) => setDrafts((d) => ({ ...d, [it.id]: v })), onSend: () => sendReply(it.id),
    files: replyFiles[it.id] || [], onFiles: (fs) => setReplyFiles((d) => ({ ...d, [it.id]: fs })),
    onDeleteFile: removeReplyFile, onError: setError, verstuurd: verstuurd?.id === it.id ? verstuurd.tekst : null,
    onEdit: (replyId, tekst) => editReply(it.id, replyId, tekst), onDeleteReply: (r) => deleteReply(it.id, r),
  });

  // Eén tabelrij (+ uitgeklapte gespreksrij) in het beheeroverzicht.
  const rij = (it) => <React.Fragment key={it.id}>
    <tr id={`fb-${it.id}`} className={`border-t border-slate-700/40 align-top ${rijKlasse(it.id)}`}>
      <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap tabular-nums">#{it.id}</td>
      <td className="px-3 py-2 text-slate-200 whitespace-nowrap">{it.user?.name || '—'}<div className="text-xs text-slate-500">{it.user?.email || ''}{it.user?.role ? ` · ${it.user.role}` : ''}</div></td>
      <td className="px-3 py-2 text-slate-400 text-xs whitespace-nowrap">{fbFmt(it.created_at)}</td>
      {!groeperen && <td className="px-3 py-2 text-slate-300 text-xs whitespace-nowrap">{it.module}</td>}
      <td className="px-3 py-2"><FbBadge cls={FB_TYPE_CLS[it.type]}>{fbLabel(FEEDBACK_TYPES, it.type)}</FbBadge></td>
      <td className="px-3 py-2"><FbBadge cls={FB_PRIO_CLS[it.priority]}>{fbLabel(FEEDBACK_PRIORITIES, it.priority)}</FbBadge></td>
      <td className="px-3 py-2 text-slate-300 max-w-md whitespace-pre-wrap"><FbTekst tekst={it.message} onNaar={naarMelding} bestaat={bestaat} /></td>
      <td className="px-3 py-2"><FbAttachments files={it.files} token={token} onDelete={removeFile} onError={setError} compact /></td>
      <td className="px-3 py-2"><FbDraadKnop item={it} open={openDraadId === it.id} onClick={() => toggleDraad(it.id)} /></td>
      <td className="px-3 py-2"><select value={it.status} onChange={(e) => updateStatus(it.id, e.target.value)} className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200">{FEEDBACK_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></td>
      <td className="px-3 py-2 text-right"><button onClick={() => remove(it.id)} title="Verwijderen" className="text-slate-500 hover:text-red-400"><Trash2 size={15} /></button></td>
    </tr>
    {/* Uitgeklapte gespreksrij — pas zichtbaar na een klik op het gespreksknopje. */}
    {openDraadId === it.id && <tr className="bg-slate-800/40"><td colSpan={11} className="px-3 pb-3 pt-1">
      <FbDraad {...draadProps(it)} isAdmin kind={replyKind[it.id] || 'message'} onKind={(k) => setReplyKind((d) => ({ ...d, [it.id]: k }))} />
    </td></tr>}
  </React.Fragment>;

  // Groepskop: pagina, aantal, en per status hoeveel — in één oogopslag zie je
  // waar het platform het vaakst wringt.
  const groepKop = (k, lijst) => {
    const dicht = dichteGroepen.has(k);
    const perStatus = FEEDBACK_STATUSES.map((s) => [s, lijst.filter((x) => x.status === s.id).length]).filter(([, n]) => n > 0);
    const nieuw = lijst.reduce((a, x) => a + (x.unread || 0), 0);
    return <tr key={`g-${k}`} className="bg-slate-800/70 border-t border-slate-700/60 cursor-pointer select-none" onClick={() => toggleGroep(k)}>
      <td colSpan={11} className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {dicht ? <ChevronRight size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          <span className="font-semibold text-slate-100">{k}</span>
          <span className="text-xs text-slate-400">{lijst.length} melding{lijst.length === 1 ? '' : 'en'}</span>
          {nieuw > 0 && <span className="px-1.5 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold leading-4" title="Nieuwe berichten in de gesprekken">{nieuw}</span>}
          <span className="ml-auto flex flex-wrap items-center gap-1.5">
            {perStatus.map(([s, n]) => <FbBadge key={s.id} cls={FB_STATUS_CLS[s.id]}>{s.label} {n}</FbBadge>)}
          </span>
        </div>
      </td>
    </tr>;
  };

  return <div className="space-y-6">
    <PageHeader title="Optimise" subtitle="Feedback over het partnerportaal — help ons het platform te verbeteren" />

    {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded text-sm">{error}</div>}
    {notice && <div className="bg-lime-500/10 border border-lime-500/30 text-lime-300 px-4 py-2 rounded text-sm">{notice}</div>}

    {/* Invoerformulier — voor ALLE gebruikers. */}
    <Card>
      <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2"><Lightbulb size={18} className="text-lime-400" />Nieuwe feedback</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Pagina / module</label>
          <select value={form.module} onChange={(e) => set('module', e.target.value)} className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100">
            {pageOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Type</label>
          <select value={form.type} onChange={(e) => set('type', e.target.value)} className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100">
            {FEEDBACK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Belangrijkheid</label>
          <select value={form.priority} onChange={(e) => set('priority', e.target.value)} className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100">
            {FEEDBACK_PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3">
        <label className="block text-xs text-slate-400 mb-1">Je feedback</label>
        <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4} maxLength={4000} placeholder="Beschrijf je opmerking, idee of het probleem zo concreet mogelijk…" className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-100" />
        <div className="text-xs text-slate-500 mt-1">{form.message.length}/4000</div>
      </div>

      {/* Bijlagen — vooral bedoeld voor een screenshot van het probleem. */}
      <div className="mt-3">
        <label className="block text-xs text-slate-400 mb-1">Bijlagen <span className="text-slate-500">(optioneel — screenshot of PDF, max {FB_MAX_FILES} × {FB_MAX_FILE_MB} MB)</span></label>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={attachments.length >= FB_MAX_FILES}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs rounded-lg">
            <Paperclip size={13} />Bestand kiezen
          </button>
          <input ref={fileInputRef} type="file" multiple accept={FB_ACCEPT} onChange={(e) => { pickFiles(e.target.files); e.target.value = ''; }} className="hidden" />
          {attachments.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 bg-slate-700/50 border border-slate-600/50 rounded text-xs text-slate-200 max-w-[240px]">
              <File size={11} className="text-slate-400 shrink-0" />
              <span className="truncate" title={f.name}>{f.name}</span>
              <span className="text-slate-500 shrink-0">{fbFileSize(f.size)}</span>
              <button type="button" onClick={() => setAttachments((cur) => cur.filter((_, j) => j !== i))} title="Verwijderen" className="text-slate-500 hover:text-red-400 shrink-0"><X size={12} /></button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex justify-end mt-3">
        <button onClick={submit} disabled={busy || !form.message.trim()} className="flex items-center gap-2 px-5 py-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-lg text-sm disabled:opacity-50"><Send size={16} />{busy ? 'Versturen…' : 'Feedback versturen'}</button>
      </div>
    </Card>

    {/* Overzicht: admin = alles (tabel), gebruiker = eigen ingaves (kaarten). */}
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="text-lg font-semibold text-slate-100">{isAdmin ? 'Alle feedback' : 'Mijn feedback'}{!loading && ` (${filtered.length})`}</h3>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <select value={fType} onChange={(e) => setFType(e.target.value)} className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200"><option value="all">Alle types</option>{FEEDBACK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
            <select value={fPriority} onChange={(e) => setFPriority(e.target.value)} className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200"><option value="all">Alle prioriteiten</option>{FEEDBACK_PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200"><option value="niet_afgehandeld">Alle behalve afgehandeld</option><option value="all">Alle statussen</option>{FEEDBACK_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select>
            <div className="relative"><Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoeken…" className="pl-7 pr-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200" /></div>
            <button type="button" onClick={() => setGroeperen((g) => !g)} title="Meldingen groeperen per pagina, zodat terugkerende problemen opvallen"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs transition-colors ${groeperen ? 'bg-lime-500/20 border-lime-500/50 text-lime-200' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}>
              <Layers size={13} />Per pagina
            </button>
          </div>
        )}
      </div>

      {loading ? <Card className="!p-3"><ListSkeleton rows={8} /></Card> : filtered.length === 0 ? (
        <EmptyState message={items.length === 0 ? (isAdmin ? 'Er is nog geen feedback ingediend.' : 'Je hebt nog geen feedback ingediend. Deel hierboven je eerste opmerking of idee.') : 'Geen feedback voor deze filters.'} />
      ) : isAdmin ? (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full text-sm resp-card">
            <thead className="bg-slate-800 text-slate-400 text-xs"><tr>
              <SortHeader label="#" k="id" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />
              <SortHeader label="Wie" k="wie" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />
              <SortHeader label="Wanneer" k="created_at" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />
              {!groeperen && <SortHeader label="Pagina" k="module" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />}
              <SortHeader label="Type" k="type" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />
              <SortHeader label="Prioriteit" k="priority" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />
              <SortHeader label="Feedback" k="message" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />
              <th className="px-3 py-2 font-medium text-left">Bijlagen</th>
              <th className="px-3 py-2 font-medium text-left">Gesprek</th>
              <SortHeader label="Status" k="status" sortConfig={fbSort.sortConfig} onSort={fbSort.handleSort} thClass="px-3 py-2 font-medium" />
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {groepen
                ? groepen.map(([k, lijst]) => [groepKop(k, lijst), ...(dichteGroepen.has(k) ? [] : lijst.map(rij))])
                : gesorteerd.map(rij)}
            </tbody>
          </table></div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => <Card key={it.id} id={`fb-${it.id}`} className={`!p-4 ${rijKlasse(it.id)} ${it.status === 'done' ? 'opacity-75' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500 tabular-nums">#{it.id}</span>
                <FbBadge cls={FB_TYPE_CLS[it.type]}>{fbLabel(FEEDBACK_TYPES, it.type)}</FbBadge>
                <FbBadge cls={FB_PRIO_CLS[it.priority]}>{fbLabel(FEEDBACK_PRIORITIES, it.priority)}</FbBadge>
                <span className="text-xs text-slate-400">{it.module}</span>
                <FbBadge cls={FB_STATUS_CLS[it.status]}>{fbLabel(FEEDBACK_STATUSES, it.status)}</FbBadge>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500">{fbFmt(it.created_at)}</span>
                <button onClick={() => remove(it.id)} title="Verwijderen" className="text-slate-500 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
            <p className="text-sm text-slate-200 mt-2 whitespace-pre-wrap"><FbTekst tekst={it.message} onNaar={naarMelding} bestaat={bestaat} /></p>
            {it.files?.length > 0 && <div className="mt-2"><FbAttachments files={it.files} token={token} onDelete={removeFile} onError={setError} /></div>}
            {/* Gesprek met het platformteam. Staat er een vraag klaar, dan valt de
                knop op met een amberen telletje en een regel ernaast. Rechts: zelf
                afsluiten ("Opgelost, bedankt") of een afgesloten melding heropenen. */}
            <div className="mt-3 pt-3 border-t border-slate-700/40">
              <div className="flex flex-wrap items-center gap-2">
                <FbDraadKnop item={it} open={openDraadId === it.id} onClick={() => toggleDraad(it.id)} />
                <span className="text-xs text-slate-400">
                  {it.unread > 0 ? 'Het platformteam heeft gereageerd.' : (it.replies?.length ? 'Gesprek over deze melding' : 'Reageren of iets toevoegen')}
                </span>
                {it.status === 'done'
                  ? <button type="button" onClick={() => updateStatus(it.id, 'open')} title="Toch niet opgelost? Zet de melding weer open." className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-600/60 text-xs text-slate-300 hover:bg-slate-700/50"><RotateCcw size={12} />Heropenen</button>
                  : <button type="button" onClick={() => updateStatus(it.id, 'done')} title="Sluit de melding zelf af: het platformteam ziet dat ze opgelost is." className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-lime-500/40 bg-lime-500/10 text-xs text-lime-300 hover:bg-lime-500/20"><Check size={12} />Opgelost, bedankt</button>}
              </div>
              {openDraadId === it.id && <div className="mt-3">
                <FbDraad {...draadProps(it)} isAdmin={false} />
              </div>}
            </div>
          </Card>)}
        </div>
      )}
    </div>
  </div>;
};

// True wanneer de app binnen het device-preview-iframe draait. Voorkomt dat de
// preview-knop opnieuw verschijnt (oneindige nesting) binnen het frame.
const IS_PREVIEW_FRAME = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('previewFrame');
// True wanneer de geplande partnervergoeding-mail deze app in headless Chromium
// opent om de zip per doel te bouwen (zie de haak in RevenuePage).
const IS_FEE_MAIL_JOB = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('feeMailJob');

// Device-preview (admin, op desktop): toont het volledige platform binnen een
// toestel-frame op tablet-/smartphone-breedte. Cruciaal: een echt <iframe> op de
// EIGEN origin — zo reageren de CSS-media-queries (Tailwind md:) op de framebreedte
// en zie je de échte mobiele layout (off-canvas menu, gestapelde kaarten, tabs…).
// Token zit in localStorage (gedeeld per origin) → het frame is automatisch ingelogd.
const DEVICE_PRESETS = {
  phone: { label: 'Smartphone', icon: Smartphone, w: 390, h: 844 },
  tablet: { label: 'Tablet', icon: Tablet, w: 834, h: 1112 },
};
const DevicePreview = ({ onClose, page }) => {
  const [device, setDevice] = useState('phone');
  const [landscape, setLandscape] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const preset = DEVICE_PRESETS[device];
  const w = landscape ? preset.h : preset.w;
  const h = landscape ? preset.w : preset.h;

  // Zelfde URL + vlag zodat de preview-knop binnen het frame verborgen blijft.
  // De actieve pagina gaat EXPLICIET mee: de app is state-based (geen router), dus
  // de URL draagt de pagina niet. Zonder dit opende het frame op de standaard
  // startpagina i.p.v. de pagina die je wilde bekijken.
  const src = useMemo(() => {
    const u = new URL(window.location.href);
    u.searchParams.set('previewFrame', '1');
    if (page) u.searchParams.set('page', page);
    return u.toString();
  }, [page]);

  // Sluiten met Esc.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Schaal het toestel zodat het — inclusief toolbar en marges — in het venster past.
  const availH = (typeof window !== 'undefined' ? window.innerHeight : 900) - 150;
  const availW = (typeof window !== 'undefined' ? window.innerWidth : 1200) - 60;
  const scale = Math.min(1, availH / h, availW / w);
  const fw = Math.round(w * scale), fh = Math.round(h * scale);

  return (
    <div className="fixed inset-0 z-[200] bg-slate-950/85 backdrop-blur-sm flex flex-col items-center py-5 overflow-auto">
      <div className="flex flex-wrap items-center justify-center gap-1.5 mb-4 bg-slate-900 border border-slate-700 rounded-full px-2 py-1.5 shadow-2xl">
        {Object.entries(DEVICE_PRESETS).map(([key, p]) => (
          <button key={key} onClick={() => setDevice(key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === key ? 'bg-lime-500 text-slate-900' : 'text-slate-300 hover:bg-slate-800'}`}>
            <p.icon size={14} />{p.label}
          </button>
        ))}
        <div className="w-px h-5 bg-slate-700 mx-0.5" />
        <button onClick={() => setLandscape(l => !l)} title="Draai het toestel" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-slate-300 hover:bg-slate-800 transition-colors"><RotateCcw size={14} />{landscape ? 'Liggend' : 'Staand'}</button>
        <button onClick={() => setReloadKey(k => k + 1)} title="Herlaad het frame" className="px-2.5 py-1.5 rounded-full text-slate-300 hover:bg-slate-800 transition-colors"><RefreshCw size={14} /></button>
        <span className="px-2 text-[11px] text-slate-500 tabular-nums hidden sm:inline">{w}×{h}</span>
        <div className="w-px h-5 bg-slate-700 mx-0.5" />
        <button onClick={onClose} title="Sluit preview (Esc)" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-800 text-slate-200 hover:bg-slate-700 transition-colors"><X size={14} />Sluiten</button>
      </div>
      <div className="rounded-[2.2rem] border-[10px] border-slate-800 bg-slate-800 shadow-2xl shrink-0" style={{ width: fw + 20, height: fh + 20 }}>
        <div className="rounded-[1.5rem] overflow-hidden bg-white" style={{ width: fw, height: fh }}>
          <iframe
            key={`${device}-${landscape}-${reloadKey}`}
            title="Mobiele preview"
            src={src}
            style={{ width: w, height: h, border: 0, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          />
        </div>
      </div>
    </div>
  );
};

// ── Welkomstscherm bij het opstarten ──────────────────────────────────────────
// Eén kaart, gebruikt in TWEE fasen van dezelfde koude start:
//  1. de sessiecontrole (AuthenticatedApp): we kennen de gebruiker nog niet, dus
//     zonder naam en met het merk van het DOMEIN als logo;
//  2. het ophalen van de dashboarddata (PartnerDashboard): met naam en met het
//     logo van het energielabel van de ingelogde gebruiker.
// Zo ziet de gebruiker één doorlopend scherm in plaats van eerst een kale spinner
// en daarna iets anders.
//
// De stappen hangen aan ECHTE requests; er wordt geen percentage verzonnen.
const welkomGroetNu = () => {
  const u = new Date().getHours();
  return u < 6 ? 'Goedenacht' : u < 12 ? 'Goedemorgen' : u < 18 ? 'Goedemiddag' : 'Goedenavond';
};

// De kleuren staan hier EXPLICIET en niet als Tailwind slate-classes. Reden: het
// centrale themablok dat `bg-slate-800` in het lichte thema naar wit remapt wordt
// door PartnerDashboard geïnjecteerd, en dat is in fase 1 nog niet gemount. Met
// classes zou dezelfde kaart dus eerst donker en daarna wit zijn.
const WELKOM_KLEUREN = {
  light: { kaart: '#ffffff', rand: '#e2e8f0', titel: '#0f172a', tekst: '#475569', zacht: '#94a3b8', spoor: '#eef2f7', cirkel: '#cbd5e1', doek: '#f1f5f9', sluier: 'rgba(241,245,249,.72)' },
  dark: { kaart: '#1e293b', rand: 'rgba(51,65,85,.7)', titel: '#f1f5f9', tekst: '#cbd5e1', zacht: '#64748b', spoor: 'rgba(51,65,85,.55)', cirkel: '#475569', doek: '#020617', sluier: 'rgba(2,6,23,.72)' },
};

const WelkomstKaart = ({ logo, merknaam, naam, stappen, uiTheme = 'light', fadet = false, overDashboard = false }) => {
  const c = WELKOM_KLEUREN[uiTheme === 'dark' ? 'dark' : 'light'];
  return (
    // Boven de sessiecontrole staat nog niets; boven het dashboard staat de echte,
    // al gerenderde app die we vervagen. Dat laat letterlijk zien dat het dashboard
    // klaargezet wordt, zonder een nagebouwde skeleton die ervan kan afwijken.
    <div
      className={`fixed inset-0 z-[9600] flex items-center justify-center p-4 transition-all duration-[400ms] ease-out ${fadet ? 'opacity-0 scale-[1.03] pointer-events-none' : 'opacity-100 scale-100'}`}
      style={overDashboard
        ? { background: c.sluier, backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)' }
        : { background: c.doek }}
      role="status"
      aria-live="polite"
    >
      {/* Mobiel vrijwel schermbreed (max-w met p-4 eromheen), op desktop compact.
          Zelfde radius en schaduwtaal als de Card-primitieve. */}
      <div
        className="w-full max-w-[26rem] rounded-2xl px-5 py-6 sm:px-7 sm:py-7"
        style={{ background: c.kaart, border: `1px solid ${c.rand}`, boxShadow: '0 18px 44px -20px rgba(15,23,42,.35), 0 2px 6px rgba(15,23,42,.06)' }}
      >
        <div className="flex justify-center mb-5">
          {logo
            ? <img src={logo} alt={merknaam || 'Partner Portal'} className="max-h-11 max-w-[70%] object-contain" />
            : <span className="text-base font-semibold" style={{ color: c.titel }}>{merknaam || 'Partner Portal'}</span>}
        </div>

        <h1 className="text-xl sm:text-2xl font-bold text-center" style={{ color: c.titel }}>
          {naam ? `${welkomGroetNu()}, ${naam}` : welkomGroetNu()}
        </h1>
        <p className="mt-1.5 text-sm text-center" style={{ color: c.tekst }}>We zetten je partnerportaal klaar.</p>
        <p className="mt-2 text-xs text-center leading-relaxed" style={{ color: c.zacht }}>
          Je meest recente gegevens worden opgehaald uit je orders, facturen en klantenportefeuille.
        </p>

        {/* Onbepaalde voortgang in de merkkleur: hoelang de fetches duren is niet
            vooraf bekend, dus een percentage zou verzonnen zijn. */}
        <div className="welkom-balk relative mt-5 h-1 w-full rounded-full overflow-hidden" style={{ background: c.spoor }} />

        <ul className="mt-5 space-y-2.5">
          {stappen.map((s, i) => {
            // Actief = de eerste stap die nog niet klaar is. Alles daarvoor staat
            // afgevinkt, alles erna wacht nog.
            const actief = !s.klaar && stappen.slice(0, i).every((v) => v.klaar);
            return (
              <li key={s.label} className="flex items-center gap-2.5">
                <span className="w-5 h-5 shrink-0 flex items-center justify-center">
                  {s.klaar
                    ? <Check size={16} style={{ color: 'var(--brand-primary, #6366f1)' }} />
                    : actief
                      ? <Spinner size={15} />
                      : <span className="w-3 h-3 rounded-full" style={{ border: `2px solid ${c.cirkel}` }} />}
                </span>
                <span className="text-[13px]" style={{ color: s.klaar ? c.tekst : actief ? c.titel : c.zacht, fontWeight: actief ? 600 : 400 }}>{s.label}</span>
              </li>
            );
          })}
        </ul>

        <p className="mt-5 text-[11px] text-center" style={{ color: c.zacht }}>Je dashboard is zo klaar.</p>
        {/* Welke oplevering draait hier? Datum/tijd van de build (= de live-push op
            Render) en de commit, zodat je in één oogopslag ziet of de laatste
            push al doorgekomen is. Zie src/versie.js. */}
        <p className="mt-2 text-[10px] text-center tabular-nums" style={{ color: c.zacht, opacity: 0.8 }}>{versieLabel()}</p>
      </div>
    </div>
  );
};
// tonen. `klaar` wordt per fase ingevuld.
const WELKOM_STAPLABELS = ['Partnergegevens laden', 'Klantenportefeuille laden', 'Orders laden', 'Facturen & vergoedingen laden'];

// ── Deep-link-tab: GEEN welkomstscherm ───────────────────────────────────────
// Een tab die via het rechtsklikmenu opent (Customer 360 / Metering, ?sel=…) gaat
// over ÉÉN klant. De welkomstkaart somt daar laadstappen op die dat scherm niet
// eens nodig heeft — facturen, vergoedingen — en staat vooral in de weg: je klikte
// op een klant, dus je wil die klant zien. Daarom in zo'n tab alleen dit stille
// laadteken zolang de sessiecontrole loopt (fase 1, we mogen nog niets tonen), en
// daarna meteen het echte scherm, dat zélf aangeeft waar het nog op wacht
// (Metering: "Aansluitingen zoeken…", Customer 360: "Klantgegevens ophalen…").
// Kleuren expliciet, om dezelfde reden als bij WelkomstKaart: het themablok van
// PartnerDashboard bestaat in fase 1 nog niet.
const StilLaadscherm = ({ uiTheme = 'light' }) => {
  const c = WELKOM_KLEUREN[uiTheme === 'dark' ? 'dark' : 'light'];
  return (
    <div className="fixed inset-0 z-[9600] flex items-center justify-center" style={{ background: c.doek }} role="status" aria-live="polite">
      <Spinner size={30} />
    </div>
  );
};

// Met welke huisstijl opent het dashboard zijn EERSTE tekening? Niet met niets:
// dan draait het portaal even in de standaardkleuren en zonder logo, wat bij een
// energielabel oogt als "eerst het platform, dan pas mijn merk".
//   1. de laatst onthouden huisstijl van DEZE gebruiker (exact, inclusief de
//      verkoopteam-laag die de server toepast);
//   2. anders — eerste aanmelding op dit toestel — de huisstijl van het merkdomein
//      waarop we staan, maar alleen als dat écht het label van deze gebruiker is;
//   3. anders niets, zoals voorheen.
// In alle gevallen vervangt het antwoord van /api/branding dit binnen een seconde.
function startBranding(user, publicBrand) {
  const onthouden = leesGebruikerBranding(user?.id);
  if (onthouden !== undefined) return onthouden;   // null = dit label heeft geen eigen huisstijl
  const label = publicBrand?.energy_label;
  if (!label || label !== (user?.energyLabel || null)) return null;
  return publicBrand.branding || null;
}

function PartnerDashboard({ publicBrand = null }) {
  const { user, token, impersonatedBy, loginWithToken, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Gebruikers-pagina: alleen Admin (volledige rechten) en ET Dossierbeheerder
  // (alleen-lezen). Master/teamleader hebben GEEN toegang meer.
  const canViewUsers = user?.role === 'admin' || user?.role === 'et_dossier';
  // Notifications: één runtime per sessie. Niet in het
  // toestel-voorbeeld (iframe) — anders klinkt en toont alles dubbel.
  const gami = useGamification(IS_PREVIEW_FRAME ? null : token, user);
  const [hoogtepuntenOpen, setHoogtepuntenOpen] = useState(false);
  // Open meteen de Helpdesk wanneer de app via een ticket-deeplink geladen wordt
  // (#helpdesk/ticket/<id>) — bv. uit een Ctrl/⌘+klik in een nieuwe tab.
  // `!IS_PREVIEW_FRAME`: in de device-preview mag de mobiele startregel NIET gelden —
  // daar wil je de pagina zien waar je op staat, niet Market Prices. De preview geeft
  // die pagina expliciet mee via ?page=, dit is het vangnet als dat ontbreekt.
  const [activePage, setActivePage] = useState(() => (ticketHashId() != null ? 'helpdesk' : (startPageId() || ((isMobileViewport() && !IS_PREVIEW_FRAME) ? 'market' : savedActivePage()) || 'market')));
  // Persisteer de actieve pagina in het sessiegeheugen: een refresh blijft op
  // dezelfde pagina, een nieuwe sessie start weer op Market Prices.
  useEffect(() => { schrijfSessieWaarde(ACTIVE_PAGE_KEY, activePage); }, [activePage]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Off-canvas mobiel menu (onder md). Op md+ staat de sidebar altijd vast.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // UI-thema: 'light' = modern licht design-system (standaard), 'dark' = het
  // originele donkere thema. Puur visueel; persistent per browser.
  const [uiTheme, setUiTheme] = useState(() => {
    try { return localStorage.getItem('uiTheme') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  });
  useEffect(() => { try { localStorage.setItem('uiTheme', uiTheme); } catch { /* ignore */ } }, [uiTheme]);
  // PWA: vang het beforeinstallprompt-event zodat we een eigen "Installeer
  // app"-knop kunnen tonen. Verschijnt enkel waar de app installeerbaar is
  // (productie + actieve service worker + nog niet geïnstalleerd).
  const [installPrompt, setInstallPrompt] = useState(() => (typeof window !== 'undefined' && window.__installPrompt) || null);
  useEffect(() => {
    if (window.__installPrompt) setInstallPrompt(window.__installPrompt); // al vóór login gevuurd
    const onBip = (e) => { e.preventDefault(); e.gebrand = !!window.__manifestGebrand; window.__installPrompt = e; setInstallPrompt(e); };
    const onInstalled = () => { window.__installPrompt = null; setInstallPrompt(null); toast.success('App geïnstalleerd — je vindt het portaal nu op je beginscherm.'); };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onBip); window.removeEventListener('appinstalled', onInstalled); };
  }, []);
  const handleInstallApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    try { await installPrompt.userChoice; } catch { /* ignore */ }
    window.__installPrompt = null;
    setInstallPrompt(null);
  };
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  // De accountbalk rechtsboven zweeft over de pagina; PageHeader reserveert zijn
  // breedte zodat hij nooit over een actieknop valt.
  const accountBarRef = useRef(null);
  useAccountBarBreedte(accountBarRef);
  const [showDevicePreview, setShowDevicePreview] = useState(false); // admin device-preview overlay
  // PWA-installbanner op mobiel (de sidebar-knop is daar off-canvas). Eenmaal
  // weggeklikt onthouden we dat zodat hij niet blijft terugkomen.
  const [installDismissed, setInstallDismissed] = useState(() => { try { return localStorage.getItem('pwaInstallDismissed') === '1'; } catch { return false; } });
  const dismissInstall = () => { try { localStorage.setItem('pwaInstallDismissed', '1'); } catch { /* ignore */ } setInstallDismissed(true); };
  // PWA: nieuwe versie beschikbaar (service worker staat 'waiting'). index.js
  // dispatcht 'sw-update-available'; "Herladen" activeert de wachtende SW →
  // controllerchange in index.js herlaadt de pagina één keer met de verse bundle.
  const [swUpdateReg, setSwUpdateReg] = useState(null);
  useEffect(() => {
    const onUpd = (e) => setSwUpdateReg((e && e.detail) || true);
    window.addEventListener('sw-update-available', onUpd);
    return () => window.removeEventListener('sw-update-available', onUpd);
  }, []);
  const applyUpdate = () => {
    const waiting = swUpdateReg && swUpdateReg.waiting;
    if (waiting) waiting.postMessage({ type: 'SKIP_WAITING' });
    else window.location.reload();
  };
  // PWA: online/offline-status voor een discrete offline-indicatie (live-data dashboard).
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  const [apiOrders, setApiOrders] = useState([]);
  // Start op true: bij het openen van de app loopt de eerste orders-fetch al, en
  // dan hoort er een laadindicator te staan i.p.v. een "geen data"-scherm.
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [apiInvoices, setApiInvoices] = useState([]);
  const [apiCreditNotes, setApiCreditNotes] = useState([]);
  const [apiSelfbills, setApiSelfbills] = useState([]);   // self bills (eigen feed, zie /api/selfbills)

  // Klant 360° prefill: setters worden vanuit OrdersPage (en straks mogelijk
  // andere pages) gevoed wanneer de gebruiker doorklikt op een klant/GSRN.
  // prefillKey is een teller zodat hetzelfde target opnieuw klikken óók
  // opnieuw triggert (waarde-gelijkheid is niet genoeg).
  // Een verse load met ?sel=… komt uit het rechtsklik-menu van een ander tabblad:
  // die selectie is meteen de prefill, met key 1 zodat de pagina hem oppikt.
  const urlSelection = useMemo(() => startSelection(), []);
  const startPage = useMemo(() => startPageId(), []);
  const urlSelectionIsMetering = !!urlSelection && startPage === 'metering';
  const [customer360Prefill, setCustomer360Prefill] = useState(urlSelection && !urlSelectionIsMetering ? urlSelection : null);
  const [customer360PrefillKey, setCustomer360PrefillKey] = useState(urlSelection && !urlSelectionIsMetering ? 1 : 0);
  const [meteringPrefill, setMeteringPrefill] = useState(urlSelectionIsMetering ? urlSelection : null);
  const [meteringPrefillKey, setMeteringPrefillKey] = useState(urlSelectionIsMetering ? 1 : 0);
  // `dest` ('customer360' default | 'metering') stuurt de bestemming. De gedeelde
  // rechtsklik-menu (useCustomer360Menu) roept dit aan met dest='metering' voor de
  // tweede actie; alle bestaande aanroepen (zonder dest) gaan naar Customer 360.
  const openCustomer360 = (target, dest) => {
    if (dest === 'metering') {
      setMeteringPrefill(target);
      setMeteringPrefillKey(k => k + 1);
      setActivePage('metering');
      return;
    }
    setCustomer360Prefill(target);
    setCustomer360PrefillKey(k => k + 1);
    setActivePage('customer360');
  };

  // Deep-link-routing voor de Helpdesk: een hash naar een ticket schakelt naar de
  // Helpdesk-pagina (HelpdeskPage leest zelf welk ticket). Verlaten we de Helpdesk
  // dan wissen we een achtergebleven ticket-hash zodat hij niet later heropent.
  useEffect(() => {
    const onHash = () => { if (ticketHashId() != null) setActivePage('helpdesk'); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    if (activePage !== 'helpdesk' && (window.location.hash || '').startsWith('#helpdesk/')) {
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) { /* noop */ }
    }
  }, [activePage]);

  const fetchOrders = useMemo(() => async () => {
    if (!token) return;
    setOrdersLoading(true);
    try {
      // Cursor-paginated fetch: backend filtert per request op user-scope vóór
      // pagineren — dus we kunnen veilig in batches loopen zonder ungescopete
      // records te zien. Voor de gemiddelde teamleader/reseller is dit één
      // call; alleen admin krijgt meerdere batches van 500.
      const data = await fetchPaginated('/api/orders', token);
      // De ET-API levert `contract` soms als object {id,url,name,...} i.p.v. string;
      // dat veld wordt rechtstreeks gerenderd (Orders-tabel/OrderDetailModal) →
      // "Objects are not valid as a React child". Defensief afvlakken naar de naam
      // (backend doet dit ook via asText; dit vangt reeds gecachte object-data op).
      setApiOrders((Array.isArray(data) ? data : []).map(o =>
        (o.contract && typeof o.contract === 'object') ? { ...o, contract: o.contract.name || '' } : o));
    } catch (err) {
      console.error('Orders ophalen mislukt:', err.message);
    } finally {
      setOrdersLoading(false);
    }
  }, [token]);

  // Verkoper (ET-reseller) van één order wijzigen. De backend dwingt de RBAC + de
  // harde "nooit cross-label"-regel af en werkt Odoo bij; hier patchen we daarna de
  // lokale order-state zodat de nieuwe verkoper/team meteen in het platform verschijnt.
  const reassignOrder = useMemo(() => async (orderId, { seller, team }) => {
    const r = await fetch(`/api/orders/${encodeURIComponent(orderId)}/reassign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ seller, team: team || '' }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
    setApiOrders((prev) => prev.map((o) => (o.id === orderId
      ? { ...o, salesperson: j.salesperson, salespersonTeam: j.salespersonTeam }
      : o)));
    return j;
  }, [token]);

  // Branding: backend levert óf alle records (admin) óf {mine} (non-admin).
  // We bepalen één 'active branding' per user; admin krijgt default.
  const [brandingGeladen, setBrandingGeladen] = useState(false);
  const [allBranding, setAllBranding] = useState([]);     // admin only
  const [allTeamBranding, setAllTeamBranding] = useState([]); // admin only — per verkoopteam
  const [availableLabels, setAvailableLabels] = useState([]); // admin only
  const [prospectTeams, setProspectTeams] = useState([]);     // admin only: prospect-resellers als "team"
  const [prospectLabels, setProspectLabels] = useState([]);   // admin only: prospect-energielabels
  // Start met de onthouden huisstijl zodat logo, favicon én merkkleuren er staan
  // vóór de eerste tekening; `brandingGeladen` hieronder blijft aan de ECHTE fetch
  // hangen, want het welkomstscherm mag niet doen alsof er al iets binnen is.
  const [activeBranding, setActiveBranding] = useState(() => startBranding(user, publicBrand));
  // De branding zoals het HUIDIGE thema ze moet tonen. Gebruik deze overal in de
  // interface; `activeBranding` blijft de ruwe rij (met beide varianten) en is
  // enkel bedoeld voor het instelscherm en voor de PDF's, die altijd op wit
  // afdrukken en dus expliciet de lichte variant nemen.
  const themedBranding = useMemo(() => brandingForTheme(activeBranding, uiTheme), [activeBranding, uiTheme]);
  const loadBranding = useMemo(() => async () => {
    if (!token) return;
    try {
      const r = await fetch('/api/branding', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      let mijn = null;
      if (d.all) {
        setAllBranding(d.all);
        setAllTeamBranding(d.all_teams || []);
        setAvailableLabels(d.available_labels || []);
        setProspectTeams(d.prospect_teams || []);
        setProspectLabels(d.prospect_labels || []);
        // Admin gebruikt standaard de "Energy Together"-huisstijl (logo/favicon/
        // kleuren/letterstijl) als die is ingesteld; anders de neutrale default.
        mijn = d.all.find(b => b.energy_label === ET_BRAND_LABEL) || null;
      } else {
        mijn = d.mine || null;
      }
      setActiveBranding(mijn);
      // Onthouden voor de VOLGENDE start van deze gebruiker op dit toestel, zodat
      // het portaal dan meteen in het juiste merk opent (zie startBranding). Ook
      // een LEGE huisstijl wordt onthouden: dat is óók een antwoord.
      schrijfGebruikerBranding(user?.id, mijn);
    } catch (e) { /* stil falen — default branding blijft staan */ }
    // Voedt de stap "Partnergegevens laden" op het welkomstscherm. Een aparte vlag,
    // want `activeBranding` mag legitiem null blijven (een label zonder eigen
    // huisstijl) en is dus geen bewijs dat de fetch al gebeurd is.
    finally { setBrandingGeladen(true); }
  }, [token, user?.id]);
  useEffect(() => { loadBranding(); }, [loadBranding]);

  // Bij impersonate: toon de branding van de gebruiker dat we nu zijn.
  // Het 'user'-object komt uit /me en bevat energyLabel; dit useEffect
  // re-fetcht branding wanneer die wisselt.
  useEffect(() => { loadBranding(); }, [user?.energyLabel, loadBranding]);
  // De lijst energielabels (met de prospect-labels en -teams) komt uit dezelfde
  // oproep en werd alleen bij het aanmelden opgehaald: een prospect die je net had
  // aangemaakt, stond pas na een herlaadbeurt in de labelkiezer van Branding.
  // Daarom verversen bij het openen van die pagina.
  useEffect(() => { if (activePage === 'settings-label' && isAdmin) loadBranding(); }, [activePage, isAdmin, loadBranding]);

  // Opportunity-stage beschrijvingen (tooltips in elke "Opp. Stage"-kolom). Eén
  // keer laden; via StageDescContext beschikbaar voor alle tabel-componenten.
  const [stageDescriptions, setStageDescriptions] = useState({});
  useEffect(() => {
    if (!token) return;
    fetch('/api/opportunity-stages', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.descriptions) setStageDescriptions(d.descriptions); })
      .catch(() => {});
  }, [token]);

  // Module-zichtbaarheid per energielabel is verwijderd: modules worden nu
  // uitsluitend per gebruiker toegekend bij aanmaak/bewerken (zie userModules).

  // Customer Tag-zichtbaarheid (admin-beheerd). `visiblePartnerTags` = Set van
  // tags die getoond mogen worden in Customer 360 + Orders-tabel; `partnerTagAll`
  // (admin) = volledige lijst {tag, visible} voor de config-tabel in Instellingen.
  const [visiblePartnerTags, setVisiblePartnerTags] = useState(null); // null = nog niet geladen
  const [partnerTagAll, setPartnerTagAll] = useState([]);
  const [feeNotAllowedTags, setFeeNotAllowedTags] = useState(new Set()); // individuele tags die fee blokkeren
  const loadPartnerTags = useMemo(() => async () => {
    if (!token) return;
    try {
      const r = await fetch('/api/partner-tags', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setVisiblePartnerTags(new Set(d.visible || []));
      setPartnerTagAll(d.all || []);
      // `feeNotAllowed` bevat ALLEEN losse tags — de backend laat combinaties er
      // bewust uit. Hier dus NIET meer op komma splitsen: dat maakte van elke combo
      // met Fee Allowed = No een blokkade voor al haar leden, ook als zo'n losse tag
      // expliciet op Yes stond (zie getFeePartnerTag hieronder).
      setFeeNotAllowedTags(new Set(d.feeNotAllowed || []));
    } catch (_) { /* stil falen — tags blijven verborgen (veilige default) */ }
  }, [token]);
  useEffect(() => { loadPartnerTags(); }, [loadPartnerTags]);

  // Per-gebruiker toegekende content-modules (uit /me). Admin ziet altijd alles.
  const userModules = useMemo(() => new Set(user?.modules || []), [user?.modules]);
  // ET Dossierbeheerder mag (als enige niet-admin) de Audit Log inzien.
  const canSeeAuditLog = isAdmin || user?.role === 'et_dossier';

  // Ongelezen berichten in de Optimise-gesprekken: voor een partner een
  // bijkomende vraag van het platformteam, voor de admin een antwoord van een
  // indiener. Zonder dit signaal blijft zo'n vraag onopgemerkt — niemand opent
  // Optimise uit zichzelf nog eens nadat hij zijn melding verstuurd heeft.
  // Geen poll-timer: de teller verrast bij elke paginawissel, dat is vaak genoeg.
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  const loadFeedbackUnread = useMemo(() => async () => {
    if (!token) return;
    try {
      const r = await fetch('/api/feedback/unread', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setFeedbackUnread(Number(d.count) || 0);
    } catch (_) { /* stil — een telletje is geen reden voor een foutmelding */ }
  }, [token]);
  useEffect(() => { loadFeedbackUnread(); }, [loadFeedbackUnread, activePage]);

  // Helpdesk: hoeveel tickets wachten op MIJ (partner: "actie vereist van
  // partner"; admin/ET: van Energy Together). Dat is de to-do van de module en
  // vandaag zag je die pas ná het openen en filteren. Zelfde ritme als Optimise:
  // bij elke paginawissel, geen poll-timer. De server antwoordt uit zijn cache
  // (goedkoop) of met null als hij het niet zonder zware ophaal weet → geen telletje.
  const helpdeskInMenu = isAdmin || userModules.has('helpdesk');
  const helpdeskLeest = isAdmin || user?.role === 'et_dossier' || (user?.helpdeskPerms || []).includes('read');
  const [helpdeskTodo, setHelpdeskTodo] = useState(0);
  const loadHelpdeskTodo = useMemo(() => async () => {
    if (!token || !helpdeskInMenu || !helpdeskLeest || user?.demoMode) { setHelpdeskTodo(0); return; }
    try {
      const r = await fetch('/api/helpdesk/todo', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setHelpdeskTodo(Number(d.count) || 0);
    } catch (_) { /* stil — een telletje is geen reden voor een foutmelding */ }
  }, [token, helpdeskInMenu, helpdeskLeest, user?.demoMode]);
  useEffect(() => { loadHelpdeskTodo(); }, [loadHelpdeskTodo, activePage]);

  // Zichtbare nav-items:
  //   - item.roles aanwezig → alleen die rollen mogen 'm zien
  //   - admin ziet alles; overige rollen alleen de aan hen toegekende modules
  const visibleNavItems = useMemo(() => NAV_ITEMS.filter(item => {
    if (Array.isArray(item.roles) && !item.roles.includes(user?.role)) return false;
    if (item.always) return true; // platform-brede module (bv. Optimise) — altijd zichtbaar
    if (isAdmin) return true;
    return userModules.has(item.id);
  }), [isAdmin, userModules, user?.role]);
  // Mijlpaalkaart → "Bekijk prestaties": alleen voor wie Sales én de subpagina heeft.
  const magPrestaties = useMemo(() => {
    if (!visibleNavItems.some((i) => i.id === 'sales')) return false;
    const toegekend = subPageSetForUser(user);
    return !toegekend || toegekend.has(subPageKey('sales', 'prestaties'));
  }, [visibleNavItems, user]);

  // Startpagina = Market Prices (zie initiële activePage hierboven). Niet elke rol
  // heeft de Market Prices-module zichtbaar (partners krijgen standaard een
  // minimale moduleset) → als de startpagina niet in hun menu staat, val eenmalig
  // terug op hun eerste zichtbare module. Loopt precies één keer per ingelogde
  // gebruiker en raakt latere, bewuste navigatie niet aan.
  const landingFixedFor = useRef(null);
  useEffect(() => {
    if (!user) return;
    const key = user.id ?? user.email;
    if (landingFixedFor.current === key) return;
    if (visibleNavItems.length === 0) return; // modules nog niet geladen
    landingFixedFor.current = key;
    const visibleIds = new Set(visibleNavItems.map((i) => i.id));
    // Alleen corrigeren wanneer de (herstelde) pagina een NAV-item is dat deze rol
    // niet mag zien. Speciale pagina's buiten NAV_ITEMS (audit-log, settings, users…)
    // worden hun eigen render-gating aan overgelaten en dus niet weggestuurd.
    if (NAV_ITEMS.some((i) => i.id === activePage) && !visibleIds.has(activePage)) setActivePage(visibleNavItems[0].id);
  }, [user, visibleNavItems, activePage]);

  // Thema-injectie via één <style>-blok (Tailwind draait via CDN, dus
  // !important utility-overrides werken betrouwbaar). Twee delen:
  //   A. Modern licht design-system — zet de donkere slate-surfaces om naar een
  //      licht palet (lichte achtergrond als basis) en voegt moderne afwerking
  //      toe: subtiele gradients, zachte schaduwen, ruimere radius, nettere
  //      typografie/knoppen/velden/scrollbar. Geldt voor ALLE rollen. Puur
  //      visueel — geen class/markup/prop/volgorde/logica in de componenten wijzigt.
  //   B. Brand-kleuren — remapt de lime-accentclasses naar primary/secondary/
  //      accent van het actieve energielabel (niet-admin); overschrijft waar nodig
  //      de lime-accenten uit deel A.
  useEffect(() => {
    const id = 'brand-color-overrides';
    let style = document.getElementById(id);
    // Alle branding loopt door brandingForTheme: logo, favicon, kleuren én
    // achtergrond hebben een aparte licht- en donkervariant, met terugval op
    // elkaar als er maar één is ingesteld.
    const b = brandingForTheme(activeBranding, uiTheme);
    const parts = [];

    // Voortgangslijn van het welkomstscherm: een strook die heen en weer loopt in
    // de merkkleur. Bewust ONBEPAALD — de duur van de fetches is niet vooraf
    // bekend, dus een percentage zou verzonnen zijn. Staat hier in het centrale
    // themablok omdat de kleur uit var(--brand-primary) komt.
    parts.push(`
        .welkom-balk::after { content:''; position:absolute; top:0; bottom:0; left:0; width:38%;
          border-radius:9999px; background:var(--brand-primary, #6366f1);
          animation: welkom-loop 1.5s cubic-bezier(.45,.05,.55,.95) infinite; }
        @keyframes welkom-loop { 0% { transform: translateX(-110%) } 100% { transform: translateX(300%) } }
        @media (prefers-reduced-motion: reduce) { .welkom-balk::after { animation-duration: 3.2s } }
    `);

    // A. Modern licht design-system — geldt voor ALLE rollen wanneer het lichte
    //    thema actief is (standaard). In 'dark' wordt deze laag overgeslagen →
    //    het originele donkere thema.
    if (uiTheme === 'light') parts.push(`
        /* ── 1. Licht palet (ontworpen, niet naïef geïnverteerd) ── */
        body { background-color: #f8fafc !important; -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; }
        .from-slate-950.via-slate-900.to-slate-950 { background-image: linear-gradient(160deg, #f8fafc 0%, #eef2f7 58%, #e9eef6 100%) !important; background-color: #f1f5f9 !important; }
        .bg-slate-900, .bg-slate-900\\/40, .bg-slate-900\\/50, .bg-slate-900\\/60, .bg-slate-900\\/95,
        .bg-slate-950\\/40, .bg-slate-950\\/60,
        .bg-slate-800\\/30, .bg-slate-800\\/70, .bg-slate-800\\/80,
        .bg-slate-800, .bg-slate-800\\/40, .bg-slate-800\\/50, .bg-slate-800\\/60 { background-color: #ffffff !important; }
        .bg-slate-700, .bg-slate-700\\/30, .bg-slate-700\\/40, .bg-slate-700\\/50, .bg-slate-700\\/60, .bg-slate-700\\/70 { background-color: #f1f5f9 !important; }
        .bg-slate-700\\/20 { background-color: #f8fafc !important; }
        .bg-slate-600, .bg-slate-600\\/30, .bg-slate-600\\/40, .bg-slate-600\\/50, .bg-slate-600\\/60 { background-color: #e2e8f0 !important; }
        .bg-slate-500 { background-color: #cbd5e1 !important; }
        .hover\\:bg-slate-800:hover, .hover\\:bg-slate-700:hover { background-color: #f1f5f9 !important; }
        .hover\\:bg-slate-600:hover { background-color: #e2e8f0 !important; }
        .text-slate-100, .text-slate-200 { color: #0f172a !important; }
        .text-slate-300 { color: #334155 !important; }
        .text-slate-400 { color: #64748b !important; }
        .text-slate-500 { color: #94a3b8 !important; }
        .border-slate-600, .border-slate-600\\/40, .border-slate-600\\/50, .border-slate-600\\/60, .border-slate-600\\/70 { border-color: #cbd5e1 !important; }
        .border-slate-700, .border-slate-700\\/30, .border-slate-700\\/40, .border-slate-700\\/50, .border-slate-700\\/60, .border-slate-700\\/70,
        .border-slate-800\\/50, .border-slate-800\\/60, .border-slate-800\\/70 { border-color: #e8edf3 !important; }
        .border-slate-500, .border-slate-500\\/30, .border-slate-400 { border-color: #cbd5e1 !important; }
        .placeholder-slate-400::placeholder, .placeholder-slate-500::placeholder { color: #94a3b8 !important; }
        /* Recharts: grid/assen/labels/tooltip licht maken (series-kleuren blijven). */
        .recharts-cartesian-grid line { stroke: #e8edf3 !important; }
        .recharts-cartesian-axis-line, .recharts-cartesian-axis-tick-line { stroke: #cbd5e1 !important; }
        .recharts-cartesian-axis-tick-value, .recharts-cartesian-axis-tick-value tspan { fill: #475569 !important; }
        .recharts-legend-item-text { color: #475569 !important; }
        .recharts-default-tooltip { background-color: #ffffff !important; border-color: #e8edf3 !important; color: #0f172a !important; border-radius: 10px !important; box-shadow: 0 8px 28px -10px rgba(15,23,42,.22) !important; }
        .recharts-default-tooltip .recharts-tooltip-label { color: #0f172a !important; }

        /* ── 2. Moderne SaaS-afwerking (puur visueel) ── */
        body { font-feature-settings: "cv02","cv03","cv04"; }
        h1, h2, h3 { letter-spacing: -0.011em; }
        /* PLATFORMREGEL — Hoofdtitel-gradient (zie ook CLAUDE.md):
           ELKE pagina-hoofdtitel draagt dezelfde merk-gradient (slate → energielabel-
           accentkleur). Mechanisme: elke titel met classes "text-2xl font-bold" (h1 óf
           h2) — d.w.z. de PageHeader-component én losse pagina-titels (Metering,
           Gebruikers, Referrals, Marktprijzen, …). NIEUWE pagina's krijgen dit
           automatisch zolang de hoofdtitel via PageHeader of een h1/h2 met
           "text-2xl font-bold" wordt opgebouwd. var(--brand-primary) = per energielabel
           (deel B); admin/zonder branding → indigo-fallback. (Sectiekoppen h3: hieronder.) */
        h1.text-2xl.font-bold, h2.text-2xl.font-bold {
          background-image: linear-gradient(95deg, #0f172a 0%, #334155 45%, var(--brand-primary, #4f46e5) 130%) !important;
          -webkit-background-clip: text !important; background-clip: text !important;
          -webkit-text-fill-color: transparent !important; color: transparent !important;
        }
        /* Sectiekoppen (h3): zelfde gradient-tekst. Enkel de slate-titels (semantisch
           gekleurde koppen zoals het amber EAN-blok behouden hun kleur). De InfoTip
           (icoon + tooltip) binnen een titel wordt hersteld zodat hij zichtbaar blijft. */
        h3.font-semibold.text-slate-100 {
          background-image: linear-gradient(95deg, #0f172a 0%, #334155 55%, var(--brand-primary, #4f46e5) 135%) !important;
          -webkit-background-clip: text !important; background-clip: text !important;
          -webkit-text-fill-color: transparent !important; color: transparent !important;
        }
        h3.font-semibold.text-slate-100 .group, h3.font-semibold.text-slate-100 .group * { -webkit-text-fill-color: initial !important; }
        /* KPI-tegels: subtiele gloss + leesbaarder accenttekst op de pale tint. */
        .rounded-xl.border.text-center { background-image: linear-gradient(165deg, rgba(255,255,255,.45) 0%, rgba(255,255,255,0) 62%) !important; }
        .rounded-xl.border.text-center .text-sky-300, .rounded-xl.border.text-center .text-sky-400 { color: #0369a1 !important; }
        .rounded-xl.border.text-center .text-violet-200, .rounded-xl.border.text-center .text-violet-300 { color: #6d28d9 !important; }
        .rounded-xl.border.text-center .text-amber-300, .rounded-xl.border.text-center .text-amber-400 { color: #b45309 !important; }
        .rounded-xl.border.text-center .text-red-300, .rounded-xl.border.text-center .text-red-400 { color: #dc2626 !important; }
        /* Datumkolom van de vertrek-tabellen (Binnenkort vertrekken, Vroegtijdig
           vertrokken, Gedropte klanten, Actieve drops). De -300-tinten zijn voor de
           donkere achtergrond gekozen en verdwijnen op wit; hier de donkere variant,
           dezelfde tinten als de KPI-tegels hierboven. */
        td.pf-datum.text-amber-300 { color: #b45309 !important; }
        td.pf-datum.text-red-300 { color: #dc2626 !important; }
        td.pf-datum.text-rose-300 { color: #be123c !important; }
        /* Badges/chips: zachtere pill-radius. */
        .rounded { border-radius: 7px !important; }
        /* Cards: zachte schaduw-verdieping bij hover (geen verschuiving). */
        .rounded-2xl.border { transition: box-shadow .18s ease !important; }
        .rounded-2xl.border:hover { box-shadow: 0 2px 4px rgba(15,23,42,.05), 0 18px 42px -18px rgba(15,23,42,.20) !important; }
        /* Consistente, modernere hoekradius. */
        .rounded-2xl { border-radius: 18px !important; }
        .rounded-xl { border-radius: 14px !important; }
        .rounded-lg { border-radius: 10px !important; }
        /* Cards & KPI-tegels: zachte, gelaagde schaduw + fijne rand. */
        .rounded-2xl.border, .rounded-xl.border { box-shadow: 0 1px 2px 0 rgba(15,23,42,.04), 0 12px 32px -16px rgba(15,23,42,.14) !important; }
        /* Sidebar: wit verloop + zachte scheidingsschaduw rechts. */
        aside.fixed { background-image: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%) !important; box-shadow: 1px 0 0 #e8edf3, 8px 0 28px -18px rgba(15,23,42,.20) !important; }
        /* Navigatie: vloeiende transities. */
        nav button { transition: background-color .15s ease, color .15s ease, border-color .15s ease !important; }
        /* Primaire (brand/lime) knoppen: schaduw + subtiele hover-lift. */
        .bg-lime-500, .bg-lime-400 { box-shadow: 0 1px 2px rgba(15,23,42,.10), 0 8px 18px -10px rgba(101,163,13,.42) !important; transition: transform .12s ease, box-shadow .15s ease, background-color .15s ease !important; }
        .bg-lime-500:hover, .bg-lime-400:hover { transform: translateY(-1px); }
        /* Lime-accenten leesbaar op licht (deel B overschrijft dit bij een eigen brand-kleur). */
        .text-lime-200, .text-lime-300, .text-lime-400, .text-lime-500 { color: #4d7c0f !important; }
        .fill-lime-400, .fill-lime-500 { fill: #65a30d !important; }
        /* Velden: nette focus-ring. */
        input:focus, select:focus, textarea:focus { box-shadow: 0 0 0 3px rgba(101,163,13,.16) !important; }
        /* Tabellen: rustige header + vloeiende rij-hover. */
        table thead th { letter-spacing: .02em; }
        tbody tr { transition: background-color .12s ease; }
        /* NB: de responsieve tabel-kaarten (table.resp-card) en de mobiele
           ergonomie stonden hier, maar zijn verplaatst naar blok C — dat geldt in
           BEIDE thema's. In donker thema bleven tabellen anders op volle
           desktopbreedte staan en liep de pagina buiten het scherm. */
        /* Moderne, dunne scrollbar. */
        *::-webkit-scrollbar { width: 10px; height: 10px; }
        *::-webkit-scrollbar-track { background: transparent; }
        *::-webkit-scrollbar-thumb { background: #cdd6e2; border: 2px solid transparent; background-clip: padding-box; border-radius: 9px; }
        *::-webkit-scrollbar-thumb:hover { background: #aab6c6; background-clip: padding-box; }
    `);

    // A2. Achtergrondkleur van het energielabel — alleen als er één is ingesteld
    //     voor DIT thema; anders blijft het bestaande verloop staan (licht
    //     #f8fafc → #e9eef6, donker de slate-950/900-gradient). Een ingestelde
    //     kleur maakt de achtergrond bewust EGAAL: een verloop tussen een gekozen
    //     kleur en de hardgecodeerde tinten zou per label anders uitvallen.
    //
    //     De pagina alléén verkleuren volstaat niet: sidebar, kaarten en filters
    //     staan op vaste slate-/witwaarden en zouden als een witte laag boven een
    //     gekleurde achtergrond blijven zweven. Ze worden daarom uit diezelfde
    //     kleur afgeleid (surfacesFromBackground), zodat de hele interface één
    //     palet vormt en de onderlinge hiërarchie (pagina < kaart < invoer) blijft.
    //     Staat ná blok A, zodat het diens hardgecodeerde regels overschrijft.
    const opp = b && b.background_color ? surfacesFromBackground(b.background_color) : null;
    if (opp) {
      parts.push(`
        body { background-color: ${opp.pagina} !important; }
        .from-slate-950.via-slate-900.to-slate-950 { background-image: none !important; background-color: ${opp.pagina} !important; }
        /* Sidebar, kaarten, tegels, modals — alles op slate-900/slate-800. */
        .bg-slate-900, .bg-slate-900\\/40, .bg-slate-900\\/50, .bg-slate-900\\/60, .bg-slate-900\\/95,
        .bg-slate-950\\/40, .bg-slate-950\\/60,
        .bg-slate-800\\/30, .bg-slate-800\\/70, .bg-slate-800\\/80,
        .bg-slate-800, .bg-slate-800\\/40, .bg-slate-800\\/50, .bg-slate-800\\/60 { background-color: ${opp.kaart} !important; }
        /* Invoervelden, selects, filterbalken. */
        .bg-slate-700, .bg-slate-700\\/30, .bg-slate-700\\/40, .bg-slate-700\\/50, .bg-slate-700\\/60, .bg-slate-700\\/70 { background-color: ${opp.invoer} !important; }
        .bg-slate-700\\/20 { background-color: ${opp.subtiel} !important; }
        .bg-slate-600, .bg-slate-600\\/30, .bg-slate-600\\/40, .bg-slate-600\\/50, .bg-slate-600\\/60 { background-color: ${opp.rand} !important; }
        .hover\\:bg-slate-800:hover, .hover\\:bg-slate-700:hover { background-color: ${opp.hover} !important; }
        .hover\\:bg-slate-600:hover { background-color: ${opp.rand} !important; }
        /* Sticky tabelkoppen mogen niet doorschijnen op de kaartkleur. */
        table.resp-card thead th { background-color: ${opp.kaart} !important; }
      `);
    }

    if (b && b.primary_color) {
      const p = b.primary_color;
      const s = b.secondary_color || p;
      const a = b.accent_color || p;
      // Auto-contrast: solide brand-knoppen krijgen automatisch zwarte óf witte
      // tekst (afhankelijk van de helderheid van de brand-kleur), zodat de
      // knoptekst altijd leesbaar is. De brand-kleur als TEKST wordt bijgesteld
      // tot voldoende contrast met de thema-achtergrond (wit voor het niet-admin
      // licht-thema, donker voor admin) — zo blijft een te lichte/donkere
      // labelkleur leesbaar.
      const onBrand = readableTextColor(p);          // tekst óp de brand-knop
      const onBrandHover = readableTextColor(s);      // idem in hover-staat
      const textBg = isAdmin ? '#0f172a' : '#ffffff'; // achtergrond achter brand-tekst
      const brandText = ensureContrast(p, textBg, 4.5);
      // A. Brand-kleuren: primary = solide knoppen/borders/ring; brandText =
      //    leesbare tekst-variant; secondary = hover-staat; accent = subtiele tints.
      parts.push(`
        :root { --brand-primary: ${p}; --brand-secondary: ${s}; --brand-accent: ${a}; }
        .bg-lime-500, .bg-lime-400 { background-color: ${p} !important; color: ${onBrand} !important; }
        .bg-lime-500 svg, .bg-lime-400 svg { color: ${onBrand} !important; }
        .hover\\:bg-lime-400:hover { background-color: ${s} !important; color: ${onBrandHover} !important; }
        .text-lime-200, .text-lime-300, .text-lime-400, .text-lime-500 { color: ${brandText} !important; }
        .border-lime-500 { border-color: ${p} !important; }
        .border-lime-500\\/30, .border-lime-500\\/40, .border-lime-500\\/50 { border-color: ${p}80 !important; }
        .ring-lime-500 { --tw-ring-color: ${p} !important; }
        .fill-lime-500 { fill: ${p} !important; }
        .bg-lime-500\\/5 { background-color: ${a}0d !important; }
        .bg-lime-500\\/10, .bg-lime-500\\/15 { background-color: ${a}1f !important; }
        .bg-lime-500\\/20, .bg-lime-500\\/50 { background-color: ${a}33 !important; }
      `);
    }

    // Letterstijl: pas de geconfigureerde font-family toe op de hele app. Geldt voor
    // elke rol met actieve branding (admin/ET = Energy Together; partners = hun label).
    if (b && b.font_family) {
      parts.push(`
        :root { --brand-font: ${b.font_family}; }
        body, button, input, select, textarea, h1, h2, h3, h4, h5, h6 { font-family: ${b.font_family} !important; }
      `);
    }

    // C. Globale micro-interacties (thema-onafhankelijk, altijd actief). Puur
    //    visueel: verhoogt de "app-feel" platformbreed zonder call-sites te raken.
    parts.push(`
        /* Kaart-hover: subtiele elevatie (enkel schaduw → geen layout-shift). */
        .app-card { transition: box-shadow .18s ease, border-color .18s ease; }
        .app-card:hover { box-shadow: 0 12px 32px -14px rgba(15,23,42,.28); }
        /* Datatabel-rijen: rustige hover-highlight voor betere scanbaarheid. Werkt
           in beide thema's (neutrale slate-alpha); expliciete rij-hover wint hiervan. */
        table.resp-card tbody tr { transition: background-color .12s ease; }
        table.resp-card tbody tr:hover { background-color: rgba(100,116,139,.07); }
        /* HTML uit helpdesk-tickets (klantmails, gesaneerd door de server): een
           afbeelding of tabel uit zo'n mail mag de kolom niet breder duwen, en een
           link moet als link herkenbaar zijn. */
        .hd-html { overflow-wrap: anywhere; }
        .hd-html img { max-width: 100%; height: auto; }
        .hd-html img[data-hd-bijlage]:not([src]) { display: inline-block; min-width: 9rem; min-height: 6rem; background: rgba(100,116,139,.18); border-radius: .375rem; }
        .hd-html img.hd-beeld { cursor: zoom-in; border-radius: .375rem; }
        .hd-html img.hd-beeld-fout { min-height: 0; background: none; }
        .hd-html table { max-width: 100%; }
        .hd-html a { text-decoration: underline; }
        /* Alinea's houden hun afstand, zoals in de mail zelf (08-10-2026): zonder deze
           regel zet de Tailwind-reset elke <p> tegen de vorige en leest een verstuurde
           mail in de gesprekshistoriek als één blok. Een inline margin uit een
           klantmail gaat voor. */
        .hd-html p { margin: 0 0 .75em; }
        .hd-html p:last-child { margin-bottom: 0; }
        .hd-html blockquote { border-left: 3px solid rgba(100,116,139,.4); padding-left: .75rem; margin: .5rem 0; opacity: .85; }
        /* Klantgegevens in een AI-voorstel: vet met een subtiele amberkleur, alleen
           intern zichtbaar — de markering gaat er bij het versturen af. */
        .hd-html mark.ai-klant, mark.ai-klant { background: rgba(245,158,11,.16); color: inherit; font-weight: 600; border-bottom: 1px dotted rgba(245,158,11,.85); padding: 0 .12em; border-radius: .15em; }
        /* Bewerkbare mailweergave (Mailen naar klant): wit met donkere inkt in ELK
           thema, in het mail-lettertype — zo lijkt het op wat de klant krijgt. */
        .hd-mail-canvas { background: #ffffff !important; color: #1e293b !important; font-family: Arial, Helvetica, sans-serif; line-height: 1.55; }
        .hd-mail-canvas p { margin: 0 0 .8em; }
        .hd-mail-canvas a { color: #2563eb; }
        .hd-mail-canvas ul, .hd-mail-canvas ol { padding-left: 1.4em; margin: 0 0 .8em; }
        .hd-mail-canvas ol { list-style: decimal; } .hd-mail-canvas ul { list-style: disc; }
        /* Blokstijlen van de opmaakbalk: zoals een mailclient ze standaard toont. */
        .hd-mail-canvas h1 { font-size: 1.6em; font-weight: 700; margin: 0 0 .5em; line-height: 1.25; }
        .hd-mail-canvas h2 { font-size: 1.35em; font-weight: 700; margin: 0 0 .5em; line-height: 1.3; }
        .hd-mail-canvas h3 { font-size: 1.15em; font-weight: 700; margin: 0 0 .5em; line-height: 1.3; }
        .hd-mail-canvas blockquote { border-left: 3px solid #cbd5e1; margin: 0 0 .8em; padding: 0 0 0 .75em; color: #475569; opacity: 1; }
        .hd-mail-canvas pre { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: .9em; background: #f1f5f9; padding: .5em .75em; border-radius: .375rem; white-space: pre-wrap; margin: 0 0 .8em; }
        .hd-mail-canvas hr { border: 0; border-top: 1px solid #cbd5e1; margin: .8em 0; }
        .hd-mail-canvas:empty::before { content: 'Schrijf je antwoord aan de klant…'; color: #94a3b8; }
        /* Helpdesk-pillen (status, actie vereist van, leeftijd): een eigen, per thema
           ontworpen palet. De generieke -300-tinten zijn voor donker gekozen en zijn
           op wit onleesbaar; hier krijgt elke toon een frisse pastel-achtergrond met
           donkere inkt (licht) of een alpha-tint met lichte inkt (donker). */
        .hd-pill { display: inline-flex; align-items: center; gap: .3rem; padding: .1rem .55rem; border-radius: 9999px; border: 1px solid transparent; font-size: 11px; font-weight: 600; line-height: 1.25rem; white-space: nowrap; letter-spacing: .005em; }
        ${uiTheme === 'light' ? `
        .hd-pill-indigo  { background:#e0e7ff; color:#3730a3; border-color:#c7d2fe; }
        .hd-pill-sky     { background:#e0f2fe; color:#075985; border-color:#bae6fd; }
        .hd-pill-amber   { background:#fef3c7; color:#92400e; border-color:#fde68a; }
        .hd-pill-violet  { background:#ede9fe; color:#5b21b6; border-color:#ddd6fe; }
        .hd-pill-emerald { background:#d1fae5; color:#065f46; border-color:#a7f3d0; }
        .hd-pill-rose    { background:#ffe4e6; color:#9f1239; border-color:#fecdd3; }
        .hd-pill-slate   { background:#f1f5f9; color:#334155; border-color:#e2e8f0; }
        .hd-age-ok   { color:#64748b; }
        .hd-age-warn { color:#b45309; font-weight:600; }
        .hd-age-late { color:#be123c; font-weight:600; }
        ` : `
        .hd-pill-indigo  { background:rgba(99,102,241,.18);  color:#c7d2fe; border-color:rgba(99,102,241,.35); }
        .hd-pill-sky     { background:rgba(14,165,233,.18);  color:#bae6fd; border-color:rgba(14,165,233,.35); }
        .hd-pill-amber   { background:rgba(245,158,11,.18);  color:#fde68a; border-color:rgba(245,158,11,.35); }
        .hd-pill-violet  { background:rgba(139,92,246,.18);  color:#ddd6fe; border-color:rgba(139,92,246,.35); }
        .hd-pill-emerald { background:rgba(16,185,129,.18);  color:#a7f3d0; border-color:rgba(16,185,129,.35); }
        .hd-pill-rose    { background:rgba(244,63,94,.18);   color:#fecdd3; border-color:rgba(244,63,94,.35); }
        .hd-pill-slate   { background:rgba(100,116,139,.22); color:#e2e8f0; border-color:rgba(100,116,139,.35); }
        .hd-age-ok   { color:#94a3b8; }
        .hd-age-warn { color:#fbbf24; font-weight:600; }
        .hd-age-late { color:#fb7185; font-weight:600; }
        `}
        /* Interactieve elementen krijgen een vloeiende kleur/achtergrond-overgang. */
        button, a, [role="button"] { transition: background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease; }
        /* Sticky datatabel-koppen (desktop; op mobiel is de thead verborgen in resp-card).
           Achtergrond volgt het actieve thema; per-label branding raakt koppen niet. */
        table.resp-card thead th { position: sticky; top: 0; z-index: 2; background-color: ${uiTheme === 'light' ? '#ffffff' : '#1e293b'}; }
        /* Bill Shock Top-selectie: de regel die de klant in de top bracht kleurt
           volledig rood (bijbetalen) of groen (terugkrijgen); de overige regels van
           dezelfde klant blijven grijs. De kleur moet de per-cel kleuren overrulen,
           vandaar de !important op de cellen én hun inhoud. Tinten per thema: op wit
           een donkere inkt, op donker een lichte. */
        tr.bs-top-betalen td, tr.bs-top-betalen td * { color: ${uiTheme === 'light' ? '#b91c1c' : '#fca5a5'} !important; }
        tr.bs-top-ontvangen td, tr.bs-top-ontvangen td * { color: ${uiTheme === 'light' ? '#15803d' : '#86efac'} !important; }
        tr.bs-top-betalen, tr.bs-top-ontvangen { font-weight: 600; }
        /* Helpdesk → AI-voorstellen: goed / niet goed / let op. De -300-tinten van
           het donkere thema zijn op wit niet leesbaar en staan niet in het lichte
           palet; deze klassen dragen per thema hun eigen inkt. */
        .ai-goed { color: ${uiTheme === 'light' ? '#15803d' : '#86efac'}; }
        .ai-slecht { color: ${uiTheme === 'light' ? '#b91c1c' : '#fca5a5'}; }
        .ai-let-op { color: ${uiTheme === 'light' ? '#b45309' : '#fcd34d'}; }
        tr.bs-groep-totaal td { background-color: ${uiTheme === 'light' ? '#f8fafc' : 'rgba(15,23,42,.55)'}; }
        /* ── Revenue-samenvatting: secties Elektriciteit / Gas ──
           De tint zit alleen op de segmentkolom; tekst en lijnen zijn in het lichte
           thema donkerder/steviger, want de pastelkleuren van het donkere thema
           hebben op wit te weinig contrast. */
        .sum-tint-el  { background-color: ${uiTheme === 'light' ? '#eff6ff' : 'rgba(14,165,233,.10)'}; }
        .sum-tint-gas { background-color: ${uiTheme === 'light' ? '#ecfdf5' : 'rgba(16,185,129,.10)'}; }
        /* Categorie-labels van de vergelijkingsgrafiek: donkerder op wit dan de
           standaard slate-400, zodat ze net als de tabellabels goed leesbaar zijn. */
        .comp-axis-tick { fill: ${uiTheme === 'light' ? '#475569' : '#94a3b8'}; }
        /* Neutrale lijnkleur voor grafieken waar een cumulatieve lijn over de
           gekleurde staven loopt (Portfolio-evolutie). Bijna-zwart op wit,
           bijna-wit op donker: botst zo met géén enkele categoriekleur en blijft
           in beide thema's leesbaar. Recharts krijgt deze var als stroke/fill. */
        :root { --chart-neutral: ${uiTheme === 'light' ? '#0f172a' : '#e2e8f0'}; }
        /* De achtergrond waarop een grafiek staat. Gebruikt als scheidingsrand
           (donutsegmenten) en als halo onder een lijn die over staven loopt.
           Stond eerder alleen als fallback in de call-sites (#1e293b) — op het
           lichte thema tekende dat een donkere rand op wit. */
        :root { --chart-surface: ${uiTheme === 'light' ? '#ffffff' : '#1e293b'}; }
        /* ── Grafiekafwerking per module (klasse grafiek-modern) ──────────────
           Een module zet deze klasse op haar buitenste element. Puur afwerking:
           reekskleuren en stapelvolgorde blijven wat ze zijn. De regels staan
           hier en niet in de grafieken zelf, omdat elke waarde per thema
           verschilt. Zie ook de CHART_-constanten bovenaan dit bestand. */
        .grafiek-modern .recharts-cartesian-grid line { stroke: ${uiTheme === 'light' ? '#eef2f6' : 'rgba(148,163,184,.13)'} !important; stroke-dasharray: none !important; }
        .grafiek-modern .recharts-cartesian-axis-line { stroke: ${uiTheme === 'light' ? '#dbe2ea' : 'rgba(148,163,184,.28)'} !important; }
        .grafiek-modern .recharts-cartesian-axis-tick-value, .grafiek-modern .recharts-cartesian-axis-tick-value tspan { fill: ${uiTheme === 'light' ? '#64748b' : '#94a3b8'} !important; }
        .grafiek-modern .recharts-legend-item { margin-right: 16px !important; }
        .grafiek-modern .recharts-legend-item-text { color: ${uiTheme === 'light' ? '#475569' : '#cbd5e1'} !important; }
        /* Gestapelde balken en donutsegmenten: haarlijn in de kleur van de kaart. */
        .grafiek-modern .recharts-bar-rectangle path { stroke: var(--chart-surface); stroke-width: 1px; }
        .grafiek-modern .recharts-line-curve { stroke-linecap: round; stroke-linejoin: round; }
        /* Aanwijzer onder de muis: zachte kolom (balken) of dunne lijn (lijnen). */
        .grafiek-modern .recharts-tooltip-cursor.recharts-rectangle { fill: ${uiTheme === 'light' ? 'rgba(100,116,139,.09)' : 'rgba(148,163,184,.12)'} !important; }
        .grafiek-modern .recharts-tooltip-cursor.recharts-curve { stroke: ${uiTheme === 'light' ? '#cbd5e1' : 'rgba(148,163,184,.4)'} !important; }
        /* Tooltip: bolletje in de reekskleur, naam links, waarde rechts. De
           reekskleur staat als tekstkleur op de regel; het bolletje erft die,
           de tekst zelf krijgt de gewone inkt. */
        .grafiek-modern .recharts-default-tooltip { padding: 10px 12px !important; font-size: 12px; line-height: 1.35; }
        .grafiek-modern .recharts-tooltip-label { font-weight: 600; margin-bottom: 4px !important; }
        .grafiek-modern .recharts-tooltip-item { display: flex !important; align-items: center; gap: 7px; padding: 2px 0 !important; }
        .grafiek-modern .recharts-tooltip-item::before { content: ''; flex: none; width: 8px; height: 8px; border-radius: 9999px; background: currentColor; }
        .grafiek-modern .recharts-tooltip-item > span { color: ${uiTheme === 'light' ? '#334155' : '#e2e8f0'}; }
        .grafiek-modern .recharts-tooltip-item-separator { display: none; }
        .grafiek-modern .recharts-tooltip-item-value { margin-left: auto; padding-left: 18px; font-weight: 600; font-variant-numeric: tabular-nums; }
        /* Donut: percentages mogen buiten het tekenvlak vallen i.p.v. afgesneden
           te worden; de verbindingslijn is neutraal en dun. */
        /* Kaartranden zonder dekking (border-slate-800) stonden niet in het lichte
           palet en bleven dus bijna-zwart op wit. Binnen deze modules volgen ze
           de gewone kaartrand. */
        ${uiTheme === 'light' ? `.grafiek-modern .border-slate-800, .grafiek-modern .divide-slate-800 > :not([hidden]) ~ :not([hidden]) { border-color: #e8edf3 !important; }` : ''}
        .donut-vrij > svg { overflow: visible; }
        .grafiek-modern .recharts-pie-label-line { stroke: ${uiTheme === 'light' ? '#cbd5e1' : 'rgba(148,163,184,.45)'} !important; }

        /* ── Semantische tags/badges (platformbreed) ──────────────────────────
           Losse bg-lime-900/50 + text-lime-300-combinaties werkten alleen in het
           donkere thema: op wit bleef de vulling donkergroen terwijl de tekst door
           het lichte thema juist DONKER werd → donker op donker. Deze klassen
           zetten vulling, tekst én rand per thema, zodat een badge in beide
           thema's leesbaar is (elke combinatie haalt ≥4.5:1 op de eigen
           achtergrond). Kleurcodering volgt de Metering-module: groen =
           elektriciteit, oranje = gas.
           Gebruik: <span className="tag tag-el">EL</span> */
        .tag { display: inline-flex; align-items: center; gap: .25rem; border: 1px solid transparent;
               border-radius: 6px; font-weight: 600; line-height: 1.15; white-space: nowrap; }
        .tag-el   { background: ${uiTheme === 'light' ? '#ecfdf5' : 'rgba(16,185,129,.16)'};
                    color: ${uiTheme === 'light' ? '#047857' : '#6ee7b7'} !important;
                    border-color: ${uiTheme === 'light' ? '#a7f3d0' : 'rgba(16,185,129,.35)'}; }
        .tag-ng   { background: ${uiTheme === 'light' ? '#fff7ed' : 'rgba(249,115,22,.16)'};
                    color: ${uiTheme === 'light' ? '#c2410c' : '#fdba74'} !important;
                    border-color: ${uiTheme === 'light' ? '#fed7aa' : 'rgba(249,115,22,.35)'}; }
        .tag-neutral { background: ${uiTheme === 'light' ? '#f1f5f9' : 'rgba(148,163,184,.16)'};
                    color: ${uiTheme === 'light' ? '#475569' : '#cbd5e1'} !important;
                    border-color: ${uiTheme === 'light' ? '#e2e8f0' : 'rgba(148,163,184,.3)'}; }
        .tag-ok   { background: ${uiTheme === 'light' ? '#ecfdf5' : 'rgba(16,185,129,.16)'};
                    color: ${uiTheme === 'light' ? '#047857' : '#6ee7b7'} !important;
                    border-color: ${uiTheme === 'light' ? '#a7f3d0' : 'rgba(16,185,129,.35)'}; }
        .tag-fail { background: ${uiTheme === 'light' ? '#fef2f2' : 'rgba(239,68,68,.16)'};
                    color: ${uiTheme === 'light' ? '#b91c1c' : '#fca5a5'} !important;
                    border-color: ${uiTheme === 'light' ? '#fecaca' : 'rgba(239,68,68,.35)'}; }
        /* ── Mobiele dichtheid ─────────────────────────────────────────────────
           Alleen onder 640px; de desktop-opmaak blijft exact zoals ze was.
           KPI-tegels mogen op een telefoon smaller worden zodat er twee naast
           elkaar passen i.p.v. één per regel (de rest van de tegel-styling zit
           in de StatTile-component zelf). */
        @media (max-width: 639px) {
          .stat-tile { --stat-mw: min(140px, 100%) !important; }
        }
        /* Bedrag-accenten met dezelfde thema-logica (bv. de som bill shock). */
        .amt-pos { color: ${uiTheme === 'light' ? '#b91c1c' : '#fca5a5'} !important; }
        .amt-neg { color: ${uiTheme === 'light' ? '#047857' : '#6ee7b7'} !important; }
        /* Dunne omlijning rond de secties Elektriciteit / Gas en rond het eindtotaal. */
        .sum-box-el    { border-color: ${uiTheme === 'light' ? '#c7dcf7' : 'rgba(14,165,233,.28)'}; }
        .sum-box-gas   { border-color: ${uiTheme === 'light' ? '#c3e7d4' : 'rgba(16,185,129,.28)'}; }
        .sum-box-total { border-color: ${uiTheme === 'light' ? '#d9e1ec' : 'rgba(71,85,105,.7)'}; }
        .sum-txt-el   { color: ${uiTheme === 'light' ? '#1d4ed8' : '#38bdf8'} !important; }
        .sum-txt-gas  { color: ${uiTheme === 'light' ? '#047857' : '#34d399'} !important; }
        table.resp-card tr.sum-row > td { border-bottom: ${uiTheme === 'light' ? '1.5px solid #dbe4ef' : '1px solid rgba(51,65,85,.6)'}; }
        /* Overgang offtake → injectie: duidelijk zwaardere lijn. */
        table.resp-card tr.sum-split > td { border-top: ${uiTheme === 'light' ? '2.5px solid #c3d0e2' : '2px solid rgba(100,116,139,.75)'}; }
        table.resp-card tr.sum-subtotal > td { border-top: ${uiTheme === 'light' ? '1.5px solid #dbe4ef' : '1px solid rgba(51,65,85,.6)'}; }
        /* Tabel-dichtheid: platformbreed COMPACT, zonder keuze. Eén regel voor alle
           tabellen — geen per-tabel wijziging nodig. (Was een toggle in de
           sidebar-footer; die is bewust verwijderd, compact is nu de norm.) */
        table.resp-card td, table.resp-card th { padding-top: .3rem !important; padding-bottom: .3rem !important; }
        /* Mobiele bottom-tab-bar: hoogte als variabele → install-banner, update-toast én
           de main-bodemmarge schuiven automatisch mee (0px op desktop, geen tab-bar daar). */
        :root { --tabbar-h: 0px; }
        @media (max-width: 767px) {
          :root { --tabbar-h: calc(56px + env(safe-area-inset-bottom, 0px)); }
          main { padding-bottom: calc(var(--tabbar-h) + 1.5rem) !important; }

          /* ── Responsieve tabel-kaarten (BEIDE thema's) ──
             Tabellen met class "resp-card" worden op mobiel gestapelde kaarten:
             elke rij een kaart, elke cel een label/waarde-regel. Zo maakt een
             brede datatabel de pagina niet breder dan het scherm. De data-labels
             worden door het effect 'responsive-table-cards' uit de koppen gevuld. */
          table.resp-card thead { display: none; }
          table.resp-card, table.resp-card tbody { display: block; width: 100%; max-width: 100%; }
          table.resp-card tr { display: block; margin-bottom: 12px; padding: 6px 12px; border: 1px solid ${uiTheme === 'light' ? '#e8edf3' : '#334155'}; border-radius: 14px; background: ${uiTheme === 'light' ? '#ffffff' : '#1e293b'}; box-shadow: 0 1px 2px 0 rgba(15,23,42,.04), 0 10px 26px -16px rgba(15,23,42,.14); }
          table.resp-card td { display: flex; justify-content: space-between; align-items: baseline; gap: 14px; width: auto !important; max-width: none !important; padding: 6px 0 !important; border: 0 !important; border-bottom: 1px solid ${uiTheme === 'light' ? '#f1f5f9' : '#293548'} !important; text-align: right !important; white-space: normal !important; overflow: visible !important; text-overflow: clip !important; overflow-wrap: anywhere; }
          table.resp-card tr td:last-child { border-bottom: 0 !important; }
          table.resp-card td::before { content: attr(data-label); flex: 0 0 42%; text-align: left; font-weight: 600; color: ${uiTheme === 'light' ? '#64748b' : '#94a3b8'}; white-space: normal; }
          table.resp-card td[colspan]::before, table.resp-card td:empty { display: none; }
          table.resp-card td[colspan] { justify-content: flex-start; text-align: left !important; }
          table.resp-card td.truncate, table.resp-card td .truncate { max-width: none !important; }

          /* ── Mobiele ergonomie (BEIDE thema's) ── */
          /* Comfortabele touch-targets (checkbox/radio uitgezonderd zodat hun
             layout intact blijft). */
          input:not([type=checkbox]):not([type=radio]), select, textarea { min-height: 44px; }
          /* Fixed topbalk-knoppen vrij van de notch/statusbalk in PWA-standalone. */
          .fixed.top-3 { top: max(0.75rem, env(safe-area-inset-top, 0px)); }
          /* Elke horizontale scroll hoort BINNEN een blok te blijven (tabel,
             grafiek, tabbalk) — nooit de hele pagina. Momentum-scroll op iOS. */
          .overflow-x-auto { -webkit-overflow-scrolling: touch; max-width: 100%; }
          /* Lange, niet-afbreekbare tekst (e-mail, EAN, URL) mag een kaart niet
             oprekken. */
          .app-card, .app-card * { overflow-wrap: anywhere; }
        }
        /* Toegankelijkheid: respecteer prefers-reduced-motion (spinners/animaties). */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: .001ms !important; animation-iteration-count: 1 !important; transition-duration: .001ms !important; scroll-behavior: auto !important; }
        }
      `);

    // D. Revenue/Partnervergoeding — verfijnde WHITE-MODE afwerking. Enkel licht thema
    //    (dark mode blijft ongewijzigd). Komt ná de generieke light-regels → wint qua
    //    bron-volgorde. Raakt alleen .revenue-kpi (KPI-kaarten) + de Revenue-detailkaarten.
    if (uiTheme === 'light') parts.push(`
        .revenue-kpi {
          background-color: #ffffff !important; background-image: none !important;
          border: 1px solid #e6ebf2 !important;
          border-top: 3px solid var(--kpi-accent, #2563eb) !important;
          border-radius: 14px !important;
          padding: 1.05rem 1.15rem !important; gap: .95rem !important;
          box-shadow: 0 1px 2px rgba(15,23,42,.05), 0 10px 26px -18px rgba(15,23,42,.18) !important;
          transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease !important;
        }
        .revenue-kpi:hover {
          border-color: #dbe3ee !important;
          box-shadow: 0 2px 5px rgba(15,23,42,.07), 0 20px 42px -20px rgba(15,23,42,.26) !important;
          transform: translateY(-1px);
        }
        .revenue-kpi-icon {
          background-color: var(--kpi-accent, #2563eb) !important; background-image: none !important;
          border-radius: 12px !important;
          box-shadow: 0 8px 16px -8px var(--kpi-accent, #2563eb) !important;
        }
        .revenue-kpi-icon svg { color: #ffffff !important; }
        .revenue-kpi-title { color: #64748b !important; font-weight: 500 !important; letter-spacing: .005em !important; }
        .revenue-kpi-value { color: #0f172a !important; font-size: 1.5rem !important; line-height: 1.12 !important; letter-spacing: -.015em !important; margin-top: .12rem !important; }
        /* Subregel onder een KPI-bedrag (Wanbetalers → openstaand incl. btw): op wit
           is slate-400 te bleek om te lezen. */
        .revenue-kpi-sub { color: #64748b !important; }
        /* Detailkaarten (GOEDE KLANTEN / WANBETALERS / …): fijnere accent-rand + zachtere schaduw. */
        .app-card.border-l-4 { border-left-width: 3px !important; box-shadow: 0 1px 2px rgba(15,23,42,.04), 0 12px 30px -18px rgba(15,23,42,.16) !important; }
      `);

    if (parts.length === 0) { if (style) style.remove(); return; }
    if (!style) { style = document.createElement('style'); style.id = id; document.head.appendChild(style); }
    style.textContent = parts.join('\n');
    return () => { /* laat staan; wordt vervangen bij volgende wijziging */ };
  }, [activeBranding, isAdmin, uiTheme]);

  // Responsieve tabel-kaarten (responsive-table-cards): vult op mobiel per <td>
  // een data-label uit de bijhorende kolomkop, zodat tabellen met class
  // "resp-card" als gestapelde label/waarde-kaarten getoond kunnen worden (zie
  // de @media-regel in het thema-blok). Auto-bijwerken bij elke re-render via
  // een MutationObserver; enkel actief onder de md-breakpoint.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    let raf = 0;
    const apply = () => {
      raf = 0;
      document.querySelectorAll('table.resp-card').forEach(tbl => {
        const heads = Array.from(tbl.querySelectorAll('thead th')).map(th => th.textContent.trim());
        if (!heads.length) return;
        tbl.querySelectorAll('tbody tr').forEach(tr => {
          let i = 0;
          Array.from(tr.children).forEach(td => {
            if (td.tagName !== 'TD') return;
            const span = td.colSpan || 1;
            if (span === 1 && heads[i]) td.setAttribute('data-label', heads[i]);
            i += span;
          });
        });
      });
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(apply); };
    const obs = new MutationObserver(schedule);
    const sync = () => { obs.disconnect(); if (mq.matches) { obs.observe(document.body, { childList: true, subtree: true }); schedule(); } };
    sync();
    mq.addEventListener?.('change', sync);
    return () => { obs.disconnect(); mq.removeEventListener?.('change', sync); if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Favicon + browsertab-titel volgen de actieve branding. Voor partners is dat hun
  // energielabel; voor admin + ET Dossierbeheerder de "Energy Together"-branding (als
  // ingesteld). Zonder branding → neutrale platform-default.
  useEffect(() => {
    const setIcon = (href) => {
      document.querySelectorAll("link[rel~='icon']").forEach(l => l.remove());
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = href;
      document.head.appendChild(link);
    };
    // Favicon volgt het thema: een label kan een lichte en een donkere variant
    // instellen (bv. een donker beeldmerk dat op een lichte browsertab verdwijnt).
    const themed = brandingForTheme(activeBranding, uiTheme);
    if (themed?.favicon_data_url) {
      setIcon(themed.favicon_data_url);
      document.title = themed.display_name || 'Partner Portal';
    } else {
      setIcon('/favicon-32x32.png');
      document.title = 'Partner Portal';
    }
    // Onthouden voor de VOLGENDE start: het scriptje in public/index.html zet titel
    // en favicon dan al vóór de eerste tekening, zodat het tabblad niet eerst het
    // platform-icoon toont. Een lege huisstijl wist het onthouden merk weer.
    onthoudTabMerk(themed?.display_name || null, themed?.favicon_data_url || null);
  }, [activeBranding, isAdmin, uiTheme]);

  // Het icoon van de GEÏNSTALLEERDE app (pc, tablet, telefoon) volgt het
  // energielabel. Dat icoon komt niet uit de React-state maar uit het web-app-
  // manifest: een bestand dat de browser zelf ophaalt, buiten de app om en zonder
  // onze Authorization-header. Vandaar dat we hier alleen de <link>-verwijzingen
  // omzetten naar /api/branding/manifest?label=… (publiek, bouwt het manifest met
  // de branding van dat label — zie backend/src/utils/appIcon.js).
  //
  // Alleen voor de rollen die het platformmerk NIET mogen zien: admin en ET
  // Dossierbeheerder houden het statische public/manifest.json. Zonder label ook,
  // want dan valt er niets te branden.
  useEffect(() => {
    const zetLink = (rel, href) => {
      let l = document.querySelector(`link[rel="${rel}"]`);
      if (!l) { l = document.createElement('link'); l.rel = rel; document.head.appendChild(l); }
      l.href = href;
      return l;
    };
    // Onthouden voor de VOLGENDE keer: het scriptje in public/index.html zet de
    // verwijzing dan al goed vóór de eerste tekening, zodat een installatie meteen
    // na het openen ook het juiste icoon krijgt.
    const onthoud = (l, t) => {
      try {
        if (l) localStorage.setItem('appIconLabel', l); else localStorage.removeItem('appIconLabel');
        if (t) localStorage.setItem('appIconTeam', t); else localStorage.removeItem('appIconTeam');
      } catch { /* private mode */ }
    };
    const label = (activeBranding && activeBranding.energy_label) || user?.energyLabel || '';
    if (seesEtBrand(user?.role) || !label) {
      zetLink('manifest', '/manifest.json');
      zetLink('apple-touch-icon', '/apple-touch-icon.png');
      onthoud(null, null);
      window.__manifestGebrand = false;   // het platform-manifest is hier het juiste
      return;
    }
    const q = new URLSearchParams({ label });
    if (user?.salesTeam) q.set('team', user.salesTeam);
    onthoud(label, user?.salesTeam || '');
    zetLink('manifest', `/api/branding/manifest?${q.toString()}`);
    // Bij een EERSTE aanmelding op dit toestel stond het manifest tot hier op het
    // statische platform-bestand: het label was nog nergens bekend. Een aanbod dat
    // Chrome dáárvoor deed, hoort dus bij het verkeerde merk — installeren zou het
    // beeldmerk van het platform op het beginscherm zetten bij een rol die dat merk
    // niet mag zien. Weggooien; Chrome hertoetst na deze wissel en biedt opnieuw
    // aan, nu met het merk-manifest. Gebeurt dat niet meer in deze sessie, dan
    // staat de knop er bij het volgende bezoek meteen goed: de bootstrap in
    // index.html zet het manifest dan al vóór de eerste tekening (onthoud(...)).
    window.__manifestGebrand = true;
    if (window.__installPrompt && !window.__installPrompt.gebrand) {
      window.__installPrompt = null;
      setInstallPrompt(null);
    }
    // iOS ("Zet op beginscherm") kijkt naar apple-touch-icon, niet naar het
    // manifest; die krijgt hetzelfde beeld langs dezelfde route.
    zetLink('apple-touch-icon', `/api/branding/app-icon?${q.toString()}`);
    // De verwijzingen blijven staan als deze component ooit ontkoppelt: het
    // alternatief zou het platform-icoon terugzetten bij een rol die het niet mag
    // zien, en de app leeft toch tot het tabblad sluit.
  }, [activeBranding, user?.role, user?.energyLabel, user?.salesTeam, setInstallPrompt]);

  // ── Snelle start bij een deep-link (?sel=…) ─────────────────────────────────
  // Het rechtsklikmenu opent Customer 360 / Metering in een NIEUW tabblad, en dat
  // is een verse app-load. Die tab wachtte tot de VOLLEDIGE orderlijst binnen was
  // voor ze iets kon tonen — voor een admin 21.200 records / 46 MB in 22 pagina's,
  // dus tien seconden welkomstscherm voor één klant.
  //
  // Daarom halen we bij zo'n start eerst alleen de orders van dát doel op (één
  // request van een paar kB, ~0,4 s). Die gaan naar Customer 360 en Metering, zodat
  // die meteen kunnen tekenen; de volledige lijst laadt gewoon door en neemt het
  // daarna over. `orders` zelf blijft dus onaangeroerd — anders zou de Orders-pagina
  // een handvol records tonen alsof dat alles is.
  const [prefillOrders, setPrefillOrders] = useState([]);
  // Staat op true zodra die doelgerichte ophaal afgerond is — gelukt of niet. Zie
  // het blok hieronder: het achtergrondladen hangt eraan.
  const [doelOphaalKlaar, setDoelOphaalKlaar] = useState(false);
  useEffect(() => {
    if (!token || !urlSelection) return undefined;
    let leeft = true;
    const sel = `${urlSelection.type}:${urlSelection.value}`;
    // Het klantnummer gaat mee zodat de snelle start dezelfde orders vindt als de
    // pagina daarna zelf selecteert — zie orderSelectie.js: naam OF klantnummer.
    const selRefQ = urlSelection.ref ? `&selref=${encodeURIComponent(urlSelection.ref)}` : '';
    fetch(`/api/orders?sel=${encodeURIComponent(sel)}${selRefQ}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      // Zelfde afvlakking als in fetchOrders: `contract` komt soms als object
      // binnen en wordt rechtstreeks gerenderd.
      .then((d) => { if (leeft) setPrefillOrders((Array.isArray(d) ? d : []).map((o) => ((o.contract && typeof o.contract === 'object') ? { ...o, contract: o.contract.name || '' } : o))); })
      .catch(() => { /* mislukt = gewoon wachten op de volledige lijst */ })
      // Ook bij een mislukking het achtergrondladen vrijgeven: dan is de volledige
      // lijst juist de enige weg naar data, en die mag niet blijven wachten.
      .finally(() => { if (leeft) setDoelOphaalKlaar(true); });
    return () => { leeft = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Het zware achtergrondladen (volledige orderlijst, facturen + creditnota's,
  // partnervergoeding) start bij een deep-link-tab PAS als het doel binnen is.
  // Anders vechten tientallen megabytes achtergronddata om dezelfde verbinding als
  // het ene kleine request waar de geopende pagina echt op wacht — precies de
  // reden dat zo'n tabblad traag aanvoelde. Bij een gewone start is er niets om op
  // te wachten en gaat alles meteen weg.
  //
  // Uitstellen, niet overslaan: de volledige lijst voedt óók het zoekveld en de
  // andere pagina's van díé tab, dus ze moet er wel komen — alleen niet vóór het
  // scherm staat. Het vangnet-tijdslot dekt een ophaal die blijft hangen (de
  // .finally hierboven dekt een mislukking).
  // Een tab die rechtstreeks op één ticket opent, heeft het dashboard niet nodig:
  // de ticketpagina haalt haar eigen gegevens op. Zolang die tab op het ticket
  // staat, blijft het zware achtergrondladen (volledige orderlijst, facturen +
  // creditnota's, partnervergoeding — tientallen megabytes) dus achterwege. Het
  // gaat om UITSTELLEN, niet overslaan: zodra je in die tab wegnavigeert of het
  // ticket sluit, komt de rest alsnog en gedraagt de tab zich als elke andere.
  const [ticketTabStil, setTicketTabStil] = useState(IS_TICKET_DEEPLINK_START);
  useEffect(() => {
    if (!ticketTabStil) return undefined;
    // Weg van de Helpdesk of het ticket gesloten (hash leeg) → rem eraf.
    if (activePage !== 'helpdesk' || ticketHashId() == null) { setTicketTabStil(false); return undefined; }
    const opHash = () => { if (ticketHashId() == null) setTicketTabStil(false); };
    window.addEventListener('hashchange', opHash);
    return () => window.removeEventListener('hashchange', opHash);
  }, [ticketTabStil, activePage]);
  const [achtergrondVrij, setAchtergrondVrij] = useState(!urlSelection && !IS_TICKET_DEEPLINK_START);
  useEffect(() => {
    if (achtergrondVrij) return undefined;
    // Ticket-deeplink: de rem gaat er pas af als de tab het ticket verlaat. Geen
    // vangnet-tijdslot zoals hieronder — er is hier niets om op te wachten, dus een
    // tijdslot zou de rem juist loslaten terwijl je nog rustig het ticket leest.
    if (IS_TICKET_DEEPLINK_START) { if (!ticketTabStil) setAchtergrondVrij(true); return undefined; }
    if (doelOphaalKlaar) { setAchtergrondVrij(true); return undefined; }
    const t = setTimeout(() => setAchtergrondVrij(true), 4000);
    return () => clearTimeout(t);
  }, [achtergrondVrij, doelOphaalKlaar, ticketTabStil]);

  // Orders worden eager opgehaald (overal nodig voor scoping + sales_team→company lookup).
  // Invoices en credit-notes komen primair via CSV-upload — de ET API is voor deze
  // datasets in praktijk te traag (~hours voor 41k invoices door API-zijdige beperkingen).
  // De Invoices-pagina heeft een knop om alsnog via API op te halen.
  useEffect(() => { if (achtergrondVrij) fetchOrders(); }, [fetchOrders, achtergrondVrij]);

  const [facturenGeladen, setFacturenGeladen] = useState(false);
  // De laatste stap van het welkomstscherm heet "Facturen & vergoedingen laden" en
  // dekt sinds de prefetch allebei; hij vinkt pas af als ze allebei rond zijn.
  const [vergoedingGeladen, setVergoedingGeladen] = useState(false);
  const [invoicesApiLoading, setInvoicesApiLoading] = useState(false);
  const [invoicesApiError, setInvoicesApiError] = useState(null);
  const [invoicesApiInfo, setInvoicesApiInfo] = useState(null);
  // Eén ophaal per sessie. Staat hier (en niet bij het effect hieronder) omdat een
  // MISLUKTE ophaal de vlag weer terugzet: dan mag een latere paginabezoek het
  // opnieuw proberen i.p.v. de rest van de sessie met een lege tabel te blijven.
  const invoicesAutoLoaded = useRef(false);

  const fetchInvoicesAndCreditNotesFromApi = useMemo(() => async (force = false) => {
    if (!token || invoicesApiLoading) return;
    setInvoicesApiLoading(true);
    setInvoicesApiError(null);
    setInvoicesApiInfo(force
      ? 'Volledige sync gestart…'
      : 'Facturen laden…');
    try {
      // De server leest uit de lokale opslag (snel) en synct op de achtergrond
      // alleen de nieuwe/gewijzigde records. force=1 forceert een volledige
      // resync (blokkerend). Beide endpoints zijn cursor-paginated + gescopet.
      const q = force ? '?refresh=1' : '';
      // allSettled, GEEN all: facturen en creditnota's zijn twee losse feeds met
      // een eigen sync. Faalt de (trage) creditnota-feed, dan hoort de pagina nog
      // altijd de facturen te tonen — met Promise.all bleef het scherm leeg omdat
      // één afwijzing de hele afhandeling oversloeg.
      let syncing = false;
      const onMeta = (j) => { if (j.sync_running) syncing = true; };
      const [invRes, creditRes, selfRes] = await Promise.allSettled([
        fetchPaginated(`/api/invoices${q}`, token, { onMeta }),
        fetchPaginated(`/api/credit-notes${q}`, token, { onMeta }),
        fetchPaginated(`/api/selfbills${q}`, token, { onMeta }),
      ]);
      if (invRes.status === 'rejected' && creditRes.status === 'rejected') throw invRes.reason;
      const inv = invRes.status === 'fulfilled' ? invRes.value : [];
      const credit = creditRes.status === 'fulfilled' ? creditRes.value : [];
      const failed = invRes.status === 'rejected' ? 'facturen'
        : creditRes.status === 'rejected' ? "creditnota's" : '';
      // De ET-API levert el_contract/ng_contract soms als object {id,url,name,...}
      // i.p.v. een string; die velden worden rechtstreeks in de tabel gerenderd →
      // "Objects are not valid as a React child". Defensief afvlakken naar de naam
      // (backend doet dit ook via asText; dit vangt reeds gecachte object-data op).
      const flattenContracts = (recs) => (Array.isArray(recs) ? recs : []).map(r => {
        const elObj = r.elContract && typeof r.elContract === 'object';
        const ngObj = r.ngContract && typeof r.ngContract === 'object';
        if (!elObj && !ngObj) return r;
        return { ...r, elContract: elObj ? (r.elContract.name || '') : r.elContract, ngContract: ngObj ? (r.ngContract.name || '') : r.ngContract };
      });
      // Alleen overschrijven wat écht geladen is — een mislukte feed mag de al
      // getoonde records niet wegvegen.
      if (invRes.status === 'fulfilled') setApiInvoices(flattenContracts(inv));
      if (creditRes.status === 'fulfilled') setApiCreditNotes(flattenContracts(credit));
      // Self bills: derde feed, zelfde regel — een mislukte feed veegt niets weg.
      if (selfRes.status === 'fulfilled') setApiSelfbills(Array.isArray(selfRes.value) ? selfRes.value : []);
      // De sync draait op de achtergrond (een volledige ophaal duurt minuten tot
      // een uur en past niet binnen één request) — zeg dus eerlijk dat dit de
      // laatst gesynchroniseerde stand is en dat er nog iets loopt.
      setInvoicesApiInfo(`${inv.length} facturen + ${credit.length} creditnota's binnen scope.${
        syncing ? ' Synchronisatie loopt op de achtergrond — herlaad straks voor de bijgewerkte set.' : ''}`);
      if (failed) {
        const reason = (invRes.status === 'rejected' ? invRes.reason : creditRes.reason)?.message || 'onbekende fout';
        setInvoicesApiError(`Deels geladen (${inv.length} facturen + ${credit.length} creditnota's) — ${failed} konden niet opgehaald worden: ${reason}`);
      }
    } catch (err) {
      setInvoicesApiError(err.message);
      setInvoicesApiInfo(null);
      // Mislukt = geen data. Vlag terugzetten zodat de eerstvolgende pagina die
      // facturen nodig heeft het opnieuw probeert (zie het effect hieronder).
      invoicesAutoLoaded.current = false;
    } finally {
      setInvoicesApiLoading(false);
      // Voedt de laatste stap op het welkomstscherm. `invoicesApiLoading` alleen
      // volstaat niet: die begint op false en staat ook na afloop weer op false,
      // dus "niet aan het laden" betekent op zichzelf niet "klaar".
      setFacturenGeladen(true);
    }
  }, [token, invoicesApiLoading]);

  // Facturen worden — net als orders — METEEN BIJ HET OPSTARTEN opgehaald, niet
  // pas wanneer je een pagina opent die ze nodig heeft. Twee redenen:
  //   1. de laatste stap van het welkomstscherm ("Facturen & vergoedingen laden")
  //      beschrijft dan ook echt wat de app aan het doen is; ze bleef anders
  //      onafgevinkt staan tot je toevallig Invoices opende;
  //   2. wie op Invoices, Customer 360, Aging, Retention of Portfolio Insights
  //      landt, ziet niet eerst een lege tabel.
  // Het welkomstscherm WACHT hier niet op — facturen horen niet bij de kritieke
  // ruggengraat (branding/portefeuille/orders), dit is puur een voorsprong. De
  // server antwoordt uit zijn lokale opslag en synct op de achtergrond, dus de
  // ophaal kost vooral doorlooptijd, geen wachttijd op de bron.
  // `achtergrondVrij`: in een deep-link-tab pas nadat het doel binnen is, zodat de
  // twee factuurfeeds niet met dat ene request concurreren (zie hierboven).
  useEffect(() => {
    if (!token || !achtergrondVrij || invoicesAutoLoaded.current) return;
    invoicesAutoLoaded.current = true;
    fetchInvoicesAndCreditNotesFromApi(false);
  }, [token, achtergrondVrij, fetchInvoicesAndCreditNotesFromApi]);

  // Vangnet bij een MISLUKTE opstart-ophaal (de catch zet de vlag terug): wie dan
  // een pagina opent die facturen nodig heeft, probeert het opnieuw i.p.v. de rest
  // van de sessie op een lege tabel te kijken.
  useEffect(() => {
    // Retention rekent met facturen (openstaand bedrag + de betalingsachterstand
    // achter Drop Risk), dus die pagina moet ze ook zelf kunnen laden. Portfolio
    // Insights deelt diezelfde vertrek-tabellen (incl. de kolom "Openstaand") —
    // zonder facturen stond daar overal € 0,00 tot je eerst Invoices of Retention
    // had geopend.
    // Metering stond hier ook, voor de coëfficiënt en de majoratie achter de
    // verkoopprijs. Die komen nu van /api/tariffs (de commissie-bron, mét EL/NG-
    // detail) i.p.v. uit deze feed, die dat detail niet draagt — zie
    // backend/src/routes/tariffs.js. De pagina heeft dus geen facturen meer nodig.
    // `achtergrondVrij` staat hier óók bij: zonder die voorwaarde zou een
    // deep-link-tab die op Customer 360 opent de factuurfeeds meteen via dít
    // vangnet starten, en was het uitstellen hierboven zinloos.
    const needsInvoices = ['invoices', 'customer360', 'billing', 'drop-loss', 'portfolio'].includes(activePage);
    if (needsInvoices && token && achtergrondVrij && !invoicesAutoLoaded.current
        && apiInvoices.length === 0 && apiCreditNotes.length === 0 && !invoicesApiLoading) {
      invoicesAutoLoaded.current = true;
      fetchInvoicesAndCreditNotesFromApi(false);
    }
  }, [activePage, token, achtergrondVrij, apiInvoices.length, apiCreditNotes.length, invoicesApiLoading, fetchInvoicesAndCreditNotesFromApi]);

  // Orders komen uit de API (server-side al gescoped per request);
  // scopeFilter is een client-side defense-in-depth vangnet. applyOnlineOrderRules
  // vult per order de lege verkoper → 'Online' en (bij Online/OdooBot) het lege
  // verkoopteam → 'Online' — één centrale plek, zodat Orders, Revenue-detail, de
  // CONTROLE-tabel, filters en exports allemaal dezelfde correctie zien.
  const orders = useMemo(() => scopeFilter(apiOrders, user).map(applyOnlineOrderRules), [apiOrders, user]);
  // Dezelfde behandeling als `orders`: client-side scope-vangnet + de Online-regels.
  // (De ophaal van `prefillOrders` zelf staat hoger, bij "Snelle start bij een
  // deep-link": ze moet vóór het achtergrondladen staan, want die wacht erop.)
  const prefillOrdersScoped = useMemo(
    () => scopeFilter(prefillOrders, user).map(applyOnlineOrderRules),
    [prefillOrders, user],
  );
  // De orders voor de twee pagina's die een deep-link kunnen krijgen: de volledige
  // lijst, aangevuld met het doel zolang die er nog niet in zit. Zodra de volledige
  // lijst binnen is, voegt dit niets meer toe (zelfde id's) en valt het weg.
  const ordersMetDoel = useMemo(() => {
    if (!prefillOrdersScoped.length) return orders;
    const gezien = new Set(orders.map((o) => o.id));
    const extra = prefillOrdersScoped.filter((o) => !gezien.has(o.id));
    return extra.length ? [...orders, ...extra] : orders;
  }, [orders, prefillOrdersScoped]);
  // Energielabel "Belvus Energie" wordt bewust UIT de Sales- en Portfolio
  // Insights-modules gehouden (op verzoek). Andere modules tonen het wel.
  const ordersExclBelvus = useMemo(
    () => orders.filter(o => (o.company || '').trim().toLowerCase() !== 'belvus energie'),
    [orders]
  );
  // Facturen + creditnota's komen uit de API (server-side al gescoped);
  // scopeFilter is een client-side defense-in-depth vangnet. Backend negeert
  // de creditnota-bedragen al.
  const facturenBase = useMemo(() => scopeFilter(apiInvoices, user), [apiInvoices, user]);
  const creditfacturen = useMemo(() => scopeFilter(apiCreditNotes, user), [apiCreditNotes, user]);
  const facturen = useMemo(() => [...facturenBase, ...creditfacturen], [facturenBase, creditfacturen]);
  // Self bills staan bewust NIET in `facturen`: ze horen niet in aging,
  // wanbetalerstatus of openstaand bedrag. Eigen prop voor Invoices en Customer 360.
  const selfbills = useMemo(() => scopeFilter(apiSelfbills, user), [apiSelfbills, user]);
  // Belvus Energie ook uit de facturen-stroom voor de Sales-module houden.
  const facturenExclBelvus = useMemo(
    () => facturen.filter(f => (f.relatieBedrijf || '').trim().toLowerCase() !== 'belvus energie'),
    [facturen]
  );

  // Per-energielabel wanbetaler-config (drempel in dagen + oninbaarheidsperiode).
  // ÉÉN fetch op app-niveau, doorgegeven aan elke module die de status berekent
  // of toont — Revenue, Orders, Facturen, Aging en Customer 360. Zou elke pagina
  // dit los ophalen (of overslaan), dan kreeg dezelfde klant per pagina een andere
  // status zodra een label van de default afwijkt. Backend scope't per rol: een
  // partner krijgt enkel zijn eigen label terug.
  const [labelConfig, setLabelConfig] = useState({});
  const loadLabelConfig = useCallback(() => {
    if (!token) return Promise.resolve();
    return fetch('/api/label-config', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setLabelConfig(normalizeLabelConfig(d.config)); })
      .catch(() => {});
  }, [token]);
  useEffect(() => { loadLabelConfig(); }, [loadLabelConfig]);

  // Centrale cascade-structuur (energielabel → verkoopteam → verkoper) voor de
  // filters in alle modules. Bron is /api/reference/sales-structure (afgeleid uit
  // het ET /resellers-endpoint) en al rol-gescopet door de backend, zodat de
  // dropdowns overal identiek zijn én aansluiten op de rechtenstructuur. Eén
  // fetch op app-niveau; refetcht bij token- of (impersonatie-)labelwissel.
  const [structuurGeladen, setStructuurGeladen] = useState(false);
  const [salesStructure, setSalesStructure] = useState(EMPTY_STRUCTURE);
  // De sales-structure laadt nu direct (resellers-gebaseerd, niet geblokkeerd door
  // de trage orders-fetch). Zodra de orders geladen zijn (ordersLoaded) halen we
  // 'm opnieuw op zodat order-only labels/teams/verkopers er alsnog bij komen.
  const ordersLoaded = apiOrders.length > 0;
  useEffect(() => {
    if (!token) return;
    fetch('/api/reference/sales-structure', { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setSalesStructure(d || EMPTY_STRUCTURE))
      .catch(() => setSalesStructure(EMPTY_STRUCTURE))
      // Voedt de stap "Klantenportefeuille laden" op het welkomstscherm. Ook bij
      // een fout klaar: de stap beschrijft of de aanvraag afgerond is, niet of ze
      // gelukt is — anders blijft het scherm hangen op een mislukte call.
      .finally(() => setStructuurGeladen(true));
  }, [token, user?.energyLabel, ordersLoaded]);
  // Belvus Energie ook uit de cascade-dropdowns (energielabel/team/verkoper) van
  // de Sales- en Portfolio-modules halen, niet alleen uit de data.
  const salesStructureExclBelvus = useMemo(() => {
    const isBelvus = (l) => (l || '').trim().toLowerCase() === 'belvus energie';
    const teams_by_label = {};
    for (const [label, v] of Object.entries(salesStructure.teams_by_label || {})) if (!isBelvus(label)) teams_by_label[label] = v;
    const sellers_by_label_team = {};
    for (const [label, v] of Object.entries(salesStructure.sellers_by_label_team || {})) if (!isBelvus(label)) sellers_by_label_team[label] = v;
    return {
      ...salesStructure,
      energy_labels: (salesStructure.energy_labels || []).filter(l => !isBelvus(l)),
      teams_by_label,
      sellers_by_label_team,
      unmatched: (salesStructure.unmatched || []).filter(u => !isBelvus(u.label)),
    };
  }, [salesStructure]);

  // Revenue draait NIET meer op facturen.csv/creditfacturen.csv, maar op de
  // nieuwe partnervergoeding-bestanden (partner_commission_export /
  // partner_commission_Credit_export), geüpload op de Revenue-pagina zelf.
  // Records worden geparset bij upload (xlsx of csv) en hier verrijkt met het
  // verkoopteam (uit de centrale sales-structuur) + scope-gefilterd. Records
  // zonder herkend energielabel (prefix-onbekend) worden NIET getoond — we
  // rapporteren alleen hun aantal als melding.
  // Per soort (sales/credit/selfbill) een LIJST van bestanden: nieuwe selecties
  // worden TOEGEVOEGD i.p.v. overschreven, zodat een grote partnervergoeding-
  // export over meerdere bestanden (bv. partner_commission_export_1/_2)
  // ingeladen kan worden. De bestandsnaam is irrelevant voor de parsing (label
  // uit factuurnummer, kolommen op naam) — puur informatief in de UI.
  const [commissionFiles, setCommissionFiles] = useState({ sales: [], credit: [], selfbill: [] });
  // Bron van de huidige commission-data: 'file' (upload) of 'api'. Stuurt de
  // weergave: upload-knoppen worden ALLEEN groen bij een echte bestand-upload;
  // bij een API-load tonen we een aparte melding met tijdstip i.p.v. groen.
  const [commissionSource, setCommissionSource] = useState(null);
  const [commissionApiInfo, setCommissionApiInfo] = useState(null);
  // CSV-/bestand-import van partnervergoeding is verwijderd — er wordt altijd via de
  // API (+ jobs) gewerkt. commissionFiles bewaart nog wel de via API geladen records.
  // Voeg de via de ET API opgehaalde commission-data toe. ACCUMULEERT PER LABEL:
  // een nieuwe fetch vervangt enkel de entries van dát energielabel (_apiLabel) en
  // behoudt de andere labels — zo blijven meerdere labels tegelijk geladen.
  // Wisselen van bestand-upload naar API vervangt wel alles (geen file+API-mix).
  const handleCommissionFromApi = (result) => {
    const label = result.label;
    setCommissionFiles(prev => {
      const base = commissionSource === 'api' ? prev : { sales: [], credit: [], selfbill: [] };
      const merge = (kind) => [
        ...(base[kind] || []).filter(f => f._apiLabel !== label),
        ...(result[kind] || []),
      ];
      return { sales: merge('sales'), credit: merge('credit'), selfbill: merge('selfbill') };
    });
    setCommissionSource('api');
    const now = new Date();
    const fmtTs = (ts) => { try { return new Date(ts).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return null; } };
    setCommissionApiInfo(prev => {
      const labels = [...new Set([...((commissionSource === 'api' && prev?.labels) || []), label].filter(Boolean))].sort();
      return {
        labels,
        syncedAt: result.info?.syncedAt ? fmtTs(result.info.syncedAt) : (commissionSource === 'api' ? prev?.syncedAt : null),
        at: now.toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      };
    });
  };
  // Partnervergoeding alvast ophalen bij het OPSTARTEN, net als de facturen. Wie
  // Revenue opent hoort er cijfers te zien; de pagina begon anders pas bij het
  // openen te laden — en bij een verouderde cache leverde dat zelfs "er staat nog
  // geen partnervergoeding klaar" op (zie de sync-race in routes/commission.js).
  //
  // Alleen voor een rol met één vast energielabel (master/teamleader/reseller).
  // Admin en ET-dossierbeheerder kijken over alle labels en kiezen er zelf een —
  // dan valt er niets te raden en zou een prefetch het verkeerde label ophalen.
  // Ook deze prefetch wacht in een deep-link-tab op het doel (`achtergrondVrij`).
  const vergoedingVoorgeladen = useRef(false);
  useEffect(() => {
    if (!token || !user || !achtergrondVrij || vergoedingVoorgeladen.current) return;
    const label = defaultScopeSelection(user).company;
    if (!label || label === 'all') { setVergoedingGeladen(true); return; }
    vergoedingVoorgeladen.current = true;
    haalPartnervergoeding({ label, token })
      .then((res) => { if (res) handleCommissionFromApi(res); })
      // Mislukt? Vlag terug, zodat de Revenue-pagina het bij het openen alsnog
      // probeert (die heeft ook een scherm om de fout op te tonen).
      .catch(() => { vergoedingVoorgeladen.current = false; })
      .finally(() => setVergoedingGeladen(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, achtergrondVrij]);

  const handleCommissionClearAll = () => {
    setCommissionFiles({ sales: [], credit: [], selfbill: [] });
    setCommissionSource(null);
    setCommissionApiInfo(null);
  };
  // Flatten een lijst bestanden → records met gegarandeerd unieke id (React key),
  // ongeacht uit welk bestand ze komen.
  const flattenCommission = (list, prefix) =>
    (list || []).flatMap((f, fi) => f.records.map((r, ri) => ({ ...r, id: `${prefix}-${fi}-${ri}` })));
  const commissionAll = useMemo(() => {
    const all = [
      ...flattenCommission(commissionFiles.sales, 'pcomm'),
      ...flattenCommission(commissionFiles.credit, 'pcredit'),
    ];
    enrichCommissionTeams(all, salesStructure); // verkoopteam per (label, verkoper)
    return all;
  }, [commissionFiles, salesStructure]);
  const revenueUnmappedCount = useMemo(
    () => commissionAll.filter(r => r._unmappedLabel).length,
    [commissionAll]
  );
  // Wat Revenue te zien krijgt: scope-gefilterd én alleen records met een
  // herkend energielabel.
  const revenueFacturen = useMemo(
    () => scopeFilter(commissionAll.filter(r => !r._unmappedLabel), user),
    [commissionAll, user]
  );
  // Self bills (partner_commission_export_SB) — alleen elektriciteit, getoond in
  // een aparte tabel op de Revenue-pagina. NIET gemengd met de hoofdfacturen
  // (commissionAll). Zelfde parser/verrijking/scoping/label-mapping.
  const selfBillsAll = useMemo(() => {
    const recs = flattenCommission(commissionFiles.selfbill, 'psb');
    enrichCommissionTeams(recs, salesStructure);
    return recs;
  }, [commissionFiles.selfbill, salesStructure]);
  const selfBillUnmappedCount = useMemo(
    () => selfBillsAll.filter(r => r._unmappedLabel).length,
    [selfBillsAll]
  );
  const revenueSelfBills = useMemo(
    () => scopeFilter(selfBillsAll.filter(r => !r._unmappedLabel), user),
    [selfBillsAll, user]
  );
  const commissionMeta = useMemo(() => {
    const recCount = list => (list || []).reduce((n, f) => n + f.records.length, 0);
    const fileNames = list => (list || []).map(f => f.name);
    return {
      salesNames: fileNames(commissionFiles.sales),
      creditNames: fileNames(commissionFiles.credit),
      selfbillNames: fileNames(commissionFiles.selfbill),
      salesFileCount: (commissionFiles.sales || []).length,
      creditFileCount: (commissionFiles.credit || []).length,
      selfbillFileCount: (commissionFiles.selfbill || []).length,
      salesCount: recCount(commissionFiles.sales),
      creditCount: recCount(commissionFiles.credit),
      selfbillCount: recCount(commissionFiles.selfbill),
      unmappedCount: revenueUnmappedCount,
      selfbillUnmappedCount: selfBillUnmappedCount,
      source: commissionSource,
      api: commissionApiInfo,
    };
  }, [commissionFiles, revenueUnmappedCount, selfBillUnmappedCount, commissionSource, commissionApiInfo]);
  const openSettings = isAdmin ? () => setActivePage('settings-general') : null;

  const onImpersonate = (newToken, userData, adminId) => {
    loginWithToken(newToken, userData, adminId);
    setActivePage('orders');
  };

  const handleStopImpersonate = async () => {
    try {
      const data = await stopImpersonate(token);
      // Terug naar het eigen merkdomein van de beheerder. `clearLocal` wist het
      // impersonatie-token op DIT domein — anders blijft daar een bruikbare
      // sessie van de geïmpersonateerde gebruiker achter.
      const host = brandHopTarget(data.user, 'partner');
      if (host) {
        try { await hopToBrandDomain(host, data.token, { page: 'users', clearLocal: true }); return; }
        catch { /* val terug: herstel de sessie binnen dit domein */ }
      }
      loginWithToken(data.token, data.user);
      setActivePage('users');
    } catch (err) { console.error(err); }
  };

  const renderPage = () => {
    const page = (() => {
    switch (activePage) {
      case 'customer360': return <Customer360Page branding={activeBranding} allBranding={allBranding} allTeamBranding={allTeamBranding} orders={ordersMetDoel} ordersLoading={ordersLoading} facturen={facturen} selfbills={selfbills} facturenLoading={!facturenGeladen} token={token} currentUser={user} prefill={customer360Prefill} prefillKey={customer360PrefillKey} visiblePartnerTags={visiblePartnerTags} salesStructure={salesStructure} onReassignOrder={reassignOrder} labelConfig={labelConfig} userModules={isAdmin ? null : userModules} />;
      case 'metering': return <MeteringPage orders={ordersMetDoel} ordersLoading={ordersLoading} token={token} currentUser={user} prefill={meteringPrefill} prefillKey={meteringPrefillKey} branding={activeBranding} allBranding={allBranding} allTeamBranding={allTeamBranding} />;
      case 'orders': return <OrdersPage orders={orders} loading={ordersLoading} facturen={facturen} onSettings={openSettings} onOpenCustomer360={openCustomer360} salesStructure={salesStructure} visiblePartnerTags={visiblePartnerTags} currentUser={user} onReassignOrder={reassignOrder} labelConfig={labelConfig} />;
      case 'sales': return <SalesPage orders={ordersExclBelvus} facturen={facturenExclBelvus} onSettings={openSettings} onOpenCustomer360={openCustomer360} salesStructure={salesStructureExclBelvus} currentUser={user} activeBranding={activeBranding} allBranding={allBranding} allTeamBranding={allTeamBranding} labelConfig={labelConfig} />;
      case 'portfolio': return <PortfolioInsightsPage orders={ordersExclBelvus} facturen={facturenExclBelvus} salesStructure={salesStructureExclBelvus} currentUser={user} onOpenCustomer360={openCustomer360} />;
      case 'drop-loss': return <RetentionPage orders={ordersExclBelvus} facturen={facturenExclBelvus} salesStructure={salesStructureExclBelvus} currentUser={user} onOpenCustomer360={openCustomer360} />;
      case 'invoices': return <InvoicesPage facturen={facturen} selfbills={selfbills} orders={orders} loading={ordersLoading || invoicesApiLoading} onOpenCustomer360={openCustomer360} onSettings={openSettings} salesStructure={salesStructure} apiLoading={invoicesApiLoading} apiError={invoicesApiError} apiInfo={invoicesApiInfo} currentUser={user} labelConfig={labelConfig} />;
      case 'revenue': return <RevenuePage facturen={revenueFacturen} selfBills={revenueSelfBills} orders={ordersExclBelvus} ordersLoading={ordersLoading} onOpenCustomer360={openCustomer360} onSettings={openSettings} currentUser={user} salesStructure={salesStructure} onCommissionFromApi={handleCommissionFromApi} onClearAllCommission={handleCommissionClearAll} commissionMeta={commissionMeta} token={token} feeNotAllowedTags={feeNotAllowedTags} visiblePartnerTags={visiblePartnerTags} partnerTagAll={partnerTagAll} onPartnerTagsChanged={loadPartnerTags} activeBranding={activeBranding} allBranding={allBranding} allTeamBranding={allTeamBranding} labelConfig={labelConfig} onLabelConfigChanged={loadLabelConfig} />;
      case 'billing': return <BillingPage facturen={facturen} loading={ordersLoading || invoicesApiLoading} onOpenCustomer360={openCustomer360} onSettings={openSettings} salesStructure={salesStructure} currentUser={user} labelConfig={labelConfig} />;
      case 'market': return <MarketPricesPage token={token} currentUser={user} branding={activeBranding} />;
      case 'billshock': return <BillShockPage token={token} currentUser={user} salesStructure={salesStructure} onOpenCustomer360={openCustomer360} />;
      case 'debt-collection': return <DebtCollectionPage orders={orders} facturen={facturen} token={token} currentUser={user} onOpenCustomer360={openCustomer360} salesStructure={salesStructure} visiblePartnerTags={visiblePartnerTags} />;
      case 'payment-plans': return <PaymentPlansPage orders={orders} token={token} />;
      case 'learning': return <LearningPage token={token} currentUser={user} />;
      case 'revenue-calculator': return <RevenueCalculatorPage token={token} currentUser={user} branding={activeBranding} />;
      case 'referrals':
        // Per-gebruiker module: admin ziet altijd; non-admin alleen indien toegekend.
        if (!isAdmin && !userModules.has('referrals')) return <OrdersPage orders={orders} facturen={facturen} onSettings={openSettings} currentUser={user} />;
        return <ReferralsPage orders={orders} onOpenCustomer360={openCustomer360} />;
      case 'helpdesk': return <HelpdeskPage token={token} currentUser={user} salesStructure={salesStructure} onOpenCustomer360={openCustomer360} onTicketGesloten={() => setTicketTabStil(false)} orders={ordersMetDoel} />;
      case 'optimise': return <OptimisePage token={token} currentUser={user} onUnread={loadFeedbackUnread} />;
      case 'users': return canViewUsers ? <UsersPage token={token} onImpersonate={onImpersonate} currentUser={user} salesStructure={salesStructure} /> : null;
      case 'account': return <AccountPage token={token} onLogout={logout} />;
      case 'audit-log': return canSeeAuditLog ? <AuditLogPage token={token} /> : null;
      case 'settings-label': return isAdmin ? <BrandingSettingsPage token={token} allBranding={allBranding} allTeamBranding={allTeamBranding} availableLabels={availableLabels} prospectTeams={prospectTeams} prospectLabels={prospectLabels} salesStructure={salesStructure} onBrandingChanged={loadBranding} /> : null;
      case 'settings-general': return isAdmin ? <GeneralSettingsPage token={token} /> : null;
      case 'index-parameters': return isAdmin ? <IndexParametersPage token={token} /> : null;
      case 'mail-templates': return isAdmin ? <MailTemplatesPage token={token} availableLabels={availableLabels} salesStructure={salesStructure} /> : null;
      case 'mail-log': return canSeeAuditLog ? <MailLogPage token={token} /> : null;
      case 'mail-domains': return isAdmin ? <MailDomainsPage token={token} availableLabels={availableLabels} /> : null;
      case 'opp-stages': return isAdmin ? <OppStagesPage token={token} onSaved={(key, entry) => setStageDescriptions((prev) => ({ ...prev, [key]: entry }))} /> : null;
      case 'helpdesk-tags': return isAdmin ? <HelpdeskTagsPage token={token} /> : null;
      case 'jobs': return isAdmin ? <JobsPage token={token} /> : null;
      case 'gamification': return isAdmin ? <GamificationAdminPage token={token} /> : null;
      default: return <OrdersPage orders={orders} facturen={facturen} onSettings={openSettings} salesStructure={salesStructure} currentUser={user} onReassignOrder={reassignOrder} />;
    }
    })();
    // De actieve module doorgeven zodat PageHeader het documentatie-icoontje naast
    // de titel kan tonen. Revenue heeft een eigen functionele beschrijving
    // (RevenueDocs), dus daar wordt de generieke functionele knop onderdrukt.
    return (
      <PageDocsContext.Provider value={{ moduleId: activePage, showFunctional: activePage !== 'revenue' }}>
        <PaginaFoutgrens resetKey={activePage}>{page}</PaginaFoutgrens>
      </PageDocsContext.Provider>
    );
  };

  // Top-right account/beheer-menu helpers
  const goToFromMenu = (p) => { setActivePage(p); setAccountMenuOpen(false); };
  const accountItemCls = (p) => `w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium ${activePage === p ? 'bg-lime-500/10 text-lime-400' : 'text-slate-300 hover:bg-slate-800 hover:text-slate-100'}`;
  const userInitials = (user?.name || user?.email || '?').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  // ── Welkomstscherm: fase 2, de dashboarddata ────────────────────────────────
  // Elke stap is klaar wanneer zijn request AFGEROND is, ook als die faalde: de
  // stap beschrijft of de app nog op iets wacht, niet of het gelukt is. Anders
  // blijft het scherm hangen op een kapotte call.
  //
  // KRITIEK = partnergegevens, klantenportefeuille en orders; dat is de ruggengraat
  // van elk scherm. Facturen & vergoedingen laden dóór na het openen: die horen bij
  // een deel van de modules, en de tabellen daar hebben hun eigen laadstaat al.
  const welkomStappen = [
    { label: WELKOM_STAPLABELS[0], klaar: brandingGeladen },
    { label: WELKOM_STAPLABELS[1], klaar: structuurGeladen },
    { label: WELKOM_STAPLABELS[2], klaar: !ordersLoading },
    { label: WELKOM_STAPLABELS[3], klaar: facturenGeladen && vergoedingGeladen },
  ];
  // Bij een deep-link (?sel=…) is de kritieke data BINNEN zodra het doel er is: die
  // tab opent op één klant, en daarvoor volstaat de doelgerichte ophaal (~0,4 s)
  // i.p.v. de volledige orderlijst (~10 s). Levert die niets op — klant buiten
  // scope, of de ophaal faalde — dan valt het scherm terug op de gewone regel, want
  // dan hangt de pagina alsnog van de volledige lijst af.
  // (Blijft staan voor het geval het welkomstscherm ooit tóch in zo'n tab hoort;
  //  vandaag komt het er niet, zie `welkomActief` hieronder.)
  const doelKlaar = prefillOrdersScoped.length > 0;
  const kritiekKlaar = brandingGeladen && structuurGeladen && (!ordersLoading || doelKlaar);

  // `welkomActief` = mag nog getoond worden (gaat één keer uit en komt niet terug,
  // dus een volgend bezoek binnen dezelfde sessie opent meteen het dashboard).
  // `welkomZichtbaar` = de drempel is gepasseerd. Zonder die drempel zou het scherm
  // bij een snelle of gecachte start heel even opflitsen, wat storender is dan geen
  // scherm. Is de kritieke data binnen vóór de drempel, dan verschijnt het nooit.
  // In een deep-link-tab (?sel=… uit het rechtsklikmenu) staat hij van meet af aan
  // UIT: die tab opent op één klant en hoort dus niet eerst het hele dashboard aan
  // te kondigen — laat staan te wachten op facturen of vergoedingen die dat scherm
  // niet nodig heeft. Customer 360 en Metering tonen zelf een laadteken zolang hún
  // gegevens onderweg zijn.
  const [welkomActief, setWelkomActief] = useState(!urlSelection && !IS_TICKET_DEEPLINK_START);
  const [welkomZichtbaar, setWelkomZichtbaar] = useState(false);
  const [welkomFadet, setWelkomFadet] = useState(false);
  useEffect(() => {
    if (!welkomActief) return undefined;
    const t = setTimeout(() => setWelkomZichtbaar(true), 250);
    return () => clearTimeout(t);
  }, [welkomActief]);
  useEffect(() => {
    if (!welkomActief || !kritiekKlaar) return undefined;
    if (!welkomZichtbaar) { setWelkomActief(false); return undefined; }
    setWelkomFadet(true);
    const t = setTimeout(() => setWelkomActief(false), 420);
    return () => clearTimeout(t);
  }, [welkomActief, kritiekKlaar, welkomZichtbaar]);

  // Het logo van het ENERGIELABEL van deze gebruiker, niet het ET-logo. Zelfde
  // regel als de mobiele topbalk (zie CLAUDE.md): partnerrollen krijgen nooit het
  // ET-logo of de merknaam te zien, wel hun eigen label of een neutrale badge.
  const welkomLogo = themedBranding?.logo_data_url || (seesEtBrand(user?.role) ? '/logo.png' : null);
  const welkomMerk = themedBranding?.display_name || (seesEtBrand(user?.role) ? 'Energy Together' : (user?.energyLabel || 'Partner Portal'));

  const welkomScherm = (welkomActief && welkomZichtbaar) ? (
    <WelkomstKaart
      logo={welkomLogo}
      merknaam={welkomMerk}
      naam={String(user?.name || '').trim().split(/\s+/)[0] || ''}
      stappen={welkomStappen}
      uiTheme={uiTheme}
      fadet={welkomFadet}
      overDashboard
    />
  ) : null;


  return <StageDescContext.Provider value={stageDescriptions}><div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex">
    {/* Het welkomstscherm ligt OVER de al gerenderde app: die bouwt intussen door,
        dus zodra het wegvalt staat het dashboard er echt — geen tweede laadmoment. */}
    {welkomScherm}
    {/* Mobiele hamburger — opent/sluit de off-canvas sidebar (alleen < md). */}
    <button onClick={() => setMobileNavOpen(o => !o)} className="md:hidden fixed top-3 left-4 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 shadow-lg text-slate-300 hover:text-slate-100 transition-colors" aria-label="Menu openen/sluiten">
      {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
    </button>
    {/* Mobiel: label-logo gecentreerd in de topbalk — altijd zichtbaar, ook als
        de sidebar dicht is. Bij gebrek aan plaats schaalt het mee (max-w) of toont
        de favicon/beeldmerk. Verborgen wanneer het off-canvas menu open is. */}
    {!mobileNavOpen && <div className="md:hidden fixed top-3 left-1/2 -translate-x-1/2 z-40 bg-white rounded-xl border border-slate-200 shadow-lg px-2.5 py-1 pointer-events-none">{(() => {
      // Merk-afscherming (zie CLAUDE.md): admin/et zien het ET-logo; partnerrollen
      // hun eigen label-logo, of (zonder logo) een neutrale tekst-badge — NOOIT
      // het ET-logo of de merknaam.
      const canEt = seesEtBrand(user?.role);
      const logo = themedBranding?.logo_data_url || (canEt ? "/logo.png" : null);
      const name = themedBranding?.display_name || (canEt ? "Energy Together" : (user?.energyLabel || "Partner Portal"));
      return logo
        ? <img src={logo} alt={name} className="h-7 max-w-[140px] object-contain" />
        : <span className="text-sm font-bold text-slate-800 px-1">{name}</span>;
    })()}</div>}
    {/* Mobiel: de bel (Hoogtepunten) naast de hamburger — rechtsboven staat de
        accountknop al, in het midden het label-logo. */}
    {gami.actief && !mobileNavOpen && <button onClick={() => setHoogtepuntenOpen(true)} className="md:hidden fixed top-3 left-[4.25rem] z-40 flex items-center justify-center w-10 h-10 rounded-full bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 shadow-lg text-slate-300 hover:text-slate-100 transition-colors" aria-label={gami.ongelezen ? `Hoogtepunten, ${gami.ongelezen} nieuw` : 'Hoogtepunten'}>
      <Bell size={18} />{gami.ongelezen > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-lime-500 border border-slate-900" />}
    </button>}
    {/* Achtergrond-overlay bij open mobiel menu (alleen < md). */}
    {mobileNavOpen && <button className="md:hidden fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm" aria-label="Menu sluiten" onClick={() => setMobileNavOpen(false)} />}
    <div ref={accountBarRef} className="fixed top-3 right-4 z-50 flex items-center gap-2">
      {isAdmin && !IS_PREVIEW_FRAME && <button onClick={() => setShowDevicePreview(true)} title="Preview als tablet/smartphone" aria-label="Preview als tablet/smartphone" className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 text-slate-300 hover:text-lime-400 hover:border-lime-500/40 shadow-lg transition-colors"><Smartphone size={16} /></button>}
      {impersonatedBy && <div className="flex items-center gap-2 bg-lime-500 text-slate-900 pl-3 pr-1.5 py-1 rounded-full shadow-lg text-xs font-medium" title={`Je bekijkt het dashboard als ${user?.name} (${user?.role})`}><Eye size={14} /><span className="hidden md:inline">Als <strong>{user?.name}</strong></span><button onClick={handleStopImpersonate} className="px-2.5 py-1 bg-slate-900 text-lime-400 rounded-full text-xs font-semibold hover:bg-slate-800 transition-colors">Terug naar admin</button></div>}
      <div className="relative">
        <button onClick={() => setAccountMenuOpen(o => !o)} className={`flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-full border shadow-lg transition-all ${accountMenuOpen ? 'bg-slate-800 border-lime-500/40' : 'bg-slate-900/95 backdrop-blur-xl border-slate-700/50 hover:border-slate-500'}`} title="Account &amp; beheer">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-lime-500/20 text-lime-400 border border-lime-500/30 text-xs font-bold shrink-0">{userInitials}</span>
          <span className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-xs font-medium text-slate-200 truncate max-w-[9rem]">{user?.name}</span>
            <span className="text-[10px] text-slate-500">{roleLabel(user?.role)}</span>
          </span>
          <Settings size={16} className={`text-slate-400 transition-transform ${accountMenuOpen ? 'rotate-90 text-lime-400' : ''}`} />
        </button>
        {accountMenuOpen && <>
          <button className="fixed inset-0 z-40 cursor-default" aria-label="Menu sluiten" onClick={() => setAccountMenuOpen(false)} />
          <div className="absolute right-0 mt-2 w-60 z-50 bg-slate-900 border border-slate-700/70 rounded-2xl shadow-2xl p-2">
            <div className="px-3 py-2 border-b border-slate-700/50 mb-1">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button onClick={() => goToFromMenu('account')} className={accountItemCls('account')}><User size={18} /><span>Account</span></button>
            {(canViewUsers || isAdmin) && <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Beheer</div>
              {canViewUsers && <button onClick={() => goToFromMenu('users')} className={accountItemCls('users')}><Users size={18} /><span>Gebruikers</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('settings-label')} className={accountItemCls('settings-label')}><Tag size={18} /><span>Branding</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('settings-general')} className={accountItemCls('settings-general')}><Settings size={18} /><span>Instellingen</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('index-parameters')} className={accountItemCls('index-parameters')}><TrendingUp size={18} /><span>Indexparameters</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('mail-templates')} className={accountItemCls('mail-templates')}><MessageSquare size={18} /><span>Mailsjablonen</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('mail-domains')} className={accountItemCls('mail-domains')}><Send size={18} /><span>Mail Domeinen</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('opp-stages')} className={accountItemCls('opp-stages')}><Tag size={18} /><span>Opp. Stages</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('helpdesk-tags')} className={accountItemCls('helpdesk-tags')}><LifeBuoy size={18} /><span>Helpdesk tags</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('jobs')} className={accountItemCls('jobs')}><CalendarDays size={18} /><span>Jobs</span></button>}
              {isAdmin && <button onClick={() => goToFromMenu('gamification')} className={accountItemCls('gamification')}><Bell size={18} /><span>Notifications</span></button>}
            </>}
            <div className="border-t border-slate-700/50 my-1"></div>
            <button onClick={() => { setAccountMenuOpen(false); logout(); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all text-sm font-medium"><LogOut size={18} /><span>Uitloggen</span></button>
          </div>
        </>}
      </div>
    </div>
    {/* Decoratieve achtergrondvlekken: 384px breed op `left-1/4`, dus op een
        telefoon ruim buiten het scherm. overflow-hidden klipt ze netjes af (ze
        zijn geblurd, dus visueel niet te zien) i.p.v. te riskeren dat ze de
        scrollbreedte beïnvloeden. */}
    <div className="fixed inset-0 pointer-events-none overflow-hidden"><div className="absolute top-0 left-1/4 w-96 h-96 bg-lime-500/5 rounded-full blur-3xl" /><div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" /></div>
    <aside className={`fixed left-0 top-0 h-full bg-slate-900/95 backdrop-blur-xl border-r border-slate-700/50 z-40 transition-all duration-300 flex flex-col ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
      {/* Header — logo + inklap-knop bovenaan */}
      {/* Uitgeklapt staat het logo GECENTREERD in de sidebar en zweeft de
          inklapknop rechts erover (absolute), zodat die de centrering niet naar
          links duwt zoals justify-between deed. Ingeklapt blijft de kolomstand:
          beeldmerk boven, knop eronder — daar is centreren al vanzelfsprekend. */}
      <div className={`p-3 border-b border-slate-700/50 shrink-0 flex items-center gap-2 ${sidebarCollapsed ? 'flex-col' : 'relative justify-center'}`}>
        {(() => {
          // Merk-afscherming (zie CLAUDE.md): admin/et zien het ET-logo; partnerrollen
          // hun eigen label-logo/favicon, of (zonder branding) een neutrale tekst-
          // badge met hun energielabel — NOOIT het ET-logo of de merknaam.
          const canEt = seesEtBrand(user?.role);
          const logo = sidebarCollapsed
            ? (themedBranding?.favicon_data_url || themedBranding?.logo_data_url || (canEt ? "/logo192.png" : null))
            : (themedBranding?.logo_data_url || (canEt ? "/logo.png" : null));
          const name = themedBranding?.display_name || (canEt ? "Energy Together" : (user?.energyLabel || "Partner Portal"));
          return logo
            ? <img src={logo} alt={name} className={`object-contain shrink-0 ${sidebarCollapsed ? 'h-10 w-10' : 'h-11 max-w-[150px]'}`} />
            : <span className={`font-bold text-slate-100 shrink-0 truncate ${sidebarCollapsed ? 'text-xs text-center w-10' : 'text-base max-w-[150px] text-center'}`}>{name}</span>;
        })()}
        <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? 'Menu uitklappen' : 'Menu inklappen'} className={`shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-all ${sidebarCollapsed ? '' : 'absolute right-3'}`}>
          <ChevronDown size={18} className={`transform transition-transform ${sidebarCollapsed ? '-rotate-90' : 'rotate-90'}`} />
        </button>
      </div>

      {/* Scrollable navigation — sluit het mobiele off-canvas menu na een keuze. */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" onClick={() => setMobileNavOpen(false)}>
        {visibleNavItems.map(item => {
          // Optimise draagt een telletje zodra er een bericht op antwoord wacht;
          // Helpdesk zodra er tickets op de gebruiker wachten. Ingeklapt past geen
          // getal meer: dan volstaat een stip op het icoon.
          const nieuw = item.id === 'optimise' ? feedbackUnread : item.id === 'helpdesk' ? helpdeskTodo : 0;
          return (
          <button key={item.id} onClick={() => setActivePage(item.id)} title={sidebarCollapsed ? `${item.label}${nieuw ? ` — ${nieuw} nieuw` : ''}` : undefined} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${activePage === item.id ? 'bg-lime-500/10 text-lime-400 border border-lime-500/30' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
            <span className="relative shrink-0">
              <item.icon size={20} className="shrink-0" />
              {nieuw > 0 && sidebarCollapsed && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-500 border border-slate-900" />}
            </span>
            {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
            {nieuw > 0 && !sidebarCollapsed && <span className="ml-auto px-1.5 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold leading-4">{nieuw}</span>}
          </button>
          );
        })}

        {canSeeAuditLog && <>
          <div className="border-t border-slate-700/50 my-1.5"></div>
          <button onClick={() => setActivePage('audit-log')} title={sidebarCollapsed ? 'Audit Log' : undefined} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${activePage === 'audit-log' ? 'bg-lime-500/10 text-lime-400 border border-lime-500/30' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
            <ScrollText size={20} className="shrink-0" />{!sidebarCollapsed && <span className="font-medium">Audit Log</span>}
          </button>
          <button onClick={() => setActivePage('mail-log')} title={sidebarCollapsed ? 'Mail Log' : undefined} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${activePage === 'mail-log' ? 'bg-lime-500/10 text-lime-400 border border-lime-500/30' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
            <Mail size={20} className="shrink-0" />{!sidebarCollapsed && <span className="font-medium">Mail Log</span>}
          </button>
        </>}
      </nav>
      {/* Footer: PWA-install (alleen als installeerbaar) + thema-toggle. */}
      <div className="p-3 border-t border-slate-700/50 shrink-0 space-y-0.5">
        {installPrompt && <button
          onClick={handleInstallApp}
          title="Installeer dit portaal als app op je toestel"
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all bg-lime-500/10 text-lime-400 border border-lime-500/30 hover:bg-lime-500/20 ${sidebarCollapsed ? 'justify-center' : ''}`}
        >
          <Download size={20} className="shrink-0" />
          {!sidebarCollapsed && <span className="font-medium">Installeer app</span>}
        </button>}
        {gami.actief && <button
          onClick={() => { setHoogtepuntenOpen(true); setMobileNavOpen(false); }}
          title={gami.ongelezen ? `Hoogtepunten — ${gami.ongelezen} nieuw` : 'Hoogtepunten'}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-slate-400 hover:bg-slate-800 hover:text-slate-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
        >
          <span className="relative shrink-0"><Bell size={20} className="shrink-0" />{gami.ongelezen > 0 && sidebarCollapsed && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-lime-500 border border-slate-900" />}</span>
          {!sidebarCollapsed && <span className="font-medium">Hoogtepunten</span>}
          {gami.ongelezen > 0 && !sidebarCollapsed && <span className="ml-auto px-1.5 rounded-full bg-lime-500 text-slate-900 text-[10px] font-bold leading-4">{gami.ongelezen > 99 ? '99+' : gami.ongelezen}</span>}
        </button>}
        <button
          onClick={() => setUiTheme(t => t === 'light' ? 'dark' : 'light')}
          title={uiTheme === 'light' ? 'Schakel naar donker thema' : 'Schakel naar licht thema'}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-slate-400 hover:bg-slate-800 hover:text-slate-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
        >
          {uiTheme === 'light' ? <Moon size={20} className="shrink-0" /> : <Sun size={20} className="shrink-0" />}
          {!sidebarCollapsed && <span className="font-medium">{uiTheme === 'light' ? 'Donker thema' : 'Licht thema'}</span>}
        </button>
      </div>
    </aside>
    {/* HOOFDOORZAAK mobiele overflow: <main> is een flex-child en had geen
        `min-w-0`. Een flex-item heeft standaard `min-width:auto`, waardoor het
        NIET onder de intrinsieke breedte van zijn inhoud kan krimpen: één brede
        tabel of nowrap-rij rekte zo de hele pagina op en dwong tot uitzoomen.
        Met min-w-0 blijft main binnen de viewport en scrollen brede blokken
        binnen hun eigen container. */}
    <main className={`flex-1 min-w-0 transition-all duration-300 ml-0 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}><div className={`w-full mx-auto px-4 pt-16 md:px-6 md:py-8 2xl:px-8 max-w-[2200px] ${installPrompt && !installDismissed && !IS_PREVIEW_FRAME ? 'pb-28' : 'pb-6'}`}>{renderPage()}</div></main>
    {showDevicePreview && <DevicePreview page={activePage} onClose={() => setShowDevicePreview(false)} />}
    {hoogtepuntenOpen && gami.actief && <HoogtepuntenPaneel gami={gami} onSluit={() => setHoogtepuntenOpen(false)} toonContext={user?.role === 'admin' || user?.role === 'et_dossier'} />}
    <MijlpaalKaart kaart={gami.kaart} onSluit={gami.sluitKaart} onBekijk={magPrestaties ? () => { gamiSalesStartTab = 'prestaties'; setActivePage('sales'); window.dispatchEvent(new Event('gami-open-prestaties')); } : null} />
    {/* Mobiele bottom-tab-bar (<md): app-achtige snelnavigatie. Toont de eerste
        modules uit het ROL-GESCOPETE menu (visibleNavItems) + een Menu-knop naar de
        volledige off-canvas nav. Actief = merk-accentkleur per energielabel (de
        text-lime-400-class wordt per label omgezet; admin/zonder branding = lime).
        bg/randen volgen het thema via het brand-color-blok. Hoogte = --tabbar-h. */}
    <nav className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/60" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} aria-label="Snelnavigatie">
      <div className="flex items-stretch justify-around">
        {visibleNavItems.slice(0, 4).map(item => {
          const Icon = item.icon; const active = activePage === item.id;
          return (
            <button key={item.id} onClick={() => { setActivePage(item.id); setMobileNavOpen(false); }} aria-current={active ? 'page' : undefined}
              className={`flex-1 min-w-0 flex flex-col items-center gap-0.5 py-2 ${active ? 'text-lime-400' : 'text-slate-400'}`}>
              <Icon size={20} className="shrink-0" />
              <span className="text-[10px] font-medium leading-none truncate max-w-full px-0.5">{item.label}</span>
            </button>
          );
        })}
        <button onClick={() => setMobileNavOpen(true)} className="flex-1 min-w-0 flex flex-col items-center gap-0.5 py-2 text-slate-400" aria-label="Volledig menu openen">
          <Menu size={20} className="shrink-0" />
          <span className="text-[10px] font-medium leading-none">Menu</span>
        </button>
      </div>
    </nav>
    {/* Mobiele PWA-installbanner: op <md is de sidebar-installknop off-canvas, dus
        tonen we hier een nette, wegklikbare onderbalk — net boven de tab-bar (--tabbar-h). */}
    {/* Zolang de banner er staat, zegt --installbanner-h hoe hoog hij is: een
        plakkende actieregel onderaan (Helpdesk → Nieuw ticket) schuift dan mee
        omhoog in plaats van eronder te verdwijnen. */}
    {installPrompt && !installDismissed && !IS_PREVIEW_FRAME && <style>{'@media (max-width: 767px) { :root { --installbanner-h: 5.25rem; } }'}</style>}
    {installPrompt && !installDismissed && !IS_PREVIEW_FRAME && (
      <div className="md:hidden fixed inset-x-0 z-40 p-3" style={{ bottom: 'calc(var(--tabbar-h) + 0.25rem)' }}>
        <div className="flex items-center gap-3 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-slate-700/60 shadow-2xl p-3">
          <div className="w-10 h-10 rounded-xl bg-lime-500/15 border border-lime-500/30 flex items-center justify-center shrink-0"><Download size={20} className="text-lime-400" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-100">Installeer als app</div>
            <div className="text-xs text-slate-400 truncate">Sneller openen vanaf je beginscherm</div>
          </div>
          <button onClick={handleInstallApp} className="shrink-0 px-3 py-2 rounded-lg bg-lime-500 hover:bg-lime-400 text-slate-900 text-sm font-semibold">Installeren</button>
          <button onClick={dismissInstall} aria-label="Sluiten" className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X size={18} /></button>
        </div>
      </div>
    )}
    {/* Demo-account: permanente markering. De cijfers kloppen, maar élk
        klantgegeven is door de server vervangen (XXXX of een pseudoniem als
        "Klant 1"/"Verkoper 1") — dat mag nooit voor echte data doorgaan, ook
        niet op een screenshot van deze sessie. */}
    {user?.demoMode && (
      <div className="fixed bottom-3 right-3 z-40" style={{ marginBottom: 'var(--tabbar-h)' }}>
        <div className="flex items-center gap-2 rounded-full bg-slate-900/95 backdrop-blur-xl border border-amber-500/40 shadow-2xl px-3 py-1.5">
          <Eye size={14} className="text-amber-400 shrink-0" />
          <span className="text-[11px] font-medium text-slate-100">Demo — klantgegevens geanonimiseerd</span>
        </div>
      </div>
    )}
    {/* PWA: discrete offline-indicatie — API-data kan verouderd zijn zonder verbinding. */}
    {!isOnline && (
      <div className="fixed top-3 left-1/2 -translate-x-1/2 z-40 px-3 max-w-[92vw]" style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center gap-2 rounded-full bg-slate-900/95 backdrop-blur-xl border border-amber-500/40 shadow-2xl px-4 py-2">
          <WifiOff size={16} className="text-amber-400 shrink-0" />
          <span className="text-xs font-medium text-slate-100">Geen verbinding — live gegevens zijn mogelijk niet actueel</span>
        </div>
      </div>
    )}
    {/* PWA: update-melding wanneer een nieuwe versie klaarstaat. */}
    {swUpdateReg && (
      <div className="fixed inset-x-0 z-40 flex justify-center px-3"
        style={{ bottom: `calc(${installPrompt && !installDismissed && !IS_PREVIEW_FRAME ? '5.75rem' : '0.75rem'} + var(--tabbar-h))` }}>
        <div className="flex items-center gap-3 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-indigo-500/40 shadow-2xl p-3 w-full max-w-md">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0"><RefreshCw size={20} className="text-indigo-400" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-100">Nieuwe versie beschikbaar</div>
            <div className="text-xs text-slate-400 truncate">Herladen om de laatste verbeteringen te gebruiken</div>
          </div>
          <button onClick={applyUpdate} className="shrink-0 px-3 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold">Herladen</button>
          <button onClick={() => setSwUpdateReg(null)} aria-label="Later" className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X size={18} /></button>
        </div>
      </div>
    )}
  </div></StageDescContext.Provider>;
}

// Volledig-scherm blokkering wanneer een gebruiker op een merkdomein zit dat niet
// bij zijn account hoort, of op een onbekend domein. BEWUST geen stille redirect:
// de sessie-token staat in localStorage en is per-origin geïsoleerd → een redirect
// naar een ander domein zou de gebruiker uitgelogd achterlaten. Daarom een
// duidelijke melding + knop naar het juiste domein, waar hij gewoon inlogt.
function BrandBlockScreen({ kind, expectedHost, brandName }) {
  const { logout } = useAuth();
  const url = expectedHost ? `https://${expectedHost}` : null;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-slate-800/80 border border-slate-700 rounded-2xl p-8 text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center"><AlertCircle className="text-amber-400" size={26} /></div>
        {kind === 'unknown' ? <>
          <h1 className="text-xl font-bold text-slate-100 mb-2">Onbekend domein</h1>
          <p className="text-sm text-slate-400">Dit webadres hoort bij geen enkel merk van het platform. Controleer de URL of neem contact op met je beheerder.</p>
        </> : <>
          <h1 className="text-xl font-bold text-slate-100 mb-2">Verkeerd merkdomein</h1>
          <p className="text-sm text-slate-400">Je account hoort bij <strong className="text-slate-200">{brandName || 'een ander merk'}</strong>. Log in via het juiste adres om je portaal te openen.</p>
          {url && <a href={url} className="inline-block mt-5 px-5 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold rounded-xl text-sm">Ga naar {expectedHost}</a>}
        </>}
        <button onClick={logout} className="block mx-auto mt-4 text-xs text-slate-500 hover:text-slate-300">Uitloggen</button>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, loading, user, acceptPrivacy, impersonatedBy } = useAuth();
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaMode, setMfaMode] = useState(null);
  const [qrCode, setQrCode] = useState(null);

  // Demo-account (en elke prospect): de weergave-maskering (EAN's gedeeltelijk,
  // voor een prospect ook het bronlabel in nummers en teksten) staat aan zolang
  // deze gebruiker ingelogd is — ook bij impersonatie. Zie src/demoWeergave.js
  // en src/prospect.js; de server maskeert de data zelf (utils/demo.js).
  useEffect(() => {
    zetProspect(user && isProspectRol(user.role) ? user.prospectLabel : null);
    if (user && (user.demoMode || isProspectRol(user.role))) { startDemoWeergave(); return stopDemoWeergave; }
    stopDemoWeergave();
    return undefined;
  }, [user]);

  // White-label pre-login branding: haal op basis van het hostname het merk-logo +
  // -naam op (publiek endpoint, geen auth) en toon dat op de login-/MFA-/reset-
  // schermen + browsertab. Dev/onbekend host → null → neutrale default.
  //
  // De EERSTE tekening komt uit het voorgeheugen (zie src/brandingCache.js): dit
  // endpoint kost een netwerkronde, en zolang die liep stond hier het platformlogo
  // — bij een energielabel dus even het verkeerde merk. Het antwoord van de server
  // overschrijft het voorgeheugen zodra het er is.
  const [publicBrand, setPublicBrand] = useState(() => leesMerkCache(window.location.hostname));
  useEffect(() => {
    let alive = true;
    const host = window.location.hostname;
    fetch(`/api/branding/public?host=${encodeURIComponent(host)}`)
      .then(r => r.ok ? r.json() : null)
      // Bewust alleen bij een GESLAAGD antwoord overschrijven: een mislukte call
      // mag een geldig onthouden merk niet wegvegen.
      .then(d => { if (alive && d) { setPublicBrand(d); schrijfMerkCache(host, d); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // Welk merk bij dit domein hoort weten we ZONDER netwerk (BRAND_CONFIG). Genoeg
  // om meteen de juiste naam te tonen én — belangrijker — om het platformlogo nooit
  // op een merkdomein te laten verschijnen: daar toont het scherm liever niets dan
  // het verkeerde merk (compliance, zie CLAUDE.md). Enkel het hoofddomein en dev
  // vallen nog op /logo.png terug.
  const hostMerk = getBrandFromHostname(window.location.hostname).brand;
  const loginBrand = {
    logo: (publicBrand && publicBrand.branding && publicBrand.branding.logo_data_url) || null,
    name: (publicBrand && publicBrand.display_name) || hostMerk?.display_name || null,
    etLogoToegestaan: etLogoToegestaan(window.location.hostname),
  };
  // Browsertab-titel + favicon vóór login op basis van het merk.
  useEffect(() => {
    if (isAuthenticated) return; // na login regelt PartnerDashboard de branding
    if (loginBrand && loginBrand.name) document.title = loginBrand.name;
    const fav = publicBrand && publicBrand.branding && publicBrand.branding.favicon_data_url;
    if (fav) {
      document.querySelectorAll("link[rel~='icon']").forEach(l => l.remove());
      const link = document.createElement('link'); link.rel = 'icon'; link.href = fav; document.head.appendChild(link);
    }
    // Onthouden voor de VOLGENDE start: index.html zet titel + favicon dan al vóór
    // de eerste tekening. Alleen zodra het publieke merk echt binnen is, anders
    // zouden we een geldig onthouden icoon wissen op grond van "nog niets bekend".
    if (publicBrand) onthoudTabMerk(loginBrand.name, fav || null);
    // Ook het INSTALLATIE-icoon volgt het merk van het domein: wie de app vanaf een
    // merkdomein installeert (dat kan al vóór het inloggen) hoort dat merk op zijn
    // beginscherm te krijgen, niet het beeldmerk van het platform. Het hoofddomein
    // heeft geen energielabel en houdt dus het statische manifest.
    const label = publicBrand && publicBrand.energy_label;
    if (!label) return;
    const q = new URLSearchParams({ label }).toString();
    const zet = (rel, href) => {
      let l = document.querySelector(`link[rel="${rel}"]`);
      if (!l) { l = document.createElement('link'); l.rel = rel; document.head.appendChild(l); }
      l.href = href;
    };
    zet('manifest', `/api/branding/manifest?${q}`);
    zet('apple-touch-icon', `/api/branding/app-icon?${q}`);
    // Zelfde regel als na het inloggen: een installatie-aanbod dat nog bij het
    // platform-manifest hoort, hoort niet bij dit merkdomein. (Zie src/index.js.)
    window.__manifestGebrand = true;
    if (window.__installPrompt && !window.__installPrompt.gebrand) window.__installPrompt = null;
  }, [isAuthenticated, publicBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fase 1 van de koude start: de sessiecontrole (/me) loopt nog. Hier stond een
  // kale spinner op een zwart scherm — precies het generieke laadscherm dat we
  // niet willen, en op een trage lijn is dit het EERSTE wat iemand ziet. Nu
  // dezelfde welkomstkaart, alleen zonder naam (die kennen we nog niet) en met het
  // merk van het DOMEIN als logo, uit het publieke branding-endpoint dat hierboven
  // al opgehaald wordt voor de login-schermen.
  if (loading) {
    const thema = typeof localStorage !== 'undefined' && localStorage.getItem('uiTheme') === 'dark' ? 'dark' : 'light';
    // Deep-link-tab (?sel=…): geen welkomstkaart, alleen een stil laadteken — zie
    // StilLaadscherm. De pagina die daarna opent toont haar eigen laadstaat.
    // Idem voor een tab die rechtstreeks op EEN ticket opent (#helpdesk/ticket/…).
    if (startSelection() || IS_TICKET_DEEPLINK_START) return <StilLaadscherm uiTheme={thema} />;
    return (
      <WelkomstKaart
        logo={loginBrand?.logo || null}
        merknaam={loginBrand?.name || null}
        stappen={WELKOM_STAPLABELS.map((label) => ({ label, klaar: false }))}
        uiTheme={thema}
      />
    );
  }

  // Reset-link uit de e-mail (?reset=<token>): toon het reset-scherm zolang je niet
  // ingelogd bent. Na succes haalt het scherm de parameter weg → terug naar login.
  const resetToken = new URLSearchParams(window.location.search).get('reset');
  if (!isAuthenticated && resetToken) {
    return <ResetPasswordPage token={resetToken} brand={loginBrand} />;
  }

  if (isAuthenticated && !user?.privacyAcceptedAt) {
    return <PrivacyPolicyPage onAccepted={acceptPrivacy} brand={loginBrand} />;
  }

  if (isAuthenticated) {
    // Canonieke hoofd-URL: admin + ET Dossierbeheerder horen op het hoofdmerk-domein
    // (partners.energytogether.be), niet op de technische onrender-URL. Stuur ze
    // daarheen (pad behouden). Cross-origin → de sessie reist niet mee, dus ze loggen
    // één keer opnieuw in op het nieuwe domein. Partnerrollen blijven ongemoeid (hun
    // eigen merkdomeinen zijn mogelijk nog niet live); localhost telt niet als onrender.
    if (isOnrenderHost(window.location.hostname) && (user.role === 'admin' || user.role === 'et_dossier')) {
      const target = `https://${MAIN_BRAND.partner_host}${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(target);
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center text-slate-400 text-sm">
          Doorsturen naar {MAIN_BRAND.partner_host}…
        </div>
      );
    }
    // Multibrand-guard: vergelijk het merk van het huidige domein met het merk van
    // de gebruiker. De DATA-scope is al server-side per energy_label afgedwongen
    // (een ander domein geeft dus nooit data van een ander label) — deze guard zorgt
    // voor de juiste branding/UX en stuurt naar het correcte merkdomein.
    const access = evaluateBrandAccess(user, window.location.hostname, 'partner');
    if (access.action === 'unknown') return <BrandBlockScreen kind="unknown" />;
    // Bij impersonatie springt de sessie normaal al naar het merkdomein van de
    // gebruiker (zie handleImpersonate → hopToBrandDomain), dus dan is dit een
    // 'allow'. Deze uitzondering blijft als vangnet voor de gevallen waarin die
    // sprong niet gebeurt — merk zonder eigen domein, of een mislukte overdracht.
    // Zonder de uitzondering zou de admin dan op een blokkeerscherm stranden.
    // Veilig want de data-scope is server-side afgedwongen (impersonatie-token) en
    // de branding/UX volgt al het label van de gebruiker, ongeacht het domein.
    if (access.action === 'redirect' && !impersonatedBy) return <BrandBlockScreen kind="redirect" expectedHost={access.expectedHost} brandName={access.expected?.display_name} />;
    // `publicBrand` reist mee: bij een EERSTE aanmelding op dit toestel is er nog
    // niets onthouden, en dan is het merk van dít domein de beste eerste gok voor
    // de huisstijl van deze gebruiker (zie startBranding).
    return <PartnerDashboard publicBrand={publicBrand} />;
  }

  if (mfaMode) {
    return (
      <MfaVerifyPage
        userId={mfaUserId}
        mode={mfaMode}
        qrCode={qrCode}
        brand={loginBrand}
        onBack={() => { setMfaMode(null); setMfaUserId(null); setQrCode(null); }}
      />
    );
  }

  return (
    <LoginPage
      brand={loginBrand}
      onMfaRequired={(userId) => { setMfaUserId(userId); setMfaMode('verify'); }}
      onMfaSetup={(userId, qr) => { setMfaUserId(userId); setQrCode(qr); setMfaMode('setup'); }}
    />
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
      <ToastHost />
    </AuthProvider>
  );
}
